import {
    ActionManager,
    ArcRotateCamera,
    AssetsManager,
    CSG,
    Color3,
    Color4,
    DefaultLoadingScreen,
    DynamicTexture,
    Engine,
    ExecuteCodeAction,
    HemisphericLight,
    Mesh,
    MeshBuilder,
    MultiMaterial,
    PointerDragBehavior,
    Scene,
    StandardMaterial,
    SubMesh,
    Texture,
    Tools,
    Vector3,
    Vector4,
    VertexData,
    float,
    Ray,
    DeepImmutableObject,
    AbstractMesh,
    BoundingInfo,
    Vector2,
    Quaternion,
    Logger,
} from "@babylonjs/core";

import { SeadevAR } from './ssb_ar';
import { 
    aframeCabin,
    atticModel,
    constants,
    componentTypeSettings,
    distanceLineVerticalTopOptions,
    distanceLineVerticalBottomOptions,
    distanceLineHorizontalLeftOptions,
    distanceLineHorizontalRightOptions,
    NOTICES,
    ePackageType,
    API_END_POINT,
    loftSides,
    zSide,
    positiveSide,
    typesCutFrame,
    layoutTypes,
    offsetTop,
    offsetBottom,
    interiors,
    leanToFrontPorchOffsetTop,
    innerObjectName,
    wallFinishName,
    wallFinish,
    colorLabels,
    convertSides,
    sideReverseShape,
    sizePlusX,
    sizePlusZ,
    roofSide,
    subComponentTypes,
} from './ssb_constants';
import * as helpers from './helpers';
import "@babylonjs/core/Debug/debugLayer";
import "@babylonjs/inspector";

export class SeadevShedBuilder {

    public _stairs: Array<Mesh> = [];

    public _roofBlock: Mesh;

    public _collarTieHeight: number = 1.667;

    public _horizontalTrimMat: string = 'trim_mat';
    
    public _cabinWidth: number = 12;
    
    public _highlightMat: StandardMaterial;

    public _frameOffset: number     = 0.045;
    public _frameWidth: number      = 0;
    public _frameDepth: number      = 0;
    public _frameHeight: number     = 0;
    public _frameDistance: number   = 0;
    public _frames: object          = {};
    public _frameRules: object      = {};
    public _frameMat: StandardMaterial;
    public _trimMat: StandardMaterial;
    public _frameMesh: Mesh;

    public _aframeDormerRoofDegree: number  = 47.726;

    public _activeDrag: String  = '';
    public _legDistance: number = 5;

    public _logCabinInitFBScale: number = 0;
    public _logCabinInitLRScale: number = 0;

    public _includeAddOns: any                      = [];
    public _includeComponents: any                  = [];
    public _isUpdateLayout: boolean                 = false;
    public _defaultLayoutComponents: any            = [];
    public _defaultLayoutLoaded: boolean            = false;
    public _isAllowChangeIncludeWindowVent: boolean = false;
    public _isLayoutBlock: boolean                  = false;
    public _isLayoutBlockAdding: boolean            = false;
    
    public _isShareLink: boolean = false;

    public _isValidZipcode      = false;
    public _isManufacturer      = false;
    public _isEnableNewLight    = false;

    public _leftWall: Mesh;
    public _leftWallMat: StandardMaterial;
    public _doorFrameClone: Mesh;
    public _tackDoor: any;
    public _openingList                 = [];
    public _openingWidth                = 8;
    public _doorFrameErrorNumber        = 0.15;
    public _horseruninWidthCondition    = 20;
    public _openingHeight               = 5.9;
    public _openingTopWidth             = 0.5;
    public _openingTopHeight            = 0.6;
    public _trimLength                  = 0.333;
    public _horseruninFrontPositionZ    = 0;
    public _horseruninBackPositionZ     = 0;
    
    public _roofMidPointY = 0;
    public _roofMidPointX = 0;
    
    public _smithbiltBarnRoofFix = 2.84;

    public _initRoofWidth = 0;
    public _initRoofHeight = 0;

    public _interiorFloorMatKey     = 'interior_floor_mat';
    public _frontWallMatKey         = 'front_wall_mat';
    public _backWallMatKey          = 'back_wall_mat';
    public _leftWallMatKey          = 'left_wall_mat';
    public _rightWallMatKey         = 'right_wall_mat';
    public _ifrontWallMatKey        = 'inner_front_wall_mat';
    public _ileftWallMatKey         = 'inner_left_wall_mat';
    public _iRightWallMatKey        = 'inner_right_wall_mat';
    public _tleftWallMatKey         = 'top_left_wall_mat';
    public _trightWallMatKey        = 'top_right_wall_mat';
    public _leftExtraWallMatKey     = 'left_extra_wall_mat';
    public _diagonalRightWallMatKey = 'diagonal_right_wall_mat';

    public _leftExtraFloorWallMatKey  = 'left_extra_floor_wall_mat';
    public _leftInnerPorchWallMatKey  = 'left_inner_porch_wall_mat';
    public _rightInnerPorchWallMatKey = 'right_inner_porch_wall_mat';
    public _frontInnerPorchWallMatKey = 'front_inner_porch_wall_mat';
    public _leftMiddlePorchWallMatKey = 'left_middle_porch_wall_mat';

    public _leftFrontWallMatKey = 'left_front_wall_mat';
    public _leftBackWallMatKey  = 'left_back_wall_mat';
    //Front Porch Wall Mat
    public _frontPorchWallMatKey  = 'front_porch_wall_mat';

    public _roofShingle01MatKey   = this.getSettingData( 'front_roof_mat_key_shingle' ) || 'roof_shingle_01_mat';
    public _roofShingle02MatKey   = this.getSettingData( 'back_roof_mat_key_shingle' ) || 'roof_shingle_02_mat';

    public _roofMetalHorizontal01MatKey   = this.getSettingData( 'front_roof_mat_key_metal_horizontal' ) || 'roof_metal_horizontal_01_mat';
    public _roofMetalHorizontal02MatKey   = this.getSettingData( 'back_roof_mat_key_metal_horizontal' ) || 'roof_metal_horizontal_02_mat';

    public _instances: object = { 'roofMetal': [], 'pavilionRafter': [], 'baluster': [], 'roofMetalLeanTo': [], 'roofMetalPorch': [] };
    public _anchorEditButton: any;
    public _firstMeshes: any;
    public _meshMoving: String;
    public _dragBehavior: PointerDragBehavior;
    public _cameraPosition = { x: 0, y: 0, z: 0 };
    public _componentPicked: String;
    public _imagePath: String;
    public _flatDormerDistance: number = 1;
    public _flatDormerHeight: number = 1.833;
    public _initDistancePorchError: number = 0;

    public _defaultOverhangFront: number    = 0;
    public _defaultOverhangBack: number     = 0;
    public _defaultOverhangLeft: number     = 0;
    public _defaultOverhangRight: number    = 0;

    public _customOverhangFront: number = 0;
    public _customOverhangBack: number  = 0;
    public _customOverhangLeft: number  = 0;
    public _customOverhangRight: number = 0;

    public _canvas: HTMLCanvasElement;
    public _engine: Engine;
    public _scene: Scene;
    private _camera: ArcRotateCamera;
    public _assetsManager: AssetsManager;
    public _bones: object = {};
    private _materials: object = {};
    private _isDrag: boolean;
    private _initialUVs: object = {};
    private _meshes: any = [];
    private _allMeshes: any = [];
    public _allComponents: any = {};
    private _skeletons: any = [];
    private _modelDir: string;
    private _materialsDir: string;
    public _componentDir: string;
    public _shedType: string;
    public _shedTypeVersion: string;
    private _roofShape: string;
    private _roofType: string;
    private _hingeType: string;
    private _sidingType: string;
    private _roofDegree: number;
    private forceRender;
    private forceAssetLoading;
    private _activeLayout;
    public _width;
    public _depth;
    public _wallHeight;
    private _roofPitchHeight;
    private _midRoofPitchHeight;
    private _buildingHeight;
    private _middleHeight: number = 0;
    private _initialWidth;
    private _initialDepth;
    private _initialWallHeight;
    private _initialRoofPitchHeight;
    private _initialMidRoofPitchHeight;
    private _initialBuildingHeight;
    private _initialOpeningSize;

    private _initGambrelMidXRatio: number = 0;
    private _initGambrelMidY: number = 0;
    public _roofLowerDegree: number = 0;
    public _roofUpperDegree: number = 0;
    public _initRoofLowerDegree: number = 0;
    public _initRoofUpperDegree: number = 0;
    private _initRoofLowerWidth: number = 0;
    private _initRoofUpperWidth: number = 0;
    
    private _initFlatRoofWidth: number = 0;

    private _initBoxGableWidth: number = 0;
    private _initSaltBoxWidth: number = 0;
    
    private _initRoofLeftWidth: number  = 0;
    private _initRoofRightWidth: number = 0;
    
    private _initRoofSaltboxHeight: number = 0;
    private _initRatioRoofSaltbox: number = 0;
    
    private _noticesList = [];

    private _initRoofFlatDormerWidth: number = 0;
    private _initFlatDormerHeight: number = 0;
    private _initFlatDormerWidth: number = 0;
    private _initBracesBR2;

    private _collisionBox;
    private _initDormerWidth: number = 0;
    private _initDogKennelBoxDepth: number = 0;
    private _dormerWidth: number = 0;
    public _porchWidth = 4;
    public _initPorchDepth = 4;
    public _initPorchWidth = 4;
    private _isCloneRuner = true;
    public _oldPorchWidth = 4;
    public _enabledRailing = false;
    public _sidingUscale = 3;
    public _sidingVscale = 3;
    public _floorUscale = 1;
    public _floorVscale = 1;
    public _customUVScale = [];
    public _globalData = [];
    private _roofDetachedMats = [];
    private _meshBlurForInterior = [];
    private _nullDiffuseTextures = [];
    private _blurMeshes = [];
    private _roofMesh: Record<string, string[]> = {};
    private _componentData = (<any>window).api_cat;
    public _dormerOption = 'none';
    public _isUseRestrictSize = false;
    public _allowAddToCart   = true;
    public _isDisableAddCart = true;
    public _isEnableZonPrice = false;
    public _availableRoofMaterials = ( <any>window ).ssb.roof_materials;;
    public _isEnableKickBoard   = false;
    public _innerMaterial: any  = '';
    public _wallFinishMat: any  = '';
    public _isEnableWallDormerSettings = false;
    public _isEnableTackRoom           = false;
    public _isAllowEnableTackRoom      = false;
    public tackroomNotFound            = false;
    public _dataSettingsTackroom       = [];
    public _dataTackroom : any         = {};
    public _doorsOpeningTackRoom : any = [];
    public _sideWallTackRoom : any     = '';
    public _partialWall : any          = [];
    public _originOverhang             = { 'left': 0, 'right' : 0, 'zerroNum': 0, 'constant': 1 };
    public _positionValidOnMove:any = undefined;
    public _isNewMovingComponent = false;
    // public _isNewDistributionMethod = false;
    public _isHorizontalDistribution = false;
    public _isDrawInteriorSiding = false;
    public _defaultColorInteriorSiding = '#ffffff';
    public _isEnablePlywoodLayer = false;
    
    public _walls: object = {
        "front" :               { "items": {} },
        "back":                 { "items": {} },
        "left":                 { "items": {} },
        "right":                { "items": {} },
        "roof":                 { "items": {} },
        "porch":                { "items": {} },
        "dormer":               { "items": {} },
        "roof_left":            { "items": {} },
        "roof_right":           { "items": {} },
        "inner_left":           { "items": {} },
        "inner_front":          { "items": {} },
        "right_inner_porch":    { "items": {} },
        "left_inner_porch":     { "items": {} },
        "front_inner_porch":    { "items": {} },
        "left_middle_porch":    { "items": {} },
        "left_front":           { "items": {} },
        "left_back":            { "items": {} },
        "diagonal_right":       { "items": {} },
        "inner_right":          { "items": {} },
        "top_left":             { "items": {} },
        "top_right":            { "items": {} },
        "top_front":            { "items": {} },
        "top_back":             { "items": {} },
        "inner_back":           { "items": {} },
        "lean_to_right":        { "items": {} },
        "lean_to_left":         { "items": {} },
        "lean_to_front":        { "items": {} },
        "lean_to_back":         { "items": {} },
        "floor":                { "items": {} },
    };

    private _hipRoofTopLength : number = 0;
    private _initHipRoofTopLength : number = 0;
    private _midPoint = 'b_b_t';
    private _midPointPosition: Vector3 = Vector3.Zero();
    public _isInterior = false;
    private _postWidth = 0.3;
    private _lightOffsetY = 0.285;

    private _dormerHeight : number = 1.5;

    public _externalPorchLocation: string;
    public _postDistance: number; 
    public _lineVerticalTopOptions = distanceLineVerticalTopOptions;
    public _lineVerticalBottomOptions = distanceLineVerticalBottomOptions;
    public _lineHorizontalLeftOptions = distanceLineHorizontalLeftOptions;
    public _lineHorizontalRightOptions = distanceLineHorizontalRightOptions;

    private _availableComponents: object = {};
    private _layoutComponents: object = {};
    private _currentSidingDiffuseTexture = "";
    private _currentSidingBumpTexture = "";
    private _currentSpecularTexture = "";
    public _componentTypeSettings = componentTypeSettings;

    // Variables for tracking move component feature
    private _arrowHorizontalLeftStart: any;
    private _arrowHorizontalLeftEnd: any;
    private _arrowHorizontalRightStart: any;
    private _arrowHorizontalRightEnd: any;
    private _arrowVerticalTopStart: any;
    private _arrowVerticalTopEnd: any;
    private _arrowVerticalBottomStart: any;
    private _arrowVerticalBottomEnd: any;
    private _lineVerticalTop: any;
    private _lineVerticalBottom: any;
    private _lineHorizontalLeft: any;
    private _lineHorizontalRight: any;
    private _distanceTextHolderHorizontalLeft: any;
    private _distanceTextHolderHorizontalRight: any;
    private _distanceTextHolderVerticalTop: any;
    private _distanceTextHolderVerticalBottom: any;
    private _distanceTextHorizontalLeft: any;
    private _distanceTextHorizontalRight: any;
    private _distanceTextVerticalTop: any;
    private _distanceTextVerticalBottom: any;
    private _color: string = 'white';
    private _font: string = 'bold 100px monospace';
    private _background: string = 'black';

    private _allowDrag: boolean = true;

    private _offsetDistance = 0.3;

    private _distance2Component = 0;

    public _numberRunner = 0;

    private _balusterOffsetLeft = 0.15;
    private _balusterOffsetFront = 0.175;
    private _balusterMiddleSpace = 0.5;

    private _quakerDistanceX = 0.586; //0.586 is the distance between the center of the "Mainus Quaker" model and the center of the other models (Barn, Cottage,...)

    private _verticalRunner: String = 'vertical';
    private _horizontalRunner: String = 'horizontal';

    private _runnerType: String = this._verticalRunner;
    public _doorFixedTarget = [];
    public NOTICES = NOTICES

    public _roofMiddleR2Position;
    public _sizeErrorValue;
    public _isShowPricePurgeDetails: boolean = false;
    public initRoofPermatileMetal = {
        'permatile_metal' : 0,
        'standing_seam' : 0,
    };

    public _electricalPackageID = 'none';
    public _defaultEPackageComponents = [];
    public _defaultAllComponentInPackage = [];
    public _isUpdateLayoutEPackage: boolean = false;
    public _apiData = [];
    public _wainscotingType: number = 1; //1: none | 2: All Sides | 3: Specific Side (s)
    public _wainscotingSide: Array<string> = [];
    public _roofMaterial = [];
    public _quakerWidth = 0;
    
    private _shuttersInObject = {};
    private _roofBoneSync = [];

    public _porchCornerDistance: number = 0.667;
    public _roofUVScale = {
        roof01: { u: 0, v: 0 },
        roof02: { u: 0, v: 0 },
        roof03: { u: 0, v: 0 },
        roof04: { u: 0, v: 0 },
        roof05: { u: 0, v: 0 },
    }
    
    constructor( canvasId: string, canvasElement: HTMLCanvasElement ) {
        let _this = this;

        _this._firstMeshes = [];

        document.getElementById( canvasId ).onwheel = function( event ) {
            event.preventDefault();
        };
        
        _this._isDrag   = false;
        _this._canvas   = canvasElement;
        
        if( _this.isMobile() ) {
            const vh            = Math.max( document.documentElement.clientHeight || 0, window.innerHeight || 0 );
            const bottomHeight  = jQuery('.wrap-price-add-to-cart').outerHeight();
            const navHeight     = jQuery('#wrapper-navbar nav.navbar').outerHeight();
            const disclaimer    = jQuery('.pricing-disclaimer').outerHeight() || 0;
            const canvasHeight  = vh - navHeight - bottomHeight - disclaimer;
            
            _this._canvas.style.height  = `${canvasHeight}px`;
            _this._canvas.style.width   = `${jQuery('#ssb-builder').width()}px`;
        }
            
        _this._engine = new Engine(_this._canvas, true,  { preserveDrawingBuffer: true, stencil: true });

        _this._modelDir                   = _this._canvas.getAttribute( "data-shed-path" );
        _this._materialsDir               = _this._canvas.getAttribute( "data-material-cdn" );
        _this._componentDir               = _this._canvas.getAttribute( "data-component-path" );
        _this._shedType                   = _this._canvas.getAttribute( "data-shed-type" );
        _this._shedTypeVersion            = _this._canvas.getAttribute( "data-shed-type-version" );
        _this._imagePath                  = _this._canvas.getAttribute( 'data-image-path' );
        _this._flatDormerDistance         = _this.getSettingData( 'dormer_alignment' ) || 1;
        _this._sizeErrorValue             = parseFloat( this._canvas.getAttribute( 'data-model-size-error-value' ) );
        _this._isShowPricePurgeDetails    = 'true' === _this._canvas.getAttribute( 'data-show-upgrade-charge-info' );
        _this._isEnableKickBoard          = 'true' === _this._canvas.getAttribute( 'is-enable-kick-board' );
        _this._isEnableWallDormerSettings = "1" == this.getSettingData( 'enable_wall_dormer_settings' );
        _this._isAllowEnableTackRoom      = "1" == _this.getSettingData( 'enable_tackroom' );
        _this._partialWall                = _this.getSettingData( 'partial_wall' );
        _this._isNewMovingComponent = '1' == _this.getSettingData( 'enable_new_feature_moving' );
        // _this._isNewDistributionMethod = '1' == _this.getSettingData( 'enable_new_distribution_method' );
        _this._isHorizontalDistribution = '1' == _this.getSettingData( 'enable_horizontal_distribution' );
        _this._isDrawInteriorSiding = "1" == _this.getSettingData( 'show_interior_siding' );
        _this._isEnablePlywoodLayer = "1" == _this.getSettingData( 'enable_plywood_layer' );
        _this._defaultColorInteriorSiding = _this.getSettingData( 'default_color_interior_siding' ) ? _this.getSettingData( 'default_color_interior_siding' ) : '#ffffff';
        _this._isUseRestrictSize = "1" == _this.getSettingData( 'is_use_restrict_size' );
        _this._allowAddToCart = _this._isUseRestrictSize ? false : true;
        if( isNaN( _this._sizeErrorValue ) || _this._sizeErrorValue <= 0 ) {
            _this._sizeErrorValue = 0 ;
        }

        _this.setHingeType( _this._canvas.getAttribute("data-model-hinge-type") )
        _this.setRoofShape( _this._canvas.getAttribute("data-model-roof-shape") );
        _this.setOpeningSize( parseFloat(_this._canvas.getAttribute("data-porch-opening-size")) );
        _this._apiData['meshes']    = [];
        _this._apiData['meshesData'] = [];
        _this._apiData['material']  = [];
        _this._apiData['interior_'] = [];
        _this._apiData['interior_ceiling_meshes'] = [];
        
        _this._apiData['interior_wall_material'] = [];
        _this._apiData['interior_ceiling_material'] = [];
        _this._apiData['wainscoting_material']              = [];
        _this._apiData['accent_gable_material']             = [];
        _this._apiData['exterior_siding_upgrades_material'] = [];
        _this._apiData['wainscoting_wall_info'] = [];
        
        let roofAngle = parseFloat( _this._canvas.getAttribute( "data-model-roof-angle" ) );
        const roofRafterLength = jQuery( 'input.ssb_roof_pitch:checked' ).val();

        if( roofRafterLength ){
            roofAngle       = this.getRoofPitchFromRadian( roofRafterLength );
            const dataKey   = jQuery( 'input.ssb_roof_pitch:checked' ).attr( 'data-key' );
            const dataName  = jQuery( 'input.ssb_roof_pitch:checked' ).attr( 'data-option-value' );
            
            jQuery( '#js-shed-data input[name="ssb-option-roof-pitch"]' ).attr( 'data-name', dataName );
            jQuery( '#js-shed-data input[name="ssb-option-roof-pitch"]' ).attr( 'data-key', dataKey );
            jQuery( '#js-shed-data input[name="ssb-option-roof-pitch"]' ).val( roofRafterLength );
        }

        _this.setRoofDegree( roofAngle );

        if( 'structure_aframe_horserunin' === _this._shedType ) {
            if( 'structure_aframe_horsebarn' === _this._shedTypeVersion ) {
                this._openingWidth              = 6.3;
                this._openingHeight             = 5.6;
                this._openingTopWidth           = 1.1;
                this._openingTopHeight          = 1.1;
                this._doorFrameErrorNumber      = 0.05;
                this._horseruninWidthCondition  = 24;
            } else {
                this._openingWidth              = 9;
                this._openingHeight             = 6;
                this._openingTopWidth           = 1;
                this._openingTopHeight          = 1.1;
                this._doorFrameErrorNumber      = 0.05;
                this._horseruninWidthCondition  = 26;
            }
        } else if( 'structure_horse_shelter' === _this._shedType ) {
            this._doorFrameErrorNumber      = 0;
            this._trimLength                = 0;
        }

        const roofShape = _this.getRoofShape();
        if( 'gambrel' === roofShape || 'monitor' === roofShape || 'dutch_style' === roofShape ) {
            const angleLower = parseFloat( _this._canvas.getAttribute( "data-model-roof-angle-lower" ) );
            if( ! isNaN( angleLower ) ) {
                _this._roofLowerDegree = angleLower;
            }

            const angleUpper = parseFloat( _this._canvas.getAttribute( "data-model-roof-angle-upper" ) );
            if( ! isNaN( angleUpper ) ) {
                _this._roofUpperDegree = angleUpper;
            }
        }

        _this.setActiveLayout('default');
        
        _this.forceRender = function () {
            clearTimeout(_this.forceRender.timerId),
                _this.forceRender.timerId = setTimeout(function () {
                    // _this.__forceRender = true;
            }, 100)
        };
        
        _this.forceAssetLoading = function () {
            clearTimeout(_this.forceAssetLoading.timerId),
                _this.forceAssetLoading.timerId = setTimeout(function () {
                    _this._assetsManager.load(), _this.forceRender()
            }, 100),
            _this.forceRender()
        }

        _this._assetsManager = new AssetsManager(_this._scene);
        
        _this._assetsManager.useDefaultLoadingScreen = false;
        _this._assetsManager.onTasksDoneObservable.add(function () {
            _this.forceRender();
        });
        _this._assetsManager.onTaskErrorObservable.add(function (error) {
            
        });

        _this._isEnableZonPrice = jQuery( 'body.enable-zone-price' ).length ? true : false;
        
        _this.handleFullScreen( canvasId );
        _this.initScene();
        _this.render();
    }

    getShuttersInObject = () => {
        return this._shuttersInObject;
    }

    getMeshByName = name => {
        return this._scene.getMeshByName( name ) as Mesh;
    }

    getAvailableMeshesTarget() {
        return this._meshes;
    }

    getAllComponents = () => {
        return this._allComponents;
    }

    getAllAvailableMeshes() {
        return this._allMeshes;
    }

    setAvailableComponents( components ) {
        let _this = this;
        _this._availableComponents = components
    }

    getAvailableComponents() {
        let _this = this;
        return _this._availableComponents;
    }
    
    setLayoutComponents( components ) {
        let _this = this;
        _this._layoutComponents         = components;
        _this._defaultLayoutComponents  = components;
    }

    getLayoutComponents() {
        const components = JSON.parse( JSON.stringify( this._layoutComponents ) );
        if( ! helpers.isSwapSide() ) {
            return components;
        }

        for( const type in components ) {
            if( ! components.hasOwnProperty( type ) ) {
                continue;
            }

            for( let i = 0; i < components[type].length; i++ ) {
                components[type][i]['side'] = this.convertSides( convertSides, components[type][i]['side'] );
                if( 'building' === components[type][i]['vertical_measured_from'] && ( 'left' === components[type][i]['side'] || 'right' === components[type][i]['side'] ) ) {
                    components[type][i]['vertical_distance']        = 0;
                    components[type][i]['vertical_measured_from']   = 'wall';
                }
            }
        }

        return components;
    }

    getRoofShape() {
        return this._roofShape;
    }

    setRoofShape(roofShape) {
        this._roofShape = roofShape;
    }

    getRoofDegree() {
        return this._roofDegree;
    }

    setRoofDegree(roofDegree) {
        this._roofDegree = roofDegree;
    }

    getRoofType() {
        return this._roofType;
    }

    setRoofType(roofType) {
        this._roofType = roofType;
    }

    getHingeType() {
        return this._hingeType;
    }

    setHingeType(hingeType) {
        this._hingeType = hingeType;
    }

    resetWall() {
        this._walls = { "front" : { "items": {} }, "back": { "items": {} }, "left": { "items": {} }, "right": { "items": {} }, "roof": { "items": {} }, "porch": { "items": {} }, "dormer": { "items": {} } };
    }

    getSidingType() {
        return this._sidingType;
    }

    setSidingType( sidingType ) {
        this._sidingType = sidingType;
    }

    getActiveLayout() {
        return this._activeLayout;
    }

    setActiveLayout(layout) {
        this._activeLayout = layout;
    }

    getInitialWidth() {
        return this._initialWidth;
    }

    getInitialDepth() {
        return this._initialDepth;
    }

    getInitialWallHeight() {
        return this._initialWallHeight;
    }

    getInitialRoofPitchHeight() {
        return this._initialRoofPitchHeight;
    }

    getInitialMidRoofPitchHeight() {
        return this._initialMidRoofPitchHeight;
    }

    getInitialBuildingHeight() {
        return this._initialBuildingHeight;
    }

    setInitialWidth(width) {
        this._initialWidth = width;
    }

    setInitialDepth(depth) {
        this._initialDepth = depth;
    }

    setInitialWallHeight(height) {
        this._initialWallHeight = height;
    }

    setInitialRoofPitchHeight(height) {
        this._initialRoofPitchHeight = height;
    }

    setInitialMidRoofPitchHeight(height) {
        this._initialMidRoofPitchHeight = height;
    }

    setInitialBuildingHeight(height) {
        this._initialBuildingHeight = height;
    }

    setOpeningSize( size ) {
        this._initialOpeningSize = size;
    }

    getOpeningSize() {
        return parseFloat( this._initialOpeningSize );
    }

    getWidth = () => {
        return parseFloat( this._width );
    }

    getDepth = () => {
        return parseFloat( this._depth );
    }

    getWallHeight = () => {
        return parseFloat( this._wallHeight ) + this._sizeErrorValue;
    }

    getSizeGroup = () => {
        return {
            width: this.getWidth(),
            depth: this.getDepth(),
            height: this.getWallHeight(),
            porchHeight: 1
        };
    }

    getRoofPitchHeight() {
        return this._roofPitchHeight;
    }

    getMidRoofPitchHeight() {
        return this._midRoofPitchHeight;
    }

    getBuildingHeight = () => {
        return Number( this._buildingHeight );
    }

    setWidth( width ) {
        if( this.isExcludePorchMeasurement() && this.isSidePorch() && ! this.isSideGable() ) {
            width += this._porchWidth;
        }

        if( this.isSideGable() ) {
            this._depth = width;
        } else {
            this._width = width;
        }
    }

    setDepth(depth) {
        if( this.isFrontPorch() ) {
            depth -= this._porchWidth;
        }

        if( this.isExcludePorchMeasurement() && this.isSidePorch() && this.isSideGable() ) {
            depth += this._porchWidth;
        }

        if( this.isSideGable() ){
            this._width = depth;
        }else{
            this._depth = depth;
        }
    }

    setWallHeight(height) {
        this._wallHeight = height;
    }

    setRoofPitchHeight(height) {
        this._roofPitchHeight = height;
    }

    setMidRoofPitchHeight(height) {
        this._midRoofPitchHeight = height;
    }

    setBuildingHeight(height) {
        this._buildingHeight = height;
    }

    /**
     * Sets a custom UV scale value for a given key.
     * 
     * This function assigns a specified UV scale value to a key in the `_customUVScale` object.
     * It allows for customizing the UV scale for different elements identified by the key.
     * 
     * @param {string} key - The key identifying the element for which the UV scale is being set.
     * @param {number} value - The UV scale value to be set.
     */
    setCustomUVScale = ( key : string, value : number ) => {
        this._customUVScale[key] = value;
    }

    /**
     * Sets a custom numeric value for a given key in the _customNumber object.
     * 
     * @param {string} key - A string representing the key to be added or updated in the _customNumber object.
     * @param {any} value - The value to associate with the provided key.
     */
    setGlobalData = ( key: string, value: any ) => {
        this._globalData[key] = value;
    }

    /**
     * Retrieves the custom UV scale value for a given key.
     * 
     * 
     * @param {string} key - The key identifying the element for which the UV scale is being retrieved.
     * @returns {number} - The UV scale value associated with the key, or 3 if the key is not found.
     */
    getCustomUVScale = ( key : string ) => {
        return this._customUVScale[key] || 3;
    }

    /**
     * Retrieves the custom numeric value for a given key in the _customNumber object.
     * 
     * @param {string} key - A string representing the key for which the value is being retrieved.
     * @returns The value associated with the key, or 1 if the key is not found.
     */
    getGlobalData = ( key: string ) => {
        return this._globalData[key] || 1;
    }

    async initModel() {
        const loftName = this.isTinyHome() ? 'loft_finishing' : 'loft';
        const externalPorch = this._canvas.getAttribute( 'data-model-porch-external' );
        const porchData = JSON.parse( atob( externalPorch ) );
        const porchName = '' == porchData.style || null == porchData.style ? 'porch' : porchData.style;
        this.getDataStructurerFromApi();

        const promises = [
            this.loadBaseModel(),
            this.loadNewRoofMaterialFiles(),
            this.loadComponentSpecial( loftName, 'loft', this.isEnableLoft() ),
            this.loadComponentSpecial( porchName, 'porch', this.isExternalPorch() ),
            this.loadSmithbiltComponentTrim(),
            this.loadComponentRoofByKey( 'permatile_metal', 'roof_permatile_aframe' ),
            this.loadComponentRoofByKey( 'standing_seam', 'roof_standing_seam_aframe' ),
            this.optimizeMaterials(),
            this.loadFrameFile(),
        ];

        const stairs = this.getAllStairs().filter( ( element, index, array ) => index === array.findIndex( item => item.name === element.name ) );
        for( let i = 0; i < stairs.length; i++ ) {
            promises.push( this.loadBabylonFile( stairs[i]['name'], 'stair' ) )
        }
        
        const rafterFile = this.getSettingData( 'rafter_style' );
        if( rafterFile ) {
            promises.push( this.loadBabylonFile( rafterFile.value, 'rafter' ) );
        }

        return Promise.all( promises );
    }

    animateSizeChange( oldSize, newSize, isChangeLayout ) {
        return new Promise( async resolve => {
            this._engine.displayLoadingUI();
            this._engine.stopRenderLoop();
            if( ! this.isCarport() ) {
                this.removeFrame();
            }
            this._hipRoofTopLength = this.getDepth() - this.getWidth();
            this.setDefaultOverhangData();
            this.isShowTackDoorOption( 'structure_horse_shelter' === this._shedType ? true : newSize.depth >= 16 );
            this.cloneExternalPost();
            oldSize = this.updateOldSize( oldSize );
            if( 'structure_barn_2story_leanto' === this._shedType ) {
                this.updateRoofOverhang();
            } else {
                this.moveBones();
            }
            this.cloneSiding3D();
            newSize.buildingHeight = this.getBuildingHeight();
            this.barnSmithbiltScaleRoof();
            this.handleExternalPorch();
            this.handleFrontPorch();
            this.handleSidePorch();            
            this.doClone();
            this.drawRoof();
            this.adjustUVs( false );
            await this.handleUpdateTackRoom();
            this.placeAllComponents(oldSize, newSize, isChangeLayout);            
            this.specialModelInjection();
            this.adjustCamera();
            this.resizeCollisionBox();
            this.updateIncludeComponents();
            await this.handleLayoutComponent();
            await this.updateComponentsBySize();
            this.handleLayoutEPackageComponent();
            this.showHorizontalTrims();
            this.distributeComponentEqually();
            this.distributeRoofComponents();
            this.updateWindowsPosition();
            this.removeLine();
            this.removeNoticeText();
            this.removeComponents( oldSize, newSize );
            this.removeComponents( oldSize, newSize, 'interior' );
            this.handleBonesScreenHouse();
            this.isValidAllComponents();
            this.updateFlatDormer();
            await this.buildMesh();
            this.removeComponentBySide( 'floor' );
            const stairs = await this.handleStairs();
            this.removeStairs();
            this._stairs = stairs;

            let event = new CustomEvent('change-size', {detail: {"oldSize": oldSize, "newSize": newSize} });
            window.dispatchEvent(event);

            this._engine.runRenderLoop( () => this._scene.render() );
            this._engine.hideLoadingUI();
            
            resolve(true);
        });
    }

    /**
     * Update component when resize
     */
    updateComponentsBySize = () => {
        const _this         = this;
        const components    = _this.getAllComponents();
        const keys          = Object.keys( components );

        return new Promise( async resolve => {
            for( const key of keys ) {
                await this.replaceComponentBySize( key );
            };

            resolve( true );
        });
    }

    /**
     * Check and replace component if it not fit with the new size
     * 
     * @param key Component key
     * @returns 
     */
    replaceComponentBySize( key ) {
        const _this = this;
        return new Promise( async resolve => {
            const editComp      = jQuery(`[data-component-key='${key}']`);
            const componentSide = editComp.attr('data-component-side');
            const componentType = editComp.attr('data-component-type');
            const componentKey  = editComp.attr('data-component-key');

            if( undefined !== componentSide && undefined !== componentType && undefined !== componentKey ) {
                const componentName = _this._walls[componentSide]['items'][componentKey]['name'];
                const widthOver     = _this.getWidthOver( componentName );
                const widthUnder    = _this.getWidthUnder( componentName );
                const heightOver    = _this.getHeightOver( componentName );
                const heightUnder   = _this.getHeightUnder( componentName );
                const wallHeight    = this.getWallHeightBySide( componentSide );
                let wallWidth       = 'front' === componentSide || 'back' === componentSide ? this.getWidth() : this.getDepth();

                if( this.isExcludePorchMeasurement() && this.isSidePorch() && ( 'front' === componentSide || 'back' === componentSide ) ) { 
                    wallWidth -= this._porchWidth;
                }
            
                if( this.isValidComponentByDimensions( widthOver, widthUnder, heightOver, heightUnder, wallWidth, wallHeight ) && this.isAllowAddComponent( componentSide, componentName ) ) {
                    return resolve( true );
                }

                this._isUpdateLayout        = true;
                const currentPosition       = _this._walls[componentSide]['items'][componentKey]['position'];
                const behavior              = _this.getBehavior( componentName, componentType );
                if( this.isHandleOffsetFloor( behavior ) ) {
                    currentPosition.y       = this.getPositionYForInterior( behavior, componentName );
                }

                const eventRemoveComponent  = new CustomEvent( 'remove-component', { detail: {
                    side: componentSide,
                    type: componentType,
                    key: componentKey
                }});
                window.dispatchEvent( eventRemoveComponent );

                const fitComponents = this.getFitComponentsByTypeAndSide( componentType, _this.getSidingType(), componentSide );
                if( fitComponents.length > 0 ) {
                    const eventAddNewComponent  = new CustomEvent( 'add-component-v2', { detail: {
                        side: componentSide,
                        type: componentType,
                        name: fitComponents[0],
                        position: currentPosition,
                    }});
                    window.dispatchEvent( eventAddNewComponent );
                }
            }
            
            return resolve( true );
        });
    }

    /**
     * Check model has inner object
     * 
     * @returns {boolean}
     */
    hasInnerObj = () => {
        if( 'structure_barn_smithbilt' === this._shedType ) {
            return true;
        }

        return false !== this.getMeshesByName( 'inner_obj' );
    }

    /**
     * Disable component moveable when interior enable or exterior enable
     */
    handleDisablePickable = () => {
        const _this      = this;
        const components = _this.getAllComponents();
        for( let k in components ) {
            let { meshes, side, type, name } = components[k];
            
            if( undefined === meshes[0] ) {
                continue;
            }

            const behavior  = _this.getBehavior( name, type );
            const firstMesh = meshes[0] as Mesh;
            if( 'door' === behavior || 'windows' === behavior || 'vent' === behavior || 'skylight_window' === behavior ) {
                _this.setPickableForWrapperMesh( firstMesh, ! _this._isInterior );
            } else if( 'cupola' === behavior || 'dormer' === behavior ) {
                _this.setPickableStatus( meshes, ! _this._isInterior );
            } else if( 'light' === behavior ) {
                _this.setPickableStatus( meshes, _this._isInterior );
            } else if( 'floor' === side ) {
                    _this.setPickableStatus( meshes, _this._isInterior );
            } else {
                _this.setPickableForWrapperMesh( firstMesh, _this._isInterior );
            }
        }
    }

    /**
     * Enable or Disable interior view
     * 
     * @param {Boolean} status Status to enable|disble for interior view
     */
    setStatusForInteriorView = ( status ) => {
        this.blurItemForInterior( status );
        this.handleDisablePickable();
    }

    isBlurStud = () => {
        return this._isDrawInteriorSiding && jQuery( '#interior_wall-material-option' ).length;
    }

    /**
     * This function is used to blur the meshes that are not interior
     * when the interior view is enabled
     */
    blurMeshes = () => {
        if( ! this.isBlurStud() ) {
            return;
        }
        const blur = this._isInterior ? 0.1 : 1;
        this._blurMeshes.forEach( name => {
            const mesh = this.getMeshByName( name );
            if( mesh ) {
                mesh.visibility     = blur;
                const childMeshes   = mesh.getChildMeshes();
                childMeshes.forEach( m => {
                    m.visibility = blur;
                } );
            }
        } );
    }

    /**
     * Disabled or Enable by status Interior
     */
    blurItemForInterior( status : boolean ) {
        const _this = this;
        let alpha   = 1, visibility = 1;

        _this._camera.checkCollisions = true;

        if( status && ! _this.hasInnerObj() ) {
            status = false;
        }

        _this._isInterior       = status;
        _this._offsetDistance   = status ? constants.interiorOffset : this.is2StoryLeanTo() ? atticModel.exteriorOffset : constants.exteriorOffset;

        this.removeLine();
        this.removeNoticeText();

        if( true === _this._isInterior ) {
            alpha       = 0.1;
            visibility  = 0.05;

            _this._camera.checkCollisions = false;
        }
        const meshes    = [ ..._this._meshBlurForInterior, 'roof_metal_obj', 'roof_shingle_obj', 'roof_metal_clone_obj', 'siding_b_obj', 'siding_f_obj', 'siding_l_obj', 'siding_r_obj', 'flat_dormer_8ft_obj', 'flat_dormer_8ft_roof_metal_obj', 'flat_dormer_8ft_roof_shingle_obj', 'flat_dormer_12ft_obj', 'flat_dormer_12ft_roof_metal_obj', 'flat_dormer_12ft_roof_shingle_obj', 'porch_f_obj', 'roof_metal_horizontal_obj', 'structure_lofted_horserunin_left_wall', 'roof_porch_metal_clone_obj', 'inner_frame_horizontal_clone_obj', 'porch_c_obj', 'back_wall_clone', 'front_wall_clone', 'inner_front_wall_clone', 'inner_left_wall_clone', 'left_wall_clone', 'right_wall_clone', 'front_wall', 'back_wall', 'left_wall', 'right_wall', 'roof_siding_M_obj', 'roof_siding_L_obj', 'roof_siding_R_obj', 'left_roof_clone', 'right_roof_clone', 'left_inner_porch_wall_clone', 'left_inner_porch_wall', 'right_inner_porch_wall_clone', 'right_inner_porch_wall', 'front_inner_porch_wall', 'left_middle_porch_wall', 'front_inner_porch_wall_clone', 'left_front_wall', 'left_front_wall_clone', 'left_back_wall', 'left_back_wall_clone', 'inner_left_wall', 'front_porch_wall_clone', 'front_porch_wall', 'diagonal_right_wall', 'inner_front_wall', 'inner_right_wall', 'diagonal_right_wall_clone', 'inner_right_wall_clone', 'roof_metal_cover_obj', 'roof_base', 'top_left_wall', 'left_extra_wall', 'top_right_wall', 'roof_base_obj', 'roof_permatile_lean_to_metal_clone_obj', 'roof_permatile_lean_to_metal_cover_obj', 'roof_standing_seam_lean_to_metal_cover_obj', 'roof_permatile_aframe_metal_clone_obj', 'roof_permatile_aframe_metal_cover_obj', 'roof_standing_seam_metal_aframe_clone_obj', 'roof_standing_seam_metal_aframe_cover_obj', 'front_porch_after_wall', 'front_porch_after_wall_clone', 'roof_standing_seam_lean_to_metal_clone_obj', 'frame_component_obj', 'inner_back_wall_clone', 'inner_back_wall', 'siding_p_obj', 'left_extra_floor_wall' ];
        const material  = [ 'trim_mat', 'frame_porch_mat', 'siding_extra_mat', 'trim_metal_mat', this._horizontalTrimMat ];
        
        meshes.forEach( name => {
            const mesh = _this.getMeshByName( name );
            
            if( mesh ) {
                mesh.visibility     = visibility;
                const childMeshes   = mesh.getChildMeshes();
                
                childMeshes.forEach( m => {
                    m.visibility = visibility;
                } );
            }
        });

        _this.blurMeshes();

        _this._scene.meshes.forEach( ( sceneMesh: Mesh ) => {
            if( undefined === sceneMesh.material || null === sceneMesh.material ) {
                return;
            }

            let materialMesh = sceneMesh.material as StandardMaterial;

            if( -1 !== material.indexOf( materialMesh.name ) ) {
                materialMesh.alpha = alpha;
            }

            if( undefined !== materialMesh['subMaterials'] ) {
                let subMaterials = materialMesh['subMaterials'];

                subMaterials.forEach( subMaterial => {
                    if( -1 !== material.indexOf( subMaterial.name ) ) {
                        subMaterial.alpha = alpha;
                    }
                })
            }
        })

        if( true !== _this._isInterior ){
            const meshName      = 'loft_obj';
            const loftMesh      = _this.getMeshByName( meshName );
            
            if( loftMesh ) {
                loftMesh.setEnabled( false );
            }
            
            loftSides.forEach( side => {
                const mesh = _this.getMeshByName( `${meshName}_${side}` );
                if( mesh ) mesh.setEnabled( false );
            } );
        }
        
        const frameComponentMeshes = this.filterMeshes( _this._scene.meshes, 'name', constants.frameComponent );
        frameComponentMeshes.forEach( m => m.setEnabled( _this._isInterior ) );
    }

    /**
     * Handle hide Inner Obj when enable siding interior
     */
    handleHideInnerObject = () => {
        if( ! this._isDrawInteriorSiding ) return;
        
        const mesh = this.getMeshByName( 'inner_obj' );
        if( ! mesh ) return;
        mesh.setEnabled( false );
    }

    /**
     * Disable pickable mesh for interior
     */
    setPickableStatus = ( meshes: Mesh[], status: boolean ) => {
        if( meshes ) {
            meshes.forEach( mesh => {
                mesh.isPickable = status;
            } );
        }
    }

    /**
     * Handle when loft change
     */
    handleLoft( dataLoft: object, position ) : void {
        const _this     = this;
        const meshName  = 'loft_obj';
        const loftMesh  = _this.getMeshByName( meshName );
        if( ! loftMesh ) {
            return;
        }

        const loftHeight        = false !== position ? position : helpers.toFixed3( Number( this._canvas.getAttribute( 'data-loft-height-from-top' ) ) / 12 );
        let newWidth: number    = _this.getWidth() / 2;
        let newDepth: number    = _this.getDepth() / 2;
        const newHeight: number = _this.getWallHeight() - loftHeight;
        const loftColor         = Color3.FromHexString( '#5e6578' );
        const loftWidth         = _this.getMeshWidth( loftMesh );
        const loftDepth         = _this.getMeshLength( loftMesh );
        const meshColors        = [ 'loft_wood_mat', 'door_trim_inner_mat', 'siding_inner_floor_mat' ];

        loftMesh.setEnabled( false );
        
        meshColors.forEach( key => {
            const materials = _this.getMaterialByName( key );
            if( materials ) {
                materials.forEach( m => {
                    m.diffuseColor  = loftColor;
                    m.emissiveColor = loftColor;
                } );
            }
        } );

        loftSides.forEach( side => {
            const mesh = _this.getMeshByName( `${meshName}_${side}` );
            if( mesh ) mesh.dispose();
        } );

        for( const side in dataLoft ) {
            const width             = dataLoft[side];
            const cloneMeshName     = `${meshName}_${side}`;
            let loftClone           = _this.getMeshByName( cloneMeshName );
            loftClone               = null === loftClone ? loftMesh.clone( cloneMeshName ) : loftClone;
            loftClone.isPickable    = false;
            loftClone.setEnabled( true );

            if( 'front' === side || 'back' === side ) {
                loftClone.scaling.x = newWidth * 2 / loftWidth;
                loftClone.scaling.z = width / loftDepth;
                const loftPositionZ = newDepth - ( width / 2 );
                loftClone.position  = new Vector3( 0, newHeight - 0.001, 'back' === side ? - loftPositionZ : loftPositionZ );
            } else if( 'left' === side || 'right' === side ) {
                loftClone.scaling.x = newDepth * 2 / loftWidth;
                loftClone.scaling.z = width / loftDepth;
                const loftPositionZ = newWidth - ( width / 2 );
                loftClone.position  = new Vector3( 'left' === side ? - loftPositionZ : loftPositionZ, newHeight, 0 );
                loftClone.rotation  = new Vector3( loftMesh.rotation.x, Math.PI / 2, loftMesh.rotation.z );
            }
        }

        if( true !== _this._isInterior ){
            loftSides.forEach( side => {
                const mesh = _this.getMeshByName( `${meshName}_${side}` );
                if( mesh ) mesh.setEnabled( false );
            } );            
        }
    }

    formatLoftSizeSide = dataSizeSide => {
        const sizeSide  = JSON.parse( atob( dataSizeSide ) );
        const dataLoft  = {};
        const width     = this.getWidth();
        const depth     = this.getDepth();

        sizeSide.forEach( data => {
            data.sides.forEach( side => {
                if( ! ( side in dataLoft ) ) {
                    let loftSize = data.size;
                    if( 'percent' === data.type ) {
                        const fullWidth = 'front' === side || 'back' === side ? depth : width;
                        loftSize = fullWidth * loftSize / 100 - data.offset;
                    }
                    dataLoft[side] = Number( loftSize );
                }
            } );
        } );

        return dataLoft;
    }

    async specialModelInjection() {
        let bones = this._bones;
        
        if( undefined !== bones['dogkennel_door_wire'] ) { // Dog Kennel bones
            this.injectDogkennel();
        }
        
        if( undefined !== bones['dogkennel_door_cloned'] ) { // Dog Kennel bones v2
            this.injectDogKennelV2();
        }

        if( undefined !== bones['pergola_cross_beam_cloned_l'] ) { // Pergola bones
            this.injectPergola();
        }
        
        if( undefined !== bones['horse_run_in_frame_u_cloned_t'] ) { // Horse Run-in
            this.injectHorserunin();
        }

        if( 'structure_loafing_shed' === this._shedType ){
            this.injectLoafingShed();
            this.moveBones();
            await this.buildMesh();
        }
    }

    createHoleForLoafing( side ){
        if( 'structure_loafing_shed' !== this._shedType || 'left' !== side ) return;

        const _this     = this;

        const wallName  = `${side}_wall`;
        const oldWall   = this.getMeshByName( wallName );
        if( null === oldWall ) return;

        const wallNameClone = `${wallName}_clone`;
        const meshClone     = this.getMeshByName( wallNameClone );
        if( meshClone ) meshClone.dispose();

        let mainMeshCSG     = CSG.FromMesh( oldWall );
        mainMeshCSG.subtractInPlace( CSG.FromMesh( _this.buildMeshHoleForLoafing() ) );

        let wall = mainMeshCSG.toMesh( wallNameClone, oldWall.material, this._scene, false );
        if( ! helpers.hasKeyInArray( this._meshBlurForInterior, wallNameClone ) ) {
            this._meshBlurForInterior.push( wallNameClone );
        }
        wall.convertToFlatShadedMesh();
        wall.setParent( this.getMeshByName( 'body_obj' ) );
        oldWall.setEnabled( false );        
    }

    handlePorchSizeOption = ( firstLoad = false ) => {
        const data = this.getPorchCustomSizeData();
        if( ! data ) return;

        atticModel.porchWidth       = parseFloat( data.width );
        atticModel.aFramePorchRoof  = helpers.toFixed3( parseFloat( data.roof_pitch ) / 12 );
        atticModel.porchHeight      = data.height ? parseFloat( data.height ) : parseFloat( this.getWallHeight() );

        jQuery( '#ssb-configurator-upgrade-external-porch-EnabledPorch' )
            .attr( 'data-price', data.price )
            .attr( 'data-price-type', data.price_type )
            .attr( 'origin-value', data.custom_name )
            .attr( 'data-option-value', data.custom_name );

        if( ! firstLoad ) {
            this.animateSizeChange( this.getSizeGroup(), this.getSizeGroup(), false );
            this.updateRoofOverhang();
        }
    }

    handleLeanToSizeOption = ( firstLoad = false ) => {
        const data = this.getLeanToData();
        if( ! data ) return;

        atticModel.leanToWidth      = parseFloat( data.width );
        atticModel.aFrameLeanToRoof = helpers.toFixed3( parseFloat( data.roof_pitch ) / 12 );
        atticModel.leanToHeight     = data.height ? parseFloat( data.height ) : parseFloat( this.getWallHeight() );

        jQuery( '#ssb-configurator-upgrade-lean-to-EnabledLeanto' )
            .attr( 'data-price', data.price )
            .attr( 'data-price-type', data.price_type )
            .attr( 'origin-value', data.custom_name )
            .attr( 'data-option-value', data.custom_name );

        if( ! firstLoad ) {
            this.animateSizeChange( this.getSizeGroup(), this.getSizeGroup(), false );
            this.updateRoofOverhang();
        }
    }

    handleLeanToOptionMesh = () => {
        const mesh = this.getMeshByName( 'lean_to_obj' );
        if( ! mesh ) return;

        const status    = this.isEnableLeanTo();
        let isDelete    = false;
        mesh.setEnabled( status );

        this.updateRoofOverhang();

        if( status ) {
            jQuery( '.group-upgrade-lean-to-size-option' ).show();
            const topRightComponents = this.getComponentBySideAndType( 'top_right', 'exterior' );
            
            if( topRightComponents ) {
                for( const key in topRightComponents ) {
                    const component = topRightComponents[key];
                    const positionY = component.position.y - component.componentHeight;
                    
                    if( positionY < this._bones['lean_to_b_r4'].position.y && null === component.parent ) {
                        this.deleteComponentV2( component.side, component.key, component.type );
                        isDelete = true;
                    }
                }
            }

            if( this.deleteAllComponentsBySide( 'right' ) ) {
                isDelete = true;
            }
            
            this.updateHoleWall( 'right' );
        } else {
            jQuery( '.group-upgrade-lean-to-size-option' ).hide();
            const meshHide = [ 'lean_to_right', 'lean_to_front', 'lean_to_back' ];
            for( let i = 0; i < meshHide.length; i++  ) {
                if( this.deleteAllComponentsBySide( meshHide[i] ) ) {
                    isDelete = true;
                }
                this.updateHoleWall( meshHide[i] );
            }
        }

        if( isDelete ) {
            this._isUpdateLayout = true;
        }
    }

    handleDormerOptionMesh = () => {
        if( 'structure_rancher_modular' !== this._shedTypeVersion ) return;
        let roofType        = this._roofType;
        const referenceRoof = jQuery( `[data-roof-type="${roofType}"]` ).attr( 'data-reference-key' );
        if( undefined !== referenceRoof && '' !== referenceRoof ) {
            const dormerRoofMesh = this.getMeshesLikeName( 'dormer', `_${roofType}_obj` );
            if( ! dormerRoofMesh ) {
                roofType = referenceRoof;
            }
        }

        const mesh1 = this.getMeshByName( `roof_${roofType}_obj` );
        const mesh2 = this.getMeshByName( `roof_${roofType}_dormer_left_obj` );
        const mesh3 = this.getMeshByName( `roof_${roofType}_dormer_right_obj` );
        const mesh4 = this.getMeshByName( `gable_3rd_left_obj` );
        const mesh5 = this.getMeshByName( `gable_3rd_right_obj` );

        if( mesh1 ) mesh1.setEnabled( false );
        if( mesh2 ) mesh2.setEnabled( false );
        if( mesh3 ) mesh3.setEnabled( false );
        if( mesh4 ) mesh4.setEnabled( false );
        if( mesh5 ) mesh5.setEnabled( false );

        if( 'dormer_left' === this._dormerOption ) {
            if( mesh2 ) mesh2.setEnabled( true );
            if( mesh4 ) mesh4.setEnabled( true );
        } else if( 'dormer_right' === this._dormerOption ) {
            if( mesh3 ) mesh3.setEnabled( true );
            if( mesh5 ) mesh5.setEnabled( true );
        } else {
            if( mesh1 ) mesh1.setEnabled( true );
        }

        let positionY1 = this.getWallHeight();
        let positionY2 = this.getWallHeight();
        if( 'dormer_left' === this._dormerOption ) {
            positionY1 = this._bones['gable_3rd_right_t'].position.y;
        } 

        if( 'dormer_right' === this._dormerOption ) {
            positionY2 = this._bones['gable_3rd_right_t'].position.y;
        }

        this._bones['dormer_bones_left']   = { position: new Vector3( this._bones['gable_3rd_left_t'].position.x, positionY1, this._bones['gable_3rd_left_t'].position.z ) };
        this._bones['dormer_bones_right']  = { position: new Vector3( this._bones['gable_3rd_right_t'].position.x, positionY2, this._bones['gable_3rd_right_t'].position.z ) };

        this.cloneMetalRoof();
        this.buildMesh();
    }

    buildMeshHoleForLoafing(){
        if( 'structure_loafing_shed' !== this._shedType ) return;
        
        const activeLayout  = jQuery( `#ssb-configurator-layout-option input[name='ssb-configurator-layout']:checked` ).attr( 'data-layout-id' );
        const offsetY       = 1;

        let shape = [
            new Vector3( this._bones['b_f_l1'].position.z, 0, 0 ),
            new Vector3( this._bones['b_f_l2'].position.z, 0, this._bones['b_f_l2'].position.y - offsetY ),
            new Vector3( this._bones['b_b_l2'].position.z, 0, this._bones['b_b_l2'].position.y - offsetY ),
            new Vector3( this._bones['b_b_l1'].position.z, 0, 0 ),
        ];

        if( 'siding_left' === activeLayout ){
            shape = [
                new Vector3( this._bones['b_f_l1'].position.z, 0, 0 ),
                new Vector3( this._bones['b_f_l2'].position.z, 0, this._bones['b_f_l2'].position.y - offsetY ),
                new Vector3( this._bones['b_b_m2'].position.z, 0, this._bones['b_b_m2'].position.y - offsetY ),
                new Vector3( this._bones['b_b_m1'].position.z, 0, 0 ),
            ];
        }else if( 'siding_right' === activeLayout ){
            shape = [
                new Vector3( this._bones['b_b_m1'].position.z, 0, 0 ),
                new Vector3( this._bones['b_b_m2'].position.z, 0, this._bones['b_b_m2'].position.y - offsetY ),
                new Vector3( this._bones['b_b_l2'].position.z, 0, this._bones['b_b_l2'].position.y - offsetY ),
                new Vector3( this._bones['b_b_l1'].position.z, 0, 0 ),
            ];
        }

        let doorLeftMesh   = this.getMeshByName( 'door_left_loafing' );
        if( doorLeftMesh ) doorLeftMesh.dispose();
        doorLeftMesh                   = MeshBuilder.ExtrudePolygon( 'door_left_loafing', { shape, depth: 0.4, sideOrientation: Mesh.DOUBLESIDE }, this._scene );            
        doorLeftMesh.rotation          = this.getWallRotation( 'right' );
        doorLeftMesh.position          = this.getWallPosition( 'right', 'b_f_l1' );
        doorLeftMesh.position._x       = doorLeftMesh.position.x + 0.2;
        doorLeftMesh.visibility        = 0;

        return doorLeftMesh;
    }

    injectLoafingShed(){
        const activeLayout  = this.getActiveLayout();
        const braceObj      = this.getMeshByName( 'post_brace_obj' );
        const coverObj      = this.getMeshByName( 'post_cover_obj' );
        const trim01        = this.getMeshByName( 'vertical_trim_01_obj' );
        const trim02        = this.getMeshByName( 'vertical_trim_02_obj' );

        let mesh            = [ braceObj, coverObj, trim01, trim02 ];
        let activeMesh      = [];
        
        mesh.forEach( ( m ) => {
            if( m ) m.setEnabled( false );
        } )
        
        if( 'no_siding' === activeLayout ){
            activeMesh = [ braceObj, trim01, trim02 ];
        }else if( 'siding_left' === activeLayout ){
            activeMesh = [ coverObj, trim01 ];
        }else if( 'siding_right' === activeLayout ){
            activeMesh = [ coverObj, trim02 ];
        }

        activeMesh.forEach( ( m ) => {
            if( m ) m.setEnabled( true );
        } )
    }

    injectHorserunin() {
        let activeLayout = this.getActiveLayout();

        const meshName      = 'horse_run_in_opening_cloned_obj';
        const openingMesh   = this.getMeshByName( meshName );

        if( openingMesh ) {
            openingMesh.metadata    = { side: 'right' };
            openingMesh.position    = new Vector3( this.getWidth() / 2, 0, 0 );

            const openingMesh1      = this.getMeshByName( `${meshName}_1` );
            const openingMesh2      = this.getMeshByName( `${meshName}_2` );
            if( openingMesh1 ) openingMesh1.dispose();
            if( openingMesh2 ) openingMesh2.dispose();

            if( 'double_opening' === activeLayout ) {
                const openingMesh1      = openingMesh.clone( `${meshName}_1` );

                openingMesh.position    = new Vector3( this.getWidth() / 2, 0, -4 );
                openingMesh1.position   = new Vector3( this.getWidth() / 2, 0, 4 );

            } else if( 'triple_opening' === activeLayout ) {
                const openingMesh1      = openingMesh.clone( `${meshName}_1` );
                const openingMesh2      = openingMesh.clone( `${meshName}_2` );

                openingMesh.position    = new Vector3( this.getWidth() / 2, 0, 0 );
                openingMesh1.position   = new Vector3( this.getWidth() / 2, 0, -8 );
                openingMesh2.position   = new Vector3( this.getWidth() / 2, 0, 8 );

            }
        }
    }

    injectPergola() {
        let activeLayout = this.getActiveLayout();
        let purlinMesh = this._scene.getMeshByName("pergola_purlin_cloned_obj") as Mesh;

        if (purlinMesh != undefined) {
            if (activeLayout == "heavy_beam") {
                purlinMesh.setEnabled(false);
                if (purlinMesh.instances.length > 0 ) {
                    purlinMesh.instances.map(instance => {
                        instance.setEnabled(false);
                    })
                }
            } else {
                purlinMesh.setEnabled(false);
                if (purlinMesh.instances.length > 0 ) {
                    purlinMesh.instances.map(instance => {
                        instance.setEnabled(true);
                    })
                }
            }
        }
    }

    injectDogkennel() {
        let sidingFloorMaterial = this._scene.getMaterialByName( "siding_floor_mat" ) as StandardMaterial;
        if (sidingFloorMaterial != undefined) {
           sidingFloorMaterial.maxSimultaneousLights = 1;
        }

        let activeLayout = this.getActiveLayout();
        let width = this.getWidth();

        if ( this._scene.getMeshByName("runner_h_obj") != undefined ) this._scene.getMeshByName("runner_h_obj").setEnabled(false);
        if ( this._scene.getMeshByName("runner_v_obj") != undefined ) this._scene.getMeshByName("runner_v_obj").setEnabled(false);

        if ( this._scene.getMeshByName("dogkennel_door_wire_4ft_full_set_obj") != undefined ) this._scene.getMeshByName("dogkennel_door_wire_4ft_full_set_obj").setEnabled(false);
        if ( this._scene.getMeshByName("dogkennel_door_wire_6ft_full_set_obj") != undefined ) this._scene.getMeshByName("dogkennel_door_wire_6ft_full_set_obj").setEnabled(false);
        if ( this._scene.getMeshByName("dogkennel_door_wire_6ft_half_set_obj") != undefined ) this._scene.getMeshByName("dogkennel_door_wire_6ft_half_set_obj").setEnabled(false);
        if ( this._scene.getMeshByName("dogkennel_door_wire_8ft_double_set_obj") != undefined ) this._scene.getMeshByName("dogkennel_door_wire_8ft_double_set_obj").setEnabled(false);
        if ( this._scene.getMeshByName("dogkennel_door_wire_8ft_half_set_obj") != undefined ) this._scene.getMeshByName("dogkennel_door_wire_8ft_half_set_obj").setEnabled(false); 

        if (activeLayout == "dogkennel_split") {
            this.cloneDogDoor();

            if ( this._scene.getMeshByName("dogkennel_door_wire_8ft_double_set_obj") != undefined ) this._scene.getMeshByName("dogkennel_door_wire_8ft_double_set_obj").setEnabled(true);

            if ( this._scene.getMeshByName("dogkennel_wire_frame_m_obj") != undefined ) this._scene.getMeshByName("dogkennel_wire_frame_m_obj").setEnabled(true);
            if ( this._scene.getMeshByName("dogkennel_wire_cross_m_cloned_obj") != undefined ) this._scene.getMeshByName("dogkennel_wire_cross_m_cloned_obj").setEnabled(true);
            if ( this._scene.getMeshByName("dogkennel_wire_hook_m_cloned_obj") != undefined ) this._scene.getMeshByName("dogkennel_wire_hook_m_cloned_obj").setEnabled(true);
        } else {
            let dogDoorObj = this._scene.getMeshByName("dogkennel_dog_door_cloned_obj") as Mesh;
            
            if (dogDoorObj != undefined ) {
                if (dogDoorObj.instances.length > 0 ) {
                    dogDoorObj.instances.map(instance => {
                        instance.dispose();
                    })
                    dogDoorObj.setEnabled(true);
                }
            }
            
            if ( this._scene.getMeshByName("dogkennel_wire_frame_m_obj") != undefined ) this._scene.getMeshByName("dogkennel_wire_frame_m_obj").setEnabled(false);
            if ( this._scene.getMeshByName("dogkennel_wire_cross_m_cloned_obj") != undefined ) this._scene.getMeshByName("dogkennel_wire_cross_m_cloned_obj").setEnabled(false);
            if ( this._scene.getMeshByName("dogkennel_wire_hook_m_cloned_obj") != undefined ) this._scene.getMeshByName("dogkennel_wire_hook_m_cloned_obj").setEnabled(false);

            if (width < 6) {
                if ( this._scene.getMeshByName("dogkennel_door_wire_4ft_full_set_obj") != undefined ) this._scene.getMeshByName("dogkennel_door_wire_4ft_full_set_obj").setEnabled(true);
            } else if (width < 8 ) {
                if ( this._scene.getMeshByName("dogkennel_door_wire_6ft_full_set_obj") != undefined ) this._scene.getMeshByName("dogkennel_door_wire_6ft_full_set_obj").setEnabled(true);
            } else {
                if ( this._scene.getMeshByName("dogkennel_door_wire_8ft_half_set_obj") != undefined ) this._scene.getMeshByName("dogkennel_door_wire_8ft_half_set_obj").setEnabled(true); 
            }
        }

        if (width < 6) {
            if ( this._scene.getMeshByName("runner_v_obj") != undefined ) this._scene.getMeshByName("runner_v_obj").setEnabled(true);
        } else if (width < 8 ) {
            if ( this._scene.getMeshByName("runner_h_obj") != undefined ) this._scene.getMeshByName("runner_h_obj").setEnabled(true);
        } else {
            if ( this._scene.getMeshByName("runner_h_obj") != undefined ) this._scene.getMeshByName("runner_h_obj").setEnabled(true);
        }
    }

    injectDogKennelV2() {
        const depth         = this.getDepth();
        const width         = this.getWidth();
        const activeLayout  = this.getActiveLayout();
        const meshName      = 'dogkennel_door_cloned_obj';
        const dogDoorObj    = this._scene.getMeshByName( meshName ) as Mesh;
        const doorSingle    = this._scene.getMeshByName( 'door_wire_8ft_single_obj' );
        const doorDouble    = this._scene.getMeshByName( 'door_wire_8ft_double_obj' );
        const doorLeft      = this._scene.getMeshByName( 'door_wire_8ft_single_left_obj' );
        const doorRight     = this._scene.getMeshByName( 'door_wire_8ft_single_right_obj' );
        const wireFrame     = this._scene.getMeshByName( 'wire_frame_m_obj' );

        if( doorSingle ) doorSingle.setEnabled( false );
        if( doorDouble ) doorDouble.setEnabled( false );
        if( doorLeft ) doorLeft.setEnabled( false );
        if( doorRight ) doorRight.setEnabled( false );
        if( wireFrame ) wireFrame.setEnabled( false );

        this.removeClone( meshName );

        if( 'dogkennel_split' === activeLayout ) {
            if( doorDouble ) doorDouble.setEnabled( true );
            if( wireFrame ) wireFrame.setEnabled( true );

            if ( ! this._scene.getMeshByName( meshName + `_1` ) ) {
                let dogDoorObjCloned = dogDoorObj.createInstance( meshName + `_1` );
                dogDoorObjCloned.position = new Vector3( width / 4, dogDoorObj.position.y, dogDoorObj.position.z );
            }

            if ( ! this._scene.getMeshByName( meshName + `_2` ) ) {
                let dogDoorObjCloned = dogDoorObj.createInstance( meshName + `_2` );
                dogDoorObjCloned.position = new Vector3( - width / 4, dogDoorObj.position.y, dogDoorObj.position.z );
            }

            dogDoorObj.setEnabled( false );
        } else if( 'single_opening_left' === activeLayout ) {
            if( doorLeft ) {
                doorLeft.setEnabled( true );
            } else if( doorSingle ) {
                doorSingle.setEnabled( true );
            }

            if( dogDoorObj ) {
                dogDoorObj.setEnabled( true );
                dogDoorObj.position = new Vector3( width / 4, dogDoorObj.position.y, dogDoorObj.position.z );
            }
        } else if( 'single_opening_right' === activeLayout ) {
            if( doorRight ) {
                doorRight.setEnabled( true );
            } else if( doorSingle ) {
                doorSingle.setEnabled( true );
            }

            if( dogDoorObj ) {
                dogDoorObj.setEnabled( true );
                dogDoorObj.position = new Vector3( - width / 4, dogDoorObj.position.y, dogDoorObj.position.z );
            }
        } else {
            if( doorSingle ) doorSingle.setEnabled( true );

            if( dogDoorObj ) {
                dogDoorObj.setEnabled( true );
                dogDoorObj.position = new Vector3( 0, dogDoorObj.position.y, dogDoorObj.position.z );
            }
        }
    }

    animateTextureChange( materialNames, textureType, newTextureImage, newTextureColor ) {
        if( undefined === materialNames ) {
            return;
        }
        
        let _this           = this;
        let listMaterials   = materialNames.split( '|' );

        _this._scene.meshes.forEach( ( sceneMesh: Mesh ) => {
            if( undefined === sceneMesh.material || null === sceneMesh.material ) {
                return;
            }

            let material = sceneMesh.material as StandardMaterial;
            if( ( 'runner_mat' === material.name || 'siding_floor_mat' === material.name ) && ! _this.isDefaultRunnerColor() ) {
                return; // Don't handle for runner/outer joist color if don't use default color
            }

            const glassMaterials = [ 'window_glass_mat', 'window_recessed_glass_mat', 'door_glass_mat' ];
            if( -1 !== glassMaterials.indexOf( material.name ) ) {
                material.reflectionTexture.level    = 1.5;
                material.diffuseColor               = Color3.FromHexString("#696969");
                material.specularColor              = Color3.FromHexString("#696969");
            }

            if( -1 !== listMaterials.indexOf( material.name ) ) {
                _this.updateTexture( material, textureType, newTextureImage, newTextureColor );
            }

            if( undefined !== material['subMaterials'] ) {
                let subMaterials = material['subMaterials'];
                subMaterials.forEach( subMaterial => {
                    if( -1 !== listMaterials.indexOf( subMaterial.name ) ) {
                        if( ( 'runner_mat' === subMaterial.name || 'siding_floor_mat' === subMaterial.name ) && ! _this.isDefaultRunnerColor() ) {
                            return; // Don't handle for runner/outer joist color if don't use default color
                        }
                        _this.updateTexture( subMaterial, textureType, newTextureImage, newTextureColor );
                    }
                });
            }
        });
    }

    private updateTexture(material, textureType, newTextureImage, newTextureColor) {
        if( material.metadata && material.metadata.hasColor ) {
            return;
        }

        let initMaterials = this._initialUVs;
        
        if( material != undefined && material != null && undefined !== initMaterials[material.name] ) {
            let initDiffuseTexture = initMaterials[material.name]['diffuseTexture'] ? initMaterials[material.name]['diffuseTexture']['texture'] : null;
            let initBumpTexture = initMaterials[material.name]['bumpTexture'] ? initMaterials[material.name]['bumpTexture']['texture'] : '';

            let diffuseTextureUscale = material.diffuseTexture ? material.diffuseTexture['uScale'] : 1;
            let diffuseTextureVscale = material.diffuseTexture ? material.diffuseTexture['vScale'] : 1;
            
            let bumpTextureUscale = material.bumpTexture ? material.bumpTexture['uScale'] : 1;
            let bumpTextureVscale = material.bumpTexture ? material.bumpTexture['vScale'] : 1;

            let currentSpecularTexture = "";
            let specularTextureUscale = ""
            let specularTextureVscale = "";
            if (material['specularTexture'] != undefined && material['specularTexture'] != null) {
                currentSpecularTexture = material['specularTexture']['url'];
                specularTextureUscale = material.specularTexture['uScale'];
                specularTextureVscale = material.specularTexture['vScale'];
            }

            const customSheds = [ 'horse_run_in', 'greenhouse', 'structure_barn_with_sprocket', 'structure_barn_with_sprocket_wood_siding' ];
            if( 'siding_fb_mat' === material.name && -1 !== customSheds.indexOf( this._shedType ) ) { // Quick fix lp_smart wrong scale UVs
                const sidingFBMat = this._scene.getMaterialByName( 'siding_fb_mat' ) as StandardMaterial;

                diffuseTextureUscale = sidingFBMat.diffuseTexture['uScale'];
                diffuseTextureVscale = sidingFBMat.diffuseTexture['vScale'];

                bumpTextureUscale = sidingFBMat.bumpTexture['uScale'];
                bumpTextureVscale = sidingFBMat.bumpTexture['vScale'];

                specularTextureUscale = sidingFBMat.specularTexture['uScale'];
                specularTextureVscale = sidingFBMat.specularTexture['vScale'];
            }

            if( 'metal_lap' === this.getRoofType() && 'roof_metal_mat' === material.name ){
                initDiffuseTexture = this._currentSidingDiffuseTexture;
                initBumpTexture = this._currentSidingBumpTexture;
                currentSpecularTexture = this._currentSpecularTexture;
            }

            if( 'flat_dormer_bottom_mat' === material.name || 'flat_dormer_top_mat' === material.name || 'flat_dormer_bottom_fix_mat' === material.name ) {
                initDiffuseTexture      = this._currentSidingDiffuseTexture;
                initBumpTexture         = this._currentSidingBumpTexture;
                currentSpecularTexture  = this._currentSpecularTexture;
            }

            let roofMetalLapMaterials = this.getMaterialByName( 'roof_metal_mat' );
            if ( 'metal' === this.getRoofType() && roofMetalLapMaterials && 'flat' !== this.getRoofShape() ) {
                let roofDegree = this.getRoofDegree();

                roofMetalLapMaterials.forEach( roofMetalLapMaterial => {
                    if( roofDegree < 14 ){
                        roofMetalLapMaterial.ambientTexture = null;
                    }
                } )
            }
            
            switch (textureType) {
                case "image":
                    material.diffuseColor = new Color3(1 ,1, 1);
                    material.diffuseTexture = new Texture(newTextureImage + "?time=" + new Date().getTime(), this._scene);
                    material.diffuseTexture['uScale'] = diffuseTextureUscale;
                    material.diffuseTexture['vScale'] = diffuseTextureVscale;
                    break;
                case "color":
                case "color_picker":
                    const matKeys   = this.getMatKeys();
                    if (( material.name.startsWith('siding') || -1 !== matKeys.indexOf( material.name ) ) &&  (material.name != "siding_floor_mat" && material.name != "siding_trim_mat" && material.name != "siding_metal_mat" )) {
                        initDiffuseTexture = this._currentSidingDiffuseTexture;
                        initBumpTexture = this._currentSidingBumpTexture;
                    }

                    if( initDiffuseTexture ){
                        if( 'structure_trading_post' === this._shedType && 'trim_mat' === material.name ){
                            initDiffuseTexture = initMaterials[material.name]['diffuseTexture']['texture'];
                        }

                        material.diffuseTexture = new Texture(initDiffuseTexture, this._scene);
                        material.diffuseTexture['uScale'] = diffuseTextureUscale;
                        material.diffuseTexture['vScale'] = diffuseTextureVscale;
                    } else if( -1 !== this._nullDiffuseTextures.indexOf( material.name ) ) {
                        material.diffuseTexture = null;
                    }
                    material.diffuseColor = Color3.FromHexString(newTextureColor);

                    if( initBumpTexture ){
                        material.bumpTexture = new Texture(initBumpTexture, this._scene);
                        material.bumpTexture['uScale'] = bumpTextureUscale;
                        material.bumpTexture['vScale'] = bumpTextureVscale;
                    }

                    const sheds = [ 'barn_without_fe_overhangs', 'classic_cottage_with_metal_trim', 'standard_cottage', 'lean_to' ];
                    if( -1 === sheds.indexOf( this._shedType ) ) {
                        if( currentSpecularTexture ){
                            material.specularTexture = new Texture(currentSpecularTexture, this._scene);
                            material.specularTexture['uScale'] = specularTextureUscale;
                            material.specularTexture['vScale'] = specularTextureVscale;
                        }
                    }
                    break;
                default:
                    break;
            }
        }
    }

    animateLayoutChange = async ( newLayoutId, components ) => {
        if( newLayoutId != this.getActiveLayout() ) {
            this.setActiveLayout( newLayoutId );
            this.deleteAllComponents();
            this.specialModelInjection();

            if (components != undefined && components != "") {
                let layoutComponents = JSON.parse( components );
                if( this._isLayoutBlock && ! helpers.isEmptyObject( layoutComponents ) ) {
                    Object.keys( layoutComponents ).forEach( key => {
                        let components = layoutComponents[key];
                        components.map( item => {
                            item.is_block = ! helpers.hasKeyInArray( interiors, item.type );
                        } );
                        layoutComponents[key] = components;
                    })
                }
                this.setLayoutComponents( layoutComponents );
                await this.loadAllDefaultComponents();
            }
        }
    }

    getSideHasDoor = () => {
        const _this             = this;
        const allDoorComponent  = Object.keys( _this._allComponents ).map( key => _this._allComponents[key] ).filter( item => 'door' === item.type );
        if( 0 === allDoorComponent.length ) return [];
        return allDoorComponent.map( item => item.side );
    }

    getSideAllowAddSwitchAndOutlet = ( componentName, index = 0 ) => {
        const sideHasDoor = this.getSideHasDoor();
        if( helpers.isEmptyObject( sideHasDoor ) || index === sideHasDoor.length ) return false;
        if( ! this.isAllowAddComponent( sideHasDoor[index], componentName ) ) {
            this.getSideAllowAddSwitchAndOutlet( componentName, ++index );
        }

        return sideHasDoor[index];
    }

    animateElectricalPackageChange = async ( newPackageID, components ) => {
        if( this._electricalPackageID === newPackageID ){
            return
        }
        
        this._electricalPackageID = newPackageID;
        this.deleteElectricalPackageComponent();
        this._defaultEPackageComponents     = [];
        this._defaultAllComponentInPackage  = [];

        if( components != undefined && components != "" ) {
            const tmpComponents = this.updatePackageComponents( JSON.parse( components ) );
            for( const component of tmpComponents ) {
                const { type, component: name } = component;
                jQuery( `.group-upgrade-${type}` ).show();
                if( this.isHandleOffsetFloor( type ) ) {
                    component.vertical_measured_from    = 'ground';
                    component.vertical_distance         = this.getPositionYForInterior( type, name );
                }                

                this._defaultAllComponentInPackage.push( component );

                if( ! this.isAllowAddComponent( component.side, component.component ) ) {
                    continue;
                }

                if( this.isValidComponentBySizeCondition( component ) ) {
                    this._defaultEPackageComponents.push( component );
                    this.addNewLayoutComponent( component, 0, 0 );
                }
            }
        }
    }

    animateRoofTypeChange(roofType) {
        this.setRoofType(roofType);
        this.initRoof();
        this.updateRoofComponentsPosition();
        this.drawRoof();
        this.cloneMetalRoof();
        this.cloneMetalHorizontalRoof();
        this.clonePorchMetalRoof();
        this.updateTextureFileRoof();
    }

    animateMaterialInteriorChange( data ) {
        let { material, materialType, diffuseTexture, bumpTexture, specularTexture, specularPower, color, uScale, vScale } = data;
        if( ! color && 'interior_wall' === material ) {
            color = this.getSidingColor( 'wall' );
        } else if( ! color && 'interior_ceiling' === material ) {
            color = this.getSidingColor( 'ceiling' )
        }

        let colorCode = color.code;
        if( 'image' === color.type ) {
            colorCode = '#FFFFFF';
        }

        this.updateWallMaterial( this._apiData[ `${material}_material` ], diffuseTexture, bumpTexture, specularTexture, colorCode, specularPower );
        this.sidingAdjustUVs( uScale, vScale );
        if( 'interior_wall' !== material ) {
            this.setVisibleMaterialMesh( this._apiData[ `${material}_material` ], this.isEnableMaterialInteriorRoofWall() );
        }
    }


    /**
     * Check option material insulation and finishing to hide material mesh roof
     * @returns 
     */
    isEnableMaterialInteriorRoofWall = () => {

        const elementMaterialInteriorCeiling = jQuery( '#interior_ceiling-material-option li.active' );
        const elementMaterialInsulationRoof = jQuery( '#insulation_roof-material-option li.active' );

        if( 0 === elementMaterialInteriorCeiling.length && 0 === elementMaterialInsulationRoof.length ) return false;

        if( 0 === elementMaterialInteriorCeiling.length && 'none' === elementMaterialInsulationRoof.data( 'material-type' ) ) return false;

        if( 0 === elementMaterialInsulationRoof.length && 'none' === elementMaterialInteriorCeiling.data( 'material-type' ) ) return false;

        if( 'none' === elementMaterialInteriorCeiling.data( 'material-type' ) && 'none' === elementMaterialInsulationRoof.data( 'material-type' ) ) return false;
        
        return true;

    }

    setVisibleMaterialMesh = ( matKeys, isEnable ) => {
        if( ! matKeys ) return;
        matKeys.forEach( matKey => {
            const mats = this.getMaterialByName( matKey );
            if( mats ) {
                mats.forEach( mat => {
                    mat.alpha = isEnable ? 1 : 0;
                } )
            }
        } );
    }

    animateAddonSidingTypeChange = ( key, diffuseTexture, bumpTexture, specularTexture, specularPower ) => {
        const colorInfo = this.getSidingColor( key );
        let color       = colorInfo.code;
        let diffuse     = diffuseTexture;

        if( 'image' === colorInfo.type ) {
            diffuse  = colorInfo.image;
            color    = '#FFFFFF';
        }

        this.updateWallMaterial( this._apiData[ `${key}_material` ], diffuse, bumpTexture, specularTexture, color, specularPower );
        this.sidingAdjustUVs();
    }

    animateSidingTypeChange = ( sidingType, sidingColor, diffuseTexture, bumpTexture, specularTexture, specularPower ) => {
        return new Promise( resolve => {
            if( undefined === sidingColor ) {
                return resolve( true );
            }

            this._currentSidingDiffuseTexture   = diffuseTexture;
            this._currentSidingBumpTexture      = bumpTexture;
            this._currentSpecularTexture        = specularTexture;

            const sidingColorInfo = this.getSidingColor( 'siding' );
            if( 'image' === sidingColorInfo.type ) {
                diffuseTexture  = sidingColorInfo.image;
                sidingColor     = '#FFFFFF';
            }

            const sidingMatKeys = this.getMatKeys( true ).filter( key => 
                ! this._apiData['wainscoting_material'].includes( key ) && 
                ! this._apiData['accent_gable_material'].includes( key ) &&
                ! this._apiData['exterior_siding_upgrades_material'].includes( key )
            );

            this.updateWallMaterial( sidingMatKeys, diffuseTexture, bumpTexture, specularTexture, sidingColor, specularPower );
            this.updateLeftWallMaterial( diffuseTexture, bumpTexture, specularTexture, sidingColor, specularPower );
            this.hideRoofTrim( this._shedType, sidingType );
            this.hideHorizontalTrim( this._shedType, sidingType );
            this.showHorizontalTrims();
            
            let diffuseColor                    = Color3.FromHexString( sidingColor );
            let frameMaterial                   = this._scene.getMaterialByName( 'frame_mat' ) as StandardMaterial;
            let sidingFloorMaterial             = this._scene.getMaterialByName( 'siding_floor_mat' ) as StandardMaterial;
            const texture                       = { diffuseTexture, bumpTexture, specularTexture }

            let materials = [ 
                'siding_fb_mat',
                'siding_lr_mat', 
                'siding_roof_mat', 
                'siding_roof_top_mat', 
                'siding_roof_mid_mat', 
                'siding_extra_mat', 
                'siding_dormer_mat', 
                'siding_dormer_roof_top_mat',
                'siding_dormer_l_mat',
                'siding_dormer_r_mat',
                'siding_porch_lr_mat',
                'siding_porch_fb_mat',
                'siding_porch_roof_top_mat',
                'siding_top_mat',
                'siding_b_top_mat',
                'siding_f_top_mat',
                'siding_b_mat',
                'siding_f_mat',
                'siding_f_01_mat',
                'siding_f_02_mat',
                'siding_f_03_mat',
                'siding_b_roof_top_mat',
                'siding_f_roof_top_mat',
                'siding_p_mat',
                'flat_dormer_top_mat',
                'flat_dormer_bottom_mat',
                'flat_dormer_bottom_fix_mat',
                'siding_fb_01_mat',
                'siding_fb_02_mat',
                'siding_fb_03_mat',
                'siding_fb_04_mat',
                'siding_roof_top_01_mat',
                'siding_roof_top_02_mat',
                'siding_porch_r_mat',
                'siding_porch_l_mat',
                'siding_l_mat',
                'siding_r_mat',
                'siding_fix_mat',
                'siding_roof_top',
                'siding_lr_a_mat',
                'siding_lr_b_mat',
                'siding_c_mat',
                'bay_window_siding_mat',
            ];
            
            if( frameMaterial != null ) {
                frameMaterial.diffuseColor = diffuseColor;
            }

            if( sidingFloorMaterial != null ) {
                if( ( ! this.isEnableOuterJoist() || ! this.getRunnerColor() ) && 'structure_aframe_metal_frontporch' !== this._shedType && 'structure_barn_smithbilt' !== this._shedType && 'structure_monoslope_smithbilt' !== this._shedType ) {
                    sidingFloorMaterial.diffuseColor = diffuseColor;
                }
            }

            materials.forEach( key => {
                const material = this.getMaterialByName( key );

                if( material ){
                    material.forEach( m => {
                        this.updateMaterial( diffuseColor, m, texture, specularPower );
                    })
                }
            } )
            
            this.updateIncludeComponents();
            this.setDefaultOverhangData();
            this.updateRoofOverhang();
            this.updateWindowsPosition();
            this.updateLoafingInteriorColor();
            this.removeLine();
            this.removeNoticeText();
            this.adjustUVs( false );

            resolve(true);
        });
    }

    /*
     * Change material of floor using given material data object
    */
    animateFloorTypeChange = ( { floorType, diffuseTexture, bumpTexture, specularTexture, uScale, vScale, specularPower } ) => {
        return new Promise( resolve => {
            let texture = this.getFloorTextureFile( floorType );

            if( 'default' === floorType ) {
                texture = { diffuseTexture, bumpTexture, specularTexture }
            }

            let materials = [
                'interior_floor_mat',
            ];
            
            materials.forEach( key => {
                const material = this.getMaterialByName( key );

                if( material ) {
                    material.forEach( mat => {
                        mat.diffuseTexture            = new Texture( texture.diffuseTexture, this._scene );
                        mat.diffuseTexture['uScale']  = uScale ?? 1;
                        mat.diffuseTexture['vScale']  = vScale ?? 1;

                        mat.bumpTexture               = new Texture( texture.bumpTexture, this._scene );
                        mat.bumpTexture['uScale']     = uScale ?? 1;
                        mat.bumpTexture['vScale']     = vScale ?? 1;

                        mat.specularTexture           = new Texture( texture.specularTexture, this._scene );
                        mat.specularTexture['uScale'] = uScale ?? 1;
                        mat.specularTexture['vScale'] = vScale ?? 1;

                        mat.specularPower             = specularPower ?? 10;

                    } );
                }
            } );  
            
            this.adjustUVs( false );

            resolve(true);
        });
    }


    /**
     * Update Material when siding type change
     * @param Color3 diffuseColor
     * @param StandardMaterial material
     * @param array texture
     */
    private updateMaterial( diffuseColor: Color3, material: StandardMaterial, texture, specularPower ) {
        const { diffuseTexture, bumpTexture, specularTexture } = texture;
        let uScale = material.diffuseTexture['uScale'];
        let vScale = material.diffuseTexture['vScale'];

        material.diffuseColor = diffuseColor;
        
        material.diffuseTexture = new Texture( diffuseTexture, this._scene );
        material.diffuseTexture['uScale'] = uScale;
        material.diffuseTexture['vScale'] = vScale;

        material.bumpTexture = new Texture(bumpTexture, this._scene);
        material.bumpTexture['uScale'] = uScale;
        material.bumpTexture['vScale'] = vScale;

        material.specularTexture = new Texture(specularTexture, this._scene);
        material.specularTexture['uScale'] = uScale;
        material.specularTexture['vScale'] = vScale; 
        if( -1 !== specularPower ) {
            material.specularPower = specularPower;
        }
    }

    private handleFullScreen = ( canvasId ) => {
        if( ! helpers.isDemoSite() ) return;

        const canvasParent  = document.getElementById( canvasId );
        const hudLayer      = document.createElement( 'div' );

        canvasParent.appendChild( hudLayer );

        hudLayer.style.position         = 'absolute';
        hudLayer.style.left             = '0';
        hudLayer.style.top              = '0';
        hudLayer.style.bottom           = '0';
        hudLayer.style.right            = '0';
        hudLayer.style.pointerEvents    = 'none';

        const enlargeButton = document.getElementById( 'fullscreen' );

        document.addEventListener( 'fullscreenchange', function() {
            if( document.fullscreenElement ) {
                canvasParent.classList.add( 'is-fullscreen' );
            } else {
                canvasParent.classList.remove( 'is-fullscreen' );
            }
        } );

        enlargeButton.onclick = function( e ) {
            canvasParent.requestFullscreen();
        }
    }

    private async initScene() {
        this._scene                     = new Scene( this._engine );
        this._scene.clearColor          = Color4.FromHexString( '#FFFFFF' );
        this._scene.ambientColor        = Color3.FromHexString( '#000000' );
        this._scene.collisionsEnabled   = true;
        this._highlightMat              = new StandardMaterial( 'highlightMat', this._scene );
        this._highlightMat.diffuseColor = Color3.FromHexString( '#ed2828' );

        this.initCamera();
        this.initCollisionBox();
        this.initLight();
        new SeadevAR( this );

        window.addEventListener( 'click', e => {   
            if( ! document.getElementById( 'ssb-canvas' )?.contains( e.target as Node ) && ! document.getElementById( 'js-component-rotate-button' )?.contains( e.target as Node ) ) {
                this.unPickComponent();
            }
        });

        if( this.isMobile() ) {
            this._scene.onPointerDown = ( evt, pickInfo ) => {
                this.handlePointerDown( pickInfo );
            }

            this._scene.onPointerPick = ( evt, pickInfo ) => {
                this.pickComponent( pickInfo );
            }

            this._scene.onPointerUp = ( evt, pickInfo ) => {
                this.removeLine();
                this.removeNoticeText();
            }
        } else {
            this._scene.onPointerDown = ( evt, pickInfo ) => {
                if( null === pickInfo.pickedMesh || null === pickInfo.pickedMesh.parent || null === pickInfo.pickedMesh.parent.metadata ) {
                    this._allowDrag     = false;
                    this._activeDrag    = '';
                    this.unPickComponent();
                } else {
                    const { type, key, side } = pickInfo.pickedMesh.parent.metadata;
                    if( key !== this._componentPicked ) {
                        this.unPickComponent();
                    }
                    if( 'front' === pickInfo.pickedMesh.name || 'cupola' === type || 'dormer' === type || 'light' === type || 'floor' === side ) {
                        this._allowDrag     = true;
                        this._activeDrag    = key;
                    } else {
                        this._allowDrag     = false;
                        this._activeDrag    = '';
                    }
                    if( this.disableMoveItem( key ) ) {
                        this.createAlert( NOTICES.tackdoorCantMove, 2000 );
                    }

                    if( this.isItemBlock( key ) ) {
                        this.createAlert( NOTICES.itemNotChange, 2000 );
                    }
                }
            }

            this._scene.onPointerUp = ( evt, pickInfo ) => {
                if( null === pickInfo.pickedMesh || 'front' !== pickInfo.pickedMesh.name || null === pickInfo.pickedMesh.parent || null === pickInfo.pickedMesh.parent.metadata ) {
                    this.removeLine();
                    this.removeNoticeText();
                }
            }

            this._scene.onPointerPick = ( evt, pickInfo ) => {
                this.handlePickMesh( pickInfo );
            }
        }

        if( this.isDebugMode() ){
            this._scene.debugLayer.show();
        }
    
    }

    private optimizeMaterials() {
        return new Promise( resolve => {
            this.limitLightSource();
            this.adjustReflection();

            resolve( true );
        });
        
    }
    
    private limitLightSource() {
        let materialLightLimits = [
            'floor_mat',
            'floor_border_mat'
        ];

        if( 'structure_aframe_streamline' === this._shedType || 'structure_aframe_the_eave' === this._shedType ){
            materialLightLimits = materialLightLimits.concat( [ 'roof_metal_mat', 'trim_mat', 'trim_top_mat' ] );
        }

        if( 'structure_greenhouse_polycarbon' === this._shedType ){
            materialLightLimits = materialLightLimits.concat( [ 'siding_fb_mat', 'siding_lr_mat', 'siding_roof_top_mat', 'trim_inner_metal_mat', 'roof_polycarbon_mat' ] );
            const backFaceMats = [ 'siding_roof_top_mat', 'siding_fb_mat' ];
            backFaceMats.forEach( key => {
                let materials = this.getMaterialByName( key );

                if( materials ){
                    materials.forEach( m => {
                        m.backFaceCulling = 1;
                    })
                }
            });

            let groundMats = this.getMaterialByName( 'ground_mat' );
            if( groundMats ){
                groundMats.forEach( mat => {
                    mat.alpha = 0;
                });
            }
        }

        if( materialLightLimits ){
            materialLightLimits.forEach( key => {
                let materials = this.getMaterialByName( key );

                if( materials ){
                    materials.forEach( m => {
                        m.maxSimultaneousLights = 1;
                    })
                }
            });            
        }
    }

    private adjustReflection() {
        const wGlassMats = [ 'window_glass_mat', 'window_recessed_glass_mat', 'door_glass_mat' ];
        wGlassMats.forEach( key => {
            const wRecessedGlassMats = this.getMaterialByName( key );
            if( wRecessedGlassMats ){
                wRecessedGlassMats.forEach( mat => {
                    mat.reflectionTexture.level = 1.5;
                    mat.diffuseColor = Color3.FromHexString( '#000000' );
                    mat.specularColor = Color3.FromHexString( '#000000' );
                } );
            }
        } );

    }

    private initCamera() : void {
        this._camera = new ArcRotateCamera("camera", 0, 0, 0, new Vector3(0,0,0), this._scene);
        this._camera.setPosition(new Vector3(-12, Math.PI/2, 18));
        this._camera.setTarget(new Vector3(0,5,0));
        this._camera.attachControl(this._canvas, true);
        this._camera.checkCollisions = true;
        this._camera.inertia = 0.5;
        this._camera.speed = 5;
        this._camera.minZ = 0.1;
        this._camera.maxZ = 99999;
        this._camera.mode = 0;
        this._camera.fov = 45;
        this._camera.upperBetaLimit = Math.PI / 2;
        this._camera.upperRadiusLimit = 200;
        
        if( -1 !== navigator.userAgent.indexOf('Firefox') ) {
            this._camera.inputs.addMouseWheel();
            this._camera.wheelPrecision = 500;
        } else {
            this._camera.pinchDeltaPercentage = 0.003;
            this._camera.wheelDeltaPercentage = 0.01;
        }
    }

    private initCollisionBox() {
        this._collisionBox = MeshBuilder.CreateBox("collisionBox", {
            height: 1,
            width: 1,
            depth: 1
        }, this._scene);
        
        this._collisionBox.position = new Vector3(0,0,0);
        this._collisionBox.checkCollisions = true;
        this._collisionBox.isVisible = false;
        this._scene.collisionsEnabled = true;
    }

    private resizeCollisionBox() {
        let height = this.getBuildingHeight();
        let width  = this.getWidth();
        let depth  = this.getDepth();

        if( this.isFrontPorch() ){
            depth += this._porchWidth;
        }else if( this.isSidePorch() ){
            width += this._porchWidth;
        }

        this._collisionBox['scaling']['x'] = width * 1.2;
        this._collisionBox['scaling']['y'] = height * 1.2;
        this._collisionBox['scaling']['z'] = depth * 1.2;
        this._collisionBox.position = new Vector3(0, height/2, 0);
    }

    private initLight() : void {

        const hemisphericLight           = new HemisphericLight( 'light', new Vector3( 0, 1, 0 ), this._scene );
        hemisphericLight.intensity       = 0.9;
        hemisphericLight.groundColor     = Color3.FromHexString( "#F0F0F0" );

        const hemisphericLightBottom           = new HemisphericLight( 'lightBottom', new Vector3( 0, -1, 0 ), this._scene );
        hemisphericLightBottom.intensity       = 0.1;
        hemisphericLightBottom.groundColor     = Color3.FromHexString( "#F0F0F0" );
    
    }
    
    private joint(e) {
        return e.bones.reduce(function(e, t) {
            return e[t.name.toLowerCase()] = t,
            e
        }, {})
    }

    private convertSkeletonsToObject(skeletons) {
        let _this = this;
        let joints = [];

        for (let index = 0; index < skeletons.length; index++) {
            joints.push(_this.joint(skeletons[index]));
        }

        for (let index = 0; index < joints.length; index++) {
            for (const key in joints[index]) {
                _this._bones[key] = joints[index][key];
            }
        }
    }

    private importSkeletons(skeletons) {
        let _this = this;
        skeletons.forEach(skeleton => {
            _this._skeletons.push(skeleton);
        });
        _this.convertSkeletonsToObject(_this._skeletons);
    }

    private swapMaterials(material: StandardMaterial) {
        if (material.emissiveTexture != null && material.emissiveTexture != undefined) {    
            material.ambientTexture = material.emissiveTexture;
            material.emissiveTexture = null;
        }
    }

    private setInitialUVs(material: StandardMaterial) {
        let uvObj: object = {};
        if (material.bumpTexture != undefined && material.bumpTexture != null) {
            let uScale = material.bumpTexture['uScale'];
            let vScale = material.bumpTexture['vScale'];
            let texture = material.bumpTexture['url'];
            uvObj["bumpTexture"] = {"uScale": uScale, "vScale": vScale, "texture": texture, "color": ""};
        }
        if (material.diffuseTexture != undefined && material.diffuseTexture != null) {
            let uScale = material.diffuseTexture['uScale'];
            let vScale = material.diffuseTexture['vScale'];
            let texture = material.diffuseTexture['url'];
            let color = material.diffuseColor;
            uvObj["diffuseTexture"] = {"uScale": uScale, "vScale": vScale, "texture": texture, "color": color};
        }
        if (material.specularTexture != undefined && material.specularTexture != null) {
            let uScale = material.specularTexture['uScale'];
            let vScale = material.specularTexture['vScale'];
            let texture = material.specularTexture['url'];
            let color = material.specularColor;
            uvObj["specularTexture"] = {"uScale": uScale, "vScale": vScale, "texture": texture, "color": color};
        }
        this._initialUVs[material.name] = uvObj;
    }

    /**
     * Handle for light seting
     */
    private setSpecularPower( material: StandardMaterial ){
        const metalMats = [ 'metal', 'slide', 'lock', 'chrom', 'vent_standard_mat', '_soil', 'plant', 'glass', 'polycarbon' ];
       
        material.specularPower = 3;
        
        metalMats.forEach( k => {
            if( -1 !== material.name.indexOf( k ) ) {
                material.specularPower = 30;
            }
        });
    }

    private importMaterials(meshes) {
        let _this = this;
        
        meshes.forEach( (mesh: Mesh) => {
            let material = mesh.material as StandardMaterial;
            if (material != null) {
                _this.swapMaterials(material);
                _this.setInitialUVs(material);
                _this.setSpecularPower(material); 
                _this.updateMaterialModel();
                _this.updateMaterialComponent();
                _this._materials[material.name] = material;
                
                if (material['subMaterials'] != undefined) {
                    let subMaterials = material['subMaterials'];
                    subMaterials.forEach(subMaterial => {
                        _this.swapMaterials(subMaterial);
                        _this.setInitialUVs(subMaterial);
                        _this.setSpecularPower(subMaterial);
                        _this.updateMaterialModel();
                        _this.updateMaterialComponent();
                        _this._materials[subMaterial.name] = subMaterial;
                    });
                }
            }
        });
    }

    private setPorchBalusterOffset( bones ){
        if( this.isSidePorch() || this.isMiddlePorch() ) return;

        let _this = this;

        if( bones['b_b_l1'] ){
            if( bones['baluster_b_l1'] ){
                _this._balusterOffsetLeft = Math.abs( bones['b_b_l1'].position.x ) - Math.abs( bones['baluster_b_l1'].position.x );
            }else if( bones['baluster_f_l1'] ){
                _this._balusterOffsetLeft = Math.abs( bones['b_b_l1'].position.x ) - Math.abs( bones['baluster_f_l1'].position.x );
            }
        }

        if( this.isExternalPorch() && bones['baluster_b_l1'] && bones['porch_f_r1'] ) {
            _this._balusterOffsetLeft = Math.abs( bones['porch_f_r1'].position.x ) - Math.abs( bones['baluster_b_l1'].position.x );
        }

        if( bones['b_f_l1'] && this.isCornerPorchLeft() ){
            if( bones['baluster_f_l1'] ){
                _this._balusterOffsetLeft = Math.abs( bones['b_f_l1'].position.z ) - Math.abs( bones['baluster_f_l1'].position.z );
            }
        }

        const railHLObj     = _this._scene.getMeshByName( 'rail_h_l_obj' ) as Mesh;
        if( railHLObj && bones['b_p_l1'] ){
            let objDepth = +( railHLObj.geometry.extend.maximum.z - railHLObj.geometry.extend.minimum.z ).toFixed( 3 );
            _this._balusterOffsetFront = bones['b_p_l1'].position.z - railHLObj.geometry.extend.maximum.z + objDepth / 2;
        }        
    }

    /**
     * Set enable alwaysSelectAsActiveMesh
     * @returns 
     */
    private enableAlwaysSelectAsActiveMesh = meshes => {
        meshes.map( mesh => {
            mesh.alwaysSelectAsActiveMesh = true;
        } );
    }
    private loadBaseModel() {
        let _this = this;
        const modelUpdatedAt = helpers.convertDateTime( this.getSettingData( 'model_updated_at' ) );
        const version = modelUpdatedAt ? `?ver=${ encodeURIComponent( modelUpdatedAt ) }` : '';

        return new Promise(resolve => {
            let loadModelTask = _this._assetsManager.addMeshTask( 'load_base_model', '', _this._modelDir, `${_this._shedTypeVersion}.babylon${version}` );
            loadModelTask.onSuccess = function (task) {
                _this.importSkeletons(task.loadedSkeletons);
                _this.importMaterials(task.loadedMeshes);
                _this.enableAlwaysSelectAsActiveMesh( task.loadedMeshes );
                let bones = _this._bones;

                if( 'structure_monitorbarns_helmuth' === _this._shedType ) {
                    const lowerOpposite = bones['b_f_r4'].position.y - bones['b_f_r3'].position.y;
                    const lowerAdjacent = bones['b_f_r3'].position.x - bones['b_f_r4'].position.x;
                    const upperOpposite = bones['b_f_t'].position.y - bones['b_f_r5'].position.y;
                    const upperAdjacent = bones['b_f_r5'].position.x - bones['b_f_t'].position.x;
                    const secondFloorHeight = helpers.toFixed3( bones['b_f_r5'].position.y - bones['b_f_r4'].position.y );
                    _this.setGlobalData( constants.initLowerRoofDegrees, helpers.toFixed3( _this.getRoofPitchFromRadian( lowerOpposite, lowerAdjacent ) ) );
                    _this.setGlobalData( constants.initUpperRoofDegrees, helpers.toFixed3( _this.getRoofPitchFromRadian( upperOpposite, upperAdjacent ) ) );
                    _this.setGlobalData( constants.initSecondFloorHeight, secondFloorHeight );
                }

                if( _this.isDoubleGable() ) {
                    _this.setCustomUVScale( 'roof_02_u_scale', helpers.toFixed3( Vector3.Distance( bones['r_m_t2'].position, bones['r_m_t3'].position ) ) );
                    _this.setCustomUVScale( 'celling_porch_u_scale', helpers.toFixed3( Vector3.Distance( bones['b_p_l2'].position, bones['b_p_l4'].position ) ) );
                    _this.setCustomUVScale( 'floor_mat_v_scale', helpers.toFixed3( Vector3.Distance( bones['b_p_l1'].position, bones['b_b_m4'].position ) ) );
                    _this.setGlobalData( 'error_baluster_f_l1_x', helpers.toFixed3( Math.abs( bones['b_p_l1'].position.x ) - Math.abs( bones['baluster_f_l1'].position.x ) ) );
                }

                if( 'structure_lean_to_with_front_porch' === _this._shedType ) {
                    if( bones['b_f_t'] && bones['b_f_p2'] ) {
                        _this.setCustomUVScale( 'siding_top_porch_mat_u_scale', helpers.toFixed3( Vector3.Distance( bones['b_f_t'].position, bones['b_f_p2'].position ) ) );
                    }

                    if( bones['b_f_p1'] && bones['b_f_p2'] ) {
                        _this.setCustomUVScale( 'siding_fb_porch_mat_u_scale', helpers.toFixed3( Vector3.Distance( bones['b_f_p1'].position, bones['b_f_p2'].position ) ) );
                    }

                    if( bones['b_f_t'] && bones['b_b_t'] ) {
                        _this.setCustomUVScale( 'siding_roof_porch_mat_u_scale', helpers.toFixed3( Vector3.Distance( bones['b_f_t'].position, bones['b_b_t'].position ) ) );
                    }

                    if( bones['b_f_t'] && bones['b_f_r2'] ) {
                        _this.setCustomUVScale( 'roof_metal_horizontal_mat_v_scale', helpers.toFixed3( Vector3.Distance( bones['b_f_t'].position, bones['b_f_r2'].position ) ) );
                    }

                    if( bones['b_f_t'] && bones['b_b_t'] && bones['b_f_r2'] ) {
                        _this.setCustomUVScale( 'roof_shingle_mat_v_scale', helpers.toFixed3( Vector3.Distance( bones['b_f_t'].position, bones['b_f_r2'].position ) ) );
                        _this.setCustomUVScale( 'roof_shingle_mat_u_scale', helpers.toFixed3( Vector3.Distance( bones['b_f_t'].position, bones['b_b_t'].position ) ) );
                    }
                }

                if( bones['r_b_t'] && bones['r_f_t'] && bones['r_f_l1'] && bones['r_f_r1'] ) {
                    _this.setCustomUVScale( _this._roofMetalHorizontal01MatKey + '_v_scale', helpers.toFixed3( Vector3.Distance( bones['r_f_t'].position, bones['r_f_l1'].position ) ) );
                    _this.setCustomUVScale( _this._roofMetalHorizontal02MatKey + '_v_scale', helpers.toFixed3( Vector3.Distance( bones['r_f_t'].position, bones['r_f_r1'].position ) ) );

                    _this.setCustomUVScale( _this._roofShingle01MatKey + '_u_scale', helpers.toFixed3( Vector3.Distance( bones['r_f_t'].position, bones['r_b_t'].position ) ) );
                    _this.setCustomUVScale( _this._roofShingle01MatKey + '_v_scale', helpers.toFixed3( Vector3.Distance( bones['r_f_t'].position, bones['r_f_l1'].position ) ) );

                    _this.setCustomUVScale( _this._roofShingle02MatKey + '_u_scale', helpers.toFixed3( Vector3.Distance( bones['r_f_t'].position, bones['r_b_t'].position ) ) );
                    _this.setCustomUVScale( _this._roofShingle02MatKey + '_v_scale', helpers.toFixed3( Vector3.Distance( bones['r_f_t'].position, bones['r_f_r1'].position ) ) );
                }

                if( 'structure_classic_aframe_dormer' === _this._shedType ) {
                    if( bones['r_f_t'] && bones['r_f_t1'] && bones['r_f_l1'] ) {
                        _this.setCustomUVScale( 'roof_01_u_scale', helpers.toFixed3( Vector3.Distance( bones['r_f_t'].position, bones['r_f_t1'].position ) ) );
                        _this.setCustomUVScale( 'roof_01_v_scale', helpers.toFixed3( Vector3.Distance( bones['r_f_t'].position, bones['r_f_l1'].position ) ) );
                    }
                    
                    if( bones['r_f_t1'] && bones['r_b_t1'] && bones['r_f_l1'] ) {
                        _this.setCustomUVScale( 'roof_02_u_scale', helpers.toFixed3( Vector3.Distance( bones['r_f_t1'].position, bones['r_b_t1'].position ) ) );
                        _this.setCustomUVScale( 'roof_02_v_scale', helpers.toFixed3( Vector3.Distance( bones['r_f_t'].position, bones['r_f_l1'].position ) ) );
                    }
                    
                    if( bones['r_b_t'] && bones['r_b_t1'] && bones['r_f_l1'] ) {
                        _this.setCustomUVScale( 'roof_03_u_scale', helpers.toFixed3( Vector3.Distance( bones['r_b_t'].position, bones['r_b_t1'].position ) ) );
                        _this.setCustomUVScale( 'roof_03_v_scale', helpers.toFixed3( Vector3.Distance( bones['r_f_t'].position, bones['r_f_l1'].position ) ) );
                    }
                    
                    if( bones['r_b_t'] && bones['r_f_t'] && bones['r_f_r1'] ) {
                        _this.setCustomUVScale( 'roof_04_u_scale', helpers.toFixed3( Vector3.Distance( bones['r_b_t'].position, bones['r_f_t'].position ) ) );
                        _this.setCustomUVScale( 'roof_04_v_scale', helpers.toFixed3( Vector3.Distance( bones['r_f_t'].position, bones['r_f_r1'].position ) ) );
                    }
                    
                    if( bones['r_d_t1'] && bones['r_d_t2'] && bones['r_d_l1'] ) {
                        _this.setCustomUVScale( 'roof_05_u_scale', helpers.toFixed3( Vector3.Distance( bones['r_d_t1'].position, bones['r_d_t2'].position ) ) );
                        _this.setCustomUVScale( 'roof_05_v_scale', helpers.toFixed3( Vector3.Distance( bones['r_d_l1'].position, bones['r_d_t2'].position ) * 2 ) );
                    }

                    if( bones['r_f_t1'] && bones['r_d_l1'] ) {
                        _this.setGlobalData( 'roof_error_number', helpers.toFixed3( bones['r_f_t1'].position.z - bones['r_d_l1'].position.z ) );
                    }

                    if( bones['r_f_t_01'] ) {
                        _this.setGlobalData( 'roof_offset_number', helpers.toFixed3( bones['r_f_t_01'].position.z ) );
                    }
                }

                if( _this.isSidePorchCabin() ) {
                    const stairObj = _this.getMeshByName( 'stair_bb_obj' );
                    if( stairObj ) {
                        stairObj.isPickable = false;
                        stairObj.visibility = 0;
                    }
                    
                    _this._roofUVScale.roof01.u = Vector3.Distance( _this._bones['r_f_t'].position, _this._bones['r_b_t'].position );
                    _this._porchCornerDistance  = helpers.toFixed3( bones['b_f_l5'].position.y - bones['b_f_l2'].position.y );
                    if( 'structure_aframe_cabin' === _this._shedType ) {
                        _this._roofUVScale.roof01.v = Vector3.Distance( _this._bones['r_f_r1'].position, _this._bones['r_f_t'].position );
                        _this._roofUVScale.roof02.u = _this._roofUVScale.roof01.u;
                        _this._roofUVScale.roof02.v = Vector3.Distance( _this._bones['r_f_l2'].position, _this._bones['r_f_t'].position );
                        _this._roofUVScale.roof03.u = _this._roofUVScale.roof01.u;
                        _this._roofUVScale.roof03.v = Vector3.Distance( _this._bones['r_f_l1'].position, _this._bones['r_f_l2'].position );
                    } else if( 'structure_porch_barn' === _this._shedType ) {
                        _this._roofUVScale.roof01.v = Vector3.Distance( _this._bones['r_f_r3'].position, _this._bones['r_f_t'].position );
                        _this._roofUVScale.roof02.u = _this._roofUVScale.roof01.u;
                        _this._roofUVScale.roof02.v = Vector3.Distance( _this._bones['r_f_r2'].position, _this._bones['r_f_r3'].position );
                        _this._roofUVScale.roof03.u = _this._roofUVScale.roof01.u;
                        _this._roofUVScale.roof04.u = _this._roofUVScale.roof01.u;
                        _this._roofUVScale.roof04.v = Vector3.Distance( _this._bones['r_f_l1'].position, _this._bones['r_f_l2'].position );
                        _this._roofUVScale.roof05.u = _this._roofUVScale.roof01.u;
                        _this._roofUVScale.roof05.v = Vector3.Distance( _this._bones['r_f_l2'].position, _this._bones['r_f_l3'].position );
                    }
                }

                if( _this.is2StoryLeanTo() ) {
                    _this._offsetDistance   = atticModel.exteriorOffset;
                    _this._trimMat          =  _this._scene.getMaterialByName( 'trim_mat' ) as StandardMaterial;
                    const secondFloor       = _this.getMeshByName( 'second_floor_obj' );
                    if( secondFloor ) {
                        secondFloor.isPickable = false;
                    }
                    const frameColliders = [ 'B_B_L_collider_obj', 'B_B_R_collider_obj', 'B_F_L_collider_obj', 'B_F_R_collider_obj' ];
                    frameColliders.forEach( m => {
                        const colliderMesh = _this.getMeshByName( m ) as Mesh;
                        if( colliderMesh ) colliderMesh.setEnabled( false );
                    });

                    _this._roofMesh[ 'metal' ]              = [ 'roof_metal_lean_to_clone_obj', 'roof_metal_lean_to_obj', 'roof_metal_porch_clone_obj', 'roof_metal_porch_obj' ];
                    _this._roofMesh[ 'metal_horizontal' ]   = [ 'roof_metal_horizontal_lean_to_obj', 'roof_metal_horizontal_porch_obj' ];
                    _this._roofMesh[ 'shingle' ]            = [ 'roof_shingle_lean_to_obj', 'roof_shingle_porch_obj' ];
                }

                if( 'structure_ranch_cabin' === _this._shedType && bones['b_f_r1'] && bones['b_b_m1'] ) {
                    _this._cabinWidth = Math.abs( bones['b_f_r1'].position.z - bones['b_b_m1'].position.z );
                } 
                
                if( _this.isAframeSingleGable() && bones['b_f_l1'] && bones['b_p_l1'] ) {
                    _this.setCustomUVScale( 'roof_02_u_scale', helpers.toFixed3( Vector3.Distance( bones['r_m_t2'].position, bones['r_m_t1'].position ) ) );
                    _this.setCustomUVScale( 'celling_porch_u_scale', helpers.toFixed3( Vector3.Distance( bones['b_p_l1'].position, bones['b_p_l4'].position ) ) );
                    _this.setGlobalData( 'error_baluster_f_l1_x', helpers.toFixed3( Math.abs( bones['b_b_l1'].position.z ) - Math.abs( bones['baluster_f_l1'].position.z ) ) );
                    _this._cabinWidth = Vector3.Distance( bones['b_f_l1'].position, bones['b_p_l1'].position );
                }

                let frame = _this.getMeshByName( 'frame_post_clone_obj' ) as Mesh;
                if( null === frame ) {
                    frame = _this.getMeshByName( 'frame_double_legs_post_clone_obj' ) as Mesh;
                }

                if( frame ) {
                    _this._frameWidth = helpers.toFixed3( frame.geometry.extend.maximum.z - frame.geometry.extend.minimum.z );
                }

                if( 'structure_aframe_horsebarn' === _this._shedTypeVersion ) {
                    const sidingInnerMat = _this.getMaterialByName( 'siding_wall_inner_mat' );
                    if( sidingInnerMat ){
                        sidingInnerMat.forEach( m => {
                            m.backFaceCulling = false;
                        });
                    }
                }

                const runnerMesh = _this.getMeshByName( 'runner_horizontal_obj' ) as Mesh;
                if( undefined !== runnerMesh && null !== runnerMesh ) {
                    _this._runnerType = _this._horizontalRunner;
                }                

                const loftMesh = _this.filterByValue( task.loadedMeshes, 'name', 'loft_obj' ) as Mesh;
                if( loftMesh ) loftMesh.dispose();
                
                const lambertMats = _this.getMaterialByName( 'lambert1' );
                if( lambertMats ){
                    lambertMats.forEach( m => {
                        m.alpha = 0;
                    });
                }

                let width = bones['b_f_r1'].position.x * 2;
                let depth = bones['b_f_r1'].position.z * 2;
                if( _this.isDeluxeCornerLeftPorch() ) {
                    depth = Vector3.Distance( _this._bones['b_b_l1'].position, _this._bones['b_p_l1'].position );
                }
                if( bones['r_f_r1'] && bones['r_b_r1'] ) {
                    _this._initRoofWidth = + ( Vector3.Distance( bones['r_f_r1'].position, bones['r_b_r1'].position ).toFixed( 3 ) );
                }

                let wallHeight      = bones['b_f_r2'] ? bones['b_f_r2'].position.y : 0;
                const widthForRoof  = 'lean_to' === _this._shedType || 'structure_monoslope_smithbilt' === _this._shedType ? width : width / 2;
                if( 'mainus_quaker' === _this._shedType || 'structure_saltbox_mwshed' === _this._shedType ) {
                    width = Math.abs( bones['b_f_l1'].position.x ) + Math.abs( bones['b_f_r3'].position.x );
                }

                if( 'structure_old_west_modular' === _this._shedType ) {
                    this._collarTieHeight = helpers.toFixed3( bones['collar_tie_b_t'].position.y - bones['collar_tie_b_r1'].position.y );
                    const slidingF  = _this.getMeshByName( 'sliding_f_obj' ) as Mesh;
                    const slidingB  = _this.getMeshByName( 'sliding_b_obj' ) as Mesh;
                    if( slidingF ) slidingF.setEnabled( false );
                    if( slidingB ) slidingB.setEnabled( false );
                }

                let initRoofPitchHeight = _this.calculateRoofPitchHeight( _this.getInitRoofDegree(), widthForRoof );
                let initMidRoofPitchHeight = _this.calculateMidRoofPitchHeight();
                let buildingHeight  = wallHeight + initRoofPitchHeight;

                const tmpWidth          = helpers.getBaseWidth();
                const tmpDepth          = helpers.getBaseDepth();
                const tmpHeight         = helpers.getBaseHeight();
                let tmpBuildingHeight   = tmpHeight;
                // For gambrel shed
                if ( 'saltbox' === _this.getRoofShape() ) {
                    _this._initRoofSaltboxHeight    = bones['b_b_t'].position.y - bones['b_b_l2'].position.y;
                    _this._initRatioRoofSaltbox     = Math.abs( bones['b_b_l1'].position.x ) * 2 / bones['b_b_t'].position.x;

                    const roofInstance              = Math.abs( _this._bones['b_b_l1'].position.x ) + Math.abs( _this._bones['b_b_t'].position.x );
                    const roofHeight                = Math.abs( _this._bones['b_b_t'].position.y ) - Math.abs( _this._bones['b_b_l1'].position.y );
                    
                    _this._initSaltBoxWidth         = Math.sqrt( roofInstance * roofInstance + roofHeight * roofHeight );

                    if( 'structure_horse_shelter' === _this._shedType ) {
                        _this._initRatioRoofSaltbox = ( Math.abs( bones['b_b_l3'].position.x ) + Math.abs( bones['b_b_r2'].position.x ) ) / bones['b_b_t'].position.x;
                    }

                    if( 'structure_cottage_shed' === _this._shedType ) {
                        _this._initRoofLeftWidth   = Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_b_t'].position );
                        _this._initRoofRightWidth  = Vector3.Distance( _this._bones['r_b_r1'].position, _this._bones['r_b_t'].position );
                    }
                }

                if ( 'box_gable' === _this.getRoofShape() || 'open_gable' === _this.getRoofShape() ) {
                    if( _this.isDoubleGable() ) {
                        const halfWidth = Math.abs( bones['b_f_l2'].position.z ) - Math.abs( bones['b_f_t2'].position.z );
                        const boxGableHeight = Math.abs( bones['b_f_t2'].position.y ) - Math.abs( bones['b_f_l2'].position.y );
                        _this._initBoxGableWidth = helpers.toFixed3( Math.sqrt( halfWidth * halfWidth + boxGableHeight * boxGableHeight ) );
                        _this.setGlobalData( constants.initRoofGableDegrees, helpers.toFixed3( Math.atan( boxGableHeight / halfWidth ) * 180 / Math.PI ) )
                        _this.setInitRoofAngel( _this.getRoofShape(), _this.getGlobalData( constants.initRoofGableDegrees ) );
                    } else if( _this.isAframeSingleGable() ) {
                        const roofHeight = helpers.toFixed3( Math.abs( bones['b_f_t2'].position.y ) - Math.abs( bones['b_f_l2'].position.y ) );
                        const halfWidth = helpers.toFixed3( Math.abs( bones['b_f_l2'].position.z ) - Math.abs( bones['b_f_t2'].position.z ) );
                        const roofAngel = helpers.toFixed3( Math.atan( roofHeight / halfWidth ) * 180 / Math.PI );

                        _this._initBoxGableWidth = helpers.toFixed3( Vector3.Distance( bones['b_f_l2'].position, bones['b_f_t2'].position ) );
                        _this._initRoofLowerDegree = roofAngel;

                        _this.setInitRoofAngel( _this.getRoofShape(), roofAngel );
                        _this.setGlobalData( 'dormerRoofHeight', roofHeight );
                        _this.setGlobalData( 'porchJointX', helpers.toFixed3( Math.abs( bones['b_b_l3'].position.y ) - Math.abs( bones['b_b_l2'].position.y ) ) );
                    } else {
                        const halfWidth = 'structure_aframe_genesis' === _this._shedType ? Math.abs( bones['b_b_r1'].position.x ) - Math.abs( bones['b_b_t'].position.x ) : Math.abs( bones['b_b_r2'].position.x ) - Math.abs( bones['b_b_t'].position.x );
                        const boxGableHeight = 'structure_aframe_genesis' === _this._shedType ? Math.abs( bones['b_b_t'].position.y ) - Math.abs( bones['b_b_r1'].position.y ) : Math.abs( bones['b_b_t'].position.y ) - Math.abs( bones['b_b_r2'].position.y );
                        _this._initBoxGableWidth = Math.sqrt( halfWidth * halfWidth + boxGableHeight * boxGableHeight );
                        
                        _this.setInitRoofAngel( _this.getRoofShape(), helpers.toFixed3( Math.atan( boxGableHeight / halfWidth ) * 180 / Math.PI ) );
                    }
                }

                if( 'structure_lakeside_bunkie_gable_roof' === _this._shedType ){
                    _this._initRoofLeftWidth   = Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_b_t'].position );
                    _this._initRoofRightWidth  = Vector3.Distance( _this._bones['r_b_r1'].position, _this._bones['r_b_t'].position );
                }

                if( 'structure_lakeside_bunkie_gambrel_roof' === _this._shedType ){
                    _this._roofDetachedMats['roof_01'] = Vector3.Distance( _this._bones['r_b_r2'].position, _this._bones['r_b_t'].position );
                    _this._roofDetachedMats['roof_02'] = Vector3.Distance( _this._bones['r_b_r2'].position, _this._bones['r_b_r1'].position );
                    _this._roofDetachedMats['roof_03'] = Vector3.Distance( _this._bones['r_b_l2'].position, _this._bones['r_b_t'].position );
                    _this._roofDetachedMats['roof_04'] = Vector3.Distance( _this._bones['r_b_l2'].position, _this._bones['r_b_l1'].position );
                    
                }

                if( bones['braces_b_r2'] ) {
                    _this._initBracesBR2 = _this._bones['braces_b_r2'].position;
                }

                if( 'structure_quaker_wannabuysheds' === _this._shedType ) {
                    _this._roofDetachedMats['roof_01'] = Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_b_t'].position );
                    _this._roofDetachedMats['roof_02'] = Vector3.Distance( _this._bones['r_b_r1'].position, _this._bones['r_b_t'].position );

                    bones['b_b_t'].position.x  -= 0.5;
                    _this._midPointPosition     = { ...bones['b_b_t'].position };
                }

                if( 'monitor' === _this.getRoofShape() ) {
                    const lowerHeight           = bones['r_b_l2'].position.y - bones['r_b_l1'].position.y;
                    const lowerWidth            = Math.abs( bones['r_b_l1'].position.x ) - Math.abs( bones['r_b_l2'].position.x );
                    _this._initRoofLowerDegree  = + ( Math.atan( lowerHeight / lowerWidth ) * 180 / Math.PI ).toFixed( 3 );
                    _this._initRoofLowerWidth   = + ( Math.sqrt( lowerWidth * lowerWidth + lowerHeight * lowerHeight ) ).toFixed( 3 );

                    const upperHeight           = bones['r_b_t'].position.y - bones['r_b_l3'].position.y;
                    const upperWidth            = Math.abs( bones['r_b_l3'].position.x ) - Math.abs( bones['r_b_t'].position.x );
                    _this._initRoofUpperDegree  = + ( Math.atan( upperHeight / upperWidth ) * 180 / Math.PI ).toFixed( 3 );
                    _this._initRoofUpperWidth   = + ( Math.sqrt( upperWidth * upperWidth + upperHeight * upperHeight ) ).toFixed( 3 );

                    _this.setInitRoofAngel( 'monitor', _this._initRoofLowerDegree, _this._initRoofUpperDegree );
                } else if ( 'gambrel' === _this.getRoofShape() || 'dutch_style' === _this.getRoofShape() ) {
                    _this._initGambrelMidXRatio = Math.abs( bones['r_b_l1'].position.x ) * 2 / bones['r_b_r2'].position.x;
                    let lowerHeight             = bones['r_b_l2'].position.y - bones['r_b_l1'].position.y;
                    let lowerWidth              = Math.abs( bones['r_b_l1'].position.x ) - Math.abs( bones['r_b_l2'].position.x );
                    let upperHeight             = bones['r_b_t'].position.y - bones['r_b_l2'].position.y;
                    let upperWidth              = Math.abs( bones['r_b_l2'].position.x ) - Math.abs( bones['r_b_t'].position.x );
                    if( 'structure_porch_barn' === _this._shedType ) {
                        _this._initGambrelMidXRatio = Math.abs( bones['r_b_r2'].position.x ) * 2 / Math.abs( bones['r_b_r3'].position.x );
                        lowerHeight                 = bones['r_f_r3'].position.y - bones['r_f_r2'].position.y;
                        lowerWidth                  = Math.abs( bones['r_f_r2'].position.x ) - Math.abs( bones['r_f_r3'].position.x );
                        upperHeight                 = bones['r_b_t'].position.y - bones['r_f_r3'].position.y;
                        upperWidth                  = Math.abs( bones['r_f_r3'].position.x ) - Math.abs( bones['r_b_t'].position.x );
                    }

                    _this._initGambrelMidY      = bones['r_b_r2'].position.y;
                    _this._roofMiddleR2Position = new Vector3( bones['r_b_r2'].position.x, bones['r_b_r2'].position.y, bones['r_b_r2'].position.z );
                    buildingHeight              = bones['r_b_t'].position.y;
                    initRoofPitchHeight         = buildingHeight - _this._initGambrelMidY;
                    tmpBuildingHeight           = buildingHeight - wallHeight + tmpHeight;
                    _this._initRoofLowerDegree  = + ( Math.atan( lowerHeight / lowerWidth ) * 180 / Math.PI ).toFixed( 3 );
                    _this._initRoofLowerWidth   = + ( Math.sqrt( lowerWidth * lowerWidth + lowerHeight * lowerHeight ) ).toFixed( 3 );
                    _this._initRoofUpperDegree  = + ( Math.atan( upperHeight / upperWidth ) * 180 / Math.PI ).toFixed( 3 );
                    _this._initRoofUpperWidth   = + ( Math.sqrt( upperWidth * upperWidth + upperHeight * upperHeight ) ).toFixed( 3 );

                    _this.setInitRoofAngel( 'gambrel', _this._initRoofLowerDegree, _this._initRoofUpperDegree );
                } else if ( _this.getRoofShape() == "flat" ) {
                    if( bones['b_f_t'] && bones['b_f_l2'] ) {
                        initRoofPitchHeight = + ( bones['b_f_t'].position.y - bones['b_f_l2'].position.y ).toFixed( 3 );
                    }
                    buildingHeight = wallHeight;
                    if( 'lean_to' !== _this._shedType && 'structure_monoslope_smithbilt' !== _this._shedType ) {
                        initRoofPitchHeight = 0;
                    }

                    let roofHeight  = 0;
                    let roofWidth   = 0;
                    if( bones['r_b_l1'] ){
                        _this._initFlatRoofWidth = Vector3.Distance( bones['r_b_l1'].position, bones['r_b_r1'].position );

                        roofHeight  = Math.abs( bones['r_b_l1'].position.y - bones['r_b_r1'].position.y );
                        roofWidth   = Math.abs( bones['r_b_l1'].position.x - bones['r_b_r1'].position.x );
                    }else{
                        _this._initFlatRoofWidth = Vector3.Distance( bones['b_b_l2'].position, bones['b_b_r2'].position );

                        roofHeight  = Math.abs( bones['b_b_l2'].position.y - bones['b_b_r2'].position.y );
                        roofWidth   = Math.abs( bones['b_b_l2'].position.x - bones['b_b_r2'].position.x );
                    }

                    _this._lightOffsetY = 0.2;

                    _this.setInitRoofAngel( 'flat', helpers.toFixed3( Math.atan( roofHeight / roofWidth ) * 180 / Math.PI ) );
                } else if( 'mainus_quaker' === _this._shedType ) {
                    initRoofPitchHeight = 0.743;
                } else if( 'structure_saltbox_mwshed' === _this._shedType ) {
                    initRoofPitchHeight = 2.111;
                } else if ( _this.getRoofShape() == 'hip' ) {
                    _this._hipRoofTopLength = tmpDepth - tmpWidth;
                    _this._initHipRoofTopLength = depth - width;
                    _this._midPoint = 'r_b_t';
                }

                if( bones['b_d_l1'] ){
                    _this._initDormerWidth = bones['b_d_l1'].position.z * 2;
                    _this._dormerWidth = bones['b_d_l1'].position.z * 2;
                }

                if( 'structure_dog_kennel' === _this._shedType ) {
                    _this._initDogKennelBoxDepth = _this.getDogKennelBoxDepth();
                }

                _this._initDistancePorchError = bones['post_t'] && bones['b_p_l2'] ? bones['post_t'].position.y - bones['b_p_l2'].position.y : 0;
                if( _this.isPorchHasTrim() ) {
                    _this._initDistancePorchError += _this._trimLength;
                }
                
                if( 'standard_cottage_front_porch' === _this._shedType ) {
                    _this._initDistancePorchError += -0.575;
                }

                if( 'structure_lark_aframe_metal_porch' === _this._shedType ) {
                    _this._initDistancePorchError += -0.7;
                }

                if( 'structure_aframe_genesis' === _this._shedType ) {
                    _this._initRoofWidth   = Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_f_l1'].position );
                    _this._initRoofHeight  = Vector3.Distance( _this._bones['r_f_t'].position, _this._bones['r_f_r1'].position );
                }

                if( 'structure_loafing_shed' === _this._shedType ) {
                    const stubObj       = _this.getMeshByName( 'stub_back_clone_obj' ) as Mesh;
                    let stubClone       = _this.getMeshByName( 'stub_back_clone_2_obj' ) as Mesh;
            
                    if( ! stubClone ) stubClone     = stubObj.clone( 'stub_back_clone_2_obj' );
                    stubClone.skeleton  = stubObj.skeleton;
                }

                if( 'structure_barn_smithbilt' === _this._shedType ) {
                    const roofSiding    = _this.getMeshByName( 'roof_siding_inner_obj' ) as Mesh;
                    const roofSidingL   = _this.getMeshByName( 'roof_siding_L_obj' ) as Mesh;
                    const roofSidingM   = _this.getMeshByName( 'roof_siding_M_obj' ) as Mesh;
                    const roofSidingR   = _this.getMeshByName( 'roof_siding_R_obj' ) as Mesh;

                    if( roofSiding ) roofSiding.isPickable      = false;
                    if( roofSidingL ) roofSidingL.isPickable    = false;
                    if( roofSidingM ) roofSidingM.isPickable    = false;
                    if( roofSidingR ) roofSidingR.isPickable    = false;
                }

                if( 'structure_rancher_modular' === _this._shedTypeVersion ) {
                    const meshes = [ 'gable_3rd_left_obj', 'gable_3rd_right_obj' ];

                    _this._roofMesh[ 'metal' ] = ['roof_metal_dormer_left_obj', 'roof_metal_dormer_right_obj'];
                    _this._roofMesh[ 'metal_horizontal' ] = ['roof_metal_horizontal_dormer_left_obj', 'roof_metal_horizontal_dormer_right_obj'];
                    _this._roofMesh[ 'shingle' ] = ['roof_shingle_dormer_left_obj', 'roof_shingle_dormer_right_obj'];

                    meshes.forEach( key => {
                        const mesh = _this.getMeshByName( key );
                        mesh.setEnabled( false );
                    } );
                }
                
                let hideMeshes = [ 'siding_metal_horizontal_clone_obj', 'siding_wooden_clone_obj', 'siding_wooden_clone_set_obj' ];
                if( _this.isCarport() ) {
                    hideMeshes = [ ...hideMeshes, 'j_trim_b_obj', 'j_trim_f_obj', 'j_trim_l_obj', 'j_trim_r_obj', 'j_trim_b_l_side_wall_obj', 'j_trim_b_r_side_wall_obj', 'j_trim_f_l_side_wall_obj', 'j_trim_f_r_side_wall_obj', 'corner_trim_b_l_obj', 'corner_trim_b_r_obj', 'corner_trim_f_l_obj', 'corner_trim_f_r_obj', 'corner_trim_b_l_side_wall_obj', 'corner_trim_b_r_side_wall_obj', 'corner_trim_f_l_side_wall_obj', 'corner_trim_f_r_side_wall_obj' ];
                }

                if( ! _this.isExternalPorch() ) {
                    hideMeshes.push( 'porch_obj' );
                }

                if( ! _this.isEnableLeanTo() ) {
                    hideMeshes.push( 'lean_to_obj' );
                }

                hideMeshes.forEach( key => {
                    const mesh = _this.getMeshByName( key );
                    if( mesh ) mesh.setEnabled( false );
                } );

                _this._balusterMiddleSpace = _this.isMiddlePorch() ? Math.abs( bones['baluster_b_l'].position.z - bones['rail_h_l_b_l1'].position.z ) + 1 / 12: _this._balusterMiddleSpace;

                _this._legDistance = _this.initFrameSpacing();
                _this.loadMaterialInner();
                _this.loadWallFinishMat();
                _this.setInitScaleForLogCabin();
                _this.setInitialWidth(width);
                _this.setInitialDepth(depth);
                _this.setInitialWallHeight(wallHeight);
                _this.setInitialBuildingHeight(buildingHeight);
                _this.setInitialRoofPitchHeight(initRoofPitchHeight);
                _this.setInitialMidRoofPitchHeight(initMidRoofPitchHeight);
                _this.setWidth( tmpWidth );
                _this.setDepth( tmpDepth );
                _this.setWallHeight( tmpHeight );
                _this.setBuildingHeight( tmpBuildingHeight );
                _this.setRoofPitchHeight(initRoofPitchHeight);
                _this.setMidRoofPitchHeight(initMidRoofPitchHeight);
                _this.initRoof();
                _this.createLine();
                _this.showHorizontalTrims();
                _this.drawNoticeText();
                _this.disablePickableSidingMesh();
                _this.setDefaultRunner();
                _this.updateRunnerColor();
                _this.updatePorchColor();
                _this.updatePorchMesh();
                _this.setPorchBalusterOffset( bones );
                _this.updateDeckingDirection();
                _this.deluxePorchHandleBrace();
                _this.handleShowHideStubModel( false );
                _this.cloneKickBoard();
                _this.cloneKickBoard( 'frame_kick_board' );
                _this.handleHideWallTackRoom();
                resolve( true );
            };

            _this.forceAssetLoading();
        });
    }
    
    /**
     * Load new roof material files based on the specified settings.
     * 
     * @returns {Promise<boolean>} A promise that resolves to true if the materials are successfully loaded or not required.
     * 
     */
    private loadNewRoofMaterialFiles = async () => {
        if( 'true' !== this.getSettingData( 'apply_new_roof' ) ) {
            return new Promise( resolve => { resolve( true ) } );
        }

        const _this     = this;
        const loadMesh  = function ( roofType, fileName, path ) {
            return new Promise( resolve => {

                const roofTask = _this._assetsManager.addMeshTask( `load_${fileName}`, '', `${path}`, `${fileName}` );

                roofTask.onError = task => {
                    resolve( false );
                }

                roofTask.onSuccess = task => {
                    task.loadedSkeletons.forEach( skeleton => {
                        skeleton.bones.forEach( bone => {
                            if( _this._bones[bone.name.toLowerCase()] ) {
                                _this._roofBoneSync.push( bone );
                                bone.setPosition( _this._bones[bone.name.toLowerCase()].position );
                            }
                        } );
                    } );

                    task.loadedMeshes.forEach( mesh => {
                        mesh.isPickable             = false;
                        _this._roofMesh[roofType]   = helpers.checkAndPushKeyToArray( _this._roofMesh[roofType], mesh.name );
                        const modelMesh             = _this.getMeshByName( mesh.name );
                        mesh.setEnabled( roofType === _this._roofType );
                        
                        if( modelMesh && modelMesh.uniqueId !== mesh.uniqueId ) {
                            modelMesh.material.dispose();
                            modelMesh.dispose();
                        }

                    } );

                    _this.importMaterials( task.loadedMeshes );

                    resolve( true );
                }
                _this.forceAssetLoading();
            } );
        };

        const listRoofs     = this.getListRoofs();
        const loadMeshArgs  = [];

        for( let key of listRoofs ) {
            loadMeshArgs.push( loadMesh( key, `roof_${key}_${this.getRoofShape()}.babylon`, this._materialsDir ) );
        }

        if( loadMeshArgs.length > 0 ) {
            Promise.all( loadMeshArgs ).then( ( results ) => {
                console.log( 'All meshes loaded:', results );
            } ).catch( ( error ) => {
                console.error( 'An error occurred while loading meshes:', error );
            } );
        }

        return true;
    }


    /**
     * handle hide stud default load model
     */
    private handleShowHideStubModel = ( show ) => {
        const mesh = this.getMeshByName( 'stud_inner_obj' );
        if( ! mesh ) return;
        mesh.setEnabled( show );
    }

    /**
     * Clone kick board then rotation and apply to side  
    */
    private cloneKickBoard = ( prefix = 'kick_board' ) => {
        if( ! this.isHorseRunIn() && 'structure_horsebarn_leanto_oaklane' !== this._shedTypeVersion ) return;
        const mesh = this.getMeshByName( `${prefix}_obj` ) as Mesh;
        let originMatName = 'frame_inner_mat';
        if( 'kick_board' === prefix && 'structure_horsebarn_leanto_oaklane' === this._shedTypeVersion ) {
            originMatName = 'kickboard_mat';
        }
        let material = this._scene.getMaterialByName( originMatName ) as StandardMaterial;
        if( ! mesh ) return;
        if( ! this._isEnableKickBoard && 'structure_horsebarn_leanto_oaklane' !== this._shedTypeVersion ) {
            mesh.setEnabled( false );
            return;
        }
        const parentMesh    = this.getMeshByName( 'body_obj' );
        const sides         = [ 'front', 'back', 'left', 'right' ];
        for( let i = 0; i < sides.length; i++ ) {
            const oldMesh = this.getMeshByName( `${prefix}_${sides[i]}_obj` );
            if( oldMesh ) oldMesh.dispose();
            const meshClone     = mesh.clone( `${prefix}_${sides[i]}_obj` );
            const materialClone = material.clone( `${originMatName}_${prefix}_${sides[i]}` );
            meshClone.material  = materialClone;
            meshClone.parent    = parentMesh;
            meshClone.isPickable = false;
            if( 'back' === sides[i] ) {
                meshClone.rotation = 'structure_horsebarn_leanto_oaklane' !== this._shedTypeVersion ? new Vector3( 0, Math.PI/2, 0 ) : new Vector3( 0, -Math.PI/2, 0 );
            } else if( 'front' === sides[i] ) {
                meshClone.rotation = 'structure_horsebarn_leanto_oaklane' !== this._shedTypeVersion ? new Vector3( 0, -Math.PI/2, 0 ) : new Vector3( 0, Math.PI/2, 0 );
            } else if( 'left' === sides[i] ) {
                meshClone.rotation = 'structure_horsebarn_leanto_oaklane' !== this._shedTypeVersion ? new Vector3( 0, Math.PI, 0 ) : new Vector3( 0, 0, 0 );
            } else if( 'right' === sides[i] ) {
                meshClone.rotation = 'structure_horsebarn_leanto_oaklane' !== this._shedTypeVersion ? new Vector3( 0, 0, 0 ) : new Vector3( 0, -Math.PI, 0 );
            }
        }
        mesh.setEnabled( false );
        this.setInitialUVs( material );
    }


    applyKickBoardToPartialWall = ( side, positionZ, meshName = '' ) => {
        if( ! this._isEnableKickBoard ) return;
        const referenceSide = 'left' === side ? 'back' : 'front';
        const kickBoardName =  '' === meshName ? `partial_${referenceSide}` :  `partial_${meshName}`;
        const mesh          = this.getMeshByName( `kick_board_${referenceSide}_obj` ) as Mesh;
        if( ! mesh ) return;

        const oldMesh       = this.getMeshByName( `kick_board_${kickBoardName}_obj` );
        if( oldMesh ) oldMesh.dispose();
        const meshClone            = mesh.clone( `kick_board_${kickBoardName}_obj` );
        const meshWidth            = this.getMeshWidth( mesh );
        meshClone.parent           = mesh.parent;
        meshClone.position.z       = 'left' === side ? positionZ + meshWidth : positionZ - meshWidth;
        meshClone.setEnabled( true );
    }

    /**
     * handle hide wall inner task room
     */
    private handleHideWallTackRoom = () => {
        const mesh = this.getMeshByName( 'inner_wall_f_obj' );
        if( ! mesh ) return;
        if( this.isHorseRunIn() ){
            mesh.setEnabled( false );
        }
    }

    /**
     * Handle cut kick board 
    */
    private handleIntersectKickBoard = ( side : string, prefix = 'kick_board' ) => {
        const mesh = this.getMeshByName( `${prefix}_${side}_obj` ) as Mesh;
        let shapeTackroom : Mesh;
      
        if( this._isEnableTackRoom ) {
            shapeTackroom = this.drawShapeTackRoom();
        }

        if( mesh ) {
            this.cutMesh( mesh, side, shapeTackroom, prefix );
        }
    }

    /**
     * Handle cut Mesh for side 
    */
    private cutMesh = ( mesh, side, otherMesh:any = false, prefix ) => {
        mesh.setEnabled( true );
        const newMeshName   = `${prefix}_${side}_cut_obj`;
        const oldMesh       = this.getMeshByName( newMeshName );
        if( oldMesh ) oldMesh.dispose();
        let intersectMesh = this.getIntersectMeshes( side );

        if( otherMesh ) {
            intersectMesh.push( otherMesh );
        }
        if( intersectMesh.length == 0 ) return mesh;
        const meshCSG       = CSG.FromMesh( mesh );
        for( let i = 0; i < intersectMesh.length; i++ ) {
            meshCSG.subtractInPlace( CSG.FromMesh( intersectMesh[i] ) );
        }
        const newMesh       = meshCSG.toMesh( newMeshName, mesh.material, this._scene, true );
        newMesh.isPickable  = false;
        newMesh.parent      = mesh.parent;
        mesh.setEnabled( false );
        return newMesh;
    }

    /**
    * Handle scale kick board follow wall 
    */
    private scaleKickBoard = ( cutMesh = false, prefix = 'kick_board' ) => {
        if( ( ! this.isHorseRunIn() && 'structure_horsebarn_leanto_oaklane' !== this._shedTypeVersion ) || ( ! this._isEnableKickBoard && 'structure_horsebarn_leanto_oaklane' !== this._shedTypeVersion ) ) return;

        const mesh = this.getMeshByName( `${prefix}_obj` ) as Mesh;
        if( ! mesh ) return;

        const meshLength = this.getMeshLength( mesh );
        const meshWidth  = this.getMeshWidth( mesh );
        let frameDepth = 0 !== this._frameDepth ? this._frameDepth + 0.004 + 0.037 : 0.004;  // 0.004 is wall depth, 0.037 ratio num
        let valueMinus = frameDepth + meshWidth/2;
        if( 'structure_horsebarn_leanto_oaklane' === this._shedTypeVersion ) {
            valueMinus = 'frame_kick_board' === prefix ? - ( meshWidth / 2 + 0.004 ) : ( meshWidth / 2 + 0.004 );
            frameDepth = 'frame_kick_board' === prefix ? -meshWidth/2 : 0.037;
        }
        const sides      = [
            {   side: 'back',
                start: 'b_b_l1',
                end  : 'b_b_r1',
                move : 'z',
                value: - valueMinus
            },
            {    
                side : 'front',
                start: 'b_f_l1',
                end  : 'b_f_r1',
                move : 'z',
                value: valueMinus,
            },
            {   
                side : 'left',
                start: 'b_f_l1',
                end  : 'b_b_l1',
                move : 'x',
                value: - valueMinus,
            },
            {
                side : 'right',
                start: 'b_b_r1',
                end  : 'b_f_r1',
                move : 'x',
                value: valueMinus,
            }
        ];
        
        let originMatName = 'frame_inner_mat';
        if( 'kick_board' === prefix && 'structure_horsebarn_leanto_oaklane' === this._shedTypeVersion ) {
            originMatName = 'kickboard_mat';
        }

        const initUScale = this._initialUVs[`${originMatName}`]['bumpTexture']['uScale'];
        const initVScale = this._initialUVs[`${originMatName}`]['bumpTexture']['vScale'];

        
        sides.forEach( item => {
            const meshClone     = this.getMeshByName( `${prefix}_${item.side}_obj` ) as Mesh;
            const material      = this._scene.getMaterialByName( `${originMatName}_${prefix}_${item.side}` ) as StandardMaterial;
            meshClone.position  = Vector3.Center( this._bones[`${item.start}`].position, this._bones[`${item.end}`].position );
            const distance      = Vector3.Distance( this._bones[`${item.start}`].position, this._bones[`${item.end}`].position );
            let ratio = ( distance - frameDepth * 2  ) / meshLength;
            let positionY = meshClone.position.y;
            if( 'frame_kick_board' === prefix ) {
                positionY = 4.5 - this.getMeshHeight( meshClone );
                ratio = 'z' === item.move ? ( distance + meshWidth * 2 )/meshLength : distance/meshLength;
            }
            meshClone.scaling.z = ratio;
            this.updateUVScale( material, 1, ratio, initUScale, initVScale )
            if( 'x' === item.move ) {
                meshClone.position = new Vector3( meshClone.position.x - item.value, positionY, meshClone.position.z );
            } else {
                meshClone.position = new Vector3( meshClone.position.x, positionY, meshClone.position.z - item.value );
            }
            if( cutMesh ) {
                this.handleIntersectKickBoard( item.side, prefix );
            }
        } );
    }

    /**
     * Load Special component. eg porch, loft.....
     */
    private async loadComponentSpecial( name: string, type: string, condition: boolean ) {
        let _this = this;

        if( this.is2StoryLeanTo() && 'porch' === name ) {
            condition = false;
        }

        return new Promise( resolve => {
            if( ! condition ) {
                return resolve( true );
            }
            const version = helpers.getVersionComponent( name, _this._componentData );
            const componentTask = _this._assetsManager.addMeshTask( `load_component_${name}`, '', `${_this._componentDir}${type}/${name}/`, `${name}.babylon${version}` );

            componentTask.onSuccess = function( task ) {
                _this.importSkeletons( task.loadedSkeletons );
                _this.importMaterials( task.loadedMeshes );
                _this.updatePorchColor();
                _this.setPorchBalusterOffset( _this._bones )
                _this.updateDeckingDirection();

                if( 'porch' === type ) {
                    if( _this._bones['porch_b_l3'] && _this._bones['porch_b_r3'] ) {
                        _this.setCustomUVScale( 'external_porch_roof_u_scale', helpers.toFixed3( Vector3.Distance( _this._bones['porch_b_l3'].position, _this._bones['porch_b_r3'].position ) ) );
                    }
                    
                    if( _this._bones['porch_b_l1'] && _this._bones['porch_f_l1'] && _this._bones['porch_b_r1'] ) {
                        _this._initPorchDepth = helpers.toFixed3( Vector3.Distance( _this._bones['porch_b_l1'].position, _this._bones['porch_f_l1'].position ) );
                        _this._initPorchWidth = helpers.toFixed3( Vector3.Distance( _this._bones['porch_b_l1'].position, _this._bones['porch_b_r1'].position ) );
                    }

                    const porchObj = _this.getMeshByName( 'porch_obj' ) as Mesh;
                    if( porchObj ) {
                        porchObj.isPickable = false;
                    }
                }

                resolve( true );
            }

            _this.forceAssetLoading();
        })
    }


    /**
     * Set doesn't allow picking mesh
     */
    disablePickableSidingMesh = () => {
        const _this         = this;
        const sidingMeshes  = [ 'siding_f_obj', 
                                'siding_b_obj', 
                                'siding_l_obj', 
                                'siding_r_obj', 
                                'outer_joist_obj', 
                                'runner_obj', 
                                'roof_metal_clone_obj', 
                                'roof_metal_obj', 
                                'roof_shingle_obj', 
                                'frame_obj', 
                                'inner_obj', 
                                'porch_f_obj', 
                                'rail_obj', 
                                'baluster_obj', 
                                'rail_h_obj', 
                                'rail_v_obj', 
                                'Porch_grp', 
                                'siding_roof_mat', 
                                'flat_dormer_8ft_obj', 
                                'flat_dormer_12ft_obj', 
                                'siding_p_obj',
                                'rail_h_l_obj',
                                'rail_h_r_obj',
                                'rail_v_l_obj',
                                'rail_v_r_obj',
                                'column_obj',
                                'column_obj_1',
                                'posts_obj',
                                'porch_c_obj',
                                'posts_braces_obj',
                                'loft_clone',
                                'loft_obj',
                                'column_braces_obj',
                                'roof_base_obj',
                                'roof_metal_cover_obj',
                                'roof_metal_horizontal_cover_obj',
                                'roof_metal_horizontal_obj',
                                'roof_standing_seam_metal_aframe_clone_obj',
                                'roof_standing_seam_metal_aframe_cover_obj',
                                'roof_permatile_aframe_metal_clone_obj',
                                'roof_permatile_aframe_metal_cover_obj',
                                'roof_permatile_lean_to_metal_clone_obj',
                                'roof_permatile_lean_to_metal_cover_obj',
                                'roof_standing_seam_lean_to_metal_clone_obj',
                                'roof_standing_seam_lean_to_metal_cover_obj',
                                'front_wall',
                                'front_wall_clone',
                                'back_wall',
                                'right_wall',
                                'front_inner_porch_wall',
                                'front_wall_clone',
                                'left_back_wall',
                                'left_front_wall',
                                'left_inner_porch_wall',
                                'left_middle_porch_wall',
                                'right_inner_porch_wall',
                                'inner_back_wall',
                                'frame_lean_to_obj',
                                'frame_porch_obj',
                                'stair_bb_obj',
                                'inner_frame_obj',
                                'floor_obj',
                                'frame_stud_obj',
                                'rafter_block_obj',
                                'front_porch_obj',
                            ];

        sidingMeshes.forEach( m => {
            const tmpMesh = _this._scene.getMeshByName(m) as Mesh;
            if( tmpMesh ) {
                tmpMesh.isPickable = false;
            }
        });
    }

    addArrowToLine() {
        let material            = new StandardMaterial( 'tmpMaterial', this._scene );
        material.alpha          = 1;
        material.diffuseColor   = new Color3( 0, 0, 0 );
        const scale             = new Vector3( 0.3, 0.3, 0.3 );

        let arrowMesh;

        if( helpers.isSeadevSite() ) {
            arrowMesh = MeshBuilder.CreateCylinder( "distanceArrow", {
                diameterTop: 0,
                diameterBottom: 0.6,
                height: 0.8,
                tessellation: 32
            }, );
        } else {
            arrowMesh = MeshBuilder.CreateDisc( 'distanceArrow', { tessellation: 3, sideOrientation: 2 } );
        }
        
        arrowMesh.position = new Vector3( 0, 0, 0 );
        arrowMesh.visibility = 0;
        arrowMesh.scaling = scale;
        arrowMesh.material = material;
        arrowMesh.renderingGroupId = 2;

        this._arrowHorizontalLeftStart = arrowMesh.clone( 'arrowHorizontalLeftStart' );
        this._arrowHorizontalLeftEnd = arrowMesh.clone( 'arrowHorizontalLeftEnd' );
        this._arrowHorizontalRightStart = arrowMesh.clone( 'arrowHorizontalRightStart' );
        this._arrowHorizontalRightEnd = arrowMesh.clone( 'arrowHorizontalRightEnd' );
        this._arrowVerticalTopStart = arrowMesh.clone( 'arrowVerticalTopStart' );
        this._arrowVerticalTopEnd = arrowMesh.clone( 'arrowVerticalTopEnd' );
        this._arrowVerticalBottomStart = arrowMesh.clone( 'arrowVerticalBottomStart' );
        this._arrowVerticalBottomEnd = arrowMesh.clone( 'arrowVerticalBottomEnd' );
    }

    createLine = () => {
        this.addArrowToLine();
        this._lineVerticalTop = MeshBuilder.CreateLines( 'lineVerticalTop', this._lineVerticalTopOptions, this._scene );
        this._lineVerticalBottom = MeshBuilder.CreateLines( 'lineVerticalBottom', this._lineVerticalBottomOptions, this._scene );
        this._lineHorizontalLeft = MeshBuilder.CreateLines( 'lineHorizontalLeft', this._lineHorizontalLeftOptions, this._scene );
        this._lineHorizontalRight = MeshBuilder.CreateLines( 'lineHorizontalRight', this._lineHorizontalRightOptions, this._scene );

        this._lineVerticalTop.renderingGroupId = 2;
        this._lineVerticalBottom.renderingGroupId = 2;
        this._lineHorizontalLeft.renderingGroupId = 2;
        this._lineHorizontalRight.renderingGroupId = 2;
    }

    drawNoticeText = () => {
        const widthBox = helpers.isShowMeasurementsInDecimal() && "fraction" === helpers.getMeasurementsDecimalFormat() ? 3 : 1.5;
        const heighBox = 0.5;

        this._distanceTextHolderHorizontalLeft = MeshBuilder.CreateGround( 'distanceTextHolderHorizontalLeft', { width: widthBox, height: heighBox, subdivisions: 25 }, this._scene );
        this._distanceTextHorizontalLeft = new DynamicTexture( 'distanceTextHorizontalLeftTexture', { width: 600, height: 256 }, this._scene, true );
        const leftMaterial = new StandardMaterial( 'Material', this._scene );
        leftMaterial.diffuseTexture = this._distanceTextHorizontalLeft;
        this._distanceTextHolderHorizontalLeft.material = leftMaterial;
        this._distanceTextHolderHorizontalLeft.visibility = 0;
        this._distanceTextHolderHorizontalLeft.isPickable = false;
        this._distanceTextHorizontalLeft.drawText( '', null, null, this._font, this._color, this._background, true );
        this._distanceTextHolderHorizontalLeft.renderingGroupId = 3;
        
        this._distanceTextHolderHorizontalRight = MeshBuilder.CreateGround( 'distanceTextHolderHorizontalRight', { width: widthBox, height: heighBox, subdivisions: 25 }, this._scene );
        this._distanceTextHorizontalRight = new DynamicTexture( 'distanceTextHorizontalRightTexture', { width: 600, height: 256 }, this._scene, true );
        const rightMaterial = new StandardMaterial( 'Material', this._scene );
        rightMaterial.diffuseTexture  = this._distanceTextHorizontalRight;
        this._distanceTextHolderHorizontalRight.material = rightMaterial;
        this._distanceTextHolderHorizontalRight.visibility = 0;
        this._distanceTextHolderHorizontalRight.isPickable = false;
        this._distanceTextHorizontalRight.drawText( '', null, null, this._font, this._color, this._background, true );
        this._distanceTextHolderHorizontalRight.renderingGroupId = 3;
        
        this._distanceTextHolderVerticalTop = MeshBuilder.CreateGround( 'distanceTextHolderVerticalTop', { width: widthBox, height: heighBox, subdivisions: 25 }, this._scene );
        this._distanceTextVerticalTop = new DynamicTexture( 'distanceTextVerticalTopTexture', { width: 512, height: 256 }, this._scene, true );
        const topMaterial = new StandardMaterial( 'Material', this._scene );
        topMaterial.diffuseTexture = this._distanceTextVerticalTop;
        this._distanceTextHolderVerticalTop.material = topMaterial;
        this._distanceTextHolderVerticalTop.visibility = 0;
        this._distanceTextHolderVerticalTop.isPickable = false;
        this._distanceTextVerticalTop.drawText( '', null, null, this._font, this._color, this._background, true );
        this._distanceTextHolderVerticalTop.renderingGroupId = 3;

        this._distanceTextHolderVerticalBottom = MeshBuilder.CreateGround( 'distanceTextHolderVerticalBottom', { width: widthBox, height: heighBox, subdivisions: 25 }, this._scene );
        this._distanceTextVerticalBottom = new DynamicTexture( 'distanceTextVerticalBottomTexture', { width: 512, height: 256 }, this._scene, true );
        const bottomMaterial = new StandardMaterial( 'Material', this._scene );
        bottomMaterial.diffuseTexture = this._distanceTextVerticalBottom;
        this._distanceTextHolderVerticalBottom.material = bottomMaterial;
        this._distanceTextHolderVerticalBottom.visibility = 0;
        this._distanceTextHolderVerticalBottom.isPickable = false;
        this._distanceTextVerticalBottom.drawText( '', null, null, this._font, this._color, this._background, true );
        this._distanceTextHolderVerticalBottom.renderingGroupId = 3;
    }

    private initRoof() {
        let roofType = 'shingle_3tab' === this.getRoofType() ? 'shingle' : this.getRoofType();
        if( undefined === roofType ) {
            return;
        }
        this.flatDormerHandleRoof();

        const roofMetalMesh                 = this._scene.getMeshByName( 'roof_metal_obj' );
        const roofShingleMesh               = this._scene.getMeshByName( 'roof_shingle_obj' );

        if( undefined === roofMetalMesh && undefined === roofShingleMesh ) {
            return;
        }
        this.setEnableRoofComponentByKeys( [ 'roof_permatile_lean_to_metal_cover_obj', 'roof_permatile_lean_to_metal_clone_obj', 'roof_permatile_aframe_metal_clone_obj', 'roof_permatile_aframe_metal_cover_obj', 'roof_standing_seam_lean_to_metal_clone_obj', 'roof_standing_seam_lean_to_metal_cover_obj', 'roof_standing_seam_metal_aframe_clone_obj', 'roof_standing_seam_metal_aframe_cover_obj' ], false );
        const availableMaterials = this._availableRoofMaterials;
        availableMaterials.forEach( item => {
            this.setEnableRoofComponentByKeys( [ `roof_${item}_clone_obj`, `roof_${item}_obj` ], false );
        } );
        this.setEnableRoofComponentByKeys( [ `roof_${roofType}_clone_obj`, `roof_${roofType}_obj` ], true );

        const referenceRoof = jQuery( `[data-roof-type="${roofType}"]` ).attr( 'data-reference-key' );
        if( undefined !== referenceRoof && '' !== referenceRoof ) {
            const currentRoofMesh = this._scene.getMeshByName( `roof_${roofType}_clone_obj` ) as Mesh;
            if( null === currentRoofMesh || undefined === currentRoofMesh ) {
                roofType = referenceRoof;
            }
        }
        
        for ( const key in this._roofMesh ) {
            if ( ! this._roofMesh.hasOwnProperty( key ) ) continue;

            const status = roofType !== key ? false : true;

            this._roofMesh[key].forEach( m => {
                const mesh = this.getMeshByName( m ) as Mesh;
                if( mesh ) mesh.setEnabled( status );
            } );
        }
       

        if( 'metal' === roofType ) {
            this.setEnableRoofComponentByKeys( [ 'roof_shingle_obj', 'porch_roof_shingle_obj', 'roof_metal_horizontal_cover_obj', 'roof_metal_horizontal_obj', 'roof_permatile_lean_to_metal_cover_obj', 'roof_permatile_lean_to_metal_clone_obj', 'roof_permatile_aframe_metal_clone_obj', 'roof_permatile_aframe_metal_cover_obj', 'roof_standing_seam_lean_to_metal_clone_obj', 'roof_standing_seam_lean_to_metal_cover_obj', 'roof_standing_seam_metal_aframe_clone_obj', 'roof_standing_seam_metal_aframe_cover_obj', 'roof_metal_horizontal_clone_obj', 'roof_metal_boxeave_obj' ], false );
            this.setEnableRoofComponentByKeys( [ 'boxeve_obj', 'roof_metal_obj', 'roof_metal_cover_obj', 'roof_metal_clone_obj', 'porch_roof_metal_clone_obj', 'porch_roof_metal_obj', 'hat_channel_obj', 'roof_metal_vertical_obj' ], true );

            if( this.isCarport() ){
                const hatChannel = this.getMeshByName( 'hat_channel_obj' );
                if( hatChannel ) {
                    hatChannel.setEnabled( false );
                }
            }
        } else if( ( 'shingle' === roofType || 'shingle_3tab' === roofType ) ) {
            this.setEnableRoofComponentByKeys( [ 'boxeve_obj', 'roof_metal_obj', 'roof_metal_cover_obj', 'porch_roof_metal_obj', 'roof_metal_horizontal_cover_obj', 'roof_metal_horizontal_obj', 'roof_metal_clone_obj', 'porch_roof_metal_clone_obj', 'roof_permatile_lean_to_metal_cover_obj', 'roof_permatile_lean_to_metal_clone_obj', 'roof_permatile_aframe_metal_clone_obj', 'roof_permatile_aframe_metal_cover_obj', 'roof_standing_seam_lean_to_metal_clone_obj', 'roof_standing_seam_lean_to_metal_cover_obj', 'roof_standing_seam_metal_aframe_clone_obj', 'roof_standing_seam_metal_aframe_cover_obj', 'roof_metal_vertical_obj' ], false );
            this.setEnableRoofComponentByKeys( [ 'porch_roof_shingle_obj', 'roof_shingle_obj' ], true );
        } else if( 'metal_horizontal' === roofType  ) {        
            if( this.isCarport() ){
                if( 'carport_lean_to_standalone' === this._shedType ) {
                    this.setEnableRoofComponentByKeys( [ 'roof_metal_horizontal_obj', 'roof_metal_obj', 'roof_shingle_obj', 'porch_roof_shingle_obj', 'roof_metal_clone_obj', 'porch_roof_metal_clone_obj', 'roof_permatile_lean_to_metal_cover_obj', 'roof_permatile_lean_to_metal_clone_obj', 'roof_permatile_aframe_metal_clone_obj', 'roof_permatile_aframe_metal_cover_obj', 'roof_standing_seam_lean_to_metal_clone_obj', 'roof_standing_seam_lean_to_metal_cover_obj', 'roof_standing_seam_metal_aframe_clone_obj', 'roof_standing_seam_metal_aframe_cover_obj', 'hat_channel_obj' ], false );
                    this.setEnableRoofComponentByKeys( [ 'roof_metal_horizontal_clone_obj', 'roof_metal_boxeave_obj' ], true );
                } else {
                    this.setEnableRoofComponentByKeys( [ 'roof_shingle_obj', 'porch_roof_shingle_obj', 'roof_metal_clone_obj', 'porch_roof_metal_clone_obj', 'roof_permatile_lean_to_metal_cover_obj', 'roof_permatile_lean_to_metal_clone_obj', 'roof_permatile_aframe_metal_clone_obj', 'roof_permatile_aframe_metal_cover_obj', 'roof_standing_seam_lean_to_metal_clone_obj', 'roof_standing_seam_lean_to_metal_cover_obj', 'roof_standing_seam_metal_aframe_clone_obj', 'roof_standing_seam_metal_aframe_cover_obj', 'hat_channel_obj' ], false );
                    this.setEnableRoofComponentByKeys( [ 'roof_metal_obj', 'roof_metal_horizontal_clone_obj' ], true );
                }
            }else{
                this.setEnableRoofComponentByKeys( [ 'boxeve_obj', 'roof_metal_obj', 'roof_metal_cover_obj', 'porch_roof_metal_obj', 'roof_shingle_obj', 'porch_roof_shingle_obj', 'roof_metal_clone_obj', 'porch_roof_metal_clone_obj', 'roof_permatile_lean_to_metal_cover_obj', 'roof_permatile_lean_to_metal_clone_obj', 'roof_permatile_aframe_metal_clone_obj', 'roof_permatile_aframe_metal_cover_obj', 'roof_standing_seam_lean_to_metal_clone_obj', 'roof_standing_seam_lean_to_metal_cover_obj', 'roof_standing_seam_metal_aframe_clone_obj', 'roof_standing_seam_metal_aframe_cover_obj' ], false );
                this.setEnableRoofComponentByKeys( [ 'roof_metal_horizontal_cover_obj', 'roof_metal_horizontal_obj' ], true );
            }
        } else if( 'permatile_metal' === roofType ) {
            this.setEnableRoofComponentByKeys( [ 'boxeve_obj', 'roof_metal_obj', 'roof_metal_cover_obj', 'porch_roof_metal_obj', 'roof_shingle_obj', 'porch_roof_shingle_obj', 'roof_metal_horizontal_cover_obj', 'roof_metal_horizontal_obj', 'roof_metal_clone_obj', 'porch_roof_metal_clone_obj', 'roof_standing_seam_lean_to_metal_clone_obj', 'roof_standing_seam_lean_to_metal_cover_obj', 'roof_standing_seam_metal_aframe_clone_obj', 'roof_standing_seam_metal_aframe_cover_obj' ], false );
            if( 'flat' === this.getRoofShape() ) {
                this.setEnableRoofComponentByKeys( [ 'roof_permatile_lean_to_metal_clone_obj', 'roof_permatile_lean_to_metal_cover_obj' ], true );
            } else {
                this.setEnableRoofComponentByKeys( [ 'roof_permatile_aframe_metal_clone_obj', 'roof_permatile_aframe_metal_cover_obj' ], true );
            }
        } else if( 'standing_seam' === roofType ) {
            this.setEnableRoofComponentByKeys( [ 'boxeve_obj', 'roof_metal_obj', 'roof_metal_cover_obj', 'porch_roof_metal_obj', 'roof_shingle_obj', 'porch_roof_shingle_obj', 'roof_metal_horizontal_cover_obj', 'roof_metal_horizontal_obj', 'roof_metal_clone_obj', 'porch_roof_metal_clone_obj', 'roof_permatile_lean_to_metal_cover_obj', 'roof_permatile_lean_to_metal_clone_obj', 'roof_permatile_aframe_metal_clone_obj', 'roof_permatile_aframe_metal_cover_obj' ], false );
            if( 'flat' === this.getRoofShape() ) {
                this.setEnableRoofComponentByKeys( [ 'roof_standing_seam_lean_to_metal_cover_obj', 'roof_standing_seam_lean_to_metal_clone_obj' ], true );
            } else {
                this.setEnableRoofComponentByKeys( [ 'roof_standing_seam_metal_aframe_cover_obj', 'roof_standing_seam_metal_aframe_clone_obj' ], true );
            }
        }
        this.handleDormerOptionMesh();
    }

    updateTextureFileRoof = () => {
        let roofType = this.getRoofType();
        if( undefined === roofType ) {
            return;
        }
        const referenceRoof = jQuery( `[data-roof-type="${roofType}"]` ).attr( 'data-reference-key' );
        const dimensional   = jQuery( `[data-roof-type="${roofType}"]` ).attr( 'data-dimensional' );
        if( '2d' !== dimensional ) return;
        let defaultMaterialName = `roof_${roofType}_mat`;
        
        if( undefined !== referenceRoof && '' !== referenceRoof ) {
            defaultMaterialName  = `roof_${referenceRoof}_mat`;
        }
        
        if( 'shingle_3tab' === roofType ) {
            defaultMaterialName  = `roof_shingle_mat`;
        }
        const { defaultDiffuse, defaultBump, defaultSpecular, uScale, vScale, specularPower, opacityTexture } = this.getSidingMaterialData( jQuery( `#roof-material-option li[data-roof-type=${roofType}]` ) );
        
        let matKeys     = [];
        const elColor   = jQuery( `.ssb-configurator-color.roof [data-roof-type="${roofType}"]` );
        if( elColor.length && elColor.attr( 'data-material' ) ) {
            matKeys = elColor.attr( 'data-material' ).split( '|' );
        }
       
        if( 0 === matKeys.length ) {
            matKeys.push( defaultMaterialName );
        }

        for( let i = 0; i < matKeys.length; i++ ) {
                let matName  = matKeys[i].trim();
                if( 'roof_metal_mat' === matName ) continue;
                let materials = this.getMaterialByName( matName );
                if( ! materials ) continue;
                materials.forEach( material => {
                    if( defaultDiffuse ) {
                        if( material.diffuseTexture ) {
                            material.diffuseTexture.updateURL( defaultDiffuse );
                        } else {
                            material.diffuseTexture = new Texture( defaultDiffuse, this._scene );
                        }
                        if( this._initialUVs[ matName ] && this._initialUVs[ matName ]['diffuseTexture'] ) {
                            this._initialUVs[ matName ]['diffuseTexture']['texture'] = defaultDiffuse;
                        }
                        
                    }
    
                    if( defaultBump ) {
                        if( material.bumpTexture ) {
                            material.bumpTexture.updateURL( defaultBump );
                        } else {
                            material.bumpTexture = new Texture( defaultBump, this._scene );
                        }
                        if( this._initialUVs[ matName ] && this._initialUVs[ matName ]['bumpTexture'] ) {
                            this._initialUVs[ matName ]['bumpTexture']['texture'] = defaultBump;
                        }
                    }
    
                    if( defaultSpecular ) {
                        if( material.specularTexture ) {
                            material.specularTexture.updateURL( defaultSpecular );
                        } else {
                            material.specularTexture = new Texture( defaultSpecular, this._scene );
                        }
                        if( this._initialUVs[ matName ] && this._initialUVs[ matName ]['specularTexture']) {
                            this._initialUVs[ matName ]['specularTexture']['texture'] = defaultSpecular;
                        }
                    }
    
                    if( opacityTexture ) {
                        if( material.opacityTexture ) {
                            material.specularTexture.updateURL( opacityTexture );
                        } else {
                            material.opacityTexture = new Texture( opacityTexture, this._scene );
                        }
                        material.opacityTexture.hasAlpha        = true;
                        material.opacityTexture.getAlphaFromRGB = true;
                    } else {
                        material.opacityTexture = null;
                    }
    
                    if( specularPower ) {
                        material.specularPower = specularPower;
                    }
                
                    if( uScale ) {
                        if( this._initialUVs[ matName ]['diffuseTexture'] ) {
                            this._initialUVs[ matName ]['diffuseTexture']['uScale'] = uScale;
                        }
                        if( this._initialUVs[ matName ]['bumpTexture'] ) {
                            this._initialUVs[ matName ]['bumpTexture']['uScale'] = uScale;
                        }
                        if( this._initialUVs[ matName ]['specularTexture'] ) {
                            this._initialUVs[ matName ]['specularTexture']['uScale'] = uScale;
                        }
                    }
    
                    if( vScale ) {
                        if( this._initialUVs[ matName ]['diffuseTexture'] ) {
                            this._initialUVs[ matName ]['diffuseTexture']['vScale'] = vScale;
                        }
                        if( this._initialUVs[ matName ]['bumpTexture'] ) {
                            this._initialUVs[ matName ]['bumpTexture']['vScale'] = vScale;
                        }
                        if( this._initialUVs[ matName ]['specularTexture'] ) {
                            this._initialUVs[ matName ]['specularTexture']['vScale'] = vScale;
                        }
                    }
                } );
        }
        
        this.adjustUVs( true );

    }

    private getRoofMeshFace = () => {
        if( 'open_gable' === this.getRoofShape() || 'box_gable' === this.getRoofShape() ) {
            return [
                [ 2, 3, 0, 1 ],
                [ 3, 2, 5, 4 ],
                [ 9, 8, 7, 6 ],
                [ 8, 9, 10, 11 ],
                [ 11, 5, 2, 8 ],
                [ 8, 2, 1, 7 ],
                [ 9, 3, 4, 10 ],
                [ 6, 0, 3, 9 ],
                [ 11, 10, 4, 5 ],
                [ 6, 7, 1, 0 ],
            ];
        }

        return [];
    }

    private drawCelling = args => {
        const { vertexCelling, key } = args;
        const ceilingName   = `${key}_celling_mesh`;
        const oldCelling    = this.getMeshByName( ceilingName );

        if( oldCelling ) oldCelling.dispose();

        let cellingMat      = this._scene.getMaterialByName( 'celling_mat' ) as StandardMaterial;
        const trimMat       = this._scene.getMaterialByName( 'trim_mat' ) as StandardMaterial;
        const vertex1       = [];
        const vertex2       = [];
        const roofDepth     = 0.03; // default in design 0.028
        const face          = this.getRoofMeshFace();

        vertexCelling.forEach( point => {
            vertex1.push( point.asArray() );
            vertex2.push( point.add( new Vector3( 0, -roofDepth, 0 ) ).asArray() );
        } );

        if( ! cellingMat ) {
            if( ! trimMat ) {
                cellingMat = new StandardMaterial( 'celling_mat', this._scene );
            } else {
                cellingMat = trimMat.clone( 'celling_mat' );
                if( cellingMat.ambientTexture ) cellingMat.ambientTexture = null;
            }
        }

        const cellingMesh       = MeshBuilder.CreatePolyhedron( ceilingName, { custom: { vertex: [...vertex1, ...vertex2], face }, updatable: true }, this._scene );
        cellingMesh.isPickable  = false;
        cellingMesh.material    = cellingMat;

        this.setInitialUVs( cellingMat );
    }

    private drawRoof2D = args => {
        const { vertexRoof, key, meshWidth, meshLength, roofKey } = args;
        const customKey     = roofKey ? roofKey : this._roofType;
        const defaultMat    = `${customKey}_${key}_default_mat`;
        const roofMatName   = `${customKey}_${key}_custom_mat`;
        const roofMeshName  = `${customKey}_${key}_roof_mesh`;
        const oldRoof       = this.getMeshByName( roofMeshName );

        if( oldRoof ) {
            oldRoof.material.dispose();
            oldRoof.dispose();
        }

        const { defaultDiffuse, defaultBump, defaultSpecular, uScale: initUScale, vScale: initVScale, specularPower } = this.getSidingMaterialData( jQuery( `#roof-material-option li[data-roof-type=${customKey}]` ) );

        let multiMaterial   = this._scene.getMaterialByName( 'multiMaterialRoof' ) as MultiMaterial;
        let roofMat         = this._scene.getMaterialByName( roofMatName ) as StandardMaterial;
        let roofMatDefault  = this._scene.getMaterialByName( defaultMat ) as StandardMaterial;
        const vScale        = helpers.toFixed3( meshWidth / ( 8 / initVScale ) ); // 8 is default width of material when design create. this._sidingVscale is init V scale from network
        const uScale        = helpers.toFixed3( meshLength / ( 8 / initUScale ) );
        const roofDepth     = 0.03; // default in design 0.028
        const vertex1       = [];
        const vertex2       = [];
        const face          = this.getRoofMeshFace();

        vertexRoof.forEach( point => {
            vertex1.push( point.add( new Vector3( 0, roofDepth, 0 ) ).asArray() );
            vertex2.push( point.asArray() );
        } );

        if( roofMat ) roofMat.dispose();
        roofMat = this.createMaterial( 
            roofMatName, 
            defaultDiffuse,
            defaultBump,
            defaultSpecular,
            { type: 'color', code: '#FFFFFF' }
        );

        if( roofMatDefault ) roofMatDefault.dispose();
        roofMatDefault = roofMat.clone( defaultMat );

        if ( ! multiMaterial ) {
            multiMaterial = new MultiMaterial( 'multiMaterialRoof', this._scene );
        }

        const roofMesh          = MeshBuilder.CreatePolyhedron( roofMeshName, { custom: { vertex: [...vertex1, ...vertex2], face }, updatable: true }, this._scene );
        roofMesh.isPickable     = false;
        roofMat.specularPower   = specularPower;
        
        this.updateUVScale( roofMat, uScale, vScale, 1, 1 );
        this.updateUVScale( roofMatDefault, uScale, vScale, 1, 1 );
        multiMaterial.subMaterials.push( roofMatDefault );
        multiMaterial.subMaterials.push( roofMat );
        multiMaterial.subMaterials.push( roofMat );

        roofMesh.material       = multiMaterial;
        const verticesCount     = roofMesh.getTotalVertices();

        new SubMesh( 1, 0, verticesCount, 0, 6, roofMesh );
        new SubMesh( 1, 0, verticesCount, 6, 6, roofMesh );
        new SubMesh( 2, 0, verticesCount, 12, 6, roofMesh );
        new SubMesh( 2, 0, verticesCount, 18, 6, roofMesh );

        if( 'main' === key ) {
            this.updateHoleRoof( 'roof_left' );
        }

        this.setInitialUVs( roofMat );
        this._roofMaterial          = helpers.checkAndPushKeyToArray( this._roofMaterial, roofMatName );
        this._roofMaterial          = helpers.checkAndPushKeyToArray( this._roofMaterial, defaultMat );
        this._roofMesh[customKey]   = helpers.checkAndPushKeyToArray( this._roofMesh[customKey], roofMeshName );

        return roofMesh;
    }

    private drawRoof = () => {
        if( 'true' !== this.getSettingData( 'apply_new_roof' ) ) {
            return
        }

        const bones         = [ 'r_f_r1', 'r_b_r1', 'r_b_t', 'r_f_t', 'r_f_l1', 'r_b_l1' ];
        const vertexRoof    = [];
        const vertexCelling = [];

        bones.forEach( key => {
            if( this._bones[key] ) {
                const customPoint = this._bones[`${key}_01`] ? this._bones[`${key}_01`].position : Vector3.Zero();
                vertexRoof.push( this._bones[key].position.add( customPoint ) );
                vertexCelling.push( this._bones[key].position );
            }
        } );

        this.drawCelling( { key: 'main', vertexCelling } );
        
        if( 'shingle' === this._roofType || 'metal_horizontal' === this._roofType ) {
            const meshWidth     = helpers.toFixed3( Vector3.Distance( this._bones['r_b_l1'].position, this._bones['r_b_t'].position ) );
            const meshLength    = helpers.toFixed3( Vector3.Distance( this._bones['r_b_l1'].position, this._bones['r_f_l1'].position ) );

            this.drawRoof2D( { key: 'main', vertexRoof, meshWidth, meshLength } );

        } else if( 'metal' === this._roofType ) {
            const roofRight = {
                name:       'roof_right_clone',
                meshName:   `roof_${this._roofType}_clone_obj`,
                startPoint: this._bones['r_b_r1_01'].position ? this._bones['r_b_r1'].position.add( this._bones['r_b_r1_01'].position ) : this._bones['r_b_r1'].position,
                endPoint:   this._bones['r_f_r1_01'].position ? this._bones['r_f_r1'].position.add( this._bones['r_f_r1_01'].position ) : this._bones['r_f_r1'].position,
                startScale: this._bones['r_b_l1_01'].position ? this._bones['r_b_l1'].position.add( this._bones['r_b_l1_01'].position ) : this._bones['r_b_l1'].position,
                endScale:   this._bones['r_b_t_01'].position ? this._bones['r_b_t'].position.add( this._bones['r_b_t_01'].position ) : this._bones['r_b_t'].position,
                rotationY:  0,
                bones:      [ 'r_b_r1', 'r_b_t', 'r_f_t', 'r_f_r1' ]
            };

            const roofRightMesh = this.doCloneRoofMetal( roofRight );
            let roofLeftMesh    = this.getMeshByName( 'roof_left_clone' );
            if( roofLeftMesh ) roofLeftMesh.dispose();
            
            roofLeftMesh            = roofRightMesh.clone( 'roof_left_clone' )
            roofLeftMesh.scaling.x  = -1;

            const mergeName     = `${this._roofType}_main_roof_mesh`;
            const meshMerge     = this._scene.getMeshByName( mergeName );
            if( meshMerge ) meshMerge.dispose();
            
            const cloneMeshs    = [ roofRightMesh, roofLeftMesh ];
            const mergeMesh     = Mesh.MergeMeshes( cloneMeshs );
            mergeMesh.name      = mergeName;
        
            this._meshBlurForInterior       = helpers.checkAndPushKeyToArray( this._meshBlurForInterior, mergeName );
            this._roofMesh[this._roofType]  = helpers.checkAndPushKeyToArray( this._roofMesh[this._roofType], mergeName );
        }
    }

    updateHoleRoof = side => {
        if( 'roof_left' !== side && 'roof_right' !== side ) return;

        const roofMeshName      = `${this._roofType}_main_roof_mesh`;
        const roofCloneMeshName = `${roofMeshName}_clone`;
        const ceilingName       = 'main_celling_mesh';
        const ceilingCloneName  = `${ceilingName}_clone`;

        const roofMesh          = this.getMeshByName( roofMeshName );
        const roofMeshClone     = this.getMeshByName( roofCloneMeshName );
        const cellingMesh       = this.getMeshByName( ceilingName );
        const cellingCloneMesh  = this.getMeshByName( ceilingCloneName );
        const dormers           = this.getComponentByType( 'dormer' );

        if( ! roofMesh || ! cellingMesh ) return;

        if( roofMeshClone ) roofMeshClone.dispose();
        if( cellingCloneMesh ) cellingCloneMesh.dispose();

        if( 0 === dormers.length ) {
            roofMesh.setEnabled( true );
            cellingMesh.setEnabled( true );

            return;
        }

        const roofMeshCLCSG = CSG.FromMesh( cellingMesh );
        const roofMeshCSG   = CSG.FromMesh( roofMesh );

        for( let i = 0; i < dormers.length; i++ ) {
            const timeStamp         = dormers[i].key.split( '_' ).pop();
            const boundingBoxMeshes = this.getMeshByName( `${dormers[i].name}_intersect_obj_${timeStamp}` );
            if( ! boundingBoxMeshes ) continue;

            const boundingBoxMeshesCSG = CSG.FromMesh( boundingBoxMeshes );

            roofMeshCSG.subtractInPlace( boundingBoxMeshesCSG );
            roofMeshCLCSG.subtractInPlace( boundingBoxMeshesCSG );
        }

        const roofHoleMesh          = roofMeshCSG.toMesh( roofCloneMeshName, roofMesh.material, this._scene, true );
        const ceilingHoleMesh       = roofMeshCLCSG.toMesh( ceilingCloneName, cellingMesh.material, this._scene );
        roofHoleMesh.isPickable     = false;
        ceilingHoleMesh.isPickable  = false

        cellingMesh.setEnabled( false );
        roofMesh.setEnabled( false );

        this._roofMesh[this._roofType] = helpers.checkAndPushKeyToArray( this._roofMesh[this._roofType], roofCloneMeshName );
        this._roofMesh[this._roofType] = helpers.checkAndPushKeyToArray( this._roofMesh[this._roofType], ceilingCloneName );
    }

    private makeIntersection( stencilRef: number, sidingMesh: Mesh, intersectMesh: Mesh, displayMesh: Mesh ) {
        let _this = this;
        _this._engine.setStencilBuffer(true);

        if (intersectMesh != undefined) {
            let intersectGroupId = 1;
            intersectMesh.renderingGroupId = intersectGroupId;
            _this._scene.setRenderingAutoClearDepthStencil(intersectGroupId, true, false, false);
        }
        
        if (displayMesh != undefined) {
            let displayGroupId = 2;
            displayMesh.renderingGroupId = displayGroupId;
            _this._scene.setRenderingAutoClearDepthStencil(displayGroupId, true, false, false);
        }

        const sidingMeshes = [ 'siding_f_obj', 'siding_b_obj', 'siding_l_obj', 'siding_r_obj', 'porch_f_obj', 'wire_frame_lr_obj', 'wire_frame_m_obj', 'door_wire_8ft_double_obj', 'door_wire_8ft_single_obj', 'door_wire_8ft_single_left_obj', 'door_wire_8ft_single_right_obj' ];
        for( let i = 0; i < sidingMeshes.length; i++ ) {
            let sidingMesh = _this._scene.getMeshByName( sidingMeshes[i] ) as Mesh;
            if( undefined !== sidingMesh && null !== sidingMesh ) {
                sidingMesh.renderingGroupId = 1;

                sidingMesh.onBeforeRenderObservable.add(() => {
                    _this._engine.setStencilOperationPass(Engine.REPLACE);
                    _this._engine.setStencilMask(0xFF);
                    _this._engine.setStencilFunction(Engine.ALWAYS);
                });                
            }
        }

        const componentMeshes = [ 'frame_obj', 'roof_obj', 'roof_shingle_obj', 'roof_metal_obj', 'runner_obj', 'roof_metal_horizontal_obj', 'baluster_obj', 'roof_metal_clone_obj', 'roof_porch_metal_clone_obj' ];
        for( let i = 0; i < componentMeshes.length; i++ ) {
            let componentMesh = _this._scene.getMeshByName( componentMeshes[i] ) as Mesh;
            if( undefined !== componentMesh && null !== componentMesh ) {
                componentMesh.onBeforeRenderObservable.add(() => {
                    _this._engine.setStencilOperationPass(Engine.REPLACE);
                    _this._engine.setStencilMask(0xFF);
                    _this._engine.setStencilFunction(Engine.ALWAYS);
                });
            }
        }

        sidingMesh.onBeforeRenderObservable.add(() => {
            _this._engine.setStencilOperationPass(Engine.REPLACE);
            _this._engine.setStencilMask(0xFF);
            _this._engine.setStencilFunction(Engine.ALWAYS);
            _this._engine.setStencilFunctionReference(stencilRef);
        });

        if (intersectMesh != undefined) {
            intersectMesh.onBeforeRenderObservable.add(() => {
                _this._engine.setColorWrite(false);
                _this._engine.setDepthFunctionToGreaterOrEqual();
                _this._engine.setStencilOperationPass(Engine.DECR);
                _this._engine.setStencilFunction(Engine.EQUAL);
                _this._engine.setStencilFunctionReference(stencilRef);
            });
            intersectMesh.onAfterRenderObservable.add(() => {
                _this._engine.setColorWrite(true);
                _this._engine.setDepthFunctionToLessOrEqual();
            });
        }

        if (displayMesh != undefined) {
            displayMesh.onBeforeRenderObservable.add(() => {
                _this._engine.setDepthFunctionToGreaterOrEqual();
                _this._engine.setStencilMask(0x0);
                _this._engine.setStencilFunction(Engine.EQUAL);
                _this._engine.setStencilFunctionReference(stencilRef);
            });

            displayMesh.onAfterRenderObservable.add(() => {
                _this._engine.setDepthFunctionToLessOrEqual();
            });
        }

    }

    private filterByValue(array, key, value) {
        
        return array.filter(obj=>obj[key].includes(value))[0];
    }

    handleBonesScreenHouse = () => {
        if( 'structure_greenhouse_fiberglass' !== this._shedType ) {
            return;
        }

        const bones         = this._bones;
        const frontDoors    = helpers.objectFilter( this._walls['front']['items'], component => 'door' !== component.type );
        const backDoors     = helpers.objectFilter( this._walls['back']['items'], component => 'door' !== component.type );
        const fHMesh        = this.getMeshByName( 'frame_horizontal_f_obj' );
        const bHMesh        = this.getMeshByName( 'frame_horizontal_b_obj' );
        const fDoorMesh     = this.getMeshByName( 'frame_door_f_obj' );
        const bDoorMesh     = this.getMeshByName( 'frame_door_b_obj' );

        if( ! helpers.isEmptyObject( frontDoors ) ) {
            const keys  = Object.keys( frontDoors );
            const fDoor = frontDoors[ keys[0] ];

            if( bones['frame_f_l1'] ) bones['frame_f_l1'].setPosition( new Vector3( - fDoor.width / 2, bones['frame_f_l1'].position.y, bones['frame_f_l1'].position.z ) );
            if( bones['frame_f_l2'] ) bones['frame_f_l2'].setPosition( new Vector3( - fDoor.width / 2, bones['frame_f_l2'].position.y, bones['frame_f_l2'].position.z ) );
            if( bones['frame_f_r1'] ) bones['frame_f_r1'].setPosition( new Vector3( fDoor.width / 2, bones['frame_f_r1'].position.y, bones['frame_f_r1'].position.z ) );
            if( bones['frame_f_r2'] ) bones['frame_f_r2'].setPosition( new Vector3( fDoor.width / 2, bones['frame_f_r2'].position.y, bones['frame_f_r2'].position.z ) );

            if( bones['frame_door_f_l1'] ) bones['frame_door_f_l1'].setPosition( new Vector3( - fDoor.width / 2, bones['frame_door_f_l1'].position.y, bones['frame_door_f_l1'].position.z ) );
            if( bones['frame_door_f_l2'] ) bones['frame_door_f_l2'].setPosition( new Vector3( - fDoor.width / 2, bones['frame_door_f_l2'].position.y, bones['frame_door_f_l2'].position.z ) );
            if( bones['frame_door_f_r1'] ) bones['frame_door_f_r1'].setPosition( new Vector3( fDoor.width / 2, bones['frame_door_f_r1'].position.y, bones['frame_door_f_r1'].position.z ) );
            if( bones['frame_door_f_r2'] ) bones['frame_door_f_r2'].setPosition( new Vector3( fDoor.width / 2, bones['frame_door_f_r2'].position.y, bones['frame_door_f_r2'].position.z ) );

            this.removeClone( 'frame_window_f_clone_obj' );

            if( fHMesh ) fHMesh.setEnabled( false );
            if( fDoorMesh ) fDoorMesh.setEnabled( true );
            
        } else {
            if( bones['frame_f_l1'] ) bones['frame_f_l1'].setPosition( new Vector3( 0, bones['frame_f_l1'].position.y, bones['frame_f_l1'].position.z ) );
            if( bones['frame_f_l2'] ) bones['frame_f_l2'].setPosition( new Vector3( 0, bones['frame_f_l2'].position.y, bones['frame_f_l2'].position.z ) );
            if( bones['frame_f_r1'] ) bones['frame_f_r1'].setPosition( new Vector3( 0, bones['frame_f_r1'].position.y, bones['frame_f_r1'].position.z ) );
            if( bones['frame_f_r2'] ) bones['frame_f_r2'].setPosition( new Vector3( 0, bones['frame_f_r2'].position.y, bones['frame_f_r2'].position.z ) );

            this.instanceMesh( 'frame_window_f_clone_obj', 48, 'b_f_l1', 'b_f_r1' );

            if( fHMesh ) fHMesh.setEnabled( true );
            if( fDoorMesh ) fDoorMesh.setEnabled( false );
        }

        if( ! helpers.isEmptyObject( backDoors ) ) {
            const keys  = Object.keys( backDoors );
            const fDoor = backDoors[ keys[0] ];

            if( bones['frame_b_l1'] ) bones['frame_b_l1'].setPosition( new Vector3( - fDoor.width / 2, bones['frame_b_l1'].position.y, bones['frame_b_l1'].position.z ) );
            if( bones['frame_b_l2'] ) bones['frame_b_l2'].setPosition( new Vector3( - fDoor.width / 2, bones['frame_b_l2'].position.y, bones['frame_b_l2'].position.z ) );
            if( bones['frame_b_r1'] ) bones['frame_b_r1'].setPosition( new Vector3( fDoor.width / 2, bones['frame_b_r1'].position.y, bones['frame_b_r1'].position.z ) );
            if( bones['frame_b_r2'] ) bones['frame_b_r2'].setPosition( new Vector3( fDoor.width / 2, bones['frame_b_r2'].position.y, bones['frame_b_r2'].position.z ) );

            if( bones['frame_door_b_l1'] ) bones['frame_door_b_l1'].setPosition( new Vector3( - fDoor.width / 2, bones['frame_door_b_l1'].position.y, bones['frame_door_b_l1'].position.z ) );
            if( bones['frame_door_b_l2'] ) bones['frame_door_b_l2'].setPosition( new Vector3( - fDoor.width / 2, bones['frame_door_b_l2'].position.y, bones['frame_door_b_l2'].position.z ) );
            if( bones['frame_door_b_r1'] ) bones['frame_door_b_r1'].setPosition( new Vector3( fDoor.width / 2, bones['frame_door_b_r1'].position.y, bones['frame_door_b_r1'].position.z ) );
            if( bones['frame_door_b_r2'] ) bones['frame_door_b_r2'].setPosition( new Vector3( fDoor.width / 2, bones['frame_door_b_r2'].position.y, bones['frame_door_b_r2'].position.z ) );            

            this.removeClone( 'frame_window_b_clone_obj' );

            if( bHMesh ) bHMesh.setEnabled( false );
            if( bDoorMesh ) bDoorMesh.setEnabled( true );

        } else {

            if( bones['frame_b_l1'] ) bones['frame_b_l1'].setPosition( new Vector3( 0, bones['frame_b_l1'].position.y, bones['frame_b_l1'].position.z ) );
            if( bones['frame_b_l2'] ) bones['frame_b_l2'].setPosition( new Vector3( 0, bones['frame_b_l2'].position.y, bones['frame_b_l2'].position.z ) );
            if( bones['frame_b_r1'] ) bones['frame_b_r1'].setPosition( new Vector3( 0, bones['frame_b_r1'].position.y, bones['frame_b_r1'].position.z ) );
            if( bones['frame_b_r2'] ) bones['frame_b_r2'].setPosition( new Vector3( 0, bones['frame_b_r2'].position.y, bones['frame_b_r2'].position.z ) );

            this.instanceMesh( 'frame_window_b_clone_obj', 48, 'b_b_l1', 'b_b_r1' );

            if( bHMesh ) bHMesh.setEnabled( true );
            if( bDoorMesh ) bDoorMesh.setEnabled( false );
        }
    }

    hideComponentMeshes(meshes) {
        let _this = this;

        if( -1 === window.location.href.indexOf( 'helmuthbuilders.shedpro' ) ) {
            [ '_badge_obj', '_bage_obj' ].forEach( (e) => {
                let badgeMesh = _this.filterByValue(meshes, 'name', e) as Mesh;
                if (badgeMesh != undefined && badgeMesh != null) {
                    badgeMesh.dispose();
                }
            });
        }

        const boundingBoxMeshes = _this.filterMeshes( meshes, 'name', '_bb_obj' );
        for( let i = 0; i < boundingBoxMeshes.length; i++ ) {
            boundingBoxMeshes[i].visibility = 0;
        }
      
        const enableDoorSlab = parseInt( this._canvas.getAttribute( 'data-enable-door-slab' ) );
        if( 1 !== enableDoorSlab ) {
            let sidingObj = _this.filterByValue( meshes, 'name', '_siding_obj' ) as Mesh;
            if( sidingObj ) sidingObj.setEnabled( false );
        }
    }

    rotateCameraBySide( side ) {
        let _this = this;

        if( _this._isInterior ) return;

        let radius = _this._camera.radius;
        let x = _this._camera.position.x;
        let y = _this._camera.position.y;
        let z = _this._camera.position.z;
        let position = new Vector3( x, y, z );

        switch (side) {
            case "front":
                position = new Vector3( x/radius, y, radius );
                break;
            case "back":
                position = new Vector3( x/radius, y, -radius );
                break;
            case "left":
                position = new Vector3( -radius, y, z/radius );
                break;
            case "roof_left":
                position = new Vector3( -radius, y, z/radius );
                break;
            case "right":
                position = new Vector3( radius, y, z/radius );
                break;
            case "roof_right":
                position = new Vector3( radius, y, z/radius );
                break;
            default:
                break;
        }

        _this._camera.position = position;
    }

    getSideFaceToCamera( componentType ) {
        let _this = this;

        let side = "";
        let camera = _this._camera as ArcRotateCamera;
        let cameraPosition  = camera.position;
        let cameraRadius    = camera.radius;
        let cameraZ         = cameraPosition.z;
        if( _this.isFrontPorch() ){
            cameraZ = cameraPosition.z - _this._porchWidth / 2
        }

        let cameraAngle = { x: cameraPosition.x / cameraRadius, y: cameraPosition.y / cameraRadius, z: cameraZ / cameraRadius};
        if( cameraAngle.y > 1 ){
            if( _this._isInterior ) {
                side = "back";
            } else {
                side = "roof";
            }
        }else{
            if ( (cameraAngle.x < -0.75 && cameraAngle.x > -1) && (cameraAngle.z < 0.75 && cameraAngle.z > -0.75) ) {
                side = "left";
            }

            if ( (cameraAngle.x > 0.75 && cameraAngle.x < 1) && (cameraAngle.z < 0.75 && cameraAngle.z > -0.75) ) {
                side = "right";
            }

            if ( (cameraAngle.x < 0.75 && cameraAngle.x > -0.75) && (cameraAngle.z < 1 && cameraAngle.z > 0.75) ) {
                side = "front";
            }

            if ( (cameraAngle.x < 0.75 && cameraAngle.x > -0.75) && (cameraAngle.z < -0.75 && cameraAngle.z > -1) ) {
                side = "back";
            }
        }

        return _this.convertSideForInterior( componentType, side );
    }

    /**
     * Check component is interior
     * 
     * @param type 
     * @returns bool
     */
    isInteriorComponent( type : string ) {
        return -1 !== interiors.indexOf( type );
    }

    /**
     * Convert side from Exterior to Interior
     * 
     * @param side String
     */
    convertSideForInterior( componentType: string, side : string ){
        if( ! this.isInteriorComponent( componentType ) ) return side;

        switch( side ) {
            case 'front':
                side = 'back'
                break;

            case 'back':
                side = 'front'
                break;

            case 'left':
                side = 'right'
                break;

            case 'right':
                side = 'left'
                break;
        
            default:
                break;
        }

        return side;
    }

    /**
     * Run when pick mesh on desktop
     * 
     * @param pickResult The result of the mesh picked
     */
    handlePickMesh = ( pickResult ) => {
        if( null === pickResult.pickedMesh || null === pickResult.pickedMesh.parent || null === pickResult.pickedMesh.parent.metadata ) {
            return;
        }

        let { key, side, type, name } = pickResult.pickedMesh.parent.metadata;

        if( 'back' === pickResult.pickedMesh.name ) {
            return;
        }

        if( 'structure_rancher' === this._shedType && ( 'top_front' === side || 'top_back' === side ) ) {
            return;   
        }

        if( undefined === key ) {
            return;
        }

        if( 'structure_monitorbarns_helmuth' === this._shedType && 'cupola' !== type ) {
            return;
        }
        if( this._isDrag ) {
            return this._isDrag = false;
        }

        if( this.ignoreHandleTackDoor( side ) ) {
            return;
        }

        if ( ! this.isMoveAlongFloor( name ) ) {
            jQuery( '[data-component-key="'+key+'"] .edit-component' ).trigger( 'click' );
        }
    }

    /**
     * Add button to edit component on the mobile
     * 
     * @param {Mesh} firstMesh          The main of component
     * @param {String} type             Component type (door|windows)
     * @param {Number} componentWidth   Component width
     * @param {Number} componentHeight  Component height
     * @param {String} side             front|back|left|right
     */
    addEditButton = ( { component, firstMesh, type, componentName, componentWidth, componentDepth, componentHeight, side } ) => {
        this._anchorEditButton = MeshBuilder.CreateSphere( 'sphere', { diameter: 0.001 }, this._scene );
        this._anchorEditButton.isPickable = false;

        this.removeEditButton();

        let x = 0;
        let y = firstMesh.position.y;
        let z = 0;

        if( 'door' === type ) {
            y = firstMesh.position.y + componentHeight;
        } else if( 'cupola' === type ) {
            y = firstMesh.position.y + componentHeight / 2 + 1;
        } else if( 'light' === type ) {
            y = firstMesh.position.y + componentHeight / 2 + 0.5;
        } else if( this.isInteriorComponent( type ) ){
            y = firstMesh.position.y + componentHeight / 2 + 0.5;
        }

        switch( side ) {
            case 'front':
            case 'top_front':
            case 'lean_to_front':
                x = firstMesh.position.x - componentWidth / 2;
                z = firstMesh.position.z;
                break;
            case 'back':
            case 'top_back':
            case 'lean_to_back':
                x = firstMesh.position.x + componentWidth / 2;
                z = firstMesh.position.z;
                break;
            case 'left':
            case 'top_left':
                x = firstMesh.position.x;
                z = firstMesh.position.z - componentWidth / 2;
                break;
            case 'right':
            case 'top_right':
            case 'lean_to_right':
                x = firstMesh.position.x;
                z = firstMesh.position.z + componentWidth / 2;
                break;
            case 'roof':
                x = firstMesh.position.x;
                z = firstMesh.position.z;
                break;
            case 'floor':
                x = firstMesh.position.x - componentWidth / 2;;
                z = firstMesh.position.z - componentDepth / 2;
                break;
        }

        this._anchorEditButton.position = new Vector3( x, y, z );

        const navHeight = jQuery( '#wrapper-navbar nav.navbar' ).outerHeight();

        const buttonGroup = document.createElement( 'div' );
        buttonGroup.className = 'component-button-group';
        buttonGroup.id = 'js-component-button-group';

        this._anchorEditButton.onBeforeRenderObservable.add( async () => {
            const coordinates = await Vector3.Project(
                Vector3.Zero(),
                this._anchorEditButton.getWorldMatrix(),
                this._scene.getTransformMatrix(),
                this._camera.viewport.toGlobal( this._engine.getRenderWidth(true), this._engine.getRenderHeight(true) )
            );

            buttonGroup.style.top = `${coordinates.y - 16 + navHeight}px`;
            buttonGroup.style.left = `${coordinates.x - 16}px`;
        });

        const editButton = document.createElement( 'BUTTON' );
        editButton.className = 'component-edit-button component-button-item';
        editButton.id = 'js-component-edit-button';
        editButton.style.background = `url("${this._imagePath}edit-component.svg")`;
       
        buttonGroup.appendChild( editButton );

        editButton.onclick = e => {
            e.preventDefault();
            jQuery( `[data-component-key="${firstMesh.metadata.key}"] .edit-component` ).trigger( 'click' );
        }

        if( this.isMoveAlongFloor( componentName ) ) {
            const rotateButton = document.createElement( 'BUTTON' );
            rotateButton.className = 'component-rotate-button component-button-item';
            rotateButton.id = 'js-component-rotate-button';
            rotateButton.style.background = `url("${this._imagePath}rotate-component.svg")`;
            
            buttonGroup.appendChild( rotateButton );

            rotateButton.onclick = e => {
                e.preventDefault();
                firstMesh.rotation.y += Math.PI / 2;

                this._allComponents[component.key]['rotation'] = firstMesh.rotation;
                this._walls[side]['items'][component.key]['rotation'] = firstMesh.rotation;
                this._isUpdateLayout = true;

                this.repositionFloorComponents( component );

                setTimeout( () => {
                    if( helpers.isSeadevSite() ) {
                        this.updateDistanceLines( component );
                        window.dispatchEvent( new CustomEvent( 'after-rotate-component' ) );
                    }
                }, 20 );
            }
        }

        document.body.appendChild(buttonGroup);
    }

    /**
     * Repositions the floor components when a component is rotated or replaced.
     * @param component - The component object.
     */
    repositionFloorComponents( component ) {
        setTimeout( () => {
            let offset = constants.innerWallThickness * 2;
            
            if( this._frameDepth ) offset += this._frameDepth;

            const firstMesh = component.meshes[0];
            const floorMesh = this.getMeshByName( 'floor' );
            const boundingBoxMesh = this.filterByValue( component.meshes, 'name', '_bb_obj' ) as Mesh;
            
            floorMesh.refreshBoundingInfo();
            boundingBoxMesh.refreshBoundingInfo();

            const floorBoundingInfo = floorMesh.getBoundingInfo();
            const floorMin = floorBoundingInfo.boundingBox.minimumWorld;
            const floorMax = floorBoundingInfo.boundingBox.maximumWorld;

            const firstMeshBoundingBox = boundingBoxMesh.getBoundingInfo();
            const firstMeshMin = firstMeshBoundingBox.boundingBox.minimumWorld;
            const firstMeshMax = firstMeshBoundingBox.boundingBox.maximumWorld;

            const firstMeshWidth = firstMeshMax.x - firstMeshMin.x;
            const firstMeshDepth = firstMeshMax.z - firstMeshMin.z;

            const isOutX = firstMeshMin.x < floorMin.x || firstMeshMax.x > floorMax.x;
            const isOutZ = firstMeshMin.z < floorMin.z || firstMeshMax.z > floorMax.z;

            if( isOutX || isOutZ ) {
                if( isOutX ) {
                    firstMesh.position.x = Math.max( floorMin.x + firstMeshWidth / 2 + offset, Math.min( firstMesh.position.x, floorMax.x - firstMeshWidth / 2 - offset ) );
                }
                if( isOutZ ) {
                    firstMesh.position.z = Math.max( floorMin.z + firstMeshDepth / 2 + offset, Math.min(firstMesh.position.z, floorMax.z - firstMeshDepth / 2 - offset ) );
                }

                this._allComponents[component.key]['position'] = firstMesh.position;
                this._walls[component.side]['items'][component.key]['position'] = firstMesh.position;
            }
        }, 10 );
    }

    /**
     * Remove button edit component
     */
    removeEditButton = () => {
        const button = jQuery( '#js-component-button-group' );
        if( button ) {
            button.prop( 'disabled', true );
            button.remove();
        }
    }

    /**
     * Unselect the component selected
     */
    unPickComponent = () => {
        this.removeEditButton();

        if( undefined === this._componentPicked  ) {
            return;
        }

        let component   = this.getComponentByKey( this._componentPicked );
        let meshes      = component.meshes;
        if( meshes ) {
            let firstMesh       = meshes[0];
            let meshHighlight   = this.filterByValue( meshes, 'name', '_bb_obj' ) as Mesh;

            firstMesh.removeBehavior( this._dragBehavior );
            this.disableHighlightMesh( meshHighlight );
        }

        this._componentPicked   = undefined;
    }

    /**
     * Handle when user click to component
     * 
     * @param pickInfo Mesh pick info
     */
    handlePointerDown = ( pickInfo ) => {
        if( ! pickInfo.pickedMesh || ! pickInfo.pickedMesh.parent || ! pickInfo.pickedMesh.parent.metadata ) {
            return this.unPickComponent();
        }

        let { key, type, side } = pickInfo.pickedMesh.parent.metadata;
        if( 'structure_rancher' === this._shedType && ( 'top_front' === side || 'top_back' === side ) ) {
            return this.unPickComponent();
        }

        if( 'structure_monitorbarns_helmuth' === this._shedType && 'cupola' !== type ) {
            return this.unPickComponent();
        }

        if( undefined === key ) {
            return this.unPickComponent();
        }

        const component = this.getComponentByKey( key );
        const behavior  = this.getBehavior( component.name, component.type );
        if( this.isUnPickComponent( component, behavior ) ) {
            return this.unPickComponent();
        }

        if( this._componentPicked !== key ) {
            this.unPickComponent();
        }

        let anchor          = this._componentTypeSettings[behavior]['anchor'];
        let meshes          = component.meshes;
        let firstMesh       = meshes[0];
        let componentHeight = this.getComponentHeight( meshes, behavior );

        if( helpers.isSeadevSite() ) {
            this.updateDistanceLines( component );
        } else {
            this.updateLine( firstMesh, componentHeight, side, anchor, behavior );
        }
    }

    isUnPickComponent = ( component, behavior ) => {
        return undefined === component || subComponentTypes.includes( behavior );
    }

    /**
     * Select component for move feature
     * 
     * @param pickInfo Mesh pick info
     */
    pickComponent = ( pickInfo ) => {
        if( ! pickInfo.hit ) {
            return this.unPickComponent();
        }
        
        if( null === pickInfo.pickedMesh || null === pickInfo.pickedMesh.parent || null === pickInfo.pickedMesh.parent.metadata ) {
            return this.unPickComponent();
        }

        let { key, type, side } = pickInfo.pickedMesh.parent.metadata;
        if( 'structure_rancher' === this._shedType && ( 'top_front' === side || 'top_back' === side ) ) {
            return this.unPickComponent();
        }

        if( 'structure_monitorbarns_helmuth' === this._shedType && 'cupola' !== type ) {
            return this.unPickComponent();
        }

        if( undefined === key ) {
            return this.unPickComponent();
        }

        const component = this.getComponentByKey( key );

        if( this.ignoreHandleTackDoor( component.side ) || this.disableMoveItem( key ) ) {
            return this.unPickComponent();
        }

        const behavior = this.getBehavior( component.name, component.type );
        if( this.isUnPickComponent( component, behavior ) ) {
            return this.unPickComponent();
        }

        if( this._componentPicked === key ) {
            return;
        }
        this._componentPicked = key;
        this.setDragForComponent( key );
    }

    /**
     * Validate Move Component
     * 
     * @param args data
     */
    validateMoveComponent = ( args ) => {
        let { key, targetPosition, component, anchor, componentWidth, componentHeight, firstMesh, tanRoofLowerCorner, tanRoofUpperCorner, limitUpperVertical, tanRoofLeftDormer, limitLowerVertical, sidePorchEndX, isMoveHorizontal } = args;
        const _this             = this;
        const type              = component["type"];
        const side              = component["side"];
        const horizontalOffset  = this.getHorizontalOffset( type );
        let wallHeight          = this.getWallHeight();
        const roofShape         = this.getRoofShape();

        if( this.isSidePorchCabin() ) {
            if( 'front' === side || 'back' === side || 'right' === side ) {
                wallHeight = helpers.toFixed3( _this._bones['b_f_r6'].position.y );
            } else if( 'left' === side ) {
                wallHeight = helpers.toFixed3( _this._bones['b_f_l2'].position.y + aframeCabin.porchOffsetHeight );
            }
        }

        if( 'saltbox_customize' === roofShape && 'left' === side ) {
            wallHeight = helpers.toFixed3( _this._bones['b_f_l2'].position.y );
        }

        const width             = this.getWidth();
        const depth             = this.getDepth();
        const buildingHeight    = _this.getBuildingHeight();
        const roofPitchHeight   = _this.getRoofPitchHeight();
        let targetYTop          = wallHeight;
        let targetYBot          = 'ground' === anchor.vertical ? 0 : this.isInteriorComponent( type ) ? componentHeight / 2 : componentHeight;
        let targetX             = ( width / 2 ) - ( componentWidth / 2 ) - _this._offsetDistance - horizontalOffset;
        let targetZ             = ( depth / 2 ) - ( componentWidth / 2 ) - _this._offsetDistance - horizontalOffset;
        let sideLimitLeft       = - targetZ;
        let startEnd            = targetZ;
        let sideMoveBy          = 'z';
        const childSize         = this.getChildSize( side, key, 'flower_box' );
        const tmpWidth          = childSize.width > componentWidth ? ( childSize.width - componentWidth ) / 2 : 0;
        const tanRoof           = helpers.getTanFromDegrees( this.getRoofDegree() );

        if( side == 'front' || side == 'back'  || 'inner_front' === side || ( ( 'top_front' === side || 'top_back' === side || 'lean_to_front' === side || 'lean_to_back' === side ) && _this.is2StoryLeanTo() ) ) {
            targetYTop = buildingHeight - roofPitchHeight;
            if( 'lean_to' === _this._shedType || 'structure_monoslope_smithbilt' === _this._shedType || 'structure_barn_standard_mwshed' === _this._shedType || 'structure_lean_to_with_front_porch' === _this._shedType ) {
                targetYTop = buildingHeight;
            } else if( 'structure_classic_aframe_dormer' === _this._shedType ) {
                targetYTop = wallHeight + roofPitchHeight;
            } else if( 'structure_cardinal_victorian' === _this._shedType && 'front' === side ) {
                targetYTop = wallHeight + 2;
            }

            if( 'building' === anchor.vertical ) {
                targetYTop = buildingHeight;
            }

            if( 'structure_alpinechalet' === _this._shedType || 'structure_rancher' === _this._shedType || this.isDoubleGable() ) {
                targetYTop = wallHeight;
            }

            if( this.isSpecialShed() ) {
                if( 'door' === type && wallHeight < componentHeight ) {
                    const positionY = componentHeight + this.getDoorFixedHeight( side );
                    targetX = this.getEndXLimit( positionY, wallHeight, width ).positiveLimit - componentWidth / 2;
                    if( targetX > width / 2 - _this._offsetDistance - horizontalOffset - componentWidth / 2 ) {
                        targetX = width / 2 - _this._offsetDistance - horizontalOffset -componentWidth / 2
                    }
                } else if( 'windows' === type && wallHeight < targetPosition.y && this._doorFixedTarget.length !== 0 && this.applyFixedHeightWindows( side, type, component.name ) ) {
                    const positionY = this.getWindowsFixedPositionY( type, componentHeight, side );
                    targetX = this.getEndXLimit( positionY, wallHeight, width ).positiveLimit - componentWidth / 2;
                    if( targetX > width / 2 - _this._offsetDistance - horizontalOffset - componentWidth / 2 ) {
                        targetX = width / 2 - _this._offsetDistance - horizontalOffset -componentWidth / 2
                    }
                }
            }

            if( _this.isSidePorch() || ( _this.isCornerPorchLeft() && 'front' === side ) ){
                let targetPorchX = targetX - _this._porchWidth;

                if( ! _this._isInterior ){
                    targetPorchX += _this._offsetDistance / 2;
                }

                if( targetPosition.x < -targetPorchX ){
                    targetPosition.x = -targetPorchX
                }

                if( _this.isSidePorch() && 'structure_hiproof' === this._shedType ) {
                    targetYTop = wallHeight;
                }
            } else if( ! ( 'saltbox' === roofShape && this._bones['b_f_r4'] ) ) {
                if( targetPosition.x < -targetX ) {
                    targetPosition.x = -targetX
                }
            }

            if( 'inner_front' === side && ! this.isDeluxeCornerLeftPorch() ) {
                targetX = targetX - ( width - constants.cornerWidth );
                targetYTop = wallHeight;

                if( ! _this._isInterior ){
                    targetX += _this._offsetDistance - tmpWidth;
                }
            }

            if( _this.is2StoryLeanTo() && _this.isEnableLeanTo() ) {
                const fullHeight = componentHeight + this.getDoorFixedHeight( side );
                if( targetPosition.y > this._bones['lean_to_f_r4'].position.y || fullHeight > this._bones['lean_to_f_r4'].position.y ) {
                    targetX = this._bones['lean_to_f_r4'].position.x - componentWidth / 2 - _this._offsetDistance - horizontalOffset;
                } else {
                    targetX = this._bones['lean_to_f_r2'].position.x - componentWidth / 2 - _this._offsetDistance - horizontalOffset;
                }
            
                const opposite  = this._bones['lean_to_f_r4'].position.y - this._bones['lean_to_f_r3'].position.y;
                const adjacent  = this._bones['lean_to_f_r3'].position.x - this._bones['lean_to_f_r4'].position.x;
                const tanDegree = helpers.getTanFromDegrees( this.getRoofPitchFromRadian( opposite, adjacent ) );
                if( 'door' === type ) {
                    const distanceHeight = fullHeight - this._bones['lean_to_f_r3'].position.y;
                    if( distanceHeight > 0 && targetPosition.x - componentWidth / 2 > this._bones['lean_to_f_r1'].position.x ) {
                        const distanceWidth = distanceHeight / tanDegree;
                        targetX             = helpers.toFixed3( this._bones['lean_to_f_r3'].position.x - distanceWidth - componentWidth / 2 );
                    }
                } else {
                    if( targetPosition.y >= this._bones['lean_to_f_r3'].position.y && targetPosition.x > this._bones['lean_to_f_r1'].position.x ) {
                        const maxDistance = tanDegree * ( this._bones['lean_to_f_r3'].position.x - Math.abs( targetPosition.x ) - ( componentWidth / 2 ) );
                        if( targetPosition.y > this._bones['lean_to_f_r3'].position.y + maxDistance ) {
                            targetPosition.y = this._bones['lean_to_f_r3'].position.y + maxDistance;
                        }
                    }
                }
            
                targetYTop  = buildingHeight;
            }

            if(targetPosition.x > targetX && ! ( 'saltbox' === roofShape && this._bones['b_f_r4'] ) ) {
                targetPosition.x = targetX
            }

            if( 'saltbox' === roofShape && ( this._bones['b_f_r4'] || 'structure_quaker_wannabuysheds' === _this._shedType ) ) {
                if( 'front' === side || 'back' === side ) {
                    const sideLimitLeft     = _this._bones['b_f_l1'].position.x + componentWidth / 2 + this._offsetDistance;
                    let sideLimitRight      = 0

                    if( 'structure_quaker_wannabuysheds' === _this._shedType ){
                        sideLimitRight    = _this._bones['b_f_r1'].position.x - componentWidth / 2 - this._offsetDistance;
                    }else{
                        sideLimitRight    = _this._bones['b_f_r4'].position.x - componentWidth / 2 - this._offsetDistance;
                    }

                    targetPosition.x = _this.limitMoveSide( targetPosition.x, sideLimitLeft, sideLimitRight );
                }
            }

            if( 'structure_dog_kennel' === this._shedType && 'dogkennel_split' === this.getActiveLayout() && 'back' === side ) {
                if( targetPosition.x + ( componentWidth / 2 ) > 0 && firstMesh.position.x < 0 ) {
                    targetPosition.x = - componentWidth / 2;
                }
                if( targetPosition.x - ( componentWidth / 2 ) < 0 && firstMesh.position.x > 0 ) {
                    targetPosition.x = componentWidth / 2;
                }
            }

            wallHeight = _this.is2StoryLeanTo() ? _this._bones['b_f_l2'].position.y : wallHeight;
            if( targetPosition.y > wallHeight && ( 'open_gable' === roofShape || 'box_gable' === roofShape || 'gambrel' === roofShape || 'dutch_style' === roofShape ) && ! this.isAframeSingleGable() && ! this.isDoubleGable() ) {
                targetYTop                  = buildingHeight;
                const limitLowerVertical    = 'structure_chalet_dutchboybarns' === _this._shedTypeVersion ? ( width / 2 ) - 0.1 : width / 2;
                const limitUpperVertical    = _this.getLimitUpperVertical();
                const frontWallTop          = this.isPorchHasTrim() ? this.getPorchBottom() - this._trimLength : this.getPorchBottom();
                targetPosition.y            = _this.getTargetPositionY( targetPosition.y, buildingHeight, tanRoofLowerCorner, tanRoofUpperCorner, wallHeight, componentWidth, limitLowerVertical, limitUpperVertical, targetPosition.x );
                if( 'front' === side && this.isFrontPorch() && 'structure_chalet_dutchboybarns' !== _this._shedTypeVersion && this.isCellingPorch() &&  targetPosition.y >= frontWallTop ) {
                    if( 'standard_cottage_front_porch' === _this._shedType ) {
                        targetPosition.y = wallHeight;
                    } else {
                        targetPosition.y = frontWallTop;
                    }
                }
                
                if( 'inner_front' === side ) {
                    targetYTop = wallHeight;
                }
            }

            if( 'saltbox_customize' === roofShape ) {
                targetYTop = buildingHeight;

                const middleX = _this._bones['b_f_t'].position.x;
                const lowerWallRoofPitch = Number( this.getSettingData( 'model_roof_angle' ) );
                const higherWallRoofPitch = this.getSettingData( 'model_second_roof_angle' ) ? Number( this.getSettingData( 'model_second_roof_angle' ) ) : lowerWallRoofPitch;
                const wallHeightDiff = this.getSettingData( 'slope_height_difference' ) ? Number( this.getSettingData( 'slope_height_difference' ) ) / 12 : 0;
                let targetVerticalOfDrag = targetPosition.x;

                if( targetPosition.y > wallHeight ) {
                    if( targetPosition.x > middleX ) {
                        targetVerticalOfDrag = width / 2 - targetPosition.x;
                        tanRoofLowerCorner = helpers.toFixed3( helpers.getTanFromDegrees( lowerWallRoofPitch ) );
                    } else {
                        targetVerticalOfDrag = width / 2 + targetPosition.x;
                        tanRoofLowerCorner = helpers.toFixed3( helpers.getTanFromDegrees( higherWallRoofPitch ) );
                        wallHeight = wallHeight + wallHeightDiff;
                    }

                    targetPosition.y = _this.getTargetPositionY( targetPosition.y, buildingHeight, tanRoofLowerCorner, tanRoofUpperCorner, wallHeight, componentWidth, limitLowerVertical, limitUpperVertical, targetVerticalOfDrag );
                }
            }
        } else if( side == 'left' || side == 'right' || 'inner_left' === side || 'lean_to_right' === side || 'top_right' === side || 'top_left' === side ) {
            if( ( 'lean_to' === _this._shedType || 'structure_monoslope_smithbilt' === _this._shedType || 'carport_lean_to_standalone' === _this._shedType ) && 'left' === side ) {
                targetYTop = buildingHeight;
            }

            if( _this.is2StoryLeanTo() && ( 'right' === side || 'left' === side || 'top_left' === side || 'top_right' === side ) ) {
                if( this.isAllowPlaceComponentOn2ndFloor() ) {
                    targetYTop = _this._bones['b_b_l2'].position.y;
                } else {
                    targetYTop = wallHeight;
                }

                if( this.isEnableLeanTo() && 'top_right' === side ) {
                    targetYBot = _this._bones['lean_to_b_r4'].position.y + componentHeight;
                }
            }

            if( 'structure_barn_2story_leanto' === _this._shedType && 'lean_to_right' === side ) {
                targetYTop = _this._bones['lean_to_f_r3'].position.y;
            }

            if( 'structure_chalet_genesis' === _this._shedType ){
                targetYTop -= 0.1;
            }

            if( 'mainus_quaker' === _this._shedType && 'right' === side ) {
                targetYTop = wallHeight - offsetTop;
            }

            if( 'structure_classic_aframe_dormer' === _this._shedType && 'left' === side ) {
                let limitDormerLeft  = helpers.toFixed3( _this._bones['b_d_l1'].position.z );
                let limitDormerRight = helpers.toFixed3( _this._bones['b_d_r1'].position.z );
                if( ( limitDormerLeft > targetPosition.z - componentWidth / 2 && limitDormerRight < targetPosition.z - componentWidth / 2 ) ) {
                    targetYTop = wallHeight + roofPitchHeight;
                    if( targetPosition.y > wallHeight ) {
                        const space         = helpers.toFixed3( Math.abs( _this._bones['b_d_t'].position.z - targetPosition.z ) );
                        const haftDormer    = _this._dormerWidth / 2;
                        targetPosition.y    = helpers.toFixed3( _this.getTargetPositionY( targetPosition.y, targetYTop, tanRoofLeftDormer, 0, wallHeight, componentWidth, haftDormer, haftDormer, space ) );
                    }
                } else {
                    targetYTop = wallHeight;
                }

            }

            if( 'structure_country_cottage' === _this._shedType && 'left' === side ) {
                if( targetPosition.y > wallHeight && limitLowerVertical - componentWidth / 2 > Math.abs( targetPosition.z ) ) {
                    targetYTop       = buildingHeight;
                    targetPosition.y = _this.getTargetPositionY( targetPosition.y, buildingHeight, tanRoofLowerCorner, tanRoofUpperCorner, wallHeight, componentWidth, limitLowerVertical, limitUpperVertical,  targetPosition.z );
                }
            }

            if( this.isDoubleGable() && 'right' === side ) {
                let anchorLeft  = helpers.toFixed3( _this._bones['b_b_m6'].position.z );
                let anchorRight = helpers.toFixed3( _this._bones['b_b_m2'].position.z );
                const leftPoint = targetPosition.z - componentWidth / 2;
                const rightPoint = targetPosition.z + componentWidth / 2;
                const isOutSideRight = leftPoint > anchorRight;
                const isOutSideLeft = rightPoint < anchorLeft;

                if( isOutSideRight || isOutSideLeft ) {
                    targetYTop = buildingHeight;
                    if( targetPosition.y > wallHeight ) {
                        const boneName = isOutSideRight ? 'b_f_t1' : 'b_b_t1';
                        const space = helpers.toFixed3( Math.abs( _this._bones[boneName].position.z - targetPosition.z ) );
                        const tanRoofGable = helpers.getTanFromDegrees( _this.getGlobalData( constants.initRoofGableDegrees ) );
                        const haftGableWidth = _this.getHalfGableWidth();
                        targetPosition.y = helpers.toFixed3( _this.getTargetPositionY( targetPosition.y, targetYTop, tanRoofGable, 0, wallHeight, componentWidth, haftGableWidth, haftGableWidth, space ) );
                    }
                } else {
                    targetYTop = wallHeight;
                }
            }

            if(targetPosition.z < -targetZ){
                targetPosition.z = -targetZ
            }

            if(targetPosition.z > targetZ){
                targetPosition.z = targetZ
            }

            if( this.isSidePorch() && 'structure_hiproof' !== this._shedType && 'left' === side ) {
                targetYTop = wallHeight - offsetTop;
            }

            if( 'structure_lean_to_with_front_porch' === this._shedType && 'left' === side ) {
                targetYTop = this._middleHeight - leanToFrontPorchOffsetTop;
            }

            if( 'structure_dog_kennel' === this._shedType && ( 'left' === side || 'right' === side ) ) {
                const endFZ = _this._bones['b_m_r1'].position.z - ( componentWidth / 2 ) - _this._offsetDistance;

                if( targetPosition.z > endFZ ) {
                    targetPosition.z = endFZ;
                }
            }

            if( _this.isCornerPorchLeft() ){
                if( 'left' === side ){
                    let targetPorchZ = targetZ - constants.cornerDepth;

                    if(targetPosition.z > targetPorchZ){
                        targetPosition.z = targetPorchZ
                    }
                }else if( 'inner_left' === side ){
                    let targetPorchZ = -targetZ + ( _this.getDepth() - constants.cornerDepth );

                    if( ! _this._isInterior ){
                        targetPorchZ -= ( _this._offsetDistance - tmpWidth );
                    }

                    if(targetPosition.z < targetPorchZ){
                        targetPosition.z = targetPorchZ
                    }
                }
            }
        }else if( side == 'roof' ) {
            if( 'cupola' === type && 'hip' === roofShape ) {
                targetX     = _this._bones['r_f_t'].position.x;
                targetZ     = _this._bones['r_f_t'].position.z - componentWidth / 2;
                targetYTop  = _this._bones['r_f_t'].position.y;

                if( 0 === _this._bones['r_f_t'].position.z ) {
                    targetPosition.z = 0;
                } else if( targetPosition.z < - targetZ ) {
                    targetPosition.z = - targetZ;
                } else if( targetPosition.z > targetZ ) {
                    targetPosition.z = targetZ;
                }
            } else {
                if( _this.isDoubleGable() ) {
                    targetX = _this._bones['r_b_t1'].position.x;
                    targetYTop = _this._bones['r_b_t1'].position.y;
                    targetZ -= _this.getHalfGableWidth();
                } else {
                    targetX     = _this._bones['b_b_t'] ? _this._bones['b_b_t'].position.x : _this._bones['r_f_t'].position.x;
                    targetYTop  = _this._bones['b_b_t'] ? _this._bones['b_b_t'].position.y : _this._bones['r_f_t'].position.y;
                }
                targetZ     += _this._offsetDistance;
                let targetZLeft = targetZ;
                let targetZRight = targetZ;

                if( _this.isAframeSingleGable() ) {
                    targetZRight = _this._bones['b_f_t1'].position.z - _this._offsetDistance - componentWidth / 2;
                }

                if( targetPosition.z < - targetZLeft ) {
                    targetPosition.z = - targetZLeft;
                }

                if( targetPosition.z > targetZRight ) {
                    targetPosition.z = targetZRight;
                }

                if( isMoveHorizontal ) {
                    targetX             = targetPosition.y > wallHeight ? this.getLimitX( targetPosition.y, componentWidth, componentHeight ) : width / 2 - componentWidth / 2;
                    targetPosition.x    = _this.limitMoveSide( targetPosition.x, - targetX, targetX );
                }
            }
        } else if( 'dormer' === side ) {
            if( this._bones['b_b_t1'] && this._bones['b_b_t2'] ) {
                targetX = _this._bones['b_b_t1'].position.x;
                targetYTop = _this._bones['b_b_t2'].position.y;
            }

            if( targetPosition.z < -targetZ ) {
                targetPosition.z = -targetZ
            }

            if( targetPosition.z > targetZ ) {
                targetPosition.z = targetZ
            }
        } else if( 'roof_left' === side || 'roof_right' === side ) {
            if(targetPosition.z < -targetZ){
                targetPosition.z = -targetZ
            }

            if(targetPosition.z > targetZ){
                targetPosition.z = targetZ
            }

            if( _this.isGableDormer( component.name ) ) {
                const dormerHeight = this.getGableDormerDistance( `${component.name}_frame_obj`, component.meshes, 'Dormer_B_F_T', 'Dormer_B_F_R1', 'y' );
                if( targetPosition.y > buildingHeight - dormerHeight ) {
                    targetPosition.y = buildingHeight - dormerHeight;
                }

                if( targetPosition.y < wallHeight ) {
                    targetPosition.y = wallHeight;
                }

                const positionX     = helpers.toFixed3( ( buildingHeight - dormerHeight - targetPosition.y ) / tanRoof );
                targetPosition.x    = 'roof_left' === side ? - positionX : positionX;
            } else if( _this.isSkylightWindow( component.name ) ) {
                const sinRoof = helpers.getSinFromDegrees( this.getRoofCorrectionDegree() );
                const tanRoof = helpers.getTanFromDegrees( this.getRoofCorrectionDegree() );
                const maxY = this.getPositionYForSkylightWindow();
                const minY = wallHeight + helpers.toFixed3( sinRoof * componentHeight ) + this.getSkylightWindowOffset();

                if( targetPosition.y > maxY ) {
                    targetPosition.y = maxY;
                } else if( targetPosition.y < minY ) {
                    targetPosition.y = minY;
                }

                const positionX = helpers.toFixed3( ( maxY - targetPosition.y ) / tanRoof );
                targetPosition.x = 'roof_left' === side ? - positionX : positionX;
            }
        } 

        if( _this.isMiddlePorch() ) {
            if( 'left_front' === side ) {
                sideLimitLeft   = constants.middleWidth / 2 + ( componentWidth / 2 ) + _this._offsetDistance;
                startEnd        = depth / 2 - componentWidth / 2 - _this._offsetDistance;
                sideMoveBy      = 'z';
            } else if( 'left_back' === side ) {
                sideLimitLeft   = - ( depth / 2 - componentWidth / 2 - _this._offsetDistance );
                startEnd        = - ( constants.middleWidth / 2 + ( componentWidth / 2 ) + _this._offsetDistance );
                sideMoveBy      = 'z';
            } else if( 'right_inner_porch' === side ) {
                sideLimitLeft   = - ( width / 2 - componentWidth / 2 - _this._offsetDistance );
                startEnd        = - ( width / 2 - constants.middleDepth  + componentWidth / 2 + tmpWidth );
                sideMoveBy      = 'x';
            } else if( 'front_inner_porch' === side ) {
                sideLimitLeft   = - ( constants.middleWidth / 2 - componentWidth / 2 - tmpWidth );
                startEnd        = constants.middleWidth / 2 - componentWidth / 2 - tmpWidth ;
                sideMoveBy      = 'z';
            } else if( 'left_inner_porch' === side ) {
                sideLimitLeft   = - ( width / 2 - componentWidth / 2 - _this._offsetDistance );
                startEnd        = - ( width / 2 - constants.middleDepth + componentWidth / 2 + tmpWidth );
                sideMoveBy      = 'x';
            }

            if( 'windows' === component.type ) {
                targetYBot += offsetBottom.level3;
            }
            
            if( 'right_inner_porch' === side || 'front_inner_porch' === side || 'left_inner_porch' === side ) {
                targetYTop -= 0.15;
            }
            
            if( 'left_front' === side || 'left_back' === side || 'right_inner_porch' === side || 'front_inner_porch' === side || 'left_inner_porch' === side ) {
                targetPosition.y = _this.limitMoveSide( targetPosition.y, targetYBot, targetYTop );
                if( 'z' === sideMoveBy ) {
                    targetPosition.z = _this.limitMoveSide( targetPosition.z, sideLimitLeft, startEnd );
                } else {
                    targetPosition.x = _this.limitMoveSide( targetPosition.x, sideLimitLeft, startEnd );
                }
            }
        } else if( _this.isDoubleGable() ) {
            let anchorLeft = 0;
            let anchorRight = 0;
            const haflComponentWidth = componentWidth / 2;
            const offset = horizontalOffset + haflComponentWidth;
            const fullOffset = offset + _this._offsetDistance;
            const tanRoofGable = helpers.getTanFromDegrees( _this.getGlobalData( constants.initRoofGableDegrees ) );
            const haftGableWidth = _this.getHalfGableWidth();
            let sideMoveBy = '';
            switch( side ) {
                case 'front':
                case 'back':
                    anchorLeft = helpers.toFixed3( _this._bones['b_f_r1'].position.x - fullOffset );
                    anchorRight = helpers.toFixed3( _this._bones['b_f_l1'].position.x + fullOffset );
                    sideMoveBy = 'x';
                    break;
                case 'right':
                    anchorLeft = helpers.toFixed3( _this._bones['b_f_l1'].position.z - fullOffset );
                    anchorRight = helpers.toFixed3( _this._bones['b_b_l1'].position.z + fullOffset );
                    sideMoveBy = 'z';
                    break;
                case 'left_front':
                    anchorLeft = helpers.toFixed3( _this._bones['b_f_l1'].position.z - fullOffset );
                    anchorRight = helpers.toFixed3( _this._bones['b_p_l1'].position.z + fullOffset );
                    sideMoveBy = 'z';

                    targetYTop = buildingHeight;
                    const leftFrontSpace = helpers.toFixed3( Math.abs( _this._bones['b_f_t2'].position.z - targetPosition.z ) );
                    targetPosition.y = helpers.toFixed3( _this.getTargetPositionY( targetPosition.y, targetYTop, tanRoofGable, 0, wallHeight, componentWidth, haftGableWidth, haftGableWidth, leftFrontSpace ) );
                    break;
                case 'left_back':
                    anchorLeft = helpers.toFixed3( _this._bones['b_p_l3'].position.z - fullOffset );
                    anchorRight = helpers.toFixed3( _this._bones['b_b_l1'].position.z + fullOffset );
                    sideMoveBy = 'z';

                    targetYTop = buildingHeight;
                    const leftBackSpace = helpers.toFixed3( Math.abs( _this._bones['b_b_t2'].position.z - targetPosition.z ) );
                    targetPosition.y = helpers.toFixed3( _this.getTargetPositionY( targetPosition.y, targetYTop, tanRoofGable, 0, wallHeight, componentWidth, haftGableWidth, haftGableWidth, leftBackSpace ) );
                    break;
                case 'left_inner_porch':
                case 'right_inner_porch':
                    anchorLeft = helpers.toFixed3( _this._bones['b_b_m4'].position.x - offset );
                    anchorRight = helpers.toFixed3( _this._bones['b_p_l1'].position.x + fullOffset );
                    sideMoveBy = 'x';
                    break;
                case 'front_inner_porch':
                    anchorLeft = helpers.toFixed3( _this._bones['b_b_m4'].position.z - offset );
                    anchorRight = helpers.toFixed3( _this._bones['b_b_m8'].position.z + offset );
                    sideMoveBy = 'z';
                    break;
            }

            if( 'z' === sideMoveBy ) {
                targetPosition.z = _this.limitMoveSide( targetPosition.z, anchorRight, anchorLeft );
            } else if( 'x' === sideMoveBy ) {
                targetPosition.x = _this.limitMoveSide( targetPosition.x, anchorRight, anchorLeft );
            }
        }

        if( this.isSidePorch() && 'left' === side && 'structure_hiproof' !== this._shedType && 'ground' !== anchor.vertical ) {
            targetYBot = componentHeight + offsetBottom.level1;
        }

        if( ( 'inner_left' === side || 'inner_front' === side ) && ! _this._isInterior ){
            targetYTop -= 0.2;
            targetYBot += offsetBottom.level1;
        }

        if( 'structure_cottage_shed' === _this._shedType && 'left' === side ){
            targetYTop -= 0.35;
        }

        if( 'structure_quaker_wannabuysheds' === _this._shedType && ( 'front' === side || 'back' === side ) ) {
            targetYTop -= 0.25;
        }

        if( 'structure_ranch_cabin' === _this._shedType || this.isAframeSingleGable() ) {
            switch( side ) {
                case 'front':
                    if( this.isAframeSingleGable() ) {
                        targetYTop = wallHeight;
                    }
                    break;
                case 'inner_back':
                    if( 'windows' === component.type ) {
                        targetYBot += offsetBottom.level3;
                    }
                    targetYTop          -= 0.15;
                    sideLimitLeft       = - ( width / 2 - componentWidth / 2 - _this._offsetDistance );
                    startEnd            = - ( width / 2 - constants.cabinPorchDepth + componentWidth / 2 + tmpWidth );
                    targetPosition.y    = _this.limitMoveSide( targetPosition.y, targetYBot, targetYTop );
                    targetPosition.x    = _this.limitMoveSide( targetPosition.x, sideLimitLeft, startEnd );
                    break;
                case 'inner_left':
                    targetYBot              = 0;
                    const innerLeftTargetZ  = helpers.toFixed3( _this._bones['b_b_m4'].position.z - componentWidth / 2 - _this._offsetDistance / 2 );
                    if( targetPosition.z > innerLeftTargetZ ) {
                        targetPosition.z = innerLeftTargetZ
                    }
                    break;
                case 'back':
                    const targetX = helpers.toFixed3( _this._bones['b_b_l1'].position.x + componentWidth / 2 + _this._offsetDistance / 2 );
                    if( targetPosition.x < targetX ) {
                        targetPosition.x = targetX
                    }

                    if( this.isAframeSingleGable() ) {
                        targetYTop = buildingHeight;
                        
                        if( targetPosition.y > wallHeight ) {
                            const opposite = this._bones['b_b_t'].position.y - this._bones['b_b_r2'].position.y;
                            const adjacent = this._bones['b_b_r2'].position.x - this._bones['b_b_t'].position.x;
                            tanRoofLowerCorner = helpers.toFixed3( opposite / adjacent );
                            
                            const topBone = 'b_b_t';
                            const space = helpers.toFixed3( Math.abs( _this._bones[topBone].position.x - targetPosition.x ) );
                            const halfCabinWidth = 5;
                            targetYTop = _this._bones[topBone].position.y;
                            targetPosition.y = helpers.toFixed3( _this.getTargetPositionY( targetPosition.y, targetYTop, tanRoofLowerCorner, tanRoofUpperCorner, wallHeight, componentWidth, halfCabinWidth, halfCabinWidth, space ) );
                        }
                    }
                    break;

                case 'right':
                    if( this.isAframeSingleGable() ) {
                        const midZ = _this._bones['b_b_m2'].position.z;
                        const currentZ = targetPosition.z;

                        if( ( currentZ - ( componentWidth / 2 ) ) > midZ ) {
                            targetYTop = buildingHeight;
                            
                            const topBone = 'b_f_t2';
                            const space = helpers.toFixed3( Math.abs( _this._bones[topBone].position.z - targetPosition.z ) );
                            const halfCabinWidth = helpers.toFixed3( _this._cabinWidth / 2 );
                            targetYTop = _this._bones[topBone].position.y;
                            targetPosition.y = helpers.toFixed3( _this.getTargetPositionY( targetPosition.y, targetYTop, tanRoofLowerCorner, tanRoofUpperCorner, wallHeight, componentWidth, halfCabinWidth, halfCabinWidth, space ) );
                        }
                    }

                    break;
                case 'left':
                    const leftTargetZ = helpers.toFixed3( _this._bones['b_p_l1'].position.z + componentWidth / 2 + _this._offsetDistance / 2 );
                    if( targetPosition.z < leftTargetZ ) {
                        targetPosition.z = leftTargetZ;
                    }

                    if( targetPosition.y > wallHeight ) {
                        const topBone = this.isAframeSingleGable() ? 'b_f_t2' : 'b_m_t2';
                        const space = helpers.toFixed3( Math.abs( _this._bones[topBone].position.z - targetPosition.z ) );
                        const halfCabinWidth = helpers.toFixed3( _this._cabinWidth / 2 );
                        targetYTop = _this._bones[topBone].position.y;
                        targetPosition.y = helpers.toFixed3( _this.getTargetPositionY( targetPosition.y, targetYTop, tanRoofLowerCorner, tanRoofUpperCorner, wallHeight, componentWidth, halfCabinWidth, halfCabinWidth, space ) );
                    }
                    break;
            }
        }

        if( this.isDeluxeCornerLeftPorch() ) {
            switch( side ) {
                case 'inner_right':
                    targetYTop = wallHeight;
                    targetYBot += offsetBottom.level2;

                    if( targetPosition.z > _this._bones['b_f_r5'].position.z - componentWidth / 2 - _this._offsetDistance ) {
                        targetPosition.z = _this._bones['b_f_r5'].position.z - componentWidth / 2 - _this._offsetDistance;
                    }

                    if( targetPosition.z < _this._bones['b_f_r3'].position.z + componentWidth / 2 + tmpWidth ) {
                        targetPosition.z = _this._bones['b_f_r3'].position.z + componentWidth / 2 + tmpWidth;
                    }
                    break;
                case 'front':
                    targetYTop = wallHeight;
                    targetYBot += offsetBottom.level2;

                    if( targetPosition.x > _this._bones['b_f_l4'].position.x - componentWidth / 2 - _this._offsetDistance ) {
                        targetPosition.x = _this._bones['b_f_l4'].position.x - componentWidth / 2 - _this._offsetDistance;
                    }
                    
                    if( targetPosition.x < _this._bones['b_f_l1'].position.x + componentWidth / 2 + _this._offsetDistance ) {
                        targetPosition.x = _this._bones['b_f_l1'].position.x + componentWidth / 2 + _this._offsetDistance;
                    }
                    break;
                case 'inner_front':
                    targetYTop = wallHeight;

                    if( targetPosition.x > _this._bones['b_f_r1'].position.x - componentWidth / 2 - _this._offsetDistance ) {
                        targetPosition.x = _this._bones['b_f_r1'].position.x - componentWidth / 2 - _this._offsetDistance;
                    }

                    if( targetPosition.x < _this._bones['b_f_r4'].position.x + componentWidth / 2 + tmpWidth ) {
                        targetPosition.x = _this._bones['b_f_r4'].position.x + componentWidth / 2 + tmpWidth;
                    }
                    break;
                case 'left':
                    if( targetPosition.z > _this._bones['b_f_l1'].position.z - componentWidth / 2 - _this._offsetDistance ) {
                        targetPosition.z = _this._bones['b_f_l1'].position.z - componentWidth / 2 - _this._offsetDistance;
                    }
                    break;
                case 'right':
                    if( targetPosition.z > _this._bones['b_f_r1'].position.z - componentWidth / 2 - _this._offsetDistance ) {
                        targetPosition.z = _this._bones['b_f_r1'].position.z - componentWidth / 2 - _this._offsetDistance;
                    }
                    break;
                case 'porch':
                    targetPosition.z = _this._bones['b_p_t'].position.z;
                    break;
                // case 'diagonal_right':
                //     targetYBot += offsetBottom.level2;
                //     const halfWidth         = componentWidth / 2;
                //     const tmpComponentWidth = Math.sqrt( ( halfWidth * halfWidth ) / 2 );
                //     const tmpDistance       = 0.2;
                //     if( targetPosition.z < _this._bones['b_f_r6'].position.z + tmpComponentWidth + tmpDistance ) {
                //         targetPosition.z = _this._bones['b_f_r6'].position.z + tmpComponentWidth + tmpDistance;
                //     }

                //     if( targetPosition.x > _this._bones['b_f_r6'].position.x - tmpComponentWidth - tmpDistance ) {
                //         targetPosition.x = _this._bones['b_f_r6'].position.x - tmpComponentWidth - tmpDistance;
                //     }

                //     if( targetPosition.z > _this._bones['b_f_l4'].position.z - tmpComponentWidth - tmpDistance ) {
                //         targetPosition.z = _this._bones['b_f_l4'].position.z - tmpComponentWidth - tmpDistance;
                //     }

                //     if( targetPosition.x < _this._bones['b_f_l4'].position.x + tmpComponentWidth + tmpDistance ) {
                //         targetPosition.x = _this._bones['b_f_l4'].position.x + tmpComponentWidth + tmpDistance;
                //     }
                //     break;
            }
        }

        if( this.isHandleOffsetFloor( type ) ) {
            targetYBot  = this.getPositionYForInterior( type, component.name );
            targetYTop  -= componentHeight / 2;
        }

        if( ( 'structure_lakeside_bunkie_gambrel_roof' === _this._shedType || 'structure_lakeside_bunkie_gable_roof' === this._shedType ) && 'front' === side ){
            targetYTop = wallHeight;
        }
    
        if( 'dormer' !== type && ! this.isPorchSide( side ) && ! this.isSkylightWindow( component.name ) ) {
            if( targetPosition.y < targetYBot ){
                targetPosition.y = targetYBot;
            }
            
            if( targetPosition.y > targetYTop ){
                targetPosition.y = targetYTop;
            }
            
            if( 'dormer' === side && this._bones['b_b_t1'] && this._bones['b_b_t2'] ) {
                if( targetPosition.y < _this._bones['b_b_t1'].position.y + componentHeight + 0.2 ) {
                    targetPosition.y = _this._bones['b_b_t1'].position.y + componentHeight + 0.2;
                }
            }
        }
    
        if( this.applyFixedHeightWindows( side, type, component.name ) ) {
            if( this.isFollowMainDoor() ) {
                if( 0 !== this._doorFixedTarget.length )
                    targetPosition.y = this.getWindowsFixedPositionY( type, componentHeight, side );
            } else {
                targetPosition.y = this.getWindowsFixedPositionY( type, componentHeight, side );
            }
        }
    
        if( this.isPorchSide( side ) ) {
            if( this.isVerticalDistribute() ) {
                const limitX = this.getLimitX( component.position.y, componentWidth, componentHeight );
                if( targetPosition.x > limitX ) {
                    targetPosition.x = limitX;
                } else if( targetPosition.x < - limitX ) {
                    targetPosition.x = - limitX;
                }
            } else {
                const roofPitch = this.getSpacePorchRoofPitch();
                let tmpTargetY  = buildingHeight - roofPitch;

                targetYTop                  = buildingHeight;
                const limitLowerVertical    = width / 2;
                const limitUpperVertical    = _this.getLimitUpperVertical();
                targetPosition.y            = _this.getTargetPositionY( targetPosition.y, buildingHeight, tanRoofLowerCorner, tanRoofUpperCorner, wallHeight, componentWidth, limitLowerVertical, limitUpperVertical, targetPosition.x );
                
                if( targetPosition.y > targetYTop ) {
                    targetPosition.y = targetYTop;
                }

                if( Math.abs( targetPosition.x ) > sidePorchEndX ) {
                    targetPosition.x = targetPosition.x > 0 ? sidePorchEndX : -sidePorchEndX;
                }

                if( componentHeight + this.getPorchBottom() > tmpTargetY ) {
                    let componentUpperHeight    = componentHeight + this.getPorchBottom() - tmpTargetY;
                    let upperX                  = componentUpperHeight / this.getTanRoofPorch( 'roofUp' );
                    let sidePorchUpperEndX      = Math.abs( limitUpperVertical ) - upperX - componentWidth / 2;

                    if( Math.abs( targetPosition.x ) > sidePorchUpperEndX ) {
                        targetPosition.x = targetPosition.x > 0 ? sidePorchUpperEndX : -sidePorchUpperEndX;
                    }
                }

                if( targetPosition.x < - targetX ) {
                    targetPosition.x = - targetX
                }

                if( targetPosition.x > targetX ) {
                    targetPosition.x = targetX
                }

                if( targetPosition.y < componentHeight + this.getPorchBottom() + childSize.height ) {
                    targetPosition.y = componentHeight + this.getPorchBottom() + childSize.height;
                }
            }
        }

        if( 'windows' === type ) {
            let porchFloorHeight = 0;
            if( this.isPorchWal( side ) && 'structure_aframe_2story_attic_leanto' !== this._shedType ) {
                porchFloorHeight = offsetBottom.level2;
            }
            if( ! this.isWindowPositionFixedEnable() ) {
                if( targetPosition.y < componentHeight + childSize.height + porchFloorHeight ) {
                    targetPosition.y = componentHeight + childSize.height + porchFloorHeight;
                }
            }
        }

        if( 'structure_aframe_genesis' === this._shedType && ( 'front' === side || 'back' === side ) ) {
            const limitX = this.getEndXLimit( targetPosition.y, wallHeight, width ).positiveLimit - componentWidth / 2;
            if( targetPosition.x <= - limitX ) {
                targetPosition.x = - limitX;
            } else if( targetPosition.x >= limitX ) {
                targetPosition.x = limitX;
            }
        }

        if( 'lean_to' === this._shedType || 'structure_lean_to_with_front_porch' === this._shedType && ( 'front' === side || 'back' === side ) ) {
            if( targetPosition.y > wallHeight ) {
                const space         = helpers.toFixed3( Math.abs( _this._bones['b_f_t'].position.x - targetPosition.x ) );
                targetPosition.y    = helpers.toFixed3( _this.getTargetPositionY( targetPosition.y, buildingHeight, tanRoofLowerCorner, tanRoofUpperCorner, wallHeight, componentWidth, width, width, space ) );
            }
        }

        if( helpers.hasKeyInArray( [ 'structure_quaker_wannabuysheds', 'structure_horse_shelter' ], this._shedType ) && ( 'front' === side || 'back' === side ) && 'vent' === type ){

            const targetRight   = new Vector3( targetPosition.x + componentWidth / 2, targetPosition.y, targetPosition.z );
            const nearestRight  = this.nearestPointToLine( targetRight, this._bones['b_b_t'].position, this._bones['b_b_r2'].position );

            const targetLeft    = new Vector3( targetPosition.x - componentWidth / 2, targetPosition.y, targetPosition.z );
            const nearesLeft    = this.nearestPointToLine( targetLeft, this._bones['b_b_l3'].position, this._bones['b_b_t'].position );

            if( nearestRight.y < targetRight.y ){
                targetPosition.y = nearestRight.y;
            }
            
            if( nearesLeft.y < targetLeft.y ){

                const litmitPoint = Vector3.Lerp(
                    this._bones['b_b_l3'].position,
                    this._bones['b_b_t'].position,
                    Math. abs( targetLeft.x - this._bones['b_b_l3'].position.x ) / Math. abs( this._bones['b_b_t'].position.x - this._bones['b_b_l3'].position.x )
                );

                targetPosition.y = litmitPoint.y;
            }

            if( 'structure_horse_shelter' === _this._shedType ) {
                const fistVentPosition = this.getFirstVentPositionY( _this._bones['b_f_t'].position.y, componentWidth, side );

                if( targetPosition.y > fistVentPosition ) {
                    targetPosition.y = fistVentPosition;
                }
            }
        }

        if( _this._isEnableTackRoom && side === this._sideWallTackRoom && 'door' === type && helpers.hasKeyInArray( zSide, side ) ) {
            if( 'left' === this._dataTackroom.position ) {
                if( this._tackDoor && key === this._tackDoor.key ) {
                    startEnd      = sideLimitLeft + this._dataTackroom.width - componentWidth - _this._offsetDistance - horizontalOffset - this._frameDepth;
                } else {
                    sideLimitLeft = sideLimitLeft + this._dataTackroom.width;
                }
            } else if( 'right' === this._dataTackroom.position ) {
                if( this._tackDoor && key === this._tackDoor.key ) {
                    sideLimitLeft   = startEnd - this._dataTackroom.width + componentWidth + _this._offsetDistance + horizontalOffset + this._frameDepth;
                } else {
                    startEnd = startEnd - this._dataTackroom.width;
                }
            } else {
                if( this._tackDoor && key === this._tackDoor.key ) {
                    sideLimitLeft   = - this._dataTackroom.width / 2 + ( componentWidth / 2 ) + this._frameDepth;
                    startEnd        = this._dataTackroom.width / 2 - ( componentWidth / 2 ) - this._frameDepth;
                }
            }

            let showAlert = false;
            if( targetPosition.z < sideLimitLeft ) {
                targetPosition.z = sideLimitLeft;
                showAlert = key === this._tackDoor.key;
            }

            if( targetPosition.z > startEnd ) {
                targetPosition.z = startEnd;
                showAlert = key === this._tackDoor.key;
            }

            if( showAlert ) {
                this.createAlert( NOTICES.tackdoorMoveOutSide );
            }
        }
    }

    /**
     * Validate position when moving component
     * @param args 
     * @returns 
     */
    validateMoveComponentV2 = ( args ) => {
        const { targetPosition, component, componentWidth, componentHeight, anchor } = args;
        const side = component['side'].replace( 'top_', '' );
        const sideMesh = this.getWallBySide( side, true );
        if( ! sideMesh ) return false;

        const horizontalOffset = this.getHorizontalOffset( component['type'] );
        const offsetWall = this._offsetDistance + horizontalOffset;
        const wallBodingBox = sideMesh.getBoundingInfo().boundingBox;
        const width = this.getWidth();
        const depth = this.getDepth();
        
        if( 'floor' !== side ) {
            let limitStart;
            let limitEnd;
            let prefix;
            if( helpers.hasKeyInArray( zSide, side ) ) {
                prefix = 'z';
                limitStart = -depth / 2 > wallBodingBox.minimumWorld.z ? ( -depth / 2 ) : wallBodingBox.minimumWorld.z;
                limitEnd = depth / 2 < wallBodingBox.maximumWorld.z ? depth / 2 : wallBodingBox.maximumWorld.z;
            } else {
                prefix = 'x';
                limitStart = -width / 2 > wallBodingBox.minimumWorld.x ? ( -width / 2 ) : wallBodingBox.minimumWorld.x;
                limitEnd = width / 2 < wallBodingBox.maximumWorld.x ? width / 2 : wallBodingBox.maximumWorld.x;
                if( this.isSidePorch() || ( this.isCornerPorchLeft() && 'front' === side ) ) {
                    limitStart = limitStart + this._porchWidth;
                }
            }

            limitStart = limitStart + ( componentWidth / 2 ) + offsetWall;
            limitEnd = limitEnd - ( componentWidth / 2 ) - offsetWall;
    
            if( targetPosition[`${prefix}`] <= limitStart || targetPosition[`${prefix}`] >= limitEnd ) {
                if( undefined === this._positionValidOnMove ) {
                    this._positionValidOnMove = targetPosition.clone();
                }
                return false;
            }
        }

        const isInside = this.isComponentInSideMeshes( { targetPosition, component, componentWidth, componentHeight, anchor, side } );
        if( ! isInside && undefined === this._positionValidOnMove ) {
            this._positionValidOnMove = targetPosition.clone();
        }
            
        if( isInside ) {
            this._positionValidOnMove = undefined;
        }

        return isInside;
    }

    /**
     * Check ray interset with mesh
     * @param ray 
     * @param meshes 
     * @returns 
     */
    isRayIntersetMesh = ( ray, meshes ) => {
        let totalMesh = meshes.length;
        for( let i = 0; i < meshes.length; i++ ) {
            var pickInfoFront = ray.intersectsMesh( meshes[i] as DeepImmutableObject<AbstractMesh> );
            if( ! pickInfoFront.hit ) {
                totalMesh--;
            }
        }
        return totalMesh > 0;
    }

    /**
     * Verifies the validity of a given mesh and returns a list of validation results.
     *
     * @param {Object} mesh 
     * @returns {Array} 
     */
    getListMeshVerify = ( mesh ) => {
        const meshName = mesh.id;
        const listMeshVerify = [ mesh ];
        const sideMeshWainscoting = this.getMeshByName( `wainscoting_${meshName}` );
        const sideMeshWainscotingTrim = this.getMeshByName( `wainscoting_wainscoting_${meshName}_trim` );
        const sideMeshAccentGable = this.getMeshByName( `accent_gable_${meshName}` );
        if( sideMeshWainscoting ) listMeshVerify.push( sideMeshWainscoting );
        if( sideMeshWainscotingTrim ) listMeshVerify.push( sideMeshWainscotingTrim );
        if( sideMeshAccentGable ) listMeshVerify.push( sideMeshAccentGable );

        return listMeshVerify;
    }

    /**
     * Calculates the direction vectors of rays based on the component's position, dimensions, and orientation.
     * 
     * This function generates a set of direction vectors based on the specified side of the component, 
     * the anchor point's vertical position, the target position, and the component's height and width. 
     * The directions are represented as `Vector3` objects, which define points in 3D space to which rays 
     * can be cast from the anchor point.
     *
     * The generated direction vectors may represent the edges or corners of a bounding box 
     * based on the component's orientation relative to the `side` and the `anchor.vertical` position.
     * 
     * @param {Object} args - The arguments object containing the parameters for the function.
     * @param {Object} args.targetPosition - The position of the component in 3D space (with `x`, `y`, and `z` coordinates).
     * @param {number} args.componentWidth - The width of the component, used to calculate ray directions.
     * @param {number} args.componentHeight - The height of the component, used to calculate ray directions.
     * @param {Object} args.anchor - The anchor point of the component, which may influence its position or orientation.
     * @param {string} args.side - The side of the component (e.g., 'front', 'back', 'left', 'right') that determines the orientation of the rays.
     * 
     * @returns {Array} - An array of `Vector3` objects representing the calculated direction vectors. 
     *                    These vectors define the directions of rays based on the provided parameters.
     */
    getDirectionsRay = ( args ) => {
        const { side, targetPosition, componentWidth, componentHeight, anchor, component, isUseTargetPosition } = args;
        let directions;
        const halfWidth = componentWidth / 2;

        if( 'floor' === side ) {
            if( isUseTargetPosition ) {
                const halfHeight = componentHeight / 2;
                directions = [
                    new Vector3( targetPosition.x - halfWidth, 0, targetPosition.z - halfHeight ),
                    new Vector3( targetPosition.x - halfWidth, 0, targetPosition.z + halfHeight ),
                    new Vector3( targetPosition.x + halfWidth, 0, targetPosition.z + halfHeight ),
                    new Vector3( targetPosition.x + halfWidth, 0, targetPosition.z - halfHeight ),
                ];
            } else {
                const boxInfo = this.getBoundingBoxLimit( component.meshes );
                directions = [
                    new Vector3( boxInfo.minX, 0, boxInfo.minZ ),
                    new Vector3( boxInfo.minX, 0, boxInfo.maxZ ),
                    new Vector3( boxInfo.maxX, 0, boxInfo.maxZ ),
                    new Vector3( boxInfo.maxX, 0, boxInfo.minZ ),
                ];
            }
        } else if( helpers.hasKeyInArray( zSide, side ) ) {
            if( 'ground' === anchor.vertical ) {
                directions = [
                    new Vector3( 0, targetPosition.y + componentHeight, targetPosition.z - halfWidth ),
                    new Vector3( 0, targetPosition.y + componentHeight, targetPosition.z + halfWidth ),
                ];
            } else {
                directions = [
                    new Vector3( 0, targetPosition.y - componentHeight, targetPosition.z - halfWidth ),
                    new Vector3( 0, targetPosition.y, targetPosition.z - halfWidth ),
                    new Vector3( 0, targetPosition.y - componentHeight, targetPosition.z + halfWidth ),
                    new Vector3( 0, targetPosition.y, targetPosition.z + halfWidth ),
                ];
            }
        } else {
            if( 'ground' === anchor.vertical ) {
                directions = [
                    new Vector3( targetPosition.x - halfWidth, targetPosition.y + componentHeight, 0 ),
                    new Vector3( targetPosition.x + halfWidth, targetPosition.y + componentHeight, 0 ),
                ];
            } else {
                directions = [
                    new Vector3( targetPosition.x - halfWidth, targetPosition.y - componentHeight, 0 ),
                    new Vector3( targetPosition.x - halfWidth, targetPosition.y, 0 ),
                    new Vector3( targetPosition.x + halfWidth, targetPosition.y - componentHeight, 0 ),
                    new Vector3( targetPosition.x + halfWidth, targetPosition.y, 0 ),
                ];
            }
        }

        return directions;
    }

    /**
     * Retrieves the bounding box limits for a given set of meshes.
     * 
     * This function filters the provided meshes to find the one with a name containing '_bb_obj',
     * and then extracts the minimum and maximum world coordinates of its bounding box.
     * 
     * @param {Array} meshes - The array of meshes to search through.
     * @returns {Object} - An object containing the minimum and maximum x, y, and z coordinates of the bounding box.
     */
    getBoundingBoxLimit = (meshes) => {
        // Filter the meshes to find the bounding box mesh
        const boundingBoxMesh = this.filterByValue( meshes, 'name', '_bb_obj' ) as Mesh;
        const boundingBoxInfo = boundingBoxMesh.getBoundingInfo().boundingBox;
        
        // Initialize bounding box coordinate variables
        let minX = 0, maxX = 0, minY = 0, maxY = 0, minZ = 0, maxZ = 0;

        // Extract the world coordinates from the bounding box info, if available
        if( boundingBoxInfo && boundingBoxInfo.minimumWorld ) {
            minX = helpers.toFixed3( boundingBoxInfo.minimumWorld.x );
            maxX = helpers.toFixed3( boundingBoxInfo.maximumWorld.x );
            minY = helpers.toFixed3( boundingBoxInfo.minimumWorld.y );
            maxY = helpers.toFixed3( boundingBoxInfo.maximumWorld.y );
            minZ = helpers.toFixed3( boundingBoxInfo.minimumWorld.z );
            maxZ = helpers.toFixed3( boundingBoxInfo.maximumWorld.z );
        }

        // Return the bounding box limits as an object
        return { minX, maxX, minY, maxY, minZ, maxZ };
    }

    /**
     * Determines whether a component is fully contained within a specified side mesh.
     * 
     * @param {Object} args - The arguments object containing the parameters for the function.
     * @param {Object} args.targetPosition - The position of the component in 3D space (with `x`, `y`, and `z` coordinates).
     * @param {number} args.componentWidth - The width of the component, used to calculate ray directions.
     * @param {number} args.componentHeight - The height of the component, used to calculate ray directions.
     * @param {Object} args.anchor - The anchor point of the component, which may influence its position or orientation.
     * @param {string} args.side - The side of the component (e.g., 'front', 'back', 'left', 'right') that determines the orientation of the rays.
     * 
     * @returns {boolean} - Returns `true` if the component is inside the specified side mesh, `false` if it is outside.
     */
    isComponentInSideMeshes = ( args ) => {
        const { targetPosition, component, componentWidth, componentHeight, anchor, side, isUseTargetPosition } = args;
        let directions = this.getDirectionsRay( { targetPosition, componentWidth, componentHeight, anchor, side, component, isUseTargetPosition } );
        const sideMesh = this.getWallBySide( side, true );
        if( ! sideMesh ) return false;
        let listMeshVerify = this.getListMeshVerify( sideMesh );
        let isInside = true;
        var pointFront = 'floor' === side ? new Vector3( 0, 1, 0 ) : ( helpers.hasKeyInArray( zSide, side ) ? new Vector3( 1, 0, 0 ) : new Vector3( 0, 0, 1 ) );
        var pointBack = 'floor' === side ? new Vector3( 0, -1, 0 ) : ( helpers.hasKeyInArray( zSide, side ) ? new Vector3( -1, 0, 0 ) : new Vector3( 0, 0, -1 ) );
        pointFront.normalize();
        pointBack.normalize();
        for( let direction of directions ) {
            var rayFront = new Ray( direction, pointFront ); 
            var rayBack = new Ray( direction, pointBack );
            if( ! this.isRayIntersetMesh( rayFront,listMeshVerify ) && ! this.isRayIntersetMesh( rayBack, listMeshVerify ) ) {
                isInside = false;
            }
        }
        return isInside;
    }

    nearestPointToLine( point: Vector3, lineStart: Vector3, lineEnd: Vector3 ) {
        const lineDirection   = lineEnd.subtract( lineStart );
        const pointDirection  = point.subtract( lineStart );

        const t = Vector3.Dot( pointDirection, lineDirection ) / lineDirection.lengthSquared();

        if( t < 0 ) {
            return lineStart;
        } else if ( t > 1 ){
            return lineEnd;
        }

        return lineStart.add( lineDirection.scale( t ) );
    }

    /**
     * Set moveable for component
     * 
     * @param key Component key
     */
    setDragForComponent = async ( key ) => {
        const _this                 = this;
        let component               = _this.getComponentByKey( key );
        const meshes                = component.meshes;
        const firstMesh             = meshes[0];
        const meshHighlight         = _this.filterByValue( meshes, 'name', '_bb_obj' ) as Mesh;
        const behavior              = _this.getBehavior( component['name'], component['type'] );
        const isMoveHorizontal      = _this.isMoveHorizontal( behavior );
        const componentHeight       = _this.getComponentHeight( meshes, behavior );
        const componentWidth        = _this.getComponentWidth( meshes, behavior, component.name, isMoveHorizontal );
        const componentDepth        = _this.getComponentDepth( meshes );
        const side                  = component["side"];
        
        _this.enableHighlightMesh( meshHighlight, new Color4( 0, 0, 0, 1 ) );
        _this.addEditButton( { component, firstMesh, type: behavior, componentName: component.name, componentWidth, componentDepth, componentHeight, side: component["side"] } );
        
        if( 'diagonal_right' === component['side'] ) {
            return;
        }

        const depth                 = _this.getDepth();
        const anchor                = _this._componentTypeSettings[behavior]["anchor"];
        let moveable                = { horizontal: true, vertical: true };
        if( ! _this.isGableDormer( component.name ) ) {
            moveable                = { ..._this._componentTypeSettings[behavior]['moveable'] };
        }

        const tanRoofLowerCorner    = _this.getTanRoofPorch();
        const tanRoofLeftDormer     = this.tanRoofLeftDormer();
        const sidePorchEndX         = _this.getLimitComponentXRoofPorch( componentHeight, componentWidth ) > 0 ? _this.getLimitComponentXRoofPorch( componentHeight, componentWidth ) : 0;
        const tanRoofUpperCorner    = helpers.getTanFromDegrees( _this._roofUpperDegree ? _this._roofUpperDegree : _this._initRoofUpperDegree );
        const limitUpperVertical    = _this._bones['b_d_l1'] ? Math.abs( _this._bones['b_d_l1'].position.z ) : depth / 2;
        const limitLowerVertical    = _this._bones['b_d_l2'] ? Math.abs( _this._bones['b_d_l2'].position.z ) : depth / 2;
        _this._dragBehavior         = new PointerDragBehavior( { dragPlaneNormal: new Vector3( 0, 0, 0 ) } );
        if( isMoveHorizontal ) {
            _this._dragBehavior = new PointerDragBehavior( { dragAxis: new Vector3( 1, 0, 0 ) } );
        } else {
            if( moveable.horizontal && ! moveable.vertical ) {
                let dragVector = 'roof' === component["side"] ? new Vector3( 0, 0, 1 ) : new Vector3( 1, 0, 0 );
                _this._dragBehavior = new PointerDragBehavior( { dragAxis: dragVector } );
            } else if( ! moveable.horizontal && moveable.vertical ) {
                _this._dragBehavior = new PointerDragBehavior( { dragAxis: new Vector3( 0, 1, 0 ) } );
            } else if( moveable.horizontal && moveable.vertical)  {
                _this._dragBehavior = new PointerDragBehavior( { dragPlaneNormal: new Vector3( 0, 0, 1 ) } );
            }
        }

        if( this.isMoveAlongFloor( component.name ) ) {
            const boundingBoxMesh = this.filterByValue( component.meshes, 'name', '_bb_obj' ) as Mesh;
            const boundingBox = boundingBoxMesh.getBoundingInfo().boundingBox;
            
            firstMesh.ellipsoid = new Vector3(
                (boundingBox.maximumWorld.x - boundingBox.minimumWorld.x) / 2,
                (boundingBox.maximumWorld.y - boundingBox.minimumWorld.y) / 2,
                (boundingBox.maximumWorld.z - boundingBox.minimumWorld.z) / 2
            );
            firstMesh.ellipsoidOffset = boundingBox.centerWorld.subtract( firstMesh.position );

            _this._dragBehavior = new PointerDragBehavior( { dragPlaneNormal: new Vector3( 0, 1, 0 ) } );
            _this._dragBehavior.moveAttached = false;
            _this._dragBehavior.updateDragPlane = false;

            _this._dragBehavior.onDragStartObservable.add( e => {
                const boundingBoxMesh = this.filterByValue( component.meshes, 'name', '_bb_obj' ) as Mesh;
                const boundingBox = boundingBoxMesh.getBoundingInfo().boundingBox;
            
                firstMesh.ellipsoid = new Vector3(
                    (boundingBox.maximumWorld.x - boundingBox.minimumWorld.x) / 2,
                    (boundingBox.maximumWorld.y - boundingBox.minimumWorld.y) / 2,
                    (boundingBox.maximumWorld.z - boundingBox.minimumWorld.z) / 2
                );
                firstMesh.ellipsoidOffset = boundingBox.centerWorld.subtract( firstMesh.position );
            } );

            _this._dragBehavior.onDragObservable.add( ( eventData ) => {
                firstMesh.moveWithCollisions( eventData.delta );
            } );
        }


        firstMesh.addBehavior( _this._dragBehavior );

        _this._dragBehavior.validateDrag = targetPosition => {
            const sidesIgnore = ['roof_left', 'roof_right', 'roof']; 
            if( _this._isNewMovingComponent && -1 === sidesIgnore.indexOf( component['side'] ) ) {
                const previousPosition = firstMesh.position.clone(); 
                const distance = Vector3.Distance( previousPosition, targetPosition );
                const steps = Math.ceil( distance / 0.1 ); 
                let interpolatedPosition = previousPosition.clone();
                let isValid = true;
                for( let i = 1; i <= steps; i++ ) {
                    Vector3.LerpToRef( previousPosition, targetPosition, i / steps, interpolatedPosition );
                    let newArgs = { key, targetPosition: interpolatedPosition, component, componentWidth, componentHeight, firstMesh, anchor };
                    if( ! _this.validateMoveComponentV2( newArgs ) ) {
                        isValid = false;
                        break;
                    }
                }
                if( ! isValid && _this._positionValidOnMove ) {
                    if( helpers.hasKeyInArray( zSide, component['side'] ) ) {
                        targetPosition.z = _this._positionValidOnMove.z;
                    } else {
                        targetPosition.x = _this._positionValidOnMove.x;
                    }
                    if( this.applyFixedHeightWindows( component['side'], component['type'], component['name'] ) ) {
                        targetPosition.y = this.getWindowsFixedPositionY( component['type'], componentHeight, component['side'] )
                    } else {
                        targetPosition.y = _this._positionValidOnMove.y;
                    }
                } else {
                    if( this.applyFixedHeightWindows( component['side'], component['type'], component['name'] ) ) {
                        if( this.isFollowMainDoor() ) {
                            if( 0 !== this._doorFixedTarget.length )
                                targetPosition.y = this.getWindowsFixedPositionY( component['type'], componentHeight, component['side'] );
                        } else {
                            targetPosition.y = this.getWindowsFixedPositionY( component['type'], componentHeight, component['side'] );
                        }
                    }
                }
            } else {
                let args = { key, targetPosition, component, anchor, componentWidth, componentHeight, firstMesh, tanRoofLowerCorner, tanRoofUpperCorner, limitUpperVertical, tanRoofLeftDormer, limitLowerVertical, sidePorchEndX, isMoveHorizontal };
                _this.validateMoveComponent( args );
            }
            
            return true;
        }

        let positionBeforeDrag = new Vector3( 0, 0, 0 );
        _this._dragBehavior.onDragStartObservable.add( e => {
            this._positionValidOnMove = undefined;
            positionBeforeDrag = component.meshes[0].position.clone();
            _this.removeEditButton();
        })

        _this._dragBehavior.onDragObservable.add( e => {
            if( helpers.isSeadevSite() ) {
                this.updateDistanceLines( component );
            } else {
                this.updateLine( firstMesh, componentHeight, component['side'], anchor, behavior );
            }
            _this.handleHighlightMesh( component['side'], key, component );
        });

        _this._dragBehavior.onDragEndObservable.add( e => {
            component = _this.updateSideComponent( component, positionBeforeDrag );
            if( helpers.isSeadevSite() ) {
                this.updateDistanceLines( component );
            } else {
                this.updateLine( firstMesh, componentHeight, component['side'], anchor, behavior );
            }
            window.dispatchEvent( new CustomEvent( 'after-drag' ) );
            _this.updateWallComponentsPosition( component['side'], component['key'], component, positionBeforeDrag );
            _this.addEditButton( { component, firstMesh, type: behavior, componentName: component.name, componentWidth, componentDepth, componentHeight, side: component["side"] } );
            _this.updateHoleWall( component['side'] );
            _this.updateHoleRoof( component['side'] );
            if( side !== component['side'] ) {
                _this.updateHoleWall( side );
                _this.updateHoleRoof( side );
            }
            _this.updateFrame( component['side'] );
            _this.handleIntersectKickBoard( component['side'] );
            _this.handleIntersectKickBoard( component['side'], 'frame_kick_board' );
            _this.disableHighlightFrame( component['side'] );
        });
    }

    handleAddCursorPointer = () => {
        const _this = this;
        for( const side in _this._walls ) {
            for( const key in _this._walls[side]["items"] ) {
                const component = _this._walls[side]["items"][key];
                const behavior  = _this.getBehavior( component['name'], component['type'] );
                const moveable  = _this._componentTypeSettings[behavior]['moveable'];
                if( ( ! moveable.horizontal && ! moveable.vertical ) || null !== component.parent ) {
                    continue;
                }

                if( 'structure_rancher' === this._shedType && ( 'top_front' === component.side || 'top_back' === component.side ) ) {
                    continue;
                }

                if( 'structure_monitorbarns_helmuth' === this._shedType && 'cupola' !== component.type ) {
                    continue;
                }

                const meshes            = component["meshes"];
                const componentHeight   = _this.getComponentHeight( meshes, behavior );
                const anchor            = _this._componentTypeSettings[behavior]['anchor'];
                const childMeshes       = component.meshes[0].getChildMeshes();
                
                childMeshes.forEach( mesh => {
                    if( null !== mesh && null === mesh.metadata || undefined === mesh.metadata.key ) {
                        return;
                    }

                    mesh.actionManager  = new ActionManager( this._scene );
                    const parentMesh    = mesh._parentNode === null ? mesh : mesh._parentNode;
                    mesh.actionManager.registerAction(
                        new ExecuteCodeAction(
                            ActionManager.OnPointerOverTrigger, obj => {
                                let hoverCursor = 'default';
                                if( 'front' === obj.meshUnderPointer.name || 'roof_left' === side || 'roof_right' === side || 'roof' === side || _this.isMoveAlongFloor( component['name'] ) ) {
                                    const target = _this.getComponentByKey( obj.meshUnderPointer.metadata.key );
                                    if( false !== target ) {
                                        if( helpers.isSeadevSite() ) {
                                            this.updateDistanceLines( component );
                                        } else {
                                            this.updateLine( parentMesh, componentHeight, side, anchor, behavior );
                                        }
                                        if( _this.ignoreHandleTackDoor( side ) || _this.disableMoveItem( obj.meshUnderPointer.metadata.key ) || this.isItemBlock( obj.meshUnderPointer.metadata.key ) ) {
                                            hoverCursor = 'not-allowed';
                                        } else {
                                            hoverCursor = 'pointer';
                                        }
                                    }
                                }

                                _this._scene.hoverCursor = hoverCursor;
                            }
                        )
                    );

                    mesh.actionManager.registerAction(
                        new ExecuteCodeAction(
                            ActionManager.OnPointerOutTrigger, obj => {
                                _this.removeLine();
                                _this.removeNoticeText();
                            }
                        )
                    );
                });
            }
        }

        _this.handleDisablePickable( );
    }

    removeLine = () => {
        const vectorZero = new Vector3( 0, 0, 0 );

        if( this._arrowHorizontalLeftStart ) {
            this._arrowHorizontalLeftStart.position = vectorZero;
            this._arrowHorizontalLeftStart.visibility = 0;

            this._arrowHorizontalLeftEnd.position = vectorZero;
            this._arrowHorizontalLeftEnd.visibility = 0;

            this._arrowHorizontalRightStart.position = vectorZero;
            this._arrowHorizontalRightStart.visibility = 0;

            this._arrowHorizontalRightEnd.position = vectorZero;
            this._arrowHorizontalRightEnd.visibility = 0;

            this._arrowVerticalTopStart.position = vectorZero;
            this._arrowVerticalTopStart.visibility = 0;

            this._arrowVerticalTopEnd.position = vectorZero;
            this._arrowVerticalTopEnd.visibility = 0;

            this._arrowVerticalBottomStart.position = vectorZero;
            this._arrowVerticalBottomStart.visibility = 0;

            this._arrowVerticalBottomEnd.position = vectorZero;
            this._arrowVerticalBottomEnd.visibility = 0;

            this._lineVerticalTopOptions.points = [ vectorZero, vectorZero ];
            this._lineVerticalTopOptions.instance = this._lineVerticalTop;
            this._lineVerticalTop = MeshBuilder.CreateLines( 'lineVerticalTop', this._lineVerticalTopOptions );

            this._lineVerticalBottomOptions.points = [ vectorZero, vectorZero ];
            this._lineVerticalBottomOptions.instance = this._lineVerticalBottom;
            this._lineVerticalBottom = MeshBuilder.CreateLines( 'lineVerticalBottom', this._lineVerticalBottomOptions );

            this._lineHorizontalLeftOptions.points = [ vectorZero, vectorZero ];
            this._lineHorizontalLeftOptions.instance = this._lineHorizontalLeft;
            this._lineHorizontalLeft = MeshBuilder.CreateLines( 'lineHorizontalLeft', this._lineHorizontalLeftOptions );

            this._lineHorizontalRightOptions.points = [ vectorZero, vectorZero ];
            this._lineHorizontalRightOptions.instance = this._lineHorizontalRight;
            this._lineHorizontalRight = MeshBuilder.CreateLines( 'lineHorizontalRight', this._lineHorizontalRightOptions );
        }
    }

    removeNoticeText = () => {
        if( this._distanceTextHolderHorizontalLeft ) {
            this._distanceTextHolderVerticalTop.visibility = 0;
            this._distanceTextHolderVerticalBottom.visibility = 0;
            this._distanceTextHolderHorizontalLeft.visibility = 0;
            this._distanceTextHolderHorizontalRight.visibility = 0;
        }
    }
/**
 * This function use to checked models has door height highless wall height at front/back wall
 * @returns Boolean
 */
    isSpecialShed = () => {
        const sheds = [ 'barn', 'barn_without_fe_overhangs', 'greenhouse', 'barn_with_extra_trims', 'apex_barn', 'apexbarn_with_horizontal_trims', 'structure_barn_with_sprocket', 'structure_barn_with_sprocket_wood_siding', 'structure_barn_standard_mwshed', 'structure_barn_standard_mwshed', 'structure_country_cottage', 'structure_mainus_barn', 'structure_lofted_horserunin', 'structure_aframe_horserunin', 'structure_barn_corner_porch', 'structure_cardinal_lofted_cabin', 'structure_porch_barn' ];
        if( -1 !== sheds.indexOf( this._shedType ) || this.isChickenCoop() ) {
            return true;
        }

        return false;
    }

    getWallHeightBySide = ( side ) => {
        if( 'dormer' === side ) {
            return this._dormerHeight;
        }

        if( 'roof_left' === side || 'roof_right' === side ) {
            if( this._bones['b_b_t'] && this._bones['b_b_r2'] ) {
                return helpers.toFixed3( Vector3.Distance( this._bones['b_b_t'].position, this._bones['b_b_r2'].position ) );
            } else {
                return helpers.toFixed3( ( this.getWidth() / 2 ) / helpers.getCosFromDegrees( this.getRoofCorrectionDegree() ) );
            }
        }

        if( 'structure_lean_to_with_front_porch' === this._shedType && 'left' === side ) {
            return this._middleHeight;
        }

        if( this.isPorchSide( side ) ) {
            return this.getPorchHeight();
        }

        if( 'structure_country_cottage' === this._shedType && 'left' === side) {
            return this.getBuildingHeight();
        }

        if( 'structure_classic_aframe_dormer' === this._shedType && ( 'left' === side || 'front' === side || 'back' === side ) ) {
            return this.getBuildingHeight();
        }

        if( ( 'lean_to' === this._shedType || 'carport_lean_to_standalone' === this._shedType ) && 'left' === side ) {
            return this.getWallHeight() + this.getRoofPitchHeight();
        }

        if( 'mainus_quaker' === this._shedType && 'right' === side ) {
            return this.getWallHeight() - 1;
        }

        if( 'structure_saltbox_mwshed' === this._shedType && 'left' === side ) {
            return this.getWallHeight() + 14/12;
        }

        if( 'front' === side && ( 'structure_lakeside_bunkie_gable_roof' === this._shedType || 'structure_lakeside_bunkie_gambrel_roof' === this._shedType ) ) {
            return this.getWallHeight();
        }

        if( 'front' === side && this.isFrontPorch() && this.isCellingPorch() && this.isSpecialShed() ) {
            let trimLength = this.isPorchHasTrim() ? this._trimLength : 0;
            return this.getPorchBottom() - trimLength;
        }

        if( this.isMiddlePorch() && ( 'left_front' === side || 'left_back' === side || 'right_inner_porch' === side || 'front_inner_porch' === side || 'left_inner_porch' === side ) ) {
            return this.getWallHeight() - this.getDoorFixedHeight( side );
        }

        if( this.isDeluxeCornerLeftPorch() ) {
            if( 'front' === side || 'diagonal_right' === side || 'inner_right' === side || 'inner_front' === side ) {
                return this.getWallHeight() - this.getDoorFixedHeight( side );
            }

            return this.getWallHeight();
        }

        if( 'structure_ranch_cabin' === this._shedType ) {
            if( 'inner_back' === side || 'inner_left' === side ) {
                return this.getWallHeight() - this.getDoorFixedHeight( side );
            }

            return this.getWallHeight();
        }

        if( ( 'structure_monitorbarns_helmuth' === this._shedType || 'structure_rancher' === this._shedType ) && ( 'top_front' === side || 'top_back' === side ) ) {
            return this._bones['b_b_t'].position.y - this._bones['b_b_l3'].position.y;
        }

        if( 'structure_old_west' === this._shedType && ( 'front' === side || 'back' === side ) ) {
            return this.getBuildingHeight();
        }

        if( this.is2StoryLeanTo() && ( 'top_left' === side || 'top_right' === side ) ) {
            return this._bones['b_b_l2'].position.y - this.getWallHeight();
        }

        if( this.isSidePorchCabin() ) {
            if( 'left' === side ) {
                return helpers.toFixed3( this.getWallHeight() + 0.51 );
            } else if( 'right' === side ) {
                return this._bones['b_f_r6'].position.y;
            }
        }

        return ( 'front' === side || 'back' === side ) && this.isSpecialShed() ? this.getBuildingHeight() : this.getWallHeight();
    }

    getWallWidthBySide = ( side ) => {
        let width = this.getWidth();
        let depth = this.getDepth();
        if( this.isSidePorch() ) width = width - this._porchWidth;

        if( this.isCornerPorchLeft() ){
            if( 'front' === side ){
                width = width - constants.cornerWidth;
            }else if( 'left' === side ){
                depth = depth - constants.cornerDepth;
            }else if( 'inner_front' === side ){
                width = constants.cornerWidth;
            }else if( 'inner_left' === side ){
                depth = constants.cornerDepth;
            }
        } else if( this.isDeluxeCornerLeftPorch() ) {
            switch( side ) {
                case 'front':
                    width = this.deluxePorchGetFrontWidth();
                    break;
                case 'inner_front':
                    width = this.deluxePorchGetPorchWidth();
                    break;
                case 'inner_right':
                    depth = constants.deluxeDepth;
                    break;
                case 'left':
                    depth = this.getDepth() - constants.deluxeDepth;
                    break;
                case 'right':
                    depth = this.getDepth() - 3 * constants.deluxeDepth;
                    break;
                case 'diagonal_right':
                    depth = 5; // distance from bone B_F_R6 to B_F_L4
                    break;
            }
        }

        if( this.isMiddlePorch() ) {
            if( 'left_front' === side || 'left_back' === side ) {
                depth = ( depth - constants.middleWidth ) / 2;
            } else if( 'left_inner_porch' === side || 'right_inner_porch' === side ) {
                width = constants.middleDepth;
            } else if( 'front_inner_porch' === side ) {
                depth = constants.middleWidth;
            }
        } else if( this.isDoubleGable() ) {
            switch( side ) {
                case 'left_front':
                case 'left_back':
                    depth = this.getHalfGableWidth() * 2;
                    break;
                case 'left_inner_porch':
                case 'right_inner_porch':
                    width = constants.doubleGablePorchDepth;
                    break;
                case 'front_inner_porch':
                    depth = this.doubleGableGetPorchWidth();
                    break;
            }
        }

        if( 'structure_ranch_cabin' === this._shedType || this.isAframeSingleGable()) {
            switch( side ) {
                case 'back':
                    width = this.getWidth() - constants.cabinPorchDepth;
                    break;
                case 'inner_back':
                    width = constants.cabinPorchDepth;
                    break;
                case 'left':
                    depth = this._cabinWidth;
                    break;
                case 'inner_left':
                    depth = this.getDepth() - this._cabinWidth;
                    break;
            }
        }

        if( 'structure_monitorbarns_helmuth' === this._shedType && ( 'top_front' === side || 'top_back' === side ) ) {
            width = width / 3;
        }

        if( 'structure_rancher' === this._shedType && ( 'top_front' === side || 'top_back' === side ) ) {
            width = width / 2;
        }

        if( this.is2StoryLeanTo() && ( 'lean_to_front' === side || 'lean_to_back' === side ) ) {
            width = atticModel.leanToWidth;
        }

        return 'front' === side || 'top_front' === side || 'lean_to_front' === side || 'lean_to_right' === side || 'back' === side || 'top_back' === side || 'lean_to_back' === side || 'inner_front' === side || this.isPorchSide( side ) || 'left_inner_porch' === side || 'right_inner_porch' === side || 'inner_back' === side ? width : depth;
    }

    isValidComponentByWall( position, componentType, componentWidth, componentHeight, side ) {
        const horizontalOffset  = this.getHorizontalOffset( componentType );
        const wallWidth         = this.getWallWidthBySide( side ) - 2 * horizontalOffset;
        let wallHeight          = this.getWallHeightBySide( side );
        
        if( 'door' === componentType ) {
            componentHeight = componentHeight + this.getDoorFixedHeight( side );
        }

        if( 'skylight_window' === componentType ) {
            if( 0 === position.x && helpers.toFixed3( position.y ) < helpers.toFixed3( this.getPositionYForSkylightWindow() ) ) {
                return false;
            }
        }

        if( this.isPorchSide( side ) ) {
            const porchHeight = Number( this.getBuildingHeight() - this.getPorchBottom() );
            if( componentHeight > porchHeight ) {
                return false;
            }

            const isValidSize = Number( ( position.y - componentHeight ).toFixed( 3 ) ) >= this.getPorchBottom();
            if( ! isValidSize ) {
                return false;
            }

            const tmpLimitX         = Math.abs( Number( ( this.getLimitComponentXRoofPorch( componentHeight, componentWidth ) + componentWidth / 2 ).toFixed( 3 ) ) );
            const isValidPosition   = - tmpLimitX <= ( position.x - componentWidth / 2 ) && tmpLimitX >= ( position.x + componentWidth / 2 );
            if( ! isValidPosition ) {
                return false;
            }
        }

        if( componentHeight > wallHeight ) {
            return false;
        }

        if( componentWidth > wallWidth ) {
            return false;
        }

        if( 'structure_dog_kennel' === this._shedType && 'back' === side && componentWidth > ( wallWidth / 2 ) && 'dogkennel_split' === this.getActiveLayout() ) {
            return false;
        }

        return true;
    }

    removeMeshes( meshes ) {
        meshes.forEach((mesh: Mesh, i) => {
            if (mesh != undefined && mesh != null) {
                mesh.dispose();
            }
        });
    }

    createNotice = message => {
        let template        = document.createElement( 'template' );
        template.innerHTML  = `<div class="wrap-notice">
            <div class="content"><div>${message}</div></div>
        </div>`;

        document.body.appendChild( template.content.firstChild );
    }

    createAlert( message, delay = 7000 ) {
        const $     = jQuery;
        const _this = this;
        if( _this._noticesList.indexOf( message ) == -1 ) {
            _this._noticesList.push( message );
        }

        if( _this._noticesList.length > 0 && $(`#js-custom-alert`).length === 0 ) {
            let template = document.createElement( 'template' );
            template.innerHTML = `<div id="js-custom-alert" class="ssb-custom-alert">
                <div class="content"></div>
            </div>`;

            document.body.appendChild( template.content.firstChild );
        }

        for( let i = 0; i < this._noticesList.length; i++ ) {
            if( $(`#js-custom-alert .content .item-${i}`).length === 0 && undefined !== this._noticesList[i] ) {
                const messageElement = $(`
                    <div class="item item-${i}">
                        <div class="message">${this._noticesList[i]}</div>
                        <button class="js-close-alert js-close-alert-${i}">Got It</button>
                    </div>
                `).hide();
                messageElement.appendTo($(`#js-custom-alert .content`)).slideDown(300);

                $(`.js-close-alert-${i}`).click(function() {
                    $(`#js-custom-alert .content .item-${i}`).slideUp( 300, () => {
                        $(this).remove();
                        delete _this._noticesList[i];
                        if( _this._noticesList.filter(Boolean).length === 0 ) {
                            $('#js-custom-alert').remove();
                            _this._noticesList = [];
                        }
                    });
                });

                setTimeout( () => {
                    $(`.js-close-alert-${i}`).trigger('click');
                }, delay );
            }
        }
    }

    updateBoundingBoxColor( meshes, componentName, componentType ) {
        const boundingBox = this.filterByValue( meshes, 'name', '_bb_obj' ) as Mesh;
        if( 'windows' === componentType ) {
            switch( componentName ) {
                case 'window_slide_trim_14x21':
                    boundingBox.scaling.x = 0.57;
                    boundingBox.scaling.z = 1.56;
                    break;
                case 'window_slide_trim_18x27':
                    boundingBox.scaling.x = 0.595;
                    boundingBox.scaling.z = 1.56;
                    break;
                case 'window_slide_trim_18x36':
                    boundingBox.scaling.x = 0.61;
                    boundingBox.scaling.z = 1.56;
                    break;
                case 'window_slide_trim_24x36':
                    boundingBox.scaling.x = 0.654;
                    boundingBox.scaling.z = 1.56;
                    break;
                case 'window_recessed_18x27':
                    boundingBox.scaling.x = 0.725;
                    boundingBox.scaling.z = 1.06;
                    break;
                case 'window_recessed_18x36':
                    boundingBox.scaling.x = 0.722;
                    boundingBox.scaling.z = 1.06;
                    break;
                case 'window_recessed_24x36':
                    boundingBox.scaling.x = 0.662;
                    boundingBox.scaling.z = 1.06;
                    break;
                case 'window_slide_14x21':
                    boundingBox.scaling.x = 0.464;
                    boundingBox.scaling.z = 1.14;
                    break;
                case 'window_slide_18x27':
                    boundingBox.scaling.x = 0.528;
                    boundingBox.scaling.z = 1.14;
                    break;
                case 'window_slide_18x36':
                    boundingBox.scaling.x = 0.528;
                    boundingBox.scaling.z = 1.14;
                    break;
                case 'window_slide_24x36':
                    boundingBox.scaling.x = 0.588;
                    boundingBox.scaling.z = 1.14;
                    break;
                case 'window_octagon_18x18':
                    boundingBox.scaling.z = 0.67;
                    break;
            }
        }
    }

    async removeComponents( oldSize, newSize, face = 'exterior' ) {
        if( this.isSidePorch() ){
            newSize.width -= this._porchWidth;
            oldSize.width -= this._oldPorchWidth;
        }

        if( ! this.isCarport() && ! this.isDeluxeCornerLeftPorch() && Number( newSize.width ) >= Number( oldSize.width ) && Number( newSize.depth ) >= Number( oldSize.depth ) && Number( newSize.height ) >= Number( oldSize.height ) ) {
            const enableDistributed = parseInt( this._canvas.getAttribute( 'data-horizontal-distributed' ) );
            if( enableDistributed !== 1 ) {
                return;
            }

            if( ! this.canMoveComponentToRoof() ) {
                return;
            }
        }

        const _this     = this;
        let isRemove    = false;
        const offset    = 'interior' === face ? constants.interiorOffset : _this._offsetDistance;

        for( const side in _this._walls ) {
            if( this.ignoreHandleTackDoor( side ) || _this.ignoreFloor( side ) ) {
                continue;
            }
            const items         = _this.getComponentBySideAndType( side, face );
            let keys            = _this.sortComponentKeyBySize( items );
            let keyRemoves      = [];
            for( const key of keys ) {
                const component         = items[key];
                const name              = component.name;
                const type              = this.getBehavior( name, component.type );
                const horizontalOffset  = _this.getHorizontalOffset( type );
                const meshes            = component.meshes;

                if( 'ramp' === type || 'flower_box' === type || 'shutters' === type ) {
                    continue;
                }

                if( ( 'dormer' === type && ! _this.isValidDormer( meshes, name, type ) ) ) {
                    _this.deleteComponentV2( side, key, type );
                    isRemove = true;
                    keyRemoves.push(key);
                    continue;
                }
                
                const isMoveHorizontal  = _this.isMoveHorizontal( type );
                const componentWidth    = _this.getComponentWidth( meshes, type, name, isMoveHorizontal );
                const componentHeight   = _this.getComponentHeight( meshes, type );
                if( this.applyFixedHeightWindows( side, type, name ) ) {
                    const tmpYPosition = this.getWindowsFixedPositionY( type, componentHeight, side );
                    if( ( this.isFollowMainDoor() || this.isOffsetFromFloorTop() ) && componentHeight > tmpYPosition ) {
                        _this.deleteComponentV2( side, key, type );
                        isRemove = true;
                        keyRemoves.push(key);
                        continue;
                    }
                }
                
                const startX    = component.position2D.horizontal.firstPoint;
                const endX      = component.position2D.horizontal.lastPoint;
                const startY    = component.position2D.vertical.firstPoint;
                const endY      = component.position2D.vertical.lastPoint;
                
                let modelStartX = 'structure_saltbox_mwshed' === _this._shedType ? _this._bones['b_b_l1'].position.x + offset : - ( this.getWidth() / 2 - offset );
                let modelEndX   = 'structure_saltbox_mwshed' === _this._shedType ? _this._bones['b_b_r4'].position.x - offset : this.getWidth() / 2 - offset;
                if( this.isCornerPorchLeft() && 'front' === side ) {
                    modelStartX = _this._bones['b_f_l1'].position.x + offset;
                    modelEndX   = _this._bones['b_f_r1'].position.x - offset;
                }
                modelStartX     += horizontalOffset;
                modelEndX       -= horizontalOffset;
                
                let modelStartZ = - ( this.getDepth() / 2 - offset );
                let modelEndZ   = this.getDepth() / 2 - offset;
                if( 'roof' === side && 'cupola' === type && 'hip' === _this.getRoofShape() ) {
                    modelStartZ = _this._bones['r_b_t'].position.z;
                    modelEndZ   = _this._bones['r_f_t'].position.z;
                }

                if( this.isDoubleGable() && 'front_inner_porch' === side ) {
                    modelStartZ = _this._bones['b_b_m8'].position.z;
                    modelEndZ   = _this._bones['b_b_m4'].position.z;
                }

                modelStartZ     += horizontalOffset;
                modelEndZ       -= horizontalOffset;

                let modelStartY = 0;
                let modelEndY = 'front' === side || 'top_front' === side || 'back' === side || 'top_back' === side || 'porch' === side || 'roof' === side || 'roof_right' === side || 'roof_left' === side ? _this.getBuildingHeight() : _this.getWallHeightBySide( side );
                if( 'dormer' === side && _this._bones['b_b_t1'] && _this._bones['b_b_t2'] ) {
                    modelStartY = _this._bones['b_b_t1'].position.y;
                    modelEndY   = _this._bones['b_b_t2'].position.y;
                }

                if( _this.is2StoryLeanTo() && ( 'top_left' === side || 'top_right' === side ) ) {
                    modelEndY   = this._bones['b_b_l2'].position.y;
                }

                if( _this.isAframeSingleGable() ) {
                    modelEndY = this.getBuildingHeight();
                }

                let newX = component.position.x;
                let newY = component.position.y;
                let newZ = component.position.z;

                if( _this.isPorchSide( side ) ) {
                    modelStartY = this._initDistancePorchError < 0 ? newSize.height + this._initDistancePorchError : newSize.height;
                }

                let isCheckPosition = false;

                if( 'front' === side || 'back' === side || _this.isPorchSide( side ) || 'left_inner_porch' === side || 'right_inner_porch' === side ) {
                    if( startX < modelStartX || endX > modelEndX ) {
                        const args      = { point: newX, componentWidth, componentStart: startX, componentEnd: endX, modelStart: modelStartX, modelEnd: modelEndX };
                        newX            = _this.calculateNewPoint( args );
                        isCheckPosition = true;
                    }
                } else if( 'left' === side || 'top_left' === side || 'right' === side || 'top_right' === side || 'dormer' === side || 'roof_left' === side || 'roof_right' === side || 'roof' === side || 'left_front' === side || 'left_back' === side || 'front_inner_porch' === side ) {
                    if( startX < modelStartZ || endX > modelEndZ ) {
                        if( 'roof' === side && 'cupola' === type && 'hip' === _this.getRoofShape() && _this._bones['r_f_t'].position.z === 0 ) {
                            newZ = 0;
                        } else {
                            if( isMoveHorizontal ) {
                                const args  = { point: newX, componentWidth, componentStart: startX, componentEnd: endX, modelStart: modelStartX, modelEnd: modelEndX };
                                newX        = _this.calculateNewPoint( args );
                            } else {
                                const args  = { point: newZ, componentWidth, componentStart: startX, componentEnd: endX, modelStart: modelStartZ, modelEnd: modelEndZ };
                                newZ        = _this.calculateNewPoint( args );
                            }
                        }
                        
                        isCheckPosition = true;
                    }
                }

                if( startY < modelStartY || endY > modelEndY ) {
                    if( ( 'cupola' === type || 'light' === type ) && _this._bones['b_b_t'] ) {
                        newY = _this._bones['b_b_t'].position.y;

                        if( 'light' === type ){
                            newY = this.getPositionYForLight();
                        }
                    } else if( 'skylight_window' === type ) {
                        newY = this.getPositionYForSkylightWindow();
                    } else {
                        let tmpHeight = componentHeight;
                        if( 'dormer' === side ) {
                            tmpHeight = modelEndY;
                        }

                        newY = _this.getNewPositionY( newY, startY, modelStartY, tmpHeight, endY, modelEndY, side );
                    }

                    isCheckPosition = true;
                }

                if( newY > _this.getWallHeight() && this.canMoveComponentToRoof() && ( 'left' === side || 'front' === side ||'back' === side || this.isPorchSide( side ) ) ) {
                    let limitSide   = 0;
                    let removeItem  = false;
                    let errorSize   = this.isPorchSide( side ) ? this._initDistancePorchError : 0;
                    if( 'left' === side && ! this.isAframeSingleGable() ) {
                        let positionCurrent = Number( Math.abs( newZ ).toFixed( 3 ) );
                        if( 'gambrel' === this.getRoofShape() ) {
                            if( this._bones['b_d_r1'] && this._bones['b_d_t'] && newY > this._bones['b_d_r1'].position.y ) {
                                limitSide = this.roofLimitComponent( this._bones['b_d_t'], this._bones['b_d_r1'], newY, componentWidth, 'z', errorSize );
                            } else if( this._bones['b_d_r1'] && this._bones['b_d_r2'] && newY < this._bones['b_d_r1'].position.y && newY > this._bones['b_d_r2'].position.y ) {
                                limitSide = this.roofLimitComponent( this._bones['b_d_r1'], this._bones['b_d_r2'], newY, componentWidth, 'z', errorSize );
                            }
                        } else {
                            // A-frame
                            if( this._bones['b_d_r1'] && this._bones['b_d_t'] && newY > this._bones['b_d_r1'].position.y ) {
                                limitSide = this.roofLimitComponent( this._bones['b_d_t'], this._bones['b_d_r1'], newY, componentWidth, 'z', errorSize );
                            }
                        }

                        if( limitSide < positionCurrent ) {                            
                            removeItem = true;
                        }
                    } else if ( 'front' === side ||'back' === side || this.isPorchSide( side ) ) {
                        let positionCurrent = Number( Math.abs( newX ).toFixed( 3 ) );
                        if( 'gambrel' === this.getRoofShape() ) {
                            if( this.isDeluxeCornerLeftPorch() ) {
                                if( this._bones['b_p_l5'] && this._bones['b_p_t'] && newY > this._bones['b_p_l5'].position.y ) {
                                    limitSide = this.roofLimitComponent( this._bones['b_p_t'], this._bones['b_p_l5'], newY, componentWidth, 'x', errorSize );
                                } else if( this._bones['b_p_l5'] && this._bones['b_p_r2'] && newY < this._bones['b_p_l5'].position.y && newY > this._bones['b_p_r2'].position.y ) {
                                    limitSide = this.roofLimitComponent( this._bones['b_p_l5'], this._bones['b_p_r2'], newY, componentWidth, 'x', errorSize );
                                }
                            } else {
                                if( this._bones['b_f_l3'] && this._bones['b_f_t'] && newY > this._bones['b_f_l3'].position.y ) {
                                    limitSide = this.roofLimitComponent( this._bones['b_f_t'], this._bones['b_f_l3'], newY, componentWidth, 'x', errorSize );
                                } else if( this._bones['b_f_l3'] && this._bones['b_f_r2'] && newY < this._bones['b_f_l3'].position.y && newY > this._bones['b_f_r2'].position.y ) {
                                    limitSide = this.roofLimitComponent( this._bones['b_f_l3'], this._bones['b_f_r2'], newY, componentWidth, 'x', errorSize );
                                }
                            }
                        } else {
                            // A-frame
                            if( this._bones['b_f_r2'] && this._bones['b_f_t'] && newY > this._bones['b_f_r2'].position.y ) {
                                limitSide = this.roofLimitComponent( this._bones['b_f_t'], this._bones['b_f_r2'], newY, componentWidth, 'x', errorSize );
                            } else if( this._bones['b_p_l2'] && this._bones['b_p_t'] && newY > this._bones['b_p_l2'].position.y ) {
                                limitSide = this.roofLimitComponent( this._bones['b_p_t'], this._bones['b_p_l2'], newY, componentWidth, 'x', errorSize );
                            } else if( 'structure_aframe_genesis' === this._shedType && this._bones['b_f_t'] && this._bones['b_f_r1'] ) {
                                limitSide = this.roofLimitComponent( this._bones['b_f_t'], this._bones['b_f_r1'], newY, componentWidth, 'x', errorSize );
                            }
                        }

                        if( limitSide < positionCurrent ) {
                            removeItem = true;
                        }
                    }

                    if( removeItem ) {
                        _this.deleteComponentV2( side, key, type );
                        isRemove = true;
                        keyRemoves.push( key );
                        continue;
                    }
                }
                
                if( helpers.toFixed3( modelEndY ) < helpers.toFixed3( newY ) && 'skylight_window' !== type ) {
                    _this.deleteComponentV2( side, key, type );
                    isRemove = true;
                    keyRemoves.push( key );
                    continue;
                }
                
                let newPosition   = { x: component.position.x, y: component.position.y, z: component.position.z };
                if( isCheckPosition ) {
                    if( 'vent' === type ) {

                        if( component.position.y > _this.getBuildingHeight() - 0.3 ) {
                            newY = _this.getBuildingHeight() - 0.3;
                        }

                        if( ( 'left' === side || 'right' === side ) && component.position.y < _this.getWallHeight() ) {
                            newY = component.position.y;
                        }
                    }

                    if( 'structure_monitorbarns_helmuth' === _this._shedType && ( 'top_left' === side || 'top_right' === side ) ) {
                        newY = component.position.y;
                    }

                    if( 'structure_rancher' === _this._shedType && ( 'top_front' === side || 'top_back' === side ) ) {
                        newY = component.position.y;
                    }

                    if( this.isDoubleGable() ) {
                        newY = component.position.y;
                    }

                    if( 'dormer' === type && _this.isGableDormer( name ) ) {
                        const dormerFrame   = `${name}_frame_obj`;
                        const dormerHeight  = this.getGableDormerDistance( dormerFrame, meshes, 'Dormer_B_F_T', 'Dormer_B_F_R1', 'y' );
                        newX                = 0;
                        newY                = _this.getBuildingHeight() - dormerHeight;
                    }

                    newPosition         = { x: newX, y: newY, z: newZ };
                    component.position  = newPosition;
                    const rotation      = _this.calculateComponentRotation( side, type );
                    _this.placeComponent( meshes, newPosition, rotation, type, side, name );
                    _this.updateComponentsPosition2D( side, key, newPosition, component );
                }

                if( ! _this.isValidComponentPosition( side, newPosition, componentWidth, componentHeight, meshes, type, name, key ) ) {
                    _this.deleteComponentV2( side, key, type );
                    isRemove = true;
                    keyRemoves.push(key);
                    continue;
                }

                for( const tmpKey of keys ) {
                    if( key === tmpKey || -1 !== keyRemoves.indexOf( tmpKey ) || 'ramp' === items[tmpKey].type || 'flower_box' === items[tmpKey].type ) {
                        continue;
                    }
    
                    const tmpStartX = items[tmpKey].position2D.horizontal.firstPoint;
                    const tmpEndX   = items[tmpKey].position2D.horizontal.lastPoint;
                    const tmpStartY = items[tmpKey].position2D.vertical.firstPoint;
                    const tmpEndY   = items[tmpKey].position2D.vertical.lastPoint;
                    if( ! this.isValidComponent( startX, endX, tmpStartX, tmpEndX, startY, endY, tmpStartY, tmpEndY ) ) {
                        _this.deleteComponentV2( side, key, type );
                        isRemove = true;
                        keyRemoves.push(key);
                    }
                }
            }
        }
        if( isRemove ) {
            _this.addNotificationRequiredDoor();
            _this.handleSliding();

            _this._isUpdateLayout           = true;
            _this._isUpdateLayoutEPackage   = true;

            _this.updateWindowsPosition();
            _this.createAlert( this.NOTICES.itemNotFit );
            window.dispatchEvent( new CustomEvent( 'update-shed-info' ) );
        }
    }
    
    /**
     * Calculates the new point of the component based on the component's original point,
     * the start and end points of the model, and the component's width.
     * 
     * @param {Object} args - The arguments object.
     * @param {number} args.point - The current point of the component.
     * @param {number} args.componentWidth - The width of the component.
     * @param {number} args.componentStart - The start point of the component.
     * @param {number} args.componentEnd - The end point of the component.
     * @param {number} args.modelStart - The start point of the model.
     * @param {number} args.modelEnd - The end point of the model.
     * 
     * @returns {number} The new point of the component.
     */
    calculateNewPoint = ( args ) => {
        let { point, componentWidth, componentStart, componentEnd, modelStart, modelEnd } = args;
        if( componentStart < modelStart ) {
            point = modelStart + componentWidth / 2;
        } else if( componentEnd > modelEnd ) {
            point = modelEnd - componentWidth / 2;
        }

        return helpers.toFixed3( point );
    }

    /**
     * Calculates the new Y position of the component based on the component's original Y position,
     * the start and end Y positions of the model, and the component's height.
     * 
     * @param {number} newY - The current Y position of the component.
     * @param {number} startY - The start Y position of the component.
     * @param {number} modelStartY - The start Y position of the model.
     * @param {number} componentHeight - The height of the component.
     * @param {number} endY - The end Y position of the component.
     * @param {number} modelEndY - The end Y position of the model.
     * @param {string} [side=null] - The side of the component. If 'side' is 'front' or 'back', the new Y position
     * is calculated differently.
     * 
     * @returns {number} The new Y position of the component.
     */
    getNewPositionY = ( newY, startY, modelStartY, componentHeight, endY, modelEndY, side = null ) => {
        if( startY < modelStartY ) {
            newY = this.isPorchSide( side ) ? modelStartY + componentHeight : componentHeight;
        } else if( endY > modelEndY ) {
            newY = modelEndY;
        }
        
        return helpers.toFixed3( newY );
    }

    getPositionYForSkylightWindow = () => {
        return this.getBuildingHeight() + this.getSkylightWindowOffset();
    }

    getSkylightWindowOffset = () => {
        const skeletonsCorrection = this.getSkeletonsCorrection();
        if( skeletonsCorrection && ! helpers.isEmptyObject( skeletonsCorrection.roof_top_point ) ) {
            return Number( skeletonsCorrection.roof_top_point.y );
        }

        return 0.3;
    }

    getRoofCorrectionDegree = () => {
        const skeletonsCorrection = this.getSkeletonsCorrection();

        if( skeletonsCorrection && ! helpers.isEmptyObject( skeletonsCorrection.roof_top_point ) && ! helpers.isEmptyObject( skeletonsCorrection.roof_overhang_point ) ) {
            let halfWidth = this.getWidth() / 2;
            let wallHeight = this.getWallHeight();
            let buildingHeight = this.getBuildingHeight();

            if( this._bones['r_b_t'] && this._bones['r_b_r1'] ) {
                halfWidth = Math.abs( this._bones['r_b_r1'].position.x - this._bones['r_b_t'].position.x );
                wallHeight = this._bones['r_b_r1'].position.y;
                buildingHeight = this._bones['r_b_t'].position.y;
            }

            const { roof_top_point : topPoint, roof_overhang_point: overHangPoint } = skeletonsCorrection;
            const opposite = helpers.toFixed3( buildingHeight - wallHeight + Number( topPoint.y ) - Number( overHangPoint.y ) );
            const adjacent = helpers.toFixed3( halfWidth + Number( overHangPoint.x ) );

            return this.getRoofPitchFromRadian( opposite, adjacent );
        }

        return this.getRoofDegree();
    }

    getSkeletonsCorrection = () => {
        let data;
        if( ! this._apiData['roof_correction'] ) {
            data = [
                {
                    version: [],
                    material: [ 'metal' ],
                    roof_top_point: { x: 0, y: 0.283 },
                    roof_overhang_point: { x: 0.137, y: 0.243 },
                    roof_slopes_joint: {}
                },
                {
                    version: [],
                    material: [ 'shingle' ],
                    roof_top_point: { x: 0, y: 0.207 },
                    roof_overhang_point: { x: 0.147, y: 0.157 },
                    roof_slopes_joint: {}
                },
            ];
        } else {
            data = this._apiData['roof_correction'];
        }

        let result = null;
        for( let i = 0; i < data.length; i++ ) {
            if( helpers.hasKeyInArray( data[i].material, this.getRoofType() ) && ( 0 === data[i].version.length || helpers.hasKeyInArray( data[i].version, this._shedTypeVersion ) ) ) {
                result = data[i];
                break;
            }
        }
        
        return result;
    }

    getPositionYForLight = () => {
        const widthLight    = 0.37; // is width of light component
        let positionY       = this._bones['b_b_t'] ? this._bones['b_b_t'].position.y : this.getBuildingHeight();
        let offsetRoof      = this._lightOffsetY;

        if( this._shedType === 'structure_hiproof' ) {
            if( 'structure_hiproof_update_roof_component' === this._shedTypeVersion ) {
                return this.getWallHeight();
            }

            return this.getWallHeight() + 0.2;
        }

        if( this.getRoofShape() === 'flat') {
            positionY   = this.getWallHeight() + helpers.getTanFromDegrees( this.getRoofDegree() ) * ( this.getWidth() / 2 );
            offsetRoof  = helpers.getTanFromDegrees( this.getRoofDegree() ) * widthLight;
            
            return positionY - offsetRoof;
        }

        if( this.getRoofShape() === 'gambrel' ) {
            offsetRoof = helpers.getTanFromDegrees( this._roofUpperDegree ? this._roofUpperDegree : this._initRoofUpperDegree ) * widthLight;
        } else if( 'saltbox' === this.getRoofShape() && 'structure_quaker_wannabuysheds' === this._shedType ) {
            offsetRoof = helpers.getTanFromDegrees( 45 ) * widthLight;
        } else {
            offsetRoof          = helpers.getTanFromDegrees( this.getRoofDegree() ) * widthLight;
            const lightPosition = this.getLightPosition();
            if( lightPosition ) {
                const measuredFrom  = lightPosition['from'];
                const lightOffset   = helpers.toFixed3( Number( lightPosition['value'] ) / 12 );
                if( 'wall_height' === measuredFrom ) {
                    return helpers.toFixed3( this.getWallHeight() - lightOffset );
                } else if( 'ground' === measuredFrom ) {
                    return lightOffset;
                } else if( 'building_height' === measuredFrom ) {
                    offsetRoof = lightOffset;
                }
            }
        }
        
        return positionY - offsetRoof;
    }

    isValidComponentPosition( side, position, componentWidth, componentHeight, meshes, componentType, componentName, ComponentKey ) {
        componentType = this.getBehavior( componentName, componentType );

        if( ! this.isValidComponentByWall( position, componentType, componentWidth, componentHeight, side ) ) {
            return false;
        }

        if( ! this.componentIsOutOfShed( position, componentWidth, componentHeight, side, componentType, componentName ) ) {
            return false;
        }

        const limitSpacing  = this.getHorizontalSpacing( componentType );
        const position2D    = this.getPosition2D( side, meshes, position, componentType, componentName );
        const startX        = position2D.horizontal.firstPoint - limitSpacing;
        const endX          = position2D.horizontal.lastPoint + limitSpacing;
        const startY        = position2D.vertical.firstPoint;
        const endY          = position2D.vertical.lastPoint;
        const face          = this.getComponentFace( componentType );
        const items         = this.getComponentBySideAndType( side, face );
        if( this._isEnableTackRoom && this.isIntersectComponentWithPartialWall( startX, endX ) && helpers.hasKeyInArray( zSide, side ) ) {
            return false;
        }
        
        if( this.applyFixedHeightWindows( side, componentType, componentName ) ) {
            let limitY          = this.getWallHeight();
            let componentHeight = this.getComponentHeight( meshes, componentType );
            const tmpYPosition  = this.getWindowsFixedPositionY( componentType, componentHeight, side );
            if( ( 'front' === side || 'back' === side ) && this.isSpecialShed() ) {
                limitY = this.getBuildingHeight();
            }

            if( tmpYPosition > limitY || componentHeight > limitY ) {
                return false;
            }
        }

        for( const key in items ) {
            if( key === ComponentKey || 'ramp' === items[key].type || 'flower_box' === items[key].type ) {
                continue;
            }

            const tmpStartX = items[key].position2D.horizontal.firstPoint;
            const tmpEndX   = items[key].position2D.horizontal.lastPoint;
            const tmpStartY = items[key].position2D.vertical.firstPoint;
            const tmpEndY   = items[key].position2D.vertical.lastPoint;
            if( ! this.isValidComponent( startX, endX, tmpStartX, tmpEndX, startY, endY, tmpStartY, tmpEndY ) ) {
                return false;
            }
        }

        return true;
    }

    isValidComponentWithComponents( params ) {
        const { side, position, meshes, componentType, componentName, componentKey } = params;
        const limitSpacing = this.getHorizontalSpacing( componentType );
        const position2D = this.getPosition2D( side, meshes, position, componentType, componentName );
        const startX = position2D.horizontal.firstPoint - limitSpacing;
        const endX = position2D.horizontal.lastPoint + limitSpacing;
        const startY = position2D.vertical.firstPoint;
        const endY = position2D.vertical.lastPoint;
        const face = this.getComponentFace( componentType );
        const items = this.getComponentBySideAndType( side, face );
        for( const key in items ) {
            if( key === componentKey || 'ramp' === items[key].type || 'flower_box' === items[key].type ) {
                continue;
            }
            const tmpStartX = items[key].position2D.horizontal.firstPoint;
            const tmpEndX = items[key].position2D.horizontal.lastPoint;
            const tmpStartY = items[key].position2D.vertical.firstPoint;
            const tmpEndY = items[key].position2D.vertical.lastPoint;
            if( ! this.isValidComponent( startX, endX, tmpStartX, tmpEndX, startY, endY, tmpStartY, tmpEndY ) ) {
                return false;
            }
        }
        
        return true;
    }

    getWindowsY = ( side, type, name, componentHeight ) => {
        if( ! this.applyFixedHeightWindows( side, type, name ) ) {
            return componentHeight;
        }

        if( this.isFollowMainDoor() ) {
            if( 0 !== this._doorFixedTarget.length ) {
                return this.getWindowsFixedPositionY( type, componentHeight, side );
            }
        } else {
            return this.getWindowsFixedPositionY( type, componentHeight, side );
        }

        return componentHeight;
    }

    isHandleOffsetFloor = ( type ) => {
        return 'breakerbox' === type || 'switch' === type || 'outlet' === type || 'shelf' === type || 'workbench' === type;
    }

    getPositionMiddle( side, modelLength, modelWidth, type, endY, componentHeight, name ) {
        type = this.getBehavior( name, type );
        let x = 0;
        let y = 'windows' === type || this.isHandleOffsetFloor( type ) ? endY : 'door' === type ? this.getDoorFixedHeight( side ) : 0;
        let z = 0;

        if( 'structure_aframe_genesis' === this._shedType && 'windows' === type && ( 'front' === side || 'back' === side ) ) {
            y = this.getWindowsY( side, type, name, componentHeight );
        }

        switch( side ) {
            case 'front':
                x = 0;
                z = modelLength / 2;
            break;
            case 'inner_front':
                x = - ( modelWidth / 2 ) + ( constants.cornerWidth / 2 );;
                z = ( modelLength / 2 ) - constants.cornerDepth;
            break;
            case 'back':
                if( 'structure_ranch_cabin' === this._shedType || this.isAframeSingleGable() ) {
                    x = helpers.toFixed3( ( this._bones['b_b_l1'].position.x + this._bones['b_b_r1'].position.x ) / 2 );
                    z = this._bones['b_b_l1'].position.z;
                } else {
                    x = 0;
                    z = - modelLength / 2;
                }
            break;
            case 'inner_back':
                x = helpers.toFixed3( ( this._bones['b_p_l1'].position.x + this._bones['b_b_m4'].position.x ) / 2 );
                z = this._bones['b_p_l1'].position.z;
            break;
            case 'top_left':
            case 'left':
                if( 'structure_ranch_cabin' === this._shedType || this.isAframeSingleGable() ) {
                    x = this._bones['b_f_l1'].position.x;
                    z = helpers.toFixed3( ( this._bones['b_f_l1'].position.z + this._bones['b_p_l1'].position.z ) / 2 );
                } else {
                    x = - modelWidth / 2;
                    z = 0;
                }

                if( 'top_left' === side && this.is2StoryLeanTo() ) { 
                    y = this._bones['b_b_l2'].position.y;
                }
            break;
            case 'inner_left':
                if( 'structure_ranch_cabin' === this._shedType || this.isAframeSingleGable() ) {
                    x = this._bones['b_b_m4'].position.x;
                    z = helpers.toFixed3( ( this._bones['b_b_m4'].position.z + this._bones['b_b_l1'].position.z ) / 2 );
                } else {
                    x = - ( modelWidth / 2 ) + ( constants.cornerWidth ) ;
                    z = ( modelLength / 2 ) - ( constants.cornerDepth / 2 );
                }
            break;
            case 'top_right':
            case 'right':
                x = modelWidth / 2;
                z = 0;

                if( 'top_right' === side && this.is2StoryLeanTo() ) { 
                    y = this._bones['b_b_l2'].position.y;
                }
            break;
            case 'roof':
                if( this.isDoubleGable() ) {
                    y = this._bones['r_b_t1'].position.y;
                    x = this._bones['r_b_t1'].position.x;
                } else if( 'hip' === this.getRoofShape() ) {
                    y = this._bones['r_b_t'].position.y;
                    x = this._bones['r_b_t'].position.x;
                } else {
                    y = this._bones['b_b_t'].position.y;
                    x = this._bones['b_b_t'].position.x;
                }

                if( 'light' === type ){
                    y = this.getPositionYForLight();
                }

                if( 'flat' === this.getRoofShape() ){
                    // const positionY = this.getWallHeight() + this.getWidth() / 2 * helpers.getTanFromDegrees( this.getRoofDegree() ) - this._lightOffsetY;

                    x = 0;
                    // y = positionY;
                }

                if( this.isAframeSingleGable() ) {
                    z = ( this._bones['b_b_t'].position.z + this._bones['b_f_t1'].position.z ) / 2;
                }
            break;
            case 'dormer':
                x = this._bones['b_b_t2'].position.x;
                y = this._bones['b_b_t2'].position.y;
            break;
            case 'porch':
                x = 0;

                if( 'structure_lakeside_bunkie_gambrel_roof' === this._shedType || 'structure_lakeside_bunkie_gable_roof' === this._shedType ){
                    z = modelLength / 2 + 32/12;
                }else{
                    z = this._bones['b_p_t'].position.z;
                }
            break;
            case 'lean_to_right':
                x = this._bones['lean_to_f_r2'].position.x;
                z = 0;
            break;
        }

        if( this.isSidePorch() ){
            if( 'front' === side || 'back' === side ) {
                x = ( modelWidth / 2 ) - ( modelWidth - this._porchWidth ) / 2;
            } else if( 'left' === side ) {
                x = this._bones['b_b_l1'].position.x;
            }
        }

        if( this.isCornerPorchLeft() ){
            if( 'left' === side ){
                z = - ( ( modelLength / 2 ) - ( modelLength - constants.cornerDepth ) / 2 );
            }
            
            if( 'front' === side ){
                x = ( modelWidth / 2 ) - ( modelWidth - constants.cornerWidth ) / 2;
            }
        }
        
        if( 'structure_dog_kennel' === this._shedType && ( 'left' === side || 'right' === side ) && this._bones['b_b_r1'] ) {
            z = this._bones['b_b_r1'].position.z + this.getDogKennelBoxDepth() / 2;
        }

        if( this.isDeluxeCornerLeftPorch() ) {
            switch( side ) {
                case 'front':
                    x = ( this._bones['b_f_l1'].position.x + this._bones['b_f_l4'].position.x ) / 2;
                    z = this._bones['b_f_l1'].position.z;
                    break;
                case 'inner_front':
                    x = ( this._bones['b_f_r1'].position.x + this._bones['b_f_r4'].position.x ) / 2;
                    z = this._bones['b_f_r1'].position.z;
                    break;
                case 'inner_right':
                    z = ( this._bones['b_f_r4'].position.z + this._bones['b_f_r6'].position.z ) / 2;
                    x = this._bones['b_f_r4'].position.x;
                    break;
                case 'right':
                    z = ( this._bones['b_b_r1'].position.z + this._bones['b_f_r1'].position.z ) / 2;
                    x = this._bones['b_b_r1'].position.x;
                    break;
                case 'left':
                    z = ( this._bones['b_f_l1'].position.z + this._bones['b_b_l1'].position.z ) / 2;
                    x = this._bones['b_f_l1'].position.x;
                    break;
            }
        }

        if( 'structure_quaker_wannabuysheds' === this._shedType ){
            if( 'front' === side || 'back' === side ){
                x = ( this._bones['b_b_l1'].position.x + this._bones['b_b_r1'].position.x ) / 2;
            }else if( 'left' === side ){
                x = this._bones['b_f_l1'].position.x;
            }
        }

        if( this.applyFixedHeightWindows( side, type, name ) ) {
            y  = this.getWindowsFixedPositionY( type, componentHeight, side );
        }

        return { x, y: y, z };
    }

    getPositionMiddleV2 = ( side, sideLimitLeft, sideLimitRight, positionY, anchorPosition, type, sideMoveBy = 'z' ) => {
        const y         = 'windows' === type ? positionY : 'door' === type ? this.getDoorFixedHeight( side ) : 0;
        let position    = { x : 0, y, z: 0 };

        if( sideMoveBy == 'z' ) {
            position.x = anchorPosition;
            position.z = ( sideLimitLeft + sideLimitRight ) / 2;
        } else {
            position.x = ( sideLimitLeft + sideLimitRight ) / 2;
            position.z = anchorPosition;
        }

        return position;
    }

    sortComponentKeyBySize( components ) {
        return Object.keys(components).sort((a, b) => {
            const widthA    = Math.abs( components[a].position2D.horizontal.lastPoint - components[a].position2D.horizontal.firstPoint );
            const heightA   = Math.abs( components[a].position2D.vertical.lastPoint - components[a].position2D.vertical.firstPoint );
            const widthB    = Math.abs( components[b].position2D.horizontal.lastPoint - components[b].position2D.horizontal.firstPoint );
            const heightB   = Math.abs( components[b].position2D.vertical.lastPoint - components[b].position2D.vertical.firstPoint );

            const acreageA  = widthA * heightA;
            const acreageB  = widthB * heightB;

            return acreageB - acreageA;
        });
    }

    sortComponentKeyByPosition( components, side ) {
        const keys = Object.keys(components).sort((a, b) => {
            if( 'front' === side || 'back' === side || this.isPorchSide( side ) || 'right_inner_porch' === side || 'left_inner_porch' === side || 'inner_back' === side ) {
                return components[a].position.x - components[b].position.x;
            } else {
                return components[a].position.z - components[b].position.z;
            }
        });

        if( 'front' === side || 'left' === side || 'roof_left' === side || 'dormer' === side || this.isPorchSide( side ) || 'left_front' === side || 'left_back' === side  || 'front_inner_porch' === side ) {
            keys.reverse();
        }

        return keys.filter( k => {
            return -1 === k.indexOf( '_ramp_' );
        });
    }

    /**
     * Get Vent Position 
     * @param {Number} componentWidth 
     * @param {Number} componentHeight 
     * @param {Number} componentsInWall List components added on Side Wall
     * @param {Number} sideBottom Side Wall Bottom number Position Y
     * @param {Number} sideTop Side Wall Top number Position Y
     * @param {Number} sideLimitLeft Side Wall Left|Right number Position X|Z
     * @param {Number} sideLimitRight Side Wall Right|Left number Position Z|X
     * @param {Number} anchor Position Z|X Number
     * @param {String} sideMoveBy x|Z
     * @returns Vector3 | false
     */
    getVentPosition = ( side, componentWidth, componentHeight, componentsInWall, sideBottom, sideTop, sideLimitLeft, sideLimitRight, anchor, sideMoveBy = 'x' ) => {
        if( 'structure_aframe_genesis' === this._shedType && ( 'left' === side || 'right' === side ) ) {
            return false;
        }

        const middlePosition    = 'saltbox' === this.getRoofShape() && 'x' === sideMoveBy ? this._bones['b_b_t'].position.x : ( sideLimitLeft + sideLimitRight ) / 2;
        const limitSpacing      = this.getHorizontalSpacing( 'vent' );

        let x = 'x' === sideMoveBy ? middlePosition : anchor;
        let y = sideTop;
        let z = 'z' === sideMoveBy ? middlePosition : anchor;
        if( Object.keys( componentsInWall ).length === 0 ) {
            return { x, y, z };
        }
        
        const halfWidth = componentWidth / 2;
        let components  = [
            {
                position: {
                    x,
                    y: this.getBuildingHeight() + 0.1,
                    z
                },
                position2D: {
                    vertical: {
                        firstPoint: sideTop,
                        lastPoint:  sideTop + componentHeight
                    },
                    horizontal: {
                        firstPoint: - halfWidth,
                        lastPoint:  halfWidth
                    }
                }
            }
        ];

        for( const key of Object.keys( componentsInWall ) ) {
            const item          = componentsInWall[key];
            const position2D    = item.position2D.horizontal;
            if( position2D.lastPoint + limitSpacing < middlePosition - halfWidth || position2D.firstPoint - limitSpacing > middlePosition + halfWidth ) continue;
            components.push( componentsInWall[item.key] );
        }

        components      = components.sort( ( a, b ) => b.position.y - a.position.y );
        const lastIndex = components.length - 1;

        for( let i = 0; i <= lastIndex; i++ ) {
            const startX    = components[i].position2D.horizontal.firstPoint;
            const endX      = components[i].position2D.horizontal.lastPoint;
            const startY    = components[i].position2D.vertical.firstPoint;
            const endY      = components[i].position2D.vertical.lastPoint;
            const topNext   = lastIndex === i ? 0 : components[i + 1].position2D.vertical.lastPoint;
            const distance  = 0 === i ? 0 : this._distance2Component;
            if( startY - componentHeight - distance >= topNext ) {
                y               = this.isPorchSide( side ) && this.isVerticalDistribute() ? this.calculateMiddlePosition( componentHeight ) : helpers.toFixed3( startY - distance );
                const tmpStartX = x - halfWidth;
                const tmpEndX   = x + halfWidth;
                const tmpEndY   = y;
                const tmpStartY = y - componentHeight;
                if( y < sideBottom + componentHeight || ! this.isValidComponent( startX, endX, tmpStartX, tmpEndX, endY, startY, tmpStartY, tmpEndY ) ) {
                    continue;
                }
                
                return { x, y, z };
            }
        }

        return false;
    }

    /**
     * Get all component from Interior or Exterior by Component type and Side
     * 
     * @param side side of model
     * @param fade interior or exterior
     */
    getComponentBySideAndType( side, face ){
        const components = this._walls[side]['items'];
        let results      = [];

        if( components ){
            for( let key in components ){
                if( face == components[key].face ){
                    if( 'flower_box' === components[key].type ) continue;
                    results[key] = components[key];
                }
            }
        }

        return results;
    }

    /**
     * Get component by name
     * 
     * @param {name} name of component
     * 
     * return list component
    */
    getComponentByName( name ){
        let result = {}, length = 0;
        for( const key in this._allComponents ){
            const component = this._allComponents[key];

            if( component.name === name ){
                result[key] = component;
                length++;
            }
        }

        return { result, length };
    }

    getComponentByType = type => {
        const components    = this.getAllComponents();
        const results       = [];

        for( let key in components ) {
            if( type === components[key].type && components[key].meshes.length ) {
                results.push( components[key] );
            }
        }

        return results;
    }

    /**
     * Validate before remove component in Package
     * @param key {string}
     */
    validateRemoveComponentInPackageInfo( key ){
        const conponentPackage = this.getComponentPackageInfo( key );

        return conponentPackage.hasPackage;
    }

    /**
     * Get component package info
     * 
     * @returns Component In Package, Component Out Package, Has Package
     */
    getComponentPackageInfo( checkKey = '' ){
        const _this                         = this;
        const electricalPackageComponent    = { ..._this.getElectricalPackageComponent() };
        const defaultEPackageComponents     = [ ..._this._defaultEPackageComponents ];
        let hasPackage                      = false;
        let dataComponentInPackage          = [];
        let componentInPackage              = [];
        let componentOutPackage             = [];
        const isIncludeComponent            = _this.includePrice()
        let includeElectricalItem           = [];
        const selectedLayout                = jQuery(`#ssb-configurator-layout-option input[name='ssb-configurator-layout']:checked`);
        const packageLayout                 = selectedLayout.data( 'layout-packages' );
        const replacePackage                = Object.keys(electricalPackageComponent).length == 1;
        
        if( checkKey ){
            delete electricalPackageComponent[checkKey];
        }

        for( const dComponent of defaultEPackageComponents ){

            includeElectricalItem.push( dComponent.component );
            for( const key in electricalPackageComponent ){

                const eComponent = electricalPackageComponent[key];
                if( dComponent.component === eComponent.name ){
                    if( -1 === dataComponentInPackage.indexOf( key ) ){
                        dataComponentInPackage.push( key );
                        componentInPackage.push( key );
                    } else {
                        componentOutPackage.push( key );
                    }

                    delete electricalPackageComponent[key];
                    break;
                }
                
            }

        }

        if( defaultEPackageComponents.length > 0 && defaultEPackageComponents.length === dataComponentInPackage.length ){
            hasPackage = true;

            if( electricalPackageComponent ){
                componentOutPackage = Object.keys( electricalPackageComponent );
            }
        }else{
            if( packageLayout.length > 0 ) {
                if( isIncludeComponent || packageLayout[0].include_price ) {
                    hasPackage = true;
                } else {
                    componentOutPackage = componentInPackage;
                    componentInPackage  = [];                    
                }
            }
        }

        return { hasPackage, componentOutPackage, componentInPackage, includeElectricalItem, replacePackage };
    }

    getFloorOffsetHardCode( name ) {
        let positionY = 0.08;

        if( this.isTinyHome() ) {
            positionY = helpers.toFixed3( this.getFloorOffset() + 0.01 );
        } else if( this.isFrontPorch() || this.isSidePorch() || this.isCornerPorchLeft() || this.isMiddlePorch() || this.isDeluxeCornerLeftPorch() ) {
            positionY = 0.4;
        }

        if( this.isDoubleGable() ) {
            positionY = 0;
        }

        if( 'wall_mounted_cabinet' === name ) {
            positionY = helpers.toFixed3( 55 / 12 );
        } else if( 'air_conditioner' === name ) {
            positionY = 1;
        }

        return positionY;
    }

    /**
     * Get Position Y for Interior component
     */
    getPositionYForInterior( type, name ) {
        const offset = jQuery( `[data-upgrade-component-name="${name}"]` ).data( 'offset-height' );
        if( undefined !== offset && ! isNaN( Number( offset ) ) ) {
            return helpers.toFixed3( this.getFloorOffset() + Number( offset ) / 12 );
        }
        
        let positionY = this.getFloorOffsetHardCode( name );
        if( 'breakerbox' === type ) {
            positionY += 5;
        } else if( 'switch' === type ) {
            positionY += 4;
        } else if( 'outlet' === type ) {
            positionY += 2;
        }

        return helpers.toFixed3( this.getFloorOffset() + positionY );
    }

    /**
     * Returns the height for the top side of a specific part of the building based on various conditions.
     *
     * @param {string} side - The side of the building ('left' or 'right').
     * @param {string} type - The type of component.
     * 
     * @returns {number} - The height for the top side of the specified part.
     */
    getTopSide = ( side, type ) => {
        const allowRoofStyles = [ 'open_gable', 'box_gable', 'gambrel', 'dutch_style', 'saltbox_customize' ];
        const allowSides = [ 'front', 'back', 'roof_left', 'roof_right', 'porch' ];
        let offset = - 0.5;
        let height = this.getWallHeight();

        if( this.isDoubleGable() ) {
            height = this.getWallHeight();
        } else if( 'left' === side && 'structure_lean_to_with_front_porch' === this._shedType ) {
            height = this._middleHeight - leanToFrontPorchOffsetTop;

        } else if( 'left' === side && 'flat' === this.getRoofShape() ) {
            height = this.getBuildingHeight();

        } else if( ( 'left' === side && this.isSidePorch() && 'structure_hiproof' !== this._shedType ) || ( 'right' === side && 'mainus_quaker' === this._shedType ) ) {
            height = this.getWallHeight() - offsetTop;

        } else if( helpers.hasKeyInArray( allowSides, side ) && helpers.hasKeyInArray( allowRoofStyles, this.getRoofShape() ) ) {

            if( ( this.isFrontPorch() && 'structure_chalet_dutchboybarns' !== this._shedTypeVersion && 'front' === side ) || this.isAframeSingleGable() ) {
                height = this.getWallHeight();
            } else {
                height = this.getBuildingHeight();
            }
        }

        if( 'skylight_window' === type ) {
            return this.getPositionYForSkylightWindow();
        }

        return helpers.toFixed3( height + offset );
    }

    /**
     * Returns the height for the bottom side of a specific part of the building based on various conditions.
     *
     * @param {string} side - The side of the building.
     * @param {string} type - The type of the building component (e.g., 'windows').
     * 
     * @returns {number} - The height for the bottom side of the specified part.
     */
    getBottomSide = ( side, type ) => {
        if( 'skylight_window' === type ) {
            return this.getWallHeight();
        }

        if( this.isMiddlePorch() || ( 'structure_ranch_cabin' === this._shedType && 'inner_back' === side && 'windows' === type ) ) {
            return offsetBottom.level3;
        }

        if( ( 'left' === side && this.isSidePorch() && 'structure_hiproof' !== this._shedType ) || 'inner_left' === side || 'inner_front' === side ) {
            return offsetBottom.level1;
        }

        if( ( this.isDeluxeCornerLeftPorch() && ( 'inner_right' === side || 'front' === side ) ) || ( this.isPorchWal( side ) && 'structure_aframe_2story_attic_leanto' !== this._shedType ) ) {
            return offsetBottom.level2;
        }

        return 0;
    }

    /**
     * Calculates new position for a component
     * 
     * @param {string} args.side - Which side of the building the component is on
     * @param {string} args.type - Type of component (e.g., door, window)
     * @param {Object} args.meshesInfo - Contains position info for left/right mesh bones
     * @param {number} args.componentWidth - Width of the component
     * @param {number} args.componentHeight - Height of the component
     * @param {string} args.componentName - Name identifier for the component
     * @param {boolean} args.isValidTackRoom - Whether component is in valid tack room
     * @param {boolean} args.isTackDoor - Whether component is a tack door
     * @param {Object} args.boundingBox - Bounding box dimensions for the component
     * 
     * @returns {Object} Generated position coordinates
     */
    getNewPosition = ( args ) => {
        let { side, type, meshesInfo, isMoveAlongFloor, componentWidth, componentHeight, componentName, isValidTackRoom, isTackDoor, boundingBox } = args;
        type                = this.getBehavior( componentName, type );
        const face          = this.getComponentFace( type );
        const components    = this.getComponentBySideAndType( side, face );
        const sortComps     = this.sortComponentPosition( components, side );
        const endY = this.getTopSide( side, type );
        const offset        = this.getHorizontalOffset( type );
        const isZSide       = helpers.hasKeyInArray( zSide, side );
        let fixedPosition   = this._bones[meshesInfo.left].position['z'];
        let vector          = 'x';
        if( isZSide ) {
            vector          = 'z';
            fixedPosition   = this._bones[meshesInfo.left].position['x'];
        }

        const isPositive    = helpers.hasKeyInArray( positiveSide, side );
        const fullOffset    = 'floor' === side ? this._frameDepth + constants.innerWallThickness : this._offsetDistance + offset;
        const limitLeft     = isPositive ? this._bones[meshesInfo.left].position[vector] - fullOffset : this._bones[meshesInfo.left].position[vector] + fullOffset;
        const limitRight    = isPositive ? this._bones[meshesInfo.right].position[vector] + fullOffset : this._bones[meshesInfo.right].position[vector] - fullOffset;

        const step          = 0.1;
        const anchor        = this.getAnchor( type, componentName );
        let isFixed         = false;
        let positionY       = helpers.toFixed3( endY );
        let limitBottom     = this.getBottomSide( side, type );
        if( isMoveAlongFloor ) {
            fixedPosition   = this.getPositionYForInterior( type, componentName );
            const maxY      = this._bones[meshesInfo.left].position.z > this._bones[meshesInfo.right].position.z ? this._bones[meshesInfo.left].position.z : this._bones[meshesInfo.right].position.z;
            positionY       = maxY - fullOffset;
            limitBottom     = this._bones[meshesInfo.top].position.z + fullOffset;
        } else if( 'door' === type ) {
            const doorSpace = helpers.toFixed3( this.getDoorFixedHeight( side ) );
            isFixed         = true;
            positionY       = doorSpace;
            limitBottom     = doorSpace;
        } else if( this.applyFixedHeightWindows( side, type, componentName ) ) {
            isFixed         = true;
            positionY       = helpers.toFixed3( this.getWindowsFixedPositionY( type, componentHeight, side ) );
        } else if( this.isInteriorComponent( type ) ) {
            isFixed         = true;
            positionY       = this.getPositionYForInterior( type, componentName );
            limitBottom     = this.getFloorOffset();
        }

        let condition       = '<';
        let operator        = '+';
        if( limitLeft > limitRight ) {
            condition       = '>';
            operator        = '-';
        }

        const rawData       = { isMoveAlongFloor, positionY, limitLeft, limitRight, limitBottom, side, componentWidth, componentHeight, type, components: sortComps, step, anchor, isFixed, start: limitLeft, condition, operator, isZSide, fixedPosition, isValidTackRoom, isTackDoor, componentName, boundingBox };

        return this.generatePosition( rawData );
    }

    getPositionForNewComponent( args ) {
        let { side, type, componentDepth, componentWidth, componentHeight, componentName, isValidTackRoom, boundingBox } = args;
        type = this.getBehavior( componentName, type );
        if( 'diagonal_right' === side ) {
            let position = this.getComponentPositionForDiagonalRight( side, type, componentName );
            if( this.isInteriorComponent( type ) && 'light' !== type ) {
                position = this.updateInteriorPosition( position, side );
            }

            return position;
        }

        const isMoveAlongFloor = this.isMoveAlongFloor( componentName );
        const meshesInfo    = this.getWallBones( side );
        const allowType     = [ 'door', 'windows', 'vent', 'skylight_window' ];
        if( undefined !== meshesInfo.left && undefined !== meshesInfo.right && ( helpers.hasKeyInArray( allowType, type ) || ( this.isInteriorComponent( type ) && 'light' !== type && this.isDoubleGable() ) || isMoveAlongFloor ) ) {
            componentWidth  = Number( componentWidth );
            componentHeight = isMoveAlongFloor ? Number( componentDepth ) : Number( componentHeight );
            if( componentWidth <= 0 || componentHeight <= 0 ) {
                return false;
            }
            const args = { side, type, meshesInfo, isMoveAlongFloor, componentWidth, componentHeight, componentName, isValidTackRoom, isTackDoor: false, boundingBox };
            return this.getNewPosition( args );
        }

        const horizontalOffset  = this.getHorizontalOffset( type );
        const face              = this.getComponentFace( type );
        let components          = this.getComponentBySideAndType( side, face );
        const modelWidth        = this.getWidth();
        const modelLength       = this.getDepth();
        const modelHeight       = this.getWallHeight();
        let endX                = 'front' === side || 'back' === side || 'inner_front' === side || this.isPorchSide( side ) ? modelWidth / 2 - this._offsetDistance - horizontalOffset : modelLength / 2 - this._offsetDistance - horizontalOffset;
        let startX              = - endX;
        let endY                = 'dormer' === side ? this._bones['b_b_t2'].position.y : modelHeight - 0.5;
        let tmpYPosition        = 'mainus_quaker' !== this._shedType ? endY : ( 'right' === side ? endY - 1 : endY );

        if( this.isHandleOffsetFloor( type ) ) {
            tmpYPosition    = this.getPositionYForInterior( type, componentName );
            endY            = 'shelf' === type || 'workbench' === type ? tmpYPosition + componentHeight : tmpYPosition + componentHeight / 2;
        }

        if( this.isSidePorch() ){
            startX = startX + this._porchWidth;

            if( 'left' === side && 'structure_hiproof' !== this._shedType ) {
                tmpYPosition -= 1;
            }
        }

        if( this.isCornerPorchLeft() ){
            if( 'front' === side ){
                startX  = endX;
                endX    = - endX + constants.cornerWidth;
            }

            if( 'inner_front' === side ){
                endX    = startX;
                startX  = -modelWidth / 2 + constants.cornerWidth;
            }

            if( 'left' === side ){
                startX  = endX - constants.cornerDepth;
                endX    = - endX;
            }

            if( 'inner_left' === side ){
                startX = endX;
                endX = ( modelLength / 2 ) - constants.cornerDepth;
            }
        } else if( 'structure_ranch_cabin' === this._shedType || this.isAframeSingleGable() ) {
            switch( side ) {
                case 'inner_left':
                    startX  = this._bones['b_b_m4'].position.z;
                    endX    = this._bones['b_b_l1'].position.z;
                    break;
                case 'back':
                    startX  = this._bones['b_b_l1'].position.x;
                    endX    = this._bones['b_b_r1'].position.x;
                    break;
                case 'inner_back':
                    startX  = this._bones['b_b_m4'].position.x;
                    endX    = this._bones['b_p_l1'].position.x;
                    break;
                case 'left':
                    startX  = this._bones['b_f_l1'].position.z;
                    endX    = this._bones['b_p_l1'].position.z;
                    break;
                case 'roof':
                    if( this.isAframeSingleGable() ) {
                        startX = this._bones['b_b_t'].position.z;
                        endX = this._bones['b_f_t1'].position.z;
                    }
                    break;
            }
        } else if( this.isDoubleGable() && 'roof' === side ) {
            startX = this._bones['r_b_t1'].position.z;
            endX = - startX;
        }

        const { sideLimitLeft, sideLimitRight, sideBottom, sideTop, anchorPosition, sideMoveBy } = this.getSideInfo( side );
        if( this.isMiddlePorch() || 'structure_ranch_cabin' === this._shedType || this.isAframeSingleGable() ) {
            if( 'left_front' === side || 'left_back' === side || 'right_inner_porch' === side || 'front_inner_porch' === side || 'left_inner_porch' === side || 'inner_back' === side ) {
                startX  = sideLimitLeft;
                endX    = sideLimitRight;
            } 
        }

        if( this.applyFixedHeightWindows( side, type, componentName ) ) {
            endY = this.getWindowsFixedPositionY( type, componentHeight, side );
        }

        if( this.isPorchSide( side ) ) {
            if( this.isVerticalDistribute() ) {
                tmpYPosition = this.calculateMiddlePosition( componentHeight );
            } else {
                tmpYPosition = this.getPorchBottom() + componentHeight;
            }
        }

        if( 'structure_dog_kennel' === this._shedType ) {
            if( 'left' === side ) {
                startX  = this._bones['b_m_r1'].position.z - this._offsetDistance - horizontalOffset;
                endX    = this._bones['b_b_r1'].position.z + this._offsetDistance + horizontalOffset;
            } else if( 'right' === side ) {
                startX  = this._bones['b_b_r1'].position.z + this._offsetDistance + horizontalOffset;
                endX    = this._bones['b_m_r1'].position.z - this._offsetDistance - horizontalOffset;
            }

            if( 'dogkennel_split' === this.getActiveLayout() && 'back' === side ) {
                return this.generateComponentPositionDogKennel( side, type, components, componentHeight, tmpYPosition );
            }
        }

        const isHandleCupola = 'roof' === side && 'hip' === this.getRoofShape() && 'cupola' === type;
        if( isHandleCupola ) {
            startX  = this._bones['r_b_t'].position.z;
            endX    = this._bones['r_f_t'].position.z;
        }

        if( 'vent' === type ) {
            tmpYPosition = this.isPorchSide( side ) && this.isVerticalDistribute() ? this.calculateMiddlePosition( componentHeight ) : this.getFirstVentPositionY( sideTop, componentWidth, side );
            return this.getVentPosition( side, componentWidth, componentHeight, components, sideBottom, tmpYPosition, sideLimitLeft, sideLimitRight, anchorPosition, sideMoveBy );
        }

        let middlePosition;
        if( ( this.isMiddlePorch() && ( 'left_front' === side || 'left_back' === side || 'right_inner_porch' === side || 'front_inner_porch' === side || 'left_inner_porch' === side ) ) || ( 'structure_ranch_cabin' === this._shedType && 'inner_back' === side ) ) {
            middlePosition = this.getPositionMiddleV2( side, sideLimitLeft, sideLimitRight, tmpYPosition, anchorPosition, type, sideMoveBy );
            middlePosition = this.validNewPosition( componentName, type, middlePosition, startX, endX, sideMoveBy, componentWidth, 0.35, this.getWallHeight() - 0.1, componentHeight );
        } else {
            middlePosition = this.getPositionMiddle( side, modelLength, modelWidth, type, tmpYPosition, componentHeight, componentName );
        }

        const limitDistance = this.getHorizontalSpacing( type );
        const sortComps     = this.sortComponentPosition( components, side );
        let newEndX         = 'front' === side || 'left' === side || 'roof_left' === side || 'dormer' === side || this.isPorchSide( side ) ? - componentWidth / 2 - limitDistance : componentWidth / 2 + limitDistance;
        let newStartX       = 'front' === side || 'left' === side || 'roof_left' === side || 'dormer' === side || this.isPorchSide( side ) ? componentWidth / 2 + limitDistance : - componentWidth / 2 - limitDistance;
        let newStartY       = 'door' === type ? this.getDoorFixedHeight( side ) : endY - componentHeight;
        let newEndY         = 'door' === type ? componentHeight + this.getDoorFixedHeight( side ) : endY;

        if( 'workbench' === type ) {
            newEndY     = this._bones['workbench_l'] ? this._bones['workbench_l'].position.y + this.getPositionYForInterior( type, componentName ) : 3;
            newStartY   = newEndY - componentHeight;
        }

        if( 'light' === type ) {
            newEndY     = this.getPositionYForLight();
            newStartY   = newEndY - componentHeight;
        }

        if( this.isPorchSide( side ) ) {
            if( this.isVerticalDistribute() ) {
                newStartY       = this.getMiddleVerticalPosition() - ( componentHeight / 2 );
                newEndY         = newStartY + componentHeight;
                const positionY = this.calculateMiddlePosition( componentHeight );
                startX          = - this.getLimitX( positionY, componentWidth, componentHeight );
            } else {
                newStartY   = this.getPorchBottom();
                newEndY     = tmpYPosition;
                endY        = this.getBuildingHeight();
                if( this._bones['b_f_l3'] && componentHeight + this.getPorchBottom() > Math.abs( this._bones['b_f_l3'].position.y ) && ! this.isDeluxeCornerLeftPorch() ) {
                    let componentUpperHeight    = componentHeight + this.getPorchBottom() - Math.abs( this._bones['b_f_l3'].position.y );
                    let upperX                  = componentUpperHeight / this.getTanRoofPorch( 'roofUp' );
                    let sidePorchUpperEndX      = Math.abs( this._bones['b_f_l3'].position.x ) - upperX;
                    startX                      = -sidePorchUpperEndX;
                } else {
                    startX = - this.getLimitComponentXRoofPorch( componentHeight, componentWidth ) - ( componentWidth / 2 ) ;
                }
            }
        }

        if( this.isCornerPorchLeft() ){
            if( 'inner_left' === side ){
                newEndX     = ( modelLength / 2 ) - ( constants.cornerDepth / 2 ) - componentWidth / 2 - limitDistance;
                newStartX   = ( modelLength / 2 ) - ( constants.cornerDepth / 2 ) + componentWidth / 2 + limitDistance;
            } else if( 'inner_front' === side ){
                newStartX   = middlePosition.x + componentWidth / 2 + limitDistance;
                newEndX     = middlePosition.x - componentWidth / 2 - limitDistance;
            } else if ( 'front' === side ) {
                startX      = this._bones['b_f_r1'].position.x - this._offsetDistance - horizontalOffset;
                endX        = this._bones['b_f_l1'].position.x + this._offsetDistance + horizontalOffset;
                newEndX     = middlePosition.x + componentWidth / 2 + limitDistance;
                newStartX   = middlePosition.x - componentWidth / 2 - limitDistance;
            }else if( 'left' === side ){
                newEndX     = middlePosition.z + componentWidth / 2 + limitDistance;
                newStartX   = middlePosition.z - componentWidth / 2 - limitDistance;
            }
        }

        if( this.isDeluxeCornerLeftPorch() ) {
            switch( side ) {
                case 'front':
                    startX      = this._bones['b_f_l4'].position.x - this._offsetDistance - horizontalOffset;
                    endX        = this._bones['b_f_l1'].position.x + this._offsetDistance + horizontalOffset;
                    newStartX   = middlePosition.x - componentWidth / 2 - limitDistance;
                    newEndX     = middlePosition.x + componentWidth / 2 + limitDistance;
                    break;
                case 'inner_front':
                    startX      = this._bones['b_f_r1'].position.x - this._offsetDistance - horizontalOffset;
                    endX        = this._bones['b_f_r4'].position.x + horizontalOffset;
                    newStartX   = middlePosition.x - componentWidth / 2 - limitDistance;
                    newEndX     = middlePosition.x + componentWidth / 2 + limitDistance;
                    break;
                case 'inner_right':
                    startX      = this._bones['b_f_r4'].position.z + horizontalOffset;
                    endX        = this._bones['b_f_r6'].position.z - this._offsetDistance - horizontalOffset;
                    newStartX   = middlePosition.z - componentWidth / 2 - limitDistance;
                    newEndX     = middlePosition.z + componentWidth / 2 + limitDistance;
                    break;
                case 'right':
                    startX      = this._bones['b_b_r1'].position.z + this._offsetDistance + horizontalOffset;
                    endX        = this._bones['b_f_r1'].position.z - this._offsetDistance - horizontalOffset;
                    newStartX   = middlePosition.z - componentWidth / 2 - limitDistance;
                    newEndX     = middlePosition.z + componentWidth / 2 + limitDistance;
                    break;
                case 'left':
                    startX      = this._bones['b_f_l1'].position.z - this._offsetDistance - horizontalOffset;
                    endX        = this._bones['b_b_l1'].position.z + this._offsetDistance + horizontalOffset;
                    newStartX   = middlePosition.z - componentWidth / 2 - limitDistance;
                    newEndX     = middlePosition.z + componentWidth / 2 + limitDistance;
                    break;
            }
        }

        if( 'saltbox' === this.getRoofShape() && this._bones['b_f_l1'] ) {
            if( 'front' === side ) {
                if( this._bones['b_f_r4'] ){
                    startX  = -this._bones['b_f_r4'].position.x + this._offsetDistance;
                    endX    = this._bones['b_f_l1'].position.x + this._offsetDistance;
                }else if( 'structure_quaker_wannabuysheds' === this._shedType ){
                    startX  = -this._bones['b_f_r1'].position.x + this._offsetDistance;
                    endX    = this._bones['b_f_l1'].position.x + this._offsetDistance;
                }
            } else if( 'back' === side ) {
                if( this._bones['b_f_r4'] ){
                    startX  = this._bones['b_f_l1'].position.x + this._offsetDistance;
                    endX    = this._bones['b_f_r4'].position.x;
                }else if( 'structure_quaker_wannabuysheds' === this._shedType ){
                    startX  = this._bones['b_f_l1'].position.x + this._offsetDistance;
                    endX    = this._bones['b_f_r1'].position.x;
                }
            }
        }

        if( ( 'front' === side || 'back' === side ) && this.isSpecialShed() ) {
            let positionY: any;
            if( 'door' === type ) {
                positionY = componentHeight + this.getDoorFixedHeight( side );
            } else if( this.applyFixedHeightWindows( side, type, componentName ) ) {
                positionY = this.getWindowsFixedPositionY( type, componentHeight, side );
            }

            if( undefined !== positionY ) {
                endX = this.getEndXLimit( positionY, modelHeight, modelWidth ).positiveLimit;
                if( endX > modelWidth / 2 - horizontalOffset - this._offsetDistance ) {
                    endX = modelWidth / 2 - horizontalOffset - this._offsetDistance;
                }
            }

            startX = -endX;
        }

        if( 'structure_aframe_genesis' === this._shedType && 'windows' === type && ( 'front' === side || 'back' === side ) ) {
            let positionY   = this.getWindowsY( side, type, componentName, componentHeight );
            endX            = this.getEndXLimit( positionY, modelHeight, modelWidth ).positiveLimit;
            startX          = - endX;
            newStartY       = positionY - componentHeight;
            newEndY         = positionY;
        }

        if( 'dormer' === type ) {
            if( this.isGableDormer( componentName ) ) {
                newEndY                 = this.getBuildingHeight();
                newStartY               = newEndY - componentHeight;
            } else {
                const heightDistance    = this.getFlatDormerHeightDistance();
                newStartY               = this.getWallHeight() + heightDistance;
                newEndY                 = newStartY + this._flatDormerHeight;
            }
        }

        if( 'structure_dog_kennel' === this._shedType && ( 'left' === side || 'right' === side ) ) {
            newStartX   = this._bones['b_b_r1'].position.z + this.getDogKennelBoxDepth() / 2 - componentWidth / 2 - limitDistance;
            newEndX     = this._bones['b_b_r1'].position.z + this.getDogKennelBoxDepth() / 2 + componentWidth / 2 + limitDistance;
        }

        if( this.isMiddlePorch() ) {
            if( 'left_front' === side || 'left_back' === side || 'front_inner_porch' === side ) {
                newEndX     = middlePosition.z + componentWidth / 2 + limitDistance;
                newStartX   = middlePosition.z - componentWidth / 2 - limitDistance;
            }
            
            if( 'right_inner_porch' === side || 'left_inner_porch' === side ) {
                newEndX     = middlePosition.x + componentWidth / 2 + limitDistance;
                newStartX   = middlePosition.x - componentWidth / 2 - limitDistance;
            }
        }

        if( 'structure_ranch_cabin' === this._shedType || this.isAframeSingleGable() ) {
            switch( side ) {
                case 'inner_back':
                    startX      = this._bones['b_p_l1'].position.x + this._offsetDistance / 2 + horizontalOffset;
                    endX        = this._bones['b_b_m4'].position.x - this._offsetDistance / 2 - horizontalOffset;
                    newEndX     = middlePosition.x + componentWidth / 2 + limitDistance;
                    newStartX   = middlePosition.x - componentWidth / 2 - limitDistance;
                    break;
                case 'inner_left':
                    startX      = this._bones['b_b_m4'].position.z - this._offsetDistance / 2 - horizontalOffset;
                    endX        = this._bones['b_b_l1'].position.z + this._offsetDistance / 2 + horizontalOffset;
                    newStartX   = middlePosition.z - componentWidth / 2 - limitDistance;
                    newEndX     = middlePosition.z + componentWidth / 2 + limitDistance;
                    break;
                case 'back':
                    startX      = this._bones['b_b_l1'].position.x + this._offsetDistance / 2 + horizontalOffset;
                    endX        = this._bones['b_b_r1'].position.z - this._offsetDistance - horizontalOffset;
                    newStartX   = middlePosition.x + componentWidth / 2 + limitDistance;
                    newEndX     = middlePosition.x - componentWidth / 2 - limitDistance;
                    break;
                case 'left':
                    startX      = this._bones['b_f_l1'].position.z - this._offsetDistance / 2 - horizontalOffset;
                    endX        = this._bones['b_p_l1'].position.z + this._offsetDistance + horizontalOffset;
                    newStartX   = middlePosition.z - componentWidth / 2 - limitDistance;
                    newEndX     = middlePosition.z + componentWidth / 2 + limitDistance;
                    break;
                case 'roof':
                    if( this.isAframeSingleGable() ) {
                        startX = this._bones['b_b_t'].position.z + this._offsetDistance / 2 + horizontalOffset;
                        endX = this._bones['b_f_t1'].position.z - this._offsetDistance - horizontalOffset;
                        newEndX = middlePosition.z + componentWidth / 2 + limitDistance;
                        newStartX = middlePosition.z - componentWidth / 2 - limitDistance;

                        if( 'cupola' === type ) {
                            newStartY = this.getBuildingHeight();
                            newEndY = newStartY + componentHeight;
                        }
                    }
                    break;
            }
        }

        if( this.is2StoryLeanTo() ) {
            if( 'top_right' === side || 'top_left' === side ) {
                newEndY     = this._bones['b_b_l2'].position.y;
                newStartY   = newEndY - componentHeight;
            }
        }

        if( isHandleCupola ) {
            newStartY   = this.getBuildingHeight();
            newEndY     = newStartY + componentHeight;
        }
       
        let isValidMiddlePosition  = true;
        for( let i = 0; i < sortComps.length; i++ ) {
            const oldStartX = sortComps[i].position2D.horizontal.firstPoint;
            const oldEndX   = sortComps[i].position2D.horizontal.lastPoint;
            const oldStartY = sortComps[i].position2D.vertical.firstPoint;
            const oldEndY   = sortComps[i].position2D.vertical.lastPoint;

            if( ! this.isValidComponent( newStartX, newEndX, oldStartX, oldEndX, newStartY, newEndY, oldStartY, oldEndY ) ) {
                isValidMiddlePosition = false;
            }
        }
        if( isValidMiddlePosition ) {
            if( this.isInteriorComponent( type ) && 'light' !== type && middlePosition ) {
                middlePosition = this.updateInteriorPosition( middlePosition, side );
            }

            return middlePosition;
        }
        let y = 'door' === type ? this.getDoorFixedHeight( side ) : tmpYPosition;
        if( 'dormer' === type ) {
            y = newStartY;
        }

        if( 'structure_aframe_genesis' === this._shedType && 'windows' === type && ( 'front' === side || 'back' === side ) ) {
            y = this.getWindowsY( side, type, componentName, componentHeight );
        }

        if( this.is2StoryLeanTo() && 'windows' === type && ( 'top_right' === side || 'top_left' === side ) ) {
            y = this._bones['b_b_l2'].position.y;
        }

        if( this.applyFixedHeightWindows( side, type, componentName ) ) {
            y  = this.getWindowsFixedPositionY( type, componentHeight, side );
        }

        if( this.isMoveHorizontal( type ) ) {
            const buildHeight   = this.getBuildingHeight();
            const lightY        = this.getPositionYForLight();
            startX              = lightY > this.getWallHeight() ? - helpers.toFixed3( ( ( buildHeight - lightY ) / helpers.getTanFromDegrees( this.getRoofDegree() ) ) - this._offsetDistance - horizontalOffset ) : - helpers.toFixed3( modelWidth / 2 - this._offsetDistance - horizontalOffset );
            endX                = - startX;
        }

        let newPosition: any = this.generateFirstPosition( componentName, type, side, sortComps, componentWidth, componentHeight, y, endY, startX, modelLength, modelWidth, isHandleCupola );
        if( false === newPosition ) {
            newPosition = this.generateComponentPosition( 0, side, type, sortComps, componentWidth, componentHeight, y, endY, componentName, modelHeight, isHandleCupola );
        }

        let newX    = 0;
        let tmpX    = 0;
        let end     = 0;
        switch( side ) {
            case 'front':
                newX = newPosition.x;
                tmpX = newX - componentWidth / 2;

                if( this.isCornerPorchLeft() ){
                    startX = -startX;
                }

                if( this.isDeluxeCornerLeftPorch() ) {
                    tmpX    = newPosition.x;
                    startX  = endX + componentWidth / 2;
                }

                if( 'saltbox' === this.getRoofShape() && ( this._bones['b_f_r4'] || 'structure_quaker_wannabuysheds' === this._shedType ) ) {
                    startX = endX;
                }

                if( this.isCornerPorchLeft() ) {
                    startX = endX;
                }

                if( tmpX < startX ) {
                    return false;
                }
                break;
            case 'inner_front':
                newX = newPosition.x;
                tmpX = newX - componentWidth / 2;
                
                if( tmpX < endX ) {
                    return false;
                }
                
                break;
            case 'back':
                newX = newPosition.x;
                tmpX = newX + componentWidth / 2;
                if( 'structure_ranch_cabin' === this._shedType || this.isAframeSingleGable() ) {
                    if( tmpX < endX ) return false;
                } else {
                    if( tmpX > endX ) return false;
                }
                break;
            case 'top_left':
            case 'left':
                newX = newPosition.z;
                tmpX = newX - componentWidth / 2;

                if( 'structure_dog_kennel' === this._shedType && tmpX < endX ) {
                    return false;
                }

                if( this.isCornerPorchLeft() && tmpX < endX ){
                    return false;
                }

                if( this.isDeluxeCornerLeftPorch() || 'structure_ranch_cabin' === this._shedType || this.isAframeSingleGable() ) {
                    if( newPosition.z < endX + componentWidth / 2 ) {
                        return false;
                    }
                } else if( tmpX < startX && 'structure_dog_kennel' !== this._shedType && ! this.isCornerPorchLeft() ) {
                    return false;
                }
                break;
            case 'inner_left':
                newX = newPosition.z;
                tmpX = newX - componentWidth / 2;

                if( tmpX < endX ) {
                    return false;
                }
                break;
            case 'top_right':
            case 'right':
                newX = newPosition.z;
                tmpX = newX + componentWidth / 2;
                if( tmpX > endX ) {
                    return false;
                }
                break;
            case 'roof':
                if( this.isMoveHorizontal( type ) ) {
                    newX = newPosition.x;
                    tmpX = newX + componentWidth / 2;
                } else {
                    newX = newPosition.z;
                    tmpX = newX + componentWidth / 2;
                }

                if( tmpX > endX ) {
                    return false;
                }
                break;
            case 'dormer':
                newX = newPosition.z;
                tmpX = newX - componentWidth / 2;
                if( tmpX < startX ) {
                    return false;
                }
                break;
            case 'roof_left':
                newX = newPosition.z;
                tmpX = newX - componentWidth / 2;
                if( tmpX < startX ) {
                    return false;
                }
                break;
            case 'roof_right':
                newX = newPosition.z;
                tmpX = newX + componentWidth / 2;
                if( tmpX > endX ) {
                    return false;
                }
                break;
            case 'porch':
                newX = newPosition.x;
                tmpX = newX - componentWidth / 2;

                if( tmpX < startX ) {
                    return false;
                }
                break;
            case 'inner_front':
                if( this.isDeluxeCornerLeftPorch() ) {
                    tmpX    = newPosition.x;
                    startX  = endX + componentWidth / 2;
                }

                if( tmpX < startX ) {
                    return false;
                }
                break;
            case 'inner_right':
                if( this.isDeluxeCornerLeftPorch() ) {
                    tmpX    = newPosition.z;
                    end     = endX - componentWidth / 2;
                }

                if( tmpX > end ) {
                    return false;
                }
                break;
            case 'inner_back':
                if( newPosition.x + componentWidth / 2 > endX ) return false;
                break;
        }

        if( 'windows' === type && 'porch' !== side ){
            let EndYWall          = this.getWallHeight();
            if( ( 'front' === side || 'back' === side ) && this._bones['b_b_r3'] ) {
                EndYWall = this._bones['b_b_r3'].position.y;
            }

            if( this.isDeluxeCornerLeftPorch() ) {
                switch( side ) {
                    case 'front':
                        EndYWall = this._bones['b_f_l2'].position.y;
                        break;
                    case 'back':
                        EndYWall = this.getWallHeight();
                        break;                        
                }
            }

            if( tmpYPosition > EndYWall ) {
                return false;
            }
        }

        if( 'left_front' === side || 'left_back' === side || 'front_inner_porch' === side || 'right_inner_porch' === side || 'left_inner_porch' === side ) {
            return this.validNewPosition( componentName, type, newPosition, startX, endX, sideMoveBy, componentWidth, 0.35, this.getWallHeight() - 0.1, componentHeight );
        }

        return newPosition;
    }

    validNewPosition = ( name, type, newPosition, startX, endX, sideMoveBy, componentWidth, startY, endY, componentHeight ) => {
        const horizontalPoint       = 'x' === sideMoveBy ? newPosition.x : newPosition.z;
        let verticalPointTop        = 'door' === type || 'shelf' === type ? componentHeight + startY : newPosition.y;
        let verticalPointBottom     = 'door' === type || 'shelf' === type ? startY : newPosition.y;

        if( 'workbench' === type ){
            verticalPointTop      = this._bones['workbench_l'] ? this._bones['workbench_l'].position.y + this.getPositionYForInterior( type, name ) : 3;
            verticalPointBottom   = endY - componentHeight;
        }

        if( ( startX + endX ) / 2 < endX ) {
            const tmpX = horizontalPoint + componentWidth / 2;
            if( tmpX > endX ) {
                return false;
            }
        } else {
            const tmpX = horizontalPoint - componentWidth / 2;
            if( tmpX < endX ) {
                return false;
            }
        }
        
        if( verticalPointBottom < startY || verticalPointTop > endY ) return false;
        return newPosition;
    }

    generateFirstPosition( componentName, type, side, sortComps, componentWidth, componentHeight, y, endY, startX, modelLength, modelWidth, isHandleCupola ) {
        const limitDistance = this.getHorizontalSpacing( type );
        let newEndX         = 'front' === side || 'left' === side || 'roof_left' === side || 'dormer' === side || 'inner_front' === side || 'top_left' === side || this.isPorchSide( side ) ? - startX : startX + componentWidth;
        let newStartX       = 'front' === side || 'left' === side || 'roof_left' === side || 'dormer' === side || 'inner_front' === side || 'top_left' === side || this.isPorchSide( side ) ? newEndX - componentWidth - limitDistance : startX + limitDistance;
        let newStartY       = 'door' === type ? this.getDoorFixedHeight( side ) : endY - componentHeight;
        let newEndY         = 'door' === type ? componentHeight + this.getDoorFixedHeight( side ) : endY;
        const wallHeight    = this.getWallHeight();
        const buildHeight   = this.getBuildingHeight();

        if( isHandleCupola ) {
            newStartY   = buildHeight;
            newEndY     = newStartY + componentHeight;
        }

        if( 'dormer' === type ) {
            if( this.isGableDormer( componentName ) ) {
                newEndY                 = buildHeight;
                newStartY               = newEndY - componentHeight;
            } else {
                const heightDistance    = this.getFlatDormerHeightDistance();
                newStartY               = wallHeight + heightDistance;
                newEndY                 = newStartY + this._flatDormerHeight;
            }
        }

        if( 'structure_dog_kennel' === this._shedType ) {
            if( 'left' === side ) {
                newStartX   = startX;
                newEndX     = startX - componentWidth - limitDistance;
            } else if( 'right' === side ) {
                newStartX   = startX;
                newEndX     = startX + componentWidth + limitDistance;
            }
        }

        if( this.isPorchSide( side ) ) {
            if( this.isVerticalDistribute() ) {
                newStartY   = this.getMiddleVerticalPosition() - ( componentHeight / 2 );
                newEndY     = newStartY + componentHeight;
            } else {
                newStartY   = this.getPorchBottom();
                newEndY     = this.getPorchBottom() + componentHeight;
            }
        }

        if( this.isCornerPorchLeft() ){
            if( 'front' === side || 'left' === side || 'inner_left' === side || 'inner_front' === side ){
                newStartX   = startX;
                newEndX     = startX - componentWidth - limitDistance;
            }
        }

        if( 'workbench' === type ){
            newEndY     = this._bones['workbench_l'] ? this._bones['workbench_l'].position.y + this.getPositionYForInterior( type, componentName ) : 3;
            newStartY   = newEndY - componentHeight;
        }

        if( 'light' === type ){
            newEndY     = this.getPositionYForLight();
            newStartY   = newEndY - componentHeight;
        }

        if( this.isDeluxeCornerLeftPorch() ) {
            switch( side ) {
                case 'front':
                case 'inner_front':
                case 'left':
                    newStartX   = startX;
                    newEndX     = startX - componentWidth - limitDistance;
                    break;
                case 'inner_right':
                    newStartX   = startX;
                    newEndX     = startX + componentWidth + limitDistance;
                    break;
            }
        }
        
        if( this.isMiddlePorch() ){
            if( 'left_front' === side || 'left_back' === side || 'front_inner_porch' === side || 'left_inner_porch' === side || 'right_inner_porch' === side ){
                newStartX   = startX;
                newEndX     = startX - componentWidth - limitDistance;
            }
        }

        if( 'structure_ranch_cabin' === this._shedType || this.isAframeSingleGable() ) {
            switch( side ) {
                case 'back':
                case 'inner_back':
                    newStartX   = helpers.toFixed3( startX );
                    newEndX     = helpers.toFixed3( startX + componentWidth + limitDistance );
                    break;
                case 'left':
                case 'inner_left':
                    newStartX   = helpers.toFixed3( startX );
                    newEndX     = helpers.toFixed3( startX - componentWidth - limitDistance );
                    break;
                case 'roof':
                    if( this.isAframeSingleGable() ) {
                        newStartX = helpers.toFixed3( startX );
                        newEndX = helpers.toFixed3( startX + componentWidth + limitDistance );

                        if( 'cupola' === type ) {
                            newStartY = this.getBuildingHeight();
                            newEndY = newStartY + componentHeight;
                        }
                    }
                    break;
            }
        }

        if( 'structure_aframe_genesis' === this._shedType && 'windows' === type && ( 'front' === side || 'back' === side ) ) {
            newEndY     = this.getWindowsY( side, type, componentName, componentHeight );
            newStartY   = newEndY - componentHeight;
        }

        if( this.is2StoryLeanTo() ) {
            if( 'top_right' === side || 'top_left' === side ) {
                newEndY     = this._bones['b_b_l2'].position.y;
                newStartY   = newEndY - componentHeight;
            }
        }

        let isValid = true;
        for( let i = 0; i < sortComps.length; i++ ) {
            const oldStartX = helpers.toFixed3( sortComps[i].position2D.horizontal.firstPoint );
            const oldEndX   = helpers.toFixed3( sortComps[i].position2D.horizontal.lastPoint );
            const oldStartY = helpers.toFixed3( sortComps[i].position2D.vertical.firstPoint );
            const oldEndY   = helpers.toFixed3( sortComps[i].position2D.vertical.lastPoint );

            if( ! this.isValidComponent( newStartX, newEndX, oldStartX, oldEndX, newStartY, newEndY, oldStartY, oldEndY ) ) {
                isValid = false;
            }
        }

        if( ! isValid ) {
            return false;
        }
        let x = 0;
        let z = 0;
        switch( side ) {
            case 'front':
                x = - ( startX + componentWidth / 2 );
                z = modelLength / 2;
                
                if( this.isCornerPorchLeft() ){
                    x = startX - componentWidth / 2;
                }

                if( this.isDeluxeCornerLeftPorch() ) {
                    x = startX - componentWidth / 2;
                    z = this._bones['b_f_l1'].position.z;
                }

                break;
            case 'inner_front':
                x = startX - componentWidth / 2;
                z = modelLength / 2 - constants.cornerDepth;

                if( this.isDeluxeCornerLeftPorch() ) {
                    x = startX - componentWidth / 2;
                    z = this._bones['b_f_r1'].position.z;
                }

                break;
            case 'back':
                x = startX + componentWidth / 2;
                z = - modelLength / 2;
                break;
            case 'top_left':
            case 'left':
                x = - modelWidth / 2;
                z = 'structure_dog_kennel' === this._shedType ? startX - componentWidth / 2 : - ( startX + componentWidth / 2 );

                if( this.isCornerPorchLeft() || this.isDeluxeCornerLeftPorch() || 'structure_ranch_cabin' === this._shedType || this.isAframeSingleGable() ) {
                    z = startX - componentWidth / 2;
                }

                if( this.isSidePorch() && 'left' === side ) {
                    x = this._bones['b_b_l1'].position.x;
                }
         
                if( 'top_left' === side && this.is2StoryLeanTo() ) { 
                    y = this._bones['b_b_l2'].position.y;
                }

                break;
            case 'inner_left':
                x = - modelWidth / 2 + constants.cornerWidth;
                z = startX - componentWidth / 2;
                break;
            case 'top_right':
            case 'right':
                x = modelWidth / 2;
                z = startX + componentWidth / 2;
                
                if( 'top_right' === side && this.is2StoryLeanTo() ) { 
                    y = this._bones['b_b_l2'].position.y;
                }
                break;
            case 'lean_to_right':
                x = this._bones['lean_to_f_r2'].position.x;
                z = startX + componentWidth / 2;
                break;                
            case 'roof':
                if( this.isDoubleGable() ) {
                    y = this._bones['r_b_t1'].position.y;
                    x = this._bones['r_b_t1'].position.x;
                } else if( 'hip' === this.getRoofShape() ) {
                    y = this._bones['r_b_t'].position.y;
                    x = this._bones['r_b_t'].position.x;
                } else {
                    y = this._bones['b_b_t'].position.y;
                    x = this._bones['b_b_t'].position.x;
                }

                z = startX + componentWidth / 2;

                if( 'light' === type ){
                    y = this.getPositionYForLight();
                }
                
                if( 'flat' === this.getRoofShape() ){
                    const positionY = this.getWallHeight() + this.getWidth() / 2 * helpers.getTanFromDegrees( this.getRoofDegree() ) + 0.05;

                    x = 0;
                    y = positionY;
                }

                if( this.isMoveHorizontal( type ) ) {
                    z = 0;
                    x = startX + componentWidth / 2;
                }

                break;
            case 'dormer':
                if( this._bones['b_b_t1'] ) {
                    x = this._bones['b_b_t1'].position.x;
                }
                z = - ( startX + componentWidth / 2 );
                break;
            case 'roof_left':
                x = 0;
                z = - ( startX + componentWidth / 2 );
                break;
            case 'roof_right':
                x = 0;
                z = startX + componentWidth / 2;
                break;
            case 'porch':
                x = - ( startX + componentWidth / 2 );
                z = this._bones['b_p_t'].position.z;
                break;
            case 'inner_right':
                if( this.isDeluxeCornerLeftPorch() ) {
                    z = startX + componentWidth / 2;
                    x = this._bones['b_f_r4'].position.x;
                }
                break;
            case 'left_front':
            case 'left_back':
                x = - modelWidth / 2;
                z =  startX - componentWidth / 2;
                break;
            case 'left_inner_porch':
                x = startX + componentWidth / 2;
                z = constants.middleWidth / 2;
                break;
            case 'inner_back':
                x = startX + componentWidth / 2;
                z = this._bones['b_p_l1'].position.z;
                break;
            case 'right_inner_porch':
                x = startX - componentWidth / 2;
                z = - constants.middleWidth / 2;
                break;
            case 'front_inner_porch':
                x = - ( modelWidth / 2 - constants.middleDepth );
                z =  startX - componentWidth / 2;
                break;
        }

        let result = { x, y, z };

        if( this.isInteriorComponent( type ) && 'light' !== type ) {
            result = this.updateInteriorPosition( result, side );
        }

        return result;
    }

    generateComponentPositionDogKennel( side, type, components, componentHeight, endY ) {
        const modelWidth    = this.getWidth();
        const x             =  modelWidth / 4;
        const z             = - this.getDepth() / 2;
        const startX        = modelWidth / 2;
        const endX          = 0;
        const newStartY     = 'door' === type ? 0 : endY - componentHeight;
        const newEndY       = 'door' === type ? componentHeight : endY;
        const keys          = this.sortComponentKeyByPosition( components, side );
        let y               = 'windows' === type ? endY : 0;

        if( jQuery.isEmptyObject( components ) ) {
            return { x: - x, y, z };
        }

        let rightOK = true;
        let leftOK  = true;

        for( const key of keys ) {
            const component     = this._walls[side]['items'][key];
            const position2D    = component.position2D;
            const oldStartX     = position2D.horizontal.firstPoint;
            const oldEndX       = position2D.horizontal.lastPoint;
            const oldStartY     = position2D.vertical.firstPoint;
            const oldEndY       = position2D.vertical.lastPoint;
            if( ! this.isValidComponent( endX, startX, oldStartX, oldEndX, newStartY, newEndY, oldStartY, oldEndY ) ) {
                rightOK = false;
            } else if( ! this.isValidComponent( - startX, endX, oldStartX, oldEndX, newStartY, newEndY, oldStartY, oldEndY ) ) {
                leftOK = false;
            }
        }

        if( leftOK ) {
            return { x: -x, y, z };
        }

        if( rightOK ) {
            return { x, y, z };
        }
        
        return false;
    }

    /**
     * Get the anchor point for a given component type.
     *
     * @param {string} type - The type of the component.
     * 
     * @returns {string} - The anchor point for the component.
     */
    getAnchor = ( type, name ) => {
        if( this.isMoveAlongFloor( name ) ) {
            return 'middle';
        }

        switch( type ) {
            case 'door':
            case 'shelf':
            case 'workbench':
                return 'bottom';
            case 'windows':
            case 'vent':
                return 'top';
            case 'skylight_window':
                return 'roof';
            case 'breakerbox':
                return 'middle';
        }
    }

    /**
     * Generate valid position for new component
     */
    generatePosition = rawData => {
        const { isMoveAlongFloor, positionY, limitLeft, limitRight, limitBottom, side, componentWidth, componentHeight, type, components, step, anchor, isFixed, start, condition, operator, isZSide, fixedPosition, isValidTackRoom, isTackDoor, componentName, boundingBox } = rawData;
        const bottomComponent = this.getStartY( anchor, positionY, componentHeight );
        if( bottomComponent < limitBottom ) {
            return false;
        }

        const args = {
            side,
            isUseTargetPosition: true,
            component: { side, type },
            componentWidth: componentWidth,
            componentHeight: componentHeight,
        };
        const positionFollowHeight = 'middle' === anchor ? positionY - componentHeight / 2 : positionY;
        if( ! this._isEnableTackRoom || this._doorsOpeningTackRoom.length == 1 || isTackDoor ) {
            const middle        = helpers.toFixed3( ( limitLeft + limitRight ) / 2 );
            const midPosition   = isMoveAlongFloor ? { x: middle, y: fixedPosition, z: positionFollowHeight } : isZSide ? { x: fixedPosition, y: positionFollowHeight, z: middle } : { x: middle, y: positionFollowHeight, z: fixedPosition };

            if( isTackDoor ) {
                return midPosition;
            } else {
                if( this.isValidPosition( isMoveAlongFloor, midPosition, side, componentWidth, componentHeight, type, components, anchor, isValidTackRoom, boundingBox ) && this.componentIsOutOfShed( midPosition, componentWidth, componentHeight, side, type, componentName ) ) {
                    args['targetPosition'] = new Vector3( midPosition.x, midPosition.y, midPosition.z );
                    if( 'floor' === side && this.isComponentInSideMeshes( args ) ) {
                        return midPosition;
                    } else if( 'floor' !== side ) {
                        return midPosition;
                    }
                }
            }
        }

        for( let i = start; eval( `${i} ${condition} ${limitRight}` ); i = eval( `${i} ${operator} ${step}` ) ) {
            const midPoint      = helpers.toFixed3( eval( `${i} ${operator} ${componentWidth / 2}` ) );
            const rightPoint    = helpers.toFixed3( eval( `${midPoint} ${operator} ${componentWidth / 2}` ) );
            const position      = isMoveAlongFloor ? { x: midPoint, y: fixedPosition, z: positionFollowHeight } : isZSide ? { x: fixedPosition, y: positionFollowHeight, z: midPoint } : { x: midPoint, y: positionFollowHeight, z: fixedPosition };
            if( this.isValidPosition( isMoveAlongFloor, position, side, componentWidth, componentHeight, type, components, anchor, isValidTackRoom, boundingBox ) && eval( `${rightPoint} ${condition} ${limitRight}` ) && this.componentIsOutOfShed( position, componentWidth, componentHeight, side, type, componentName ) ) {
                args['targetPosition'] = new Vector3( position.x, position.y, position.z );
                if( 'floor' === side && this.isComponentInSideMeshes( args ) ) {
                    return position;
                } else if( 'floor' !== side ) {
                    return position;
                }
            }
        }

        if( isFixed ) {
            return false;
        }

        rawData.positionY = helpers.toFixed3( positionY - step );

        return this.generatePosition( rawData );
    }

    /**
     * Checks if a position is valid for placing a component considering existing components.
     * @param {object} position - The position of the component to check.
     * @param {string} side - The side of the component.
     * @param {number} componentWidth - The width of the component.
     * @param {number} componentHeight - The height of the component.
     * @param {string} type - The type of component.
     * @param {array} components - Array of existing components.
     * @param {object} anchor - The anchor point for positioning.
     * 
     * @returns {boolean} - Returns true if the position is valid, otherwise false.
     */
    isValidPosition = ( isMoveAlongFloor, position, side, componentWidth, componentHeight, type, components, anchor, isValidTackRoom, boundingBox ) => {
        let limitSpacing    = this.getHorizontalSpacing( type );
        limitSpacing        = 0 !== limitSpacing ? limitSpacing : this._distance2Component;
        const vector        = helpers.hasKeyInArray( zSide, side ) ? 'z' : 'x';
        const x             = position[vector];
        const startX        = x - componentWidth / 2 - limitSpacing;
        const endX          = x + componentWidth / 2 + limitSpacing;
        const positionY     = isMoveAlongFloor ? position.z : position.y;
        let startY          = this.getStartY( anchor, positionY, componentHeight );
        if( ( 'workbench' === type || 'shelf'=== type ) && boundingBox && boundingBox.geometry && boundingBox.geometry.extend.minimum.y > 0 && ! isMoveAlongFloor ) {
            startY += boundingBox.geometry.extend.minimum.y;
        }
        const endY          = startY + componentHeight;

        for( let i = 0; i < components.length; i++ ) {
            let oldHeight = components[i].componentHeight;
            let positionY = components[i].position3D.y;
            if( this.isMoveAlongFloor( components[i].name ) ) {
                oldHeight = components[i].componentDepth;
                positionY = components[i].position3D.z;
            }

            const oldWidth  = components[i].componentWidth;
            const tmpX      = components[i].position3D[vector];
            const tmpStartX = tmpX - oldWidth / 2;
            const tmpEndX   = tmpX + oldWidth / 2;
            const behavior  = this.getBehavior( components[i].name, components[i].componentType );
            const tmpAnchor = this.getAnchor( behavior, components[i].name );
            const tmpStartY = this.getStartY( tmpAnchor, positionY, oldHeight );
            const tmpEndY   = tmpStartY + oldHeight;

            if( ! this.isValidComponent( startX, endX, tmpStartX, tmpEndX, startY, endY, tmpStartY, tmpEndY ) ) {
                return false;
            }
        }

        if( this._isEnableTackRoom ) {
            if( side === this._sideWallTackRoom && isValidTackRoom ) {
                const tackRoomWidth     = this._dataTackroom.width;
                const tackRoomPosition  = this._dataTackroom.position;
                let tmpStartX           = - tackRoomWidth / 2;
                let tmpEndX             = tackRoomWidth / 2;
                const haftDepth         = this.getDepth() / 2;
                if( 'left' === tackRoomPosition ) {
                    tmpStartX  = -haftDepth;
                    tmpEndX    = -haftDepth + tackRoomWidth;
                } else if( 'right' === tackRoomPosition ) {
                    tmpStartX  = haftDepth - tackRoomWidth;
                    tmpEndX    = haftDepth;
                }
                if( ! this.isValidHorizontal( startX, endX, tmpStartX, tmpEndX ) ) {
                    return false;
                }
            } else {
                if( this.isIntersectComponentWithPartialWall( startX, endX ) && helpers.hasKeyInArray( zSide, side ) ) {
                    return false;
                }
            }
            
        }

        return true;
    }

    /**
     * Calculates the starting Y position based on anchor and component height.
     * @param {string} anchor - The anchor point ('middle', 'top', etc.).
     * @param {number} positionY - The Y position of the component.
     * @param {number} componentHeight - The height of the component.
     * 
     * @returns {number} - The calculated starting Y position.
     */
    getStartY = ( anchor, positionY, componentHeight ) => {
        if( 'roof' === anchor ) {
            return positionY - helpers.toFixed3( helpers.getSinFromDegrees( this.getRoofCorrectionDegree() ) * componentHeight );
        }

        return 'middle' === anchor ? helpers.toFixed3( positionY - ( componentHeight / 2 ) ) : ( 'top' === anchor ? helpers.toFixed3( positionY - componentHeight ) : positionY );
    }

    generateComponentPosition( start, side, type, sortComps, componentWidth, componentHeight, y, endY, componentName, modelHeight, isHandleCupola ) {
        if( this.isMoveHorizontal( type ) ) {
            sortComps.sort( ( a, b ) => a.position3D.x - b.position3D.x );
        }

        const leftPosition2D    = sortComps[start].position2D;
        const leftPosition3D    = sortComps[start].position3D;
        const oldCompWidth      = sortComps[start].componentWidth;
        const wallHeight        = this.getWallHeight();
        let newStartY           = 'door' === type ? this.getDoorFixedHeight( side ) : endY - componentHeight - this._distance2Component;
        let newEndY             = 'door' === type ? componentHeight + this.getDoorFixedHeight( side ) : endY - this._distance2Component;
        const buildingHeight    = this.getBuildingHeight();

        if( isHandleCupola ) {
            newStartY   = buildingHeight;
            newEndY     = newStartY + componentHeight;
        }
        
        if( 'workbench' === type ){
            newEndY     = this._bones['workbench_l'] ? this._bones['workbench_l'].position.y + this.getPositionYForInterior( type, componentName) : 3;
            newStartY   = newEndY - componentHeight;
        }

        if( 'light' === type ){
            newEndY     = this.getPositionYForLight();
            newStartY   = newEndY - componentHeight;
        }

        if( 'dormer' === type ) {
            if( this.isGableDormer( componentName ) ) {
                newEndY                 = buildingHeight;
                newStartY               = newEndY - componentHeight;
            } else {
                const heightDistance    = this.getFlatDormerHeightDistance();
                newStartY               = wallHeight + heightDistance;
                newEndY                 = newStartY + this._flatDormerHeight;
            }
        }

        if( this.isPorchSide( side ) ) {
            if( this.isVerticalDistribute() ) {
                newStartY   = this.getMiddleVerticalPosition() - ( componentHeight / 2 );
                newEndY     = newStartY + componentHeight;
            } else {
                newStartY   = wallHeight;
                newEndY     = wallHeight + componentHeight;
            }
        }

        if( 'structure_aframe_genesis' === this._shedType && 'windows' === type && ( 'front' === side || 'back' === side ) ) {
            newStartY   = modelHeight;
            newEndY     = this.getWindowsY( side, type, componentName, componentHeight );
        }

        if( this.is2StoryLeanTo() ) {
            if( 'top_right' === side || 'top_left' === side ) {
                newEndY     = this._bones['b_b_l2'].position.y;
                newStartY   = newEndY - componentHeight;
            }
        }

        let x               = 0;
        let z               = 0;
        let newStartX       = 0;
        let newEndX         = 0;
        let limitDistance   = this.getHorizontalSpacing( type );
        limitDistance       = 0 !== limitDistance ? limitDistance : this._distance2Component;

        switch( side ) {
            case 'front':
            case 'inner_front':
            case 'right_inner_porch':
            case 'porch':
                newStartX   = leftPosition2D.horizontal.firstPoint - limitDistance / 2;
                newEndX     = newStartX - componentWidth - limitDistance / 2;

                x = leftPosition3D.x - oldCompWidth / 2 - componentWidth / 2 - limitDistance;
                z = leftPosition3D.z;
                break;

            case 'back':
            case 'left_inner_porch':
            case 'inner_back':
                newStartX   = leftPosition2D.horizontal.lastPoint + limitDistance / 2;
                newEndX     = newStartX + componentWidth + limitDistance / 2;

                x = leftPosition3D.x + oldCompWidth / 2 + componentWidth / 2 + limitDistance;
                z = leftPosition3D.z;
                break;

            case 'left':
            case 'inner_left':
            case 'left_front':
            case 'left_back':
            case 'dormer':
            case 'roof_left':
            case 'front_inner_porch':
            case 'top_left':
                newStartX   = leftPosition2D.horizontal.firstPoint - limitDistance / 2;
                newEndX     = newStartX - componentWidth - limitDistance / 2;

                x = leftPosition3D.x;
                z = leftPosition3D.z - oldCompWidth / 2 - componentWidth / 2 - limitDistance;
                break;

            case 'right':
            case 'inner_right':
            case 'roof_right':
            case 'lean_to_right':
            case 'top_right':
                newStartX   = leftPosition2D.horizontal.lastPoint + limitDistance / 2;
                newEndX     = newStartX + componentWidth + limitDistance / 2;

                x = leftPosition3D.x;
                z = leftPosition3D.z + oldCompWidth / 2 + componentWidth / 2 + limitDistance;
                break;
                
            case 'roof':
                newStartX   = leftPosition2D.horizontal.lastPoint + limitDistance / 2;
                newEndX     = newStartX + componentWidth + limitDistance / 2;
                if( this.isDoubleGable() ) {
                    y = this._bones['r_b_t1'].position.y;
                    x = this._bones['r_b_t1'].position.x;
                } else if( 'hip' === this.getRoofShape() ) {
                    y = this._bones['r_b_t'].position.y;
                    x = this._bones['r_b_t'].position.x;
                } else {
                    y = this._bones['b_b_t'].position.y;
                    x = this._bones['b_b_t'].position.x;
                }

                z = leftPosition3D.z + oldCompWidth / 2 + componentWidth / 2 + limitDistance;

                if( 'light' === type ){
                    y = this.getPositionYForLight();
                }

                if( 'flat' === this.getRoofShape() ){
                    const positionY = this.getWallHeight() + this.getWidth() / 2 * helpers.getTanFromDegrees( this.getRoofDegree() ) + 0.05;

                    x = 0;
                    y = positionY;
                }

                if( this.isMoveHorizontal( type ) ) {
                    z = 0;
                    x = leftPosition3D.x + oldCompWidth / 2 + componentWidth / 2 + limitDistance;
                }

                if( this.isAframeSingleGable() ) {
                    newStartY = this.getBuildingHeight();
                    newEndY = newStartY + componentHeight;
                }

                break;
        }
        if( start === sortComps.length - 1 ) {
            return { x, y, z };
        }
        for( let i = 0; i < sortComps.length; i++ ) {
            const oldStartX = sortComps[i].position2D.horizontal.firstPoint - limitDistance / 2;
            const oldEndX   = sortComps[i].position2D.horizontal.lastPoint + limitDistance / 2;
            const oldStartY = sortComps[i].position2D.vertical.firstPoint;
            const oldEndY   = sortComps[i].position2D.vertical.lastPoint;

            if( ! this.isValidComponent( newStartX, newEndX, oldStartX, oldEndX, newStartY, newEndY, oldStartY, oldEndY ) ) {
                return this.generateComponentPosition( start + 1, side, type, sortComps, componentWidth, componentHeight, y, endY, componentName, modelHeight, isHandleCupola );
            }
        }

        if( this.applyFixedHeightWindows( side, type, componentName ) && ! this.isInteriorComponent( type ) ) {
            y  = this.getWindowsFixedPositionY( type, componentHeight, side );
        }

        return { x, y, z };
    }

    /**
     * Hide or Show horizontal trims on 7ft/8ft for Apex Shed
     * 
     */
    showHorizontalTrims(){
        const _this = this;
        const trim_7ft = _this._scene.getMeshByName( 'trim_7ft' ) as Mesh;
        const trim_8ft = _this._scene.getMeshByName( 'trim_8ft' ) as Mesh;        

        if( null !== trim_7ft && null !== trim_8ft ){
            trim_7ft.setEnabled(false)

            if( 'apexshed_with_horizontal_trims' === _this._shedType ){
                trim_8ft.setEnabled(false);
                let trimHeight = _this.getWallHeight();

                if( 7 === parseFloat( _this._wallHeight ) ) {
                    trimHeight += 0.5
                }

                _this.buildHorizontalTrim( trimHeight );
            }
        }

        //Handle Setting for horizontal trim
        const horizontalTrims = this.getSettingData( 'horizontal_trim' );
        if( 'true' !== horizontalTrims.enable ) return;

        const { material }  = horizontalTrims;
        const argsTrims     = [ 'trim_7ft', 'trim_8ft', 'horizontal_trim_back', 'horizontal_trim_front', 'horizontal_trim_obj' ];
        argsTrims.forEach( key => {
            const mesh = _this.getMeshByName( key ) as Mesh;
            if( mesh ) mesh.dispose();
        } );

        if( this._apiData['horizontal_trim'] ) {
            let status = false;
            if( 0 === material.length || helpers.hasKeyInArray( material, this.getSidingType() ) ) {
                status = true;
            }
            
            this._apiData['horizontal_trim'].forEach( key => {
                const mesh = _this.getMeshByName( key ) as Mesh;
                if( mesh ) mesh.setEnabled( status );
            } );
        }
    }

    getTrimByCondition = ( side : string ) => {
        const horizontalTrims = this.getSettingData( 'horizontal_trim' );
        if( 'true' !== horizontalTrims.enable ) return;

        const wallHeight                    = this._wallHeight;
        const { trim_condition, material }  = horizontalTrims;

        const positionData      = trim_condition.filter( data => data.wall_height == wallHeight && helpers.hasKeyInArray( data.sides, side ) );
        this._horizontalTrimMat = '' !== horizontalTrims.mat_key ? horizontalTrims.mat_key : this._horizontalTrimMat;

        if( positionData.length > 0 ) {
            const trimPosition = '' !== positionData[0].trim_position ? positionData[0].trim_position : wallHeight;
            return { type: 'horizontal_trim', position: Number( trimPosition ), matKey: this._horizontalTrimMat, material };
        }

        return false;
    }

    /**
     * Build horizontal trim
     */
    buildHorizontalTrim( positionY: float ) {
        const _this             = this;
        const trimHeight        = 0.3;
        const wallHeight        = _this.getWallHeight();
        const halfWidth         = _this.getWidth() / 2;
        let shape               = [];

        if( positionY > wallHeight ){
            let roofTan     = helpers.getTanFromDegrees( 90 - _this.getRoofDegree() );
            let tempY       = positionY - wallHeight;

            let tmpX        = roofTan * tempY;
            let trimX       = halfWidth - tmpX - 0.05;
            let tmpX2       = roofTan * ( tempY + 0.3 );
            let trimX2      = halfWidth - tmpX2 - 0.05;
            

            shape = [ 
                new Vector3( -trimX, 0, 0 ),
                new Vector3( -trimX2, 0, trimHeight ),
                new Vector3(  trimX2, 0, trimHeight ),
                new Vector3(  trimX, 0, 0 ),
            ];
        }else{
            shape = [ 
                new Vector3( -halfWidth, 0, 0 ),
                new Vector3( -halfWidth, 0, -trimHeight ),
                new Vector3(  halfWidth, 0, -trimHeight ),
                new Vector3(  halfWidth, 0, 0 ),
            ];
        }

        let horizontalTrimFrontMesh = _this.getMeshByName( 'horizontal_trim_front' );
        let horizontalTrimBackMesh  = _this.getMeshByName( 'horizontal_trim_back' );
        let cloneTrimMat            = _this._scene.getMaterialByName( this._horizontalTrimMat ) as StandardMaterial;
        if( null === cloneTrimMat ) {
            const trimMat           = _this._scene.getMaterialByName( 'trim_mat' ) as StandardMaterial;
            cloneTrimMat            = trimMat.clone( this._horizontalTrimMat );

            _this._initialUVs[this._horizontalTrimMat] = cloneTrimMat
        }
        cloneTrimMat.ambientTexture = null;

        cloneTrimMat.diffuseTexture.updateSamplingMode( 1 );
        
        if( horizontalTrimFrontMesh ) horizontalTrimFrontMesh.dispose();
        if( horizontalTrimBackMesh ) horizontalTrimBackMesh.dispose();
        
        let faceUV = new Array(6);
        for (var i = 0; i < 6; i++) {
            faceUV[i] = new Vector4( 10/1024, 250/1024, 680/1024, 280/1024 );
        }

        horizontalTrimFrontMesh = MeshBuilder.ExtrudePolygon( 'horizontal_trim_front', { shape: shape, faceUV: faceUV, depth: 0.04, sideOrientation: Mesh.DOUBLESIDE } );
        horizontalTrimBackMesh  = horizontalTrimFrontMesh.clone( 'horizontal_trim_back' );

        horizontalTrimFrontMesh.position    = new Vector3( 0, positionY, _this._bones['b_f_l1'].position.z + 0.04 );
        horizontalTrimFrontMesh.rotation    = new Vector3( -Math.PI/2, Math.PI, 0 );
        horizontalTrimFrontMesh.material    = cloneTrimMat;

        horizontalTrimBackMesh.position     = new Vector3( 0, positionY, _this._bones['b_b_l1'].position.z );
        horizontalTrimBackMesh.rotation     = new Vector3( -Math.PI/2, Math.PI, 0 );
        horizontalTrimBackMesh.material     = cloneTrimMat;
    }

    notification = ( meshes, notice ) => {
        this.removeMeshes( meshes );
        this._engine.hideLoadingUI();
        this.addNotificationRequiredDoor();
        return this.createAlert( notice );
    }

    getPositionChangeComponent = ( side, position, componentSize, componentType ) => {
        let left  = 0;
        let right = 0;
        let check = 'x';

        switch ( side ) {
            case "front":
            case "back":
                left  = this._bones['b_f_l1'].position.x;
                right = this._bones['b_f_r1'].position.x;
                check = 'x';

                if( this.isCornerPorchLeft() ) {
                    left = this._bones['b_b_l1'].position.x;
                } else if( this.isDeluxeCornerLeftPorch() ) {
                    if( 'front' === side ) {
                        right   = this._bones['b_f_l4'].position.x;
                    } else {
                        left    = this._bones['b_b_l1'].position.x;
                        right   = this._bones['b_b_r1'].position.x;
                    }
                }

                if( ( 'structure_ranch_cabin' === this._shedType || this.isAframeSingleGable() ) && 'back' === side ) {
                    left = this._bones['b_b_l1'].position.x;
                }

                break;

            case "inner_front":
                check = 'x';

                if( this.isCornerPorchLeft() ){
                    left  = this._bones['b_p_l1'].position.x;
                    right = this._bones['b_b_m4'].position.x;
                }else if( this.isDeluxeCornerLeftPorch() ){
                    left  = this._bones['b_f_r4'].position.x;
                    right = this._bones['b_f_r1'].position.x;
                }
                
                break;

            case "left":
            case "right":
                left    = this._bones['b_b_l1'].position.z;
                right   = this._bones['b_f_r1'].position.z;
                check   = 'z';

                if( 'left' === side ) {
                    if( this.isDeluxeCornerLeftPorch() ) {
                        right = this._bones['b_f_l1'].position.z;
                    } else if( 'structure_ranch_cabin' === this._shedType || this.isAframeSingleGable() ) {
                        left = this._bones['b_p_l1'].position.z;
                    }
                }

                break;

            case "inner_left":
                left    = this._bones['b_b_l1'].position.z;
                right   = this._bones['b_f_r1'].position.z;
                check   = 'z';

                if( 'structure_ranch_cabin' === this._shedType || this.isAframeSingleGable() ) {
                    right = this._bones['b_b_m4'].position.z;
                }

                break;

            case "inner_right":
                left    = this._bones['b_f_r4'].position.z;
                right   = this._bones['b_f_r6'].position.z;
                check   = 'z';

                break;

            case "front_inner_porch":
                if( this.isDoubleGable() ) {
                    left    = this._bones['b_b_m8'].position.z;
                    right   = this._bones['b_b_m4'].position.z;
                } else {
                    left    = this._bones['b_b_m4'].position.z;
                    right   = this._bones['b_f_m4'].position.z;
                }
                check   = 'z';

                break;

            case "left_front":
                if( this.isDoubleGable() ) {
                    left    = this._bones['b_p_l1'].position.z;
                    right   = this._bones['b_f_l1'].position.z;
                } else {
                    left    = this._bones['b_p_l3'].position.z;
                    right   = this._bones['b_f_l1'].position.z;
                }
                check   = 'z';

                break;

            case "left_back":
                if( this.isDoubleGable() ) {
                    left    = this._bones['b_b_l1'].position.z;
                    right   = this._bones['b_p_l3'].position.z;
                } else {
                    left    = this._bones['b_b_l1'].position.z;
                    right   = this._bones['b_p_l1'].position.z;
                }
                check   = 'z';

                break;

            case "left_inner_porch":
                if( this.isDoubleGable() ) {
                    left    = this._bones['b_p_l1'].position.x;
                    right   = this._bones['b_b_m4'].position.x;
                } else {
                    left    = this._bones['b_p_l3'].position.x;
                    right   = this._bones['b_f_m4'].position.x;
                }
                check   = 'x';

                break;

            case "inner_back":
                left    = this._bones['b_p_l1'].position.x;
                right   = this._bones['b_b_m4'].position.x;
                check   = 'x';

                break;

            case "right_inner_porch":
                if( this.isDoubleGable() ) {
                    left    = this._bones['b_p_l3'].position.x;
                    right   = this._bones['b_b_m8'].position.x;
                } else {
                    left    = this._bones['b_p_l1'].position.x;
                    right   = this._bones['b_b_m4'].position.x;
                }
                check   = 'x';

                break;

            case "diagonal_right":
                position = Vector3.Center( this._bones['b_f_r6'].position, this._bones['b_f_l4'].position );

                break;

            case "roof":
                left    = this._bones['r_b_t'] ? this._bones['r_b_t'].position.z : - this.getDepth() / 2;
                right   = this._bones['r_f_t'] ? this._bones['r_f_t'].position.z : this.getDepth() / 2;
                check   = 'z';

                if( this.isFrontPorch() ){
                    right = this._bones['b_f_t'].position.z;
                }

                if( 'hip' === this.getRoofShape() ){
                    left    = - this.getDepth() / 2;
                    right   = this.getDepth() / 2;
                }

                break;
            
            default:
                break;
        }

        if( 'diagonal_right' === side ){
            return position;
        }

        let positionCheck = 'x' === check ? position.x : position.z;

        if( ( positionCheck - componentSize/2 ) < left ){
            if( 'windows' === componentType || 'door' === componentType ){
                left += this._offsetDistance;
            }

            const newPosition = left + componentSize/2;

            if( 'x' === check ) {
                position = new Vector3( newPosition, position.y, position.z );
            }else{
                position = new Vector3( position.x , position.y, newPosition );
            }

        }else if( ( positionCheck + componentSize/2 ) > right ){
            if( 'windows' === componentType || 'door' === componentType ){
                right -= this._offsetDistance;
            }

            const newPosition = right - componentSize/2;

            if( 'x' === check ) {
                position = new Vector3( newPosition, position.y, position.z );
            }else{
                position = new Vector3( position.x , position.y, newPosition );
            }

        }

        return position;
    }

    /**
     * Get component behavior
     * 
     * @param componentName Name of the component
     * @param type Type of the component
     * @returns {String}
     */
    getBehavior = ( componentName, type ) => {
        const categories = this.getComponentCategory( componentName );

        return categories['match_behavior'] ? categories['match_behavior'] : type;
    }

    /**
     * Get component behavior
     * 
     * @param componentName Name of the component
     * @returns {String}
     */
    getComponentBehavior = ( componentName ) => {
        return jQuery( `[data-upgrade-component-name="${componentName}"]` ).data( 'behavior' );
    }

    /**
     * Check if the component moves along the floor
     * 
     * @param {String} componentName Name of the component
     * 
     * @returns {Boolean}
     */
    isMoveAlongFloor = ( componentName ) => {
        const componentBehavior = this.getComponentBehavior( componentName );

        return constants.moveAlongFloor === componentBehavior;
    }
    
    /**
     * Add new component to canvas, init data, handle position, rotation.... for component
     * 
     * @param args Array
     * @returns 
     */
    async addNewComponent( args ) {
        let _this = this;
        let { side, componentType, componentName, parentKey, position, rotation, vertical, horizontal, isChangeComponent, isTackDoor, componentSize, id, order, lockData, isUpdateComponent, isBlock, colorInfo, side_floor_place_along_side, side_floor_place_along_side_offset, side_floor_horizontal_anchor, side_floor_horizontal_distance, blockOptions, isPlaceAnchorPosition } = args;
        if( 'switch' === componentType || 'outlet' === componentType ) {
            const tieWithDoor           = jQuery( `[data-upgrade-component-name="${componentName}"]` ).data( 'tie-with-door' );
            const componentDisplayName  = jQuery( `[data-upgrade-component-name="${componentName}"]` ).val();
            let listSideHasDoor : any   = _this.getSideHasDoor();
            if( tieWithDoor && ! listSideHasDoor.includes( side ) ) {
                let sideHasDoor = _this.getSideAllowAddSwitchAndOutlet( componentName, 0 );
                if( ! sideHasDoor ) {
                    return _this.createAlert( `Structure requires ${componentDisplayName} to be placed near a door. Please add a door first then add the ${componentDisplayName} next to it.` );
                }
                side = sideHasDoor;
                _this.createAlert( `Structure requires ${componentDisplayName} to be placed near a door. Moved the item to side ${helpers.getSideLabel( side, '' )} accordingly.` );
            }
        }
        if( 'shelf' === componentType || 'workbench' === componentType ) {
            const wallWidth = _this.getWallWidthBySide( side );
            if( wallWidth < componentSize ) {
                return _this.createAlert( this.NOTICES.itemNotFit );
            }
        }

        if( _this.hasDiagonalRightComponent( side, componentType ) || _this.allowOneComponent( side, componentType ) ) {
            return this.createAlert( _this.NOTICES.allowOneComponent );
        }

        const version = helpers.getVersionComponent( componentName, _this._componentData );
        return new Promise(resolve => {
            const componentTask = _this._assetsManager.addMeshTask( `load_${componentType}_${componentName}`, '', `${_this._componentDir}${componentType}/${componentName}/`, `${componentName}.babylon${version}` );

            componentTask.onError   = ( task, message, exception ) => {
                console.error( exception );
                resolve( false );
            }
            
            componentTask.onSuccess = ( task ) => {
                const componentKey  = side + "_" + componentName + "_" + new Date().getTime();
                let meshes          = task.loadedMeshes as Mesh[];
                const isMoveOnFloor = _this.isMoveAlongFloor( componentName );

                if( -1 !== typesCutFrame.indexOf( componentType ) ) {
                    _this.scaleIntersectMesh( componentName, meshes );
                    _this.handleCutBlock( meshes, componentType, componentName );
                }

                if( 'door' === componentType ) {
                    _this.handleHingeAndLock( lockData, meshes );
                }

                if( 'dormer' !== componentType && 'cupola' !== componentType && 'light' !== componentType && 'floor' !== side ) {
                    _this.setPickableStatus( meshes, false );
                }

                _this.addWrapperObject( meshes, componentType, componentName, componentSize );

                if( 'dormer' === componentType ) {
                    if( ! _this.isValidDormer( meshes, componentName, componentType ) ) {
                        return _this.notification( meshes, _this.NOTICES.itemTooLarge );
                    }

                    if( ! _this.isGableDormer( componentName ) ) {
                        _this.importSkeletons(task.loadedSkeletons);
                        _this.importMaterials(meshes);
                        _this.flatDormerHandleRoof();
                        if( 0 === _this._initRoofFlatDormerWidth ) {
                            if( _this._bones['flat_dormer_f'] && _this._bones['flat_dormer_b'] && _this._bones['flat_dormer_t'] ) {
                                const flatDormerWidth           = _this._bones['flat_dormer_f'].position.z - _this._bones['flat_dormer_b'].position.z;
                                const flatDormerHeight          = _this._bones['flat_dormer_t'].position.y - _this._bones['flat_dormer_b'].position.y;
                                _this._initRoofFlatDormerWidth  = Math.sqrt( flatDormerWidth * flatDormerWidth + flatDormerHeight * flatDormerHeight );
                            }
                        }

                        if( 0 === _this._initFlatDormerHeight ) {
                            if( _this._bones['flat_dormer_t'] && _this._bones['flat_dormer_b'] ) {
                                _this._initFlatDormerHeight = _this._bones['flat_dormer_t'].position.y - _this._bones['flat_dormer_b'].position.y;
                            }
                        }

                        if( 0 === _this._initFlatDormerWidth ) {
                            if( _this._bones['flat_dormer_f'] ) {
                                _this._initFlatDormerWidth = _this._bones['flat_dormer_f'].position.z;
                            }
                        }
                        _this.dormerAdjustUVs();
                    }
                }

                if( 'shelf' === componentType || 'workbench' === componentType ) {
                    _this.importSkeletons( task.loadedSkeletons );
                    componentSize   = componentSize || 2;
                    const positionX = componentSize / 2 - constants.interiorOffset;

                    if( _this._bones[`${componentType}_l`] ) _this._bones[`${componentType}_l`].setPosition( new Vector3( -positionX, _this._bones[`${componentType}_l`].position.y, _this._bones[`${componentType}_l`].position.z ) );
                    if( _this._bones[`${componentType}_r`] ) _this._bones[`${componentType}_r`].setPosition( new Vector3(  positionX, _this._bones[`${componentType}_r`].position.y, _this._bones[`${componentType}_r`].position.z ) );

                    let boundingBox = this.filterByValue( meshes, 'name', '_bb_obj' ) as Mesh;
                    boundingBox.geometry.extend.maximum.x =  positionX;
                    boundingBox.geometry.extend.minimum.x = -positionX;
                    
                    meshes.forEach( m => {
                        m.refreshBoundingInfo( true );
                    });

                    if( position ){
                        position = _this.getPositionChangeComponent( side, position, componentSize, componentType );
                    }
                }

                if( 'light' === componentType ){
                    componentSize = this.getComponentWidth( meshes, componentType, componentName );
                    if( position ){
                        position = _this.getPositionChangeComponent( side, position, componentSize, componentType );
                    }
                }

                if( 'structure_quaker_wannabuysheds' === this._shedType && ( 'door' === componentType || 'windows' === componentType ) ) {
                    componentSize = this.getComponentWidth( meshes, componentType, componentName );

                    if( position ){
                        position = _this.getPositionChangeComponent( side, position, componentSize, componentType );
                    }
                }

                if( 'structure_dog_kennel' === this._shedType ) {
                    if( 'front' === side ) {
                        return _this.notification( meshes, _this.NOTICES.sideNotAccept );
                    }

                    if( 'door' === componentType && 'dogkennel_split' === this.getActiveLayout() && 'back' === side ) {
                        if( -1 !== componentName.split("_").indexOf('double') ) {
                            return _this.notification( meshes, _this.NOTICES.itemTooLarge );
                        }
                    }
                }

                _this._firstMeshes.push( task.loadedMeshes[0] );

                _this.updateBoundingBoxColor( meshes, componentName, componentType );

                const componentDepth    = _this.getComponentDepth( meshes );
                const componentWidth    = _this.getComponentWidth( meshes, componentType, componentName, this.isMoveHorizontal( componentType ) );
                let componentHeight     = _this.getComponentHeight( meshes, componentType );

                if( position && this.isPorchSide( side ) && this.isVerticalDistribute() ) {
                    position.y = this.calculateMiddlePosition( componentHeight );
                }

                if( 0 !== componentWidth && 0 !== componentHeight && 'flower_box' !== componentType && 'shutters' !== componentType ) {
                    let endY                    = _this.getWallHeight();
                    const wallHeight            = _this.getWallHeightBySide( side );
                    const fixedHeightWindows    = this.getWindowsFixedPositionY( componentType, componentHeight, side );
                    const isApplyFixedHeight    = this.applyFixedHeightWindows( side, componentType, componentName );
                    const isFollowMainDoor      = this.isFollowMainDoor();
                    if( isApplyFixedHeight && ! isFollowMainDoor && fixedHeightWindows > wallHeight ) {
                        _this.notification( meshes, _this.NOTICES.windowNotAdd );
                        return resolve( false );
                    }

                    if( 'structure_monitorbarns_helmuth' === _this._shedType && ( 'top_left' === side || 'top_right' === side ) ) {
                        endY = _this._bones['b_f_l5'].position.y;
                    }
                    
                    if( ( undefined === position || null === position ) && undefined === vertical && undefined === horizontal ) {
                        if( _this.isGableDormer( componentName ) ) {
                            componentHeight = this.getGableDormerDistance( `${componentName}_frame_obj`, meshes, 'Dormer_B_F_T', 'Dormer_B_F_R1', 'y' );
                        }
                        const isValidTackRoom  = this._isEnableTackRoom && isTackDoor ? false : 'door' === componentType;
                        let newPosition :any   = '';
                        if( isTackDoor ) {
                            const meshesInfo   = this.getWallBones( side );
                            const args = { side, type: componentType, meshesInfo, componentWidth, componentHeight, componentName, isValidTackRoom: false, isTackDoor: true, boundingBox: false };
                            newPosition = _this.getNewPosition( args );
                        } else {
                            const boundingBox = this.filterByValue( meshes, 'name', '_bb_obj' ) as Mesh;
                            newPosition = _this.getPositionForNewComponent( { side, type: componentType, componentDepth, componentWidth, componentHeight, componentName, isValidTackRoom, boundingBox } );
                        }
                        if( isApplyFixedHeight ) {
                            if( ( isFollowMainDoor && componentHeight > fixedHeightWindows ) || ( ! isFollowMainDoor && fixedHeightWindows > wallHeight ) ) {
                                return _this.notification( meshes, _this.NOTICES.windowNotAdd );
                            }

                            if( _this.isRequireDoor() ) {
                                return _this.notification( meshes, _this.NOTICES.requireDoor );
                            }
                        }

                        if( false !== newPosition ) {
                            position = newPosition;
                        } else {
                            return _this.notification( meshes, _this.NOTICES.itemTooLarge );
                        }
                    }

                    if( true === isChangeComponent ) {
                        if( isApplyFixedHeight ) {
                            if( ( ( isFollowMainDoor || _this.isOffsetFromFloorTop() ) && componentHeight > fixedHeightWindows ) || ( ! isFollowMainDoor && fixedHeightWindows > wallHeight ) ) {
                                _this.handleDeleteComboElectricalComponentTieDoor( side );
                                return _this.notification( meshes, _this.NOTICES.windowNotAdd );
                            }
                        }

                        if( this.isMiddlePorch() && undefined !== position ) {
                            const wallWidth = this.getWallWidthBySide( side );
                            if( 'right' === side ) {
                                if( Math.abs( position.z ) + componentWidth / 2 > wallWidth / 2 ) {
                                    _this.handleDeleteComboElectricalComponentTieDoor( side );
                                    return _this.notification( meshes, _this.NOTICES.itemTooLarge );
                                }
                            } else if( ('front' === side || 'back' === side ) && Math.abs( position.x ) + componentWidth / 2 > wallWidth / 2 ) {
                                _this.handleDeleteComboElectricalComponentTieDoor( side );
                            }
                        }
                    }

                    if( ! horizontal || ! vertical ) {
                        horizontal = { distance: 0, measured_from: 'left', roof_top_aligned: 'manual' }
                        let verticalMeasuredFrom = 'door' === componentType || 'shelf' === componentType || 'workbench' === componentType ? 'ground' : 'wall';
                        let verticalDistance = 'door' === componentType ? position.y : endY - position.y;

                        if( 'dormer' === side || this.isPorchSide( side ) ||'vent' === componentType ) {
                            verticalDistance = _this.getBuildingHeight() - position.y;
                        }

                        vertical = { distance: verticalDistance, measured_from: verticalMeasuredFrom }
                    }
                }
                
                if( 'cupola' === componentType ){
                    let cupolaMetalMaterials = _this.getMaterialByName("cupola_roof_mat");
                    let diffuseColor = Color3.FromHexString( '#000000' );
                    if( cupolaMetalMaterials ){                        
                        cupolaMetalMaterials.forEach( cupolaMetalMaterial => {                            
                            cupolaMetalMaterial.diffuseColor = diffuseColor;
                        });
                    }
                }

                if( ! _this.isMobile() ) {
                    _this._scene.onReadyObservable.add( () => {
                        _this.handleAddCursorPointer();
                    });
                }
                
                _this._meshes = meshes;
                let firstMesh = meshes[0];
                meshes.forEach( m => {
                    _this._allMeshes.push( m );
                });
                
                const smithbiltComponentTrim = this.getMeshByName( 'smithbilt_component_trim_obj' );
                let trimMesh;
                if( smithbiltComponentTrim && ( 'left' === side || 'right' === side ) && ( 'door' === componentType || 'windows' === componentType ) ) {
                    trimMesh            = smithbiltComponentTrim.clone( `smithbilt_component_trim_${componentName}` );
                    trimMesh.skeleton   = smithbiltComponentTrim.skeleton.clone( `skeleton_smithbilt_component_trim_${componentName}` );
                    trimMesh.setParent( firstMesh );
                    trimMesh.visibility = 0;
                }

                if (meshes != undefined) {
                    meshes.map( mesh => {
                        mesh.alwaysSelectAsActiveMesh = true;
                        mesh.metadata = {
                            key: componentKey,
                            name: componentName,
                            side,
                            type: componentType,
                            trimMesh,
                        };
                    } );

                    firstMesh.getChildMeshes().map( mesh => {
                        mesh.alwaysSelectAsActiveMesh   = true;
                        mesh.metadata                   = {
                            key: componentKey,
                            name: componentName,
                            side,
                            type: componentType,
                            trimMesh,
                        };

                        if( 'front' === mesh.name || 'back' === mesh.name ) {
                            meshes.push( mesh as Mesh );
                        }
                    } );
                }

                _this.hideComponentMeshes(meshes);
                _this.importMaterials(meshes);
                _this.optimizeMaterials();
                
                const behavior = this.getBehavior( componentName, componentType );

                firstMesh.detachFromBone();
                if (position == undefined || position == null) {
                    if( 'floor' === side ) {
                        const horizontalDistance = helpers.toFixed3( Number( side_floor_horizontal_distance ) / 12 );
                        const positionType = 'left' === side_floor_horizontal_anchor || 'right' === side_floor_horizontal_anchor ? 'manual' : 'default';
    
                        vertical = { distance: this.getPositionYForInterior( componentType, componentName ), measured_from: 'ground' };
                        horizontal = { distance: horizontalDistance, measured_from: side_floor_horizontal_anchor, roof_top_aligned: positionType };
                        position = _this.calculateComponentPosition( meshes, side_floor_place_along_side, vertical, horizontal, componentType, componentName );

                        if( this.isBetaFeature( 'refactor_calc_layout_position' ) ) {
                            position = _this.calculateLayoutPosition( meshes, side_floor_place_along_side, vertical, horizontal, componentType, componentName );
                        }
        
                        let offset  = ! isNaN( Number( side_floor_place_along_side_offset ) ) && 0 !== Number( side_floor_place_along_side_offset ) ? helpers.toFixed3( Number( side_floor_place_along_side_offset ) / 12 ) : 0;
                        offset += componentDepth / 2 + constants.innerWallThickness;
    
                        position = this.getFloorComponentPositionByOffset( position, side_floor_place_along_side, offset );
                    } else {
                        position = _this.calculateComponentPosition(meshes, side, vertical, horizontal, componentType, componentName);
                        if( this.isBetaFeature( 'refactor_calc_layout_position' ) ) {
                            position = _this.calculateLayoutPosition( meshes, side, vertical, horizontal, componentType, componentName );
                        }

                        if( isPlaceAnchorPosition && ! _this.isValidComponentPosition( side, position, componentWidth, componentHeight, meshes, componentType, componentName, componentKey ) ) {
                            if( ! _this.isValidComponentWithComponents( { side, position, meshes, componentType, componentName, componentKey } ) ) {
                                return _this.notification( meshes, _this.NOTICES.cannotPlaceVent );
                            } else {
                                return _this.notification( meshes, _this.NOTICES.itemNotFit );
                            }
                        }
                    }
                }

                if( true === isChangeComponent && ! _this.isValidComponentPosition( side, position, componentWidth, componentHeight, meshes, componentType, componentName, componentKey ) && ! isMoveOnFloor ) {
                    _this.addNotificationRequiredDoor();
                    
                    if( 'door' === componentType ) {
                        _this.handleDeleteComboElectricalComponentTieDoor( side );
                        _this.updateWindowsPosition();
                    }

                    if( ! isTackDoor ) {
                        _this.notification( meshes, _this.NOTICES.itemNotFit );
                    } else {
                        this.removeMeshes( meshes );
                    }

                    return resolve( false );
                }

                if( undefined === rotation ) {
                    const rotationSide = 'floor' === side && undefined !== side_floor_place_along_side ? side_floor_place_along_side : side;
                    rotation = _this.calculateComponentRotation( rotationSide, behavior );
                }

                if ( parentKey ) {
                    let parentMesh = _this._walls[side]["items"][parentKey]['meshes'][0];

                    if( 'shutters' === componentType ) {
                        const shuttersBoxHeight = this.getShuttersPositionY( _this._walls[side]["items"][parentKey]['meshes'], _this._walls[side]["items"][parentKey]['type'], _this._walls[side]["items"][parentKey]['name'] );
                        const shuttersPosition  = this.getShuttersPositionX( _this._walls[side]["items"][parentKey]['meshes'], _this._walls[side]["items"][parentKey]['name'] );
                        position                = { x: 0, y: - shuttersBoxHeight / 2, z: 0 };
                        for( let i = 0; i < meshes.length; i++ ) {
                            if( `${componentName}_L_obj` === meshes[i].name || `${componentName}_L_bb_obj` === meshes[i].name ) {
                                meshes[i].position.x = shuttersPosition;
                            } else if( `${componentName}_R_obj` === meshes[i].name || `${componentName}_R_bb_obj` === meshes[i].name ) {
                                meshes[i].position.x = - shuttersPosition;
                            }
                        }
                    } else if( 'flower_box' === componentType ) {
                        const parentComponent   = this.getComponentByKey( parentKey );
                        const parentHeight      = this.getComponentHeight( _this._walls[side]["items"][parentKey]['meshes'], 'windows');
                        if( parentComponent.position.y < parentHeight + componentHeight ) {
                            return _this.notification( meshes, _this.NOTICES.itemNotFit );
                        }
                        position = { x: 0, y: - parentHeight, z: 0 };
                    } else {
                        const deckObj = _this.getMeshByName( 'deck_obj' );

                        if( ( _this.isFrontPorch() && 'front' === side ) || ( _this.isSidePorch() && 'left' === side && 'structure_hiproof' !== _this._shedType ) || ( 'inner_left' === side || 'inner_front' === side ) || 'front_inner_porch' === side ) {
                            let z    = 'inner_front' === side ? constants.cornerDepth : 'front_inner_porch' === side ? constants.middleDepth : _this._porchWidth;
                            position = { x: 0, y: - 0.3, z: z };
    
                        } else if( _this._externalPorchLocation === side && deckObj ) {
                            let y = _this._porchWidth ? - 0.4 : 0;
                            position = { x: 0, y: y, z: _this._porchWidth };
    
                        } else {
                            position = { x: 0, y: 0, z: 0 };
                        }
    
                        if( this.isDeluxeCornerLeftPorch() ) {
                            if( 'front' === side ) {
                                position = { x: 0, y: 0, z: constants.deluxeDepth };
                            } else if( 'inner_front' === side ) {
                                position = { x: 0, y: 0, z: 3 * constants.deluxeDepth };
                            }
                        }

                        if( this.isSidePorchCabin() && 'left' === side ) {
                            position = { x: 0, y: 0, z: _this._porchWidth };
                        }
                    }
                    
                    rotation = { x: 0, y: 0, z: 0 };

                    firstMesh.setParent(parentMesh);
                }

                _this.placeComponent( meshes, position, rotation, componentType, side, componentName );
                _this.intersectComponent( meshes, side );
                if( _this.isBayWindow( componentName ) ) {
                    _this.handleBayWindow( meshes[0] );
                    _this.updateSidingMaterial();
                    _this.updateTextureFileRoof();
                }
                
                if( _this.isGableDormer( componentName ) ) {
                    _this.handleGableDomer( componentName, meshes[0], componentKey );
                    _this.gableDormerHandleRoofColor();
                }
                const position2D        = _this.getPosition2D( side, meshes, meshes[0].position, componentType, componentName );
                const sizeWidth         = _this.getComponentWidth( meshes, componentType, componentName );
                const face              = _this.getComponentFace( componentType );
                
                const colorComponent            = _this.getColorComponent( meshes, componentType );
                const colorComponentCountertops = _this.getColorComponent( meshes, 'countertops' );
                const colorComponentAppliance   = _this.getColorComponent( meshes, 'appliance' );

                const colors = [];
                if( colorComponent.color ) {
                    colors.push( {
                        type: componentType,
                        type_display: colorLabels[componentType] ? colorLabels[componentType] : '',
                        display: colorComponent.color,
                        color_price: colorComponent.colorPrice,
                        color_price_type: colorComponent.colorPriceType,
                        texture_type: colorComponent.textureType,
                        texture: colorComponent.texture,
                        description: colorComponent.description,
                        upcharge: _this.getColorUpcharge( colorComponent.color, componentName ),
                    } );
                }

                if( colorComponentCountertops.color ) {
                    colors.push( {
                        type: 'countertops',
                        type_display: colorLabels['countertops'] ? colorLabels['countertops'] : '',
                        display: colorComponentCountertops.color,
                        color_price: colorComponentCountertops.colorPrice,
                        color_price_type: colorComponentCountertops.colorPriceType,
                        texture_type: colorComponentCountertops.textureType,
                        texture: colorComponentCountertops.texture,
                        description: colorComponentCountertops.description,
                    } );
                }

                if( colorComponentAppliance.color ) {
                    colors.push( {
                        type: 'appliance',
                        type_display: colorLabels['appliance'] ? colorLabels['appliance'] : '',
                        display: colorComponentAppliance.color,
                        color_price: colorComponentAppliance.colorPrice,
                        color_price_type: colorComponentAppliance.colorPriceType,
                        texture_type: colorComponentAppliance.textureType,
                        texture: colorComponentAppliance.texture,
                        description: colorComponentAppliance.description,
                    } );
                }

                _this._allComponents[componentKey] = {
                    key: componentKey,
                    name: componentName,
                    parent: parentKey || null,
                    type: componentType,
                    componentSize,
                    rotation,
                    position,
                    meshes,
                    side,
                    face,
                    colors,
                    isBlock,
                    width: sizeWidth,
                    blockOptions,
                    vertical,
                    horizontal
                };

                let wallItem = {
                    id,
                    order,
                    type: componentType,
                    key: componentKey,
                    name: componentName,
                    meshes,
                    side,
                    position,
                    position2D,
                    rotation,
                    width: sizeWidth,
                    componentHeight,
                    componentWidth,
                    componentDepth,
                    vertical,
                    horizontal,
                    parent: parentKey || null,
                    face,
                    componentSize,
                    colors,
                    isTackDoor,
                    isUpdateComponent,
                    isIgnoreValidPosition: false,
                    isBlock,
                    blockOptions: blockOptions ? blockOptions : []
                }

                if( 'door' === componentType ) {
                    wallItem['defaultLock'] = lockData ? lockData['defaultLock'] : undefined;
                    wallItem['customLock']  = lockData ? lockData['customLock'] : undefined;
                    wallItem['enableHinge'] = lockData ? lockData['enableHinge'] : undefined;
                    wallItem['lockPrice']   = lockData ? lockData['lockPrice'] : 0;
                }

                _this._walls[side]["items"][componentKey] = wallItem;
                if( ! isTackDoor && 'door' === componentType ) {
                    _this._doorsOpeningTackRoom.push( wallItem );
                }

                _this.updateComponentsPosition2D( side, componentKey, position, { meshes, type: componentType, name: componentName } );
                if( 'door' === componentType && this.isWindowPositionFixedEnable() && this.isMainSide( side ) ) {
                    _this._doorFixedTarget.push( {side, componentHeight, key: componentKey} );
                }

                if( 'cupola' === componentType || 'dormer' === componentType ) {
                    _this.updateTextureFileRoof();
                }

                _this.handleBonesScreenHouse();
                _this.enableComponentMoveable();

                _this.disableShuttersInComponent( meshes );

                const isUpdateColor = _this.isUpdateColorForSpecificComponent( componentType );

                _this.updateColor( 'group-shutter-color' );
                _this.updateColor( 'group-trim-color' );
                _this.updateColor( 'group-door-color' );
                _this.updateColor( 'group-siding-color' );
                _this.updateColor( 'group-vent-color' );
                _this.updateColor( 'group-windows-color' );
                _this.updateColor( 'group-roof-color' );
                _this.updateColor( 'group-countertops-color' );
                _this.updateColor( 'group-appliance-color' );
                
                if( isUpdateColor && colorInfo && ! helpers.isEmptyObject( colorInfo ) ) {
                    const { textureType, textureImage, textureColor, name, price, priceType, description } = colorInfo;
                    if( _this.updateComponentColor( componentType, textureType, textureImage, textureColor, meshes ) ) {
                        const texture = 'color' === textureType ? textureColor : textureImage;
                        const colors = {
                            type: componentType,
                            type_display: colorLabels[componentType] ? colorLabels[componentType] : '',
                            display: name,
                            color_price: price,
                            color_price_type: priceType,
                            texture_type: textureType,
                            texture,
                            description,
                            upcharge: _this.getColorUpcharge( name, componentName ),
                        };

                        this._walls[side]['items'][componentKey]['colors'].push( colors );
                    }
                }

                if( 'dormer' === componentType ) {
                    if( ! this.isGableDormer( componentName ) ) {
                        _this.flatDormerHandleWall( firstMesh );
                        _this.updateSidingMaterial();
                    } else {
                        _this.updateHoleRoof( side );
                    }
                }

                if( 'door' === componentType ) {
                    this.moveBonseFramingGreenHousePolycarbon( side, sizeWidth );
                }

                if( isChangeComponent != 'default' ){
                    _this.updatePositionLayoutComponents( side );
                }

                if( -1 !== typesCutFrame.indexOf( componentType ) ) {
                    _this.handleFrameComponent( wallItem );
                }

                _this.removeNotificationRequiredDoor();
                if( 'door' === componentType ) {
                    _this.handleSliding();
                }

                if( isChangeComponent && isMoveOnFloor ) {
                    _this.repositionFloorComponents( wallItem );
                }

                let event = new CustomEvent('add-component', {detail: wallItem});
                window.dispatchEvent(event);
                resolve(wallItem);
            };

            _this.forceAssetLoading();
        });
    }

    /**
     * Get color for component
     * @param meshes 
     * @param type 
     * @returns 
     */
    getColorComponent = ( meshes, type ) => {
        type                = 'shutters' === type ? 'shutter' : type;
        const activeElement = jQuery( `.active[data-color-type="${type}"]` );
        const materials     = activeElement.data( 'material' );
        let color           = jQuery( `[name=ssb-option-${type}-color]` ).val() as string;
        let textureType     = activeElement.data( 'texture-type' );
        let textureImage    = activeElement.data( 'texture-image' );
        let textureColor    = activeElement.data( 'texture-color' );
        let colorPrice      = activeElement.data( 'price' );
        let colorPriceType  = activeElement.data( 'price-type' );
        let texture         = ( textureType && textureType  == 'image' ) ? textureImage : textureColor;
        let description     = '';
        const defaultResult = { color: '', colorPrice: 0, colorPriceType: '', textureType: '', texture: '', description: '' };
        if( '' === materials || undefined === materials ) {
            return defaultResult;
        }
        const listMaterials = materials.split( '|' );
        let isIncludeMat    = false;
        meshes.forEach( mesh => {
            let material = mesh.material as StandardMaterial;
            if( material ) {
                if( -1 !== listMaterials.indexOf( material.name ) ) {
                    isIncludeMat = true;
                    return;
                }
                if( undefined !== material['subMaterials'] ) {
                    let subMaterials = material['subMaterials'];
                    subMaterials.forEach( subMaterial => {
                        if( -1 !== listMaterials.indexOf( subMaterial.name ) ) {
                            isIncludeMat = true;
                            return;
                        }
                    } );
                }
            }
        } );

        return isIncludeMat ? { color, colorPrice, colorPriceType, textureType, texture, description } : defaultResult;
    }

    updateColorComponent = ( type ) => {
        const componentType  = 'shutter' === type ? 'shutters' : type;
        const _this          = this;
        const listComponents = {..._this._allComponents};

        for( let k in listComponents ) {
            const component = _this._allComponents[k];
            if( listComponents[k].type === componentType ) {
                const colorComponent = _this.getColorComponent( listComponents[k].meshes, listComponents[k].type );
                const { color, colorPrice, colorPriceType, textureType, texture, description } = colorComponent;

                if( _this._allComponents[k].colors && _this._allComponents[k].colors.length > 0 ) {
                    const colorIndex = _this._allComponents[k].colors.findIndex( color => color.type === componentType );
                    if( colorIndex > -1 ) {
                        _this._allComponents[k].colors[colorIndex].color_price = colorPrice;
                        _this._allComponents[k].colors[colorIndex].color_price_type = colorPriceType;
                        _this._allComponents[k].colors[colorIndex].display = color;
                        _this._allComponents[k].colors[colorIndex].texture_type = textureType;
                        _this._allComponents[k].colors[colorIndex].texture = texture;
                        _this._allComponents[k].colors[colorIndex].description = description;
                    }
                }
                if( _this._walls[component.side]['items'][k].colors && _this._walls[component.side]['items'][k].colors.length > 0 ) {
                    const colorIndex = _this._walls[component.side]['items'][k].colors.findIndex( color => color.type === componentType );

                    if( colorIndex > -1 ) {
                        _this._walls[component.side]['items'][k].colors[colorIndex].color_price = colorPrice;
                        _this._walls[component.side]['items'][k].colors[colorIndex].color_price_type = colorPriceType;
                        _this._walls[component.side]['items'][k].colors[colorIndex].display = color;
                        _this._walls[component.side]['items'][k].colors[colorIndex].texture_type = textureType;
                        _this._walls[component.side]['items'][k].colors[colorIndex].texture = texture;
                        _this._walls[component.side]['items'][k].colors[colorIndex].description = description;
                    }
                }
            }
            
            if( 'countertops' === componentType || 'appliance' === componentType ) {
                const colorComponent = _this.getColorComponent( listComponents[k].meshes, componentType );
                const { color, colorPrice, colorPriceType, textureType, texture, description } = colorComponent;

                if( _this._allComponents[k].colors && _this._allComponents[k].colors.length > 0 ) {
                    const allComponentColorIndex = _this._allComponents[k].colors.findIndex( color => color.type === componentType );
                    if( allComponentColorIndex > -1 ) {
                        _this._allComponents[k].colors[allComponentColorIndex].color_price = colorPrice;
                        _this._allComponents[k].colors[allComponentColorIndex].color_price_type = colorPriceType;
                        _this._allComponents[k].colors[allComponentColorIndex].display = color;
                        _this._allComponents[k].colors[allComponentColorIndex].texture_type = textureType;
                        _this._allComponents[k].colors[allComponentColorIndex].texture = texture;
                        _this._allComponents[k].colors[allComponentColorIndex].description = description;
                    }
                }
                if( _this._walls[component.side]['items'][k].colors && _this._walls[component.side]['items'][k].colors.length > 0 ) {
                    const wallComponentColorIndex = _this._walls[component.side]['items'][k].colors.findIndex( color => color.type === componentType );
                    if( wallComponentColorIndex > -1 ) {
                        _this._walls[component.side]['items'][k].colors[wallComponentColorIndex].color_price = colorPrice;
                        _this._walls[component.side]['items'][k].colors[wallComponentColorIndex].color_price_type = colorPriceType;
                        _this._walls[component.side]['items'][k].colors[wallComponentColorIndex].display = color;
                        _this._walls[component.side]['items'][k].colors[wallComponentColorIndex].texture_type = textureType;
                        _this._walls[component.side]['items'][k].colors[wallComponentColorIndex].texture = texture;
                        _this._walls[component.side]['items'][k].colors[wallComponentColorIndex].description = description;
                    }
                }
            }
        }

        if( 'shutters' === componentType && Object.keys( _this._shuttersInObject ).length > 0 ) {
            for( let shutter in _this._shuttersInObject ) {
                const colorComponent = _this.getColorComponent( _this._shuttersInObject[shutter].meshes, _this._shuttersInObject[shutter].type );
                const { color, colorPrice, colorPriceType, textureType, texture, description } = colorComponent;

                if( _this._shuttersInObject[shutter].colors && _this._shuttersInObject[shutter].colors.length > 0 ) {
                    const colorIndex = _this._shuttersInObject[shutter].colors.findIndex( color => color.type === componentType );
                    if( colorIndex > -1 ) {
                        _this._shuttersInObject[shutter].colors[colorIndex].color_price = colorPrice;
                        _this._shuttersInObject[shutter].colors[colorIndex].color_price_type = colorPriceType;
                        _this._shuttersInObject[shutter].colors[colorIndex].display = color;
                        _this._shuttersInObject[shutter].colors[colorIndex].texture_type = textureType;
                        _this._shuttersInObject[shutter].colors[colorIndex].texture = texture;
                        _this._shuttersInObject[shutter].colors[colorIndex].description = description;
                    }
                }
            }
        }
    }


    /**
     * update Specular Power , Diffiuse Color after load component
     */
    private updateMaterialComponent( ) {
        const _this = this;
        const materials = [
            { key: 'door_chrom_mat',                specularPower: 3,   diffuseColor: Color3.FromHexString("#696969") },
            { key: 'lock_mat',                      specularPower: 3,   diffuseColor: Color3.FromHexString("#000000") },
            { key: 'door_rollup_mat',               specularPower: 6,   diffuseColor: false },
            { key: 'ramp_alu_mat',                  specularPower: 10,  diffuseColor: false },
            { key: 'door_metal_trim_mat',           specularPower: 3,   diffuseColor: false },
            { key: 'door_metal_slab_mat',           specularPower: 3,   diffuseColor: false },
            { key: 'window_recessed_metal_mat',     specularPower: 3,   diffuseColor: false },
            { key: 'window_awning_mat',             specularPower: 30,  diffuseColor: false },
            { key: 'window_octagon_mat',            specularPower: 30,  diffuseColor: false },
            { key: 'cupola_roof_mat',               specularPower: 30,  diffuseColor: false },
            { key: 'transom_mat',                   specularPower: 30,  diffuseColor: false },
            { key: 'pine_leaf_mat',                 specularPower: 20,  diffuseColor: false },
            { key: 'pine_mat',                      specularPower: 10,  diffuseColor: false },
            { key: 'bell_mat',                      specularPower: 20,  diffuseColor: false },
            { key: 'ball_mat',                      specularPower: 15,  diffuseColor: false },
            { key: 'roof_corrugatedaluminum_mat',   specularPower: 30,  diffuseColor: false },
            { key: 'countertop_mat',                specularPower: 40,  diffuseColor: false },
            { key: 'bathroom_wall_mat',             specularPower: 3.5,   diffuseColor: false },
            { key: 'shower_hardware_mat',           specularPower: 30,  diffuseColor: false },
            { key: 'glass_mat',                     specularPower: 30,  diffuseColor: false },
            { key: 'mirror_mat',                    specularPower: 30,  diffuseColor: false },
            { key: 'washing_glass_mat',             specularPower: 30,  diffuseColor: false },
            { key: 'washing_inside_mat',            specularPower: 30,  diffuseColor: false },
            { key: 'sink_mat',                      specularPower: 30,  diffuseColor: false },
            { key: 'loft_finishing_mat',            specularPower: 8,  diffuseColor: false },
        ];

        for( let i = 0; i < materials.length; i++ ) {
            const mats = _this.getMaterialByName( materials[i].key );
            if( mats ) {
                mats.forEach( mat  => {
                    if( materials[i].diffuseColor !== false ) {
                        mat.diffuseColor = materials[i].diffuseColor;
                    }

                    if( 'pine_leaf_mat' === materials[i].key ) {
                        mat.transparencyMode = StandardMaterial.MATERIAL_ALPHATEST;
                    }

                    mat.specularPower = materials[i].specularPower;
                });
            }
        }
    }

    private updateMaterialModel() {
        const _this = this;

        const materials = [
            { key: 'horizontal_polycarbon_tex', specularPower: 30 },
            { key: 'ventstandard_mat',          specularPower: 6 },
            { key: 'vent_greenhouse_mat',       specularPower: 6 },
            { key: 'vent_gable_halfround_mat',  specularPower: 6 },
            { key: 'vent_tombstone_mat',        specularPower: 6 },
            { key: 'vent_square_mat',           specularPower: 6 },
            { key: 'vent_square_gamish_mat',    specularPower: 6 },
            { key: 'vent_square_trim_mat',      specularPower: 6 },
        ];

        for( let i = 0; i < materials.length; i++ ) {
            const mats = _this.getMaterialByName( materials[i].key );
            if( mats ) {
                mats.forEach( mat  => {
                    mat.specularPower = materials[i].specularPower;
                });
            }
        }
    }

    /**
     * Update json for list component
     */
    private restructureLayoutComponents(){
        let layoutComponents = this.getLayoutComponents();
        let argsComponents = [];

        for( const componentType in layoutComponents ) {
            if( layoutComponents.hasOwnProperty( componentType ) ) {
                const components = layoutComponents[componentType];
                components.forEach( ( component ) => {
                    if( component.component ) {
                        argsComponents.push( component );
                    }

                } )
            }
        }

        return argsComponents.sort((a,b) => (a.order > b.order) ? 1 : ((b.order > a.order) ? -1 : 0))
    }

    /**
     * Update object order of layout components
     */
    private updatePositionLayoutComponents( side ){
        let _this = this;
        let components = _this._walls[side]["items"];
        let argsComponents = [];
        if( ! components ) return;
        
        for( const key in components ) {
            argsComponents.push( components[key] );
        }

        if( side == 'left' || side == 'left_front' || side == 'left_back' || side == 'front_inner_porch' ) {
            argsComponents.sort((a, b) => (a.position.z < b.position.z) ? 1 : ((b.position.z < a.position.z) ? -1 : 0))
        } else if( side == 'right' ) {
            argsComponents.sort((a, b) => (a.position.z > b.position.z) ? 1 : ((b.position.z > a.position.z) ? -1 : 0))
        } else if( side == 'front' || side == 'right_inner_porch' ) {
            argsComponents.sort((a, b) => (a.position.x < b.position.x) ? 1 : ((b.position.x < a.position.x) ? -1 : 0))
        } else if( side == 'back' || side == 'left_inner_porch' ) {
            argsComponents.sort((a, b) => (a.position.x > b.position.x) ? 1 : ((b.position.x > a.position.x) ? -1 : 0))
        } else if( side == 'porch' ) {
            argsComponents.sort((a, b) => (a.position.x < b.position.x) ? 1 : ((b.position.x < a.position.x) ? -1 : 0))
        }
        _this._walls[side]["items"] = {};
        for( const component of argsComponents ) {
            _this._walls[side]["items"][component.key] = component;
        }
    }

    async loadAllDefaultComponents() {
        const _this                 = this;
        let promises                = [];
        const components            = _this.restructureLayoutComponents();
        const horizontalOffset      = this.getHorizontalOffset();
        const enableDistributed     = parseInt( this._canvas.getAttribute( 'data-horizontal-distributed' ) );
        _this._defaultLayoutLoaded  = false;
        const hasAddons             = [];
        if( components ) {
            _this.updateIncludeComponents();
            
            for( const component of components ) {
                if( ! _this.isValidComponentByLayoutCondition( component ) ) {
                    continue;
                }

                if( _this.isCarport() && ! _this.isFullEnclosedWall( component.side ) ) {
                    continue;
                }

                if( 'door' === component.type || 'windows' === component.type ) {
                    hasAddons.push( component );
                }
                
                promises.push( _this.addNewLayoutComponent( component, horizontalOffset, enableDistributed ) );
            };

            _this.addNotificationRequiredDoor();
        }

        return Promise.all( promises ).then( () => {
            _this.distributeComponentEqually();
            _this.updateWindowsPosition();
            _this.isValidAllComponents();
            _this.addNewAddOn( hasAddons );

            _this._defaultLayoutLoaded = true;

            _this._canvas.setAttribute( 'data-layout-loaded', 'true' );
        } );
    }

    /**
     * Adds new add-ons (components) based on the provided components array.
     * Handles different types of components such as ramps, windows, shutters, and flower boxes.
     * 
     * @param {Array} components - Array of component objects to be added, each containing details like side, type, id, and component name.
     */
    addNewAddOn = ( components ) => {
        const sidingType = jQuery( `.ssb-configurator-siding-type li.active` ).data( 'siding-type' );
        for( const component of components ) {
            const { side, type, id, component: componentName, isUpdateComponent } = component;

            if( 'door' === type ) {
                let rampName = component['ramp_name'];
                if( rampName ) {
                    if( ! this.isExistAddon( rampName, componentName, 'ramp' ) ) {
                        if( this.isRequiredAddon( componentName, 'required-ramp' ) ) {
                            rampName = this.getFirstAddOn( componentName, 'ramp' );
                        } else {
                            rampName = false;
                        }
                    }
                } else if( this.isRequiredAddon( componentName, 'required-ramp' ) ) {
                    rampName = this.getFirstAddOn( componentName, 'ramp' );
                }

                if( rampName && this.isAllowAddComponent( side, rampName ) ) {
                    const comInfo = this.getComponentInfoById( id, side );
                    if( comInfo ) {
                        const args = { side, componentType: 'ramp', componentName: rampName, parentKey: comInfo['key'], isChangeComponent: 'default' };
                        this.addNewComponent( args );
                    }
                }
            }

            if( 'windows' === type ) {
                let shuttersName    = false;
                let flowerBoxName   = false;

                if( isUpdateComponent ) {
                    if( this.isRequiredAddon( componentName, 'required-shutters' ) ) {
                        shuttersName = this.getFirstShutters( sidingType, componentName );
                    }

                    if( this.isRequiredAddon( componentName, 'required-flower_box' ) ) {
                        flowerBoxName = this.getFirstAddOn( componentName, 'flower_box' );
                    }
                } else {
                    shuttersName    = component['shutters_name'];
                    flowerBoxName   = component['flower_box_name'];

                    if( shuttersName && this.isExistAddon( shuttersName, componentName, 'shutters' ) ) {
                        if( ! this.isValidShutters( sidingType, shuttersName, componentName ) ) {
                            shuttersName = this.getFirstShutters( sidingType, componentName );
                            if( shuttersName ) {
                                this.replaceComponentNotice( component['shutters_name'], shuttersName, [ NOTICES.sidingMaterial ] );
                            } else {
                                this.removeComponentNotice( component['shutters_name'], [ NOTICES.sidingMaterial ] );
                            }
                        }
                    } else if( this.isRequiredAddon( componentName, 'required-shutters' ) ) {
                        shuttersName = this.getFirstShutters( sidingType, componentName );
                    }

                    if( flowerBoxName && this.isExistAddon( flowerBoxName, componentName, 'flower_box' ) ) {
                        if( ! this.isValidFlowerBox( flowerBoxName, componentName ) ) {
                            flowerBoxName = this.getFirstAddOn( componentName, 'flower_box' );
                        }
                    } else if( this.isRequiredAddon( componentName, 'required-flower_box' ) ) {
                        flowerBoxName = this.getFirstAddOn( componentName, 'flower_box' );
                    }
                }

                if( shuttersName ) {
                    const comInfo = this.getComponentInfoById( id, side );
                    if( comInfo ) {
                        this.addNewShutters( shuttersName, comInfo['meshes'], side, comInfo['key'] );
                    }
                }

                if( flowerBoxName && this.isAllowAddComponent( side, flowerBoxName ) ) {
                    const comInfo   = this.getComponentInfoById( id, side );
                    if( comInfo ) {
                        const args  = { side, componentType: 'flower_box', componentName: flowerBoxName, parentKey: comInfo['key'], isChangeComponent: 'default' };
                        this.addNewComponent( args );
                    }
                }
            }
        }
    }

    isValidAllComponents = () => {
        let _this       = this;
        let _isValidAll = true;
        for( const side in _this._walls ) {
            for( const key in _this._walls[side]["items"] ) {
                const component = _this._walls[side]["items"][key];
                if( undefined === component ) {
                    continue;
                }
                if( component.isTackDoor ) {
                    continue;
                }

                const type              = component.type;
                const isMoveHorizontal  = this.isMoveHorizontal( type );
                if( 'door' !== type && 'windows' !== type && 'vent' !== type && 'skylight_window' !== type && ! isMoveHorizontal ) {
                    continue;
                }

                const position          = component.position;
                const meshes            = component.meshes;
                const name              = component.name;
                const componentWidth    = _this.getComponentWidth( meshes, type, name );
                const componentHeight   = _this.getComponentHeight( meshes, type );
                if( ! _this.isValidComponentPosition( side, position, componentWidth, componentHeight, meshes, type, name, key ) ) {
                    _this.deleteComponentV2( side, key, type );
                    _isValidAll = false;
                }
            }
        }

        if( ! _isValidAll ) {
            _this.addNotificationRequiredDoor();
            _this._isUpdateLayout = true;
            _this.createAlert( this.NOTICES.itemNotFit );
            window.dispatchEvent( new CustomEvent( 'update-shed-info' ) );
        }
    }

    /**
     * Get width over
     * 
     * @param componentName component name
     * @returns 
     */
    getWidthOver = ( componentName ) => {
        return jQuery(`[data-upgrade-component-name='${componentName}']`).data('limit-width-over');
    }

    /**
     * Get width under
     * 
     * @param componentName component name
     * @returns 
     */
    getWidthUnder = ( componentName ) => {
        return jQuery(`[data-upgrade-component-name='${componentName}']`).data('limit-width-under');
    }

    /**
     * Get height over
     * 
     * @param componentName component name
     * @returns 
     */
    getHeightOver = ( componentName ) => {
        return jQuery(`[data-upgrade-component-name='${componentName}']`).data('limit-height-over');
    }

    /**
     * Get height under
     * 
     * @param componentName component name
     * @returns 
     */
    getHeightUnder = ( componentName ) => {
        return jQuery(`[data-upgrade-component-name='${componentName}']`).data('limit-height-under');
    }

    /**
     * Retrieves the roof pitch restriction data for a given component.
     * Fetches the encoded roof pitch data from the DOM element with the specified component name,
     * decodes and parses it as JSON, and returns the result.
     * 
     * @param {string} componentName - The name of the component to get the roof pitch restriction data for.
     * 
     * @returns {Array} - The decoded and parsed roof pitch restriction data, or an empty array if no data is found.
     */
    getRestrictionRoofPitch = ( componentName ) => {
        const roofPitchData = jQuery( `[data-upgrade-component-name='${componentName}']` ).data( 'roof-pitch' );

        return roofPitchData ? JSON.parse( atob( roofPitchData ) ) : [];
    }

    /**
     * Check is valid current component and replace if not is valid
     * 
     * @param sidingType    siding type (lp_smart|vinyl|metal|)
     * @param type          component type (door|window)
     * @param componentName the component to check and replace
     * @param side          front|back|left|right
     * @param widthOver     width over
     * @param widthUnder    width under
     * @param heightOver    height over
     * @param heightUnder   height under
     * @returns 
     */
     checkAndReplaceComponent = ( sidingType, type, componentName, side ) => {
        const widthOver     = this.getWidthOver( componentName );
        const widthUnder    = this.getWidthUnder( componentName );
        const heightOver    = this.getHeightOver( componentName );
        const heightUnder   = this.getHeightUnder( componentName );
        const roofPitchData = this.getRestrictionRoofPitch( componentName );
        const wallHeight    = this.getWallHeightBySide( side );
        let wallWidth       = 'front' === side || 'back' === side ? this.getWidth() : this.getDepth();
        let reason          = [];

        if( this.isExcludePorchMeasurement() && this.isSidePorch() && ( 'front' === side || 'back' === side ) ) {
            wallWidth -= this._porchWidth;
        }

        if( ! this.isValidComponentByDimensions( widthOver, widthUnder, heightOver, heightUnder, wallWidth, wallHeight ) ) {
            reason.push( NOTICES.size );
        }

        if( ! this.isValidComponentBySidingType( sidingType, type, componentName ) ) {
            reason.push( NOTICES.sidingMaterial );
        }

        if( ! this.isValidComponentByRoofPitch( type, roofPitchData ) ) {
            reason.push( NOTICES.roofPitch );
        }

        if( ! this.isAllowAddComponent( side, componentName ) ) {
            reason.push( NOTICES.sideSize );
        }
        
        if( reason.length ) {
            const fitComponent = this.getFitComponentsByTypeAndSide( type, sidingType, side );
            if( fitComponent.length > 0 ) {
                this.replaceComponentNotice( componentName, fitComponent[0], reason );

                return fitComponent[0];
            }
            
            this.removeComponentNotice( componentName, reason );

            return false;
        }

        return componentName;
    }

    /**
     * Get component fit with dimensions
     * 
     * @param componentType component type (door|window)
     * @param sidingType    siding type (lp_smart|vinyl|metal|)
     * @param side          front|back|left|right
     * @returns array
     */
    getFitComponentsByTypeAndSide = ( componentType, sidingType, side ) => {
        let results         = [];
        const _this         = this;
        const wallHeight    = this.getWallHeightBySide( side );
        // if( 'vent' === componentType && helpers.isOhiOslution() && ( 'metal' === sidingType || 'vertical_metal' === sidingType ) ) {
        //     return [];
        // }
        let wallWidth       = 'front' === side || 'back' === side ? this.getWidth() : this.getDepth();

        if( this.isExcludePorchMeasurement() && this.isSidePorch() && ( 'front' === side || 'back' === side ) ) { 
            wallWidth -= this._porchWidth;
        }

        jQuery( `[data-upgrade-component-type='${componentType}']` ).each( ( i, v ) => {
            const widthOver     = jQuery( v ).data( 'limit-width-over' );
            const widthUnder    = jQuery( v ).data( 'limit-width-under' );
            const heightOver    = jQuery( v ).data( 'limit-height-over' );
            const heightUnder   = jQuery( v ).data( 'limit-height-under' );
            let roofPitchData   = jQuery( v ).data( 'roof-pitch' );
            roofPitchData       = roofPitchData ? JSON.parse( atob( roofPitchData ) ) : [];

            if( 'door' === componentType || 'windows' === componentType || 'vent' === componentType ) {
                if( jQuery( v ).hasClass( sidingType ) && _this.isValidComponentByDimensions( widthOver, widthUnder, heightOver, heightUnder, wallWidth, wallHeight ) ) {
                    results.push( jQuery( v ).data( 'upgrade-component-name' ) );
                }
            } else {
                if( _this.isValidComponentByRoofPitch( componentType, roofPitchData ) && _this.isValidComponentByDimensions( widthOver, widthUnder, heightOver, heightUnder, wallWidth, wallHeight ) ) {
                    results.push( jQuery( v ).data( 'upgrade-component-name' ) );
                }
            }
        });

        return results.filter( componentName => this.isAllowAddComponent( side, componentName ) );
    }

    /**
     * Validates if a component is valid based on the roof pitch data.
     * If there is no roof pitch data or the component is not a dormer, it returns true.
     * Otherwise, it checks if the current roof pitch is included in the roof pitch data.
     * 
     * @param {string} componentType - The type of the component (e.g., 'dormer').
     * @param {Array} roofPitchData - The roof pitch restriction data for the component.
     * 
     * @returns {boolean} - True if the component is valid based on the roof pitch data, false otherwise.
     */
    isValidComponentByRoofPitch = ( componentType, roofPitchData ) => {
        const roofPitch = jQuery( '.group-upgrade-roof_pitch input[name="ssb-configurator-upgrade-roof_pitch"]:checked' ).val();
        if( 0 === roofPitchData.length || 'dormer' !== componentType || undefined === roofPitch ) {
            return true;
        }

        return helpers.hasKeyInArray( roofPitchData, roofPitch );
    }

    /**
     * Check is valid component by dimensions
     * 
     * @param widthOver     width over
     * @param widthUnder    width under
     * @param heightOver    height over
     * @param heightUnder   height under
     * @param wallWidth     wall width
     * @param wallHeight    wall height
     * @returns boolean
     */
    isValidComponentByDimensions = ( widthOver, widthUnder, heightOver, heightUnder, wallWidth, wallHeight ) => {
        if( '' === widthOver && '' === widthUnder && '' === heightOver && '' === heightUnder ) {
            return true;
        }

        wallHeight = helpers.isTexasCustomShed() && 'barn_without_fe_overhangs' === this._shedType ? this.getWallHeight() : wallHeight;
        let isValid = this.isValidOneDimensions( widthOver, wallWidth, true );
        if( isValid ) {
            isValid = this.isValidOneDimensions( widthUnder, wallWidth, false );
            if( isValid ) {
                isValid = this.isValidOneDimensions( heightOver, wallHeight, true );
                if( isValid ) {
                    isValid = this.isValidOneDimensions( heightUnder, wallHeight, false );
                }
            }
        }

        return isValid;
    }

    /**
     * Check is valid component by siding type
     * 
     * @param sidingType    siding type (siding material) 
     * @param componentName component name
     * @returns {boolean}
     */
    isValidComponentBySidingType = ( sidingType, componentType, componentName ) => {
        // if( '' !== componentType && '' !== sidingType && 'vent' === componentType && helpers.isOhiOslution() && ( 'metal' === sidingType || 'vertical_metal' === sidingType ) ) {
        //     return false;
        // }
        if( 'door' !== componentType && 'windows' !== componentType && 'vent' !== componentType ) {
            return true;
        }

        return 0 !== jQuery(`.${sidingType}[data-upgrade-component-type="${componentType}"][data-upgrade-component-name="${componentName}"]`).length;
    }

    /**
     * Check limit component with one dimensions
     * 
     * @param dimensions    limit dimensions
     * @param compare       width|length|height
     * @param isOver        over or under
     * @returns boolean
     */
    isValidOneDimensions = ( dimensions, compare, isOver ) => {
        if( '' === dimensions ) {
            return true;
        }
        
        const tmpDimensions = parseFloat( dimensions );
        if( isNaN( tmpDimensions ) ) {
            return true;
        }

        if( isOver ) {
            if( compare >= tmpDimensions ) {
                return true;
            }
        } else {
            if( compare < tmpDimensions) {
                return true;
            }
        }

        return false;
    }

    placeAllComponents(oldSize, newSize, isChangeLayout) {
        const _this         = this;
        const sizeChange    = {
            width: newSize.width / oldSize.width,
            depth: newSize.depth / oldSize.depth,
            height: newSize.height / oldSize.height,
            buildingHeight: newSize.buildingHeight / oldSize.buildingHeight
        }
        
        for (const wall in _this._walls) {
            if( _this.ignoreFloor( wall ) ) {
                continue;
            }

            for (const componentKey in _this._walls[wall]["items"]) {
                let component =  _this._walls[wall]["items"][componentKey];
                let { parent: parentKey, position: currentPosition, meshes, horizontal, vertical, type, name, side } = component;

                if( this.ignoreHandleTackDoor( side ) || component.isTackDoor ) {
                    continue;
                }

                if( 'vent' === type ) {
                    vertical    = { distance: 0, measured_from: 'wall' };
                    horizontal  = { distance: 0, measured_from: 'left', roof_top_aligned: 'manual' };
                }
                
                let position = _this.calculateLayoutPosition( meshes, side, vertical, horizontal, type, name );
                if ( vertical && vertical.measured_from ) {
                    if( true !== isChangeLayout ) {
                        let newWallHeight = _this.getWallHeightBySide( side );
                        if( 'lean_to' === _this._shedType || 'structure_monoslope_smithbilt' === _this._shedType || 'structure_classic_aframe_dormer' === _this._shedType || 'structure_saltbox_mwshed' === _this._shedType || 'structure_country_cottage' === _this._shedType || 'structure_lean_to_with_front_porch' === _this._shedType ) {
                            newWallHeight = _this.getWallHeight();
                        }
                        const roofHeight    = newWallHeight - parseFloat( newSize.height );
                        let oldWallHeight   = parseFloat( oldSize.height );
                        if( ( 'front' === side || 'back' === side ) && _this.isSpecialShed() ) {
                            oldWallHeight   = parseFloat( oldSize.height ) + roofHeight;
                        }

                        const scale = newWallHeight / oldWallHeight;
                        position.y  = parseFloat( currentPosition.y ) * scale;
                        if( ( 'cupola' === type || 'light' === type ) && _this._bones['b_b_t'] ) {
                            position.y = _this._bones['b_b_t'].position.y;

                            if( 'light' === type ){
                                position.y = _this.getPositionYForLight();
                            }

                            if( 'flat' === this.getRoofShape() ){
                                const positionY = this.getWallHeight() + this.getWidth() / 2 * helpers.getTanFromDegrees( this.getRoofDegree() ) + 0.05;
                                position.y = positionY;
                            }
                        }

                        if( 'dormer' === side && _this._bones['b_b_t2'] ) {
                            position.y = _this._bones['b_b_t2'].position.y;
                        }

                        if( 'front' === side || 'back' === side || 'inner_front' === side || 'porch' === side ) {
                            position.x = parseFloat( currentPosition.x ) * sizeChange.width;
                        } else {
                            position.z = parseFloat( currentPosition.z ) * sizeChange.depth;
                        }
                    }
                }

                if( this.canMoveComponentToRoof() ) {
                    if( ( 'front' === side || 'back' === side ) ) {
                        if( ! ( 'front' === side && this.isFrontPorch() ) ) {
                            position.y = parseFloat( currentPosition.y ) * sizeChange.buildingHeight;
                        } else {
                            position.y = parseFloat( currentPosition.y ) * sizeChange.height;
                        }
                    }

                    if( parseFloat( currentPosition.y ) > oldSize.height ) {
                        if( ( 'structure_country_cottage' === _this._shedType || 'structure_classic_aframe_dormer' === _this._shedType ) && 'left' === side ) {
                            position.y = parseFloat( currentPosition.y ) * sizeChange.buildingHeight;
                            position.z = parseFloat( currentPosition.z ) * sizeChange.width;
                        } else {
                            if( 'light' !== type ){
                                position.y = this.getPorchPoitionYResize( oldSize, newSize, currentPosition );;
                            }
                        }
                    }
                }

                if ( this.isPorchSide( side ) ) {
                    if ( this._bones['b_p_t'] ) {
                        position.z = this._bones['b_p_t'].position.z;
                    }
                    position.y = this.getPorchPoitionYResize( oldSize, newSize, currentPosition );
                    position.x = parseFloat( currentPosition.x ) * sizeChange.width;
                }
                
                if( 'roof' === side ) {
                    if( this.isMoveHorizontal( type ) ) {
                        position.x = parseFloat( currentPosition.x ) * sizeChange.width;
                        position.z = 0;
                    } else {
                        position.z = parseFloat( currentPosition.z ) * sizeChange.depth;
                    }
                    
                    if( 'cupola' === type && 'hip' === _this.getRoofShape() ) {
                        position.y = _this._bones['r_f_t'].position.y;
                        if( 0 === _this._bones['r_f_t'].position.z ) {
                            position.z = 0;
                        }
                    }
                }

                if( this.isMiddlePorch() ) {
                    if( 'front_inner_porch' === side ) {
                        position.z = parseFloat( currentPosition.z );
                    }
                    if( 'left_inner_porch' === side || 'right_inner_porch' === side ) {
                        position.z  = parseFloat( currentPosition.z );
                        
                        let oldSpaceToSide  = oldSize.width / 2 - Math.abs( currentPosition.x );
                        let newX            = newSize.width / 2 - oldSpaceToSide * ( constants.middleDepth / constants.middleDepth );
                        position.x          = currentPosition.x < 0 ? - newX : newX;
                    }
                    if( 'left_front' === side || 'left_back' === side ) {
                        let scale   = ( newSize.depth - constants.middleWidth ) / ( oldSize.depth - constants.middleWidth );
                        let newZ    = ( Math.abs( parseFloat( currentPosition.z ) ) - constants.middleWidth / 2 ) * scale + constants.middleWidth / 2;
                        position.z  = currentPosition.z < 0 ? - newZ : newZ;
                    }
                }

                if( this.isDoubleGable() ) {
                    if( 'left_front' === side || 'left_back' === side ) {
                        const distance = helpers.toFixed3( ( newSize.depth - oldSize.depth ) / 2 );
                        position.z = currentPosition.z < 0 ? currentPosition.z - distance : currentPosition.z + distance;
                    } else if( 'left_inner_porch' === side ) {
                        position.z = this._bones['b_p_l1'].position.z;
                        position.x = currentPosition.x;
                    } else if( 'front_inner_porch' === side ) {
                        const oldPorchWidth = helpers.toFixed3( oldSize.depth - 4 * this.getHalfGableWidth() );
                        const scale = this.doubleGableGetPorchWidth() / oldPorchWidth;
                        position.z = scale * currentPosition.z;
                    } else if( 'right_inner_porch' === side ) {
                        position.z = this._bones['b_p_l3'].position.z;
                        position.x = currentPosition.x;
                    } else if( 'roof' === side ) {
                        position.x = currentPosition.x;
                    }
                }

                if( 'structure_ranch_cabin' === this._shedType || this.isAframeSingleGable() ) {
                    if( 'inner_back' === side ) {
                        position.x  = currentPosition.x;
                        position.z  = this._bones['b_p_l1'].position.z;

                        if( this.isInteriorComponent( type ) && 'light' !== type ) {
                            position = _this.updateInteriorPosition( position, side );
                        }
                    }
                }

                if( this.applyFixedHeightWindows( side, type, name ) && this._doorFixedTarget.length !== 0 ) {
                    const componentHeight   = this.getComponentHeight( meshes, type );
                    position.y              = this.getWindowsFixedPositionY( type, componentHeight, side );
                }

                if( 'structure_rancher' === this._shedType && ( 'top_front' === side || 'top_back' === side ) ) {
                    if( this._isShareLink ) {
                        if( 'door' === type ) {
                            position.y              = this._bones['b_b_l3'].position.y;
                        } else {
                            const componentHeight   = this.getComponentHeight( meshes, type );
                            position.y              = this._bones['b_b_l3'].position.y + componentHeight;
                        }
                    } else {
                        if( vertical ) {
                            if( 'building' === vertical.measured_from || 'wall' === vertical.measured_from ) {
                                position.y = _this._bones['b_f_t'].position.y - vertical.distance;
                            } else if( 'ground' === vertical.measured_from ) {
                                position.y = _this._bones['b_f_l3'].position.y + vertical.distance;
                            }
                        }
                    }
                }

                if( this.is2StoryLeanTo() ) {
                    switch( side ) {
                        case 'top_front':
                        case 'top_back':
                            position.y = helpers.toFixed3( Number( currentPosition.y ) * sizeChange.buildingHeight );
                            position.x = helpers.toFixed3( Number( currentPosition.x ) * sizeChange.width );
                            break;
                        case 'front':
                        case 'back':
                            position.y = helpers.toFixed3( Number( currentPosition.y ) * sizeChange.height );
                            break;
                        case 'top_right':
                        case 'top_left':
                            position.y = Number( currentPosition.y );
                            position.z = helpers.toFixed3( Number( currentPosition.z ) * sizeChange.depth );
                            break;
                    }
                }

                _this._walls[side]["items"][componentKey]["position"] = position;
                
                let rotation = _this.calculateComponentRotation( side, type );

                const componentWidth = _this.getComponentWidth( meshes, type, component.name );

                if( this.isSidePorch() ) {
                    switch( side ) {
                        case 'front':
                            const newFrontWidth         = Vector3.Distance( this._bones['b_f_r1'].position, this._bones['b_f_l1'].position );
                            const frontArgs             = { oldAnchor: oldSize.anchorFront, newAnchor: _this._bones['b_f_r1'].position.x, oldWidth: oldSize.frontWidth, newWidth: newFrontWidth, currentPosition: currentPosition.x, componentWidth, startPoint: _this._bones['b_f_r1'].position.x, endPoint: _this._bones['b_f_l1'].position.x, operator: '-' };
                            position.x                  = _this.getNewComponentPosition( frontArgs );
                            break;
                        case 'back':
                            const newBackWidth         = Vector3.Distance( this._bones['b_b_r1'].position, this._bones['b_b_l1'].position );
                            const backArgs             = { oldAnchor: oldSize.anchorBack, newAnchor: _this._bones['b_b_r1'].position.x, oldWidth: oldSize.backWidth, newWidth: newBackWidth, currentPosition: currentPosition.x, componentWidth, startPoint: _this._bones['b_b_r1'].position.x, endPoint: _this._bones['b_b_l1'].position.x, operator: '-' };
                            position.x                  = _this.getNewComponentPosition( backArgs );
                            break;
                    }
                } else if( _this.isCornerPorchLeft() ) {
                    switch( side ) {
                        case 'inner_front':
                            const newInnerFrontWidth    = Vector3.Distance( this._bones['b_b_m4'].position, this._bones['b_p_l1'].position );
                            const innerFrontWidthArgs   = { oldAnchor: oldSize.anchorInnerFront, newAnchor: _this._bones['b_b_m4'].position.x, oldWidth: oldSize.innerFrontWidth, newWidth: newInnerFrontWidth, currentPosition: currentPosition.x, componentWidth, startPoint: _this._bones['b_b_m4'].position.x, endPoint: _this._bones['b_p_l1'].position.x, operator: '-' };
                            position.x                  = _this.getNewComponentPosition( innerFrontWidthArgs );
                            break;
                        case 'inner_left':
                            const newInnrLeftWidth      = Vector3.Distance( this._bones['b_f_l1'].position, this._bones['b_b_m4'].position );
                            const innerLeftWidthArgs    = { oldAnchor: oldSize.anchorInnerLeft, newAnchor: _this._bones['b_f_l1'].position.z, oldWidth: oldSize.innerLeftWidth, newWidth: newInnrLeftWidth, currentPosition: currentPosition.z, componentWidth, startPoint: _this._bones['b_f_l1'].position.z, endPoint: _this._bones['b_b_m4'].position.z, operator: '-' };
                            position.z                  = _this.getNewComponentPosition( innerLeftWidthArgs );
                            break;
                        case 'front':
                            const newFrontWidth         = Vector3.Distance( this._bones['b_f_r1'].position, this._bones['b_f_l1'].position );
                            const frontWidthArgs        = { oldAnchor: oldSize.anchorFront, newAnchor: _this._bones['b_f_r1'].position.x, oldWidth: oldSize.frontWidth, newWidth: newFrontWidth, currentPosition: currentPosition.x, componentWidth, startPoint: _this._bones['b_f_r1'].position.x, endPoint: _this._bones['b_f_l1'].position.x, operator: '-' };
                            position.x                  = _this.getNewComponentPosition( frontWidthArgs );
                            break;
                        case 'left':
                            const newLeftWidth          = Vector3.Distance( this._bones['b_p_l1'].position, this._bones['b_b_l1'].position );
                            const leftWidthArgs         = { oldAnchor: oldSize.anchorLeft, newAnchor: _this._bones['b_p_l1'].position.z, oldWidth: oldSize.leftWidth, newWidth: newLeftWidth, currentPosition: currentPosition.z, componentWidth, startPoint: _this._bones['b_p_l1'].position.z, endPoint: _this._bones['b_b_l1'].position.z, operator: '-' };
                            position.z                  = _this.getNewComponentPosition( leftWidthArgs );
                            break;
                    }
                } else if( _this.isDeluxeCornerLeftPorch() ) {
                    switch( side ) {
                        case 'front':
                            const frontWidthArgs        = { oldAnchor: oldSize.anchorFront, newAnchor: _this._bones['b_f_l4'].position.x, oldWidth: oldSize.frontWidth, newWidth: _this.deluxePorchGetFrontWidth(), currentPosition: currentPosition.x, componentWidth, startPoint: _this._bones['b_f_l4'].position.x, endPoint: _this._bones['b_f_l1'].position.x, operator: '-' };
                            position.x                  = _this.getNewComponentPosition( frontWidthArgs );
                            break;
                        case 'inner_front':
                            const innerFrontWidthArgs   = { oldAnchor: oldSize.anchorInnerFront, newAnchor: _this._bones['b_f_r1'].position.x, oldWidth: oldSize.innerFrontWidth, newWidth: _this.deluxePorchGetPorchWidth(), currentPosition: currentPosition.x, componentWidth, startPoint: _this._bones['b_f_r1'].position.x, endPoint: _this._bones['b_f_r4'].position.x, operator: '-' };
                            position.x                  = _this.getNewComponentPosition( innerFrontWidthArgs );
                            break;
                        case 'inner_right':
                            const distance              = Math.abs( oldSize.anchorInnerRight - currentPosition.z );
                            position.z                  = _this._bones['b_f_r4'].position.z + distance;
                            break;
                        case 'left':
                            const newLeftWidth          = Vector3.Distance( this._bones['b_f_l1'].position, this._bones['b_b_l1'].position );
                            const leftWidthArgs         = { oldAnchor: oldSize.anchorLeft, newAnchor: _this._bones['b_f_l1'].position.z, oldWidth: oldSize.leftWidth, newWidth: newLeftWidth, currentPosition: currentPosition.z, componentWidth, startPoint: _this._bones['b_f_l1'].position.z, endPoint: _this._bones['b_b_l1'].position.z, operator: '-' };
                            position.z                  = _this.getNewComponentPosition( leftWidthArgs );
                            break;
                        case 'right':
                            const newRightWidth         = Vector3.Distance( this._bones['b_b_r1'].position, this._bones['b_f_r1'].position );
                            const rightWidthArgs        = { oldAnchor: oldSize.anchorRight, newAnchor: _this._bones['b_b_r1'].position.z, oldWidth: oldSize.rightWidth, newWidth: newRightWidth, currentPosition: currentPosition.z, componentWidth, startPoint: _this._bones['b_f_r1'].position.z, endPoint: _this._bones['b_b_r1'].position.z, operator: '+' };
                            position.z                  = _this.getNewComponentPosition( rightWidthArgs );
                            break;
                        case 'diagonal_right':
                            position.x                  = ( _this._bones['b_f_r6'].position.x + _this._bones['b_f_l4'].position.x ) / 2;
                            position.z                  = ( _this._bones['b_f_r6'].position.z + _this._bones['b_f_l4'].position.z ) / 2;

                            if( this.isInteriorComponent( type ) && 'light' !== type ) {
                                position = _this.updateInteriorPosition( position, side );
                            }

                            break;
                    }
                } else if( _this.isAframeSingleGable() ) {
                    switch( side ) {
                        case 'left':
                            const newLeftWidth = Vector3.Distance( this._bones['b_f_l1'].position, this._bones['b_p_l1'].position );
                            const leftWidthArgs = { oldAnchor: oldSize.anchorLeft, newAnchor: _this._bones['b_f_l1'].position.z, oldWidth: oldSize.leftWidth, newWidth: newLeftWidth, currentPosition: currentPosition.z, componentWidth, startPoint: _this._bones['b_f_l1'].position.z, endPoint: _this._bones['b_p_l1'].position.z, operator: '-' };
                            position.z = _this.getNewComponentPosition( leftWidthArgs );
                        break;

                        case 'inner_left':
                            const newInnrLeftWidth = Vector3.Distance( this._bones['b_p_l1'].position, this._bones['b_b_l1'].position );
                            const innerLeftWidthArgs = { oldAnchor: oldSize.anchorInnerLeft, newAnchor: _this._bones['b_p_l1'].position.z, oldWidth: oldSize.innerLeftWidth, newWidth: newInnrLeftWidth, currentPosition: currentPosition.z, componentWidth, startPoint: _this._bones['b_p_l1'].position.z, endPoint: _this._bones['b_b_l1'].position.z, operator: '-' };
                            position.z = _this.getNewComponentPosition( innerLeftWidthArgs );
                        break;
                    }
                }

                if( 'structure_saltbox_mwshed' === _this._shedType ) {
                    if( currentPosition.x >= 0 ) {
                        position.x = currentPosition.x;
                    } else {
                        position.x = currentPosition.x * ( ( newSize.width - _this._bones['b_b_r4'].position.x ) / ( oldSize.width - _this._bones['b_b_r4'].position.x ) )
                    }

                }else if( 'structure_quaker_wannabuysheds' === _this._shedType && 'vent' === type && ( 'front' === side || 'back' === side ) ){
                    const targetRight   = new Vector3( position.x + componentWidth / 2, position.y, position.z );
                    const nearestRight  = _this.nearestPointToLine( targetRight, _this._bones['b_b_t'].position, _this._bones['b_b_r2'].position );
        
                    const targetLeft    = new Vector3( position.x - componentWidth / 2, position.y, position.z );
                    const nearesLeft    = _this.nearestPointToLine( targetLeft, _this._bones['b_b_l3'].position, _this._bones['b_b_t'].position );

                    const offset    = 0.3;
                    const leftY     = nearesLeft.y - offset;
                    const rightY    = nearestRight.y - offset;
                    

                    if( rightY < position.y && leftY < position.y ){
                        position.y = Math.min( rightY, leftY );

                    }else if( rightY < position. y){
                        position.y = rightY;

                    }else if( leftY < position.y ){
                        position.y = leftY;
                    }

                    if( _this._bones['b_b_l1'].position.x > targetLeft.x ){
                        position.x = currentPosition.x;
                    }
                }
                
                if( 'door' === type ) {
                    position.y = _this.getDoorFixedHeight( side );
                }

                if( this.isPorchSide( side ) && this.isVerticalDistribute() ) {
                    const componentHeight   = this.getComponentHeight( meshes, type );
                    position.y              = this.calculateMiddlePosition( componentHeight );
                }

                if( this.isHandleOffsetFloor( type ) ) {
                    position.y = this.getPositionYForInterior( type, name );
                }

                if( 'dormer' === type && _this.isGableDormer( name ) ) {
                    const dormerFrame   = `${name}_frame_obj`;
                    const dormerHeight  = this.getGableDormerDistance( dormerFrame, meshes, 'Dormer_B_F_T', 'Dormer_B_F_R1', 'y' );
                    position.x          = 0;
                    position.y          = _this.getBuildingHeight() - dormerHeight;
                }

                if( 'skylight_window' === type ) {
                    position.y = _this.getPositionYForSkylightWindow();
                }

                if (parentKey == null || parentKey == undefined) {
                    _this.placeComponent( meshes, position, rotation, type, side, name );
                    _this.intersectComponent( meshes, side );
                    _this.enableComponentMoveable();
                    _this.updateComponentsPosition2D( side, componentKey, position, component );
                }
            }
        }
    }

    getPorchPoitionYResize = ( oldSize, newSize, currentPosition ) => {
        let oldPorch        = oldSize.buildingHeight - oldSize.height;
        let newPorch        = newSize.buildingHeight - newSize.height;
        let scale           = newPorch / oldPorch;
        let positionInPorch = currentPosition.y - oldSize.height;

        return positionInPorch * scale + newSize.height;
    }

    updateWindowsPosition = () => {
        let _isValid = true;
        if( this._doorFixedTarget.length ) {
            for ( const wall in this._walls ) {
                for ( const componentKey in this._walls[wall]["items"] ) {
                    let component   =  this._walls[wall]["items"][componentKey];
                    let { parent: parentKey, position, meshes, type, side, name } = component;

                    if( 'windows' !== type ) {
                        continue;
                    }

                    if( this.applyFixedHeightWindows( side, type, name ) ) {
                        const componentWidth    = this.getComponentWidth( meshes, type, name );
                        const componentHeight   = this.getComponentHeight( meshes, type );
                        position.y              = this.getWindowsFixedPositionY( type, componentHeight, side );
                        this._walls[side]["items"][componentKey]["position"] = position;
                        let rotation = this.calculateComponentRotation( side, type );
                        if ( parentKey == null || parentKey == undefined ) {
                            this.placeComponent( meshes, position, rotation, type, side, name );
                            this.updateComponentsPosition2D( side, componentKey, position, component );
                            
                            if( ! this.isValidComponentPosition( side, position, componentWidth, componentHeight, meshes, type, name, componentKey ) ) {
                                this.deleteComponentV2( side, componentKey, type );
                                _isValid = false;
                            }
                        }
                    }
                }
            }
        }

        if( ! _isValid ) {
            this._isUpdateLayout = true;
            this.createAlert( this.NOTICES.itemNotFit );
            window.dispatchEvent( new CustomEvent( 'update-shed-info' ) );
        }
    }
    
    componentIsOutOfShed( position, componentWidth, componentHeight, side, type, name ) {
        const isMoveHorizontal = this.isMoveHorizontal( type );
        if( 'windows' !== type && 'door' !== type && 'vent' !== type && ! isMoveHorizontal ) {
           return true;
        }
        
        const allowRoofStyles = [ 'open_gable', 'box_gable', 'gambrel', 'dutch_style', 'saltbox_customize' ];
        if( -1 === allowRoofStyles.indexOf( this.getRoofShape() ) ) {
            return true;
        }

        const height            = this.getWallHeight();
        const width             = this.getWidth();
        const topPosition       = this.getComponentTopPosition( position.y, type, side, componentHeight, name );
        const buildingHeight    = this.getBuildingHeight();
        if( 'structure_country_cottage' === this._shedType && 'left' === side && topPosition > height ) {
            return this.isValidXPosition( buildingHeight, topPosition, position.z, componentWidth, width, height );
        }

        if( 'structure_classic_aframe_dormer' === this._shedType && 'left' === side && topPosition > height ) {
            return this.aframeDormerIsValidXPosition( buildingHeight, topPosition, width, height, position, componentWidth );
        }

        if( 'front' !== side && 'back' !== side && 'porch' !== side && ! isMoveHorizontal ) {
            return true;
        }

        return this.isValidXPosition( buildingHeight, topPosition, position.x, componentWidth, width, height );
    }

    aframeDormerIsValidXPosition = ( buildingHeight, topPosition, width, height, position, componentWidth ) => {
        if( buildingHeight < topPosition ) {
            return false;
        }

        let limitX = 0;
        if( topPosition <= height ) {
            limitX = width / 2;
        } else {
            const tanRoof   = Number( helpers.getTanFromDegrees( this._aframeDormerRoofDegree ).toFixed( 3 ) );
            const distance  = Number( ( buildingHeight - topPosition ).toFixed( 3 ) );
            limitX          = Number( ( distance / tanRoof ).toFixed( 3 ) );
        }

        return - limitX <= ( position.z - componentWidth / 2 ) && limitX >= ( position.z + componentWidth / 2 );
    }

    isValidXPosition = ( buildingHeight, topPosition, position, componentWidth, width, height ) => {
        if( buildingHeight < topPosition ) {
            return false;
        }
        
        const limitX = this.getEndXLimit( topPosition, height, width );
        
        position = helpers.toFixed3( Number( position ) );
        const componentHalfWidth = helpers.toFixed3( Number( componentWidth ) / 2 );
        
        return limitX.negativeLimit <= ( position - componentHalfWidth ) && limitX.positiveLimit >= ( position + componentHalfWidth );
    }

    getComponentTopPosition = ( positionY, type, side, componentHeight, name ) => {
        let topPosition = positionY;
        if( 'door' === type ) {
            topPosition = componentHeight + this.getDoorFixedHeight( side );
        } else {
            if( this.applyFixedHeightWindows( side, type, name ) ) {
                if( this.isFollowMainDoor() ) {
                    if( 0 !== this._doorFixedTarget.length ) {
                        topPosition = this.getWindowsFixedPositionY( type, componentHeight, side );
                    }
                } else {
                    topPosition = this.getWindowsFixedPositionY( type, componentHeight, side );
                }
            }
        }

        return topPosition;
    }

    deleteAllComponents() {
        let _this               = this;
        _this._allComponents    = {};
        
        for( const side in _this._walls ) {
            for( const key in _this._walls[side]["items"] ) {
                if ( _this._walls[side]["items"][key] != undefined ) {
                    let meshes = _this._walls[side]["items"][key]["meshes"];
                    if( meshes !== undefined ) {
                        meshes.forEach( ( mesh: Mesh, i ) => {
                            if( mesh !== undefined && mesh != null ) {
                                const key = mesh.metadata.key;
                                if( undefined !== key ) {
                                    jQuery( '[data-component-key="' + key + '"]' ).remove();
                                }
                                mesh.dispose();
                            }
                        });
                    }

                    delete _this._walls[side]["items"][key];
                }
            }
        }

        const shutters = _this.getShuttersInObject();
        for( const key in shutters ) {
            _this.removeShuttersInObject( key );
        }

        _this.handleSliding();
    }

    deleteAllComponentsBySide = ( side ) => {
        const _this     = this;
        let isDelete    = false;
        for( const key in _this._walls[side]["items"] ) {
            delete _this._allComponents[key];
            for( let k in _this._allComponents ) {
                if( _this._allComponents[k].parent === key ) {
                    delete _this._allComponents[k];
                }
            }

            if( _this._walls[side]["items"][key] ) {
                const meshes = _this._walls[side]["items"][key]["meshes"];
                if( meshes ) {
                    meshes.forEach( ( mesh: Mesh, i ) => {
                        if( mesh.metadata && mesh.metadata.key ) {
                            jQuery( `[data-component-key="${mesh.metadata.key}"]` ).remove();
                        }

                        mesh.dispose();
                    });
                }

                delete _this._walls[side]["items"][key];
                isDelete = true;
            }
        }

        const shutters = _this.getShuttersInObjectBySide( side );
        for( const key in shutters ) {
            _this.removeShuttersInObject( key );
        }

        return isDelete;
    }

    /**
     * Get list Electrical Package Component
     * 
     * @returns components
     */
    getElectricalPackageComponent(){
        return helpers.objectFilter( this._allComponents, component => -1 === ePackageType.indexOf( component.type ) );
    }

    /**
     * Delete all Electrical Package Component
     */
    deleteElectricalPackageComponent(){
        const _this         = this;
        const components    = this.getElectricalPackageComponent();

        for( const key in components ){
            const { side, type } = components[key];
            _this.deleteComponentV2( side, key, type );
        }
    }

    /**
     * Delete all switch and outlet in wall door
     */
    deleteSwitchOutletInWall = ( components, side ) => {
        if( 0 == components.length ) return;
        let componentsRemove = [];
        for( let i = 0; i < components.length; i++ ) {
            const componentName = jQuery( `[data-upgrade-component-name="${components[i].name}"]` ).val();
            componentsRemove.push( componentName );
            const eventRemoveComponent = new CustomEvent( 'remove-component', { detail: {
                side:   components[i].side,
                type:   components[i].type,
                key:    components[i].key
            }} );
            window.dispatchEvent( eventRemoveComponent );
        }
        if( componentsRemove.length ) {
            this.createAlert( `${componentsRemove.join( ', ' ) } removed from side ${helpers.getSideLabel( side, '' )} as no door was found nearby. This item must be placed near a door on the same wall. Please re-add from the "Interior" tab.` );
        }
    }

    /**
     * Check and remove Electrical item tie door
     * @param side 
     */
    handleDeleteComboElectricalComponentTieDoor = side => {
        let sideHasDoor : any   = this.getSideHasDoor();
        let componentNeedRemove = [];
        for( let k in this._allComponents ) {
            if( ! sideHasDoor.includes( side ) && side === this._allComponents[k].side && ( 'outlet' === this._allComponents[k].type || 'switch' === this._allComponents[k].type ) ) {
                const tieWithDoor = jQuery( `[data-upgrade-component-name="${this._allComponents[k].name}"]` ).data( 'tie-with-door' );
                if( tieWithDoor ) {
                    componentNeedRemove.push( this._allComponents[k] );
                }
            }
        }
        this.deleteSwitchOutletInWall( componentNeedRemove, side ); 
    }

    deleteComponentV2( side, key, type ) {
        return new Promise( resolve => {
            let _this = this;
            delete _this._allComponents[key];
            for( let k in _this._allComponents ) {
                if( _this._allComponents[k].parent === key ) {
                    delete _this._allComponents[k];
                }
            }
            if( _this._isEnableTackRoom && _this._doorsOpeningTackRoom.length ) {
                for( let k in _this._doorsOpeningTackRoom ) {
                    if( _this._doorsOpeningTackRoom[k].key === key ) {
                        _this._doorsOpeningTackRoom.splice( k, 1 );
                    }
                }
            }
            if( 'door' === type ) {
                _this.handleDeleteComboElectricalComponentTieDoor( side );
                _this.moveBonseFramingGreenHousePolycarbon( side );
            }

            if ( _this._walls[side]["items"][key] != undefined ) {
                let meshes = _this._walls[side]["items"][key]["meshes"];
                if (meshes != undefined) {
                    meshes.forEach((mesh: Mesh, i) => {
                        if (mesh != undefined && mesh != null) {
                            mesh.dispose();
                        }
                    });
                }

                _this._doorFixedTarget  = _this._doorFixedTarget.filter( item => item.key != key );
                const childComponents   = _this.getComponentChildByParentKey( key, side );
                for( let i = 0; i < childComponents.length; i++ ) {
                    delete _this._walls[side]["items"][childComponents[i]['key']];
                    _this.removeShuttersInObject( childComponents[i]['key'] );
                }

                _this.updateHoleWall( side );

                _this.updateHoleRoof( side );

                _this.updateFrame( side );
                _this.handleIntersectKickBoard( side );
                _this.handleIntersectKickBoard( side, 'frame_kick_board' );

                delete _this._walls[side]["items"][key];
            }

            _this.addNotificationRequiredDoor();
            _this.handleSliding();

            jQuery(`ul.added-components.${type}`).find(`li[data-component-key='${key}']`).remove();

            resolve( true );
        });
    }
    

    deleteComponent( side, key, isReplace = false ) {
        return new Promise( resolve => {
            let _this               = this;
            let componentNeedRemove = [];
            const { type, name } = _this._allComponents[key];
            delete _this._allComponents[key];
            let sideHasDoor : any   = _this.getSideHasDoor();
            for( let k in _this._allComponents ) {
                if( 'door' === type && ! isReplace && ! sideHasDoor.includes( side ) && side === _this._allComponents[k].side && ( 'outlet' === _this._allComponents[k].type || 'switch' === _this._allComponents[k].type ) ) {
                    const tieWithDoor = jQuery( `[data-upgrade-component-name="${_this._allComponents[k].name}"]` ).data( 'tie-with-door' );
                    if( tieWithDoor ) {
                        componentNeedRemove.push( _this._allComponents[k] );
                    }
                }

                if( _this._allComponents[k].parent === key ) {
                    delete _this._allComponents[k];
                }
            }

            if( _this._isEnableTackRoom && _this._doorsOpeningTackRoom.length ) {
                for( let k in _this._doorsOpeningTackRoom ) {
                    if( _this._doorsOpeningTackRoom[k].key === key ) {
                        _this._doorsOpeningTackRoom.splice( k, 1 );
                    }
                }
            }

            _this.addNotificationRequiredDoor();

            if ( _this._walls[side]["items"][key] != undefined ) {
                let meshes = _this._walls[side]["items"][key]["meshes"];
                if (meshes != undefined) {
                    meshes.forEach((mesh: Mesh, i) => {
                        if (mesh != undefined && mesh != null) {
                            mesh.dispose();
                        }
                    });
                }

                _this._doorFixedTarget = _this._doorFixedTarget.filter( item => item.key != key );
                delete _this._walls[side]["items"][key];
            }

            const childComponents = _this.getComponentChildByParentKey( key, side );
            for( let i = 0; i < childComponents.length; i++ ) {
                delete _this._walls[side]["items"][childComponents[i]['key']];
                _this.removeShuttersInObject( childComponents[i]['key'] );
            }

            _this.handleSliding();
            _this.updateWindowsPosition();
            _this.removeEditButton();
            _this.removeLine();
            _this.removeNoticeText();
            _this.updateHoleWall( side );
            _this.updateHoleRoof( side );
            _this.updateFrame( side );
            _this.handleIntersectKickBoard( side );
            _this.handleIntersectKickBoard( side, 'frame_kick_board' );
            _this.deleteSwitchOutletInWall( componentNeedRemove , side );
            if( 'door' === type ) {
                _this.moveBonseFramingGreenHousePolycarbon( side );
            }
            window.dispatchEvent( new CustomEvent( 'remove-component-callback', { detail: { side, type, name } } ) );
            
            resolve( true );
        });
    }
   

    private intersectComponent( meshes, side ) {
        let drawWallModels = [ 'structure_cottage_shed', 'structure_barn_smithbilt', 'structure_barn_corner_porch', 'structure_cardinal_lofted_cabin', 'structure_monitorbarns_helmuth', 'structure_rancher', 'structure_cardinal_victorian', 'structure_chalet_genesis', 'structure_loafing_shed' ];
        
        drawWallModels = drawWallModels.concat( this.getCarportModels() );

        let drawWallModelsWithOptions = ['structure_aframe_cornerporch', 'cottage_without_fe_overhangs', 'windsor', 'barn_without_fe_overhangs', 'structure_lapsider_endgable', 'standard_cottage_front_porch', 'standard_cottage', 'structure_aframe_genesis' ];

        if( this.ignoreHandleTackDoor( side ) || this.isSidePorch() || this.isMiddlePorch() || this.isDeluxeCornerLeftPorch() || -1 !== drawWallModels.indexOf( this._shedType ) || ( -1 !== drawWallModelsWithOptions.indexOf( this._shedType ) && 'true' === this.getSettingData( 'apply_new_walls' ) ) || 'true' === this.getSettingData( 'apply_new_walls' ) ) {
            return;
        }

        let _this = this;

        let sidingMeshName = "";
        let stencilRef: number;

        switch (side) {
            case "front":
            case "inner_front":
                sidingMeshName =  'siding_f_obj';
                stencilRef = 3;
                break;

            case "inner_front":
                sidingMeshName =  'siding_f_obj';
                stencilRef = 4;
                break;

            case "back":
                sidingMeshName = 'siding_b_obj';
                stencilRef = 5;
                break;

            case "left":
            case "inner_left":
                sidingMeshName =  'siding_l_obj';
                stencilRef = 6;
                break;

            case "inner_left":
                sidingMeshName =  'siding_l_obj';
                stencilRef = 7;
                break;

            case "right":
                sidingMeshName =  'siding_r_obj';
                stencilRef = 9;
                break;

            case "porch":
                sidingMeshName =  'porch_f_obj';
                stencilRef = 11;
                break;
            default:
                break;
        }

        let sidingMesh = _this._scene.getMeshByName(sidingMeshName) as Mesh;
        let intersectMesh = _this.filterByValue(meshes, 'name', 'intersect') as Mesh;
        let displayMesh = _this.filterByValue(meshes, 'name', 'display') as Mesh;

        if (intersectMesh != undefined && displayMesh != undefined) {
            _this.makeIntersection( stencilRef, sidingMesh, intersectMesh, displayMesh );
        }
    }

    getComponentSameSide = ( side, face ) => {
        if( ! this.is2StoryLeanTo() ) {
            return this.getComponentBySideAndType( side, face );
        }

        if( 'front' === side || 'top_front' === side || 'lean_to_front' === side ) {
            return { ...this.getComponentBySideAndType( 'front', face ), ...this.getComponentBySideAndType( 'top_front', face ), ...this.getComponentBySideAndType( 'lean_to_front', face ) };
        }
        
        if( 'back' === side || 'top_back' === side || 'lean_to_back' === side ) {
            return { ...this.getComponentBySideAndType( 'back', face ), ...this.getComponentBySideAndType( 'top_back', face ), ...this.getComponentBySideAndType( 'lean_to_back', face ) };
        }
        
        if( 'left' === side || 'top_left' === side ) {
            return { ...this.getComponentBySideAndType( 'left', face ), ...this.getComponentBySideAndType( 'top_left', face ) };
        }
        
        if( 'right' === side || 'top_right' === side ) {
            return { ...this.getComponentBySideAndType( 'right', face ), ...this.getComponentBySideAndType( 'top_right', face ) };
        }

        return this.getComponentBySideAndType( side, face );
    }

    updateRoofComponentsPosition = () => {
        const components = this.getComponentByType( 'skylight_window' );
        const tanRoof = helpers.getTanFromDegrees( this.getRoofCorrectionDegree() );
        const maxY = this.getPositionYForSkylightWindow();

        for( let i = 0; i < components.length; i++ ) {
            const component = components[i];
            const { side, key, meshes, type, name } = component;

            if( meshes[0] ) {
                const positionY = helpers.toFixed3( maxY - ( Math.abs( component.meshes[0].position.x ) * tanRoof ) );
                const position = new Vector3( meshes[0].position.x, positionY, meshes[0].position.z );
                const rotation = this.calculateComponentRotation( side, type );

                this.placeComponent( meshes, position, rotation, type, side, name );
                this.updateComponentsPosition2D( side, key, position, component );
            }

        }
    }

    private updateWallComponentsPosition( side, key, component, positionBeforeDrag ) {
        const face          = this.getComponentFace( component.type );
        const components    = this.getComponentSameSide( side, face );
        const newPosition   = components[key]["meshes"][0]["position"];
        const limitSpacing  = this.getHorizontalSpacing( component.type );

        this._walls[side]["items"][key]["position"] = newPosition;
        this.updateComponentsPosition2D( side, key, newPosition, component );

        const childSize             = this.getChildSize( side, key, 'flower_box' );
        const childHeight           = 'windows' === component.type ? childSize.height : 0;
        const childWidth            = 'windows' === component.type ? childSize.width : 0;
        const componentWidth        = 'windows' === component.type ? this.getComponentWidth( component.meshes, component.type, component.name ) : 0;
        const tmpWidth              = childWidth > componentWidth ? ( childWidth - componentWidth ) / 2 : 0;
        const lastPointVertical     = component.position2D.vertical.lastPoint;
        const firstPointVertical    = component.position2D.vertical.firstPoint;
        const lastPointHorizontal   = component.position2D.horizontal.lastPoint + limitSpacing;
        const firstPointHorizontal  = component.position2D.horizontal.firstPoint - limitSpacing;
        const startXForLeg          = component.position2D.horizontal.firstPoint;
        const endXForLeg            = component.position2D.horizontal.lastPoint;
        const cLastPointVertical    = component.position2D.vertical.firstPoint;
        const cFirstPointVertical   = component.position2D.vertical.firstPoint - childHeight;
        const cLastPointHorizontal  = component.position2D.horizontal.lastPoint + tmpWidth + limitSpacing;
        const cFirstPointHorizontal = component.position2D.horizontal.firstPoint - tmpWidth - limitSpacing;
        
        for( const compKey in components ) {
            const oldComponent = components[compKey];
            if( key !== compKey && 'ramp' !== oldComponent.type ) {
                const childSize         = this.getChildSize( side, compKey, 'flower_box' );
                const childHeight       = 'windows' === oldComponent.type ? childSize.height : 0;
                const childWidth        = 'windows' === oldComponent.type ? childSize.width : 0;
                const componentWidth    = 'windows' === oldComponent.type ? this.getComponentWidth( oldComponent.meshes, oldComponent.type, oldComponent.name ) : 0;
                const tmpWidth          = childWidth > componentWidth ? ( childWidth - componentWidth ) / 2 : 0;
                
                const lastPointVerticalOld      = oldComponent.position2D.vertical.lastPoint;
                const firstPointVerticalOld     = oldComponent.position2D.vertical.firstPoint;
                const lastPointHorizontalOld    = oldComponent.position2D.horizontal.lastPoint;
                const firstPointHorizontalOld   = oldComponent.position2D.horizontal.firstPoint;

                const cLastPointVerticalOld      = oldComponent.position2D.vertical.firstPoint;
                const cFirstPointVerticalOld     = oldComponent.position2D.vertical.firstPoint - childHeight;
                const cLastPointHorizontalOld    = oldComponent.position2D.horizontal.lastPoint + tmpWidth;
                const cFirstPointHorizontalOld   = oldComponent.position2D.horizontal.firstPoint - tmpWidth;

                const child2D = {
                    horizontal : { cFirstPointHorizontal, cLastPointHorizontal, cFirstPointHorizontalOld, cLastPointHorizontalOld },
                    vertical : { cFirstPointVertical, cLastPointVertical, cFirstPointVerticalOld, cLastPointVerticalOld }
                }
                const parent2D = {
                    horizontal : { firstPointHorizontal, lastPointHorizontal, firstPointHorizontalOld, lastPointHorizontalOld },
                    vertical : { firstPointVertical, lastPointVertical, firstPointVerticalOld, lastPointVerticalOld }
                }

                if( 'floor' !== side && this.isIntersectComponent( parent2D, child2D ) ) {
                    if( this._walls[side]["items"][key]["meshes"][0].position.x != positionBeforeDrag.x || this._walls[side]["items"][key]["meshes"][0].position.y != positionBeforeDrag.y || this._walls[side]["items"][key]["meshes"][0].position.z != positionBeforeDrag.z ) {
                        this.createAlert( this.NOTICES.cannotMove );
                    }

                    this._walls[side]["items"][key]["meshes"][0].position = positionBeforeDrag;
                    this.updateComponentsPosition2D( side, key, positionBeforeDrag, component );
                    if( this.isMobile() ) {
                        let meshes          = component.meshes;
                        let meshHighlight   = this.filterByValue( meshes, 'name', '_bb_obj' ) as Mesh;
                        this.enableHighlightMesh( meshHighlight, new Color4( 0, 0, 0, 1 ) );
                    }
                }
            }

            if( this.isMobile() ) {
                if( component.key !== oldComponent.key ) {
                    const meshHighlight = this.filterByValue( oldComponent.meshes, 'name', '_bb_obj' ) as Mesh;
                    if( undefined !== meshHighlight && null !== meshHighlight ) {
                        meshHighlight.visibility = 0;
                    }

                    const childComponents       = this.getComponentChildByParentKey( key, side );
                    const newMeshes             = this.getAllChildMeshes( childComponents );
                    const childMeshHighlight    = this.filterMeshes( newMeshes, 'name', '_bb_obj' );
                    childMeshHighlight.forEach( e => e.visibility = 0 );

                    const oldChildComponents    = this.getComponentChildByParentKey( compKey, side );
                    const oldMeshes             = this.getAllChildMeshes( oldChildComponents );
                    const oldChildMeshHighlight = this.filterMeshes( oldMeshes, 'name', '_bb_obj' );
                    oldChildMeshHighlight.forEach( e => e.visibility = 0 );
                }
            } else {
                const meshHighlight = this.filterByValue( oldComponent.meshes, 'name', '_bb_obj' ) as Mesh;
                if( undefined !== meshHighlight && null !== meshHighlight ) {
                    meshHighlight.visibility = 0;
                }

                const childComponents       = this.getComponentChildByParentKey( key, side );
                const newMeshes             = this.getAllChildMeshes( childComponents );
                const childMeshHighlight    = this.filterMeshes( newMeshes, 'name', '_bb_obj' );
                childMeshHighlight.forEach( e => e.visibility = 0 );

                const oldChildComponents    = this.getComponentChildByParentKey( compKey, side );
                const oldMeshes             = this.getAllChildMeshes( oldChildComponents );
                const oldChildMeshHighlight = this.filterMeshes( oldMeshes, 'name', '_bb_obj' );
                oldChildMeshHighlight.forEach( e => e.visibility = 0 );
            }
        }

        const types = [ 'door', 'windows', 'vent' ];
        if( this.is2StoryLeanTo() && - 1 !== types.indexOf( component.type ) ) {
            if( this.isEnableLeanTo() ) {
                let trimMesh = this.getTrimMesh( side );
                if( trimMesh && lastPointVertical < this._bones['lean_to_f_r4'].position.y ) {
                    const isIntersect = this.isIntersectComponentWithTrim( startXForLeg, endXForLeg, firstPointVertical, lastPointVertical );
                    if( isIntersect ) {
                        this._walls[side]['items'][key]['meshes'][0].position = positionBeforeDrag;
                        this.updateComponentsPosition2D( side, key, positionBeforeDrag, component );
                        trimMesh.setEnabled( false );
                    }
                }
            }
            
            let secondFloor = this.getMeshByName( 'second_floor_obj' );
            if( secondFloor && this.isIntersectComponentWithFloor( side, secondFloor, startXForLeg, endXForLeg, firstPointVertical, lastPointVertical ) ) {
                this._walls[side]['items'][key]['meshes'][0].position = positionBeforeDrag;
                this.updateComponentsPosition2D( side, key, positionBeforeDrag, component );
                secondFloor.material = this._trimMat;
            }
        }

        if( this._isEnableTackRoom && this.isIntersectComponentWithPartialWall( startXForLeg, endXForLeg ) && key !== this._tackDoor.key && helpers.hasKeyInArray( zSide, side ) ) {
            this._walls[side]['items'][key]['meshes'][0].position = positionBeforeDrag;
            this.updateComponentsPosition2D( side, key, positionBeforeDrag, component );
        }
    }

    /**
     * Get the trim mesh based on the side.
     *
     * @param {string} side - The side of the trim.
     * @returns {Mesh|undefined} - The trim mesh if found, otherwise null.
     */
    getTrimMesh = ( side ) => {
        let trimMesh;
        if( 'front' === side || 'top_front' === side || 'lean_to_front' === side ) {
            trimMesh = this.getMeshByName( 'B_F_R_collider_obj' );
        } else if( 'back' === side || 'top_back' === side || 'lean_to_back' === side ) {
            trimMesh = this.getMeshByName( 'B_B_R_collider_obj' );
        }

        return trimMesh;
    }

    /**
     * Check if a component intersects with trim.
     *
     * @param {number} componentStartX - The start X coordinate of the component.
     * @param {number} componentEndX - The end X coordinate of the component.
     * @param {number} componentStartY - The start Y coordinate of the component.
     * @param {number} componentEndY - The end Y coordinate of the component.
     * @returns {boolean} - True if the component intersects with trim, false otherwise.
     */
    isIntersectComponentWithTrim = ( componentStartX, componentEndX, componentStartY, componentEndY ) => {
        const trimStartX = this._bones['b_f_r1'].position.x + 0.1;
        const trimEndX   = trimStartX - 0.5;
        const trimStartY = 0;
        const trimEndY   = this._bones['lean_to_f_r4'].position.y;
        
        return ! this.isValidComponent( componentStartX, componentEndX, trimStartX, trimEndX, componentStartY, componentEndY, trimStartY, trimEndY );
    }

    isIntersectComponentWithPartialWall = ( componentStartX, componentEndX ) => {
        const listMeshName = [ 'partial_wall_back', 'partial_wall_front' ];
        for( let i = 0; i < listMeshName.length ; i++ ) {
            const mesh      = this.getMeshByName( listMeshName[i] ) as Mesh;
            if( ! mesh ) continue;
            const startX    = mesh.position.z - 0.04;
            const endX      = mesh.position.z + 0.04;
            if( ! this.isValidHorizontal( componentStartX, componentEndX, startX, endX ) ) {
                return true;
            }
        }

        return false;
    }

    /**
     * Check if a component intersects with the floor.
     *
     * @param {string} side - The side of the component.
     * @param {boolean} secondFloor - Whether the floor is the second floor.
     * @param {number} componentStartX - The start X coordinate of the component.
     * @param {number} componentEndX - The end X coordinate of the component.
     * @param {number} componentStartY - The start Y coordinate of the component.
     * @param {number} componentEndY - The end Y coordinate of the component.
     * @returns {boolean} - True if the component intersects with the floor, false otherwise.
     */
    isIntersectComponentWithFloor = ( side, secondFloor, componentStartX, componentEndX, componentStartY, componentEndY ) => {
        const halfWidth   = this.getWidth() / 2;
        const halfDepth   = this.getDepth() / 2;
        const wallHeight  = this.getWallHeight();
        const floorStartX = - 1 !== zSide.indexOf( side ) ? - halfDepth : - halfWidth;
        const floorEndX   = - 1 !== zSide.indexOf( side ) ? halfDepth : halfWidth;
        const floorStartY = wallHeight;
        const floorEndY   = wallHeight + this.getMeshHeight( secondFloor );

        return ! this.isValidComponent( componentStartX, componentEndX, floorStartX, floorEndX, componentStartY, componentEndY, floorStartY, floorEndY );
    }    

    getSpaceHasComponent = ( side, key? ) => {
        let spaceHasComponent = [];
        for( const compKey in this._walls[side]["items"] ) {
            if( key && key === compKey ) {
                continue;
            }

            const comp = this._walls[side]["items"][compKey];
            spaceHasComponent.push({
                    horizontal: {
                        fPoint: comp.position2D.horizontal.firstPoint,
                        lPoint: comp.position2D.horizontal.lastPoint
                    },
                    vertical: {
                        fPoint: comp.position2D.vertical.firstPoint,
                        lPoint: comp.position2D.vertical.lastPoint
                    }
            });
        }

        return spaceHasComponent;
    }

    isValidComponent( firstX, lastX, oldFirstX, oldLastX, firstY, lastY, oldFirstY, oldLastY ) {
        if( ! this.isValidHorizontal( firstX, lastX, oldFirstX, oldLastX ) && ! this.isValidVertical( firstY, lastY, oldFirstY, oldLastY ) ) {
            return false;
        }

        return true;
    }

    isValidHorizontal = ( first, last, oldFirst, oldLast ) => {        
        first       = helpers.toFixed3( first );
        last        = helpers.toFixed3( last );
        oldFirst    = helpers.toFixed3( oldFirst );
        oldLast     = helpers.toFixed3( oldLast );
        if( ( first === oldFirst && last === oldLast ) || ( first === oldLast && last === oldFirst ) ) {
            return false;
        }

        if( first > oldFirst && first < oldLast ) {
            return false;
        }

        if( first > oldLast && first < oldFirst ) {
            return false;
        }

        if( last > oldFirst && last < oldLast ) {
            return false;
        }

        if( last > oldLast && last < oldFirst ) {
            return false;
        }

        if( oldFirst > first && oldFirst < last ) {
            return false;
        }

        if( oldFirst > last && oldFirst < first ) {
            return false;
        }

        if( oldLast > first && oldLast < last ) {
            return false;
        }

        if( oldLast > last && oldLast < first ) {
            return false;
        }

        return true;
    }

    isValidVertical = ( first, last, oldFirst, oldLast ) => {
        first       = helpers.toFixed3( first );
        last        = helpers.toFixed3( last );
        oldFirst    = helpers.toFixed3( oldFirst );
        oldLast     = helpers.toFixed3( oldLast );
        if( ( first === oldFirst && last === oldLast ) || ( first === oldLast && last === oldFirst ) ) {
            return false;
        }

        if( first > oldFirst && first < oldLast ) {
            return false;
        }

        if( first > oldLast && first < oldFirst ) {
            return false;
        }

        if( last > oldFirst && last < oldLast ) {
            return false;
        }

        if( last > oldLast && last < oldFirst ) {
            return false;
        }

        if( oldFirst > first && oldFirst < last ) {
            return false;
        }

        if( oldFirst > last && oldFirst < first ) {
            return false;
        }

        if( oldLast > first && oldLast < last ) {
            return false;
        }

        if( oldLast > last && oldLast < first ) {
            return false;
        }

        return true;
    }

    getComponentHeight( meshes, type ): float {
        let boundingBox = this.filterByValue( meshes, 'name', '_bb_obj' ) as Mesh;

        if( undefined !== boundingBox && null !== boundingBox ) {
            if( boundingBox.geometry ) {
                if( 'door' === type ) {
                    return +( boundingBox.geometry.extend.maximum.y ).toFixed( 3 );
                }
                
                return +( boundingBox.geometry.extend.maximum.y - boundingBox.geometry.extend.minimum.y ).toFixed( 3 );
            }
        }

        return 0;
    }

    getComponentDepth( meshes ):float {
        let boundingBox = this.filterByValue( meshes, 'name', '_bb_obj' ) as Mesh;

        if( undefined !== boundingBox && null !== boundingBox ) {
            if( boundingBox.geometry ) {
                return +( boundingBox.geometry.extend.maximum.z - boundingBox.geometry.extend.minimum.z ).toFixed( 3 );
            }
        }

        return 0;
    }
    
    getComponentWidth( meshes, type, componentName, isMoveHorizontal = false ): float {
        if( 'windows' === type || 'transom' === type || 'flower_box' === type ) {
            let tmpMesh = this.filterByValue( meshes, 'name', `${componentName}_trim_obj` ) as Mesh;
            if( undefined === tmpMesh || null === tmpMesh ) {
                tmpMesh = this.filterByValue( meshes, 'name', `${componentName}_obj` ) as Mesh;
            }
            
            if( undefined === tmpMesh || null === tmpMesh ) {
                tmpMesh = this.filterByValue( meshes, 'name', '_bb_obj' ) as Mesh;
            }
            
            if( undefined !== tmpMesh && null !== tmpMesh ) {
                if( tmpMesh.geometry ) {
                    return +( tmpMesh.geometry.extend.maximum.x - tmpMesh.geometry.extend.minimum.x ).toFixed( 3 );
                }
            }
        }

        let boundingBox = this.filterByValue( meshes, 'name', '_bb_obj' ) as Mesh;
        if( undefined !== boundingBox && null !== boundingBox ) {
            if( boundingBox.geometry ) {
                if( 'light' === type && ! isMoveHorizontal ) {
                    return helpers.toFixed3( boundingBox.geometry.extend.maximum.z - boundingBox.geometry.extend.minimum.z );
                } else {
                    return helpers.toFixed3( boundingBox.geometry.extend.maximum.x - boundingBox.geometry.extend.minimum.x );
                }
            }
        }

        return 0;
    }

    getPosition2D( side, meshes, position, componentType, componentName ) {
        componentType           = this.getBehavior( componentName, componentType );
        let firstPointWidth     = 0;
        let lastPointWidth      = 0;
        let firstPointHeight    = 0;
        let lastPointHeight     = 0;
        const isMoveHorizontal  = this.isMoveHorizontal( componentType);
        const sizeWidth         = this.getComponentWidth( meshes, componentType, componentName, isMoveHorizontal );
        const sizeHeight        = this.getComponentHeight( meshes, componentType );
        if( 0 !== sizeWidth && 0 !== sizeHeight ) {
            const tmpWidth      = isMoveHorizontal || 'front' === side || 'top_front' === side || 'lean_to_front' === side || 'back' === side || 'top_back' === side || 'lean_to_back' === side || 'inner_front' === side || 'left_inner_porch' === side || 'right_inner_porch' === side || 'inner_back' === side || this.isPorchSide( side ) ? Number( position.x ) : Number( position.z );
            const tmpHeight     = position.y;
            const floorPosition = this.getPositionYForInterior( componentType, componentName );

            if( 'shelf' === componentType ) {
                const boundingBox       = this.filterByValue( meshes, 'name', '_bb_obj' ) as Mesh;
                if( boundingBox && boundingBox.geometry ) {
                    firstPointHeight    = boundingBox.geometry.extend.minimum.y + floorPosition;
                    lastPointHeight     = boundingBox.geometry.extend.maximum.y + floorPosition;
                } else {
                    firstPointHeight    = 0;
                    lastPointHeight     = sizeHeight;
                }
            } else if( 'workbench' === componentType ) {
                lastPointHeight     = this._bones['workbench_l'] ? this._bones['workbench_l'].position.y + floorPosition : 3;
                firstPointHeight    = lastPointHeight - sizeHeight;
            } else if( 'switch' === componentType || 'outlet' === componentType || 'breakerbox' === componentType ) {
                lastPointHeight     = tmpHeight + sizeHeight / 2;
                firstPointHeight    = tmpHeight - sizeHeight / 2;
            } else if( 'cupola' === componentType ) {
                firstPointHeight    = tmpHeight;
                lastPointHeight     = tmpHeight + sizeHeight;
            } else if( 'skylight_window' === componentType ) {
                const boundingBox = this.filterByValue( meshes, 'name', '_bb_obj' ) as Mesh;
                const boudingMesh = boundingBox.clone( 'bb_clone_mesh' );
                const boundingBoxInfo = boudingMesh.getBoundingInfo().boundingBox;
                firstPointHeight = helpers.toFixed3( boundingBoxInfo.minimumWorld.x );
                lastPointHeight = helpers.toFixed3( boundingBoxInfo.maximumWorld.x );

                boudingMesh.dispose();

            } else if( 'door' !== componentType && 'shelf' !== componentType ) {
                firstPointHeight    = tmpHeight - sizeHeight;
                lastPointHeight     = tmpHeight;
            } else {
                firstPointHeight    = 0;
                lastPointHeight     = sizeHeight;
            }

            firstPointWidth = tmpWidth - sizeWidth / 2;
            lastPointWidth  = tmpWidth + sizeWidth / 2;

            if( 'dormer' === componentType ) {
                if( this.isGableDormer( componentName ) ) {
                    firstPointHeight    = position.y;
                    lastPointHeight     = firstPointHeight + sizeHeight;
                } else {
                    const heightDistance    = this.getFlatDormerHeightDistance();
                    firstPointHeight        = this.getWallHeight() + heightDistance;
                    lastPointHeight         = firstPointHeight + this._flatDormerHeight;
                }
            }
        }

        return {
            horizontal: { firstPoint: firstPointWidth, lastPoint: lastPointWidth },
            vertical: { firstPoint: firstPointHeight, lastPoint: lastPointHeight }
        };
    }

    updateComponentsPosition2D( side, componentKey, position, component ) {
        if( this._walls[side]["items"][componentKey] ) {
            this._walls[side]["items"][componentKey]["position2D"]  = this.getPosition2D( side, component.meshes, position, component.type, component.name );
            this._walls[side]["items"][componentKey]["position"]    = position;
        }
    }

    private placeComponent( meshes, position, rotation, type, side, name ) {
        if( ! rotation ) rotation = this.calculateComponentRotation( side, type );
        const behavior = this.getBehavior( name, type );
        let firstMesh       = meshes[0] as Mesh;
        const wallHeight    = this.getWallHeight();
        firstMesh.rotation  = new Vector3( rotation.x, rotation.y, rotation.z );

        if( 'structure_monitorbarns_helmuth' === this._shedType ) {
            if( 'left' === side ) {
                position.x = this._bones['b_b_l2'].position.x;
            } else if( 'right' === side ) {
                position.x = this._bones['b_b_r2'].position.x;
            }
        }

        if( ( 'structure_lakeside_bunkie_gambrel_roof' === this._shedType || 'structure_lakeside_bunkie_gable_roof' === this._shedType ) && 'windows' === behavior && 'front' === side && position.y >= wallHeight ){
            position.y = wallHeight;
        }
        
        if( 'dormer' === behavior ) {
            const dormerName = firstMesh.name.replace( '_set_obj', '' );
            if( this.isGableDormer( dormerName ) ) {
                const childMeshes       = firstMesh.getChildMeshes();
                const dormerFrame       = `${dormerName}_frame_obj`;
                const dormerHeight      = this.getGableDormerDistance( dormerFrame, childMeshes, 'Dormer_B_F_T', 'Dormer_B_F_R1', 'y' );
                firstMesh.position.y    = helpers.toFixed3( this.getBuildingHeight() - dormerHeight );
                firstMesh.position.x    = position.x;
                firstMesh.position.z    = position.z;

                if( this._isShareLink ) {
                    firstMesh.position.y = position.y;
                }
            } else {
                let fullHeight          = 'structure_aframe_2story_attic_leanto' === this._shedType ? this._bones['b_b_l2'].position.y : wallHeight;
                if( 'structure_aframe_cabin' === this._shedType ) {
                    fullHeight = this._bones['b_f_r6'].position.y
                }
                const heightDistance    = this.getFlatDormerHeightDistance();
                const dormerPositionY   = fullHeight + heightDistance
                const dormerTop         = this._bones['b_b_t'].position.y - ( fullHeight + heightDistance );
                const dormerWidth       = this.getFlatDormerWidth();
                firstMesh.position.y    = dormerPositionY;
                firstMesh.position.z    = position.z;

                this._scene.skeletons.forEach( s => {
                    const bones = s.bones;
                    bones.forEach( b => {
                        if( b.name === 'flat_dormer_F' ) {
                            b.setPosition( new Vector3( b.position.x, b.position.y, dormerWidth ) );
                        } else if( b.name === 'flat_dormer_T' ) {
                            b.setPosition( new Vector3( b.position.x, dormerTop, b.position.z ) );
                        }
                    });
                });
            }
        } else {
            if( this.applyFixedHeightWindows( side, behavior, firstMesh.metadata.name ) ) {
                if( this.isFollowMainDoor() ) {
                    if( this._doorFixedTarget.length !== 0 ) {
                        const componentHeight   = this.getComponentHeight( meshes, behavior );
                        position.y              = this.getWindowsFixedPositionY( behavior, componentHeight, side );
                    }
                } else {
                    const componentHeight   = this.getComponentHeight( meshes, behavior );
                    position.y              = this.getWindowsFixedPositionY( behavior, componentHeight, side );
                }
            }

            if( 'structure_saltbox_mwshed' === this._shedType && ( 'right' === side || 'left' === side ) && 'ramp' !== behavior && 'flower_box' !== behavior && 'shutters' !== behavior ) {
                if( 'left' === side ){
                    firstMesh.position = new Vector3( this._bones['b_b_l1'].position.x, position.y, position.z );
                }else if( 'right' === side ){
                    firstMesh.position = new Vector3( this._bones['b_b_r4'].position.x, position.y, position.z );
                }
            }else if( 'mainus_quaker' === this._shedType && 'vent' === behavior ) {
                firstMesh.position = new Vector3( this._bones['b_b_t'].position.x, this._bones['b_b_t'].position.y - 0.5, position.z );
            } else if('vent' === behavior && this.isSidePorch() && 'structure_hiproof' !== this._shedType && ( 'front' === side || 'back' === side ) ) {
                firstMesh.position = new Vector3( this._bones['b_b_t'].position.x, position.y, position.z );
            } else {
                if( ( 'windows' === behavior || 'vent' === behavior ) && 'structure_aframe_horserunin' === this._shedType ) {
                    if( position.z > 0 ) {
                        position.z = position.z + 0.045;
                    } else {
                        position.z = position.z - 0.045;
                    }
                }

                if( this.isPorchSide( side ) && 'flower_box' !== behavior ) {
                    if( this._bones['b_p_t'] ) {
                        position.z = this._bones['b_p_t'].position.z;
                    }else if( this._bones['r_f_t'] ){
                        position.z = this.getDepth() / 2 + 32/12;
                    }
                }

                if( this.isSidePorch() && 'left' === side && ( 'vent' === behavior || 'door' === behavior || 'windows' === behavior || 'shelf' === behavior || 'workbench' === behavior ) ) {
                    firstMesh.position = new Vector3( this._bones['b_b_l1'].position.x, position.y, position.z );
                } else {
                    firstMesh.position = new Vector3( position.x, position.y, position.z );
                }
            }

        }

        this.removeNotificationRequiredDoor();

        window.dispatchEvent( new CustomEvent( 'place-component', {} ) );

        this.updateHoleWall( side );

        this.updateFrame( side );
        this.handleIntersectKickBoard( side );
        this.handleIntersectKickBoard( side, 'frame_kick_board' );


        this.handleSmithbiltComponentTrim( firstMesh );
        
        // const boundingBoxMesh = this.filterByValue( meshes, 'name', '_bb_obj' ) as Mesh;
        // firstMesh.computeWorldMatrix();
        // boundingBoxMesh.computeWorldMatrix();
        // boundingBoxMesh.refreshBoundingInfo();
    }

    handleMoveComponent = ( side, key ) => {
        if( this.isMobile() || this.isItemBlock( key ) || this.isItemBlockMoving( key ) ) {
            return;
        }
        if( this._isEnableTackRoom && ! this._dataSettingsTackroom['enable_moving_tackroom_door'] && this._walls[side]["items"][key].isTackDoor ) {
           return;
        }

        const _this     = this;
        let component   = _this._walls[side]['items'][key];
        const behavior  = _this.getBehavior( component.name, component['type'] );
        
        if( ( 'structure_monitorbarns_helmuth' === _this._shedType && 'cupola' !== component['type'] ) || ( 'true' === _this.getSettingData( 'block_off_centered' ) && 'door' === component['type'] ) ) {
            return;
        }
        const meshes    = component['meshes'];
        const anchor    = _this._componentTypeSettings[behavior]['anchor'];
        let moveable    = { horizontal: true, vertical: true };
        if( ! _this.isGableDormer( component.name ) ) {
            moveable    = { ..._this._componentTypeSettings[behavior]['moveable'] };
        }

        const depth     = _this.getDepth();
        const firstMesh = meshes[0] as Mesh;

        const tanRoofLowerCorner    = _this.getTanRoofPorch();
        const tanRoofLeftDormer     = _this.tanRoofLeftDormer();
        const isMoveHorizontal      = _this.isMoveHorizontal( behavior );
        const componentHeight       = _this.getComponentHeight( meshes, component['type'] );
        const componentWidth        = _this.getComponentWidth( meshes, component['type'], component.name, isMoveHorizontal );
        const componentDepth        = _this.getComponentDepth( meshes );
        const sidePorchEndX         = _this.getLimitComponentXRoofPorch( componentHeight, componentWidth ) > 0 ? _this.getLimitComponentXRoofPorch( componentHeight, componentWidth ) : 0;
        const tanRoofUpperCorner    = helpers.getTanFromDegrees( _this._roofUpperDegree ? _this._roofUpperDegree : _this._initRoofUpperDegree );
        const limitUpperVertical    = _this._bones['b_d_l1'] ? Math.abs( _this._bones['b_d_l1'].position.z ) : depth / 2;
        const limitLowerVertical    = _this._bones['b_d_l2'] ? Math.abs( _this._bones['b_d_l2'].position.z ) : depth / 2;
        const boundingBoxMesh       = this.filterByValue( meshes, 'name', '_bb_obj' ) as Mesh;
        let dragBehavior            = new PointerDragBehavior( { dragPlaneNormal: new Vector3( 0, 0, 0 ) } );
        if( isMoveHorizontal ) {
            dragBehavior = new PointerDragBehavior( { dragAxis: new Vector3( 1, 0, 0 ) } );
        } else {
            if( moveable.horizontal && ! moveable.vertical ) {
                let dragVector  = 'roof' === side ? new Vector3( 0, 0, 1 ) : new Vector3( 1, 0, 0 );
                dragBehavior    = new PointerDragBehavior( { dragAxis: dragVector } );
            } else if( ! moveable.horizontal && moveable.vertical ) {
                dragBehavior = new PointerDragBehavior( { dragAxis: new Vector3( 0, 1, 0 ) } );
            } else if( moveable.horizontal && moveable.vertical ) {
                dragBehavior = new PointerDragBehavior( { dragPlaneNormal: new Vector3( 0, 0, 1 ) } );
                if( this.isPorchSide( side ) && this.isVerticalDistribute() ) {
                    dragBehavior = new PointerDragBehavior( { dragAxis: new Vector3( 1, 0, 0 ) } );
                }
            }
        }
        
        if( this.isMoveAlongFloor( component.name ) ) {
            const boundingBox = boundingBoxMesh.getBoundingInfo().boundingBox;
            
            firstMesh.ellipsoid = new Vector3(
                (boundingBox.maximumWorld.x - boundingBox.minimumWorld.x) / 2,
                (boundingBox.maximumWorld.y - boundingBox.minimumWorld.y) / 2,
                (boundingBox.maximumWorld.z - boundingBox.minimumWorld.z) / 2
            );
            firstMesh.ellipsoidOffset = boundingBox.centerWorld.subtract( firstMesh.position );

            dragBehavior = new PointerDragBehavior( { dragPlaneNormal: new Vector3( 0, 1, 0 ) } );
            dragBehavior.moveAttached = false;
            dragBehavior.updateDragPlane = false;

            dragBehavior.onDragStartObservable.add( e => {
                _this.removeEditButton();
                const boundingBox = boundingBoxMesh.getBoundingInfo().boundingBox;
            
                firstMesh.ellipsoid = new Vector3(
                    (boundingBox.maximumWorld.x - boundingBox.minimumWorld.x) / 2,
                    (boundingBox.maximumWorld.y - boundingBox.minimumWorld.y) / 2,
                    (boundingBox.maximumWorld.z - boundingBox.minimumWorld.z) / 2
                );
                firstMesh.ellipsoidOffset = boundingBox.centerWorld.subtract( firstMesh.position );
            } );

            dragBehavior.onDragObservable.add( ( eventData ) => {
                firstMesh.moveWithCollisions( eventData.delta );
            } );
        }

        if( firstMesh.behaviors.length === 0 ) {
            firstMesh.addBehavior( dragBehavior );
        } else {
            if( undefined === firstMesh.behaviors.find( b => b.name === 'PointerDrag' ) ) {
                firstMesh.addBehavior( dragBehavior );
            }
        }
        
        dragBehavior.validateDrag = targetPosition => {
            _this._isDrag = true;
            if( ! _this._allowDrag || _this._activeDrag !== key ) {
                _this.removeLine();
                _this.removeNoticeText();

                return false;
            }

            const sidesIgnore = ['roof_left', 'roof_right', 'roof']; 
            if( _this._isNewMovingComponent && -1 === sidesIgnore.indexOf( component['side'] ) ) {
                const previousPosition = firstMesh.position.clone(); 
                const distance = Vector3.Distance( previousPosition, targetPosition );
                const steps = Math.ceil( distance / 0.1 ); 
                let interpolatedPosition = previousPosition.clone();
                let isValid = true;
                for( let i = 1; i <= steps; i++ ) {
                    Vector3.LerpToRef( previousPosition, targetPosition, i / steps, interpolatedPosition );
                    let newArgs = { key, targetPosition: interpolatedPosition, component, componentWidth, componentHeight, firstMesh, anchor };
                    if( ! _this.validateMoveComponentV2( newArgs ) ) {
                        isValid = false;
                        break;
                    }
                }
                if( ! isValid && _this._positionValidOnMove ) {
                    if( 'floor' === side ) {
                        targetPosition.z = _this._positionValidOnMove.z;
                        targetPosition.x = _this._positionValidOnMove.x;
                    } else if( helpers.hasKeyInArray( zSide, component['side'] ) ) {
                        targetPosition.z = _this._positionValidOnMove.z;
                    } else {
                        targetPosition.x = _this._positionValidOnMove.x;
                    }
                    
                    if( this.applyFixedHeightWindows( component['side'], component['type'], component['name'] ) ) {
                        targetPosition.y = this.getWindowsFixedPositionY( component['type'], componentHeight, component['side'] )
                    } else {
                        targetPosition.y = _this._positionValidOnMove.y;
                    }
                    _this.removeLine();
                    _this.removeNoticeText();
                } else {
                    if( this.applyFixedHeightWindows( component['side'], component['type'], component['name'] ) ) {
                        if( this.isFollowMainDoor() ) {
                            if( 0 !== this._doorFixedTarget.length )
                                targetPosition.y = this.getWindowsFixedPositionY( component['type'], componentHeight, component['side'] );
                        } else {
                            targetPosition.y = this.getWindowsFixedPositionY( component['type'], componentHeight, component['side'] );
                        }
                    }
                }
            } else {
                const args = { key, targetPosition, component, anchor, componentWidth, componentHeight, firstMesh, tanRoofLowerCorner, tanRoofUpperCorner, limitUpperVertical, tanRoofLeftDormer, limitLowerVertical, sidePorchEndX, isMoveHorizontal };
                _this.validateMoveComponent( args )
            }
            return true;
        }

        let positionBeforeDrag = new Vector3( 0, 0, 0 );
        dragBehavior.onDragStartObservable.add( e => {
            this._positionValidOnMove = undefined;
            if( _this._allowDrag && _this._activeDrag === key ) {
                _this._scene.hoverCursor = 'pointer';
            }

            positionBeforeDrag = component.meshes[0].position.clone();
        } );
        
        dragBehavior.onDragObservable.add( e => {
            if( _this._allowDrag && _this._activeDrag === key ) {
                if( helpers.isSeadevSite() ) {
                    this.updateDistanceLines( component );
                } else {
                    this.updateLine( firstMesh, componentHeight, component['side'], anchor, behavior );
                }
                _this.handleHighlightMesh( component['side'], key, component );
            }
        } );

        dragBehavior.onDragEndObservable.add( e => {
            component                   = _this.updateSideComponent( component, positionBeforeDrag );
            _this._isDrag               = false;
            document.body.style.cursor  = 'default';

            if( _this._allowDrag && _this._activeDrag === key ) {
                if( helpers.isSeadevSite() ) {
                    this.updateDistanceLines( component );
                } else {
                    this.updateLine( firstMesh, componentHeight, component['side'], anchor, behavior );
                }
            }
            _this.updateWallComponentsPosition( component['side'], component['key'], component, positionBeforeDrag );
            _this.updatePositionLayoutComponents( component['side'] );
            _this.updateHoleWall( component['side'] );
            _this.updateHoleRoof( component['side'] );
            if( side !== component['side'] ) {
                _this.updateHoleWall( side );
                _this.updateHoleRoof( side );
            }
            _this.updateFrame( component['side'] );
            _this.handleIntersectKickBoard( component['side'] );
            _this.handleIntersectKickBoard( component['side'], 'frame_kick_board' );
            _this.disableHighlightFrame( component['side'] );
            if( this.isMoveAlongFloor( component['name'] ) ) {
                this._componentPicked = key;
                _this.enableHighlightMesh( boundingBoxMesh, new Color4( 0, 0, 0, 1 ) );
                _this.addEditButton( { component, firstMesh, type: behavior, componentName: component.name, componentWidth, componentDepth, componentHeight, side: component["side"] } );
            }
            window.dispatchEvent( new CustomEvent( 'after-drag' ) );
        } );
    }

    private totalBoundingInfo( parent: Mesh ) {
        let min = new Vector3( Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE );
        let max = new Vector3( -Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE );

        const meshes = parent.getChildMeshes();
        const parentPosition = parent.position;

        for (const mesh of meshes) {
            mesh.computeWorldMatrix(true);
            const boundingInfo = mesh.getBoundingInfo();
            const boundingBox = boundingInfo.boundingBox;
    
            const worldMin = boundingBox.minimumWorld;
            const worldMax = boundingBox.maximumWorld;
    
            const adjustedMin = worldMin.subtract( parentPosition );
            const adjustedMax = worldMax.subtract( parentPosition );
    
            min = Vector3.Minimize( min, adjustedMin );
            max = Vector3.Maximize( max, adjustedMax );
        }
    
        return new BoundingInfo( min.add( parentPosition ), max.add( parentPosition ) );
    }

    private enableComponentMoveable() {
        if( this.isMobile() ) {
            return;
        }

        for( let side in this._walls ) {
            if( this.ignoreHandleTackDoor( side ) || 'diagonal_right' === side || ( 'structure_rancher' === this._shedType && ( 'top_front' === side || 'top_back' === side ) ) ) {
                continue;
            }

            for( const key in this._walls[side]['items'] ) {
                this.handleMoveComponent( side, key );
            }
        }
    }

    limitMoveSide = ( targetPosition, sideLimitLeft, sideLimitRight ) => {
        let positionXZ = targetPosition;
        if( sideLimitLeft > targetPosition ) {
            positionXZ = sideLimitLeft;
        }
        if( sideLimitRight < targetPosition ) {
            positionXZ = sideLimitRight;
        }
        return positionXZ;
    }

    enableHighlightMesh = ( mesh, boderColor, overlayColor: any = false ) => {
        if( 'workbench' !== mesh.metadata.type && 'shelf' !== mesh.metadata.type && 'dormer' !== mesh.metadata.type ) {
            mesh.enableEdgesRendering();
        }
        mesh.edgesColor = boderColor;
        mesh.edgesWidth = 5;

        if( overlayColor ) {
            mesh.visibility     = 0.4;
            mesh.renderOverlay  = true;
            mesh.overlayColor   = overlayColor;
            mesh.material.alpha = 1;
        } else {
            mesh.renderOverlay  = false;
            mesh.visibility     = 1;
            mesh.material.alpha = 0;
        }
    }

    disableHighlightMesh = ( mesh ) => {
        if( mesh ) {
            mesh.visibility = 0;
            mesh.disableEdgesRendering();
        }
    }

    handleHighlightMesh( side, key, component ) {
        if( 'floor' === side ) return;
        const _this                 = this;
        const behavior              = this.getBehavior( component.name, component.type );
        let position                = component.meshes[0].position;
        const position2D            = this.getPosition2D( side, component.meshes, position, component.type, component.name );
        const childSize             = this.getChildSize( side, key, 'flower_box' );
        const childHeight           = 'windows' === component.type ? childSize.height : 0;
        const childWidth            = 'windows' === component.type ? childSize.width : 0;
        const componentWidth        = 'windows' === component.type ? this.getComponentWidth( component.meshes, behavior, component.name ) : 0;
        const tmpWidth              = childWidth > componentWidth ? ( childWidth - componentWidth ) / 2 : 0;
        const limitSpacing          = this.getHorizontalSpacing( component.type );

        const lastPointVertical     = position2D.vertical.lastPoint;
        const firstPointVertical    = position2D.vertical.firstPoint;
        const lastPointHorizontal   = position2D.horizontal.lastPoint + limitSpacing;
        const firstPointHorizontal  = position2D.horizontal.firstPoint - limitSpacing;
        const startXForLeg          = position2D.horizontal.firstPoint;
        const endXForLeg            = position2D.horizontal.lastPoint;

        const cLastPointVertical    = position2D.vertical.firstPoint;
        const cFirstPointVertical   = position2D.vertical.firstPoint - childHeight;
        const cLastPointHorizontal  = position2D.horizontal.lastPoint + tmpWidth + limitSpacing;
        const cFirstPointHorizontal = position2D.horizontal.firstPoint - tmpWidth - limitSpacing;

        const boundingBox           = this.filterByValue( component.meshes, 'name', '_bb_obj' ) as Mesh;
        const childComponents       = this.getComponentChildByParentKey( key, side );
        const newMeshes             = this.getAllChildMeshes( childComponents );
        const childMeshHighlight    = this.filterMeshes( newMeshes, 'name', '_bb_obj' );
        const face                  = this.getComponentFace( behavior );
        let disableHighlight        = true;
        const components            = this.getComponentSameSide( side, face );

        for( const compKey in components ) {
            if( key !== compKey ) {
                const oldComponent = components[compKey];
                if( 'flower_box' === oldComponent.type && key === oldComponent.parent ) continue;
                if( 'ramp' !== oldComponent.type && 'flower_box' !== oldComponent.type ) {
                    const componentWidth   = this.getComponentWidth( oldComponent.meshes, oldComponent.type, oldComponent.name );
                    const childOldSize      = this.getChildSize( side, compKey, 'flower_box' );
                    const childOldHeight    = childOldSize.height;
                    const childOldWidth     = childOldSize.width;
                    const tmpOldWidth       = childOldWidth > componentWidth ? ( childOldWidth - componentWidth ) / 2 : 0;

                    const lastPointVerticalOld      = oldComponent.position2D.vertical.lastPoint;
                    const firstPointVerticalOld     = oldComponent.position2D.vertical.firstPoint;
                    const lastPointHorizontalOld    = oldComponent.position2D.horizontal.lastPoint;
                    const firstPointHorizontalOld   = oldComponent.position2D.horizontal.firstPoint;
                    const meshHighlight             = this.filterByValue( oldComponent.meshes, 'name', '_bb_obj' ) as Mesh;
                    const cLastPointVerticalOld     = oldComponent.position2D.vertical.firstPoint;
                    const cFirstPointVerticalOld    = oldComponent.position2D.vertical.firstPoint - childOldHeight;
                    const cLastPointHorizontalOld   = oldComponent.position2D.horizontal.lastPoint + tmpOldWidth;
                    const cFirstPointHorizontalOld  = oldComponent.position2D.horizontal.firstPoint - tmpOldWidth;
                    const oldChildComponents        = this.getComponentChildByParentKey( compKey, side );
                    const oldMeshes                 = this.getAllChildMeshes( oldChildComponents );
                    const oldChildMeshHighlight     = this.filterMeshes( oldMeshes, 'name', '_bb_obj' );

                    const child2D = {
                        horizontal : { cFirstPointHorizontal, cLastPointHorizontal, cFirstPointHorizontalOld, cLastPointHorizontalOld },
                        vertical : { cFirstPointVertical, cLastPointVertical, cFirstPointVerticalOld, cLastPointVerticalOld }
                    }
                    const parent2D = {
                        horizontal : { firstPointHorizontal, lastPointHorizontal, firstPointHorizontalOld, lastPointHorizontalOld },
                        vertical : { firstPointVertical, lastPointVertical, firstPointVerticalOld, lastPointVerticalOld }
                    }

                    if( this.isIntersectComponent( parent2D, child2D ) ) {
                        disableHighlight = false;
                        if( boundingBox && meshHighlight ) {
                            _this.enableHighlightMesh( meshHighlight, new Color4( 1, 1, 1, 1 ), Color3.FromHexString( '#F83636' ) );
                            _this.enableHighlightMesh( boundingBox, new Color4( 1, 1, 1, 1 ), Color3.FromHexString( '#F83636' ) );
                        }

                        childMeshHighlight.forEach( e => {
                            if( e.metadata && 'shutters' !== e.metadata.type ) {
                                _this.enableHighlightMesh( e, new Color4( 1, 1, 1, 1 ), Color3.FromHexString( '#F83636' ) );
                            }
                        } );
                        oldChildMeshHighlight.forEach( e => {
                            if( e.metadata && 'shutters' !== e.metadata.type ) {
                                _this.enableHighlightMesh( e, new Color4( 1, 1, 1, 1 ), Color3.FromHexString( '#F83636' ) );
                            }
                        } );
                    } else {
                        if( meshHighlight ) {
                            this.disableHighlightMesh( meshHighlight );
                        }

                        oldChildMeshHighlight.forEach( e => _this.disableHighlightMesh( e ) );
                    }
                }
            }
        }

        if( ! this._isInterior ) {
            let isIntersect = false;
            const frames    = this.getFrameBySide( side );
            for( let i = 0; i < frames.length; i++ ) {
                const frame                 = frames[i] as Mesh;
                const position              = - 1 !== zSide.indexOf( side ) ? frame.position.z : frame.position.x;
                const firstHorizontalOld    = position - this._frameWidth / 2;
                const lastHorizontalOld     = position + this._frameWidth / 2;
                const firstVerticalOld      = 0;
                const lastVerticalOld       = this.getWallHeight();
                const isValidPosition       = this.isValidComponent( startXForLeg, endXForLeg, firstHorizontalOld, lastHorizontalOld, firstPointVertical, lastPointVertical, firstVerticalOld, lastVerticalOld );
                let groupId                 = 0;
                let frameMat                = this._frameMat;
                if( ! frameMat ) {
                    frameMat = this._scene.getMaterialByName( 'carport_frame_mat' ) as StandardMaterial;
                }

                if( ! isValidPosition ) {
                    isIntersect         = true;
                    disableHighlight    = false;
                    groupId             = 1;
                    frameMat            = this._highlightMat;
                    if( boundingBox ) {
                        _this.enableHighlightMesh( boundingBox, new Color4( 1, 1, 1, 1 ), Color3.FromHexString( '#F83636' ) );
                    }
                }

                this.frameUpdateMaterial( frame, frameMat, groupId );
            }
        
            const wall = this.getWallBySide( side );
            if( wall ) {
                if( isIntersect ) {
                    wall.visibility = 0.7;
                } else {
                    wall.visibility = 1;
                }
            }
        }

        const types = [ 'door', 'windows', 'vent' ];
        if( this.is2StoryLeanTo() && - 1 !== types.indexOf( component.type ) ) {
            if( this.isEnableLeanTo() ) {
                let trimMesh = this.getTrimMesh( side );
                if( trimMesh ) {
                    if( lastPointVertical < this._bones['lean_to_f_r4'].position.y ) {
                        trimMesh.setEnabled( this.isIntersectComponentWithTrim( startXForLeg, endXForLeg, firstPointVertical, lastPointVertical ) );
                    } else {
                        trimMesh.setEnabled( false );
                    }
                }
            }

            
            let secondFloor = this.getMeshByName( 'second_floor_obj' );
            if( secondFloor ) {
                const isIntersect       = this.isIntersectComponentWithFloor( side, secondFloor, startXForLeg, endXForLeg, firstPointVertical, lastPointVertical );
                secondFloor.material    = isIntersect ? this._highlightMat : this._trimMat;
            }
        }

        if( _this.isMobile() ) {
            if( disableHighlight ) {
                this.enableHighlightMesh( boundingBox, new Color4( 0, 0, 0, 1 ) );
                childMeshHighlight.forEach( e => this.disableHighlightMesh( e ) );
            }
        } else {
            if( disableHighlight ) {
                this.disableHighlightMesh( boundingBox );
                childMeshHighlight.forEach( e => this.disableHighlightMesh( e ) );
            }
        }
    }

    getDistanceHorizontal( side, component ) {
        if( undefined === component.meshes[0] ) {
            return 0;
        }

        const positionX = parseFloat( component.meshes[0].position.x );
        const positionZ = parseFloat( component.meshes[0].position.z );

        return this.getHorizontalSpace( side, this.getWidth(), this.getDepth(), positionX, positionZ );
    }

    getDistanceBottom( component ) {
        if( undefined === component.meshes[0] ) {
            return 0;
        }

        const behavior      = this.getBehavior( component.name, component.type );
        const positionY     = parseFloat( component.meshes[0].position.y );
        const compVertical  = this.getComponentHeight( component.meshes, component.type );
        const anchor        = this._componentTypeSettings[behavior]['anchor'];

        return 'ground' === anchor.vertical ? compVertical / 2 + positionY : positionY - compVertical;
    }

    getHorizontalSpace = ( side, width, length, x, z ) => {
        let tmpWidth    = width / 2;
        let tmpPosition = x;

        if( 'saltbox' === this.getRoofShape() ) {
            if( 'front' === side || 'back' === side ) {
                if( this._bones['b_f_r4'] ){
                    tmpWidth = Math.abs( this._bones['b_f_r4'].position.x );
                }else if( 'structure_quaker_wannabuysheds' === this._shedType ){
                    tmpWidth = Math.abs( this._bones['b_f_r1'].position.x );
                }
            }
        }

        if( this.isSidePorch() || ( this.isCornerPorchLeft() && 'front' === side ) ){
            tmpWidth = this.getWidth() / 2;
        }

        const sides = [ 'left', 'right', 'top_left', 'top_right', 'roof', 'dormer', 'roof_left', 'roof_right', 'inner_left', 'left_front', 'left_back', 'front_inner_porch', 'inner_right', 'lean_to_right' ];
        if( -1 !== sides.indexOf( side ) ) {
            tmpWidth    = length / 2;
            tmpPosition = z;
        }

        if( this.isCornerPorchLeft() ){
            if( 'left' === side ){
                tmpWidth = this.getDepth() / 2 - constants.cornerDepth;
            }else if( 'inner_left' === side ){
                tmpWidth = this.getDepth() / 2;
            }else if( 'inner_front' === side ){
                tmpWidth = - this.getWidth() / 2 + constants.cornerWidth;
            }
        }

        if( this.isDeluxeCornerLeftPorch() ) {
            switch( side ) {
                case 'front':
                    tmpWidth = - this.getWidth() / 2 + this.deluxePorchGetFrontWidth();
                    break;
                case 'inner_right':
                    tmpWidth = this._bones['b_f_r5'].position.z;
                    break;
                case 'inner_front':
                    tmpWidth = this._bones['b_f_r1'].position.x;
                    break;
                case 'left':
                    tmpWidth = this._bones['b_f_l1'].position.z;
                    break;
                case 'right':
                    tmpWidth = this._bones['b_f_r1'].position.z;
                    break;
            }
        }

        if( this.isMiddlePorch() ) {
            switch ( side ) {
                case 'left_inner_porch':
                case 'right_inner_porch':
                    tmpWidth    = Math.abs( x );
                    tmpPosition = this.getWidth() / 2 - constants.middleDepth;
                    break;
                case 'front_inner_porch':
                    tmpWidth = constants.middleWidth / 2;
                    break;
                case 'left_back':
                    tmpWidth = - constants.middleWidth / 2;
                    break;
            }
        } else if( 'structure_ranch_cabin' === this._shedType || this.isAframeSingleGable() ) {
            switch ( side ) {
                case 'inner_back':
                    tmpWidth    = Math.abs( x );
                    tmpPosition = this.getWidth() / 2 - constants.cabinPorchDepth;
                    break;
                case 'inner_left':
                    tmpWidth    = this.getDepth() / 2 - this._cabinWidth;
                    break;
            }
        } else if( this.is2StoryLeanTo() ) {
            switch ( side ) {
                case 'lean_to_front':
                    tmpWidth    = this._bones['lean_to_f_r2'].position.x - x;
                    tmpPosition = 0;
                    break;
                case 'lean_to_back':
                    tmpWidth    = this._bones['lean_to_b_r2'].position.x - x;
                    tmpPosition = 0;
                    break;
            }
        }

        return 0 === tmpPosition ? tmpWidth : tmpWidth - tmpPosition;
    }

    /**
     * Update distance lines for a component.  
     */
    updateDistanceLines( component ) {
        const { meshes, side, type, name } = component;
        const behavior = this.getBehavior( name, type );

        const disallowSheds = [ 'structure_monitorbarns_helmuth' ];
        const disallowSides = [ 'roof', 'roof_left', 'roof_right', 'diagonal_right' ]; // TODO: distance lines should work with special sides
        const isDoorBlocked = 'true' === this.getSettingData( 'block_off_centered' ) && 'door' === behavior;
        if( disallowSheds.includes( this._shedType ) || disallowSides.includes( side ) || isDoorBlocked ) {
            return;
        }
        
        let linePoints;
        if( this.isMoveAlongFloor( name ) && side === 'floor' ) {
            linePoints = this.calculateFloorLinePoints( component );
        } else {
            linePoints = this.calculateWallLinePoints( component );
        }

        if ( ! linePoints ) return;

        this.createDistanceLines( component, linePoints );
    }

    /**
     * Calculates the points for wall distance lines.
     */
    calculateWallLinePoints( component ) {
        const { meshes, side, type, name } = component;

        const behavior = this.getBehavior( name, type );
        const anchor = this._componentTypeSettings[behavior]['anchor'];

        const wallBoneName = this.getWallBones( side );
        const leftEndPoint = this._bones[wallBoneName?.left]?.position;
        const rightEndPoint = this._bones[wallBoneName?.right]?.position; 
        const topEndPoint = this._bones[wallBoneName?.top]?.position;

        if( ! leftEndPoint || ! rightEndPoint ) return null;

        const firstMesh = meshes[0];
        const componentPosition = firstMesh.position;
        const componentWidth = this.getComponentWidth( meshes, type, name );
        const componentHeight = this.getComponentHeight( meshes, type );

        let horizontalY = componentPosition.y - ( componentHeight / 2 );
        if( 'breakerbox' === type || 'switch' === type || 'outlet' === type ) {
            horizontalY = componentPosition.y;
        }
        if( 'ground' === anchor.vertical ) {
            horizontalY = componentPosition.y + componentHeight / 2;

            if( 'workbench' === type ) {
                horizontalY = 1.5;
            }
        }

        // Determine if the wall is aligned along X or Z axis
        const wallDirection = this.getVectorDirection( rightEndPoint, leftEndPoint );
        const isXAxis = Math.abs( wallDirection.x ) > Math.abs( wallDirection.z );
        const directionSign = isXAxis ? Math.sign( wallDirection.x ) : Math.sign( wallDirection.z );
        
        // Compute start positions
        const horizontalStartOffset = ( componentWidth / 2 ) * directionSign;
        const horizontalEndLeft = isXAxis ? leftEndPoint.x : leftEndPoint.z;
        const horizontalEndRight = isXAxis ? rightEndPoint.x : rightEndPoint.z;

        // Assign horizontalLeft and horizontalRight dynamically
        const horizontalLeft = {
            start: new Vector3(
                isXAxis ? componentPosition.x - horizontalStartOffset : componentPosition.x,
                horizontalY,
                isXAxis ? componentPosition.z : componentPosition.z - horizontalStartOffset
            ),
            end: new Vector3(
                isXAxis ? horizontalEndLeft : leftEndPoint.x,
                horizontalY,
                isXAxis ? leftEndPoint.z : horizontalEndLeft
            )
        };

        const horizontalRight = {
            start: new Vector3(
                isXAxis ? componentPosition.x + horizontalStartOffset : componentPosition.x,
                horizontalY,
                isXAxis ? componentPosition.z : componentPosition.z + horizontalStartOffset
            ),
            end: new Vector3(
                isXAxis ? horizontalEndRight : rightEndPoint.x,
                horizontalY,
                isXAxis ? rightEndPoint.z : horizontalEndRight
            )
        };

        let topEndY;

        const walls = this.getWallInfo();
        const wall = walls?.find( wall => wall.side === side );
        if( wall ) {
            const wallPoints = this.get3DPointsFromWall( wall );
            if( wallPoints.length ) 
                topEndY = Math.max( ...wallPoints.map( point => point._y ) );
        } else {
            topEndY = topEndPoint?.y || this.getWallHeightBySide( side );
        }
        
        const topStartY = componentPosition.y;

        const verticalTop = {
            start: new Vector3( componentPosition.x, topStartY, componentPosition.z ),
            end: new Vector3( componentPosition.x, topEndY, componentPosition.z )
        };

        const bottomStartY = componentPosition.y - componentHeight;
        const bottomEndY = this.isPorchSide( side ) ? this.getPorchBottom() : 0;

        const verticalBottom = {
            start: new Vector3( componentPosition.x, bottomStartY, componentPosition.z ),
            end: new Vector3( componentPosition.x, bottomEndY, componentPosition.z )
        };

        return { horizontalLeft, horizontalRight, verticalTop, verticalBottom };
    }

    /**
     * Calculates the points for floor distance lines. 
     */
    calculateFloorLinePoints( component ) {
        const { meshes, side, type, name } = component;

        if( ! this.isMoveAlongFloor( name ) || side !== 'floor' ) return null;

        const wallBones = this.getWallBones( side );
        const leftEndPoint = this._bones[wallBones?.left]?.position;
        const rightEndPoint = this._bones[wallBones?.right]?.position;
        const topEndPoint = this._bones[wallBones?.top]?.position;
        const bottomEndPoint = this._bones[wallBones?.bottom]?.position;

        if( ! leftEndPoint || ! rightEndPoint || ! topEndPoint || ! bottomEndPoint ) return null;

        const firstMesh = meshes[0];
        const componentPosition = firstMesh.position;

        const boundingBoxMesh = this.filterByValue( meshes, 'name', '_bb_obj' ) as Mesh;
        const firstMeshBoundingBox = boundingBoxMesh.getBoundingInfo();
        const firstMeshMin = firstMeshBoundingBox.boundingBox.minimumWorld;
        const firstMeshMax = firstMeshBoundingBox.boundingBox.maximumWorld;

        const componentWidth = firstMeshMax.x - firstMeshMin.x;
        const componentDepth = firstMeshMax.z - firstMeshMin.z;

        const horizontalLeft = {
            start: new Vector3( componentPosition.x + ( componentWidth / 2 ), componentPosition.y, componentPosition.z ),
            end: new Vector3( leftEndPoint.x, componentPosition.y, componentPosition.z )
        };

        const horizontalRight = {
            start: new Vector3( componentPosition.x - ( componentWidth / 2 ), componentPosition.y, componentPosition.z ),
            end: new Vector3( rightEndPoint.x, componentPosition.y, componentPosition.z )
        };
        
        const verticalTop = {
            start: new Vector3( componentPosition.x, componentPosition.y, componentPosition.z - ( componentDepth / 2 ) ),
            end: new Vector3( componentPosition.x, componentPosition.y, topEndPoint.z )
        };

        const verticalBottom = {
            start: new Vector3( componentPosition.x, componentPosition.y, componentPosition.z + ( componentDepth / 2 ) ),
            end: new Vector3( componentPosition.x, componentPosition.y, bottomEndPoint.z )
        };

        return { horizontalLeft, horizontalRight, verticalTop, verticalBottom };
    }

    /**
     * Generate horizontal distance lines for components.
     */
    createDistanceLines( component, linePoints ) {
        const { meshes, side, type, name } = component;
        const firstMesh = meshes[0];
        const behavior = this.getBehavior( name, type );

        if( ! this.isMoveAlongFloor( name ) || side !== 'floor' ) delete linePoints?.verticalTop;
        
        const isExcludeVerticalLine = ( ( ! this.isMoveAlongFloor( name ) || side !== 'floor' ) ) && ( 'dormer' === side || 'door' === behavior || this.isInteriorComponent( behavior ) );
        if( isExcludeVerticalLine ) {
            delete linePoints?.verticalTop;
            delete linePoints?.verticalBottom;
        }

        const horizontalLeft = linePoints?.horizontalLeft ?? null;
        const horizontalRight = linePoints?.horizontalRight ?? null;
        const verticalTop = linePoints?.verticalTop ?? null; 
        const verticalBottom = linePoints?.verticalBottom ?? null; 

        if( horizontalLeft ) {
            this._lineHorizontalLeftOptions.points = [horizontalLeft.start, horizontalLeft.end];
            this._lineHorizontalLeftOptions.instance = this._lineHorizontalLeft;
            this._lineHorizontalLeft = MeshBuilder.CreateLines( 'lineHorizontalLeft', this._lineHorizontalLeftOptions );

            this.updateDistanceLineText( horizontalLeft, this._distanceTextHolderHorizontalLeft, this._distanceTextHorizontalLeft, component );
            this.updateDistanceLineArrow( horizontalLeft, this._arrowHorizontalLeftStart, this._arrowHorizontalLeftEnd );
        }
        
        if( horizontalRight ) {
            this._lineHorizontalRightOptions.points = [horizontalRight.start, horizontalRight.end];
            this._lineHorizontalRightOptions.instance = this._lineHorizontalRight;
            this._lineHorizontalRight = MeshBuilder.CreateLines( 'lineHorizontalRight', this._lineHorizontalRightOptions );

            this.updateDistanceLineText( horizontalRight, this._distanceTextHolderHorizontalRight, this._distanceTextHorizontalRight, component );
            this.updateDistanceLineArrow( horizontalRight, this._arrowHorizontalRightStart, this._arrowHorizontalRightEnd );
        }
        
        if( verticalTop ) {
            this._lineVerticalTopOptions.points = [verticalTop.start, verticalTop.end];
            this._lineVerticalTopOptions.instance = this._lineVerticalTop;
            this._lineVerticalTop = MeshBuilder.CreateLines( 'lineVerticalTop', this._lineVerticalTopOptions );

            this.updateDistanceLineText( verticalTop, this._distanceTextHolderVerticalTop, this._distanceTextVerticalTop, component );
            this.updateDistanceLineArrow( verticalTop, this._arrowVerticalTopStart, this._arrowVerticalTopEnd );
        }

        if( verticalBottom ) {
            this._lineVerticalBottomOptions.points = [verticalBottom.start, verticalBottom.end];
            this._lineVerticalBottomOptions.instance = this._lineVerticalBottom;
            this._lineVerticalBottom = MeshBuilder.CreateLines( 'lineVerticalBottom', this._lineVerticalBottomOptions );

            this.updateDistanceLineText( verticalBottom, this._distanceTextHolderVerticalBottom, this._distanceTextVerticalBottom, component );
            this.updateDistanceLineArrow( verticalBottom, this._arrowVerticalBottomStart, this._arrowVerticalBottomEnd );
        }
    }

    /**
     * Update line text according to component position.
     */
    updateDistanceLineText( position, textHolderMesh, textMesh, component ) {
        const startPosition = position?.start ?? null;
        const endPosition = position?.end ?? null;

        if( ! startPosition || ! endPosition ) return;

        // Render dístance text holder
        this.renderDistanceTextHolder( startPosition, endPosition, textHolderMesh, component );

        // Render calculated distance text
        this.renderDistanceText( startPosition, endPosition, textMesh );
    }

    /**
     * Renders distance text holder for a component that located in the middle between startPosition and endPosition.
     */
    renderDistanceTextHolder( startPosition, endPosition, textHolderMesh, component ) {
        const { side, type, name } = component;
        const behavior = this.getBehavior( name, type );

        const direction = this.getVectorDirectionBySide( side );
        if( ! direction ) return;

        const isXAxis = Math.abs( direction.x ) > Math.abs( direction.z );

        const componentRotation = this.calculateComponentRotation( side, behavior );
        let isFacingFront = componentRotation.y === 0;
        let isFacingRight = componentRotation.y === Math.PI / 2;
        if( this._isInterior ) {
            isFacingFront = componentRotation.y === Math.PI;
            isFacingRight = componentRotation.y === 3 * Math.PI / 2;
        }

        textHolderMesh.visibility = 1;
        textHolderMesh.position = Vector3.Lerp(startPosition, endPosition, 0.5);
        
        // Set mesh rotation & position based on wall direction
        textHolderMesh.rotation.x = 3 * Math.PI / 2;
        if( isXAxis ) { // Wall runs along X-axis (Front/Back)
            textHolderMesh.rotation.y = isFacingFront ? Math.PI : 2 * Math.PI;
        } else { // Wall runs along Z-axis (Left/Right)
            textHolderMesh.rotation.y = isFacingRight ? 3 * Math.PI / 2 : Math.PI / 2;
        }
        
        if( this._isInterior ) {
            textHolderMesh.rotation.y += Math.PI;
        }

        if( this.isMoveAlongFloor( name ) && side === 'floor' ) {
            textHolderMesh.rotation.x = 0;
            textHolderMesh.rotation.y = Math.PI;
            textHolderMesh.rotation.z = 0;
        }
    }

    /**
     * Renders distance text for a component that calculated from startPosition to endPosition.
     */
    renderDistanceText( startPosition, endPosition, textMesh ) {
        const distance: any = this.calculateDistanceSpace( startPosition, endPosition );
        const space = distance?.converted?.replaceAll( ' ', '' ) || '0';

        const font = helpers.isShowMeasurementsInDecimal() && helpers.getMeasurementsDecimalFormat() === "fraction"
            ? "70px monospace"
            : this._font;
    
        textMesh.drawText( space, null, null, font, this._color, this._background, true );
    }

    /**
     * Calculate distance of 2 points base on their positions
     */
    calculateDistanceSpace( startPosition, endPosition ) {
        if( ! startPosition || ! endPosition ) return null;

        const direction = this.getVectorDirection( startPosition, endPosition );
        const isXAxis = Math.abs( direction.x ) > Math.abs( direction.z );

        const space = Vector3.Distance( startPosition, endPosition );
        const unitSpace: string = helpers.convertUnit( space );

        return { original: space, converted: unitSpace };
    }

    /**
     * Update arrow mesh based on component position and direction.
     */
    updateDistanceLineArrow( position, arrowStartMesh, arrowEndMesh ) {
        const startPosition = position?.start ?? null;
        const endPosition = position?.end ?? null;

        if( ! startPosition || ! endPosition ) return;

        const arrowdDirection = endPosition.subtract( startPosition ).normalize();
        const arrowReverseDirection = startPosition.subtract( endPosition ).normalize();
        
        const offsetAmount = 0.118; // Offset arrow position to the edge of lines
        const startOffsetPosition = startPosition.subtract( arrowReverseDirection.scale( offsetAmount ) );
        const endOffsetPosition = endPosition.subtract( arrowdDirection.scale( offsetAmount ) );

        // Set arrow positions and visibility
        arrowStartMesh.visibility = 1;
        arrowStartMesh.position = startOffsetPosition;

        arrowEndMesh.visibility = 1;
        arrowEndMesh.position = endOffsetPosition;

        arrowStartMesh.rotationQuaternion = this.calculateArrowRotation( arrowReverseDirection );
        arrowEndMesh.rotationQuaternion = this.calculateArrowRotation( arrowdDirection );
    }

    /**
     * Calculate the rotation of arrows based on their orientation and position.
     */
    calculateArrowRotation( direction ) {
        let rotation = Quaternion.Identity();
        
        let referenceVector = Vector3.Up();
        const axis = Vector3.Cross( referenceVector, direction ).normalize();
        const angle = Math.acos( Vector3.Dot( referenceVector, direction ) );
        
        if( ! isNaN( angle ) && axis.length() > 0 ) {
            rotation = Quaternion.RotationAxis( axis, angle );
        }

        if( Math.abs( direction.y ) > 0.9999 && direction.y < 0 ) { // Vertical downward arrow
            rotation = rotation.multiply( Quaternion.RotationAxis( Vector3.Right(), Math.PI ) );
        } else { // Vertical upward arrow
            rotation = rotation.multiply( Quaternion.RotationAxis( Vector3.Right(), 2 * Math.PI ) );
        }

        return rotation;
    }

    /**
     * Get direction normalized vector by side.
     */
    getVectorDirectionBySide( side ) {
        const wallBones = this.getWallBones( side );
        const wallLeftPoint = this._bones[wallBones?.left]?.position;
        const wallRightPoint = this._bones[wallBones?.right]?.position;
    
        if( ! wallLeftPoint || ! wallRightPoint ) return null;
    
        return new Vector3(
            wallRightPoint.x - wallLeftPoint.x,
            wallRightPoint.y - wallLeftPoint.y,
            wallRightPoint.z - wallLeftPoint.z
        ).normalize();
    }

    /**
     * Get direction normalized vector.
     */
    getVectorDirection( start: Vector3, end: Vector3 ) {
        return start.subtract( end ).normalize();
    }

    getHeightForTextBySide = ( side ) => {
        if( this.is2StoryLeanTo() ) {
            if( helpers.hasKeyInArray( [ 'front', 'back', 'top_front', 'top_back' ], side ) ) {
                return this.getBuildingHeight();
            } else if( helpers.hasKeyInArray( [ 'left', 'right', 'top_left', 'top_right' ], side ) ) {
                return this._bones['b_b_l2'].position.y;
            }
        }

        if( 'structure_country_cottage' === this._shedType && 'left' === side ) {
            return this.getBuildingHeight();
        }

        if( 'mainus_quaker' === this._shedType && 'right' === side ) {
            return this.getWallHeight() - 1 - this._sizeErrorValue;
        }

        if( 'structure_lean_to_with_front_porch' === this._shedType && 'left' === side ) {
            return this._middleHeight - this._sizeErrorValue;
        }

        if( 'lean_to' === this._shedType && 'left' === side ) {
            return this.getWallHeight() + this.getRoofPitchHeight() - this._sizeErrorValue;
        }

        if( 'structure_classic_aframe_dormer' === this._shedType && ( 'left' === side || 'front' === side || 'back' === side ) ) {
            return this.getBuildingHeight();
        }

        if( 'left' === side || 'right' === side ) {
            return this.getWallHeight() - this._sizeErrorValue;
        }

        if( this.isSpecialShed() || 'porch' == side || 'back' === side ) {
            return this.getBuildingHeight();
        }

        return this.getWallHeight() - this._sizeErrorValue;
    }
    
    private updateLine( firstMesh, componentHeight, side, anchor, type ) {
        if( 'structure_monitorbarns_helmuth' === this._shedType ) {
            return;
        }

        if( 'true' === this.getSettingData( 'block_off_centered' ) && 'door' === type ) {
            return;
        }

        if( 'roof' === side || 'roof_left' === side || 'roof_right' === side || 'diagonal_right' === side || 'floor' === side ) return;
        
        const componentWidth = this.getComponentWidth( firstMesh._children, type, firstMesh.metadata.name );

        const positionX = parseFloat( firstMesh.position.x );
        const positionY = parseFloat( firstMesh.position.y );
        const positionZ = parseFloat( firstMesh.position.z );
        let width       = this.isSidePorch() ? this.getWidth() - this._porchWidth : this.getWidth();
        let depth       = this.getDepth();
        const height    = this.getWallHeightBySide( side );

        if( this.isSidePorch() || ( this.isCornerPorchLeft() && 'front' === side  ) ){
            width = this.getWidth() - this._porchWidth
        }

        if( this.isCornerPorchLeft() ){
            if( 'left' === side ){
                depth = this.getDepth() - constants.cornerDepth;
            }else if( 'inner_left' === side ){
                depth = constants.cornerDepth;
            }else if( 'inner_front' === side ){
                width = constants.cornerWidth;
            }
        }
        
        if( this.isDeluxeCornerLeftPorch() ) {
            switch( side ) {
                case 'front':
                    width = this.deluxePorchGetFrontWidth();
                    break;
                case 'inner_right':
                    width = constants.deluxeDepth;
                    break;
                case 'inner_front':
                    width = constants.deluxeDepth;
                    break;
                case 'left':
                    width = this.getWidth() - constants.deluxeDepth;
                    break;
                case 'right':
                    width = this.getWidth() - 3 * constants.deluxeDepth;
                    break;
            }
        }

        if( this.is2StoryLeanTo() ) {
            switch( side ) {
                case 'lean_to_front':
                case 'lean_to_back':
                    width = atticModel.leanToWidth;
                    break;
            }
        }

        let x1  = 0;
        let x2  = x1 = positionX;
        let z1  = depth / 2;
        let z2  = positionZ;
        let y   = 'breakerbox' === type || 'switch' === type || 'outlet' === type ? positionY : positionY - ( componentHeight / 2 );

        let xNegative   = positionX;
        let xPositive   = positionX;
        let zNegative   = positionZ + ( componentWidth + 0.3 ) / 2;
        let zPositive   = positionZ - ( componentWidth + 0.3 ) / 2;
        
        if( 'ground' === anchor.vertical ) {
            y = componentHeight / 2 + positionY;

            if( 'workbench' === type ){
                y = 1.5;
            }
        }
        if( 'front' === side || 'top_front' === side || 'lean_to_front' === side || 'back' === side || 'top_back' === side || 'lean_to_back' === side || 'inner_front' === side || this.isPorchSide( side ) || 'right_inner_porch' === side || 'left_inner_porch' === side || 'inner_back' === side ) {
            x1          = width / 2;
            z1          = positionZ;
            
            xNegative   = positionX + ( componentWidth + 0.3 ) / 2;
            xPositive   = positionX - ( componentWidth + 0.3 ) / 2;
            
            zNegative   = positionZ;
            zPositive   = positionZ;

            if( this.isSidePorch() || ( this.isCornerPorchLeft() && 'front' === side ) ){
                x1 = this.getWidth() / 2;
            }
        }
        
        if( 'saltbox' === this.getRoofShape() ) {
            if( 'front' === side || 'back' === side ) {
                if( this._bones['b_f_r4'] ){
                    x1  = this._bones['b_f_r4'].position.x;

                }else if( 'structure_quaker_wannabuysheds' === this._shedType && this._bones['b_f_r1'] ){
                    x1  = this._bones['b_f_r1'].position.x;
                }
            }
        }

        if( this.isCornerPorchLeft() ){
            if( 'left' === side ){
                z1 = this.getDepth() / 2 - constants.cornerDepth;
            }else if( 'inner_left' === side ){
                z1 = this.getDepth() / 2;
            }else if( 'inner_front' === side ){
                x1 = - ( this.getWidth() / 2 ) + constants.cornerWidth;
            }
        }

        if( this.isDeluxeCornerLeftPorch() ) {
            switch( side ) {
                case 'front':
                    x1 = this._bones['b_f_l4'].position.x;
                    break;
                case 'inner_right':
                    z1 = this._bones['b_f_r5'].position.z;
                    break;
                case 'inner_front':
                    x1 = this._bones['b_f_r1'].position.x;
                    break;
                case 'left':
                    z1 = this._bones['b_f_l1'].position.z;
                    break;
                case 'right':
                    z1 = this._bones['b_f_r1'].position.z;
                    break;
            }
        } else if( this.isDoubleGable() ) {
            const porchWidth = this.doubleGableGetPorchWidth();
            switch( side ) {
                case 'left_back':
                    z1 = - porchWidth / 2;
                    depth = - porchWidth;
                    break;
                case 'right_inner_porch':
                case 'left_inner_porch':
                    x1 = - ( this.getWidth() / 2 - constants.doubleGablePorchDepth );
                    width = x1;     
                    break;
                case 'front_inner_porch':
                    z1 = porchWidth / 2;
                    depth = porchWidth;
                    break;
            }
        } else if( this.isMiddlePorch() ) {
            if( 'left_back' === side ) {
                z1      = - constants.middleWidth / 2;
                depth   = - constants.middleWidth;
            } else if( 'right_inner_porch' === side || 'left_inner_porch' === side ) {
                x1      = - ( this.getWidth() / 2 - constants.middleDepth );
                width   = x1; 
            } else if( 'front_inner_porch' === side ) {
                z1      = constants.middleWidth / 2;
                depth   = constants.middleWidth;
            }
        } else if( 'structure_ranch_cabin' === this._shedType || this.isAframeSingleGable() ) {
            switch( side ) {
                case 'inner_back':
                    x1      = - ( this.getWidth() / 2 - constants.cabinPorchDepth );
                    width   = x1;
                    break;
                case 'inner_left':
                    z1      = this.getDepth() / 2 - this._cabinWidth;
                    depth   = this.getDepth() - this._cabinWidth;
                    break;
            }
        } else if( this.is2StoryLeanTo() ) {
            switch( side ) {
                case 'lean_to_front':
                    x1 = this._bones['lean_to_f_r2'].position.x;
                    break;
                case 'lean_to_back':
                    x1 = this._bones['lean_to_b_r2'].position.x;
                    break;
            }
        }

        this._lineHorizontalLeftOptions.points   = [ new Vector3( xNegative, y, zNegative ), new Vector3( x1, y, z1 ) ];
        this._lineHorizontalLeftOptions.instance = this._lineHorizontalLeft;
        this._lineHorizontalLeft = MeshBuilder.CreateLines( 'lineHorizontalLeft', this._lineHorizontalLeftOptions );
        this.updateLineTextV2( side, firstMesh, x2 + componentWidth / 2, y, z2 + componentWidth / 2, x1, y, z1, this._distanceTextHolderHorizontalLeft, this._distanceTextHorizontalLeft, true );
        this.updateArrowLeft( side, x1, y, z1 );

        if( this._isInterior ){
            this._lineHorizontalLeft.renderingGroupId = 2;
        }

        let x3 = x1;
        let z3 = -z1;

        if( 'front' === side || 'top_front' === side || 'lean_to_front' === side || 'back' === side || 'top_back' === side || 'lean_to_back' === side || 'inner_front' === side || this.isPorchSide( side ) || 'left_inner_porch' === side || 'right_inner_porch' === side || 'inner_back' === side ) {
            x3 = -x1;
            z3 = z1;
            
            if( this.isSidePorch() || ( this.isCornerPorchLeft() && 'front' === side ) ){
                x3 = - this.getWidth() / 2 + this._porchWidth;
            }
        }
        
        if( this.isCornerPorchLeft() ){
            if( 'left' === side ){
                z3 = - this.getDepth() / 2;
            }else if( 'inner_left' === side ){
                z3 = z1 - constants.cornerDepth;
            }else if( 'inner_front' === side ){
                x3 = - this.getWidth() / 2;
            }
        }

        if( this.isDeluxeCornerLeftPorch() ) {
            switch( side ) {
                case 'front':
                    x3 = this._bones['b_f_l1'].position.x;
                    break;
                case 'inner_right':
                    z3 = z1 - constants.deluxeDepth;
                    break;
                case 'inner_front':
                    x3 = this._bones['b_f_r4'].position.x;
                    break;
                case 'left':
                    z3 = this._bones['b_b_l1'].position.z;
                    break;
                case 'right':
                    z3 = this._bones['b_b_r1'].position.z;
                    break;
            }
        } else if( this.isDoubleGable() ) {
            const porchWidth = this.doubleGableGetPorchWidth();
            switch( side ) {
                case 'left_front':
                    z3 = porchWidth / 2;
                    break;
                case 'left_back':
                    z3 = - this.getDepth() / 2;
                    break;
                case 'right_inner_porch':
                case 'left_inner_porch':
                    x3 = - this.getWidth() / 2;
                    break;
                case 'front_inner_porch':
                    z3 = - porchWidth / 2;
                    break;
            }
        } else if( this.isMiddlePorch() ) {
            if( 'left_front' === side ) {
                z3 = constants.middleWidth / 2;
            } else if( 'left_back' === side ) {
                z3 = - this.getDepth() / 2;
            } else if( 'right_inner_porch' === side || 'left_inner_porch' === side ) {
                x3 = - this.getWidth() / 2;
            } else if( 'front_inner_porch' === side ) {
                z3 = - constants.middleWidth / 2;
            }
        } else if( 'structure_ranch_cabin' === this._shedType || this.isAframeSingleGable() ) {
            switch( side ) {
                case 'inner_back':
                    x3 = - this.getWidth() / 2;
                    break;
                case 'inner_left':
                    z3 = this._bones['b_b_l1'].position.z;
                    break;
                case 'back':
                    x3 = this._bones['b_b_l1'].position.x;
                    break;
                case 'left':
                    z3 = this._bones['b_p_l1'].position.z;
                    break;
            }
        } else if( this.is2StoryLeanTo() ) {
            switch( side ) {
                case 'lean_to_front':
                    x3 = this._bones['lean_to_f_r1'].position.x;
                    break;
                case 'lean_to_back':
                    x3 = this._bones['lean_to_b_r1'].position.x;
                    break;
            }
        }

        if( 'saltbox' === this.getRoofShape() && this._bones['b_f_l1'] ) {
            if( 'front' === side || 'back' === side ) {
                if( this._bones['b_f_r4'] || 'structure_quaker_wannabuysheds' === this._shedType ){
                    x3  = this._bones['b_f_l1'].position.x;
                }
            }
        }

        this._lineHorizontalRightOptions.points   = [ new Vector3( xPositive, y, zPositive ), new Vector3( x3, y, z3 ) ];
        this._lineHorizontalRightOptions.instance = this._lineHorizontalRight;
        this._lineHorizontalRight = MeshBuilder.CreateLines( 'lineHorizontalRight', this._lineHorizontalRightOptions );
        this.updateLineTextV2( side, firstMesh, x2 - componentWidth / 2, y, z2 - componentWidth / 2, x3, y, z3, this._distanceTextHolderHorizontalRight, this._distanceTextHorizontalRight, true );
        this.updateArrowRight( side, x3, y, z3 );

        if( this._isInterior ){
            this._lineHorizontalRight.renderingGroupId = 2;
        }

        this.updateMiddleArrow( side, type );
        
        if( 'dormer' === side || 'door' === type || this.isInteriorComponent( type ) ) {
            return;
        }

        /* Handle vertical line */
        if( 'left' === side || 'right' === side || 'inner_left' === side || 'left_front' === side || 'left_back' === side || 'front_inner_porch' === side || 'inner_right' === side || 'lean_to_right' === side || 'top_right' === side || 'top_left' === side ) {
            z1 = positionZ;
        }

        if( 'front' === side || 'top_front' === side || 'lean_to_front' === side || 'back' === side || 'top_back' === side || 'lean_to_back' === side || 'inner_front' === side || this.isPorchSide( side ) || 'left_inner_porch' === side || 'right_inner_porch' === side || 'inner_back' === side ) {
            x1 = positionX;
        }
        
        const bottomPoint   = this.isPorchSide( side ) ? this.getPorchBottom() : 0;

        this._lineVerticalBottomOptions.points   = [ new Vector3( x2, y - componentHeight / 2, z2 ), new Vector3( x1, bottomPoint, z1 ) ];
        this._lineVerticalBottomOptions.instance = this._lineVerticalBottom;
        this._lineVerticalBottom = MeshBuilder.CreateLines( 'lineVerticalBottom', this._lineVerticalBottomOptions );
        this.updateLineTextV2( side, firstMesh, x2, y - componentHeight / 2, z2, x3, bottomPoint, z3, this._distanceTextHolderVerticalBottom, this._distanceTextVerticalBottom, false );
        this.updateArrowBottom( side, x2, bottomPoint, z2 );
    }

    updateMiddleArrow = ( side, type ) => {
        const rotation = this.calculateComponentRotation( side, type );

        this._arrowHorizontalLeftStart.visibility  = 1;
        this._arrowHorizontalRightStart.visibility = 1;

        this._arrowHorizontalRightStart.rotation.z = Math.PI;
        this._arrowHorizontalRightStart.rotation.y = this._arrowHorizontalLeftStart.rotation.y = rotation.y + Math.PI;

        let position1 = this._lineHorizontalLeftOptions.points[0];
        let position2 = this._lineHorizontalRightOptions.points[0];
    
        if( ! helpers.hasKeyInArray( positiveSide, side ) ) {
            [ position1, position2 ] = [ position2, position1 ];
        }

        if( this.isInteriorComponent( type ) ) {
            [ position1, position2 ] = [ position2, position1 ];
        }
    
        this._arrowHorizontalLeftStart.position  = position1;
        this._arrowHorizontalRightStart.position = position2;
    }

    updateArrowRight( side, x, y, z ) {
        this._arrowHorizontalRightEnd.visibility    = 1;
        const tmpInstance               = 0.12;

        switch( side ) {
            case 'front':
            case 'top_front':
            case 'lean_to_front':
            case 'porch':
            case 'inner_front':
            case 'right_inner_porch':
                this._arrowHorizontalRightEnd.position      = new Vector3( x + tmpInstance, y, z );
                this._arrowHorizontalRightEnd.rotation.z    = 2 * Math.PI;
                this._arrowHorizontalRightEnd.rotation.y    = Math.PI;
                break;
            case 'back':
            case 'top_back':
            case 'lean_to_back':
            case 'left_inner_porch':
            case 'inner_back':
                this._arrowHorizontalRightEnd.position      = new Vector3( x + tmpInstance, y, z );
                this._arrowHorizontalRightEnd.rotation.z    = Math.PI;
                this._arrowHorizontalRightEnd.rotation.y    = 2 * Math.PI;
                break;
            case 'left':
            case 'dormer':
            case 'left_back':
            case 'inner_left':
            case 'left_front':
            case 'front_inner_porch':
            case 'left_middle_porch':
            case 'top_left':
                this._arrowHorizontalRightEnd.position      = new Vector3( x, y, z + tmpInstance );
                this._arrowHorizontalRightEnd.rotation.z    = 2 * Math.PI;
                this._arrowHorizontalRightEnd.rotation.y    = Math.PI / 2;
                break;
            case 'right':
            case 'inner_right':
            case 'lean_to_right':
            case 'top_right':
                this._arrowHorizontalRightEnd.position      = new Vector3( x, y, z + tmpInstance );
                this._arrowHorizontalRightEnd.rotation.z    = Math.PI;
                this._arrowHorizontalRightEnd.rotation.y    = 3 * Math.PI / 2;
                break;
        }

        if( this._isInterior ){
            this._arrowHorizontalRightEnd.rotation.y = this._arrowHorizontalRightEnd.rotation.y + Math.PI;
            this._arrowHorizontalRightEnd.rotation.z = this._arrowHorizontalRightEnd.rotation.z + Math.PI;
        }
    }

    updateArrowBottom( side, x, y, z ) {
        this._arrowVerticalBottomStart.position      = new Vector3( x, this._lineVerticalBottomOptions.points[0].y - 0.15, z );
        this._arrowVerticalBottomStart.visibility    = 1;
        this._arrowVerticalBottomStart.rotation.z    = Math.PI / 2;

        this._arrowVerticalBottomEnd.position      = new Vector3( x, y, z );
        this._arrowVerticalBottomEnd.visibility    = 1;
        this._arrowVerticalBottomEnd.rotation.z    = 3 * Math.PI / 2;

        switch( side ) {
            case 'front':
            case 'top_front':
            case 'lean_to_front':
            case 'porch':
            case 'inner_front':
            case 'right_inner_porch':
                this._arrowVerticalBottomStart.rotation.y    = Math.PI;
                this._arrowVerticalBottomEnd.rotation.y    = Math.PI;
                break;
            case 'back':
            case 'top_back':
            case 'lean_to_back':
            case 'left_inner_porch':
            case 'inner_back':
                this._arrowVerticalBottomStart.rotation.y    = 2 * Math.PI;
                this._arrowVerticalBottomEnd.rotation.y    = 2 * Math.PI;
                break;
            case 'left':
            case 'left_back':
            case 'inner_left':
            case 'left_front':
            case 'front_inner_porch':
            case 'left_middle_porch':
            case 'top_left':
                this._arrowVerticalBottomStart.rotation.y    = Math.PI / 2;
                this._arrowVerticalBottomEnd.rotation.y    = Math.PI / 2;
                break;
            case 'right':
            case 'inner_right':
            case 'top_right':
            case 'lean_to_right':
                this._arrowVerticalBottomStart.rotation.y    = 3 * Math.PI / 2;
                this._arrowVerticalBottomEnd.rotation.y    = 3 * Math.PI / 2;
                break;
        }

        if( this._isInterior ){
            this._arrowVerticalBottomStart.rotation.y = this._arrowVerticalBottomStart.rotation.y + Math.PI;
            this._arrowVerticalBottomEnd.rotation.y = this._arrowVerticalBottomEnd.rotation.y + Math.PI;
        }
    }

    updateArrowLeft( side, x, y, z ) {
        this._arrowHorizontalLeftEnd.visibility    = 1;
        const tmpInstance               = 0.12;

        switch( side ) {
            case 'front':
            case 'top_front':
            case 'lean_to_front':
            case 'porch':
            case 'inner_front':
            case 'right_inner_porch':
                this._arrowHorizontalLeftEnd.position      = new Vector3( x - tmpInstance, y, z );
                this._arrowHorizontalLeftEnd.rotation.z    = Math.PI;
                this._arrowHorizontalLeftEnd.rotation.y    = Math.PI;
                break;
            case 'back':
            case 'top_back':
            case 'lean_to_back':
            case 'left_inner_porch':
            case 'inner_back':
                this._arrowHorizontalLeftEnd.position      = new Vector3( x - tmpInstance, y, z );
                this._arrowHorizontalLeftEnd.rotation.z    = 2 * Math.PI;
                this._arrowHorizontalLeftEnd.rotation.y    = 2 * Math.PI;
                break;
            case 'left':
            case 'dormer':
            case 'left_back':
            case 'inner_left':
            case 'left_front':
            case 'front_inner_porch':
            case 'top_left':
                this._arrowHorizontalLeftEnd.position      = new Vector3( x, y, z - tmpInstance );
                this._arrowHorizontalLeftEnd.rotation.z    = Math.PI;
                this._arrowHorizontalLeftEnd.rotation.y    = Math.PI / 2;
                break;
            case 'right':
            case 'inner_right':
            case 'top_right':
            case 'lean_to_right':
                this._arrowHorizontalLeftEnd.position      = new Vector3( x, y, z - tmpInstance );
                this._arrowHorizontalLeftEnd.rotation.z    = 2 * Math.PI;
                this._arrowHorizontalLeftEnd.rotation.y    = 3 * Math.PI / 2;
                break;
        }

        if( this._isInterior ){
            this._arrowHorizontalLeftEnd.rotation.y = this._arrowHorizontalLeftEnd.rotation.y + Math.PI;
            this._arrowHorizontalLeftEnd.rotation.z = this._arrowHorizontalLeftEnd.rotation.z + Math.PI;
        }
    }

    updateLineTextV2( side, componentMesh, x1, y1, z1, x2, y2, z2, mesh, text, isHorizontal ) {
        let space = Math.abs( helpers.toFixed3( y1 ) - helpers.toFixed3( y2 ) );
        if( isHorizontal ) {
            if( 'front' === side || 'top_front' === side || 'lean_to_front' === side || 'back' === side || 'top_back' === side || 'lean_to_back' === side || 'inner_front' === side || this.isPorchSide( side ) || 'left_inner_porch' === side || 'right_inner_porch' === side || 'inner_back' === side ) {
                space = Math.abs( helpers.toFixed3( x1 ) - helpers.toFixed3( x2 ) );
            } else {
                space = Math.abs( helpers.toFixed3( z1 ) - helpers.toFixed3( z2 ) );
            }
        }

        mesh.visibility = 1;
        mesh.rotation.x = 3 * Math.PI / 2;
        mesh.position.y = isHorizontal ? y1 : ( parseFloat( y1 ) + parseFloat( y2 ) ) / 2;

        const font          = helpers.isShowMeasurementsInDecimal() && "fraction" === helpers.getMeasurementsDecimalFormat() ? "70px monospace" : this._font;
        let unitSpace:any   = helpers.convertUnit( space );
        unitSpace           = unitSpace.replaceAll( ' ', '' );

        text.drawText( unitSpace, null, null, font, this._color, this._background, true );
        switch( side ) {
            case 'front':
            case 'top_front':
            case 'lean_to_front':
            case 'porch':
            case 'inner_front':
            case 'right_inner_porch':
                mesh.rotation.y = Math.PI;
                mesh.position.z = componentMesh.position.z + 0.01;
                mesh.position.x = isHorizontal ? ( x1 + x2 ) / 2 : x1;
                break;
            case 'back':
            case 'top_back':
            case 'lean_to_back':
            case 'left_inner_porch':
            case 'inner_back':
                mesh.rotation.y = 2 * Math.PI;
                mesh.position.z = componentMesh.position.z - 0.01;
                mesh.position.x = isHorizontal ? ( x1 + x2 ) / 2 : x1;
                break;
            case 'left':
            case 'dormer':
            case 'left_back':
            case 'left_front':
            case 'inner_left':
            case 'front_inner_porch':
            case 'top_left':
                mesh.rotation.y = Math.PI / 2;
                mesh.position.x = componentMesh.position.x - 0.01;
                mesh.position.z = isHorizontal ? ( z1 + z2 ) / 2 : z1;
                break;
            case 'right':
            case 'inner_right':
            case 'lean_to_right':
            case 'top_right':
                mesh.rotation.y = 3 * Math.PI / 2;
                mesh.position.x = componentMesh.position.x + 0.01;
                mesh.position.z = isHorizontal ? ( z1 + z2 ) / 2 : z1;
                break;
        }

        if( this._isInterior ){
            mesh.rotation.y = mesh.rotation.y + Math.PI;
        }
    }

    private calculateComponentRotation( side, componentType ) {
        let rotateY;
        let rotateX = 0;
        const roofDegree = this.getRoofCorrectionDegree() * Math.PI / 180;

        switch (side) {
            case "lean_to_front":
            case "top_front":
            case "front":
            case "inner_front":
            case "porch":
            case "right_inner_porch":
            case "roof":
            case "floor":
                rotateY = 0;
            break;
            
            case "top_right":
            case "right":
            case "inner_right":
            case "lean_to_right":
                rotateY = Math.PI / 2;
                break;

            case "lean_to_back":
            case "top_back":
            case "back":
            case "left_inner_porch":
            case "inner_back":
                rotateY = Math.PI;
                break;
                
            case "top_left":
            case "left":
            case "inner_left":
            case "dormer":
            case "left_front":
            case "left_back":
            case "front_inner_porch":
            case "left_middle_porch":
                rotateY = 3 * Math.PI / 2;
                break;
            case "roof_left":
                rotateY = 3 * Math.PI / 2;
                rotateX = 3 * Math.PI / 2 + roofDegree;
                break;
            case "roof_right":
                rotateY = Math.PI / 2;
                rotateX = 3 * Math.PI / 2 + roofDegree;
                break;
            case "diagonal_right":
                rotateY = Math.PI / 4;
                break;

            default:
                break;
        }

        if( 'skylight_window' !== componentType ) {
            rotateX = 0;
        }

        rotateY = this.isInteriorComponent( componentType ) ? rotateY + Math.PI : rotateY;
        let rotation = { x: rotateX, y: rotateY, z: 0 };
        
        return rotation;
    }

    updateLineText( side, width, length, x, y, z, componentWidth, componentHeight, mesh, text, isHorizontal ) {
        let space           = isHorizontal ? this.getHorizontalSpace( side, width, length, x, z ) - componentWidth / 2 : y - componentHeight / 2;
        const wallHeight    = this.getWallHeight();
        mesh.visibility     = 1;
        mesh.rotation.x     = 3 * Math.PI / 2;
        if( this.isPorchSide( side ) && ! isHorizontal ) {
            space = y - wallHeight;
            mesh.position.y = wallHeight + space / 2;
        } else {
            mesh.position.y = isHorizontal ? y : y / 2;
        }
        
        text.drawText( helpers.convertUnit( space ), null, null, this._font, this._color, this._background, true );

        switch( side ) {
            case 'front':
            case 'top_front':
            case 'lean_to_front':
            case 'porch':
            case 'inner_front':
            case 'right_inner_porch':
                if( 'saltbox' === this.getRoofShape() ) {
                    if( this._bones['b_f_r4'] ){
                        width = Math.abs( this._bones['b_f_r4'].position.x ) * 2;

                    }else if( 'structure_quaker_wannabuysheds' === this._shedType ){
                        width = Math.abs( this._bones['b_f_r1'].position.x ) * 2;
                    }
                }
                mesh.rotation.y = Math.PI;
                mesh.position.z = z + 0.01;
                mesh.position.x = isHorizontal ? ( width / 2 + x ) / 2 : x;
                break;
            case 'back':
            case 'top_back':
            case 'lean_to_back':
            case 'left_inner_porch':
            case 'inner_back':
                if( 'saltbox' === this.getRoofShape() ) {
                    if( this._bones['b_f_r4'] ){
                        width = Math.abs( this._bones['b_f_r4'].position.x ) * 2;

                    }else if( 'structure_quaker_wannabuysheds' === this._shedType ){
                        width = Math.abs( this._bones['b_f_r1'].position.x ) * 2;
                    }
                }
                mesh.rotation.y = 2 * Math.PI;
                mesh.position.z = z - 0.01;
                mesh.position.x = isHorizontal ? ( width / 2 + x ) / 2 : x;
                break;
            case 'left':
            case 'dormer':
            case 'left_back':
            case 'inner_left':
            case 'left_front':
            case 'front_inner_porch':
            case 'top_left':
                mesh.rotation.y = Math.PI / 2;
                mesh.position.x = x - 0.01;
                mesh.position.z = isHorizontal ? ( length / 2 + z ) / 2 : z;
                break;
            case 'right':
            case 'inner_right':
            case 'lean_to_right':
            case 'top_right':
                mesh.rotation.y = 3 * Math.PI / 2;
                mesh.position.x = x + 0.01;
                mesh.position.z = isHorizontal ? ( length / 2 + z ) / 2 : z;
                break;
        }
        
        if( ( this.isSidePorch() && isHorizontal && ( 'front' === side || 'back' === side ) ) || ( this.isCornerPorchLeft() && isHorizontal && 'front' === side ) ){
            mesh.position.x = ( ( width + this._porchWidth ) / 2 + x ) / 2;
        }

        if( this.isCornerPorchLeft() && isHorizontal ){
            if( 'left' === side ){
                mesh.position.z = ( ( length - constants.cornerDepth ) / 2 + z ) / 2;
            }else if( 'inner_left' === side ){
                mesh.position.z = ( this.getDepth() / 2 + z ) / 2;
            }else if( 'inner_front' === side ){
                mesh.position.x = ( ( - this.getWidth() / 2 + constants.cornerWidth ) + x ) / 2;
            }
        }

        if( ( 'structure_ranch_cabin' === this._shedType || this.isAframeSingleGable() ) && isHorizontal ) {
            if( 'inner_left' === side ) {
                mesh.position.z = ( ( length - this._cabinWidth ) / 2 + z ) / 2;
            }
        }

        if( this._isInterior ){
            mesh.rotation.y = mesh.rotation.y + Math.PI;
        }

        if( this.isDeluxeCornerLeftPorch() && isHorizontal ) {
            switch( side ) {
                case 'front':
                    mesh.position.x = ( ( - this.getWidth() / 2 + this.deluxePorchGetFrontWidth() ) + x ) / 2;
                    break;
                case 'inner_right':
                    mesh.position.z = ( this._bones['b_f_r6'].position.z + z ) / 2;
                    break;
                case 'inner_front':
                    mesh.position.x = ( this.getWidth() / 2 + x ) / 2;
                    break;
                case 'left':
                    mesh.position.z = ( this._bones['b_f_l1'].position.z + z ) / 2;
                    break;
                case 'right':
                    mesh.position.z = ( this._bones['b_f_r1'].position.z + z ) / 2;
                    break;
            }
        }

        if( this.is2StoryLeanTo() ) {
            switch( side ) {
                case 'lean_to_front':
                    mesh.position.x = isHorizontal ? helpers.toFixed3( ( this._bones['lean_to_f_r2'].position.x + x ) / 2 ) : x;
                    break;
                case 'lean_to_back':
                    mesh.position.x = isHorizontal ? helpers.toFixed3( ( this._bones['lean_to_b_r2'].position.x + x ) / 2 ) : x;
                    break;
            }
        }
    }

    calculateLayoutPosition = ( meshes, side, vertical, horizontal, componentType, componentName ) => {
        // Initialize constants and default values
        let result : Vector3 = Vector3.Zero();
        const componentBehavior = this.getBehavior( componentName, componentType );
        const [ buildingHeight, wallHeight ] = [ this.getBuildingHeight(), this.getWallHeight() ];
        const [ componentWidth, componentHeight ] = [ this.getComponentWidth( meshes, componentBehavior, componentName ), this.getComponentHeight( meshes, componentBehavior ) ];

        // Initiate defaults
        const { distance: hDistance = 0, measured_from: hMeasuredFrom = '', roof_top_aligned: roofTopAligned } = horizontal || {};
        const { distance: vDistance = 0, measured_from: vMeasuredFrom = '' } = vertical || {};

        // Calculate horizontal position
        const wallBone = this.getWallBones( side );
        const startPoint = wallBone.left ? this._bones[wallBone.left].position : Vector3.Zero();
        const endPoint = wallBone.right ? this._bones[wallBone.right].position : Vector3.Zero();
        const topWallPoint = wallBone.topWall ? this._bones[wallBone.topWall].position.y : wallHeight;
        const direction = endPoint.subtract( startPoint ).normalize();
        const distance = helpers.toFixed3( componentWidth / 2 ) + helpers.toFixed3( hDistance ) + this._offsetDistance;

        // Horizontal positioning logic
        if( 'default' === roofTopAligned ) {
            result.copyFrom( Vector3.Center( startPoint, endPoint ) );
        } else {
            switch( hMeasuredFrom ) {
                case 'left':
                    result.copyFrom( startPoint.add( direction.scale( distance ) ) );
                    break;
                case 'right':
                    result.copyFrom( endPoint.subtract( direction.scale( distance ) ) );
                    break;
            }            
        }

        // Vertical positioning logic
        const isPorchSide = this.isPorchSide( side );
        const isCustomPlacement = this.isCustomPlacement( side );
        switch( vMeasuredFrom ) {
            case 'building':
                result.y = buildingHeight - vDistance;
                break;
            case 'wall':
                result.y = isCustomPlacement ? startPoint.y + componentHeight + vDistance : topWallPoint - vDistance;
                break;
            case 'ground':
                result.y = startPoint.y + vDistance;
                break;
        }

        // Component-specific adjustments
        if( componentBehavior === 'door' && ! helpers.hasKeyInArray( [ 'top_front', 'top_back' ], side ) ) {
            result.y = this.getDoorFixedHeight( side );
        }

        if( this.applyFixedHeightWindows( side, componentBehavior, componentName ) ) {
            if( ! ( 0 === this._doorFixedTarget.length && this.isFollowMainDoor() ) ) {
                result.y = this.getWindowsFixedPositionY( componentBehavior, componentHeight, side );
            }
        }

        if( isPorchSide && this.isVerticalDistribute() ) {
            result.y = this.calculateMiddlePosition( componentHeight );
        }

        if( 'skylight_window' === componentBehavior ) {
            result.y = this.getPositionYForSkylightWindow();
        }

        if( 'light' === componentType ){
            result.y = this.getPositionYForLight();
        }

        if( this.isHandleOffsetFloor( componentBehavior ) ) {
            result.y = this.getPositionYForInterior( componentBehavior, componentName );
        }

        // Custom position component adjustments for specific case
        result = this.adjustCustomPosition( result, side, componentBehavior );

        // Interior component adjustment
        if( this.isInteriorComponent( componentBehavior ) && 'light' !== componentBehavior ) {
            result = this.updateInteriorPosition( result, side );
        }

        return result;
    }

    adjustCustomPosition = ( position, side, componentType ) => {
        if( this.isCornerPorchLeft() && 'front' === side && 'vent' === componentType && this._bones['b_b_t'] ) {
            position.x = this._bones['b_b_t'].position.x;
        }


        return position;
    }

    private calculateComponentPosition( meshes, side, vertical, horizontal, componentType, componentName ) {
        let _this                   = this;
        componentType               = _this.getBehavior( componentName, componentType );
        let width : number          = _this.getWidth();
        let depth : number          = _this.getDepth();
        let height : number         = ( _this.getRoofShape() === 'saltbox_customize' && side === 'left' ) ? _this._bones['b_f_l2'].position.y : _this.getWallHeight();
        let buildingHeight : number = _this.getBuildingHeight();

        let horizontalDistance = horizontal ? parseFloat(horizontal.distance) : 0;
        horizontalDistance = horizontalDistance ? horizontalDistance : 0;
        let horizontalMeasuredFrom = horizontal ? horizontal.measured_from : '';
        let verticalDistance = vertical ? parseFloat(vertical.distance) : 0;
        let verticalMeasuredFrom = vertical ? vertical.measured_from : '';

        if( 'dormer' === componentType && 'distribute' !== horizontalMeasuredFrom ) {
            horizontalDistance = 0;
        }

        let sidingLRStart   = -depth / 2;
        let sidingLREnd     = depth / 2;
        let sidingFBStart   = _this.isSidePorch() ? -width / 2 + _this._porchWidth : -width / 2;
        let sidingFBEnd     = width / 2;

        if( this.isDoubleGable() ) {
            switch( side ) {
                case 'left_front':
                    sidingLRStart = _this._bones['b_f_l1'].position.z;
                    sidingLREnd = _this._bones['b_p_l1'].position.z;
                    break;
                case 'left_back':
                    sidingLRStart = _this._bones['b_p_l3'].position.z;
                    sidingLREnd = _this._bones['b_b_l1'].position.z;
                    break;
                case 'left_inner_porch':
                    sidingFBStart = _this._bones['b_p_l1'].position.x;
                    sidingFBEnd = _this._bones['b_b_m4'].position.x;
                    break;
                case 'right_inner_porch':
                    sidingFBStart = _this._bones['b_b_m8'].position.x;
                    sidingFBEnd = _this._bones['b_p_l3'].position.x;
                    break;
                case 'front_inner_porch':
                    sidingLRStart = _this._bones['b_b_m4'].position.z;
                    sidingLREnd = _this._bones['b_b_m8'].position.z;
                    break;
            }
        } else if( 'structure_ranch_cabin' === _this._shedType || this.isAframeSingleGable() ) {
            switch( side ) {
                case 'inner_back':
                    sidingFBStart   = _this._bones['b_p_l1'].position.x;
                    sidingFBEnd     = sidingFBStart + 4;
                    break;
                case 'inner_left':
                    sidingLRStart   = _this._bones['b_b_m4'].position.z;
                    sidingLREnd     = _this._bones['b_b_l1'].position.z;
                    break;
                case 'back':
                    sidingFBStart   = _this._bones['b_b_l1'].position.x;
                    sidingFBEnd     = _this._bones['b_b_r1'].position.x;
                    break;
                case 'left':
                    sidingLRStart   = _this._bones['b_f_l1'].position.z;
                    sidingLREnd     = _this._bones['b_p_l1'].position.z;
                    break;
            }
        } else if( _this.isCornerPorchLeft() ){
            if( 'front' === side ){
                sidingFBStart = - width / 2 + constants.cornerWidth;
            }

            if( 'inner_front' === side ){
                sidingFBEnd = - width / 2 + constants.cornerWidth;
            }

            if( 'left' === side ){
                sidingLREnd = depth / 2 - constants.cornerDepth;
            }

            if( 'inner_left' === side ){
                sidingFBStart = depth / 2 - constants.cornerDepth;
            }
        } else if( _this.isDeluxeCornerLeftPorch() ) {
            if( 'front' === side ) {
                sidingFBStart   = _this._bones['b_f_l1'].position.x;
                sidingFBEnd     = _this._bones['b_f_l4'].position.x;
            }
        }
 
        let componentSize = _this.getComponentWidth(meshes, componentType, componentName);

        let componentHeight  = this.getComponentHeight( meshes, componentType );
        let positionX: float = 0;
        let positionZ: float = 0;
        let positionY: float = 0;

        if( this.isHandleOffsetFloor( componentType ) ) {
            verticalMeasuredFrom    = 'ground';
            verticalDistance        = this.getPositionYForInterior( componentType, componentName );
        }

        let dogKennelMiddlePositionZ = positionZ;

        if( 'structure_dog_kennel' === this._shedType && ( 'left' === side || 'right' === side ) ) {
            sidingLRStart               = this._bones['b_b_r1'].position.z;
            sidingLREnd                 = this._bones['b_m_r1'].position.z;
            dogKennelMiddlePositionZ    = this._bones['b_b_r1'].position.z + this.getDogKennelBoxDepth() / 2;
        }

        if( 'saltbox' === this.getRoofShape() && this._bones['b_f_l1'] ) {
            if( 'front' === side || 'back' === side ) {
                if( this._bones['b_f_r4'] ){
                    sidingFBStart   = this._bones['b_f_l1'].position.x;
                    sidingFBEnd     = this._bones['b_f_r4'].position.x;
                }else if( 'structure_quaker_wannabuysheds' === this._shedType ){
                    sidingFBStart   = this._bones['b_f_l1'].position.x;
                    sidingFBEnd     = this._bones['b_f_r1'].position.x;
                }
            }
        }

        if( this.isMiddlePorch() && ( 'front' !== side || 'back' !== side || 'right' !== side ) ) {
            sidingLRStart   = - sidingLREnd;
            sidingLREnd     = - sidingLRStart;
            switch (side) {
                case 'left_front':
                    sidingLRStart = constants.middleWidth / 2;
                    break;
                case 'left_back':
                    sidingLREnd = - constants.middleWidth / 2;
                    break;
                case 'left_inner_porch':
                case 'right_inner_porch':
                    sidingFBStart   = - width / 2;
                    sidingFBEnd     = - ( width / 2 - constants.middleDepth );
                    break;
                case 'front_inner_porch':
                    sidingLRStart   = - ( constants.middleWidth / 2 );
                    sidingLREnd     = constants.middleWidth / 2;
                    break;
            }
        }

        if( this.is2StoryLeanTo() ) {
            switch( side ) {
                case 'lean_to_front':
                    sidingFBStart   = _this._bones['lean_to_f_r2'].position.x;
                    sidingFBEnd     = _this._bones['lean_to_f_r1'].position.x;
                    break;
                case 'lean_to_back':
                    sidingFBStart   = _this._bones['lean_to_b_r1'].position.x;
                    sidingFBEnd     = _this._bones['lean_to_b_r2'].position.x;
                    break;
                case 'lean_to_right':
                    sidingFBStart   = _this._bones['lean_to_b_r2'].position.x;
                    sidingFBEnd     = _this._bones['lean_to_f_r2'].position.x;
                    break;
            }
        }

        // Middle Point X
        if (sidingFBStart + sidingFBEnd != 0) {
            positionX = ((sidingFBStart + sidingFBEnd) / 2) - horizontalDistance;
        } else {
            positionX = positionX - horizontalDistance;
        }

        if( 'structure_rancher' === _this._shedType && ( 'top_front' === side || 'top_back' === side ) ) {
            height = buildingHeight;
        }
       
        if( this._isEnableTackRoom && ( "right" === side || "left" === side ) && 'distribute' === horizontalMeasuredFrom && side === this._sideWallTackRoom ) {
            const dataTackRoom = this._dataTackroom;
            if( "left" === dataTackRoom.position ) {
                sidingLRStart = - ( depth / 2 ) + dataTackRoom.width;
            } else if( "right" === dataTackRoom.position  ) {
                sidingLREnd  = ( depth / 2 ) - dataTackRoom.width;
            }
        }


        // Middle Point Z
        if (sidingLRStart + sidingLREnd != 0) {
            positionZ = ((sidingLRStart + sidingLREnd) / 2) - horizontalDistance;
        } else {
            positionZ = 'structure_dog_kennel' === this._shedType && ( 'left' === side || 'right' === side ) ? dogKennelMiddlePositionZ : positionZ - horizontalDistance;
        }
        if( horizontalMeasuredFrom == 'distribute' ){
            positionY = horizontal.positionY
        }else if( verticalMeasuredFrom == 'building' ){
            positionY = buildingHeight - verticalDistance;
        }else if( verticalMeasuredFrom == 'wall' ){
            positionY = this.isPorchSide( side ) ? height + componentHeight + verticalDistance: height - verticalDistance;
        }else if( verticalMeasuredFrom == 'ground' ){
            positionY = verticalDistance;
        }else{
            positionY = 0;
        }

        

        if( 'structure_rancher' === _this._shedType && ( 'top_front' === side || 'top_back' === side ) ) {
            if( 'distribute' === horizontalMeasuredFrom ) {
                positionY = horizontal.positionY
            } else if( 'building' === verticalMeasuredFrom ) {
                positionY = buildingHeight - verticalDistance;
            } else if( 'wall' === verticalMeasuredFrom ) {
                positionY = height - verticalDistance;
            }else if( 'ground' === verticalMeasuredFrom ) {
                positionY = _this._bones['b_f_l3'].position.y + verticalDistance
            }            
        }
       
        let distanceSide = _this._offsetDistance + ( componentSize/2 ) + horizontalDistance;
        switch(side) {
            case 'front':
                if( horizontalMeasuredFrom == 'right' ){
                    positionX = sidingFBStart + distanceSide;
                }else if( horizontalMeasuredFrom == 'left' ){
                    positionX = sidingFBEnd - distanceSide;
                }else if (horizontalMeasuredFrom == 'distribute'){
                    if( 'saltbox' === this.getRoofShape() && this._bones['b_f_l1'] ) {
                        if( this._bones['b_f_r4'] ){
                            horizontalDistance += ( Math.abs( this._bones['b_f_r4'].position.x ) - Math.abs( this._bones['b_f_l1'].position.x ) ) / 2;
                        }else if( 'structure_quaker_wannabuysheds' === this._shedType ){
                            horizontalDistance += ( Math.abs( this._bones['b_f_r1'].position.x ) - Math.abs( this._bones['b_f_l1'].position.x ) ) / 2;
                        }
                    }

                    positionX = horizontalDistance;
                }

                if( _this.isDeluxeCornerLeftPorch() ) {
                    positionZ = _this._bones['b_f_l1'].position.z;
                } else {
                    positionZ = depth/2;
                }
                break;

            case 'back':
                if (horizontalMeasuredFrom == 'right') {
                    positionX = sidingFBEnd - distanceSide;                    
                } else if (horizontalMeasuredFrom == 'left') {
                    positionX = sidingFBStart + distanceSide;
                }else if (horizontalMeasuredFrom == 'distribute'){
                    if( 'saltbox' === this.getRoofShape() && this._bones['b_f_l1'] ) {
                        if( this._bones['b_f_r4'] ){
                            horizontalDistance -= ( Math.abs( this._bones['b_f_r4'].position.x ) - Math.abs( this._bones['b_f_l1'].position.x ) ) / 2;
                        }else if( 'structure_quaker_wannabuysheds' === this._shedType ){
                            horizontalDistance -= ( Math.abs( this._bones['b_f_r1'].position.x ) - Math.abs( this._bones['b_f_l1'].position.x ) ) / 2;
                        }
                    }
                    
                    positionX = -horizontalDistance;
                }

                positionZ = -(depth/2);
                break;

            case 'left':
                if( _this.isDeluxeCornerLeftPorch() ) {
                    if( horizontalMeasuredFrom === 'right' ) {
                        positionZ = _this._bones['b_b_l1'].position.z + distanceSide;
                    } else if( horizontalMeasuredFrom === 'left' ) {
                        positionZ = _this._bones['b_f_l1'].position.z - distanceSide;
                    } else if( horizontalMeasuredFrom === 'distribute' ) {
                        positionZ = horizontalDistance;
                    }

                    positionX = _this._bones['b_b_l1'].position.x;
                } else if( this.isSidePorch() ) {
                    positionX = _this._bones['b_b_l1'].position.x;

                    if( 'right' === horizontalMeasuredFrom ) {
                        positionZ = sidingLRStart + distanceSide;
                    } else if( 'left' === horizontalMeasuredFrom ) {
                        positionZ = sidingLREnd - distanceSide;
                    } else if( 'distribute' === horizontalMeasuredFrom ) {
                        positionZ = horizontalDistance;
                    }

                } else if( 'structure_ranch_cabin' === _this._shedType || this.isAframeSingleGable() ) {
                    if( 'right' === horizontalMeasuredFrom ) {
                        positionZ = _this._bones['b_p_l1'].position.z + distanceSide - _this._offsetDistance / 2;
                    } else if( 'left' === horizontalMeasuredFrom ) {
                        positionZ = _this._bones['b_f_l1'].position.z - distanceSide;
                    } else if( 'distribute' === horizontalMeasuredFrom ) {
                        positionZ = horizontalDistance;
                    }

                    positionX = _this._bones['b_f_l1'].position.x;
                } else {
                    if( horizontalMeasuredFrom == 'right'){
                        positionZ = sidingLRStart + distanceSide;
                    }else if (horizontalMeasuredFrom == 'left'){
                        positionZ = sidingLREnd - distanceSide;
                    }else if (horizontalMeasuredFrom == 'distribute'){
                        positionZ = 'structure_dog_kennel' === this._shedType ? dogKennelMiddlePositionZ + horizontalDistance : horizontalDistance;
                        if( 'lean_to' !== this._shedType ) {
                            if( positionY > this.getWallHeight() ) {
                                positionY = this.getWallHeight() - 0.5;
                            }
                        }
                    }

                    positionX = -(width/2);
                }
                break;

            case 'lean_to_right':
            case 'right':
                if( _this.isDeluxeCornerLeftPorch() ) {
                    if( horizontalMeasuredFrom === 'right' ) {
                        positionZ = _this._bones['b_f_r1'].position.z - distanceSide;
                    } else if( horizontalMeasuredFrom === 'left' ) {
                        positionZ = _this._bones['b_b_r1'].position.z + distanceSide;
                    } else if( horizontalMeasuredFrom === 'distribute' ) {
                        positionZ = horizontalDistance;
                    }

                    positionX = _this._bones['b_f_r1'].position.x;
                } else {
                    if (horizontalMeasuredFrom == 'right') {
                        positionZ = sidingLREnd - distanceSide;
                    } else if (horizontalMeasuredFrom == 'left') {
                        positionZ = sidingLRStart + distanceSide;
                    }else if (horizontalMeasuredFrom == 'distribute'){
                        positionZ = 'structure_dog_kennel' === this._shedType ? dogKennelMiddlePositionZ - horizontalDistance : -horizontalDistance;
                    }

                    positionX = width/2;

                    if( 'lean_to_right' === side ) {
                        positionX = _this._bones['lean_to_f_r2'].position.x;
                    }
                }
                break;

            case "porch":
                let componentHeight = positionY - height;
                let offsetDistance  = _this._offsetDistance + horizontalDistance;

                sidingFBEnd     = this.getLimitComponentXRoofPorch( componentHeight, componentSize );
                sidingFBStart   = -sidingFBEnd;
                if( horizontalMeasuredFrom == 'right' ){
                    positionX = sidingFBStart + offsetDistance;
                }else if( horizontalMeasuredFrom == 'left' ){
                    positionX = sidingFBEnd - offsetDistance;
                    // positionX = 'vent' === componentType ? 0 : sidingFBEnd - offsetDistance;
                }else if (horizontalMeasuredFrom == 'distribute'){
                    positionX = horizontalDistance;
                }

                positionZ = depth/2 + _this._porchWidth;
                break;
                
            case 'roof':
                positionY = _this._bones['b_b_t'] ? _this._bones['b_b_t'].position.y : _this.getBuildingHeight();
                positionX = _this._bones['b_b_t'] ? _this._bones['b_b_t'].position.x : 0;
                if( _this.isDoubleGable() ) {
                    positionX = _this._bones['r_b_t1'].position.x;
                    sidingLRStart = _this._bones['r_b_t1'].position.z;
                    sidingLREnd = _this._bones['r_f_t1'].position.z;
                }

                if( 'light' === componentType ){
                    positionY = this.getPositionYForLight();
                }
                if( 'flat' === this.getRoofShape() ){
                    positionX = 0;
                }

                if( _this.isMoveHorizontal( componentType ) ) {
                    if( 'right' === horizontalMeasuredFrom ) {
                        positionX = sidingFBStart + distanceSide;
                    } else if( 'left' === horizontalMeasuredFrom ) {
                        positionX = sidingFBEnd - distanceSide;
                    } else if( 'distribute' === horizontalMeasuredFrom ) {
                        positionX = horizontalDistance;
                    }

                    positionZ = 0;
                } else {
                    if( 'right' === horizontalMeasuredFrom ) {
                        positionZ = sidingLRStart + distanceSide;
                    } else if( 'left' === horizontalMeasuredFrom ) {
                        positionZ = sidingLREnd - distanceSide;
                    } else if( 'distribute' === horizontalMeasuredFrom ) {
                        positionZ = horizontalDistance;
                    }
                }

                break;

            case 'dormer':
                positionX = _this._bones['b_b_t2'].position.x;
                positionY = _this._bones['b_b_t2'].position.y - verticalDistance;

                if( horizontalMeasuredFrom == 'right' ) {
                    positionZ = sidingLRStart + distanceSide;
                } else if( horizontalMeasuredFrom == 'left' ) {
                    positionZ = sidingLREnd - distanceSide;
                } else if ( horizontalMeasuredFrom == 'distribute' ) {
                    positionZ = horizontalDistance;
                }
                
                break;

            case 'inner_left':
                if( 'structure_ranch_cabin' === _this._shedType || this.isAframeSingleGable() ) {
                    positionX = _this._bones['b_b_m4'].position.x;

                    if( 'right' === horizontalMeasuredFrom ) {
                        positionZ = sidingLREnd + distanceSide;
                    } else if( 'left' === horizontalMeasuredFrom ) {
                        positionZ = sidingLRStart - distanceSide;
                    }else if( 'distribute' === horizontalMeasuredFrom ) {
                        positionZ = horizontalDistance;
                    }
                } else {
                    positionX = sidingFBEnd - ( width - constants.cornerWidth );
                    
                    if( horizontalMeasuredFrom == 'right'){
                        positionZ = sidingLRStart + ( depth - constants.cornerDepth ) + distanceSide - _this._offsetDistance;
                    }else if (horizontalMeasuredFrom == 'left'){
                        positionZ = sidingLREnd - distanceSide;
                    }else if (horizontalMeasuredFrom == 'distribute'){
                        positionZ = horizontalDistance;
                    }
                }

                break;

            case 'inner_front':
                if( _this.isDeluxeCornerLeftPorch() ) {
                    if( horizontalMeasuredFrom == 'right' ) {
                        positionX = _this._bones['b_f_r4'].position.x + distanceSide;
                    } else if( horizontalMeasuredFrom == 'left' ) {
                        positionX = _this._bones['b_f_r1'].position.x - distanceSide;
                    } else if( horizontalMeasuredFrom == 'distribute' ) {
                        positionX = horizontalDistance;
                    }
    
                    positionZ = _this._bones['b_f_r1'].position.z;
                } else {
                    if( horizontalMeasuredFrom == 'right' ){
                        positionX = sidingFBStart + distanceSide;
                    }else if( horizontalMeasuredFrom == 'left' ){
                        positionX = sidingFBEnd - distanceSide + _this._offsetDistance;
                    }else if (horizontalMeasuredFrom == 'distribute'){
                        positionX = horizontalDistance;
                    }
                    
                    positionZ = depth/2 - constants.cornerDepth;
                }
                break;

            case 'diagonal_right':
                const diagonal          = Math.sqrt( distanceSide * distanceSide + distanceSide * distanceSide );
                const actualDistance    = distanceSide * distanceSide / diagonal;
                if( horizontalMeasuredFrom == 'right' ) {
                    positionX = _this._bones['b_f_l4'].position.x + actualDistance;
                    positionZ = _this._bones['b_f_l4'].position.z - actualDistance;
                } else if( horizontalMeasuredFrom == 'left' ) {
                    positionX = _this._bones['b_f_r6'].position.x - actualDistance;
                    positionZ = _this._bones['b_f_r6'].position.z + actualDistance;
                } else if( horizontalMeasuredFrom == 'distribute' ) {
                    positionX = horizontalDistance;
                }

                break;
            case 'right_inner_porch':
            case 'left_inner_porch':
                if( horizontalMeasuredFrom == 'right' ) {
                    positionX = sidingFBStart + distanceSide;
                } else if( horizontalMeasuredFrom == 'left' ) {
                    positionX = sidingFBEnd - distanceSide + _this._offsetDistance;
                } else if (horizontalMeasuredFrom == 'distribute') {
                    positionX = sidingFBStart - horizontalDistance;
                }

                if( this.isDoubleGable() ) {
                    if( 'left_inner_porch' === side ) {
                        if( horizontalMeasuredFrom == 'right' ) {
                            positionX = sidingFBEnd - distanceSide + _this._offsetDistance;
                        } else if( horizontalMeasuredFrom == 'left' ) {
                            positionX = sidingFBStart + distanceSide;
                        }
                    } else if( 'right_inner_porch' === side ) {
                        if( horizontalMeasuredFrom == 'right' ) {
                            positionX = sidingFBEnd + distanceSide;
                        } else if( horizontalMeasuredFrom == 'left' ) {
                            positionX = sidingFBStart - distanceSide + _this._offsetDistance;
                        } else if (horizontalMeasuredFrom == 'distribute') {
                            positionX = sidingFBEnd + horizontalDistance;
                        }
                    }
                }

                positionZ = 'right_inner_porch' === side ? - constants.middleWidth / 2 : constants.middleWidth / 2;
                break;
            case 'left_front':
            case 'left_back':    
            case 'front_inner_porch':
                if( _this.isDoubleGable() ) {
                    if( horizontalMeasuredFrom == 'right') {
                        positionZ = sidingLREnd + distanceSide;
                    } else if (horizontalMeasuredFrom == 'left') {
                        positionZ = sidingLRStart - distanceSide;
                    } else if (horizontalMeasuredFrom == 'distribute') {
                        positionZ = horizontalDistance;
                    }
                } else {
                    if( horizontalMeasuredFrom == 'right') {
                        positionZ = sidingLRStart + distanceSide;
                    } else if (horizontalMeasuredFrom == 'left') {
                        positionZ = sidingLREnd - distanceSide;
                    } else if (horizontalMeasuredFrom == 'distribute') {
                        positionZ = sidingLRStart - horizontalDistance;
                    }
                }
                positionX = 'front_inner_porch' === side ? -( width / 2 - this._porchWidth ) : - width / 2;
                break;
            case 'inner_right':
                if( horizontalMeasuredFrom == 'right' ) {
                    positionZ = _this._bones['b_f_r6'].position.z - distanceSide;
                } else if( horizontalMeasuredFrom == 'left' ) {
                    positionZ = _this._bones['b_f_r4'].position.z + distanceSide;
                } else if( horizontalMeasuredFrom == 'distribute' ) {
                    positionZ = horizontalDistance;
                }

                positionX = _this._bones['b_f_r6'].position.x;
                break;
            case 'top_left':
                
                if( this.is2StoryLeanTo() ) {
                    positionX = _this._bones['b_b_l2'].position.x;
                    
                    if( horizontalMeasuredFrom == 'right' ) {
                        positionZ = _this._bones['b_b_l2'].position.z + distanceSide;
                    } else if( horizontalMeasuredFrom == 'left' ) {
                        positionZ = _this._bones['b_f_l2'].position.z - distanceSide;
                    } else if( horizontalMeasuredFrom == 'distribute' ) {
                        positionZ = horizontalDistance;
                    }

                    if( 'building' === verticalMeasuredFrom ) {
                        positionY = _this._bones['b_f_t'].position.y - verticalDistance;
                    } else if( 'wall' === verticalMeasuredFrom ) {
                        positionY = _this._bones['b_f_l2'].position.y - verticalDistance;
                    } else if( 'ground' === verticalMeasuredFrom ) {
                        positionY = verticalDistance;
                    } else {
                        positionY = 0;
                    }

                } else {
                    positionX = _this._bones['b_f_l5'].position.x;

                    if( horizontalMeasuredFrom == 'right' ) {
                        positionZ = _this._bones['b_b_l5'].position.z + distanceSide;
                    } else if( horizontalMeasuredFrom == 'left' ) {
                        positionZ = _this._bones['b_f_l5'].position.z - distanceSide;
                    } else if( horizontalMeasuredFrom == 'distribute' ) {
                        positionZ = horizontalDistance;
                    }

                    if( 'building' === verticalMeasuredFrom ) {
                        positionY = _this._bones['b_f_t'].position.y - verticalDistance;
                    } else if( 'wall' === verticalMeasuredFrom ) {
                        positionY = _this._bones['b_f_l5'].position.y - verticalDistance;
                    } else if( 'ground' === verticalMeasuredFrom ) {
                        positionY = verticalDistance;
                    } else {
                        positionY = 0;
                    }
                }

                break;
            case 'top_right':
                if( this.is2StoryLeanTo() ) {
                    positionX = _this._bones['b_b_r2'].position.x;
                    
                    if( horizontalMeasuredFrom == 'right' ) {
                        positionZ = _this._bones['b_b_r2'].position.z + distanceSide;
                    } else if( horizontalMeasuredFrom == 'left' ) {
                        positionZ = _this._bones['b_f_r2'].position.z - distanceSide;
                    } else if( horizontalMeasuredFrom == 'distribute' ) {
                        positionZ = horizontalDistance;
                    }

                    if( 'building' === verticalMeasuredFrom ) {
                        positionY = _this._bones['b_f_t'].position.y - verticalDistance;
                    } else if( 'wall' === verticalMeasuredFrom ) {
                        positionY = _this._bones['b_f_l2'].position.y - verticalDistance;
                    } else if( 'ground' === verticalMeasuredFrom ) {
                        positionY = verticalDistance;
                    } else {
                        positionY = 0;
                    }

                } else {
                    positionX = _this._bones['b_f_r5'].position.x;
                    if( 'right' === horizontalMeasuredFrom ) {
                        positionZ = _this._bones['b_b_r5'].position.z + distanceSide;
                    } else if( 'left' === horizontalMeasuredFrom ) {
                        positionZ = _this._bones['b_f_r5'].position.z - distanceSide;
                    } else if( 'distribute' === horizontalMeasuredFrom ) {
                        positionZ = horizontalDistance;
                    }

                    if( 'building' === verticalMeasuredFrom ) {
                        positionY = _this._bones['b_f_t'].position.y - verticalDistance;
                    } else if( 'wall' === verticalMeasuredFrom ) {
                        positionY = _this._bones['b_f_l5'].position.y - verticalDistance;
                    } else if( 'ground' === verticalMeasuredFrom ) {
                        positionY = verticalDistance;
                    } else {
                        positionY = 0;
                    }
                }

                break;
            case 'top_front':
                positionX = 0;
                positionZ = _this._bones['b_f_l1'].position.z;
                if( 'structure_monitorbarns_helmuth' === _this._shedType ) {
                    positionY = _this._bones['b_f_l3'].position.y - 0.2;
                }

                if( 'structure_rancher_modular' === _this._shedTypeVersion ) {
                    positionY = _this._bones['b_f_l3'].position.y + 0.1;
                }

                if( this.is2StoryLeanTo() ) {
                    if( 'right' === horizontalMeasuredFrom ) {
                        positionX = sidingFBStart + distanceSide;
                    } else if( 'left' === horizontalMeasuredFrom ) {
                        positionX = sidingFBEnd - distanceSide;
                    } else if ( 'distribute' === horizontalMeasuredFrom ) {
                        positionX = horizontalDistance;
                    }
    
                    if( 'distribute' === horizontalMeasuredFrom ) {
                        positionY = horizontal.positionY
                    } else if( 'building' === verticalMeasuredFrom ) {
                        positionY = _this._bones['b_f_t'].position.y - verticalDistance;
                    } else if( 'wall' === verticalMeasuredFrom ) {
                        positionY = _this._bones['b_f_l2'].position.y - verticalDistance;
                    } else if( 'ground' === verticalMeasuredFrom ) {
                        positionY = verticalDistance;
                    } else {
                        positionY = 0;
                    }
                }

                break;
            case 'top_back':
                positionX = 0;
                positionZ = _this._bones['b_b_l1'].position.z;
                if( 'structure_monitorbarns_helmuth' === _this._shedType ) {
                    positionY = _this._bones['b_f_l3'].position.y - 0.2;
                }

                if( 'structure_rancher_modular' === _this._shedTypeVersion ) {
                    positionY = _this._bones['b_f_l3'].position.y + 0.1;
                }

                if( 'right' === horizontalMeasuredFrom ) {
                    positionX = sidingFBEnd - distanceSide;                    
                } else if( 'left' === horizontalMeasuredFrom ) {
                    positionX = sidingFBStart + distanceSide;
                } else if( 'distribute' === horizontalMeasuredFrom ) {
                    positionX = - horizontalDistance;
                }

                if( 'distribute' === horizontalMeasuredFrom ) {
                    positionY = horizontal.positionY
                } else if( 'building' === verticalMeasuredFrom ) {
                    positionY = _this._bones['b_b_t'].position.y - verticalDistance;
                } else if( 'wall' === verticalMeasuredFrom ) {
                    positionY = _this._bones['b_b_l2'].position.y - verticalDistance;
                } else if( 'ground' === verticalMeasuredFrom ) {
                    positionY = verticalDistance;
                } else {
                    positionY = 0;
                }

                break;
            case 'inner_back':
                if( horizontalMeasuredFrom == 'right' ) {
                    positionX = _this._bones['b_p_l1'].position.x + constants.cabinPorchDepth - distanceSide;
                }else if( horizontalMeasuredFrom == 'left' ) {
                    positionX = _this._bones['b_p_l1'].position.x + distanceSide;
                }else if ( horizontalMeasuredFrom == 'distribute' ) {
                    positionX = sidingFBStart - horizontalDistance;
                }
                
                positionZ = _this._bones['b_p_l1'].position.z;

                break;
            case 'lean_to_front':
                positionZ = _this._bones['b_f_l1'].position.z;
                if( 'right' === horizontalMeasuredFrom ) {
                    positionX = _this._bones['lean_to_f_r1'].position.x + distanceSide;
                } else if ( 'left' === horizontalMeasuredFrom ) {
                    positionX = _this._bones['lean_to_f_r2'].position.x - distanceSide;
                } else if ( 'distribute' === horizontalMeasuredFrom ) {
                    positionX = horizontalDistance;
                }

                if( 'building' === verticalMeasuredFrom ) {
                    positionY = _this._bones['b_f_t'].position.y - verticalDistance;
                } else if( 'wall' === verticalMeasuredFrom ) {
                    positionY = _this._bones['lean_to_f_r3'].position.y - verticalDistance;
                } else if( 'ground' === verticalMeasuredFrom ) {
                    positionY = verticalDistance;
                } else {
                    positionY = 0;
                }

                break;
            case 'lean_to_back':
                positionZ = _this._bones['b_b_l1'].position.z;
                if( 'right' === horizontalMeasuredFrom ) {
                    positionX = _this._bones['lean_to_b_r2'].position.x - distanceSide;
                } else if ( 'left' === horizontalMeasuredFrom ) {
                    positionX = _this._bones['lean_to_b_r1'].position.x + distanceSide;
                } else if ( 'distribute' === horizontalMeasuredFrom ) {
                    positionX = horizontalDistance;
                }

                if( 'building' === verticalMeasuredFrom ) {
                    positionY = _this._bones['b_b_t'].position.y - verticalDistance;
                } else if( 'wall' === verticalMeasuredFrom ) {
                    positionY = _this._bones['lean_to_b_r3'].position.y - verticalDistance;
                } else if( 'ground' === verticalMeasuredFrom ) {
                    positionY = verticalDistance;
                } else {
                    positionY = 0;
                }

                break;
            case 'roof_left':
                positionX = 0;
                
                if( 'skylight_window' === componentType ) {
                    positionY = this.getPositionYForSkylightWindow();

                    if( horizontalMeasuredFrom == 'right' ) {
                        positionZ = sidingLRStart + distanceSide;
                    } else if( horizontalMeasuredFrom == 'left' ) {
                        positionZ = sidingLREnd - distanceSide;
                    } else if( horizontalMeasuredFrom == 'distribute' ) {
                        positionZ = horizontalDistance;
                    }                    
                }

                break;
            case 'roof_right':
                positionX = 0;

                if( 'skylight_window' === componentType ) {
                    if( horizontalMeasuredFrom == 'right' ) {
                        positionZ = sidingLREnd - distanceSide;
                    } else if( horizontalMeasuredFrom == 'left' ) {
                        positionZ = sidingLRStart + distanceSide;
                    } else if( horizontalMeasuredFrom == 'distribute' ) {
                        positionZ = -horizontalDistance;
                    }

                    positionY = this.getPositionYForSkylightWindow();
                }

                break;
        }

        if( horizontal && horizontal.roof_top_aligned === 'default' && horizontalMeasuredFrom !== 'distribute' ) {
            if( side === 'front' || side === 'back' ) {
                if( undefined !== _this._bones[_this._midPoint] ) {
                    positionX = _this._bones[_this._midPoint].position.x;
                }

                if( _this.isSidePorch() || ( this.isCornerPorchLeft() && 'front' === side ) ){
                    positionX = ( sidingFBEnd + sidingFBStart ) / 2;

                    if( 'vent' === componentType && this._bones['b_b_t'] ) {
                        positionX = this._bones['b_b_t'].position.x;
                    }
                }else if( 'structure_quaker_wannabuysheds' === this._shedType ){
                    positionX = ((sidingFBStart + sidingFBEnd) / 2) - horizontalDistance;
                }

                if( 'lean_to' === _this._shedType || 'structure_monoslope_smithbilt' === _this._shedType || 'structure_lean_to_with_front_porch' === _this._shedType ) {
                    positionX = 0;
                }
            } else if ( 'porch' === side ) {
                positionX = _this._bones[_this._midPoint] ? _this._bones[_this._midPoint].position.x : 0;
            } else {
                if( _this.isMoveHorizontal( componentType ) ) {
                    positionX = 0;
                } else {
                    positionZ = 'structure_dog_kennel' === this._shedType ? dogKennelMiddlePositionZ : 0;
                }
            }

            if( 'left_front' === side || 'left_back' === side ) {
                positionZ = ( sidingLRStart + sidingLREnd ) / 2;
            }
            
            if( 'right_inner_porch' === side || 'left_inner_porch' === side ) {
                positionX = ( sidingFBEnd + sidingFBStart ) / 2;
                positionZ = 'right_inner_porch' === side ? - constants.middleWidth / 2 : constants.middleWidth / 2;
            }

            if( this.is2StoryLeanTo() ) {
                switch( side ) {
                    case 'top_front':
                    case 'lean_to_front':
                        positionZ = this._bones['b_f_l1'].position.z;
                        positionX = helpers.toFixed3( ( sidingFBEnd + sidingFBStart ) / 2 );
                        break;
                    case 'top_back':
                    case 'lean_to_back':
                        positionZ = this._bones['b_b_l1'].position.z;
                        positionX = helpers.toFixed3( ( sidingFBEnd + sidingFBStart ) / 2 );
                        break;
                }
            }

            if( _this.isCornerPorchLeft() ){
                if( 'left' === side ){
                    positionZ = ( sidingLRStart + sidingLREnd ) / 2;
                }

                if( 'inner_left' === side ){
                    positionZ = ( sidingLRStart + ( depth - constants.cornerDepth ) + sidingLREnd ) / 2;
                }

                if( 'inner_front' === side ){
                    positionZ = depth/2 - constants.cornerDepth;
                    positionX = ( sidingFBEnd + sidingFBStart ) / 2;
                }
            }

            if( 'structure_ranch_cabin' === _this._shedType || this.isAframeSingleGable() ) {
                switch( side ) {
                    case 'inner_back':
                        positionX = ( sidingFBEnd + sidingFBStart ) / 2;
                        positionZ = _this._bones['b_p_l1'].position.z;
                        break;
                    case 'inner_left':
                        positionZ = ( ( sidingLRStart + sidingLREnd ) / 2 ) - horizontalDistance;
                        break;
                    case 'back':
                        positionX = ( sidingFBEnd + sidingFBStart ) / 2;
                        break;
                    case 'left':
                        positionZ = ( sidingLRStart + sidingLREnd ) / 2;
                        break;
                }
            }

            if( _this.isDeluxeCornerLeftPorch() ) {
                switch( side ) {
                    case 'front':
                        positionX = ( sidingFBStart + sidingFBEnd ) / 2;
                        break;
                    case 'diagonal_right':
                        positionX = ( _this._bones['b_f_r6'].position.x + _this._bones['b_f_l4'].position.x ) / 2;
                        positionZ = ( _this._bones['b_f_r6'].position.z + _this._bones['b_f_l4'].position.z ) / 2;
                        break;
                    case 'inner_right':
                        positionZ = ( _this._bones['b_f_r4'].position.z + _this._bones['b_f_r6'].position.z ) / 2;
                        break;
                    case 'inner_front':
                        positionX = ( _this._bones['b_f_r1'].position.x + _this._bones['b_f_r4'].position.x ) / 2;
                        positionZ = _this._bones['b_f_r1'].position.z;
                        break;
                    case 'left':
                        positionZ = ( _this._bones['b_b_l1'].position.z + _this._bones['b_f_l1'].position.z ) / 2;
                        break;
                    case 'right':
                        positionZ = ( _this._bones['b_f_r1'].position.z + _this._bones['b_b_r1'].position.z ) / 2;
                        break;
                }
            }

            if( 'structure_rancher' === this._shedType ) {
                if( 'top_front' === side ) {
                    positionZ = _this._bones['b_f_l1'].position.z;
                } else if( 'top_back' === side ) {
                    positionZ = _this._bones['b_b_l1'].position.z;
                }
            }
        }

        if( _this.isDoubleGable() ) {
            if( 'left_inner_porch' === side ) {
                positionZ = _this._bones['b_p_l1'].position.z;
            } else if( 'right_inner_porch' === side ) {
                positionZ = _this._bones['b_p_l3'].position.z;
            } else if( 'front_inner_porch' === side ) {
                positionX = _this._bones['b_b_m4'].position.x;
            }
        }

        if( 'door' === componentType && 'top_front' !== side && 'top_back' !== side ) {
            positionY = this.getDoorFixedHeight( side );
        }

        if( this.applyFixedHeightWindows( side, componentType, componentName ) ) {
            if( ! ( this._doorFixedTarget.length === 0 && this.isFollowMainDoor() ) ) {
                positionY = this.getWindowsFixedPositionY( componentType, componentHeight, side );
            }
        }

        if( this.isPorchSide( side ) && this.isVerticalDistribute() ) {
            positionY = this.calculateMiddlePosition( componentHeight );
        }

        let result = { x: positionX, y: positionY, z: positionZ };

        if( this.isInteriorComponent( componentType ) && 'light' !== componentType ) {
            result = this.updateInteriorPosition( result, side );
        }

        return result;
    }

    getDogKennelBoxDepth() {
        return Math.abs( this._bones['b_b_r1'].position.z - this._bones['b_m_r1'].position.z );
    }

    /**
     * Distribute components evenly on the roof
     * Only handle for roof_left and roof_right side
     */
    distributeRoofComponents = () => {
        const roofSide = [ 'roof_left', 'roof_right' ];
        for( const side of roofSide ) {
            const components = Object.values( this._walls[side].items );
            if( ! components.length ) continue;

            const sortComponents = components;

            // sort components by position
            const sortOrder = side === 'roof_left' ? -1 : side === 'roof_right' ? 1 : 0;
            if( sortOrder !== 0 ) {
                sortComponents.sort( ( a, b ) => sortOrder * ( a['position'].z - b['position'].z ) );
            }

            // Calculate horizontal offsets
            const horizontalOffset = Number( this.getHorizontalOffset() ) || 0;
            const startPointOffset = horizontalOffset;
            const endPointOffset = horizontalOffset;

            // Distribute roof components
            const wallBoneName = this.getWallBones( side );
            const startPoint = this._bones[wallBoneName.left].position;
            const endPoint = this._bones[wallBoneName.right].position;
            this.distributeWallComponentsHorizontally( startPoint, endPoint, sortComponents, startPointOffset, endPointOffset );
        }
    }

    distributeComponentEqually(){
        if( ! this._isHorizontalDistribution ) return;

        // Switch to new distribution method if enabled in settings
        if( this.isBetaFeature( 'refactor_calc_layout_position' ) ) {
            this.distributeAllWallComponentsHorizontally();
            return;
        }

        const horizontalOffset  = this.getHorizontalOffset();
        
        let _this = this;

        for( const side in _this._walls ) {
            this.updatePositionLayoutComponents( side );

            let width = _this.isSidePorch() ? _this.getWidth() - _this._porchWidth : _this.getWidth();
            let depth = _this.getDepth();

            if( _this.isDoubleGable() ) {
                switch( side ) {
                    case 'left_front':
                    case 'left_back':
                        depth = 2 * this.getHalfGableWidth();
                        break;
                    case 'left_inner_porch':
                    case 'right_inner_porch':
                        width = constants.doubleGablePorchDepth;
                        break;
                    case 'front_inner_porch':
                        depth = this.doubleGableGetPorchWidth();
                        break;
                }
            } else if( _this.isCornerPorchLeft() ){
                if( 'front' === side ){
                    width = _this.getWidth() - constants.cornerWidth;
                }else if( 'left' === side ){
                    depth = _this.getDepth() - constants.cornerDepth;
                }else if( 'inner_front' === side ){
                    width = constants.cornerWidth;
                }else if( 'inner_left' === side ){
                    depth = constants.cornerDepth;
                }
            } else if( _this.isDeluxeCornerLeftPorch() ) {
                if( 'front' === side ) {
                    width = _this.deluxePorchGetFrontWidth();
                } else if( 'inner_front' === side ) {
                    width = _this.deluxePorchGetPorchWidth();
                } else if( 'inner_right' === side ) {
                    depth = constants.deluxeDepth;
                } else if( 'left' === side ) {
                    depth = _this.getDepth() - constants.deluxeDepth;
                } else if( 'right' === side ) {
                    depth = _this.getDepth() - 3 * constants.deluxeDepth;
                }
            } else if( this.isMiddlePorch() ) {
                if( 'left_front' === side || 'left_back' === side ) {
                    depth = ( _this.getDepth() - constants.middleWidth ) / 2;
                } else if( 'left_inner_porch' === side || 'right_inner_porch' === side ) {
                    width = constants.middleDepth;
                } else if( 'front_inner_porch' === side ) {
                    depth = constants.middleWidth;
                }
            } else if( 'structure_ranch_cabin' === _this._shedType || this.isAframeSingleGable() ) {
                switch( side ) {
                    case 'inner_back':
                        width = helpers.toFixed3( Vector3.Distance( _this._bones['b_p_l2'].position, _this._bones['b_b_m3'].position ) );
                        break;
                    case 'inner_left':
                        depth = helpers.toFixed3( _this.getDepth() - _this._cabinWidth );
                        break;
                    case 'back':
                        width = helpers.toFixed3( _this.getWidth() - constants.cabinPorchDepth );
                        break;
                    case 'left':
                        depth = _this._cabinWidth;
                        break;
                }
            } else if( this.is2StoryLeanTo() ) {
                switch( side ) {
                    case 'lean_to_front':
                    case 'lean_to_back':
                        width = atticModel.leanToWidth;
                        break;
                }
            }

            if( _this.ignoreHandleTackDoor( side ) ) {
                continue;
            }
            
            let sideComponentLength: float  = 0;
            let sideComponentCount: number  = 0;
            let firstComponentKey : string  = '';
            const wallsComponents           = _this._walls[side]["items"];
            let sortComponents              = [];

            for( var key in wallsComponents ) {
                sortComponents.push( [ key, wallsComponents[key] ] );
            }

            sortComponents = sortComponents.filter( element => {
                const type      = element[1]['type'];
                const name      = element[1]['name'];
                const behavior  = _this.getBehavior( name, type );

                return 'windows' === behavior || 'door' === behavior;
            } );

            if( ! this._isUpdateLayout ) {
                sortComponents.sort( ( a, b ) => ( a[1]['order'] - b[1]['order'] ) );
            }

            for( let i = 0; i < sortComponents.length; i++ ) {
                const component = sortComponents[i][1];
                if( component.isTackDoor ) continue;
                sideComponentCount++;
                if( sideComponentCount === 1 ) {
                    firstComponentKey = sortComponents[i][0];
                }

                if( component != undefined && component != null ) {
                    sideComponentLength += component.width;
                }
            }
            
            let sidingLength = ( side === 'front' || side === 'top_front' || side === 'lean_to_front' || side === 'back' || side === 'top_back' || side === 'lean_to_back' || side === 'inner_back' || side === 'inner_front' || 'porch' === side || 'right_inner_porch' === side || 'left_inner_porch' === side || 'diagonal_right' === side ) ? width : 'structure_dog_kennel' === _this._shedType ? this.getDogKennelBoxDepth() : depth;
            let sidingLengthDistance = sidingLength - sideComponentLength - (horizontalOffset * 2) - (_this._offsetDistance * 2);
            let horizontalDistance = sidingLengthDistance / (sideComponentCount + 1);
            let halfSidingLength : float = (sidingLength / 2);
            let currentComponentLength : float = 0;

            if( _this.isDoubleGable() ) {
                const halfGableWidth = _this.getHalfGableWidth();
                switch( side ) {
                    case 'left_front':
                        currentComponentLength = helpers.toFixed3( - _this.getDepth() / 2 + halfGableWidth );
                        break;
                    case 'left_back':
                        currentComponentLength = helpers.toFixed3( halfGableWidth + _this.doubleGableGetPorchWidth() / 2 );
                        break;
                    case 'left_inner_porch':
                        currentComponentLength = constants.doubleGablePorchDepth / 2;
                        break;
                    case 'right_inner_porch':
                        currentComponentLength = - constants.doubleGablePorchDepth / 2;
                        break;
                }
            } else if( _this.isSidePorch() ){
                if( 'front' === side ){
                    currentComponentLength = - _this._porchWidth/2;
                }else if( 'back' === side ){
                    currentComponentLength = _this._porchWidth/2;
                }
            } else if( _this.isCornerPorchLeft() ){
                if( 'front' === side ){
                    currentComponentLength = - constants.cornerWidth / 2;
                }else if( 'left' === side ){
                    currentComponentLength = constants.cornerDepth / 2;
                }else if( 'inner_front' === side ){
                    currentComponentLength = _this.getWidth() / 2 - constants.cornerWidth / 2;
                }else if( 'inner_left' === side ){
                    currentComponentLength = - _this.getDepth() / 2 + constants.cornerDepth / 2;
                }
            } else if( this.isMiddlePorch() ) {
                if( 'left_front' === side || 'left_back' === side ) {
                    currentComponentLength = ( _this.getDepth() - constants.middleWidth ) / 4;
                } else if( 'left_inner_porch' === side || 'right_inner_porch' === side ) {
                    currentComponentLength = constants.middleDepth / 2;
                } else if( 'front_inner_porch' === side ) {
                    currentComponentLength = constants.middleWidth / 2;
                }
            } else if( 'structure_ranch_cabin' === _this._shedType || this.isAframeSingleGable() ) {
                switch( side ) {
                    case 'inner_back':
                        currentComponentLength = helpers.toFixed3( Vector3.Distance( _this._bones['b_p_l2'].position, _this._bones['b_b_m3'].position ) / 2 );
                        break;
                    case 'inner_left':
                        currentComponentLength = helpers.toFixed3( _this._cabinWidth / 2 );
                        break;
                    case 'back':
                        currentComponentLength = helpers.toFixed3( constants.cabinPorchDepth / 2  );
                        break;
                    case 'left':
                        currentComponentLength = - _this.getDepth() / 2 + _this._cabinWidth / 2;
                        break;
                }
            } else if( this.is2StoryLeanTo() ) {
                switch( side ) {
                    case 'lean_to_front':
                    case 'lean_to_back':
                        currentComponentLength = - helpers.toFixed3( ( _this.getWidth() + atticModel.leanToWidth ) / 2 );
                        break;
                }
            } else if( _this.isDeluxeCornerLeftPorch() ) {
                if( 'front' === side ) {
                    currentComponentLength = ( _this.deluxePorchGetPorchWidth() + constants.deluxeDepth ) / 2;
                } else if( 'inner_front' === side ) {
                    currentComponentLength = - ( ( _this.deluxePorchGetFrontWidth() +  constants.deluxeDepth ) / 2 );
                } else if( 'inner_right' === side ) {
                    currentComponentLength = - ( ( _this.getDepth() - constants.deluxeFullDepth ) / 2 );
                } else if( 'left' === side ) {
                    currentComponentLength = constants.deluxeDepth / 2;
                } else if( 'right' === side ) {
                    currentComponentLength = 3 * constants.deluxeDepth / 2;
                }
            }
            
            if( this._isEnableTackRoom && ( 'right' === side ||  'left' === side ) && side === this._sideWallTackRoom ) {
                const dataTackRoom = this._dataTackroom;
                if( dataTackRoom && dataTackRoom.width ) {
                    depth                   = depth - dataTackRoom.width;
                    sidingLength            = depth;
                    halfSidingLength        = depth / 2;
                    sidingLengthDistance    = sidingLength - sideComponentLength - ( horizontalOffset * 2 ) - ( _this._offsetDistance * 2 );
                    horizontalDistance      = sidingLengthDistance / ( sideComponentCount + 1 );
                    if( 'right' === side ) {
                        currentComponentLength = 'right' === dataTackRoom.position ? - dataTackRoom.width/2 : dataTackRoom.width/2;
                    } else {
                        currentComponentLength = 'left' === dataTackRoom.position ? - dataTackRoom.width/2 : dataTackRoom.width/2;
                    }
                }
            }
            
            for( let i = 0; i < sortComponents.length; i++ ) {
                const componentKey  = sortComponents[i][0]
                const component     = sortComponents[i][1];
                const { meshes, type, vertical, name, width, isTackDoor } = component;
                if( isTackDoor ) continue;
                currentComponentLength += width + horizontalDistance;
                if( componentKey === firstComponentKey ) {
                    currentComponentLength += _this._offsetDistance + horizontalOffset;
                }
                
                let position    = { x: 0, y: 0, z: 0 };
                const rotation  = _this.calculateComponentRotation( side, type );
                if( 'diagonal_right' === side ) {
                    const positionX = ( _this._bones['b_f_r6'].position.x + _this._bones['b_f_l4'].position.x ) / 2;
                    const positionZ = ( _this._bones['b_f_r6'].position.z + _this._bones['b_f_l4'].position.z ) / 2;
                    position        = { x: positionX, y: component.position.y, z: positionZ };
                } else {
                    const componentPosition = helpers.toFixed3( halfSidingLength - currentComponentLength + width / 2 );
                    const horizontal        = { distance: componentPosition, measured_from: 'distribute', positionY: component.position.y }
                    position                = _this.calculateComponentPosition( meshes, side, vertical, horizontal, type, name );
                }
                _this.updateComponentsPosition2D( side, componentKey, position, component );
                _this.placeComponent( meshes, position, rotation, type, side, name );
            }
        }
    }

    /**
     * Distribute all wall components horizontally across all sides of the wall.
     * 
     * This function checks if horizontal distribution is enabled, filters the valid wall components (windows and doors),
     * applies sorting based on their order, adjusts the distribution offsets for tack rooms if applicable, 
     * and finally distributes the components across the wall sides.
     * 
     * @returns {void}
     */
    distributeAllWallComponentsHorizontally(): void {
        if( ! this._isHorizontalDistribution ) return;

        // Loop through each wall side
        for( const side in this._walls ) {
            if( ! Object.keys( this._walls[side].items ).length || this.ignoreHandleTackDoor( side ) ) continue;
            this.updatePositionLayoutComponents( side );

            // Filter valid components
            const components = Object.values( this._walls[side].items ).filter( ( { name, type, isTackDoor } ) => {
                const behavior = this.getBehavior( name, type );
                return ! isTackDoor && ['windows', 'door'].includes( behavior );
            } );
            
            if( ! components.length ) continue;
            
            // Sort components by order
            if( ! this._isUpdateLayout ) {
                components.sort( ( a, b ) => ( a['order'] - b['order'] ) );
            }

            // Calculate horizontal offsets
            const horizontalOffset = Number( this.getHorizontalOffset() ) || 0;
            let startPointOffset = horizontalOffset;
            let endPointOffset = horizontalOffset;

            // Adjust offset for tack room, if applicable
            if( this._isEnableTackRoom && ( side === 'right' || side === 'left' ) && side === this._sideWallTackRoom ) {
                const { width, position } = this._dataTackroom || {};
                const tackRoomWidth = Number( width ) || 0;
                
                side === 'right' 
                ? position === 'left' ? startPointOffset += tackRoomWidth : endPointOffset += tackRoomWidth 
                : position === 'right' ? startPointOffset += tackRoomWidth : endPointOffset += tackRoomWidth;
            }

            // Distribute components
            const wallBoneName = this.getWallBones( side );
            const startPoint = this._bones[wallBoneName.left].position;
            const endPoint = this._bones[wallBoneName.right].position;
            this.distributeWallComponentsHorizontally( startPoint, endPoint, components, startPointOffset, endPointOffset );
        }
    }

    /**
     * Distribute wall components horizontally along a given segment of a wall.
     * 
     * This function calculates the positions of wall components, distributing them
     * evenly between the start and end points while considering offsets and widths.
     * 
     * @param {Vector3} startPoint Starting point of the wall side.
     * @param {Vector3} endPoint Ending point of the wall side.
     * @param {Array} components Array of component objects to be distributed.
     * @param {number} [startPointOffset=0] Offset from the starting point of the wall side.
     * @param {number} [endPointOffset=0] Offset from the ending point of the wall side.
     * 
     * @returns {void}
     */
    distributeWallComponentsHorizontally( startPoint: Vector3, endPoint: Vector3, components: any[], startPointOffset: number = 0, endPointOffset: number = 0 ): void {
        const start = new Vector3( startPoint.x, 0, startPoint.z );
        const end = new Vector3( endPoint.x, 0, endPoint.z );

        // Vector calculations
        const directionVector = end.subtract( start ); // Vector pointing from the start point to the end point
        const normalizedDirection = directionVector.normalize(); // Normalize the direction vector to a length of 1, keeping the direction unchanged.

        // Adjust start and end points by offset
        const adjustedStartPoint = start.add( normalizedDirection.scale( startPointOffset ) );
        const adjustedEndPoint = end.subtract( normalizedDirection.scale( endPointOffset ) );

        // Calculate the gap between components
        const wallLength = Vector3.Distance( adjustedStartPoint, adjustedEndPoint );
        const totalComponentLength = components.reduce( ( accumulator, component ) => accumulator + component.width, 0 );
        const remainingSpace = wallLength - totalComponentLength;
        const gap = remainingSpace / ( components.length + 1 );
        let currentPosition = gap;

        // Position each component
        components.forEach( component => {
            const { key, side, meshes, type, vertical, name, width, position } = component;
            const componentOffset = currentPosition + width / 2;
            
            // Calculate and apply new component position
            const newPosition = adjustedStartPoint.add( normalizedDirection.scale( componentOffset ) );
            position.x = newPosition.x;
            position.z = newPosition.z;
            this.updateComponentsPosition2D( side, key, position, component );
            this.placeComponent( meshes, position, false, type, side, name );

            currentPosition += width + gap; // Update offset for next component
        } );
    }

    checkCondition(boolean1, operator, boolean2) {
        return operator == 'and' ? (boolean1 && boolean2) : (boolean1 || boolean2);
    }

    private calculateRoofPitchHeight(roofDegree, width) {
        return Math.tan( roofDegree * Math.PI / 180 ) * width;
    }

    private calculateMidRoofPitchHeight() {
        let _this   = this;
        let bones   = _this._bones;
                
        if( ! bones['b_f_r3'] || ! bones['b_f_r2'] ) return 0;
        
        let midTop  = bones['b_f_r3'].position.y;
        let midBot  = bones['b_f_r2'].position.y;

        if( 'structure_barn_with_sprocket_wood_siding' === _this._shedType ) {
            return midTop - midBot - 0.73598007219;
        }

        return midTop - midBot;
    }

    private moveBones() {
        let _this = this;

        let initialWidth = _this.getInitialWidth();
        let width: number = _this.getWidth();
        let bones = _this._bones;
        let roofDegree = _this.getRoofDegree();

        if ( 'greenhouse' === _this._shedType ) {
            if (_this.getWidth() < 10) {
                roofDegree = 45;
            } else {
                roofDegree = 35;
            }
        }

        let newWidth: number = _this.getWidth() / 2;
        let newDepth: number = _this.getDepth() / 2;
        let newWallHeight: number = _this.getWallHeight();
        
        let newRoofPitchHeight = _this.calculateRoofPitchHeight(roofDegree, newWidth);

        if( 'carport_standard_regularroof' === _this._shedType ){
            newRoofPitchHeight = _this.calculateRoofPitchHeight( roofDegree, newWidth - 1.079 ) + 1.847;
        }

        let newBuildingHeight: number = newRoofPitchHeight + newWallHeight;

        let fixedPart = Math.floor((newDepth*2) / 3) + ((newDepth*2) % 3);

        let errorNumber = 0;
        const roofShape =  _this.getRoofShape();
        if ( roofShape == "gambrel" ) {
            let newRoofMidPointX    = width / _this._initGambrelMidXRatio;
            let newRoofMidPointY    = ( width / 2 - newRoofMidPointX ) * helpers.getTanFromDegrees( 0 !== _this._roofLowerDegree ? _this._roofLowerDegree : _this._initRoofLowerDegree ) + newWallHeight;

            if( 'structure_barn_with_sprocket' === _this._shedType || 'structure_barn_with_sprocket_wood_siding' === _this._shedType ) {
                const ratio = [[9, 0.025], [10, 0.05], [11, 0.075], [12, 0.1], [13, 0.125], [14, 0.15], [15, 0.175], [16, 0.23]];
                ratio.forEach( e => {
                    if( width == e[0] ) {
                        newRoofMidPointX += e[1];
                        newRoofMidPointY += e[1];
                    }
                });
            }

            _this._roofMidPointX    = newRoofMidPointX;
            _this._roofMidPointY    = newRoofMidPointY;

            const y_BBT_BBL3    = newRoofMidPointX * helpers.getTanFromDegrees( 0 !== _this._roofUpperDegree ? _this._roofUpperDegree : _this._initRoofUpperDegree );
            newBuildingHeight   = newRoofMidPointY + y_BBT_BBL3;
            newRoofPitchHeight  = newBuildingHeight - newRoofMidPointY;

            if( 'structure_cardinal_lofted_cabin' === _this._shedType ) {
                let tanRoof         = _this._roofLowerDegree ? helpers.getTanFromDegrees( _this._roofLowerDegree ) : helpers.getTanFromDegrees( _this._initRoofLowerDegree );
                let tanRoofUpper    = _this._roofUpperDegree ? helpers.getTanFromDegrees( _this._roofUpperDegree ) :  helpers.getTanFromDegrees( _this._initRoofUpperDegree );
                let newY            = newWallHeight + ( ( this._initialWidth / 2 ) - this._roofMiddleR2Position.x ) * tanRoof;
                let newX            = Number( Math.abs( newWidth - ( ( this._initialWidth / 2 ) - this._roofMiddleR2Position.x ) ).toFixed( 3 ) );
                newBuildingHeight   = newY + tanRoofUpper * newX;

                _this._roofMidPointX    = newX;
                _this._roofMidPointY    = newY;
            }

            if( 'structure_country_cottage' === _this._shedType ) {
                if( bones['b_d_t'] != undefined ) bones['b_d_t'].setPosition( new Vector3( -newWidth, newBuildingHeight, 0 ) );
                if( bones['r_d_t1'] != undefined ) bones['r_d_t1'].setPosition( new Vector3( 0, newBuildingHeight, 0 ) );
                if( bones['r_d_t2'] != undefined ) bones['r_d_t2'].setPosition( new Vector3( -newWidth, newBuildingHeight, 0 ) );

                if( bones['r_d_l1'] != undefined ) bones['r_d_l1'].setPosition( new Vector3( -newWidth, newWallHeight, newWidth ) );
                if( bones['r_d_l2'] != undefined ) bones['r_d_l2'].setPosition( new Vector3( -newWidth, newRoofMidPointY, newRoofMidPointX ) );
                if( bones['r_d_l3'] != undefined ) bones['r_d_l3'].setPosition( new Vector3( -newRoofMidPointX, newRoofMidPointY, newRoofMidPointX ) );
                if( bones['r_d_l4'] != undefined ) bones['r_d_l4'].setPosition( new Vector3( 0, newRoofMidPointY, newRoofMidPointX ) );
                if( bones['r_d_l5'] != undefined ) bones['r_d_l5'].setPosition( new Vector3( 0, newWallHeight, newWidth ) );
                if( bones['r_d_l6'] != undefined ) bones['r_d_l6'].setPosition( new Vector3( -newRoofMidPointX, newRoofMidPointY, newWidth ) );

                if( bones['r_d_m1'] != undefined ) bones['r_d_m1'].setPosition( new Vector3( -newRoofMidPointX, newRoofMidPointY, 0 ) );
                if( bones['r_d_m2'] != undefined ) bones['r_d_m2'].setPosition( new Vector3( -newWidth, newWallHeight, 0 ) );

                if( bones['r_d_r1'] != undefined ) bones['r_d_r1'].setPosition( new Vector3( -newWidth, newWallHeight, -newWidth ) );
                if( bones['r_d_r2'] != undefined ) bones['r_d_r2'].setPosition( new Vector3( -newWidth, newRoofMidPointY, -newRoofMidPointX ) );
                if( bones['r_d_r3'] != undefined ) bones['r_d_r3'].setPosition( new Vector3( -newRoofMidPointX, newRoofMidPointY, -newRoofMidPointX ) );
                if( bones['r_d_r4'] != undefined ) bones['r_d_r4'].setPosition( new Vector3( 0, newRoofMidPointY, -newRoofMidPointX ) );
                if( bones['r_d_r5'] != undefined ) bones['r_d_r5'].setPosition( new Vector3( 0, newWallHeight, -newWidth ) );
                if( bones['r_d_r6'] != undefined ) bones['r_d_r6'].setPosition( new Vector3( -newRoofMidPointX, newRoofMidPointY, -newWidth ) );

                if( bones['b_d_l3'] != undefined ) bones['b_d_l3'].setPosition( new Vector3( -newWidth, 0, newWidth ) );
                if( bones['b_d_l2'] != undefined ) bones['b_d_l2'].setPosition( new Vector3( -newWidth, newWallHeight, newWidth ) );
                if( bones['b_d_l1'] != undefined ) bones['b_d_l1'].setPosition( new Vector3( -newWidth, newRoofMidPointY, newRoofMidPointX ) );

                if( bones['b_d_r3'] != undefined ) bones['b_d_r3'].setPosition( new Vector3( -newWidth, 0, -newWidth ) );
                if( bones['b_d_r2'] != undefined ) bones['b_d_r2'].setPosition( new Vector3( -newWidth, newWallHeight, -newWidth ) );
                if( bones['b_d_r1'] != undefined ) bones['b_d_r1'].setPosition( new Vector3( -newWidth, newRoofMidPointY, -newRoofMidPointX ) );
            }

            if( 'structure_lofted_horserunin' === _this._shedType ) {
                if( bones['inner_frame_horizontal_clone_l1'] ) bones['inner_frame_horizontal_clone_l1'].setPosition( new Vector3( -newWidth, newWallHeight, bones['inner_frame_horizontal_clone_l1'].position.z ) );
                if( bones['inner_frame_horizontal_clone_l2'] ) bones['inner_frame_horizontal_clone_l2'].setPosition( new Vector3( -newRoofMidPointX, newRoofMidPointY, bones['inner_frame_horizontal_clone_l2'].position.z ) );
                if( bones['inner_frame_horizontal_clone_t'] ) bones['inner_frame_horizontal_clone_t'].setPosition( new Vector3( bones['inner_frame_horizontal_clone_t'].position.x, newBuildingHeight, bones['inner_frame_horizontal_clone_t'].position.z ) );
                if( bones['inner_frame_horizontal_clone_r2'] ) bones['inner_frame_horizontal_clone_r2'].setPosition( new Vector3( newWidth, newWallHeight, bones['inner_frame_horizontal_clone_r2'].position.z ) );
                if( bones['inner_frame_horizontal_clone_r3'] ) bones['inner_frame_horizontal_clone_r3'].setPosition( new Vector3( newRoofMidPointX, newRoofMidPointY, bones['inner_frame_horizontal_clone_r3'].position.z ) );
                if( bones['inner_frame_horizontal_clone_r1'] ) bones['inner_frame_horizontal_clone_r1'].setPosition( new Vector3( newWidth, bones['inner_frame_horizontal_clone_r1'].position.y, bones['inner_frame_horizontal_clone_l1'].position.z ) );
                
                if( bones['inner_wall_clone_l1'] ) bones['inner_wall_clone_l1'].setPosition( new Vector3( - newWidth, bones['inner_wall_clone_l1'].position.y, bones['inner_wall_clone_l1'].position.z ) );
                if( bones['inner_wall_clone_l2'] ) bones['inner_wall_clone_l2'].setPosition( new Vector3( - newWidth, newWallHeight, bones['inner_wall_clone_l2'].position.z ) );
                if( bones['inner_wall_clone_l3'] ) bones['inner_wall_clone_l3'].setPosition( new Vector3( - newRoofMidPointX, newRoofMidPointY, bones['inner_wall_clone_l3'].position.z ) );
                if( bones['inner_wall_clone_t'] ) bones['inner_wall_clone_t'].setPosition( new Vector3( bones['inner_wall_clone_t'].position.x, newBuildingHeight, bones['inner_wall_clone_t'].position.z ) );
                if( bones['inner_wall_clone_r1'] ) bones['inner_wall_clone_r1'].setPosition( new Vector3( newWidth, bones['inner_wall_clone_r1'].position.y, bones['inner_wall_clone_r1'].position.z ) );
                if( bones['inner_wall_clone_r2'] ) bones['inner_wall_clone_r2'].setPosition( new Vector3( newWidth, newWallHeight, bones['inner_wall_clone_r2'].position.z ) );
                if( bones['inner_wall_clone_r3'] ) bones['inner_wall_clone_r3'].setPosition( new Vector3( newRoofMidPointX, newRoofMidPointY, bones['inner_wall_clone_r3'].position.z ) );
            }

            if (bones['b_b_l3'] != undefined) bones['b_b_l3'].setPosition(new Vector3(-newRoofMidPointX, newRoofMidPointY, -newDepth));
            if (bones['b_f_l3'] != undefined) bones['b_f_l3'].setPosition(new Vector3(-newRoofMidPointX, newRoofMidPointY, newDepth));
            if (bones['b_b_r3'] != undefined) bones['b_b_r3'].setPosition(new Vector3(newRoofMidPointX, newRoofMidPointY, -newDepth));
            if (bones['b_f_r3'] != undefined) bones['b_f_r3'].setPosition(new Vector3(newRoofMidPointX, newRoofMidPointY, newDepth));

            if (bones['b_b_m5'] != undefined) bones['b_b_m5'].setPosition(new Vector3(newRoofMidPointX, newRoofMidPointY, newDepth - constants.cornerDepth));
            if (bones['b_p_l5'] != undefined) bones['b_p_l5'].setPosition(new Vector3(-newRoofMidPointX, newRoofMidPointY, newDepth - constants.cornerDepth));

            if (bones['r_b_l2'] != undefined) bones['r_b_l2'].setPosition(new Vector3(-newRoofMidPointX, newRoofMidPointY, -newDepth));
            if (bones['r_f_l2'] != undefined) bones['r_f_l2'].setPosition(new Vector3(-newRoofMidPointX, newRoofMidPointY, newDepth));
            if (bones['r_b_r2'] != undefined) bones['r_b_r2'].setPosition(new Vector3(newRoofMidPointX, newRoofMidPointY, -newDepth));
            if (bones['r_f_r2'] != undefined) bones['r_f_r2'].setPosition(new Vector3(newRoofMidPointX, newRoofMidPointY, newDepth));

            if (bones['rm_l2'] != undefined) bones['rm_l2'].setPosition(new Vector3(-newRoofMidPointX, newRoofMidPointY, -newDepth));
            if (bones['rm_r2'] != undefined) bones['rm_r2'].setPosition(new Vector3(newRoofMidPointX, newRoofMidPointY, -newDepth));

            if( 'structure_lakeside_bunkie_gambrel_roof' === _this._shedType || 'structure_lakeside_bunkie_gable_roof' === _this._shedType ){
                const overHangZ = newDepth + 32/12;

                if (bones['r_f_l2'] != undefined) bones['r_f_l2'].setPosition(new Vector3(-newRoofMidPointX, newRoofMidPointY, overHangZ));
                if (bones['r_f_r2'] != undefined) bones['r_f_r2'].setPosition(new Vector3(newRoofMidPointX, newRoofMidPointY, overHangZ));
            }

            _this.deluxePorchGambrelMoveBones( bones, newRoofMidPointX, newRoofMidPointY, newDepth );
        } else if ( roofShape == "saltbox" && 'mainus_quaker' !== _this._shedType ) {
            let extraHeight = 0;

            newWallHeight = newWallHeight + extraHeight;
            newRoofPitchHeight = _this.calculateRoofPitchHeight(roofDegree, newWidth);
            newBuildingHeight = newRoofPitchHeight + newWallHeight;
        }  else if ( roofShape == "open_gable" ) {
            let extraHeight = 0.2;

            if( 'structure_aframe_metal_frontporch' === _this._shedType || 'structure_cardinal_victorian' === _this._shedType || 'structure_cardinal_cabin' === _this._shedType ){
                extraHeight = 0;
            }

            newWallHeight = newWallHeight + extraHeight;
            newRoofPitchHeight = _this.calculateRoofPitchHeight(roofDegree, newWidth);
            newBuildingHeight = newRoofPitchHeight + newWallHeight;
        } else if ( roofShape == "flat" ) {
            newRoofPitchHeight = 0;
            newBuildingHeight = newWallHeight;
            if( 'lean_to' === _this._shedType || 'structure_monoslope_smithbilt' === _this._shedType ) {
                newRoofPitchHeight  = _this.calculateRoofPitchHeight( _this.getInitRoofDegree(), _this.getWidth() );
                const leanToHeight  = newWallHeight + newWidth * 2 * helpers.getTanFromDegrees( this.getRoofDegree() );
                newBuildingHeight   = leanToHeight;
                
                if( bones['b_b_t'] ) bones['b_b_t'].setPosition( new Vector3( -newWidth, leanToHeight, -newDepth ) );
                
                if( undefined !== bones['b_f_t'] ) {
                    bones['b_f_t'].setPosition( new Vector3( -newWidth, leanToHeight, newDepth ) );
                }
                if( undefined !== bones['b_b_l2'] ) {
                    bones['b_b_l2'].setPosition( new Vector3( -newWidth, newWallHeight, -newDepth ) );
                }
                if( undefined !== bones['b_f_l2'] ) {
                    bones['b_f_l2'].setPosition( new Vector3( -newWidth, newWallHeight, newDepth ) );
                }

                errorNumber = 0;
                if( undefined !== bones['b_b_r1'] ) {
                    bones['b_b_r1'].setPosition(new Vector3(newWidth - errorNumber, bones['b_b_r1'].position.y, -newDepth));
                }
                if( undefined !== bones['b_b_r2'] ) {
                    bones['b_b_r2'].setPosition( new Vector3( newWidth - errorNumber, newWallHeight, -newDepth ) );
                }
                if( undefined !== bones['b_f_r1'] ) {
                    bones['b_f_r1'].setPosition(new Vector3(newWidth - errorNumber, bones['b_f_r1'].position.y, newDepth));
                }
                if( undefined !== bones['b_f_r2'] ) {
                    bones['b_f_r2'].setPosition(new Vector3(newWidth - errorNumber, newWallHeight, newDepth));
                }

                const leanToBones = [ 'r_b_l1', 'r_f_l1', 'rm_l1' ];
                leanToBones.forEach( b => {
                    if( undefined !== bones[b] ) {
                        const tmpDepth = 'b_f_l2' === b || 'r_f_l1' === b ? newDepth : -newDepth;
                        bones[b].setPosition( new Vector3( -newWidth, leanToHeight, tmpDepth ) );
                    }
                });

                
                const horizontalTrimObj = _this.getMeshByName( 'horizontal_trim_obj' );
                if( horizontalTrimObj ) {
                    let trimFixedHeight     = 8;
                    const trimHeight        = 0.3;
                    if( 'structure_lean_to_sunrise_shed_oklahoma' === _this._shedTypeVersion ) {
                        trimFixedHeight = 7.167;
                    }
                    
                    if( trimFixedHeight + trimHeight > leanToHeight ) {
                        horizontalTrimObj.setEnabled( false );
                    } else {
                        horizontalTrimObj.setEnabled( true );
                    }
                    
                    let trimSpecialBoneBottom   = newWidth;
                    let trimSpecialBoneTop      = newWidth;
                    if( newWallHeight <= trimFixedHeight ) {
                        const trimErrorNumber   = 0.24;
                        trimSpecialBoneBottom   = trimSpecialBoneBottom - ( trimFixedHeight - newWallHeight ) / helpers.getTanFromDegrees( this.getRoofDegree() );
                        trimSpecialBoneTop      = trimSpecialBoneBottom - ( trimHeight / helpers.getTanFromDegrees( this.getRoofDegree() ) );
    
                        trimSpecialBoneBottom   = trimSpecialBoneBottom + trimErrorNumber;
                        trimSpecialBoneTop      = trimSpecialBoneTop + trimErrorNumber;
                    }
    
                    if( bones['horizontal_trim_b_l'] ) bones['horizontal_trim_b_l'].setPosition( new Vector3( - newWidth, trimFixedHeight, - newDepth ) );
                    if( bones['horizontal_trim_b_r1'] ) bones['horizontal_trim_b_r1'].setPosition( new Vector3( trimSpecialBoneBottom, trimFixedHeight, - newDepth ) );
                    if( bones['horizontal_trim_b_r2'] ) bones['horizontal_trim_b_r2'].setPosition( new Vector3( trimSpecialBoneTop, trimFixedHeight + trimHeight, - newDepth ) );
    
                    if( bones['horizontal_trim_f_l'] ) bones['horizontal_trim_f_l'].setPosition( new Vector3( - newWidth, trimFixedHeight, newDepth ) );
                    if( bones['horizontal_trim_f_r1'] ) bones['horizontal_trim_f_r1'].setPosition( new Vector3( trimSpecialBoneBottom, trimFixedHeight, newDepth ) );
                    if( bones['horizontal_trim_f_r2'] ) bones['horizontal_trim_f_r2'].setPosition( new Vector3( trimSpecialBoneTop, trimFixedHeight + trimHeight, newDepth ) );
                }
            }else if( 'structure_loafing_shed' === _this._shedType ) {
                newRoofPitchHeight  = _this.calculateRoofPitchHeight( roofDegree, _this.getWidth() );
                newBuildingHeight   = newWallHeight + newRoofPitchHeight;
            }
        }else if( 'hip' === roofShape ){
            let extraHeight = 4/12;
            newBuildingHeight += extraHeight;
        } else if( 'special' === roofShape ) {
            const roofWidth     = newWidth * 2 / 3;
            const roofX         = newWidth - roofWidth;
            const lowerRoofTopY = roofWidth * helpers.getTanFromDegrees( 22.62 ) + newWallHeight;
            const roofTopY      = lowerRoofTopY + _this._dormerHeight;

            if( bones['r_f_l1'] ) bones['r_f_l1'].setPosition( new Vector3( -roofX, newWallHeight, newDepth ) );
            if( bones['r_f_r1'] ) bones['r_f_r1'].setPosition( new Vector3( roofX, newWallHeight, newDepth ) );
            if( bones['r_f_t1'] ) bones['r_f_t1'].setPosition( new Vector3( -roofX, lowerRoofTopY, newDepth ) );
            if( bones['r_f_t2'] ) bones['r_f_t2'].setPosition( new Vector3( -roofX, roofTopY, newDepth ) );

            if( bones['r_b_l1'] ) bones['r_b_l1'].setPosition( new Vector3( -roofX, newWallHeight, -newDepth ) );
            if( bones['r_b_r1'] ) bones['r_b_r1'].setPosition( new Vector3( roofX, newWallHeight, -newDepth ) );
            if( bones['r_b_t1'] ) bones['r_b_t1'].setPosition( new Vector3( -roofX, lowerRoofTopY, -newDepth ) );
            if( bones['r_b_t2'] ) bones['r_b_t2'].setPosition( new Vector3( -roofX, roofTopY, -newDepth ) );

            if( bones['b_f_t1'] ) bones['b_f_t1'].setPosition( new Vector3( -roofX, lowerRoofTopY, newDepth ) );
            if( bones['b_f_t2'] ) bones['b_f_t2'].setPosition( new Vector3( -roofX, roofTopY, newDepth ) );

            if( bones['b_b_t1'] ) bones['b_b_t1'].setPosition( new Vector3( -roofX, lowerRoofTopY, -newDepth ) );
            if( bones['b_b_t2'] ) bones['b_b_t2'].setPosition( new Vector3( -roofX, roofTopY, -newDepth ) );

            if( bones['b_f_m1'] ) bones['b_f_m1'].setPosition( new Vector3( -roofX, 0, newDepth ) );
            if( bones['b_f_m2'] ) bones['b_f_m2'].setPosition( new Vector3( -roofX, newWallHeight, newDepth ) );

            if( bones['b_b_m1'] ) bones['b_b_m1'].setPosition( new Vector3( -roofX, 0, -newDepth ) );
            if( bones['b_b_m2'] ) bones['b_b_m2'].setPosition( new Vector3( -roofX, newWallHeight, -newDepth ) );

            if( bones['rm_t1'] ) bones['rm_t1'].setPosition( new Vector3( -roofX, lowerRoofTopY, -newDepth ) );
            if( bones['rm_t2'] ) bones['rm_t2'].setPosition( new Vector3( -roofX, roofTopY, -newDepth ) );
            
            newBuildingHeight   = roofTopY;
        } else if( 'monitor' === roofShape ) {
            const mainSizeSetting   = this.getSettingData( 'main_size_data' );
            const mainSizeData      = this.getMainSizeByCondition( mainSizeSetting );
            const fixedRoofHeight   = 1.667; // 20 inch
            const quarterWidth      = newWidth * 2 / 4;
            const roofLowerHeight   = _this._roofLowerDegree ? helpers.getTanFromDegrees( _this._roofLowerDegree ) * quarterWidth : helpers.getTanFromDegrees( _this._initRoofLowerDegree ) * quarterWidth;
            const roofUpperHeight   = _this._roofUpperDegree ? helpers.getTanFromDegrees( _this._roofUpperDegree ) * quarterWidth :  helpers.getTanFromDegrees( _this._initRoofUpperDegree ) * quarterWidth;
            newBuildingHeight       = newWallHeight + roofLowerHeight + fixedRoofHeight + roofUpperHeight;
            const middlePointX      = mainSizeData ? helpers.toFixed3( mainSizeData / 2 ) : newWidth - quarterWidth;

            if( bones['r_b_l1'] ) bones['r_b_l1'].setPosition( new Vector3( - newWidth, newWallHeight, - newDepth ) );
            if( bones['r_b_l2'] ) bones['r_b_l2'].setPosition( new Vector3( - middlePointX, newWallHeight + roofLowerHeight, - newDepth ) );
            if( bones['r_b_l3'] ) bones['r_b_l3'].setPosition( new Vector3( - middlePointX, newWallHeight + roofLowerHeight + fixedRoofHeight, - newDepth ) );
            if( bones['r_b_r1'] ) bones['r_b_r1'].setPosition( new Vector3( newWidth, newWallHeight, - newDepth ) );
            if( bones['r_b_r2'] ) bones['r_b_r2'].setPosition( new Vector3( middlePointX, newWallHeight + roofLowerHeight, - newDepth ) );
            if( bones['r_b_r3'] ) bones['r_b_r3'].setPosition( new Vector3( middlePointX, newWallHeight + roofLowerHeight + fixedRoofHeight, - newDepth ) );
            
            if( bones['r_f_l1'] ) bones['r_f_l1'].setPosition( new Vector3( - newWidth, newWallHeight, newDepth ) );
            if( bones['r_f_l2'] ) bones['r_f_l2'].setPosition( new Vector3( - middlePointX, newWallHeight + roofLowerHeight, newDepth ) );
            if( bones['r_f_l3'] ) bones['r_f_l3'].setPosition( new Vector3( - middlePointX, newWallHeight + roofLowerHeight + fixedRoofHeight, newDepth ) );
            if( bones['r_f_r1'] ) bones['r_f_r1'].setPosition( new Vector3( newWidth, newWallHeight, newDepth ) );
            if( bones['r_f_r2'] ) bones['r_f_r2'].setPosition( new Vector3( middlePointX, newWallHeight + roofLowerHeight, newDepth ) );
            if( bones['r_f_r3'] ) bones['r_f_r3'].setPosition( new Vector3( middlePointX, newWallHeight + roofLowerHeight + fixedRoofHeight, newDepth ) );
            
            if( bones['rm_l1'] ) bones['rm_l1'].setPosition( new Vector3( bones['r_b_l1'].position.x, bones['r_b_l1'].position.y, bones['r_b_l1'].position.z ) );
            if( bones['rm_l2'] ) bones['rm_l2'].setPosition( new Vector3( bones['r_b_l2'].position.x, bones['r_b_l2'].position.y, bones['r_b_l2'].position.z ) );
            if( bones['rm_l3'] ) bones['rm_l3'].setPosition( new Vector3( bones['r_b_l3'].position.x, bones['r_b_l3'].position.y, bones['r_b_l3'].position.z ) );
            if( bones['rm_r1'] ) bones['rm_r1'].setPosition( new Vector3( bones['r_b_r1'].position.x, bones['r_b_r1'].position.y, bones['r_b_r1'].position.z ) );
            if( bones['rm_r2'] ) bones['rm_r2'].setPosition( new Vector3( bones['r_b_r2'].position.x, bones['r_b_r2'].position.y, bones['r_b_r2'].position.z ) );
            if( bones['rm_r3'] ) bones['rm_r3'].setPosition( new Vector3( bones['r_b_r3'].position.x, bones['r_b_r3'].position.y, bones['r_b_r3'].position.z ) );
            
            if( bones['b_b_l3'] ) bones['b_b_l3'].setPosition( new Vector3( bones['r_b_l2'].position.x, bones['r_b_l2'].position.y, bones['r_b_l2'].position.z ) );
            if( bones['b_b_l4'] ) bones['b_b_l4'].setPosition( new Vector3( bones['r_b_l3'].position.x, bones['r_b_l3'].position.y, bones['r_b_l3'].position.z ) );
            if( bones['b_b_r3'] ) bones['b_b_r3'].setPosition( new Vector3( bones['r_b_r2'].position.x, bones['r_b_r2'].position.y, bones['r_b_r2'].position.z ) );
            if( bones['b_b_r4'] ) bones['b_b_r4'].setPosition( new Vector3( bones['r_b_r3'].position.x, bones['r_b_r3'].position.y, bones['r_b_r3'].position.z ) );
            if( bones['b_b_t'] ) bones['b_b_t'].setPosition( new Vector3( bones['r_b_t'].position.x, bones['r_b_t'].position.y, bones['r_b_t'].position.z ) );
            
            if( bones['b_f_l3'] ) bones['b_f_l3'].setPosition( new Vector3( bones['r_f_l2'].position.x, bones['r_f_l2'].position.y, bones['r_f_l2'].position.z ) );
            if( bones['b_f_l4'] ) bones['b_f_l4'].setPosition( new Vector3( bones['r_f_l3'].position.x, bones['r_f_l3'].position.y, bones['r_f_l3'].position.z ) );
            if( bones['b_f_r3'] ) bones['b_f_r3'].setPosition( new Vector3( bones['r_f_r2'].position.x, bones['r_f_r2'].position.y, bones['r_f_r2'].position.z ) );
            if( bones['b_f_r4'] ) bones['b_f_r4'].setPosition( new Vector3( bones['r_f_r3'].position.x, bones['r_f_r3'].position.y, bones['r_f_r3'].position.z ) );
            if( bones['b_f_t'] ) bones['b_f_t'].setPosition( new Vector3( bones['r_f_t'].position.x, bones['r_f_t'].position.y, bones['r_f_t'].position.z ) );
        } else if( 'dutch_style' === roofShape ) {
            const fullWidth         = newWidth * 2;
            const leanToWidth       = helpers.toFixed3( fullWidth / 3 );
            const lowerHeight       = leanToWidth * helpers.getTanFromDegrees( 0 !== _this._roofLowerDegree ? _this._roofLowerDegree : _this._initRoofLowerDegree );
            const upperHeight       = leanToWidth / 2 * helpers.getTanFromDegrees( 0 !== _this._roofUpperDegree ? _this._roofUpperDegree : _this._initRoofUpperDegree );
            newRoofPitchHeight      = helpers.toFixed3( lowerHeight + upperHeight );
            newBuildingHeight       = helpers.toFixed3( newRoofPitchHeight + newWallHeight );
            _this._roofMidPointX    = helpers.toFixed3( newWidth / 3 );
            _this._roofMidPointY    = helpers.toFixed3( newWallHeight + lowerHeight );
        }
        
        if ( bones['dogkennel_dog_door_cloned'] != undefined ) {
            let extraHeight = 0.1;

            newWallHeight = newWallHeight + extraHeight;
            newRoofPitchHeight = _this.calculateRoofPitchHeight(roofDegree, newWidth);
            newBuildingHeight = newRoofPitchHeight + newWallHeight;
        }

        if( 'lean_to' === _this._shedType || 'structure_monoslope_smithbilt' === _this._shedType ) {
            newRoofPitchHeight = _this.calculateRoofPitchHeight( roofDegree, newWidth * 2 );
        }

        if( 'mainus_quaker' === _this._shedType ) {
            const tmpHalfWidth  = ( newWidth * 2 - 2.414 ) / 2; // 2.414 is instance b_b_r4 and center(b_b_r2)
            newRoofPitchHeight  = helpers.getTanFromDegrees( 22.5 ) * tmpHalfWidth;
            newBuildingHeight   = newRoofPitchHeight + newWallHeight;
        }

        if( 'structure_quaker_wannabuysheds' === _this._shedType ) {
            const ratio     = ( newWidth * 2 + 1 ) / _this._initialWidth;

            newRoofPitchHeight  = 1.6 * ratio;
            newBuildingHeight   = newRoofPitchHeight + newWallHeight;

            if( 'structure_quaker_milerstorage' === this._shedTypeVersion ) {
                const newPointTop = this.calculatePointOnSegment( new Vector3( - ( newWidth + 1.33 ), newWallHeight, 0 ), new Vector3( newWidth, newWallHeight, 0 ), 8/12, 4/12 );

                newBuildingHeight = newPointTop.y;
                newRoofPitchHeight = newBuildingHeight - newWallHeight;
            }
        }

        _this.setRoofPitchHeight(newRoofPitchHeight);
        _this.setBuildingHeight(newBuildingHeight);
        
        if( 'apexbarn_with_horizontal_trims' === _this._shedType ) {
            errorNumber = 0.01;
        }

        if (bones['b_b_l1'] != undefined) bones['b_b_l1'].setPosition(new Vector3(-newWidth + errorNumber, bones['b_b_l1'].position.y, -newDepth + errorNumber));
        if (bones['b_f_l1'] != undefined) bones['b_f_l1'].setPosition(new Vector3(-newWidth + errorNumber, bones['b_f_l1'].position.y, newDepth - errorNumber));

        if( 'lean_to' !== _this._shedType && 'structure_monoslope_smithbilt' !== _this._shedType ) {
            let errorRoofDegreeLow = 0;
            if( roofDegree < 14 ){
                errorRoofDegreeLow = newWidth/250;
            }

            if( 'structure_boxeave_endgable' === _this._shedType || 'structure_boxeave_sidegable' === _this._shedType ){
                errorRoofDegreeLow = newWidth / 50;

                if (bones['boxeve_b_t'] != undefined) bones['boxeve_b_t'].setPosition(new Vector3(bones['boxeve_b_t'].position.x, newBuildingHeight - errorRoofDegreeLow, -newDepth));
                if (bones['boxeve_f_t'] != undefined) bones['boxeve_f_t'].setPosition(new Vector3(bones['boxeve_f_t'].position.x, newBuildingHeight - errorRoofDegreeLow, newDepth));

                if (bones['boxeve_b_l1'] != undefined) bones['boxeve_b_l1'].setPosition(new Vector3(-newWidth + errorNumber, newWallHeight - errorRoofDegreeLow, -newDepth + errorNumber));
                if (bones['boxeve_f_l1'] != undefined) bones['boxeve_f_l1'].setPosition(new Vector3(-newWidth + errorNumber, newWallHeight - errorRoofDegreeLow, newDepth - errorNumber));
                if (bones['boxeve_b_r1'] != undefined) bones['boxeve_b_r1'].setPosition(new Vector3(newWidth - errorNumber, newWallHeight - errorRoofDegreeLow, -newDepth + errorNumber));
                if (bones['boxeve_f_r1'] != undefined) bones['boxeve_f_r1'].setPosition(new Vector3(newWidth - errorNumber, newWallHeight - errorRoofDegreeLow, newDepth - errorNumber));                
            }

            if (bones['b_b_r1'] != undefined) bones['b_b_r1'].setPosition(new Vector3(newWidth - errorNumber, bones['b_b_r1'].position.y, -newDepth + errorNumber));
            if (bones['b_f_r1'] != undefined) bones['b_f_r1'].setPosition(new Vector3(newWidth - errorNumber, bones['b_f_r1'].position.y, newDepth - errorNumber));
            if (bones['b_b_l2'] != undefined) bones['b_b_l2'].setPosition(new Vector3(-newWidth + errorNumber, newWallHeight - errorRoofDegreeLow, -newDepth + errorNumber));
            if (bones['b_f_l2'] != undefined) bones['b_f_l2'].setPosition(new Vector3(-newWidth + errorNumber, newWallHeight - errorRoofDegreeLow, newDepth - errorNumber));
            if (bones['b_b_r2'] != undefined) bones['b_b_r2'].setPosition(new Vector3(newWidth - errorNumber, newWallHeight - errorRoofDegreeLow, -newDepth + errorNumber));
            if (bones['b_f_r2'] != undefined) bones['b_f_r2'].setPosition(new Vector3(newWidth - errorNumber, newWallHeight - errorRoofDegreeLow, newDepth - errorNumber));
            if (bones['b_b_t'] != undefined) bones['b_b_t'].setPosition(new Vector3(bones['b_b_t'].position.x, newBuildingHeight, -newDepth));
            if (bones['b_f_t'] != undefined) bones['b_f_t'].setPosition(new Vector3(bones['b_f_t'].position.x, newBuildingHeight, newDepth));
            if (bones['r_b_l1'] != undefined) bones['r_b_l1'].setPosition(new Vector3(-newWidth, newWallHeight, -newDepth));
            if (bones['r_f_l1'] != undefined) bones['r_f_l1'].setPosition(new Vector3(-newWidth, newWallHeight, newDepth));
            if (bones['rm_l1'] != undefined) bones['rm_l1'].setPosition(new Vector3(-newWidth, newWallHeight, -newDepth));
        }

        if (bones['b_b_m'] != undefined) bones['b_b_m'].setPosition(new Vector3(bones['b_b_m'].position.x, newWallHeight, -newDepth));
        if (bones['b_f_m'] != undefined) bones['b_f_m'].setPosition(new Vector3(bones['b_f_m'].position.x, newWallHeight, newDepth));

        if (bones['r_b_r1'] != undefined) bones['r_b_r1'].setPosition(new Vector3(newWidth, newWallHeight, -newDepth));
        if (bones['r_f_r1'] != undefined) bones['r_f_r1'].setPosition(new Vector3(newWidth, newWallHeight, newDepth));

        if (bones['r_b_t'] != undefined) bones['r_b_t'].setPosition(new Vector3(bones['r_b_t'].position.x, newBuildingHeight, -newDepth));
        if (bones['r_f_t'] != undefined) bones['r_f_t'].setPosition(new Vector3(bones['r_f_t'].position.x, newBuildingHeight, newDepth));

        if (bones['rm_r1'] != undefined) bones['rm_r1'].setPosition(new Vector3(newWidth, newWallHeight, -newDepth));
        if (bones['rm_t'] != undefined) bones['rm_t'].setPosition(new Vector3(bones['rm_t'].position.x, newBuildingHeight, -newDepth));
        
        if( 'structure_lofted_horserunin' !== _this._shedType ) {
            if( bones['inner_frame_horizontal_clone_t'] ) bones['inner_frame_horizontal_clone_t'].setPosition( new Vector3( bones['inner_frame_horizontal_clone_t'].position.x, newBuildingHeight, bones['inner_frame_horizontal_clone_t'].position.z ) );
            if( bones['inner_frame_horizontal_clone_l2'] ) bones['inner_frame_horizontal_clone_l2'].setPosition( new Vector3( -newWidth, newWallHeight, bones['inner_frame_horizontal_clone_l2'].position.z ) );
            if( bones['inner_frame_horizontal_clone_r2'] ) bones['inner_frame_horizontal_clone_r2'].setPosition( new Vector3( newWidth, newWallHeight, bones['inner_frame_horizontal_clone_r2'].position.z ) );
            if( bones['inner_frame_horizontal_clone_l1'] ) bones['inner_frame_horizontal_clone_l1'].setPosition( new Vector3( -newWidth, bones['inner_frame_horizontal_clone_l1'].position.y, bones['inner_frame_horizontal_clone_l1'].position.z ) );
            if( bones['inner_frame_horizontal_clone_r1'] ) bones['inner_frame_horizontal_clone_r1'].setPosition( new Vector3( newWidth, bones['inner_frame_horizontal_clone_r1'].position.y, bones['inner_frame_horizontal_clone_l1'].position.z ) );
        }

        if( bones['inner_frame_vertical_clone_b1'] ) bones['inner_frame_vertical_clone_b1'].setPosition( new Vector3( bones['inner_frame_vertical_clone_b1'].position.x, bones['inner_frame_vertical_clone_b1'].position.y, -newDepth ) );
        if( bones['inner_frame_vertical_clone_b2'] ) bones['inner_frame_vertical_clone_b2'].setPosition( new Vector3( bones['inner_frame_vertical_clone_b2'].position.x, newWallHeight, -newDepth ) );
        if( bones['inner_frame_vertical_clone_f1'] ) bones['inner_frame_vertical_clone_f1'].setPosition( new Vector3( bones['inner_frame_vertical_clone_f1'].position.x, bones['inner_frame_vertical_clone_f1'].position.y, newDepth ) );        
        if( bones['inner_frame_vertical_clone_f2'] ) bones['inner_frame_vertical_clone_f2'].setPosition( new Vector3( bones['inner_frame_vertical_clone_f2'].position.x, newWallHeight, newDepth ) );        

        if( _this._runnerType === _this._verticalRunner ) {
            if (bones['runner_b'] != undefined) bones['runner_b'].setPosition(new Vector3(bones['runner_b'].position.x, bones['runner_b'].position.y, -newDepth));
            if (bones['runner_f'] != undefined) bones['runner_f'].setPosition(new Vector3(bones['runner_f'].position.x, bones['runner_f'].position.y, newDepth));
        } else if( _this._runnerType === _this._horizontalRunner ) {
            if (bones['runner_b'] != undefined) bones['runner_b'].setPosition(new Vector3(bones['runner_b'].position.x, bones['runner_b'].position.y, -newWidth));
            if (bones['runner_f'] != undefined) bones['runner_f'].setPosition(new Vector3(bones['runner_f'].position.x, bones['runner_f'].position.y, newWidth));
        }        

        // Dog Kennel bones
        if (bones['runner_b_l'] != undefined) bones['runner_b_l'].setPosition(new Vector3(-newWidth, bones['runner_b_l'].position.y, -newDepth));
        if (bones['runner_b_r'] != undefined) bones['runner_b_r'].setPosition(new Vector3(newWidth, bones['runner_b_r'].position.y, -newDepth));
        if (bones['runner_f_l'] != undefined) bones['runner_f_l'].setPosition(new Vector3(-newWidth, bones['runner_f_l'].position.y, newDepth));
        if (bones['runner_f_r'] != undefined) bones['runner_f_r'].setPosition(new Vector3(newWidth, bones['runner_f_r'].position.y, newDepth));
        
        if (bones['b_m_l1'] != undefined) bones['b_m_l1'].setPosition(new Vector3(-newWidth, bones['b_m_l1'].position.y, -newDepth+fixedPart));
        if (bones['b_m_l2'] != undefined) bones['b_m_l2'].setPosition(new Vector3(-newWidth, newWallHeight, -newDepth+fixedPart));
        if (bones['b_m_r1'] != undefined) bones['b_m_r1'].setPosition(new Vector3(newWidth, bones['b_m_r1'].position.y, -newDepth+fixedPart));
        if (bones['b_m_r2'] != undefined) bones['b_m_r2'].setPosition(new Vector3(newWidth, newWallHeight, -newDepth+fixedPart));
        if (bones['b_m_t'] != undefined) bones['b_m_t'].setPosition(new Vector3(bones['b_m_t'].position.x, newBuildingHeight, -newDepth+fixedPart));

        if (bones['dogkennel_wire_b_l1'] != undefined) bones['dogkennel_wire_b_l1'].setPosition(new Vector3(-newWidth, bones['dogkennel_wire_b_l1'].position.y, -newDepth+fixedPart));
        if (bones['dogkennel_wire_f_l1'] != undefined) bones['dogkennel_wire_f_l1'].setPosition(new Vector3(-newWidth, bones['dogkennel_wire_f_l1'].position.y, newDepth));
        if (bones['dogkennel_wire_b_r1'] != undefined) bones['dogkennel_wire_b_r1'].setPosition(new Vector3(newWidth, bones['dogkennel_wire_b_r1'].position.y, -newDepth+fixedPart));
        if (bones['dogkennel_wire_f_r1'] != undefined) bones['dogkennel_wire_f_r1'].setPosition(new Vector3(newWidth, bones['dogkennel_wire_f_r1'].position.y, newDepth));

        if (bones['dogkennel_wire_b_l2'] != undefined) bones['dogkennel_wire_b_l2'].setPosition(new Vector3(-newWidth, newWallHeight, -newDepth+fixedPart));
        if (bones['dogkennel_wire_f_l2'] != undefined) bones['dogkennel_wire_f_l2'].setPosition(new Vector3(-newWidth, newWallHeight, newDepth));
        if (bones['dogkennel_wire_b_r2'] != undefined) bones['dogkennel_wire_b_r2'].setPosition(new Vector3(newWidth, newWallHeight, -newDepth+fixedPart));
        if (bones['dogkennel_wire_f_r2'] != undefined) bones['dogkennel_wire_f_r2'].setPosition(new Vector3(newWidth, newWallHeight, newDepth));

        if (bones['dogkennel_wire_cross_lr_cloned_b_l'] != undefined) bones['dogkennel_wire_cross_lr_cloned_b_l'].setPosition(new Vector3(-newWidth, newWallHeight/2, -newDepth+fixedPart));
        if (bones['dogkennel_wire_cross_lr_cloned_f_l'] != undefined) bones['dogkennel_wire_cross_lr_cloned_f_l'].setPosition(new Vector3(-newWidth, newWallHeight/2, newDepth));
        if (bones['dogkennel_wire_cross_lr_cloned_b_r'] != undefined) bones['dogkennel_wire_cross_lr_cloned_b_r'].setPosition(new Vector3(newWidth, newWallHeight/2, -newDepth+fixedPart));
        if (bones['dogkennel_wire_cross_lr_cloned_f_r'] != undefined) bones['dogkennel_wire_cross_lr_cloned_f_r'].setPosition(new Vector3(newWidth, newWallHeight/2, newDepth));

        if (bones['dogkennel_wire_cross_m_cloned_b'] != undefined) bones['dogkennel_wire_cross_m_cloned_b'].setPosition(new Vector3(bones['dogkennel_wire_cross_m_cloned_b'].position.x, newWallHeight/2, -newDepth+fixedPart));
        if (bones['dogkennel_wire_cross_m_cloned_f'] != undefined) bones['dogkennel_wire_cross_m_cloned_f'].setPosition(new Vector3(bones['dogkennel_wire_cross_m_cloned_f'].position.x, newWallHeight/2, newDepth));
        
        if (bones['dogkennel_wire_hook_lr_cloned_b_l'] != undefined) bones['dogkennel_wire_hook_lr_cloned_b_l'].setPosition(new Vector3(-newWidth, newWallHeight/2, -newDepth+fixedPart));
        if (bones['dogkennel_wire_hook_lr_cloned_f_l'] != undefined) bones['dogkennel_wire_hook_lr_cloned_f_l'].setPosition(new Vector3(-newWidth, newWallHeight/2, newDepth));
        if (bones['dogkennel_wire_hook_lr_cloned_b_r'] != undefined) bones['dogkennel_wire_hook_lr_cloned_b_r'].setPosition(new Vector3(newWidth, newWallHeight/2, -newDepth+fixedPart));
        if (bones['dogkennel_wire_hook_lr_cloned_f_r'] != undefined) bones['dogkennel_wire_hook_lr_cloned_f_r'].setPosition(new Vector3(newWidth, newWallHeight/2, newDepth));

        if (bones['dogkennel_wire_hook_m_cloned_b_l'] != undefined) bones['dogkennel_wire_hook_m_cloned_b'].setPosition(new Vector3(bones['dogkennel_wire_hook_m_cloned_b'].position.x, newWallHeight/2, -newDepth+fixedPart));
        if (bones['dogkennel_wire_hook_m_cloned_f'] != undefined) bones['dogkennel_wire_hook_m_cloned_f'].setPosition(new Vector3(bones['dogkennel_wire_hook_m_cloned_f'], newWallHeight/2, newDepth));
        
        if (bones['dogkennel_wire_frame_m_b_m1'] != undefined) bones['dogkennel_wire_frame_m_b_m1'].setPosition(new Vector3(bones['dogkennel_wire_frame_m_b_m1'].x, bones['dogkennel_wire_frame_m_b_m1'].y, -newDepth+fixedPart));
        if (bones['dogkennel_wire_frame_m_b_m2'] != undefined) bones['dogkennel_wire_frame_m_b_m2'].setPosition(new Vector3(bones['dogkennel_wire_frame_m_b_m2'].x, newWallHeight, -newDepth+fixedPart));
        if (bones['dogkennel_wire_frame_m_f_m1'] != undefined) bones['dogkennel_wire_frame_m_f_m1'].setPosition(new Vector3(bones['dogkennel_wire_frame_m_f_m1'].x, bones['dogkennel_wire_frame_m_f_m1'].y, newDepth));
        if (bones['dogkennel_wire_frame_m_f_m2'] != undefined) bones['dogkennel_wire_frame_m_f_m2'].setPosition(new Vector3(bones['dogkennel_wire_frame_m_f_m2'].x, newWallHeight, newDepth));

        if (bones['dogkennel_door_wire'] != undefined) bones['dogkennel_door_wire'].setPosition(new Vector3(bones['dogkennel_door_wire'].position.x, bones['dogkennel_door_wire'].position.y, newDepth));
        if (bones['dogkennel_dog_door_cloned'] != undefined) bones['dogkennel_dog_door_cloned'].setPosition(new Vector3(bones['dogkennel_dog_door_cloned'].position.x, bones['dogkennel_dog_door_cloned'].position.y, -newDepth+fixedPart));
        // End Dog Kennel bones

        // Green House bones
        if (bones['greenhouse_frame_cloned_l'] != undefined) bones['greenhouse_frame_cloned_l'].setPosition(new Vector3(-newWidth, bones['greenhouse_frame_cloned_l'].position.y, bones['greenhouse_frame_cloned_l'].position.z));
        if (bones['greenhouse_frame_cloned_r'] != undefined) bones['greenhouse_frame_cloned_r'].setPosition(new Vector3(newWidth, bones['greenhouse_frame_cloned_r'].position.y, bones['greenhouse_frame_cloned_r'].position.z));
        if (bones['greenhouse_frame_cloned_t'] != undefined) bones['greenhouse_frame_cloned_t'].setPosition(new Vector3(bones['greenhouse_frame_cloned_t'].position.x, newBuildingHeight, bones['greenhouse_frame_cloned_t'].position.z));

        // structure_greenhouse_rockwood
        if( 'structure_greenhouse_rockwood' === _this._shedType ) {
            if ( bones['greenhouse_frame_cloned_l1'] ) bones['greenhouse_frame_cloned_l1'].setPosition( new Vector3( -newWidth, bones['greenhouse_frame_cloned_l1'].position.y, bones['greenhouse_frame_cloned_l1'].position.z ) );
            if ( bones['greenhouse_frame_cloned_l2'] ) bones['greenhouse_frame_cloned_l2'].setPosition( new Vector3( -newWidth, newWallHeight, bones['greenhouse_frame_cloned_l2'].position.z ) );
            if ( bones['greenhouse_frame_cloned_r1'] ) bones['greenhouse_frame_cloned_r1'].setPosition( new Vector3( newWidth, bones['greenhouse_frame_cloned_r1'].position.y, bones['greenhouse_frame_cloned_r1'].position.z ) );
            if ( bones['greenhouse_frame_cloned_r2'] ) bones['greenhouse_frame_cloned_r2'].setPosition( new Vector3( newWidth, newWallHeight, bones['greenhouse_frame_cloned_r2'].position.z ) );
        }

        // Pavillion bones
        if (bones['pavilion_rafter_cloned_t'] != undefined) bones['pavilion_rafter_cloned_t'].setPosition(new Vector3(bones['pavilion_rafter_cloned_t'].position.x, newBuildingHeight, bones['pavilion_rafter_cloned_t'].position.z));
        if (bones['pavilion_rafter_cloned_l'] != undefined) bones['pavilion_rafter_cloned_l'].setPosition(new Vector3(-newWidth, newWallHeight, bones['pavilion_rafter_cloned_l'].position.z));
        if (bones['pavilion_rafter_cloned_r'] != undefined) bones['pavilion_rafter_cloned_r'].setPosition(new Vector3(newWidth, newWallHeight, bones['pavilion_rafter_cloned_r'].position.z));

        if (bones['pavilion_post_cloned_t'] != undefined) bones['pavilion_post_cloned_t'].setPosition(new Vector3(bones['pavilion_post_cloned_t'].position.x, newBuildingHeight, bones['pavilion_post_cloned_t'].position.z));
        if (bones['pavilion_post_cloned_m'] != undefined) bones['pavilion_post_cloned_m'].setPosition(new Vector3(bones['pavilion_post_cloned_m'].position.x, newWallHeight, bones['pavilion_post_cloned_m'].position.z));
        if (bones['pavilion_post_cloned_l1'] != undefined) bones['pavilion_post_cloned_l1'].setPosition(new Vector3(-newWidth, bones['pavilion_post_cloned_l1'].position.y, bones['pavilion_post_cloned_l1'].position.z));
        if (bones['pavilion_post_cloned_l2'] != undefined) bones['pavilion_post_cloned_l2'].setPosition(new Vector3(-newWidth, newWallHeight, bones['pavilion_post_cloned_l2'].position.z));
        if (bones['pavilion_post_cloned_r1'] != undefined) bones['pavilion_post_cloned_r1'].setPosition(new Vector3(newWidth, bones['pavilion_post_cloned_r1'].position.y, bones['pavilion_post_cloned_r1'].position.z));
        if (bones['pavilion_post_cloned_r2'] != undefined) bones['pavilion_post_cloned_r2'].setPosition(new Vector3(newWidth, newWallHeight, bones['pavilion_post_cloned_r2'].position.z));

        // Pergola bones
        if (bones['pergola_cross_beam_cloned_l'] != undefined) bones['pergola_cross_beam_cloned_l'].setPosition(new Vector3(-newWidth, newWallHeight, bones['pergola_cross_beam_cloned_l'].position.z));
        if (bones['pergola_cross_beam_cloned_r'] != undefined) bones['pergola_cross_beam_cloned_r'].setPosition(new Vector3(newWidth, newWallHeight, bones['pergola_cross_beam_cloned_r'].position.z));

        if (bones['pergola_purlin_cloned_b'] != undefined) bones['pergola_purlin_cloned_b'].setPosition(new Vector3(bones['pergola_purlin_cloned_b'].position.x, newWallHeight, -newDepth));
        if (bones['pergola_purlin_cloned_f'] != undefined) bones['pergola_purlin_cloned_f'].setPosition(new Vector3(bones['pergola_purlin_cloned_f'].position.x, newWallHeight, newDepth));

        if (bones['pergola_post_cloned_l1'] != undefined) bones['pergola_post_cloned_l1'].setPosition(new Vector3(-newWidth, bones['pergola_post_cloned_l1'].position.y, bones['pergola_post_cloned_l1'].position.z));
        if (bones['pergola_post_cloned_l2'] != undefined) bones['pergola_post_cloned_l2'].setPosition(new Vector3(-newWidth, newWallHeight, bones['pergola_post_cloned_l2'].position.z));
        if (bones['pergola_post_cloned_r1'] != undefined) bones['pergola_post_cloned_r1'].setPosition(new Vector3(newWidth, bones['pergola_post_cloned_r1'].position.y, bones['pergola_post_cloned_r1'].position.z));
        if (bones['pergola_post_cloned_r2'] != undefined) bones['pergola_post_cloned_r2'].setPosition(new Vector3(newWidth, newWallHeight, bones['pergola_post_cloned_r2'].position.z));

        // Horse Run-in
        if (bones['horse_run_in_opening_cloned_b_l1'] != undefined) bones['horse_run_in_opening_cloned_b_l1'].setPosition(new Vector3(-newWidth, bones['horse_run_in_opening_cloned_b_l1'].position.y, -newDepth));
        if (bones['horse_run_in_opening_cloned_b_l2'] != undefined) bones['horse_run_in_opening_cloned_b_l2'].setPosition(new Vector3(-newWidth, newWallHeight, -newDepth));
        if (bones['horse_run_in_opening_cloned_f_l1'] != undefined) bones['horse_run_in_opening_cloned_f_l1'].setPosition(new Vector3(-newWidth, bones['horse_run_in_opening_cloned_f_l1'].position.y, newDepth));
        if (bones['horse_run_in_opening_cloned_f_l2'] != undefined) bones['horse_run_in_opening_cloned_f_l2'].setPosition(new Vector3(-newWidth, newWallHeight, newDepth));
        if (bones['horse_run_in_opening_cloned_t_m1'] != undefined) bones['horse_run_in_opening_cloned_t_m1'].setPosition(new Vector3(-newWidth, bones['horse_run_in_opening_cloned_t_m1'].position.y, bones['horse_run_in_opening_cloned_t_m1'].position.z));
        if (bones['horse_run_in_opening_cloned_t_m2'] != undefined) bones['horse_run_in_opening_cloned_t_m2'].setPosition(new Vector3(-newWidth, newWallHeight, bones['horse_run_in_opening_cloned_t_m2'].position.z));

        if (bones['horse_run_in_opening_double_cloned_b_l1'] != undefined) bones['horse_run_in_opening_double_cloned_b_l1'].setPosition(new Vector3(-newWidth, bones['horse_run_in_opening_double_cloned_b_l1'].position.y, -newDepth));
        if (bones['horse_run_in_opening_double_cloned_b_l2'] != undefined) bones['horse_run_in_opening_double_cloned_b_l2'].setPosition(new Vector3(-newWidth, newWallHeight, -newDepth));
        if (bones['horse_run_in_opening_double_cloned_f_l1'] != undefined) bones['horse_run_in_opening_double_cloned_f_l1'].setPosition(new Vector3(-newWidth, bones['horse_run_in_opening_double_cloned_f_l1'].position.y, newDepth));
        if (bones['horse_run_in_opening_double_cloned_f_l2'] != undefined) bones['horse_run_in_opening_double_cloned_f_l2'].setPosition(new Vector3(-newWidth, newWallHeight, newDepth));
        if (bones['horse_run_in_opening_double_cloned_t_m1'] != undefined) bones['horse_run_in_opening_double_cloned_t_m1'].setPosition(new Vector3(-newWidth, bones['horse_run_in_opening_double_cloned_t_m1'].position.y, bones['horse_run_in_opening_double_cloned_t_m1'].position.z));
        if (bones['horse_run_in_opening_double_cloned_t_m2'] != undefined) bones['horse_run_in_opening_double_cloned_t_m2'].setPosition(new Vector3(-newWidth, newWallHeight, bones['horse_run_in_opening_double_cloned_t_m2'].position.z));

        if (bones['horse_run_in_opening_triple_cloned_b_l1'] != undefined) bones['horse_run_in_opening_triple_cloned_b_l1'].setPosition(new Vector3(-newWidth, bones['horse_run_in_opening_triple_cloned_b_l1'].position.y, -newDepth));
        if (bones['horse_run_in_opening_triple_cloned_b_l2'] != undefined) bones['horse_run_in_opening_triple_cloned_b_l2'].setPosition(new Vector3(-newWidth, newWallHeight, -newDepth));
        if (bones['horse_run_in_opening_triple_cloned_f_l1'] != undefined) bones['horse_run_in_opening_triple_cloned_f_l1'].setPosition(new Vector3(-newWidth, bones['horse_run_in_opening_triple_cloned_f_l1'].position.y, newDepth));
        if (bones['horse_run_in_opening_triple_cloned_f_l2'] != undefined) bones['horse_run_in_opening_triple_cloned_f_l2'].setPosition(new Vector3(-newWidth, newWallHeight, newDepth));
        if (bones['horse_run_in_opening_triple_cloned_t_m1'] != undefined) bones['horse_run_in_opening_triple_cloned_t_m1'].setPosition(new Vector3(-newWidth, bones['horse_run_in_opening_triple_cloned_t_m1'].position.y, bones['horse_run_in_opening_triple_cloned_t_m1'].position.z));
        if (bones['horse_run_in_opening_triple_cloned_t_m2'] != undefined) bones['horse_run_in_opening_triple_cloned_t_m2'].setPosition(new Vector3(-newWidth, newWallHeight, bones['horse_run_in_opening_triple_cloned_t_m2'].position.z));

        if (bones['horse_run_in_frame_v_cloned_b1'] != undefined) bones['horse_run_in_frame_v_cloned_b1'].setPosition( new Vector3(bones['horse_run_in_frame_v_cloned_b1'].position.x, 0, -newDepth ) );
        if (bones['horse_run_in_frame_v_cloned_b2'] != undefined) bones['horse_run_in_frame_v_cloned_b2'].setPosition( new Vector3( bones['horse_run_in_frame_v_cloned_b2'].position.x, newWallHeight, -newDepth ) );

        if (bones['horse_run_in_frame_v_cloned_f1'] != undefined) bones['horse_run_in_frame_v_cloned_f1'].setPosition(new Vector3( bones['horse_run_in_frame_v_cloned_f1'].position.x, 0, newDepth ) );
        if (bones['horse_run_in_frame_v_cloned_f2'] != undefined) bones['horse_run_in_frame_v_cloned_f2'].setPosition(new Vector3( bones['horse_run_in_frame_v_cloned_f2'].position.x, newWallHeight, newDepth ) );

        if (bones['horse_run_in_frame_u_cloned_l1'] != undefined) bones['horse_run_in_frame_u_cloned_l1'].setPosition( new Vector3( -newWidth, 0, bones['horse_run_in_frame_u_cloned_l1'].position.z ) );
        if (bones['horse_run_in_frame_u_cloned_l2'] != undefined) bones['horse_run_in_frame_u_cloned_l2'].setPosition( new Vector3( -newWidth, newWallHeight, bones['horse_run_in_frame_u_cloned_l2'].position.z ) );

        if (bones['horse_run_in_frame_u_cloned_r'] != undefined) bones['horse_run_in_frame_u_cloned_r'].setPosition(new Vector3(newWidth, newWallHeight, bones['horse_run_in_frame_u_cloned_r'].position.z));
        if (bones['horse_run_in_frame_u_cloned_t'] != undefined) bones['horse_run_in_frame_u_cloned_t'].setPosition(new Vector3(bones['horse_run_in_frame_u_cloned_t'].position.x, newBuildingHeight, bones['horse_run_in_frame_u_cloned_t'].position.z));

        //Classic A-frame Dormer
        if( _this._shedType == 'structure_classic_aframe_dormer' ){
            newRoofPitchHeight          = helpers.toFixed3( newRoofPitchHeight );
            let dormerRatio             = newRoofPitchHeight/_this.getInitialRoofPitchHeight();
            let dormerZ                 = dormerRatio * 2.5; // position Z default base model is 2.5, get from B_D_L1
            _this._dormerWidth          = dormerZ * 2;
            let positionDormerLeft      = dormerZ;
            let positionDormerRight     = -dormerZ;
            let positionDormerCenter    = 0;
            let errorNumber             = 0;
            if( _this._isEnableWallDormerSettings ) {
                const depthShed                 = newDepth * 2;
                const wallDormerSettings        = this.getSettingData( 'wall_dormer_settings' );
                const dormerRoofPitchOptions    = wallDormerSettings.ssb_dormer_roof_pitch;
                const isFollowMainRoofPitch     = 'roof_pitch' === wallDormerSettings.ssb_dormer_size_defined && 'follow_main' === dormerRoofPitchOptions;
                const dormerWidthOptions        = wallDormerSettings.ssb_dormer_width_options;
                const dormerWidthValue          = Number( wallDormerSettings.ssb_dormer_width_value );
                const dormerPositionOptions     = wallDormerSettings.ssb_dormer_position_options;
                const dormerPositionValue       = Number( wallDormerSettings.ssb_dormer_position_value );
                const dormerRoofPitch           = Number( wallDormerSettings.ssb_dormer_roof_pitch_value );

                if( 'roof_pitch' === wallDormerSettings.ssb_dormer_size_defined && 'roof_pitch' === dormerRoofPitchOptions ) {
                    _this._dormerWidth = helpers.toFixed3( newRoofPitchHeight / ( dormerRoofPitch / 12 ) ) * 2;
                } else if( 'building_length' === dormerWidthOptions ) {
                    _this._dormerWidth = Number( depthShed / dormerWidthValue );
                } else {
                    _this._dormerWidth = dormerWidthValue;
                }

                _this._dormerWidth      = 0 != _this._dormerWidth ? _this._dormerWidth : Number( depthShed / 3 );
                const haftDormerWidth   = isFollowMainRoofPitch ? newRoofPitchHeight / helpers.getTanFromDegrees( roofDegree ) : ( _this._dormerWidth / 2 );
                _this._dormerWidth      = isFollowMainRoofPitch ? haftDormerWidth * 2 : _this._dormerWidth;
                let offsetLeft          = dormerPositionValue;
                if( 'right' === dormerPositionOptions ) {
                    offsetLeft = depthShed - _this._dormerWidth - dormerPositionValue;
                }
                errorNumber             = this.getGlobalData( 'roof_error_number' );
                offsetLeft              = 'center' === dormerPositionOptions || ( offsetLeft + _this._dormerWidth ) > ( newDepth * 2 ) ? ( newDepth - haftDormerWidth ) : offsetLeft;
                positionDormerRight     = newDepth - offsetLeft - _this._dormerWidth;
                positionDormerLeft      = newDepth - offsetLeft;
                positionDormerCenter    = positionDormerLeft - haftDormerWidth;
            } 

            if( bones['b_d_l1'] != undefined) bones['b_d_l1'].setPosition( new Vector3( -newWidth, newWallHeight, positionDormerLeft ) );
            if( bones['b_d_r1'] != undefined) bones['b_d_r1'].setPosition( new Vector3( -newWidth, newWallHeight, positionDormerRight ) );
            if( bones['b_d_t'] != undefined) bones['b_d_t'].setPosition( new Vector3( -newWidth, newBuildingHeight, positionDormerCenter ) );
            if( bones['b_d_r2'] != undefined) bones['b_d_r2'].setPosition( new Vector3( -newWidth, bones['b_d_r2'].position.y, positionDormerRight ) );
            if( bones['b_d_l2'] != undefined) bones['b_d_l2'].setPosition( new Vector3( -newWidth, bones['b_d_l2'].position.y, positionDormerLeft ) );
            if(bones['r_d_l1'] != undefined) bones['r_d_l1'].setPosition( new Vector3( -newWidth, newWallHeight, positionDormerLeft ));
            if(bones['r_d_r1'] != undefined) bones['r_d_r1'].setPosition(new Vector3(-newWidth, newWallHeight, positionDormerRight ) );
            if(bones['r_d_t1'] != undefined) bones['r_d_t1'].setPosition(new Vector3( bones['r_d_t1'].position.x, newBuildingHeight, positionDormerCenter ) );
            if(bones['r_d_t2'] != undefined) bones['r_d_t2'].setPosition(new Vector3( -newWidth, newBuildingHeight, positionDormerCenter ) );

            if(bones['r_f_t1'] != undefined) bones['r_f_t1'].setPosition(new Vector3( 0, newBuildingHeight, positionDormerLeft + errorNumber ));
            if(bones['r_b_t1'] != undefined) bones['r_b_t1'].setPosition(new Vector3( 0, newBuildingHeight, positionDormerRight - errorNumber ));

            if(bones['r_d_l2'] != undefined) bones['r_d_l2'].setPosition(new Vector3( 0, newWallHeight, positionDormerLeft ));
            if(bones['r_d_r2'] != undefined) bones['r_d_r2'].setPosition(new Vector3( 0, newWallHeight, positionDormerRight ));
        }

        if( 'hip' === roofShape ){

            if (bones['r_b_t'] != undefined) bones['r_b_t'].setPosition(new Vector3(bones['r_b_t'].position.x, newBuildingHeight, -this._hipRoofTopLength/2));
            if (bones['r_f_t'] != undefined) bones['r_f_t'].setPosition(new Vector3(bones['r_f_t'].position.x, newBuildingHeight, this._hipRoofTopLength/2));

            if (bones['r_b_l2'] != undefined) bones['r_b_l2'].setPosition(new Vector3(-newWidth, newWallHeight, -this._hipRoofTopLength/2));
            if (bones['r_f_l2'] != undefined) bones['r_f_l2'].setPosition(new Vector3(-newWidth, newWallHeight, this._hipRoofTopLength/2));
            if (bones['r_b_r2'] != undefined) bones['r_b_r2'].setPosition(new Vector3(newWidth, newWallHeight, -this._hipRoofTopLength/2));
            if (bones['r_f_r2'] != undefined) bones['r_f_r2'].setPosition(new Vector3(newWidth, newWallHeight, this._hipRoofTopLength/2));            
        }
        
        //Horizontal trims on 7ft/8ft
        let trimHeight = newWallHeight;
        let trimWidth = newWidth;
        if( _this._shedType == 'apexshed_with_horizontal_trims' ){
            if( 7 === parseFloat( this._wallHeight ) ) {
                const heightChange: number          = 0.7;
                const standardDeviation: number     = 0.2; // Only apply for corner 19 degree
                const tmpStandardDeviation: number  = standardDeviation * this.getRoofDegree() / this.getInitRoofDegree();

                trimHeight  += heightChange;
                trimWidth   -= ( ( heightChange / helpers.getTanFromDegrees( this.getRoofDegree() ) ) - tmpStandardDeviation );
            }
        }

        if( 'apexbarn_with_horizontal_trims' === _this._shedType ) {
            const horizontalTrim = this._scene.getMeshByName( 'horizontal_trim_obj' ) as Mesh;
            if( horizontalTrim ) {
                const trimHeight    = 0.3;
                const tanRoofLower  = _this._roofLowerDegree ? helpers.getTanFromDegrees( _this._roofLowerDegree ) : helpers.getTanFromDegrees( _this._initRoofLowerDegree );
                const tmpPositionX  = trimHeight / tanRoofLower;
                let trimPositionY   = newWallHeight + 0.5;
                if( 7 === parseFloat( this._wallHeight ) ) {
                    trimPositionY += 0.74;
                }
    
                const trimPositionX = newWidth - ( trimPositionY - newWallHeight ) / tanRoofLower;
    
                if (bones['horizontal_trim_b_l1'] ) bones['horizontal_trim_b_l1'].setPosition(new Vector3( - trimPositionX, trimPositionY, - newDepth ) );
                if (bones['horizontal_trim_b_l2'] ) bones['horizontal_trim_b_l2'].setPosition(new Vector3( - ( trimPositionX - tmpPositionX ), trimPositionY + trimHeight, - newDepth ) );
                if (bones['horizontal_trim_b_r1'] ) bones['horizontal_trim_b_r1'].setPosition(new Vector3( trimPositionX, trimPositionY, - newDepth ) );
                if (bones['horizontal_trim_b_r2'] ) bones['horizontal_trim_b_r2'].setPosition(new Vector3( trimPositionX - tmpPositionX, trimPositionY + trimHeight, - newDepth ) );
                
                if (bones['horizontal_trim_f_l1'] ) bones['horizontal_trim_f_l1'].setPosition(new Vector3( - trimPositionX, trimPositionY, newDepth ) );
                if (bones['horizontal_trim_f_l2'] ) bones['horizontal_trim_f_l2'].setPosition(new Vector3( - ( trimPositionX - tmpPositionX ), trimPositionY + trimHeight, newDepth ) );
                if (bones['horizontal_trim_f_r1'] ) bones['horizontal_trim_f_r1'].setPosition(new Vector3( trimPositionX, trimPositionY, newDepth ) );
                if (bones['horizontal_trim_f_r2'] ) bones['horizontal_trim_f_r2'].setPosition(new Vector3( trimPositionX - tmpPositionX, trimPositionY + trimHeight, newDepth ) );
            } else {
                trimHeight += 0.5;
                if( 7 === parseFloat( this._wallHeight ) ) {
                    trimHeight += 0.74;
                } else {
                    trimWidth += 0.2;
                }
            }
        }

        if (bones['t8ft_b_l'] != undefined) bones['t8ft_b_l'].setPosition(new Vector3(-trimWidth, trimHeight, -newDepth));
        if (bones['t8ft_b_r'] != undefined) bones['t8ft_b_r'].setPosition(new Vector3(trimWidth , trimHeight, -newDepth));
        if (bones['t8ft_f_l'] != undefined) bones['t8ft_f_l'].setPosition(new Vector3(-trimWidth, trimHeight, newDepth));
        if (bones['t8ft_f_r'] != undefined) bones['t8ft_f_r'].setPosition(new Vector3(trimWidth, trimHeight, newDepth));
        //END Horizontal trims on 7ft/8ft

        if( 'mainus_quaker' !== _this._shedType ) {
            if( undefined !== bones['outer_joist_l1'] ) {
                bones['outer_joist_l1'].setPosition( new Vector3( -newWidth, bones['outer_joist_l1'].position.y, -newDepth ) );
            }
            if( undefined !== bones['outer_joist_r1'] ) {
                bones['outer_joist_r1'].setPosition( new Vector3( newWidth, bones['outer_joist_r1'].position.y, -newDepth ) );
            }
            if( undefined !== bones['outer_joist_l2'] ) {
                bones['outer_joist_l2'].setPosition( new Vector3( -newWidth, bones['outer_joist_l2'].position.y, newDepth ) );
            }
            if( undefined !== bones['outer_joist_r2'] ) {
                bones['outer_joist_r2'].setPosition( new Vector3( newWidth, bones['outer_joist_r2'].position.y, newDepth ) );
            }
        }

        if( _this.isFrontPorch() ) {
            let roofDepth = newDepth + _this._porchWidth;
            if( 'structure_trading_post' === _this._shedType ){
                roofDepth -= _this._porchWidth;
            }

            if (bones['r_f_l1'] ) bones['r_f_l1'].setPosition( new Vector3( -newWidth, newWallHeight, roofDepth ) );
            if (bones['r_f_r1'] ) bones['r_f_r1'].setPosition( new Vector3( newWidth, newWallHeight, roofDepth ) );
            if (bones['r_f_t'] )  bones['r_f_t'].setPosition( new Vector3( bones['r_f_t'].position.x, newBuildingHeight, roofDepth ) );

            if( bones['b_p_l1'] ) bones['b_p_l1'].setPosition( new Vector3( -newWidth, bones['b_p_l1'].position.y, newDepth + _this._porchWidth ) );
            if( bones['b_p_l2'] ) bones['b_p_l2'].setPosition( new Vector3( -newWidth, newWallHeight, newDepth + _this._porchWidth ) );
            if( bones['b_p_r1'] ) bones['b_p_r1'].setPosition( new Vector3( newWidth, bones['b_p_r1'].position.y, newDepth + _this._porchWidth ) );
            if( bones['b_p_r2'] ) bones['b_p_r2'].setPosition( new Vector3( newWidth, newWallHeight, newDepth + _this._porchWidth ) );
            if( bones['b_p_t'] )  bones['b_p_t'].setPosition( new Vector3( bones['b_p_t'].position.x, newBuildingHeight, newDepth + _this._porchWidth ) );            

            if (bones['r_f_l2'] ) bones['r_f_l2'].setPosition( new Vector3( bones['r_f_l2'].position.x, bones['r_f_l2'].position.y, newDepth + _this._porchWidth ) );
            if (bones['r_f_l2'] ) bones['r_f_r2'].setPosition( new Vector3( bones['r_f_r2'].position.x, bones['r_f_r2'].position.y, newDepth + _this._porchWidth ) );            

            if (bones['b_p_l3'] ) bones['b_p_l3'].setPosition( new Vector3( bones['b_f_l3'].position.x, bones['b_f_l3'].position.y, newDepth + _this._porchWidth ) );            
            if (bones['b_p_r3'] ) bones['b_p_r3'].setPosition( new Vector3( bones['b_f_r3'].position.x, bones['b_f_r3'].position.y, newDepth + _this._porchWidth ) );            
            if (bones['rail_l1'] ) bones['rail_l1'].setPosition( new Vector3( -newWidth, bones['rail_l1'].position.y, newDepth + _this._porchWidth ) );            
            if (bones['rail_l2'] ) bones['rail_l2'].setPosition( new Vector3( -newWidth, bones['rail_l2'].position.y, newDepth ) );            
            if (bones['rail_r1'] ) bones['rail_r1'].setPosition( new Vector3( newWidth, bones['rail_r1'].position.y, newDepth + _this._porchWidth ) );            
            if (bones['rail_r2'] ) bones['rail_r2'].setPosition( new Vector3( newWidth, bones['rail_r2'].position.y, newDepth ) );            
            if (bones['baluster_l'] ) bones['baluster_l'].setPosition( new Vector3( -newWidth, bones['baluster_l'].position.y, newDepth + _this._porchWidth - 0.675 ) );            
            if (bones['baluster_r'] ) bones['baluster_r'].setPosition( new Vector3( newWidth, bones['baluster_r'].position.y, newDepth + _this._porchWidth - 0.675 ) );            

            if( bones['runner_f'] ) bones['runner_f'].setPosition( new Vector3( bones['runner_f'].position.x, bones['runner_f'].position.y, newDepth + _this._porchWidth ) );
            
            const beamY = ( newBuildingHeight + newWallHeight ) / 2;
            const beamX = newWidth / 2;
            if( bones['beam_b_l1'] ) bones['beam_b_l1'].setPosition( new Vector3( -beamX, beamY, newDepth ) );
            if( bones['beam_b_r1'] ) bones['beam_b_r1'].setPosition( new Vector3( beamX, beamY, newDepth ) );
            if( bones['beam_f_l1'] ) bones['beam_f_l1'].setPosition( new Vector3( -beamX, beamY, newDepth + _this._porchWidth ) );
            if( bones['beam_f_r1'] ) bones['beam_f_r1'].setPosition( new Vector3( beamX, beamY, newDepth + _this._porchWidth ) );

            if( bones['braces_f_b'] ) bones['braces_f_b'].setPosition( new Vector3( bones['braces_f_b'].position.x, newWallHeight, newDepth + _this._porchWidth ) );
            if( bones['braces_f_l1'] ) bones['braces_f_l1'].setPosition( new Vector3( -newWidth, newWallHeight, newDepth + _this._porchWidth ) );            
            if( bones['braces_f_r1'] ) bones['braces_f_r1'].setPosition( new Vector3( newWidth, newWallHeight, newDepth + _this._porchWidth ) );            
            if( bones['braces_f_t'] ) bones['braces_f_t'].setPosition( new Vector3( bones['braces_f_t'].position.x, newBuildingHeight, newDepth + _this._porchWidth ) );

            const bracesX = ( newWidth * helpers.getTanFromDegrees( _this.getRoofDegree() ) ) / ( helpers.getTanFromDegrees( _this.getRoofDegree() ) + helpers.getTanFromDegrees( 45 ) );
            const bracesY = bracesX + newWallHeight * helpers.getTanFromDegrees( 45 );
            if( bones['braces_f_l2'] ) bones['braces_f_l2'].setPosition( new Vector3( -bracesX, bracesY, newDepth + _this._porchWidth ) );
            if( bones['braces_f_r2'] ) bones['braces_f_r2'].setPosition( new Vector3( bracesX, bracesY, newDepth + _this._porchWidth ) );
        }

        if( _this.isSidePorch() ){
            if( bones['b_b_l1'] ) bones['b_b_l1'].setPosition( new Vector3( -newWidth + _this._porchWidth, bones['b_b_l1'].position.y, -newDepth ) );
            if( bones['b_b_l2'] ) bones['b_b_l2'].setPosition( new Vector3( -newWidth + _this._porchWidth, newWallHeight, -newDepth ) );

            if( bones['b_f_l1'] ) bones['b_f_l1'].setPosition( new Vector3( -newWidth + _this._porchWidth, bones['b_f_l1'].position.y, newDepth ) );
            if( bones['b_f_l2'] ) bones['b_f_l2'].setPosition( new Vector3( -newWidth + _this._porchWidth, newWallHeight, newDepth ) );

            if( bones['b_p_l1'] ) bones['b_p_l1'].setPosition( new Vector3( -newWidth, bones['b_p_l1'].position.y, -newDepth ) );
            if( bones['b_p_l2'] ) bones['b_p_l2'].setPosition( new Vector3( -newWidth, newWallHeight, -newDepth ) );

            if( bones['b_p_r1'] ) bones['b_p_r1'].setPosition( new Vector3( -newWidth, bones['b_p_r1'].position.y, newDepth ) );
            if( bones['b_p_r2'] ) bones['b_p_r2'].setPosition( new Vector3( -newWidth, newWallHeight, newDepth ) );

            if( bones['outer_joist_l1'] ) bones['outer_joist_l1'].setPosition( bones['b_b_l1'].position );
            if( bones['outer_joist_l2'] ) bones['outer_joist_l2'].setPosition( bones['b_f_l1'].position );
        }

        if( _this.isCornerPorchLeft() ){
            if( bones['b_f_l1'] ) bones['b_f_l1'].setPosition( new Vector3( -newWidth + constants.cornerWidth, bones['b_f_l1'].position.y, newDepth ) );
            if( bones['b_f_l2'] ) bones['b_f_l2'].setPosition( new Vector3( -newWidth + constants.cornerWidth, newWallHeight, newDepth ) );

            if( bones['b_p_l1'] ) bones['b_p_l1'].setPosition( new Vector3( -newWidth, bones['b_p_l1'].position.y, newDepth - constants.cornerDepth ) );
            if( bones['b_p_l2'] ) bones['b_p_l2'].setPosition( new Vector3( -newWidth, newWallHeight, newDepth - constants.cornerDepth ) );
            if( bones['b_p_l3'] ) bones['b_p_l3'].setPosition( new Vector3( -newWidth, newWallHeight, newDepth ) );
            if( bones['b_p_l4'] ) bones['b_p_l4'].setPosition( new Vector3( -newWidth, bones['b_p_l4'].position.y, newDepth ) );
            
            if( bones['b_b_m1'] ) bones['b_b_m1'].setPosition( new Vector3( newWidth, bones['b_b_m1'].position.y, newDepth - constants.cornerDepth ) );
            if( bones['b_b_m2'] ) bones['b_b_m2'].setPosition( new Vector3( newWidth, newWallHeight, newDepth - constants.cornerDepth ) );
            if( bones['b_b_m3'] ) bones['b_b_m3'].setPosition( new Vector3( -newWidth + constants.cornerWidth, newWallHeight, newDepth - constants.cornerDepth ) );
            if( bones['b_b_m4'] ) bones['b_b_m4'].setPosition( new Vector3( -newWidth + constants.cornerWidth, bones['b_b_m4'].position.y, newDepth - constants.cornerDepth ) );
            
            if( bones['b_m_t'] ) bones['b_m_t'].setPosition( new Vector3( 0, newBuildingHeight, newDepth - constants.cornerDepth ) );

            if( bones['rail_v_r_b_l'] ) bones['rail_v_r_b_l'].setPosition( new Vector3( -newWidth + constants.cornerWidth, bones['rail_v_r_b_l'].position.y, newDepth ) );
            if( bones['rail_v_r_f_l'] ) bones['rail_v_r_f_l'].setPosition( new Vector3( -newWidth, bones['rail_v_r_f_l'].position.y, newDepth ) );

            if( bones['baluster_f_l1'] ) bones['baluster_f_l1'].setPosition( new Vector3( -newWidth + constants.cornerWidth - 0.35, bones['baluster_f_l1'].position.y, newDepth - _this._balusterOffsetLeft ) );
        }

        if( 'mainus_quaker' === _this._shedType ) {
            const widthChange       = newWidth * 2 - initialWidth;
            const quakerX           = 3.586 + widthChange; // 3.586 is original coordinates of b_b_l1 and b_b_l2
            const quakerHalfWidth   = ( newWidth * 2 - 2.414 ) / 2; // 2.414 is instance b_b_r4 and center(b_b_r2)

            if( bones['b_b_l1'] != undefined ) bones['b_b_l1'].setPosition( new Vector3( _this.quakerGetXPosition( -quakerX ), 0, -newDepth ) );
            if( bones['b_b_l2'] != undefined ) bones['b_b_l2'].setPosition( new Vector3( _this.quakerGetXPosition( -quakerX ), newWallHeight, -newDepth ) );
            
            if( bones['b_b_r1'] != undefined ) bones['b_b_r1'].setPosition( new Vector3( _this.quakerGetXPosition( 0 ), 0, -newDepth ) );
            if( bones['b_b_r2'] != undefined ) bones['b_b_r2'].setPosition( new Vector3( _this.quakerGetXPosition( 0 ), newWallHeight, -newDepth ) );
            
            if( bones['b_b_r3'] != undefined ) bones['b_b_r3'].setPosition( new Vector3( _this.quakerGetXPosition( 2.414 ), newWallHeight - 1, -newDepth ) );
            if( bones['b_b_r4'] != undefined ) bones['b_b_r4'].setPosition( new Vector3( _this.quakerGetXPosition( 2.414 ), 0, -newDepth ) );
            
            if( bones['b_b_t'] != undefined ) bones['b_b_t'].setPosition( new Vector3( _this.quakerGetXPosition( -quakerHalfWidth ), newBuildingHeight, -newDepth ) );

            if( bones['b_f_l1'] != undefined ) bones['b_f_l1'].setPosition( new Vector3( _this.quakerGetXPosition( -quakerX ), 0, newDepth ) );
            if( bones['b_f_l2'] != undefined ) bones['b_f_l2'].setPosition( new Vector3( _this.quakerGetXPosition( -quakerX ), newWallHeight, newDepth ) );

            if( bones['b_f_r1'] != undefined ) bones['b_f_r1'].setPosition( new Vector3( _this.quakerGetXPosition( 0 ), 0, newDepth ) );
            if( bones['b_f_r2'] != undefined ) bones['b_f_r2'].setPosition( new Vector3( _this.quakerGetXPosition( 0 ), newWallHeight, newDepth ) );

            if( bones['b_f_r3'] != undefined ) bones['b_f_r3'].setPosition( new Vector3( _this.quakerGetXPosition( 2.414 ), newWallHeight - 1, newDepth ) );
            if( bones['b_f_r4'] != undefined ) bones['b_f_r4'].setPosition( new Vector3( _this.quakerGetXPosition( 2.414 ), 0, newDepth ) );

            if( bones['b_f_t'] != undefined ) bones['b_f_t'].setPosition( new Vector3( _this.quakerGetXPosition( -quakerHalfWidth ), newBuildingHeight, newDepth ) );

            if( bones['outer_joist_l1'] != undefined ) bones['outer_joist_l1'].setPosition( new Vector3( _this.quakerGetXPosition( -quakerX ), bones['outer_joist_l1'].position.y, -newDepth ) );
            if( bones['outer_joist_r1'] != undefined ) bones['outer_joist_r1'].setPosition( new Vector3( _this.quakerGetXPosition( 2.414 ), bones['outer_joist_r1'].position.y, -newDepth ) );
            if( bones['outer_joist_l2'] != undefined ) bones['outer_joist_l2'].setPosition( new Vector3( _this.quakerGetXPosition( -quakerX ), bones['outer_joist_l2'].position.y, newDepth ) );
            if( bones['outer_joist_r2'] != undefined ) bones['outer_joist_r2'].setPosition( new Vector3( _this.quakerGetXPosition( 2.414 ), bones['outer_joist_r2'].position.y, newDepth ) );

            if( bones['r_b_l1'] != undefined ) bones['r_b_l1'].setPosition( new Vector3( _this.quakerGetXPosition( -quakerX ), newWallHeight, -newDepth ) );
            if( bones['r_b_r1'] != undefined ) bones['r_b_r1'].setPosition( new Vector3( _this.quakerGetXPosition( 2.414 ), newWallHeight - 1, -newDepth ) );
            
            if( bones['r_b_t'] != undefined ) bones['r_b_t'].setPosition( new Vector3( _this.quakerGetXPosition( -quakerHalfWidth ), newBuildingHeight, -newDepth ) );
            if( bones['r_f_t'] != undefined ) bones['r_f_t'].setPosition( new Vector3( _this.quakerGetXPosition( -quakerHalfWidth ), newBuildingHeight, newDepth ) );

            if( bones['r_f_l1'] != undefined ) bones['r_f_l1'].setPosition( new Vector3( _this.quakerGetXPosition( -quakerX ), newWallHeight, newDepth ) );
            if( bones['r_f_r1'] != undefined ) bones['r_f_r1'].setPosition( new Vector3( _this.quakerGetXPosition( 2.414 ), newWallHeight - 1, newDepth ) );

            if( bones['rm_l1'] != undefined ) bones['rm_l1'].setPosition( new Vector3( _this.quakerGetXPosition( -quakerX ), newWallHeight, -newDepth ) );
            if( bones['rm_r1'] != undefined ) bones['rm_r1'].setPosition( new Vector3( _this.quakerGetXPosition( 2.414 ), newWallHeight - 1, -newDepth ) );

            if( bones['rm_t'] != undefined ) bones['rm_t'].setPosition( new Vector3( _this.quakerGetXPosition( -quakerHalfWidth ), newBuildingHeight, -newDepth ) );
        }

        if( 'structure_saltbox_mwshed' === _this._shedType ){
            const saltboxFHeight   = 88/12 + this._sizeErrorValue;
            const saltboxBHeight   = 74/12 + this._sizeErrorValue;

            const bWallX        = ( saltboxFHeight + ( 16/12 ) * helpers.getTanFromDegrees( _this.getRoofDegree() ) - saltboxBHeight ) / helpers.getTanFromDegrees( _this.getRoofDegree() );
            const fWallX        = width - bWallX;
            const roofX         = fWallX / 2;
            const bHeight       = saltboxBHeight + 14/12 + 16 * helpers.getTanFromDegrees( _this.getRoofDegree() ) / 12 + ( width - bWallX ) / 2 * helpers.getTanFromDegrees( _this.getRoofDegree() );
            const midHeight     = saltboxFHeight + 16/12 * helpers.getTanFromDegrees( _this.getRoofDegree() );

            _this.setRoofPitchHeight( bHeight - newWallHeight );
            _this.setBuildingHeight( bHeight );

            if( bones['b_b_t'] ) bones['b_b_t'].setPosition( new Vector3( -roofX, bHeight, -newDepth ) );

            if( bones['b_b_l1'] ) bones['b_b_l1'].setPosition( new Vector3( -fWallX, 0, -newDepth ) );
            if( bones['b_b_l2'] ) bones['b_b_l2'].setPosition( new Vector3( -fWallX, midHeight, -newDepth ) );

            if( bones['b_b_r1'] ) bones['b_b_r1'].setPosition( new Vector3( 0, 0, -newDepth ) );
            if( bones['b_b_r2'] ) bones['b_b_r2'].setPosition( new Vector3( 0, midHeight, -newDepth ) );            
            if( bones['b_b_r3'] ) bones['b_b_r3'].setPosition( new Vector3( bWallX, newWallHeight, -newDepth ) );
            if( bones['b_b_r4'] ) bones['b_b_r4'].setPosition( new Vector3( bWallX, 0, -newDepth ) );

            if( bones['b_f_t'] ) bones['b_f_t'].setPosition( new Vector3( -roofX, bHeight, newDepth ) );

            if( bones['b_f_l1'] ) bones['b_f_l1'].setPosition( new Vector3( -fWallX, 0, newDepth ) );
            if( bones['b_f_l2'] ) bones['b_f_l2'].setPosition( new Vector3( -fWallX, midHeight, newDepth ) );

            if( bones['b_f_r1'] ) bones['b_f_r1'].setPosition( new Vector3( 0, 0, newDepth ) );
            if( bones['b_f_r2'] ) bones['b_f_r2'].setPosition( new Vector3( 0, midHeight, newDepth ) );
            if( bones['b_f_r3'] ) bones['b_f_r3'].setPosition( new Vector3( bWallX, newWallHeight, newDepth ) );
            if( bones['b_f_r4'] ) bones['b_f_r4'].setPosition( new Vector3( bWallX, 0, newDepth ) );


            if( bones['r_b_l1'] ) bones['r_b_l1'].setPosition( new Vector3( -fWallX, midHeight, -newDepth ) );
            if( bones['r_b_r1'] ) bones['r_b_r1'].setPosition( new Vector3( bWallX, newWallHeight, -newDepth ) );
            
            if( bones['r_b_t'] ) bones['r_b_t'].setPosition( new Vector3( -roofX, bHeight, -newDepth ) );
            if( bones['r_f_t'] ) bones['r_f_t'].setPosition( new Vector3( -roofX, bHeight, newDepth ) );

            if( bones['r_f_l1'] ) bones['r_f_l1'].setPosition( new Vector3( -fWallX, midHeight, newDepth ) );
            if( bones['r_f_r1'] ) bones['r_f_r1'].setPosition( new Vector3( bWallX, newWallHeight, newDepth ) );

            if( bones['rm_l1'] ) bones['rm_l1'].setPosition( new Vector3( -fWallX, midHeight, -newDepth ) );
            if( bones['rm_r1'] ) bones['rm_r1'].setPosition( new Vector3( bWallX, newWallHeight, -newDepth ) );

            if( bones['rm_t'] ) bones['rm_t'].setPosition( new Vector3( -roofX, bHeight, -newDepth ) ); 
            
            if( bones['outer_joist_l1'] ) bones['outer_joist_l1'].setPosition( new Vector3( -fWallX, bones['outer_joist_l1'].position.y, -newDepth ) );
            if( bones['outer_joist_r1'] ) bones['outer_joist_r1'].setPosition( new Vector3( bWallX, bones['outer_joist_r1'].position.y, -newDepth ) );
            if( bones['outer_joist_l2'] ) bones['outer_joist_l2'].setPosition( new Vector3( -fWallX, bones['outer_joist_l2'].position.y, newDepth ) );
            if( bones['outer_joist_r2'] ) bones['outer_joist_r2'].setPosition( new Vector3( bWallX, bones['outer_joist_r2'].position.y, newDepth ) );
        }

        if( 'structure_cottage_shed' === _this._shedType ) {
            if( bones['b_f_l2'] ) bones['b_f_l2'].setPosition( new Vector3( -newWidth, newWallHeight, newDepth + 0.002 ) );            
            if( bones['r_f_l1'] ) bones['r_f_l1'].setPosition( new Vector3( bones['b_f_l2'].position.x - 1.25, bones['b_f_l2'].position.y + 0.5, bones['b_f_l2'].position.z ) ); 
            if( bones['r_b_l1'] ) bones['r_b_l1'].setPosition( new Vector3( bones['b_b_l2'].position.x - 1.25, bones['b_b_l2'].position.y + 0.5, bones['b_b_l2'].position.z ) ); 
            if( bones['rm_l1'] ) bones['rm_l1'].setPosition( new Vector3( bones['b_b_l2'].position.x - 1.25, bones['b_b_l2'].position.y + 0.5, bones['b_b_l2'].position.z ) ); 
        }

        if( 'structure_quaker_silvercreek' === _this._shedType ) {
            const tmpX          = width / _this._initRatioRoofSaltbox;
            this._quakerWidth   = helpers.toFixed3( width / 2 + tmpX );
            const tmpY          = this._quakerWidth * helpers.getTanFromDegrees( _this.getRoofDegree() ) + _this.getWallHeight();

            if (bones['b_b_t'] != undefined) bones['b_b_t'].setPosition(new Vector3( tmpX, tmpY, bones['b_b_t'].position.z));
            if (bones['b_f_t'] != undefined) bones['b_f_t'].setPosition(new Vector3( tmpX, tmpY, bones['b_f_t'].position.z));
            if (bones['r_b_t'] != undefined) bones['r_b_t'].setPosition(new Vector3( tmpX, tmpY, bones['r_b_t'].position.z));
            if (bones['r_f_t'] != undefined) bones['r_f_t'].setPosition(new Vector3( tmpX, tmpY, bones['r_f_t'].position.z));
            if (bones['rm_t'] != undefined) bones['rm_t'].setPosition(new Vector3( tmpX, tmpY, bones['rm_t'].position.z));

            if( bones['horizontal_trim_l1'] ){
                let roofTan     = helpers.getTanFromDegrees( _this.getRoofDegree() );
                let roofTan2    = helpers.getTanFromDegrees( Math.atan( ( bones['b_b_t'].position.y - bones['b_b_l2'].position.y ) / ( newWidth - tmpX ) ) * 180 / Math.PI );
        
                let trimX       = ( ( tmpY - bones['horizontal_trim_l1'].position.y ) / roofTan ) - tmpX;
                let trimX2      = ( ( tmpY - bones['horizontal_trim_l4'].position.y ) / roofTan ) - tmpX;

                let trimX4      = ( ( tmpY - bones['horizontal_trim_l4'].position.y ) / roofTan2 ) + tmpX;
                let trimX5      = ( ( tmpY - bones['horizontal_trim_l1'].position.y ) / roofTan2 ) + tmpX;

                if( newWallHeight >= bones['horizontal_trim_l2'].position.y ){
                    trimX = trimX5 = newWidth;
                }

                if( newWallHeight >= bones['horizontal_trim_l3'].position.y ){
                    trimX2 = trimX4 = newWidth;
                }
                
                if( bones['horizontal_trim_l1'] ) bones['horizontal_trim_l1'].setPosition( new Vector3( -trimX, bones['horizontal_trim_l1'].position.y, -newDepth ) );
                if( bones['horizontal_trim_l4'] ) bones['horizontal_trim_l4'].setPosition( new Vector3( -trimX2, bones['horizontal_trim_l4'].position.y, -newDepth ) );
                if( bones['horizontal_trim_r4'] ) bones['horizontal_trim_r4'].setPosition( new Vector3( trimX4, bones['horizontal_trim_r4'].position.y, -newDepth ) );
                if( bones['horizontal_trim_r1'] ) bones['horizontal_trim_r1'].setPosition( new Vector3( trimX5, bones['horizontal_trim_r1'].position.y, -newDepth ) );

                if( bones['horizontal_trim_l2'] ) bones['horizontal_trim_l2'].setPosition( new Vector3( -trimX, bones['horizontal_trim_l2'].position.y, newDepth ) );
                if( bones['horizontal_trim_l3'] ) bones['horizontal_trim_l3'].setPosition( new Vector3( -trimX2, bones['horizontal_trim_l3'].position.y, newDepth ) );
                if( bones['horizontal_trim_r3'] ) bones['horizontal_trim_r3'].setPosition( new Vector3( trimX4, bones['horizontal_trim_r3'].position.y, newDepth ) );
                if( bones['horizontal_trim_r2'] ) bones['horizontal_trim_r2'].setPosition( new Vector3( trimX5, bones['horizontal_trim_r2'].position.y, newDepth ) );
            }

            const hTrimMesh = _this.getMeshByName( 'horizontal_trim_obj' );
            if( hTrimMesh ){
                if( newBuildingHeight <= bones['horizontal_trim_l4'].position.y ){
                    hTrimMesh.setEnabled( false );
                }else{
                    hTrimMesh.setEnabled( true );
                }
            }

            _this.setBuildingHeight( tmpY );
        }

        if( 'structure_quaker_wannabuysheds' === _this._shedType ) {
            const ratio     = ( newWidth * 2 + 1 ) / _this._initialWidth;
            let buildingX   = _this._midPointPosition._x * ratio;
            let positionX   = newWidth + 1;

            if( 'structure_quaker_milerstorage' === this._shedTypeVersion ) {
                const newPointTop = this.calculatePointOnSegment( new Vector3( - ( newWidth + 1.33 ), newWallHeight, 0 ), new Vector3( newWidth, newWallHeight, 0 ), 8/12, 4/12 );
                buildingX = newPointTop.x;
                positionX = newWidth + 1.33;
            }

            if( bones['b_b_l1'] ) bones['b_b_l1'].setPosition( new Vector3( -newWidth, bones['b_b_l1'].position.y, -newDepth ) );
            if( bones['b_b_l2'] ) bones['b_b_l2'].setPosition( new Vector3( -newWidth, newWallHeight, -newDepth ) );
            if( bones['b_f_l1'] ) bones['b_f_l1'].setPosition( new Vector3( -newWidth, bones['b_f_l1'].position.y,  newDepth ) );
            if( bones['b_f_l2'] ) bones['b_f_l2'].setPosition( new Vector3( -newWidth, newWallHeight,  newDepth ) );

            if( bones['b_b_l3'] ) bones['b_b_l3'].setPosition( new Vector3( -positionX, newWallHeight, -newDepth ) );
            if( bones['b_f_l3'] ) bones['b_f_l3'].setPosition( new Vector3( -positionX, newWallHeight,  newDepth ) );

            if( bones['rm_l1'] ) bones['rm_l1'].setPosition( new Vector3( -positionX, bones['rm_l1'].position.y, bones['rm_l1'].position.z ) );
            if( bones['r_b_l1'] ) bones['r_b_l1'].setPosition( new Vector3( -positionX, bones['r_b_l1'].position.y, bones['r_b_l1'].position.z ) );
            if( bones['r_f_l1'] ) bones['r_f_l1'].setPosition( new Vector3( -positionX, bones['r_f_l1'].position.y, bones['r_f_l1'].position.z ) );

            if( bones['b_b_t'] ) bones['b_b_t'].setPosition( new Vector3( buildingX, newBuildingHeight, bones['b_b_t'].position.z ) );
            if( bones['b_f_t'] ) bones['b_f_t'].setPosition( new Vector3( buildingX, newBuildingHeight, bones['b_f_t'].position.z ) );

            if( bones['r_b_t'] ) bones['r_b_t'].setPosition( new Vector3( buildingX, newBuildingHeight, bones['r_b_t'].position.z ) );
            if( bones['r_f_t'] ) bones['r_f_t'].setPosition( new Vector3( buildingX, newBuildingHeight, bones['r_f_t'].position.z ) );
            if( bones['rm_t'] ) bones['rm_t'].setPosition( new Vector3( buildingX, newBuildingHeight, bones['rm_t'].position.z ) );
        }

        if( 'structure_teahouse' === _this._shedType ) {
            const balusterToSide    = 0.276;
            const intWidth          = this.getInitialWidth() / 2;
            const scale             = intWidth / newWidth;
            const bracesBX          = this._initBracesBR2._x / scale;
            const bracesBY          = ( newWidth - bracesBX ) *  helpers.getTanFromDegrees( _this._roofDegree ) + newWallHeight;
            if ( bones['b_m_l1'] ) bones['b_m_l1'].setPosition( new Vector3( -newWidth, 0, 0 ) );
            if ( bones['b_m_l2'] ) bones['b_m_l2'].setPosition( new Vector3( -newWidth, newWallHeight, 0 ) );
            if ( bones['b_m_r1'] ) bones['b_m_r1'].setPosition( new Vector3( newWidth, 0, 0 ) );
            if ( bones['b_m_r2'] ) bones['b_m_r2'].setPosition( new Vector3( newWidth, newWallHeight, 0 ) );

            if ( bones['b_b_m1'] ) bones['b_b_m1'].setPosition( new Vector3( 0, 0, -newDepth ) );
            if ( bones['b_b_m2'] ) bones['b_b_m2'].setPosition( new Vector3( 0, newWallHeight, -newDepth ) );
            if ( bones['b_f_m1'] ) bones['b_f_m1'].setPosition( new Vector3( 0, 0, newDepth ) );
            if ( bones['b_f_m2'] ) bones['b_f_m2'].setPosition( new Vector3( 0, newWallHeight, newDepth ) );
 
            if ( bones['braces_b'] ) bones['braces_b'].setPosition( new Vector3( 0, newWallHeight, -newDepth ) );
            if ( bones['braces_f'] ) bones['braces_f'].setPosition( new Vector3( 0, newWallHeight, newDepth ) );
            
            if ( bones['braces_b_t'] ) bones['braces_b_t'].setPosition( new Vector3( 0, newBuildingHeight, -newDepth ) );
            if ( bones['braces_f_t'] ) bones['braces_f_t'].setPosition( new Vector3( 0, newBuildingHeight, newDepth ) );
            
            if ( bones['braces_b_r2'] ) bones['braces_b_r2'].setPosition( new Vector3( bracesBX, bracesBY, -newDepth ) );
            if ( bones['braces_b_l2'] ) bones['braces_b_l2'].setPosition( new Vector3( -bracesBX, bracesBY, -newDepth ) );
            if ( bones['braces_f_r2'] ) bones['braces_f_r2'].setPosition( new Vector3( bracesBX, bracesBY, newDepth ) );
            if ( bones['braces_f_l2'] ) bones['braces_f_l2'].setPosition( new Vector3( -bracesBX, bracesBY, newDepth ) );

            if ( bones['inner_frame_horizontal_clone_l1'] ) bones['inner_frame_horizontal_clone_l1'].setPosition( new Vector3( -newWidth, newWallHeight, 0 ) );
            if ( bones['inner_frame_horizontal_clone_r1'] ) bones['inner_frame_horizontal_clone_r1'].setPosition( new Vector3( newWidth, newWallHeight, 0 ) );

            if ( bones['baluster_m_l'] ) bones['baluster_m_l'].setPosition( new Vector3( newWidth - balusterToSide, bones['baluster_m_l'].position.y, 0 ) );

            if ( bones['baluster_f_lr_01'] ) bones['baluster_f_lr_01'].setPosition( new Vector3( - newWidth + balusterToSide, bones['baluster_f_lr_01'].position.y, bones['baluster_f_lr_01'].position.z ) );
            if ( bones['baluster_f_lr_02'] ) bones['baluster_f_lr_02'].setPosition( new Vector3( newWidth - balusterToSide, bones['baluster_f_lr_02'].position.y, bones['baluster_f_lr_01'].position.z ) );

            if ( bones['baluster_b_lr_01'] ) bones['baluster_b_lr_01'].setPosition( new Vector3( - newWidth + balusterToSide, bones['baluster_b_lr_01'].position.y, bones['baluster_b_lr_01'].position.z ) );
            if ( bones['baluster_b_lr_02'] ) bones['baluster_b_lr_02'].setPosition( new Vector3( newWidth - balusterToSide, bones['baluster_b_lr_02'].position.y, bones['baluster_b_lr_02'].position.z ) );

            if ( bones['baluster_l_fb_01'] ) bones['baluster_l_fb_01'].setPosition( new Vector3( bones['baluster_l_fb_01'].position.x, bones['baluster_l_fb_01'].position.y, newDepth - balusterToSide ) );
            if ( bones['baluster_l_fb_02'] ) bones['baluster_l_fb_02'].setPosition( new Vector3( bones['baluster_l_fb_02'].position.x, bones['baluster_l_fb_02'].position.y, - newDepth + balusterToSide ) );

            if ( bones['baluster_r_fb_01'] ) bones['baluster_r_fb_01'].setPosition( new Vector3( bones['baluster_r_fb_01'].position.x, bones['baluster_r_fb_01'].position.y, newDepth - balusterToSide ) );
            if ( bones['baluster_r_fb_02'] ) bones['baluster_r_fb_02'].setPosition( new Vector3( bones['baluster_r_fb_02'].position.x, bones['baluster_r_fb_02'].position.y, - newDepth + balusterToSide ) );
        }

        if( 'structure_dog_kennel' === _this._shedType ) { // Dog kennel V2
            //roof inner frame
            if ( bones['inner_frame_horizontal_clone_l1'] ) bones['inner_frame_horizontal_clone_l1'].setPosition( new Vector3( - newWidth, newWallHeight, 0 ) );
            if ( bones['inner_frame_horizontal_clone_r1'] ) bones['inner_frame_horizontal_clone_r1'].setPosition( new Vector3( newWidth, newWallHeight, 0 ) );
            //wire frame
            fixedPart = newDepth <= 4 ? newDepth - 4 : newDepth - 8;
            const wire_frame_m_y = ( newDepth + fixedPart ) / 2;
            if ( bones['wire_frame_lr_f_l1'] ) bones['wire_frame_lr_f_l1'].setPosition( new Vector3( - newWidth, bones['wire_frame_lr_f_l1'].position.y, newDepth ) );
            if ( bones['wire_frame_lr_f_l2'] ) bones['wire_frame_lr_f_l2'].setPosition( new Vector3( - newWidth, newWallHeight / 2, newDepth ) );
            if ( bones['wire_frame_lr_f_l3'] ) bones['wire_frame_lr_f_l3'].setPosition( new Vector3( - newWidth, newWallHeight, newDepth ) );
            
            if ( bones['wire_frame_lr_f_r1'] ) bones['wire_frame_lr_f_r1'].setPosition( new Vector3( newWidth, bones['wire_frame_lr_f_r1'].position.y, newDepth ) );
            if ( bones['wire_frame_lr_f_r2'] ) bones['wire_frame_lr_f_r2'].setPosition( new Vector3( newWidth, newWallHeight / 2, newDepth ) );
            if ( bones['wire_frame_lr_f_r3'] ) bones['wire_frame_lr_f_r3'].setPosition( new Vector3( newWidth, newWallHeight, newDepth ) );
            
            if ( bones['wire_frame_lr_m_l1'] ) bones['wire_frame_lr_m_l1'].setPosition( new Vector3( - newWidth, bones['wire_frame_lr_m_l1'].position.y, wire_frame_m_y ) );
            if ( bones['wire_frame_lr_m_l2'] ) bones['wire_frame_lr_m_l2'].setPosition( new Vector3( - newWidth, newWallHeight, wire_frame_m_y ) );
            if ( bones['wire_frame_lr_m_r1'] ) bones['wire_frame_lr_m_r1'].setPosition( new Vector3( newWidth, bones['wire_frame_lr_m_r1'].position.y, wire_frame_m_y ) );
            if ( bones['wire_frame_lr_m_r2'] ) bones['wire_frame_lr_m_r2'].setPosition( new Vector3( newWidth, newWallHeight, wire_frame_m_y ) );

            if ( bones['wire_frame_lr_b_l1'] ) bones['wire_frame_lr_b_l1'].setPosition( new Vector3( - newWidth, bones['wire_frame_lr_b_l1'].position.y, fixedPart ) );
            if ( bones['wire_frame_lr_b_l2'] ) bones['wire_frame_lr_b_l2'].setPosition( new Vector3( - newWidth, newWallHeight / 2, fixedPart ) );
            if ( bones['wire_frame_lr_b_l3'] ) bones['wire_frame_lr_b_l3'].setPosition( new Vector3( - newWidth, newWallHeight, fixedPart ) );
            
            if ( bones['wire_frame_lr_b_r1'] ) bones['wire_frame_lr_b_r1'].setPosition( new Vector3( newWidth, bones['wire_frame_lr_b_r1'].position.y, fixedPart ) );
            if ( bones['wire_frame_lr_b_r2'] ) bones['wire_frame_lr_b_r2'].setPosition( new Vector3( newWidth, newWallHeight / 2, fixedPart ) );
            if ( bones['wire_frame_lr_b_r3'] ) bones['wire_frame_lr_b_r3'].setPosition( new Vector3( newWidth, newWallHeight, fixedPart ) );
            //midle side
            if ( bones['b_m_l1'] ) bones['b_m_l1'].setPosition( new Vector3( - newWidth, bones['b_m_l1'].position.y, fixedPart ) );
            if ( bones['b_m_l2'] ) bones['b_m_l2'].setPosition( new Vector3( - newWidth, newWallHeight, fixedPart ) );
            if ( bones['b_m_r1'] ) bones['b_m_r1'].setPosition( new Vector3( newWidth, bones['b_m_r1'].position.y, fixedPart ) );
            if ( bones['b_m_r2'] ) bones['b_m_r2'].setPosition( new Vector3( newWidth, newWallHeight, fixedPart ) );
            if ( bones['b_m_t'] ) bones['b_m_t'].setPosition( new Vector3( 0, newBuildingHeight, fixedPart ) );
            //Single door
            if ( bones['door_wire_8ft_single_l1'] ) bones['door_wire_8ft_single_l1'].setPosition( new Vector3( - newWidth, bones['door_wire_8ft_single_l1'].position.y, newDepth ) );
            if ( bones['door_wire_8ft_single_l2'] ) bones['door_wire_8ft_single_l2'].setPosition( new Vector3( - newWidth, newWallHeight / 2, newDepth ) );
            if ( bones['door_wire_8ft_single_l3'] ) bones['door_wire_8ft_single_l3'].setPosition( new Vector3( - newWidth, newWallHeight, newDepth ) );
            if ( bones['door_wire_8ft_single_m1'] ) bones['door_wire_8ft_single_m1'].setPosition( new Vector3( 0, bones['door_wire_8ft_single_m1'].position.y, newDepth ) );
            if ( bones['door_wire_8ft_single_m2'] ) bones['door_wire_8ft_single_m2'].setPosition( new Vector3( 0, newWallHeight / 2, newDepth ) );
            if ( bones['door_wire_8ft_single_m3'] ) bones['door_wire_8ft_single_m3'].setPosition( new Vector3( 0, newWallHeight, newDepth ) );
            if ( bones['door_wire_8ft_single_r1'] ) bones['door_wire_8ft_single_r1'].setPosition( new Vector3( newWidth, bones['door_wire_8ft_single_r1'].position.y, newDepth ) );
            if ( bones['door_wire_8ft_single_r2'] ) bones['door_wire_8ft_single_r2'].setPosition( new Vector3( newWidth, newWallHeight / 2, newDepth ) );
            if ( bones['door_wire_8ft_single_r3'] ) bones['door_wire_8ft_single_r3'].setPosition( new Vector3( newWidth, newWallHeight, newDepth ) );
            //double door
            if ( bones['door_wire_8ft_double_l1'] ) bones['door_wire_8ft_double_l1'].setPosition( new Vector3( - newWidth, bones['door_wire_8ft_double_l1'].position.y, newDepth ) );
            if ( bones['door_wire_8ft_double_l2'] ) bones['door_wire_8ft_double_l2'].setPosition( new Vector3( - newWidth, newWallHeight / 2, newDepth ) );
            if ( bones['door_wire_8ft_double_l3'] ) bones['door_wire_8ft_double_l3'].setPosition( new Vector3( - newWidth, newWallHeight, newDepth ) );
            if ( bones['door_wire_8ft_double_m1'] ) bones['door_wire_8ft_double_m1'].setPosition( new Vector3( 0, bones['door_wire_8ft_double_m1'].position.y, newDepth ) );
            if ( bones['door_wire_8ft_double_m2'] ) bones['door_wire_8ft_double_m2'].setPosition( new Vector3( 0, newWallHeight, newDepth ) );
            if ( bones['door_wire_8ft_double_r1'] ) bones['door_wire_8ft_double_r1'].setPosition( new Vector3( newWidth, bones['door_wire_8ft_double_r1'].position.y, newDepth ) );
            if ( bones['door_wire_8ft_double_r2'] ) bones['door_wire_8ft_double_r2'].setPosition( new Vector3( newWidth, newWallHeight / 2, newDepth ) );
            if ( bones['door_wire_8ft_double_r3'] ) bones['door_wire_8ft_double_r3'].setPosition( new Vector3( newWidth, newWallHeight, newDepth ) );
            // side run
            if ( bones['wire_frame_m_b_l1'] ) bones['wire_frame_m_b_l1'].setPosition( new Vector3( 0, bones['wire_frame_m_b_l1'].position.y, fixedPart ) );
            if ( bones['wire_frame_m_b_l2'] ) bones['wire_frame_m_b_l2'].setPosition( new Vector3( 0, newWallHeight / 2, fixedPart ) );
            if ( bones['wire_frame_m_b_l3'] ) bones['wire_frame_m_b_l3'].setPosition( new Vector3( 0, newWallHeight, fixedPart ) );
            
            if ( bones['wire_frame_m_m_l1'] ) bones['wire_frame_m_m_l1'].setPosition( new Vector3( 0, bones['wire_frame_m_m_l1'].position.y, wire_frame_m_y ) );
            if ( bones['wire_frame_m_m_l2'] ) bones['wire_frame_m_m_l2'].setPosition( new Vector3( 0, newWallHeight, wire_frame_m_y ) );

            if ( bones['wire_frame_m_f_l1'] ) bones['wire_frame_m_f_l1'].setPosition( new Vector3( 0, bones['wire_frame_m_f_l1'].position.y, newDepth ) );
            if ( bones['wire_frame_m_f_l2'] ) bones['wire_frame_m_f_l2'].setPosition( new Vector3( 0, newWallHeight / 2, newDepth ) );
            if ( bones['wire_frame_m_f_l3'] ) bones['wire_frame_m_f_l3'].setPosition( new Vector3( 0, newWallHeight, newDepth ) );
            //door clone middle side
            if ( bones['dogkennel_door_cloned'] ) bones['dogkennel_door_cloned'].setPosition( new Vector3( 0, 0, fixedPart ) );
        }

        if( 'structure_cardinal_lofted_cabin' === _this._shedType ) {
            let tanRoof         = _this._roofLowerDegree ? helpers.getTanFromDegrees( _this._roofLowerDegree ) : helpers.getTanFromDegrees( _this._initRoofLowerDegree );
            let newY            = newWallHeight + ( ( this._initialWidth / 2 ) - this._roofMiddleR2Position.x ) * tanRoof;
            let newX            = Number( Math.abs( newWidth - ( ( this._initialWidth / 2 ) - this._roofMiddleR2Position.x ) ).toFixed( 3 ) );

            if (bones['b_b_l3'] ) bones['b_b_l3'].setPosition( new Vector3( -newX,newY, -newDepth ) );
            if (bones['b_b_r3'] ) bones['b_b_r3'].setPosition( new Vector3( newX, newY, -newDepth ) );

            if (bones['b_f_l3'] ) bones['b_f_l3'].setPosition( new Vector3( -newX,newY, newDepth ) );
            if (bones['b_f_r3'] ) bones['b_f_r3'].setPosition( new Vector3( newX, newY, newDepth ) );

            if (bones['b_p_l3'] ) bones['b_p_l3'].setPosition( new Vector3( -newX, newY, newDepth + _this._porchWidth ) );
            if (bones['b_p_r3'] ) bones['b_p_r3'].setPosition( new Vector3( newX, newY, newDepth + _this._porchWidth ) );

            if (bones['r_b_l2'] ) bones['r_b_l2'].setPosition( new Vector3( -newX, newY, -newDepth ) );
            if (bones['r_b_r2'] ) bones['r_b_r2'].setPosition( new Vector3( newX, newY, -newDepth ) );

            if (bones['r_f_l2'] ) bones['r_f_l2'].setPosition( new Vector3( -newX, newY, newDepth + _this._porchWidth ) );
            if (bones['r_f_r2'] ) bones['r_f_r2'].setPosition( new Vector3( newX, newY, newDepth + _this._porchWidth ) );

            if (bones['rm_l2'] ) bones['rm_l2'].setPosition( new Vector3( -newX, newY, -newDepth ) );
            if (bones['rm_r2'] ) bones['rm_r2'].setPosition( new Vector3( newX, newY, -newDepth ) );
            if (bones['rm_t'] ) bones['rm_t'].setPosition( new Vector3( 0, newBuildingHeight, -newDepth ) );

            if (bones['b_b_t'] ) bones['b_b_t'].setPosition( new Vector3( 0, newBuildingHeight, -newDepth ) );
            if (bones['r_b_t'] ) bones['r_b_t'].setPosition( new Vector3( 0, newBuildingHeight, -newDepth ) );
            if (bones['b_f_t'] ) bones['b_f_t'].setPosition( new Vector3( 0, newBuildingHeight, newDepth ) );
            if (bones['b_p_t'] ) bones['b_p_t'].setPosition( new Vector3( 0, newBuildingHeight, newDepth + _this._porchWidth ) );
            if (bones['r_f_t'] ) bones['r_f_t'].setPosition( new Vector3( 0, newBuildingHeight, newDepth + _this._porchWidth ) );
        }

        if( 'structure_lakeside_bunkie_gambrel_roof' === _this._shedType || 'structure_lakeside_bunkie_gable_roof' === this._shedType ){
            const overHangZ = newDepth + 32/12;
            if (bones['r_f_r1'] != undefined) bones['r_f_r1'].setPosition(new Vector3(newWidth, newWallHeight, overHangZ));
            if (bones['r_f_l1'] != undefined) bones['r_f_l1'].setPosition(new Vector3(-newWidth, newWallHeight, overHangZ));
            if (bones['r_f_t'] != undefined) bones['r_f_t'].setPosition(new Vector3(bones['r_f_t'].position.x, newBuildingHeight, overHangZ));
        }

        if( this.isCarport() ){
            const offsetFromTop = 'carport_triplewide_attic_rooftruss' === this._shedType ? 1.74 : 0.395;
            let braceSideY2     = newWallHeight - 1.487;
            let braceSideY3     = newWallHeight;
            let braceSideY4     = newWallHeight + Math.tan( roofDegree * Math.PI / 180 ) * 1.828;
            let braceSideX      = newWidth - 1.828;
            let braceTopY       = newBuildingHeight - offsetFromTop;
            let braceTopX       = offsetFromTop / Math.tan( roofDegree * Math.PI / 180 );
            let braceX3         = newWidth;
            let braceSideY6     = newWallHeight + 0.369;

            if( 'carport_standard_regularroof' === _this._shedType ) {
                braceSideY2     = newWallHeight;
                braceSideY3     = newWallHeight + 1.847;
                braceSideY4     = braceSideY3 + Math.tan( roofDegree * Math.PI / 180 ) * 0.752;
                braceX3         = newWidth - 1.079;

                if( roofDegree > 18.43 ){
                    braceSideY6 -= 0.0055 * ( roofDegree - 18.43 );
                }

                if( bones['r_b_l2'] ) bones['r_b_l2'].setPosition( new Vector3( -newWidth, braceSideY6, -newDepth ) );
                if( bones['r_b_r2'] ) bones['r_b_r2'].setPosition( new Vector3( newWidth, braceSideY6, -newDepth ) );
                if( bones['r_f_l2'] ) bones['r_f_l2'].setPosition( new Vector3( -newWidth, braceSideY6, newDepth ) );
                if( bones['r_f_r2'] ) bones['r_f_r2'].setPosition( new Vector3( newWidth, braceSideY6, newDepth ) );

                if( bones['r_b_l1'] ) bones['r_b_l1'].setPosition( new Vector3( -braceX3, braceSideY3, -newDepth ) );
                if( bones['r_b_r1'] ) bones['r_b_r1'].setPosition( new Vector3( braceX3, braceSideY3, -newDepth ) );
                if( bones['r_f_l1'] ) bones['r_f_l1'].setPosition( new Vector3( -braceX3, braceSideY3, newDepth ) );
                if( bones['r_f_r1'] ) bones['r_f_r1'].setPosition( new Vector3( braceX3, braceSideY3, newDepth ) );
            }

            if( bones['frame_clone_l1'] ) bones['frame_clone_l1'].setPosition( new Vector3( -newWidth, bones['frame_clone_l1'].position.y, bones['frame_clone_l1'].position.z ) );
            if( bones['frame_clone_l2'] ) bones['frame_clone_l2'].setPosition( new Vector3( -newWidth, braceSideY2, bones['frame_clone_l2'].position.z ) );
            if( bones['frame_clone_l3'] ) bones['frame_clone_l3'].setPosition( new Vector3( -braceX3, braceSideY3, bones['frame_clone_l3'].position.z ) );
            if( bones['frame_clone_l4'] ) bones['frame_clone_l4'].setPosition( new Vector3( -braceSideX, braceSideY4, bones['frame_clone_l4'].position.z ) );
            if( bones['frame_clone_l5'] ) bones['frame_clone_l5'].setPosition( new Vector3( -braceTopX, braceTopY, bones['frame_clone_l5'].position.z ) );
            if( bones['frame_clone_l6'] ) bones['frame_clone_l6'].setPosition( new Vector3( -newWidth, braceSideY6, bones['frame_clone_l6'].position.z ) );

            if( bones['frame_clone_t'] ) bones['frame_clone_t'].setPosition( new Vector3( bones['frame_clone_t'].position.x, newBuildingHeight, bones['frame_clone_t'].position.z ) );

            if( bones['frame_clone_r1'] ) bones['frame_clone_r1'].setPosition( new Vector3( newWidth, bones['frame_clone_r1'].position.y, bones['frame_clone_r1'].position.z ) );
            if( bones['frame_clone_r2'] ) bones['frame_clone_r2'].setPosition( new Vector3( newWidth, braceSideY2, bones['frame_clone_r2'].position.z ) );
            if( bones['frame_clone_r3'] ) bones['frame_clone_r3'].setPosition( new Vector3( braceX3, braceSideY3, bones['frame_clone_r3'].position.z ) );
            if( bones['frame_clone_r4'] ) bones['frame_clone_r4'].setPosition( new Vector3( braceSideX, braceSideY4, bones['frame_clone_r4'].position.z ) );
            if( bones['frame_clone_r5'] ) bones['frame_clone_r5'].setPosition( new Vector3( braceTopX, braceTopY, bones['frame_clone_r5'].position.z ) );
            if( bones['frame_clone_r6'] ) bones['frame_clone_r6'].setPosition( new Vector3( newWidth, braceSideY6, bones['frame_clone_r6'].position.z ) );

            if( bones['b_b_l2'] ) bones['b_b_l2'].setPosition( new Vector3( -newWidth, braceSideY2, -newDepth ) );
            if( bones['b_b_l3'] ) bones['b_b_l3'].setPosition( new Vector3( -braceX3, braceSideY3, -newDepth ) );
            if( bones['b_b_l4'] ) bones['b_b_l4'].setPosition( new Vector3( -braceSideX, braceSideY4, -newDepth ) );
            if( bones['b_b_l5'] ) bones['b_b_l5'].setPosition( new Vector3( -braceTopX, braceTopY, -newDepth ) );
            if( bones['b_b_l6'] ) bones['b_b_l6'].setPosition( new Vector3( -newWidth, braceSideY6, -newDepth ) );

            if( bones['b_b_r2'] ) bones['b_b_r2'].setPosition( new Vector3( newWidth, braceSideY2, -newDepth ) );
            if( bones['b_b_r3'] ) bones['b_b_r3'].setPosition( new Vector3( braceX3, braceSideY3, -newDepth ) );
            if( bones['b_b_r4'] ) bones['b_b_r4'].setPosition( new Vector3( braceSideX, braceSideY4, -newDepth ) );
            if( bones['b_b_r5'] ) bones['b_b_r5'].setPosition( new Vector3( braceTopX, braceTopY, -newDepth ) );
            if( bones['b_b_r6'] ) bones['b_b_r6'].setPosition( new Vector3( newWidth, braceSideY6, -newDepth ) );

            if( bones['b_f_l2'] ) bones['b_f_l2'].setPosition( new Vector3( -newWidth, braceSideY2, newDepth ) );
            if( bones['b_f_l3'] ) bones['b_f_l3'].setPosition( new Vector3( -braceX3, braceSideY3, newDepth ) );
            if( bones['b_f_l4'] ) bones['b_f_l4'].setPosition( new Vector3( -braceSideX, braceSideY4, newDepth ) );
            if( bones['b_f_l5'] ) bones['b_f_l5'].setPosition( new Vector3( -braceTopX, braceTopY, newDepth ) );
            if( bones['b_f_l6'] ) bones['b_f_l6'].setPosition( new Vector3( -newWidth, braceSideY6, newDepth ) );

            if( bones['b_f_r2'] ) bones['b_f_r2'].setPosition( new Vector3( newWidth, braceSideY2, newDepth ) );
            if( bones['b_f_r3'] ) bones['b_f_r3'].setPosition( new Vector3( braceX3, braceSideY3, newDepth ) );
            if( bones['b_f_r4'] ) bones['b_f_r4'].setPosition( new Vector3( braceSideX, braceSideY4, newDepth ) );
            if( bones['b_f_r5'] ) bones['b_f_r5'].setPosition( new Vector3( braceTopX, braceTopY, newDepth ) );
            if( bones['b_f_r6'] ) bones['b_f_r6'].setPosition( new Vector3( newWidth, braceSideY6, newDepth ) );

            if( bones['hat_channel_b'] ) bones['hat_channel_b'].setPosition( new Vector3( bones['hat_channel_b'].position.x, bones['hat_channel_b'].position.y, -newDepth ) );
            if( bones['hat_channel_f'] ) bones['hat_channel_f'].setPosition( new Vector3( bones['hat_channel_f'].position.x, bones['hat_channel_f'].position.y, newDepth ) );

            if( bones['roof_metal_horizontal_b'] ) bones['roof_metal_horizontal_b'].setPosition( new Vector3( bones['roof_metal_horizontal_b'].position.x, bones['roof_metal_horizontal_b'].position.y, -newDepth ) );
            if( bones['roof_metal_horizontal_f'] ) bones['roof_metal_horizontal_f'].setPosition( new Vector3( bones['roof_metal_horizontal_f'].position.x, bones['roof_metal_horizontal_f'].position.y, newDepth ) );

            if( 'carport_lean_to_standalone' === _this._shedType ) {
                newRoofPitchHeight  = _this.calculateRoofPitchHeight( roofDegree, _this.getWidth() );
                newBuildingHeight   = newWallHeight + newRoofPitchHeight;
                _this.setRoofPitchHeight( newRoofPitchHeight );
                _this.setBuildingHeight( newBuildingHeight );

                if( bones['corner_trim_b_l_t'] ) bones['corner_trim_b_l_t'].setPosition( new Vector3( -newWidth, newBuildingHeight, -newDepth ) );
                if( bones['corner_trim_f_l_t'] ) bones['corner_trim_f_l_t'].setPosition( new Vector3( -newWidth, newBuildingHeight, newDepth ) );
            } else {
                if( bones['corner_trim_b_l_t'] ) bones['corner_trim_b_l_t'].setPosition( new Vector3( -newWidth, newWallHeight, -newDepth ) );
                if( bones['corner_trim_f_l_t'] ) bones['corner_trim_f_l_t'].setPosition( new Vector3( -newWidth, newWallHeight, newDepth ) );
            }

            if( bones['corner_trim_f_l_b'] ) bones['corner_trim_f_l_b'].setPosition( new Vector3( -newWidth, bones['corner_trim_f_l_b'].position.y, newDepth ) );
            if( bones['corner_trim_b_l_b'] ) bones['corner_trim_b_l_b'].setPosition( new Vector3( -newWidth, bones['corner_trim_b_l_b'].position.y, -newDepth ) );

            if( bones['corner_trim_b_r_b'] ) bones['corner_trim_b_r_b'].setPosition( new Vector3( newWidth, bones['corner_trim_b_r_b'].position.y, -newDepth ) );
            if( bones['corner_trim_b_r_t'] ) bones['corner_trim_b_r_t'].setPosition( new Vector3( newWidth, newWallHeight, -newDepth ) );

            if( bones['corner_trim_f_r_b'] ) bones['corner_trim_f_r_b'].setPosition( new Vector3( newWidth, bones['corner_trim_f_r_b'].position.y, newDepth ) );
            if( bones['corner_trim_f_r_t'] ) bones['corner_trim_f_r_t'].setPosition( new Vector3( newWidth, newWallHeight, newDepth ) );

            if( bones['j_trim_b_l'] ) bones['j_trim_b_l'].setPosition( new Vector3( -newWidth, bones['j_trim_b_l'].position.y, -newDepth ) );
            if( bones['j_trim_b_r'] ) bones['j_trim_b_r'].setPosition( new Vector3( newWidth, bones['j_trim_b_r'].position.y, -newDepth ) );

            if( bones['j_trim_f_l'] ) bones['j_trim_f_l'].setPosition( new Vector3( -newWidth, bones['j_trim_f_l'].position.y, newDepth ) );
            if( bones['j_trim_f_r'] ) bones['j_trim_f_r'].setPosition( new Vector3( newWidth, bones['j_trim_f_r'].position.y, newDepth ) );

            if( bones['j_trim_r_b'] ) bones['j_trim_r_b'].setPosition( new Vector3( newWidth, bones['j_trim_r_b'].position.y, -newDepth ) );
            if( bones['j_trim_r_f'] ) bones['j_trim_r_f'].setPosition( new Vector3( newWidth, bones['j_trim_r_f'].position.y, newDepth ) );

            if( bones['j_trim_l_b'] ) bones['j_trim_l_b'].setPosition( new Vector3( -newWidth, bones['j_trim_l_b'].position.y, -newDepth ) );
            if( bones['j_trim_l_f'] ) bones['j_trim_l_f'].setPosition( new Vector3( -newWidth, bones['j_trim_l_f'].position.y, newDepth ) );
            
            if( bones['j_trim_f_l_side_wall_l'] ) bones['j_trim_f_l_side_wall_l'].setPosition( new Vector3( 0, bones['j_trim_f_l_side_wall_l'].position.y, newDepth ) );
            if( bones['j_trim_f_l_side_wall_r'] ) bones['j_trim_f_l_side_wall_r'].setPosition( new Vector3( newWidth, bones['j_trim_f_l_side_wall_r'].position.y, newDepth ) );
            
            if( bones['j_trim_f_r_side_wall_l'] ) bones['j_trim_f_r_side_wall_l'].setPosition( new Vector3( - newWidth, bones['j_trim_f_r_side_wall_l'].position.y, newDepth ) );
            if( bones['j_trim_f_r_side_wall_r'] ) bones['j_trim_f_r_side_wall_r'].setPosition( new Vector3( 0, bones['j_trim_f_r_side_wall_r'].position.y, newDepth ) );
            
            if( bones['j_trim_b_l_side_wall_l'] ) bones['j_trim_b_l_side_wall_l'].setPosition( new Vector3( 0, bones['j_trim_b_l_side_wall_l'].position.y, - newDepth ) );
            if( bones['j_trim_b_l_side_wall_r'] ) bones['j_trim_b_l_side_wall_r'].setPosition( new Vector3( newWidth, bones['j_trim_b_l_side_wall_r'].position.y, - newDepth ) );
            
            if( bones['j_trim_b_r_side_wall_l'] ) bones['j_trim_b_r_side_wall_l'].setPosition( new Vector3( - newWidth, bones['j_trim_b_r_side_wall_l'].position.y, - newDepth ) );
            if( bones['j_trim_b_r_side_wall_r'] ) bones['j_trim_b_r_side_wall_r'].setPosition( new Vector3( 0, bones['j_trim_b_r_side_wall_r'].position.y, - newDepth ) );

            if( bones['corner_trim_b_l_side_wall_b'] ) bones['corner_trim_b_l_side_wall_b'].setPosition( new Vector3( 0, bones['corner_trim_b_l_side_wall_b'].position.y, - newDepth ) );
            if( bones['corner_trim_b_l_side_wall_t'] ) bones['corner_trim_b_l_side_wall_t'].setPosition( new Vector3( 0, bones['corner_trim_b_l_side_wall_t'].position.y, - newDepth ) );

            if( bones['corner_trim_b_r_side_wall_b'] ) bones['corner_trim_b_r_side_wall_b'].setPosition( new Vector3( 0, bones['corner_trim_b_r_side_wall_b'].position.y, - newDepth ) );
            if( bones['corner_trim_b_r_side_wall_t'] ) bones['corner_trim_b_r_side_wall_t'].setPosition( new Vector3( 0, bones['corner_trim_b_r_side_wall_t'].position.y, - newDepth ) );

            if( bones['corner_trim_f_l_side_wall_b'] ) bones['corner_trim_f_l_side_wall_b'].setPosition( new Vector3( 0, bones['corner_trim_f_l_side_wall_b'].position.y, newDepth ) );
            if( bones['corner_trim_f_l_side_wall_t'] ) bones['corner_trim_f_l_side_wall_t'].setPosition( new Vector3( 0, bones['corner_trim_f_l_side_wall_t'].position.y, newDepth ) );
            
            if( bones['corner_trim_f_r_side_wall_b'] ) bones['corner_trim_f_r_side_wall_b'].setPosition( new Vector3( 0, bones['corner_trim_f_r_side_wall_b'].position.y, newDepth ) );
            if( bones['corner_trim_f_r_side_wall_t'] ) bones['corner_trim_f_r_side_wall_t'].setPosition( new Vector3( 0, bones['corner_trim_f_r_side_wall_t'].position.y, newDepth ) );

            if( 'carport_lean_to_standalone' === _this._shedType ) {
                braceSideX              = newWidth - 1.192;
                braceSideY3             = newBuildingHeight;
                braceSideY2             = newBuildingHeight - 1.847;
                braceSideY4             = newBuildingHeight - Math.tan( roofDegree * Math.PI / 180 ) * 1.192;
                const roofErrorNumber   = Math.tan( roofDegree * Math.PI / 180 ) * 0.35;

                if( bones['b_f_l2'] ) bones['b_f_l2'].setPosition( new Vector3( bones['b_f_l2'].position.x, braceSideY2, bones['b_f_l2'].position.z ) );
                if( bones['b_f_l3'] ) bones['b_f_l3'].setPosition( new Vector3( bones['b_f_l3'].position.x, braceSideY3, bones['b_f_l3'].position.z ) );
                if( bones['b_f_l4'] ) bones['b_f_l4'].setPosition( new Vector3( - braceSideX, braceSideY4, bones['b_f_l4'].position.z ) );

                if( bones['b_b_l2'] ) bones['b_b_l2'].setPosition( new Vector3( bones['b_b_l2'].position.x, braceSideY2, bones['b_b_l2'].position.z ) );
                if( bones['b_b_l3'] ) bones['b_b_l3'].setPosition( new Vector3( bones['b_b_l3'].position.x, braceSideY3, bones['b_b_l3'].position.z ) );
                if( bones['b_b_l4'] ) bones['b_b_l4'].setPosition( new Vector3( - braceSideX, braceSideY4, bones['b_b_l4'].position.z ) );

                if( bones['frame_clone_l1'] ) bones['frame_clone_l1'].setPosition( new Vector3( -newWidth, bones['frame_clone_l1'].position.y, bones['frame_clone_l1'].position.z ) );
                if( bones['frame_clone_l2'] ) bones['frame_clone_l2'].setPosition( new Vector3( -newWidth, braceSideY2, bones['frame_clone_l2'].position.z ) );
                if( bones['frame_clone_l3'] ) bones['frame_clone_l3'].setPosition( new Vector3( -braceX3, braceSideY3, bones['frame_clone_l3'].position.z ) );
                if( bones['frame_clone_l4'] ) bones['frame_clone_l4'].setPosition( new Vector3( -braceSideX, braceSideY4, bones['frame_clone_l4'].position.z ) );
            
                if( bones['r_b_r1'] ) bones['r_b_r1'].setPosition( new Vector3( bones['r_b_r1'].position.x, newWallHeight + roofErrorNumber, bones['r_b_r1'].position.z ) );
                if( bones['r_f_r1'] ) bones['r_f_r1'].setPosition( new Vector3( bones['r_f_r1'].position.x, newWallHeight + roofErrorNumber, bones['r_f_r1'].position.z ) );
                if( bones['r_f_l1'] ) bones['r_f_l1'].setPosition( new Vector3( bones['r_f_l1'].position.x, braceSideY3 + roofErrorNumber, bones['r_f_l1'].position.z ) );
                if( bones['r_b_l1'] ) bones['r_b_l1'].setPosition( new Vector3( bones['r_b_l1'].position.x, braceSideY3 + roofErrorNumber, bones['r_b_l1'].position.z ) );

                if( bones['rm_l1'] ) bones['rm_l1'].setPosition( new Vector3( bones['rm_l1'].position.x, braceSideY3 + roofErrorNumber, bones['rm_l1'].position.z ) );
                if( bones['rm_r1'] ) bones['rm_r1'].setPosition( new Vector3( bones['rm_r1'].position.x, newWallHeight + roofErrorNumber, bones['rm_r1'].position.z ) );

                const roofCapPosition = _this.getRoofCapPosition();
                if( bones['roof_cap_f_l1'] ) bones['roof_cap_f_l1'].setPosition( new Vector3( bones['roof_cap_f_l1'].position.x, roofCapPosition, bones['roof_cap_f_l1'].position.z ) );
                if( bones['roof_cap_b_l1'] ) bones['roof_cap_b_l1'].setPosition( new Vector3( bones['roof_cap_b_l1'].position.x, roofCapPosition, bones['roof_cap_b_l1'].position.z ) );
            }

            if( 'carport_triplewide_attic_rooftruss' === _this._shedType ) {
                if( bones['b_b_l6'] ) bones['b_b_l6'].setPosition( new Vector3( - ( braceTopX / 2 ), braceTopY, - newDepth ) );
                if( bones['b_b_l7'] ) bones['b_b_l7'].setPosition( new Vector3( - ( braceTopX / 2 ), newBuildingHeight - ( newBuildingHeight - braceTopY ) / 2, - newDepth ) );
                
                if( bones['b_b_r6'] ) bones['b_b_r6'].setPosition( new Vector3( braceTopX / 2, braceTopY, - newDepth ) );
                if( bones['b_b_r7'] ) bones['b_b_r7'].setPosition( new Vector3( braceTopX / 2, newBuildingHeight - ( newBuildingHeight - braceTopY ) / 2, - newDepth ) );
                
                if( bones['b_f_l6'] ) bones['b_f_l6'].setPosition( new Vector3( - ( braceTopX / 2 ), braceTopY, newDepth ) );
                if( bones['b_f_l7'] ) bones['b_f_l7'].setPosition( new Vector3( - ( braceTopX / 2 ), newBuildingHeight - ( newBuildingHeight - braceTopY ) / 2, newDepth ) );
                
                if( bones['b_f_r6'] ) bones['b_f_r6'].setPosition( new Vector3( braceTopX / 2, braceTopY, newDepth ) );
                if( bones['b_f_r7'] ) bones['b_f_r7'].setPosition( new Vector3( braceTopX / 2, newBuildingHeight - ( newBuildingHeight - braceTopY ) / 2, newDepth ) );

                if( bones['frame_clone_l6'] ) bones['frame_clone_l6'].setPosition( new Vector3( - ( braceTopX / 2 ), braceTopY, bones['frame_clone_l6'].position.z ) );
                if( bones['frame_clone_l7'] ) bones['frame_clone_l7'].setPosition( new Vector3( - ( braceTopX / 2 ), newBuildingHeight - ( newBuildingHeight - braceTopY ) / 2, bones['frame_clone_l7'].position.z ) );
                
                if( bones['frame_clone_r6'] ) bones['frame_clone_r6'].setPosition( new Vector3( braceTopX / 2, braceTopY, bones['frame_clone_r6'].position.z ) );
                if( bones['frame_clone_r7'] ) bones['frame_clone_r7'].setPosition( new Vector3( braceTopX / 2, newBuildingHeight - ( newBuildingHeight - braceTopY ) / 2, bones['frame_clone_r7'].position.z ) );
            }
        }

        if ( bones['rpm_b'] ) bones['rpm_b'].setPosition( new Vector3( newWidth, newWallHeight, newDepth + _this._porchWidth ) );
        if ( bones['rpm_f'] ) bones['rpm_f'].setPosition( new Vector3( newWidth, newWallHeight + 1.4, newDepth ) );
        if ( bones['inner_frame_horizontal_clone_f'] ) bones['inner_frame_horizontal_clone_f'].setPosition( new Vector3( 0, newWallHeight, newDepth + _this._porchWidth ) );
        if ( bones['inner_frame_horizontal_clone_b'] ) bones['inner_frame_horizontal_clone_b'].setPosition( new Vector3( 0, newWallHeight + 1.4, newDepth ) );

        if( 'structure_trading_post' === _this._shedType ){
            if ( bones['pannel'] ) bones['pannel'].setPosition( new Vector3( bones['pannel'].position.x, newWallHeight + 2.083, newDepth ) );
        }

        if( this.isMiddlePorch() ) {
            if ( bones['b_p_l1'] ) bones['b_p_l1'].setPosition( new Vector3( - newWidth, bones['b_p_l1'].position.y, bones['b_p_l1'].position.z ) );
            if ( bones['b_p_l3'] ) bones['b_p_l3'].setPosition( new Vector3( - newWidth, bones['b_p_l3'].position.y, bones['b_p_l3'].position.z ) );
            if ( bones['b_p_l4'] ) bones['b_p_l4'].setPosition( new Vector3( - newWidth, newWallHeight, bones['b_p_l4'].position.z ) );
            if ( bones['b_p_l6'] ) bones['b_p_l6'].setPosition( new Vector3( - newWidth, newWallHeight, bones['b_p_l6'].position.z ) );
            if ( bones['b_p_l2'] ) bones['b_p_l2'].setPosition( new Vector3( - newWidth, bones['b_p_l2'].position.y, bones['b_p_l2'].position.z ) );
            if ( bones['b_p_l5'] ) bones['b_p_l5'].setPosition( new Vector3( - newWidth, newWallHeight, 0 ) );

            if ( bones['b_p_l7'] ) bones['b_p_l7'].setPosition( new Vector3( bones['b_b_l3'].position.x, bones['b_b_l3'].position.y, bones['b_p_l7'].position.z ) );
            if ( bones['b_p_l8'] ) bones['b_p_l8'].setPosition( new Vector3( bones['b_b_l3'].position.x, bones['b_b_l3'].position.y, bones['b_p_l8'].position.z ) );
            if ( bones['b_b_m5'] ) bones['b_b_m5'].setPosition( new Vector3( bones['b_b_r3'].position.x, bones['b_b_r3'].position.y, 4 ) );
            if ( bones['b_f_m5'] ) bones['b_f_m5'].setPosition( new Vector3( bones['b_b_r3'].position.x, bones['b_b_r3'].position.y, bones['b_f_m5'].position.z ) );

            if ( bones['baluster_b_l'] ) bones['baluster_b_l'].setPosition( new Vector3( - ( newWidth - constants.balusterToSide ), bones['baluster_b_l'].position.y, bones['baluster_b_l'].position.z ) );
            if ( bones['rail_h_l_b_l1'] ) bones['rail_h_l_b_l1'].setPosition( new Vector3( - newWidth, bones['rail_h_l_b_l1'].position.y, bones['rail_h_l_b_l1'].position.z ) );
            if ( bones['rail_h_l_m_l1'] ) bones['rail_h_l_m_l1'].setPosition( new Vector3( - newWidth, bones['rail_h_l_m_l1'].position.y, bones['rail_h_l_m_l1'].position.z ) );

            if ( bones['b_f_m1'] ) bones['b_f_m1'].setPosition( new Vector3( newWidth, bones['b_f_m1'].position.y, bones['b_f_m1'].position.z ) );
            if ( bones['b_f_m2'] ) bones['b_f_m2'].setPosition( new Vector3( newWidth, newWallHeight, bones['b_f_m2'].position.z ) );
            if ( bones['b_b_m1'] ) bones['b_b_m1'].setPosition( new Vector3( newWidth, bones['b_b_m1'].position.y, bones['b_b_m1'].position.z ) );
            if ( bones['b_b_m2'] ) bones['b_b_m2'].setPosition( new Vector3( newWidth, newWallHeight, bones['b_b_m2'].position.z ) );

            if ( bones['b_f_m3'] ) bones['b_f_m3'].setPosition( new Vector3( - ( newWidth - constants.middleDepth ), newWallHeight, bones['b_f_m3'].position.z ) );
            if ( bones['b_f_m4'] ) bones['b_f_m4'].setPosition( new Vector3( - ( newWidth - constants.middleDepth ), bones['b_f_m4'].position.y, bones['b_f_m4'].position.z ) );
            if ( bones['b_b_m3'] ) bones['b_b_m3'].setPosition( new Vector3( - ( newWidth - constants.middleDepth ), newWallHeight, bones['b_b_m3'].position.z ) );
            if ( bones['b_b_m4'] ) bones['b_b_m4'].setPosition( new Vector3( - ( newWidth - constants.middleDepth ), bones['b_b_m4'].position.y, bones['b_b_m4'].position.z ) );
            if ( bones['b_b_m_t'] ) bones['b_b_m_t'].setPosition( new Vector3( bones['b_b_m_t'].position.x, newBuildingHeight, bones['b_b_m_t'].position.z ) );
            if ( bones['b_f_m_t'] ) bones['b_f_m_t'].setPosition( new Vector3( bones['b_f_m_t'].position.x, newBuildingHeight, bones['b_f_m_t'].position.z ) );
        }

        if( 'structure_monitorbarns_helmuth' === _this._shedType ) {
            const width = 12;
            const tanLower = helpers.toFixed3( helpers.getTanFromDegrees( _this._roofLowerDegree ? _this._roofLowerDegree : _this.getGlobalData( constants.initLowerRoofDegrees ) ) );
            const tanUpper = helpers.toFixed3( helpers.getTanFromDegrees( _this._roofUpperDegree ? _this._roofUpperDegree : _this.getGlobalData( constants.initUpperRoofDegrees ) ) );
            const secondFloorHeight = _this.getSecondFloorHeight();
            const wallHeight1 = newWallHeight;
            const wallHeight2 = helpers.toFixed3( wallHeight1 + ( tanLower * width ) );
            const wallHeight3 = helpers.toFixed3( wallHeight2 + secondFloorHeight );
            const wallHeight4 = helpers.toFixed3( wallHeight3 + ( tanUpper * ( width / 2 ) ) );

            if ( bones['b_b_l1'] ) bones['b_b_l1'].setPosition( new Vector3( - 5, 0, - newDepth ) );
            if ( bones['b_b_l2'] ) bones['b_b_l2'].setPosition( new Vector3( - 18, 0, - newDepth ) );
            if ( bones['b_b_l3'] ) bones['b_b_l3'].setPosition( new Vector3( - 18, wallHeight1, - newDepth ) );
            if ( bones['b_b_l4'] ) bones['b_b_l4'].setPosition( new Vector3( - 6, wallHeight2, - newDepth ) );
            if ( bones['b_b_l5'] ) bones['b_b_l5'].setPosition( new Vector3( - 6, wallHeight3, - newDepth ) );
            if ( bones['b_b_r1'] ) bones['b_b_r1'].setPosition( new Vector3( 5, 0, - newDepth ) );
            if ( bones['b_b_r2'] ) bones['b_b_r2'].setPosition( new Vector3( 18, 0, - newDepth ) );
            if ( bones['b_b_r3'] ) bones['b_b_r3'].setPosition( new Vector3( 18, wallHeight1, - newDepth ) );
            if ( bones['b_b_r4'] ) bones['b_b_r4'].setPosition( new Vector3( 6, wallHeight2, - newDepth ) );
            if ( bones['b_b_r5'] ) bones['b_b_r5'].setPosition( new Vector3( 6, wallHeight3, - newDepth ) );
            if ( bones['b_b_t'] ) bones['b_b_t'].setPosition( new Vector3( 0, wallHeight4, - newDepth ) );

            if ( bones['b_f_l1'] ) bones['b_f_l1'].setPosition( new Vector3( - 5, 0, newDepth ) );
            if ( bones['b_f_l2'] ) bones['b_f_l2'].setPosition( new Vector3( - 18, 0, newDepth ) );
            if ( bones['b_f_l3'] ) bones['b_f_l3'].setPosition( new Vector3( - 18, wallHeight1, newDepth ) );
            if ( bones['b_f_l4'] ) bones['b_f_l4'].setPosition( new Vector3( - 6, wallHeight2, newDepth ) );
            if ( bones['b_f_l5'] ) bones['b_f_l5'].setPosition( new Vector3( - 6, wallHeight3, newDepth ) );
            if ( bones['b_f_r1'] ) bones['b_f_r1'].setPosition( new Vector3( 5, 0, newDepth ) );
            if ( bones['b_f_r2'] ) bones['b_f_r2'].setPosition( new Vector3( 18, 0, newDepth ) );
            if ( bones['b_f_r3'] ) bones['b_f_r3'].setPosition( new Vector3( 18, wallHeight1, newDepth ) );
            if ( bones['b_f_r4'] ) bones['b_f_r4'].setPosition( new Vector3( 6, wallHeight2, newDepth ) );
            if ( bones['b_f_r5'] ) bones['b_f_r5'].setPosition( new Vector3( 6, wallHeight3, newDepth ) );
            if ( bones['b_f_t'] ) bones['b_f_t'].setPosition( new Vector3( 0, wallHeight4, newDepth ) );

            if ( bones['r_b_l1'] ) bones['r_b_l1'].setPosition( new Vector3( - 18, wallHeight1, - newDepth ) );
            if ( bones['r_b_l2'] ) bones['r_b_l2'].setPosition( new Vector3( - 6, wallHeight2, - newDepth ) );
            if ( bones['r_b_l3'] ) bones['r_b_l3'].setPosition( new Vector3( - 6, wallHeight3, - newDepth ) );
            if ( bones['r_b_r1'] ) bones['r_b_r1'].setPosition( new Vector3( 18, wallHeight1, - newDepth ) );
            if ( bones['r_b_r2'] ) bones['r_b_r2'].setPosition( new Vector3( 6, wallHeight2, - newDepth ) );
            if ( bones['r_b_r3'] ) bones['r_b_r3'].setPosition( new Vector3( 6, wallHeight3, - newDepth ) );
            if ( bones['r_b_t'] ) bones['r_b_t'].setPosition( new Vector3( 0, wallHeight4, - newDepth ) );
            
            if ( bones['r_f_l1'] ) bones['r_f_l1'].setPosition( new Vector3( - 18, wallHeight1,  newDepth ) );
            if ( bones['r_f_l2'] ) bones['r_f_l2'].setPosition( new Vector3( - 6, wallHeight2, newDepth ) );
            if ( bones['r_f_l3'] ) bones['r_f_l3'].setPosition( new Vector3( - 6, wallHeight3, newDepth ) );
            if ( bones['r_f_r1'] ) bones['r_f_r1'].setPosition( new Vector3( 18, wallHeight1,  newDepth ) );
            if ( bones['r_f_r2'] ) bones['r_f_r2'].setPosition( new Vector3( 6, wallHeight2, newDepth ) );
            if ( bones['r_f_r3'] ) bones['r_f_r3'].setPosition( new Vector3( 6, wallHeight3, newDepth ) );
            if ( bones['r_f_t'] ) bones['r_f_t'].setPosition( new Vector3( 0, wallHeight4, newDepth ) );

            if ( bones['outer_joist_l1'] ) bones['outer_joist_l1'].setPosition( new Vector3( - 18, 0, - newDepth ) );
            if ( bones['outer_joist_l2'] ) bones['outer_joist_l2'].setPosition( new Vector3( - 18, 0, newDepth ) );
            if ( bones['outer_joist_l3'] ) bones['outer_joist_l3'].setPosition( new Vector3( - 5, 0, newDepth ) );
            if ( bones['outer_joist_l4'] ) bones['outer_joist_l4'].setPosition( new Vector3( - 5, 0, - newDepth ) );
            
            if ( bones['outer_joist_r1'] ) bones['outer_joist_r1'].setPosition( new Vector3( 18, 0, - newDepth ) );
            if ( bones['outer_joist_r2'] ) bones['outer_joist_r2'].setPosition( new Vector3( 18, 0, newDepth ) );
            if ( bones['outer_joist_r3'] ) bones['outer_joist_r3'].setPosition( new Vector3( 5, 0, newDepth ) );
            if ( bones['outer_joist_r4'] ) bones['outer_joist_r4'].setPosition( new Vector3( 5, 0, - newDepth ) );

            if ( bones['rm_l1'] ) bones['rm_l1'].setPosition( new Vector3( - 18, wallHeight1, - newDepth ) );
            if ( bones['rm_l2'] ) bones['rm_l2'].setPosition( new Vector3( - 6, wallHeight2, - newDepth ) );
            if ( bones['rm_l3'] ) bones['rm_l3'].setPosition( new Vector3( - 6, wallHeight3, - newDepth ) );
            if ( bones['rm_r1'] ) bones['rm_r1'].setPosition( new Vector3( 18, wallHeight1, - newDepth ) );
            if ( bones['rm_r2'] ) bones['rm_r2'].setPosition( new Vector3( 6, wallHeight2, - newDepth ) );
            if ( bones['rm_r3'] ) bones['rm_r3'].setPosition( new Vector3( 6, wallHeight3, - newDepth ) );
            if ( bones['rm_t'] ) bones['rm_t'].setPosition( new Vector3( 0, wallHeight4, - newDepth ) );

            _this.setBuildingHeight( bones['b_b_t'].position.y );
        }

        _this.roofComponentMoveBones( newWidth, newWallHeight, newDepth, newBuildingHeight, newRoofPitchHeight );

        if( 'structure_horse_shelter' === _this._shedType ) {
            const offsetHeight  = 0.4;
            const overHang      = 1.25;

            if( bones['b_b_l2'] ) bones['b_b_l2'].setPosition( new Vector3( -newWidth, newWallHeight + offsetHeight, -newDepth ) );
            if( bones['b_b_l3'] ) bones['b_b_l3'].setPosition( new Vector3( -newWidth - overHang, newWallHeight + offsetHeight, -newDepth ) );

            if( bones['b_f_l2'] ) bones['b_f_l2'].setPosition( new Vector3( -newWidth, newWallHeight + offsetHeight, newDepth ) );
            if( bones['b_f_l3'] ) bones['b_f_l3'].setPosition( new Vector3( -newWidth - overHang, newWallHeight + offsetHeight, newDepth ) );

            if( bones['b_b_r2'] ) bones['b_b_r2'].setPosition( new Vector3( newWidth, newWallHeight, -newDepth ) );
            if( bones['b_f_r2'] ) bones['b_f_r2'].setPosition( new Vector3( newWidth, newWallHeight, newDepth ) );

            if( bones['rm_l1'] ) bones['rm_l1'].setPosition( new Vector3( -newWidth - overHang, newWallHeight + offsetHeight, -newDepth ) );
            if( bones['rm_r1'] ) bones['rm_r1'].setPosition( new Vector3( newWidth, newWallHeight, -newDepth ) );
            if( bones['r_b_l1'] ) bones['r_b_l1'].setPosition( new Vector3( -newWidth - overHang, newWallHeight + offsetHeight, -newDepth ) );
            if( bones['r_b_r1'] ) bones['r_b_r1'].setPosition( new Vector3( newWidth, newWallHeight, -newDepth ) );
            if( bones['r_f_l1'] ) bones['r_f_l1'].setPosition( new Vector3( -newWidth - overHang, newWallHeight + offsetHeight, newDepth ) );
            if( bones['r_f_r1'] ) bones['r_f_r1'].setPosition( new Vector3( newWidth, newWallHeight, newDepth ) );

            if( bones['b_b_m1'] ) bones['b_b_m1'].setPosition( new Vector3( -newWidth, bones['b_b_m1'].position.y, bones['b_b_m1'].position.z ) );
            if( bones['b_b_m2'] ) bones['b_b_m2'].setPosition( new Vector3( -newWidth, newWallHeight + offsetHeight, bones['b_b_m2'].position.z ) );
            if( bones['b_f_m1'] ) bones['b_f_m1'].setPosition( new Vector3( -newWidth, bones['b_f_m1'].position.y, bones['b_f_m1'].position.z ) );
            if( bones['b_f_m2'] ) bones['b_f_m2'].setPosition( new Vector3( -newWidth, newWallHeight + offsetHeight, bones['b_f_m2'].position.z ) );

            if( bones['stub_back_clone_f_m1'] ) bones['stub_back_clone_f_m1'].setPosition( new Vector3( 0, bones['stub_back_clone_f_m1'].position.y, bones['stub_back_clone_f_m1'].position.z ) );
            if( bones['stub_back_clone_f_m2'] ) bones['stub_back_clone_f_m2'].setPosition( new Vector3( 0, newWallHeight, bones['stub_back_clone_f_m2'].position.z ) );

            if( bones['inner_wall_r1'] ) bones['inner_wall_r1'].setPosition( new Vector3( newWidth, bones['inner_wall_r1'].position.y, bones['inner_wall_r1'].position.z ) );
            if( bones['inner_wall_r2'] ) bones['inner_wall_r2'].setPosition( new Vector3( newWidth, newWallHeight, bones['inner_wall_r2'].position.z ) );
            if( bones['inner_wall_l1'] ) bones['inner_wall_l1'].setPosition( new Vector3( -newWidth, bones['inner_wall_l1'].position.y, bones['inner_wall_l1'].position.z ) );
            if( bones['inner_wall_l2'] ) bones['inner_wall_l2'].setPosition( new Vector3( -newWidth, newWallHeight + offsetHeight, bones['inner_wall_l2'].position.z ) );

            if( bones['stud_left_right_clone_b_l1'] ) bones['stud_left_right_clone_b_l1'].setPosition( new Vector3( bones['stud_left_right_clone_b_l1'].position.x, bones['stud_left_right_clone_b_l1'].position.y, -newDepth ) );
            if( bones['stud_left_right_clone_b_l2'] ) bones['stud_left_right_clone_b_l2'].setPosition( new Vector3( bones['stud_left_right_clone_b_l2'].position.x, newWallHeight, -newDepth ) );
            if( bones['stud_left_right_clone_f_l1'] ) bones['stud_left_right_clone_f_l1'].setPosition( new Vector3( bones['stud_left_right_clone_f_l1'].position.x, bones['stud_left_right_clone_f_l1'].position.y, newDepth ) );
            if( bones['stud_left_right_clone_f_l2'] ) bones['stud_left_right_clone_f_l2'].setPosition( new Vector3( bones['stud_left_right_clone_f_l2'].position.x, newWallHeight, newDepth ) );


            const tmpX = ( Math.abs( bones['b_b_l3'].position.x ) + Math.abs( bones['b_b_r2'].position.x ) ) / _this._initRatioRoofSaltbox;
            const tmpY = ( Math.abs( bones['b_b_l3'].position.x ) - Math.abs( bones['b_b_t'].position.x ) ) * helpers.getTanFromDegrees( _this.getRoofDegree() ) + _this.getWallHeight() + offsetHeight;

            _this.setBuildingHeight( tmpY );

            if( bones['b_f_t'] ) bones['b_f_t'].setPosition( new Vector3( tmpX, tmpY, newDepth ) );     
            if( bones['b_b_t'] ) bones['b_b_t'].setPosition( new Vector3( tmpX, tmpY, -newDepth ) );     
            if( bones['rm_t'] ) bones['rm_t'].setPosition( new Vector3( tmpX, tmpY, -newDepth ) );     
            if( bones['r_b_t'] ) bones['r_b_t'].setPosition( new Vector3( tmpX, tmpY, -newDepth ) );     
            if( bones['r_f_t'] ) bones['r_f_t'].setPosition( new Vector3( tmpX, tmpY, newDepth ) );
            if( bones['stub_back_clone_f_m3'] ) bones['stub_back_clone_f_m3'].setPosition( new Vector3( tmpX - newWidth, tmpY, bones['stub_back_clone_f_m3'].position.z ) );
            if( bones['inner_wall_t'] ) bones['inner_wall_t'].setPosition( new Vector3( tmpX, tmpY, bones['inner_wall_t'].position.z ) );
        }
        
        if( 'structure_lofted_horserunin' === _this._shedType || 'structure_aframe_horserunin' === _this._shedType || 'structure_horse_shelter' === _this._shedType ) {
            _this.drawLeftWall();
        }

        if( 'structure_aframe_horserunin' === _this._shedType ) {
            if( bones['inner_frame_horizontal_clone_02_l1'] ) bones['inner_frame_horizontal_clone_02_l1'].setPosition( new Vector3( - newWidth, newWallHeight, bones['inner_frame_horizontal_clone_02_l1'].position.z ) );
            if( bones['inner_frame_horizontal_clone_02_t'] ) bones['inner_frame_horizontal_clone_02_t'].setPosition( new Vector3( bones['inner_frame_horizontal_clone_02_t'].position.x, newBuildingHeight, bones['inner_frame_horizontal_clone_02_t'].position.z ) );
            if( bones['inner_frame_horizontal_clone_02_r2'] ) bones['inner_frame_horizontal_clone_02_r2'].setPosition( new Vector3( newWidth, newWallHeight, bones['inner_frame_horizontal_clone_02_r2'].position.z ) );
            if( bones['inner_frame_horizontal_clone_02_r1'] ) bones['inner_frame_horizontal_clone_02_r1'].setPosition( new Vector3( newWidth, bones['inner_frame_horizontal_clone_02_r1'].position.y, bones['inner_frame_horizontal_clone_02_l1'].position.z ) );
            
            if( bones['inner_wall_clone_l1'] ) bones['inner_wall_clone_l1'].setPosition( new Vector3( - newWidth, bones['inner_wall_clone_l1'].position.y, bones['inner_wall_clone_l1'].position.z ) );
            if( bones['inner_wall_clone_l2'] ) bones['inner_wall_clone_l2'].setPosition( new Vector3( - newWidth, newWallHeight, bones['inner_wall_clone_l2'].position.z ) );
            if( bones['inner_wall_clone_t'] ) bones['inner_wall_clone_t'].setPosition( new Vector3( bones['inner_wall_clone_t'].position.x, newBuildingHeight, bones['inner_wall_clone_t'].position.z ) );
            if( bones['inner_wall_clone_r1'] ) bones['inner_wall_clone_r1'].setPosition( new Vector3( newWidth, bones['inner_wall_clone_r1'].position.y, bones['inner_wall_clone_r1'].position.z ) );
            if( bones['inner_wall_clone_r2'] ) bones['inner_wall_clone_r2'].setPosition( new Vector3( newWidth, newWallHeight, bones['inner_wall_clone_r2'].position.z ) );

            if( bones['inner_frame_horizontal_clone_01_b_b'] ) bones['inner_frame_horizontal_clone_01_b_b'].setPosition( new Vector3( bones['inner_frame_horizontal_clone_01_b_b'].position.x, 0, - newDepth ) );
            if( bones['inner_frame_horizontal_clone_01_b_t'] ) bones['inner_frame_horizontal_clone_01_b_t'].setPosition( new Vector3( bones['inner_frame_horizontal_clone_01_b_t'].position.x, newWallHeight, - newDepth ) );
            if( bones['inner_frame_horizontal_clone_01_f_b'] ) bones['inner_frame_horizontal_clone_01_f_b'].setPosition( new Vector3( bones['inner_frame_horizontal_clone_01_f_b'].position.x, 0, newDepth ) );
            if( bones['inner_frame_horizontal_clone_01_f_t'] ) bones['inner_frame_horizontal_clone_01_f_t'].setPosition( new Vector3( bones['inner_frame_horizontal_clone_01_f_t'].position.x, newWallHeight, newDepth ) );
        }

        if( 'structure_chalet_genesis' === _this._shedType ){
            const genesisFrontOverhang  = 0.25 + _this._defaultOverhangFront + _this._customOverhangFront;
            const genesisBackOverhang   = 0.25 + _this._defaultOverhangBack + _this._customOverhangBack;
            const offsetX               = newWidth;
            if( bones['frame_component_b_l1'] ) bones['frame_component_b_l1'].setPosition( new Vector3( -offsetX, bones['frame_component_b_l1'].position.y, -newDepth ) );
            if( bones['frame_component_b_l2'] ) bones['frame_component_b_l2'].setPosition( new Vector3( -offsetX, newWallHeight, -newDepth ) );
            if( bones['frame_component_b_l3'] ) bones['frame_component_b_l3'].setPosition( new Vector3( -offsetX, bones['frame_component_b_l3'].position.y, -newDepth - genesisBackOverhang ) );
            if( bones['frame_component_b_l4'] ) bones['frame_component_b_l4'].setPosition( new Vector3( -offsetX, newWallHeight, -newDepth - genesisBackOverhang ) );
            if( bones['frame_component_b_r1'] ) bones['frame_component_b_r1'].setPosition( new Vector3( offsetX, bones['frame_component_b_r1'].position.y, -newDepth ) );
            if( bones['frame_component_b_r2'] ) bones['frame_component_b_r2'].setPosition( new Vector3( offsetX, newWallHeight, -newDepth ) );
            if( bones['frame_component_b_r3'] ) bones['frame_component_b_r3'].setPosition( new Vector3( offsetX, bones['frame_component_b_r3'].position.y, -newDepth - genesisBackOverhang ) );
            if( bones['frame_component_b_r4'] ) bones['frame_component_b_r4'].setPosition( new Vector3( offsetX, newWallHeight, -newDepth - genesisBackOverhang ) );
            if( bones['frame_component_b_t1'] ) bones['frame_component_b_t1'].setPosition( new Vector3( bones['frame_component_b_t1'].position.x, newBuildingHeight, -newDepth ) );
            if( bones['frame_component_b_t2'] ) bones['frame_component_b_t2'].setPosition( new Vector3( bones['frame_component_b_t2'].position.x, newBuildingHeight, -newDepth - genesisBackOverhang ) );

            if( bones['frame_component_f_t2'] ) bones['frame_component_f_t2'].setPosition( new Vector3( bones['frame_component_f_t2'].position.x, newBuildingHeight, newDepth + genesisFrontOverhang ) );
            if( bones['frame_component_f_t1'] ) bones['frame_component_f_t1'].setPosition( new Vector3( bones['frame_component_f_t1'].position.x, newBuildingHeight, newDepth ) );
            if( bones['frame_component_f_r4'] ) bones['frame_component_f_r4'].setPosition( new Vector3( offsetX, newWallHeight, newDepth + genesisFrontOverhang ) );
            if( bones['frame_component_f_r3'] ) bones['frame_component_f_r3'].setPosition( new Vector3( offsetX, bones['frame_component_f_r3'].position.y, newDepth + genesisFrontOverhang ) );
            if( bones['frame_component_f_r2'] ) bones['frame_component_f_r2'].setPosition( new Vector3( offsetX, newWallHeight, newDepth ) );
            if( bones['frame_component_f_r1'] ) bones['frame_component_f_r1'].setPosition( new Vector3( offsetX, bones['frame_component_f_r1'].position.y, newDepth ) );
            if( bones['frame_component_f_l4'] ) bones['frame_component_f_l4'].setPosition( new Vector3( -offsetX, newWallHeight, newDepth + genesisFrontOverhang ) );
            if( bones['frame_component_f_l3'] ) bones['frame_component_f_l3'].setPosition( new Vector3( -offsetX, bones['frame_component_f_l3'].position.y, newDepth + genesisFrontOverhang ) );
            if( bones['frame_component_f_l2'] ) bones['frame_component_f_l2'].setPosition( new Vector3( -offsetX, newWallHeight, newDepth ) );
            if( bones['frame_component_f_l1'] ) bones['frame_component_f_l1'].setPosition( new Vector3( -offsetX, bones['frame_component_f_l1'].position.y, newDepth ) );
        }

        if( 'structure_loafing_shed' === _this._shedType ){
            const middlePointY = newWallHeight + _this.calculateRoofPitchHeight( roofDegree, newWidth );
            const activeLayout = jQuery( `#ssb-configurator-layout-option input[name='ssb-configurator-layout']:checked` ).attr( 'data-layout-id' );

            let roofOffset = 0;
            if( roofDegree > 10.5 ){
                roofOffset = ( roofDegree - 10.5 ) * 0.005;
            }
            
            if( bones['stud_left_right_clone_b_l1'] ) bones['stud_left_right_clone_b_l1'].setPosition( new Vector3( bones['stud_left_right_clone_b_l1'].position.x, bones['stud_left_right_clone_b_l1'].position.y, -newDepth ) );
            if( bones['stud_left_right_clone_b_l2'] ) bones['stud_left_right_clone_b_l2'].setPosition( new Vector3( bones['stud_left_right_clone_b_l2'].position.x, middlePointY, -newDepth ) );
            if( bones['stud_left_right_clone_f_l1'] ) bones['stud_left_right_clone_f_l1'].setPosition( new Vector3( bones['stud_left_right_clone_f_l1'].position.x, bones['stud_left_right_clone_f_l1'].position.y, newDepth ) );
            if( bones['stud_left_right_clone_f_l2'] ) bones['stud_left_right_clone_f_l2'].setPosition( new Vector3( bones['stud_left_right_clone_f_l2'].position.x, middlePointY, newDepth ) );

            if( bones['stub_back_clone_f_m1'] ) bones['stub_back_clone_f_m1'].setPosition( new Vector3( 0, bones['stub_back_clone_f_m1'].position.y, bones['stub_back_clone_f_m1'].position.z ) );
            if( bones['stub_back_clone_f_m2'] ) bones['stub_back_clone_f_m2'].setPosition( new Vector3( 0, newWallHeight, bones['stub_back_clone_f_m2'].position.z ) );

            if( bones['post_cover_b_m1'] ) bones['post_cover_b_m1'].setPosition( new Vector3( -newWidth, bones['post_cover_b_m1'].position.y, bones['post_cover_b_m1'].position.z ) );
            if( bones['post_cover_b_m2'] ) bones['post_cover_b_m2'].setPosition( new Vector3( -newWidth, newBuildingHeight, bones['post_cover_b_m2'].position.z ) );

            if( bones['post_brace_b_l1'] ) bones['post_brace_b_l1'].setPosition( new Vector3( -newWidth, newBuildingHeight, -newDepth ) );
            if( bones['post_brace_f_l1'] ) bones['post_brace_f_l1'].setPosition( new Vector3( -newWidth, newBuildingHeight, newDepth ) );
            if( bones['post_brace_b_m1'] ) bones['post_brace_b_m1'].setPosition( new Vector3( -newWidth, bones['post_brace_b_m1'].position.y, bones['post_brace_b_m1'].position.z ) );
            if( bones['post_brace_b_m2'] ) bones['post_brace_b_m2'].setPosition( new Vector3( -newWidth, newBuildingHeight, bones['post_brace_b_m2'].position.z ) );

            if( bones['jtrim_b_l1'] ) bones['jtrim_b_l1'].setPosition( new Vector3( -newWidth, newBuildingHeight, -newDepth ) );
            if( bones['jtrim_f_l1'] ) bones['jtrim_f_l1'].setPosition( new Vector3( -newWidth, newBuildingHeight, newDepth ) );

            if( bones['beam_clone_b_l1'] ) bones['beam_clone_b_l1'].setPosition( new Vector3( bones['beam_clone_b_l1'].position.x, middlePointY, -newDepth ) );
            if( bones['beam_clone_f_l1'] ) bones['beam_clone_f_l1'].setPosition( new Vector3( bones['beam_clone_f_l1'].position.x, middlePointY, newDepth ) );

            if( bones['b_b_l2'] ) bones['b_b_l2'].setPosition( new Vector3( -newWidth, newBuildingHeight, -newDepth ) );
            if( bones['b_b_r2'] ) bones['b_b_r2'].setPosition( new Vector3( newWidth, newWallHeight, -newDepth ) );
            if( bones['b_f_l2'] ) bones['b_f_l2'].setPosition( new Vector3( -newWidth, newBuildingHeight, newDepth ) );
            if( bones['b_f_r2'] ) bones['b_f_r2'].setPosition( new Vector3( newWidth, newWallHeight, newDepth ) );
            if( bones['b_b_m1'] ) bones['b_b_m1'].setPosition( new Vector3( -newWidth, bones['b_b_m1'].position.y, bones['b_b_m1'].position.z ) );
            if( bones['b_b_m2'] ) bones['b_b_m2'].setPosition( new Vector3( -newWidth, newBuildingHeight, bones['b_b_m2'].position.z ) );
            if( bones['b_f_m1'] ) bones['b_f_m1'].setPosition( new Vector3( newWidth, bones['b_f_m1'].position.y, bones['b_f_m1'].position.z ) );
            if( bones['b_f_m2'] ) bones['b_f_m2'].setPosition( new Vector3( newWidth, newWallHeight, bones['b_f_m2'].position.z ) );
            if( bones['b_f_m2'] ) bones['b_f_m2'].setPosition( new Vector3( newWidth, newWallHeight, bones['b_f_m2'].position.z ) );
            if( bones['rm_l1'] ) bones['rm_l1'].setPosition( new Vector3( -newWidth, newBuildingHeight + roofOffset, -newDepth ) );

            if( bones['vertical_trim_01_f_l1'] ) bones['vertical_trim_01_f_l1'].setPosition( new Vector3( -newWidth, 0, newDepth ) );
            if( bones['vertical_trim_01_f_l2'] ) bones['vertical_trim_01_f_l2'].setPosition( new Vector3( -newWidth, newBuildingHeight, newDepth ) );

            if( bones['vertical_trim_02_f_l1'] ) bones['vertical_trim_02_f_l1'].setPosition( new Vector3( -newWidth, 0, -newDepth ) );
            if( bones['vertical_trim_02_f_l2'] ) bones['vertical_trim_02_f_l2'].setPosition( new Vector3( -newWidth, newBuildingHeight, -newDepth ) );

            if( 'siding_left' === activeLayout ){
                this._bones['jtrim_b_l1'].setPosition( new Vector3( this._bones['jtrim_b_l1'].position.x, this._bones['jtrim_b_l1'].position.y, -0.34 ) );
            }else if( 'siding_right' === activeLayout ){
                this._bones['jtrim_f_l1'].setPosition( new Vector3( this._bones['jtrim_f_l1'].position.x, this._bones['jtrim_f_l1'].position.y, 0.34 ) );
            }
        }

        if( 'structure_greenhouse_fiberglass' === _this._shedType ) {
            if( bones['frame_f_l1'] ) bones['frame_f_l1'].setPosition( new Vector3( 0, bones['frame_f_l1'].position.y, newDepth ) );
            if( bones['frame_f_l2'] ) bones['frame_f_l2'].setPosition( new Vector3( 0, newWallHeight, newDepth ) );
            if( bones['frame_f_l3'] ) bones['frame_f_l3'].setPosition( new Vector3( - newWidth, bones['frame_f_l3'].position.y, newDepth ) );
            if( bones['frame_f_l4'] ) bones['frame_f_l4'].setPosition( new Vector3( - newWidth, newWallHeight, newDepth ) );

            if( bones['frame_f_r1'] ) bones['frame_f_r1'].setPosition( new Vector3( 0, bones['frame_f_r1'].position.y, newDepth ) );
            if( bones['frame_f_r2'] ) bones['frame_f_r2'].setPosition( new Vector3( 0, newWallHeight, newDepth ) );
            if( bones['frame_f_r3'] ) bones['frame_f_r3'].setPosition( new Vector3( newWidth, bones['frame_f_r3'].position.y, newDepth ) );
            if( bones['frame_f_r4'] ) bones['frame_f_r4'].setPosition( new Vector3( newWidth, newWallHeight, newDepth ) );

            if( bones['frame_b_l1'] ) bones['frame_b_l1'].setPosition( new Vector3( 0, bones['frame_b_l1'].position.y, - newDepth ) );
            if( bones['frame_b_l2'] ) bones['frame_b_l2'].setPosition( new Vector3( 0, newWallHeight, - newDepth ) );
            if( bones['frame_b_l3'] ) bones['frame_b_l3'].setPosition( new Vector3( - newWidth, bones['frame_b_l3'].position.y, - newDepth ) );
            if( bones['frame_b_l4'] ) bones['frame_b_l4'].setPosition( new Vector3( - newWidth, newWallHeight, - newDepth ) );

            if( bones['frame_b_r1'] ) bones['frame_b_r1'].setPosition( new Vector3( 0, bones['frame_b_r1'].position.y, - newDepth ) );
            if( bones['frame_b_r2'] ) bones['frame_b_r2'].setPosition( new Vector3( 0, newWallHeight, - newDepth ) );
            if( bones['frame_b_r3'] ) bones['frame_b_r3'].setPosition( new Vector3( newWidth, bones['frame_b_r3'].position.y, - newDepth ) );
            if( bones['frame_b_r4'] ) bones['frame_b_r4'].setPosition( new Vector3( newWidth, newWallHeight, - newDepth ) );

            if( bones['frame_window_b_clone_b'] ) bones['frame_window_b_clone_b'].setPosition( new Vector3( 0, 0, 0 ) );
            if( bones['frame_window_b_clone_t'] ) bones['frame_window_b_clone_t'].setPosition( new Vector3( 0, newWallHeight, 0 ) );

            if( bones['frame_window_f_clone_b'] ) bones['frame_window_f_clone_b'].setPosition( new Vector3( 0, 0, 0 ) );
            if( bones['frame_window_f_clone_t'] ) bones['frame_window_f_clone_t'].setPosition( new Vector3( 0, newWallHeight, 0 ) );

            if( bones['frame_window_lr_clone_l1'] ) bones['frame_window_lr_clone_l1'].setPosition( new Vector3( - newWidth, 0, 0 ) );
            if( bones['frame_window_lr_clone_r1'] ) bones['frame_window_lr_clone_r1'].setPosition( new Vector3( newWidth, 0, 0 ) );
            if( bones['frame_window_lr_clone_l2'] ) bones['frame_window_lr_clone_l2'].setPosition( new Vector3( - newWidth, newWallHeight, 0 ) );
            if( bones['frame_window_lr_clone_r2'] ) bones['frame_window_lr_clone_r2'].setPosition( new Vector3( newWidth, newWallHeight, 0 ) );
            
            if( bones['frame_door_f_l1'] ) bones['frame_door_f_l1'].setPosition( new Vector3( 0, 0, newDepth ) );
            if( bones['frame_door_f_l2'] ) bones['frame_door_f_l2'].setPosition( new Vector3( 0, newWallHeight, newDepth ) );
            if( bones['frame_door_f_r1'] ) bones['frame_door_f_r1'].setPosition( new Vector3( 0, 0, newDepth ) );
            if( bones['frame_door_f_r2'] ) bones['frame_door_f_r2'].setPosition( new Vector3( 0, newWallHeight, newDepth ) );
            
            if( bones['frame_door_b_l1'] ) bones['frame_door_b_l1'].setPosition( new Vector3( 0, 0, - newDepth ) );
            if( bones['frame_door_b_l2'] ) bones['frame_door_b_l2'].setPosition( new Vector3( 0, newWallHeight, - newDepth ) );
            if( bones['frame_door_b_r1'] ) bones['frame_door_b_r1'].setPosition( new Vector3( 0, 0, - newDepth ) );
            if( bones['frame_door_b_r2'] ) bones['frame_door_b_r2'].setPosition( new Vector3( 0, newWallHeight, - newDepth ) );

            if( bones['frame_horizontal_b_l1'] ) bones['frame_horizontal_b_l1'].setPosition( new Vector3( - newWidth, bones['frame_horizontal_b_l1'].position.y, - newDepth ) );
            if( bones['frame_horizontal_b_r1'] ) bones['frame_horizontal_b_r1'].setPosition( new Vector3( newWidth, bones['frame_horizontal_b_r1'].position.y, - newDepth ) );

            if( bones['frame_horizontal_f_l1'] ) bones['frame_horizontal_f_l1'].setPosition( new Vector3( - newWidth, bones['frame_horizontal_f_l1'].position.y, newDepth ) );
            if( bones['frame_horizontal_f_r1'] ) bones['frame_horizontal_f_r1'].setPosition( new Vector3( newWidth, bones['frame_horizontal_f_r1'].position.y, newDepth ) );

            if( bones['frame_window_lr_clone'] ) bones['frame_window_lr_clone'].setPosition( new Vector3( - newWidth, 0, 0 ) );
        }

        if( 'structure_rancher_modular' === _this._shedTypeVersion ) {
            if( bones['stair'] ) bones['stair'].setPosition( new Vector3( bones['stair'].position.x, bones['stair'].position.y, newDepth ) );

            if( bones['gable_3rd_left_l1'] ) bones['gable_3rd_left_l1'].setPosition( new Vector3( - 18, bones['gable_3rd_left_l1'].position.y, bones['gable_3rd_left_l1'].position.z ) );
            if( bones['gable_3rd_left_l2'] ) bones['gable_3rd_left_l2'].setPosition( new Vector3( - 18, bones['gable_3rd_left_l2'].position.y, bones['gable_3rd_left_l2'].position.z ) );
            if( bones['gable_3rd_left_m'] ) bones['gable_3rd_left_m'].setPosition( new Vector3( - 18, bones['gable_3rd_left_m'].position.y, bones['gable_3rd_left_m'].position.z ) );
            if( bones['gable_3rd_left_r1'] ) bones['gable_3rd_left_r1'].setPosition( new Vector3( - 18, bones['gable_3rd_left_r1'].position.y, bones['gable_3rd_left_r1'].position.z ) );
            if( bones['gable_3rd_left_r2'] ) bones['gable_3rd_left_r2'].setPosition( new Vector3( - 18, bones['gable_3rd_left_r2'].position.y, bones['gable_3rd_left_r2'].position.z ) );
            if( bones['gable_3rd_left_t'] ) bones['gable_3rd_left_t'].setPosition( new Vector3( - 18, bones['gable_3rd_left_t'].position.y, bones['gable_3rd_left_t'].position.z ) );

            if( bones['gable_3rd_right_l1'] ) bones['gable_3rd_right_l1'].setPosition( new Vector3( 18, bones['gable_3rd_right_l1'].position.y, bones['gable_3rd_right_l1'].position.z ) );
            if( bones['gable_3rd_right_l2'] ) bones['gable_3rd_right_l2'].setPosition( new Vector3( 18, bones['gable_3rd_right_l2'].position.y, bones['gable_3rd_right_l2'].position.z ) );
            if( bones['gable_3rd_right_m'] ) bones['gable_3rd_right_m'].setPosition( new Vector3( 18, bones['gable_3rd_right_m'].position.y, bones['gable_3rd_right_m'].position.z ) );
            if( bones['gable_3rd_right_r1'] ) bones['gable_3rd_right_r1'].setPosition( new Vector3( 18, bones['gable_3rd_right_r1'].position.y, bones['gable_3rd_right_r1'].position.z ) );
            if( bones['gable_3rd_right_r2'] ) bones['gable_3rd_right_r2'].setPosition( new Vector3( 18, bones['gable_3rd_right_r2'].position.y, bones['gable_3rd_right_r2'].position.z ) );
            if( bones['gable_3rd_right_t'] ) bones['gable_3rd_right_t'].setPosition( new Vector3( 18, bones['gable_3rd_right_t'].position.y, bones['gable_3rd_right_t'].position.z ) );
            
            if( bones['roof_dormer_left_b_l1'] ) bones['roof_dormer_left_b_l1'].setPosition( bones['b_b_l3'].position );
            if( bones['roof_dormer_left_b_l2'] ) bones['roof_dormer_left_b_l2'].setPosition( bones['b_b_l4'].position );
            if( bones['roof_dormer_left_b_l3'] ) bones['roof_dormer_left_b_l3'].setPosition( bones['b_b_l5'].position );
            if( bones['roof_dormer_left_b_r1'] ) bones['roof_dormer_left_b_r1'].setPosition( bones['b_b_r3'].position );
            if( bones['roof_dormer_left_b_r2'] ) bones['roof_dormer_left_b_r2'].setPosition( bones['b_b_r4'].position );
            if( bones['roof_dormer_left_b_r3'] ) bones['roof_dormer_left_b_r3'].setPosition( bones['b_b_r5'].position );
            if( bones['roof_dormer_left_b_t1'] ) bones['roof_dormer_left_b_t1'].setPosition( bones['b_b_t'].position );
            
            if( bones['roof_dormer_left_f_l1'] ) bones['roof_dormer_left_f_l1'].setPosition( bones['b_f_l3'].position );
            if( bones['roof_dormer_left_f_l2'] ) bones['roof_dormer_left_f_l2'].setPosition( bones['b_f_l4'].position );
            if( bones['roof_dormer_left_f_l3'] ) bones['roof_dormer_left_f_l3'].setPosition( bones['b_f_l5'].position );
            if( bones['roof_dormer_left_f_r1'] ) bones['roof_dormer_left_f_r1'].setPosition( bones['b_f_r3'].position );
            if( bones['roof_dormer_left_f_r2'] ) bones['roof_dormer_left_f_r2'].setPosition( bones['b_f_r4'].position );
            if( bones['roof_dormer_left_f_r3'] ) bones['roof_dormer_left_f_r3'].setPosition( bones['b_f_r5'].position );
            if( bones['roof_dormer_left_f_t'] ) bones['roof_dormer_left_f_t'].setPosition( bones['b_f_t'].position );
            if( bones['roof_dormer_left_f_t1'] ) bones['roof_dormer_left_f_t1'].setPosition( bones['b_f_t'].position );

            if( bones['roof_dormer_left_d_l1'] ) bones['roof_dormer_left_d_l1'].setPosition( new Vector3( - 18, bones['roof_dormer_left_d_l1'].position.y, bones['roof_dormer_left_d_l1'].position.z ) );
            if( bones['roof_dormer_left_d_l1'] ) bones['roof_dormer_left_d_l1'].setPosition( new Vector3( - 18, bones['roof_dormer_left_d_l1'].position.y, bones['roof_dormer_left_d_l1'].position.z ) );
            if( bones['roof_dormer_left_d_r1'] ) bones['roof_dormer_left_d_r1'].setPosition( new Vector3( - 18, bones['roof_dormer_left_d_r1'].position.y, bones['roof_dormer_left_d_r1'].position.z ) );
            if( bones['roof_dormer_left_d_t1'] ) bones['roof_dormer_left_d_t1'].setPosition( new Vector3( - 18, bones['roof_dormer_left_d_t1'].position.y, bones['roof_dormer_left_d_t1'].position.z ) );

            if( bones['roof_dormer_right_b_l1'] ) bones['roof_dormer_right_b_l1'].setPosition( bones['b_b_l3'].position );
            if( bones['roof_dormer_right_b_l2'] ) bones['roof_dormer_right_b_l2'].setPosition( bones['b_b_l4'].position );
            if( bones['roof_dormer_right_b_l3'] ) bones['roof_dormer_right_b_l3'].setPosition( bones['b_b_l5'].position );
            if( bones['roof_dormer_right_b_r1'] ) bones['roof_dormer_right_b_r1'].setPosition( bones['b_b_r3'].position );
            if( bones['roof_dormer_right_b_r2'] ) bones['roof_dormer_right_b_r2'].setPosition( bones['b_b_r4'].position );
            if( bones['roof_dormer_right_b_r3'] ) bones['roof_dormer_right_b_r3'].setPosition( bones['b_b_r5'].position );
            if( bones['roof_dormer_right_b_t1'] ) bones['roof_dormer_right_b_t1'].setPosition( bones['b_b_t'].position );
            
            if( bones['roof_dormer_right_f_l1'] ) bones['roof_dormer_right_f_l1'].setPosition( bones['b_f_l3'].position );
            if( bones['roof_dormer_right_f_l2'] ) bones['roof_dormer_right_f_l2'].setPosition( bones['b_f_l4'].position );
            if( bones['roof_dormer_right_f_l3'] ) bones['roof_dormer_right_f_l3'].setPosition( bones['b_f_l5'].position );
            if( bones['roof_dormer_right_f_r1'] ) bones['roof_dormer_right_f_r1'].setPosition( bones['b_f_r3'].position );
            if( bones['roof_dormer_right_f_r2'] ) bones['roof_dormer_right_f_r2'].setPosition( bones['b_f_r4'].position );
            if( bones['roof_dormer_right_f_r3'] ) bones['roof_dormer_right_f_r3'].setPosition( bones['b_f_r5'].position );
            if( bones['roof_dormer_right_f_t1'] ) bones['roof_dormer_right_f_t1'].setPosition( bones['b_f_t'].position );

            if( bones['roof_dormer_right_d_l1'] ) bones['roof_dormer_right_d_l1'].setPosition( new Vector3( 18, bones['roof_dormer_right_d_l1'].position.y, bones['roof_dormer_right_d_l1'].position.z ) );
            if( bones['roof_dormer_right_d_l1'] ) bones['roof_dormer_right_d_l1'].setPosition( new Vector3( 18, bones['roof_dormer_right_d_l1'].position.y, bones['roof_dormer_right_d_l1'].position.z ) );
            if( bones['roof_dormer_right_d_r1'] ) bones['roof_dormer_right_d_r1'].setPosition( new Vector3( 18, bones['roof_dormer_right_d_r1'].position.y, bones['roof_dormer_right_d_r1'].position.z ) );
            if( bones['roof_dormer_right_d_t1'] ) bones['roof_dormer_right_d_t1'].setPosition( new Vector3( 18, bones['roof_dormer_right_d_t1'].position.y, bones['roof_dormer_right_d_t1'].position.z ) );

            if( bones['sliding_b_l1'] ) bones['sliding_b_l1'].setPosition( new Vector3( bones['sliding_b_l1'].position.x, bones['sliding_b_l1'].position.y, - newDepth ) );
            if( bones['sliding_b_r1'] ) bones['sliding_b_r1'].setPosition( new Vector3( bones['sliding_b_r1'].position.x, bones['sliding_b_r1'].position.y, - newDepth ) );
            if( bones['sliding_f_l1'] ) bones['sliding_f_l1'].setPosition( new Vector3( bones['sliding_f_l1'].position.x, bones['sliding_f_l1'].position.y, newDepth ) );
            if( bones['sliding_f_r1'] ) bones['sliding_f_r1'].setPosition( new Vector3( bones['sliding_f_r1'].position.x, bones['sliding_f_r1'].position.y, newDepth ) );
        }

        if( 'structure_old_west' === _this._shedType ) {
            const partWidth     = newWidth * 2 / 3;
            const middlePointY  = newWallHeight + ( partWidth * helpers.getTanFromDegrees( _this._roofLowerDegree ? _this._roofLowerDegree : _this._initRoofLowerDegree ) );
            newBuildingHeight   = middlePointY + ( ( partWidth / 2 ) * helpers.getTanFromDegrees( _this._roofUpperDegree ? _this._roofUpperDegree : _this._initRoofUpperDegree ) );

            _this.setBuildingHeight( newBuildingHeight );

            if( bones['b_b_l4'] ) bones['b_b_l4'].setPosition( new Vector3( - partWidth / 2, bones['b_b_l4'].position.y, bones['b_b_l1'].position.z ) );
            if( bones['b_b_r4'] ) bones['b_b_r4'].setPosition( new Vector3( partWidth / 2, bones['b_b_r4'].position.y, bones['b_b_l1'].position.z ) );
            if( bones['b_f_l4'] ) bones['b_f_l4'].setPosition( new Vector3( - partWidth / 2, bones['b_f_l4'].position.y, bones['b_f_l1'].position.z ) );
            if( bones['b_f_r4'] ) bones['b_f_r4'].setPosition( new Vector3( partWidth / 2, bones['b_f_r4'].position.y, bones['b_f_l1'].position.z ) );
            
            if( bones['b_b_l3'] ) bones['b_b_l3'].setPosition( new Vector3( - partWidth / 2, middlePointY, bones['b_b_l1'].position.z ) );
            if( bones['b_b_r3'] ) bones['b_b_r3'].setPosition( new Vector3( partWidth / 2, middlePointY, bones['b_b_l1'].position.z ) );
            
            if( bones['b_f_l3'] ) bones['b_f_l3'].setPosition( new Vector3( - partWidth / 2, middlePointY, bones['b_f_l1'].position.z ) );
            if( bones['b_f_r3'] ) bones['b_f_r3'].setPosition( new Vector3( partWidth / 2, middlePointY, bones['b_f_l1'].position.z ) );
            
            if( bones['b_f_t'] ) bones['b_f_t'].setPosition( new Vector3( bones['b_f_t'].position.x, newBuildingHeight, bones['b_f_t'].position.z ) );
            if( bones['b_b_t'] ) bones['b_b_t'].setPosition( new Vector3( bones['b_b_t'].position.x, newBuildingHeight, bones['b_b_t'].position.z ) );
            
            if( bones['r_b_l2'] ) bones['r_b_l2'].setPosition( new Vector3( - partWidth / 2, middlePointY, - newDepth ) );
            if( bones['r_b_r2'] ) bones['r_b_r2'].setPosition( new Vector3( partWidth / 2, middlePointY, - newDepth ) );
            
            if( bones['r_f_l2'] ) bones['r_f_l2'].setPosition( new Vector3( - partWidth / 2, middlePointY, newDepth ) );
            if( bones['r_f_r2'] ) bones['r_f_r2'].setPosition( new Vector3( partWidth / 2, middlePointY, newDepth ) );
            
            if( bones['r_b_t'] ) bones['r_b_t'].setPosition( new Vector3( bones['r_b_t'].position.x, newBuildingHeight, bones['r_b_t'].position.z ) );
            if( bones['r_f_t'] ) bones['r_f_t'].setPosition( new Vector3( bones['r_f_t'].position.x, newBuildingHeight, bones['r_f_t'].position.z ) );
            
            if( bones['rm_l2'] ) bones['rm_l2'].setPosition( new Vector3( - partWidth / 2, middlePointY, - newDepth ) );
            if( bones['rm_r2'] ) bones['rm_r2'].setPosition( new Vector3( partWidth / 2, middlePointY, - newDepth ) );
            
            if( bones['rm_t'] ) bones['rm_t'].setPosition( new Vector3( bones['rm_t'].position.x, newBuildingHeight, bones['rm_t'].position.z ) );
        }

        if( "greenhouse" === _this._shedType ) {
            const positionChange = 0.04;
            if( bones['b_b_l1'] ) bones['b_b_l1'].setPosition( new Vector3( bones['b_b_l1'].position.x - positionChange, bones['b_b_l1'].position.y, bones['b_b_l1'].position.z - positionChange ) );
            if( bones['b_b_l2'] ) bones['b_b_l2'].setPosition( new Vector3( bones['b_b_l2'].position.x - positionChange, bones['b_b_l2'].position.y, bones['b_b_l2'].position.z - positionChange) );
            if( bones['b_b_r1'] ) bones['b_b_r1'].setPosition( new Vector3( bones['b_b_r1'].position.x + positionChange, bones['b_b_r1'].position.y, bones['b_b_r1'].position.z - positionChange) );
            if( bones['b_b_r2'] ) bones['b_b_r2'].setPosition( new Vector3( bones['b_b_r2'].position.x + positionChange, bones['b_b_r2'].position.y, bones['b_b_r2'].position.z - positionChange) );
            if( bones['b_f_l1'] ) bones['b_f_l1'].setPosition( new Vector3( bones['b_f_l1'].position.x - positionChange, bones['b_f_l1'].position.y, bones['b_f_l1'].position.z + positionChange ) );
            if( bones['b_f_l2'] ) bones['b_f_l2'].setPosition( new Vector3( bones['b_f_l2'].position.x - positionChange, bones['b_f_l2'].position.y, bones['b_f_l2'].position.z + positionChange) );
            if( bones['b_f_r1'] ) bones['b_f_r1'].setPosition( new Vector3( bones['b_f_r1'].position.x + positionChange, bones['b_f_r1'].position.y, bones['b_f_r1'].position.z + positionChange) );
            if( bones['b_f_r2'] ) bones['b_f_r2'].setPosition( new Vector3( bones['b_f_r2'].position.x + positionChange, bones['b_f_r2'].position.y, bones['b_f_r2'].position.z + positionChange) );
            if( bones['b_f_t'] ) bones['b_f_t'].setPosition( new Vector3( bones['b_f_t'].position.x, bones['b_f_t'].position.y, bones['b_f_t'].position.z + positionChange) );
        }

        if( "structure_saltbox_run-in" === _this._shedTypeVersion ) {
            if( bones['r_f_l1'] ) bones['r_f_l1'].setPosition( new Vector3( bones['r_f_l1'].position.x, bones['b_f_l2'].position.y, bones['r_f_l1'].position.z ) );
            if( bones['r_b_l1'] ) bones['r_b_l1'].setPosition( new Vector3( bones['r_b_l1'].position.x, bones['b_b_l2'].position.y, bones['r_b_l1'].position.z ) );
            if( bones['rm_l1'] ) bones['rm_l1'].setPosition( new Vector3( bones['rm_l1'].position.x, bones['b_b_l2'].position.y, bones['rm_l1'].position.z ) );
            
            if( bones['b_b_r2'] ) bones['b_b_r2'].setPosition( new Vector3( bones['b_b_r2'].position.x, bones['b_b_l2'].position.y - 1.33, bones['b_b_r2'].position.z ) );
            if( bones['b_f_r2'] ) bones['b_f_r2'].setPosition( new Vector3( bones['b_f_r2'].position.x, bones['b_b_l2'].position.y - 1.33, bones['b_f_r2'].position.z ) );
            if( bones['r_b_r1'] ) bones['r_b_r1'].setPosition( new Vector3( bones['r_b_r1'].position.x, bones['b_b_l2'].position.y - 1.33, bones['r_b_r1'].position.z ) );
            if( bones['r_f_r1'] ) bones['r_f_r1'].setPosition( new Vector3( bones['r_f_r1'].position.x, bones['b_b_l2'].position.y - 1.33, bones['r_f_r1'].position.z ) );
            if( bones['rm_r1'] ) bones['rm_r1'].setPosition( new Vector3( bones['rm_r1'].position.x, bones['b_b_l2'].position.y - 1.33, bones['rm_r1'].position.z ) );
        }

        if( 'structure_greenhouse_polycarbon' === _this._shedType ) {
            const frameOffsetX  = 2;
            const collarTie     = _this.getSettingData( 'collar_tie' );
            const frameY        = _this.calculateRoofPitchHeight( roofDegree, frameOffsetX ) + newWallHeight;
            const tieXPosition  = helpers.toFixed3( collarTie.distance / Math.tan( roofDegree * Math.PI / 180 ) );
            const collarTieX    = tieXPosition > newWidth ? newWidth : tieXPosition;

            if( bones['frame_door_b_l1'] ) bones['frame_door_b_l1'].setPosition( new Vector3( - newWidth + frameOffsetX, 0, - newDepth ) );
            if( bones['frame_door_b_l2'] ) bones['frame_door_b_l2'].setPosition( new Vector3( - newWidth + frameOffsetX, frameY, - newDepth ) );
            if( bones['frame_door_b_r1'] ) bones['frame_door_b_r1'].setPosition( new Vector3( newWidth - frameOffsetX, 0, - newDepth ) );
            if( bones['frame_door_b_r2'] ) bones['frame_door_b_r2'].setPosition( new Vector3( newWidth - frameOffsetX, frameY, - newDepth ) );

            if( bones['frame_door_f_l1'] ) bones['frame_door_f_l1'].setPosition( new Vector3( - newWidth + frameOffsetX, 0, newDepth ) );
            if( bones['frame_door_f_l2'] ) bones['frame_door_f_l2'].setPosition( new Vector3( - newWidth + frameOffsetX, frameY, newDepth ) );
            if( bones['frame_door_f_r1'] ) bones['frame_door_f_r1'].setPosition( new Vector3( newWidth - frameOffsetX, 0, newDepth ) );
            if( bones['frame_door_f_r2'] ) bones['frame_door_f_r2'].setPosition( new Vector3( newWidth - frameOffsetX, frameY, newDepth ) );

            if( bones['frame_horizontal_b_l1'] ) bones['frame_horizontal_b_l1'].setPosition( new Vector3( - newWidth, 0, - newDepth ) );
            if( bones['frame_horizontal_b_l2'] ) bones['frame_horizontal_b_l2'].setPosition( new Vector3( - newWidth, newWallHeight, - newDepth ) );
            if( bones['frame_horizontal_b_l3'] ) bones['frame_horizontal_b_l3'].setPosition( new Vector3( - newWidth + frameOffsetX, newWallHeight, - newDepth ) );
            if( bones['frame_horizontal_b_l4'] ) bones['frame_horizontal_b_l4'].setPosition( new Vector3( - newWidth + frameOffsetX, 0, - newDepth ) );

            if( bones['frame_horizontal_b_r1'] ) bones['frame_horizontal_b_r1'].setPosition( new Vector3( newWidth, 0, - newDepth ) );
            if( bones['frame_horizontal_b_r2'] ) bones['frame_horizontal_b_r2'].setPosition( new Vector3( newWidth, newWallHeight, - newDepth ) );
            if( bones['frame_horizontal_b_r3'] ) bones['frame_horizontal_b_r3'].setPosition( new Vector3( newWidth - frameOffsetX, newWallHeight, - newDepth ) );
            if( bones['frame_horizontal_b_r4'] ) bones['frame_horizontal_b_r4'].setPosition( new Vector3( newWidth - frameOffsetX, 0, - newDepth ) );

            if( bones['frame_horizontal_f_l1'] ) bones['frame_horizontal_f_l1'].setPosition( new Vector3( - newWidth, 0, newDepth ) );
            if( bones['frame_horizontal_f_l2'] ) bones['frame_horizontal_f_l2'].setPosition( new Vector3( - newWidth, newWallHeight, newDepth ) );
            if( bones['frame_horizontal_f_l3'] ) bones['frame_horizontal_f_l3'].setPosition( new Vector3( - newWidth + frameOffsetX, newWallHeight, newDepth ) );
            if( bones['frame_horizontal_f_l4'] ) bones['frame_horizontal_f_l4'].setPosition( new Vector3( - newWidth + frameOffsetX, 0, newDepth ) );

            if( bones['frame_horizontal_f_r1'] ) bones['frame_horizontal_f_r1'].setPosition( new Vector3( newWidth, 0, newDepth ) );
            if( bones['frame_horizontal_f_r2'] ) bones['frame_horizontal_f_r2'].setPosition( new Vector3( newWidth, newWallHeight, newDepth ) );
            if( bones['frame_horizontal_f_r3'] ) bones['frame_horizontal_f_r3'].setPosition( new Vector3( newWidth - frameOffsetX, newWallHeight, newDepth ) );
            if( bones['frame_horizontal_f_r4'] ) bones['frame_horizontal_f_r4'].setPosition( new Vector3( newWidth - frameOffsetX, 0, newDepth ) );

            if( bones['inner_frame_horizontal_clone_l3'] ) bones['inner_frame_horizontal_clone_l3'].setPosition( new Vector3( - collarTieX, newBuildingHeight - collarTie.distance, 0 ) );
            if( bones['inner_frame_horizontal_clone_r3'] ) bones['inner_frame_horizontal_clone_r3'].setPosition( new Vector3( collarTieX, newBuildingHeight - collarTie.distance, 0 ) );

            if( bones['rafter_b'] ) bones['rafter_b'].setPosition( new Vector3( 0, -0.3, - newDepth + 0.01 ) );
            if( bones['rafter_f'] ) bones['rafter_f'].setPosition( new Vector3( 0, -0.3, newDepth - 0.01 ) );

            this.updateBonesFramingGreenHousePolycarbon();
        }
        
        this.aframeDoubleGableMoveBones( newWallHeight, newWidth, newDepth );
        this.aframeCabinMoveBones( newWallHeight, newWidth, newDepth );
        this.porchBarnMoveBones( newWallHeight, newWidth, newDepth );
        this.aframe2StoryAtticLeanToMoveBones( newWidth, newDepth );
        this.barn2StoryLeanToMoveBones( newWidth, newDepth );
        this.oldWestModularMoveBones( newWallHeight, newBuildingHeight, newWidth, newDepth );
        this.ranchCabinMoveBones( newWallHeight, newBuildingHeight, newWidth, newDepth );
        this.deluxePorchMoveBones( newWallHeight, newBuildingHeight, newDepth );
        this.leanToFrontPorchMoveBones( newWallHeight, newWidth, newDepth );
        this.moveBonesCustomizedSaltboxRoof( newWallHeight, width, newWidth, newDepth );
        this.moveBonesOverhangRoofCenter();
        this.moveBonesAframeSingleGable( newWallHeight, newBuildingHeight, newWidth, newDepth );

        _this.handleRoofOverhang();
        _this.roofMoveBoneByModelKey();

        let newMidRoofPitchHeight = _this.calculateMidRoofPitchHeight();
        _this.setMidRoofPitchHeight(newMidRoofPitchHeight);
        _this.updateExternalPorchRunnerPosition();

        _this.updateRoofBlock( bones );
        _this.scaleKickBoard();
        _this.scaleKickBoard( true, 'frame_kick_board' );
    }

    moveBonesBase = ( listBones = [], w, h, d, newBuildingHeight ) => {
        if( listBones.length === 0 ) return;
        let porchWidth = this.isFrontPorch() ? this._porchWidth : 0;
        for( let boneName of listBones ) {
            if( boneName.length === 0 || ! this._bones[boneName[0]] ) continue;
            let x = w;
            let y = h;
            let z = d + porchWidth;
            if( 'b' === boneName[1] ) {  
                z = - d; 
            }

            if( 'l' === boneName[2] ) { 
                x = - w; 
            } else if( 't' === boneName[2] ) {
                x = 0;
                y = newBuildingHeight;
            }
            this._bones[boneName[0]].setPosition( new Vector3( x, y, z ) );
        }
    }

   
    /**
     * Calculate the center of the customized saltbox roof
     * @returns An object containing the middle width and middle height
     */
    calculateSaltboxCustomizedRoofCenter = () => {
        const width = this.getWidth();

        const lowerWallRoofPitch = Number( this.getSettingData( 'model_roof_angle' ) );
        const higherWallRoofPitch = this.getSettingData( 'model_second_roof_angle' ) ? Number( this.getSettingData( 'model_second_roof_angle' ) ) : lowerWallRoofPitch;
        const wallHeightDiff = this.getSettingData( 'slope_height_difference' ) ? Number( this.getSettingData( 'slope_height_difference' ) ) / 12 : 0;

        const tanAlpha     = helpers.toFixed3( helpers.getTanFromDegrees( lowerWallRoofPitch ) );
        const tanBeta      = helpers.toFixed3( helpers.getTanFromDegrees( higherWallRoofPitch ) );
        const middleWidth  = helpers.toFixed3( ( width * tanBeta + wallHeightDiff ) / ( tanAlpha + tanBeta ) );
        const middleHeight = helpers.toFixed3( this.calculateRoofPitchHeight( lowerWallRoofPitch, middleWidth ) );

        return { middleWidth, middleHeight };
    }

    /**
     * Move the bones of a customized saltbox roof
     * 
     * @param newWallHeight The new height of the walls
     * @param width The width of the roof
     * @param newWidth The new width of the roof
     * @param newDepth The new depth of the roof
     */
    moveBonesCustomizedSaltboxRoof = ( newWallHeight, width, newWidth, newDepth ) => {
        if( 'saltbox_customize' !== this.getRoofShape() ) return;
        
        let wallHeightDiff          = this.getSettingData( 'slope_height_difference' );
        wallHeightDiff      = ! wallHeightDiff ? 0 : wallHeightDiff / 12;

        const bones = this._bones;
       
        const roofTop  = this.calculateSaltboxCustomizedRoofCenter();
        const roofTopX = newWidth - roofTop.middleWidth;
        const roofTopY = newWallHeight + roofTop.middleHeight;

        this.setRoofPitchHeight(roofTop.middleHeight);
        this.setBuildingHeight(roofTopY);

        const lowerWallHeight  = newWallHeight;
        const higherWallHeight = newWallHeight + wallHeightDiff;
        
        if( bones['b_b_t'] != undefined ) bones['b_b_t'].setPosition( new Vector3( roofTopX, roofTopY, -newDepth ) );
        if( bones['b_f_t'] != undefined ) bones['b_f_t'].setPosition( new Vector3( roofTopX, roofTopY, newDepth ) );
        if( bones['r_b_t'] != undefined ) bones['r_b_t'].setPosition( new Vector3( roofTopX, roofTopY, -newDepth ) );
        if( bones['r_f_t'] != undefined ) bones['r_f_t'].setPosition( new Vector3( roofTopX, roofTopY, newDepth ) );
        if( bones['rm_t'] != undefined ) bones['rm_t'].setPosition( new Vector3( roofTopX, roofTopY, -newDepth ) )
        
        if( bones['b_b_l2'] ) bones['b_b_l2'].setPosition( new Vector3( -newWidth, higherWallHeight, -newDepth ) );
        if( bones['r_b_l1'] ) bones['r_b_l1'].setPosition( new Vector3( -newWidth, higherWallHeight, -newDepth ) );
        if( bones['rm_l1'] ) bones['rm_l1'].setPosition( new Vector3( -newWidth, higherWallHeight, -newDepth ) );

        if( bones['b_f_l2'] ) bones['b_f_l2'].setPosition( new Vector3( -newWidth, higherWallHeight, newDepth ) );
        if( bones['r_f_l1'] ) bones['r_f_l1'].setPosition( new Vector3( -newWidth, higherWallHeight, newDepth ) );
    }

    /**
     * Calculate siding area of saltbox_customize roof shape model
     */
    calculateSaltboxCustomizeSidingArea = () => {
        if ( ! this._bones['b_f_t'] || ! this._bones['b_f_l1'] || ! this._bones['b_f_l2'] || ! this._bones['b_f_r1'] || ! this._bones['b_f_r2'] ) return 0;

        const depth = this.getDepth();
        const wallHeight = helpers.getBaseHeight();
        
        let wallHeightDiff = this.getSettingData( 'slope_height_difference' );
        wallHeightDiff = ! wallHeightDiff ? 0 : wallHeightDiff / 12;

        const points = [
            {
                x: this._bones['b_f_t'].position.x,
                y: this._bones['b_f_t'].position.y
            },
            {
                x: this._bones['b_f_l2'].position.x,
                y: wallHeight + wallHeightDiff
            },
            {
                x: this._bones['b_f_l1'].position.x,
                y: this._bones['b_f_l1'].position.y,
            },
            {
                x: this._bones['b_f_r1'].position.x,
                y: this._bones['b_f_r1'].position.y,
            },
            {
                x: this._bones['b_f_r2'].position.x,
                y: wallHeight
            }
        ];

        const leftRightArea = ( depth * wallHeight ) + ( depth * ( wallHeight + wallHeightDiff ) );
        const frontBackArea = helpers.toFixed3( this.calculatePolygonArea( points ) ) * 2 || 0;
        
        return helpers.toFixed3( leftRightArea + frontBackArea );
    }

    /**
     * Calculate roof area of saltbox_customize roof shape model
     */
    calculateSaltboxCustomizeRoofArea = () => {
        const depth = this.getDepth();
        const width = this.getWidth();
        
        const lowerWallRoofPitch = Number( this.getSettingData( 'model_roof_angle' ) );
        const higherWallRoofPitch = this.getSettingData( 'model_second_roof_angle' ) ? Number( this.getSettingData( 'model_second_roof_angle' ) ) : lowerWallRoofPitch;
        
        const middleRoofWidth = this.calculateSaltboxCustomizedRoofCenter().middleWidth;
        
        const lowerRoofWidth = helpers.toFixed3( middleRoofWidth / helpers.getCosFromDegrees(lowerWallRoofPitch) );
        const higherRoofWidth = helpers.toFixed3( ( width - middleRoofWidth ) / helpers.getCosFromDegrees(higherWallRoofPitch) );
        
        return helpers.toFixed3( lowerRoofWidth * depth + higherRoofWidth * depth );
    }

    /**
     * Calculate area of any polygon by 2D coordinate points
     * @param points Array of point objects with x and y attribute
     */
    calculatePolygonArea = ( points ) => {
        if( ! Array.isArray( points ) || points.length < 3 ) return 0;

        const allEqual = ( arr, key ) => arr.every( p => p[key] === arr[0][key] );
        let plane;
        if( allEqual( points, 'z' ) ) {
            plane = 'xy';
        } else if( allEqual( points, 'x' ) ) {
            plane = 'yz';
        } else if( allEqual( points, 'y' ) ) {
            plane = 'xz';
        } else {
            throw new Error( "Unable to determine plane: points are not coplanar in XY, YZ or XZ." );
        }

        const axis1 = plane[0];
        const axis2 = plane[1];

        let area = 0;
        const countPoints = points.length;
        let j = countPoints - 1;

        for( let i = 0; i < countPoints; i++ ) {
            const xi = points[i][axis1];
            const yi = points[i][axis2];
            const xj = points[j][axis1];
            const yj = points[j][axis2];

            area += ( xj + xi ) * ( yj - yi );
            j = i;
        }

        return Math.abs( area / 2 );
    }

    roofMoveBoneByModelKey = () => {
        for( let bone of this._roofBoneSync ) {
            if( this._bones[bone.name.toLowerCase()] ) {
                bone.setPosition( this._bones[bone.name.toLowerCase()].position );
            }
        }        
    }

    roofComponentMoveBones = ( newWidth, newWallHeight, newDepth, newBuildingHeight, newRoofPitchHeight ) => {
        if( 'hip' === this.getRoofShape() ) {
            const synchronizedSkeletons = {
                'roof_permatile_hiproof_b_l1':      'r_b_l1',
                'roof_permatile_hiproof_b_r1':      'r_b_r1',
                'roof_permatile_hiproof_b_t':       'r_b_t',
                'roof_permatile_hiproof_f_l1':      'r_f_l1',
                'roof_permatile_hiproof_f_r1':      'r_f_r1',
                'roof_permatile_hiproof_f_t':       'r_f_t',
                'roof_standing_seam_hiproof_b_l1':  'r_b_l1',
                'roof_standing_seam_hiproof_b_r1':  'r_b_r1',
                'roof_standing_seam_hiproof_b_t':   'r_b_t',
                'roof_standing_seam_hiproof_f_l1':  'r_f_l1',
                'roof_standing_seam_hiproof_f_r1':  'r_f_r1',
                'roof_standing_seam_hiproof_f_t':   'r_f_t',
            };

            const baseBone = this._scene.getBoneByName( 'R_B_L1' );
            if( baseBone ) {
                baseBone.getSkeleton().onBeforeComputeObservable.add( () => {
                    for( const key in synchronizedSkeletons ) {
                        const keyMain = synchronizedSkeletons[key];
        
                        if( this._bones[key] && this._bones[keyMain] ) {
                            this._bones[key].setPosition( this._bones[keyMain].position );
                        }
                    }
                } );
            }

        } else if( 'flat' === this.getRoofShape() ) {
            let leanToHeight = newRoofPitchHeight + newWallHeight;
            if( 0 !== this.initRoofPermatileMetal.standing_seam ) { // standing_seam
                let listBonesLeanToLeft = [
                    ['roof_standing_seam_lean_to_b_l1', 'b', 'l'],
                    ['roof_standing_seam_lean_to_f_l1', 'f', 'l'],
                    ['roof_standing_seam_lean_to_l1', 'b', 'l'],
                ];
    
                this.moveBonesBase( listBonesLeanToLeft, newWidth, leanToHeight, newDepth, leanToHeight );
    
                let listBonesLeanToRight = [
                    ['roof_standing_seam_lean_to_b_r1', 'b', 'r'],
                    ['roof_standing_seam_lean_to_f_r1', 'f' ,'r'],
                    ['roof_standing_seam_lean_to_r1', 'b', 'r'],
                ];
    
                this.moveBonesBase( listBonesLeanToRight, newWidth, newWallHeight, newDepth, leanToHeight );
            }

            if( 0 !== this.initRoofPermatileMetal.permatile_metal ) { // permatile_metal
                let listBonesLeanToLeft = [
                    ['roof_permatile_lean_to_b_l1', 'b', 'l'],
                    ['roof_permatile_lean_to_f_l1', 'f', 'l'],
                    ['roof_permatile_lean_to_l1', 'b', 'l'],
                ];
    
                this.moveBonesBase( listBonesLeanToLeft, newWidth, leanToHeight, newDepth, leanToHeight );
    
                let listBonesLeanToRight = [
                    ['roof_permatile_lean_to_b_r1', 'b', 'r'],
                    ['roof_permatile_lean_to_f_r1', 'f' ,'r'],
                    ['roof_permatile_lean_to_r1', 'b', 'r'],
                ];
    
                this.moveBonesBase( listBonesLeanToRight, newWidth, newWallHeight, newDepth, leanToHeight );
            }
        } else {
            if( 0 !== this.initRoofPermatileMetal.standing_seam ) { // standing_seam
                let listBonesStandingSeamRoof = [
                    ['roof_standing_seam_aframe_b_t', 'b', 't'],
                    ['roof_standing_seam_aframe_f_t', 'f', 't'],
                    ['roof_standing_seam_aframe_b_l1', 'b', 'l'],
                    ['roof_standing_seam_aframe_b_r1', 'b', 'r'],
                    ['roof_standing_seam_aframe_f_l1', 'f', 'l'],
                    ['roof_standing_seam_aframe_f_r1', 'f', 'r'],
                    ['roof_standing_seam_aframe_metal_t', 'b', 't'],
                    ['roof_standing_seam_aframe_metal_r1', 'b', 'r'],
                    ['roof_standing_seam_aframe_metal_l1', 'b', 'l'],
                ];
                this.moveBonesBase( listBonesStandingSeamRoof, newWidth, newWallHeight, newDepth, newBuildingHeight );
            }

            if( 0 !== this.initRoofPermatileMetal.permatile_metal ) { // permatile_metal
                let listBonesPermatileMetal = [
                    ['roof_permatile_aframe_b_t', 'b', 't'],
                    ['roof_permatile_aframe_f_t', 'f', 't'],
                    ['roof_permatile_aframe_b_l1', 'b', 'l'],
                    ['roof_permatile_aframe_b_r1', 'b', 'r'],
                    ['roof_permatile_aframe_f_l1', 'f', 'l'],
                    ['roof_permatile_aframe_f_r1', 'f', 'r'],
                    ['roof_permatile_aframe_metal_t', 'b', 't'],
                    ['roof_permatile_aframe_metal_r1', 'b', 'r'],
                    ['roof_permatile_aframe_metal_l1', 'b', 'l'],
                ];
                this.moveBonesBase( listBonesPermatileMetal, newWidth, newWallHeight, newDepth, newBuildingHeight );
            }
        }
    }

    updateBonesFramingGreenHousePolycarbon = () => {
        if( helpers.isEmptyObject( this._allComponents ) ) return;
        for( const key in this._allComponents ) {
            const component = this._allComponents[key];
            if( 'door' !== component.type ) continue;
            const side              = component.side;
            const componentInfor    = this._walls[side]["items"][key];
            this.moveBonseFramingGreenHousePolycarbon( component.side, componentInfor.width );
        }
    }

    moveBonseFramingGreenHousePolycarbon = ( side = '', componentWidth = 0 ) => {
        if( 'structure_greenhouse_polycarbon' !== this._shedType || ( 'back' !== side && 'front' !== side ) ) return;
        const newDepth              = this.getDepth() / 2;
        let roofDegree              = this.getRoofDegree();
        let newWidth: number        = this.getWidth() / 2;
        let newWallHeight: number   = this.getWallHeight();
        const frameOffsetX          = componentWidth > 0 ? newWidth - ( componentWidth / 2 ) : 2;
        const frameY                = this.calculateRoofPitchHeight( roofDegree, frameOffsetX ) + newWallHeight;
        const bones                 = this._bones;
        
        if( 'back' === side ) {
            if( bones['frame_door_b_l1'] ) bones['frame_door_b_l1'].setPosition( new Vector3( - newWidth + frameOffsetX, 0, - newDepth ) );
            if( bones['frame_door_b_l2'] ) bones['frame_door_b_l2'].setPosition( new Vector3( - newWidth + frameOffsetX, frameY, - newDepth ) );
            if( bones['frame_door_b_r1'] ) bones['frame_door_b_r1'].setPosition( new Vector3( newWidth - frameOffsetX, 0, - newDepth ) );
            if( bones['frame_door_b_r2'] ) bones['frame_door_b_r2'].setPosition( new Vector3( newWidth - frameOffsetX, frameY, - newDepth ) );
            if( bones['frame_horizontal_b_l3'] ) bones['frame_horizontal_b_l3'].setPosition( new Vector3( - newWidth + frameOffsetX, bones['frame_horizontal_b_l3'].position.y, - newDepth ) );
            if( bones['frame_horizontal_b_l4'] ) bones['frame_horizontal_b_l4'].setPosition( new Vector3( - newWidth + frameOffsetX, 0, - newDepth ) );
            if( bones['frame_horizontal_b_r3'] ) bones['frame_horizontal_b_r3'].setPosition( new Vector3( newWidth - frameOffsetX, bones['frame_horizontal_b_r3'].position.y, - newDepth ) );
            if( bones['frame_horizontal_b_r4'] ) bones['frame_horizontal_b_r4'].setPosition( new Vector3( newWidth - frameOffsetX, 0, - newDepth ) );
        } else {
            if( bones['frame_door_f_l1'] ) bones['frame_door_f_l1'].setPosition( new Vector3( - newWidth + frameOffsetX, 0, newDepth ) );
            if( bones['frame_door_f_l2'] ) bones['frame_door_f_l2'].setPosition( new Vector3( - newWidth + frameOffsetX, frameY, newDepth ) );
            if( bones['frame_door_f_r1'] ) bones['frame_door_f_r1'].setPosition( new Vector3( newWidth - frameOffsetX, 0, newDepth ) );
            if( bones['frame_door_f_r2'] ) bones['frame_door_f_r2'].setPosition( new Vector3( newWidth - frameOffsetX, frameY, newDepth ) );
            if( bones['frame_horizontal_f_l3'] ) bones['frame_horizontal_f_l3'].setPosition( new Vector3( - newWidth + frameOffsetX, bones['frame_horizontal_f_l3'].position.y, newDepth ) );
            if( bones['frame_horizontal_f_l4'] ) bones['frame_horizontal_f_l4'].setPosition( new Vector3( - newWidth + frameOffsetX, 0, newDepth ) );
            if( bones['frame_horizontal_f_r3'] ) bones['frame_horizontal_f_r3'].setPosition( new Vector3( newWidth - frameOffsetX, bones['frame_horizontal_f_r3'].position.y, newDepth ) );
            if( bones['frame_horizontal_f_r4'] ) bones['frame_horizontal_f_r4'].setPosition( new Vector3( newWidth - frameOffsetX, 0, newDepth ) );
        }
    }
    /**
     * Get new X position by offset with center
     */
    quakerGetXPosition = ( oldX ) => {
        return oldX + ( ( this.getWidth() - 6 ) / 2 + this._quakerDistanceX );
    }

    getMaterialByName = ( name : string ) => {
        let _this = this;
        let materials = [];

        _this._scene.materials.forEach( material => {
            if( material.name === name ){
                materials.push(material);
            }
        });

        return materials.length > 0 ? materials : false;
    }

    /**
     * Handle bonse for external porch
     */
    private handleExternalPorch(){
        if( ! this.isExternalPorch() ) return;

        const porchGroup    = this.getMeshByName( 'Porch_grp' );
        if( ! porchGroup ) return;

        const bones         = this._bones;
        const width         = this.getWidth();
        const depth         = this.getDepth();
        const location      = this._externalPorchLocation;
        const porch_bones   = [ 'porch_b_l1', 'porch_b_l2', 'porch_b_l3', 'porch_f_l1', 'porch_f_l2', 'porch_f_l3', 'porch_b_r1', 'porch_b_r2', 'porch_b_r3', 'porch_f_r1', 'porch_f_r2', 'porch_f_r3', 'porch_roof_b_l1', 'porch_roof_f_l1', 'porch_roof_b_r1', 'porch_roof_f_r1' ];

        let positionZ       = 'front' === location ? depth/2 : width/2;
        let positionX       = 'front' === location ? width/2 : depth/2;
        if( bones['porch'] ) bones['porch'].setPosition( new Vector3(0, 0, positionZ) );
        if( bones['porch_roof'] ) bones['porch_roof'].setPosition( new Vector3(0, 0, positionZ) );
        if( bones['porch_roof_metal'] ) bones['porch_roof_metal'].setPosition( new Vector3(0, 0, positionZ) );
        if( bones['column'] ) bones['column'].setPosition( new Vector3(0, 0, positionZ) );
        if( bones['baluster'] ) bones['baluster'].setPosition( new Vector3(0, 0, positionZ) );    
        if( bones['column_b'] ) bones['column_b'].setPosition( new Vector3(-positionX, bones['column_b'].position.y, bones['column_b'].position.z) );
        if( bones['column_t'] ) bones['column_t'].setPosition( new Vector3(-positionX, bones['column_t'].position.y, bones['column_t'].position.z) );
        if( bones['baluster_b_l1'] ) bones['baluster_b_l1'].setPosition( new Vector3(0, bones['baluster_b_l1'].position.y, 0 ) );

        porch_bones.forEach( key => {
            const vector = ( -1 !== key.indexOf( '_l' ) ) ? -positionX : positionX;           
            if( bones[key] ) bones[key].setPosition( new Vector3( vector, bones[key].position.y, bones[key].position.z ) );
        });

        if( 'left' === location ){
            if( porchGroup ) porchGroup.rotation = new Vector3(0, -Math.PI/2, 0);
        }
    }

    /**
     * Check porch opening size custom is enabled
     */
    isEnableCustomOpeningSize = () => {
        let isOpeningCustom = this._canvas.getAttribute( 'data-porch-custom-opening-size' );

        return 'true' === isOpeningCustom && this.getOpeningSize();
    }

    /**
     * Check Brace is enabled or not
     */
    isEnableBrace = () => {
        let isBrace  = this._canvas.getAttribute( 'data-enable-brace' );
        let braceObj = this._scene.getMeshByName( 'column_braces_obj' ) as Mesh;

        return 'true' === isBrace && braceObj;
    }

    /**
     * Check debug mode is enable
     */
    isDebugMode = () => {
        return 'true' === this._canvas.getAttribute( 'data-debug-mode' );
    }

    /**
     * Get Porch Opening Position
     */
    getOpeningPosition = () => {
        const openingPosition = jQuery( '[name=ssb-configurator-upgrade-opening-position]:checked' ).val();

        if( openingPosition ){
            return openingPosition.toString().toLowerCase();
        }

        return 'middle';
    }

    /**
     * Handle bones for front porch
     */
    handleFrontPorch = () => {
        if( ! this.isFrontPorch() ) return;

        const _this         = this;
        const bones         = _this._bones;
        const width         = _this.getWidth();
        const depth         = _this.getDepth();
        const wallHeight    = _this.getWallHeight();
        const oPosition     = _this.getOpeningPosition();
        
        const positionZ     = depth / 2 + _this._porchWidth;
        let postDistance    = width / 3;

        const meshName      = _this.isEnableBrace() ? 'column_braces_obj' : 'column_obj';
        const postObj       = _this._scene.getMeshByName( meshName ) as Mesh;

        if( _this.isEnableCustomOpeningSize() && 'middle' === oPosition ){
            const openingSize = _this.getOpeningSize();
            postDistance      = ( width - openingSize ) / 2;

        }else if( 'left' === oPosition || 'right' === oPosition ){
            postDistance    = width / 2;
            
            let postClone   = _this._scene.getMeshByName( `${meshName}_1` );
            if( postClone ) postClone.setEnabled( false );
            if( postObj ) postObj.position.x = 0;
        }
        
        _this._postDistance = postDistance;

        if( postObj && 'middle' === oPosition ){
            let postClone       = _this._scene.getMeshByName( `${meshName}_1` );
            if( ! postClone ){
                postClone       = postObj.createInstance( `${meshName}_1` );
            }

            postClone.setEnabled( true );
            postObj.position.x    =  width / 2 - postDistance;
            postClone.position.x  = -width / 2 + postDistance;
        }        

        let errorNumber = 0;
        if( 'structure_cardinal_victorian' === _this._shedType || 'structure_cardinal_cabin' === _this._shedType ){
            errorNumber = helpers.getTanFromDegrees( _this.getRoofDegree() ) * postDistance + 0.4;
        }

        if( 'structure_cardinal_lofted_cabin' === _this._shedType ) {
            // if( postObj.position.x <= Math.abs( bones['b_p_r3'].position.x ) ){
            //     let space1  = bones['b_p_r3'].position.x - postObj.position.x;
            //     errorNumber = ( helpers.getTanFromDegrees( _this._roofUpperDegree ? _this._roofUpperDegree : _this._initRoofUpperDegree ) * space1 ) + ( bones['b_p_r3'].position.y - wallHeight );
            // }else{
            //     let space2  = ( width / 2 ) - postObj.position.x;
            //     errorNumber = ( helpers.getTanFromDegrees( _this._roofLowerDegree ? _this._roofLowerDegree : _this._initRoofLowerDegree ) * space2 );
            // }
            errorNumber = 89 / 12 - ( 6 + 3 / 12);
        }

        if( bones['column_b'] ) bones['column_b'].setPosition( new Vector3(0, bones['column_b'].position.y, positionZ) );
        if( bones['column_t'] ) bones['column_t'].setPosition( new Vector3(0, wallHeight + errorNumber, positionZ) );
        if( bones['post_b'] ) bones['post_b'].setPosition( new Vector3(0, bones['post_b'].position.y, positionZ) );
        if( bones['post_t'] ) bones['post_t'].setPosition( new Vector3(0, wallHeight + errorNumber, positionZ) );

        let rail_h_x = width/2 - postDistance;
        if( 'left' === oPosition || 'right' === oPosition ){
            rail_h_x = 0;
        }        

        //Move bonse for Rail_h_l
        if( bones['rail_h_l_f_l1'] ) bones['rail_h_l_f_l1'].setPosition( new Vector3( -width/2, bones['rail_h_l_f_l1'].position.y, positionZ ) );
        if( bones['rail_h_l_f_l2'] ) bones['rail_h_l_f_l2'].setPosition( new Vector3( -rail_h_x, bones['rail_h_l_f_l2'].position.y, positionZ ) );

        //Move bonse fo Rail_v_l
        if( bones['rail_v_l_b_l1'] ) bones['rail_v_l_b_l1'].setPosition( new Vector3( -width/2, bones['rail_v_l_b_l1'].position.y, depth / 2 ) );
        if( bones['rail_v_l_f_l1'] ) bones['rail_v_l_f_l1'].setPosition( new Vector3( -width/2, bones['rail_v_l_f_l1'].position.y, positionZ ) );

        //Move bonse for Rail_h_r
        if( bones['rail_h_r_f_l1'] ) bones['rail_h_r_f_l1'].setPosition( new Vector3( width/2, bones['rail_h_r_f_l1'].position.y, positionZ ) );
        if( bones['rail_h_r_f_l2'] ) bones['rail_h_r_f_l2'].setPosition( new Vector3( rail_h_x, bones['rail_h_r_f_l2'].position.y, positionZ ) );

        //Move bonse fo Rail_v_r
        if( bones['rail_v_r_b_l1'] ) bones['rail_v_r_b_l1'].setPosition( new Vector3( width/2, bones['rail_v_r_b_l1'].position.y, depth / 2 ) );
        if( bones['rail_v_r_f_l1'] ) bones['rail_v_r_f_l1'].setPosition( new Vector3( width/2, bones['rail_v_r_f_l1'].position.y, positionZ ) );        

        //Move bonse for B_PB
        if( bones['b_pb_l1'] ) bones['b_pb_l1'].setPosition( new Vector3( -width/2, 0, depth / 2 + _this._porchWidth ) );
        if( bones['b_pb_l2'] ) bones['b_pb_l2'].setPosition( new Vector3( -width/2, wallHeight, depth / 2 + _this._porchWidth ) );

        if( bones['b_pb_r1'] ) bones['b_pb_r1'].setPosition( new Vector3( width/2, 0, depth / 2 + _this._porchWidth ) );
        if( bones['b_pb_r2'] ) bones['b_pb_r2'].setPosition( new Vector3( width/2, wallHeight, depth / 2 + _this._porchWidth ) );

        if( _this.isPorchFullRailing() ){
            if( bones['baluster_b_l1'] ) bones['baluster_b_l1'].setPosition( new Vector3( 0, bones['baluster_b_l1'].position.y, depth / 2 ) );
            if( bones['baluster_f_l1'] ) bones['baluster_f_l1'].setPosition( new Vector3( 0, bones['baluster_f_l1'].position.y, depth / 2 ) );
        }
    }


    /**
     * Handle bones for side porch
     */
    handleSidePorch = () => {
        if( ! this.isSidePorch() ) return;

        const _this         = this;
        const bones         = _this._bones;
        const width         = _this.getWidth();
        const depth         = _this.getDepth();
        const wallHeight    = _this.getWallHeight();
        const oPosition     = _this.getOpeningPosition();
        
        const positionX     = - width / 2 + _this._porchWidth;
        let postDistance    = depth / 3;

        const meshName      = _this.isEnableBrace() ? 'column_braces_obj' : 'column_obj';
        const postObj       = _this._scene.getMeshByName( meshName ) as Mesh;

        if( _this.isEnableCustomOpeningSize() && 'middle' === oPosition ){
            const openingSize = _this.getOpeningSize();
            postDistance      = ( depth - openingSize ) / 2;

        }else if( 'left' === oPosition || 'right' === oPosition ){
            postDistance    = depth / 2;
            
            let postClone   = _this._scene.getMeshByName( `${meshName}_1` );
            if( postClone ) postClone.setEnabled( false );
            if( postObj ) postObj.position.z = 0;
        }

        _this._postDistance = postDistance;

        if( postObj && 'middle' === oPosition ){
            let postClone       = _this._scene.getMeshByName( `${meshName}_1` );
            if( ! postClone ){
                postClone       = postObj.createInstance( `${meshName}_1` );
            }

            postClone.setEnabled( true );
            postObj.position.z    = depth / 2 - postDistance;
            postClone.position.z  = - depth / 2 + postDistance;
        }

        if( bones['column_b'] ) bones['column_b'].setPosition( new Vector3(-width/2, bones['column_b'].position.y, 0) );
        if( bones['column_t'] ) bones['column_t'].setPosition( new Vector3(-width/2, wallHeight, 0) );

        //Move bonse for Rail_h_l
        if( bones['rail_h_l_f_l1'] ) bones['rail_h_l_f_l1'].setPosition( new Vector3( - width / 2, bones['rail_h_l_f_l1'].position.y, - depth / 2 ) );
        if( bones['rail_h_l_f_l2'] ) bones['rail_h_l_f_l2'].setPosition( new Vector3( - width / 2, bones['rail_h_l_f_l2'].position.y, - depth / 2 + postDistance ) );

        //Move bonse fo Rail_v_l
        if( bones['rail_v_l_b_l1'] ) bones['rail_v_l_b_l1'].setPosition( new Vector3( positionX, bones['rail_v_l_b_l1'].position.y, - depth / 2 ) );
        if( bones['rail_v_l_f_l1'] ) bones['rail_v_l_f_l1'].setPosition( new Vector3( - width / 2, bones['rail_v_l_f_l1'].position.y, - depth / 2 ) );

        //Move bonse for Rail_h_r
        if( bones['rail_h_r_f_l1'] ) bones['rail_h_r_f_l1'].setPosition( new Vector3( - width / 2, bones['rail_h_r_f_l1'].position.y, depth / 2 ) );
        if( bones['rail_h_r_f_l2'] ) bones['rail_h_r_f_l2'].setPosition( new Vector3( - width / 2, bones['rail_h_r_f_l2'].position.y, depth / 2 - postDistance ) );

        //Move bonse fo Rail_v_r
        if( bones['rail_v_r_b_l1'] ) bones['rail_v_r_b_l1'].setPosition( new Vector3( positionX, bones['rail_v_r_b_l1'].position.y, depth / 2 ) );
        if( bones['rail_v_r_f_l1'] ) bones['rail_v_r_f_l1'].setPosition( new Vector3( - width / 2, bones['rail_v_r_f_l1'].position.y, depth / 2 ) );

        if( bones['baluster_f_l1'] ) bones['baluster_f_l1'].setPosition( new Vector3( 0, bones['baluster_f_l1'].position.y, width / 2 - _this._porchWidth - 0.2 ) );

        //Move bonse for B_PB
        if( bones['b_pb_l1'] ) bones['b_pb_l1'].setPosition( new Vector3( -width/2, 0, - depth / 2 ) );
        if( bones['b_pb_l2'] ) bones['b_pb_l2'].setPosition( new Vector3( -width/2, wallHeight, - depth / 2 ) );

        if( bones['b_pb_r1'] ) bones['b_pb_r1'].setPosition( new Vector3( -width/2, 0, depth / 2 ) );
        if( bones['b_pb_r2'] ) bones['b_pb_r2'].setPosition( new Vector3( -width/2, wallHeight, depth / 2 ) );
    }

        /**
     * Handle bones for MIddle porch
     */
        handleMiddlePorch = () => {
        if( ! this.isMiddlePorch() ) return;
        const bones     = this._bones;
        const oPosition = this.getOpeningPosition();
        const meshName  = 'rail_h_l_obj';
        const railObj   = this._scene.getMeshByName( meshName ) as Mesh;
        if( 'left' === oPosition && bones['b_p_l1'] ) {
            if( railObj ) railObj.position.z = bones['b_p_l1'].position.z - 0.15;
        } else if( 'right' === oPosition ) {
            if( railObj ) railObj.position.z = 0;
        }
    }

    /**
     * Update UV when change size and change material
     * note https://domain/wp-json/wp/v2/updateMaterial run this link in local for update database init uv scale
     * 
     * @param isShingleRoof 
     */
    private adjustUVs( isShingleRoof ) {
        let _this = this;
        let initialUVs = _this._initialUVs;

        _this.dormerAdjustUVs();
        _this.sidingAdjustUVs();
        _this.floorAdjustUVs();

        let floor02Mat                   = _this.getMaterialByName( 'second_floor_02_mat' );
        let sidingFBMaterials            = _this.getMaterialByName( 'siding_fb_mat' );
        let sidingRoofTopMaterials       = _this.getMaterialByName( 'siding_roof_top_mat' );
        let sidingLRMaterials            = _this.getMaterialByName( 'siding_lr_mat' );
        let sidingDormerRoofTopMaterials = _this.getMaterialByName( 'siding_dormer_roof_top_mat' );
        let sidingDormerMaterials        = _this.getMaterialByName( 'siding_dormer_mat' );
        let sidingRoofLowMaterials       = _this.getMaterialByName( 'siding_roof_low_mat' );
        let sidingExtraLowMaterials      = _this.getMaterialByName( 'siding_extra_low_mat' );
        let sidingRoofMidMaterials       = _this.getMaterialByName( 'siding_roof_mid_mat' );
        let sidingTopPorchMaterials      = _this.getMaterialByName( 'siding_top_porch_mat' );
        let sidingFBPorchMaterials       = _this.getMaterialByName( 'siding_fb_porch_mat' );
        let sidingLRPorchMaterials       = _this.getMaterialByName( 'siding_lr_porch_mat' );
        let sidingRoofPorchMaterials     = _this.getMaterialByName( 'siding_roof_porch_mat' );

        let sidingDormerLMaterials = _this.getMaterialByName('siding_dormer_l_mat');
        let sidingDormerRMaterials = _this.getMaterialByName('siding_dormer_r_mat');

        let roofShingle01Mats = _this.getMaterialByName( _this._roofShingle01MatKey );
        let roofShingle02Mats = _this.getMaterialByName( _this._roofShingle02MatKey );
        let roofShingle03Mats = _this.getMaterialByName('roof_shingle_03_mat');
        let roofShingle04Mats = _this.getMaterialByName('roof_shingle_04_mat');
        let roofShingle05Mats = _this.getMaterialByName('roof_shingle_05_mat');
        let roofShingle06Mats = _this.getMaterialByName('roof_shingle_06_mat');
        
        let roofMetalHorizontalMats     = _this.getMaterialByName( 'roof_metal_horizontal_mat' );
        let roofMetalHorizontal01Mats   = _this.getMaterialByName( _this._roofMetalHorizontal01MatKey );
        let roofMetalHorizontal02Mats   = _this.getMaterialByName( _this._roofMetalHorizontal02MatKey );
        let roofMetalHorizontal03Mats   = _this.getMaterialByName( 'roof_metal_horizontal_03_mat' );
        let roofMetalHorizontal04Mats   = _this.getMaterialByName( 'roof_metal_horizontal_04_mat' );
        let roofMetalHorizontal05Mats   = _this.getMaterialByName( 'roof_metal_horizontal_05_mat' );
    
        let floorMaterials = _this.getMaterialByName('floor_mat');
        let floorPorchMats = _this.getMaterialByName('floor_porch_mat');
        let trimMaterials = _this.getMaterialByName('trim_mat');
        let sidingFloorMaterials = _this.getMaterialByName('siding_floor_mat');
        let sidingPorchMaterials = _this.getMaterialByName('siding_porch_mat');
        let sidingPorchRMaterials = _this.getMaterialByName('siding_porch_r_mat');
        let sidingPorchLMaterials = _this.getMaterialByName('siding_porch_l_mat');
        let ceilingPorchMetalMat = _this.getMaterialByName('ceiling_porch_metal_mat');
        let roofMetalCellingMat = _this.getMaterialByName('roof_metal_celling_mat');
    
        let sidingPorchLRMaterials = _this.getMaterialByName('siding_porch_lr_mat');
        let sidingPorchFBMaterials = _this.getMaterialByName('siding_porch_fb_mat');
        let sidingPorchRoofTopMaterials = _this.getMaterialByName('siding_porch_roof_top_mat');
        let sidingLMaterial = _this._scene.getMaterialByName("siding_l_mat") as StandardMaterial;
        let sidingRMaterial = _this._scene.getMaterialByName("siding_r_mat") as StandardMaterial;
        let sidingOpeningMaterial = _this.getMaterialByName("siding_opening_mat");
        let sidingRoofMaterial = _this._scene.getMaterialByName("siding_roof_mat") as StandardMaterial;
        let sidingExtraMaterial = _this._scene.getMaterialByName("siding_extra_mat") as StandardMaterial;
        let doorSidingMaterial = _this._scene.getMaterialByName("door_siding_mat") as StandardMaterial;
        let roofShingleMaterial = _this._scene.getMaterialByName("roof_shingle_mat") as StandardMaterial;
        let roofPorchShingleMaterial = _this._scene.getMaterialByName("roof_porch_shingle_mat") as StandardMaterial;
        let metalWireSidingMaterial = _this._scene.getMaterialByName("meta_wire_siding_mat") as StandardMaterial;

        let roofShingleRMaterial = _this._scene.getMaterialByName("roof_shingle_r_mat") as StandardMaterial;
        let roofShingleMMaterial = _this._scene.getMaterialByName("roof_shingle_m_mat") as StandardMaterial;
        let roofShingleDormerMaterial = _this._scene.getMaterialByName("roof_shingle_dormer_mat") as StandardMaterial;

        let sidingInnerLRMaterial = _this._scene.getMaterialByName("siding_inner_lr_mat") as StandardMaterial;
        let sidingInnerKickboardLRMaterial = _this._scene.getMaterialByName("siding_inner_kickboard_lr_mat") as StandardMaterial;
        let sidingInnerKickboardFBMaterial = _this._scene.getMaterialByName("siding_inner_kickboard_fb_mat") as StandardMaterial;        

        let roofShingleLRMaterial = _this._scene.getMaterialByName("roof_shingle_lr_mat") as StandardMaterial;
        let roofShingleFBMaterial = _this._scene.getMaterialByName("roof_shingle_fb_mat") as StandardMaterial;
        
        let alpineChaletSidingRoofTop01Mat  = _this.getMaterialByName("siding_roof_top_01_mat");
        let alpineChaletSidingRoofTop02Mat  = _this.getMaterialByName("siding_roof_top_02_mat");
        let alpineChaletSidingFB01Mat       = _this.getMaterialByName("siding_fb_01_mat");
        let alpineChaletSidingFB04Mat       = _this.getMaterialByName("siding_fb_04_mat");
        let alpineChaletSidingFB02Mat       = _this.getMaterialByName("siding_fb_02_mat");
        let alpineChaletSidingFB03Mat       = _this.getMaterialByName("siding_fb_03_mat");

        let sidingBTopMats              = _this.getMaterialByName('siding_b_top_mat');
        let sidingFTopMats              = _this.getMaterialByName('siding_f_top_mat');
        let sidingBMaterials            = _this.getMaterialByName('siding_b_mat');
        let sidingFMaterials            = _this.getMaterialByName('siding_f_mat');
        let sidingF01Materials          = _this.getMaterialByName('siding_f_01_mat');
        let sidingF02Materials          = _this.getMaterialByName('siding_f_02_mat');
        let sidingF03Materials          = _this.getMaterialByName('siding_f_03_mat');
        let sidingRoofBTopMaterials     = _this.getMaterialByName('siding_b_roof_top_mat');
        let sidingRoofFTopMaterials     = _this.getMaterialByName('siding_f_roof_top_mat');
        let sidingPMaterials            = _this.getMaterialByName('siding_p_mat');
        let cellingPorchMaterials       = _this.getMaterialByName('celling_porch_mat');

        let floorInner01Mat             = _this.getMaterialByName('floor_inner_01_mat');
        let floorInner02Mat             = _this.getMaterialByName('floor_inner_02_mat');

        let sidingExtraTopMaterials     = _this.getMaterialByName('siding_extra_top_mat');
        let sidingLRAMaterials          = _this.getMaterialByName('siding_lr_a_mat');
        let sidingLRBMaterials          = _this.getMaterialByName('siding_lr_b_mat');
        let sidingCmat                  = _this.getMaterialByName('siding_c_mat');

        const roofVerticalMetal02Mat = _this.getMaterialByName( 'roof_vertical_metal_02_mat' );

        let initialWidth = _this.getInitialWidth();
        let initialDepth = _this.getInitialDepth();
        let initialWallHeight = _this.getInitialWallHeight();
        let initialRoofPitchHeight = _this.getInitialRoofPitchHeight();
        let initialMidRoofPitchHeight = _this.getInitialMidRoofPitchHeight();
        let initialBuildingHeight = _this.getInitialBuildingHeight();

        let width = _this.getWidth();
        let depth = _this.getDepth();

        let wallHeight = _this.getWallHeight();
        let roofPitchHeight = _this.getRoofPitchHeight();
        let roofMidPitchHeight = _this.getMidRoofPitchHeight();
        let buildingHeight = _this.getBuildingHeight();

        let inoxWireSideMat1    = _this.getMaterialByName('inox_wire_01_mat');
        let inoxWireDoorMat     = _this.getMaterialByName('inox_wire_02_mat');
        let inoxWireDoorMat3    = _this.getMaterialByName('inox_wire_03_mat');

        let trim01Mats  = _this.getMaterialByName( 'trim_01_mat' );
        let trim02Mats  = _this.getMaterialByName( 'trim_02_mat' );
        const roofShape = this.getRoofShape();

        if( trim01Mats ){
            const uScale = ( _this._defaultOverhangBack + _this._customOverhangBack ) / 0.25;
            const vScale = buildingHeight / initialBuildingHeight;

            trim01Mats.forEach( mat => {
                _this.updateUVScale( mat, uScale, vScale );
            } )
        }

        if( trim02Mats ){
            const uScale = ( _this._defaultOverhangFront + _this._customOverhangFront ) / 0.25;
            const vScale = buildingHeight / initialBuildingHeight;

            trim02Mats.forEach( mat => {
                _this.updateUVScale( mat, uScale, vScale );
            } )
        }
        
        if( inoxWireSideMat1 && inoxWireDoorMat && inoxWireDoorMat3 ) { //Struct dog kennel
            const activeLayout      = this.getActiveLayout();
            const sideRightSpace    = 0.215;
            const sideLeftSpace     = 0.635;
            const sideTopSpace      = 0.523;
            const sideBottomSpace   = 0.456;
            const doubleDoorWidth   = 3.067;
            const singleDoorWidth   = 2.675;
            const singleDoorSpaceLR = 0.517;

            const inoxWireBoxDepth              = Math.abs( _this._bones['b_f_l1'].position.z - _this._bones['b_m_l1'].position.z ) - sideLeftSpace - sideRightSpace;
            const inoxWireHorizontalMatsUScale  = inoxWireBoxDepth / ( 8 - sideLeftSpace - sideRightSpace );
            const inoxWireHorizontalMatsVScale  = ( wallHeight - sideTopSpace - sideBottomSpace ) / ( _this._initialWallHeight - sideTopSpace - sideBottomSpace );

            inoxWireSideMat1.forEach( mat => {
                _this.updateUVScale( mat, inoxWireHorizontalMatsUScale, inoxWireHorizontalMatsVScale, initialUVs['inox_wire_01_mat']['bumpTexture']['uScale'], initialUVs['inox_wire_01_mat']['bumpTexture']['vScale'] );
            });
            
            const inoxWireScalesWidth               = 'dogkennel_split' === activeLayout && depth >= 14 && width >= 8 ? width - 2 * doubleDoorWidth : ( width - singleDoorWidth - 2 * singleDoorSpaceLR ) / 2;
            const inoxWireDoorHorizontalMatsUScale  = 'dogkennel_split' === activeLayout && depth >= 14 && width >= 8 ? inoxWireScalesWidth / ( _this._initialWidth - 2 * doubleDoorWidth ) : inoxWireScalesWidth / ( (  _this._initialWidth - singleDoorWidth - 2 * singleDoorSpaceLR ) / 2 );
            inoxWireDoorMat.forEach( mat => {
                _this.updateUVScale( mat, inoxWireDoorHorizontalMatsUScale, inoxWireHorizontalMatsVScale, initialUVs['inox_wire_02_mat']['bumpTexture']['uScale'], initialUVs['inox_wire_02_mat']['bumpTexture']['vScale'] );
            });
            const doorTopBottomSpace = 0.606;
            const inoxWireHorizontalMatsVScale2 = ( wallHeight - 2 * doorTopBottomSpace ) / ( _this._initialWallHeight - 2 * doorTopBottomSpace );
            inoxWireDoorMat3.forEach( mat => {
                _this.updateUVScale( mat, 1, inoxWireHorizontalMatsVScale2, initialUVs['inox_wire_03_mat']['bumpTexture']['uScale'], initialUVs['inox_wire_03_mat']['bumpTexture']['vScale'] );
            });
        }

        if( roofMetalHorizontalMats ) {
            let roofMetalHorizontalMatsVScale = _this._bones['r_b_l1'] && _this._bones['r_b_t'] ? helpers.toFixed3( Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_b_t'].position ) / _this._initBoxGableWidth ) : 1;

            if( 'structure_quaker_silvercreek' === _this._shedType ){
                const roofInstance              = Math.abs( _this._bones['b_b_l1'].position.x ) + Math.abs( _this._bones['b_b_t'].position.x );
                const roofHeight                = Math.abs( _this._bones['b_b_t'].position.y ) - Math.abs( _this._bones['b_b_l1'].position.y );
                const roofWidth                 = Math.sqrt( roofInstance * roofInstance + roofHeight * roofHeight );
                roofMetalHorizontalMatsVScale   =  roofWidth/_this._initSaltBoxWidth ;
            }

            if( 'structure_lean_to_with_front_porch' === _this._shedType && _this._bones['b_f_t'] && _this._bones['b_f_r2'] ) {
                roofMetalHorizontalMatsVScale = helpers.toFixed3( Vector3.Distance( _this._bones['b_f_t'].position, _this._bones['b_f_r2'].position ) / _this.getCustomUVScale( 'roof_metal_horizontal_mat_v_scale' ) );
            }

            roofMetalHorizontalMats.forEach( mat => {
                _this.updateUVScale( mat, 1, roofMetalHorizontalMatsVScale, initialUVs['roof_metal_horizontal_mat']['bumpTexture']['uScale'], initialUVs['roof_metal_horizontal_mat']['bumpTexture']['vScale'] );
            });
        }

        if( roofMetalHorizontal01Mats ) {
            let roofMetalHorizontal01MatsVScale = 1;
            if( _this._bones['b_b_l3'] ) {
                const upperHeight               = _this._bones['b_b_t'].position.y - _this._bones['b_b_l3'].position.y;
                const upperWidth                = Math.abs( _this._bones['b_b_l3'].position.x ) - Math.abs( _this._bones['b_b_t'].position.x );
                const roofUpperWidth            = Math.sqrt( upperWidth * upperWidth + upperHeight * upperHeight );
                roofMetalHorizontal01MatsVScale = helpers.toFixed3( roofUpperWidth / _this._initRoofUpperWidth );
            }

            if( 'structure_cottage_shed' === this._shedType ) {
                const roofLeftWidth             = Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_b_t'].position );
                roofMetalHorizontal01MatsVScale = roofLeftWidth / _this._initRoofLeftWidth;
            }

            if( 'structure_lakeside_bunkie_gambrel_roof' === _this._shedType ){
                const roofWidth         = Vector3.Distance( _this._bones['r_b_r2'].position, _this._bones['r_b_t'].position );
                roofMetalHorizontal01MatsVScale = +( roofWidth / _this._roofDetachedMats['roof_01'] ).toFixed( 3 );
            }

            if( 'monitor' === roofShape ) {
                roofMetalHorizontal01MatsVScale = + ( Vector3.Distance( _this._bones['r_b_l3'].position, _this._bones['r_b_t'].position ) / _this._initRoofUpperWidth ).toFixed( 3 );
            }

            if( 'structure_monitorbarns_helmuth' === _this._shedType ) {
                roofMetalHorizontal01MatsVScale = 1;
            }
            
            if( 'dutch_style' === roofShape ) {
                roofMetalHorizontal01MatsVScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_b_l2'].position, _this._bones['r_b_t'].position ) / _this._initRoofUpperWidth );
            }

            if( 'structure_aframe_2story_attic_leanto' === _this._shedType ) {
                roofMetalHorizontal01MatsVScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_b_t'].position ) / _this._initBoxGableWidth );
            }

            if( 'structure_aframe_cabin' === _this._shedType ) {
                roofMetalHorizontal01MatsVScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_r1'].position, _this._bones['r_f_t'].position ) / _this._roofUVScale.roof01.v );
            }

            if( 'structure_porch_barn' === _this._shedType ) {
                roofMetalHorizontal01MatsVScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_r3'].position, _this._bones['r_f_t'].position ) / _this._roofUVScale.roof01.v );
            }


            if( 'saltbox_customize' === roofShape ) {
                const roofDepth = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_t'].position, _this._bones['r_f_l1'].position ) );
                
                roofMetalHorizontal01MatsVScale = helpers.toFixed3( roofDepth / _this.getCustomUVScale( _this._roofMetalHorizontal01MatKey + '_v_scale' ) );
            }

            roofMetalHorizontal01Mats.forEach( mat => {
                _this.updateUVScale( mat, 1, roofMetalHorizontal01MatsVScale, initialUVs[_this._roofMetalHorizontal01MatKey]['bumpTexture']['uScale'], initialUVs[_this._roofMetalHorizontal01MatKey]['bumpTexture']['vScale'] );
            });
        }

        if( roofMetalHorizontal02Mats ) {
            let roofMetalHorizontal02MatsVScale = 1;
            if( ( _this._bones['r_b_l1'] && _this._bones['r_b_l2'] ) || 'dutch_style' === roofShape ) {
                roofMetalHorizontal02MatsVScale = + ( Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_b_l2'].position ) / _this._initRoofLowerWidth ).toFixed( 3 );
            }

            if( 'structure_cottage_shed' === this._shedType ) {
                const roofRighttWidth           = Vector3.Distance( _this._bones['r_b_r1'].position, _this._bones['r_b_t'].position );
                roofMetalHorizontal02MatsVScale = roofRighttWidth / _this._initRoofRightWidth;
            }

            if( 'monitor' === roofShape ) {
                roofMetalHorizontal02MatsVScale = + ( Vector3.Distance( _this._bones['r_b_r3'].position, _this._bones['r_b_t'].position ) / _this._initRoofUpperWidth ).toFixed( 3 );
            }

            if( 'structure_lakeside_bunkie_gambrel_roof' === _this._shedType ){
                const roofWidth         = Vector3.Distance( _this._bones['r_b_r2'].position, _this._bones['r_b_r1'].position );
                roofMetalHorizontal02MatsVScale = +( roofWidth / _this._roofDetachedMats['roof_02'] ).toFixed( 3 );
            }

            if( 'structure_monitorbarns_helmuth' === _this._shedType ) {
                roofMetalHorizontal02MatsVScale = 1;
            }

            if( 'structure_aframe_cabin' === _this._shedType ) {
                roofMetalHorizontal02MatsVScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_l2'].position, _this._bones['r_f_t'].position ) / _this._roofUVScale.roof02.v );
            }

            if( 'structure_porch_barn' === _this._shedType ) {
                roofMetalHorizontal02MatsVScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_r2'].position, _this._bones['r_f_r3'].position ) / _this._roofUVScale.roof02.v );
            }

            if( 'saltbox_customize' === roofShape ) {
                const roofDepth = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_t'].position, _this._bones['r_f_r1'].position ) );
                
                roofMetalHorizontal02MatsVScale = helpers.toFixed3( roofDepth / _this.getCustomUVScale( _this._roofMetalHorizontal02MatKey + '_v_scale' ) );
            }

            roofMetalHorizontal02Mats.forEach( mat => {
                _this.updateUVScale( mat, 1, roofMetalHorizontal02MatsVScale, initialUVs[_this._roofMetalHorizontal02MatKey]['bumpTexture']['uScale'], initialUVs[_this._roofMetalHorizontal02MatKey]['bumpTexture']['vScale'] );
            });
        }

        if( roofMetalHorizontal03Mats ) {
            let roofMetalHorizontal03MatsUScale = 1;
            let roofMetalHorizontal03MatsVScale = 1;

            if( 'monitor' === roofShape ) {
                roofMetalHorizontal03MatsVScale = + ( Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_b_l2'].position ) / _this._initRoofLowerWidth ).toFixed( 3 );
            }

            if( 'structure_lakeside_bunkie_gambrel_roof' === _this._shedType ){
                const roofWidth         = Vector3.Distance( _this._bones['r_b_l2'].position, _this._bones['r_b_t'].position );
                roofMetalHorizontal03MatsVScale = +( roofWidth / _this._roofDetachedMats['roof_03'] ).toFixed( 3 );
            }

            if( 'structure_rancher_modular' === this._shedTypeVersion ) {
                roofMetalHorizontal03MatsUScale = ( depth - 12 ) / ( initialDepth - 12 );
            }

            if( 'structure_aframe_cabin' === _this._shedType ) {
                roofMetalHorizontal03MatsVScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_l1'].position, _this._bones['r_f_l2'].position ) / _this._roofUVScale.roof03.v );
            }

            roofMetalHorizontal03Mats.forEach( mat => {
                _this.updateUVScale( mat, roofMetalHorizontal03MatsUScale, roofMetalHorizontal03MatsVScale, initialUVs['roof_metal_horizontal_03_mat']['bumpTexture']['uScale'], initialUVs['roof_metal_horizontal_03_mat']['bumpTexture']['vScale'] );
            });
        }

        if( roofMetalHorizontal04Mats ) {
            let roofMetalHorizontal04MatsVScale = 1;

            if( 'monitor' === roofShape ) {
                roofMetalHorizontal04MatsVScale = + ( Vector3.Distance( _this._bones['r_b_r1'].position, _this._bones['r_b_r2'].position ) / _this._initRoofLowerWidth ).toFixed( 3 );
            }

            if( 'structure_lakeside_bunkie_gambrel_roof' === _this._shedType ){
                const roofWidth         = Vector3.Distance( _this._bones['r_b_l2'].position, _this._bones['r_b_l1'].position );
                roofMetalHorizontal04MatsVScale = +( roofWidth / _this._roofDetachedMats['roof_04'] ).toFixed( 3 );
            }

            if( 'structure_porch_barn' === _this._shedType ) {
                roofMetalHorizontal04MatsVScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_l1'].position, _this._bones['r_f_l2'].position ) / _this._roofUVScale.roof04.v );
            }

            roofMetalHorizontal04Mats.forEach( mat => {
                _this.updateUVScale( mat, 1, roofMetalHorizontal04MatsVScale, initialUVs['roof_metal_horizontal_04_mat']['bumpTexture']['uScale'], initialUVs['roof_metal_horizontal_04_mat']['bumpTexture']['vScale'] );
            });
        }

        if( roofMetalHorizontal05Mats ) {
            let roofMetalHorizontal05MatsVScale = 1;
            if( 'structure_porch_barn' === _this._shedType ) {
                roofMetalHorizontal05MatsVScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_l2'].position, _this._bones['r_f_l3'].position ) / _this._roofUVScale.roof05.v );
            }
            roofMetalHorizontal05Mats.forEach( mat => {
                _this.updateUVScale( mat, 1, roofMetalHorizontal05MatsVScale, initialUVs['roof_metal_horizontal_05_mat']['bumpTexture']['uScale'], initialUVs['roof_metal_horizontal_05_mat']['bumpTexture']['vScale'] );
            });
        }

        if( sidingBMaterials && sidingFMaterials ){
            let sidingFBUScale = ( width - _this._porchWidth ) / ( initialWidth - 3 );
            let sidingFBVScale = (wallHeight / initialWallHeight);

            if( _this.isCornerPorchLeft() ){
                let sidingFBUScale = ( width - _this._porchWidth ) / ( initialWidth - 4 );
                let sidingFUScale  = width / initialWidth;

                sidingBMaterials.forEach( mat=> {
                    _this.updateUVScale( mat, sidingFUScale, sidingFBVScale );
                } )

                sidingFMaterials.forEach( mat=> {
                    _this.updateUVScale( mat, sidingFBUScale, sidingFBVScale );
                } )
            }else{
                [...sidingBMaterials, ...sidingFMaterials].forEach(mat => {
                    _this.updateUVScale( mat, sidingFBUScale, sidingFBVScale );
                });
            }
        }

        if( sidingLRAMaterials ){
            let uscale = 1;
            let vscale = wallHeight / initialWallHeight;

            sidingLRAMaterials.forEach( mat=> {
                _this.updateUVScale( mat, uscale, vscale );
            } )
        }

        if( sidingLRBMaterials ){
            let uscale = ( depth - constants.cornerDepth ) / ( initialDepth - constants.cornerDepth );
            let vscale = wallHeight / initialWallHeight;

            sidingLRBMaterials.forEach( mat=> {
                _this.updateUVScale( mat, uscale, vscale );
            } )
        }

        if( sidingCmat ){
            let uscale = 1;
            let vscale = wallHeight / initialWallHeight;

            sidingCmat.forEach( mat=> {
                _this.updateUVScale( mat, uscale, vscale );
            } )
        }

        if( sidingBMaterials && sidingF01Materials && sidingF03Materials ){
            let sidingFBUScale = width / initialWidth;
            let sidingFBVScale = wallHeight / initialWallHeight;

            [...sidingBMaterials, ...sidingF03Materials].forEach(mat => {
                _this.updateUVScale( mat, sidingFBUScale, sidingFBVScale );
            });
        }

        if( sidingF01Materials ){
            let sidingFBUScale = width / initialWidth;
            let sidingFBVScale = ( wallHeight + 2.4 ) / ( initialWallHeight + 2.4 );

            sidingF01Materials.forEach(mat => {
                _this.updateUVScale( mat, sidingFBUScale, sidingFBVScale );
            });
        }

        if( sidingF02Materials ){
            let sidingFBUScale = ( width - 3 ) / ( initialWidth - 3 ); //4 is the distance of the sign from the siding
            let sidingFBVScale = wallHeight / initialWallHeight;

            sidingF02Materials.forEach(mat => {
                _this.updateUVScale( mat, sidingFBUScale, sidingFBVScale );
            });
        }

        if( sidingRoofBTopMaterials && sidingRoofFTopMaterials ){
            let sidingRoofTopUScale = (width / initialWidth);
            let sidingRoofTopVScale = (roofPitchHeight / initialRoofPitchHeight);

            [...sidingRoofBTopMaterials, ...sidingRoofFTopMaterials].forEach(mat => {
                _this.updateUVScale( mat, sidingRoofTopUScale, sidingRoofTopVScale );
            })
        }

        if( _this.isCornerPorchLeft() && sidingRoofFTopMaterials ){
            let uScale = width / initialWidth;
            let vScale = roofPitchHeight / initialRoofPitchHeight;
            
            sidingRoofFTopMaterials.forEach( mat => {
                _this.updateUVScale( mat, uScale, vScale );
            })
        }

        if( _this.isCornerPorchLeft() && sidingFTopMats ){
            let uScale = width / initialWidth;
            let vScale = 1;
            
            sidingFTopMats.forEach( mat => {
                _this.updateUVScale( mat, uScale, vScale );
            })
        }

        if( sidingBTopMats && sidingFTopMats ){
            let sidingTopMatUScale = (width / initialWidth);
            let sidingTopMatVScale = 1;

            [...sidingBTopMats, ...sidingFTopMats].forEach( mat => {
                _this.updateUVScale( mat, sidingTopMatUScale, sidingTopMatVScale );
            })
        }

        if( sidingPMaterials ){
            let sidingPUScale = depth / initialDepth;
            let sidingPVScale = 1;

            sidingPMaterials.forEach( mat => {
                _this.updateUVScale( mat, sidingPUScale, sidingPVScale );
            });            
        }

        if( cellingPorchMaterials ){
            let sidingPUScale = depth / initialDepth;
            let sidingPVScale = 1;

            if( _this.isCornerPorchLeft() ){
                sidingPUScale = 1;
            }

            if( 'structure_ranch_cabin' === _this._shedType ) {
                sidingPUScale = helpers.toFixed3( ( depth - this._cabinWidth ) / ( initialDepth - this._cabinWidth ) );
                sidingPVScale = 1;
            }

            if( _this.isDoubleGable() ) {
                sidingPUScale = helpers.toFixed3( Vector3.Distance( _this._bones['b_p_l2'].position, _this._bones['b_p_l4'].position ) / _this.getCustomUVScale( 'celling_porch_u_scale' ) );
                sidingPVScale = 1;
            }

            if( _this.isAframeSingleGable() ) {
                sidingPUScale = helpers.toFixed3( Vector3.Distance( _this._bones['b_p_l1'].position, _this._bones['b_p_l4'].position ) / _this.getCustomUVScale( 'celling_porch_u_scale' ) );
            }

            cellingPorchMaterials.forEach( mat => {
                _this.updateUVScale( mat, sidingPUScale, sidingPVScale, initialUVs['celling_porch_mat']['bumpTexture']['uScale'], initialUVs['celling_porch_mat']['bumpTexture']['vScale'] );
            });
        }

        if( 'structure_alpinechalet' === _this._shedType ) {
            if( alpineChaletSidingRoofTop01Mat ) {
                let alpineChaletSidingRoofTop01MatUScale = ( Math.abs( _this._bones['b_f_m2'].position.x ) + Math.abs( _this._bones['b_f_r2'].position.x ) ) / 6.667;
                let alpineChaletSidingRoofTop01MatVScale = ( _this._bones['b_f_t2'].position.y - _this._bones['b_f_m2'].position.y ) / 2.778;

                alpineChaletSidingRoofTop01Mat.forEach( mat => {
                    _this.updateUVScale( mat, alpineChaletSidingRoofTop01MatUScale, alpineChaletSidingRoofTop01MatVScale );
                });
            }

            if( alpineChaletSidingRoofTop02Mat ) {
                let alpineChaletSidingRoofTop02MatUScale = ( Math.abs( _this._bones['b_f_l2'].position.x ) - Math.abs( _this._bones['b_f_m2'].position.x ) ) / 3.333;
                let alpineChaletSidingRoofTop02MatVScale = ( _this._bones['b_f_t1'].position.y - _this._bones['b_f_m2'].position.y ) / 1.388;

                alpineChaletSidingRoofTop02Mat.forEach( mat => {
                    _this.updateUVScale( mat, alpineChaletSidingRoofTop02MatUScale, alpineChaletSidingRoofTop02MatVScale );
                });
            }

            if( alpineChaletSidingFB01Mat ) {
                let alpineChaletSidingFB01MatUScale = width / 3 * 2 / 6.667;
                let alpineChaletSidingFB01MatVScale = wallHeight / initialWallHeight;

                alpineChaletSidingFB01Mat.forEach( mat => {
                    _this.updateUVScale( mat, alpineChaletSidingFB01MatUScale, alpineChaletSidingFB01MatVScale );
                });
            }

            if( alpineChaletSidingFB04Mat ) {
                let alpineChaletSidingFB04MatUScale = width / 3 * 2 / 6.667;
                let alpineChaletSidingFB04MatVScale = wallHeight / initialWallHeight;

                alpineChaletSidingFB04Mat.forEach( mat => {
                    _this.updateUVScale( mat, alpineChaletSidingFB04MatUScale, alpineChaletSidingFB04MatVScale );
                });
            }

            if( alpineChaletSidingFB02Mat ) {
                let alpineChaletSidingFB02MatUScale = width / 3 / 3.333;
                let alpineChaletSidingFB02MatVScale = wallHeight / initialWallHeight;

                alpineChaletSidingFB02Mat.forEach( mat => {
                    _this.updateUVScale( mat, alpineChaletSidingFB02MatUScale, alpineChaletSidingFB02MatVScale );
                });
            }

            if( alpineChaletSidingFB03Mat ) {
                let alpineChaletSidingFB03MatUScale = width / 3 / 3.333;
                let alpineChaletSidingFB03MatVScale = wallHeight / initialWallHeight;

                alpineChaletSidingFB03Mat.forEach( mat => {
                    _this.updateUVScale( mat, alpineChaletSidingFB03MatUScale, alpineChaletSidingFB03MatVScale );
                });
            }
        }
        if( roofShingle01Mats ) {
            let roofShingle01MatsUScale = 1;
            let roofShingle01MatsVScale = 1;
            if( _this._bones['r_b_l2'] &&  _this._bones['r_b_l1'] &&  _this._bones['r_f_l1'] && _this._bones['r_b_t'] ) {
                const roofDepth         = Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_f_l1'].position );
                roofShingle01MatsUScale = + ( roofDepth / initialDepth ).toFixed( 3 );
                roofShingle01MatsVScale = + ( Vector3.Distance( _this._bones['r_b_l2'].position, _this._bones['r_b_t'].position ) / _this._initRoofUpperWidth ).toFixed( 3 );
                
                if( 'structure_cardinal_lofted_cabin' === this._shedType ) {
                    roofShingle01MatsUScale = + ( roofDepth / ( 4 + initialDepth ) ).toFixed( 3 );
                }
            }

            if( 'structure_cottage_shed' === this._shedType || 'structure_lakeside_bunkie_gable_roof' === this._shedType ) {
                const roofLefttWidth    = Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_b_t'].position );
                const roofDepth         = Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_f_l1'].position );
                roofShingle01MatsVScale = roofLefttWidth / _this._initRoofLeftWidth;
                roofShingle01MatsUScale = roofDepth / initialDepth;
            }

            if( 'structure_lakeside_bunkie_gambrel_roof' === _this._shedType ){
                const roofWidth         = Vector3.Distance( _this._bones['r_b_r2'].position, _this._bones['r_b_t'].position );
                const roofDepth         = Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_f_l1'].position );
                roofShingle01MatsVScale = +( roofWidth / _this._roofDetachedMats['roof_01'] ).toFixed( 3 );
                roofShingle01MatsUScale = +( roofDepth / initialDepth ).toFixed( 3 );
            }

            if( 'monitor' === roofShape ) {
                const roofDepth         = Vector3.Distance( _this._bones['r_b_l3'].position, _this._bones['r_f_l3'].position );
                roofShingle01MatsUScale = + ( roofDepth / initialDepth ).toFixed( 3 );
                roofShingle01MatsVScale = + ( Vector3.Distance( _this._bones['r_b_l3'].position, _this._bones['r_b_t'].position ) / _this._initRoofUpperWidth ).toFixed( 3 );
            }

            if( 'structure_aframe_genesis' === _this._shedType ) {
                const roofWidth         = Vector3.Distance( _this._bones['r_b_r1'].position, _this._bones['r_f_r1'].position );
                const roofHeight        = Vector3.Distance( _this._bones['r_f_t'].position, _this._bones['r_f_r1'].position );
                roofShingle01MatsUScale = roofWidth / _this._initRoofWidth;
                roofShingle01MatsVScale = roofHeight / _this._initRoofHeight;
            }

            if( 'structure_quaker_wannabuysheds' === _this._shedType ){
                const roofWidth         = Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_b_t'].position );
                roofShingle01MatsUScale = +( depth / initialDepth ).toFixed( 3 );
                roofShingle01MatsVScale = +( roofWidth / _this._roofDetachedMats['roof_01'] ).toFixed( 3 );
            }

            if( 'structure_ranch_cabin' === _this._shedType || this.isAframeSingleGable() ) {
                roofShingle01MatsUScale = helpers.toFixed3( depth / initialDepth );
                roofShingle01MatsVScale = 1;
            }

            if( 'structure_monitorbarns_helmuth' === _this._shedType ) {
                roofShingle01MatsVScale = 1;
            }

            if( 'dutch_style' === roofShape ) {
                roofShingle01MatsUScale = 1;
                roofShingle01MatsVScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_r2'].position, _this._bones['r_f_t'].position ) / _this._initRoofUpperWidth );
                
                if( 'structure_old_west_modular' === _this._shedType ) {
                    roofShingle01MatsUScale = helpers.toFixed3( depth / initialDepth );
                }
            }

            if( 'structure_aframe_2story_attic_leanto' === _this._shedType ) {
                roofShingle01MatsUScale = helpers.toFixed3( depth / initialDepth );
                roofShingle01MatsVScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_b_t'].position ) / _this._initBoxGableWidth );
            }

            if( 'structure_aframe_cabin' === _this._shedType ) {
                roofShingle01MatsUScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_t'].position, _this._bones['r_b_t'].position ) / _this._roofUVScale.roof01.u );
                roofShingle01MatsVScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_r1'].position, _this._bones['r_f_t'].position ) / _this._roofUVScale.roof01.v );
            }

            if( 'structure_classic_aframe_dormer' === _this._shedType && _this._isEnableWallDormerSettings ) {
                const roofWidth         = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_t'].position, _this._bones['r_f_t1'].position ) );
                const roofDepth         = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_t'].position, _this._bones['r_f_l1'].position ) );
                roofShingle01MatsUScale = helpers.toFixed3( ( roofWidth + _this.getGlobalData( 'roof_offset_number' ) ) / ( _this.getCustomUVScale( 'roof_01_u_scale' ) + _this.getGlobalData( 'roof_offset_number' ) ) );
                roofShingle01MatsVScale = helpers.toFixed3( roofDepth / _this.getCustomUVScale( 'roof_01_v_scale' ) );
            }

            if( 'structure_porch_barn' === _this._shedType ) {
                roofShingle01MatsUScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_t'].position, _this._bones['r_b_t'].position ) / _this._roofUVScale.roof01.u );
                roofShingle01MatsVScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_r3'].position, _this._bones['r_f_t'].position ) / _this._roofUVScale.roof01.v );
            }

            if( _this.isAframeSingleGable() ) {
                roofShingle01MatsUScale = 1;
                roofShingle01MatsVScale = 1;
            }

            if( 'saltbox_customize' === roofShape ) {
                const roofWidth = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_t'].position, _this._bones['r_b_t'].position ) );
                const roofDepth = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_t'].position, _this._bones['r_f_l1'].position ) );
                
                roofShingle01MatsUScale = helpers.toFixed3( roofWidth / _this.getCustomUVScale( _this._roofShingle01MatKey + '_u_scale' ) );
                roofShingle01MatsVScale = helpers.toFixed3( roofDepth / _this.getCustomUVScale( _this._roofShingle01MatKey + '_v_scale' ) );
            }

            roofShingle01Mats.forEach( mat => {
                _this.updateUVScale( mat, roofShingle01MatsUScale, roofShingle01MatsVScale, initialUVs[_this._roofShingle01MatKey]['bumpTexture']['uScale'], initialUVs[_this._roofShingle01MatKey]['bumpTexture']['vScale'] );
            });
        }

        if( roofShingle02Mats ) {
            let roofShingle02MatsUScale = 1;
            let roofShingle02MatsVScale = 1;
            if( _this._bones['r_b_l2'] &&  _this._bones['r_b_l1'] &&  _this._bones['r_f_l1'] ) {
                const roofDepth         = Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_f_l1'].position );
                roofShingle02MatsUScale = + ( roofDepth / initialDepth ).toFixed( 3 );
                roofShingle02MatsVScale = + ( Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_b_l2'].position ) / _this._initRoofLowerWidth ).toFixed( 3 );

                if( 'structure_cardinal_lofted_cabin' === this._shedType ) {
                    roofShingle02MatsUScale = + ( roofDepth / ( 4 + initialDepth ) ).toFixed( 3 );
                }
            }

            if( 'structure_cottage_shed' === this._shedType || 'structure_lakeside_bunkie_gable_roof' === this._shedType ) {
                const roofRightWidth    = Vector3.Distance( _this._bones['r_b_r1'].position, _this._bones['r_b_t'].position );
                const roofDepth         = Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_f_l1'].position );
                roofShingle02MatsVScale = roofRightWidth / _this._initRoofRightWidth;
                roofShingle02MatsUScale = roofDepth / initialDepth;
            }

            if( 'structure_lakeside_bunkie_gambrel_roof' === _this._shedType ){
                const roofWidth         = Vector3.Distance( _this._bones['r_b_r2'].position, _this._bones['r_b_r1'].position );
                const roofDepth         = Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_f_l1'].position );
                roofShingle02MatsVScale = +( roofWidth / _this._roofDetachedMats['roof_02'] ).toFixed( 3 );
                roofShingle02MatsUScale = +( roofDepth / initialDepth ).toFixed( 3 );
            }

            if( 'monitor' === roofShape ) {
                const roofDepth         = Vector3.Distance( _this._bones['r_b_r3'].position, _this._bones['r_f_r3'].position );
                roofShingle02MatsVScale = + ( Vector3.Distance( _this._bones['r_b_r3'].position, _this._bones['r_b_t'].position ) / _this._initRoofUpperWidth ).toFixed( 3 );
                roofShingle02MatsUScale = + ( roofDepth / initialDepth ).toFixed( 3 );
            }

            if( 'structure_aframe_genesis' === _this._shedType ) {
                const roofWidth         = Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_f_l1'].position );
                const roofHeight        = Vector3.Distance( _this._bones['r_f_t'].position, _this._bones['r_f_l1'].position );
                roofShingle02MatsUScale = roofWidth / _this._initRoofWidth;
                roofShingle02MatsVScale = roofHeight / _this._initRoofHeight;
            }

            if( 'structure_quaker_wannabuysheds' === _this._shedType ){
                const roofWidth         = Vector3.Distance( _this._bones['r_b_r1'].position, _this._bones['r_b_t'].position );
                roofShingle02MatsUScale = +( depth / initialDepth ).toFixed( 3 );
                roofShingle02MatsVScale = +( roofWidth / _this._roofDetachedMats['roof_02'] ).toFixed( 3 );
            }

            if( 'structure_ranch_cabin' === _this._shedType ) {
                roofShingle02MatsUScale = helpers.toFixed3( ( depth - this._cabinWidth ) / ( initialDepth - this._cabinWidth ) );
                roofShingle02MatsVScale = 1;
            }

            if( 'structure_monitorbarns_helmuth' === _this._shedType ) {
                roofShingle02MatsVScale = 1;
            }

            if( 'dutch_style' === roofShape ) {
                roofShingle02MatsUScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_b_t'].position, _this._bones['r_f_t'].position ) / initialDepth );
                roofShingle02MatsVScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_r2'].position, _this._bones['r_f_t'].position ) / _this._initRoofUpperWidth );
            }

            if( 'structure_aframe_2story_attic_leanto' === _this._shedType ) {
                roofShingle02MatsUScale = helpers.toFixed3( depth / initialDepth );
                roofShingle02MatsVScale = 1;
            }
            if( 'structure_classic_aframe_dormer' === _this._shedType && _this._isEnableWallDormerSettings ) {
                const roofWidth = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_t1'].position, _this._bones['r_b_t1'].position ) );
                const roofDepth = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_t'].position, _this._bones['r_f_l1'].position ) );
                roofShingle02MatsUScale = helpers.toFixed3( roofWidth / _this.getCustomUVScale( 'roof_02_u_scale' ) );
                roofShingle02MatsVScale = helpers.toFixed3( roofDepth / _this.getCustomUVScale( 'roof_02_v_scale' ) );
            }

            if( 'structure_aframe_cabin' === _this._shedType ) {
                roofShingle02MatsUScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_t'].position, _this._bones['r_b_t'].position ) / _this._roofUVScale.roof02.u );
                roofShingle02MatsVScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_l2'].position, _this._bones['r_f_t'].position ) / _this._roofUVScale.roof02.v );
            }

            if( 'structure_porch_barn' === _this._shedType ) {
                roofShingle02MatsUScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_t'].position, _this._bones['r_b_t'].position ) / _this._roofUVScale.roof02.u );
                roofShingle02MatsVScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_r2'].position, _this._bones['r_f_r3'].position ) / _this._roofUVScale.roof02.v );
            }

            if( 'saltbox_customize' === roofShape ) {
                const roofWidth = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_t'].position, _this._bones['r_b_t'].position ) );
                const roofDepth = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_t'].position, _this._bones['r_f_r1'].position ) );
                
                roofShingle02MatsUScale = helpers.toFixed3( roofWidth / _this.getCustomUVScale( _this._roofShingle02MatKey + '_u_scale' ) );
                roofShingle02MatsVScale = helpers.toFixed3( roofDepth / _this.getCustomUVScale( _this._roofShingle02MatKey + '_v_scale' ) );
            }

            if( _this.isDoubleGable() && _this._bones['r_m_t2'] && _this._bones['r_m_t3'] ) {
                roofShingle02MatsVScale = 1;
                roofShingle02MatsUScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_m_t2'].position, _this._bones['r_m_t3'].position ) / _this.getCustomUVScale( 'roof_02_u_scale' ) );
            }

            if( _this.isAframeSingleGable() ) {
                const roofWidth = helpers.toFixed3( Vector3.Distance( _this._bones['r_m_t2'].position, _this._bones['r_m_t1'].position ) );
                roofShingle02MatsUScale = helpers.toFixed3( roofWidth / _this.getCustomUVScale( 'roof_02_u_scale' ) );
            }

            roofShingle02Mats.forEach( mat => {
                _this.updateUVScale( mat, roofShingle02MatsUScale, roofShingle02MatsVScale, initialUVs[_this._roofShingle02MatKey]['bumpTexture']['uScale'], initialUVs[_this._roofShingle02MatKey]['bumpTexture']['vScale'] );
            });
        }
        
        if( roofShingle03Mats ) {
            let roofShingle03MatsUScale = depth / initialDepth;
            let roofShingle03MatsVScale = 1;

            if( 'monitor' === roofShape ) {
                const roofDepth         = Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_f_l1'].position );
                roofShingle03MatsUScale = + ( roofDepth / initialDepth ).toFixed( 3 );
                roofShingle03MatsVScale = + ( Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_b_l2'].position ) / _this._initRoofLowerWidth ).toFixed( 3 );
            }

            if( 'structure_lakeside_bunkie_gambrel_roof' === _this._shedType ){
                const roofWidth         = Vector3.Distance( _this._bones['r_b_l2'].position, _this._bones['r_b_t'].position );
                const roofDepth         = Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_f_l1'].position );
                roofShingle03MatsVScale = +( roofWidth / _this._roofDetachedMats['roof_03'] ).toFixed( 3 );
                roofShingle03MatsUScale = +( roofDepth / initialDepth ).toFixed( 3 );
            }

            if( 'structure_ranch_cabin' === _this._shedType ) {
                roofShingle03MatsUScale = 1;
                roofShingle03MatsVScale = 1;
            }

            if( 'dutch_style' === roofShape ) {
                roofShingle03MatsUScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_b_t'].position, _this._bones['r_f_t'].position ) / initialDepth );
                roofShingle03MatsVScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_l1'].position, _this._bones['r_f_l2'].position ) / _this._initRoofLowerWidth );
            }

            if( this.is2StoryLeanTo() ) {
                roofShingle03MatsUScale = helpers.toFixed3( depth / initialDepth );
                roofShingle03MatsVScale = 1;
            }

            if( 'structure_aframe_cabin' === _this._shedType ) {
                roofShingle03MatsUScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_t'].position, _this._bones['r_b_t'].position ) / _this._roofUVScale.roof03.u );
                roofShingle03MatsVScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_l1'].position, _this._bones['r_f_l2'].position ) / _this._roofUVScale.roof03.v );
            }
            
            if( 'structure_classic_aframe_dormer' === _this._shedType && _this._isEnableWallDormerSettings ) {
                const roofWidth         = helpers.toFixed3( Vector3.Distance( _this._bones['r_b_t'].position, _this._bones['r_b_t1'].position ) );
                const roofDepth         = helpers.toFixed3( Vector3.Distance( _this._bones['r_b_t'].position, _this._bones['r_b_l1'].position ) );

                roofShingle03MatsUScale = helpers.toFixed3( ( roofWidth + _this.getGlobalData( 'roof_offset_number' ) ) / ( _this.getCustomUVScale( 'roof_03_u_scale' ) + _this.getGlobalData( 'roof_offset_number' ) ) );
                roofShingle03MatsVScale = helpers.toFixed3( roofDepth / _this.getCustomUVScale( 'roof_03_v_scale' ) );
            }

            if( 'structure_porch_barn' === _this._shedType ) {
                roofShingle03MatsUScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_t'].position, _this._bones['r_b_t'].position ) / _this._roofUVScale.roof03.u );
                roofShingle03MatsVScale = 1;
            }

            roofShingle03Mats.forEach( mat => {
                _this.updateUVScale( mat, roofShingle03MatsUScale, roofShingle03MatsVScale, initialUVs['roof_shingle_03_mat']['bumpTexture']['uScale'], initialUVs['roof_shingle_03_mat']['bumpTexture']['vScale'] );
            });
        }

        if( roofShingle04Mats ) {
            let roofShingle04MatsUScale = 1;
            let roofShingle04MatsVScale = 1;

            if( 'monitor' === roofShape ) {
                const roofDepth         = Vector3.Distance( _this._bones['r_b_r1'].position, _this._bones['r_f_r1'].position );
                roofShingle04MatsUScale = + ( roofDepth / initialDepth ).toFixed( 3 );
                roofShingle04MatsVScale = + ( Vector3.Distance( _this._bones['r_b_r1'].position, _this._bones['r_b_r2'].position ) / _this._initRoofLowerWidth ).toFixed( 3 );
            }

            if( 'structure_lakeside_bunkie_gambrel_roof' === _this._shedType ){
                const roofWidth         = Vector3.Distance( _this._bones['r_b_l2'].position, _this._bones['r_b_l1'].position );
                const roofDepth         = Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_f_l1'].position );
                roofShingle04MatsVScale = +( roofWidth / _this._roofDetachedMats['roof_04'] ).toFixed( 3 );
                roofShingle04MatsUScale = +( roofDepth / initialDepth ).toFixed( 3 );
            }

            if( 'structure_barn_2story_leanto' === _this._shedType ) {
                roofShingle04MatsUScale = helpers.toFixed3( depth / initialDepth );
                roofShingle04MatsVScale = 1;
            }

            if( 'structure_classic_aframe_dormer' === _this._shedType && _this._isEnableWallDormerSettings ) {
                const roofWidth         = Vector3.Distance( _this._bones['r_f_t'].position, _this._bones['r_b_t'].position );
                const roofDepth         = Vector3.Distance( _this._bones['r_b_t'].position, _this._bones['r_b_r1'].position );
                
                roofShingle04MatsUScale = helpers.toFixed3( ( roofWidth + _this.getGlobalData( 'roof_offset_number' ) ) / ( _this.getCustomUVScale( 'roof_04_u_scale' ) + _this.getGlobalData( 'roof_offset_number' ) ) );
                roofShingle04MatsVScale = helpers.toFixed3( roofDepth / _this.getCustomUVScale( 'roof_04_v_scale' ) );
            }

            if( 'structure_porch_barn' === _this._shedType ) {
                roofShingle04MatsUScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_t'].position, _this._bones['r_b_t'].position ) / _this._roofUVScale.roof04.u );
                roofShingle04MatsVScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_l1'].position, _this._bones['r_f_l2'].position ) / _this._roofUVScale.roof04.v );
            }

            roofShingle04Mats.forEach( mat => {
                _this.updateUVScale( mat, roofShingle04MatsUScale, roofShingle04MatsVScale, initialUVs['roof_shingle_04_mat']['bumpTexture']['uScale'], initialUVs['roof_shingle_04_mat']['bumpTexture']['vScale'] );
            });
        }

        if( roofShingle05Mats ) {
            let roofShingle05MatsUScale = 1;
            let roofShingle05MatsVScale = 1;
            if( 'structure_porch_barn' === _this._shedType ) {
                roofShingle05MatsUScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_t'].position, _this._bones['r_b_t'].position ) / _this._roofUVScale.roof05.u );
                roofShingle05MatsVScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_l2'].position, _this._bones['r_f_l3'].position ) / _this._roofUVScale.roof05.v );
            }

            if( 1 !== roofShingle05MatsUScale && 1 !== roofShingle05MatsVScale ) {
                roofShingle05Mats.forEach( mat => {
                    _this.updateUVScale( mat, roofShingle05MatsUScale, roofShingle05MatsVScale, initialUVs['roof_shingle_05_mat']['bumpTexture']['uScale'], initialUVs['roof_shingle_05_mat']['bumpTexture']['vScale'] );
                } );
            }
        }

        if( floorMaterials ){
            let floorUScale = (width / initialWidth);
            let floorVScale = _this._porchWidth / 4;

            if( _this.isExternalPorch() ){
                floorVScale = 0.5;

                if( _this._externalPorchLocation === 'left' ) {
                    floorUScale = helpers.toFixed3( depth / _this._initPorchWidth );
                } else {
                    floorUScale = helpers.toFixed3( width / _this._initPorchWidth );
                }
            }

            if( _this.isSidePorch() ){
                floorUScale = _this._porchWidth / 3; // 3 is default porch width
                floorVScale = depth / initialDepth;
            }

            if( _this.isCornerPorchLeft() ){
                floorUScale = width / initialWidth;
                floorVScale = depth / initialDepth;
            }

            if( 'structure_monitorbarns_helmuth' === _this._shedType ) {
                floorUScale = 1;
                floorVScale = 1;
            }

            if( 'structure_ranch_cabin' === _this._shedType ) {
                floorUScale = 1;
                floorVScale = helpers.toFixed3( ( depth - this._cabinWidth ) / ( initialDepth - this._cabinWidth ) );
            }

            if( _this.isSidePorchCabin() ) {
                if( _this.isRevertDecking() ) {
                    floorUScale = 1;
                    floorVScale = helpers.toFixed3( _this._porchWidth / 4 );
                } else {
                    floorUScale = helpers.toFixed3( depth / initialDepth );
                    floorVScale = 1;
                }
            }

            if( _this.isDoubleGable() ) {
                floorUScale = 1;
                floorVScale = 1;
                if( _this.isRevertDecking() ) {
                    const porchWidth = _this.doubleGableGetPorchWidth();
                    floorVScale = helpers.toFixed3( porchWidth / _this.getCustomUVScale( 'floor_mat_v_scale' ) );
                }
            }

            if( this.isAframeSingleGable() ) {
                floorUScale = 1;
                floorVScale = 1;
            }

            floorMaterials.forEach(mat => {
                if( _this.isRevertDecking() ){
                    _this.updateUVScale( mat, floorUScale, floorVScale, initialUVs['floor_mat']['bumpTexture']['vScale'], initialUVs['floor_mat']['bumpTexture']['uScale'] );
                }else{
                    _this.updateUVScale( mat, floorUScale, floorVScale, initialUVs['floor_mat']['bumpTexture']['uScale'], initialUVs['floor_mat']['bumpTexture']['vScale'] );
                }
            });
        }

        if( floorInner01Mat ){
            let floorUScale = 'structure_barn_middle_porch' === this._shedType ?  width / initialWidth : ( width - constants.cornerWidth ) / ( initialWidth - constants.cornerWidth );
            let floorVScale = 1;

            floorInner01Mat.forEach(mat => {
                _this.updateUVScale( mat, floorUScale, floorVScale, initialUVs['floor_inner_01_mat']['bumpTexture']['uScale'], initialUVs['floor_inner_01_mat']['bumpTexture']['vScale'] );
            });
        }

        if( floorInner02Mat ){
            let floorUScale = 'structure_barn_middle_porch' === this._shedType ?  ( width - constants.middleDepth ) / ( initialWidth - constants.middleDepth ) : ( width / initialWidth );
            let floorVScale = 1;

            floorInner02Mat.forEach(mat => {
                _this.updateUVScale( mat, floorUScale, floorVScale, initialUVs['floor_inner_02_mat']['bumpTexture']['uScale'], initialUVs['floor_inner_02_mat']['bumpTexture']['vScale'] );
            });
        }

        if( floorPorchMats ) {
            let floorUScale = width / initialWidth;
            if( ( _this.isExternalPorch() && _this._externalPorchLocation === 'left' ) || _this.isSidePorch() ) {
                floorUScale = depth / initialDepth;
            }

            floorPorchMats.forEach( mat => {
                _this.updateUVScale( mat, floorUScale, 1, initialUVs['floor_porch_mat']['bumpTexture']['uScale'], initialUVs['floor_porch_mat']['bumpTexture']['vScale'] );
            });
        }

        if( trimMaterials && 'structure_aframe_metal_frontporch' === _this._shedType ){
            trimMaterials.forEach( trimMaterial => {
                trimMaterial.diffuseColor = Color3.FromHexString( '#D9BF86' );
            })
        }

        if( sidingFloorMaterials && 'structure_aframe_metal_frontporch' === _this._shedType ){
            sidingFloorMaterials.forEach( sidingFloorMaterial => {
                sidingFloorMaterial.diffuseColor = Color3.FromHexString( '#D9BF86' );
            })
        }

        if( sidingPorchMaterials ){
            let sidingPorchUScale = (width / initialWidth);
            let sidingPorchVScale = (roofPitchHeight / initialRoofPitchHeight);

            sidingPorchMaterials.forEach(mat => {
                _this.updateUVScale( mat, sidingPorchUScale, sidingPorchVScale );
            });
        }

        if( ceilingPorchMetalMat ){
            let uScale = (_this._porchWidth / 4);
            let vScale = (width / initialWidth);

            ceilingPorchMetalMat.forEach(mat => {
                _this.updateUVScale( mat, uScale, vScale, initialUVs['ceiling_porch_metal_mat']['bumpTexture']['uScale'], initialUVs['ceiling_porch_metal_mat']['bumpTexture']['vScale'] );
            });
        }

        if( roofMetalCellingMat ){
            let uScale = (depth / initialDepth);
            let vScale = 1;

            roofMetalCellingMat.forEach(mat => {
                _this.updateUVScale( mat, uScale, vScale, initialUVs['ceiling_porch_metal_mat']['bumpTexture']['uScale'], initialUVs['ceiling_porch_metal_mat']['bumpTexture']['vScale'] );
            });
        }
        
        if( sidingExtraTopMaterials ){

            sidingExtraTopMaterials.forEach( mat => {
                _this.updateUVScale( mat, 1, 1 );
            });
        }

        if( floor02Mat ) {
            let floor02MatUScale = helpers.toFixed3( ( width - 7 ) / 3 );

            floor02Mat.forEach( mat => {
                _this.updateUVScale( mat, floor02MatUScale, 1, initialUVs['second_floor_02_mat']['bumpTexture']['uScale'], initialUVs['second_floor_02_mat']['bumpTexture']['vScale'] );
            } );
        }

        if( sidingFBMaterials ){
            let sidingFBUScale = (width / initialWidth);
            let sidingFBVScale = (wallHeight / initialWallHeight);

            if( 'mainus_quaker' === _this._shedType ) {
                sidingFBUScale = ( width - 2.414 ) / 3.586; // 3.586 is initial width of the siding_roof_top_mat material, 2.414 is the rest of the width
            }

            if( 'structure_saltbox_mwshed' === _this._shedType ){
                sidingFBUScale = ( width - 4.133 ) / 1.867; // 4.133 is B_B_R4, 1.865 is B_F_L2
                sidingFBVScale = 1;
            }

            if( _this.isSidePorch() ){
                sidingFBUScale = ( width - _this._porchWidth ) / ( initialWidth - 3 );
            }

            if( 'structure_greenhouse_rockwood' === _this._shedType ) {
                sidingFBVScale = 1;
            }

            if( 'structure_monitorbarns_helmuth' === _this._shedType ) {
                sidingFBUScale = 1;
                sidingFBVScale = 1;
            }

            if( 'structure_chalet_genesis' === _this._shedType ) {
                sidingFBUScale = 1;
                sidingFBVScale = buildingHeight;
            }

            sidingFBMaterials.forEach(mat => {
                _this.updateUVScale( mat, sidingFBUScale, sidingFBVScale, _this._sidingUscale, _this._sidingVscale );
            });
        }
        
        if( sidingExtraLowMaterials ){
            let sidingFBVScale = (wallHeight / initialWallHeight);

            sidingExtraLowMaterials.forEach(mat => {
                _this.updateUVScale( mat, 1, sidingFBVScale );
            });
        }

        if( sidingRoofLowMaterials ){
            let sidingFBUScale = (width / initialWidth);
            sidingRoofLowMaterials.forEach(mat => {                
                _this.updateUVScale( mat, sidingFBUScale, 1 );
            });
        }

        if( sidingPorchFBMaterials ){
            let sidingFBUScale = (width / initialWidth);
            let sidingFBVScale = (wallHeight / initialWallHeight);

            if( _this.isExternalPorch() && _this._externalPorchLocation === 'left' ){
                sidingFBUScale = (depth / initialDepth);
            }

            if( _this.isSidePorch() ){
                sidingFBUScale = _this._porchWidth / 3; // 4 is default design porch model
            }

            if( _this.isFrontPorch() ){
                sidingFBVScale = 1;
            }

            if( 'structure_cardinal_victorian' === _this._shedType ){
                sidingFBUScale = ( width - 6.4 ) / ( initialWidth - 6.4 );
            }

            if( 'structure_cardinal_cabin' === _this._shedType ){
                sidingFBUScale = ( width - 2.74 ) / ( initialWidth - 2.74 );
            }

            if( 'structure_barn_deluxe_porch' === _this._shedType ){
                sidingFBUScale = width / initialWidth;
                sidingFBVScale = ( _this._bones['b_p_l5'].position.y - _this._bones['b_p_l2'].position.y ) / 1.982;
            }

            sidingPorchFBMaterials.forEach(mat => {
                _this.updateUVScale( mat, sidingFBUScale, sidingFBVScale );
            });
        }

        if( sidingPorchRoofTopMaterials ){
            let sidingFBUScale = (width - 6.4) / (initialWidth - 6.4);
            let sidingFBVScale = (roofPitchHeight - 2.25) / (initialRoofPitchHeight - 2.25);
            
            if( 'structure_cardinal_cabin' === _this._shedType ){
                sidingFBUScale = (width - 2.74) / (initialWidth - 2.74);
                sidingFBVScale = (roofPitchHeight - 0.65) / (initialRoofPitchHeight - 0.65);
            }

            sidingPorchRoofTopMaterials.forEach(mat => {
                _this.updateUVScale( mat, sidingFBUScale, sidingFBVScale );
            });
        }

        if( sidingPorchLRMaterials ) {
            let sidingLRPorchVScale = 1;
            let sidingLRPorchUScale = 1;

            if( _this.isFrontPorch() ){
                sidingLRPorchUScale = _this._porchWidth / 4;
            }

            if( _this.isSidePorch() ){
                sidingLRPorchUScale = depth / initialDepth;
            }

            if( 'structure_cardinal_victorian' === _this._shedType ){
                sidingLRPorchUScale = 1;
            }

            sidingPorchLRMaterials.forEach( mat => {
                _this.updateUVScale( mat, sidingLRPorchUScale, sidingLRPorchVScale );
            });
        }

        if( sidingPorchLMaterials ){
            let uScale = 1;
            let vScale = 1;

            sidingPorchLMaterials.forEach( mat => {
                _this.updateUVScale( mat, uScale, vScale );
            });            
        }

        if( sidingPorchRMaterials ){
            let uScale = 1;
            let vScale = 1;

            let sidingFBUScale = ( ( width - 6.4 ) / ( initialWidth - 6.4 ) ) * this._sidingUscale;
            let uOffset = ( ( sidingFBUScale / ( 3 / 8 ) ) - Math.floor( sidingFBUScale / ( 3 / 8 ) ) ) * 0.25; // ( 3/8 ) is default uv scale div default parten

            if( 'structure_cardinal_cabin' === _this._shedType ){
                sidingFBUScale = ( ( width - 2.74 ) / ( initialWidth - 2.74 ) ) * this._sidingUscale;
                uOffset = ( ( ( sidingFBUScale / ( 3 / 13.5 ) ) - Math.floor( sidingFBUScale / ( 3 / 13.5 ) ) ) * 0.25 ) - 0.125; // ( 3/13.5 ) is default uv scale div default parten
            }

            if( 'vertical_metal' === this._sidingType ){
                uOffset = ( ( sidingFBUScale / ( 3 / 6 ) ) - Math.floor( sidingFBUScale / ( 3 / 6 ) ) ) * 1/3;

                if( 'structure_cardinal_cabin' === _this._shedType ){
                    uOffset = ( ( ( sidingFBUScale / ( 3 / 10 ) ) - Math.floor( sidingFBUScale / ( 3 / 10 ) ) ) * 1/3 );
                }
            }

            sidingPorchRMaterials.forEach( mat => {
                _this.updateUVScale( mat, uScale, vScale );
                if( mat.bumpTexture ) mat.bumpTexture['uOffset'] = uOffset;
                if( mat.diffuseTexture ) mat.diffuseTexture['uOffset'] = uOffset;
                if( mat.specularTexture ) mat.specularTexture['uOffset'] = uOffset;
                if( mat.opacityTexture ) mat.opacityTexture['uOffset'] = uOffset;
            });            
        }

        if (sidingInnerKickboardFBMaterial != null) {
            let sidingFBUScale = (width / initialWidth);
            let sidingFBVScale = (wallHeight / initialWallHeight);

            if( 'horse_run_in' === _this._shedType ) {
                sidingFBVScale = 1;
            }

            _this.updateUVScale( sidingInnerKickboardFBMaterial, sidingFBUScale, sidingFBVScale, initialUVs['siding_inner_kickboard_fb_mat']['bumpTexture']['uScale'], initialUVs['siding_inner_kickboard_fb_mat']['bumpTexture']['vScale'] );
        }
        
        if (sidingInnerKickboardLRMaterial != null) {
            let sidingLRUScale = (depth / initialDepth);
            let sidingLRVScale = (wallHeight / initialWallHeight);

            if( 'horse_run_in' === _this._shedType ) {
                sidingLRVScale = 1;
            }

            _this.updateUVScale( sidingInnerKickboardLRMaterial, sidingLRUScale, sidingLRVScale, initialUVs['siding_inner_kickboard_lr_mat']['bumpTexture']['uScale'], initialUVs['siding_inner_kickboard_lr_mat']['bumpTexture']['vScale'] );
        }

        if (sidingInnerLRMaterial != null) {
            let sidingLRUScale = (depth / initialDepth);
            let sidingLRVScale = (wallHeight / initialWallHeight);

            _this.updateUVScale( sidingInnerLRMaterial, sidingLRUScale, sidingLRVScale );
        }

        if( sidingLRMaterials ){
            let sidingLRUScale = (depth / initialDepth);
            let sidingLRVScale = (wallHeight / initialWallHeight);

            if( 'structure_saltbox_mwshed' === _this._shedType || 'structure_greenhouse_rockwood' === _this._shedType ) {
                sidingLRVScale = 1;
            } else if( 'structure_dog_kennel' === _this._shedType ) {
                sidingLRUScale = _this.getDogKennelBoxDepth() / _this._initDogKennelBoxDepth;
            }

            if( 'structure_barn_smithbilt' === _this._shedType ) {
                sidingLRVScale = ( Vector3.Distance( _this._bones['b_b_l1'].position, _this._bones['b_b_l2'].position ) - _this._smithbiltBarnRoofFix ) / ( initialWallHeight - _this._smithbiltBarnRoofFix );
            }

            if( 'structure_monitorbarns_helmuth' === _this._shedType ) {
                sidingLRUScale = depth / initialDepth;
                sidingLRVScale = 1;
            }

            sidingLRMaterials.forEach( mat => {
                _this.updateUVScale( mat, sidingLRUScale, sidingLRVScale );
            })
        }

        if( sidingDormerRoofTopMaterials ){
            let sidingDormerRoofTopUScale = (_this._dormerWidth / _this._initDormerWidth);
            let sidingDormerRoofTopVScale = (roofPitchHeight) / (initialRoofPitchHeight);
            
            sidingDormerRoofTopMaterials.forEach( mat => {
                _this.updateUVScale( mat, sidingDormerRoofTopUScale, sidingDormerRoofTopVScale );
            })
        }

        if( sidingDormerMaterials ){
            let sidingDormerUScale = (_this._dormerWidth / _this._initDormerWidth);
            let sidingDormerVScale = (wallHeight / initialWallHeight);

            sidingDormerMaterials.forEach( mat => {
                _this.updateUVScale( mat, sidingDormerUScale, sidingDormerVScale );
            })
        }

        if( 'structure_country_cottage' === _this._shedType && initialUVs[_this._roofShingle01MatKey] ) {
            let roofShingleMatsUScale = initialUVs[_this._roofShingle01MatKey]['bumpTexture']['uScale'];
            let roofShingleMatsVScale = initialUVs[_this._roofShingle01MatKey]['bumpTexture']['vScale'];

            if( roofShingle01Mats ) {
                let roofShingle01MatsUScale = ( ( _this._bones['r_f_l1'].position.z - _this._bones['r_d_l1'].position.z ) / 3 );
                let roofShingle01MatsVScale = ( ( _this._bones['r_d_l3'].position.y - _this._bones['r_f_l1'].position.y ) / 3.417 );

                roofShingle01Mats.forEach( mat => {
                    _this.updateUVScale( mat, roofShingle01MatsUScale, roofShingle01MatsVScale, roofShingleMatsUScale, roofShingleMatsVScale );
                });
            }

            if( roofShingle02Mats ) {
                let roofShingle02MatsUScale = ( ( _this._bones['r_d_l6'].position.z - _this._bones['r_d_l3'].position.z ) / 1.6 );
                let roofShingle02MatsVScale = ( ( _this._bones['r_d_l3'].position.y - _this._bones['r_f_l1'].position.y ) / 3.417 );

                roofShingle02Mats.forEach( mat => {
                    _this.updateUVScale( mat, roofShingle02MatsUScale, roofShingle02MatsVScale, roofShingleMatsUScale, roofShingleMatsVScale );
                });
            }

            if( roofShingle03Mats ) {
                let roofShingle03MatsUScale = ( width / 2 ) / ( initialWidth / 2 );
                let roofShingle03MatsVScale = width / initialWidth;
            
                roofShingle03Mats.forEach( mat => {
                    _this.updateUVScale( mat, roofShingle03MatsUScale, roofShingle03MatsVScale, roofShingleMatsUScale, roofShingleMatsVScale );
                });
            }

            if( roofShingle05Mats ) {
                let roofShingle05MatsUScale = ( ( _this._bones['r_f_l1'].position.z - _this._bones['r_d_l1'].position.z ) / 3 );

                roofShingle05Mats.forEach( mat => {
                    _this.updateUVScale( mat, roofShingle05MatsUScale, false, roofShingleMatsUScale, roofShingleMatsVScale );
                });
            }

            if( roofShingle06Mats ) {
                let roofShingle06MatsUScale = ( depth / initialDepth );

                roofShingle06Mats.forEach( mat => {
                    _this.updateUVScale( mat, roofShingle06MatsUScale, false, roofShingleMatsUScale, roofShingleMatsVScale );
                });
            }

        }
        if( sidingDormerLMaterials ){
            let sidingDormerHalf = (depth - _this._dormerWidth) / 2;
            let initsidingDormerHalf = (initialDepth - _this._initDormerWidth) / 2;
            let sidingDormerLUScale = (sidingDormerHalf / initsidingDormerHalf);
            let sidingDormerLVScale = (wallHeight / initialWallHeight);

            if( 'structure_country_cottage' === _this._shedType ) {
                sidingDormerHalf        = ( depth - width ) / 2;
                initsidingDormerHalf    = ( initialDepth - initialWidth ) / 2;
                sidingDormerLUScale     = sidingDormerHalf / initsidingDormerHalf;
            }


            sidingDormerLMaterials.forEach( mat => {
                _this.updateUVScale( mat, sidingDormerLUScale, sidingDormerLVScale );
            })
        }

        if( sidingDormerRMaterials ){
            let sidingDormerHalf = (depth - _this._dormerWidth) / 2;
            let initsidingDormerHalf = (initialDepth - _this._initDormerWidth ) / 2;
            
            let sidingDormerURScale = (sidingDormerHalf / initsidingDormerHalf);
            let sidingDormerVRScale = (wallHeight / initialWallHeight);

            if( 'structure_country_cottage' === _this._shedType ) {
                sidingDormerHalf        = ( depth - width ) / 2;
                initsidingDormerHalf    = ( initialDepth - initialWidth ) / 2;
                sidingDormerURScale     = sidingDormerHalf / initsidingDormerHalf;
            }

            sidingDormerRMaterials.forEach( mat => {
                _this.updateUVScale( mat, sidingDormerURScale, sidingDormerVRScale );
            })
        }

        if( null !== sidingRMaterial ) {
            let sidingRUScale = (depth / initialDepth);
            let sidingRVScale = (wallHeight / initialWallHeight);

            _this.updateUVScale( sidingRMaterial, sidingRUScale, sidingRVScale );
        }

        if( null !== sidingLMaterial ) {
            let sidingLUScale = depth / initialDepth;
            let sidingLVScale = ( roofPitchHeight + wallHeight ) / ( initialWallHeight + 1 );

            _this.updateUVScale( sidingLMaterial, sidingLUScale, sidingLVScale );
        }

        if ( sidingOpeningMaterial ) {
            let openingWidth = 6;
            if( "double_opening" === this.getActiveLayout() ) {
                openingWidth = 14;
            } else if( "triple_opening" === this.getActiveLayout() ) {
                openingWidth = 22;
            }

            let sidingOpeningUScale = ( depth - openingWidth ) / ( initialDepth - 6 );
            let sidingOpeningVScale = ( wallHeight / initialWallHeight );
            
            sidingOpeningMaterial.forEach( mat => {
                _this.updateUVScale( mat, sidingOpeningUScale, sidingOpeningVScale, 3, 3 );
            })
        }

        if (sidingRoofMaterial != null) {
            let sidingRoofUScale = (width / initialWidth);
            let sidingRoofVScale = ((buildingHeight - wallHeight - roofPitchHeight) / (initialBuildingHeight - initialWallHeight - initialRoofPitchHeight));

            if( 'structure_alpinechalet' == _this._shedType ) {
                sidingRoofUScale = depth / initialDepth;
                sidingRoofVScale = ( _this._bones['b_f_t2'].position.y - _this._bones['b_f_t1'].position.y ) / 1.389;
            }
            
            _this.updateUVScale( sidingRoofMaterial, sidingRoofUScale, sidingRoofVScale );
        }

        if( sidingRoofTopMaterials ){
            if( 'structure_barn_with_sprocket' === _this._shedType || 'structure_barn_with_sprocket_wood_siding' === _this._shedType ) {
                roofPitchHeight -= 0.125;
            } 

            let sidingRoofTopUScale = (width / initialWidth);
            let sidingRoofTopVScale = (roofPitchHeight / initialRoofPitchHeight);

            if( 'mainus_quaker' === _this._shedType ) {
                sidingRoofTopUScale = ( width - 2.414 ) / 3.586; // 3.586 is initial width of the siding_roof_top_mat material, 2.414 is the rest of the width
            }

            if( 'structure_saltbox_mwshed' === _this._shedType ){
                sidingRoofTopUScale = ( width - 4.133 ) / 1.867; // 4.133 is B_B_R4, 1.865 is B_F_L2
                sidingRoofTopVScale = Math.abs( _this._bones['b_b_t'].position.x ) / 0.9333; // 0.9333 is top roof
            }
            
            if( 'structure_quaker_silvercreek' === _this._shedType ){
                sidingRoofTopVScale = ( _this._bones['b_b_t'].position.y - _this._bones['b_b_l2'].position.y ) / _this._initRoofSaltboxHeight;
            }

            if( 'structure_log_cabin' === _this._shedType ) {
                sidingRoofTopVScale = ( _this._bones['b_b_t'].position.y - _this._bones['b_b_r2'].position.y ) / 1.823;
                sidingRoofTopUScale = width / initialWidth;
            }

            if( 'structure_aframe_genesis' === _this._shedType ) {
                sidingRoofTopVScale = ( _this._bones['b_b_t'].position.y - _this._bones['b_b_r1'].position.y ) / 9;
                sidingRoofTopUScale = width / initialWidth;
            }

            sidingRoofTopMaterials.forEach(mat => {
                _this.updateUVScale( mat, sidingRoofTopUScale, sidingRoofTopVScale );
            })
        }

        if( sidingTopPorchMaterials ) {
            let sidingTopPorchUScale = 1;
            let sidingTopPorchVScale = 1;
            if( _this._bones['b_f_t'] && _this._bones['b_f_p2'] ) {
                sidingTopPorchUScale = helpers.toFixed3( Vector3.Distance( _this._bones['b_f_t'].position, _this._bones['b_f_p2'].position ) / _this.getCustomUVScale( 'siding_top_porch_mat_u_scale' ) );
            }

            if( 'standard_cottage_front_porch' === _this._shedType ) {
                sidingTopPorchVScale = helpers.toFixed3( Vector3.Distance( _this._bones['b_f_t'].position, _this._bones['b_f_l2'].position ) / _this._initBoxGableWidth );
                sidingTopPorchUScale = helpers.toFixed3( _this._porchWidth / 3 );
            }

            sidingTopPorchMaterials.forEach( mat => {
                _this.updateUVScale( mat, sidingTopPorchUScale, sidingTopPorchVScale, initialUVs['siding_top_porch_mat']['bumpTexture']['uScale'], initialUVs['siding_top_porch_mat']['bumpTexture']['vScale'] );
            } );
        }

        if( sidingFBPorchMaterials ) {
            let sidingFBPorchUScale = 1;
            let sidingFBPorchVScale = 1;
            if( _this._bones['b_f_p1'] && _this._bones['b_f_p2'] ) {
                sidingFBPorchUScale = helpers.toFixed3( Vector3.Distance( _this._bones['b_f_p1'].position, _this._bones['b_f_p2'].position ) / _this.getCustomUVScale( 'siding_fb_porch_mat_u_scale' ) );
            }

            sidingFBPorchMaterials.forEach( mat => {
                _this.updateUVScale( mat, sidingFBPorchUScale, sidingFBPorchVScale, initialUVs['siding_fb_porch_mat']['bumpTexture']['uScale'], initialUVs['siding_fb_porch_mat']['bumpTexture']['vScale'] );
            } );
        }

        if( sidingLRPorchMaterials ) {
            let sidingLRPorchUScale = helpers.toFixed3( _this._porchWidth / 3 );
            let sidingLRPorchVScale = helpers.toFixed3( wallHeight / initialWallHeight );
            sidingLRPorchMaterials.forEach( mat => {
                _this.updateUVScale( mat, sidingLRPorchUScale, sidingLRPorchVScale, initialUVs['siding_lr_porch_mat']['bumpTexture']['uScale'], initialUVs['siding_lr_porch_mat']['bumpTexture']['vScale'] );
            } );
        }

        if( sidingRoofPorchMaterials ) {
            let sidingRoofPorchUScale = 1;
            let sidingRoofPorchVScale = 1;
            if( _this._bones['b_f_t'] && _this._bones['b_b_t'] ) {
                sidingRoofPorchUScale = helpers.toFixed3( Vector3.Distance( _this._bones['b_f_t'].position, _this._bones['b_b_t'].position ) / _this.getCustomUVScale( 'siding_roof_porch_mat_u_scale' ) );
            }

            sidingRoofPorchMaterials.forEach( mat => {
                _this.updateUVScale( mat, sidingRoofPorchUScale, sidingRoofPorchVScale, initialUVs['siding_roof_porch_mat']['bumpTexture']['uScale'], initialUVs['siding_roof_porch_mat']['bumpTexture']['vScale'] );
            } );
        }

        if( sidingRoofMidMaterials ) {
            if( 'structure_barn_with_sprocket' === _this._shedType || 'structure_barn_with_sprocket_wood_siding' === _this._shedType ) {
                roofMidPitchHeight += 0.125;
            }

            let sidingRoofMidUScale = (width / initialWidth);
            let sidingRoofMidVScale = (roofMidPitchHeight / initialMidRoofPitchHeight);

            if( 'structure_greenhouse_rockwood' === _this._shedType ) {
                let wallLRMat       = 42 / 12;
                sidingRoofMidVScale = ( wallHeight - wallLRMat ) / ( initialWallHeight - wallLRMat );
            }

            sidingRoofMidMaterials.forEach( mat => {
                _this.updateUVScale( mat, sidingRoofMidUScale, sidingRoofMidVScale );
            } )
        }        

        if (sidingExtraMaterial != null) {
            let sidingExtraUScale = 1;
            let sidingExtraVScale = (wallHeight / initialWallHeight);

            if( 'structure_saltbox_mwshed' === _this._shedType ){
                sidingExtraVScale = 1;
            }            

            if( 'structure_quaker_wannabuysheds' === _this._shedType || 'structure_horse_shelter' === _this._shedType || this.isSidePorch() ) {
                sidingExtraUScale = depth / initialDepth;
            }
            
            _this.updateUVScale( sidingExtraMaterial, sidingExtraUScale, sidingExtraVScale );
        }

        if (doorSidingMaterial != null) {
            let doorSidingUScale = 1;
            let doorSidingVScale = 1;

            _this.updateUVScale( doorSidingMaterial, doorSidingUScale, doorSidingVScale, initialUVs['door_siding_mat']['bumpTexture']['uScale'], initialUVs['door_siding_mat']['bumpTexture']['vScale'] );
        }

        if (metalWireSidingMaterial != null) {
            let metalWireSidingUScale = (depth / initialDepth);
            let metalWireSidingVScale = (wallHeight / initialWallHeight);
            
            _this.updateUVScale( metalWireSidingMaterial, metalWireSidingUScale, metalWireSidingVScale );
        }

        if ( roofShingleMaterial ) {
            let roofShingleUScale = + ( Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_f_l1'].position ) / initialDepth ).toFixed( 3 );
            if( 0 !== _this._initRoofWidth ) {
                roofShingleUScale = + ( Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_f_l1'].position ) / _this._initRoofWidth ).toFixed( 3 );
            }
            let roofShingleVScale = + ( Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_b_r1'].position ) / initialWidth ).toFixed( 3 );
            if( 'flat' === roofShape ) {
                roofShingleVScale = + ( Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_b_r1'].position ) / _this._initFlatRoofWidth ).toFixed( 3 );
            } else if( 'box_gable' === roofShape || 'open_gable' === roofShape ) {
                roofShingleVScale = + ( Vector3.Distance( _this._bones['r_b_t'].position, _this._bones['r_b_l1'].position ) / _this._initBoxGableWidth ).toFixed( 3 );
            }
            
            if( 'hip' === roofShape ) {
                roofShingleUScale = _this._hipRoofTopLength / _this._initHipRoofTopLength;
            }

            if( 'structure_monitorbarns_helmuth' === _this._shedType ) {
                roofShingleVScale = 1;
            }

            if( 'structure_lean_to_with_front_porch' === _this._shedType && _this._bones['b_f_t'] && _this._bones['b_b_t'] && _this._bones['b_f_r2'] ) {
                roofShingleUScale = helpers.toFixed3( Vector3.Distance( _this._bones['b_f_t'].position, _this._bones['b_b_t'].position ) / _this.getCustomUVScale( 'roof_shingle_mat_u_scale' ) );
                roofShingleVScale = helpers.toFixed3( Vector3.Distance( _this._bones['b_f_t'].position, _this._bones['b_f_r2'].position ) / _this.getCustomUVScale( 'roof_shingle_mat_v_scale' ) );
            }

            _this.updateUVScale( roofShingleMaterial, roofShingleUScale, roofShingleVScale, initialUVs['roof_shingle_mat']['bumpTexture']['uScale'], initialUVs['roof_shingle_mat']['bumpTexture']['vScale'] );
        }

        if( roofPorchShingleMaterial ) {
            let roofRoofShingleUScale = 1;
            if( _this._bones['porch_b_l3'] && _this._bones['porch_b_r3'] ) {
                roofRoofShingleUScale = helpers.toFixed3( Vector3.Distance( _this._bones['porch_b_l3'].position, _this._bones['porch_b_r3'].position ) / _this.getCustomUVScale( 'external_porch_roof_u_scale' ) );
            }

            _this.updateUVScale( roofPorchShingleMaterial, roofRoofShingleUScale, 1, initialUVs['roof_porch_shingle_mat']['bumpTexture']['uScale'], initialUVs['roof_porch_shingle_mat']['bumpTexture']['vScale'] );
        }

        //UVscale for hip roof shed
        if (roofShingleLRMaterial != null) {
            const roofLRLength          = Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_f_l1'].position ) - _this._hipRoofTopLength;
            const initRoofLRLength      = initialDepth - _this._initHipRoofTopLength;
            const roofShingleLRUScale   = roofLRLength / initRoofLRLength;
            const roofShingleLRVScale   = Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_b_r1'].position ) / initialWidth;

            _this.updateUVScale( roofShingleLRMaterial, roofShingleLRUScale, roofShingleLRVScale, initialUVs['roof_shingle_lr_mat']['bumpTexture']['uScale'], initialUVs['roof_shingle_lr_mat']['bumpTexture']['vScale'] );
        }

        if (roofShingleFBMaterial != null) {
            const roofFBLength          = Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_f_l1'].position ) - _this._hipRoofTopLength;
            const initRoofFBLength      = initialDepth - _this._initHipRoofTopLength;
            const roofShingleFBUScale   = + ( Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_b_r1'].position ) / initialWidth ).toFixed( 3 );
            const roofShingleFBVScale   = + ( roofFBLength / initRoofFBLength ).toFixed( 3 );

            _this.updateUVScale( roofShingleFBMaterial, roofShingleFBUScale, roofShingleFBVScale, initialUVs['roof_shingle_fb_mat']['bumpTexture']['uScale'], initialUVs['roof_shingle_fb_mat']['bumpTexture']['vScale'] );
        }
        
        //#UVscale for hip roof shed
        const roofType  = jQuery(`#js-shed-data [name="ssb-option-roof-type"]`).attr('data-key');
        let roofMetalLapMaterials = this.getMaterialByName("roof_metal_mat");
        if ( 'metal_lap' == roofType && roofMetalLapMaterials) {
            let roofMetalLapVScale = width / initialWidth;
            if( _this._bones['r_b_l1'] && _this._bones['r_b_r1'] ) {
                roofMetalLapVScale = + ( Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_b_r1'].position ) / initialWidth ).toFixed( 3 );
            }

            roofMetalLapMaterials.forEach( roofMetalLapMaterial => {
                _this.updateUVScale( roofMetalLapMaterial, 1, roofMetalLapVScale, 1 );
            });
        }

        //UVscale for dormer shed
        if( roofShingleRMaterial != null ){
            let roofShingleUScale = (depth / initialDepth);
            let roofShingleVScale = (width / initialWidth);
            
            roofShingleRMaterial.bumpTexture['uScale'] = roofShingleUScale * initialUVs['roof_shingle_r_mat']['bumpTexture']['uScale'];
            roofShingleRMaterial.diffuseTexture['uScale'] = roofShingleUScale * initialUVs['roof_shingle_r_mat']['diffuseTexture']['uScale'];
            if( undefined !== roofShingleRMaterial.specularTexture && undefined !== initialUVs['roof_shingle_r_mat']['specularTexture'] ) {
                roofShingleRMaterial.specularTexture['uScale'] = roofShingleUScale * initialUVs['roof_shingle_r_mat']['specularTexture']['uScale'];
            }

            roofShingleRMaterial.bumpTexture['vScale'] = roofShingleVScale * initialUVs['roof_shingle_r_mat']['bumpTexture']['vScale'];
            roofShingleRMaterial.diffuseTexture['vScale'] = roofShingleVScale * initialUVs['roof_shingle_r_mat']['diffuseTexture']['vScale'];
            if( undefined !== roofShingleRMaterial.specularTexture && undefined !== initialUVs['roof_shingle_r_mat']['specularTexture'] ) {
                roofShingleRMaterial.specularTexture['vScale'] = roofShingleVScale * initialUVs['roof_shingle_r_mat']['specularTexture']['vScale'];
            }
        }

        if( roofShingleMMaterial != null ){
            let depth = _this._bones['r_f_t1'].position.z * 2;
            let roofShingleUScale = (depth / 6.66);
            let roofShingleVScale = (width / initialWidth);

            roofShingleMMaterial.bumpTexture['uScale'] = roofShingleUScale * initialUVs['roof_shingle_m_mat']['bumpTexture']['uScale'];
            roofShingleMMaterial.diffuseTexture['uScale'] = roofShingleUScale * initialUVs['roof_shingle_m_mat']['diffuseTexture']['uScale'];
            if( undefined !== roofShingleMMaterial.specularTexture && undefined !== initialUVs['roof_shingle_m_mat']['specularTexture'] ) {
                roofShingleMMaterial.specularTexture['uScale'] = roofShingleUScale * initialUVs['roof_shingle_m_mat']['specularTexture']['uScale'];
            }

            roofShingleMMaterial.bumpTexture['vScale'] = roofShingleVScale * initialUVs['roof_shingle_m_mat']['bumpTexture']['vScale'];
            roofShingleMMaterial.diffuseTexture['vScale'] = roofShingleVScale * initialUVs['roof_shingle_m_mat']['diffuseTexture']['vScale'];
            if( undefined !== roofShingleMMaterial.specularTexture && undefined !== initialUVs['roof_shingle_m_mat']['specularTexture'] ) {
                roofShingleMMaterial.specularTexture['vScale'] = roofShingleVScale * initialUVs['roof_shingle_m_mat']['specularTexture']['vScale'];
            }
        }

        if( roofShingleDormerMaterial != null ){
            let roofShingleUScale = (width / initialWidth);
            let roofShingleVScale = (width / initialWidth);
            if( 'structure_classic_aframe_dormer' === _this._shedType && _this._isEnableWallDormerSettings ) {
                const depth         = helpers.toFixed3( Vector3.Distance( _this._bones['r_d_t1'].position, _this._bones['r_d_t2'].position ) );
                const width         = helpers.toFixed3( Vector3.Distance( _this._bones['r_d_l1'].position, _this._bones['r_d_t2'].position ) * 2 );
                roofShingleUScale   = helpers.toFixed3( depth / _this.getCustomUVScale( 'roof_05_u_scale' ) );
                roofShingleVScale   = helpers.toFixed3( width / _this.getCustomUVScale( 'roof_05_v_scale' ) );
            }
            
            roofShingleDormerMaterial.bumpTexture['uScale'] = roofShingleUScale * initialUVs['roof_shingle_dormer_mat']['bumpTexture']['uScale'];
            roofShingleDormerMaterial.diffuseTexture['uScale'] = roofShingleUScale * initialUVs['roof_shingle_dormer_mat']['diffuseTexture']['uScale'];
            if( undefined !== roofShingleDormerMaterial.specularTexture && undefined !== initialUVs['roof_shingle_dormer_mat']['specularTexture'] ) {
                roofShingleDormerMaterial.specularTexture['uScale'] = roofShingleUScale * initialUVs['roof_shingle_dormer_mat']['specularTexture']['uScale'];
            }

            roofShingleDormerMaterial.bumpTexture['vScale'] = roofShingleVScale * initialUVs['roof_shingle_dormer_mat']['bumpTexture']['vScale'];
            roofShingleDormerMaterial.diffuseTexture['vScale'] = roofShingleVScale * initialUVs['roof_shingle_dormer_mat']['diffuseTexture']['vScale'];
            if( undefined !== roofShingleDormerMaterial.specularTexture && undefined !== initialUVs['roof_shingle_dormer_mat']['specularTexture'] ) {
                roofShingleDormerMaterial.specularTexture['vScale'] = roofShingleVScale * initialUVs['roof_shingle_dormer_mat']['specularTexture']['vScale'];
            }
        }
        //#UVscale dormer shed

        let innerUpper01Mat = _this.getMaterialByName('siding_inner_upper_01_mat');
        if( innerUpper01Mat ) {
            innerUpper01Mat.forEach( mat => {
                _this.updateUVScale( mat, width / initialWidth, 1, initialUVs['siding_inner_upper_01_mat']['bumpTexture']['uScale'], initialUVs['siding_inner_upper_01_mat']['bumpTexture']['vScale'] );
            });
        }
        
        let innerUpper02Mat = _this.getMaterialByName('siding_inner_upper_02_mat');
        if( innerUpper02Mat ) {
            innerUpper02Mat.forEach( mat => {
                _this.updateUVScale( mat, depth / initialDepth, 1, initialUVs['siding_inner_upper_02_mat']['bumpTexture']['uScale'], initialUVs['siding_inner_upper_02_mat']['bumpTexture']['vScale'] );
            });
        }

        let innerUpper03Mat = _this.getMaterialByName('siding_inner_upper_03_mat');
        if( innerUpper03Mat ) {
            innerUpper03Mat.forEach( mat => {
                _this.updateUVScale( mat, width / initialWidth, 1, initialUVs['siding_inner_upper_03_mat']['bumpTexture']['uScale'], initialUVs['siding_inner_upper_03_mat']['bumpTexture']['vScale'] );
            });
        }

        let innerUpper04Mat = _this.getMaterialByName('siding_inner_upper_04_mat');
        if( innerUpper04Mat ) {
            innerUpper04Mat.forEach( mat => {
                _this.updateUVScale( mat, width / initialWidth, 1, initialUVs['siding_inner_upper_04_mat']['bumpTexture']['uScale'], initialUVs['siding_inner_upper_04_mat']['bumpTexture']['vScale'] );
            });
        }

        let innerUpper05Mat = _this.getMaterialByName('siding_inner_upper_05_mat');
        if( innerUpper05Mat ) {
            innerUpper05Mat.forEach( mat => {
                _this.updateUVScale( mat, depth / initialDepth, 1, initialUVs['siding_inner_upper_05_mat']['bumpTexture']['uScale'], initialUVs['siding_inner_upper_05_mat']['bumpTexture']['vScale'] );
            });
        }

        let innerUpper06Mat = _this.getMaterialByName('siding_inner_upper_06_mat');
        if( innerUpper06Mat ) {
            innerUpper06Mat.forEach( mat => {
                _this.updateUVScale( mat, depth / initialDepth, 1, initialUVs['siding_inner_upper_06_mat']['bumpTexture']['uScale'], initialUVs['siding_inner_upper_06_mat']['bumpTexture']['vScale'] );
            });
        }

        let innerBottom01Mat = _this.getMaterialByName('siding_inner_bottom_01_mat');
        if( innerBottom01Mat ) {
            innerBottom01Mat.forEach( mat => {
                _this.updateUVScale( mat, width / initialWidth, 1, initialUVs['siding_inner_bottom_01_mat']['bumpTexture']['uScale'], initialUVs['siding_inner_bottom_01_mat']['bumpTexture']['vScale'] );
            });
        }

        let innerBottom02Mat = _this.getMaterialByName('siding_inner_bottom_02_mat');
        if( innerBottom02Mat ) {
            innerBottom02Mat.forEach( mat => {
                _this.updateUVScale( mat, depth / initialDepth, 1, initialUVs['siding_inner_bottom_02_mat']['bumpTexture']['uScale'], initialUVs['siding_inner_bottom_02_mat']['bumpTexture']['vScale'] );
            });
        }

        let sidingRoof02Mats = _this.getMaterialByName('siding_roof_02_mat');
        if( sidingRoof02Mats ) {
            sidingRoof02Mats.forEach( mat => {
                _this.updateUVScale( mat, 1, width / initialWidth, initialUVs['siding_roof_02_mat']['bumpTexture']['uScale'], initialUVs['siding_roof_02_mat']['bumpTexture']['vScale'] );
            });
        }

        let sidingRoofInner02Mats = _this.getMaterialByName('siding_roof_inner_02_mat');
        if( sidingRoofInner02Mats ) {
            sidingRoofInner02Mats.forEach( mat => {
                _this.updateUVScale( mat, 1, width / initialWidth, initialUVs['siding_roof_inner_02_mat']['bumpTexture']['uScale'], initialUVs['siding_roof_inner_02_mat']['bumpTexture']['vScale'] );
            });
        }
        
        if( 'structure_monoslope_smithbilt' === _this._shedType ) {
            if( alpineChaletSidingRoofTop01Mat ) {
                alpineChaletSidingRoofTop01Mat.forEach( mat => {
                    _this.updateUVScale( mat, depth / initialDepth, roofPitchHeight / initialRoofPitchHeight );
                });
            }

            const roofMaterial = _this.getMaterialByName( 'roof_metal_mat' );
            if( roofMaterial ) {
                const vScale = + ( Math.sqrt( roofPitchHeight * roofPitchHeight + width * width ) / Math.sqrt( initialRoofPitchHeight * initialRoofPitchHeight + initialWidth * initialWidth ) ).toFixed( 3 );
                roofMaterial.forEach( mat => {
                    _this.updateUVScale( mat, depth / initialDepth, vScale, initialUVs['roof_metal_mat']['bumpTexture']['uScale'], initialUVs['roof_metal_mat']['bumpTexture']['vScale'] );
                });
            }
        }

        if( 'lean_to' === _this._shedType ) {
            if( alpineChaletSidingRoofTop01Mat ) {
                alpineChaletSidingRoofTop01Mat.forEach( mat => {
                    _this.updateUVScale( mat, depth / initialDepth, roofPitchHeight / initialRoofPitchHeight );
                });
            }
        }

        let innerFBMats = _this.getMaterialByName('siding_inner_fb_mat');
        if( innerFBMats ) {
            innerFBMats.forEach( mat => {
                _this.updateUVScale( mat, width / initialWidth, wallHeight / initialWallHeight, initialUVs['siding_inner_fb_mat']['bumpTexture']['uScale'], initialUVs['siding_inner_fb_mat']['bumpTexture']['vScale'] );
            });
        }

        let innerLRMats = _this.getMaterialByName('siding_inner_lr_mat');
        if( innerLRMats ) {
            innerLRMats.forEach( mat => {
                _this.updateUVScale( mat, depth / initialDepth, wallHeight / initialWallHeight, initialUVs['siding_inner_lr_mat']['bumpTexture']['uScale'], initialUVs['siding_inner_lr_mat']['bumpTexture']['vScale'] );
            });
        }

        let innerRoofTopMats = _this.getMaterialByName('siding_inner_roof_top_mat');
        if( innerRoofTopMats ) {
            innerRoofTopMats.forEach( mat => {
                _this.updateUVScale( mat, width / initialWidth, roofPitchHeight / initialRoofPitchHeight, initialUVs['siding_inner_roof_top_mat']['bumpTexture']['uScale'], initialUVs['siding_inner_roof_top_mat']['bumpTexture']['vScale'] );
            });
        }

        let innerRoofMats = _this.getMaterialByName('siding_inner_roof_mat');
        if( innerRoofMats ) {
            innerRoofMats.forEach( mat => {
                _this.updateUVScale( mat, depth / initialDepth, width / initialWidth, initialUVs['siding_inner_roof_mat']['bumpTexture']['uScale'], initialUVs['siding_inner_roof_mat']['bumpTexture']['vScale'] );
            });
        }

        let floorInnerMats = _this.getMaterialByName('floor_inner_mat');
        if( floorInnerMats ) {
            floorInnerMats.forEach( mat => {
                _this.updateUVScale( mat, width / initialWidth, depth / initialDepth, initialUVs['floor_inner_mat']['bumpTexture']['uScale'], initialUVs['floor_inner_mat']['bumpTexture']['vScale'] );
            });
        }
    
        if( 'structure_cardinal_lofted_cabin' === _this._shedType ) {
            let distance                    = Number( ( ( this._initialWidth / 2 ) - this._roofMiddleR2Position.x ).toFixed( 3 ) );
            let sidingUScale                = ( width - distance * 2 )  / ( initialWidth - distance * 2 );
            let sidingVScale                = wallHeight / _this._initialWallHeight;

            let sidingPorchBottomMaterial   = _this.getMaterialByName( 'ceiling_porch_01_mat' );
            let sidingPorchBottomLMaterial  = _this.getMaterialByName( 'ceiling_porch_02_mat' );
            let sidingPorchBottomRMaterial  = _this.getMaterialByName( 'ceiling_porch_03_mat' );
            //Porch side below
            if( sidingPorchBottomMaterial ) {
                sidingUScale = ( width - distance * 2 ) / ( this._initialWidth - distance * 2 );
                sidingVScale = _this._porchWidth / 4;
                sidingPorchBottomMaterial.forEach( mat => {
                    _this.updateUVScale( mat, sidingUScale, sidingVScale );
                });
            }

            if( sidingPorchBottomLMaterial && sidingPorchBottomRMaterial ) {
                [ ...sidingPorchBottomLMaterial, ... sidingPorchBottomRMaterial ].forEach( mat => {
                    _this.updateUVScale( mat, 1, sidingVScale );
                });
            }
        }

        if( 0 !== _this.initRoofPermatileMetal.permatile_metal ) {
            if( 'flat' === roofShape ) {
                _this.vScaleComoponentRoofUnit( 'roof_permatile_metal_panel_02_mat', _this._bones['roof_permatile_lean_to_l1'].position, _this._bones['roof_permatile_lean_to_r1'].position, _this.initRoofPermatileMetal.permatile_metal );
            } else if( 'hip' === roofShape ) {
                _this.vScaleComoponentRoofUnit( 'roof_permatile_metal_panel_02_mat', _this._bones['r_b_t'].position, _this._bones['r_b_r2'].position, _this.initRoofPermatileMetal.permatile_metal );
            } else {
                _this.vScaleComoponentRoofUnit( 'roof_permatile_metal_panel_01_mat', _this._bones['roof_permatile_aframe_metal_t'].position, _this._bones['roof_permatile_aframe_metal_l1'].position, _this.initRoofPermatileMetal.permatile_metal );
                _this.vScaleComoponentRoofUnit( 'roof_permatile_metal_panel_02_mat', _this._bones['roof_permatile_aframe_metal_t'].position, _this._bones['roof_permatile_aframe_metal_r1'].position, _this.initRoofPermatileMetal.permatile_metal );
            }
        }
        
        if( 'flat' === roofShape ) {
            let soffitDepth = _this.getMaterialByName('soffit_vents_01_mat');
            if( soffitDepth ) {
                let roofDepth   = Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_f_l1'].position ) - 4;
                let vScale      = roofDepth / ( initialDepth - 4 );
                soffitDepth.forEach( mat => {
                    _this.updateUVScale( mat, 1, vScale, initialUVs['soffit_vents_01_mat']['bumpTexture']['uScale'], initialUVs['soffit_vents_01_mat']['bumpTexture']['vScale'] );
                    if( mat.ambientTexture && vScale ) mat.ambientTexture['vScale'] = vScale * this._initialUVs['soffit_vents_01_mat']['bumpTexture']['vScale'];
                });
            }

            let soffitWidth = _this.getMaterialByName('soffit_vents_02_mat');
            if( soffitWidth ) {
                let roofWidth       = Vector3.Distance( _this._bones['r_b_l1'].position, _this._bones['r_b_r1'].position ) - 3.82;
                let vScale          = roofWidth / 0.334;
                soffitWidth.forEach( mat => {
                    _this.updateUVScale( mat, 1, vScale, initialUVs['soffit_vents_02_mat']['bumpTexture']['uScale'], initialUVs['soffit_vents_02_mat']['bumpTexture']['vScale'] );
                    if( mat.ambientTexture && vScale ) mat.ambientTexture['vScale'] = vScale * this._initialUVs['soffit_vents_02_mat']['bumpTexture']['vScale'];
                });
            }
        }

        if( this.isDeluxeCornerLeftPorch() ) {
            const deluxeMats1 = [ 'floor_01_mat', 'celling_porch_01_mat' ];
            const deluxeMats3 = [ 'floor_03_mat', 'celling_porch_03_mat' ];
            this.deluxePorchUVsScale( deluxeMats1, initialUVs, this.deluxePorchGetFrontWidth() );
            this.deluxePorchUVsScale( deluxeMats3, initialUVs, this.deluxePorchGetPorchWidth() );

            let floorInner01Mats = this.getMaterialByName( 'floor_inner_01_mat' );
            if( floorInner01Mats ) {
                floorInner01Mats.forEach( mat => {
                    this.updateUVScale( mat, this.deluxePorchGetFrontWidth() / 4, ( this.getDepth() - 4 ) / 16, initialUVs['floor_inner_01_mat']['bumpTexture']['uScale'], initialUVs['floor_inner_01_mat']['bumpTexture']['vScale'] );
                });
            }

            let floorInner02Mats = this.getMaterialByName( 'floor_inner_02_mat' );
            if( floorInner02Mats ) {
                floorInner02Mats.forEach( mat => {
                    this.updateUVScale( mat, 1, ( this.getDepth() - 8 ) / 12, initialUVs['floor_inner_02_mat']['bumpTexture']['uScale'], initialUVs['floor_inner_02_mat']['bumpTexture']['vScale'] );
                });
            }

            let floorInner03Mats = this.getMaterialByName( 'floor_inner_03_mat' );
            if( floorInner03Mats ) {
                floorInner03Mats.forEach( mat => {
                    this.updateUVScale( mat, this.deluxePorchGetPorchWidth() / 4, ( this.getDepth() - 12 ) / 8, initialUVs['floor_inner_03_mat']['bumpTexture']['uScale'], initialUVs['floor_inner_03_mat']['bumpTexture']['vScale'] );
                });
            }
        }

        if( roofVerticalMetal02Mat ) {
            let roofVerticalMetal02MatUScale = 1;
            let roofVerticalMetal02MatVScale = 1;
            
            if( _this.isDoubleGable() && _this._bones['r_m_t2'] && _this._bones['r_m_t3'] ) {
                roofVerticalMetal02MatUScale = helpers.toFixed3( Vector3.Distance( _this._bones['r_m_t2'].position, _this._bones['r_m_t3'].position ) / _this.getCustomUVScale( 'roof_02_u_scale' ) );
            }

            roofVerticalMetal02Mat.forEach( mat => {
                _this.updateUVScale( mat, roofVerticalMetal02MatUScale, roofVerticalMetal02MatVScale, initialUVs['roof_vertical_metal_02_mat']['bumpTexture']['uScale'], initialUVs['roof_vertical_metal_02_mat']['bumpTexture']['vScale'] );
            } );
        }

        if( 'structure_country_cottage' === this._shedType ) {
            const mat01 = this.getMaterialByName( 'roof_vertical_metal_01_mat' );
            if( mat01 ) {
                mat01.forEach( mat => {
                    this.updateUVScale( mat, ( depth + 0.67 * 2 ) / ( initialDepth + 0.67 * 2 ), 1, initialUVs['roof_vertical_metal_01_mat']['bumpTexture']['uScale'], initialUVs['roof_vertical_metal_01_mat']['bumpTexture']['vScale'] );
                });
            }

            const mat02 = this.getMaterialByName( 'roof_vertical_metal_02_mat' );
            if( mat02 ) {
                const offset = 0.43;
                const uScale = ( _this._bones['r_f_l1'].position.z - _this._bones['r_d_l1'].position.z + offset ) / ( 3 + offset );

                mat02.forEach( mat => {
                    this.updateUVScale( mat, uScale, 1, initialUVs['roof_vertical_metal_02_mat']['bumpTexture']['uScale'], initialUVs['roof_vertical_metal_02_mat']['bumpTexture']['vScale'] );
                });
            }

            const mat03 = this.getMaterialByName( 'roof_vertical_metal_03_mat' );
            if( mat03 ) {
                mat03.forEach( mat => {
                    this.updateUVScale( mat, width / initialWidth, 1, initialUVs['roof_vertical_metal_03_mat']['bumpTexture']['uScale'], initialUVs['roof_vertical_metal_03_mat']['bumpTexture']['vScale'] );
                });
            }
        }

        if( this._shedType === 'structure_classic_aframe_dormer' ) {
            const mat01 = this.getMaterialByName( 'roof_vertical_metal_01_mat' );
            if( mat01 ) {
                const width     = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_t'].position, _this._bones['r_f_t1'].position ) );
                const uScale    = ( width + this.getGlobalData( 'roof_offset_number' ) ) / ( this.getCustomUVScale( 'roof_01_u_scale' ) + this.getGlobalData( 'roof_offset_number' ) );

                mat01.forEach( mat => {
                    this.updateUVScale( mat, uScale, 1, initialUVs['roof_vertical_metal_01_mat']['bumpTexture']['uScale'], initialUVs['roof_vertical_metal_01_mat']['bumpTexture']['vScale'] );
                } );
            }
            
            const mat02 = this.getMaterialByName( 'roof_vertical_metal_02_mat' );
            if( mat02 ) {
                const width     = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_t1'].position, _this._bones['r_b_t1'].position ) );
                const uScale    = width / this.getCustomUVScale( 'roof_02_u_scale' );

                mat02.forEach( mat => {
                    this.updateUVScale( mat, uScale, 1, initialUVs['roof_vertical_metal_02_mat']['bumpTexture']['uScale'], initialUVs['roof_vertical_metal_02_mat']['bumpTexture']['vScale'] );
                } );
            }
            
            const mat03 = this.getMaterialByName( 'roof_vertical_metal_03_mat' );
            if( mat03 ) {
                const width     = helpers.toFixed3( Vector3.Distance( _this._bones['r_b_t'].position, _this._bones['r_b_t1'].position ) );
                const uScale    = ( width + this.getGlobalData( 'roof_offset_number' ) ) / ( this.getCustomUVScale( 'roof_03_u_scale' ) + this.getGlobalData( 'roof_offset_number' ) );

                mat03.forEach( mat => {
                    this.updateUVScale( mat, uScale, 1, initialUVs['roof_vertical_metal_03_mat']['bumpTexture']['uScale'], initialUVs['roof_vertical_metal_03_mat']['bumpTexture']['vScale'] );
                } );
            }
            
            const mat04 = this.getMaterialByName( 'roof_vertical_metal_04_mat' );
            if( mat04 ) {
                const roofWidth = helpers.toFixed3( Vector3.Distance( _this._bones['r_f_t'].position, _this._bones['r_b_t'].position ) );
                const uScale    = helpers.toFixed3( ( roofWidth + _this.getGlobalData( 'roof_offset_number' ) ) / ( _this.getCustomUVScale( 'roof_04_u_scale' ) + _this.getGlobalData( 'roof_offset_number' ) ) );

                mat04.forEach( mat => {
                    this.updateUVScale( mat, uScale, 1, initialUVs['roof_vertical_metal_04_mat']['bumpTexture']['uScale'], initialUVs['roof_vertical_metal_04_mat']['bumpTexture']['vScale'] );
                } );
            }
            
            const matDormer = this.getMaterialByName( 'roof_vertical_metal_dormer_mat' );
            if( matDormer ) {
                const width     = helpers.toFixed3( Vector3.Distance( _this._bones['r_d_t1'].position, _this._bones['r_d_t2'].position ) );
                const uScale    = width / this.getCustomUVScale( 'roof_05_u_scale' );

                matDormer.forEach( mat => {
                    this.updateUVScale( mat, uScale, 1, initialUVs['roof_vertical_metal_dormer_mat']['bumpTexture']['uScale'], initialUVs['roof_vertical_metal_dormer_mat']['bumpTexture']['vScale'] );
                } );
            }
        }

        if( this.isAframeSingleGable() ) {
            const mat01 = this.getMaterialByName( 'roof_vertical_metal_01_mat' );
            const mat02 = this.getMaterialByName( 'roof_vertical_metal_02_mat' );

            if( mat01 ) {
                mat01.forEach( mat => {
                    this.updateUVScale( mat, 1, 1, initialUVs['roof_vertical_metal_01_mat']['bumpTexture']['uScale'], initialUVs['roof_vertical_metal_01_mat']['bumpTexture']['vScale'] );
                } );
            }

            if( mat02 ) {
                const roofWidth = helpers.toFixed3( Vector3.Distance( _this._bones['r_m_t2'].position, _this._bones['r_m_t1'].position ) );
                const uScale = helpers.toFixed3( roofWidth / _this.getCustomUVScale( 'roof_02_u_scale' ) );

                mat02.forEach( mat => {
                    this.updateUVScale( mat, uScale, 1, initialUVs['roof_vertical_metal_02_mat']['bumpTexture']['uScale'], initialUVs['roof_vertical_metal_02_mat']['bumpTexture']['vScale'] );
                } );
            }
        }

        if( this.isCarport() ){
            let groundMat   = _this.getMaterialByName( 'ground_mat' );
            let uScale      = width / initialWidth;
            let vScale      = depth / initialDepth;

            if( groundMat ){

                groundMat.forEach( mat => { 
                    mat.specularPower = 30;

                    if( mat.bumpTexture ) mat.bumpTexture['uScale'] = uScale * initialUVs['ground_mat']['bumpTexture']['uScale'];
                    if( mat.diffuseTexture ) mat.diffuseTexture['uScale'] = uScale * initialUVs['ground_mat']['bumpTexture']['uScale'];
                    if( mat.specularTexture ) mat.specularTexture['uScale'] = uScale * initialUVs['ground_mat']['bumpTexture']['uScale'];

                    if( mat.bumpTexture ) mat.bumpTexture['vScale'] = vScale * initialUVs['ground_mat']['bumpTexture']['vScale'];
                    if( mat.diffuseTexture ) mat.diffuseTexture['vScale'] = vScale * initialUVs['ground_mat']['bumpTexture']['vScale'];
                    if( mat.specularTexture ) mat.specularTexture['vScale'] = vScale * initialUVs['ground_mat']['bumpTexture']['vScale'];
                });
            }
        }

        let carportFrameMat = _this.getMaterialByName( 'carport_frame_mat' );
        if( carportFrameMat ){
            carportFrameMat.forEach( mat => {
                if( mat.reflectionTexture ) mat.reflectionTexture['level'] = 0.2;
            });
        }

        const bayWindow = this.getMaterialByName( 'bay_window_siding_mat' );
        if( bayWindow ) {
            bayWindow.forEach( mat => {
                this.updateUVScale( mat, 1, 1 );
            } );
        }
    }

    deluxePorchUVsScale = ( materials, initialUVs, newWidth ) => {
        for( let i = 0; i < materials.length; i++ ) {
            let deluxeMats = this.getMaterialByName( materials[i] );
            if( deluxeMats ) {
                deluxeMats.forEach( mat => {
                    this.updateUVScale( mat, newWidth / 4, 1, initialUVs[materials[i]]['bumpTexture']['uScale'], initialUVs[materials[i]]['bumpTexture']['vScale'] );
                });
            }
        }
    }

    /**
     * Update UV Scale
     * @param StandardMaterial mat Material
     * @param any uScale ratio U scale
     * @param any vScale ratio V scale
     * @param float initUScale  init U scale
     * @param float initVScale init V scale
     */
    updateUVScale( mat: StandardMaterial, uScale, vScale, initUScale: float = null, initVScale: float = null ){
        if( ! mat ) return;

        if( ! initUScale ) initUScale = this._sidingUscale;
        if( ! initVScale ) initVScale = this._sidingVscale;

        if( uScale ){
            if( mat.bumpTexture ) mat.bumpTexture['uScale'] = uScale * initUScale;
            if( mat.diffuseTexture ) mat.diffuseTexture['uScale'] = uScale * initUScale;
            if( mat.specularTexture ) mat.specularTexture['uScale'] = uScale * initUScale;
            if( mat.opacityTexture ) mat.opacityTexture['uScale'] = uScale * initUScale;
        }

        if( vScale ){
            if( mat.bumpTexture ) mat.bumpTexture['vScale'] = vScale * initVScale;
            if( mat.diffuseTexture ) mat.diffuseTexture['vScale'] = vScale * initVScale;
            if( mat.specularTexture ) mat.specularTexture['vScale'] = vScale * initVScale;
            if( mat.opacityTexture ) mat.opacityTexture['vScale'] = vScale * initVScale;
        }
    }    

    /**
     * Remove mesh instance
     * 
     * @param {string} instance mesh instance name
     */
    removeInstance = ( instance: string ) => {
        return new Promise( resolve => {
            let roofInstance = this._scene.getMeshByName( instance );
            if( roofInstance ) {
                roofInstance.dispose();
            }

            resolve( true );
        });
    }

    /**
     * Set instance for mesh
     * 
     * @param {string} instanceName mesh instance name
     * @param {Mesh} mesh mesh need to set instance
     */
    setInstance = ( instanceName: string, mesh: Mesh ) => {
        return new Promise( resolve => {
            let instance = this._scene.getMeshByName( instanceName );
            if( instance ) {
                instance.setParent( mesh );
            }

            resolve( true );
        });
    }

    /**
     * 
     * Create shape with custom vertex (will handle later, don't remove)
     * 
     * @param vertex 
     * @param face 
     * @param size 
     * @returns mesh
     */
    createPolyhedron = ( vertex, face, size ) => {
        // Test draw roof shape
        // const face      = [ [ 0, 1, 2, 3 ], [ 0, 1, 5, 4 ], [ 1, 2 ,5 ], [ 2, 3, 4, 5 ], [ 0, 4, 3 ] ];
        // const bones     = [ 'r_f_r1', 'r_f_l1', 'r_b_l1', 'r_b_r1', 'r_b_t', 'r_f_t' ];
        // const vertices  = [];
        // bones.forEach( key => {
        //     if( this._bones[key] ) vertices.push( this._bones[key].position.asArray() );
        // } );

        // const roofShape     = this.createPolyhedron( vertices, face, 1 );

        const meshName  = 'roof_abc';
        const faceUVs   = [ [ 0, 0 ], [ 1, 0 ], [ 1, 1 ], [ 0, 1 ] ];
        const positions = [];
        const indices   = [];
        const normals   = [];
        let uvs         = [];

        const mesh      = this.getMeshByName( meshName );
        if( mesh ) {
            mesh.dispose();
        }

        for( let i = 0; i < vertex.length; i++ ) {
            positions.push( vertex[i][0] * size, vertex[i][1] * size, vertex[i][2] * size );
        }

        for( let f = 0; f < face.length; f++ ) {
            for( let j = 0; j < face[f].length; j++ ) {
                uvs = uvs.concat( faceUVs[j] );
            }

            for( let i = 0; i < face[f].length - 2; i++ ) {
                indices.push( face[f][0], face[f][i + 2], face[f][i + 1] );
            }
        }

        VertexData.ComputeNormals( positions, indices, normals );
        VertexData._ComputeSides( Mesh.BACKSIDE, positions, indices, normals, uvs );
        const vertexData        = new VertexData();
        vertexData.positions    = positions;
        vertexData.indices      = indices;
        vertexData.normals      = normals;
        vertexData.uvs          = uvs;

        const polygon           = new Mesh( meshName, this._scene );
        vertexData.applyToMesh( polygon );

        return polygon;
    }

    doCloneRoofMetal = ( data ) => {
        const { meshName }  = data;
        const mesh          = this.getMeshByName( meshName );

        if( ! mesh ) {
            return
        }

        const { name: finalMeshName, startPoint, endPoint, startScale, endScale, bones, rotationY } = data;

        const finalMesh     = this.getMeshByName( finalMeshName );

        if( finalMesh ) {
            finalMesh.dispose();
        }

        const meshLength    = this.getMeshLength( mesh );
        const meshWidth     = this.getMeshWidth( mesh );
        const scaleDistance = helpers.toFixed3( Vector3.Distance( startScale, endScale ) );
        const scaleX        = helpers.toFixed3( scaleDistance / meshWidth );
        const distance      = Vector3.Distance( startPoint, endPoint );
        const divisions     = Math.floor( distance / meshLength );
        const vectorBetween = endScale.subtract( startScale );
        const rotationZ     = Math.atan2( vectorBetween.y, vectorBetween.x );
        const argsMeshMerge = [];
        const shapeRoof     = [];

        const partMissing   = distance - ( divisions * meshLength );
        const newMeshLength = ( partMissing / divisions ) + meshLength;
        const zScale        = newMeshLength / meshLength;

        for( let i = 0; i < divisions; i++ ) {
            const meshClone         = mesh.clone( `${meshName}_${i}` );
            meshClone.position      = Vector3.Lerp( startPoint, endPoint, ( 1 / divisions ) * i );
            meshClone.rotation      = new Vector3( 0, rotationY, - rotationZ );
            meshClone.scaling._x    = scaleX;
            meshClone.scaling._z    = zScale;
            argsMeshMerge.push( meshClone );
        }

        bones.forEach( key => {
            const customPoint = this._bones[`${key}_01`] ? this._bones[`${key}_01`].position : Vector3.Zero();
            shapeRoof.push( this._bones[key].position.add( customPoint ) );
        } );

        const roofMesh      = MeshBuilder.ExtrudePolygon( 'temporary_mesh', { shape: shapeRoof, depth: this.getBuildingHeight(), sideOrientation: Mesh.BACKSIDE }, this._scene );
        roofMesh.position.y = this.getBuildingHeight() + 1;
        const roofShapeCSG  = CSG.FromMesh( roofMesh );
        const meshsMerge    = Mesh.MergeMeshes( argsMeshMerge );
        const meshsMergeCSG = CSG.FromMesh( meshsMerge );
        const intersectMesh = meshsMergeCSG.intersect( roofShapeCSG );
        const mainMesh      = intersectMesh.toMesh( finalMeshName, mesh.material, this._scene, true );

        mainMesh.isPickable = false;
        mainMesh.setEnabled( true );
        mesh.setEnabled( false );
        meshsMerge.dispose();
        roofMesh.dispose();

        return mainMesh;
    }

    /**
     * Clone and cut roof shape
     */
    cloneMetalHipRoof = () => {
        const roofType      = this.getRoofType();
        const meshName      = `roof_${roofType}_clone_obj`;
        const mesh          = this.getMeshByName( meshName );

        if( ! mesh ) {
            return
        }

        const roofRight = {
            name:       'roof_right_clone',
            meshName:   meshName,
            startPoint: this._bones['r_b_r1'].position,
            endPoint:   this._bones['r_f_r1'].position,
            startScale: this._bones['r_b_l2'].position,
            endScale:   this._bones['r_b_t'].position,
            rotationY:  0,
            bones:      [ 'r_b_r1', 'r_b_t', 'r_f_t', 'r_f_r1' ]
        };

        const roofRightMesh = this.doCloneRoofMetal( roofRight );
        let roofLeftMesh    = this.getMeshByName( 'roof_left_clone' );
        if( roofLeftMesh ) roofLeftMesh.dispose();
        
        roofLeftMesh            = roofRightMesh.clone( 'roof_left_clone' )
        roofLeftMesh.scaling.x  = -1;

        const roofFront = {
            name:       'roof_front_clone',
            meshName:   meshName,
            startPoint: this._bones['r_b_l1'].position,
            endPoint:   this._bones['r_b_r1'].position,
            startScale: this._bones['r_b_l2'].position,
            endScale:   this._bones['r_b_t'].position,
            rotationY:  Math.PI / 2,
            bones:      [ 'r_b_l1', 'r_b_t', 'r_b_r1' ]
        };

        const roofFrontMesh = this.doCloneRoofMetal( roofFront );
        let roofBackMesh    = this.getMeshByName( 'roof_back_clone' );
        if( roofBackMesh ) roofBackMesh.dispose();

        roofBackMesh            = roofFrontMesh.clone( 'roof_back_clone' )
        roofBackMesh.scaling.z  = -1;

        const cloneMeshs = [ 'roof_right_clone', 'roof_left_clone', 'roof_front_clone', 'roof_back_clone' ];
        cloneMeshs.forEach( key => {
            if( ! helpers.hasKeyInArray( this._meshBlurForInterior, key ) ) {
                this._meshBlurForInterior.push( key );
            }

            this._roofMesh[roofType] = helpers.checkAndPushKeyToArray( this._roofMesh[roofType], key );
        } );
    }

    cloneRoofMetalRancherModular = mesh => {
        let meshName    = 'roof_metal_clone_obj';
        const start     = this._bones['roof_dormer_left_f_t1'].position;
        const end       = this._bones['roof_dormer_left_f_t2'].position;

        this._instances['roofMetal']   = [];
        const roofWidth: number         = Vector3.Distance( start, end );
        const onePieceLength: number    = this.getMeshLength( mesh );
        const numberPiece: number       = Math.floor( roofWidth / onePieceLength );
        const partMissing: number       = roofWidth - ( numberPiece * onePieceLength );
        const newOnePieceLength: number = ( partMissing / numberPiece ) + onePieceLength;
        const zScale: number            = newOnePieceLength / onePieceLength;
        let bones                       = [ 'rm_l1', 'rm_l2', 'rm_l3', 'rm_r1', 'rm_r2', 'rm_r3', 'rm_t', 'rm_t1', 'rm_t2' ];

        bones.forEach( b => {
            if( undefined !== this._bones[b] ) {
                this._bones[b].setScale( new Vector3( 1, 1, zScale ) );
            }
        } );

        for( let index = 0; index < numberPiece; index++ ) {
            let instanceKey1 = `${meshName}_${index}`;
            let instanceKey2 = `${meshName}_left_${index}`;

            if( ! this.getMeshByName( instanceKey1 ) && ! this.getMeshByName( instanceKey2 ) ) {
                this._instances['roofMetal'].push( instanceKey1, instanceKey2 );

                let roofClone           = mesh.createInstance( instanceKey1 );
                roofClone.isPickable    = false;
                const positionZ         = newOnePieceLength * index;

                roofClone.alwaysSelectAsActiveMesh  = true;
                roofClone.position                  = new Vector3( mesh.position.x, mesh.position.y, positionZ );
                roofClone.setParent( mesh.parent );

                let roofClone2          = mesh.createInstance( instanceKey2 );
                roofClone2.isPickable   = false;
                const positionZ2        = this.getDepth() / 2 + 6 + newOnePieceLength * index;

                roofClone2.alwaysSelectAsActiveMesh  = true;
                roofClone2.position                  = new Vector3( mesh.position.x, mesh.position.y, positionZ2 );
                roofClone2.setParent( mesh.parent );
            }
        }

        mesh.setEnabled( false );
    }
    
    cloneMetalRoof = () => {
        
        if( 'true' === this.getSettingData( 'apply_new_roof' ) ) {
            return
        }

        const roofType = this.getRoofType();
        if( 'structure_hiproof' === this._shedType && ( 'metal' === roofType || 'permatile_metal' === roofType || 'standing_seam' === roofType ) ) {
            this.cloneMetalHipRoof();
            return;
        }

        const referenceRoof = jQuery( `[data-roof-type="${roofType}"]` ).attr( 'data-reference-key' );
        if( 'metal' !== roofType && 'permatile_metal' !== roofType && 'standing_seam' !== roofType && 'metal' !== referenceRoof ) {
            return;
        }

        this.cloneMetalRoofFollowMesh( roofType, referenceRoof, 'clone_obj', 'roofMetal' );
        if( this.is2StoryLeanTo() ) {
            this.cloneMetalRoofFollowMesh( roofType, referenceRoof, 'porch_clone_obj', 'roofMetalPorch' );
            this.cloneMetalRoofFollowMesh( roofType, referenceRoof, 'lean_to_clone_obj', 'roofMetalLeanTo' );
        }
    }

    /**
     * Clone metal roof
     */
    cloneMetalRoofFollowMesh = ( roofType, referenceRoof, suffix, instanceData ) => {
        const _this         = this;
        let meshName        = `roof_${roofType}_${suffix}`;
        let referenceMesh   = '';

        if( 'permatile_metal' === roofType ) {
            if( 'flat' === this.getRoofShape() ) {
                meshName = 'roof_permatile_lean_to_metal_clone_obj';
            } else {
                meshName = 'roof_permatile_aframe_metal_clone_obj';
            }
        }

        if( 'standing_seam' === roofType ) {
            if( 'flat' === this.getRoofShape() ) {
                meshName = 'roof_standing_seam_lean_to_metal_clone_obj';
            } else {
                meshName = 'roof_standing_seam_metal_aframe_clone_obj';
            }
        }

        if( undefined !== referenceRoof && '' !== referenceRoof ) {
            referenceMesh = `roof_${referenceRoof}_${suffix}`;
            this._meshBlurForInterior.push( meshName, `roof_${roofType}_obj` );
        }
        
        let roofMetalMesh = _this._scene.getMeshByName( meshName ) as Mesh;
        if( null === roofMetalMesh || undefined === roofMetalMesh ) {
            roofMetalMesh = _this._scene.getMeshByName( referenceMesh ) as Mesh;
            if( null === roofMetalMesh || undefined === roofMetalMesh ) return;
        }

        for( let i = 0; i < _this._instances[instanceData].length; i++ ) {
            _this.removeInstance( _this._instances[instanceData][i] );
        }

        roofMetalMesh.alwaysSelectAsActiveMesh = true;

        if( 'structure_rancher_modular' === _this._shedTypeVersion ) {
            roofMetalMesh.setEnabled( true );
            
            if( 'dormer_left' === _this._dormerOption || 'dormer_right' === _this._dormerOption ) {
                _this.cloneRoofMetalRancherModular( roofMetalMesh );
                return;
            }
        }

        _this._instances[instanceData]   = [];
        const roofWidth: number         = _this.getRoofWidth();
        const onePieceLength: number    = +( roofMetalMesh.getBoundingInfo().boundingBox.extendSize.z * 2 ).toFixed( 3 );
        const numberPiece: number       = Math.floor( roofWidth / onePieceLength );
        const partMissing: number       = roofWidth - ( numberPiece * onePieceLength );
        const newOnePieceLength: number = ( partMissing / numberPiece ) + onePieceLength;
        const zScale: number            = newOnePieceLength / onePieceLength;
        let bones                       = [ 'rm_l1', 'rm_l2', 'rm_l3', 'rm_r1', 'rm_r2', 'rm_r3', 'rm_t', 'rm_t1', 'rm_t2' ];

        if( 'permatile_metal' === roofType ) {
            if( 'flat' === this.getRoofShape() ) {
                bones = [ 'roof_permatile_lean_to_l1', 'roof_permatile_lean_to_r1' ];
            } else {
                bones = [ 'roof_permatile_aframe_metal_l1', 'roof_permatile_aframe_metal_r1', 'roof_permatile_aframe_metal_t' ];
            }
        }

        if( 'standing_seam' === roofType ) {
            if( 'flat' === this.getRoofShape() ) {
                bones = [ 'roof_standing_seam_lean_to_l1', 'roof_standing_seam_lean_to_r1' ];
            } else {
                bones = [ 'roof_standing_seam_aframe_metal_l1', 'roof_standing_seam_aframe_metal_r1', 'roof_standing_seam_aframe_metal_t' ];
            }
        }
        
        if( 'roof_metal_lean_to_clone_obj' === meshName ) {
            bones = [ 'roof_metal_lean_to_r1', 'roof_metal_lean_to_t' ];
        } else if( 'roof_metal_porch_clone_obj' === meshName ) {
            bones = [ 'roof_metal_porch_l1', 'roof_metal_porch_t' ];
        }
        
        bones.forEach( b => {
            if( undefined !== _this._bones[b] ) {
                _this._bones[b].setScale( new Vector3( 1, 1, zScale ) );
            }
        });

        const startPoint            = _this._defaultOverhangBack + _this._customOverhangBack;
        roofMetalMesh.position.z    = - startPoint;

        let offsetZ = 0;
        if( 'structure_trading_post' === this._shedType ){
            offsetZ = ( newOnePieceLength - onePieceLength ) / 2;
            roofMetalMesh.position      = new Vector3( roofMetalMesh.position.x, roofMetalMesh.position.y, offsetZ - startPoint );
        }
        for( let index = 1; index < numberPiece; index++ ) {
            let instanceKey = `${meshName}_${index}`;

            if( 'roof_metal_lean_to_clone_obj' === meshName || 'roof_metal_porch_clone_obj' === meshName ) {
                _this._roofMesh[roofType] = helpers.checkAndPushKeyToArray( this._roofMesh[roofType], instanceKey );
            }

            if( ! _this._scene.getMeshByName( instanceKey ) ) {
                _this._instances[instanceData].push( instanceKey );

                let roofClone           = roofMetalMesh.createInstance( instanceKey );
                roofClone.isPickable    = false;
                const positionZ         = newOnePieceLength * index + offsetZ - startPoint;

                roofClone.alwaysSelectAsActiveMesh  = true;
                roofClone.position                  = new Vector3( roofMetalMesh.position.x, roofMetalMesh.position.y, positionZ );
                roofClone.setParent( roofMetalMesh.parent );
            }
        }

        if( 'structure_greenhouse_polycarbon' === this._shedType ){
            let instanceKey = `${meshName}_2`;
            let meshRoof2   = _this._scene.getMeshByName( instanceKey );
            if( meshRoof2 ) {
                roofMetalMesh.position  = new Vector3( roofMetalMesh.position.x, roofMetalMesh.position.y, meshRoof2.position.z );
                meshRoof2.position      = new Vector3( roofMetalMesh.position.x, roofMetalMesh.position.y, 0 );
            }
        }
    }

    cloneMetalHorizontalRoof = () => {
        const _this             = this;

        if( 'metal_horizontal' !== _this.getRoofType() ) {
            return;
        }

        const meshName          = 'roof_metal_horizontal_clone_obj'
        const roofMetalMesh     = _this.getMeshByName( meshName ) as Mesh

        if( ! roofMetalMesh ) return;       
        
        const roofDegree = _this.getRoofDegree();
        const isLeanTo   = 'carport_lean_to_standalone' === this._shedType;
        const leftName   = `${meshName}_left`;
        const rightName  = `${meshName}_right`;
        let rotation     = roofDegree * Math.PI / 180;
        const errorY     = roofDegree < 5 ? 0.03 : ( roofDegree < 10 ? 0.01 : 0 );

        const instancesLength = roofMetalMesh.instances.length;
        if( instancesLength > 0 ) {
            for( let i = 1; i <= instancesLength; i++ ) {
                if( ! isLeanTo ) {
                    let oldMeshLeft = _this.getMeshByName( `${leftName}_${i}` );
                    if( oldMeshLeft ) oldMeshLeft.dispose();
                }

                let oldMeshRight = _this.getMeshByName( `${rightName}_${i}` );
                if( oldMeshRight ) oldMeshRight.dispose();
            }
        } 


        let distance    = 0;
        let start       = new Vector3( 0, 0, 0 );
        let end         = new Vector3( 0, 0, 0 );
        if( isLeanTo ) {
            const boxEaveWidth      = 0.5;
            const boxEaveHeight     = this.getBoxEaveHeight();
            start                   = new Vector3( _this._bones['r_b_l1'].position.x - boxEaveWidth, _this._bones['r_b_l1'].position.y + boxEaveHeight, _this._bones['r_b_l1'].position.z );
            end                     = new Vector3( _this._bones['r_b_r1'].position.x + boxEaveWidth, _this._bones['r_b_r1'].position.y - 0.13, _this._bones['r_b_r1'].position.z );
            distance                = Vector3.Distance( start, end );
            const width             = Math.abs( start.x ) + Math.abs( end.x );
            const height            = start.y - end.y;
            const newDegree         = this.getRoofPitchFromRadian( height, width );
            rotation                = newDegree * Math.PI / 180;
        } else {
            distance = Vector3.Distance(
                _this._bones['b_b_t'].position,
                _this._bones['b_b_r3'].position
            );
        }

        const roofWidth: number         = distance;
        const onePieceLength: number    = roofMetalMesh.getBoundingInfo().boundingBox.extendSize.x * 2;
        const numberPiece: number       = Math.floor( roofWidth / onePieceLength );
        const partMissing: number       = roofWidth - ( numberPiece * onePieceLength );
        const newOnePieceLength: number = ( partMissing / numberPiece ) + onePieceLength;
        const zScale: number            = newOnePieceLength / onePieceLength;
        let bones                       = [ 'roof_metal_horizontal_b', 'roof_metal_horizontal_f' ];

        bones.forEach( b => {
            if( _this._bones[b] ) {
                _this._bones[b].setScale( new Vector3( zScale, 1, 1 ) );
            }
        });

        for( let i = 1; i <= numberPiece; i++ ) {
            let newClonePosition = new Vector3( 0 , 0 , 0 );
            if( isLeanTo ) {
                newClonePosition = Vector3.Lerp(
                    start,
                    end,
                    ( 1 / numberPiece ) * i
                );
            } else {
                newClonePosition = Vector3.Lerp(
                    _this._bones['b_b_t'].position,
                    _this._bones['b_b_r3'].position,
                    ( 1 / numberPiece ) * i
                );
            }

            if( ! isLeanTo ) {
                const leftMeshData = { 
                    mesh: roofMetalMesh,
                    name: `${leftName}_${i}`,
                    position: new Vector3( - newClonePosition.x, newClonePosition.y + errorY, 0 ),
                    rotation: new Vector3( 0, 0, rotation ),
                    scale: new Vector3( 1, 1, 1 )
                }
                
                _this.createInstance( leftMeshData );
            }

            const rightMeshData = { 
                mesh: roofMetalMesh,
                name: `${rightName}_${i}`,
                position: new Vector3( newClonePosition.x, newClonePosition.y + errorY, 0 ),
                rotation: new Vector3( 0, Math.PI, rotation ),
                scale: new Vector3( 1, 1, 1 )
            }

            _this.createInstance( rightMeshData );
        }

        roofMetalMesh.setEnabled( false );
    }

    /**
     * Create new Instance for Clone Mesh
     * @param data object
     */
    createInstance = ( data ) => {
        const { mesh, name, position, rotation, scale } = data;
        const meshClone        = mesh.createInstance( name ) as Mesh;
        meshClone.position     = position;
        meshClone.rotation     = rotation;
        meshClone.scaling      = scale;
        meshClone.isPickable   = false;
        meshClone.setParent( mesh.parent );
    }

    carportHandleDoCloneSiding() {
        const _this = this;

        jQuery( '.sides-ends-area' ).each( ( i, element ) => {
            const side              = jQuery( element ).find( 'option:selected' ).attr( 'data-side' );
            const value             = Number( jQuery( element ).find( 'option:selected' ).attr( 'data-wall-height' ) );
            const gableEnd          = jQuery( element ).find( 'option:selected' ).attr( 'data-gable-end' );
            const measurementTypes  = jQuery( element ).find( 'option:selected' ).attr( 'data-measurement-types' );

            _this.cloneSidingForCarport( side, value, gableEnd, measurementTypes );
        });
    }

    /**
     * Clone Horizontal metal siding for Carport
     * 
     * @param {String} side             front | back | left | right
     * @param {Number} wallOption       Height of the wall
     * @param {String} gableEnd         Gable end options
     * @param {String} measurementTypes percent | ft
     * 
     * @returns void
     */
    cloneSidingForCarport = ( side, wallOption, gableEnd, measurementTypes ) => {
        const _this = this;
        if( ! side ) return;

        const meshName          = 'siding_metal_horizontal_clone_obj'
        const wallHeight        = _this.getWallHeight();
        const buildingHeight    = _this.getBuildingHeight();
        const cloneWallName     = `wall_${side}_clone`;
        const cloneWallMesh     = _this.getMeshByName( cloneWallName );
        if( cloneWallMesh ) cloneWallMesh.dispose();

        let isOpen      = false;
        let sideHeight  = wallHeight;
        if( 'carport_lean_to_standalone' === _this._shedType && 'left' === side ) {
            sideHeight = buildingHeight;
        }

        let offsetY = 0;
        if( 'carport_standard_regularroof' === _this._shedType ) {
            // offsetY = 0.1;
        } else if( 'carport_standard_verticalroof' === _this._shedType || 'carport_triplewide_attic_rooftruss' === _this._shedType ) {
            offsetY = 0.3;
        } else if( 'carport_lean_to_standalone' === _this._shedType ) {
            offsetY = 0.2;
        }

        let offsetFloor = 0;
        if( 'left' === side || 'right' === side ) {
            if( 0 === wallOption ) {
                isOpen = true;
            } else {
                if( 'ft' === measurementTypes ) {
                    offsetFloor = sideHeight < wallOption ? 0 : sideHeight - wallOption;
                } else if( 'percent' === measurementTypes ) {
                    const tmpWallOption = wallOption > 100 ? 100 : wallOption;
                    offsetFloor         = sideHeight - ( sideHeight * tmpWallOption / 100 );
                }
            }
        } else if( 'front' === side || 'back' === side ) {
            if( 'fully_enclosed' === gableEnd ) {                
                offsetFloor = sideHeight - offsetY;
            } else {
                if( 0 === wallOption ) {
                    isOpen = true;
                } else {
                    if( 'ft' === measurementTypes ) {
                        offsetFloor = sideHeight < wallOption ? 0 : sideHeight - wallOption;
                    } else if( 'percent' === measurementTypes ) {
                        const tmpWallOption = wallOption > 100 ? 100 : wallOption;
                        offsetFloor         = sideHeight - ( sideHeight * tmpWallOption / 100 );
                    }
                }
            }
        }

        let jTrims = [];
        if( 'front' === side ) {
            const jTrim         = 'j_trim_f_obj';
            const jTrimLeft     = 'j_trim_f_l_side_wall_obj';
            const jTrimRight    = 'j_trim_f_r_side_wall_obj';
            jTrims              = this.handleJTrimPositionGable( gableEnd, measurementTypes, wallOption, jTrim, jTrimLeft, jTrimRight, offsetFloor, wallHeight - offsetY );
        } else if( 'back' === side ) {
            const jTrim         = 'j_trim_b_obj';
            const jTrimLeft     = 'j_trim_b_r_side_wall_obj';
            const jTrimRight    = 'j_trim_b_l_side_wall_obj';
            jTrims              = this.handleJTrimPositionGable( gableEnd, measurementTypes, wallOption, jTrim, jTrimLeft, jTrimRight, offsetFloor, wallHeight - offsetY );
        } else if( 'left' === side ) {
            jTrims.push( { name: 'j_trim_l_obj', positionY: offsetFloor } );
        } else if( 'right' === side ) {
            jTrims.push( { name: 'j_trim_r_obj', positionY: offsetFloor } );
        }

        jTrims.forEach( j => {
            const jTrim = _this.getMeshByName( j.name );
            if( jTrim ) jTrim.position = new Vector3( jTrim.position.x, j.positionY, jTrim.position.z );
        });

        let shape = [];
        if( 'front' === side || 'back' === side ) {
            if( 'carport_standard_regularroof' === _this._shedType ){
                shape = _this.getShapeCarportRegularRoof( side, gableEnd, offsetFloor, offsetY, wallHeight );
            } else if( 'carport_lean_to_standalone' === _this._shedType ) {
                shape = _this.getShapeCarportLeanTo( side, gableEnd, offsetFloor, offsetY );
            } else if( 'carport_standard_verticalroof' === _this._shedType || 'carport_triplewide_attic_rooftruss' === _this._shedType ) {
                shape = _this.getShapeStandardCarport( side, gableEnd, offsetFloor, offsetY );
            }
        } else {
            if( 'carport_lean_to_standalone' === _this._shedType ) {
                if( 'left' === side ) {
                    shape = [ 
                        new Vector3( _this._bones['b_f_l1'].position.z, 0, offsetFloor ),
                        new Vector3( _this._bones['b_f_l3'].position.z, 0, _this._bones['b_f_l3'].position.y ),
                        new Vector3( _this._bones['b_b_l3'].position.z, 0, _this._bones['b_b_l3'].position.y ),
                        new Vector3( _this._bones['b_b_l1'].position.z, 0, offsetFloor ),
                    ];
                } else {
                    shape = [ 
                        new Vector3( _this._bones['b_f_r1'].position.z, 0, offsetFloor ),
                        new Vector3( _this._bones['b_f_r3'].position.z, 0, _this._bones['b_f_r3'].position.y ),
                        new Vector3( _this._bones['b_b_r3'].position.z, 0, _this._bones['b_b_r3'].position.y ),
                        new Vector3( _this._bones['b_b_r1'].position.z, 0, offsetFloor ),
                    ];
                }
            } else {
                if( 'carport_standard_regularroof' === _this._shedType ){
                    shape = [ 
                        new Vector3( _this._bones['b_f_r1'].position.z, 0, offsetFloor ),
                        new Vector3( _this._bones['b_f_r2'].position.z, 0, _this._bones['b_f_r2'].position.y ),
                        new Vector3( _this._bones['b_b_r2'].position.z, 0, _this._bones['b_b_r2'].position.y ),
                        new Vector3( _this._bones['b_b_r1'].position.z, 0, offsetFloor ),
                    ];
                }else{
                    shape = [ 
                        new Vector3( _this._bones['b_f_r1'].position.z, 0, offsetFloor ),
                        new Vector3( _this._bones['b_f_r3'].position.z, 0, _this._bones['b_f_r3'].position.y ),
                        new Vector3( _this._bones['b_b_r3'].position.z, 0, _this._bones['b_b_r3'].position.y ),
                        new Vector3( _this._bones['b_b_r1'].position.z, 0, offsetFloor ),
                    ];
                }
            }
        }

        if( ! isOpen ) {
            _this.handleCloneSiding3D( { side, meshName, shape } );
        }

        _this.updateHoleWall( side );

        if( 'back' === side ) {
            _this.carportHandleMiddleTrim( gableEnd, 'corner_trim_b_r_side_wall_obj', 'corner_trim_b_l_side_wall_obj', offsetFloor, 'corner_trim_b_r_side_wall_t', 'corner_trim_b_r_side_wall_b', 'corner_trim_b_l_side_wall_t', 'corner_trim_b_l_side_wall_b', offsetY );
        } else if( 'front' === side ) {
            _this.carportHandleMiddleTrim( gableEnd, 'corner_trim_f_l_side_wall_obj', 'corner_trim_f_r_side_wall_obj', offsetFloor, 'corner_trim_f_l_side_wall_t', 'corner_trim_f_l_side_wall_b', 'corner_trim_f_r_side_wall_t', 'corner_trim_f_r_side_wall_b', offsetY );
        }

        const condition = [];
        condition.push( { meshTrim: 'corner_trim_f_r_obj', sideL: 'wall_front_clone', sideR: 'wall_right_clone', bone: 'corner_trim_f_r_b', boneTop: 'corner_trim_f_r_t', id: '#ssb-configurator-front-gable-end' } );
        condition.push( { meshTrim: 'corner_trim_f_l_obj', sideL: 'wall_left_clone', sideR: 'wall_front_clone', bone: 'corner_trim_f_l_b', boneTop: 'corner_trim_f_l_t', id: '#ssb-configurator-front-gable-end' } );
        condition.push( { meshTrim: 'corner_trim_b_r_obj', sideL: 'wall_right_clone', sideR: 'wall_back_clone', bone: 'corner_trim_b_r_b', boneTop: 'corner_trim_b_r_t', id: '#ssb-configurator-back-gable-end' } );
        condition.push( { meshTrim: 'corner_trim_b_l_obj', sideL: 'wall_back_clone', sideR: 'wall_left_clone', bone: 'corner_trim_b_l_b', boneTop: 'corner_trim_b_l_t', id: '#ssb-configurator-back-gable-end' } );

        condition.forEach( data => {
            const meshTrim  = _this.getMeshByName( data.meshTrim );
            if( ! meshTrim ) {
                return;
            }
            meshTrim.alwaysSelectAsActiveMesh = true;

            let trimHeight  = buildingHeight;
            let sideL       = _this.getMeshByName( data.sideL );
            let sideR       = _this.getMeshByName( data.sideR );
            
            let gableType = jQuery( data.id ).find( 'option:selected' ).attr( 'data-gable-end' );
            if( 'left_side' === gableType && ( 'corner_trim_f_l_obj' === data.meshTrim || 'corner_trim_b_r_obj' === data.meshTrim ) ) {
                sideR = undefined;
            } else if( 'right_side' === gableType && ( 'corner_trim_f_r_obj' === data.meshTrim || 'corner_trim_b_l_obj' === data.meshTrim ) ) {
                sideL = undefined;
            } else if( 'right_side_gable' === gableType && 'corner_trim_f_r_obj' === data.meshTrim ) {
                sideL = undefined;
            } else if( 'left_side_gable' === gableType && 'corner_trim_b_r_obj' === data.meshTrim ) {
                sideR = undefined;
            }

            if( sideL ) {
                trimHeight = + ( sideL.geometry.extend.minimum.y ).toFixed( 3 );
            }

            if( sideR && + ( sideR.geometry.extend.minimum.y ).toFixed( 3 ) < trimHeight ) {
                trimHeight = + ( sideR.geometry.extend.minimum.y ).toFixed( 3 );
            }

            if( trimHeight >= _this._bones[data.boneTop].position.y || ( ! sideL && ! sideR ) ) {
                meshTrim.setEnabled( false );
            } else {
                meshTrim.setEnabled( true );

                if( _this._bones[data.bone] ) {
                    _this._bones[data.bone].setPosition( new Vector3( _this._bones[data.bone].position.x, trimHeight, _this._bones[data.bone].position.z ) );
                }

                if( 'left_side_gable' === gableType && 'corner_trim_f_l_obj' === data.meshTrim ) {
                    if( sideL && + ( sideL.geometry.extend.minimum.y ).toFixed( 3 ) < wallHeight ) {
                        _this._bones[data.bone].setPosition( new Vector3( _this._bones[data.bone].position.x, + ( sideL.geometry.extend.minimum.y ).toFixed( 3 ), _this._bones[data.bone].position.z ) );
                    } else {
                        _this._bones[data.bone].setPosition( new Vector3( _this._bones[data.bone].position.x, wallHeight - offsetY, _this._bones[data.bone].position.z ) );
                    }
                } else if( 'right_side_gable' === gableType && 'corner_trim_b_l_obj' === data.meshTrim ) {
                    if( sideR && + ( sideR.geometry.extend.minimum.y ).toFixed( 3 ) < wallHeight ) {
                        _this._bones[data.bone].setPosition( new Vector3( _this._bones[data.bone].position.x, + ( sideR.geometry.extend.minimum.y ).toFixed( 3 ), _this._bones[data.bone].position.z ) );
                    } else {
                        _this._bones[data.bone].setPosition( new Vector3( _this._bones[data.bone].position.x, wallHeight - offsetY, _this._bones[data.bone].position.z ) );
                    }
                }
            }
        });
    }

    handleJTrimPositionGable = ( gableEnd, measurementTypes, wallOption, jTrim, jTrimLeft, jTrimRight, offsetFloor, wallHeight ) => {
        let jTrims = [];
        if( 'fully_enclosed' === gableEnd || 'extended_top_gable' === gableEnd && wallOption > 0 ) {
            jTrims.push( { name: jTrim, positionY: offsetFloor } );
        } else if( 'left_side' === gableEnd && wallOption > 0 ) {
            jTrims.push( { name: jTrimLeft, positionY: offsetFloor } );
        } else if( 'right_side' === gableEnd && wallOption > 0 ) {
            jTrims.push( { name: jTrimRight, positionY: offsetFloor } );
        } else if( 'left_side_gable' === gableEnd ) {
            jTrims.push( { name: jTrimRight, positionY: wallHeight } );
            if( ( 'ft' === measurementTypes && wallOption < wallHeight && 0 !== wallOption ) || ( 'percent' === measurementTypes && wallOption < 100 && 0 !== wallOption ) ) {
                jTrims.push( { name: jTrimLeft, positionY: offsetFloor } );
            }
        } else if( 'right_side_gable' === gableEnd ) {
            jTrims.push( { name: jTrimLeft, positionY: wallHeight } );
            if( ( 'ft' === measurementTypes && wallOption < wallHeight && 0 !== wallOption ) || ( 'percent' === measurementTypes && wallOption < 100 && 0 !== wallOption ) ) {
                jTrims.push( { name: jTrimRight, positionY: offsetFloor } );
            }
        }

        return jTrims;
    }

    carportHandleMiddleTrim = ( gableType, leftTrimName, rightTrimName, offsetFloor, leftTrimBoneTop, leftTrimBoneBottom, rightTrimBoneTop, rightTrimBoneBottom, offsetY ) => {
        const leftTrim  = this.getMeshByName( leftTrimName );
        const rightTrim = this.getMeshByName( rightTrimName );
        if( leftTrim && rightTrim ) {
            leftTrim.alwaysSelectAsActiveMesh   = true;
            rightTrim.alwaysSelectAsActiveMesh  = true;

            const wallHeight        = this.getWallHeight();
            const buildingHeight    = this.getBuildingHeight();
            let trimHeight          = 'carport_lean_to_standalone' === this._shedType ? ( buildingHeight + wallHeight ) / 2 : buildingHeight - 0.1;
            trimHeight              = 'left_side' === gableType || 'right_side' === gableType ? trimHeight : wallHeight - offsetY;

            leftTrim.setEnabled( false );
            rightTrim.setEnabled( false );

            if( 'left_side' === gableType || 'left_side_gable' === gableType ) {
                leftTrim.setEnabled( true );
                this._bones[leftTrimBoneTop].setPosition( new Vector3( this._bones[leftTrimBoneTop].position.x, trimHeight, this._bones[leftTrimBoneTop].position.z ) );
                this._bones[leftTrimBoneBottom].setPosition( new Vector3( this._bones[leftTrimBoneBottom].position.x, offsetFloor, this._bones[leftTrimBoneBottom].position.z ) );
            } else if ( 'right_side' === gableType || 'right_side_gable' === gableType ) {
                rightTrim.setEnabled( true );
                this._bones[rightTrimBoneTop].setPosition( new Vector3( this._bones[rightTrimBoneTop].position.x, trimHeight, this._bones[rightTrimBoneTop].position.z ) );
                this._bones[rightTrimBoneBottom].setPosition( new Vector3( this._bones[rightTrimBoneBottom].position.x, offsetFloor, this._bones[rightTrimBoneBottom].position.z ) );
            }
        }
    }

    getShapeCarportRegularRoof = ( side, gableEnd, offsetFloor, offsetY, wallHeight ) => {
        let shape       = [];
        const points    = [ [ 0.009, 0.649 ], [ 0.03, 0.923 ], [ 0.184, 1.179 ], [ 0.346, 1.406 ], [ 0.552, 1.597 ], [ 0.791, 1.742 ], [ 1.06, 1.84 ] ];
        shape.push(new Vector3( this._bones['b_b_r1'].position.x, 0, offsetFloor ));
        shape.push(new Vector3( this._bones['b_b_r2'].position.x, 0, wallHeight ));

        points.forEach( p => {
            shape.push(new Vector3( this._bones['b_b_r2'].position.x - p[0] - 0.04, 0, wallHeight + p[1] - 0.04 ));
        });

        shape.push(new Vector3( this._bones['b_b_r3'].position.x, 0, this._bones['b_b_r3'].position.y ));
        shape.push(new Vector3( this._bones['b_b_t'].position.x, 0, this._bones['b_b_t'].position.y ));
        shape.push(new Vector3( this._bones['b_b_l3'].position.x, 0, this._bones['b_b_l3'].position.y ));

        points.reverse().forEach( p => {
            shape.push(new Vector3( this._bones['b_b_l2'].position.x + p[0] + 0.04, 0, wallHeight + p[1] - 0.04 ));
        });
        
        shape.push(new Vector3( this._bones['b_b_l2'].position.x, 0, wallHeight ));
        shape.push(new Vector3( this._bones['b_b_l1'].position.x, 0, offsetFloor ));

        points.reverse();

        if( 'front' === side ) {
            if( 'left_side' === gableEnd ) {
                shape = [ new Vector3( this._bones['b_f_r1'].position.x, 0, offsetFloor ) ];
                shape.push( new Vector3( this._bones['b_f_r2'].position.x, 0, wallHeight ) );

                points.forEach( p => {
                    shape.push(new Vector3( this._bones['b_f_r2'].position.x - p[0] - 0.04, 0, wallHeight + p[1] - 0.04 ));
                });

                shape.push( new Vector3( this._bones['b_f_r3'].position.x, 0, this._bones['b_f_r3'].position.y ) );
                shape.push( new Vector3( this._bones['b_f_t'].position.x, 0, this._bones['b_f_t'].position.y ) );
                
                shape.push( new Vector3( 0, 0, offsetFloor ) );
            } else if( 'right_side' === gableEnd ) {
                shape = [ new Vector3( 0, 0, offsetFloor ) ];
                shape.push( new Vector3( this._bones['b_f_t'].position.x, 0, this._bones['b_f_t'].position.y ) );
                shape.push( new Vector3( this._bones['b_f_l3'].position.x, 0, this._bones['b_f_l3'].position.y ) );

                points.reverse().forEach( p => {
                    shape.push(new Vector3( this._bones['b_f_l2'].position.x + p[0] + 0.04, 0, wallHeight + p[1] - 0.04 ));
                });

                shape.push( new Vector3( this._bones['b_f_l2'].position.x, 0, wallHeight ) );
                shape.push( new Vector3( this._bones['b_f_l1'].position.x, 0, offsetFloor ) );

                points.reverse();
            } else if( 'left_side_gable' === gableEnd ) {
                shape = [ new Vector3( this._bones['b_f_r1'].position.x, 0, offsetFloor ) ];
                shape.push( new Vector3( this._bones['b_f_r2'].position.x, 0, wallHeight ) );

                points.forEach( p => {
                    shape.push(new Vector3( this._bones['b_f_r2'].position.x - p[0] - 0.04, 0, wallHeight + p[1] - 0.04 ));
                });

                shape.push( new Vector3( this._bones['b_f_r3'].position.x, 0, this._bones['b_f_r3'].position.y ) );
                shape.push( new Vector3( this._bones['b_f_t'].position.x, 0, this._bones['b_f_t'].position.y ) );
                shape.push( new Vector3( this._bones['b_f_l3'].position.x, 0, this._bones['b_f_r3'].position.y ) );

                points.reverse().forEach( p => {
                    shape.push( new Vector3( this._bones['b_f_l2'].position.x + p[0] + 0.04, 0, wallHeight + p[1] - 0.04 ) );
                });

                shape.push( new Vector3( this._bones['b_f_l2'].position.x, 0, wallHeight ) );
                shape.push( new Vector3( 0, 0, wallHeight ), );
                shape.push( new Vector3( 0, 0, offsetFloor ), );

                points.reverse();
            } else if( 'right_side_gable' === gableEnd ) {
                shape = [ new Vector3( this._bones['b_f_r2'].position.x, 0, wallHeight ) ];

                points.forEach( p => {
                    shape.push(new Vector3( this._bones['b_f_r2'].position.x - p[0] - 0.04, 0, wallHeight + p[1] - 0.04 ));
                });

                shape.push( new Vector3( this._bones['b_f_r3'].position.x, 0, this._bones['b_f_r3'].position.y ) );
                shape.push( new Vector3( this._bones['b_f_t'].position.x, 0, this._bones['b_f_t'].position.y ) );
                shape.push( new Vector3( this._bones['b_f_l3'].position.x, 0, this._bones['b_f_r3'].position.y ) );

                points.reverse().forEach( p => {
                    shape.push( new Vector3( this._bones['b_f_l2'].position.x + p[0] + 0.04, 0, wallHeight + p[1] - 0.04 ) );
                });

                shape.push( new Vector3( this._bones['b_f_l2'].position.x, 0, wallHeight ) );
                shape.push( new Vector3( this._bones['b_f_l1'].position.x, 0, offsetFloor ) );
                shape.push( new Vector3( 0, 0, offsetFloor ) );
                shape.push( new Vector3( 0, 0, wallHeight ) );

                points.reverse();
            }
        } else if( 'back' === side ) {
            if( 'left_side' === gableEnd ) {
                shape = [ new Vector3( 0, 0, offsetFloor ) ];
                shape.push( new Vector3( this._bones['b_b_t'].position.x, 0, this._bones['b_b_t'].position.y ) );
                shape.push( new Vector3( this._bones['b_b_l3'].position.x, 0, this._bones['b_b_l3'].position.y ) );

                points.reverse().forEach( p => {
                    shape.push( new Vector3( this._bones['b_b_l2'].position.x + p[0] + 0.04, 0, wallHeight + p[1] - 0.04 ) );
                });

                shape.push( new Vector3( this._bones['b_b_l2'].position.x, 0, wallHeight ) );
                shape.push( new Vector3( this._bones['b_b_l1'].position.x, 0, offsetFloor ) );

                points.reverse();
            } else if( 'right_side' === gableEnd ) {
                shape = [ new Vector3( this._bones['b_b_r1'].position.x, 0, offsetFloor ) ];
                shape.push( new Vector3( this._bones['b_b_r2'].position.x, 0, wallHeight ) );

                points.forEach( p => {
                    shape.push( new Vector3( this._bones['b_b_r2'].position.x - p[0] - 0.04, 0, wallHeight + p[1] - 0.04 ) );
                });

                shape.push( new Vector3( this._bones['b_b_r3'].position.x, 0, this._bones['b_b_r3'].position.y ) );
                shape.push( new Vector3( this._bones['b_b_t'].position.x, 0, this._bones['b_b_t'].position.y ) );
                shape.push( new Vector3( 0, 0, offsetFloor ) );
            } else if( 'left_side_gable' === gableEnd ) {
                shape = [ new Vector3( 0, 0, offsetFloor ) ];
                shape.push( new Vector3( 0, 0, wallHeight ) );
                shape.push( new Vector3( this._bones['b_b_r2'].position.x, 0, wallHeight ) );

                points.forEach( p => {
                    shape.push( new Vector3( this._bones['b_b_r2'].position.x - p[0] - 0.04, 0, wallHeight + p[1] - 0.04 ) );
                });

                shape.push( new Vector3( this._bones['b_b_t'].position.x, 0, this._bones['b_b_t'].position.y ) );
                shape.push( new Vector3( this._bones['b_b_l3'].position.x, 0, this._bones['b_b_l3'].position.y ) );

                points.reverse().forEach( p => {
                    shape.push( new Vector3( this._bones['b_b_l2'].position.x + p[0] + 0.04, 0, wallHeight + p[1] - 0.04 ) );
                });

                shape.push( new Vector3( this._bones['b_b_l2'].position.x, 0, wallHeight ) );
                shape.push( new Vector3( this._bones['b_b_l1'].position.x, 0, offsetFloor ) );

                points.reverse();
            } else if( 'right_side_gable' === gableEnd ) {
                shape = [ new Vector3( this._bones['b_b_r1'].position.x, 0, offsetFloor ) ];
                shape.push( new Vector3( this._bones['b_b_r2'].position.x, 0, wallHeight ) );

                points.forEach( p => {
                    shape.push( new Vector3( this._bones['b_b_r2'].position.x - p[0] - 0.04, 0, wallHeight + p[1] - 0.04 ) );
                });
                
                shape.push( new Vector3( this._bones['b_b_r3'].position.x, 0, this._bones['b_b_r3'].position.y ) );
                shape.push( new Vector3( this._bones['b_b_t'].position.x, 0, this._bones['b_b_t'].position.y ) );
                shape.push( new Vector3( this._bones['b_b_l3'].position.x, 0, this._bones['b_b_l3'].position.y ) );

                points.reverse().forEach( p => {
                    shape.push( new Vector3( this._bones['b_b_l2'].position.x + p[0] + 0.04, 0, wallHeight + p[1] - 0.04 ) );
                });

                shape.push( new Vector3( this._bones['b_b_l2'].position.x, 0, wallHeight ) );
                shape.push( new Vector3( 0, 0, wallHeight ) );
                shape.push( new Vector3( 0, 0, offsetFloor ) );

                points.reverse();
            }
        }

        return shape;
    }

    getShapeStandardCarport = ( side, gableEnd, offsetFloor, offsetY ) => {
        let shape = [ 
            new Vector3( this._bones['b_b_r1'].position.x, 0, offsetFloor ),
            new Vector3( this._bones['b_b_r3'].position.x, 0, this._bones['b_b_r3'].position.y ),
            new Vector3( this._bones['b_b_t'].position.x, 0, this._bones['b_b_t'].position.y ),
            new Vector3( this._bones['b_b_l3'].position.x, 0, this._bones['b_b_l3'].position.y ),
            new Vector3( this._bones['b_b_l1'].position.x, 0, offsetFloor ),
        ];

        if( 'front' === side ) {
            if( 'left_side' === gableEnd ) {
                shape = [ 
                    new Vector3( this._bones['b_f_r1'].position.x, 0, offsetFloor ),
                    new Vector3( this._bones['b_f_r3'].position.x, 0, this._bones['b_f_r3'].position.y ),
                    new Vector3( 0, 0, this._bones['b_f_t'].position.y ),
                    new Vector3( 0, 0, offsetFloor ),
                ];
            } else if( 'right_side' === gableEnd ) {
                shape = [ 
                    new Vector3( 0, 0, offsetFloor ),
                    new Vector3( 0, 0, this._bones['b_f_t'].position.y ),
                    new Vector3( this._bones['b_f_l3'].position.x, 0, this._bones['b_f_l3'].position.y ),
                    new Vector3( this._bones['b_f_l1'].position.x, 0, offsetFloor ),
                ];
            } else if( 'left_side_gable' === gableEnd ) {
                shape = [ 
                    new Vector3( this._bones['b_f_r1'].position.x, 0, offsetFloor ),
                    new Vector3( this._bones['b_f_r3'].position.x, 0, this._bones['b_f_r3'].position.y ),
                    new Vector3( this._bones['b_f_t'].position.x, 0, this._bones['b_f_t'].position.y ),
                    new Vector3( this._bones['b_f_l3'].position.x, 0, this._bones['b_f_r3'].position.y ),
                    new Vector3( this._bones['b_f_l3'].position.x, 0, this._bones['b_f_r3'].position.y - offsetY ),
                    new Vector3( 0, 0, this._bones['b_f_r3'].position.y - offsetY ),
                    new Vector3( 0, 0, offsetFloor ),
                ];
            } else if( 'right_side_gable' === gableEnd ) {
                shape = [
                    new Vector3( this._bones['b_f_r3'].position.x, 0, this._bones['b_f_r3'].position.y - offsetY),
                    new Vector3( this._bones['b_f_r3'].position.x, 0, this._bones['b_f_r3'].position.y),
                    new Vector3( this._bones['b_f_t'].position.x, 0, this._bones['b_f_t'].position.y),
                    new Vector3( this._bones['b_f_l3'].position.x, 0, this._bones['b_f_l3'].position.y ),
                    new Vector3( this._bones['b_f_l1'].position.x, 0, offsetFloor ),
                    new Vector3( 0, 0, offsetFloor ),
                    new Vector3( 0, 0, this._bones['b_f_r3'].position.y - offsetY ),
                ];
            }
        } else if( 'back' === side ) {
            if( 'left_side' === gableEnd ) {
                shape = [ 
                    new Vector3( 0, 0, offsetFloor ),
                    new Vector3( this._bones['b_b_t'].position.x, 0, this._bones['b_b_t'].position.y ),
                    new Vector3( this._bones['b_b_l3'].position.x, 0, this._bones['b_b_l3'].position.y ),
                    new Vector3( this._bones['b_b_l1'].position.x, 0, offsetFloor ),
                ];
            } else if( 'right_side' === gableEnd ) {
                shape = [ 
                    new Vector3( this._bones['b_b_r1'].position.x, 0, offsetFloor ),
                    new Vector3( this._bones['b_b_r3'].position.x, 0, this._bones['b_b_r3'].position.y ),
                    new Vector3( this._bones['b_b_t'].position.x, 0, this._bones['b_b_t'].position.y ),
                    new Vector3( 0, 0, offsetFloor ),
                ];
            } else if( 'left_side_gable' === gableEnd ) {
                shape = [
                    new Vector3( 0, 0, offsetFloor ),
                    new Vector3( 0, 0, this._bones['b_b_r3'].position.y - offsetY ),
                    new Vector3( this._bones['b_b_r3'].position.x, 0, this._bones['b_b_r3'].position.y - offsetY ),
                    new Vector3( this._bones['b_b_r3'].position.x, 0, this._bones['b_b_r3'].position.y ),
                    new Vector3( this._bones['b_b_t'].position.x, 0, this._bones['b_b_t'].position.y ),
                    new Vector3( this._bones['b_b_l3'].position.x, 0, this._bones['b_b_l3'].position.y ),
                    new Vector3( this._bones['b_b_l1'].position.x, 0, offsetFloor ),
                ];
            } else if( 'right_side_gable' === gableEnd ) {
                shape = [ 
                    new Vector3( this._bones['b_b_r1'].position.x, 0, offsetFloor ),
                    new Vector3( this._bones['b_b_r3'].position.x, 0, this._bones['b_b_r3'].position.y ),
                    new Vector3( this._bones['b_b_t'].position.x, 0, this._bones['b_b_t'].position.y ),
                    new Vector3( this._bones['b_b_l3'].position.x, 0, this._bones['b_b_l3'].position.y ),
                    new Vector3( this._bones['b_b_l3'].position.x, 0, this._bones['b_b_l3'].position.y - offsetY ),
                    new Vector3( 0, 0, this._bones['b_b_l3'].position.y - offsetY ),
                    new Vector3( 0, 0, offsetFloor ),
                ];
            }
        }

        return shape;
    }

    getShapeCarportLeanTo = ( side, gableEnd, offsetFloor, offsetY ) => {
        let shape = [ 
            new Vector3( this._bones['b_b_r1'].position.x, 0, offsetFloor ),
            new Vector3( this._bones['b_b_r3'].position.x, 0, this._bones['b_b_r3'].position.y ),
            new Vector3( this._bones['b_b_l3'].position.x, 0, this._bones['b_b_l3'].position.y ),
            new Vector3( this._bones['b_b_l1'].position.x, 0, offsetFloor ),
        ];

        if( 'front' === side ) {
            if( 'left_side' === gableEnd ) {
                shape = [ 
                    new Vector3( this._bones['b_f_r1'].position.x, 0, offsetFloor ),
                    new Vector3( this._bones['b_f_r3'].position.x, 0, this._bones['b_f_r3'].position.y ),
                    new Vector3( 0, 0, ( this._bones['b_f_r3'].position.y + this._bones['b_f_l3'].position.y ) / 2 ),
                    new Vector3( 0, 0, offsetFloor ),
                ];
            } else if( 'right_side' === gableEnd ) {
                shape = [ 
                    new Vector3( 0, 0, offsetFloor ),
                    new Vector3( 0, 0, ( this._bones['b_f_r3'].position.y + this._bones['b_f_l3'].position.y ) / 2 ),
                    new Vector3( this._bones['b_f_l3'].position.x, 0, this._bones['b_f_l3'].position.y ),
                    new Vector3( this._bones['b_f_l1'].position.x, 0, offsetFloor ),
                ];
            } else if( 'left_side_gable' === gableEnd ) {
                shape = [ 
                    new Vector3( this._bones['b_f_r1'].position.x, 0, offsetFloor ),
                    new Vector3( this._bones['b_f_r3'].position.x, 0, this._bones['b_f_r3'].position.y ),
                    new Vector3( this._bones['b_f_l3'].position.x, 0, this._bones['b_f_l3'].position.y ),
                    new Vector3( this._bones['b_f_l3'].position.x, 0, this._bones['b_f_r3'].position.y - offsetY ),
                    new Vector3( 0, 0, this._bones['b_f_r3'].position.y - offsetY ),
                    new Vector3( 0, 0, offsetFloor ),
                ];
            } else if( 'right_side_gable' === gableEnd ) {
                shape = [
                    new Vector3( this._bones['b_f_r3'].position.x, 0, this._bones['b_f_r3'].position.y - offsetY),
                    new Vector3( this._bones['b_f_r3'].position.x, 0, this._bones['b_f_r3'].position.y),
                    new Vector3( this._bones['b_f_l3'].position.x, 0, this._bones['b_f_l3'].position.y ),
                    new Vector3( this._bones['b_f_l1'].position.x, 0, offsetFloor ),
                    new Vector3( 0, 0, offsetFloor ),
                    new Vector3( 0, 0, this._bones['b_f_r3'].position.y - offsetY ),
                ];
            }
        } else if( 'back' === side ) {
            if( 'left_side' === gableEnd ) {
                shape = [ 
                    new Vector3( 0, 0, offsetFloor ),
                    new Vector3( 0, 0, ( this._bones['b_f_r3'].position.y + this._bones['b_f_l3'].position.y ) / 2 ),
                    new Vector3( this._bones['b_f_l3'].position.x, 0, this._bones['b_f_l3'].position.y ),
                    new Vector3( this._bones['b_f_l1'].position.x, 0, offsetFloor ),
                ];
            } else if( 'right_side' === gableEnd ) {
                shape = [ 
                    new Vector3( this._bones['b_b_r1'].position.x, 0, offsetFloor ),
                    new Vector3( this._bones['b_b_r3'].position.x, 0, this._bones['b_b_r3'].position.y ),
                    new Vector3( 0, 0, ( this._bones['b_b_r3'].position.y + this._bones['b_b_l3'].position.y ) / 2 ),
                    new Vector3( 0, 0, offsetFloor ),
                ];
            } else if( 'left_side_gable' === gableEnd ) {
                shape = [
                    new Vector3( this._bones['b_f_r3'].position.x, 0, this._bones['b_f_r3'].position.y - offsetY),
                    new Vector3( this._bones['b_f_r3'].position.x, 0, this._bones['b_f_r3'].position.y),
                    new Vector3( this._bones['b_f_l3'].position.x, 0, this._bones['b_f_l3'].position.y ),
                    new Vector3( this._bones['b_f_l1'].position.x, 0, offsetFloor ),
                    new Vector3( 0, 0, offsetFloor ),
                    new Vector3( 0, 0, this._bones['b_f_r3'].position.y - offsetY ),
                ];
            } else if( 'right_side_gable' === gableEnd ) {
                shape = [ 
                    new Vector3( this._bones['b_f_r1'].position.x, 0, offsetFloor ),
                    new Vector3( this._bones['b_f_r3'].position.x, 0, this._bones['b_f_r3'].position.y ),
                    new Vector3( this._bones['b_f_l3'].position.x, 0, this._bones['b_f_l3'].position.y ),
                    new Vector3( this._bones['b_f_l3'].position.x, 0, this._bones['b_f_r3'].position.y - offsetY ),
                    new Vector3( 0, 0, this._bones['b_f_r3'].position.y - offsetY ),
                    new Vector3( 0, 0, offsetFloor ),
                ];
            }
        }

        return shape;
    }

    /**
     * Clone 3D mesh for siding
     * 
     * args side | mesh Name | shape
     * return void
     */
    handleCloneSiding3D = ( args ) => {
        const _this                     = this;
        const { side, meshName, shape, direction } = args;
        const { scale: zScale, rotation, position, distance, positionMM } = _this.getCloneSidingInfo( side, direction );

        const cloneWallName             = `wall_${side}_clone`;
        const cloneMeshName             = `${side}_${meshName}`;
        const sidingMetalMesh           = _this.getMeshByName( meshName ) as Mesh
        if( ! sidingMetalMesh ) return;
        
        const cloneWallMesh     = _this.getMeshByName( cloneWallName );
        let sidingStatus        = true;
        if( cloneWallMesh ){
            sidingStatus        = cloneWallMesh.isEnabled();
            cloneWallMesh.dispose();
        }

        const onePieceLength: number    = +( sidingMetalMesh.getBoundingInfo().boundingBox.extendSize.x * 2 ).toFixed( 3 );
        const numberPiece: number       = 'vertical' === direction && ( 'left' === side || 'right' === side ) ? Math.floor( distance / onePieceLength ) : Math.ceil( distance / onePieceLength );
        const cloneHeight: number       = onePieceLength * numberPiece;
        const partMissing: number       = 'vertical' === direction && ( 'left' === side || 'right' === side ) ? distance - cloneHeight : 0;
        const newOnePieceLength: number = ( partMissing / numberPiece ) + onePieceLength;
        const xScale: number            = newOnePieceLength / onePieceLength;
        const argsMeshMerge             = [];

        sidingMetalMesh.scaling._z      = zScale;

        for( let i = 1; i <= numberPiece; i++ ) {
            const newClonePosition = Vector3.Lerp(
                Vector3.Zero(),
                new Vector3( 0, cloneHeight, 0 ),
                ( 1 / numberPiece ) * i
            );

            const meshClone         = sidingMetalMesh.clone( `${cloneMeshName}_${i}` );
            meshClone.position      = newClonePosition;
            meshClone.rotation      = new Vector3( 0, Math.PI / 2, Math.PI / 2 );
            argsMeshMerge.push( meshClone );
        }

        if( argsMeshMerge.length > 0 && shape ){
            const meshsMerge        = Mesh.MergeMeshes( argsMeshMerge );

            if( 'vertical' === direction ){
                meshsMerge.rotation     = new Vector3( 0, 0, Math.PI / 2 );
                meshsMerge.position     = positionMM;
            }

            const tempMesh          = MeshBuilder.ExtrudePolygon( 'temporary_mesh', { shape, depth: 0.4, sideOrientation: Mesh.DOUBLESIDE }, this._scene );            
            if( 'back' === side ) {
                tempMesh.position   = new Vector3( 0, 0, 0.2 );
                tempMesh.rotation   = new Vector3( ( Math.PI / 2 ) * 3, Math.PI, 0 );
            } else {
                tempMesh.position   = new Vector3( 0, 0, -0.2 );
                tempMesh.rotation   = new Vector3( ( Math.PI / 2 ) * 3, 0, 0 );
            }
            
            const meshMerge         = CSG.FromMesh( meshsMerge );
            const tempMeshCSG       = CSG.FromMesh( tempMesh );
            const intersectMesh     = meshMerge.intersect( tempMeshCSG );
            const mainMesh          = intersectMesh.toMesh( cloneWallName, sidingMetalMesh.material, _this._scene, true );
            mainMesh.position       = position;
            mainMesh.rotation       = rotation;
            mainMesh.isPickable     = false;
            mainMesh.scaling._y     = xScale;
            
            if( ! helpers.hasKeyInArray( this._meshBlurForInterior, cloneWallName ) ) {
                this._meshBlurForInterior.push( cloneWallName );
            }
            mainMesh.setEnabled( sidingStatus );
            mainMesh.convertToFlatShadedMesh();
            tempMesh.dispose();
            meshsMerge.dispose();
        }
    }

    cloneSiding3D = () => {
        const _this = this;

        if( 'structure_chalet_genesis' !== _this._shedType ) return

        const overHangFront = _this._defaultOverhangFront + _this._customOverhangFront;
        const overHangBack = _this._defaultOverhangBack + _this._customOverhangBack;

        let shapeGable = [ 
            new Vector3( _this._bones['b_f_r1'].position.z + overHangFront, 0, 0 ),
            new Vector3( _this._bones['b_f_r2'].position.z + overHangFront, 0, _this._bones['b_f_r2'].position.y ),
            new Vector3( _this._bones['b_b_r2'].position.z - overHangBack, 0, _this._bones['b_b_r2'].position.y ),
            new Vector3( _this._bones['b_b_r1'].position.z - overHangBack, 0, 0 ),
        ];

        let shapeEnd = [ 
            new Vector3( _this._bones['b_b_r1'].position.x - 0.03, 0, 0 ),
            new Vector3( _this._bones['b_b_r2'].position.x - 0.03, 0, _this._bones['b_b_r2'].position.y ),
            new Vector3( _this._bones['b_b_t'].position.x, 0, _this._bones['b_b_t'].position.y ),
            new Vector3( _this._bones['b_b_l2'].position.x + 0.03, 0, _this._bones['b_b_l2'].position.y ),
            new Vector3( _this._bones['b_b_l1'].position.x + 0.03, 0, 0 ),
        ];
        
        _this.handleCloneSiding3D( { side: 'front', meshName: 'siding_wooden_clone_set_obj', shape: shapeEnd, direction: 'vertical' } );
        _this.handleCloneSiding3D( { side: 'back', meshName: 'siding_wooden_clone_set_obj', shape: shapeEnd, direction: 'vertical' } );
        _this.handleCloneSiding3D( { side: 'left', meshName: 'siding_metal_horizontal_clone_obj', shape: shapeGable, direction: 'vertical' } );
        _this.handleCloneSiding3D( { side: 'right', meshName: 'siding_metal_horizontal_clone_obj', shape: shapeGable, direction: 'vertical' } );
    }

    /**
     * Get siding data for clone
     * @param side siding to clone
     * @returns 
     */
    getCloneSidingInfo = ( side, direction ) => {
        const _this = this;
        const wallWidth         = _this.getWidth();
        const wallDepth         = _this.getDepth();
        const wallHeight        = _this.getWallHeight();
        const buildingHeight    = _this.getBuildingHeight();

        let rotation, position, scale, distance, positionMM;
    
        switch( side ) {
            case 'front':
                positionMM  = new Vector3( wallWidth / 2, buildingHeight / 2, 0 );
                rotation    = 'vertical' === direction ? new Vector3( 0, 0, Math.PI / 2 ) : new Vector3( 0, 0, 0 );
                position    = 'vertical' === direction ? new Vector3( wallWidth / 2, buildingHeight / 2, wallDepth / 2 ) : new Vector3( 0, 0, wallDepth / 2 );
                scale       = 'vertical' === direction ? buildingHeight : wallWidth;
                distance    = 'vertical' === direction ? wallWidth : buildingHeight;

                break;
    
            case 'back':
                positionMM  = new Vector3( wallWidth / 2, buildingHeight / 2, 0 );
                rotation    = 'vertical' === direction ? new Vector3( 0, Math.PI, Math.PI / 2 ) : new Vector3( 0, Math.PI, 0 );
                position    = 'vertical' === direction ? new Vector3( - wallWidth / 2, buildingHeight / 2, - wallDepth / 2 ) : new Vector3( 0, 0, - wallDepth / 2 );
                scale       = 'vertical' === direction ? buildingHeight : wallWidth;
                distance    = 'vertical' === direction ? wallWidth : buildingHeight;
                
                break;
    
            case 'left':
                positionMM  = new Vector3( wallDepth / 2, wallHeight / 2, 0 );
                rotation    = 'vertical' === direction ? new Vector3( 0, 3 * Math.PI / 2, Math.PI / 2 ) : new Vector3( 0, 3 * Math.PI / 2, 0 );
                position    = 'vertical' === direction ? new Vector3( - wallWidth / 2, wallHeight / 2, wallDepth / 2 ) : new Vector3( - wallWidth / 2, 0, 0 );
                scale       = 'vertical' === direction ? wallHeight : wallDepth;
                distance    = 'vertical' === direction ? wallDepth : wallHeight;

                if( 'structure_chalet_genesis' === _this._shedType ){
                    const overhangFront = _this._defaultOverhangFront + _this._customOverhangFront
                    const overhangBack  = _this._defaultOverhangBack + _this._customOverhangBack;
                    positionMM   = new Vector3( wallDepth / 2 + overhangFront, wallHeight / 2, 0 );
                    position     = position.add( new Vector3( 0, 0, overhangFront ) );
                    distance    += overhangFront + overhangBack;
                } else if( 'carport_lean_to_standalone' === _this._shedType ) {
                    distance     = 'vertical' === direction ? wallDepth : buildingHeight;
                }

                break;
                
            case 'right':
                positionMM  = new Vector3( wallDepth / 2, wallHeight / 2, 0 );
                rotation    = 'vertical' === direction ? new Vector3( 0, Math.PI / 2, 3 * Math.PI / 2 ) : new Vector3( 0, Math.PI / 2, 0 );
                position    = 'vertical' === direction ? new Vector3( wallWidth / 2, wallHeight / 2, wallDepth / 2 ) : new Vector3( wallWidth / 2, 0, 0 );
                scale       = 'vertical' === direction ? wallHeight : wallDepth;
                distance    = 'vertical' === direction ? wallDepth : wallHeight;

                if( 'structure_chalet_genesis' === _this._shedType ){
                    const overhangFront = _this._defaultOverhangFront + _this._customOverhangFront
                    const overhangBack  = _this._defaultOverhangBack + _this._customOverhangBack;
                    positionMM   = new Vector3( wallDepth / 2 + overhangFront, wallHeight / 2, 0 );
                    position     = position.add( new Vector3( 0, 0, overhangFront ) );
                    distance    += overhangFront + overhangBack;
                }

                break;
        
            default:
                break;
        }
    
        return { position, positionMM, rotation, scale, distance };
    }

    handleJTrimChange = ( data ) => {
        const jTrimFront        = this.getMeshByName( 'j_trim_f_obj' );
        const jTrimBack         = this.getMeshByName( 'j_trim_b_obj' );
        const jTrimLeft         = this.getMeshByName( 'j_trim_l_obj' );
        const jTrimRight        = this.getMeshByName( 'j_trim_r_obj' );
        const jTrimFrontLeft    = this.getMeshByName( 'j_trim_f_l_side_wall_obj' );
        const jTrimFrontRight   = this.getMeshByName( 'j_trim_f_r_side_wall_obj' );
        const jTrimBackLeft     = this.getMeshByName( 'j_trim_b_r_side_wall_obj' );
        const jTrimBackRight    = this.getMeshByName( 'j_trim_b_l_side_wall_obj' );

        if( jTrimFront ) { jTrimFront.alwaysSelectAsActiveMesh = true; jTrimFront.setEnabled( false ); }
        if( jTrimBack ) { jTrimBack.alwaysSelectAsActiveMesh = true; jTrimBack.setEnabled( false ); }
        if( jTrimLeft ) { jTrimLeft.alwaysSelectAsActiveMesh = true; jTrimLeft.setEnabled( false ); }
        if( jTrimRight ) { jTrimRight.alwaysSelectAsActiveMesh = true; jTrimRight.setEnabled( false ); }
        if( jTrimFrontLeft ) { jTrimFrontLeft.alwaysSelectAsActiveMesh = true; jTrimFrontLeft.setEnabled( false ); }
        if( jTrimFrontRight ) { jTrimFrontRight.alwaysSelectAsActiveMesh = true; jTrimFrontRight.setEnabled( false ); }
        if( jTrimBackLeft ) { jTrimBackLeft.alwaysSelectAsActiveMesh = true; jTrimBackLeft.setEnabled( false ); }
        if( jTrimBackRight ) { jTrimBackRight.alwaysSelectAsActiveMesh = true; jTrimBackRight.setEnabled( false ); }
        
        if( 'sides-ends' === data ) {
            this.handleShowJTrimSide( 'left-sidewall', jTrimLeft );
            this.handleShowJTrimSide( 'right-sidewall', jTrimRight );
            this.handleShowJTrimGable( 'front-gable-end', jTrimFront, jTrimFrontLeft, jTrimFrontRight );
            this.handleShowJTrimGable( 'back-gable-end', jTrimBack, jTrimBackLeft, jTrimBackRight );
        } else if( 'sides' === data ) {
            this.handleShowJTrimSide( 'left-sidewall', jTrimLeft );
            this.handleShowJTrimSide( 'right-sidewall', jTrimRight );
        } else if( 'ends' === data ) {
            this.handleShowJTrimGable( 'front-gable-end', jTrimFront, jTrimFrontLeft, jTrimFrontRight );
            this.handleShowJTrimGable( 'back-gable-end', jTrimBack, jTrimBackLeft, jTrimBackRight );
        }
    }

    handleShowJTrimSide = ( classSide, jTrimFull ) => {
        const wallHeight        = 'left-sidewall' === classSide && 'carport_lean_to_standalone' === this._shedType ? this.getBuildingHeight() : this.getWallHeight();
        const wallOption        = Number(jQuery(`.${classSide}`).find('option:selected').data('wall-height'));
        const measurementTypes  = jQuery(`.${classSide}`).find('option:selected').data('measurement-types');
        if( 'ft' === measurementTypes && wallOption < wallHeight && 0 !== wallOption ) {
            if( jTrimFull ) jTrimFull.setEnabled( true );
        } else if( 'percent' === measurementTypes && wallOption < 100 && 0 !== wallOption ) {
            if( jTrimFull ) jTrimFull.setEnabled( true );
        }
    }

    handleShowJTrimGable = ( classSide, jTrimFull, jTrimLeft, jTrimRight ) => {
        const wallHeight        = this.getWallHeight();
        const gableOption       = jQuery(`.${classSide}`).find('option:selected').data('gable-end');
        const wallOption        = Number(jQuery(`.${classSide}`).find('option:selected').data('wall-height'));
        const measurementTypes  = jQuery(`.${classSide}`).find('option:selected').data('measurement-types');
        
        switch( gableOption ) {
            case 'fully_enclosed':
                if( jTrimFull ) jTrimFull.setEnabled( true );
                break;
            case 'extended_top_gable':
                if( 'ft' === measurementTypes && wallOption < wallHeight && 0 !== wallOption ) {
                    if( jTrimFull ) jTrimFull.setEnabled( true );
                } else if( 'percent' === measurementTypes && wallOption < 100 && 0 !== wallOption ) {
                    if( jTrimFull ) jTrimFull.setEnabled( true );
                }
                break;
            case 'left_side':
                if( 'ft' === measurementTypes && wallOption < wallHeight && 0 !== wallOption ) {
                    if( jTrimLeft ) jTrimLeft.setEnabled( true );
                } else if( 'percent' === measurementTypes && wallOption < 100 && 0 !== wallOption ) {
                    if( jTrimLeft ) jTrimLeft.setEnabled( true );
                }
                break;
            case 'right_side':
                if( 'ft' === measurementTypes && wallOption < wallHeight && 0 !== wallOption ) {
                    if( jTrimRight ) jTrimRight.setEnabled( true );
                } else if( 'percent' === measurementTypes && wallOption < 100 && 0 !== wallOption ) {
                    if( jTrimRight ) jTrimRight.setEnabled( true );
                }
                break;
            case 'left_side_gable':
                if( jTrimRight ) jTrimRight.setEnabled( true );
                if( 'ft' === measurementTypes && wallOption < wallHeight && 0 !== wallOption ) {
                    if( jTrimLeft ) jTrimLeft.setEnabled( true );
                } else if( 'percent' === measurementTypes && wallOption < 100 && 0 !== wallOption ) {
                    if( jTrimLeft ) jTrimLeft.setEnabled( true );
                }
                break;
            case 'right_side_gable':
                if( jTrimLeft ) jTrimLeft.setEnabled( true );
                if( 'ft' === measurementTypes && wallOption < wallHeight && 0 !== wallOption ) {
                    if( jTrimRight ) jTrimRight.setEnabled( true );
                } else if( 'percent' === measurementTypes && wallOption < 100 && 0 !== wallOption ) {
                    if( jTrimRight ) jTrimRight.setEnabled( true );
                }
                break;
            default:
                break;
        }
    }

    /**
     * Clone metal roof for porch external
     */
    clonePorchMetalRoof = () => {
        const _this = this;
        if( 'metal' !== _this.getRoofType() ) {
            return;
        }

        const meshName      = 'porch_roof_metal_clone_obj';
        let roofMetalMesh   = _this._scene.getMeshByName( meshName ) as Mesh;
        if( ! roofMetalMesh ) return

        const instancesLength = roofMetalMesh.instances.length;
        if( instancesLength > 0 ) {
            for( let index = 1; index <= instancesLength; index++ ) {
                let oldRoofClone = this._scene.getMeshByName( `${meshName}_${index}` );
                if( oldRoofClone ) oldRoofClone.dispose(); // Dispose old roof_metal_clone_obj cloned before that
            }
        }

        const roofWidth: number         = _this._externalPorchLocation === 'left' ? _this.getDepth() - 0.3 : _this.getWidth() - 0.3;
        const onePieceLength: number    = roofMetalMesh.getBoundingInfo().boundingBox.extendSize.x * 2;
        const numberPiece: number       = Math.floor( roofWidth / onePieceLength );
        const partMissing: number       = roofWidth - ( numberPiece * onePieceLength );
        const newOnePieceLength: number = ( partMissing / numberPiece ) + onePieceLength;
        const zScale: number            = newOnePieceLength / onePieceLength;
        const bones                     = [ 'rm_b_r1', 'rm_f_r1' ];
        bones.forEach( b => {
            if( _this._bones[b] ) {
                _this._bones[b].setScale( new Vector3( zScale, 1, zScale ) );
                _this._bones[b].position = new Vector3( 0, _this._bones[b].position.y, _this._bones[b].position.z );
            }
        } );

        const positionIndex     = roofWidth / 2 + 0.15;
        roofMetalMesh.position  = new Vector3( positionIndex, roofMetalMesh.position.y, roofMetalMesh.position.z );

        roofMetalMesh.alwaysSelectAsActiveMesh = true;
        for( let index = 1; index < numberPiece; index++ ) {
            if( ! _this._scene.getMeshByName( `${meshName}_${index}` ) ) {
                let roofClone           = roofMetalMesh.createInstance( `${meshName}_${index}` );
                roofClone.isPickable    = false;
                const positionX         = newOnePieceLength * index;
                roofClone.setParent( this._scene.getMeshByName( 'Porch_grp' ) );
                roofClone.rotation      = new Vector3 ( 0, 0, 0 );

                roofClone.alwaysSelectAsActiveMesh  = true;
                roofClone.position                  = new Vector3( -positionX + positionIndex, roofMetalMesh.position.y, roofMetalMesh.position.z );
            }
        }

    }

    /**
     * Clone Roof and Rafters for Trading Post
     */
    cloneRoofRaftersForTradingPost(){
        if( 'structure_trading_post' !== this._shedType  ) return;

        const _this                     = this;

        const meshName                  = 'roof_porch_metal_clone_obj';
        let roofPorchMesh               = _this._scene.getMeshByName( meshName ) as Mesh;
        if( ! roofPorchMesh ) return

        _this.removeClone( meshName );

        const roofWidth: number         = _this.getWidth() + 0.5;
        const onePieceLength: number    = roofPorchMesh.getBoundingInfo().boundingBox.extendSize.x * 2;
        const numberPiece: number       = Math.floor( roofWidth / onePieceLength );
        const partMissing: number       = roofWidth - ( numberPiece * onePieceLength );
        const newOnePieceLength: number = ( partMissing / numberPiece ) + onePieceLength;
        const zScale: number            = newOnePieceLength / onePieceLength;
        const bones                     = [ 'rpm_b', 'rpm_f' ];

        bones.forEach( b => {
            if( _this._bones[b] ) _this._bones[b].setScale( new Vector3( zScale, 1, zScale ) );
        });

        let positionIndex = 0;

        roofPorchMesh.position = new Vector3( positionIndex, roofPorchMesh.position.y, roofPorchMesh.position.z )       
        for( let index = 1; index < numberPiece; index++ ) {
            if( ! _this._scene.getMeshByName( `${meshName}_${index}` ) ) {
                let roofClone           = roofPorchMesh.createInstance( `${meshName}_${index}` );
                roofClone.isPickable    = false;
                const positionX         = newOnePieceLength * index;
                roofClone.setParent( this._scene.getMeshByName( 'Porch_grp' ) );
                roofClone.rotation      = new Vector3 ( 0, 0, 0 );

                roofClone.alwaysSelectAsActiveMesh  = true;
                roofClone.position                  = new Vector3( -positionX + positionIndex, roofPorchMesh.position.y, roofPorchMesh.position.z );
            }
        }        
    }    

    /**
     * Create post instance for external porch
     * @param start number of starting post
     * @param numberPost number of posts to create
     * @param meshName name of post mesh
     * @param postObj post mesh object
     * @param postDistance distance between each post
     * @param offset offset of first post
     */
    createPostInstance = ( start: number, numberPost: number, meshName: string, postObj: Mesh, postDistance: number, offset: number ) => {
        for( let i = start; i < numberPost; i++ ) {
            if( ! this._scene.getMeshByName( `${meshName}_${i}` ) ) {
                let postClone       = postObj.createInstance( `${meshName}_${i}` );
                const positionX     = postDistance * i - offset;
                postClone.setParent( this._scene.getMeshByName( 'Porch_grp' ) );
                postClone.alwaysSelectAsActiveMesh  = true;
                postClone.rotation                  = new Vector3 ( 0, 0, 0 );
                postClone.position                  = new Vector3( positionX, postObj.position.y, postObj.position.z );
            }
        }
    }

    /**
     * Clone a mesh and set its skeleton to the same skeleton of the main mesh
     * @param {string} meshCloneName - Name of the cloned mesh
     * @param {Mesh} mainMesh - Main mesh to clone from
     * 
     * @returns {Mesh} Cloned mesh with the same skeleton as the main mesh
     */
    cloneAndSetSkeleton = ( meshCloneName: string, mainMesh: Mesh ): Mesh => {
        const meshClone = mainMesh.clone( meshCloneName );
        meshClone.skeleton = mainMesh.skeleton;

        return meshClone;
    }

    /**
     * Sets up balusters along a specified path between start and end points
     * 
     * @param {Object} args - Configuration object for baluster setup
     * @param {Vector3} args.start - Starting point of the baluster path
     * @param {Vector3} args.end - Ending point of the baluster path
     * @param {number} args.totalPost - Total number of posts/balusters to create
     * @param {string} args.postName - Name/identifier for the posts
     * @param {Object} args.porchGroup - Group to which balusters will be added
     * @param {Array} args.meshClones - Array of existing mesh clones
     * @param {Object} args.baluster - Main baluster mesh to be cloned
     * @param {number} args.distance - Distance between balusters
     * @param {string} args.cloneName - Name for the cloned balusters
     * @param {Vector3} args.direction - Direction vector for positioning
     * 
     * @returns {Array} Updated array of mesh clones
     */
    setupRightBalusters = args => {
        let { start, end, totalPost, postName, porchGroup, meshClones, baluster, distance, cloneName, direction } = args; // Destructure input arguments
        const fullDistance = Vector3.Distance( start, end ); // Calculate the full distance between start and end points
        let tmpDistance = helpers.toFixed3( fullDistance / totalPost ); // Calculate the distance between each post (normalized and rounded)
        const tmpEnd = end.subtract( direction.scale( tmpDistance ) ); // Adjust the end point by subtracting a scaled direction vector
        tmpDistance = tmpDistance * 12 + 1; // Modify distance (+ 1 to handle cases error number)
        const bones = this.instanceMesh( postName, tmpDistance, start, tmpEnd, porchGroup, true ); // Create instances of the post mesh along the path

        // If bones are successfully created as an array
        if( Array.isArray( bones ) ) {
            bones.push( end ); // Add the end point to the bones array
            meshClones = this.createBalusterClones( { bones, meshes: meshClones, mainMesh: baluster, distance, cloneName, groupName: porchGroup } ); // Create clones of the baluster mesh along the bones
        }

        return meshClones; // Return the updated mesh clones
    }

    /**
     * Sets up balusters along a specified path between start and end points for the left side
     * 
     * @param {Object} args - Configuration object for baluster setup
     * @param {Vector3} args.start - Starting point of the baluster path
     * @param {Vector3} args.end - Ending point of the baluster path
     * @param {number} args.totalPost - Total number of posts/balusters to create
     * @param {string} args.postName - Name/identifier for the posts
     * @param {Object} args.porchGroup - Group to which balusters will be added
     * @param {Array} args.meshClones - Array of existing mesh clones
     * @param {Object} args.baluster - Main baluster mesh to be cloned
     * @param {number} args.distance - Distance between balusters
     * @param {string} args.cloneName - Name for the cloned balusters
     * @param {Vector3} args.direction - Direction vector for positioning
     * 
     * @returns {Array} Updated array of mesh clones
     */
    setupLeftBalusters = args => {
        let { start, end, totalPost, postName, porchGroup, meshClones, baluster, distance, cloneName, direction } = args; // Destructure input arguments
        const fullDistance = Vector3.Distance( start, end ); // Calculate the full distance between start and end points
        let tmpDistance = helpers.toFixed3( fullDistance / totalPost ); // Calculate the distance between each post (normalized and rounded)
        const tmpStart = start.add( direction.scale( tmpDistance ) ); // Adjust the start point by adding a scaled direction vector
        tmpDistance = tmpDistance * 12 + 1; // Modify distance (+ 1 to handle cases error number)
        const bones = this.instanceMesh( postName, tmpDistance, tmpStart, end, porchGroup, true ); // Create instances of the post mesh along the path

        // If bones are successfully created as an array
        if( Array.isArray( bones ) ) {
            bones.unshift( start ); // Add the start point to the beginning of the bones array
            meshClones = this.createBalusterClones( { bones, meshes: meshClones, mainMesh: baluster, distance, cloneName, groupName: porchGroup } ); // Create clones of the baluster mesh along the bones
        }

        return meshClones; // Return the updated mesh clones
    }

    /**
     * Creates clones of a baluster mesh and positions them between bone points
     * 
     * @param {Object} args - Configuration object for baluster cloning
     * @param {Array<Vector3>} args.bones - Array of bone/positioning points
     * @param {Array} args.meshes - Existing array of mesh clones to append new clones
     * @param {Object} args.mainMesh - The original baluster mesh to be cloned
     * @param {number} args.distance - Distance between baluster instances
     * @param {string} args.cloneName - Base name for the cloned meshes
     * @param {string} args.groupName - Name of the parent group/container
     * 
     * @returns {Array} Updated array of mesh clones
     */
    createBalusterClones = args => {
        const { bones, meshes, mainMesh, distance, cloneName, groupName } = args; // Destructure input arguments
        const parentMesh = this.getMeshByName( groupName ); // Retrieve the parent mesh group

        // Iterate through bones, creating clones between each pair of points
        for( let i = 0; i < bones.length - 1; i++ ) {
            const name = `${cloneName}_${i}_clone`; // Generate a unique name for each clone
            const mesh = this.cloneAndSetSkeleton( name, mainMesh ); // Clone the main mesh and set up its skeleton
            if( parentMesh ) mesh.setParent( parentMesh ); // If a parent mesh exists, set the clone's parent
            meshes.push( mesh ); // Add the new clone to the meshes array
            this.instanceMesh( name, distance, bones[i], bones[i + 1], groupName ); // Instance the mesh between the current and next bone points
        }

        return meshes; // Return the updated meshes array
    }

    /**
     * Disposes old cloned meshes associated with a given key
     * 
     * @param {string} key - The key used to retrieve the array of old mesh clones
     */
    disposeOldCloneMesh = ( key: string ) => {
        const oldMeshClones = this.getGlobalData( key ); // Retrieve the array of old mesh clones using the key from global data
        // Check if the retrieved data is an array
        if( Array.isArray( oldMeshClones ) ) {
            // Iterate through the array and dispose each mesh instance
            for( let i = 0; i < oldMeshClones.length; i++ ) {
                oldMeshClones[i].dispose();
            }
        }
    }

    /**
     * Clone post and baluster objects based on the given start and end bone names
     * @param {string} startBone - Name of the start bone
     * @param {string} endBone - Name of the end bone
     */
    clonePostAndBaluster = ( startBone, endBone ) => {
        // Early exit conditions: Empty bone names, No opening settings, External porch
        if( '' === startBone || '' === endBone || ! this.hasOpeningSetting() || this.isExternalPorch() ) return;

        // Retrieve necessary mesh objects
        const postMesh = this._scene.getMeshByName( 'column_obj' ) as Mesh;
        const balusterMesh = this._scene.getMeshByName( 'baluster_obj' ) as Mesh;
        const railLeft = this._scene.getMeshByName( 'rail_h_r_obj' );
        const railRight = this._scene.getMeshByName( 'rail_h_l_obj' );

        // Validate mesh objects exist
        if( null === postMesh || null === balusterMesh || null === railLeft || null === balusterMesh ) return;
        
        const openingSetting = this.getOpeningSetting(); // Get opening settings
        if( ! openingSetting ) return;

        // Clean up previous clones
        this.disposeOldCloneMesh( constants.balusterCloneKey );
        this.disposeOldCloneMesh( constants.postCloneKey );

        // Hide/show original and rail meshes
        postMesh.setEnabled( false );
        balusterMesh.setEnabled( false );
        railLeft.setEnabled( true );
        railRight.setEnabled( true );

        // Initialize clone arrays and prepare variables
        let balusterClones = [];
        let postClones = [];
        const bones = this._bones;
        let totalPost = this.getTotalPost( openingSetting['number_post'] );
        const openingSize = Number( openingSetting['opening_size'] );

        // Calculate start and end positions
        const start = bones[startBone].position;
        const end = bones[endBone].position;

        // Calculate direction and normalize
        const direction = end.subtract( start );
        direction.normalize();

        // Prepare additional configuration variables
        const porchWidth = Vector3.Distance( start, end );
        const balusterDistance = this._balusterMiddleSpace * 12;
        const openingPosition = this.getOpeningPosition();
        const porchGroup = 'Porch_grp';
        const postLeftName = 'column_left';
        const postRightName = 'column_right';
        const balusterCloneName = `baluster`;

        // Calculate post distance
        this._postDistance = porchWidth - openingSize;
        
        // Handle different opening position scenarios
        if( 'middle' === openingPosition ) { // 'middle' opening position handling
            // Ensure even number of posts for middle opening
            if( totalPost % 2 !== 0 ) {
                totalPost += 1;
            }

            // Calculate middle points
            const middlePoint = Vector3.Center( start, end );
            const middleLeft = middlePoint.subtract( direction.scale( openingSize / 2 ) );
            const middleRight = middlePoint.add( direction.scale( openingSize / 2 ) );

            const balusterLeftName = 'baluster_left';
            const balusterRightName = 'baluster_right';

            // Create and position posts
            const postLeft = this.cloneAndSetSkeleton( postLeftName, postMesh );
            const postRight = this.cloneAndSetSkeleton( postRightName, postMesh );
            postClones.push( postLeft );
            postClones.push( postRight );

            // Handle different total post scenarios
            if( 2 === totalPost ) {
                // Simple positioning for two posts
                postLeft.position = middleLeft;
                postRight.position = middleRight;
                postLeft.setEnabled( true );
                postRight.setEnabled( true );

                // Create and position balusters
                balusterClones.push( this.cloneAndSetSkeleton( balusterLeftName, balusterMesh ) );
                balusterClones.push( this.cloneAndSetSkeleton( balusterRightName, balusterMesh ) );
                this.instanceMesh( balusterLeftName, balusterDistance, start, middleLeft, porchGroup );
                this.instanceMesh( balusterRightName, balusterDistance, middleRight, end, porchGroup );
            } else {
                // More complex positioning for multiple posts
                const leftArgs = { start, end: middleLeft, totalPost: totalPost / 2, postName: postLeftName, porchGroup, meshClones: balusterClones, baluster: balusterMesh, distance: balusterDistance, cloneName: balusterLeftName, direction };
                balusterClones = this.setupLeftBalusters( leftArgs );
                const rightArgs = { start: middleRight, end, totalPost: totalPost / 2, postName: postRightName, porchGroup, meshClones: balusterClones, baluster: balusterMesh, distance: balusterDistance, cloneName: balusterRightName, direction };
                balusterClones = this.setupRightBalusters( rightArgs );
            }

            // Adjust rail positions
            if( bones['rail_h_l_f_l1'] ) bones['rail_h_l_f_l1'].setPosition( new Vector3( end.x, bones['rail_h_l_f_l1'].position.y, end.z ) );
            if( bones['rail_h_l_f_l2'] ) bones['rail_h_l_f_l2'].setPosition( new Vector3( middleRight.x, bones['rail_h_l_f_l2'].position.y, middleRight.z ) );
            if( bones['rail_h_r_f_l1'] ) bones['rail_h_r_f_l1'].setPosition( new Vector3( start.x, bones['rail_h_r_f_l1'].position.y, start.z ) );
            if( bones['rail_h_r_f_l2'] ) bones['rail_h_r_f_l2'].setPosition( new Vector3( middleLeft.x, bones['rail_h_r_f_l2'].position.y, middleLeft.z ) );
        } else if( 'left' === openingPosition ) { // 'left' opening position handling
            // Disable left rail, enable right rail
            railLeft.setEnabled( false );
            railRight.setEnabled( true );

            const tmpStart = start.add( direction.scale( openingSize ) ); // Calculate start point after opening size

            // Clone left post
            const postLeft = this.cloneAndSetSkeleton( postLeftName, postMesh );
            postClones.push( postLeft );

            if( 1 === totalPost ) {
                // Simple case: single post
                postLeft.position = tmpStart;
                postLeft.setEnabled( true );

                // Create and position single baluster
                balusterClones.push( this.cloneAndSetSkeleton( balusterCloneName, balusterMesh ) );
                this.instanceMesh( balusterCloneName, balusterDistance, tmpStart, end, porchGroup );
            } else {
                // Multiple posts scenario
                const rightArgs = { start: tmpStart, end, totalPost, postName: postLeftName, porchGroup, meshClones: balusterClones, baluster: balusterMesh, distance: balusterDistance, cloneName: balusterCloneName, direction };
                balusterClones = this.setupRightBalusters( rightArgs );
            }

            // Adjust rail positions
            if( bones['rail_h_l_f_l1'] ) bones['rail_h_l_f_l1'].setPosition( new Vector3( end.x, bones['rail_h_l_f_l1'].position.y, end.z ) );
            if( bones['rail_h_l_f_l2'] ) bones['rail_h_l_f_l2'].setPosition( new Vector3( tmpStart.x, bones['rail_h_l_f_l2'].position.y, tmpStart.z ) );
        } else if( 'right' === openingPosition ) { // 'right' opening position handling
            // Enable left rail, disable right rail
            railLeft.setEnabled( true );
            railRight.setEnabled( false );
            
            const tmpEnd = end.subtract( direction.scale( openingSize ) ); // Calculate end point before opening size

            // Clone right post
            const postRight = this.cloneAndSetSkeleton( postRightName, postMesh );
            postClones.push( postRight );

            if( 1 === totalPost ) { // Simple case: single post
                postRight.position = tmpEnd;
                postRight.setEnabled( true );

                // Create and position single baluster
                balusterClones.push( this.cloneAndSetSkeleton( balusterCloneName, balusterMesh ) );
                this.instanceMesh( balusterCloneName, balusterDistance, start, tmpEnd, porchGroup );
            } else {
                // Multiple posts scenario
                const args = { start, end: tmpEnd, totalPost, postName: postRightName, porchGroup, meshClones: balusterClones, baluster: balusterMesh, distance: balusterDistance, cloneName: balusterCloneName, direction };
                balusterClones = this.setupLeftBalusters( args );
            }

            // Adjust rail positions
            if( bones['rail_h_r_f_l1'] ) bones['rail_h_r_f_l1'].setPosition( new Vector3( start.x, bones['rail_h_r_f_l1'].position.y, start.z ) );
            if( bones['rail_h_r_f_l2'] ) bones['rail_h_r_f_l2'].setPosition( new Vector3( tmpEnd.x, bones['rail_h_r_f_l2'].position.y, tmpEnd.z ) );
        }

        // Store clone data globally
        this.setGlobalData( constants.balusterCloneKey, balusterClones );
        this.setGlobalData( constants.postCloneKey, postClones );
    }

    /**
     * Clone post for external porch
     */
    cloneExternalPost = () => {
        if( ! this.isExternalPorch() ) return;

        const bones         = this._bones;
        const meshName      = 'column_obj';
        const width         = this.getWidth();
        const depth         = this.getDepth();
        const postObj       = this._scene.getMeshByName( meshName ) as Mesh;
        const porchExternal = this._canvas.getAttribute( 'data-model-porch-external' );
        const porchData     = JSON.parse( atob( porchExternal ) );
        const { distance, location }  = porchData;
        let porchWidth      = 'front' === location ? width : depth;
        const porchDepth    = 'front' === location ? depth : width;

        if( ! postObj ) return;

        const instancesLength = ( postObj && postObj.instances ) ? postObj.instances.length : 0;
        if( instancesLength > 0 ) {
            for( let index = 1; index <= instancesLength; index++ ) {
                let oldPostClone = this._scene.getMeshByName( `${meshName}_${index}` );
                if( oldPostClone ) oldPostClone.dispose(); // Dispose old column_obj cloned before that
            }
        }

        postObj.setEnabled( false );
        let countPost: number   = 0;

        if( this.hasOpeningSetting() ) {
            const openingSetting    = this.getOpeningSetting();
            const openingPosition   = this.getOpeningPosition();

            if( openingSetting ) {
                let totalPost       = Number( openingSetting['number_post'] );
                const openingSize   = Number( openingSetting['opening_size'] );
                if( 'middle' === openingPosition ) {
                    if( totalPost % 2 !== 0 ) {
                        totalPost += 1;
                    }

                    this._postDistance  = porchWidth / 2 - openingSize / 2;
                    const numberPost    = ( totalPost - 2 ) / 2;
                    const postDistance  = this._postDistance / numberPost;

                    this.createPostInstance( 1, numberPost + 1, meshName, postObj, postDistance, 0 );
                    this.createPostInstance( numberPost + 1, totalPost - 1, meshName, postObj, postDistance, postDistance - openingSize );
                } else if( 'left' === openingPosition || 'right' === openingPosition ) {
                    this._postDistance  = porchWidth - openingSize;
                    const numberPost    = totalPost - 2;
                    const postDistance  = this._postDistance / numberPost;
                    const offset        = 'left' === openingPosition ? 0 : postDistance - openingSize;

                    this.createPostInstance( 1, numberPost + 1, meshName, postObj, postDistance, offset );
                }
            } else {
                this._postDistance = 0;
            }
        } else {
            countPost           = Math.ceil( porchWidth / distance );
            this._postDistance  = porchWidth / countPost;

            this.createPostInstance( 1, countPost, meshName, postObj, this._postDistance, 0 );
        }
        
        //Move bonse for Rail_h_l
        if( bones['rail_h_l_f_l1'] ) bones['rail_h_l_f_l1'].setPosition( new Vector3( -porchWidth/2 - 0.02, bones['rail_h_l_f_l1'].position.y, porchDepth / 2 + this._porchWidth ) );
        if( bones['rail_h_l_f_l2'] ) bones['rail_h_l_f_l2'].setPosition( new Vector3( -porchWidth/2 + this._postDistance, bones['rail_h_l_f_l2'].position.y, porchDepth / 2 + this._porchWidth ) );

        //Move bonse fo Rail_v_l
        if( bones['rail_v_l_b_l1'] ) bones['rail_v_l_b_l1'].setPosition( new Vector3( -porchWidth/2, bones['rail_v_l_b_l1'].position.y, porchDepth / 2 ) );
        if( bones['rail_v_l_f_l1'] ) bones['rail_v_l_f_l1'].setPosition( new Vector3( -porchWidth/2, bones['rail_v_l_f_l1'].position.y, porchDepth / 2 + this._porchWidth ) );

        //Move bonse for Rail_h_r
        if( bones['rail_h_r_f_l1'] ) bones['rail_h_r_f_l1'].setPosition( new Vector3( porchWidth/2, bones['rail_h_r_f_l1'].position.y, porchDepth / 2 + this._porchWidth ) );
        if( bones['rail_h_r_f_l2'] ) bones['rail_h_r_f_l2'].setPosition( new Vector3( porchWidth/2 - this._postDistance, bones['rail_h_r_f_l2'].position.y, porchDepth / 2 + this._porchWidth ) );

        //Move bonse fo Rail_v_r
        if( bones['rail_v_r_b_l1'] ) bones['rail_v_r_b_l1'].setPosition( new Vector3( porchWidth/2, bones['rail_v_r_b_l1'].position.y, porchDepth / 2 ) );
        if( bones['rail_v_r_f_l1'] ) bones['rail_v_r_f_l1'].setPosition( new Vector3( porchWidth/2, bones['rail_v_r_f_l1'].position.y, porchDepth / 2 + this._porchWidth ) );
    }

    /**
     * Clone Baluster for Front Porch & External Porch
     */
    cloneBaluster = async () => {
        if( this.isDoubleGable() || this.isAframeSingleGable() ) {
            return;
        }

        const _this         = this;
        const meshName      = 'baluster_obj';
        const balusterObj   = _this._scene.getMeshByName( meshName ) as Mesh;
        const railObj       = _this._scene.getMeshByName( 'rail_obj' ) as Mesh;
        const width         = _this.getWidth();
        const depth         = _this.getDepth();
        const oPosition     = _this.isDeluxeCornerLeftPorch() ? 'left' :_this.getOpeningPosition();
        const postWidth     = _this._postWidth;

        if( ! balusterObj ) return;
        
        if( 'structure_ranch_cabin' === this._shedType ) {
            return this.ranchCabinCloneBaluster( meshName, balusterObj, oPosition );
        } else if( this.isSidePorchCabin() ) {
            return this.aframeCabinCloneBaluster( meshName, balusterObj, oPosition );
        }

        if( ! _this._porchWidth ){
            balusterObj.setEnabled( _this._enabledRailing );
            if( railObj ) railObj.setEnabled( _this._enabledRailing );
            
            return;
        }
        
        if( _this._instances['baluster'] ) {
            _this._instances['baluster'].forEach( k => {
                let mesh = _this._scene.getMeshByName( k );
                mesh.dispose()
            });
        }

        _this._instances['baluster']    = [];
        const onePieceLength : number   = _this._balusterMiddleSpace;
        let porchWith : number        =  _this.isMiddlePorch() ? ( constants.middleWidth / 2 ) - postWidth / 2 + 2 / 12 : _this._porchWidth - postWidth / 2;
        if( _this.isDoubleGable() ) {
            porchWith = 5;
        }
        let numberPiece : number        = Math.floor( porchWith / onePieceLength );
        let balusterSideDistance        = +( porchWith / ( numberPiece + 1 ) ).toFixed( 3 );

        let sideWidth: number = _this._externalPorchLocation === 'left' ? depth : width;
        if( _this.isExternalPorch() ){
            balusterObj.position = new Vector3( sideWidth / 2 - _this._balusterOffsetLeft, balusterObj.position.y, balusterSideDistance );
            numberPiece *= 2;
        }

        if( _this.isPorchFullRailing() ){
            sideWidth = _this.isSidePorch() ? depth : width;
            balusterObj.position = new Vector3( sideWidth / 2 - _this._balusterOffsetLeft, balusterObj.position.y, balusterSideDistance );
            numberPiece *= 2;
        }

        if( _this.isMiddlePorch() || _this.isDoubleGable() ) {
            if( 'right' === oPosition ) {
                balusterObj.position = new Vector3( 0, balusterObj.position.y, 0 );
            } else if( 'left' === oPosition ) {
                balusterObj.position = new Vector3( 0, balusterObj.position.y, - porchWith - 1 / 12 );
            }
        }

        for( let i = 1; i < numberPiece; i++ ){
            let balusterClone = _this._scene.getMeshByName( `${meshName}_${i}` );
            if( ! balusterClone ) {
                balusterClone = balusterObj.clone( `${meshName}_${i}` );
            }
            
            _this._instances['baluster'].push( `${meshName}_${i}` );
            let positionZ = balusterSideDistance * i;
            balusterClone.alwaysSelectAsActiveMesh  = true;
            balusterClone.isPickable                = false;
            balusterClone.skeleton                  = balusterObj.skeleton;

            if( _this.isCornerPorchLeft() ) {
                balusterClone.position = new Vector3( - positionZ, balusterObj.position.y, balusterObj.position.z );
            } else if( _this.isMiddlePorch() || _this.isDoubleGable() ) {
                if( 'right' === oPosition ) {
                    balusterClone.position = new Vector3( 0, balusterObj.position.y, - positionZ );
                } else if( 'left' === oPosition ) {
                    balusterClone.position = new Vector3( 0, balusterObj.position.y, - ( positionZ + porchWith + 1 / 12 ) );
                }
            }else{
                balusterClone.position = new Vector3( balusterObj.position.x, balusterObj.position.y, - positionZ );
            }

            if( _this.isExternalPorch() || _this.isPorchFullRailing() ){
                let positionX = sideWidth / 2 - _this._balusterOffsetLeft;
                positionZ += balusterSideDistance;
                if( i >= numberPiece/2 ){
                    positionX = -positionX;
                    positionZ = balusterSideDistance * ( i - numberPiece/2 ) + balusterSideDistance;
                    if( _this.isDeluxeCornerLeftPorch() && _this._bones['b_f_l1'] && _this._bones['b_f_r1'] ) {
                        positionZ += _this._bones['b_f_l1'].position.z - _this._bones['b_f_r1'].position.z;
                    }
                }

                balusterClone.setParent( _this._scene.getMeshByName( 'Porch_grp' ) );
                balusterClone.rotation = new Vector3 ( 0, 0, 0 );
                balusterClone.position = new Vector3( positionX, balusterObj.position.y, positionZ );
                balusterClone.isPickable = false;
            }
        }

        //clone baluster for post
        if( _this.isExternalPorch() || _this.isPorchFullRailing() ){
            let totalPostDistance = _this._postDistance - postWidth;
            if( _this.isDeluxeCornerLeftPorch() ) {
                totalPostDistance = width / 2 - postWidth;
            }
            let countPostBaluster = Math.floor( totalPostDistance / onePieceLength );
            let balusterDistance  = +( totalPostDistance / ( countPostBaluster + 1 ) ).toFixed( 3 );
            let totalI            = countPostBaluster * 2 + numberPiece;
            let offsetX           = ( postWidth / 2 ) + ( _this._balusterOffsetLeft / 2 );

            for( let i = numberPiece; i < totalI; i++ ){
                let j = i - numberPiece;

                if( 'right' === oPosition  && j >= countPostBaluster ) continue;
                if( 'left' === oPosition   && j < countPostBaluster ) continue;

                let balusterClone = _this._scene.getMeshByName( `${meshName}_${i}` );
                if( ! balusterClone ) balusterClone = balusterObj.clone( `${meshName}_${i}` );
                
                let positionX = sideWidth/2 - ( balusterDistance * ( j + 1 ) ) - offsetX; // left baluster
                if( j >= countPostBaluster ){
                    positionX = -sideWidth/2 + ( balusterDistance * ( j - countPostBaluster + 1 ) ) + offsetX; // Right baluster
                }
                
                _this._instances['baluster'].push( `${meshName}_${i}` );
                balusterClone.setParent( _this._scene.getMeshByName( 'Porch_grp' ) );
                balusterClone.alwaysSelectAsActiveMesh  = true;
                balusterClone.rotation                  = new Vector3 ( 0, 0, 0 );
                balusterClone.position                  = new Vector3( positionX, balusterObj.position.y, _this._porchWidth - _this._balusterOffsetFront );
                balusterClone.isPickable                = false;
                let balusterCloneSkeleton               = _this._scene.getSkeletonByName( 'balusterClone' );
                
                if( ! balusterCloneSkeleton ){
                    balusterCloneSkeleton = balusterObj.skeleton.clone( 'balusterClone' );
                }

                if( ! _this.isSidePorch() ){
                    balusterCloneSkeleton.bones[1].rotation = new Vector3( 0, Math.PI/2, 0 );
                }

                balusterCloneSkeleton.bones[0].position = _this._bones['baluster'].position;
                balusterCloneSkeleton.bones[1].position = _this._bones['baluster_b_l1'] ? _this._bones['baluster_b_l1'].position : _this._bones['baluster_f_l1'].position;

                if( _this.isDeluxeCornerLeftPorch() ) {
                    balusterClone.position                      = new Vector3( positionX, balusterObj.position.y, - _this._balusterOffsetFront );
                    balusterCloneSkeleton.bones[1].position.z   = _this._bones['b_p_l1'] ? _this._bones['b_p_l1'].position.z : 0;
                }

                balusterClone.skeleton = balusterCloneSkeleton;
            }
        }
    }

    cloneBalusterForTeaHouse = () => {
        if( 'structure_teahouse' !== this._shedType  ) {
            return;
        }

        const width         = this.getWidth();
        const depth         = this.getDepth();
        const postWidth     = 0.5;
        let railingWidth    = 0.158;
        let railingSpace    = 0.293;
        let balusterSpace   = railingWidth + railingSpace;
        let opening         = 8;
        let distance        = width / 2 - postWidth - postWidth / 2 - 0.225;

        this.addClone( "baluster_L_fb_obj", distance, 'x', -1 , balusterSpace );
        this.addClone( "baluster_R_fb_obj", distance, 'x', 1 , balusterSpace );
        distance = ( depth - opening ) / 2 - postWidth - 0.225;
        this.addClone( "baluster_F_lr_obj", distance, 'z', 1 , balusterSpace );
        this.addClone( "baluster_B_lr_obj", distance, 'z', -1 , balusterSpace );
    }

    /**
     * 
     * @param {string} meshName Object name
     * @param {number} distance space between start baluster and last baluster
     * @param {string} position = x|y|z
     * @param {number} vector = -1|1
     * @param {number} balusterSpace space between 2 baluster
     */
    addClone = ( meshName, distance, position, vector, balusterSpace ) => {
        this.removeClone( meshName );
        const balusterObj       = this._scene.getMeshByName( meshName ) as Mesh;
        if( ! balusterObj ) return;
        let balusterCloneLast   = this._scene.getMeshByName( `${meshName}_last` );
        if( ! balusterCloneLast ) {
            balusterCloneLast = balusterObj.createInstance( `${meshName}_last` );
        }
        let x = balusterObj.position.x;
        let y = balusterObj.position.y;
        let z = balusterObj.position.z;
        if( position === 'x' ) {
            x = distance * vector;
        } else if( position === 'y' ) {
            y = distance * vector;
        } else if( position === 'z' ) {
            z = distance * vector;
        } 
        balusterCloneLast.position  = new Vector3( x ,y, z );
        const numbertBaluster       = Math.round( distance / balusterSpace );
        const space                 = distance / numbertBaluster ;
    
        for( let i = 1; i < numbertBaluster; i++ ) {
            let balusterClone = this._scene.getMeshByName( `${meshName}_${i}` );

            if( ! balusterClone ) {
                balusterClone = balusterObj.createInstance( `${meshName}_${i}` );
            }
            if( position === 'x' ) {
                x = space * i  * vector;
            } else if( position === 'z' ) {
                z = space * i  * vector;
            }

            balusterClone.position = new Vector3( x , y, z );
        }
    }

    removeClone = ( meshName ) => {
        const balusterObj = this._scene.getMeshByName( meshName ) as Mesh;
        if( ! balusterObj ) return;
        const instancesLength = balusterObj.instances.length;
        if( instancesLength > 0 ) {
            for( let index = 0; index <= instancesLength; index++ ) {
                let oldBalusterClone = this._scene.getMeshByName( `${meshName}_${index}` );
                if( oldBalusterClone ) oldBalusterClone.dispose();
            }
        }
    }
    /**
     * Handle logic to show runner follow setting
     */
    cloneRunnerForNewModel = async () => {
        let mainRunner: Mesh;
        let mainHorizontalRunner: Mesh;
        let isHorizontalRunner: boolean = false;
        mainRunner                      = this._scene.getMeshByName( 'runner_obj' ) as Mesh;
        mainHorizontalRunner            = this._scene.getMeshByName( 'runner_horizontal_obj' ) as Mesh;

        if( this.isEnableOuterJoist() ) {
            if( mainRunner ) {
                mainRunner.dispose();
            }
            if( mainHorizontalRunner ) {
                mainHorizontalRunner.dispose();
            }
            return;
        }

        if( mainRunner ) {
            if( mainHorizontalRunner ) {
                mainHorizontalRunner.dispose();
            }
            isHorizontalRunner = false;
        } else if( mainHorizontalRunner ) {
            mainRunner = mainHorizontalRunner;
            isHorizontalRunner = true;
        }

        if( ! mainRunner ) {
            return;
        }
        
        let bones               = this._bones;
        const outsideRunners    = this.getSettingData( 'outside_runners' );
        const runnerDirection   = this.getRunnerDirection();
        const runnerWidth       = 0.4;
        let porchWidth          = 0;
        let wallWidth           = this.getWidth() - runnerWidth;
        if( isHorizontalRunner ) {
            wallWidth           = this.getDepth() - runnerWidth;
        }

        mainRunner.rotation = new Vector3( 0, 0, 0 );
        if( ! isHorizontalRunner ) {
            if( 'side-to-side' === runnerDirection ) {
                wallWidth           = this.getDepth() - runnerWidth;
                let extPotrchRunnerB = 0
                let extPotrchRunnerF = 0
                if( this.isFrontPorch() ) {
                    wallWidth += this._porchWidth;
                    porchWidth = this._porchWidth;
                } else if( this.isExternalPorch() ) {
                    const porchExternal = this._canvas.getAttribute( 'data-model-porch-external' );
                    const porchData     = JSON.parse( atob( porchExternal ) );
                    const { location }  = porchData;

                    if( 'front' === location ) {
                        if( 'left' === this._externalPorchLocation ) {
                            wallWidth += this._porchWidth;
                            porchWidth = this._porchWidth;
                        }
                    } else {
                        if( 'left' === this._externalPorchLocation ) {
                            extPotrchRunnerB = - this._porchWidth;
                        } else {
                            extPotrchRunnerF = this._porchWidth;
                        }
                    }
                }

                mainRunner.rotation = new Vector3(0, Math.PI/2, 0);
                if( bones['runner_b'] ) bones['runner_b'].setPosition(new Vector3( bones['runner_b'].position.x, bones['runner_b'].position.y, - this.getWidth() / 2 + extPotrchRunnerB ));
                if( bones['runner_f'] ) bones['runner_f'].setPosition(new Vector3( bones['runner_f'].position.x, bones['runner_f'].position.y, this.getWidth() / 2 + extPotrchRunnerF ));

                if( this.isSidePorch() && this.isExcludePorchMeasurement() ) {
                    if( bones['runner_b'] ) bones['runner_b'].setPosition( new Vector3( bones['runner_b'].position.x, bones['runner_b'].position.y, bones['b_f_l1'].position.x ) );
                    if( 'structure_lean_to_with_front_porch' === this._shedType ) {
                        if( bones['runner_b'] ) bones['runner_b'].setPosition( new Vector3( bones['runner_b'].position.x, bones['runner_b'].position.y, bones['b_f_p1'].position.x ) );
                    }
                }
            } else {
                wallWidth           = this.getWidth() - runnerWidth;
                if( ! this.isFrontPorch() && ! this.isExternalPorch() ) {
                    if( bones['runner_b'] ) bones['runner_b'].setPosition(new Vector3( bones['runner_b'].position.x, bones['runner_b'].position.y, - this.getDepth() / 2 ));
                    if( bones['runner_f'] ) bones['runner_f'].setPosition(new Vector3( bones['runner_f'].position.x, bones['runner_f'].position.y, this.getDepth() / 2 ));
                    
                } else if( this.isFrontPorch() ) {
                    porchWidth = this._porchWidth;
                    if( bones['runner_f'] ) bones['runner_f'].setPosition(new Vector3( bones['runner_f'].position.x, bones['runner_f'].position.y, this.getDepth() / 2 + this._porchWidth ) );
                }
            }
        } else {
            let runnerHorizontalZ = this.getDepth() / 2;
            if( 'side-to-side' !== runnerDirection ) { 
                wallWidth           = this.getWidth() - runnerWidth;
                mainRunner.rotation = new Vector3(0, Math.PI/2, 0);
            } else {
                runnerHorizontalZ = this.getWidth() / 2;
            }

            if( bones['runner_horizontal_b'] ) bones['runner_horizontal_b'].setPosition(new Vector3( bones['runner_horizontal_b'].position.x, bones['runner_horizontal_b'].position.y, - runnerHorizontalZ ));
            if( bones['runner_horizontal_f'] ) bones['runner_horizontal_f'].setPosition(new Vector3( bones['runner_horizontal_f'].position.x, bones['runner_horizontal_f'].position.y, runnerHorizontalZ ));
            if( bones['runner_b'] ) bones['runner_b'].setPosition(new Vector3( bones['runner_b'].position.x, bones['runner_b'].position.y, - runnerHorizontalZ ));
            if( bones['runner_f'] ) bones['runner_f'].setPosition(new Vector3( bones['runner_f'].position.x, bones['runner_f'].position.y, runnerHorizontalZ ));
        }

        mainRunner.setEnabled( false );

        const runners = this.getSettingData( 'runners' );
        if( ! runners || ! Array.isArray( runners ) ) {
            return;
        }

        const runner = runners.find( item => {
            return helpers.isValidSize( 'between', wallWidth + runnerWidth, Number( item['min_width'] ) + 0.01, Number( item['max_width'] ) )
        } );

        let oldRunnerLeft = this._scene.getMeshByName( 'runner_left' );
        if( null !== oldRunnerLeft ) {
            oldRunnerLeft.dispose();
        }

        let oldRunnerRight = this._scene.getMeshByName( 'runner_right' );
        if( null !== oldRunnerRight ) {
            oldRunnerRight.dispose();
        }
        
        for( let r = 0; r < this._numberRunner; r++ ) {
            let oldRunner = this._scene.getMeshByName( `runner_${r}` );
            if( null !== oldRunner ) {
                oldRunner.dispose();
            }
        }

        if( 'true' === outsideRunners.enable ) {
            wallWidth -= this._porchWidth;
        }

        if( this.isSidePorchCabin() ) {
            const cabinRunnerName   = 'cabin_runner_porch';
            let runnerPorch         = this._scene.getMeshByName( cabinRunnerName );
            if( null === runnerPorch ) {
                runnerPorch         = mainRunner.createInstance( cabinRunnerName );
            }
            const positionX         = helpers.toFixed3( ( this._bones['b_p_r1'].position.x + runnerWidth + this._bones['b_f_l1'].position.x ) / 2 );
            runnerPorch.position    = new Vector3( positionX, 0, 0 );
            runnerPorch.isPickable  = false;
            runnerPorch.setEnabled( true );
        }

            if( runner ) {
                this._isCloneRuner = true;
                const { number_runner, spacing_distribution: distribution } = runner;
                const { spacing_distribution_data } = distribution;
                const numberRunner = number_runner < 2 ? 2 : number_runner;
                
                let spacingDistribution = [];
                try {
                    spacingDistribution = JSON.parse( atob( spacing_distribution_data ) );
                } catch ( e ) {}
                
                const edgeLimitLeft = spacingDistribution[0] || 0;
                const edgeLimitRight = spacingDistribution[numberRunner] || 0;

                let positionX       = wallWidth / 2 - edgeLimitLeft / 12;
                let positionXLeft   = positionX;
                let positionXRight  = wallWidth / 2 - edgeLimitRight / 12;
                let wrongNumber     = 'mainus_quaker' === this._shedType ? this._quakerDistanceX : 0;
                this._numberRunner  = numberRunner;

                if( 'true' === outsideRunners.enable ) {
                    const bonesFrom = outsideRunners.from.trim().toLowerCase();
                    const bonesTo   = outsideRunners.to.trim().toLowerCase();

                    if( this._bones[bonesFrom] && this._bones[bonesTo] ) {
                        positionXLeft   = Math.abs( this._bones[bonesFrom].position.x ) - runnerWidth / 2 - edgeLimitLeft / 12;
                        positionXRight  = Math.abs( this._bones[bonesTo].position.x ) - runnerWidth / 2 - edgeLimitLeft / 12;

                        if( 'side-to-side' === runnerDirection ) {
                            positionXLeft   = Math.abs( this._bones[bonesFrom].position.z ) - runnerWidth / 2 - edgeLimitLeft / 12;
                            positionXRight  = Math.abs( this._bones[bonesTo].position.z ) - runnerWidth / 2 - edgeLimitLeft / 12;
                        }

                        wallWidth       = Vector3.Distance( this._bones[bonesFrom].position, this._bones[bonesTo].position ) - runnerWidth;
                        positionX       = positionXLeft;
                    }
                }

                let runnerLeft = this._scene.getMeshByName( 'runner_left' );
                if( null === runnerLeft ) {
                    runnerLeft = mainRunner.createInstance( 'runner_left' );
                }
                runnerLeft.setEnabled( true );

                let runnerRight = this._scene.getMeshByName( 'runner_right' );
                if( null === runnerRight ) {
                    runnerRight = mainRunner.createInstance( 'runner_right' );
                }
                runnerRight.setEnabled( true );

                if( ! isHorizontalRunner ) {
                    if( 'structure_saltbox_mwshed' === this._shedType ) {                        
                        positionXLeft   = Math.abs( this._bones['b_b_l1'].position.x ) + runnerWidth - edgeLimitLeft / 12;
                        positionXRight  = Math.abs( this._bones['b_b_r4'].position.x ) - runnerWidth * 2 - edgeLimitLeft / 12;
                    }

                    if( 'side-to-side' === runnerDirection ) {
                        if( 'mainus_quaker' === this._shedType ) {
                            wrongNumber *= -1;
                        }
                        runnerLeft.position     = new Vector3( 0, 0, - positionXLeft - wrongNumber + porchWidth / 2 );
                        runnerRight.position    = new Vector3( 0, 0, positionXRight - wrongNumber + porchWidth / 2 );
                    } else {
                        runnerLeft.position     = new Vector3( - positionXLeft - wrongNumber, 0, 0 );
                        runnerRight.position    = new Vector3( positionXRight - wrongNumber, 0, 0 );
                    }
                    
                    runnerLeft.isPickable   = false;
                    runnerRight.isPickable  = false;
                } else {
                    if( 'side-to-side' === runnerDirection ) {
                        runnerLeft.position     = new Vector3( 0, 0, - positionXLeft - wrongNumber + porchWidth / 2 );
                        runnerRight.position    = new Vector3( 0, 0, positionXRight - wrongNumber + porchWidth / 2 );
                    } else {
                        runnerLeft.position     = new Vector3( - positionXLeft - wrongNumber, 0, 0 );
                        runnerRight.position    = new Vector3( positionXRight - wrongNumber, 0, 0 );
                    }

                    runnerLeft.isPickable   = false;
                    runnerRight.isPickable  = false;
                }

                if( 'structure_saltbox_mwshed' === this._shedType ) {
                    positionX = Math.abs( this._bones['b_b_l1'].position.x ) + runnerWidth - edgeLimitLeft / 12;

                }

                if( numberRunner > 2 ) {
                    const adjustedWallWidth = wallWidth - ( edgeLimitLeft / 12 + edgeLimitRight / 12 );
                    const spacingRatio = adjustedWallWidth / ( adjustedWallWidth + runnerWidth );
                    const distributeArgs = this.distributeRunnerSpacing( spacingDistribution, wallWidth + runnerWidth );
                    let currentPositionX = - positionX;

                    for( let j = 0; j < numberRunner - 2; j++ ) {
                        const runnerSpacing = ( distributeArgs[j+1] * spacingRatio );

                        currentPositionX += helpers.toFixed3( runnerSpacing );
                        let runnerClone = this._scene.getMeshByName( `runner_${j}` );
                        if( null === runnerClone ) {
                            runnerClone = mainRunner.createInstance( `runner_${j}` );
                        }
                        runnerClone.isPickable = false;
                        runnerClone.setEnabled( true );

                        if( 'side-to-side' === runnerDirection ) {
                            runnerClone.position = new Vector3( 0, 0, currentPositionX - wrongNumber + porchWidth / 2 );
                        } else {
                            runnerClone.position = new Vector3( currentPositionX - wrongNumber, 0, 0 );
                        }
                    }
                }
                
                const deckObj = this.getMeshByName( 'deck_obj' );
                if( this.isExternalPorch() && ! this.is2StoryLeanTo() && deckObj ) {
                    let runnerPorch = this._scene.getMeshByName( 'runner_porch' );
                    if( ! runnerPorch ) runnerPorch = mainRunner.createInstance( 'runner_porch' );
                        runnerPorch.setEnabled( true );
                        runnerPorch.isPickable   = false;
                    if( 'left' === this._externalPorchLocation ) {
                        runnerPorch.position     = new Vector3( - positionXLeft - this._porchWidth, 0, 0 );
                        if( 'side-to-side' === runnerDirection ) {
                            runnerPorch.setEnabled( false );
                        }
                    } else {
                        if( 'side-to-side' === runnerDirection ) {
                            runnerPorch.position = new Vector3( 0, 0, positionXLeft + this._porchWidth );
                        } else {
                            runnerPorch.setEnabled( false );
                        }
                    }
                }
            } else {
                this._isCloneRuner  = false;
                let runnerPorch     = this._scene.getMeshByName( 'runner_porch' );
                if( runnerPorch ) runnerPorch.setEnabled( false );
            }
    }

    /**
     * Distributes a given array of values into a given total length.
     * 
     * If the array contains empty values, the given total length will be distributed evenly.
     * If the array does not contain empty values, the given total length will be ignored.
     * 
     * @param {string[]} runners       The array of values to distribute.
     * @param {number}   totalLength The total length to distribute into.
     * 
     * @returns {string[]} The distributed array of values.
     */
    distributeRunnerSpacing = ( runners, totalLength ) => {
        const result = runners.map( val => ( val === '' ? val : Number( val ) / 12 ) );

        const availableSpace = totalLength - result[0] - result[result.length - 1];
        const emptyIndices = [];
        let totalFilled = result.reduce( ( sum, val, i ) => {
            if( i > 0 && i < result.length - 1 ) {
                if( val === '' ) emptyIndices.push( i );
                else sum += val;
            }
            return sum;
        }, 0 );
    
        const totalSpacing = availableSpace - totalFilled;
        if ( totalSpacing <= 0 || emptyIndices.length === 0 ) return result.map( String );
    
        const distributedValue = helpers.toFixed3( totalSpacing / emptyIndices.length );
        emptyIndices.forEach( i => ( result[i] = distributedValue ) );
    
        return result.map( String );
    };

    /**
     * Check model use default runner color or custom runner color
     * 
     * @returns {boolean}
     */
    isDefaultRunnerColor() {
        const useDefault = jQuery('#js-runner-use-default-color').data('runner-use-default-color');

        return useDefault === 1;
    }

    /**
     * Update runner color
     */
    updateRunnerColor = () => {
        if( this.isDefaultRunnerColor() ) {
            return;
        }

        const runnerColor = this.getRunnerColor();
        if( ! runnerColor ) {
            return;
        }

        let material;
        if( this.isEnableOuterJoist() ) {
            material = this.getMaterialByName('siding_floor_mat');
        } else {
            material = this.getMaterialByName('runner_mat');
        }

        if( ! material ) {
            return;
        }

        material.forEach( m => {
            m.diffuseColor = Color3.FromHexString(runnerColor);
        });
    }

    /**
     * Update Deck and post railing color
     */
    updatePorchColor = () => {
        const _this         = this;
        const postColor     = jQuery( "#js-post-railing-data" ).data( 'color' ) || '#7a372a';
        const postMaterial  = jQuery( "#js-post-railing-data" ).data( 'materials' ) || 'posts_mat';

        const deckColor     = jQuery( "#js-deck-data" ).data( 'color' ) || '#D9BF86';
        const deckMaterial  = jQuery( "#js-deck-data" ).data( 'materials' ) || 'floor_mat';

        if( postMaterial && postColor ){
            const listMaterials   = postMaterial.split( '|' );
            const postColor3      = Color3.FromHexString( postColor ); 

            listMaterials.forEach( key  => {
                const postMat = _this.getMaterialByName( key );

                if( postMat ) postMat.forEach( mat => {
                    mat.diffuseColor = postColor3;
                });
            });
        }

        if( deckMaterial && deckColor ){
            const listMaterials   = deckMaterial.split( '|' );
            const deckColor3      = Color3.FromHexString( deckColor ); 

            listMaterials.forEach( key  => {
                const deckMat = _this.getMaterialByName( key );

                if( deckMat ) deckMat.forEach( mat => {
                    mat.diffuseColor = deckColor3;
                });
            });            
        }
    }

    updatePorchMesh = () => {
        if( ! this.isFrontPorch() && ! this.isSidePorch() && 'structure_ranch_cabin' !== this._shedType && ! this.isSidePorchCabin() ) return false;

        const postObj = this.getMeshByName( 'column_obj' );
        if( postObj ) this._postWidth = +( postObj.geometry.extend.maximum.z - postObj.geometry.extend.minimum.z ).toFixed( 3 );

        const meshs  = this.isEnableBrace() ? [ 'column_obj', 'posts_obj' ] : [ 'column_braces_obj', 'posts_braces_obj' ];

        meshs.forEach( k => {
            const mesh  = this.getMeshByName( k );
            if( mesh ) mesh.dispose(); 
        });
    }

    updateDeckingDirection = () => {
        if( ! this.isRevertDecking() ) return false;

        const _this = this;
        const mats  = [ 'floor_mat' ];
        
        mats.forEach( k => {
            const mat = _this.getMaterialByName( k );

            if( mat ){
                mat.forEach( m => {
                    if( m.bumpTexture ) m.bumpTexture['wAng']             = Math.PI / 2;
                    if( m.diffuseTexture ) m.diffuseTexture['wAng']       = Math.PI / 2;
                    if( m.specularTexture ) m.specularTexture['wAng']     = Math.PI / 2;
                });
            }
        });        
    }

    getRunnerColor = () => {
        const runnerColor = jQuery('#js-runner-custom-color').data('runner-custom-color');
        if( ! runnerColor ) {
            return false;
        }

        return runnerColor;
    }

    /**
     * Get runner direction
     * 
     * @returns {string} Runner direction (gable-end-to-gable-end or side-to-side)
     */
    getRunnerDirection = () => {
        const initialDirection = jQuery( '#js-runner-custom-direction' ).data( 'runner-direction' );
        if( ! initialDirection ) {
            return false;
        }
    
        if( 'follow-width' !== initialDirection && 'follow-length' !== initialDirection ) {
            return initialDirection;
        }

        const width = this.getWidth();
        const depth = this.getDepth();
        if( width == depth ) {
            return this.getSettingData( 'runner_direction' );
        }
    
        const followWidthDirection  = width > depth ? 'gable-end-to-gable-end' : 'side-to-side';
        const followLengthDirection = width > depth ? 'side-to-side' : 'gable-end-to-gable-end';
    
        return 'follow-width' === initialDirection ? followWidthDirection : followLengthDirection;
    }

    isWindowPositionFixedEnable = () => {
        return 1 == jQuery('#js-window-height-fixed-active').data('window-height-fixed-active');
    }

    /**
    * Get settings Applied to Transom/Octagon windows?
    * @returns {Boolean}
    */
    enableFixedHeightToTransom = () => {
        return 1 == jQuery('#js-fixed-height-transom-active').data('fixed-height-transom-active');
    }

    /**
    * Check Window is octagon/Transom type
    * @param {string} componentName Use component key for get transom/octagon
    * @returns {Boolean}
    */
    isTransomOctagonWindow = componentName => {
        const componentData = this._componentData[ `${componentName}` ];
        
        if( undefined === componentData ) return false;

        return "window_transom" === componentData['sub_category'] || "window_octagon" ===  componentData['sub_category'];
    }

    /**
    * Apply Fixed height windows logics
    * @param {string} componentType    Only apply  windows type
    * @param {string} componentName    Use component key for get transom/octagon
    * @param {string} side             Apply special side wall
    * @returns {Boolean}
    */
    applyFixedHeightWindows = ( side, componentType, componentName ) => {
        if( ! this.isWindowPositionFixedEnable() ) return false;
        if( 'windows' !== componentType ) return false;
        if( ! this.isMainSide( side ) ) return false;
        if( ! this.enableFixedHeightToTransom() && this.isTransomOctagonWindow( componentName ) ) {
            return false;
        }

        return true;
    }

    getMainSides = () => {
        return [ 'front', 'back', 'left', 'right', 'inner_front', 'inner_left', 'left_front', 'left_back', 'left_inner_porch', 'front_inner_porch', 'right_inner_porch', 'inner_right', 'diagonal_right', 'inner_back' ];
    }
    
    isMainSide = side => {
        const sideWalls = this.getMainSides();
        return -1 !== sideWalls.indexOf( side );
    }

    getFixedHeightForWindowsType = () => {
        return jQuery('#js-fixed-height-options').data('fixed-height-options');
    }

    isFollowMainDoor = () => {
        return 'door' === this.getFixedHeightForWindowsType();
    }

    isOffsetFromFloorTop = () => {
        return 'floor_top' === this.getFixedHeightForWindowsType();
    }

    isFollowDoorSameSiding = () => {
        return 1 === jQuery('#js-fixed-height-follow-door-same-siding').data('fixed-height-options');
    }

    getFixedFromFloorNumber = () => {
        if( jQuery('#js-fixed-from-floor-number').length ) {
            return Number( jQuery('#js-fixed-from-floor-number').data('fixed-from-floor-number') ) / 12;
        }

        return 0;
    }

    getFixedFromFloorTopNumber = () => {
        if( jQuery('#js-fixed-height-options-floor-top').length ) {
            return Number( jQuery('#js-fixed-height-options-floor-top').data('fixed-from-floor-number') ) / 12;
        }

        return 0;
    }

    /**
     * 
     * @param {String} type             Component Type
     * @param {Number} componentHeight  Component Height
     * @param {String} side             Current side wall
     * @returns 
     */
    getWindowsFixedPositionY = ( type, componentHeight, side ) => {
        const floorPorch = this.getDoorFixedHeight( side );

        if( this.isFollowMainDoor() ) {
            if( this._doorFixedTarget.length ) {
                const wallHeight = this.getWallHeight();
                if( wallHeight <= this.getDoorHeightFixedWindows( side ) && ( ( 'gambrel' !== this.getRoofShape() && ! this.isChickenCoop() ) || ( ( 'left' === side || 'right' === side ) && 'gambrel' === this.getRoofShape() ) ) ) {
                    return wallHeight - ( 8 /12 ) + floorPorch;
                }
                return this.getDoorHeightFixedWindows( side ) + floorPorch;
            }
        } else if( this.isOffsetFromFloorTop() ) {
            return this.getFixedFromFloorTopNumber();
        } else {
            return this.getFixedFromFloorNumber() + componentHeight + floorPorch;
        }
    }

    /**
     * Get door height is position fixed of windows
     * @param {String} side 
     * @returns {Number}
     */
    getDoorHeightFixedWindows = side => {
        let components  = this._doorFixedTarget;
        if( components.length === 1 ) {
            return components[0].componentHeight;
        } else if( components.length > 1 ) {
            if( this.isFollowDoorSameSiding() ) {
                const compInSide = components.filter( d => side === d.side );
                if( compInSide.length ) {
                    return this.getDoorHeightHighest( compInSide );
                }
            }

            const sideWalls = this.getMainSides();
            for( let i = 0; i < sideWalls.length; i++ ) {
                const compInSide = components.filter( d => sideWalls[i] === d.side );
                if( compInSide.length ) {
                    return this.getDoorHeightHighest( compInSide );
                }
            }
        }

        return 0;
    }

    isRequireDoor = () => {
        return this.isWindowPositionFixedEnable() && this.isFollowMainDoor() && this._doorFixedTarget.length === 0;
    }

    addNotificationRequiredDoor = () => {
        if( ! this.isRequireDoor() ) {
            return;
        }

        jQuery( '.no-door-notice' ).remove();
        jQuery( '.group-upgrade-door .ssb-configurator-group-name' ).after( helpers.addWarningMessage( 'no-door-notice', NOTICES.requireDoor ) );
        jQuery( 'button[name="add-to-cart"]' ).prop( "disabled", true );
    }

    removeNotificationRequiredDoor = () => {
        if( this.isWindowPositionFixedEnable() && this._doorFixedTarget.length !== 0 ) {
            jQuery( '.no-door-notice' ).remove();
            
            if( this.isEnableAddToCartButton() ) {
                jQuery( '.woocommerce.single .single_add_to_cart_button, .woocommerce.single .group-button, .ssb-mobile-button-request-a-quote' ).prop( 'disabled', false );
                jQuery( '.woocommerce.single .single_add_to_cart_button, .woocommerce.single .group-button, .ssb-mobile-button-request-a-quote' ).removeClass( 'is_disabled' );
                jQuery('.show-admin-price-wrap').show();
            } else {
                jQuery( '.woocommerce.single .single_add_to_cart_button, .woocommerce.single .group-button, .ssb-mobile-button-request-a-quote' ).prop( 'disabled', true );
                jQuery( '.woocommerce.single .single_add_to_cart_button, .woocommerce.single .group-button, .ssb-mobile-button-request-a-quote' ).addClass( 'is_disabled' );
            }
        }
    }

    /**
     * Return door height highest  of list door in Wall
     * @param {Object} compDoorsInSide 
     * @returns {Number}
     */
    getDoorHeightHighest = compDoorsInSide => {
        if( compDoorsInSide.length === 0 ) {
            return 0;
        }

        if( compDoorsInSide.length === 1 ) {
            return compDoorsInSide[0].componentHeight;
        }
        
        compDoorsInSide.sort( ( a, b ) => b.componentHeight - a.componentHeight  );
        return compDoorsInSide[0].componentHeight;
    }
    /**
     * Set default runner
     */
    setDefaultRunner = () => {
        if( this.isOldModel() ) {
            return;
        }

        if( this.isEnableOuterJoist() ) {
            let runnerMesh: Mesh;
            if( this._runnerType === this._verticalRunner ) {
                runnerMesh = this._scene.getMeshByName( 'runner_obj' ) as Mesh;
            } else if( this._runnerType === this._horizontalRunner ) {
                runnerMesh = this._scene.getMeshByName( 'runner_horizontal_obj' ) as Mesh;
            }

            if( undefined !== runnerMesh && null !== runnerMesh ) {
                runnerMesh.dispose();
            }
        } else {
            const outerJoistMesh = this._scene.getMeshByName( 'outer_joist_obj' ) as Mesh;
            if( undefined !== outerJoistMesh && null !== outerJoistMesh ) {
                outerJoistMesh.dispose();
            }
        }
    }

    /**
     * Check condition and clone runner
     */
    cloneRunner = () => {
        if( this.isOldModel() ) {
            this.cloneRunnerForOldModel();
        } else {
            this.cloneRunnerForNewModel();
        }
    }

    /**
     * Check model old or new
     * 
     * @returns {Boolean}
     */
    isOldModel = () => {
        const oldModels = [ 'lakeside', 'pergola', 'greenhouse', 'pavilion' ];
        if( -1 !== oldModels.indexOf( this._shedType ) && -1 !== oldModels.indexOf( this._shedTypeVersion ) ) {
            return true;
        }

        return false;
    }

    /**
     * Check is enable outer joist
     * 
     * @returns {boolean}
     */
    isEnableOuterJoist = () => {
        return 1 === jQuery('#js-runner-enable-outer-joist').data('runner-enable-outer-joist');
    }

    isEnableLeanTo = () => {
        return 'enabled_leanto' === jQuery( '.lean-to [name=ssb-configurator-upgrade-lean-to]:checked' ).data( 'key' );
    }

    isEnableExternalPorch = () => {
        return 'enabled_porch' === jQuery( '.external-porch [name=ssb-configurator-upgrade-external-porch]:checked' ).data( 'key' );
    }

    /**
     * Clone runner for old model (pergola, greenhouse, pavilion)
     */
    cloneRunnerForOldModel = async () => {
        let _this = this;
        let width = _this.getWidth();

        let meshName = "runner_obj";
        let runnerObj = _this._scene.getMeshByName(meshName) as Mesh;

        if (runnerObj != null) {

            //Hide runner for kodiak site
            if( document.body.classList.contains('seadev-site-id-13') ){
                runnerObj.setEnabled(false);
                return;
            }

            let runnerObjClonedItems = [];
            let runnerObjInstances = runnerObj.instances;
        
            if (runnerObjInstances.length > 0) {
                _this._scene.getMeshByName(meshName+"_1").dispose();
                _this._scene.getMeshByName(meshName+"_2").dispose();
            }

            // Willow Lake Replicate runners for "Cottage with metal trim model" https://trello.com/c/MV6WlXJH/334-wlb-21-001-2210-replicate-runners-for-cottage-with-metal-trim-model-3hrs
            // Willow Lake Site ID = 8
            if( document.body.classList.contains('seadev-site-id-8') && _this._shedType === 'classic_cottage_with_metal_trim' ){
                runnerObj.setEnabled(false);

                let offsetX: number = 1;
                let positionX: number = 0;
                let positionY: number = 0;
                let positionZ: number = 0;
                
                if (width >= 12) {
                    offsetX = 3; 
                }else if( width >= 10){
                    offsetX = 2;
                }

                positionX = width / 2 - offsetX - 0.3;

                if (!_this._scene.getMeshByName(meshName+"_1")){
                    let runnerObjCloned1 = runnerObj.createInstance(meshName+"_1");
                    runnerObjCloned1.position = new Vector3(-positionX, positionY, positionZ);
                    runnerObjClonedItems.push(runnerObjCloned1);
                }

                if (!_this._scene.getMeshByName(meshName+"_2")){
                    let runnerObjCloned2 = runnerObj.createInstance(meshName+"_2");
                    runnerObjCloned2.position = new Vector3(positionX, positionY, positionZ);
                    runnerObjClonedItems.push(runnerObjCloned2);
                }

                return;
            }

            if (width >= 10) {
                let positionX: number = (width / 4) - 0.3;
                let positionY: number = 0;
                let positionZ: number = 0;

                runnerObj.setEnabled(true);
                
                if (!_this._scene.getMeshByName(meshName+"_1")){
                    let runnerObjCloned1 = runnerObj.createInstance(meshName+"_1");
                    runnerObjCloned1.position = new Vector3(-positionX, positionY, positionZ);
                    runnerObjClonedItems.push(runnerObjCloned1);
                }

                if (!_this._scene.getMeshByName(meshName+"_2")){
                    let runnerObjCloned2 = runnerObj.createInstance(meshName+"_2");
                    runnerObjCloned2.position = new Vector3(positionX, positionY, positionZ);
                    runnerObjClonedItems.push(runnerObjCloned2);
                }
            } else {
                runnerObj.setEnabled(false);
            }
        }
    }

    private cloneDogDoor() : void {
        let _this = this;
        let width = _this.getWidth();
        
        let meshName = "dogkennel_dog_door_cloned_obj";
        let dogDoorObj = _this._scene.getMeshByName(meshName) as Mesh;

        if (dogDoorObj != null) {

            let dogDoorObjClonedItems = [];
            let dogDoorObjInstances = dogDoorObj.instances;
        
            if (dogDoorObjInstances.length > 0) {
                _this._scene.getMeshByName(meshName+"_1").dispose();
                _this._scene.getMeshByName(meshName+"_2").dispose();
            }
                let positionX: number = (width / 4);
                let positionY: number = 0;
                let positionZ: number = 0;

                if (!_this._scene.getMeshByName(meshName+"_1")){
                    let dogDoorObjCloned1 = dogDoorObj.createInstance(meshName+"_1");
                    dogDoorObjCloned1.position = new Vector3(-positionX, positionY, positionZ);
                    dogDoorObjClonedItems.push(dogDoorObjCloned1);
                }

                if (!_this._scene.getMeshByName(meshName+"_2")){
                    let dogDoorObjCloned2 = dogDoorObj.createInstance(meshName+"_2");
                    dogDoorObjCloned2.position = new Vector3(positionX, positionY, positionZ);
                    dogDoorObjClonedItems.push(dogDoorObjCloned2);
                }

                dogDoorObj.setEnabled(false);
        }
    }

    /**
     * Clone Inner Frame for Greenhouse Polycarbonate
     * @param meshName 
     * @param direction 
     */
    private cloneInnerFrame( meshName, direction, space = 24 ) : void {
        let _this = this;
        let frameClonedMesh = _this._scene.getMeshByName( meshName ) as Mesh;

        if( 'structure_trading_post' === _this._shedType ){
            direction = 'vertical';
        }

        if( frameClonedMesh ) {
            let depth = 'vertical' === direction ? _this.getWidth() : _this.getDepth();

            if( 'structure_teahouse' !== _this._shedType && 'structure_lofted_horserunin' !== _this._shedType && 'structure_aframe_horserunin' !== _this._shedType && 'structure_dog_kennel' !== _this._shedType && ! _this.isCarport() ) {
                depth -= 4;
            }

            if( 'structure_trading_post' === _this._shedType ){
                depth = _this.getWidth();
            }            
            
            _this.removeClone( meshName );

            let countFrameMesh: number =  Math.round( depth / ( space / 12 ) );

            if( 'structure_trading_post' === _this._shedType ){
                countFrameMesh = Math.ceil( depth / ( 16 / 12 ) );
                
            }else if( _this.isCarport() ){
                countFrameMesh = Math.ceil( depth / _this._legDistance );
            }

            for( let index = 1; index < countFrameMesh; index++ ){
                if( !_this._scene.getMeshByName( `${meshName}_${index}` ) ){
                    let frameClonedMeshInstace = frameClonedMesh.createInstance( `${meshName}_${index}` );
                    frameClonedMeshInstace.alwaysSelectAsActiveMesh = true;
                    let positionZ = -( depth/2 ) + ( depth / countFrameMesh * index );
                    let position = new Vector3( frameClonedMesh.position.x, frameClonedMesh.position.y, positionZ );

                    if( 'vertical' === direction ){
                        let positionX = -( depth/2 ) + ( depth / countFrameMesh * index );
                        position = new Vector3( positionX, frameClonedMesh.position.y, frameClonedMesh.position.z );
                    }

                    frameClonedMeshInstace.position = position;
                }
            }

            frameClonedMesh.setEnabled( false );
        }
    }

    private instanceMesh( meshName, space, boneStart, boneEnd, parentName = '', cloneFull = false ) {
        if( space <= 0 ) return;
        const meshClone = this._scene.getMeshByName( meshName ) as Mesh;
        if( ! meshClone ) return;
        this.removeClone( meshName );

        const startPoint    = 'string' === typeof boneStart ? this._bones[boneStart].position : boneStart;
        const endPoint      = 'string' === typeof boneEnd ? this._bones[boneEnd].position : boneEnd;
        const distance      = Vector3.Distance( startPoint, endPoint );
        const divisions     = Math.ceil( distance / ( space / 12  ) );
        let start           = cloneFull ? 0 : 1;
        let end             = cloneFull ? divisions + 1 : divisions;
        let results = [];
        const parentMesh    = '' !== parentName ? this.getMeshByName( parentName ) : false;
        for( start; start < end; start++ ) {
            const instance                      = meshClone.createInstance( `${meshName}_${start}` );
            const position = Vector3.Lerp( startPoint, endPoint, ( 1 / divisions ) * start );
            instance.position                   = position;
            results.push( position );
            instance.isPickable                 = false;
            instance.alwaysSelectAsActiveMesh   = true;
            instance.setEnabled( true );
            if( parentMesh ) instance.setParent( parentMesh );
        }

        meshClone.setEnabled( false );

        return results;
    }

    /**
     * Clone Hat Channel for CarPort
     * @param meshName name of mesh for clone
     */
    private cloneHatChannel( meshName ) : void {
        const _this = this;
        const mesh  = _this.getMeshByName( meshName ) as Mesh

        if( ! mesh ) return;       
        
        const errorY        = _this.getRoofDegree() < 5 ? 0.03 : ( _this.getRoofDegree() < 10 ? 0.01 : 0 );
        const status        = 'metal' === _this.getRoofType() ? true : false;
        const leftHatName   = `${meshName}_left`;
        const rightHatName  = `${meshName}_right`;

        const instancesLength = mesh.instances.length;
        if( instancesLength > 0 ) {
            for( let i = 1; i <= instancesLength; i++ ) {
                let oldMeshLeft     = _this.getMeshByName( `${leftHatName}_${i}` );
                let oldMeshRight    = _this.getMeshByName( `${rightHatName}_${i}` );
                if( oldMeshLeft ) oldMeshLeft.dispose();
                if( oldMeshRight ) oldMeshRight.dispose();
            }
        } 
        
        const leftHat       = _this.getMeshByName( leftHatName ) || mesh.createInstance( leftHatName );
        const rightHat      = _this.getMeshByName( rightHatName ) || mesh.createInstance( rightHatName );
        const rotation      = _this.getRoofDegree() * Math.PI / 180;
        const startPoint    = _this._bones['b_b_t'].position;
        const endPoint      = _this._bones['b_b_r3'] ? _this._bones['b_b_r3'].position : _this._bones['b_b_r2'].position;
        let distance        = Vector3.Distance( startPoint, endPoint );

        const firstPosition = Vector3.Lerp(
            startPoint, endPoint,
            ( 6/12 ) / distance 
        );
        
        if( _this._bones['r_b_l2'] ) _this._bones['r_b_l2'].setPosition( new Vector3( - firstPosition.x, firstPosition.y, - _this.getDepth() / 2 ) );
        if( _this._bones['r_f_l2'] ) _this._bones['r_f_l2'].setPosition( new Vector3( - firstPosition.x, firstPosition.y,  _this.getDepth() / 2 ) );
        if( _this._bones['r_b_r2'] ) _this._bones['r_b_r2'].setPosition( new Vector3( firstPosition.x, firstPosition.y, - _this.getDepth() / 2 ) );
        if( _this._bones['r_f_r2'] ) _this._bones['r_f_r2'].setPosition( new Vector3( firstPosition.x, firstPosition.y,  _this.getDepth() / 2 ) );

        leftHat.position    = new Vector3( -firstPosition.x, firstPosition.y, 0 );
        leftHat.rotation    = new Vector3( 0, 0, rotation);
        leftHat.setEnabled( status );

        rightHat.position   = new Vector3( firstPosition.x, firstPosition.y, 0 );
        rightHat.rotation   = new Vector3( 0, 0, - rotation);
        rightHat.setEnabled( status );

        if( 'structure_greenhouse_polycarbon' === this._shedType ) {
            leftHat.setEnabled( false );
            rightHat.setEnabled( false );
        }

        distance            = Vector3.Distance( firstPosition, endPoint );
        const legSpace      = _this.getSettingData( 'hat_channel_distance' ) || 2;
        const divisions     = Math.ceil( distance / legSpace );

        for( let i = 1; i < divisions; i++ ) {
            
            const newClonePosition = Vector3.Lerp(
                firstPosition, endPoint,
                ( 1 / divisions ) * i
            );
            
            const meshCloneLeft         = mesh.createInstance( `${leftHatName}_${i}` );
            meshCloneLeft.position      = new Vector3( - newClonePosition.x, newClonePosition.y - errorY, 0 );
            meshCloneLeft.rotation      = new Vector3( 0, 0, rotation);
            meshCloneLeft.alwaysSelectAsActiveMesh = true;
            meshCloneLeft.setEnabled( status );
            
            const meshCloneRight        = mesh.createInstance( `${rightHatName}_${i}` );
            meshCloneRight.position     = new Vector3( newClonePosition.x, newClonePosition.y - errorY, 0 );
            meshCloneRight.rotation     = new Vector3( 0, 0, - rotation);
            meshCloneRight.alwaysSelectAsActiveMesh = true;
            meshCloneRight.setEnabled( status );
        }

        mesh.setEnabled( false );
        
    }
    

    private cloneFrame() : void {
        let _this = this;
        let depth = _this.getDepth();

        let meshName = "greenhouse_frame_cloned_obj";
        let frameClonedMesh = _this._scene.getMeshByName(meshName) as Mesh;

            if (frameClonedMesh != null) {

                let frameInstances = frameClonedMesh.instances;
            
                if (frameInstances.length > 0) {
                    let frameInstancesLength = frameInstances.length;
                    for (let index = 1; index <= frameInstancesLength; index++) {
                        _this._scene.getMeshByName(`${meshName}_${index}`).dispose();
                    }
                }

                let countFrameMesh: number =  Math.round(depth / 2);
                for (let index = 1; index < countFrameMesh; index++) {  
                    if (!_this._scene.getMeshByName(`${meshName}_${index}`)){
                        let frameClonedMeshInstace = frameClonedMesh.createInstance(`${meshName}_${index}`);
                        frameClonedMeshInstace.alwaysSelectAsActiveMesh = true;
                        let positionZ = -(depth/2) + (depth / countFrameMesh * index);
                        frameClonedMeshInstace.position = new Vector3(frameClonedMesh.position.x, frameClonedMesh.position.y, positionZ);
                    }
                }

                frameClonedMesh.setEnabled(false);
            }
    }

    private clonePost(meshName) : void {
        let _this = this;
        let depth = _this.getDepth();

        let postClonedMesh = _this._scene.getMeshByName(meshName) as Mesh;
            if (postClonedMesh != null) {
                let postInstances = postClonedMesh.instances;
                if (postInstances.length > 0) {
                    let postInstancesLength = postInstances.length;
                    for (let index = 1; index <= postInstancesLength; index++) {
                        _this._scene.getMeshByName(`${meshName}_${index}`).dispose();
                    }
                }

                let countPostMesh: number =  Math.floor(depth / 15 + 1);
                for (let index = 1; index < countPostMesh; index++) {  
                    if (_this._scene.getMeshByName(`${meshName}_${index}`) != null) {
                        _this._scene.getMeshByName(`${meshName}_${index}`).dispose();
                    }

                    let postClonedMeshInstance = postClonedMesh.createInstance(`${meshName}_${index}`);
                    postClonedMeshInstance.alwaysSelectAsActiveMesh = true;
                    let positionZ = -(depth/2) + (depth / countPostMesh * index);
                    postClonedMeshInstance.position = new Vector3(postClonedMesh.position.x, postClonedMesh.position.y, positionZ);
                }

                postClonedMesh.setEnabled(false);
            }
    }

    private cloneRafter = async () => {
        let _this = this;
        let depth = _this.getDepth();

        let meshName = "pavilion_rafter_cloned_obj";
        let rafterClonedMesh = _this._scene.getMeshByName(meshName) as Mesh;

        if( rafterClonedMesh ) {
            for( let i = 0; i < _this._instances['pavilionRafter'].length; i++ ) {
                await _this.removeInstance( _this._instances['pavilionRafter'][i] );
            }

            _this._instances['pavilionRafter'] = [];

            // Create cloned meshes when resize
            let countPostMesh: number = Math.floor(depth / 15 + 1);
            let postSpacing: number =  depth / countPostMesh;
            for (let postIndex = 0; postIndex < countPostMesh; postIndex++) {  
                let countCrossBeamMesh: number =  Math.floor(postSpacing / 2);
                let postOffset = postSpacing * postIndex;
                
                for (let index = 0; index < countCrossBeamMesh; index++) {  
                    let cloneName = meshName + postIndex.toString() + index.toString();
                    
                    if ( _this._scene.getMeshByName(cloneName) != null ) {
                        _this._scene.getMeshByName(cloneName).dispose();
                    }
                    
                    _this._instances['pavilionRafter'].push( cloneName );
                    let rafterClonedMeshInstance = rafterClonedMesh.createInstance(cloneName);
                    rafterClonedMeshInstance.alwaysSelectAsActiveMesh = true;
                    let positionZ = -(depth/2) + (postSpacing / countCrossBeamMesh * index) + postOffset;
                    rafterClonedMeshInstance.position = new Vector3(rafterClonedMesh.position.x, rafterClonedMesh.position.y, positionZ); 
                }
            }

            rafterClonedMesh.setEnabled(false);
        }
    }

    private cloneCrossBeam() : void {
        let _this = this;
        let depth = _this.getDepth();

        let meshName = "pergola_cross_beam_cloned_obj";
        let crossBeamClonedMesh = _this._scene.getMeshByName(meshName) as Mesh;

        if (crossBeamClonedMesh != null) {
            let crossBeamInstances = crossBeamClonedMesh.instances;
            let crossBeamInstancesLength = crossBeamInstances.length;

            // Remove old cloned meshes when resize
            if (crossBeamInstances.length > 0) {    
                for (let postIndex = 0; postIndex < 10; postIndex++) {
                    for (let index = 0; index <= crossBeamInstancesLength; index++) {   
                        let cloneName = meshName + postIndex.toString() + index.toString();
                        if ( _this._scene.getMeshByName(cloneName) != null ) {
                            _this._scene.getMeshByName(cloneName).dispose();
                        }
                    }
                }
            }

            // Create cloned meshes when resize
            let countPostMesh: number = Math.floor(depth / 15 + 1);
            let postSpacing: number =  depth / countPostMesh;
            for (let postIndex = 0; postIndex < countPostMesh; postIndex++) {  
                let countCrossBeamMesh: number =  Math.floor(postSpacing / 2);
                let postOffset = postSpacing * postIndex;
                
                for (let index = 0; index <= countCrossBeamMesh; index++) {  
                    let cloneName = meshName + postIndex.toString() + index.toString();
                    
                    if ( _this._scene.getMeshByName(cloneName) != null ) {
                        _this._scene.getMeshByName(cloneName).dispose();
                    }
                    
                    let crossBeamClonedMeshInstance = crossBeamClonedMesh.createInstance(cloneName);
                    crossBeamClonedMeshInstance.alwaysSelectAsActiveMesh = true;
                    let positionZ = -(depth/2) + (postSpacing / countCrossBeamMesh * index) + postOffset;
                    crossBeamClonedMeshInstance.position = new Vector3(crossBeamClonedMesh.position.x, crossBeamClonedMesh.position.y, positionZ); 
                }
            }

            crossBeamClonedMesh.setEnabled(false);
        }
    }

    private clonePurlin() : void {
        let _this = this;
        let width = _this.getWidth();

        let meshName = "pergola_purlin_cloned_obj";
        let purlinClonedMesh = _this._scene.getMeshByName(meshName) as Mesh;

        if (purlinClonedMesh != null) {
            let purlinInstances = purlinClonedMesh.instances;
        
            if (purlinInstances.length > 0) {
                let purlinInstancesLength = purlinInstances.length;
                for (let index = 1; index < purlinInstancesLength; index++) {
                    _this._scene.getMeshByName(`${meshName}_${index}`).dispose();
                }
            }

            let countPurlinMesh: number = Math.floor( width / 2 );
            for ( let index = 0; index <= countPurlinMesh; index++) {  
                if (_this._scene.getMeshByName(`${meshName}_${index}`) != null) {
                    _this._scene.getMeshByName(`${meshName}_${index}`).dispose();
                }
                
                let purlinClonedMeshInstance = purlinClonedMesh.createInstance(`${meshName}_${index}`);
                purlinClonedMeshInstance.alwaysSelectAsActiveMesh = true;
                let positionX = -( width / 2 ) + ( width / ( countPurlinMesh ) * index );

                if( 0 === index ) positionX = -width/2;
                if( countPurlinMesh === index ) positionX = width/2;

                purlinClonedMeshInstance.position = new Vector3(positionX, purlinClonedMesh.position.y, purlinClonedMesh.position.z);
            }

            purlinClonedMesh.setEnabled(false);
        }
    }

    private doClone() {
        this.cloneMetalRoof();
        this.cloneMetalHorizontalRoof();
        this.clonePorchMetalRoof();
        this.cloneRunner();
        this.cloneBaluster();
        this.cloneBalusterForTeaHouse();
        this.cloneRoofRaftersForTradingPost();
        this.cloneExternalPost();
        this.cloneRafter();
        this.cloneCrossBeam();
        this.clonePurlin();
        this.clonePost('pergola_post_cloned_obj');
        this.clonePost('pavilion_post_cloned_obj');
        if( 0 === jQuery( '.ssb-configurator-options.frame input.ssb-option' ).length ) {
            this.cloneFrame();
            this.cloneInnerFrame( 'inner_frame_horizontal_clone_obj', 'horizontal' );
            this.cloneInnerFrame( 'inner_frame_vertical_clone_obj', 'vertical' );
            this.cloneInnerFrame( 'inner_frame_horizontal_clone_01_obj', 'vertical' );
            this.cloneInnerFrame( 'inner_frame_horizontal_clone_02_obj', 'horizontal' );
            this.cloneInnerFrame( 'inner_frame_horizontal_clone_03_obj', 'vertical' );
            this.cloneInnerFrame( 'inner_frame_horizontal_clone_04_obj', 'vertical' );
            if( 'carport_lean_to_standalone' !== this._shedType ) {
                this.cloneInnerFrame( 'frame_clone_obj', 'horizontal' );
            }
            if( 'structure_horse_shelter' !== this._shedType ) {
                this.instanceMesh( 'stub_back_clone_obj', 40, 'b_f_r1', 'b_f_m1' );
                this.instanceMesh( 'stub_back_clone_2_obj', 40, 'b_b_r1', 'b_f_m1' );        
            }

            this.instanceMesh( 'frame_window_f_clone_obj', 48, 'b_f_l1', 'b_f_r1' );
            this.instanceMesh( 'frame_window_b_clone_obj', 48, 'b_b_l1', 'b_b_r1' );
            this.instanceMesh( 'frame_window_lr_clone_obj', 48, 'b_b_r1', 'b_f_r1' );
        } else {
            this.removeFrameInObject();
        }
        
        this.initLegs();
        this.cloneHatChannel( 'hat_channel_obj' );
        this.cloneHatChannel( 'rafter_clone_obj' );
        this.logCabinCloneWall();
        this.carportHandleDoCloneSiding();
        this.cloneRafterComponent();
        if( this.is2StoryLeanTo() ) {
            const porchExternal = this._canvas.getAttribute( 'data-model-porch-external' );
            const porchData     = JSON.parse( atob( porchExternal ) );
            const distance      = 12 * Number( porchData.distance );
            this.instanceMesh( 'column_braces_obj', distance, 'porch_f_l1', 'porch_b_l1', 'porch_obj' );
        }

        this.handlePostAndBaluster();
    }

    private adjustCamera() {
        let z = 0;
        if ( this.isFrontPorch() ) {
            z = this._porchWidth / 2;
        }

        const beta      = this._camera.beta;
        const height    = this.getBuildingHeight();
        const depth     = this.getDepth();
        let width       = this.getWidth();
        let ratio       = 2.5;

        if( this.is2StoryLeanTo() ) {
            if( this.isEnableLeanTo() ) {
                width += atticModel.leanToWidth;
            }
            if( this.isEnableExternalPorch() ) {
                width += atticModel.porchWidth;
            }
        }
        
        if( this._canvas.width < 600 ) {
            ratio = 3;
        }

        let radius = Math.sqrt( width * width + depth * depth + height * height ) / 2;

        this._camera.setTarget( new Vector3( 0, this.getBuildingHeight() / 2, z ) );
        this._camera.radius = radius * ratio;
        this._camera.beta   = beta;
    }

    private render() : void {
        let _this = this;

        _this._engine.runRenderLoop(() => {
            _this._scene.render();
        });        

        // The canvas/window resize event handler.
        window.addEventListener( 'resize', () => {
            window.dispatchEvent( new CustomEvent( 'close-edit-component' ) );

            const resizeInterval = setInterval( () => {
                const canvasWidth   = jQuery( '.ssb-model-builder-inner' ).width();
                const bodyWidth     = jQuery( 'body' ).width();

                if( canvasWidth == bodyWidth || jQuery( window ).width() >= 1024 ) {
                    _this.resizeCanvas( resizeInterval );
                }
            }, 100 );

            setTimeout( () => {
                _this.resizeCanvas( resizeInterval );
            }, 1000 );
        } );
        
    }

    /**
     * Resize canvas and clear resize Interval
     * 
     * @param {number} resizeInterval Interval Id
     */
    resizeCanvas = ( resizeInterval ) => {
        this._canvas.style.height  = `${jQuery( '.ssb-model-builder-inner' ).height()}px`;
        this._canvas.style.width   = `${jQuery( '.ssb-model-builder-inner' ).width()}px`;
        this._engine.resize();

        if( jQuery( window ).width() <= 1024 ) {
            if( 'none' === jQuery( '.mobile-customize-panel' ).css( 'display' ) ) {
                jQuery('#mobile-custom-shed').click();
                jQuery('.icon-close').click();
            } else {
                jQuery('.icon-close').click();
                jQuery('#mobile-custom-shed').click();
            }
        }
        
        clearInterval( resizeInterval );
    }

    /**
     * Enable Vinyl shutter for Window
     * 
     * @param Array listMesh
     */
    enableVinylShutter( listMesh ) {
        listMesh.forEach( m => {
            if( -1 !== m.name.indexOf( '_shutter_vinyl_obj' ) ) {
                m.setEnabled( true );
            }

            if( -1 !== m.name.indexOf( '_shutter_wood_obj' ) ) {
                m.setEnabled( false );
            }
        });

        this.enableComponentMoveable();
    }

    /**
     * Enable Wood shutter for Window
     * 
     * @param Array listMesh
     */
    enableWoodShutter( listMesh ) {
        listMesh.forEach( m => {
            if( -1 !== m.name.indexOf( '_shutter_wood_obj' ) ) {
                m.setEnabled(true);
            }

            if( -1 !== m.name.indexOf( '_shutter_vinyl_obj' ) ) {
                m.setEnabled(false);
            }
        });
        this.enableComponentMoveable();
    }

    /**
     * Disable Window shutter
     * 
     * @param Array meshes
     */
    disableShuttersInComponent( meshes ) {
        meshes.forEach( m => {
            if( -1 !== m.name.indexOf( '_shutter_vinyl_obj' ) || -1 !== m.name.indexOf( '_shutter_wood_obj' ) ) {
                m.setEnabled( false );
            }
        });
    }

    /**
     * Get Mesh from walls by mesh key
     * 
     * @param String side Front|Back|Left|Right
     * @param key mesh key
     * @returns
     */
    getComponentByKey( key ) {
        const sides = [ 'front', 'back', 'left', 'right', 'roof', 'porch', 'dormer', 'roof_left', 'roof_right', 'inner_front', 'inner_left', 'left_front', 'left_back', 'front_inner_porch', 'right_inner_porch', 'left_inner_porch', 'inner_right', 'diagonal_right', 'inner_back', 'top_front', 'top_back', 'lean_to_front', 'lean_to_back', 'lean_to_right', 'top_right', 'top_left', 'floor' ];
        for( let i = 0; i < sides.length; i++ ) {
            if( undefined !== this._walls[sides[i]]["items"][key] ) {
                return this._walls[sides[i]]["items"][key];
            }
        }
    
        return false;
    }

    /**
     * Get Mesh from walls by side and mesh key
     * 
     * @param String side Front|Back|Left|Right
     * @param key mesh key
     * @returns 
     */
    getComponentBySideAndKey( side, key ) {
        if( this.validWallItem( side, key ) ) {
            return this._walls[side]["items"][key];
        }

        if( 'left' === side ) {
            let leftWalls = [ 'inner_left', 'left_front', 'left_back', 'front_inner_porch', 'left_inner_porch', 'right_inner_porch' ];

            if( this._isInterior ){
                leftWalls = [ 'inner_left', 'left_front', 'left_back', 'front_inner_porch' ];
            }

            for ( let i = 0; i < leftWalls.length; i ++ ) {
                if( this.validWallItem( leftWalls[i], key ) ) return this._walls[leftWalls[i]]["items"][key];
            }
        }

        if( 'front' === side ) {
            const frontWalls = [ 'inner_front', 'porch', 'diagonal_right' ];
            for ( let i = 0; i < frontWalls.length; i ++ ) {
                if( this.validWallItem( frontWalls[i], key ) ) return this._walls[frontWalls[i]]["items"][key];
            }
            
        }

        if( 'back' === side && this._isInterior ) {
            const frontWalls = [ 'left_inner_porch', 'inner_back' ];
            for ( let i = 0; i < frontWalls.length; i ++ ) {
                if( this.validWallItem( frontWalls[i], key ) ) return this._walls[frontWalls[i]]["items"][key];
            }
        }

        if( 'front' === side && this._isInterior ) {
            const frontWalls = [ 'right_inner_porch' ];
            for ( let i = 0; i < frontWalls.length; i ++ ) {
                if( this.validWallItem( frontWalls[i], key ) ) return this._walls[frontWalls[i]]["items"][key];
            }
        }

        if( 'right' === side ) {
            const rightWalls = [ 'inner_right', 'diagonal_right' ];
            for ( let i = 0; i < rightWalls.length; i ++ ) {
                if( this.validWallItem( rightWalls[i], key ) ) return this._walls[rightWalls[i]]["items"][key];
            }
        }

        return false;
    }

    validWallItem = ( side, key ) => this._walls[side] && this._walls[side]["items"][key];

    /**
     * Extend roof overhang
     * Resize roof by bones
     */
    updateRoofOverhang = () => {
        this.setCustomOverhangData();
        this.moveBones();
        this.cloneSiding3D();
        this.handleBonesScreenHouse();
        if( 'gambrel' === this.getRoofShape() ) {
            this.overhangGambrelRoof( this._customOverhangFront, this._customOverhangBack, this._customOverhangLeft, this._customOverhangRight );
        } else if( 'open_gable' === this.getRoofShape() || 'box_gable' === this.getRoofShape() ) {
            this.overhangOpenGableRoof( this._customOverhangFront, this._customOverhangBack, this._customOverhangLeft, this._customOverhangRight );
        } else if( 'saltbox_customize' === this.getRoofShape() ) {
            this.overhangCustomizedSaltboxRoof( this._customOverhangFront, this._customOverhangBack, this._customOverhangLeft, this._customOverhangRight );
        } else if( 'special' === this.getRoofShape() && 'structure_alpinechalet' === this._shedType ) {
            this.overhangSpecialRoof( this._customOverhangFront, this._customOverhangBack, this._customOverhangLeft, this._customOverhangRight );
        } else if( 'flat' === this.getRoofShape() ) {
            this.overhangFlatRoof( this._customOverhangFront, this._customOverhangBack, this._customOverhangLeft, this._customOverhangRight );
        } else if( 'saltbox' === this.getRoofShape() ) {
            this.overhangSaltboxRoof( this._customOverhangFront, this._customOverhangBack, this._customOverhangLeft, this._customOverhangRight );
        } else if( 'hip' === this.getRoofShape() ) {
            this.overhangHipRoof( this._customOverhangFront );
        } else if( 'monitor' === this.getRoofShape() ) {
            this.overhangMonitorRoof( this._customOverhangFront, this._customOverhangBack, this._customOverhangLeft, this._customOverhangRight );
        }

        this.drawRoof();
        this.cloneMetalRoof();
        this.cloneMetalHorizontalRoof();
        this.gableDormerHandleRoofColor();
        this.roofMoveBoneByModelKey();
        this.adjustUVs( true );
        this.cloneRafterComponent();
    }

    /**
     * Calculates Roof pitch from Radians
     * @param {number} opposite 
     * @param {number} adjacent default 12
     */
    getRoofPitchFromRadian( opposite, adjacent = 12 ) {
        return Math.atan( opposite / adjacent ) * 180 / Math.PI;
    }

    /**
     * Capture model image
     * 
     * @param   {string}        side front, back, left, right
     * @returns {base64Image}   data image
     */
    captureCartThumbnail = ( side ) => {
        this.removeLine();
        this.removeNoticeText();
        this.blurItemForInterior( false );
        
        let base64Image = '';
        let alpha       = this.getAlpha();
        let radius      = alpha;
        const zoom      = 1.5;
        switch( side ) {
            case 'front_left':
                alpha   = - alpha;
                break;
            case 'back_left':
                alpha   = - alpha;
                radius  = - radius;
                break;
            case 'back_right':
                radius  = - radius;
                break;
            case 'front':
                radius  = alpha * zoom;
                alpha   = 0;
                break;
            case 'back':
                radius  = - ( alpha * zoom );
                alpha   = 0;
                break;
            case 'left':
                alpha   = alpha * zoom;
                radius  = 0;
                break;
            case 'right':
                alpha   = - ( alpha * zoom );
                radius  = 0;
                break;
        }

        let targetY = 4;
        if( this.getWallHeight() > 10 ) {
            targetY = 7;
        } else if( this.getWallHeight() >= 7 ) {
            targetY = 4.5;
        }

        let thumbnailSceneCamera = new ArcRotateCamera( 'thumbnailSceneCamera', 0, 0, 0, new Vector3( 0, 0, 0 ), this._scene );
        thumbnailSceneCamera.setPosition( new Vector3( alpha, 5, radius ) );

        let z = 0;
        if( this.isFrontPorch() ){
            z = this._porchWidth / 2;
        }        

        thumbnailSceneCamera.setTarget( new Vector3( 0, targetY, z ) );

        Tools.CreateScreenshotUsingRenderTarget( this._engine, thumbnailSceneCamera, 512, data => {
            base64Image = data;
        });

        return base64Image;
    }

    /**
     * Captures a thumbnail image based on the specified side.
     * 
     * @param {string} side - The side for which to capture the thumbnail ('front_left', 'back_left', 'back_right', 'front', 'back', 'left', 'right').
     * 
     * @returns {Promise<string>} - A Promise that resolves with the base64 image data of the captured thumbnail.
     * 
     */
    captureCartThumbnailV2 = async ( side ) => {
        this.removeLine();
        this.removeNoticeText();
        this.blurItemForInterior( false );
        
        let alpha       = this.getAlpha();
        let radius      = alpha;
        const zoom      = 1.5;
        switch( side ) {
            case 'front_left':
                alpha   = - alpha;
                break;
            case 'back_left':
                alpha   = - alpha;
                radius  = - radius;
                break;
            case 'back_right':
                radius  = - radius;
                break;
            case 'front':
                radius  = alpha * zoom;
                alpha   = 0;
                break;
            case 'back':
                radius  = - ( alpha * zoom );
                alpha   = 0;
                break;
            case 'left':
                alpha   = alpha * zoom;
                radius  = 0;
                break;
            case 'right':
                alpha   = - ( alpha * zoom );
                radius  = 0;
                break;
        }

        let targetY = 4;
        if( this.getWallHeight() > 10 ) {
            targetY = 7;
        } else if( this.getWallHeight() >= 7 ) {
            targetY = 4.5;
        }

        let thumbnailSceneCamera = new ArcRotateCamera( 'thumbnailSceneCamera', 0, 0, 0, new Vector3( 0, 0, 0 ), this._scene );
        thumbnailSceneCamera.setPosition( new Vector3( alpha, 5, radius ) );

        let z = 0;
        if( this.isFrontPorch() ){
            z = this._porchWidth / 2;
        }        

        thumbnailSceneCamera.setTarget( new Vector3( 0, targetY, z ) );
    
        try {
            const base64Image = await Tools.CreateScreenshotUsingRenderTargetAsync( this._engine, thumbnailSceneCamera, 512 );
            return base64Image;
        } catch( error ) {
            console.error( 'Error capturing screenshot:', error );
            return '';
        }
    };

    /**
     * Capture an image of the scene
     * @returns {string} - a base64 encoded image
     */
    captureImage = async () => {
        try {
            return await Tools.CreateScreenshotUsingRenderTargetAsync( this._engine, this._camera, 512 );
        } catch( error ) {
            console.error( 'Error capturing image:', error );
            return '';
        }
    }

    /**
     * Get data for roof overhang
     * Get alpha for camera
     * 
     * @returns {number}
     */
    getAlpha = () => {
        const height        = this.getWallHeight();
        const width         = this.getWidth();
        let length          = this.getDepth();

        if( this.isFrontPorch() ) {
            length += this._porchWidth;
        }

        let max1 = width;
        let max2 = height;
        if( max1 < max2 ) {
            max1 = max2;
            max2 = width;
        }

        if( max2 < length ) {
            max2 = length;
        }

        if( max1 === height ) {
            max1 = this.getBuildingHeight();
        } else if( max2 === height ) {
            max2 = this.getBuildingHeight();
        }

        let alpha           = 14; // Define by width = 8 and length = 8
        const oldHypotenuse = 11.3138; // Calculate by width = 8 and length = 8
        const newHypotenuse = Math.sqrt( max1 * max1 + max2 * max2 );

        return newHypotenuse * alpha / oldHypotenuse;
    }

    getOverhangData = () => {
        if( 'structure_barn_2story_leanto' === this._shedType ) {
            let sides               = [];
            let overhang            = 0;
            const isEnableLeanTo    = this.isEnableLeanTo();
            const isEnablePorch     = 'enabled_porch' === jQuery( 'input.ssb_external-porch:checked' ).data( 'key' );

            if( ! isEnableLeanTo ) {
                sides.push( 'right' );
                overhang = atticModel.overhang;
            }
            
            if( ! isEnablePorch ) {
                sides.push( 'left' );
                overhang = atticModel.overhang;
            }

            return { overhang, sides };
        }

        const overhang = Number( jQuery( 'input.ssb_roof_overhang:checked' ).val() );
        if( isNaN( overhang ) ) {
            return false;
        }

        let sides = jQuery( 'input.ssb_roof_overhang:checked' ).attr( 'data-sides' );
        if( undefined === sides ) {
            return false;
        }

        sides = JSON.parse( window.atob( sides ) );

        return { overhang, sides };
    }

    /**
     * Set data for customize roof overhang
     */
    setCustomOverhangData = () => {
        this.resetCustomRoofOverhang();

        const overhangData = this.getOverhangData();
        if( false === overhangData ) {
            return;
        }

        const { overhang, sides } = overhangData;
        
        if( overhang === 0 && sides.length === 0 ) {
            this.resetCustomRoofOverhang();

            return;
        }

        for( let i = 0; i < sides.length; i++  ) {
            if( 'front' === sides[i] ) {
                this._customOverhangFront = + ( overhang / 12 ).toFixed( 3 );
            }

            if( 'back' === sides[i] ) {
                this._customOverhangBack = + ( overhang / 12 ).toFixed( 3 );
            }

            if( 'left' === sides[i] ) {
                this._customOverhangLeft = + ( overhang / 12 ).toFixed( 3 );
            }

            if( 'right' === sides[i] ) {
                this._customOverhangRight = + ( overhang / 12 ).toFixed( 3 );
            }
        }
    }

    /**
     * Reset custom roof overhang to 0
     */
    resetCustomRoofOverhang = () => {
        this._customOverhangFront   = 0;
        this._customOverhangBack    = 0;
        this._customOverhangLeft    = 0;
        this._customOverhangRight   = 0;
    }

    /**
     * Get default roof degree
     */
    getInitRoofDegree = () => {
        if( 'structure_capecod' === this._shedType ) {
            return 30.5;
        }

        if( 'structure_aframe_colonialbarns' === this._shedType ) {
            return 25.41;
        }

        if( 'structure_trading_post' === this._shedType ) {
            return 22.62;
        }

        if( 'open_gable' === this.getRoofShape() || 'saltbox' === this.getRoofShape() ) {
            if( 'structure_aframe_metal_frontporch' === this._shedType ){
                return 14.04;
            }

            return 30;
        }

        if( 'box_gable' === this.getRoofShape() ) {
            if( 'greenhouse' === this._shedType ) {
                return 45;
            }else if( 'structure_classic_aframe_dormer' === this._shedType ){
                return this.getRoofDegree();
            }

            return 19;
        }

        if( 'flat' === this.getRoofShape() && ( 'lean_to' === this._shedType || 'structure_monoslope_smithbilt' === this._shedType ) ) {
            return 15;
        }

        if( 'hip' == this.getRoofShape() ){
            return this.getRoofDegree();
        }
        
        return 0;
    }
    
    /**
     * Get roof width for each model
     */
    getRoofWidth = () => {
        let depth = this.getDepth() + this._defaultOverhangFront + this._defaultOverhangBack + this._customOverhangFront + this._customOverhangBack;
        if( 'structure_saltbox_run-in' === this._shedTypeVersion ) {
            depth += 0.3;
        }

        if( this.isFrontPorch() ){
            depth += this._porchWidth;
        }

        switch( this._shedType ) {
            case 'barn':
                depth += 0.3;
                break;
            case 'structure_barn_standard_mwshed':
                depth += 0.15;
                break;
            case 'standard_barn_front_porch':
                depth += 0.3;
                break;
            case 'barn_without_fe_overhangs':
                depth += 0.07;
                break;
            case 'barn_with_extra_trims':
                depth += 0.07;
                break;
            case 'structure_mainus_barn':
                depth += 0.1;
                break;
            case 'apexbarn_with_horizontal_trims':
                depth += 0.07;
                break;
            case 'apex_barn':
                depth += 0.07;
                break;
            case 'cottage':
                depth += 0.11;
                break;
            case 'standard_cottage_side_porch':
                depth += 0.11;
                break;
            case 'classic_cottage_with_metal_trim':
                depth += 0.11;
                break;
            case 'standard_cottage_front_porch':
                if( 'structure_chalet_dutchboybarns' === this._shedTypeVersion ) {
                    depth -= 0.15;
                } else {
                    depth += 0.11;
                }
                
                break;
            case 'cottage_without_fe_overhangs':
                depth += 0.07;
                break;
            case 'apexshed_with_horizontal_trims':
                depth += 0.07;
                break;
            case 'classic_cottage_with_metal_trim':
                depth += 0.11;
                break;
            case 'lakeside':
                depth += 0.11;
                break;
            case 'windsor':
                if( ! this.getMeshByName( 'roof_base_obj' ) )
                depth += 0.32;
                break;
            case 'horse_run_in':
                depth += 0.11;
                break;
            case 'pavilion':
                depth += 0.11;
                break;
            case 'dog_kennel':
                depth += 0.11;
                break;
            case 'structure_capecod':
                depth += 0.32;
                break;
            case 'mainus_quaker':
                depth += 0.32;
                break;
            case 'structure_alpinechalet':
                depth += 0.43;
                break;
            case 'structure_americana_endgable':
                depth -= 0.61;
                break;
            case 'structure_lofted_horserunin':
                depth += 0.05;
                break;
            case 'structure_saltbox_mwshed':
                depth += 0.3;
                break;
            case 'structure_monitorbarns_helmuth':
                depth += 0.1;
                break;
            case 'structure_rancher':
                depth -= 0.09;
                break;
            case 'structure_aframe_horserunin':
                depth -= 0.45;
                break;
            case 'structure_aframe_2story_attic_leanto':
                depth += 1.09;
                break;
            case 'structure_barn_2story_leanto':
                depth += 0.79;
                break;
            case 'structure_trading_post':
                depth -= this._porchWidth;
                break;
            case 'structure_lakeside_bunkie_gambrel_roof':
            case 'structure_lakeside_bunkie_gable_roof':
                depth += 32/12;
                break;
            case 'structure_ranch_cabin':
                depth -= this._cabinWidth;
                break;

        }

        if( 'permatile_metal' === this.getRoofType() ) {
            if( 'flat' === this.getRoofShape() ) {
                depth = Vector3.Distance( this._bones['roof_permatile_lean_to_b_l1'].position, this._bones['roof_permatile_lean_to_f_l1'].position ) + 0.05;
            } else {
                depth = Vector3.Distance( this._bones['roof_permatile_aframe_f_t'].position, this._bones['roof_permatile_aframe_b_t'].position ) + 0.05;
            }
        }

        if( 'standing_seam' === this.getRoofType() ) {
            if( 'flat' === this.getRoofShape() ) {
                depth = Vector3.Distance( this._bones['roof_standing_seam_lean_to_b_l1'].position, this._bones['roof_standing_seam_lean_to_f_l1'].position ) + 0.05;
            } else {
                depth = Vector3.Distance( this._bones['roof_standing_seam_aframe_b_t'].position, this._bones['roof_standing_seam_aframe_f_t'].position ) + 0.05;
            }
        }

        return depth;
    }

    isCustomPlacement = ( side : string ) => {
        return 'porch' === side;
    }

    isPorchSide( side ) {
        return 'porch' === side;
    }

    isFrontPorch = () => {
        return 'front' === this._canvas.getAttribute( 'data-porch-style' );
    }

    isSidePorch = () => {
        return 'side' === this._canvas.getAttribute( 'data-porch-style' );
    }

    isSidePorchCabin = () => {
        return 'side-porch-cabin' === this._canvas.getAttribute( 'data-porch-style' );
    }

    isMiddlePorch = () => {
        return 'middle' === this._canvas.getAttribute( 'data-porch-style' );
    }

    isExternalPorch = () => {
        if( '1' != this._canvas.getAttribute( 'data-model-enable-porch' ) ) {
            return false;
        }

        if( this.is2StoryLeanTo() ) {
            return 'true' == this._canvas.getAttribute( 'data-model-porch-has-size-options' );
        }

        return '1' == this._canvas.getAttribute( 'data-model-porch-size-single-option' );
    }

    /**
     * Checks if the current shed type is an 'A-Frame Single Gable' structure.
     * 
     * @returns {boolean} Returns true if the shed type is 'structure_aframe_single_gable', otherwise false.
    */
    isAframeSingleGable = () => {
        return 'structure_aframe_single_gable' === this._shedType;
    }

    isCornerPorchLeft = () => {
        return 'corner-left' === this._canvas.getAttribute( 'data-porch-style' );
    }

    isDeluxeCornerLeftPorch = () => {
        return 'deluxe-corner-left' === this._canvas.getAttribute( 'data-porch-style' );
    }

    isEnableLoft = () => {
        return parseInt( this._canvas.getAttribute( 'data-enable-loft' ) ) === 1;
    }

    isExcludePorchMeasurement = () => {
        return this.getSettingData( 'porch_measurement' ) === 'true';
    }

    isPorchFullRailing = () => {
        const railHLObj = this._scene.getMeshByName( 'rail_h_l_obj' ) as Mesh;

        if( railHLObj && ! this.isExternalPorch() && ! this.isMiddlePorch() ) return true;

        return false;
    }

    isCarport = () =>{
        const type = this.getCarportModels();

        return -1 !== type.indexOf( this._shedType );
    }

    getCarportModels = () => {
        return [ 'carport_standard_regularroof', 'carport_standard_verticalroof', 'carport_lean_to_standalone', 'carport_triplewide_attic_rooftruss' ];
    }

    /**
     * Check side gable for revert with and depth
     */
    isSideGable = () => {
        if( helpers.isAllowSwapSide() ) {
            return helpers.isSwapSide();
        }
        
        return 'side_gable' === this._canvas.getAttribute( 'data-side-gable' );
    }

    /**
     * Check decking direction is revert or not
     */
    isRevertDecking = () => {
        return this._canvas.getAttribute( 'data-decking-direction' ) === 'true';
    }

    isHidePorchInfo = () => {
        return this.getSettingData( 'hide_porch_info' ) === 'true';
    }

    /**
     * Update porch
     * 
     * @param {number} length   The model length
     */
    updatePorch = ( oldSize, newSize ) => {
        this.animateSizeChange( oldSize, newSize, false ).then( data => {
            this.updateRampPosition();
            this.enableComponentMoveable();
            this.updateRoofOverhang();
        });
    }

    aframeCabinHandleOpening = status => {
        const meshName  = this.isEnableBrace() ? 'column_braces_obj' : 'column_obj';
        const postObj   = this._scene.getMeshByName( meshName ) as Mesh;
        if( null === postObj ) {
            return;
        }

        this.removeClone( meshName );

        postObj.isPickable                  = false;
        postObj.alwaysSelectAsActiveMesh    = true;

        let bones       = this._bones;
        const depth     = this.getDepth();
        const oPosition = this.getOpeningPosition();
        const opening   = this.isEnableCustomOpeningSize() ? this.getOpeningSize() : 4;

        if( 'middle' === oPosition ) {
            let postClone   = this._scene.getMeshByName( `${meshName}_1` );
            if( null === postClone ) {
                postClone               = postObj.createInstance( `${meshName}_1` );
                postClone.isPickable    = false;
            }
            
            if( bones['rail_h_r_f_l1'] ) bones['rail_h_r_f_l1'].setPosition( new Vector3( bones['rail_h_r_f_l1'].position.x, bones['rail_h_r_f_l1'].position.y, bones['b_p_r1'].position.z ) );
            if( bones['rail_h_r_f_l2'] ) bones['rail_h_r_f_l2'].setPosition( new Vector3( bones['rail_h_r_f_l2'].position.x, bones['rail_h_r_f_l2'].position.y, bones['b_p_r1'].position.z - ( ( depth - opening ) / 2 ) ) );
            
            if( bones['rail_h_l_f_l1'] ) bones['rail_h_l_f_l1'].setPosition( new Vector3( bones['rail_h_l_f_l1'].position.x, bones['rail_h_l_f_l1'].position.y, bones['b_p_l1'].position.z ) );
            if( bones['rail_h_l_f_l2'] ) bones['rail_h_l_f_l2'].setPosition( new Vector3( bones['rail_h_l_f_l2'].position.x, bones['rail_h_l_f_l2'].position.y, bones['b_p_l1'].position.z + ( ( depth - opening ) / 2 ) ) );
            
            postClone.alwaysSelectAsActiveMesh  = true;
            postObj.position.z                  = bones['rail_h_r_f_l2'].position.z;
            postClone.position.z                = bones['rail_h_l_f_l2'].position.z;
        } else if( 'left' === oPosition ) {
            const positionZ     = bones['b_p_r1'].position.z - opening;
            postObj.position.z  = positionZ;

            if( bones['rail_h_l_f_l1'] ) bones['rail_h_l_f_l1'].setPosition( new Vector3( bones['rail_h_l_f_l1'].position.x, bones['rail_h_l_f_l1'].position.y, bones['b_p_l1'].position.z ) );
            if( bones['rail_h_l_f_l2'] ) bones['rail_h_l_f_l2'].setPosition( new Vector3( bones['rail_h_l_f_l2'].position.x, bones['rail_h_l_f_l2'].position.y, positionZ ) );
        } else if( 'right' === oPosition ) {
            const positionZ     = bones['b_p_l1'].position.z + opening;
            postObj.position.z  = positionZ;

            if( bones['rail_h_r_f_l1'] ) bones['rail_h_r_f_l1'].setPosition( new Vector3( bones['rail_h_r_f_l1'].position.x, bones['rail_h_r_f_l1'].position.y, bones['b_p_r1'].position.z ) );
            if( bones['rail_h_r_f_l2'] ) bones['rail_h_r_f_l2'].setPosition( new Vector3( bones['rail_h_r_f_l2'].position.x, bones['rail_h_r_f_l2'].position.y, positionZ ) );
        }
    }

    /**
     * Update railing
     * 
     * @param {boolean} status status hide/show of Railing
     */
    updateRailing = async ( status : boolean ) => {
        let _this = this;

        const balusterObj     = _this._scene.getMeshByName( 'baluster_obj' ) as Mesh;
        const railObj         = _this._scene.getMeshByName( 'rail_obj' ) as Mesh;
        const railVLObj       = _this._scene.getMeshByName( 'rail_v_l_obj' ) as Mesh;
        const railHLObj       = _this._scene.getMeshByName( 'rail_h_l_obj' ) as Mesh;
        const railVRObj       = _this._scene.getMeshByName( 'rail_v_r_obj' ) as Mesh;
        const railHRObj       = _this._scene.getMeshByName( 'rail_h_r_obj' ) as Mesh;
        const oPosition       = _this.getOpeningPosition();
        
        _this._enabledRailing = status;

        if( this.hasOpeningSetting() && this.getOpeningSetting() && ! this.isEnableExternalPorch() ) {
            if( balusterObj ) balusterObj.setEnabled( false );
        } else {
            if( balusterObj ) balusterObj.setEnabled( status );
        }
        if( railObj ) railObj.setEnabled( status );
        if( railVLObj ) railVLObj.setEnabled( status );
        if( railVRObj ) railVRObj.setEnabled( status );
        if( railHLObj ) railHLObj.setEnabled( status );
        if( railHRObj ) railHRObj.setEnabled( status );

        const oldMeshClones = this.getGlobalData( constants.balusterCloneKey );
        if( Array.isArray( oldMeshClones ) ) {
            for( let i = 0; i < oldMeshClones.length; i++ ) {
                const instancesLength = oldMeshClones[i].instances.length;
                if( instancesLength > 0 ) {
                    for( let index = 0; index <= instancesLength; index++ ) {
                        const instanceMesh = this._scene.getMeshByName( `${oldMeshClones[i].name}_${index}` );
                        if( instanceMesh ) instanceMesh.setEnabled( status );
                    }
                }
            }
        }

        if( this.isExternalPorch() && this.hasOpeningSetting() && undefined === this.getOpeningSetting() ) {
            if( railHRObj ) railHRObj.setEnabled( false );
            if( railHLObj ) railHLObj.setEnabled( false );
        }

        if( 'left' ===  oPosition ){
            if( railHRObj ) railHRObj.setEnabled( false );
        }else if( 'right' === oPosition ){
            if( railHLObj ) railHLObj.setEnabled( false );
        }

        if( _this.isSidePorch() ){
            let porchGroup = _this._scene.getMeshByName( 'Porch_grp' );
            if( railHLObj ) railHLObj.setParent( null );
            if( railHRObj ) railHRObj.setParent( null );
            if( railVLObj ) railVLObj.setParent( null );
            if( railVRObj ) railVRObj.setParent( null );
            if( balusterObj ) balusterObj.setParent( porchGroup );
            if( porchGroup ) porchGroup.rotation = new Vector3(0, -Math.PI/2, 0);
        }

        if( 'structure_cardinal_victorian' === _this._shedType || 'structure_cardinal_cabin' === _this._shedType || 'structure_cardinal_lofted_cabin' === _this._shedType ){
            const columnObj         = _this._scene.getMeshByName( 'column_obj' ) as Mesh;
            const columnCloneObj    = _this._scene.getMeshByName( 'column_obj_1' ) as Mesh;

            if( columnObj ) columnObj.setEnabled( status );
            if( columnCloneObj ) columnCloneObj.setEnabled( status );

            if( 'left' ===  oPosition || 'right' === oPosition ){
                if( columnCloneObj ) columnCloneObj.setEnabled( false );
            }
        }
        
        if( _this.isMiddlePorch() ) {
            const postsMesh = _this._scene.getMeshByName( 'posts_obj' ) as Mesh;

            if( railHLObj ) railHLObj.setEnabled( status );
            if( postsMesh ) postsMesh.setEnabled( status );
            if( 'middle' === oPosition ) {
                if( railHLObj ) railHLObj.setEnabled( false );
                if( balusterObj ) balusterObj.setEnabled( false );
                if( postsMesh ) postsMesh.setEnabled( false );
            }
            this.handleMiddlePorch();
        }

        if( 'structure_ranch_cabin' === _this._shedType ) {
            _this.ranchCabinHandleOpening( status );
        } else if( this.isSidePorchCabin() ) {
            _this.aframeCabinHandleOpening( status );
        }

        await _this.cloneBaluster();
        if( balusterObj && balusterObj.instances ){
            balusterObj.instances.forEach( i => {
                i.setEnabled( status );
            });
        }

        if( _this._instances['baluster'] ) {
            _this._instances['baluster'].forEach( k => {
                let mesh = _this._scene.getMeshByName( k );
                mesh.setEnabled( status );
            });
        }

        _this.handleExternalPorch();
    }

    /**
     * Update porch External
     * 
     * @param {boolean} status status hide/show of porch External
     */
    updateExternalPorch = async ( status : boolean ) => {
        const porchMeshName = this.is2StoryLeanTo() ? 'porch_obj' : 'Porch_grp';
        const porchGroup    = this.getMeshByName( porchMeshName ) as Mesh;
        if( porchGroup ) porchGroup.setEnabled( status );

        const runnerPorch = this._scene.getMeshByName( 'runner_porch' );
        if( runnerPorch ) runnerPorch.setEnabled( status );

        this._porchWidth = status ? this._initPorchDepth : 0;

        if( this.is2StoryLeanTo() ) {
            this._porchWidth = 0;
        }

        this.updateRoofOverhang();
        this.updateRampPosition();
        this.updateExternalPorchRunnerPosition();
        this.cloneExternalPost();
        this.cloneBaluster();
        this.handleExternalPorch();
        this.updateWindowsPosition();
    }

    /**
     * Update runner position for external Porch
     */
    updateExternalPorchRunnerPosition = () => {
        const _this             = this;
        const bones             = _this._bones;
        const runnerDirection   = _this.getRunnerDirection();
        let newWidth: number    = 'side-to-side' == runnerDirection ? _this.getWidth() / 2 : _this.getDepth() / 2;
        let runnerPorch         = _this._scene.getMeshByName( 'runner_porch' );
        let runnerLeft          = _this._scene.getMeshByName( 'runner_left' );
        let runnerRight         = _this._scene.getMeshByName( 'runner_right' );
        const deckObj = _this.getMeshByName( 'deck_obj' );

        if( 'front' === _this._externalPorchLocation ) {
            if( 'side-to-side' == runnerDirection ) {
                if( bones['runner_f'] ) bones['runner_f'].setPosition( new Vector3( bones['runner_f'].position.x, bones['runner_f'].position.y, newWidth ) );
                if( bones['runner_b'] ) bones['runner_b'].setPosition( new Vector3( bones['runner_b'].position.x, bones['runner_b'].position.y, -newWidth ) );
                    if( _this._porchWidth != 0 && runnerPorch ) {
                        runnerPorch.setEnabled( true );
                        if( runnerRight ) runnerPorch.position = new Vector3( 0, 0, runnerRight.position.z  + _this._porchWidth );
                    } 
                    if( ! _this._isCloneRuner && runnerPorch ) {
                        runnerPorch.setEnabled( false );
                    }
            } else {
                let extPotrchRunnerF = _this._porchWidth;

                if( ! deckObj ) {
                    extPotrchRunnerF = 0;
                }

                if( bones['runner_f'] ) bones['runner_f'].setPosition( new Vector3( bones['runner_f'].position.x, bones['runner_f'].position.y, newWidth + extPotrchRunnerF ) );
                if( bones['runner_b'] ) bones['runner_b'].setPosition( new Vector3( bones['runner_b'].position.x, bones['runner_b'].position.y, -newWidth ) );
                if( runnerPorch ) runnerPorch.setEnabled( false );
            }
        } else {
            if( 'side-to-side' == runnerDirection ) {
                let extPotrchRunnerB = 0;
                if( 'left' === this._externalPorchLocation && deckObj ) {
                    extPotrchRunnerB = this._porchWidth;
                }

                if( bones['runner_f'] ) bones['runner_f'].setPosition( new Vector3( bones['runner_f'].position.x, bones['runner_f'].position.y, newWidth ) );
                if( bones['runner_b'] ) bones['runner_b'].setPosition( new Vector3( bones['runner_b'].position.x, bones['runner_b'].position.y, - newWidth - extPotrchRunnerB ) );
                if( runnerPorch ) runnerPorch.setEnabled( false );

                if( this.isSidePorch() && this.isExcludePorchMeasurement() ) {
                    if( bones['runner_b'] ) bones['runner_b'].setPosition( new Vector3( bones['runner_b'].position.x, bones['runner_b'].position.y, bones['b_f_l1'].position.x ) );
                    if( 'structure_lean_to_with_front_porch' === this._shedType ) {
                        if( bones['runner_b'] ) bones['runner_b'].setPosition( new Vector3( bones['runner_b'].position.x, bones['runner_b'].position.y, bones['b_f_p1'].position.x ) );
                    }
                }

            } else {
                let porchWidth = 0;
                if( this.isFrontPorch() ) {
                    porchWidth = this._porchWidth;
                }
                
                if( bones['runner_f'] ) bones['runner_f'].setPosition( new Vector3( bones['runner_f'].position.x, bones['runner_f'].position.y, newWidth + porchWidth ) );
                if( bones['runner_b'] ) bones['runner_b'].setPosition( new Vector3( bones['runner_b'].position.x, bones['runner_b'].position.y, - newWidth ) );

                if( _this._porchWidth == 0 ) {
                    if( runnerPorch ) runnerPorch.setEnabled( false );
                } else {
                    if( runnerPorch ) {
                        runnerPorch.setEnabled( true );
                        if( runnerLeft ) runnerPorch.position = new Vector3( runnerLeft.position.x  - _this._porchWidth, 0, 0 );
                    }
                }
            }
        }
    }

    /**
     * Update Ramp position when change porch size
     */
    updateRampPosition = () => {        
        const _this = this;
        const deckObj = _this.getMeshByName( 'deck_obj' );

        for( const side in _this._walls ) {
            for( const key in _this._walls[side]["items"] ) {
                const component = _this._walls[side]["items"][key];
                if( component.type === 'flower_box' ) continue;

                if( 'ramp' === component.type && this.isSidePorchCabin() && 'left' === side ) {
                    _this.placeComponent( component.meshes, { x: 0, y: 0, z: _this._porchWidth }, component.rotation, component.type, side, component.name );
                }

                if( 'ramp' === component.type && ( ( _this.isFrontPorch() && 'front' === component.side ) || ( _this._externalPorchLocation == component.side && deckObj ) || ( _this.isSidePorch() && 'left' === component.side ) || ( 'inner_left' === component.side ) || ( 'front_inner_porch' === component.side ) ) ) {
                    let y = component.position.y;
                    let z = this._porchWidth;

                    if( _this._externalPorchLocation == component.side ){
                        y = _this._porchWidth ? -0.4 : 0;
                    }

                    if( _this.isSidePorch() || _this.isFrontPorch() ){
                        y = -0.3;
                    }

                    if( 'structure_hiproof' === this._shedType  ) {
                        y = component.position.y;
                        z = component.position.z;
                    }

                    const position = { x: 0, y, z }
                    _this.placeComponent( component.meshes, position, component.rotation, component.type, side, component.name );
                }

                if( ( 'door' === component.type || 'garage_door' === component.type ) && _this._externalPorchLocation == component.side ){
                    const y = this.getDoorFixedHeight( side );
                    const position = { x: component.position.x, y: y, z: component.position.z}
                    _this.placeComponent( component.meshes, position, component.rotation, component.type, side, component.name );
                }

                if( _this.isFrontPorch() && -1 !== [ 'vent', 'shelf', 'workbench', 'switch', 'outlet', 'light', 'breakerbox' ].indexOf( component.type ) ){
                    let positionZ = component.position.z;

                    if( this.isPorchSide( side ) ) {
                        positionZ = _this.getDepth() / 2 + _this._porchWidth;
                    } else if( 'front' === side ) {
                        positionZ = _this.getDepth() / 2;
                    } else if( 'back' === side ) {
                        positionZ = - _this.getDepth() / 2;
                    }

                    let position = { x: component.position.x, y: component.position.y, z: positionZ }
                    if( ( this.isPorchSide( side ) || 'front' === side || 'back' === side ) && this.isInteriorComponent( component.type ) && 'light' !== component.type ) {
                        position = this.updateInteriorPosition( position, side );
                    }

                    _this.placeComponent( component.meshes, position, component.rotation, component.type, side, component.name );
                }

                if( _this.isSidePorch() && 'left' === component.side && 'ramp' !== component.type ) {
                    _this.placeComponent( component.meshes, component.position, component.rotation, component.type, side, component.name );
                }
            }
        }
    }

    /*
     * Update camera position follow layout setting
     */
    updateCameraPosition = () => {
        const side = jQuery('.ssb-configurator-layout .ssb-option:checked').attr('data-layout-main-side');
        switch( side ) {
            case 'front_right':
                this._camera.setPosition( new Vector3( 12, Math.PI / 2, 18 ) );
                break;
            case 'back_left':
                this._camera.setPosition( new Vector3( -12, Math.PI / 2, -18 ) );
                break;
            case 'back_right':
                this._camera.setPosition( new Vector3( 12, Math.PI / 2, -18 ) );
                break;
            default:
                this._camera.setPosition( new Vector3( -12, Math.PI / 2, 18 ) );
                break;
        }
    }

    /**
     * Check device loading model
     * 
     * @returns {boolean}
     */
    isMobile = () => {
        if( /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ) {
            return true;
        }

        return false;
    }
    
    /**
     * Hide horizontal trim by shed type and siding type
     * 
     * @param {String} shedType Shed type (barn|classic_cottage|lakeside,...)
     * @param {String} sidingType Siding type (lp_smart|vinyl|metal,...)
     */
    hideHorizontalTrim = ( shedType, sidingType ) => {
        if( 'apexbarn_with_horizontal_trims' !== shedType ) {
            return;
        }

        let horizontalTrim = this._scene.getMeshByName( 'horizontal_trim_obj' ) as Mesh;
        if( horizontalTrim ) {
            this.apexBarnSetStatusHorizontalTrim( horizontalTrim, sidingType );
        } else {
            let horizontalTrim = this._scene.getMeshByName( 'trim_8ft' ) as Mesh;
            if( horizontalTrim ) {
                this.apexBarnSetStatusHorizontalTrim( horizontalTrim, sidingType );
            }
        }
    }

    apexBarnSetStatusHorizontalTrim = ( horizontalTrim, sidingType ) => {
        const sidingTypes = [ 'metal', 'vertical_metal', 'metal_lap' ];
        if( -1 !== sidingTypes.indexOf( sidingType ) ) {
            horizontalTrim.setEnabled( false );
        } else {
            horizontalTrim.setEnabled( true );
        }
    }

    /**
     * Hide roof_trim_obj by shed type and siding type
     * 
     * @param {String} shedType Shed type (barn|classic_cottage|lakeside,...)
     * @param {String} sidingType Siding type (lp_smart|vinyl|metal,...)
     */
    hideRoofTrim = ( shedType, sidingType ) => {
        if( 'structure_aframe_colonialbarns' !== shedType ) {
            return;
        }

        let roofTrim = this._scene.getMeshByName('roof_trim_obj') as Mesh;
        if( roofTrim ) {
            const sidingTypes = [ 'vinyl' ];
            if( -1 !== sidingTypes.indexOf(sidingType) ) {
                roofTrim.setEnabled(false);
            } else {
                roofTrim.setEnabled(true);
            }
        }
    }


    /**
     * Show or hide floor mesh
     * 
     * @param {Boolean} isShowed
     */
    showFloorMesh = ( isShowed = true ) => {
        const floorMesh = this._scene.getMeshByName( 'floor' ) as Mesh;
        
        if( floorMesh ) {
            floorMesh.setEnabled( isShowed );
        }
    }

    flatDormerHandleWall = firstMesh => {
        const skeletonKeys  = [ 'flat_dormer_T', 'flat_dormer_F' ];
        const meshName      = firstMesh.name.replace( '_set_obj', '' );
        const frameMesh     = this.filterByValue( firstMesh._children, 'name', `${meshName}_obj` ) as Mesh;
        const width         = this.getMeshWidth( frameMesh ) - 0.09;
        const bones         = [];
        const leftPosition  = new Vector3( width / 2, 0, 0 );
        const rightPosition = new Vector3( - width / 2, 0, 0 );
        const timeStamp     = firstMesh.metadata.key.split( '_' ).pop();
        
        skeletonKeys.forEach( key => {
            const bone          = this.filterByValue( frameMesh.skeleton.bones, 'name', key );
            const boneOffset    = this.filterByValue( frameMesh.skeleton.bones, 'name', `${key}_01` );
            if( bone ) {
                let x = bone.position.z;
                let y = bone.position.y;

                if( 'flat_dormer_F' === key ) {
                    bones.push( new Vector3( x, 0, 0 ) );
                }

                bones.push( new Vector3( x, 0, y ) );
            }
        } );

        const { shape, rotation } = this.calculateWallInfo( { bones , side: 'left', position: new Vector3( 0, 0, 0 ) } );
        this.drawWall( `left_wall_${timeStamp}`, '', shape, rotation, leftPosition, `left_mat_${timeStamp}`, undefined, firstMesh );
        this.drawWall( `right_wall_${timeStamp}`, '', shape, rotation, rightPosition, `right_mat_${timeStamp}`, undefined, firstMesh );

    }

    flatDormerHandleRoof = () => {
        let  roofType = this.getRoofType();

        if( undefined === roofType ) {
            return;
        }

        const referenceRoof = jQuery( `[data-roof-type="${roofType}"]` ).attr( 'data-reference-key' );
        if( undefined !== referenceRoof && '' !== referenceRoof ) {
            const dormerRoofMesh = this.getMeshesLikeName( 'dormer', `_${roofType}_obj` );
            if( ! dormerRoofMesh ) {
                roofType = referenceRoof;
            }
        }

        const roofMetalObjs   = this.getMeshesLikeName( 'dormer', '_roof_metal_obj' );
        const roofShingleObjs = this.getMeshesLikeName( 'dormer', '_roof_shingle_obj' );

        if( 'metal' === roofType || 'metal_lap' === roofType || 'metal_horizontal'  === roofType ) {
            if( roofMetalObjs ) {
                roofMetalObjs.forEach( m => {
                    m.setEnabled( true );
                });
            }

            if( roofShingleObjs ) {
                roofShingleObjs.forEach( m => {
                    m.setEnabled( false );
                });
            }
        } else if( 'shingle' === roofType || 'shingle_3tab' === roofType ) {
            if( roofMetalObjs ) {
                roofMetalObjs.forEach( m => {
                    m.setEnabled( false );
                });
            }

            if( roofShingleObjs ) {
                roofShingleObjs.forEach( m => {
                    m.setEnabled( true );
                });
            }
        }
    }

    getMeshesByName = ( name ) => {
        let results = [];
        this._scene.meshes.forEach( mesh => {
            if( mesh.name === name ) {
                results.push( mesh );
            }
        });

        return results.length > 0 ? results : false;
    }

    getMeshesLikeName = ( before, after ) => {
        let results = [];
        this._scene.meshes.forEach( mesh => {
            if( -1 !== mesh.name.indexOf( before ) && -1 !== mesh.name.indexOf( after ) ) {
                results.push( mesh );
            }
        });

        return results.length > 0 ? results : false;
    }
    
    sidingAdjustUVs = ( defaultVScale = undefined, defaultUScale = undefined ) => {
        if( ! Array.isArray( this._apiData['meshes'] ) ) return;
        const activeFloorMaterial = this.getActiveFloorMaterialData();
        this._apiData['meshes'].forEach( key => {
            const mesh = this.getMeshByName( key );

            if( ! mesh || mesh.name === 'floor' ) return;

            const mat           = mesh.material as StandardMaterial;
            const matName       = mat && mat.hasOwnProperty( 'name' ) ? mat.name : '';
            let wallHeight    = mesh.geometry.extend.maximum.z - mesh.geometry.extend.minimum.z;
            let wallWidth     = mesh.geometry.extend.maximum.x - mesh.geometry.extend.minimum.x;
            let initUScale : any = this._sidingUscale;
            let initVScale : any = this._sidingVscale;
            if( this._apiData['wainscoting_material'].includes( matName ) ) {
                initUScale = this.getCustomUVScale( `wainscoting_uscale` );
                initVScale = this.getCustomUVScale( `wainscoting_vscale` );
            } else if( this._apiData['accent_gable_material'].includes( matName ) ) {
                initUScale = this.getCustomUVScale( `accent_gable_uscale` );
                initVScale = this.getCustomUVScale( `accent_gable_vscale` );
            } else if( this._apiData['exterior_siding_upgrades_material'].includes( matName ) ) {
                initUScale = this.getCustomUVScale( `exterior_siding_upgrades_uscale` );
                initVScale = this.getCustomUVScale( `exterior_siding_upgrades_vscale` );
            } else if( matName === this._interiorFloorMatKey ) {
                initUScale = activeFloorMaterial?.uScale ?? 1;
                initVScale = activeFloorMaterial?.vScale ?? 1;
            } else if( this._apiData['interior_wall_material'].includes( matName ) ) {
                if( jQuery( '#interior_wall-material-option' ).length && 'none' !== jQuery( '#interior_wall-material-option li.active' ).data( 'material-type' ) ) {
                    initUScale = jQuery( '#interior_wall-material-option li.active' ).attr( 'data-vscale' ) ?? initUScale;
                    initVScale = jQuery( '#interior_wall-material-option li.active' ).attr( 'data-vscale' ) ?? initVScale;
                } else if( jQuery( '#insulation_wall-material-option' ).length && 'none' !== jQuery( '#insulation_wall-material-option li.active' ).data( 'material-type' ) ) {
                    initUScale = jQuery( '#insulation_wall-material-option li.active' ).attr( 'data-vscale' ) ?? initUScale;
                    initVScale = jQuery( '#insulation_wall-material-option li.active' ).attr( 'data-vscale' ) ?? initVScale;
                } else {
                    initUScale = this._sidingUscale;
                    initVScale = this._sidingVscale;
                }
            } else if( this._apiData['interior_ceiling_material'].includes( matName ) ) {
                if( jQuery( '#interior_ceiling-material-option' ).length ) {
                    initUScale = jQuery( '#interior_ceiling-material-option li.active' ).attr( 'data-vscale' ) ?? initUScale;
                    initVScale = jQuery( '#interior_ceiling-material-option li.active' ).attr( 'data-vscale' ) ?? initVScale;
                } else if( jQuery( '#insulation_roof-material-option' ).length ) {
                    initUScale = jQuery( '#insulation_roof-material-option li.active' ).attr( 'data-vscale' ) ?? initUScale;
                    initVScale = jQuery( '#insulation_roof-material-option li.active' ).attr( 'data-vscale' ) ?? initVScale;
                }
            }

            if( this._apiData['interior_wall_material'].includes( matName ) || this._apiData['interior_ceiling_material'].includes( matName ) ) {
                wallHeight = this._apiData['meshesData'][ `${matName}` ].wallHeight;
                wallWidth = this._apiData['meshesData'][ `${matName}` ].wallWidth;
            }

            const vScale        = + ( wallHeight / ( 8 / initVScale ) ).toFixed( 3 ); // 8 is default width of material when design create. this._sidingVscale is init V scale from network
            const uScale        = + ( wallWidth / ( 8 / initUScale ) ).toFixed( 3 );

            this.updateUVScale( mat, uScale, vScale, 1, 1 );
        } );
    }

    /*
     * Adjust uv scale for floor material
     */
    floorAdjustUVs = () => {
        const mesh = this.getMeshByName( 'floor' );

        if( ! mesh ) return;

        const activeFloorMaterial = this.getActiveFloorMaterialData();

        const mat           = mesh.material as StandardMaterial;
        const wallDepth     = this.getMeshLength( mesh );
        const wallWidth     = this.getMeshWidth( mesh );
        let initVScale      = activeFloorMaterial.uScale ?? 1;
        let initUScale      = activeFloorMaterial.vScale ?? 1;

        const vScale        = helpers.toFixed3( wallDepth / ( 8 / initUScale ) );
        const uScale        = helpers.toFixed3( wallWidth / ( 8 / initVScale ) );

        this.updateUVScale( mat, uScale, vScale, 1, 1 );
    }

    dormerAdjustUVs = () => {
        const _this = this;

        if( undefined === this._bones['b_b_t'] ) {
            return;
        }

        const flatDormerRoofShingleMats     = _this.getMaterialByName( 'flat_dormer_roof_shingle_mat' );
        const flatDormerSidingTopMats       = _this.getMaterialByName( 'flat_dormer_top_mat' );
        const flatDormerSidingBottomMats    = _this.getMaterialByName( 'flat_dormer_bottom_mat' );
        
        const heightDistance    = this.getFlatDormerHeightDistance();
        const dormerHeight      = this._bones['b_b_t'].position.y - this.getWallHeight() - heightDistance - this._flatDormerHeight;
        const dormerWidth       = this.getFlatDormerWidth();

        if( flatDormerRoofShingleMats ) {
            const roofType           = this.getRoofType();
            const { uScale, vScale } = this.getSidingMaterialData( jQuery( `#roof-material-option li[data-roof-type=${roofType}]` ) );
            const roofDormerWidth               = Math.sqrt( dormerHeight * dormerHeight + dormerWidth * dormerWidth );
            const flatDormerRoofShingleVScale   = roofDormerWidth / _this._initRoofFlatDormerWidth;
            flatDormerRoofShingleMats.forEach( mat => {
                _this.updateUVScale( mat, 1, flatDormerRoofShingleVScale, uScale, vScale );
            });
        }

        if( flatDormerSidingTopMats ) {
            const flatDormerSidingTopUScale = dormerWidth / _this._initFlatDormerWidth;
            const flatDormerSidingTopVScale = dormerHeight / _this._initFlatDormerHeight;
            flatDormerSidingTopMats.forEach( mat => {
                mat.alpha = 0;
                _this.updateUVScale( mat, flatDormerSidingTopUScale, flatDormerSidingTopVScale );
            });
        }

        if( flatDormerSidingBottomMats ) {
            const flatDormerSidingBottomUScale = dormerWidth / _this._initFlatDormerWidth;
            flatDormerSidingBottomMats.forEach( mat => {
                mat.alpha = 0;
                _this.updateUVScale( mat, flatDormerSidingBottomUScale, 1 );
            });
        }
    }

    getFlatDormerHeightDistance = () => {
        let errorNumber = 0;

        if( 'windsor' === this._shedType ) {
            if ( "open_gable" === this.getRoofShape() ) {
                errorNumber = 0.32;
            } else if ( "box_gable" === this.getRoofShape() ) {
                errorNumber = 0.093;
            }
        }

        if( 'structure_lakeside_bunkie_gable_roof' === this._shedType ){
            errorNumber = 0.184;
        }

        return helpers.getTanFromDegrees( this.getRoofDegree() ) * this._flatDormerDistance + errorNumber;
    }

    getFlatDormerWidth = () => {
        return this.getWidth() / 2 - this._flatDormerDistance;
    }

    updateColor = ( groupClass ) => {
        const defaultColor  = jQuery(`.${groupClass}`).find('.ssb-option.active');
        const materialName  = defaultColor.attr('data-material');
        const textureType   = defaultColor.attr('data-texture-type');
        const textureImage  = defaultColor.attr('data-texture-image');
        const textureColor  = defaultColor.attr('data-texture-color');
        const typeColor     = defaultColor.attr('data-color-type');
        if( -1 !== ['windows', 'door', 'vent', 'shutter', 'countertops', 'appliance'].indexOf( typeColor ) ) {
            this.updateColorComponent( typeColor );
        }
        if( '' === materialName ) {
            return;
        }

        this.animateTextureChange( materialName, textureType, textureImage, textureColor );
    }

    getTextureFile = ( sidingType, diffuseTexture, bumpTexture, specularTexture, specularPower ) => {
        const ssbSettings = (<any>window);

        if( 1=== Number( ssbSettings.ssb.network_materials ) ) {
            const walls = this.getWallInfo();

            if( Array.isArray( walls ) && walls.length > 0 && diffuseTexture && bumpTexture && specularTexture ){
                return { diffuseTexture, bumpTexture, specularTexture, specularPower };
            }

            const version = jQuery( `[data-siding-type=${sidingType}]` ).attr( 'data-version' );

            return {
                diffuseTexture: `${this._materialsDir}siding_${sidingType}_dif.jpg${version}`,
                bumpTexture: `${this._materialsDir}siding_${sidingType}_nor.jpg${version}`,
                specularTexture: `${this._materialsDir}siding_${sidingType}_spe.jpg${version}`,
                specularPower
            };
        }
        
        switch( sidingType ) {
            case "lp_smart":
                diffuseTexture  = "siding_lpsmart_tex_dif.jpg";
                bumpTexture     = "siding_lpsmart_tex_nor.jpg";
                specularTexture = "siding_lpsmart_tex_spe.jpg";
                break;
            case "duratemp":
                diffuseTexture  = "siding_lpsmart_tex_dif.jpg";
                bumpTexture     = "siding_lpsmart_tex_nor.jpg";
                specularTexture = "siding_lpsmart_tex_spe.jpg";
                break;
            case "vinyl":
                diffuseTexture  = "siding_dutch_vinyl_tex_dif.jpg";
                bumpTexture     = "siding_dutch_vinyl_tex_nor.jpg";
                specularTexture = "siding_dutch_vinyl_tex_spe.jpg";
                break;
            case "wood":
                diffuseTexture  = "siding_wood_tex_dif.jpg";
                bumpTexture     = "siding_wood_tex_nor.jpg";
                specularTexture = "siding_wood_tex_spe.jpg";
                break;
            case "fir":
                diffuseTexture  = "siding_wood_tex_dif.jpg";
                bumpTexture     = "siding_wood_tex_nor.jpg";
                specularTexture = "siding_wood_tex_spe.jpg";
                break;
            case "metal":
                diffuseTexture  = "siding_metal_tex_dif.jpg";
                bumpTexture     = "siding_metal_tex_nor.jpg";
                specularTexture = "siding_metal_tex_spe.jpg";
                break;
            case "t1_11_4":
                diffuseTexture  = "siding_t1_11_4_dif.jpg";
                bumpTexture     = "siding_t1_11_4_nor.jpg";
                specularTexture = "siding_t1_11_4_spe.jpg";
                break;
            case "lp_lap":
                diffuseTexture  = "siding_lp_lap_dif.jpg";
                bumpTexture     = "siding_lp_lap_nor.jpg";
                specularTexture = "siding_lp_lap_spe.jpg";
                break;
            case "vertical_metal":
                diffuseTexture  = "siding_vertical_metal_dif.jpg";
                bumpTexture     = "siding_vertical_metal_nor.jpg";
                specularTexture = "siding_vertical_metal_spe.jpg";
                break;
            case "board_batten":
                diffuseTexture  = "siding_board_batten_dif.jpg";
                bumpTexture     = "siding_board_batten_nor.jpg";
                specularTexture = "siding_board_batten_spe.jpg";
                break;
            case "metal_lap":
                diffuseTexture  = "siding_metal_lap_dif.jpg";
                bumpTexture     = "siding_metal_lap_nor.jpg";
                specularTexture = "siding_metal_lap_spe.jpg";
                break;
            case "vinyl_clapboard":
                diffuseTexture  = "siding_vinyl_clapboard_dif.jpg";
                bumpTexture     = "siding_vinyl_clapboard_nor.jpg";
                specularTexture = "siding_vinyl_clapboard_spe.jpg";
                break;
            case "groove":
                diffuseTexture  = "siding_groove_dif.jpg";
                bumpTexture     = "siding_groove_nor.jpg";
                specularTexture = "siding_groove_spe.jpg";
                break;
            default:
                break;
        }

        return {
            diffuseTexture: `${this._modelDir}${diffuseTexture}`,
            bumpTexture: `${this._modelDir}${bumpTexture}`,
            specularTexture: `${this._modelDir}${specularTexture}`,
            specularPower
        };
    }

    getSidingMaterialData = ( element ) => {
        const sidingType        = element.attr( 'data-siding-type') ;
        const defaultDiffuse    = element.attr( 'data-texture-diffuse' );
        const defaultBump       = element.attr( 'data-texture-bump' );
        const defaultSpecular   = element.attr( 'data-texture-specular' );
        const opacityTexture    = element.attr( 'data-texture-opa' );
        const uScale            = parseFloat( element.attr( 'data-uscale' ) );
        const vScale            = parseFloat( element.attr( 'data-vscale' ) );
        const specularPower     = isNaN( parseFloat( element.attr( 'data-specular-power' ) ) ) ? -1 : parseFloat( element.attr( 'data-specular-power' ) );
        
        const defaultInnerDiffuse = element.attr( 'data-texture-inner-diffuse' ) ?? defaultDiffuse;
        const defaultInnerBump = element.attr( 'data-texture-inner-bump' ) ?? defaultBump;
        const defaultInnerSpecular = element.attr( 'data-texture-inner-specular' ) ?? defaultSpecular;
        const opacityInnerTexture = element.attr( 'data-texture-inner-opa' ) ?? opacityTexture;

        return { sidingType, defaultDiffuse, defaultBump, defaultSpecular, uScale, vScale, specularPower, opacityTexture, defaultInnerDiffuse, defaultInnerBump, defaultInnerSpecular, opacityInnerTexture };
    }

    getSidingInnerMaterialData = () => {
        let color:any;
        let diffuseTexture, bumpTexture, specularTexture, specularPower = '';
        let dataMaterial;
        let uScale = this._sidingUscale;
        let vScale = this._sidingVscale;
        if( jQuery( '#interior_wall-material-option' ).length && 'none' !== jQuery( '#interior_wall-material-option li.active' ).data( 'material-type' ) ) {
            let { diffuseTexture, bumpTexture, specularTexture, specularPower } = this.getTextureData( 'interior_wall-material-option' );
            color = this.getSidingColor( 'wall' );
            uScale = parseFloat( jQuery( '#interior_wall-material-option li.active' ).attr( 'data-uscale' ) );
            vScale = parseFloat( jQuery( '#interior_wall-material-option li.active' ).attr( 'data-vscale' ) );
            return { diffuseTexture, bumpTexture, specularTexture, specularPower, color, uScale, vScale }
        } else if( jQuery( '#insulation_wall-material-option' ).length && 'none' !== jQuery( '#insulation_wall-material-option li.active' ).data( 'material-type' ) ) {
            let { diffuseTexture, bumpTexture, specularTexture, specularPower } = this.getTextureData( 'insulation_wall-material-option' );
            uScale = parseFloat( jQuery( '#insulation_wall-material-option li.active' ).attr( 'data-uscale' ) );
            vScale = parseFloat( jQuery( '#insulation_wall-material-option li.active' ).attr( 'data-vscale' ) );
            return { diffuseTexture, bumpTexture, specularTexture, specularPower, color, uScale, vScale }
        } else {
            let { sidingType, defaultInnerDiffuse, defaultInnerBump, defaultInnerSpecular, specularPower : spec } = this.getSidingMaterialData( jQuery( '#siding-material-option li.active' ) );
            let { diffuseTexture, bumpTexture, specularTexture, specularPower } = this.getTextureFile( sidingType, defaultInnerDiffuse, defaultInnerBump, defaultInnerSpecular, spec );
            if( '' !== this._defaultColorInteriorSiding ) {
                color = { type: 'color' , image: '', code: this._defaultColorInteriorSiding }
            }
            return { diffuseTexture, bumpTexture, specularTexture, specularPower, color, uScale, vScale }
        }
        return { diffuseTexture, bumpTexture, specularTexture, specularPower, color, uScale, vScale }
    }

    updateSidingMaterial = () => {
        const sidingColor = jQuery(`.group-siding-color`).find('.ssb-option.active').attr('data-texture-color');
        const { sidingType, defaultDiffuse, defaultBump, defaultSpecular, specularPower }  = this.getSidingMaterialData( jQuery('#siding-material-option li.active') );
        const { diffuseTexture, bumpTexture, specularTexture } = this.getTextureFile( sidingType, defaultDiffuse, defaultBump, defaultSpecular, specularPower );
        
        this.animateSidingTypeChange( sidingType, sidingColor, diffuseTexture, bumpTexture, specularTexture, specularPower );
    }

    setDefaultOverhangData = () => {
        const data                  = this.getOverhangSetting();
        this._defaultOverhangFront  = + ( Number( data['front'] ) / 12 ).toFixed( 3 );
        this._defaultOverhangBack   = + ( Number( data['back'] ) / 12 ).toFixed( 3 );
        this._defaultOverhangLeft   = + ( Number( data['left'] ) / 12 ).toFixed( 3 );
        this._defaultOverhangRight  = + ( Number( data['right'] ) / 12 ).toFixed( 3 );

        if( "1" === this.getSettingData( 'is_center_rooftop' ) ) {
            this._originOverhang = {
                'left'    : this._defaultOverhangLeft,
                'right'   : this._defaultOverhangRight,
                'zerroNum': Math.abs( helpers.toFixed3( ( this._defaultOverhangLeft - this._defaultOverhangRight ) / 2 ) ),
                'constant': this._defaultOverhangLeft > this._defaultOverhangRight ? -1 : 1,
            }
            const totalOverhangLeftRight    = this._defaultOverhangLeft + this._defaultOverhangRight;
            this._defaultOverhangLeft       = helpers.toFixed3( totalOverhangLeftRight / 2 );
            this._defaultOverhangRight      = helpers.toFixed3( totalOverhangLeftRight / 2 );
        }
    }

    overhangMoveBoneZ = ( extend, bones ) => {
        if( 0 === extend ) {
            return;
        }

        bones.forEach( i => {
            if( this._bones[i] ) {
                const z = this.getRoofOverhangPosition( this._bones[i].position.z, extend );
                
                this._bones[i].setPosition( new Vector3( this._bones[i].position.x, this._bones[i].position.y, z ) );
            }
        });
    }

    overhangMoveBoneXY = ( extend, bones, roofDegree, isPlus = false ) => {
        if( 0 === extend ) {
            return;
        }

        const y = + ( extend * helpers.getTanFromDegrees( roofDegree ) ).toFixed( 3 );

        bones.forEach( i => {
            if( this._bones[i] ) {
                const x = this.getRoofOverhangPosition( this._bones[i].position.x, extend );

                if( isPlus ) {
                    this._bones[i].setPosition( new Vector3( x, this._bones[i].position.y + y, this._bones[i].position.z ) );
                } else {
                    this._bones[i].setPosition( new Vector3( x, this._bones[i].position.y - y, this._bones[i].position.z ) );
                }
            }
        });
    }

    overhangGambrelRoof = ( front, back, left, right ) => {
        const frontBones    = [ 'r_f_l1', 'r_f_l2', 'r_f_r1', 'r_f_r2', 'r_f_t' ];
        const backBones     = [ 'r_b_l1', 'r_b_l2', 'r_b_r1', 'r_b_r2', 'r_b_t' ];
        const leftBones     = [ 'r_b_l1', 'r_f_l1', 'rm_l1' ];
        const rightBones    = [ 'r_b_r1', 'r_f_r1', 'rm_r1' ];
        const roofDegree    = 0 !== this._roofLowerDegree ? this._roofLowerDegree : this._initRoofLowerDegree

        this.overhangMoveBoneZ( front, frontBones );
        this.overhangMoveBoneZ( back, backBones );
        this.overhangMoveBoneXY( left, leftBones, roofDegree );
        this.overhangMoveBoneXY( right, rightBones, roofDegree );
    }

    overhangOpenGableRoof = ( front, back, left, right ) => {
        const frontBones    = [ 'r_f_l1', 'r_f_r1', 'r_f_t', 'roof_permatile_aframe_f_l1', 'roof_permatile_aframe_f_r1', 'roof_permatile_aframe_f_t', 'roof_standing_seam_aframe_f_l1', 'roof_standing_seam_aframe_f_r1', 'roof_standing_seam_aframe_f_t' ];
        const backBones     = [ 'r_b_l1', 'r_b_r1', 'r_b_t', 'roof_permatile_aframe_b_l1', 'roof_permatile_aframe_b_r1', 'roof_permatile_aframe_b_t', 'roof_standing_seam_aframe_b_l1', 'roof_standing_seam_aframe_b_r1', 'roof_standing_seam_aframe_b_t' ];
        const leftBones     = [ 'r_b_l1', 'r_f_l1', 'rm_l1', 'roof_permatile_aframe_b_l1', 'roof_permatile_aframe_f_l1', 'roof_permatile_aframe_metal_l1', 'roof_standing_seam_aframe_b_l1', 'roof_standing_seam_aframe_f_l1', 'roof_standing_seam_aframe_metal_l1' ];
        const rightBones    = [ 'r_b_r1', 'r_f_r1', 'rm_r1', 'roof_permatile_aframe_b_r1', 'roof_permatile_aframe_f_r1', 'roof_permatile_aframe_metal_r1', 'roof_standing_seam_aframe_b_r1', 'roof_standing_seam_aframe_f_r1', 'roof_standing_seam_aframe_metal_r1' ];
        const roofDegree    = this.getRoofDegree();

        this.overhangMoveBoneZ( front, frontBones );
        this.overhangMoveBoneZ( back, backBones );
        this.overhangMoveBoneXY( left, leftBones, roofDegree );
        this.overhangMoveBoneXY( right, rightBones, roofDegree );
    }

    /**
     * @description Move bones for customized saltbox roof
     * @param front string
     * @param back string
     * @param left string
     * @param right string
     * @returns void
     */
    overhangCustomizedSaltboxRoof = ( front, back, left, right ) => {
        const frontBones    = [ 'r_f_l1', 'r_f_r1', 'r_f_t' ];
        const backBones     = [ 'r_b_l1', 'r_b_r1', 'r_b_t' ];
        const leftBones     = [ 'r_b_l1', 'r_f_l1', 'rm_l1' ];
        const rightBones    = [ 'r_b_r1', 'r_f_r1', 'rm_r1' ];
        
        const backRoofPitch  = this.getSettingData( 'model_roof_angle' );
        const frontRoofPitch = ! this.getSettingData( 'model_second_roof_angle' ) ? backRoofPitch : this.getSettingData( 'model_second_roof_angle' );

        this.overhangMoveBoneZ( front, frontBones );
        this.overhangMoveBoneZ( back, backBones );
        this.overhangMoveBoneXY( left, leftBones, frontRoofPitch );
        this.overhangMoveBoneXY( right, rightBones, backRoofPitch );
    }

    overhangSpecialRoof = ( front, back, left, right ) => {
        if( undefined === this._bones['b_b_t2'] || undefined === this._bones['b_b_r2'] ) {
            return;
        }
        
        const frontBones    = [ 'r_f_l1', 'r_f_r1', 'r_f_t1', 'r_f_t2' ];
        const backBones     = [ 'r_b_l1', 'r_b_r1', 'r_b_t1', 'r_b_t2' ];
        const leftBones     = [ 'r_b_l1', 'r_f_l1', 'rm_l1' ];
        const dormerBones   = [ 'r_b_t2', 'r_f_t2', 'rm_t2' ];
        const rightBones    = [ 'r_b_r1', 'r_f_r1', 'rm_r1' ];
        const roofDegree    = 22.62;
        const dormerHeight  = Math.abs( this._bones['b_b_t2'].position.y ) - Math.abs( this._bones['b_b_r2'].position.y );
        const dormerWidth   = Math.abs( this._bones['b_b_t2'].position.x ) + Math.abs( this._bones['b_b_r2'].position.x );
        const dormerDegree  = + ( Math.atan( dormerHeight / dormerWidth ) * 180 / Math.PI ).toFixed( 3 );

        this.overhangMoveBoneZ( front, frontBones );
        this.overhangMoveBoneZ( back, backBones );
        this.overhangMoveBoneXY( left, leftBones, roofDegree );
        this.overhangMoveBoneXY( right, rightBones, roofDegree );
        this.overhangMoveBoneXY( left, dormerBones, dormerDegree, true );
    }

    overhangMonitorRoof = ( front, back, left, right ) => {
        const frontBones        = [ 'r_f_l1', 'r_f_l2', 'r_f_l3', 'r_f_t', 'r_f_r3', 'r_f_r2', 'r_f_r1' ];
        const backBones         = [ 'r_b_l1', 'r_b_l2', 'r_b_l3', 'r_b_t', 'r_b_r3', 'r_b_r2', 'r_b_r1' ];
        const leftUpperBones    = [ 'r_b_l3', 'r_f_l3', 'rm_l3' ];
        const rightUpperBones   = [ 'r_b_r3', 'r_f_r3', 'rm_r3' ];
        const leftLowerBones    = [ 'r_b_l1', 'r_f_l1', 'rm_l1' ];
        const rightLowerBones   = [ 'r_b_r1', 'r_f_r1', 'rm_r1' ];

        const roofLowerDegree   = 0 !== this._roofLowerDegree ? this._roofLowerDegree : this._initRoofLowerDegree;
        const roofUpperDegree   = 0 !== this._roofUpperDegree ? this._roofUpperDegree : this._initRoofUpperDegree;

        this.overhangMoveBoneZ( front, frontBones );
        this.overhangMoveBoneZ( back, backBones );

        this.overhangMoveBoneXY( left, leftUpperBones, roofUpperDegree );
        this.overhangMoveBoneXY( right, rightUpperBones, roofUpperDegree );

        this.overhangMoveBoneXY( left, leftLowerBones, roofLowerDegree );
        this.overhangMoveBoneXY( right, rightLowerBones, roofLowerDegree );
    }

    overhangFlatRoof = ( front, back, left, right ) => {
        const frontBones    = [ 'r_f_l1', 'r_f_r1', 'roof_permatile_lean_to_f_l1', 'roof_permatile_lean_to_f_r1', 'roof_standing_seam_lean_to_f_l1', 'roof_standing_seam_lean_to_f_r1', 'roof_metal_horizontal_f' ];
        const backBones     = [ 'r_b_l1', 'r_b_r1', 'roof_permatile_lean_to_b_l1', 'roof_permatile_lean_to_b_r1', 'roof_standing_seam_lean_to_b_l1', 'roof_standing_seam_lean_to_b_r1', 'roof_metal_horizontal_b' ];
        const leftBones     = [ 'r_b_l1', 'r_f_l1', 'rm_l1', 'roof_permatile_lean_to_b_l1', 'roof_permatile_lean_to_f_l1', 'roof_permatile_lean_to_l1', 'roof_standing_seam_lean_to_b_l1', 'roof_standing_seam_lean_to_f_l1', 'roof_standing_seam_lean_to_l1' ];
        const rightBones    = [ 'r_b_r1', 'r_f_r1', 'rm_r1', 'roof_permatile_lean_to_b_r1', 'roof_permatile_lean_to_f_r1', 'roof_permatile_lean_to_r1', 'roof_standing_seam_lean_to_b_r1', 'roof_standing_seam_lean_to_f_r1', 'roof_standing_seam_lean_to_r1' ];
        const roofDegree    = this.getRoofDegree();

        this.overhangMoveBoneZ( front, frontBones );
        this.overhangMoveBoneZ( back, backBones );
        this.overhangMoveBoneXY( left, leftBones, roofDegree, true );
        this.overhangMoveBoneXY( right, rightBones, roofDegree );
    }

    overhangSaltboxRoof = ( front, back, left, right ) => {
        if( undefined === this._bones['r_f_t'] || undefined === this._bones['r_f_l1'] || undefined === this._bones['r_f_r1'] ) {
            return;
        }

        const frontBones    = [ 'r_f_l1', 'r_f_t', 'r_f_r1' ];
        const backBones     = [ 'r_b_l1', 'r_b_t', 'r_b_r1' ];
        const leftBones     = [ 'r_b_l1', 'r_f_l1', 'rm_l1' ];
        const rightBones    = [ 'r_b_r1', 'r_f_r1', 'rm_r1' ];
        const leftHeight    = Math.abs( this._bones['r_f_t'].position.y - this._bones['r_f_l1'].position.y );
        const leftWidth     = Math.abs( this._bones['r_f_t'].position.x - this._bones['r_f_l1'].position.x );
        const rightHeight   = Math.abs( this._bones['r_f_t'].position.y - this._bones['r_f_r1'].position.y );
        const rightWidth    = Math.abs( this._bones['r_f_t'].position.x - this._bones['r_f_r1'].position.x );
        const rightDegree   = + ( Math.atan( rightHeight / rightWidth ) * 180 / Math.PI ).toFixed( 3 );
        let leftDegree      = + ( Math.atan( leftHeight / leftWidth ) * 180 / Math.PI ).toFixed( 3 );
        if( 'lakeside' === this._shedType ) {
            leftDegree      = this.getRoofDegree();
        }

        this.overhangMoveBoneZ( front, frontBones );
        this.overhangMoveBoneZ( back, backBones );
        this.overhangMoveBoneXY( left, leftBones, leftDegree );
        this.overhangMoveBoneXY( right, rightBones, rightDegree );
    }

    overhangHipRoof = ( front ) => {
        const topBones      = [ 'r_b_t', 'r_f_t' ];
        const cornerBones   = [ 'r_f_l1', 'r_f_r1', 'r_b_l1', 'r_b_r1' ];
        const midBones      = [ 'r_f_l2', 'r_f_r2', 'r_b_l2', 'r_b_r2' ];
        const roofDegree    = this.getRoofDegree();

        if( 0 !== front ) {
            const y = + ( front * helpers.getTanFromDegrees( roofDegree ) ).toFixed( 3 );
            
            cornerBones.forEach( i => {
                if( this._bones[i] ) {
                    const x = this.getRoofOverhangPosition( this._bones[i].position.x, front );
                    const z = this.getRoofOverhangPosition( this._bones[i].position.z, front );

                    this._bones[i].setPosition( new Vector3( x, this._bones[i].position.y - y, z ) );
                }
            });

            midBones.forEach( i => {
                if( this._bones[i] ) {
                    const x = this.getRoofOverhangPosition( this._bones[i].position.x, front );

                    this._bones[i].setPosition( new Vector3( x, this._bones[i].position.y - y, this._bones[i].position.z ) );
                }
            });

            if( this.isSidePorch() ) {
                topBones.concat( cornerBones, midBones ).forEach( i => {
                    if( this._bones[i] ) {
                        this._bones[i].setPosition( new Vector3( this._bones[i].position.x, this._bones[i].position.y + y, this._bones[i].position.z ) );
                    }
                } );
    
                this.setBuildingHeight( this._bones['r_b_t'].position.y );
            }
        }
    }

    handleRoofOverhang = () => {
        if( 'structure_aframe_streamline' === this._shedType || 'structure_country_cottage' === this._shedType || 'structure_classic_aframe_dormer' === this._shedType ) {
            return;
        }

        if( 'gambrel' === this.getRoofShape() ) {
            return this.overhangGambrelRoof( this._defaultOverhangFront, this._defaultOverhangBack, this._defaultOverhangLeft, this._defaultOverhangRight );
        }
        
        if( 'open_gable' === this.getRoofShape() || 'box_gable' === this.getRoofShape() ) {
            return this.overhangOpenGableRoof( this._defaultOverhangFront, this._defaultOverhangBack, this._defaultOverhangLeft, this._defaultOverhangRight );
        }

        if( 'saltbox_customize' === this.getRoofShape() ) {
            return this.overhangCustomizedSaltboxRoof( this._defaultOverhangFront, this._defaultOverhangBack, this._defaultOverhangLeft, this._defaultOverhangRight );
        }
        
        if( 'special' === this.getRoofShape() && 'structure_alpinechalet' === this._shedType ) {
            return this.overhangSpecialRoof( this._defaultOverhangFront, this._defaultOverhangBack, this._defaultOverhangLeft, this._defaultOverhangRight );
        }

        if( 'flat' === this.getRoofShape() ) {
            return this.overhangFlatRoof( this._defaultOverhangFront, this._defaultOverhangBack, this._defaultOverhangLeft, this._defaultOverhangRight );
        }

        if( 'saltbox' === this.getRoofShape() ) {
            return this.overhangSaltboxRoof( this._defaultOverhangFront, this._defaultOverhangBack, this._defaultOverhangLeft, this._defaultOverhangRight );
        }

        if( 'hip' === this.getRoofShape() ) {
            return this.overhangHipRoof( this._defaultOverhangFront );
        }

        if( 'monitor' === this.getRoofShape() ) {
            return this.overhangMonitorRoof( this._defaultOverhangFront, this._defaultOverhangBack, this._defaultOverhangLeft, this._defaultOverhangRight );
        }
    }

    moveBonesOverhangRoofCenter = () => {
        if(  "1" !== this.getSettingData( 'is_center_rooftop' ) ) return;
        const bones             = this._bones;
        const haftlWidth        = this.getWidth() / 2;
        const roofDegree        = this.getRoofDegree();
        const constantMoveY     = Math.abs( helpers.toFixed3( this._defaultOverhangLeft *  helpers.getTanFromDegrees( roofDegree ) ) ); 
        const constantMoveX     = this._originOverhang.zerroNum * this._originOverhang.constant;

        if( bones['b_b_t'] ) bones['b_b_t'].setPosition( new Vector3( constantMoveX, bones['b_b_t'].position.y + constantMoveY, bones['b_b_t'].position.z ) );
        if( bones['b_f_t'] ) bones['b_f_t'].setPosition( new Vector3( constantMoveX, bones['b_f_t'].position.y + constantMoveY, bones['b_f_t'].position.z ) );
        if( bones['r_f_t'] ) bones['r_f_t'].setPosition( new Vector3( constantMoveX, bones['r_f_t'].position.y + constantMoveY, bones['r_f_t'].position.z ) );
        if( bones['r_b_t'] ) bones['r_b_t'].setPosition( new Vector3( constantMoveX, bones['r_b_t'].position.y + constantMoveY, bones['r_b_t'].position.z ) );
        if( bones['r_f_r1'] ) bones['r_f_r1'].setPosition( new Vector3( bones['r_f_r1'].position.x + constantMoveX, bones['r_f_r1'].position.y + constantMoveY, bones['r_f_r1'].position.z ) );
        if( bones['r_b_r1'] ) bones['r_b_r1'].setPosition( new Vector3( bones['r_b_r1'].position.x + constantMoveX, bones['r_b_r1'].position.y + constantMoveY, bones['r_b_r1'].position.z ) );
        if( bones['r_b_l1'] ) bones['r_b_l1'].setPosition( new Vector3( bones['r_b_l1'].position.x + constantMoveX, bones['r_b_l1'].position.y + constantMoveY, bones['r_b_l1'].position.z ) );
        if( bones['r_f_l1'] ) bones['r_f_l1'].setPosition( new Vector3( bones['r_f_l1'].position.x + constantMoveX, bones['r_f_l1'].position.y + constantMoveY, bones['r_f_l1'].position.z ) );
        if( bones['rm_t'] ) bones['rm_t'].setPosition( new Vector3( constantMoveX, bones['rm_t'].position.y + constantMoveY, bones['rm_t'].position.z ) );
        if( bones['rm_l1'] ) bones['rm_l1'].setPosition( new Vector3( bones['rm_l1'].position.x + constantMoveX, bones['rm_l1'].position.y + constantMoveY, bones['rm_l1'].position.z ) );
        if( bones['rm_r1'] ) bones['rm_r1'].setPosition( new Vector3( bones['rm_r1'].position.x + constantMoveX, bones['rm_r1'].position.y + constantMoveY, bones['rm_r1'].position.z ) );
    } 

    getRoofOverhangPosition = ( oldPosition, extend ) => {
        if( oldPosition > 0 ) {
            return oldPosition + extend;
        }

        return oldPosition - extend;
    }

    oneOpening = ( openingWidth, openingHeight, openingTopWidth, openingTopHeight ) => {
        return [
            new Vector3( - ( openingWidth / 2 ), 0, 0 ),
            new Vector3( - ( openingWidth / 2 ), 0, openingHeight ),
            new Vector3( - ( ( openingWidth / 2 ) - openingTopWidth ), 0, openingHeight + openingTopHeight ),
            new Vector3( openingWidth / 2 - openingTopWidth, 0, openingHeight + openingTopHeight ),
            new Vector3( openingWidth / 2, 0, openingHeight ),
            new Vector3( openingWidth / 2, 0, 0 ),
        ]
    }

    twoOpening = ( halfDepth, space, openingHeight, openingTopHeight, openingWidth, openingTopWidth ) => {
        return [
            [
                new Vector3( - halfDepth + space, 0, 0 ),
                new Vector3( - halfDepth + space, 0, openingHeight ),
                new Vector3( - halfDepth + space + openingTopWidth, 0, openingHeight + openingTopHeight ),
                new Vector3( - halfDepth + space + openingWidth - openingTopWidth, 0, openingHeight + openingTopHeight ),
                new Vector3( - halfDepth + space + openingWidth, 0, openingHeight ),
                new Vector3( - halfDepth + space + openingWidth, 0, 0 ),
            ],
            [
                new Vector3( halfDepth - space, 0, 0 ),
                new Vector3( halfDepth - space, 0, openingHeight ),
                new Vector3( halfDepth - space - openingTopWidth, 0, openingHeight + openingTopHeight ),
                new Vector3( halfDepth - space - openingWidth + openingTopWidth, 0, openingHeight + openingTopHeight ),
                new Vector3( halfDepth - space - openingWidth, 0, openingHeight ),
                new Vector3( halfDepth - space - openingWidth, 0, 0 ),
            ]
        ];
    }

    drawLeftWall = async () => {
        const doorFrame = this._scene.getMeshByName('door_frame_clone_obj');
        if( doorFrame ) {
            doorFrame.position.x = this._bones['b_b_l1'].position.x - this._doorFrameErrorNumber;
            if( undefined === this._doorFrameClone ) {
                this._doorFrameClone = doorFrame.clone( 'door_frame_clone_obj_1', null ) as Mesh;
            }
        } else {
            return;
        }

        if( this._doorFrameClone ) {
            this._doorFrameClone.position.x = this._bones['b_b_l1'].position.x - this._doorFrameErrorNumber;
            this._doorFrameClone.visibility = 0;
        } else {
            return;
        }

        const innerWallBackMesh = this.getMeshByName( 'inner_wall_b_obj' );
        let isHiddenFrameBack   = true;
        if( innerWallBackMesh ) {
            innerWallBackMesh.visibility = 0;
        } else if( 'structure_horse_shelter' !== this._shedType ) {
            return;
        }

        const innerWallFrontMesh    = this.getMeshByName( 'inner_wall_f_obj' );
        let isHiddenFrameFront      = true;
        if( innerWallFrontMesh ) {
            innerWallFrontMesh.visibility = 0;
        } else {
            return;
        }

        if( undefined !== this._tackDoor && this._tackDoor.meshes ) {
            this._tackDoor.meshes[0].setEnabled( false );
        }

        let holes               = [];
        const depth             = this.getDepth();
        const height            = this.getWallHeight();
        const tackDoorInfo      = this.getGlobalData( constants.tackDoorInfo );
        const tackDoor          = tackDoorInfo['name'];
        const colorInfo         = tackDoorInfo['colorInfo'];
        const halfDepth         = + ( depth / 2 ).toFixed( 3 );
        let isShowTackDoor      = false;
        const openingWidth      = this._openingWidth;
        const holeWidth         = openingWidth + this._trimLength;
        const openingHeight     = this._openingHeight;
        const openingTopWidth   = this._openingTopWidth;
        const openingTopHeight  = this._openingTopHeight;

        if( 'structure_horse_shelter' === this._shedType ) {
            if( 'no' !== tackDoor ) {
                const componentType = 'door';
                const componentSide = 'left';

                if( undefined === this._tackDoor ) {
                    this._tackDoor = true;
                    const args     = { side: componentSide, componentType, componentName: tackDoor, isTackDoor: true, colorInfo };
                    this._tackDoor = await this.addNewComponent( args );
                    this._engine.hideLoadingUI();
                }

                if( this._tackDoor.meshes ) {
                    if( this.isEnableTackDoor() ) {
                        const tackDoorWidth     = this.getComponentWidth( this._tackDoor.meshes, componentType, tackDoor );
                        const allComponentWidth = tackDoorWidth + holeWidth;
                        const space             = helpers.toFixed3( ( depth - allComponentWidth ) / 3 );
                        const tackRoomWidth     = jQuery( '#js-enable-tack-door' ).data( 'tackroom-size' );

                        this._tackDoor.meshes[0].setEnabled( true );

                        this._allComponents[this._tackDoor.key]['isHidden']          = false;
                        this._walls['left']['items'][this._tackDoor.key]['isHidden'] = false;

                        isShowTackDoor  = true;

                        this._tackDoor.meshes[0].position.x = this._bones['b_b_l1'].position.x;
                        innerWallFrontMesh.visibility       = 1;

                        if( 0 != tackRoomWidth ) {
                            innerWallFrontMesh.position.z       = - halfDepth + tackRoomWidth;
                            this._tackDoor.meshes[0].position.z = ( this._bones['b_b_l1'].position.z + innerWallFrontMesh.position.z ) / 2;
                            doorFrame.position.z                = ( this._bones['b_f_l1'].position.z + innerWallFrontMesh.position.z ) / 2;
                        } else {
                            innerWallFrontMesh.position.z       = - halfDepth + space + tackDoorWidth + space / 2;
                            this._tackDoor.meshes[0].position.z = - halfDepth + space + tackDoorWidth / 2;
                            doorFrame.position.z                = halfDepth - space - holeWidth / 2;
                        }

                        if( this._bones['b_b_m1'] ) this._bones['b_b_m1'].setPosition( new Vector3( this._bones['b_b_m1'].position.x, this._bones['b_b_m1'].position.y, doorFrame.position.z - openingWidth / 2 ) );
                        if( this._bones['b_b_m2'] ) this._bones['b_b_m2'].setPosition( new Vector3( this._bones['b_b_m2'].position.x, this._bones['b_b_m2'].position.y, doorFrame.position.z - openingWidth / 2 ) );
                        if( this._bones['b_f_m1'] ) this._bones['b_f_m1'].setPosition( new Vector3( this._bones['b_f_m1'].position.x, this._bones['b_f_m1'].position.y, doorFrame.position.z + openingWidth / 2 ) );
                        if( this._bones['b_f_m2'] ) this._bones['b_f_m2'].setPosition( new Vector3( this._bones['b_f_m2'].position.x, this._bones['b_f_m2'].position.y, doorFrame.position.z + openingWidth / 2 ) );
                    }else {
                        this._tackDoor.meshes[0].setEnabled( false );
                        
                        isShowTackDoor                      = false;
                        doorFrame.position.z                = 0;

                        this._allComponents[this._tackDoor.key]['isHidden']          = true;
                        this._walls['left']['items'][this._tackDoor.key]['isHidden'] = true;
                        if( this._bones['b_b_m1'] ) this._bones['b_b_m1'].setPosition( new Vector3( this._bones['b_b_m1'].position.x, this._bones['b_b_m1'].position.y, - openingWidth / 2 ) );
                        if( this._bones['b_b_m2'] ) this._bones['b_b_m2'].setPosition( new Vector3( this._bones['b_b_m2'].position.x, this._bones['b_b_m2'].position.y, - openingWidth / 2 ) );
                        if( this._bones['b_f_m1'] ) this._bones['b_f_m1'].setPosition( new Vector3( this._bones['b_f_m1'].position.x, this._bones['b_f_m1'].position.y, openingWidth / 2 ) );
                        if( this._bones['b_f_m2'] ) this._bones['b_f_m2'].setPosition( new Vector3( this._bones['b_f_m2'].position.x, this._bones['b_f_m2'].position.y, openingWidth / 2 ) );
                    }
                }
            }

            holes.push( [
                new Vector3( this._bones['b_b_m1'].position.z, 0, this._bones['b_b_m1'].position.y ),
                new Vector3( this._bones['b_b_m2'].position.z, 0, this._bones['b_b_m2'].position.y ),
                new Vector3( this._bones['b_f_m2'].position.z, 0, this._bones['b_f_m2'].position.y ),
                new Vector3( this._bones['b_f_m1'].position.z, 0, this._bones['b_f_m1'].position.y ),
            ] );

            this._openingList = [{ x: this._bones['b_b_l1'].position.x, y: 0, z: doorFrame.position.z }];

            const selectedFrame = jQuery( `input[name="ssb-configurator-upgrade-frame"]:checked` );
            if( selectedFrame.length ) {
                const file              = selectedFrame.data( 'file' );
                const distributionRules = JSON.parse( window.atob( selectedFrame.data( 'distribution-rules' ) ) );
                if( 'in-model' === file ) {
                    this.cloneFrameInModelObject( distributionRules );
                }
            } else if( 0 === jQuery( `input[name="ssb-configurator-upgrade-frame"]` ).length ) {
                if( this.isEnableTackDoor() ) {
                    const innerPosition = new Vector3( this._bones['b_f_r1'].position.x, this._bones['b_f_r1'].position.y, innerWallFrontMesh.position.z );
                    this.instanceMesh( 'stub_back_clone_obj', 40, 'b_f_r1', innerPosition );
                } else {
                    this.instanceMesh( 'stub_back_clone_obj', 40, 'b_f_r1', 'b_b_r1' );
                }
            }

        } else if( 'no' === tackDoor || ! this.isEnableTackDoor() ) {
            const oneOpening = depth < this._horseruninWidthCondition && 'structure_aframe_horsebarn' !== this._shedTypeVersion;
            if( oneOpening ) {
                holes.push( this.oneOpening( holeWidth, openingHeight, openingTopWidth, openingTopHeight ) );
                this._openingList = [{ x: this._bones['b_b_l1'].position.x, y: 0, z: 0 }];

                doorFrame.position.z = 0;
            } else {
                const space = + ( ( depth - holeWidth * 2 ) / 3 ).toFixed( 3 );

                holes.push( ...this.twoOpening( halfDepth, space, openingHeight, openingTopHeight, holeWidth, openingTopWidth ) );
                this._openingList = [
                    { x: this._bones['b_b_l1'].position.x, y: 0, z: - halfDepth + space + holeWidth / 2 },
                    { x: this._bones['b_b_l1'].position.x, y: 0, z: halfDepth - space - holeWidth / 2 },
                ];

                doorFrame.position.z            = - halfDepth + space + holeWidth / 2;
                this._doorFrameClone.visibility = 1;
                this._doorFrameClone.position.z = halfDepth - space - holeWidth / 2;

                if( 'structure_aframe_horserunin' === this._shedType ) {
                    innerWallFrontMesh.visibility   = 1;
                    innerWallFrontMesh.position.z   = 0;
                    isHiddenFrameFront              = false;
                    innerWallBackMesh.visibility    = 1;
                    innerWallBackMesh.position.z    = 0;
                    isHiddenFrameBack               = false;
                    this._horseruninFrontPositionZ  = 0;
                    this._horseruninBackPositionZ   = 0;
                }
            }

            if( this._tackDoor && this._tackDoor.key ) {
                this._allComponents[this._tackDoor.key]['isHidden']          = true;
                this._walls['left']['items'][this._tackDoor.key]['isHidden'] = true;
            }
        } else {
            const componentType = 'door';
            const componentSide = 'left';
            if( undefined === this._tackDoor ) {
                this._tackDoor = true;
                const args     = { side: componentSide, componentType, componentName: tackDoor, isTackDoor: true, colorInfo };
                this._tackDoor = await this.addNewComponent( args );
                this._engine.hideLoadingUI();
            }

            if( depth > this._horseruninWidthCondition ) {
                if( this._tackDoor.meshes ) {
                    const tackDoorWidth     = this.getComponentWidth( this._tackDoor.meshes, componentType, tackDoor ) - this._trimLength;
                    const tackDoorHeight    = this.getComponentHeight( this._tackDoor.meshes, componentType ) - this._trimLength / 2;
                    const allComponentWidth = tackDoorWidth + ( 2 * holeWidth );
                    const trimModelWidth    = 2 / 3 * this._trimLength;

                    if( allComponentWidth < depth - ( trimModelWidth * 2 ) && tackDoorHeight < height ) {
                        this.isShowTackDoorOption( true );
                        this._tackDoor.meshes[0].setEnabled( true );
                        this._allComponents[this._tackDoor.key]['isHidden']          = false;
                        this._walls['left']['items'][this._tackDoor.key]['isHidden'] = false;
                        isShowTackDoor  = true;
                        const space     = + ( ( depth - allComponentWidth ) / 4 ).toFixed( 3 );

                        holes.push( ...this.twoOpening( halfDepth, space, openingHeight, openingTopHeight, holeWidth, openingTopWidth ) );
                        this._openingList = [
                            { x: this._bones['b_b_l1'].position.x, y: 0, z: - halfDepth + space + holeWidth / 2 },
                            { x: this._bones['b_b_l1'].position.x, y: 0, z: halfDepth - space - holeWidth / 2 },
                        ];

                        doorFrame.position.z                = - halfDepth + space + holeWidth / 2;
                        this._doorFrameClone.visibility     = 1;
                        this._doorFrameClone.position.z     = halfDepth - space - holeWidth / 2;
                        this._tackDoor.meshes[0].position.x = this._bones['b_b_l1'].position.x;
                        this._tackDoor.meshes[0].position.z = 0;
                        innerWallFrontMesh.visibility       = 1;
                        isHiddenFrameFront                  = false;
                        this._horseruninFrontPositionZ      = tackDoorWidth / 2 + space / 2;
                        innerWallFrontMesh.position.z       = this._horseruninFrontPositionZ;
                        innerWallBackMesh.visibility        = 1;
                        isHiddenFrameBack                   = false;
                        this._horseruninBackPositionZ       = - ( tackDoorWidth / 2 + space / 2 );
                        innerWallBackMesh.position.z        = this._horseruninBackPositionZ;
                    } else {
                        this._tackDoor.meshes[0].setEnabled( false );
                        this._allComponents[this._tackDoor.key]['isHidden']          = true;
                        this._walls['left']['items'][this._tackDoor.key]['isHidden'] = true;

                        const space = + ( ( depth - holeWidth * 2 ) / 3 ).toFixed( 3 );

                        holes.push( ...this.twoOpening( halfDepth, space, openingHeight, openingTopHeight, holeWidth, openingTopWidth ) );
                        this._openingList = [
                            { x: this._bones['b_b_l1'].position.x, y: 0, z: - halfDepth + space + holeWidth / 2 },
                            { x: this._bones['b_b_l1'].position.x, y: 0, z: halfDepth - space - holeWidth / 2 },
                        ];

                        doorFrame.position.z            = - halfDepth + space + holeWidth / 2;
                        this._doorFrameClone.visibility = 1;
                        this._doorFrameClone.position.z = halfDepth - space - holeWidth / 2;

                        this.createAlert( "Tack door is removed as it's too big for this size." );
                        this.isShowTackDoorOption( false );
                    }
                } else {

                    this.isShowTackDoorOption( false );
                    const space = + ( ( depth - holeWidth * 2 ) / 3 ).toFixed( 3 );

                    holes.push( ...this.twoOpening( halfDepth, space, openingHeight, openingTopHeight, holeWidth, openingTopWidth ) );
                    this._openingList = [
                        { x: this._bones['b_b_l1'].position.x, y: 0, z: - halfDepth + space + holeWidth / 2 },
                        { x: this._bones['b_b_l1'].position.x, y: 0, z: halfDepth - space - holeWidth / 2 },
                    ];
    
                    doorFrame.position.z            = - halfDepth + space + holeWidth / 2;
                    this._doorFrameClone.visibility = 1;
                    this._doorFrameClone.position.z = halfDepth - space - holeWidth / 2;
                }
            } else {
                if( this._tackDoor.meshes ) {
                    const tackDoorWidth     = this.getComponentWidth( this._tackDoor.meshes, componentType, tackDoor ) - this._trimLength;
                    const tackDoorHeight    = this.getComponentHeight( this._tackDoor.meshes, componentType ) - this._trimLength / 2;
                    const allComponentWidth = tackDoorWidth + holeWidth;
                    const trimModelWidth    = 2 / 3 * this._trimLength;

                    if( allComponentWidth < depth - ( trimModelWidth * 2 ) && tackDoorHeight < height ) {
                        this.isShowTackDoorOption( true );
                        this._tackDoor.meshes[0].setEnabled( true );
                        this._allComponents[this._tackDoor.key]['isHidden']          = false;
                        this._walls['left']['items'][this._tackDoor.key]['isHidden'] = false;

                        isShowTackDoor  = true;
                        const space     = + ( ( depth - allComponentWidth ) / 3 ).toFixed( 3 );

                        holes.push([
                            new Vector3( halfDepth - space, 0, 0 ),
                            new Vector3( halfDepth - space, 0, openingHeight ),
                            new Vector3( halfDepth - space - openingTopWidth, 0, openingHeight + openingTopHeight ),
                            new Vector3( halfDepth - space - holeWidth + openingTopWidth, 0, openingHeight + openingTopHeight ),
                            new Vector3( halfDepth - space - holeWidth, 0, openingHeight ),
                            new Vector3( halfDepth - space - holeWidth, 0, 0 ),
                        ]);

                        this._openingList = [
                            { x: this._bones['b_b_l1'].position.x, y: 0, z: halfDepth - space - holeWidth / 2 },
                        ];

                        doorFrame.position.z                = halfDepth - space - holeWidth / 2;
                        this._tackDoor.meshes[0].position.x = this._bones['b_b_l1'].position.x;
                        this._tackDoor.meshes[0].position.z = - halfDepth + space + tackDoorWidth / 2;
                        innerWallFrontMesh.visibility       = 1;
                        isHiddenFrameFront                  = false;
                        this._horseruninFrontPositionZ      = - ( halfDepth - ( space + tackDoorWidth + space / 2 ) );
                        innerWallFrontMesh.position.z       = this._horseruninFrontPositionZ;
                    } else {
                        this._tackDoor.meshes[0].setEnabled( false );
                        this._allComponents[this._tackDoor.key]['isHidden']          = true;
                        this._walls['left']['items'][this._tackDoor.key]['isHidden'] = true;
                        
                        holes.push( this.oneOpening( holeWidth, openingHeight, openingTopWidth, openingTopHeight ) );
                        this._openingList = [{ x: this._bones['b_b_l1'].position.x, y: 0, z: 0 }];
        
                        doorFrame.position.z = 0;

                        this.createAlert( "Tack door is removed as it's too big for this size." );
                        this.isShowTackDoorOption( false );
                    }
                } else {
                    this.isShowTackDoorOption( false );
                    holes.push( this.oneOpening( holeWidth, openingHeight, openingTopWidth, openingTopHeight ) );
                    this._openingList = [{ x: this._bones['b_b_l1'].position.x, y: 0, z: 0 }];

                    doorFrame.position.z = 0;
                }
            }
        }

        let shape       = [];
        const rotation  = new Vector3( - Math.PI / 2, - Math.PI / 2, 0 );
        const position  = new Vector3( this._bones['b_b_l1'].position.x, 0, 0 );
        const leftBones = [ 'b_b_l1', 'b_b_l2', 'b_f_l2', 'b_f_l1' ];
        for( let i = 0; i < leftBones.length; i++ ) {
            if( undefined !== this._bones[leftBones[i]] ) {
                const y = this._bones[leftBones[i]].position.y;
                shape.push( new Vector3( this._bones[leftBones[i]].position.z, 0, y ) );
            }
        }

        const meshName = 'structure_lofted_horserunin_left_wall';
        this._leftWall = await this.liveStockCreateWall( meshName, shape, holes, rotation, position );

        let intersectMesh: Mesh;
        let isParentIntersect = false;
        if( this._tackDoor && this._tackDoor.meshes ) {
            for( let i = 0; i < this._tackDoor.meshes.length; i++ ) {
                if( this._tackDoor.meshes[i].name.includes( '_intersect_obj' ) ) {
                    intersectMesh                       = this._tackDoor.meshes[i];
                    isParentIntersect                   = true;
                    this._tackDoor.meshes[i].visibility = 0;
                }
            }
        }

        if( isShowTackDoor && isParentIntersect ) {
            const leftWallOldRotationX = this._leftWall.rotation.x;
            const tackDooroldRotationX = this._tackDoor.meshes[0].rotation.x;
            
            this._tackDoor.meshes[0].rotation.x = Math.PI / 2;
            this._leftWall.rotation.x           = 0;

            const lefWallCSG    = CSG.FromMesh( this._leftWall );
            const intesectCSG   = CSG.FromMesh( intersectMesh );
            const subCSG        = lefWallCSG.subtract( intesectCSG );
            const oldMesh       = this.getMeshByName( meshName );
            if( oldMesh ) {
                oldMesh.dispose();
            }

            this._leftWall = subCSG.toMesh( meshName, this._leftWallMat, this._scene, true );

            this._tackDoor.meshes[0].rotation.x = tackDooroldRotationX;
            this._leftWall.rotation.x           = leftWallOldRotationX;
        }

        if( 'structure_aframe_horserunin' === this._shedType ) {
            if( isHiddenFrameFront ) {
                if( this._bones['inner_frame_horizontal_clone_03_b'] ) this._bones['inner_frame_horizontal_clone_03_b'].setPosition( new Vector3( 0, 0, 0 ) );
                if( this._bones['inner_frame_horizontal_clone_03_t'] ) this._bones['inner_frame_horizontal_clone_03_t'].setPosition( new Vector3( 0, 0, 0 ) );
            } else {
                if( this._bones['inner_frame_horizontal_clone_03_b'] ) this._bones['inner_frame_horizontal_clone_03_b'].setPosition( new Vector3( this._bones['inner_frame_horizontal_clone_03_b'].position.x, 0, this._horseruninFrontPositionZ ) );
                if( this._bones['inner_frame_horizontal_clone_03_t'] ) this._bones['inner_frame_horizontal_clone_03_t'].setPosition( new Vector3( this._bones['inner_frame_horizontal_clone_03_t'].position.x, this.getWallHeight(), this._horseruninFrontPositionZ ) );
            }

            if( isHiddenFrameBack ) {
                if( this._bones['inner_frame_horizontal_clone_04_b'] ) this._bones['inner_frame_horizontal_clone_04_b'].setPosition( new Vector3( 0, 0, 0 ) );
                if( this._bones['inner_frame_horizontal_clone_04_t'] ) this._bones['inner_frame_horizontal_clone_04_t'].setPosition( new Vector3( 0, 0, 0 ) );
            } else {
                if( this._bones['inner_frame_horizontal_clone_04_b'] ) this._bones['inner_frame_horizontal_clone_04_b'].setPosition( new Vector3( this._bones['inner_frame_horizontal_clone_04_b'].position.x, 0, this._horseruninBackPositionZ ) );
                if( this._bones['inner_frame_horizontal_clone_04_t'] ) this._bones['inner_frame_horizontal_clone_04_t'].setPosition( new Vector3( this._bones['inner_frame_horizontal_clone_04_t'].position.x, this.getWallHeight(), this._horseruninBackPositionZ ) );
            }
        }

        window.dispatchEvent( new CustomEvent( 'update-shed-info' ) );
    }


    /**
     * handle disable tack room
     */
    handleDisableTackRoom = ( disableAll ) => {
        this._isEnableTackRoom = ! disableAll;
        const keyTackDoor      = this._tackDoor ? this._tackDoor.key : false;
        if( keyTackDoor && this._sideWallTackRoom ) {
            this.deleteComponent( this._sideWallTackRoom, keyTackDoor );
        }
        this._tackDoor         = undefined;
        this._sideWallTackRoom = '';
        const arrMeshName      = [ 
            'partial_wall_back',
            'temp_partial_wall_back',
            'kick_board_partial_right_obj',
            'partial_wall_front',
            'temp_partial_wall_front',
            'kick_board_partial_left_obj',
         ];
        arrMeshName.forEach( item => {
            const mesh  = this.getMeshByName( item );
            if( mesh ) mesh.dispose();
        } );

        this.cutKickBoardByTackRoom();
       
    }

    /**
     * Handle update tackroom
     * @param args 
     * @returns 
     */
    animateTackroomChange = async ( args, isUpdatePositionTackDoor = true, isMoveDoorInSide = false ) => {
        const _this = this;
        return new Promise( async resolve => {
            const { width, position, side } = args;
            const depth = this.getDepth();
            if( ( '' !== _this._dataSettingsTackroom['tackroom_available_length'] && depth < _this._dataSettingsTackroom['tackroom_available_length'] ) || '' === width || 0 === Number( width ) ) {
                const disableAll = '' !== _this._dataSettingsTackroom['tackroom_available_length'] && depth < _this._dataSettingsTackroom['tackroom_available_length'];
                _this.handleDisableTackRoom( disableAll );
                return resolve( false );
            }
            _this._sideWallTackRoom = side;
            _this._isEnableTackRoom = true;
            _this._dataTackroom = args;
            if( ! _this._isAllowEnableTackRoom || '' === _this._sideWallTackRoom ) return resolve( false );
           
            const tackDoor          = _this._dataSettingsTackroom['default_tackroom_door'];
            let bones               = _this._bones;
            const shedDepth         = _this.getDepth();
            const shedWidth         = _this.getWidth();
            const wallHeight        = _this.getWallHeight();
            const componentType     = 'door';
            const halfDepth         = helpers.toFixed3( shedDepth / 2 );
            const halfWidth         = helpers.toFixed3( shedWidth / 2 );
            let positionZ           = - halfDepth + width;
            let positionTackDoor    = - halfDepth + width / 2;
            let startPoint          = - halfDepth;
            let endPoint            = - halfDepth + width;
            const meshesInfo        = _this.getWallBones( side );
            const canAddNew         = ! _this._isShareLink || _this.tackroomNotFound;

            if( 'right' == position ) {
                positionZ        = halfDepth - width;
                positionTackDoor = halfDepth - width / 2;
                startPoint       = halfDepth - width;
                endPoint         = halfDepth;
            }

            if( 'center' === position ) {
                await _this.drawPartialWall( - ( width / 2 ), 'front' );
                await _this.drawPartialWall( width / 2, 'back' );
                _this.applyKickBoardToPartialWall( 'left', width / 2, 'left' );
                _this.applyKickBoardToPartialWall( 'right', - ( width / 2 ), 'right' );
                positionTackDoor    = 0;
                startPoint          = - width / 2;
                endPoint            = width / 2;
            } else {
                const meshPartyBack     = _this.getMeshByName( 'partial_wall_back' );
                const meshTempPartyBack = _this.getMeshByName( 'temp_partial_wall_back' );
                const meshKickBoard     = _this.getMeshByName( `kick_board_partial_right_obj` );

                if( meshPartyBack ) meshPartyBack.dispose();
                if( meshTempPartyBack ) meshTempPartyBack.dispose();
                if( meshKickBoard ) meshKickBoard.dispose();
                
                await _this.drawPartialWall( positionZ, 'front' );
                _this.applyKickBoardToPartialWall( position, positionZ, 'left' );
            }

            if( undefined === _this._tackDoor && tackDoor && canAddNew ) {
                _this._tackDoor = true;
                const lockData = helpers.getDefaultLock( 'door', tackDoor );
                const colorInfo = _this._dataSettingsTackroom['colorInfo'];
                const argsAddNew = { side, componentType, componentName: tackDoor, isTackDoor: true, lockData, colorInfo };
                _this._tackDoor = await _this.addNewComponent( argsAddNew );
            }
            if( _this._tackDoor && _this._tackDoor.meshes && isUpdatePositionTackDoor ) {
                const tackDoorWidth         = _this.getComponentWidth( _this._tackDoor.meshes, componentType, _this._tackDoor.name );
                const tackDoorHeight        = _this.getComponentHeight( _this._tackDoor.meshes, componentType );
                const args = { side, type: componentType, meshesInfo, componentWidth: tackDoorWidth, componentHeight: tackDoorHeight, componentName: _this._tackDoor.name, isValidTackRoom: false, isTackDoor: true, boundingBox: false };
                let newPositionTackDoor = _this.getNewPosition( args );
                let rotation                = _this.calculateComponentRotation( side, componentType );
                if( helpers.hasKeyInArray( zSide, side ) ) {
                    newPositionTackDoor.z = positionTackDoor;
                }
                _this._tackDoor.meshes[0].rotation = new Vector3( rotation.x, rotation.y, rotation.z );
                _this._tackDoor.meshes[0].position = new Vector3( newPositionTackDoor.x, newPositionTackDoor.y, newPositionTackDoor.z );
                _this.handleMoveComponentToSide( _this._tackDoor.side, side, _this._tackDoor );
            }
            if( _this._doorsOpeningTackRoom.length && isMoveDoorInSide ) {
                for( const k in _this._doorsOpeningTackRoom ) {
                    const item = _this._doorsOpeningTackRoom[k];
                    if( item.side !== _this._sideWallTackRoom ) continue;
                    const tmpStartX       = item.position2D.horizontal.firstPoint;
                    const tmpEndX         = item.position2D.horizontal.lastPoint;
                    const componentWidth  = _this.getComponentWidth( item.meshes, componentType, item.name );
                    const componentHeight = _this.getComponentHeight( item.meshes, componentType );
                    let newPosition:any   = false; 
                    let isChangePosition  = false;
                    if( ! _this.isValidHorizontal( startPoint, endPoint, tmpStartX, tmpEndX ) ) {
                        isChangePosition = true;
                        _this._walls[side]['items'][item.key].isIgnoreValidPosition = true;
                        const args = { side, type: componentType, meshesInfo, componentWidth, componentHeight, componentName: item.name, isValidTackRoom: true, isTackDoor: false, boundingBox: false };
                        newPosition = _this.getNewPosition( args );
                    }
                    if( newPosition ) {
                        item.meshes[0].position.z = newPosition.z
                        _this.updateComponentsPosition2D( side, item.key, item.meshes[0].position, item );
                        _this._walls[side]['items'][item.key].isIgnoreValidPosition = false;
                    } else if( isChangePosition ) {
                        _this.notification( item.meshes, _this.NOTICES.itemTooLarge );
                        _this.deleteComponent( side, item.key );
                    }
                }
            }
            if( isMoveDoorInSide ) {
                _this.isValidAllComponents();
            }

            if( ! _this._isUpdateLayout && "center" !== position ) {
                _this.distributeComponentEqually();
            }
            _this.updateHoleWall( side );
            _this.updateFrame( side );
            _this.cutKickBoardByTackRoom();
            resolve( true );
        } );
    }

    /**
     * Hanlde move component from side to new side
     * @param oldSide 
     * @param newSide 
     * @param componentInfo 
     */
    handleMoveComponentToSide = ( oldSide, newSide, componentInfo ) => {
        const key   = componentInfo.key;
        const type  = componentInfo.type;
        if( oldSide === newSide ) {
            this.updateComponentsPosition2D( newSide, key, componentInfo.meshes[0].position, componentInfo );
            return;
        }
        delete this._walls[oldSide]['items'][key];

        let meshes = componentInfo.meshes;
        for( let i = 0; i < meshes.length; i++ ) {
            meshes[i].metadata.side = newSide;
        }
        this.updateFrame( oldSide );
        componentInfo.side                  = newSide;
        componentInfo.meshes                = meshes;
        this._allComponents[key]            = componentInfo;
        this._walls[newSide]['items'][key]  = componentInfo;
        this.updateComponentsPosition2D( newSide, key, componentInfo.meshes[0].position, componentInfo );
        this.updateHoleWall( oldSide );
        jQuery( `ul.added-components.${type}` ).find( `li[data-component-key='${key}']` ).remove();
        window.dispatchEvent( new CustomEvent( 'add-component', { detail: componentInfo } ) );
        this.handleAddCursorPointer();
        this.handleMoveComponent( newSide, key );
    }

    /**
     * Handle cut kickboard inside tack room
     */
    cutKickBoardByTackRoom = () => {
        const sides      = [ 'back', 'front', 'left', 'right' ];
        sides.forEach( item => {
            this.handleIntersectKickBoard( item );
        } );
    }

    /**
     * Draw shape tackroom to cut kickboard
     * @returns 
     */
    drawShapeTackRoom = () => {
        const _this             = this;
        const dataTackroom      = _this._dataTackroom;
        const tackRoomWidth     = _this._dataTackroom.width;
        const tackRoomPosition  = _this._dataTackroom.position;
        const meshName          = `shape_tackroom_${tackRoomPosition}_${tackRoomWidth}`;
        let shapeTackRoom       = this.getMeshByName( meshName );
        if( shapeTackRoom ) {
            shapeTackRoom.dispose();
        }
        
        const height            = _this.getWallHeight();
        const depth             = _this.getWidth();
        let tmpStartX           = - tackRoomWidth / 2;
        let tmpEndX             = tackRoomWidth / 2;
        const haftDepth         = this.getDepth() / 2;
        if( 'left' === tackRoomPosition ) {
            tmpStartX  = -haftDepth;
            tmpEndX    = -haftDepth + tackRoomWidth;
        } else if( 'right' === tackRoomPosition ) {
            tmpStartX  = haftDepth - tackRoomWidth;
            tmpEndX    = haftDepth;
        }
        const shape = [
            new Vector3( -depth, 0, tmpStartX ),
            new Vector3( depth, 0, tmpStartX ),
            new Vector3( depth, 0, tmpEndX ),
            new Vector3( -depth, 0, tmpEndX ),
        ];
        const faceUV    = [];
        faceUV[0]       = new Vector4( 0, 0, 1, 1 );
        faceUV[2]       = new Vector4( 0, 1, 1, 0 );

        shapeTackRoom = MeshBuilder.ExtrudePolygon( meshName, { shape, holes: [], depth: height, faceUV, sideOrientation: Mesh.DOUBLESIDE }, this._scene );
        shapeTackRoom.position.y = height;
        shapeTackRoom.isPickable = false;
        shapeTackRoom.setEnabled( false );

        return shapeTackRoom;
    }

    handleUpdateTackRoom = async ( isUpdatePositionTackDoor = true ) => {
        const currentTackRoom = jQuery( 'input.ssb-tackroom:checked' );
        if( 0 === currentTackRoom.length ) return;
        const width     = currentTackRoom.data( 'width' );
        const position  = currentTackRoom.data( 'position' );
        const price     = currentTackRoom.data( 'price' );
        const priceType = currentTackRoom.data( 'price-type' );
        const side      = currentTackRoom.data( 'side' );
        const args      = { width, position, price, priceType, side }
        await this.animateTackroomChange( args, isUpdatePositionTackDoor, false );
    }

    getSidingTexture = () => {
        const { sidingType, defaultDiffuse, defaultBump, defaultSpecular, specularPower } = this.getSidingMaterialData( jQuery( '#siding-material-option li.active' ) );
        return this.getTextureFile( sidingType, defaultDiffuse, defaultBump, defaultSpecular, specularPower );
    }

    getTextureData = ( key ) => {
        const { sidingType, defaultDiffuse, defaultBump, defaultSpecular, specularPower } = this.getSidingMaterialData( jQuery( `#${key} li.active` ) );
        return this.getTextureFile( sidingType, defaultDiffuse, defaultBump, defaultSpecular, specularPower );
    }


    /**
     * Get texture files of floor material
     * 
     * @param {string} floorType type of floor material
     * 
     * @returns object of texture files
     */
    getFloorTextureFile = ( floorType ) => {
        const version = jQuery( `[data-floor-type=${floorType}]` ).attr( 'data-version' );

        let diffuseTexture  = null
        let bumpTexture     = null;
        let specularTexture = null;

        if( 'default' !== floorType ) {
            diffuseTexture = `${this._materialsDir}floor_${floorType}_dif.jpg${version}`;
            bumpTexture = `${this._materialsDir}floor_${floorType}_nor.jpg${version}`;
            specularTexture = `${this._materialsDir}floor_${floorType}_spe.jpg${version}`;
        }

        return { diffuseTexture, bumpTexture, specularTexture };
    }

    /**
     * Create Material for new Wall
     * 
     * @param {string} name name of material
     * @param {string} diffuseTexture 
     * @param {string} bumpTexture 
     * @param {string} specularTexture 
     * @param {Color} color
     * 
     * @returns new material
     */
    createMaterial = ( name, diffuseTexture, bumpTexture, specularTexture, color ) => {
        let material                = new StandardMaterial( name, this._scene );
        material.diffuseTexture     = new Texture( diffuseTexture, this._scene );
        material.bumpTexture        = new Texture( bumpTexture, this._scene );
        material.specularTexture    = new Texture( specularTexture, this._scene );
        
        if( color && 'color' === color.type && '' !== color.code ) { 
            material.diffuseColor = Color3.FromHexString( color.code );
        } 
        
        if( color && 'image' === color.type && '' !== color.image ) {
            material.diffuseTexture = new Texture( color.image, this._scene );
        }

        return material;
    }

    getSidingColor = ( groupType: string ) => {
        let element = jQuery( `.ssb-configurator-color.${groupType}` ).find( '.ssb-option.active' );
        if( undefined === element ) {
            element = jQuery( `.ssb-configurator-color.${groupType}` ).find( '.ssb-option:not(.unavailable):eq(0)' );
        }

        const type  = element.attr( 'data-texture-type' );
        const image = element.attr( 'data-texture-image' );
        const code  = element.attr( 'data-texture-color' );
        const name  = element.find( '.ssb-option-name' ).attr( 'data-option-value' );

        return { type, image, code, name };
    }

    liveStockCreateWall = async ( name, shape, holes, rotation, position ) => {
        const oldMesh = this.getMeshByName( name );
        if( oldMesh ) {
            oldMesh.dispose();
        }

        const wall          = MeshBuilder.ExtrudePolygon( name, { shape, holes, depth: 0.001 }, this._scene );
        wall.rotation       = rotation;
        wall.position       = position;
        const wallHeight    = wall.geometry.extend.maximum.z;
        const wallWidth     = wall.geometry.extend.maximum.x - wall.geometry.extend.minimum.x;
        const scale         = + ( 8 / 3 ).toFixed( 3 ); // 8 is half width of the initial model, 3 is scale of the initial model
        const vScale        = + ( wallHeight / scale ).toFixed( 3 );
        const uScale        = + ( wallWidth / scale ).toFixed( 3 );

        const { diffuseTexture, bumpTexture, specularTexture, specularPower } = await this.getSidingTexture();
        
        if( undefined === this._leftWallMat ) {
            this._leftWallMat = this.createMaterial( 'leftWallMat', diffuseTexture, bumpTexture, specularTexture, this.getSidingColor( 'siding' ) );

            if( -1 !== specularPower ) {
                this._leftWallMat.specularPower = specularPower;
            }
        }

        this._leftWallMat.bumpTexture['vScale']      = vScale;
        this._leftWallMat.diffuseTexture['vScale']   = vScale;
        this._leftWallMat.specularTexture['vScale']  = vScale;
        
        this._leftWallMat.bumpTexture['uScale']      = uScale;
        this._leftWallMat.diffuseTexture['uScale']   = uScale;
        this._leftWallMat.specularTexture['uScale']  = uScale;

        wall.material = this._leftWallMat;

        return wall;
    }

    updateLeftWallMaterial = ( diffuseTexture, bumpTexture, specularTexture, color, specularPower ) => {
        if( undefined === this._leftWallMat ) {
            return;
        }

        const vScale = this._leftWallMat.bumpTexture['vScale'];
        const uScale = this._leftWallMat.bumpTexture['uScale'];

        this._leftWallMat.diffuseTexture    = new Texture( diffuseTexture, this._scene );
        this._leftWallMat.bumpTexture       = new Texture( bumpTexture, this._scene );
        this._leftWallMat.specularTexture   = new Texture( specularTexture, this._scene );
        
        this._leftWallMat.bumpTexture['vScale']     = vScale;
        this._leftWallMat.diffuseTexture['vScale']  = vScale;
        this._leftWallMat.specularTexture['vScale'] = vScale;
        
        this._leftWallMat.bumpTexture['uScale']     = uScale;
        this._leftWallMat.diffuseTexture['uScale']  = uScale;
        this._leftWallMat.specularTexture['uScale'] = uScale;

        if( false !== specularPower ) {
            this._leftWallMat.specularPower = specularPower;
        }

        this.updateLeftWallColor( color );
    }

    getMatKeys = ( filterFloorMat = false ) => {
        let matKeys = [ ...this._apiData['material'], this._frontWallMatKey, this._backWallMatKey, this._leftWallMatKey, this._rightWallMatKey, this._ileftWallMatKey, this._ifrontWallMatKey, this._frontPorchWallMatKey, this._leftInnerPorchWallMatKey, this._rightInnerPorchWallMatKey, this._frontInnerPorchWallMatKey, this._leftMiddlePorchWallMatKey, this._leftFrontWallMatKey, this._leftBackWallMatKey, this._diagonalRightWallMatKey, this._iRightWallMatKey, this._tleftWallMatKey, this._trightWallMatKey, this._leftExtraWallMatKey ];
        if( 'structure_barn_smithbilt' === this._shedType ) {
            matKeys.push(
                `${this._frontWallMatKey}_interior`,
                `${this._backWallMatKey}_interior`,
                `${this._leftWallMatKey}_interior`,
                `${this._rightWallMatKey}_interior`,
            );
        }

        if( filterFloorMat ) {
            matKeys = matKeys.filter( key => 
                key !== this._interiorFloorMatKey
            );
        }

        return matKeys;
    }

    updateWallMaterial = ( matKeys, diffuseTexture, bumpTexture, specularTexture, color, specularPower ) => {
        if( ! matKeys ) return;

        matKeys.forEach( matKey => {
            const mats  = this.getMaterialByName( matKey );
            if( mats ) {
                mats.forEach( mat => {
                    const vScale = mat.bumpTexture['vScale'];
                    const uScale = mat.bumpTexture['uScale'];

                    mat.diffuseTexture    = new Texture( diffuseTexture, this._scene );
                    mat.bumpTexture       = new Texture( bumpTexture, this._scene );
                    mat.specularTexture   = new Texture( specularTexture, this._scene );
                    
                    mat.bumpTexture['vScale']     = vScale;
                    mat.diffuseTexture['vScale']  = vScale;
                    mat.specularTexture['vScale'] = vScale;
                    
                    mat.bumpTexture['uScale']     = uScale;
                    mat.diffuseTexture['uScale']  = uScale;
                    mat.specularTexture['uScale'] = uScale;

                    if( false !== specularPower ) {
                        mat.specularPower = specularPower;
                    }

                    if( color ) {
                        if( mat.name && -1 !== mat.name.indexOf( '_interior' ) && 0 === jQuery( '#interior_wall-material-option' ).length && this._isDrawInteriorSiding ) {
                            color = this._defaultColorInteriorSiding;
                        }
    
                        this.updateMatColor( mat, color );
                    }
                });
            }
        });
    }

    /**
     * Update material color
     * 
     * @param {StandardMaterial}    mat material
     * @param {String}              color color code
     */
    updateMatColor = ( mat, color ) => {
        mat.diffuseColor = Color3.FromHexString( color );
    }

    /**
     * 
     * @param textureType {String} image | color
     * @param textureImage {String} url of texture image file
     * @param textureColor {String} hex code color
     */
    updateWallColor = ( textureType, textureImage, textureColor, type = 'siding' ) => {
        let matKeys   = this.getMatKeys( true ).filter( key => 
            ! this._apiData['wainscoting_material'].includes( key ) && 
            ! this._apiData['accent_gable_material'].includes( key ) && 
            ! this._apiData['exterior_siding_upgrades_material'].includes( key )
        );

        if( this._isDrawInteriorSiding ) {
            matKeys = matKeys.filter( key => -1 === key.indexOf( '_interior' ) );
        }
        
        matKeys.forEach( matKey => {
            const mats  = this.getMaterialByName( matKey );
            if( mats ) {
                mats.forEach( mat => {
                    this.updateTexture( mat, textureType, textureImage, textureColor );
                });
            }
        });
    }

    updateDrawMaterialColor = ( textureType, textureImage, textureColor, key = '' ) => {
        const materials             = this._apiData[`${key}_material`];
        const { defaultDiffuse }    = this.getSidingMaterialData( jQuery( `#${key}-material-option li.active` ) );
        
        for( const key of materials ) {
            const mats  = this.getMaterialByName( key );
            if( ! mats ) continue;

            mats.forEach( mat => {
                switch( textureType ) {
                    case "image":
                        mat.diffuseColor = new Color3( 1, 1, 1 );
                        mat.diffuseTexture.updateURL( textureImage );
                        break;

                    case "color":
                    case "color_picker":
                        mat.diffuseColor = Color3.FromHexString( textureColor );
                        mat.diffuseTexture.updateURL( defaultDiffuse );
                        break;
                }
            } );
        };
    }

    updateLoafingInteriorColor = () => {
        if( 'structure_loafing_shed' !== this._shedType ) return;

        this.updatePorchColor();
    }

    updateLeftWallColor = ( color ) => {
        if( undefined === this._leftWallMat ) {
            return;
        }

        this.updateMatColor( this._leftWallMat, color );
    }

    getTackDoor = () => {
        return this._canvas.getAttribute('data-tack-door');
    }

    isEnableTackDoor = () => {
        if( this.getDepth() < 16 ) {
            return false;
        }

        return jQuery('#js-enable-tack-door').is(":checked");
    }

    ignoreHandleTackDoor = ( side ) => {
        return ( 'structure_lofted_horserunin' === this._shedType || 'structure_aframe_horserunin' === this._shedType || 'structure_horse_shelter' === this._shedType ) && 'left' === side;
    }

    disableMoveItem = key => {
        if( ! this._isEnableTackRoom || undefined === this._tackDoor || key !== this._tackDoor.key ) {
            return false;
        }
        
        return ! this._dataSettingsTackroom['enable_moving_tackroom_door'];
    }

    isItemBlock = ( key ) => {
        const componentInfor = this._allComponents[ key ];
        if( ! componentInfor ) return false;

        return componentInfor.isBlock ? true : false;
    }

    isItemBlockMoving = ( key ) => {
        const componentInfor = this._allComponents[ key ];
        if( ! componentInfor || ! componentInfor.blockOptions || ! componentInfor.hasOwnProperty( 'blockOptions' ) ) return false;
        return -1 !== componentInfor.blockOptions.indexOf( 'move' );
    }

    isItemBlockDelete = ( key ) => {
        const componentInfor = this._allComponents[ key ];
        if( ! componentInfor || ! componentInfor.blockOptions || ! componentInfor.hasOwnProperty( 'blockOptions' ) ) return false;
        
        return -1 !== componentInfor.blockOptions.indexOf( 'delete' );
    }

    isItemBlockChange = ( key ) => {
        const componentInfor = this._allComponents[ key ];
        if( ! componentInfor || ! componentInfor.blockOptions || ! componentInfor.hasOwnProperty( 'blockOptions' ) ) return false;
        
        return -1 !== componentInfor.blockOptions.indexOf( 'change' );
    }

    isShowTackDoorOption = ( isShow: boolean ) => {
        if( isShow ) {
            jQuery('.group-upgrade-tack-door').show();
        } else {
            jQuery('.group-upgrade-tack-door').hide();
        }
    }
    /**
     * Get data from admin setting
     * 
     * @param  {String} key 
     */
    getSettingData( key ) {
        const w     = ( <any> window );
        const data  = w.shedpro;

        if( data[key] ) return data[key];

        return false;
    }

    getTanRoofPorch = ( roofUp = null ) => {
        let roofUpperDegree = this._roofUpperDegree ? this._roofUpperDegree : this._initRoofUpperDegree;
        let roofLowerDegree = this._roofLowerDegree ? this._roofLowerDegree : this._initRoofLowerDegree;

        return 'roofUp' === roofUp && roofUpperDegree ? helpers.getTanFromDegrees( roofUpperDegree ) : roofLowerDegree ? helpers.getTanFromDegrees( roofLowerDegree ) : helpers.getTanFromDegrees( this._roofDegree );  
    }

    getLimitComponentXRoofPorch = ( componentHeight, componentWidth ) => {
        let width   = this.getWidth() / 2;
        let tanRoof = this.getTanRoofPorch();
        if( this._initDistancePorchError >= 0 )
            return ( width - this._initDistancePorchError / tanRoof ) - ( componentHeight / tanRoof ) - ( componentWidth / 2 );

        return width - ( ( componentHeight + this._initDistancePorchError ) / tanRoof ) - ( componentWidth / 2 );
    }

    canMoveComponentToRoof = () => {
        return 'gambrel' === this.getRoofShape() || 'open_gable' === this.getRoofShape() || 'box_gable' === this.getRoofShape();
    }

    tanRoofLeftDormer = () => {
        if( this._bones['b_d_t'] && this._bones['b_d_l1'] ) {
            let ab = this._bones['b_d_t'].position.y - this._bones['b_d_l1'].position.y;
            let ac = Math.abs( this._bones['b_d_t'].position.z - this._bones['b_d_l1'].position.z );
            return ab / ac;
        }
        return 0;
    }
    
    getSpacePorchRoofPitch = () => {
        if( 'structure_barn_deluxe_porch' === this._shedType ) {
            return this._bones['b_p_t'].position.y - this._bones['b_p_l5'].position.y;
        } else if( 'structure_old_west_modular' === this._shedType ) {
            return this._bones['b_f_t'].position.y - this._bones['b_f_l4'].position.y;
        } else if( 'structure_porch_barn' === this._shedType ) {
            return this._bones['b_f_t'].position.y - this._bones['b_f_r7'].position.y;
        }

        return this._bones['b_f_t'] && this._bones['b_f_l3'] ? this._bones['b_f_t'].position.y - this._bones['b_f_l3'].position.y : 0;
    }

    isPorchModel = () => {
        return this.getMeshesByName( 'porch_f_obj' ) !== false;
    }

    isValidComponentSide = ( side, componentHeight ) => {
        return componentHeight <= this.getWallHeightBySide( side );
    }

    getPorchHeight = () => {
        return this.getBuildingHeight() - this.getPorchBottom();
    }

    getPorchBottom = () => {
        return this._bones['post_t'] || 'standard_cottage_front_porch' === this._shedType || 'structure_lark_aframe_metal_porch' === this._shedType ? Number( ( this.getWallHeight() + this._initDistancePorchError ).toFixed(3) ) : this._bones['b_p_l2'] ? Number( ( this._bones['b_p_l2'].position.y ).toFixed(3) ) : this.getWallHeight();
    }

    isPorchHasTrim = () => {
        let trimPorchModel = [ 'structure_cardinal_cabin', 'structure_cardinal_lofted_cabin' ];
        return -1 !== trimPorchModel.indexOf( this._shedType );
    }

    isCellingPorch = () => {
        return this.getMaterialByName( 'ceiling_porch_01_mat' ) || this.getMaterialByName( 'ceiling_porch_metal_mat' ) || this.getMaterialByName( 'floor_porch_mat' );
    }

    placeAndScaleMesh = ( meshName, scaleZ, positionX, positionY ) => {
        let mesh        = this.getMeshByName( meshName );
        mesh.position.x = positionX;
        mesh.position.y = positionY;
        mesh.scaling.z  = scaleZ;
        mesh.visibility = 1;
    }

    createMeshFromCSG = ( meshCloneName, mainMeshName, intersectMeshes ) => {
        const meshClone = this.getMeshByName( meshCloneName );
        if( meshClone ) {
            meshClone.dispose();
        }

        let mainMesh = this.getMeshByName( mainMeshName );
        if( intersectMeshes.length ) {
            mainMesh.setEnabled( false );
        } else {
            return mainMesh.setEnabled( true );
        }

        let mainMeshCSG = CSG.FromMesh( mainMesh );
        for( let i = 0; i < intersectMeshes.length; i++ ) {
            mainMeshCSG.subtractInPlace( CSG.FromMesh( intersectMeshes[i] ) );
        }

        const wall = mainMeshCSG.toMesh( meshCloneName, mainMesh.material, this._scene, false );
        wall.visibility = mainMesh.visibility;
        
    }

    getIntersectMeshes = ( side ) => {
        const intersectMeshes   = [];
        for( const mesh of this._scene.meshes ) {
            if( -1 !== mesh.name.indexOf( '_intersect_obj' ) ) {
                const parentMesh = mesh.parent as Mesh;
                if( parentMesh.metadata.side === side ) {
                    mesh.visibility = 0;
                    intersectMeshes.push( mesh );
                }
            }
        }
        
        if( 'structure_loafing_shed' === this._shedType ){
            intersectMeshes.push( this.buildMeshHoleForLoafing() );
        }

        return intersectMeshes;
    }

    getIntersectMeshesAfterRotate = ( side ) => {
        const intersectMeshes = [];
        for( const mesh of this._scene.meshes ) {
            if( -1 !== mesh.name.indexOf( '_intersect_obj' ) ) {
                const parentMesh = mesh.parent as Mesh;
                if( parentMesh.metadata.side === side ) {
                    mesh.visibility = 0;
                    intersectMeshes.push( mesh );
                }
            }
        }

        return intersectMeshes;
    }

    resetIntersectMeshes = () => {
        for( const mesh of this._scene.meshes ) {
            if( -1 !== mesh.name.indexOf( '_intersect_obj' ) ) {
                mesh.position.y = 0;
                mesh.position.z = 0;
                mesh.scaling.y  = 1;
                mesh.rotation   = new Vector3( 0, 0, 0 );
            }
        }
    }

    getWallRotation = side => {
        switch( side ) {
            case 'front':
            case 'porch':
            case 'top_front':
            case 'inner_front':
            case 'lean_to_front':
            case 'right_inner_porch':
                return new Vector3( 3 * Math.PI / 2, 0, 0 );
            case 'back':
            case 'top_back':
            case 'inner_back':
            case 'lean_to_back':
            case 'left_inner_porch':
                return new Vector3( 3 * Math.PI / 2, 0, 0 );
            case 'left':
            case 'top_left':
            case 'left_back':
            case 'left_extra':
            case 'inner_left':
            case 'left_front':
            case 'front_inner_porch':
            case 'left_middle_porch':
                return new Vector3( 3 * Math.PI / 2, 3 * Math.PI / 2, 0 );
            case 'right':
            case 'top_right':
            case 'lean_to_right':
                return new Vector3( 3 * Math.PI / 2, 3 * Math.PI / 2, 0 );
            case 'inner_right':
                return new Vector3( 3 * Math.PI / 2, Math.PI / 2, 0 );
            case 'top_left':
                return new Vector3( 0, 3 * Math.PI / 2, 0 );
            case 'diagonal_right':
                return new Vector3( 3 * Math.PI / 2, Math.PI / 4, 0 );
            case 'soffit_left':
            case 'soffit_right':
                    return new Vector3( 0, Math.PI / 2, 0 );
            case 'floor':
                return new Vector3( 0, 0, 0 );
            default:
                return new Vector3( 0, 0, 0 );
                break;
        }
    }

    getWallInteriorPosition = ( side, position ) => {
        switch( side ) {
            case 'front':
                return new Vector3( position.x, position.y, position.z - 0.01 );
            case 'back':
                return new Vector3( position.x, position.y, position.z + 0.01 );
            case 'left':
                return new Vector3( position.x + 0.01, position.y, position.z );
            case 'right':
                return new Vector3( position.x - 0.01, position.y, position.z );

            default:
                break;                
        }
    }

    getWallPositionBySide = ( side, position, offset = 0 ) => {
        switch( side ) {
            case 'top_front':
            case 'top_back':
            case 'lean_to_front':
            case 'lean_to_back':
            case 'front':
            case 'back':
            case 'inner_front':
            case 'right_inner_porch':
            case 'left_inner_porch':
            case 'porch':
            case 'inner_back':
                return new Vector3( 0, 0, position.z + offset );
            case 'left':
            case 'top_left':
            case 'right':
            case 'lean_to_right':
            case 'top_right':
            case 'inner_left':
            case 'inner_right':
            case 'left_back':
            case 'left_front':
            case 'front_inner_porch':
            case 'left_middle_porch':
                return new Vector3( position.x + offset, 0, 0 );
            case 'diagonal_right':
                return new Vector3( position.x, 0, position.z );
            case 'floor':
                const floorHeight = this.getFloorOffset();
                return new Vector3( 0, floorHeight + offset, 0 );
            case 'soffit_left':
            case 'soffit_right':
                return new Vector3( 0, position.y + offset, 0 );
            default:
                break;
        }
    }

    getWallPosition = ( side, key, offset = 0 ) => {
        if( ! this._bones[key] ) {
            return key;
        }
        let bonePosition    = this._bones[key].position;
        const parentBone    = this._bones[key].getParent();
        if( parentBone && 'Body' !== parentBone.name ) {
            const boneName = parentBone.name.toLowerCase();
            if( parentBone.name && this._bones[boneName] ) bonePosition = bonePosition.add( this._bones[boneName].position );
        }
        return this.getWallPositionBySide( side, bonePosition, offset );
    }

    calculateWallInfo = ( wall ) => {
        if( ! wall.bones ) {
            return;
        }
        const _this     = this;
        let shape       = [];
        const rotation  = _this.getWallRotation( wall.side );
        const offset    = wall.offset ? Number( wall.offset ) : 0;
        const position  = _this.getWallPosition( wall.side, wall.position, offset );
        
        const isFloor   = 'floor' === wall.side;
        const isZSide   = zSide.indexOf( wall.side ) !== -1;
        const isSoffit  = 'soffit_left' === wall.side || 'soffit_right' === wall.side;
        wall.bones.forEach(key => {
            const bone = _this._bones[key];
            if( bone ) {
                let x = isZSide && !isFloor ? bone.position.z : bone.position.x;
                let z = isFloor ? bone.position.z : bone.position.y;
                if( isSoffit ) {
                    x = bone.position.z;
                    z = bone.position.x;
                }
                shape.push( new Vector3( x, 0, z ) );
            } else {
                shape.push( key );
            }
        });

        return {
            shape,
            rotation,
            position,
        };
    }

    updateHoleWall = ( side ) => {
        const _this         = this;
        const modelsWall3D  = [ 'structure_chalet_genesis' ].concat( _this.getCarportModels() );

        if( ( 'top_front' === side || 'top_back' === side || 'top_left' === side || 'top_right' === side ) && this.is2StoryLeanTo() ) {
            side = side.replace( 'top_', '' );
        }

        if( -1 !== modelsWall3D.indexOf( _this._shedType ) ) {
            const sides = [ 'front', 'back', 'left', 'right' ];

            sides.forEach( wall => {
                if( side === wall ) {
                    const wallName = `wall_${side}_clone`;
                    _this.createHoleWall3D( side, wallName );
                }
            });
        } else {
            const walls = this.getWallInfo();
            walls.forEach( wall => {
                if( side === wall.side ) {
                    _this.createHoleWall3D( side, wall.name );
                    _this.createHoleWall3D( side, wall.name + '_interior' );
                    _this.barnSmithbiltHoleRoof( side );
                }
            });
        }

        let wallsInner = _this.getInteriorWallInfo();
        wallsInner.forEach( wall => {
            if( side === wall.side ) {
                _this.createHoleWall3D( side, wall.name, true );
            }
        } );
        _this.createWallInner();
        // _this.createWallFinish();
    }

    buildMesh = async () => {
        const bones = this._bones;
        const walls = this.getWallInfo();
        this.removeWainscotingMesh();
        this.setWaincostingWallInfor( walls );
        if( 'structure_barn_smithbilt' === this._shedType || 'structure_loafing_shed' === this._shedType ) {
            walls.forEach( async wall => {
                const { shape, rotation, position } = this.calculateWallInfo( wall );
                const interiorPosition              = this.getWallInteriorPosition( wall.side, position )
                await this.drawWall( wall.name, wall.oldName, shape, rotation, position, wall.materialName, wall.height, wall.parentMesh, wall.side );
                await this.drawInterior( `${wall.name}_interior`, shape, rotation, interiorPosition, `${wall.materialName}_interior`, wall.height, wall.side );
                this.createHoleForLoafing( wall.side );
                this.updateHoleWall( wall.side );

                if( ! helpers.hasKeyInArray( this._meshBlurForInterior, `${wall.name}_interior` ) ) {
                    this._meshBlurForInterior.push( `${wall.name}_interior` );
                }
            });

            this.updateLoafingInteriorColor();
        } else {
            walls.forEach( async wall => {
                const { shape, rotation, position } = this.calculateWallInfo( wall );
                await this.drawWall( wall.name, wall.oldName, shape, rotation, position, wall.materialName, wall.height, wall.parentMesh, wall.side );
                this.drawTrim( wall );
                this.updateHoleWall( wall.side );

                if( ! helpers.hasKeyInArray( this._meshBlurForInterior, wall.name ) && 'floor' !== wall.side ) {
                    this._meshBlurForInterior.push( wall.name );
                }
            });
        }
        if( this._isDrawInteriorSiding ) {
            await this.drawInteriorlWallCeiling();
        }
    }

    drawWall = async ( name, defaultName, shape, rotation, position, materialName, height, parentMeshName, wallSide = '', depth= 0.001, faceUV = [], orientation = Mesh.DEFAULTSIDE ) => {
        const activeFloorMaterial = this.getActiveFloorMaterialData();
        if( 'floor' === wallSide && ! activeFloorMaterial?.floorType ) return;

        const oldMesh = this.getMeshByName( name );
        if( oldMesh ) {
            oldMesh.dispose();
        }

        const defaultMesh = this.getMeshByName( defaultName );
        if( defaultMesh ) {
            defaultMesh.dispose();
        }

        const wallNameClone = `${name}_clone`;
        const meshClone     = this.getMeshByName( wallNameClone );
        if( meshClone ) {
            meshClone.dispose();
        }

        let parentMesh = 'string' === typeof parentMeshName ? this.getMeshByName( parentMeshName ) : parentMeshName;
        if( ! parentMesh ) {
            parentMesh = this.getMeshByName( 'body_obj' );
        }
        if( 0 == faceUV.length ) {
            faceUV[0]       = new Vector4( 0, 0, 1, 1 );
            faceUV[2]       = new Vector4( 0, 1, 1, 0 );
        }
        const wall      = MeshBuilder.ExtrudePolygon( name, { shape, holes: [], depth, faceUV, sideOrientation: orientation }, this._scene );
        wall.isPickable = false;
        wall.checkCollisions = true;
        wall.setParent( parentMesh );
        wall.rotation   = new Vector3( rotation.x, rotation.y, rotation.z );
        wall.position   = new Vector3( position.x, position.y, position.z );

        if( ! this._apiData['material'].includes( materialName ) ) {
            this._apiData['material'].push( materialName );
        }

        if( ! this._apiData['meshes'].includes( name ) ) {
            this._apiData['meshes'].push( name );
        }
       
        await this.applyMaterial( { wall, materialName, wallHeight: height, wallWidth: 0, wallSide } );

        return wall;
    }
    
    /**
     * Get walls data from hard code models
     * 
     * @returns {Array} Array of wall objects from hard code model
     */
    getWallInfoFromHardCode = ( allowGet ) => {
        const _this       = this;
        const bones       = _this._bones;
        const allowDraw   = _this.getSettingData( 'apply_new_walls' );
        let hardCodeWalls = [];
        
        if( 'structure_barn_smithbilt' === this._shedType ) {
            hardCodeWalls = [
                {
                    side: 'front',
                    position: 'b_f_l1',
                    bones: [
                        'b_f_l1',
                        new Vector3( bones['b_f_l2'].position.x, 0, bones['b_f_l2'].position.y - this._smithbiltBarnRoofFix ),
                        new Vector3( bones['b_f_l2'].position.x - 0.5, 0, bones['b_f_l2'].position.y - this._smithbiltBarnRoofFix ),
                        new Vector3( bones['b_f_l2'].position.x - 0.24, 0, bones['b_f_l2'].position.y - 1 ),
                        new Vector3( bones['b_f_l2'].position.x - 0.16, 0, bones['b_f_l2'].position.y - 0.5 ),
                        new Vector3( bones['b_f_l2'].position.x - 0.1, 0, bones['b_f_l2'].position.y - 0.18 ),
                        new Vector3( bones['b_f_l2'].position.x - 0.09, 0, bones['b_f_l2'].position.y - 0.15 ),
                        new Vector3( bones['b_f_l2'].position.x - 0.07, 0, bones['b_f_l2'].position.y - 0.1 ),
                        'b_f_l2',
                        'b_f_t',
                        'b_f_r2',
                        new Vector3( bones['b_f_r2'].position.x + 0.07, 0, bones['b_f_r2'].position.y - 0.1 ),
                        new Vector3( bones['b_f_r2'].position.x + 0.09, 0, bones['b_f_r2'].position.y - 0.15 ),
                        new Vector3( bones['b_f_r2'].position.x + 0.1, 0, bones['b_f_r2'].position.y - 0.18 ),
                        new Vector3( bones['b_f_r2'].position.x + 0.16, 0, bones['b_f_r2'].position.y - 0.5 ),
                        new Vector3( bones['b_f_r2'].position.x + 0.24, 0, bones['b_f_r2'].position.y - 1 ),
                        new Vector3( bones['b_f_r2'].position.x + 0.5, 0, bones['b_f_r2'].position.y - this._smithbiltBarnRoofFix ),
                        new Vector3( bones['b_f_r2'].position.x, 0, bones['b_f_r2'].position.y - this._smithbiltBarnRoofFix ),
                        'b_f_r1'
                    ],
                    name: 'front_wall',
                    oldName: 'siding_f_obj',
                    materialName: this._frontWallMatKey,
                    height: undefined
                },
                {
                    side: 'back',
                    position: 'b_b_l1',
                    bones: [
                        'b_b_l1',
                        new Vector3( bones['b_b_l2'].position.x, 0, bones['b_b_l2'].position.y - this._smithbiltBarnRoofFix ),
                        new Vector3( bones['b_b_l2'].position.x - 0.5, 0, bones['b_b_l2'].position.y - this._smithbiltBarnRoofFix ),
                        new Vector3( bones['b_b_l2'].position.x - 0.24, 0, bones['b_b_l2'].position.y - 1 ),
                        new Vector3( bones['b_b_l2'].position.x - 0.16, 0, bones['b_b_l2'].position.y - 0.5 ),
                        new Vector3( bones['b_b_l2'].position.x - 0.1, 0, bones['b_b_l2'].position.y - 0.18 ),
                        new Vector3( bones['b_b_l2'].position.x - 0.09, 0, bones['b_b_l2'].position.y - 0.15 ),
                        new Vector3( bones['b_b_l2'].position.x - 0.07, 0, bones['b_b_l2'].position.y - 0.1 ),
                        'b_b_l2',
                        'b_b_t',
                        'b_b_r2',
                        new Vector3( bones['b_b_r2'].position.x + 0.07, 0, bones['b_b_r2'].position.y - 0.1 ),
                        new Vector3( bones['b_b_r2'].position.x + 0.09, 0, bones['b_b_r2'].position.y - 0.15 ),
                        new Vector3( bones['b_b_r2'].position.x + 0.1, 0, bones['b_b_r2'].position.y - 0.18 ),
                        new Vector3( bones['b_b_r2'].position.x + 0.16, 0, bones['b_b_r2'].position.y - 0.5 ),
                        new Vector3( bones['b_b_r2'].position.x + 0.24, 0, bones['b_b_r2'].position.y - 1 ),
                        new Vector3( bones['b_b_r2'].position.x + 0.5, 0, bones['b_b_r2'].position.y - this._smithbiltBarnRoofFix ),
                        new Vector3( bones['b_b_r2'].position.x, 0, bones['b_b_r2'].position.y - this._smithbiltBarnRoofFix ),
                        'b_b_r1'
                    ],
                    name: 'back_wall',
                    oldName: 'siding_b_obj',
                    materialName: this._backWallMatKey,
                    height: undefined
                },
                {
                    side: 'left',
                    position: 'b_b_l1',
                    bones: [ 'b_b_l1', new Vector3( bones['b_b_l2'].position.z, 0, bones['b_b_l2'].position.y - this._smithbiltBarnRoofFix ), new Vector3( bones['b_f_l2'].position.z, 0, bones['b_f_l2'].position.y - this._smithbiltBarnRoofFix ), 'b_f_l1' ],
                    name: 'left_wall',
                    oldName: 'siding_l_obj',
                    materialName: this._leftWallMatKey,
                    height: this.getWallHeight() - this._smithbiltBarnRoofFix
                },
                {
                    side: 'right',
                    position: 'b_b_r1',
                    bones: [ 'b_b_r1', new Vector3( bones['b_b_r2'].position.z, 0, bones['b_b_r2'].position.y - this._smithbiltBarnRoofFix ), new Vector3( bones['b_f_r2'].position.z, 0, bones['b_f_r2'].position.y - this._smithbiltBarnRoofFix ), 'b_f_r1' ],
                    name: 'right_wall',
                    oldName: 'siding_r_obj',
                    materialName: this._rightWallMatKey,
                    height: this.getWallHeight() - this._smithbiltBarnRoofFix
                },
            ];
        }

        if( 'structure_barn_corner_porch' === _this._shedType ) {
            hardCodeWalls = [
                {
                    side: 'front',
                    position: 'b_f_l1',
                    bones: [
                        new Vector3( bones['b_f_l1'].position.x, 0, bones['b_f_l1'].position.y + 0.22 ), 
                        new Vector3( bones['b_f_l2'].position.x, 0, bones['b_f_l2'].position.y - 0.16 ), 
                        new Vector3( bones['b_p_l3'].position.x, 0, bones['b_p_l3'].position.y - 0.16 ), 
                        'b_p_l3', 'b_f_l3', 'b_f_t', 'b_f_r3', 'b_f_r2', 
                        new Vector3( bones['b_f_r1'].position.x, 0, bones['b_f_r1'].position.y + 0.22 ), 
                    ],
                    name: 'front_wall',
                    oldName: 'siding_f_obj',
                    materialName: this._frontWallMatKey,
                },
                {
                    side: 'back',
                    position: 'b_b_l1',
                    bones: [ 
                        new Vector3( bones['b_b_l1'].position.x, 0, bones['b_b_l1'].position.y + 0.22 ), 
                        'b_b_l2', 'b_b_l3', 'b_b_t', 'b_b_r3', 'b_b_r2', 
                        new Vector3( bones['b_b_r1'].position.x, 0, bones['b_b_r1'].position.y + 0.22 ), 
                    ],
                    name: 'back_wall',
                    oldName: 'siding_b_obj',
                    materialName: this._backWallMatKey,
                },
                {
                    side: 'left',
                    position: 'b_p_l1',
                    bones: [
                        new Vector3( bones['b_p_l1'].position.z, 0, bones['b_p_l1'].position.y + 0.22 ), 
                        new Vector3( bones['b_p_l2'].position.z, 0, bones['b_p_l2'].position.y - 0.16 ), 
                        new Vector3( bones['b_p_l3'].position.z, 0, bones['b_p_l3'].position.y - 0.16 ), 
                        new Vector3( bones['b_p_l3'].position.z, 0, bones['b_p_l3'].position.y ), 
                        'b_b_l2', 
                        new Vector3( bones['b_b_l1'].position.z, 0, bones['b_b_l1'].position.y + 0.22 ), 
                    ],
                    name: 'left_wall',
                    oldName: 'siding_l_obj',
                    materialName: this._leftWallMatKey,
                },
                {
                    side: 'right',
                    position: 'b_b_r1',
                    bones: [ 
                        new Vector3( bones['b_b_r1'].position.z, 0, bones['b_b_r1'].position.y + 0.22 ), 
                        'b_b_r2', 'b_f_r2', 
                        new Vector3( bones['b_f_r1'].position.z, 0, bones['b_f_r1'].position.y + 0.22 ), 
                    ],
                    name: 'right_wall',
                    oldName: 'siding_r_obj',
                    materialName: this._rightWallMatKey,
                },
                {
                    side: 'inner_left',
                    position: 'b_f_l1',
                    bones: [ 
                        new Vector3( bones['b_f_l1'].position.z, 0, bones['b_f_l1'].position.y + 0.22 ), 
                        'b_f_l2', 'b_b_m3',
                        new Vector3( bones['b_b_m4'].position.z, 0, bones['b_b_m4'].position.y + 0.22 ), 
                    ],
                    name: 'inner_left_wall',
                    oldName: '',
                    materialName: this._ileftWallMatKey,
                },
                {
                    side: 'inner_front',
                    position: 'b_b_m4',
                    bones: [ 
                        new Vector3( bones['b_b_m4'].position.x, 0, bones['b_b_m4'].position.y + 0.22 ), 
                        'b_b_m3', 'b_p_l2', 
                        new Vector3( bones['b_p_l1'].position.x, 0, bones['b_p_l1'].position.y + 0.22 ), 
                    ],
                    name: 'inner_front_wall',
                    oldName: '',
                    materialName: this._ifrontWallMatKey,
                }
            ];
        }

        if( 'structure_aframe_cornerporch' === _this._shedType && allowGet ) {
            hardCodeWalls = [
                {
                    side: 'front',
                    position: 'b_f_r1',
                    bones: [ 
                        new Vector3( bones['b_f_r1'].position.x, 0, bones['b_f_r1'].position.y + 0.22 ),
                        'b_f_r2', 'b_f_t', 
                        new Vector3( bones['b_p_l3'].position.x, 0, bones['b_p_l3'].position.y - 0.15 ), 
                        new Vector3( bones['b_f_l2'].position.x, 0, bones['b_f_l2'].position.y - 0.15 ), 
                        new Vector3( bones['b_f_l1'].position.x, 0, bones['b_f_l1'].position.y + 0.22 )
                    ],
                    name: 'front_wall',
                    oldName: 'siding_f_obj',
                    materialName: this._frontWallMatKey,
                },
                {
                    side: 'back',
                    position: 'b_b_l1',
                    bones: [ 
                        new Vector3( bones['b_b_l1'].position.x, 0, bones['b_b_l1'].position.y + 0.22 ), 
                        'b_b_l2', 'b_b_t', 'b_b_r2', 
                        new Vector3( bones['b_b_r1'].position.x, 0, bones['b_b_r1'].position.y + 0.22 ), 
                    ],
                    name: 'back_wall',
                    oldName: 'siding_b_obj',
                    materialName: this._backWallMatKey,
                },
                {
                    side: 'left',
                    position: 'b_p_l1',
                    bones: [ 
                        new Vector3( bones['b_b_l1'].position.z, 0, bones['b_b_l1'].position.y + 0.22 ), 
                        'b_b_l2', 'b_p_l2', 
                        new Vector3( bones['b_p_l1'].position.z, 0, bones['b_p_l1'].position.y + 0.22 )
                    ],
                    name: 'left_wall',
                    oldName: 'siding_l_obj',
                    materialName: this._leftWallMatKey,
                },
                {
                    side: 'right',
                    position: 'b_b_r1',
                    bones: [ 
                        new Vector3( bones['b_b_r1'].position.z, 0, bones['b_b_r1'].position.y + 0.22 ), 
                        'b_b_r2', 'b_f_r2', 
                        new Vector3( bones['b_f_r1'].position.z, 0, bones['b_f_r1'].position.y + 0.22 ), 
                    ],
                    name: 'right_wall',
                    oldName: 'siding_r_obj',
                    materialName: this._rightWallMatKey,
                },
                {
                    side: 'inner_left',
                    position: 'b_f_l1',
                    bones: [ 
                        new Vector3( bones['b_b_m4'].position.z, 0, bones['b_b_m4'].position.y + 0.44 ),
                        'b_b_m3', 'b_f_l2', 
                        new Vector3( bones['b_f_l1'].position.z, 0, bones['b_f_l1'].position.y + 0.44 )
                    ],
                    name: 'inner_left_wall',
                    oldName: '',
                    materialName: this._ileftWallMatKey,
                },
                {
                    side: 'inner_front',
                    position: 'b_b_m4',
                    bones: [ 
                        new Vector3( bones['b_b_m4'].position.x, 0, bones['b_b_m4'].position.y + 0.44 ),
                        'b_b_m3', 'b_p_l2', 
                        new Vector3( bones['b_p_l1'].position.x, 0, bones['b_p_l1'].position.y + 0.44 )
                    ],
                    name: 'inner_front_wall',
                    oldName: '',
                    materialName: this._ifrontWallMatKey,
                }
            ];
        }

        if( 'structure_cardinal_lofted_cabin' === this._shedType ) {
            const tanRoof           = this._roofLowerDegree ? helpers.getTanFromDegrees( this._roofLowerDegree ) : helpers.getTanFromDegrees( this._initRoofLowerDegree );
            const porchMidleSpace   = ( 89 / 12 - ( 6 + 3 / 12 ) );
            const porchMidleX       = Number( ( porchMidleSpace / tanRoof ).toFixed( 3 ) );
            hardCodeWalls = [
                {
                    side: 'front',
                    position: 'b_f_r1',
                    bones: [ new Vector3( bones['b_f_r1'].position.x, 0, 0.168 ), 'b_f_r2', 'b_f_r3', 'b_f_t', 'b_f_l3', 'b_f_l2', new Vector3( bones['b_f_l1'].position.x, 0, 0.168 ) ],
                    name: 'front_wall',
                    oldName: 'siding_f_obj',
                    materialName: this._frontWallMatKey,
                    height: undefined
                },
                {
                    side: 'back',
                    position: 'b_b_l1',
                    bones: [new Vector3( bones['b_b_l1'].position.x, 0, 0.168 ), 'b_b_l2', 'b_b_l3', 'b_b_t', 'b_b_r3', 'b_b_r2', new Vector3( bones['b_b_r1'].position.x, 0, 0.168 ) ],
                    name: 'back_wall',
                    oldName: 'siding_b_obj',
                    materialName: this._backWallMatKey,
                    height: undefined
                },
                {
                    side: 'left',
                    position: 'b_b_l1',
                    bones: [ new Vector3( bones['b_b_l1'].position.z, 0, 0.168 ), 'b_b_l2', 'b_f_l2', new Vector3( bones['b_f_l1'].position.z, 0, 0.168 ), ],
                    name: 'left_wall',
                    oldName: 'siding_l_obj',
                    materialName: this._leftWallMatKey,
                    height: undefined
                },
                {
                    side: 'right',
                    position: 'b_b_r1',
                    bones: [ new Vector3( bones['b_b_r1'].position.z, 0, 0.168 ), 'b_b_r2', 'b_f_r2', new Vector3( bones['b_f_r1'].position.z, 0, 0.168 ) ],
                    name: 'right_wall',
                    oldName: 'siding_r_obj',
                    materialName: this._rightWallMatKey,
                    height: undefined
                },
                {
                    side: 'porch',
                    position: 'b_p_r2',
                    bones: [ 'b_p_r2', 'b_p_r3', 'b_p_t', 'b_p_l3','b_p_l2',
                        new Vector3( bones['b_p_l2'].position.x + 9 / 12, 0, bones['b_p_l2'].position.y ), 
                        new Vector3( + ( bones['b_p_l2'].position.x + porchMidleX + 9 / 12 ).toFixed( 2 ), 0, bones['b_p_l2'].position.y + porchMidleSpace ),
                        new Vector3( + ( bones['b_p_r2'].position.x - porchMidleX - 9 / 12 ).toFixed( 2 ), 0, bones['b_p_r2'].position.y + porchMidleSpace ),
                        new Vector3( bones['b_p_r2'].position.x - 9 / 12, 0, bones['b_p_r2'].position.y )
                    ],
                    name: 'front_porch_wall',
                    oldName: '',
                    materialName: this._frontPorchWallMatKey,
                    height: undefined
                },
            ];
        }

        if( 'structure_cottage_shed' === this._shedType ) {
            if( 'structure_saltbox_run-in' === this._shedTypeVersion ) {
                hardCodeWalls = [
                    {
                        side: 'front',
                        position: 'b_f_l1',
                        bones: [
                            'b_f_l1', 
                            new Vector3( bones['b_f_l2'].position.x, 0, bones['b_f_l2'].position.y - 1.33 ),
                            new Vector3( bones['b_f_l2'].position.x - 1.33, 0, bones['b_f_l2'].position.y ),
                            'b_f_t',
                            'b_f_r2',
                            'b_f_r1',
                        ],
                        name: 'front_wall',
                        oldName: 'siding_f_obj',
                        materialName: this._frontWallMatKey,
                    },
                    {
                        side: 'back',
                        position: 'b_b_l1',
                        bones: [
                            'b_b_l1', 
                            new Vector3( bones['b_b_l2'].position.x, 0, bones['b_b_l2'].position.y - 1.33 ),
                            new Vector3( bones['b_b_l2'].position.x - 1.33, 0, bones['b_b_l2'].position.y ),
                            'b_b_t',
                            'b_b_r2',
                            'b_b_r1',
                        ],
                        name: 'back_wall',
                        oldName: 'siding_b_obj',
                        materialName: this._backWallMatKey,
                    },
                    {
                        side: 'left',
                        position: 'b_f_l1',
                        bones: [ 'b_f_l1', 'b_f_l2', 'b_b_l2', 'b_b_l1' ],
                        name: 'left_wall',
                        oldName: 'siding_l_obj',
                        materialName: this._leftWallMatKey,
                    },
                    {
                        side: 'right',
                        position: 'b_b_r1',
                        bones: [ 'b_b_r1', 'b_b_r2', 'b_f_r2', 'b_f_r1' ],
                        name: 'right_wall',
                        oldName: 'siding_r_obj',
                        materialName: this._rightWallMatKey,
                    },
                    {
                        side: 'left_extra_floor',
                        position: new Vector3( 0, this._bones['b_b_l2'].position.y, 0 ),
                        bones: [ 
                            new Vector3( bones['b_b_l2'].position.x - 1.33, bones['b_b_l2'].position.y, bones['b_b_l2'].position.z ),
                            new Vector3( bones['b_f_l2'].position.x - 1.33, bones['b_f_l2'].position.y, bones['b_f_l2'].position.z ),
                            new Vector3( bones['b_f_l2'].position.x, bones['b_f_l2'].position.y, bones['b_f_l2'].position.z ),
                            new Vector3( bones['b_b_l2'].position.x, bones['b_b_l2'].position.y, bones['b_b_l2'].position.z ),
                        ],
                        name: 'left_extra_floor_wall',
                        oldName: '',
                        materialName: this._leftExtraFloorWallMatKey,
                        height: 5
                    },
                ];
            } else {
                hardCodeWalls = [
                    {
                        side: 'front',
                        position: 'b_f_l1',
                        bones: [
                            'b_f_l1', 
                            new Vector3( bones['b_f_l2'].position.x, 0, bones['b_f_l2'].position.y - 1.25 ),
                            new Vector3( bones['b_f_l2'].position.x - 1.25, 0, bones['b_f_l2'].position.y + 0.07 ),
                            new Vector3( bones['b_f_l2'].position.x - 1.25, 0, bones['b_f_l2'].position.y + 0.5 ),
                            'b_f_t',
                            'b_f_r2',
                            'b_f_r1',
                        ],
                        name: 'front_wall',
                        oldName: 'siding_f_obj',
                        materialName: this._frontWallMatKey,
                    },
                    {
                        side: 'back',
                        position: 'b_b_l1',
                        bones: [
                            'b_b_l1', 
                            new Vector3( bones['b_b_l2'].position.x, 0, bones['b_b_l2'].position.y - 1.25 ),
                            new Vector3( bones['b_b_l2'].position.x - 1.25, 0, bones['b_b_l2'].position.y + 0.07 ),
                            new Vector3( bones['b_b_l2'].position.x - 1.25, 0, bones['b_b_l2'].position.y + 0.5 ),
                            'b_b_t',
                            'b_b_r2',
                            'b_b_r1',
                        ],
                        name: 'back_wall',
                        oldName: 'siding_b_obj',
                        materialName: this._backWallMatKey,
                    },
                    {
                        side: 'left',
                        position: 'b_f_l1',
                        bones: [ 'b_f_l1', 'b_f_l2', 'b_b_l2', 'b_b_l1' ],
                        name: 'left_wall',
                        oldName: 'siding_l_obj',
                        materialName: this._leftWallMatKey,
                    },
                    {
                        side: 'right',
                        position: 'b_b_r1',
                        bones: [ 'b_b_r1', 'b_b_r2', 'b_f_r2', 'b_f_r1' ],
                        name: 'right_wall',
                        oldName: 'siding_r_obj',
                        materialName: this._rightWallMatKey,
                    },
                    {
                        side: 'top_left',
                        position: new Vector3( this._bones['b_f_l2'].position.x, this._bones['b_f_l2'].position.y, 0 ),
                        bones: [ 
                            new Vector3( bones['b_f_l1'].position.z, 0, bones['b_f_l1'].position.y ),
                            new Vector3( bones['b_f_l1'].position.z, 0, bones['b_f_l1'].position.y + 0.9 ),
                            new Vector3( bones['b_b_l1'].position.z, 0, bones['b_b_l1'].position.y + 0.9 ),
                            new Vector3( bones['b_b_l1'].position.z, 0, bones['b_b_l1'].position.y ),
                        ],
                        name: 'top_left_wall',
                        oldName: '',
                        materialName: this._tleftWallMatKey,
                    },
                    {
                        side: 'left_extra',
                        position: new Vector3( this._bones['b_f_l2'].position.x - 1.25, this._bones['b_f_l2'].position.y, 0 ),
                        bones: [ 
                            new Vector3( bones['b_f_l1'].position.z + 0.05, 0, 0 ),
                            new Vector3( bones['b_f_l1'].position.z + 0.05, 0, 0.43 ),
                            new Vector3( bones['b_b_l1'].position.z - 0.05, 0, 0.43 ),
                            new Vector3( bones['b_b_l1'].position.z - 0.05, 0, 0 ),
                        ],
                        name: 'left_extra_wall',
                        oldName: '',
                        materialName: this._leftExtraWallMatKey,
                    },
                ];
            }
        }

        if( this.isMiddlePorch() ) {
            const floorHeight         = 0.2;
            const floorPorchHeight    = 0.4;
            let sideFrontBones  = [ new Vector3( bones['b_f_r1'].position.x, 0, floorHeight ), 'b_f_r2', 'b_f_t', 'b_f_l2', new Vector3( bones['b_f_l1'].position.x, 0, floorHeight ) ];
            let sideBackBones   = [ new Vector3( bones['b_b_r1'].position.x, 0, floorHeight ), 'b_b_r2', 'b_b_t', 'b_b_l2', new Vector3( bones['b_b_l1'].position.x, 0, floorHeight ) ];
            
            if( 'gambrel' === this.getRoofShape() ) {
                sideFrontBones = [ new Vector3( bones['b_f_r1'].position.x, 0, floorHeight ), 'b_f_r2', 'b_f_r3', 'b_f_t', 'b_f_l3', 'b_f_l2', new Vector3( bones['b_f_l1'].position.x, 0, floorHeight ) ];
                sideBackBones = [ new Vector3( bones['b_b_r1'].position.x, 0, floorHeight ), 'b_b_r2','b_b_r3', 'b_b_t', 'b_b_l3', 'b_b_l2', new Vector3( bones['b_b_l1'].position.x, 0, floorHeight ) ];
            }

            hardCodeWalls = [
                {
                    side: 'front',
                    position: 'b_f_r1',
                    bones: sideFrontBones,
                    name: 'front_wall',
                    oldName: '',
                    materialName: this._frontWallMatKey,
                    height: undefined
                },
                {
                    side: 'back',
                    position: 'b_b_r1',
                    bones: sideBackBones,
                    name: 'back_wall',
                    oldName: '',
                    materialName: this._backWallMatKey,
                    height: undefined
                },
                {
                    side: 'left_front',
                    position: 'b_f_l1',
                    bones: [ new Vector3( bones['b_f_l1'].position.z, 0, floorHeight ), 'b_f_l2', 'b_p_l4', new Vector3( bones['b_p_l3'].position.z, 0, floorHeight ) ],
                    name: 'left_front_wall',
                    oldName: '',
                    materialName: this._leftBackWallMatKey,
                    height: undefined
                },
                {
                    side: 'left_back',
                    position: 'b_b_l1',
                    bones: [ new Vector3( bones['b_b_l1'].position.z, 0, floorHeight ), 'b_b_l2', 'b_p_l6', new Vector3( bones['b_p_l1'].position.z, 0, floorHeight ) ],
                    name: 'left_back_wall',
                    oldName: '',
                    materialName: this._leftFrontWallMatKey,
                    height: undefined
                },
                {
                    side: 'right',
                    position: 'b_b_r1',
                    bones: [ new Vector3( bones['b_b_r1'].position.z, 0, floorHeight ), 'b_b_r2', 'b_f_r2', new Vector3( bones['b_f_r1'].position.z, 0, floorHeight ) ],
                    name: 'right_wall',
                    oldName: '',
                    materialName: this._rightWallMatKey,
                    height: undefined
                },
                {
                    side: 'front_inner_porch',
                    position: 'b_f_m4',
                    bones: [ new Vector3( bones['b_f_m4'].position.z, 0, floorPorchHeight ), 'b_f_m3', 'b_b_m3', new Vector3( bones['b_b_m4'].position.z, 0, floorPorchHeight ) ],
                    name: 'front_inner_porch_wall',
                    oldName: '',
                    materialName: this._frontInnerPorchWallMatKey,
                    height: undefined
                },
                {
                    side: 'left_middle_porch',
                    position: 'b_p_l4',
                    bones: [ 'b_p_l4', 'b_p_l6', new Vector3( bones['b_p_l6'].position.z, 0, bones['b_p_l6'].position.y - 0.24 ), new Vector3( bones['b_p_l4'].position.z, 0, bones['b_p_l6'].position.y - 0.24 ) ],
                    name: 'left_middle_porch_wall',
                    oldName: '',
                    materialName: this._leftMiddlePorchWallMatKey,
                    height: undefined
                },
                {
                    side: 'right_inner_porch',
                    position: 'b_b_m4',
                    bones: [ new Vector3( bones['b_b_m4'].position.x, 0, floorPorchHeight ), 'b_b_m3', 'b_p_l6', new Vector3( bones['b_p_l1'].position.x, 0, floorPorchHeight )],
                    name: 'right_inner_porch_wall',
                    oldName: '',
                    materialName: this._rightInnerPorchWallMatKey,
                    height: undefined
                },
                {
                    side: 'left_inner_porch',
                    position: 'b_f_m4',
                    bones: [ new Vector3( bones['b_f_m4'].position.x, 0, floorPorchHeight ), 'b_f_m3', 'b_p_l4', new Vector3( bones['b_p_l3'].position.x, 0, floorPorchHeight ) ],
                    name: 'left_inner_porch_wall',
                    oldName: '',
                    materialName: this._leftInnerPorchWallMatKey,
                    height: undefined
                },
            ];
        }

        if( this.isSidePorch() && allowGet ) {
            const meshs = [ 'siding_b_h_obj', 'siding_b_v_obj', 'siding_f_h_obj', 'siding_f_v_obj', 'siding_l_obj', 'siding_r_obj' ];
            
            meshs.forEach( k => {
                let mesh = this._scene.getMeshByName( k );
                if( mesh ) mesh.dispose();
            });
        }

        if( this.isDeluxeCornerLeftPorch() ) {
            const diagonalWidth = Vector3.Distance( this._bones['b_f_r6'].position, this._bones['b_f_l4'].position );
            hardCodeWalls = [
                {
                    side: 'front',
                    position: 'b_f_l4',
                    bones: [ 'b_f_l1', 'b_f_l2', 'b_f_l3', 'b_f_l4' ],
                    name: 'front_wall',
                    oldName: 'siding_f_obj',
                    materialName: this._frontWallMatKey,
                    height: undefined
                },
                {
                    side: 'left',
                    position: 'b_f_l1',
                    bones: [ 'b_b_l1', 'b_b_l2', 'b_p_l2', 
                            new Vector3( this._bones['b_p_l2'].position.z, 0, this._bones['b_p_l2'].position.y - 0.2 ),
                            new Vector3( this._bones['b_f_l2'].position.z, 0, this._bones['b_f_l2'].position.y - 0.2 ),
                            'b_f_l1' ],
                    name: 'left_wall',
                    oldName: 'siding_l_obj',
                    materialName: this._leftWallMatKey,
                    height: undefined
                },
                {
                    side: 'right',
                    position: 'b_b_r1',
                    bones: [ 'b_b_r1', 'b_b_r2', 'b_p_r2', 
                            new Vector3( this._bones['b_p_r2'].position.z, 0, this._bones['b_p_r2'].position.y - 0.2 ),
                            new Vector3( this._bones['b_f_r2'].position.z, 0, this._bones['b_f_r2'].position.y - 0.2 ),
                            'b_f_r1' ],
                    name: 'right_wall',
                    oldName: 'siding_r_obj',
                    materialName: this._rightWallMatKey,
                    height: undefined
                },
                {
                    side: 'diagonal_right',
                    position: new Vector3( ( bones['b_f_r6'].position.x + bones['b_f_l4'].position.x ) / 2, 0, ( bones['b_f_r6'].position.z + bones['b_f_l4'].position.z ) / 2 ),
                    bones: [
                        new Vector3( - ( diagonalWidth / 2 ), 0, bones['b_f_r6'].position.y ),
                        new Vector3( - ( diagonalWidth / 2 ), 0, bones['b_f_r5'].position.y ),
                        new Vector3( diagonalWidth / 2, 0, bones['b_f_l3'].position.y ),
                        new Vector3( diagonalWidth / 2, 0, bones['b_f_l4'].position.y ),
                    ],
                    name: 'diagonal_right_wall',
                    oldName: '',
                    materialName: this._diagonalRightWallMatKey,
                    height: undefined
                },
                {
                    side: 'inner_front',
                    position: 'b_f_r1',
                    bones: [ 'b_f_r4', 'b_f_r3', 'b_f_r2', 'b_f_r1' ],
                    name: 'inner_front_wall',
                    oldName: '',
                    materialName: this._ifrontWallMatKey,
                    height: undefined
                },
                {
                    side: 'inner_right',
                    position: new Vector3( this._bones['b_f_r4'].position.x, 0, this.getDepth() - constants.deluxeFullDepth ),
                    bones: [ 'b_f_r4', 'b_f_r3', 'b_f_r5', 'b_f_r6' ],
                    name: 'inner_right_wall',
                    oldName: '',
                    materialName: this._iRightWallMatKey,
                    height: undefined
                },
                {
                    side: 'back',
                    position: 'b_b_l1',
                    bones: _this.deluxePorchGetPoins( 'back' ),
                    name: 'back_wall',
                    oldName: 'siding_b_obj',
                    materialName: this._backWallMatKey,
                    height: undefined
                },
                {
                    side: 'porch',
                    position: 'b_p_r2',
                    bones: _this.deluxePorchGetPoins( 'porch' ),
                    name: 'porch_f_obj',
                    oldName: 'porch_f_obj',
                    materialName: this._frontPorchWallMatKey,
                    height: undefined
                },
            ];
        }

        if( 'windsor' === this._shedType && allowGet ) {
            hardCodeWalls = [
                {
                    side: 'front',
                    position: 'b_f_l1',
                    bones: [ 'b_f_l1', 'b_f_l2', 'b_f_t', 'b_f_r2', 'b_f_r1' ],
                    name: 'front_wall',
                    oldName: 'siding_f_obj',
                    materialName: this._frontWallMatKey,
                    height: undefined
                },
                {
                    side: 'back',
                    position: 'b_b_l1',
                    bones: [ 'b_b_l1', 'b_b_l2', 'b_b_t', 'b_b_r2', 'b_b_r1' ],
                    name: 'back_wall',
                    oldName: 'siding_b_obj',
                    materialName: this._backWallMatKey,
                    height: undefined
                },
                {
                    side: 'left',
                    position: 'b_f_l1',
                    bones: [ 'b_f_l1', 'b_f_l2', 'b_b_l2', 'b_b_l1' ],
                    name: 'left_wall',
                    oldName: 'siding_l_obj',
                    materialName: this._leftWallMatKey,
                    height: undefined
                },
                {
                    side: 'right',
                    position: 'b_b_r1',
                    bones: [ 'b_b_r1', 'b_b_r2', 'b_f_r2', 'b_f_r1' ],
                    name: 'right_wall',
                    oldName: 'siding_r_obj',
                    materialName: this._rightWallMatKey,
                    height: undefined
                },
            ]
        }

        if( 'barn_without_fe_overhangs' === this._shedType && allowGet ){
            hardCodeWalls = [
                {
                    side: 'front',
                    position: 'b_f_l1',
                    bones: [ 'b_f_l1', 'b_f_l2', 'b_f_l3', 'b_f_t', 'b_f_r3', 'b_f_r2', 'b_f_r1' ],
                    name: 'front_wall',
                    oldName: 'siding_f_obj',
                    materialName: this._frontWallMatKey,
                    height: undefined
                },
                {
                    side: 'back',
                    position: 'b_b_l1',
                    bones: [ 'b_b_l1', 'b_b_l2', 'b_b_l3', 'b_b_t', 'b_b_r3', 'b_b_r2', 'b_b_r1' ],
                    name: 'back_wall',
                    oldName: 'siding_b_obj',
                    materialName: this._backWallMatKey,
                    height: undefined
                },
                {
                    side: 'left',
                    position: 'b_f_l1',
                    bones: [ 'b_f_l1', 'b_f_l2', 'b_b_l2', 'b_b_l1' ],
                    name: 'left_wall',
                    oldName: 'siding_l_obj',
                    materialName: this._leftWallMatKey,
                    height: undefined
                },
                {
                    side: 'right',
                    position: 'b_b_r1',
                    bones: [ 'b_b_r1', 'b_b_r2', 'b_f_r2', 'b_f_r1' ],
                    name: 'right_wall',
                    oldName: 'siding_r_obj',
                    materialName: this._rightWallMatKey,
                    height: undefined
                },
            ]
        }

        if( 'structure_lapsider_endgable' === this._shedType && allowGet ){
            hardCodeWalls = [
                {
                    side: 'front',
                    position: 'b_f_l1',
                    bones: [ 'b_f_l1', 'b_f_l2', 'b_f_t', 'b_f_r2', 'b_f_r1' ],
                    name: 'front_wall',
                    oldName: 'siding_f_obj',
                    materialName: this._frontWallMatKey,
                    height: undefined
                },
                {
                    side: 'back',
                    position: 'b_b_l1',
                    bones: [ 'b_b_l1', 'b_b_l2', 'b_b_t', 'b_b_r2', 'b_b_r1' ],
                    name: 'back_wall',
                    oldName: 'siding_b_obj',
                    materialName: this._backWallMatKey,
                    height: undefined
                },
                {
                    side: 'left',
                    position: 'b_f_l1',
                    bones: [ 'b_f_l1', 'b_f_l2', 'b_b_l2', 'b_b_l1' ],
                    name: 'left_wall',
                    oldName: 'siding_l_obj',
                    materialName: this._leftWallMatKey,
                    height: undefined
                },
                {
                    side: 'right',
                    position: 'b_b_r1',
                    bones: [ 'b_b_r1', 'b_b_r2', 'b_f_r2', 'b_f_r1' ],
                    name: 'right_wall',
                    oldName: 'siding_r_obj',
                    materialName: this._rightWallMatKey,
                    height: undefined
                },
            ]
        }

        // if( 'standard_cottage_front_porch' === this._shedType && 'true' == allowDraw ){
        //     let offsetPorchTop  = 0.75;
        //     let frontBones      = [ 'b_f_l1', 'b_f_l2', 'b_f_r2', 'b_f_r1' ];

        //     if( 'structure_aframe_front_porch_suncrest' === this._shedTypeVersion || 'structure_aframe_front_porch_suncrest_update_roof_component' === this._shedTypeVersion ){
        //         offsetPorchTop  = 0.5;
        //         frontBones      = [ 'b_f_l1', 'b_f_l2', 'b_f_t', 'b_f_r2', 'b_f_r1' ];

        //         hardCodeWalls.push( {
        //             side: 'back',
        //             position: new Vector3( 0, 0, bones['b_p_r2'].position.z - 0.4 ),
        //             bones: [ 
        //                 new Vector3( bones['b_p_r2'].position.x, 0, bones['b_p_r2'].position.y - 0.15 ),
        //                 'b_p_r2', 'b_p_t', 'b_p_l2',
        //                 new Vector3( bones['b_p_l2'].position.x, 0, bones['b_p_l2'].position.y - 0.15 ),
        //             ],
        //             name: 'front_porch_after_wall',
        //             materialName: this._frontPorchWallMatKey,
        //             height: undefined
        //         } );
        //     }

        //     hardCodeWalls = [...hardCodeWalls,
        //         {
        //             side: 'front',
        //             position: 'b_f_l1',
        //             bones: frontBones,
        //             name: 'front_wall',
        //             oldName: 'siding_f_obj',
        //             materialName: this._frontWallMatKey,
        //             height: undefined
        //         },
        //         {
        //             side: 'back',
        //             position: 'b_b_l1',
        //             bones: [ 'b_b_l1', 'b_b_l2', 'b_b_t', 'b_b_r2', 'b_b_r1' ],
        //             name: 'back_wall',
        //             oldName: 'siding_b_obj',
        //             materialName: this._backWallMatKey,
        //             height: undefined
        //         },
        //         {
        //             side: 'left',
        //             position: 'b_f_l1',
        //             bones: [ 
        //                 'b_b_l1', 'b_b_l2', 'b_p_l2', 
        //                 new Vector3( bones['b_p_l2'].position.z, 0, bones['b_p_l2'].position.y - offsetPorchTop ), 
        //                 new Vector3( bones['b_f_l2'].position.z, 0, bones['b_f_l2'].position.y - offsetPorchTop ), 
        //                 'b_f_l1', 
        //             ],
        //             name: 'left_wall',
        //             oldName: 'siding_l_obj',
        //             materialName: this._leftWallMatKey,
        //             height: undefined
        //         },
        //         {
        //             side: 'right',
        //             position: 'b_b_r1',
        //             bones: [ 
        //                 'b_b_r1', 'b_b_r2', 'b_p_r2', 
        //                 new Vector3( bones['b_p_r2'].position.z, 0, bones['b_p_r2'].position.y - offsetPorchTop ), 
        //                 new Vector3( bones['b_f_r2'].position.z, 0, bones['b_f_r2'].position.y - offsetPorchTop ), 
        //                 'b_f_r1', 
        //             ],
        //             name: 'right_wall',
        //             oldName: 'siding_r_obj',
        //             materialName: this._rightWallMatKey,
        //             height: undefined
        //         },
        //         {
        //             side: 'porch',
        //             position: 'b_p_r2',
        //             bones: [ 
        //                 new Vector3( bones['b_p_l2'].position.x, 0, bones['b_p_l2'].position.y - offsetPorchTop ),
        //                 'b_p_l2', 'b_p_t', 'b_p_r2',
        //                 new Vector3( bones['b_p_r2'].position.x, 0, bones['b_p_r2'].position.y - offsetPorchTop ),
        //             ],
        //             name: 'porch_f_obj',
        //             oldName: 'porch_f_obj',
        //             materialName: this._frontPorchWallMatKey,
        //             height: undefined
        //         },
        //     ]
        // }

        if( 'structure_rancher' === this._shedType ) {
            hardCodeWalls = [
                {
                    side: 'front',
                    position: 'b_f_l1',
                    bones: [ 'b_f_l1', 'b_f_l2', 'b_f_l3', 'b_f_l4', 'b_f_t', 'b_f_r4', 'b_f_r3', 'b_f_r2', 'b_f_r1' ],
                    name: 'front_wall',
                    oldName: '',
                    materialName: this._frontWallMatKey,
                    height: undefined
                },
                {
                    side: 'back',
                    position: 'b_b_l1',
                    bones: [ 'b_b_l1', 'b_b_l2', 'b_b_l3', 'b_b_l4', 'b_b_t', 'b_b_r4', 'b_b_r3', 'b_b_r2', 'b_b_r1' ],
                    name: 'back_wall',
                    oldName: '',
                    materialName: this._backWallMatKey,
                    height: undefined
                },
                {
                    side: 'left',
                    position: 'b_f_l1',
                    bones: [ 'b_f_l1', 'b_f_l2', 'b_b_l2', 'b_b_l1' ],
                    name: 'left_wall',
                    oldName: '',
                    materialName: this._leftWallMatKey,
                    height: undefined
                },
                {
                    side: 'right',
                    position: 'b_b_r1',
                    bones: [ 'b_b_r1', 'b_b_r2', 'b_f_r2', 'b_f_r1' ],
                    name: 'right_wall',
                    oldName: '',
                    materialName: this._rightWallMatKey,
                    height: undefined
                },
                {
                    side: 'top_left',
                    position: 'b_f_l3',
                    bones: [ 'b_f_l3', 'b_f_l4', 'b_b_l4', 'b_b_l3' ],
                    name: 'top_left_wall',
                    oldName: '',
                    materialName: this._tleftWallMatKey,
                    height: undefined
                },
                {
                    side: 'top_right',
                    position: 'b_b_r3',
                    bones: [ 'b_b_r3', 'b_b_r4', 'b_f_r4', 'b_f_r3' ],
                    name: 'top_right_wall',
                    oldName: '',
                    materialName: this._trightWallMatKey,
                    height: undefined
                },
            ]
        }

        if( 'structure_cardinal_victorian' === this._shedType ) {
            const errorNumber   = 0.385;
            const spaceGround   = 0.22;
            hardCodeWalls = [
                {
                    side: 'front',
                    position: 'b_f_l1',
                    bones: [
                        new Vector3( bones['b_f_l1'].position.x, 0, bones['b_f_l1'].position.y + spaceGround ),
                        new Vector3( bones['b_f_l2'].position.x, 0, bones['b_f_l2'].position.y + errorNumber ),
                        new Vector3( bones['b_f_t'].position.x, 0, bones['b_f_t'].position.y + errorNumber ),
                        new Vector3( bones['b_f_r2'].position.x, 0, bones['b_f_r2'].position.y + errorNumber ),
                        new Vector3( bones['b_f_r1'].position.x, 0, bones['b_f_r1'].position.y + spaceGround ),
                    ],
                    name: 'front_wall',
                    oldName: 'siding_f_obj',
                    materialName: this._frontWallMatKey,
                    height: undefined
                },
                {
                    side: 'back',
                    position: 'b_b_l1',
                    bones: [
                        new Vector3( bones['b_b_l1'].position.x, 0, bones['b_b_l1'].position.y + spaceGround ),
                        new Vector3( bones['b_b_l2'].position.x, 0, bones['b_b_l2'].position.y + errorNumber ),
                        new Vector3( bones['b_b_t'].position.x, 0, bones['b_b_t'].position.y + errorNumber ),
                        new Vector3( bones['b_b_r2'].position.x, 0, bones['b_b_r2'].position.y + errorNumber ),
                        new Vector3( bones['b_b_r1'].position.x, 0, bones['b_b_r1'].position.y + spaceGround ),
                    ],
                    name: 'back_wall',
                    oldName: 'siding_b_obj',
                    materialName: this._backWallMatKey,
                    height: undefined
                },
                {
                    side: 'left',
                    position: 'b_f_l1',
                    bones: [
                        new Vector3( bones['b_f_l1'].position.z, 0, bones['b_f_l1'].position.y + spaceGround ),
                        'b_f_l2',
                        'b_b_l2',
                        new Vector3( bones['b_b_l1'].position.z, 0, bones['b_b_l1'].position.y + spaceGround ),
                    ],
                    name: 'left_wall',
                    oldName: 'siding_l_obj',
                    materialName: this._leftWallMatKey,
                    height: undefined
                },
                {
                    side: 'right',
                    position: 'b_b_r1',
                    bones: [
                        new Vector3( bones['b_b_r1'].position.z, 0, bones['b_b_r1'].position.y + spaceGround ),
                        'b_b_r2',
                        'b_f_r2',
                        new Vector3( bones['b_f_r1'].position.z, 0, bones['b_f_r1'].position.y + spaceGround ),
                    ],
                    name: 'right_wall',
                    oldName: 'siding_r_obj',
                    materialName: this._rightWallMatKey,
                    height: undefined
                },
            ]
        }

        if ( 'standard_cottage' === this._shedType && allowGet ) {
            hardCodeWalls = [
                {
                    side: 'left',
                    position: 'b_b_l1',
                    bones: [ 'b_b_l1', 'b_b_l2', 'b_f_l2', 'b_f_l1' ],
                    name: 'left_wall',
                    oldName: 'siding_l_obj',
                    materialName: this._leftWallMatKey,
                    height: undefined
                },
                {
                    side: 'right',
                    position: 'b_b_r2',
                    bones: ['b_b_r2', 'b_f_r2', 'b_f_r1', 'b_b_r1'],
                    name: 'right_wall',
                    oldName: 'siding_r_obj',
                    materialName: this._rightWallMatKey,
                    height: undefined
                },
                {
                    side: 'front',
                    position: 'b_f_l1',
                    bones: ['b_f_l1', 'b_f_l2', 'b_f_t', 'b_f_r2', 'b_f_r1'],
                    name: 'front_wall',
                    oldName: 'siding_f_obj',
                    materialName: this._frontWallMatKey,
                    height: undefined
                },
                {
                    side: 'back',
                    position: 'b_b_l1',
                    bones: ['b_b_l1', 'b_b_l2', 'b_b_t', 'b_b_r2', 'b_b_r1'],
                    name: 'back_wall',
                    oldName: 'siding_b_obj',
                    materialName: this._backWallMatKey,
                    height: undefined
                }
            ];
        }

        if ( 'structure_aframe_genesis' === this._shedType && allowGet ) {
            hardCodeWalls = [
                {
                    side: 'front',
                    position: 'b_f_l1',
                    bones: ['b_f_l1', 'b_f_t', 'b_f_r1'],
                    name: 'front_wall',
                    oldName: 'siding_f_obj',
                    materialName: this._frontWallMatKey,
                    height: undefined
                },
                {
                    side: 'back',
                    position: 'b_b_l1',
                    bones: ['b_b_l1', 'b_b_t', 'b_b_r1'],
                    name: 'back_wall',
                    oldName: 'siding_b_obj',
                    materialName: this._backWallMatKey,
                    height: undefined
                }
            ];
        }

        hardCodeWalls = hardCodeWalls.map( wall => {
            return { ...wall, trim: this.getTrimByCondition( wall.side ) }; 
        } );

        return hardCodeWalls;
    }

    getWallInfo = ( isGetOrigin = false ) => {
        const _this             = this;
        const allowDraw         = _this.getSettingData( 'apply_new_walls' );
        const wainscoting       = _this.getSettingData( 'wainscoting' );
        const gableAccent       = _this.getSettingData( 'accent_gable' );
        const exteriorFinishes  = _this.getExteriorSidingUpgradesInfo();
        let isDataFromNetwork   = false;
        let allowGet            = isGetOrigin ? isGetOrigin : 'true' === allowDraw;

        // Get wall from hard code models
        let walls = _this.getWallInfoFromHardCode( allowGet );
        // Get wall from network if not defined in hard code
        if( 0 === walls.length && allowGet ) {
            const rawWalls      = _this._apiData['wall_raw'];
            walls               = _this.formatWallInfoFromAPI( rawWalls );
            isDataFromNetwork   = true;
        }

        // Add floor data to walls
        const floorSide = this.getFloorSideInfo( walls, isDataFromNetwork );
        if( null !== floorSide ) {
            walls.push( floorSide );
        }
        if( isGetOrigin ) {
            return walls;
        }

        if( 0 < walls.length && ! helpers.isEmptyObject( exteriorFinishes ) ) {
            walls = _this.getExteriorSidingUpgradesWallInfo( walls, exteriorFinishes );
        }
        
        if( 0 < walls.length && gableAccent ) {
            walls = _this.getAccentGableWallInfo( walls, gableAccent );
        }

        if( 0 < walls.length && wainscoting ) {
            walls = _this.getWainscotingWallInfo( walls, wainscoting );
        }

        return walls;
    }

    /**
     * get interior wall infor from network api
     */
    getInteriorWallInfo = () => {
        const _this = this;
        let exteriorWall = _this._apiData['wall_raw'].filter( item => ( ! item.hasOwnProperty( 'disableDrawInterior' )  || 1 != item.disableDrawInterior ) && ( ! item.hasOwnProperty( 'modelVersions' ) || ( 0 == item.modelVersions.length || helpers.hasKeyInArray( item.modelVersions, _this._shedTypeVersion ) ) ) );
        let interiorWall = [];
        if( _this._apiData.hasOwnProperty( 'interior_wall_raw' ) && _this._apiData['interior_wall_raw'].length ) {
            interiorWall = _this._apiData['interior_wall_raw'];
        }
        exteriorWall = exteriorWall.map( item => Object.assign( {}, item, {type:'exterior'} ) );
        interiorWall = interiorWall.map( item => Object.assign( {}, item, {type:'interior'} ) );
        let rawWalls = [ ...exteriorWall, ...interiorWall ];

        return _this.formatWallInteriorInfoFromAPI( rawWalls );
    }

    /**
     * Formats and processes wall interior information from API data.
     * 
     * @param {Array<Object>} data - The raw API data containing wall details.
     * @returns {Array<Object>
     **/
    formatWallInteriorInfoFromAPI = ( data ) => {
        const _this = this;
        if( ! data ) return [];

        const wallInfo = helpers.filterUniqueItems( data, 'modelVersions', _this._shedTypeVersion );
        const walls    = [];
        wallInfo.forEach( wall => {
            const { side, name, bones, oldName, type } = wall;
            const newName = 'interior_' + name;
            const modelVersions = ( wall.hasOwnProperty( 'modelVersions' ) ) ? wall.modelVersions : [];
            let shape = [];
            let parentMesh  = 'body_obj';
            let materialName = newName.toLowerCase();

            bones.forEach( bone => {
                const boneName = bone.name?.trim().toLowerCase();
                if( ! boneName || ! _this._bones[boneName] ) {
                    return;
                }

                const boneData = _this._bones[boneName];
                // Extract and parse the offset values (x, y, z) if they exist and are not null, otherwise default to 0
                let offsetX = bone.hasOwnProperty( 'x' ) && bone.x !== null ? parseFloat( bone.x ) : 0;
                let offsetY = bone.hasOwnProperty( 'y' ) && bone.y !== null ? parseFloat( bone.y ) : 0;
                let offsetZ = bone.hasOwnProperty( 'z' ) && bone.z !== null ? parseFloat( bone.z ) : 0;

                if( type === 'exterior' && helpers.hasKeyInArray( zSide, side ) ) {
                    offsetX = 0;
                    offsetZ = bone.hasOwnProperty( 'x' ) && bone.x !== null ? parseFloat( bone.x ) : 0;
                }
                // Compute the final position by adding offsets to the bone's original position
                let point = new Vector3(
                    boneData.position.x + offsetX,
                    boneData.position.y + offsetY,
                    boneData.position.z + offsetZ 
                );
                shape.push( point );
            } );
            // Update the position of the shape based on the given side (e.g., left, right, front, back)
            // This function likely adjusts the shape's vertices to fit the correct placement
            shape = this.updatePositionDrawWallCeling( side, shape );
            const isRevertFace = wall.hasOwnProperty( 'reverse' ) && 1 == wall.reverse;
            if ( shape && shape.length > 0 ) {
                const data = { side, name: newName, oldName, bones: shape, materialName: `api_${materialName}_wall_mat`, parentMesh, isRevertFace };
                walls.push( data );
            }
           
        } );

        return walls;
    }

    updatePositionDrawWallCeling = ( side, points ) => {
        let zeroOffset = 0.004; // 0.004 is wall depth, 0.06 ratio num
        if( jQuery( '#interior_wall-material-option' ).length ) {
            zeroOffset = this._frameDepth !== 0 ? this._frameDepth + 0.054 : zeroOffset;
        }

        // Function to compute the average value of a given key ("x", "y", or "z") in the 'points' array
        const average = ( key ) => points.reduce( ( sum, p ) => sum + p[key], 0) / points.length;

        // Compute the average X,Y,Z
        const avgX = roofSide.includes( side ) || ! zSide.includes( side ) ? average( "x" ) : 0;
        const avgY = roofSide.includes( side ) ? average( "y" ) : 0;
        const avgZ = roofSide.includes( side ) || zSide.includes( side ) ? average( "z" ) : 0;

        // Find the minimum and maximum X coordinates among all points
        const minX = Math.min( ...points.map( p => p.x ) );
        const maxX = Math.max( ...points.map( p => p.x ) );

        // Find the minimum and maximum Z coordinates among all points
        const minZ = Math.min( ...points.map( p => p.z ) );
        const maxZ = Math.max( ...points.map( p => p.z ) );

        // Find the maximum Y coordinate (highest point)
        const maxY = Math.max( ...points.map( p => p.y ) );

        const allZEqual = points.every( p => Math.abs( p.z - points[0].z ) < 1e-6 );
        const allXEqual = points.every( p => Math.abs( p.x - points[0].x ) < 1e-6 );
        
        return points.map( point => {
            let { x, y, z } = point;
            if( roofSide.includes( side ) ) {
                y = y > avgY ? y - zeroOffset : y;
                z = z > avgZ ? z - zeroOffset : z + zeroOffset;
                if( 'lower_roof_left' === side || 'lower_roof_right' === side ) {
                    x = x > 0 ? x - zeroOffset : x + zeroOffset;
                } else {
                    x = y <= avgY ? ( x > avgX ? x - zeroOffset : x + zeroOffset ) : x;
                }
            } else if( ( 'inner_left' === side && this.isCornerPorchLeft() ) || ( 'inner_back' === side && ( 'structure_ranch_cabin' === this._shedType || this.isAframeSingleGable() ) ) || ( 'left_inner_porch' === side && ( this.isMiddlePorch() || this.isDoubleGable() ) ) || ( 'inner_front' === side && this.isDeluxeCornerLeftPorch() ) ) {
                x += zeroOffset;
                z += zeroOffset;
            } else if( ( 'inner_front' === side && this.isCornerPorchLeft() ) || ( 'right_inner_porch' === side && ( this.isMiddlePorch() || this.isDoubleGable() ) ) ) {
                x += zeroOffset;
                z -= zeroOffset;
            } else if( ( 'inner_left' === side && ( 'structure_ranch_cabin' === this._shedType || this.isAframeSingleGable() ) ) ) {
                z = z < avgZ ? z - zeroOffset : z + zeroOffset;
                x += zeroOffset;
            } else if( 'front_inner_porch' === side && ( this.isMiddlePorch() || this.isDoubleGable() ) ) {
                z = z < avgZ ? z - zeroOffset : z + zeroOffset;
                x += zeroOffset;
            } else if( 'inner_right' === side && this.isDeluxeCornerLeftPorch() ) {
                z = z < avgZ ? z - zeroOffset : z + zeroOffset;
                x -= zeroOffset;
            } else if ( zSide.includes( side ) ) {
                x += sizePlusX.includes( side ) ? zeroOffset : -zeroOffset;
                z = ( z != minZ && z != maxZ ) ? z : ( z > avgZ ? z - zeroOffset : z + zeroOffset );
            } else {
                z += sizePlusZ.includes(side) ? zeroOffset : -zeroOffset;
                x = ( x != minX && x != maxX ) ? x  : ( x > avgX ? x - zeroOffset : x + zeroOffset );
                y = y == maxY ? y - zeroOffset : y;
            }

            if( allZEqual ) {
                z += z > 0 ? -0.04 : 0.04;
            }
            
            if( allXEqual ) {
                x += x > 0 ? -0.04 : 0.04;
            }
            
            return new Vector3( x, y, z );
        });
    }

    /**
     * Get Floor side Info
     * 
     * Prioritize to get floor info from network first.
     * If there is no floor side from network, using walls side data to auto generate floor side.
     * If walls data from hard code, request to network to get floor side first, if no floor side on network using walls data hard code to generate floor side.
     * 
     * @param {Array} walls Array of walls objects
     * @param {boolean} isDataFromNetwork check if data is from network of hard code
     * 
     * @returns {object} floor side info
     */
    getFloorSideInfo = ( walls, isDataFromNetwork ) => {
        let floorSide                   = null;
        let networkWalls                = this.formatWallInfoFromAPI( this._apiData['wall_raw'] );
        const networkWallsWithoutFloor  = networkWalls.filter( wall => 'floor' !== wall.side );
        const networkFloor              = networkWalls.filter( wall => 'floor' === wall.side );
        const activeFloorMaterial       = this.getActiveFloorMaterialData();

        if( 0 < networkFloor.length ) {
            floorSide = networkFloor[0];
        } else if( 0 < networkWallsWithoutFloor.length ) {
            floorSide = this.autoGenerateFloorFromWallsData( networkWallsWithoutFloor );
        }
        
        if( 0 < walls.length && ! isDataFromNetwork ) {
            if( 0 < networkFloor.length ) {
                floorSide = networkFloor[0];
            } else {
                floorSide = this.autoGenerateFloorFromWallsData( walls );
            }
        }

        if( ! floorSide && activeFloorMaterial?.floorType ) {
            this.createAlert( 'No data found to draw floor' );
        }

        if( floorSide ) floorSide.materialName = this._interiorFloorMatKey;

        return floorSide;
    } 

    /**
     * Auto generate Floor side from walls data
     * 
     * @param {Array} walls Array of wall object
     * 
     * @returns {object} floor side data
     */
    autoGenerateFloorFromWallsData = ( walls ) => {
        const floorBonesFromWalls = this.getFloorPointsDataFromWalls( walls );
        const floorBones          = [];

        floorBonesFromWalls.forEach( bone => {
            if( bone ) {
                const defaultFloorOffset = 0.01;
                const defaultOffsetX     = bone._x > 0 ? - defaultFloorOffset : defaultFloorOffset;
                const defaultOffsetZ     = bone._z > 0 ? - defaultFloorOffset : defaultFloorOffset;

                bone._x = bone._x + defaultOffsetX;
                bone._z = bone._z + defaultOffsetZ;

                floorBones.push( bone );
            }
        } );
        
        if( 0 === floorBones.length ) return null;

        const floorData = { 
            side        : 'floor',
            position    : 'b_b_l1',
            bones       : floorBones,
            name        : 'floor',
            oldName     : '',
            materialName: this._interiorFloorMatKey,
            height      : undefined
        };
        
        return floorData;
    }

    /**
     * Get Floor point data from walls data
     * 
     * @param {Array} walls Array of wall object
     * 
     * @returns {object} floor points
     */
    getFloorPointsDataFromWalls = ( walls = [] ) => {
        const dataPoint = [];
        for( const k in walls ) {
            const wall = walls[k];
            const { shape, position } = this.calculateWallInfo( wall );
            let points = [];
            for( let key in shape ) {
                if( 'diagonal_right' === wall.side || 'floor' === wall.side ) continue;
                const point = shape[key];
                if( point.z < 0.5 ) {
                    let temp: any = new Vector3( point.x, point.y, point.z );
                    if( - 1 !== zSide.indexOf( wall.side ) ) {
                        temp.z = point.x;
                        temp.x = position.x;
                    } else {
                        temp.z = position.z;
                    }
                    temp.y = 0;
                    points.push( temp );
                }
            }
            if( points.length > 0 ) {
                dataPoint.push( points );
            }
        }
        return this.connectPointsToDrawFloor( dataPoint, [] );
    }

     /**
     * Connnect points to draw floor
     * 
     * @param {array} dataPoints 
     * @param {array} points
     * 
     * @returns {object} points
     */
    connectPointsToDrawFloor = ( dataPoints, points = [] ) => {
        if( dataPoints.length === 0 ) {
            return points;
        }
    
        if( points.length === 0 ) {
            const [firstPoint] = dataPoints;
            return this.connectPointsToDrawFloor( dataPoints.slice( 1 ), [firstPoint[1], firstPoint[0]] );
        }
    
        if( dataPoints.length === 1 ) {
            return points;
        }
    
        const lastPoint      = points[points.length - 1];
        const nextPointIndex = dataPoints.findIndex( subArray => 
            subArray.some( point => point.equals( lastPoint ) )
        );
    
        if( nextPointIndex === -1 ) {
            return points;
        }
    
        const nextPoints          = dataPoints[nextPointIndex].filter( point => !point.equals( lastPoint ) );
        const remainingDataPoints = dataPoints.filter( ( _, index ) => index !== nextPointIndex );
    
        return this.connectPointsToDrawFloor( remainingDataPoints, [...points, ...nextPoints] );
    };

    /**
     * Get 3d point data from wall create by polygon
     */
    get3DPointsFromWall = ( wall ) => {
        const { shape, position } = this.calculateWallInfo( wall );
        let points = [];
        
        for( let key in shape ) {
            if( 'diagonal_right' === wall.side || 'floor' === wall.side ) continue;
            const point = shape[key];
            let temp: any = new Vector3( point.x, point.y, point.z );
            if( - 1 !== zSide.indexOf( wall.side ) ) {
                temp.z = point.x;
                temp.x = position.x;
            } else {
                temp.z = position.z;
            }
            temp.y = point.z;
            points.push( temp );
        }
        
        return points;
    }


    /**
     * Updates the walls array with exterior finish material data based on the side information provided.
     *
     * @param {Array} walls - Array of wall objects to be updated.
     * @param {Object} data - Data object containing 'side' information to match against the walls.
     * @return {Array} Updated walls array with Exterior Siding Upgrades material data.
    */
    getExteriorSidingUpgradesWallInfo = ( walls, data ) => {
        const { side } = data;

        if( ! side || helpers.isEmptyObject( side ) ) {
            return walls;
        }

        for( const wall of walls ) {
            if( helpers.hasKeyInArray( side, wall.side ) ) {
                wall.materialName = `exterior_siding_upgrades_${wall.name}_mat`;

                this._apiData['exterior_siding_upgrades_material'] = helpers.checkAndPushKeyToArray( this._apiData['exterior_siding_upgrades_material'], wall.materialName );
            }
        }

        return walls;
    }

    /**
     * Processes walls to add accent gable information.
     * 
     * This function processes a list of wall objects to determine and add accent gable details 
     * based on the provided gableAccent settings. If the accent gable is not active or is not 
     * set as default, it returns the original list of walls. Otherwise, it iterates over each 
     * wall and calculates new shapes and materials for walls on specific sides (front, back, porch).
     * 
     * @param {Array} walls - Array of wall objects to be processed.
     * @param {Object} gableAccent - Object containing accent gable settings.
     * @returns {Array} - Array of processed wall objects with accent gable information if applicable.
     */
    getAccentGableWallInfo = ( walls, gableAccent ) => {
        if( 'false' === gableAccent.is_active || 'false' === gableAccent.default ) {
            return walls;
        }
        
        const offset     = 0.2;
        const allWalls   = [];
        const wallHeight = this.getWallHeight();

        for( const wall of walls ) {
            if( -1 === [ 'front', 'back', 'porch' ].indexOf( wall.side ) ) {
                allWalls.push( wall );
                continue;
            }

            const { shape }     = this.calculateWallInfo( wall );
            const materialName  = `accent_gable_${wall.name}_mat`;
            const wallShape     = shape.filter( bone => wallHeight + offset >= bone._z );
            const gableShape    = shape.filter( bone => wallHeight - offset <= bone._z );

            if( Object.keys( gableShape ).length > 2 ) {
                const wallData = { 
                    ...wall, 
                    bones: gableShape,
                    materialName,
                    oldName: null,
                    name: `accent_gable_${wall.name}`,
                    trim: false
                };

                allWalls.push( wallData );
                allWalls.push( { ...wall, bones: wallShape } );

                this._apiData['accent_gable_material'] = helpers.checkAndPushKeyToArray( this._apiData['accent_gable_material'], materialName );
            } else {
                allWalls.push( wall );
            }
        }

        return allWalls;
    }

    /**
     * Get Wainscoting Info
     * 
     * @param {object} walls walls info
     * @param {object} wainscoting wainscoting setting data
     * 
     * @returns {object} walls with wainscoting wall
     */
    getWainscotingWallInfo = ( walls, wainscoting ) => {
        const wallWainsCoting   = [];
        const wainscotingData   = this.getWainscotingByCondition( wainscoting );
        const floorOffset       = 0.5;

        if( helpers.isEmptyObject( wainscotingData ) || 1 === this._wainscotingType ) {
            return walls;
        }

        const { height : wainscotingHeight } = wainscotingData;

        walls.forEach( wall => {

            if( 'floor' === wall.side || ( 3 === this._wainscotingType && -1 === this._wainscotingSide.indexOf( wall.side ) ) ) {
                return wallWainsCoting.push( { ...wall } );
            }

            const { shape }     = this.calculateWallInfo( wall );
            const floorBones    = helpers.objectFilter( shape, b => floorOffset < b._z );
            const materialName  = `wainscoting_${wall.name}_mat`;
            const oldZ          = [];
            
            Object.keys( shape ).forEach( key => {
                if( floorOffset >= shape[key].z ) {
                    oldZ.push( shape[key]._z );
                    shape[key].z = wainscotingHeight;
                }
            } );

            wallWainsCoting.push( { ...wall, bones: shape } );

            if( helpers.isEmptyObject( floorBones ) ) {
                return;
            }

            const newBone = [];
            Object.keys( floorBones ).forEach( ( key, i ) => {
                const position1 = new Vector3( floorBones[key].x, 0, oldZ[i] );
                const position2 = new Vector3( position1.x, 0, floorBones[key].z );

                if( newBone.length >= Object.keys( floorBones ).length ) {
                    newBone.push( position2, position1 );
                } else {
                    newBone.push( position1, position2 );
                }
            } );

            const wallData = { 
                ...wall, 
                bones: newBone,
                materialName,
                oldName: null,
                name: `wainscoting_${wall.name}`,
                trim: true
            };

            wallWainsCoting.push( wallData );

            if( ! this._apiData['wainscoting_material'].includes( materialName ) ) {
                this._apiData['wainscoting_material'].push( materialName );
            }
        } );

        return wallWainsCoting;
    }

    getShapePartialWall = ( shape, height ) => {
        let floorBones  = helpers.objectFilter( shape, b => b._z > 0.5 );
        if( helpers.isEmptyObject( floorBones ) ) {
            const haftWidth = this.getWidth() / 2;
            return [
                new Vector3( -haftWidth, 0, 0 ),
                new Vector3( -haftWidth, 0, height ),
                new Vector3( haftWidth, 0, height ),
                new Vector3( haftWidth, 0, 0 ),
            ]
        }
        const newBones = [];
        Object.keys( floorBones ).forEach( ( key, i ) => {
            const position1 = new Vector3( floorBones[key].x, 0, floorBones[key].z );
            const position2 = new Vector3( floorBones[key].x, 0, height );

            if( newBones.length >= Object.keys( floorBones ).length ) {
                newBones.push( position2, position1 );
            } else {
                newBones.push( position1, position2 );
            }
        } );

        return newBones;
    }

    /**
     * Get list Side Wainscoting
     */
    getListWainscotingSide = () => {
        const floorOffset   = 0.5;
        const walls         = this.getWallInfo();
        const sides         = [];

        walls.forEach( wall => {
            const { shape }     = this.calculateWallInfo( wall );
            const floorBones    = helpers.objectFilter( shape, b => floorOffset < b._z );

            if( ! helpers.isEmptyObject( floorBones ) && -1 === sides.indexOf( wall.side ) ) {
                sides.push( wall.side );
            }
        } );
        
        return sides;
    }

    /**
     * Remove Wainscoting Mesh
     */
    removeWainscotingMesh = () => {
        const meterials = this._apiData['wainscoting_material'];
        this._apiData[ 'wainscoting_wall_info' ] = [];

        meterials.forEach( key => {
            const meshName  = key.replace( '_mat', '' );
            const mesh      = this.getMeshByName( meshName );
            const meshClone = this.getMeshByName( `${meshName}_clone` );

            if( mesh ) mesh.dispose();
            if( meshClone ) meshClone.dispose();

        } );
    }

    /**
     * Set waincosting wall infor
     */
    setWaincostingWallInfor = ( walls ) => {

        if( ! walls || 0 === walls.length ) {
            this._apiData[ 'wainscoting_wall_info' ] = [];
        }

        this._apiData[ 'wainscoting_wall_info' ] = walls.filter( item => 0 === item.name.indexOf( 'wainscoting_' ) );
    }

    /**
     * Get waincosting wall infor
     */
    getWaincostingWallInfor = () => {

        if( ! this._apiData[ 'wainscoting_wall_info' ] ) return [];

        let waincostingWalls = this._apiData[ 'wainscoting_wall_info' ];
        for( let i = 0; i < waincostingWalls.length; i++ ) {
            waincostingWalls[i].bones = waincostingWalls[i].bones.map( bone => new Vector3( bone.x, bone.y, bone.z < 0.5 ? 0 : bone.z ) );
        };
        
        return waincostingWalls;
    }

    /**
     * Returns the main size of data that satisfies the conditions.
     * @param {Array} sizeData - An array containing size information.
     * @returns {number|boolean} - The main size or false if not found.
     */
    getMainSizeByCondition = sizeData => {
        if( ! Array.isArray( sizeData ) ) {
            return false;
        }

        const validSizes = sizeData.filter( d => {
            return helpers.isValidSize( d.width_operation, this.getWidth(), d.width_min, d.width_max ) && helpers.isValidSize( d.length_operation, this.getDepth(), d.length_min, d.length_max );
        } );

        if( 0 >= validSizes.length || '' == validSizes[0].main_size || 0 >= validSizes[0].main_size || validSizes[0].main_size / 12 >= this.getWidth() ) {
            return false;
        }

        return validSizes[0].main_size / 12;
    }

    /**
     * get Wainscoting Height
     * 
     * @param {object} wainscoting wainscoting setting data
     * 
     * @return {object}
     */
    getWainscotingByCondition = ( wainscoting ) : Readonly<{height?: float, name?: string, priceType?: string, price?: float}> => {
        if( ! Array.isArray( wainscoting ) ) {
            return {};
        }

        const sizeValid = wainscoting.filter( wainscot => {
            let isValidSidingMaterial = true;

            if( '' != wainscot.siding_material ) {
                const material = JSON.parse( window.atob( wainscot.siding_material ) );
                isValidSidingMaterial = this.isValidSidingMaterialCondition( material );
            }
            
            return isValidSidingMaterial && helpers.isValidSize( wainscot.operation, this._wallHeight, wainscot.condition_height_min, wainscot.condition_height_max );
        } );

        if( 0 >= sizeValid.length ) {
            return {};
        }
        
        const { wainscot_height_type: type, height_value: value, name, price_type, price_value, material } = sizeValid[0];
                
        if( '' == value || value <= 0 ) {
            return {};
        }

        if( 'percentage' === type ) {
            return { 
                name,
                priceType: price_type,
                price: Number( price_value ),
                height: helpers.toFixed3( value * this._wallHeight / 100 ),
            };
        }

        return {
            name,
            priceType: price_type,
            price: Number( price_value ),
            height: Number( value ),
        };
    }

    /**
     * Retrieves and decodes the selected Exterior Siding Upgrades data.
     *
     * This function selects the checked input for Exterior Siding Upgrades, extracts its 
     * 'exterior-finishes' data attribute (base64 encoded), decodes it, and parses 
     * it as a JSON object. If no data is available, it returns an empty object.
     *
     * @return {Object} Parsed Exterior Siding Upgrades data or an empty object.
     */
    getExteriorSidingUpgradesInfo = () => {
        const finishes = jQuery( 'input[name=ssb-configurator-upgrade-exterior_siding_upgrades]:checked' ).data( 'exterior-finishes' );

        return finishes ? JSON.parse ( window.atob( finishes ) ) : {};
    }

    /**
     * Get back points for deluxe porch
     * 
     * @returns {Array}
     */
    deluxePorchGetPoins = side => {
        const bones = this._bones;

        if( 'structure_aframe_deluxe_porch' === this._shedType ) {
            if( 'back' === side ) {
                return [ 'b_b_l1', 'b_b_l2', 'b_b_t', 'b_b_r2', 'b_b_r1' ];
            } else if( 'porch' === side ) {
                return [ 
                    new Vector3( bones['b_p_l2'].position.x, 0, bones['b_p_l2'].position.y - 0.2 ),
                    'b_p_l2', 'b_p_t', 'b_p_r2',
                    new Vector3( bones['b_p_r2'].position.x, 0, bones['b_p_r2'].position.y - 0.2 ),
                ]                
            }
        }

        if( 'structure_barn_deluxe_porch' === this._shedType ) {
            if( 'back' === side ) {
                return [ 'b_b_l1', 'b_b_l2', 'b_b_l4', 'b_b_t', 'b_b_r4', 'b_b_r2', 'b_b_r1' ];
            } else if( 'porch' === side ) {
                return [ 
                    new Vector3( bones['b_p_l2'].position.x, 0, bones['b_p_l2'].position.y - 0.2 ),
                    'b_p_l2', 'b_p_l5', 'b_p_t', 'b_p_r5', 'b_p_r2',
                    new Vector3( bones['b_p_r2'].position.x, 0, bones['b_p_r2'].position.y - 0.2 ),
                ] 
            }
        }

        return [];
    }
    

    createHoleWall3D = ( side, wallName, isInterior = false ) => {
        const wallNameClone = `${wallName}_clone`;
        const meshClone     = this.getMeshByName( wallNameClone );
        if( meshClone ) {
            meshClone.dispose();
        }

        const oldWall = this.getMeshByName( wallName );
        if( null === oldWall ) {
            return;
        }

        let intersectMeshes = this.getIntersectMeshes( side );
        if( ( 'front' === side || 'back' === side || 'left' === side || 'right' === side  ) && this.is2StoryLeanTo() ) {
            intersectMeshes = intersectMeshes.concat( this.getIntersectMeshes( `top_${side}` ) );
        }

        if( intersectMeshes.length <= 0 ) {
            return oldWall.setEnabled( true );
        }

        const mainMeshCSG = CSG.FromMesh( oldWall );
        const boudingOldWall = this.createBoundingBoxMesh( oldWall );
        const boudingOldWallCSG = CSG.FromMesh( boudingOldWall );
        for( let i = 0; i < intersectMeshes.length; i++ ) {
            if( isInterior ) {
                const insterset = boudingOldWallCSG.intersect( CSG.FromMesh( intersectMeshes[i] ) );
                mainMeshCSG.subtractInPlace( insterset );
            } else {
                mainMeshCSG.subtractInPlace( CSG.FromMesh( intersectMeshes[i] ) );
            }
        }

        boudingOldWall.dispose();

        const wall = mainMeshCSG.toMesh( wallNameClone, oldWall.material, this._scene, false );
        if( ! helpers.hasKeyInArray( this._meshBlurForInterior, wallNameClone )  && ! isInterior ) {
            this._meshBlurForInterior.push( wallNameClone );
        }
        
        const childMeshes = oldWall.getChildMeshes();
        for( let i = 0; i < childMeshes.length; i++ ) {
            const childMesh     = childMeshes[i] as Mesh;
            const childMeshCSG  = CSG.FromMesh( childMesh );
            if( isInterior ) {
                const boudingMesh = this.createBoundingBoxMesh( childMesh );
                const boudingMeshCSG = CSG.FromMesh( boudingMesh );
                for( let i = 0; i < intersectMeshes.length; i++ ) {
                    const insterset = boudingMeshCSG.intersect( CSG.FromMesh( intersectMeshes[i] ) );
                    childMeshCSG.subtractInPlace( insterset );
                }
                boudingOldWall.dispose();
            } else {
                for( let i = 0; i < intersectMeshes.length; i++ ) {
                    childMeshCSG.subtractInPlace( CSG.FromMesh( intersectMeshes[i] ) );
                }
            }

            const childWall = childMeshCSG.toMesh( childMesh.name, childMesh.material, this._scene, false );
            
            childWall.isPickable = false;
            childWall.convertToFlatShadedMesh();
            childWall.setParent( wall );
            childWall.position.copyFrom( childMesh.position );
            childWall.rotation.copyFrom( childMesh.rotation );
        }

        wall.convertToFlatShadedMesh();
        wall.isPickable = false;
        wall.setParent( oldWall.parent );
        wall.visibility = oldWall.visibility;
        wall.checkCollisions = true;
        oldWall.setEnabled( false );
    }

    // create boudingbox follow mesh inner
    createBoundingBoxMesh( mesh ) {
        const oldMesh = this.getMeshByName( 'boundingBoxMesh' );
        // if( oldMesh ) {
        //     oldMesh.dispose();
        // }
        // Get the bounding box info
        let boundingInfo = mesh.getBoundingInfo();
        let min = boundingInfo.boundingBox.minimum;
        let max = boundingInfo.boundingBox.maximum;
    
        // Calculate size from min & max
        let sizeX = max.x - min.x;
        let sizeY = max.y - min.y;
        let sizeZ = max.z - min.z;
    
        // Create the box
        let boundingBoxMesh = MeshBuilder.CreateBox( "boundingBoxMesh", {
            width: sizeX,
            height: sizeY,
            depth: sizeZ,
        }, this._scene);
    
        // Position the box at the center of the original mesh
        let center = boundingInfo.boundingBox.center;
        boundingBoxMesh.position.copyFrom(center);
    
        return boundingBoxMesh;
    }

    /**
     * Check if the current shed is a tiny home or not
     * 
     * @returns {boolean} Return true if the current shed is a tiny home, false otherwise
     */
    isTinyHome = (): boolean => {
        return -1 !== this._shedTypeVersion.indexOf( 'tinyhome' );
    }

    /**
     * Check if the current shed is a tiny home with a front porch
     * 
     * @returns {boolean} Return true if the current shed is a tiny home with a front porch, false otherwise
     */
    isTinyHomeFrontPorch = (): boolean => {
        return 'tinyhome_standard_cottage_front_porch' === this._shedTypeVersion;
    }

    /**
     * Check if the current shed is a tiny home with a cottage style
     * 
     * @returns {boolean} Return true if the current shed is a tiny home with a cottage style, false otherwise
     */
    isTinyHomeCottage = (): boolean => {
        return 'tinyhome_standard_cottage' === this._shedTypeVersion;
    }
    
    /**
     * Check if the current shed is a tiny home with a lean to
     * 
     * @returns {boolean} Return true if the current shed is a tiny home with a lean to, false otherwise
     */
    isTinyHomeLeanTo = (): boolean => {
        return 'tinyhome_lean_to' === this._shedTypeVersion;
    }

    /**
     * Check if the current shed needs to draw the inner wall or not
     * 
     * @returns {boolean} Return true if the current shed needs to draw the inner wall, false otherwise
     */
    isDrawInner = (): boolean => {
        return this.isHorseRunIn() || this._isEnablePlywoodLayer;
    }

    /**
     * Hole the inner wall
     * 
     * @param {string} originName The name of the mesh that you want to hole
     * @param {string} customName The name of the mesh that you want to create
     */
    holeInnerWall = ( originName, customName ) => {
        const sides     = [ 'front', 'back', 'left', 'right' ];
        const meshCut   = this.getMeshByName( customName );
        if( meshCut ) meshCut.dispose();
        const mesh      = this.getMeshByName( originName );
        if( ! mesh ) {
            return;
        }

        mesh.setEnabled( true );
        let innerCSG    = CSG.FromMesh( mesh );
        sides.forEach( side => {
            const intersectMesh = this.getIntersectMeshes( side );
            if( intersectMesh.length > 0 ) {
                for( let i = 0; i < intersectMesh.length; i++ ) {
                    innerCSG = innerCSG.subtract( CSG.FromMesh( intersectMesh[i] ) );
                }
            }
        } );

        const newMesh       = innerCSG.toMesh( customName, mesh.material, this._scene, true );
        newMesh.parent      = mesh.parent;
        mesh.isPickable     = false;
        newMesh.isPickable  = false;
        newMesh.checkCollisions = true;

        mesh.dispose();
        this._scene.defaultMaterial.alpha = 0;
    }

    /**
     * Remove wall finish
     *
     * This function removes the wall finish mesh from the scene.
     */
    removeWallFinish = () => {
        const wallFinish = this.getMeshByName( wallFinishName.cut );
        if( wallFinish ) wallFinish.dispose();
    }

    /**
     * Create wall finish
     */
    createWallFinish = () => {
        if( ! this.isDrawWallFinish() ) return;
        this.drawWallFinish();
        this.holeInnerWall( wallFinishName.origin, wallFinishName.cut );
    }

    /**
     * hole wall inner
     */
    createWallInner = () => {
        if( ! this.isDrawInner() ) return;
        this.drawWallInner();
        this.holeInnerWall( innerObjectName.origin, innerObjectName.cut );
    }

    /**
     * calculate uvs for wall
     * @param points
     * @returns 
     */
    computeUVs = ( points ) => {
        if ( points.length < 3 ) return [];
    
        // **1. Compute the normal vector**
        const vec1 = points[1].subtract( points[0] );
        const vec2 = points[2].subtract( points[0] );
        const normal = Vector3.Cross( vec1, vec2 ).normalize();
    
        // **2. Determine the UV projection axis**
        let axisU, axisV;
        if( Math.abs( normal.x ) > Math.abs( normal.y ) && Math.abs( normal.x ) > Math.abs( normal.z ) ) {
            //If the plane is nearly perpendicular to the X-axis → Use YZ but SWAP U & V
            axisU = new Vector3( 0, 0, 1 );
            axisV = new Vector3( 0, 1, 0 );
        } else if ( Math.abs( normal.y ) > Math.abs( normal.z ) ) {
            //If the plane is nearly perpendicular to the Y-axis → Use XZ but SWAP U & V
            axisU = new Vector3( 0, 0, 1 );
            axisV = new Vector3( 1, 0, 0 );
        } else {
            //If the plane is nearly perpendicular to the Z-axis → Use XY (unchanged)
            axisU = new Vector3( 1, 0, 0 );
            axisV = new Vector3( 0, 1, 0 );
        }

        // **3. Project points onto the UV axis**
        const minU = Math.min( ...points.map( p => Vector3.Dot( p, axisU ) ) );
        const maxU = Math.max( ...points.map( p => Vector3.Dot( p, axisU ) ) );
        const minV = Math.min( ...points.map( p => Vector3.Dot( p, axisV ) ) );
        const maxV = Math.max( ...points.map( p => Vector3.Dot( p, axisV ) ) );
    
        // **4. Normalize UV coordinates from 0 to 1**
        const uvs = points.flatMap( p => [
            ( Vector3.Dot( p, axisU ) - minU ) / ( maxU - minU ), 
            ( Vector3.Dot( p, axisV ) - minV ) / ( maxV - minV )
        ]);
    
        return uvs;
    }

    /**
     * Performs ear-clipping triangulation on a simple polygon.
     * 
     * This algorithm iteratively removes "ear" triangles (triangles that do not contain 
     * any other points within them) until the polygon is completely triangulated.
     *
     * @param {Array} points - An array of 2D points (Vector2) representing the polygon.
     * @returns {Array} - An array of indices representing the triangulated polygon.
     */
    earClippingTriangulation = ( points ) => {
        let indices = [];
        let remaining = points.map( ( _, i ) => i );
        let isCW = ! this.isClockwiseAlongXY( points );
        while( remaining.length > 3 ) {
            let found = false;
            for(let i = 0; i < remaining.length; i++) {
                let a = remaining[i];
                let b = remaining[ ( i + 1 ) % remaining.length];
                let c = remaining[ ( i + 2 ) % remaining.length];
                if( this.isEar( points, a, b, c, remaining, isCW ) ) {
                    indices.push( a, b, c );
                    remaining.splice((i + 1) % remaining.length, 1); // Xóa điểm giữa (b)
                    found = true;
                    break;
                }
            }
            if( ! found ) throw new Error( "Triangulation failed - Mesh Error." );
        }

        indices.push( remaining[0], remaining[1], remaining[2] );
        return indices;
    }

    /**
     * Generates the index arrays for an extruded 3D shape using polygon triangulation.
     *
     * @param {Array} points - An array of Vector3 points representing the base polygon in 3D space.
     * @returns {Object} - An object containing indices for the front face, back face, and side faces.
     */
    generateExtrudedIndices = ( points ) => {
        let points2D = this.convertPointTo2D( points );
        const indicesFront = this.earClippingTriangulation( points2D );
        const n = points2D.length;
        const indicesBack = indicesFront.map( ( i, idx ) => indicesFront[indicesFront.length - 1 - idx] + n );
        let indicesSide = [];
        for (let i = 0; i < n; i++) {
            let next = ( i + 1 ) % n;
            indicesSide.push(
                i, next, i + n, 
                next, next + n, i + n
            );
        }

        return { indicesFront, indicesSide, indicesBack };
    }

    /**
     * Checks if a given triangle formed by three points is an "ear" in the context of 
     * polygon triangulation (meaning it can be safely removed without affecting the remaining shape).
     *
     * @param {Array} points - The array of points representing the polygon.
     * @param {number} a - The index of the first vertex of the triangle.
     * @param {number} b - The index of the second vertex of the triangle.
     * @param {number} c - The index of the third vertex of the triangle.
     * @param {Array} remaining - The indices of the remaining points in the polygon.
     * @param {boolean} isCW - Boolean indicating if the polygon is oriented clockwise.
     * @returns {boolean} - Returns true if the triangle (a, b, c) is an ear, otherwise false.
     */
    isEar = ( points, a, b, c, remaining, isCW ) => {
        if( ! this.isConvex( points[a], points[b], points[c], isCW ) ) {
            return false; 
        }

        let triangle:any = [points[a], points[b], points[c]];
        for( let i of remaining ) {
            if(i !== a && i !== b && i !== c && this.pointInTriangle( points[i], triangle ) ) {
                return false;
            }
        }
        return true;
    }

    /**
     * Determines if a given point is inside a triangle using the area method.
     *
     * @param {Object} p - The point to check (with properties x and y).
     * @param {Array} [a, b, c] - The three vertices of the triangle.
     * @returns {boolean} - Returns true if the point is inside the triangle, otherwise false.
     */
    pointInTriangle = ( p, [a, b, c] ) => {
        let area = ( x1, y1, x2, y2, x3, y3 ) =>
            Math.abs( ( x1 * ( y2 - y3 ) + x2 * ( y3 - y1 ) + x3 * ( y1 - y2 ) ) / 2 );
        
        let total = area( a.x, a.y, b.x, b.y, c.x, c.y );
        let sum = area( p.x, p.y, b.x, b.y, c.x, c.y ) +
                area( a.x, a.y, p.x, p.y, c.x, c.y ) +
                area( a.x, a.y, b.x, b.y, p.x, p.y );

        return Math.abs( total - sum ) < 1e-10;
    }

    /**
     * Projects a set of 3D points onto a 2D plane.
     * 
     * This function determines an appropriate 2D coordinate system for the points based on their orientation 
     * in 3D space and then projects them onto that plane.
     *
     * @param {Array} points - An array of Vector3 objects representing the 3D points.
     * @returns {Array} - An array of Vector2 objects representing the projected 2D points.
     */
    convertPointTo2D = ( points ) => {
        const allZEqual = points.every( p => Math.abs( p.z - points[0].z ) < 1e-6 );
        const allXEqual = points.every( p => Math.abs( p.x - points[0].x ) < 1e-6 );

        let axisX, axisY;

        if( allZEqual ) {
            axisX = new Vector3( 1, 0, 0 );
            axisY = new Vector3( 0, 1, 0 );
        } else if( allXEqual ) {
            axisX = new Vector3( 0, 1, 0 );
            axisY = new Vector3( 0, 0, 1 );
        } else {
            let v1 = points[1].subtract( points[0] );
            let v2 = points[2].subtract( points[0] );
            let normal = Vector3.Cross( v1, v2 ).normalize();

            axisX = v1.normalize();
            axisY = Vector3.Cross( normal, axisX ).normalize();
        }

        return points.map( p => new Vector2(
            Vector3.Dot( p, axisX ), 
            Vector3.Dot( p, axisY )
        ));
    }

    /**
     * Determines whether the turn formed by three points (a, b, c) is convex.
     *
     * @param {Object} a - The first point (with properties x and y).
     * @param {Object} b - The second point (with properties x and y).
     * @param {Object} c - The third point (with properties x and y).
     * @param {boolean} isCW - A flag indicating whether the polygon is oriented clockwise.
     * @returns {boolean} - Returns true if the turn is convex, false otherwise.
     */
    isConvex = ( a, b, c, isCW ) => {
        let cross = ( b.x - a.x ) * ( c.y - a.y ) - ( b.y - a.y ) * ( c.x - a.x );
        return isCW ? cross > 0 : cross < 0;
    }

    /**
    * draws an interior wall mesh with the specified properties.
    * @param {Array<Vector3>} points - An array of Vector3 points defining the shape of the wall.
    * @param {string} name - The name of the new wall mesh.
    * @param {string} defaultName - The name of the default mesh to be replaced (if exists).
    * @param {string} materialName - The name of the material to be applied to the wall.
    * @param {string} side - The side of the wall where the material should be applied.
    * @param {string|Mesh} parentMeshName - The name of the parent mesh or the mesh object itself.
     */
    drawInteriorWall = async ( points, name, defaultName, materialName, side, parentMeshName, isRevertFace ) => {
        const _this = this;
        const defaultMesh = this.getMeshByName( defaultName );
        if( defaultMesh ) {
            defaultMesh.dispose();
        }

        // check and remove wall clone
        const wallNameClone = `${name}_clone`;
        const meshClone = this.getMeshByName( wallNameClone );
        if( meshClone ) {
            meshClone.dispose();
        }

        let parentMesh = 'string' === typeof parentMeshName ? this.getMeshByName( parentMeshName ) : parentMeshName;

        if( ! parentMesh ) {
            parentMesh = this.getMeshByName( 'body_obj' );
        }

        // check and remove old wall
        const oldMesh = this.getMeshByName( name );
        if( oldMesh ) {
            oldMesh.dispose();
        }
        const thickness = 0.0001; // define thickness mesh

        // Compute the cross product of vec1 and vec2 to get the normal vector (perpendicular to both)
        // Then, normalize the result to ensure it has a length of 1
        const vec1 = points[1].subtract( points[0] );
        const vec2 = points[2].subtract( points[0] );
        const normal = Vector3.Cross( vec1, vec2 ).normalize();

        // determine the vertices of the face
        const frontVertices = points.flatMap( p => [ p.x, p.y, p.z ] );
        const backPoints = points.map( p => p.add( normal.scale( thickness ) ) );
        const backVertices = backPoints.flatMap(p => [ p.x, p.y, p.z ]);
        const finalVertices = [...frontVertices, ...backVertices];
        const { indicesFront, indicesSide, indicesBack } = this.generateExtrudedIndices( points );
        const uvs = this.computeUVs( points );
        const wall = new Mesh( name, this._scene);
        const vertexData = new VertexData();
        vertexData.positions = finalVertices;
        const indicesShow = isRevertFace ? indicesBack : indicesFront;
        vertexData.indices = [...indicesShow, ...indicesSide ];
        vertexData.uvs = [...uvs, ...uvs];

        // Compute normals for the vertices
        VertexData.ComputeNormals(vertexData.positions, indicesShow, vertexData.normals = []);

        vertexData.applyToMesh( wall );

        wall.isPickable = false;
        wall.setParent( parentMesh );
        if( -1 !== materialName.indexOf( '_roof' ) ) {
            if( ! this._apiData['interior_ceiling_material'].includes( materialName ) ) {
                this._apiData['interior_ceiling_material'].push( materialName );
            }
            if( ! this._apiData['interior_ceiling_meshes'].includes( name ) ) {
                this._apiData['interior_ceiling_meshes'].push( name );
            }
        } else {
            if( ! this._apiData['interior_wall_material'].includes( materialName ) ) {
                this._apiData['interior_wall_material'].push( materialName );
            }
            if( ! this._apiData['interior_'].includes( name ) ) {
                this._apiData['interior_'].push( name );
            }
        }
       
        let wallHeight = wall.geometry.extend.maximum.y - wall.geometry.extend.minimum.y;
        const wallWidth = Math.max( ( wall.geometry.extend.maximum.x - wall.geometry.extend.minimum.x ), ( wall.geometry.extend.maximum.z - wall.geometry.extend.minimum.z ) )
        if( materialName.indexOf('_roof') !== -1 ) {
            wallHeight = this.getHeightRoofMesh( points );
        }

        if( ! this._apiData['meshes'].includes( name ) ) {
            this._apiData['meshes'].push( name );
        } 

        this._apiData['meshesData'][ `${materialName}` ] = { wallHeight, wallWidth };

        await this.applyMaterial( { wall, materialName, wallHeight, wallWidth, wallSide: side } );
    }

    getHeightRoofMesh( points ) {
        let distances = 0;
        for(let i = 0; i < points.length; i++) {
            for(let j = i + 1; j < points.length; j++) {
                if (points[i].z === points[j].z && points[i].x !== points[j].x) {
                    return Vector3.Distance( points[i], points[j] )
                }
            }
        }
    
        return distances;
    }

    /**
     * Draw interial wall, ceiling
     */
    drawInteriorlWallCeiling = async () => {
        const _this = this;
        let walls = _this.getInteriorWallInfo();
        if( 0 == walls.length ) return;
        const innerOld = this.getMeshByName( 'inner_obj' );
        if( innerOld ) {
            innerOld.visibility = 0;
        }

        walls.forEach( async ( wall ) => {
            const { side, name, oldName, bones, materialName, parentMesh, isRevertFace } = wall;
            await _this.drawInteriorWall( bones, name, oldName, materialName, side, parentMesh, isRevertFace );
            _this.createHoleWall3D( side, name, true );
        } );
    }

    /**
     * Get floor material data of given DOM
     * 
     * @param {Object} element DOM element of floor material
     * 
     * @returns object of floor material data
     */
    getFloorMaterialData = ( element ) => {
        const floorType         = element.attr( 'data-floor-type' );
        const diffuseTexture    = element.attr( 'data-texture-diffuse' );
        const bumpTexture       = element.attr( 'data-texture-bump' );
        const specularTexture   = element.attr( 'data-texture-specular' );
        const uScale            = Number( element.attr( 'data-uscale' ) );
        const vScale            = Number( element.attr( 'data-vscale' ) );
        const specularPower     = isNaN( Number( element.attr( 'data-specular-power' ) ) ) ? -1 : Number( element.attr( 'data-specular-power' ) );
        
        return { floorType, diffuseTexture, bumpTexture, specularTexture, uScale, vScale, specularPower };
    }

    /**
     * Get active DOM of floor material
     * 
     * @returns object of floor material data from active DOM
     */
    getActiveFloorMaterialData = () => {
        let element = jQuery( '#floor-material-option li.active' );

        return this.getFloorMaterialData( element );
    }

    /**
     * Check if wall finish material should be loaded
     * 
     * @returns true if wall finish material should be loaded, false otherwise
     */
    isLoaddWallFinishMat = (): boolean => {
        return this.isTinyHome() && jQuery( '[name="ssb-group-wall_ceiling_finishing"]' ).length > 0;
    }

    /**
     * Check if wall finish should be drawn
     * 
     * @returns true if wall finish should be drawn, false otherwise
     */
    isDrawWallFinish = (): boolean => {
        return this.isTinyHome() && 'enable' === jQuery( '[name="ssb-group-wall_ceiling_finishing"]:checked' ).val();
    }

    /**
     * Load wall finish material
     */
    loadWallFinishMat = () => {
        if( ! this.isLoaddWallFinishMat() ) return;

        const multiMaterial     = new MultiMaterial( 'material_inner', this._scene );
        const material1         = new StandardMaterial( wallFinish.mat1, this._scene );
        material1.diffuseColor  = Color3.FromHexString( '#D2D2D2' );
        material1.specularColor = Color3.FromHexString( '#646464' );
        material1.specularPower = 4;
        const material2         = material1.clone( wallFinish.mat2 );
        const material3         = material1.clone( wallFinish.mat3 );
        const material4         = material1.clone( wallFinish.mat4 );
        material2.specularPower = 3;
        material3.specularPower = 2;
        material4.specularPower = 1;

        multiMaterial.subMaterials.push( material1 );
        multiMaterial.subMaterials.push( material2 );
        multiMaterial.subMaterials.push( material3 );
        
        if( this.isTinyHomeCottage() || this.isTinyHomeFrontPorch() ) {
            multiMaterial.subMaterials.push( material4 );
        }

        this._initialUVs[wallFinish.mat1]   = material1;
        this._initialUVs[wallFinish.mat2]   = material2;
        this._initialUVs[wallFinish.mat3]   = material3;
        this._initialUVs[wallFinish.mat4]   = material4;
        this._wallFinishMat                 = multiMaterial;
        this._nullDiffuseTextures           = [ wallFinish.mat1, wallFinish.mat2, wallFinish.mat3, wallFinish.mat4 ];
    }

    /**
     * Define material inner obj
     * Get source from material inner [siding_inner_mat]
    */
    loadMaterialInner = () => {
        if( ! this.isDrawInner() ) return;
        const sidingInnerMat    = this._scene.getMaterialByName( 'siding_inner_mat' ) as StandardMaterial;
        if( ! sidingInnerMat ) return;
        const multiMaterial     = new MultiMaterial( 'material_inner', this._scene );
        const material1         = new StandardMaterial( 'in1', this._scene );
        
        material1.diffuseTexture    = sidingInnerMat.diffuseTexture;
        material1.bumpTexture       = sidingInnerMat.bumpTexture;
        material1.specularTexture   = sidingInnerMat.specularTexture;

        const material2 = material1.clone( 'in2' );
        const material3 = material1.clone( 'in3' );
        const material4 = material1.clone( 'in4' );
        const material5 = material1.clone( 'in5' );

        material2.diffuseColor  = Color3.FromHexString( '#f5f5f5' );
        material3.diffuseColor  = Color3.FromHexString( '#aaaaaa' );
        material4.diffuseColor  = Color3.FromHexString( '#878787' );
        material5.diffuseColor  = Color3.FromHexString( '#5e5e5e' );

        multiMaterial.subMaterials.push( material1 );
        multiMaterial.subMaterials.push( material2 );
        multiMaterial.subMaterials.push( material3 );
        
        if( this.isHorseRunIn() || this._isEnablePlywoodLayer ) {
            multiMaterial.subMaterials.push( material4 );
        }
        
        if( 'saltbox' === this._roofShape ) {
            multiMaterial.subMaterials.push( material5 );
        }

        this._initialUVs['in1'] = material1;
        this._initialUVs['in2'] = material2;
        this._initialUVs['in3'] = material3;
        this._initialUVs['in4'] = material4;
        this._initialUVs['in5'] = material5;

        this._innerMaterial = multiMaterial;
    }

    /** 
     * Check is Animal Horse Run In
     */
    isHorseRunIn = () => {
        return 'structure_shopstyle_run-in' === this._shedTypeVersion || 'structure_single_slope_run-in' === this._shedTypeVersion || 'structure_saltbox_run-in'  === this._shedTypeVersion;
    }
    
    /**
     * Get vertex to draw inner salt box
     */
    getVertexInnerSaltBox = () => {
        const _this     = this;
        const vertex    = [];
        const keys      = [ 
            'b_f_r1', 
            'b_b_r1',
            'b_b_r2', 
            'b_b_t',
            'b_b_l4',
            'b_b_l2', 
            'b_b_l1',
            'b_f_l1',
            'b_f_l2',
            'b_f_l4',
            'b_f_t',
            'b_f_r2', 
        ];
        keys.forEach( key => { 
            if( "b_b_t" === key || "b_f_t" === key ) {
                vertex.push( [ _this._bones[key].position.x, _this._bones[key].position.y + 0.107, _this._bones[key].position.z] );
            } else if( key == "b_b_l4" ) {
                vertex.push( [_this._bones["b_b_l2"].position.x - 1.33, _this._bones["b_b_l2"].position.y + 0.04, _this._bones["b_b_l2"].position.z] );
            } else if( key == "b_f_l4") {
                vertex.push( [_this._bones["b_f_l2"].position.x - 1.33, _this._bones["b_f_l2"].position.y + 0.04, _this._bones["b_f_l2"].position.z] );
            } else {
                vertex.push( _this.calculatePositionInner( this._bones[key].position ).asArray() );
            }
        } );

        return vertex;
    }

    calculatePositionInner = ( vector, zeroNum = 0 ) => {
        const x = vector.x;
        const y = vector.y;
        const z = vector.z;
        let newX = x < 0 ? ( x + 0.04 + zeroNum ) : ( x - 0.04 - zeroNum);
        let newY = y > 0 ? ( y - 0.04 + zeroNum ) : ( y + 0.04 - zeroNum );
        let newZ = z < 0 ? ( z + 0.04 + zeroNum) : ( z - 0.04 - zeroNum );
        return new Vector3( newX, newY, newZ );
    }

    /**
     * Draw wall finish by code
     * 
     * @return {Mesh} mesh of wall finish
     */
    drawWallFinish = () => {
        const oldMesh = this.getMeshByName( wallFinishName.origin );
        if( oldMesh ) oldMesh.dispose();

        const studOffset    = 0.045;
        const offsetDepth   = this._frameDepth + studOffset;
        const offsetWidth   = this._frameWidth + studOffset;
        let vertex          = [];
        let keys            = [];
        let face            = [];
        
        if( this.isTinyHomeFrontPorch() ) {
            keys = [ 
                'b_f_r1',
                'b_b_r1',
                'b_b_r2', 
                'b_b_t',
                'b_b_l2',
                'b_b_l1', 
                'b_f_l1',
                'b_f_l2',
                'b_f_r2',
                'b_p_r2',
                'b_p_l2',
                'b_p_t',
            ];
            face = [ 
                [1,2,3,4,5],
                [0,6,7,8],
                [8,2,1,0],
                [7,6,5,4],
                [7,10,9,8],
                [10,11,9],
                [9,11,3,2],
                [11,10,4,3],
            ];

            const tanRoof       = helpers.toFixed3( Number( helpers.getTanFromDegrees( this.getRoofDegree() ) ) );
            const offsetY       = helpers.toFixed3( tanRoof * offsetDepth );
            const frontBones    = [ 'b_f_l1', 'b_f_l2', 'b_f_r1', 'b_f_r2' ];
            const porchBones    = [ 'b_p_r2', 'b_p_l2', 'b_p_t' ];
            const leftBones     = [ 'b_f_l1', 'b_f_l2', 'b_b_l1', 'b_b_l2', 'b_p_l2' ];
            const rightBones    = [ 'b_b_r1', 'b_b_r2', 'b_f_r1', 'b_f_r2', 'b_p_r2' ];
            const keyOffsetY    = [ 'b_f_l2', 'b_f_r2', 'b_b_l2', 'b_b_r2', 'b_p_r2', 'b_p_l2' ];

            keys.forEach( key => {
                if( this._bones[key] ) {
                    const point = this._bones[key].position.asArray();
                    point[0]    = helpers.hasKeyInArray( leftBones, key ) ? point[0] + offsetDepth : helpers.hasKeyInArray( rightBones, key ) ? point[0] - offsetDepth : point[0];
                    point[1]    = helpers.hasKeyInArray( keyOffsetY, key ) ? point[1] + offsetY : point[1];
                    point[2]    = helpers.hasKeyInArray( frontBones, key ) ? point[2] - offsetDepth : helpers.hasKeyInArray( porchBones, key ) ? point[2] - offsetWidth : point[2] + offsetDepth;
                    
                    vertex.push( point );
                }
            } );
        } else if( this.isTinyHomeCottage() ) {
            keys = [ 
                'b_f_r1', 
                'b_b_r1',
                'b_b_r2', 
                'b_b_t',
                'b_b_l2', 
                'b_b_l1',
                'b_f_l1',
                'b_f_l2',
                'b_f_t',
                'b_f_r2', 
            ];

            face = [ 
                [1,2,3,4,5],
                [0,6,7,8,9],
                [9,2,1,0],
                [7,6,5,4],
                [3,2,9,8],
                [8,7,4,3],
            ];

            const tanRoof       = helpers.toFixed3( Number( helpers.getTanFromDegrees( this.getRoofDegree() ) ) );
            const offsetY       = helpers.toFixed3( tanRoof * offsetDepth );
            const frontBones    = [ 'b_f_l1', 'b_f_l2', 'b_f_r1', 'b_f_r2', 'b_f_t' ];
            const leftBones     = [ 'b_f_l1', 'b_f_l2', 'b_b_l1', 'b_b_l2' ];
            const rightBones    = [ 'b_b_r1', 'b_b_r2', 'b_f_r1', 'b_f_r2' ];
            const keyOffsetY    = [ 'b_f_l2', 'b_f_r2', 'b_b_l2', 'b_b_r2' ];
            keys.forEach( key => {
                if( this._bones[key] ) {
                    const point = this._bones[key].position.asArray();
                    point[0]    = helpers.hasKeyInArray( leftBones, key ) ? point[0] + offsetDepth : helpers.hasKeyInArray( rightBones, key ) ? point[0] - offsetDepth : point[0];
                    point[1]    = helpers.hasKeyInArray( keyOffsetY, key ) ? point[1] + offsetY : point[1];
                    point[2]    = helpers.hasKeyInArray( frontBones, key ) ? point[2] - offsetDepth : point[2] + offsetDepth;
                    
                    vertex.push( point );
                }
            } );
        } else if( this.isTinyHomeLeanTo() ) {
            keys = [ 
                'b_f_r1', 
                'b_b_r1',
                'b_b_r2', 
                'b_b_t',
                'b_b_l1', 
                'b_f_l1',
                'b_f_t',
                'b_f_r2', 
            ];

            face = [ 
                [1,2,3,4],
                [0,5,6,7],
                [7,2,1,0],
                [6,5,4,3],
                [7,6,3,2],
            ];

            const tanRoof       = helpers.toFixed3( Number( helpers.getTanFromDegrees( this.getRoofDegree() ) ) );
            const offsetY       = helpers.toFixed3( tanRoof * offsetDepth );
            const frontBones    = [ 'b_f_l1', 'b_f_r1', 'b_f_r2', 'b_f_t' ];
            const leftBones     = [ 'b_b_l1', 'b_f_l1', 'b_f_t', 'b_b_t' ];
            keys.forEach( key => {
                if( this._bones[key] ) {
                    const point = this._bones[key].position.asArray();
                    point[0]    = helpers.hasKeyInArray( leftBones, key ) ? point[0] + offsetDepth : point[0] - offsetDepth;
                    point[2]    = helpers.hasKeyInArray( frontBones, key ) ? point[2] - offsetDepth : point[2] + offsetDepth;

                    if( 'b_f_r2' === key || 'b_b_r2' === key ) {
                        point[1] += offsetY;
                    } else if( 'b_f_t' === key || 'b_b_t' === key ) {
                        point[1] -= offsetY;
                    }
                    
                    vertex.push( point );
                }
            } );
        }

        if( vertex.length === 0 || face.length === 0 ) {
            return;
        }

        let wallFinish      = MeshBuilder.CreatePolyhedron( wallFinishName.origin, { custom: { vertex, face }, updatable: true }, this._scene );
        wallFinish.material = this._wallFinishMat;
        wallFinish.parent   = this.getMeshByName( 'body_obj' );
        const verticesCount = wallFinish.getTotalVertices();
        if( this.isTinyHomeLeanTo() ) { 
            new SubMesh( 1, 0, verticesCount, 0, 6, wallFinish ); // back
            new SubMesh( 1, 0, verticesCount, 6, 6, wallFinish ); // front
            new SubMesh( 2, 0, verticesCount, 12, 6, wallFinish ); // right
            new SubMesh( 2, 0, verticesCount, 18, 6, wallFinish ); // left
            new SubMesh( 3, 0, verticesCount, 24, 6, wallFinish ); // roof
        } else if( this.isTinyHomeCottage() ) {
            new SubMesh( 1, 0, verticesCount, 0, 8, wallFinish ); // back
            new SubMesh( 1, 0, verticesCount, 9, 9, wallFinish ); // front
            new SubMesh( 2, 0, verticesCount, 18, 6, wallFinish ); // right
            new SubMesh( 2, 0, verticesCount, 24, 6, wallFinish ); // left
            new SubMesh( 3, 0, verticesCount, 30, 6, wallFinish ); // roof left
            new SubMesh( 4, 0, verticesCount, 36, 6, wallFinish ); // roof right
        } else if( this.isTinyHomeFrontPorch() ) {
            new SubMesh( 1, 0, verticesCount, 0, 9, wallFinish ); // back
            new SubMesh( 1, 0, verticesCount, 9, 6, wallFinish ); // front
            new SubMesh( 2, 0, verticesCount, 15, 6, wallFinish ); // right
            new SubMesh( 2, 0, verticesCount, 21, 6, wallFinish ); // left
            new SubMesh( 3, 0, verticesCount, 27, 6, wallFinish ); // floor porch
            new SubMesh( 4, 0, verticesCount, 33, 3, wallFinish ); // side porch
            new SubMesh( 3, 0, verticesCount, 36, 6, wallFinish ); // roof right
        }

        this.updateColor( 'group-wall-color' );

        return wallFinish;
    }

    /**
     * Draw inner obj by code
     */
    drawWallInner = () => {
        const oldMesh       = this.getMeshByName( innerObjectName.origin );
        const innerOld      = this.getMeshByName( 'inner_obj' );
        innerOld.visibility = 0;
        if( oldMesh ) oldMesh.dispose();
        let vertex      = [];
        let keys        = [];
        let face        = [];
        if( 'flat' === this._roofShape ) {
            keys = [ 
                'b_f_r1', 
                'b_b_r1',
                'b_b_r2', 
                'b_b_t',
                'b_b_l1', 
                'b_f_l1',
                'b_f_t',
                'b_f_r2', 
            ];

            if( this.isTinyHomeLeanTo() ) {
                face = [ 
                    [1,2,3,4],
                    [0,5,6,7],
                    [7,2,1,0],
                    [6,5,4,3],
                ];
            } else {
                face = [ 
                    [1,2,3,4],
                    [0,5,6,7],
                    [7,2,1,0],
                    [6,5,4,3],
                    [7,6,3,2],
                    // [0,1,4,5],
                ];
            }
        } else if( 'saltbox' === this._roofShape ) { 
            face = [ 
                [5,6,1,2,3,4],
                [10,11,0,7,8,9],
                [11,2,1,0],
                [8,7,6,5],
                [3,2,11,10],
                [10,9,4,3],
                [ 9,8,5,4],
                // [0,1,6,7],
            ];
        } else {
            keys = [ 
                'b_f_r1', 
                'b_b_r1',
                'b_b_r2', 
                'b_b_t',
                'b_b_l2', 
                'b_b_l1',
                'b_f_l1',
                'b_f_l2',
                'b_f_t',
                'b_f_r2', 
            ];

            if( this.isTinyHomeCottage() ) {
                face = [ 
                    [1,2,3,4,5],
                    [0,6,7,8,9],
                    [9,2,1,0],
                    [7,6,5,4],
                ];
            } else {
                face = [ 
                    [1,2,3,4,5],
                    [0,6,7,8,9],
                    [9,2,1,0],
                    [7,6,5,4],
                    [3,2,9,8],
                    [8,7,4,3],
                    // [0,1,5,6],
                ];
            }

            if( this.isTinyHomeFrontPorch() ) {
                keys = [ 
                    'b_f_r1',
                    'b_b_r1',
                    'b_b_r2', 
                    'b_b_t',
                    'b_b_l2',
                    'b_b_l1', 
                    'b_f_l1',
                    'b_f_l2',
                    'b_f_r2',
                    'b_p_r2',
                    'b_p_l2',
                    'b_p_t',
                ];
                face = [ 
                    [1,2,3,4,5],
                    [0,6,7,8],
                    [8,2,1,0],
                    [7,6,5,4],
                    [7,10,9,8],
                    [10,11,9]
                ];
            }
        }

        if( 'saltbox' === this._roofShape ) {
            vertex = this.getVertexInnerSaltBox();
        } else {
            keys.forEach( key => {
                if( this._bones[key] ) vertex.push( this.calculatePositionInner( this._bones[key].position ).asArray() );
            } );
        }

        let innerBlock      = MeshBuilder.CreatePolyhedron( innerObjectName.origin, { custom: { vertex, face }, updatable: true }, this._scene );
        innerBlock.material = this._innerMaterial;
        innerBlock.parent   = this.getMeshByName( 'body_obj' );
        const verticesCount = innerBlock.getTotalVertices();
        if( 'flat' === this._roofShape ) { 
            new SubMesh( 1, 0, verticesCount, 0, 6, innerBlock ); // back
            new SubMesh( 1, 0, verticesCount, 6, 6, innerBlock ); // front
            new SubMesh( 2, 0, verticesCount, 12, 6, innerBlock ); // right
            new SubMesh( 2, 0, verticesCount, 18, 6, innerBlock ); // left
            new SubMesh( 3, 0, verticesCount, 24, 6, innerBlock ); // roof
        } else if( 'saltbox' === this._roofShape ) {
            new SubMesh( 1, 0, verticesCount, 0, 12, innerBlock ); // back
            new SubMesh( 1, 0, verticesCount, 12, 12, innerBlock ); // front
            new SubMesh( 2, 0, verticesCount, 24, 6, innerBlock ); // right
            new SubMesh( 2, 0, verticesCount, 30, 6, innerBlock ); // left
            new SubMesh( 3, 0, verticesCount, 36, 6, innerBlock );
            new SubMesh( 3, 0, verticesCount, 42, 6, innerBlock ); // roof right
            new SubMesh( 4, 0, verticesCount, 48, 6, innerBlock ); // roofleft
        } else {
            if( this.isTinyHomeFrontPorch() ) {
                new SubMesh( 1, 0, verticesCount, 0, 9, innerBlock ); // back
                new SubMesh( 1, 0, verticesCount, 9, 6, innerBlock); // front
                new SubMesh( 2, 0, verticesCount, 15, 6, innerBlock ); // right
                new SubMesh( 2, 0, verticesCount, 21, 6, innerBlock); // left
                new SubMesh( 3, 0, verticesCount, 27, 6, innerBlock ); // floor porch
                new SubMesh( 1, 0, verticesCount, 33, 3, innerBlock ); // side porch
            } else {
                new SubMesh( 1, 0, verticesCount, 0, 8, innerBlock ); // back
                new SubMesh( 1, 0, verticesCount, 9, 9, innerBlock); // front
                new SubMesh( 2, 0, verticesCount, 18, 6, innerBlock ); // right
                new SubMesh( 2, 0, verticesCount, 24, 6, innerBlock); // left
                new SubMesh( 3, 0, verticesCount, 30, 6, innerBlock ); // roof left
                new SubMesh( 3, 0, verticesCount, 36, 6, innerBlock ); // roof right
            }
        }

        return innerBlock;
    }

    removeDuplicates = arr => {
        const seen = new Set();
        return arr.filter( point => {
            const key = JSON.stringify( point ); // Works for nested objects too
            return seen.has( key ) ? false : seen.add( key );
        });
    }


    /**
     * Draw shape floo
     * @returns 
     */
    drawShapeCutFloor = () => {
        const _this     = this;
        const walls     = this.getWallInfo( true );
        const wallFloor = helpers.objectFilter( walls, item => item.side !== 'floor' );
        if( helpers.isEmptyObject( wallFloor ) ) return;
        const keys      = Object.keys( wallFloor );
        const wall      = wallFloor[ keys[0] ];
        let { shape }   = this.calculateWallInfo( wall );
        shape           = this.removeDuplicates( shape );
        shape           = shape.map( item => {
            return _this.calculatePositionInner( item, this._frameDepth );
        } );
        const depth     = this.getBuildingHeight() + 5;
        const faceUV    = [];
        faceUV[0]       = new Vector4( 0, 0, 1, 1 );
        faceUV[2]       = new Vector4( 0, 1, 1, 0 );
        const meshName  = 'shape_cut_floor';
        let shapeFloor  = this.getMeshByName( meshName );
        if( shapeFloor ) shapeFloor.dispose();
        shape = ! this.isClockwiseAlongXZ( shape ) ? shape.reverse() : shape;
        shapeFloor = MeshBuilder.ExtrudePolygon( meshName, { shape, holes: [], depth, faceUV, sideOrientation: Mesh.DOUBLESIDE }, this._scene );
        shapeFloor.position.y = depth - 2;
        shapeFloor.isPickable = false;
        shapeFloor.setEnabled( false );

        return shapeFloor;
    }
    
    /**
     * Cut partial wall by shape floor
     * @param partialWall 
     * @param wallName 
     */
    cutPatialWallByShapeFloor = ( partialWall, wallName ) => {
        const shapeFloor        = this.drawShapeCutFloor();
        let oldWall             = this.getMeshByName( wallName );
        if( oldWall ) oldWall.dispose();
        var csgShapeFloor       = CSG.FromMesh( shapeFloor );
        var csgPartialWall      = CSG.FromMesh( partialWall );
        let subCSG              = csgPartialWall.subtract( csgShapeFloor );
        var newMesh             = subCSG.toMesh( wallName, partialWall.material, this._scene, true);
        newMesh.parent          = partialWall.parent;
        shapeFloor.visibility   = 0;
        partialWall.visibility  = 0;
    }

    /**
     * Find wall to draw partial wall
     * @param walls 
     * @returns 
     */
    getSideReferenceDrawPartialWall = ( walls ) => {
        const wallBack          = helpers.objectFilter( walls, item => item.side !== 'back' );
        const wallFront         = helpers.objectFilter( walls, item => item.side !== 'front' );
        const dataBackWall      =  this.calculateWallInfo( wallBack[ Object.keys( wallBack )[0] ] );
        const dataFrontWall     =  this.calculateWallInfo( wallFront[ Object.keys( wallFront )[0] ] );
        let floorBonesBack      = helpers.objectFilter( dataBackWall.shape, b => b._z > 0.5 );
        let floorBonesKeysBack  = Object.keys( floorBonesBack );
        let floorBonesFront     = helpers.objectFilter( dataFrontWall.shape, b => b._z > 0.5 );
        let floorBonesKeysFront = Object.keys( floorBonesFront );
        if( floorBonesKeysFront.length >= 2 && floorBonesKeysFront.length >= 2 ) {
            if(  Vector3.Distance( floorBonesBack[floorBonesKeysBack[0]], floorBonesBack[floorBonesKeysBack[1]] ) < Vector3.Distance( floorBonesFront[floorBonesKeysFront[0]], floorBonesFront[floorBonesKeysFront[1]] ) ) {
                return 'front';
            }
        }

        return 'back';
    }

    /**
     * Draw partial wall
     * @param positionZ 
     * @param prefix 
     * @returns 
     */
    drawPartialWall = async ( positionZ, prefix ) => {
        const wallName      = `partial_wall_${prefix}`;
        const tempWallName  = `temp_partial_wall_${prefix}`;
        const oldMesh       = this.getMeshByName( tempWallName );
        const floorOffset   = this.getFloorOffset();
        if( oldMesh ) oldMesh.dispose();
        let partyWall = this.getMeshByName( wallName );
        if( partyWall ) partyWall.dispose();
        const walls   = this.getWallInfo( true );
        if( helpers.isEmptyObject( walls ) ) return;
        const sideReference  = this.getSideReferenceDrawPartialWall( walls );
        const wallReferen    = helpers.objectFilter( walls, item => item.side !== sideReference );
        if( helpers.isEmptyObject( wallReferen ) ) return;
        const keys                          = Object.keys( wallReferen );
        const wall                          = wallReferen[ keys[0] ];
        const partialWalls                  = this._partialWall;
        const width                         = this.getWidth() / 2;
        const { shape, rotation, position } = this.calculateWallInfo( wall );
        let heightWall : any                = 0;
        let partialSaphe : any              = shape;
        if( 'full' === partialWalls.height ) {
            heightWall = this.getBuildingHeight();
        } else if( 'building' === partialWalls.height ) {
            heightWall   = this.getWallHeight();
            partialSaphe = this.getShapePartialWall( shape, heightWall );
        } else {
            heightWall   = partialWalls.value;
            partialSaphe = this.getShapePartialWall( shape, heightWall );
        }
        partialSaphe = this.isClockwiseAlongXZ( partialSaphe ) ? partialSaphe.reverse() : partialSaphe;

        const frameDepth    = 0 !== this._frameDepth ? this._frameDepth + 0.004 + 0.037 : 0.004;
        partialSaphe        = partialSaphe.map( item => {
            let temp = new Vector3( item.x, item.y, item.z)
            if( Math.abs( item.x ) === Math.abs( width ) ){
                temp.x = item.x > 0 ? item.x - frameDepth : item.x + frameDepth;
            }
            if( item.z < 0.5 ) {
                temp.z = item.z + floorOffset;
            }
            return temp;
        } );

        const faceUV                        = [];
        faceUV[0]                           = new Vector4( 0, 0, 1, 1);
        faceUV[1]                           = new Vector4( 0, 0, 1, 1);
        faceUV[2]                           = new Vector4( 0, 0, 1, 1);
        faceUV[3]                           = new Vector4( 0, 0, 1, 1);
        faceUV[4]                           = new Vector4( 0, 0, 1, 1);
        faceUV[5]                           = new Vector4( 0, 0, 1, 1);
        partyWall                           = await this.drawWall ( tempWallName, '', partialSaphe, rotation, position, wall.materialName, heightWall, wall.parentMesh, '', 0.08, faceUV, Mesh.DOUBLESIDE );
        partyWall.alwaysSelectAsActiveMesh  = true;
        partyWall.position.z                = positionZ;
        const sidingInnerMat                = this._scene.getMaterialByName( "siding_inner_mat" ) as StandardMaterial;
        
        if( ! sidingInnerMat ) return partyWall;

        let material = this._scene.getMaterialByName( "party_inner_mat" ) as StandardMaterial;
        if( ! material ) {
            material                    = new StandardMaterial( "party_inner_mat", this._scene);
            material.diffuseTexture     = sidingInnerMat.diffuseTexture;
            material.bumpTexture        = sidingInnerMat.bumpTexture;
            material.specularTexture    = sidingInnerMat.specularTexture;
        }
        partyWall.material = material;
        this.cutPatialWallByShapeFloor( partyWall, wallName );

        return partyWall;

    }
    

    drawInterior = async ( name, shape, rotation, position, materialName, height, wallSide ) => {
        if( 'floor' === wallSide ) return;
        
        const oldMesh = this.getMeshByName( name );
        if( oldMesh ) {
            oldMesh.dispose();
        }
        
        const faceUV = [ new Vector4( 0, 0, 1, 1 ), new Vector4( 0, 1, 1, 0 ) ];
        const wall = MeshBuilder.ExtrudePolygon( name, { shape, holes: [], depth: 0.001, faceUV, sideOrientation: Mesh.DOUBLESIDE }, this._scene );
        wall.rotation.copyFrom( rotation );
        wall.position.copyFrom( position );

        wall.isPickable           = false;
        wall.checkCollisions = true;
        if( ! this._apiData['material'].includes( materialName ) ) {
            this._apiData['material'].push( materialName );
        }

        if( ! this._apiData['meshes'].includes( name ) ) {
            this._apiData['meshes'].push( name );
        }
        
        await this.applyMaterial( { wall, materialName, wallHeight: height, wallWidth: 0, wallSide: '' } );
    }
    
    applyMaterial = async ( data ) => {
        let { wall, materialName, wallHeight, wallWidth, wallSide, initUScale, initVScale } = data;
        initUScale = undefined !== initVScale ? initVScale : this._sidingUscale;
        initVScale = undefined !== initVScale ? initVScale : this._sidingVscale;
        const activeFloorMaterial   = this.getActiveFloorMaterialData();
        
        if( -1 !== this._apiData['wainscoting_material'].indexOf( materialName ) ) {
            initUScale = this.getCustomUVScale( `wainscoting_uscale` );
            initVScale = this.getCustomUVScale( `wainscoting_vscale` );
        } else if( -1 !== this._apiData['accent_gable_material'].indexOf( materialName ) ) {
            initUScale = this.getCustomUVScale( `accent_gable_uscale` );
            initVScale = this.getCustomUVScale( `accent_gable_vscale` );
        } else if( -1 !== this._apiData['exterior_siding_upgrades_material'].indexOf( materialName ) ) {
            initUScale = this.getCustomUVScale( `exterior_siding_upgrades_uscale` );
            initVScale = this.getCustomUVScale( `exterior_siding_upgrades_vscale` );
        } else if( 'floor' === wallSide && activeFloorMaterial?.floorType ) {
            initUScale = activeFloorMaterial?.uScale ?? 1;
            initVScale = activeFloorMaterial?.vScale ?? 1;
        }

        const wallDepth = wall.geometry.extend.maximum.z - wall.geometry.extend.minimum.z;
        wallWidth = wallWidth !== 0 ? wallWidth : wall.geometry.extend.maximum.x - wall.geometry.extend.minimum.x;
        wallHeight      = undefined !== wallHeight ? wallHeight : wallDepth;
        let material = this._scene.getMaterialByName( materialName ) as StandardMaterial;

        if( null === material ) {
            if( -1 !== this._apiData['wainscoting_material'].indexOf( materialName ) ) {
                const { defaultDiffuse, defaultBump, defaultSpecular, specularPower } = this.getSidingMaterialData( jQuery( '#wainscoting-material-option li.active' ) );
                material = this.createMaterial( materialName, defaultDiffuse, defaultBump, defaultSpecular, this.getSidingColor( 'wainscoting' ) ) as StandardMaterial;

                if( -1 !== specularPower ) {
                    material.specularPower = specularPower;
                }
        
            } else if( -1 !== this._apiData['accent_gable_material'].indexOf( materialName ) ) {
                const { defaultDiffuse, defaultBump, defaultSpecular, specularPower } = this.getSidingMaterialData( jQuery( '#accent_gable-material-option li.active' ) );
                material = this.createMaterial( materialName, defaultDiffuse, defaultBump, defaultSpecular, this.getSidingColor( 'accent_gable' ) ) as StandardMaterial;

                if( -1 !== specularPower ) {
                    material.specularPower = specularPower;
                }
            } else if( -1 !== this._apiData['exterior_siding_upgrades_material'].indexOf( materialName ) ) {
                const { defaultDiffuse, defaultBump, defaultSpecular, specularPower } = this.getSidingMaterialData( jQuery( '#exterior_siding_upgrades-material-option li.active' ) );
                material = this.createMaterial( materialName, defaultDiffuse, defaultBump, defaultSpecular, this.getSidingColor( 'exterior_siding_upgrades' ) ) as StandardMaterial;

                if( -1 !== specularPower ) {
                    material.specularPower = specularPower;
                }
            }  else if( 'floor' === wallSide ) {
                if ( ! activeFloorMaterial?.floorType ) return;
                const { diffuseTexture, bumpTexture, specularTexture } = this.getFloorTextureFile( activeFloorMaterial.floorType );
                material = this.createMaterial( materialName, diffuseTexture, bumpTexture, specularTexture, { type: 'color', code: '#FFFFFF', image: '' } ) as StandardMaterial;
                
                if( -1 !== activeFloorMaterial.specularPower ) {
                    material.specularPower = activeFloorMaterial.specularPower;
                }
            } else if( -1 !== this._apiData['interior_wall_material'].indexOf( materialName ) ) {
                const { diffuseTexture, bumpTexture, specularTexture, color, uScale: sidingUscale, vScale: sidingVscale } = await this.getSidingInnerMaterialData();
                initUScale = sidingUscale;
                initVScale = sidingVscale;
                material = this.createMaterial( materialName, diffuseTexture, bumpTexture, specularTexture, color ) as StandardMaterial;   
            } else if( -1 !== this._apiData['interior_ceiling_material'].indexOf( materialName ) ) {
                if( jQuery( '#insulation_roof-material-option' ).length || jQuery( '#interior_ceiling-material-option' ).length ) {
                    if( jQuery( '#interior_ceiling-material-option' ).length ) {
                        let { diffuseTexture, bumpTexture, specularTexture, specularPower } = this.getTextureData( 'interior_ceiling-material-option' );
                        material = this.createMaterial( materialName, diffuseTexture, bumpTexture, specularTexture, this.getSidingColor( 'ceiling' ) ) as StandardMaterial;
                        initUScale = parseFloat( jQuery( '#interior_ceiling-material-option li.active' ).attr( 'data-uscale' ) );
                        initVScale = parseFloat( jQuery( '#interior_ceiling-material-option li.active' ).attr( 'data-vscale' ) );
                    } else {
                        let { diffuseTexture, bumpTexture, specularTexture, specularPower } = this.getTextureData( 'insulation_roof-material-option' );
                        let color = { type: 'color', image: '', code: this._defaultColorInteriorSiding };
                        initUScale = parseFloat( jQuery( '#insulation_roof-material-option li.active' ).attr( 'data-uscale' ) );
                        initVScale = parseFloat( jQuery( '#insulation_roof-material-option li.active' ).attr( 'data-vscale' ) );
                        material = this.createMaterial( `${materialName}`, diffuseTexture, bumpTexture, specularTexture, color ) as StandardMaterial;
                    }

                    if( ! this.isEnableMaterialInteriorRoofWall() ) {
                        material.alpha = 0;
                    }
                } else {
                    wall.visibility = 0;
                }
               
            } else {
                const { diffuseTexture, bumpTexture, specularTexture, specularPower } = await this.getSidingTexture();
                material = this.createMaterial( materialName, diffuseTexture, bumpTexture, specularTexture, this.getSidingColor( 'siding' ) ) as StandardMaterial;

                if( -1 !== specularPower ) {
                    material.specularPower = specularPower;
                }
            }
        }

        const vScale = + ( wallHeight / ( 8 / initVScale ) ).toFixed( 3 ); // 8 is default width of material when design create. this._sidingVscale is init V scale from network
        const uScale = + ( wallWidth / ( 8 / initUScale ) ).toFixed( 3 );

        if( null !== material ) {
            if( -1 !== materialName.indexOf( '_interior' ) && 0 === jQuery( '#interior_wall-material-option' ).length && this._isDrawInteriorSiding ) {
                material.diffuseColor = Color3.FromHexString( this._defaultColorInteriorSiding );
            }
    
            material.bumpTexture['vScale']      = vScale;
            material.diffuseTexture['vScale']   = vScale;
            material.specularTexture['vScale']  = vScale;
            
            material.bumpTexture['uScale']      = uScale;
            material.diffuseTexture['uScale']   = uScale;
            material.specularTexture['uScale']  = uScale;
    
            wall.material = material;
            this.setInitialUVs( material );
        }
    }

    /**
     * set Wainscoting Color
     * 
     * @param {StandardMaterial} material bone roof top
     */
    setWainscotingColor = ( material : StandardMaterial ) => {
        if( ! material || -1 === this._apiData['wainscoting_material'].indexOf( material.name ) ) return;

        let element = jQuery( '.group-wainscoting-color' ).find( '.ssb-option.active' );

        if( undefined === element ) {
            element = jQuery( '.group-wainscoting-color' ).find( '.ssb-option:not(.unavailable):eq(0)' );
        }

        const textureType   = element.attr( 'data-texture-type' );
        const textureImage  = element.attr( 'data-texture-image' );
        const textureColor  = element.attr( 'data-texture-color' );

        this.updateTexture( material, textureType, textureImage, textureColor );
    }

    drawTrim = wall => {
        if( ! wall.trim ) return;

        const { rotation, position, shape: sidingShape } = this.calculateWallInfo( wall );
        const trimHeight    = 0.3;
        const faceUV        = new Array(6);
        const sidingMesh    = this.getMeshByName( wall.name );
        const positionLeft  = sidingMesh.geometry.extend.minimum.x;
        const positionRight = sidingMesh.geometry.extend.maximum.x;
        const positionY     = 'object' == typeof wall.trim ? wall.trim.position : sidingMesh.geometry.extend.maximum.z;
        const meshName      = 'object' == typeof wall.trim ? `${wall.name}_trim` : `wainscoting_${wall.name}_trim`;
        const trimMatKey    = 'object' == typeof wall.trim ? wall.trim.matKey : 'trim_mat';
        const type          = 'object' == typeof wall.trim ? wall.trim.type : 'trim';

        if( positionY > sidingMesh.geometry.extend.maximum.z || positionY < sidingMesh.geometry.extend.minimum.z ) {
            return;
        }

        let trimMesh        = this.getMeshByName( meshName );
        let trimMat         = this._scene.getMaterialByName( 'trim_mat' ) as StandardMaterial;
        let cloneTrimMat    = this._scene.getMaterialByName( trimMatKey ) as StandardMaterial;

        if( ! cloneTrimMat ) {
            cloneTrimMat = trimMat.clone( trimMatKey );
            cloneTrimMat.diffuseTexture.updateSamplingMode( 1 );

            this._initialUVs[trimMatKey] = cloneTrimMat
        }

        cloneTrimMat.ambientTexture = null;

        if( trimMesh ) trimMesh.dispose();
        
        for( let i = 0; i < 6; i++ ) {
            faceUV[i] = new Vector4( 10/1024, 250/1024, 680/1024, 280/1024 );
        }

        const shape = [ 
            new Vector3( helpers.toFixed3( positionLeft ), 0, helpers.toFixed3( positionY ) ),
            new Vector3( helpers.toFixed3( positionLeft ), 0, helpers.toFixed3( positionY + trimHeight ) ),
            new Vector3( helpers.toFixed3( positionRight ), 0, helpers.toFixed3( positionY + trimHeight ) ),
            new Vector3( helpers.toFixed3( positionRight ), 0, helpers.toFixed3( positionY ) ),
        ];

        trimMesh = MeshBuilder.ExtrudePolygon( meshName, { shape, faceUV, depth: 0.03, sideOrientation: Mesh.DOUBLESIDE } );

        if( 'object' == typeof wall.trim ) {
            const orientation   = this.isClockwiseAlongXZ( sidingShape ) ? Mesh.BACKSIDE : Mesh.FRONTSIDE;
            const tempMesh      = MeshBuilder.ExtrudePolygon( `${wall.name}_temporary_mesh`, { shape: sidingShape, depth: 1, sideOrientation: orientation }, this._scene );
            tempMesh.position   = new Vector3( 0, 0.5, 0 );
            const tempMeshCSG   = CSG.FromMesh( tempMesh );
            const trimMeshCSG   = CSG.FromMesh( trimMesh );
            const intersectMesh = trimMeshCSG.intersect( tempMeshCSG );

            trimMesh.dispose();
            tempMesh.dispose();
            trimMesh = intersectMesh.toMesh( meshName, cloneTrimMat, this._scene, true );

            if( 0 !== wall.trim.material.length && ! helpers.hasKeyInArray( wall.trim.material, this.getSidingType() ) ) {
                trimMesh.setEnabled( false );
            }
        }

        this._apiData[type] = helpers.checkAndPushKeyToArray( this._apiData[type], meshName );
        trimMesh.material   = cloneTrimMat;
        trimMesh.isPickable = false;

        trimMesh.position.copyFrom( position );
        trimMesh.rotation.copyFrom( rotation );
        sidingMesh.addChild( trimMesh );
        trimMesh.position.copyFrom( new Vector3( trimMesh.position.x, trimMesh.position.y + 0.01, trimMesh.position.z ) );
    }

    isClockwiseAlongXZ = points => {
        let sumXZ = 0;
        for( let i = 0; i < points.length; i++ ) {
            const current   = points[i];
            const next      = points[(i + 1) % points.length];

            sumXZ += ( next.x - current.x ) * ( next.z + current.z );
        }

        return sumXZ > 0;
    }

    isClockwiseAlongXY = points => {
        let sum = 0;
        let n = points.length;

        for (let i = 0; i < n; i++) {
            let j = (i + 1) % n;
            sum += (points[j].x - points[i].x) * (points[j].y + points[i].y);
        }

        return sum > 0; // True nếu theo chiều kim đồng hồ (CW)
    }

    isClockwiseAlongXYZ = ( points ) => {
        if( points.length < 3 ) return false; // Không đủ điểm để xác định hướng
    
        // Tính vector pháp tuyến bằng cross product của hai cạnh đầu tiên
        let v1 = {
            x: points[1].x - points[0].x,
            y: points[1].y - points[0].y,
            z: points[1].z - points[0].z
        };
        let v2 = {
            x: points[2].x - points[0].x,
            y: points[2].y - points[0].y,
            z: points[2].z - points[0].z
        };
        
        // Cross product: normal = v1 × v2
        let normal = {
            x: v1.y * v2.z - v1.z * v2.y,
            y: v1.z * v2.x - v1.x * v2.z,
            z: v1.x * v2.y - v1.y * v2.x
        };
    
        // Chiếu lên mặt phẳng có thành phần lớn nhất của pháp tuyến
        let maxComponent = Math.max(Math.abs(normal.x), Math.abs(normal.y), Math.abs(normal.z));
        let projectedPoints;
    
        if (maxComponent === Math.abs(normal.x)) {
            // Chiếu lên mặt phẳng YZ
            projectedPoints = points.map(p => ({ x: p.y, y: p.z }));
        } else if (maxComponent === Math.abs(normal.y)) {
            // Chiếu lên mặt phẳng XZ
            projectedPoints = points.map(p => ({ x: p.x, y: p.z }));
        } else {
            // Chiếu lên mặt phẳng XY
            projectedPoints = points.map(p => ({ x: p.x, y: p.y }));
        }
    
        // Tính diện tích có hướng (Signed Area)
        let sum = 0;
        for (let i = 0; i < projectedPoints.length; i++) {
            let j = (i + 1) % projectedPoints.length;
            sum += (projectedPoints[j].x - projectedPoints[i].x) * 
                   (projectedPoints[j].y + projectedPoints[i].y);
        }
    
        return sum < 0; // Trả về true nếu CW, false nếu CCW
    }

    barnSmithbiltHoleRoof = ( side ) => {
        if( 'structure_barn_smithbilt' !== this._shedType ) {
            return;
        }

        if( 'left' === side ) {
            const intersectMeshes = this.getIntersectMeshes( side );
            this.createMeshFromCSG( 'left_roof_clone', 'roof_siding_L_obj', intersectMeshes );
            this.createMeshFromCSG( 'left_frame_clone', 'frame_l_obj', intersectMeshes );
        } else if( 'right' === side ) {
            const intersectMeshes = this.getIntersectMeshes( side );
            this.createMeshFromCSG( 'right_roof_clone', 'roof_siding_R_obj', intersectMeshes );
            this.createMeshFromCSG( 'right_frame_clone', 'frame_r_obj', intersectMeshes );
        }
        
        this.resetIntersectMeshes();
    }

    barnSmithbiltScaleRoof = () => {
        if( 'structure_barn_smithbilt' !== this._shedType ) {
            return;
        }

        const scaleZ    = this.getDepth() / 10; // 10 is default depth of this model
        const positionX = this.getWidth() / 2 - 5; // 5 is half width of this model
        const positionY = this.getWallHeight() - 6; // 6 is default wall height
    
        this.placeAndScaleMesh( 'roof_siding_L_obj', scaleZ, - positionX, positionY );
        this.placeAndScaleMesh( 'frame_l_obj', scaleZ, - positionX, positionY );
        this.placeAndScaleMesh( 'roof_siding_R_obj', scaleZ, positionX, positionY );
        this.placeAndScaleMesh( 'frame_r_obj', scaleZ, positionX, positionY );
    }

    /**
     * load Smithbilt Component Trim
     */
    loadSmithbiltComponentTrim = async () => {
        let _this   = this;
        const name  = 'smithbilt_component_trim';
        const type  = 'trim';
        return new Promise( resolve => {
            if( 'structure_barn_smithbilt' !== this._shedType ) {
                return resolve( true );
            }

            const componentTask     = _this._assetsManager.addMeshTask( `load_${name}`, '', `${_this._componentDir}${type}/${name}/`, `${name}.babylon` );
            componentTask.onSuccess = task => {
                _this.importMaterials( task.loadedMeshes );
                task.loadedMeshes[0].visibility = 0;

                resolve( true );
            };
            _this.forceAssetLoading();
        })
    }
    /**
     * permatile_metal
     * @param {string} roofKey component name
     * @returns Void 
     */
    loadComponentRoofByKey = async ( roofKey: string, fileName: string ) => {
        let _this   = this;
        const type  = 'roof';
        fileName    = _this.getComponentFileName( fileName, roofKey );
        return new Promise( resolve => {
            if( ! _this.isRoofCanload( roofKey ) ) return resolve( true );

            const folder = fileName;

            if( helpers.isRobinBuilders() ) {
                fileName += '_noridgecap';
            }

            const version = helpers.getVersionComponent( fileName, _this._componentData );
            const componentTask = _this._assetsManager.addMeshTask( `load_${folder}`, '', `${_this._componentDir}${type}/${folder}/`, `${fileName}.babylon${version}` );
            componentTask.onSuccess = task => {
                _this.importSkeletons(task.loadedSkeletons);
                _this.importMaterials( task.loadedMeshes );
                task.loadedMeshes[0].visibility = 0;
                _this.setComponentRoofVariable( roofKey );
                resolve( true );
            };
            _this.forceAssetLoading();
        })
    }

    getComponentFileName = ( fileName, roofKey ) =>{
        if( 'lean_to' === this._shedType || 'structure_lean_to_with_front_porch' === this._shedType ) {
            if( 'permatile_metal' === roofKey ) return 'roof_permatile_lean_to';
            if( 'standing_seam' === roofKey ) return 'roof_standing_seam_lean_to';
        }

        if( 'structure_hiproof' === this._shedType ) {
            if( 'permatile_metal' === roofKey ) return 'roof_permatile_hiproof';
            if( 'standing_seam' === roofKey ) return 'roof_standing_seam_hiproof';
        }

        return fileName;
    }

    setComponentRoofVariable = ( roofKey ) => {
        if( 'permatile_metal' === roofKey ) {
            if( 'flat' === this.getRoofShape() ) {
                if( this._bones['roof_permatile_lean_to_l1'] && this._bones['roof_permatile_lean_to_r1'] ) this.initRoofPermatileMetal.permatile_metal = Vector3.Distance( this._bones['roof_permatile_lean_to_l1'].position, this._bones['roof_permatile_lean_to_r1'].position );
            } else if( 'hip' === this.getRoofShape() ) {
                if( this._bones['r_b_t'] && this._bones['r_b_l2'] ) this.initRoofPermatileMetal.permatile_metal = Vector3.Distance( this._bones['r_b_t'].position, this._bones['r_b_l2'].position );
            } else {
                if( this._bones['roof_permatile_aframe_metal_t'] && this._bones['roof_permatile_aframe_metal_l1'] ) this.initRoofPermatileMetal.permatile_metal = Vector3.Distance( this._bones['roof_permatile_aframe_metal_t'].position, this._bones['roof_permatile_aframe_metal_l1'].position );
            }
        }

        if( 'standing_seam' === roofKey ) {
            if( 'flat' === this.getRoofShape() ) {
                if( this._bones['roof_standing_seam_lean_to_l1'] && this._bones['roof_standing_seam_lean_to_r1'] ) this.initRoofPermatileMetal.standing_seam = Vector3.Distance( this._bones['roof_standing_seam_lean_to_l1'].position, this._bones['roof_standing_seam_lean_to_r1'].position );
            } else {
                if( this._bones['roof_standing_seam_aframe_metal_t'] && this._bones['roof_standing_seam_aframe_metal_l1'] ) this.initRoofPermatileMetal.standing_seam = Vector3.Distance( this._bones['roof_standing_seam_aframe_metal_t'].position, this._bones['roof_standing_seam_aframe_metal_l1'].position );
            }
        }

        const roofCloneMesh = this.getMeshByName( `roof_${roofKey}_clone_obj` );
        if( roofCloneMesh ) roofCloneMesh.setEnabled( false );

        this._roofMesh[roofKey] = helpers.checkAndPushKeyToArray( this._roofMesh[roofKey], `roof_${roofKey}_cover_obj` );
        if( ! helpers.hasKeyInArray( this._meshBlurForInterior, `roof_${roofKey}_cover_obj` ) ) {
            this._meshBlurForInterior.push( `roof_${roofKey}_cover_obj` );
        }
    }
    
    getListRoofs = () => {
        var listRoof = [];
        jQuery( '#roof-material-option .ssb-option' ).each( function(i, e){
            listRoof.push( jQuery( e ).data( 'roof-type' ) );
        } );
        
        return listRoof;
    }

    isRoofCanload = ( roofKey ) => {
        const roofList = this.getListRoofs();
        return -1 !== roofList.indexOf( roofKey );
    }

     /**
     * 
     * @param {string} materialname Material name
     * @param {Vector3} vector1 bone roof top
     * @param {Vector3} vector2 bone roof left or right
     * @param {Vector3} initRoofLength Roof length base component that is distance from vector1 to vector2
     */
     vScaleComoponentRoofUnit = ( materialname : string, vector1: Vector3, vector2 : Vector3, initRoofLength: number ) => {
        const roofLength    = Vector3.Distance( vector1, vector2 );
        const vScale        = roofLength / initRoofLength;
        const materials     = this.getMaterialByName( materialname );
        if( materials )
        materials.forEach( mat => {
            this.updateUVScale( mat, 1, vScale, this._initialUVs[materialname]['bumpTexture']['uScale'], this._initialUVs[materialname]['bumpTexture']['vScale'] );
            if( mat.ambientTexture ) mat.ambientTexture['vScale'] = vScale * this._initialUVs[materialname]['bumpTexture']['vScale'];
        });
    }

    /**
     * Use hide roof when change roof type
     * @param {Array} listKey list obj of roof
     * @param {boolean} status control show or hide 
     * @returns void
     */
    setEnableRoofComponentByKeys = ( listKey, status ) => {
        const _this = this;
        if( listKey.length === 0 ) return;
        for( const key of listKey ) {
            const roofMeshes = this._scene.getMeshByName( key ) as Mesh;
            if( roofMeshes ) {
                roofMeshes.setEnabled( status );
                for( const roofMesh of roofMeshes.instances ) {
                    // roofMesh.setEnabled( status );
                    roofMesh.isVisible = false;
                }
            }
        }
    }

    getMeshWidth = mesh => {
        return +( mesh.geometry.extend.maximum.x - mesh.geometry.extend.minimum.x ).toFixed( 3 );
    }

    getMeshLength = mesh => {
        return +( mesh.geometry.extend.maximum.z - mesh.geometry.extend.minimum.z ).toFixed( 3 );
    }
    
    getMeshHeight = mesh => {
        return +( mesh.geometry.extend.maximum.y - mesh.geometry.extend.minimum.y ).toFixed( 3 );
    }

    handleSmithbiltComponentTrim = mesh => {
        const hasHandle = 'structure_barn_smithbilt' === this._shedType && ( 'left' === mesh.metadata.side || 'right' === mesh.metadata.side ) && ( 'door' === mesh.metadata.type || 'windows' === mesh.metadata.type );
        if( ! hasHandle ) {
            return;
        }

        const meshes            = mesh.getChildMeshes();
        let intersectWidth      = 0;
        let intersectHeight     = 0;
        let boundingBoxHeight   = 0;
        meshes.forEach( m => {
            if( `${mesh.metadata.name}_intersect_obj` === m.name ) {
                intersectWidth  = this.getMeshWidth( m );
                intersectHeight = this.getMeshHeight( m );
            }

            if( `${mesh.metadata.name}_bb_obj` === m.name ) {
                boundingBoxHeight = this.getMeshHeight( m );
            }

            if( `${mesh.metadata.name}_drip_edge_obj` === m.name ) {
                m.visibility = 0;
            }
        });

        const errorNumber   = 0.001;
        let trimMesh        = mesh.metadata.trimMesh;
        trimMesh.visibility = 1;
        if( 'door' === mesh.metadata.type ) {
            trimMesh.position.y = boundingBoxHeight;
        }

        trimMesh.position.y = trimMesh.position.y - errorNumber;

        let y = mesh.position.y - ( this.getWallHeight() - this._smithbiltBarnRoofFix ) - errorNumber;
        if( 'door' === mesh.metadata.type ) {
            y = boundingBoxHeight - ( this.getWallHeight() - this._smithbiltBarnRoofFix ) - errorNumber;
        }

        trimMesh.skeleton.bones.forEach( ( bone, index ) => {
            if( 0 === index ) {
                return;
            }

            if( -1 !== bone.name.indexOf( 'smithbilt_component_trim_L' ) ) {
                bone.position = new Vector3( - ( intersectWidth / 2 - errorNumber ), bone.position.y, bone.position.z );
            } else if( -1 !== bone.name.indexOf( 'smithbilt_component_trim_R' ) ) {
                bone.position = new Vector3( intersectWidth / 2 - errorNumber, bone.position.y, bone.position.z );
            }

            const fixedY = [ 'smithbilt_component_trim_L3', 'smithbilt_component_trim_L4', 'smithbilt_component_trim_R3', 'smithbilt_component_trim_R4' ];
            if( -1 !== fixedY.indexOf( bone.name ) ) {
                bone.position = new Vector3( bone.position.x, - y, bone.position.z );
            }            
        });
    }

    /**
    * @param {number} targetPositionY       Mouse position Y
    * @param {number} buildingHeight        Building Height
    * @param {number} tanRoofLowerCorner    Tan roof lower of roof type Gambrel
    * @param {number} tanRoofUpperCorner    Tan roof upper of roof type Gambrel
    * @param {number} wallHeight            Wall Height
    * @param {number} componentWidth        Component Width
    * @param {number} limitLowerVertical    Adjacent side of roof Lower angle, ex (width / 2) ...
    * @param {number} limitUpperVertical    Adjacent side of roof Upper angle only roof Gambrel ...
    * @param {number} targetVerticalOfDrag  Component drag follow by targetPosition X or Y.
    * @returns {number} targetPositionY     Current position y of component
    */
    getTargetPositionY = ( targetPositionY, buildingHeight, tanRoofLowerCorner, tanRoofUpperCorner, wallHeight, componentWidth, limitLowerVertical, limitUpperVertical, targetVerticalOfDrag ) => {
        let maxDistance         = 0;
        const maxDistanceBelow  = helpers.toFixed3( buildingHeight - this.getSpacePorchRoofPitch() );
        if( 'dutch_style' === this.getRoofShape() ) {
            if( targetPositionY <= maxDistanceBelow ) {
                maxDistance = tanRoofLowerCorner * ( limitLowerVertical - Math.abs( targetVerticalOfDrag ) - ( componentWidth / 2 ) );
                if( targetPositionY > wallHeight + maxDistance ) {
                    targetPositionY = wallHeight + maxDistance;
                }
            } else if( targetPositionY > maxDistanceBelow && tanRoofUpperCorner ) {
                maxDistance = tanRoofUpperCorner * ( Math.abs( limitUpperVertical ) - Math.abs( targetVerticalOfDrag ) - ( componentWidth / 2 ) );
                if( targetPositionY > maxDistanceBelow + maxDistance ) {
                    targetPositionY = maxDistanceBelow + maxDistance;
                }
            }
        } else {
            maxDistance = tanRoofLowerCorner * ( limitLowerVertical - Math.abs( targetVerticalOfDrag ) - ( componentWidth / 2 ) );
            if( 'saltbox_customize' === this.getRoofShape() ) {
                maxDistance = tanRoofLowerCorner * ( Math.abs( targetVerticalOfDrag ) - ( componentWidth / 2 ) );
            }
            if( targetPositionY > maxDistance + wallHeight ) {
                targetPositionY = maxDistance + wallHeight;
            }
            
            if( 0 !== tanRoofUpperCorner ) {
                maxDistance = ( this._bones['b_f_l3'] || 'structure_porch_barn' === this._shedType ) ? tanRoofUpperCorner * ( Math.abs( limitUpperVertical ) - Math.abs( targetVerticalOfDrag ) - ( componentWidth / 2 ) ) : 0;
                if( targetPositionY > maxDistance + maxDistanceBelow ) {
                    targetPositionY = maxDistance + maxDistanceBelow;
                }
            }
        }

        return targetPositionY;
    }
    
    isEnableAddToCartButton = () => {
        if( ( this._isUseRestrictSize && ! this._allowAddToCart ) || ! this._isDisableAddCart ) return false;
        if( '1' === this._canvas.getAttribute( 'is_expired_price' ) ) return false;
        if( this._isValidZipcode && this.isValidFixedWindows() ) {
            return true;
        } else {
            return false
        }
    }

    isValidFixedWindows = () => {
        if( this.isWindowPositionFixedEnable() ) {
            if( this._doorFixedTarget.length !== 0 ) {
                return true;
            }

            return false;
        }

        return true;
    }

    isValidComponentByLayoutCondition = ( component ) => {
        return this.isValidComponentBySizeCondition( component ) && this.isValidComponentBySidingMaterial( component );
    }

    isValidComponentBySizeCondition = ( component ) => {
        const widthOperator  = component['size_condition']['width']['operator'];
        const widthValue     = component['size_condition']['width']['value'];
        const widthValueMax  = component['size_condition']['width']['max'];
        const depthOperator  = component['size_condition']['length']['operator'];
        const depthValue     = component['size_condition']['length']['value'];
        const depthValueMax  = component['size_condition']['length']['max'];
        const heightOperator = component['size_condition']['height']['operator'];
        const heightValue    = component['size_condition']['height']['value'];
        const heightValueMax = component['size_condition']['height']['max'];

        const wallWidth = helpers.getBaseWidth();
        const wallDepth = helpers.getBaseDepth();

        const sizes = [
            { size: wallWidth, operator: widthOperator, value: widthValue, max: widthValueMax },
            { size: wallDepth, operator: depthOperator, value: depthValue, max: depthValueMax },
            { size: this._wallHeight, operator: heightOperator, value: heightValue, max: heightValueMax }
        ];

        for( let i = 0; i < sizes.length; i++ ) {
            if( ! helpers.isValidSize( sizes[i]['operator'], sizes[i]['size'], sizes[i]['value'], sizes[i]['max'] ) ) {
                return false;
            }
        }

        return true;
    }

    isValidComponentBySidingMaterial = ( component ) => {
        const materials = component['siding_material'];
        if( 0 === materials.length ) {
            return true;
        }

        return -1 !== materials.indexOf( this.getSidingType() );
    }

    handleLayoutComponent = async () => {
        const _this = this;
        const horizontalOffset = this.getHorizontalOffset();
        const enableDistributed = parseInt( this._canvas.getAttribute( 'data-horizontal-distributed' ) );
        
        if( ! _this._defaultLayoutLoaded ) {
            return _this.placeComponentBlockInLayout( horizontalOffset, enableDistributed );
        }

        const allLayoutComponents = _this.restructureLayoutComponents();
        if( allLayoutComponents.length <= 0 ) {
            return;
        }

        if( _this._isUpdateLayout ) {
            return _this.placeComponentBlockInLayout( horizontalOffset, enableDistributed );
        }

      
        let promises            = [];
        let components          = [];

        for( const layoutComponent of allLayoutComponents ) {
            const behavior = this.getBehavior( layoutComponent['component'], layoutComponent['type'] );
            if( -1 === layoutTypes.indexOf( behavior ) ) {
                continue;
            }

            if( _this.isCarport() && ! _this.isFullEnclosedWall( layoutComponent['side'] ) ) {
                continue;
            }

            if( ! _this.isAllowAddComponent( layoutComponent['side'], layoutComponent['component'] ) ) {
                continue;
            }

            const componentInfo = _this.getComponentInfoById( layoutComponent['id'], layoutComponent['side'] );
            const componentKey  = componentInfo ? componentInfo['key'] : undefined;
            if( _this.isValidComponentByLayoutCondition( layoutComponent ) ) {
                if( undefined === componentKey ) {
                    components.push( layoutComponent );
                    promises.push( _this.addNewLayoutComponent( layoutComponent, horizontalOffset, enableDistributed ) );
                } else {
                    // Update component position follow layout settings
                    const isFloor = 'floor' === layoutComponent['side'];
                    let side = layoutComponent['side'];
                    let vertical = { distance: layoutComponent['vertical_distance'], measured_from: layoutComponent['vertical_measured_from'] }
                    let horizontal = { distance: layoutComponent['horizontal_distance'] + horizontalOffset, measured_from: layoutComponent['horizontal_measured_from'], roof_top_aligned: layoutComponent['roof_top_aligned'] }
                    if( isFloor ) {
                        const horizontalDistance = helpers.toFixed3( Number( layoutComponent['side_floor_horizontal_distance'] ) / 12 );
                        const positionType = 'left' === layoutComponent['side_floor_horizontal_anchor'] || 'right' === layoutComponent['side_floor_horizontal_anchor'] ? 'manual' : 'default';
                        side = layoutComponent['side_floor_place_along_side'];
                        vertical = { distance: this.getPositionYForInterior( layoutComponent['type'], layoutComponent['component'] ), measured_from: 'ground' };
                        horizontal = { distance: horizontalDistance, measured_from: layoutComponent['side_floor_horizontal_anchor'], roof_top_aligned: positionType };
                    }
                    let position = _this.calculateComponentPosition( componentInfo['meshes'], side, vertical, horizontal, layoutComponent['type'], layoutComponent['component'] );

                    if( this.isBetaFeature( 'refactor_calc_layout_position' ) ) {
                        position = _this.calculateLayoutPosition( componentInfo['meshes'], side, vertical, horizontal, layoutComponent['type'], layoutComponent['component'] );
                    }

                    if( isFloor ) {
                        let offset  = ! isNaN( Number( layoutComponent['side_floor_place_along_side_offset'] ) ) && 0 !== Number( layoutComponent['side_floor_place_along_side_offset'] ) ? helpers.toFixed3( Number( layoutComponent['side_floor_place_along_side_offset'] ) / 12 ) : 0;
                        offset += componentInfo['componentDepth'] / 2 + constants.innerWallThickness;
                        position = this.getFloorComponentPositionByOffset( position, layoutComponent['side_floor_place_along_side'], offset );
                    }
                    
                    _this.placeComponent( componentInfo['meshes'], position, componentInfo['rotation'], layoutComponent['type'], layoutComponent['side'], layoutComponent['component'] );
                    _this.updateComponentsPosition2D( layoutComponent['side'], componentKey, position, componentInfo );
                }
            } else if( componentKey ) {
                promises.push( _this.deleteComponentV2( layoutComponent['side'], componentKey, layoutComponent['type'] ) );
            }
        }

        return Promise.all( promises ).then( () => {
            _this.addNewAddOn( components );
        } );
    }

    placeComponentBlockInLayout( horizontalOffset, enableDistributed ) {
        // TODO : hardcode for component type vent => in the feture has been work all type component 
        const _this = this;
        let allComponents:any = Object.entries( _this.getAllComponents() ).map( item => item[1] );
        allComponents = allComponents.filter( item => 'vent' === item.type && ( item.isBlock || ( item.blockOptions && -1 !== item.blockOptions.indexOf( 'move' ) ) ) );
        let layoutComponents:any = this.getLayoutComponents();
        let layoutVentComponents = layoutComponents['vent'];
        if( ! layoutVentComponents || 0 == allComponents.length ) return;
        layoutVentComponents = layoutVentComponents.filter( item => _this.isValidComponentByLayoutCondition( item ) && ( item.is_block || -1 !== item.block_options.indexOf( 'move' ) ) );

        for( const key in allComponents ) {
            const component = allComponents[key];
            if( layoutVentComponents.length ) {
                const findIndexAnchor = _this.findIndexAnchorComponentExits( { componentSide: component['side'], componentVertical: component.vertical, componentHorizontal: component.horizontal, layoutComponents: layoutVentComponents, enableDistributed, horizontalOffset } )
                if( -1 !== findIndexAnchor ) continue;
                const indexItemSameSide = layoutVentComponents.findIndex( item => item.side === component.side );
                if( -1 === indexItemSameSide ) continue;
                const componentInfo = layoutVentComponents[ indexItemSameSide ];
                const componentKey = component['key'];
                const componentName = componentInfo['component'];
                const meshes = component['meshes'];
                let { side, type, horizontal_distance, horizontal_measured_from, roof_top_aligned, vertical_distance, vertical_measured_from, is_block, block_options } = componentInfo;
                if( ( 'windows' === type || 'door' === type ) && 1 === enableDistributed ) {
                    horizontal_distance = componentInfo.order;
                    horizontal_measured_from = 'left';
                } else {
                    horizontal_distance += horizontalOffset;
                }
                let horizontal = { distance: horizontal_distance, measured_from: horizontal_measured_from, roof_top_aligned: roof_top_aligned };
                let vertical = { distance: vertical_distance, measured_from: vertical_measured_from };
                // const findItem = allComponents.findIndex( item => JSON.stringify( horizontal ) === JSON.stringify( item.horizontal ) && JSON.stringify( vertical ) === JSON.stringify( item.vertical ) );
                // if( -1 !== findItem ) continue;
                let position = _this.calculateComponentPosition( meshes, component['side'], vertical, horizontal, type, componentName );
                const componentWidth = _this.getComponentWidth( meshes, type, componentName, this.isMoveHorizontal( type ) );
                const componentHeight = _this.getComponentHeight( meshes, type );
                if( ! _this.isValidComponentPosition( side, position, componentWidth, componentHeight, meshes, type, componentName, componentKey ) ) {
                    if( ! _this.isValidComponentWithComponents( { side, position, meshes, type, componentName, componentKey } ) ) {
                        _this.notification( meshes, _this.NOTICES.cannotPlaceVent );
                    } else {
                        _this.notification( meshes, _this.NOTICES.itemNotFit );
                    }
                    _this.deleteComponentV2( side, componentKey, type );
                } else {
                    _this.placeComponent( meshes, position, component['rotation'], component['type'], component['side'], componentName );
                    _this.updateComponentsPosition2D( side, componentKey, position, component );
                    if( this._allComponents[ componentKey ] ) {
                        this._allComponents[ componentKey ].vertical = vertical;
                        this._allComponents[ componentKey ].horizontal = horizontal;
                        this._allComponents[ componentKey ].position = position;
                    }
                    if( _this._walls[side]['items'][componentKey] ) {
                        _this._walls[side]['items'][componentKey]['position'] = position;
                        _this._walls[side]['items'][componentKey]['vertical'] = vertical;
                        _this._walls[side]['items'][componentKey]['horizontal'] = horizontal;
                    }

                    layoutVentComponents.splice( indexItemSameSide, 1 );
                }
            }
        }
    }

    findIndexAnchorComponentExits = ( params ) => {
        const { componentSide, componentVertical, componentHorizontal, layoutComponents, enableDistributed, horizontalOffset } = params;
        for( let i = 0; i < layoutComponents.length; i++ ) {
            const componentInfo = layoutComponents[i];
            let { side, type, horizontal_distance, horizontal_measured_from, roof_top_aligned, vertical_distance, vertical_measured_from, is_block, block_options } = layoutComponents[i];
            if( side !== componentSide ) continue;
            if( ( 'windows' === type || 'door' === type ) && 1 === enableDistributed ) {
                horizontal_distance = componentInfo.order;
                horizontal_measured_from = 'left';
            } else {
                horizontal_distance += horizontalOffset;
            }

            let horizontal = { distance: horizontal_distance, measured_from: horizontal_measured_from, roof_top_aligned: roof_top_aligned };
            let vertical = { distance: vertical_distance, measured_from: vertical_measured_from };

            if( JSON.stringify( componentHorizontal ) === JSON.stringify( horizontal ) && JSON.stringify( componentVertical ) === JSON.stringify( vertical ) ) {
                return i;
            }
        }

        return -1;
    }


    handleLayoutEPackageComponent = async () =>{
        const _this = this;
        
        if( _this._isUpdateLayoutEPackage || _this._isShareLink ) {
            return;
        }

        const defaultLayoutEPackageComponents = this.getDefaultPackageComponents();
        for( const layoutEComponent of defaultLayoutEPackageComponents ) {
            const componentInfo = _this.getComponentInfoById( layoutEComponent['id'], layoutEComponent['side'] );
            const componentKey  = componentInfo ? componentInfo['key'] : undefined;
            if( _this.isValidComponentBySizeCondition( layoutEComponent ) ) {
                if( undefined === componentKey ) {
                    _this.addNewLayoutComponent( layoutEComponent, 0, 0 );
                }
            } else if( componentKey ) {
                _this.deleteComponentV2( layoutEComponent['side'], componentKey, layoutEComponent['type'] );
            }
        }
    }

    getComponentInfoById = ( id, side ) => {
        const _this = this;
        for( const key in _this._walls[side]["items"] ) {
            const components = this._walls[side]["items"][key];
            if( id === components.id ) {
                return components;
            }
        }

        return undefined;
    }

    addNewLayoutComponent = ( component, horizontalOffset, enableDistributed ) => {
        let promises = [];
        let { side, type, horizontal_distance, horizontal_measured_from, roof_top_aligned, vertical_distance, vertical_measured_from, addon_price, lock_data, size, is_block, colorInfo, side_floor_place_along_side, side_floor_place_along_side_offset, side_floor_horizontal_anchor, side_floor_horizontal_distance, block_options } = component;
        if( ( 'windows' === type || 'door' === type ) && 1 === enableDistributed ) {
            horizontal_distance         = component.order;
            horizontal_measured_from    = 'left';
        } else {
            horizontal_distance += horizontalOffset;
        }

        const horizontal        = { distance: horizontal_distance, measured_from: horizontal_measured_from, roof_top_aligned: roof_top_aligned }
        const vertical          = { distance: vertical_distance, measured_from: vertical_measured_from }
        const sidingType        = jQuery( `.ssb-configurator-siding-type li.active` ).data( 'siding-type' );
        let componentName       = component.component;
        let isUpdateComponent   = false;
        if( 'door' === type || 'windows' === type || 'garage_door' === type || 'vent' === type || 'dormer' === type ) {
            const newComponentName = this.checkAndReplaceComponent( sidingType, type, component.component, side );
            if( componentName !== newComponentName ) {
                this._isUpdateLayout = true;
                isUpdateComponent = true;
                componentName = newComponentName;
                colorInfo = helpers.getDefaultComponentColor( newComponentName );
            }
        }
    
        if( false === componentName ) {
            return;
        }

        const lockData  = helpers.getDefaultLock( type, componentName );
        const args      = { side, componentType: type, componentName, vertical, horizontal, isChangeComponent: 'default', id: component['id'], order: component['order'], lockData, componentSize: Math.round( size ), isUpdateComponent, isBlock: is_block, blockOptions: block_options, colorInfo, side_floor_place_along_side, side_floor_place_along_side_offset, side_floor_horizontal_anchor, side_floor_horizontal_distance };
        const result    = this.addNewComponent( args );

        window.dispatchEvent( new CustomEvent( 'update-shed-info' ) );

        return result;
    }

    getComponentChildByParentKey = ( parentKey, side ) => {
        const components    = this._walls[side]["items"];
        const shutters      = this.getShuttersInObjectBySide( side );
        const allComponents = { ...components, ...shutters };
        let results         = [];
        for( const key in allComponents ) {
            const component = allComponents[key];
            if( parentKey === component.parent ) {
                results.push( component );
            }
        }
    
        return results;
    }

    includePrice = () => {
        const selectedLayout = jQuery(`#ssb-configurator-layout-option input[name='ssb-configurator-layout']:checked`);

        return '1' === selectedLayout.attr('data-layout-include-price');
    }

    updateIncludeComponents = () => {
        const _this                 = this;
        const components            = _this._defaultLayoutComponents;
        _this._includeAddOns        = [];
        _this._includeComponents    = [];
        const isIncludeComponent    = _this.includePrice()
        for( const type in components ) {
            for( const component of components[type] ) {
                if( ! _this.isValidComponentByLayoutCondition( component ) ) {
                    continue;
                }

                if( isIncludeComponent ) {
                    _this._includeComponents.push( component['component'] );
                } else if( component['is_include'] ) {
                    _this._includeComponents.push( component['component'] );
                }
    
                if( component['addon_price'] ) {
                    if( 'windows' === component['type'] ) {
                        if( component['shutters_name'] && _this.isExistAddon( component['shutters_name'], component['component'], 'shutters' ) ) {
                            _this._includeAddOns.push( component['shutters_name'] );
                        }

                        if( component['flower_box_name'] ) {
                            _this._includeAddOns.push( component['flower_box_name'] );
                        }
                    } else if( 'door' === component['type'] && component['ramp_name'] ) {
                        _this._includeAddOns.push( component['ramp_name'] );
                    }
                }
            }
        }

        /**update package item include default */
        const ePackageComponents = this.getDefaultPackageComponents();
        _this._defaultEPackageComponents = ePackageComponents.filter( item => _this.isValidComponentBySizeCondition( item ) );
        _this.getComponentPackageInfo();
    }

    isValidSidingMaterialCondition = ( materials, type = '' ) => {
        const { sidingType: wainscotingType } = this.getSidingMaterialData( jQuery( '#wainscoting-material-option li.active:not(.unavailable)' ) );
        const dataType = 'wainscoting' === type ? wainscotingType : this.getSidingType();

        if( 'wainscoting' === type && 0 === materials.length ) {
            return false;
        }
        
        if( 0 === materials.length ) {
            return true;
        }

        return -1 !== materials.indexOf( dataType );
    }

    getOverhangSetting = () => {
        const selectedLayout    = jQuery(`#ssb-configurator-layout-option input[name='ssb-configurator-layout']:checked`);
        let overhangsetting     = selectedLayout.attr('data-layout-overhang');
        overhangsetting         = JSON.parse( overhangsetting );
        const wallWidth         = this.getWidth();

        for( let i = 0; i < overhangsetting.length; i++ ) {
            let sizes       = overhangsetting[i]['overhang_size_condition'];
            let materials   = overhangsetting[i]['overhang_siding_material'];
            if( '' !== materials ) {
                materials   = window.atob( materials );
                materials   = JSON.parse( materials );
            } else {
                materials = [];
            }

            if( helpers.isValidSize( sizes['operator'], wallWidth, sizes['value'], sizes['max'] ) && this.isValidSidingMaterialCondition( materials ) ) {
                return {
                    front:  overhangsetting[i]['front'],
                    back:   overhangsetting[i]['back'],
                    left:   overhangsetting[i]['left'],
                    right:  overhangsetting[i]['right'],
                };
            }
        }

        return {
            front:  0,
            back:   0,
            left:   0,
            right:  0
        };
    }

    /**
     * Get porch width for Deluxe porch
     * 
     * @returns {Number}
     */
    deluxePorchGetPorchWidth = () => {
        const porchWidth = jQuery('input.ssb_porch:checked').data('porch-size') || 4;

        return Number( porchWidth );
    }

    /**
     * Get front porch width for Deluxe porch
     * 
     * @returns {Number}
     */
    deluxePorchGetFrontWidth = () => {
        const porchWidth    = this.deluxePorchGetPorchWidth();
        const width         = this.getWidth();

        return width - porchWidth - constants.deluxeDepth;
    }

    /**
     * Move bones for Deluxe porch model
     * 
     * @param {Number} wallHeight       New wall height
     * @param {Number} buildingHeight   New build height
     * @param {Number} depth            New depth
     */
    deluxePorchMoveBones = ( wallHeight, buildingHeight, depth ) => {
        if( ! this.isDeluxeCornerLeftPorch() ) {
            return;
        }

        let bones           = this._bones;
        const porchWidth    = this.deluxePorchGetPorchWidth();
        if( bones['b_b_r3'] ) bones['b_b_r3'].setPosition( new Vector3( bones['b_b_r1'].position.x - porchWidth, 0, bones['b_b_l1'].position.z ) );
        if( bones['b_b_l3'] ) bones['b_b_l3'].setPosition( new Vector3( bones['b_b_r3'].position.x - constants.deluxeDepth, 0, bones['b_b_l1'].position.z ) );

        if( bones['b_p_r1'] ) bones['b_p_r1'].setPosition( new Vector3( bones['b_b_r1'].position.x, bones['b_b_r1'].position.y, - bones['b_b_r1'].position.z ) );
        if( bones['b_p_r3'] ) bones['b_p_r3'].setPosition( new Vector3( bones['b_p_r1'].position.x - porchWidth, bones['b_b_r3'].position.y, - bones['b_b_r3'].position.z ) );
        if( bones['b_p_l3'] ) bones['b_p_l3'].setPosition( new Vector3( bones['b_p_r3'].position.x - constants.deluxeDepth, bones['b_b_l3'].position.y, - bones['b_b_l3'].position.z ) );
        if( bones['b_p_l1'] ) bones['b_p_l1'].setPosition( new Vector3( bones['b_b_l1'].position.x, bones['b_b_l1'].position.y, - bones['b_b_l1'].position.z ) );

        if( bones['b_p_r2'] ) bones['b_p_r2'].setPosition( new Vector3( bones['b_p_r1'].position.x, wallHeight, bones['b_p_r1'].position.z ) );
        if( bones['b_p_r4'] ) bones['b_p_r4'].setPosition( new Vector3( bones['b_p_r3'].position.x, wallHeight, bones['b_p_r3'].position.z ) );
        if( bones['b_p_l4'] ) bones['b_p_l4'].setPosition( new Vector3( bones['b_p_l3'].position.x, wallHeight, bones['b_p_l3'].position.z ) );
        if( bones['b_p_l2'] ) bones['b_p_l2'].setPosition( new Vector3( bones['b_b_l2'].position.x, bones['b_b_l2'].position.y, - bones['b_b_l2'].position.z ) );
        if( bones['b_p_t'] ) bones['b_p_t'].setPosition( new Vector3( bones['b_p_t'].position.x, buildingHeight, depth ) );

        if( bones['b_f_r1'] ) bones['b_f_r1'].setPosition( new Vector3( bones['b_p_r1'].position.x, bones['b_p_r1'].position.y, bones['b_p_r1'].position.z - ( 3 * constants.deluxeDepth ) ) );
        if( bones['b_f_r4'] ) bones['b_f_r4'].setPosition( new Vector3( bones['b_p_r3'].position.x, bones['b_p_r3'].position.y, bones['b_f_r1'].position.z ) );
        if( bones['b_f_r6'] ) bones['b_f_r6'].setPosition( new Vector3( bones['b_p_r3'].position.x, bones['b_p_r3'].position.y, bones['b_f_r4'].position.z + constants.deluxeDepth ) );
        if( bones['b_f_l4'] ) bones['b_f_l4'].setPosition( new Vector3( bones['b_f_r6'].position.x - constants.deluxeDepth, bones['b_f_r6'].position.y, bones['b_f_r6'].position.z + constants.deluxeDepth ) );
        if( bones['b_f_l1'] ) bones['b_f_l1'].setPosition( new Vector3( bones['b_p_l1'].position.x, bones['b_p_l1'].position.y, bones['b_p_l1'].position.z - constants.deluxeDepth ) );

        if( bones['b_f_r2'] ) bones['b_f_r2'].setPosition(new Vector3( bones['b_f_r1'].position.x, wallHeight, bones['b_f_r1'].position.z ) );
        if( bones['b_f_r3'] ) bones['b_f_r3'].setPosition(new Vector3( bones['b_f_r4'].position.x, wallHeight, bones['b_f_r4'].position.z ) );
        if( bones['b_f_r5'] ) bones['b_f_r5'].setPosition(new Vector3( bones['b_f_r6'].position.x, wallHeight, bones['b_f_r6'].position.z ) );
        if( bones['b_f_l3'] ) bones['b_f_l3'].setPosition(new Vector3( bones['b_f_l4'].position.x, wallHeight, bones['b_f_l4'].position.z ) );
        if( bones['b_f_l2'] ) bones['b_f_l2'].setPosition(new Vector3( bones['b_f_l1'].position.x, wallHeight, bones['b_f_l1'].position.z ) );

        if( bones['column_b2'] ) bones['column_b2'].setPosition( new Vector3( bones['b_f_r1'].position.x, bones['b_f_r6'].position.y, bones['b_f_r6'].position.z) );
        if( bones['column_t2'] ) bones['column_t2'].setPosition( new Vector3( bones['b_f_r2'].position.x, bones['b_f_r5'].position.y, bones['b_f_r5'].position.z) );
        if( bones['column_b1'] ) bones['column_b1'].setPosition( new Vector3( bones['column_b1'].position.x, bones['b_p_l1'].position.y, bones['b_p_l1'].position.z) );
        if( bones['column_t1'] ) bones['column_t1'].setPosition( new Vector3( bones['column_t1'].position.x, bones['b_p_l2'].position.y, bones['b_p_l2'].position.z) );

        if( bones['rail_v_r_b_l1'] ) bones['rail_v_r_b_l1'].setPosition( new Vector3( bones['b_f_r1'].position.x, bones['rail_v_r_b_l1'].position.y, bones['b_f_r1'].position.z) );
        if( bones['rail_v_r_f_l1'] ) bones['rail_v_r_f_l1'].setPosition( new Vector3( bones['b_f_r1'].position.x, bones['rail_v_r_f_l1'].position.y, bones['b_f_r6'].position.z) );
        if( bones['rail_h_l_f_l1'] ) bones['rail_h_l_f_l1'].setPosition( new Vector3( bones['b_p_l1'].position.x, bones['rail_h_l_f_l1'].position.y, bones['b_p_l1'].position.z) );
        if( bones['rail_h_l_f_l2'] ) bones['rail_h_l_f_l2'].setPosition( new Vector3( bones['column_b1'].position.x, bones['rail_h_l_f_l2'].position.y, bones['column_b1'].position.z) );
        if( bones['rail_v_l_b_l1'] ) bones['rail_v_l_b_l1'].setPosition( new Vector3( bones['b_f_l1'].position.x, bones['rail_v_l_b_l1'].position.y, bones['b_f_l1'].position.z) );
        if( bones['rail_v_l_f_l1'] ) bones['rail_v_l_f_l1'].setPosition( new Vector3( bones['b_p_l1'].position.x, bones['rail_v_l_f_l1'].position.y, bones['b_p_l1'].position.z) );
        if( bones['baluster_f_l1'] ) bones['baluster_f_l1'].setPosition( new Vector3( 0, bones['baluster_f_l1'].position.y, bones['b_f_r1'].position.z ) );
    }

    deluxePorchGambrelMoveBones = ( bones, newRoofMidPointX, newRoofMidPointY, newDepth ) => {
        if( ! this.isDeluxeCornerLeftPorch() || 'structure_barn_deluxe_porch' !== this._shedType ) {
            return;
        }

        if( bones['b_b_l4'] ) bones['b_b_l4'].setPosition( new Vector3( - newRoofMidPointX, newRoofMidPointY, - newDepth ) );
        if( bones['b_b_r4'] ) bones['b_b_r4'].setPosition( new Vector3( newRoofMidPointX, newRoofMidPointY, - newDepth ) );
        
        if( bones['b_p_l5'] ) bones['b_p_l5'].setPosition( new Vector3( - newRoofMidPointX, newRoofMidPointY, newDepth ) );
        if( bones['b_p_r5'] ) bones['b_p_r5'].setPosition( new Vector3( newRoofMidPointX, newRoofMidPointY, newDepth ) );
    }

    /**
     * 
     * @param {object} boneUpper 
     * @param {object} boneLower 
     * @param {number} pointY
     * @param {number} componentWidth 
     * @param {string} vector x|z Use get position bone
     * @returns {number} limitSide
     */
    roofLimitComponent = ( boneUpper, boneLower, pointY, componentWidth, vector, errorSize = 0 ) => {
        let limitSide = 0;
        if( boneUpper && boneLower && pointY < boneUpper.position.y && pointY > boneLower.position.y ) {
            let distanceU   = 'x' === vector ? Math.abs( boneLower.position.x ) -  Math.abs( boneUpper.position.x ) : Math.abs( boneLower.position.z ) -  Math.abs( boneUpper.position.z );
            let limitMax    = 'x' === vector ? Math.abs( boneLower.position.x ) : Math.abs( boneLower.position.z );
            let distanceV   =  Math.abs( boneUpper.position.y - boneLower.position.y );
            let tanRoof     = distanceV / distanceU;
            let upperHeight = pointY - boneLower.position.y;
            if( errorSize >= 0 ) {
                limitSide = ( limitMax - errorSize / tanRoof ) - ( upperHeight / tanRoof ) - ( componentWidth / 2 );
            } else {
                limitSide = limitMax - ( ( upperHeight + errorSize ) / tanRoof ) - ( componentWidth / 2 );
            }
        }

        if( 0 >= limitSide ) {
            return 0;
        }

        return Number( limitSide.toFixed( 3 ) );
    }

    /**
     * Check is Side move component by position Z
     * @param side 
     * @returns {Bolean}
     */
    getMoveComponentByZ = side => {
        const sides = [ 'left', 'right', 'inner_left', 'inner_right', 'left_front', 'left_back', 'front_inner_porch', 'lean_to_right', 'top_left', 'top_right' ];
        return -1 !== sides.indexOf( side );
    }

    /**
    * Get Side Height Top
    * @param side 
    * @returns {Number}
    */
    getSideTop = side => {
        const roofShape     = this.getRoofShape();
        const wallHeight    = this.getWallHeight();
        const porchBottom   = this.getPorchBottom();
        const modelheight   = this._bones['b_b_t'] ? this._bones['b_b_t'].position.y : this.getBuildingHeight();

        const sides = ['left', 'right', 'inner_left', 'inner_front', 'inner_right', 'front_inner_porch', 'left_front', 'left_back', 'left_inner_porch', 'front_inner_porch', 'right_inner_porch', 'diagonal_right', 'inner_back' ];
        let sideTop = -1 !== sides.indexOf( side ) ? this.getWallHeight() : modelheight;
        
        if( 'front' === side && this.isFrontPorch() && this.isCellingPorch() ) {
            sideTop = this.isPorchHasTrim() ? porchBottom - this._trimLength : porchBottom;
            if( 'standard_cottage_front_porch' === this._shedType ) {
                sideTop = wallHeight;
            }
        }

        if( 'hip' === roofShape || 'flat' === roofShape ) {
            sideTop = wallHeight;
        }

        if( 'left' === side && this._bones['b_d_t'] ) {
            sideTop = this._bones['b_d_t'].position.y;
        }

        if( this.isDeluxeCornerLeftPorch() && ( 'front' === side || 'diagonal_right' === side ) ) {
            sideTop = wallHeight;
        }

        if( this.is2StoryLeanTo() ) {
            if( 'front' === side || 'back' === side ) {
                sideTop = wallHeight;
            } else if( 'top_left' === side || 'top_right' === side ) {
                sideTop = this._bones['b_b_r2'].position.y;
            } else if( 'lean_to_right' === side ) {
                sideTop = this._bones['lean_to_b_r3'].position.y;
            }
        }

        return sideTop;
    }

    /**
     * Side Wall info
     * @param side 
     * @returns {Object}
     */
    getSideInfo = side => {
        const modelWidth    = this.getWidth();

        let sideMoveBy      = this.getMoveComponentByZ( side ) ? 'z' : 'x';
        let sideLimitRight  = this.getMoveComponentByZ( side ) ? Math.abs( this._bones['b_b_l1'].position.z ) : Math.abs( this._bones['b_b_l1'].position.x );
        let sideLimitLeft   = - sideLimitRight;
        let sideBottom      = 'porch' === side ? this.getPorchBottom() : 0;
        let sideTop         = this.getSideTop( side );
        let anchorPosition  = 0;

        if( this.getMoveComponentByZ( side ) ) {
            anchorPosition = 'right' === side ? modelWidth / 2 : - modelWidth / 2;
        } else {
            anchorPosition = 'back' === side ? this._bones['b_b_l1'].position.z : this._bones['b_f_l1'].position.z;
        }

        if( this.isSidePorch() && 'left' === side ) {
            anchorPosition = - ( modelWidth / 2 - this._porchWidth );
        }

        if( this.isCornerPorchLeft() ) {
            switch ( side ) {
                case 'inner_front':
                    sideLimitLeft   = this._bones['b_f_l1'].position.x;
                    sideLimitRight  = this._bones['b_p_l1'].position.x;
                    anchorPosition  = this._bones['b_p_l1'].position.z;
                    break;
                case 'inner_left':
                    sideLimitLeft   = this._bones['b_f_l1'].position.z;
                    sideLimitRight  = this._bones['b_p_l1'].position.z;
                    anchorPosition  = this._bones['b_f_l1'].position.x;
                    break;
                case 'front':
                    sideLimitLeft   = this._bones['b_f_r1'].position.x;
                    sideLimitRight  = this._bones['b_b_l1'].position.x;
                    anchorPosition  = this._bones['b_f_l1'].position.z;
                    break;
                case 'left':
                    sideLimitLeft   = this._bones['b_p_l1'].position.z;
                    sideLimitRight  = this._bones['b_b_l1'].position.z;
                    anchorPosition  = this._bones['b_b_l1'].position.x;
                    break;
            }
        }

        if( this.isMiddlePorch() ) {
            switch ( side ) {
                case 'left_front':
                    sideLimitLeft   = this._bones['b_f_l1'].position.z - this._offsetDistance;
                    sideLimitRight  = constants.middleWidth / 2 + this._offsetDistance;
                    anchorPosition  = this._bones['b_b_l1'].position.x;
                    break;
                case 'left_back':
                    sideLimitLeft   = - constants.middleWidth / 2 - this._offsetDistance;
                    sideLimitRight  = this._bones['b_b_l1'].position.z + this._offsetDistance;
                    anchorPosition  = this._bones['b_b_l1'].position.x;
                    break;
                case 'right_inner_porch':
                    sideLimitLeft   = - ( modelWidth / 2 - constants.middleDepth ) - this._offsetDistance;
                    sideLimitRight  = - modelWidth / 2 + this._offsetDistance;
                    anchorPosition  = - constants.middleWidth / 2;
                    break;
                case 'front_inner_porch':
                    sideLimitLeft   = constants.middleWidth / 2 - this._offsetDistance;
                    sideLimitRight  = - constants.middleWidth / 2 + this._offsetDistance;
                    anchorPosition          = - ( modelWidth / 2 - constants.middleDepth );
                    break;
                case 'left_inner_porch':
                    sideLimitLeft   = - modelWidth / 2 + this._offsetDistance;
                    sideLimitRight  = - ( modelWidth / 2 - constants.middleDepth ) - this._offsetDistance;
                    anchorPosition  = constants.middleWidth / 2;
                    break;
            }
        }

        if( 'structure_ranch_cabin' === this._shedType || this.isAframeSingleGable() ) {
            switch ( side ) {
                case 'inner_back':
                    sideTop         -= this._offsetDistance;
                    sideLimitLeft   = - modelWidth / 2 + this._offsetDistance;
                    sideLimitRight  = - ( modelWidth / 2 - constants.cabinPorchDepth ) - this._offsetDistance;
                    anchorPosition  = this._bones['b_p_l1'].position.z;
                    break;
                case 'inner_left':
                    sideTop         -= this._offsetDistance;
                    sideLimitLeft   = this._bones['b_b_m4'].position.z;
                    sideLimitRight  = this._bones['b_b_l1'].position.z;
                    anchorPosition  = this._bones['b_b_m4'].position.x;
                    break;
                case 'left':
                    const topBone = this.isAframeSingleGable() ? 'b_f_t2' : 'b_m_t2';
                    sideTop         = this._bones[topBone].position.y;
                    sideLimitLeft   = this._bones['b_f_l1'].position.z;
                    sideLimitRight  = this._bones['b_p_l1'].position.z;
                    anchorPosition  = this._bones['b_f_l1'].position.x;
                    break;
            }
        }

        if( this.isDeluxeCornerLeftPorch() ) {
            switch ( side ) {
                case 'front':
                    sideLimitLeft   = this._bones['b_f_l4'].position.x - this._offsetDistance;
                    sideLimitRight  = this._bones['b_f_l1'].position.x + this._offsetDistance;
                    anchorPosition  = this._bones['b_f_l1'].position.z;
                    break;
                case 'inner_front':
                    sideLimitLeft   = this._bones['b_f_r1'].position.x - this._offsetDistance;
                    sideLimitRight  = this._bones['b_f_r4'].position.x + this._offsetDistance;
                    anchorPosition  = this._bones['b_f_r1'].position.z;
                    break;
                case 'inner_right':
                    sideLimitLeft   = this._bones['b_f_r4'].position.z;
                    sideLimitRight  = this._bones['b_f_r6'].position.z - this._offsetDistance;
                    anchorPosition  = this._bones['b_f_r6'].position.x;
                    break;
                case 'diagonal_right':
                    sideLimitLeft   = this._bones['b_f_r6'].position.z + this._offsetDistance;
                    sideLimitRight  = this._bones['b_f_l4'].position.z - this._offsetDistance;
                    anchorPosition  = ( this._bones['b_f_r6'].position.z + this._bones['b_f_l4'].position.z ) / 2;
                    break;
                case 'left':
                    sideLimitLeft   = this._bones['b_b_l1'].position.z + this._offsetDistance;
                    sideLimitRight  = this._bones['b_f_l1'].position.z - this._offsetDistance;
                    anchorPosition  = this._bones['b_f_l1'].position.x;
                    break;
                case 'right':
                    sideLimitLeft   = this._bones['b_b_r1'].position.z + this._offsetDistance;
                    sideLimitRight  = this._bones['b_f_r1'].position.z - this._offsetDistance;
                    anchorPosition  = this._bones['b_f_r1'].position.x;
                    break;
            }
        }
        
        if( 'saltbox' === this.getRoofShape() && ( 'front' === side || 'back' === side ) ) {
            sideLimitRight = sideLimitLeft = this._bones['b_b_t'].position.x;
        }

        if( 'structure_rancher' === this._shedType ) {
            sideTop = this._bones['b_b_l2'].position.y;
        }

        if( 'structure_alpinechalet' === this._shedType ) {
            sideTop = this.getWallHeight();
        }

        if( this.is2StoryLeanTo() ) {
            if( 'lean_to_right' === side ) {
                anchorPosition = this._bones['lean_to_b_r2'].position.x;
            } else if( 'top_right' === side ) {
                anchorPosition = modelWidth / 2;
            }
            
            if( 'top_left' === side || 'top_right' === side ) {
                sideBottom = this._bones['lean_to_f_r4'].position.y;
            }
        }
        
        return { 
            side,
            sideLimitLeft,
            sideLimitRight,
            sideBottom,
            sideTop,
            anchorPosition,
            sideMoveBy
        }
    };

    getComponentPositionForDiagonalRight = ( side, type, name ) => {
        const positionX     = ( this._bones['b_f_r6'].position.x + this._bones['b_f_l4'].position.x ) / 2;
        const positionZ     = ( this._bones['b_f_r6'].position.z + this._bones['b_f_l4'].position.z ) / 2;
        const modelHeight   = this.getWallHeight();

        let positionY = 0;
        if( 'door' === type ) {
            positionY = this.getDoorFixedHeight( side );
        } else if( 'windows' === type || 'vent' === type ) {
            positionY = modelHeight - 0.5;
        }else if( this.isHandleOffsetFloor( type ) ){
            positionY = this.getPositionYForInterior( type, name );
        }

        return { x: positionX, y: positionY, z: positionZ };
    }

    updateOldSize = ( oldSize ) => {
        oldSize.buildingHeight  = this.getBuildingHeight();

        if( this.isDeluxeCornerLeftPorch() ) {
            oldSize.frontWidth          = Vector3.Distance( this._bones['b_f_l4'].position, this._bones['b_f_l1'].position );
            oldSize.anchorFront         = this._bones['b_f_l4'].position.x;
            oldSize.innerFrontWidth     = Vector3.Distance( this._bones['b_f_r1'].position, this._bones['b_f_r4'].position );
            oldSize.anchorInnerFront    = this._bones['b_f_r1'].position.x;
            oldSize.anchorInnerRight    = this._bones['b_f_r4'].position.z;
            oldSize.leftWidth           = Vector3.Distance( this._bones['b_f_l1'].position, this._bones['b_b_l1'].position );
            oldSize.anchorLeft          = this._bones['b_f_l1'].position.z;
            oldSize.rightWidth          = Vector3.Distance( this._bones['b_b_r1'].position, this._bones['b_f_r1'].position );
            oldSize.anchorRight         = this._bones['b_b_r1'].position.z;
        } else if( this.isCornerPorchLeft() ) {
            oldSize.frontWidth          = Vector3.Distance( this._bones['b_f_r1'].position, this._bones['b_f_l1'].position );
            oldSize.anchorFront         = this._bones['b_f_r1'].position.x;
            oldSize.leftWidth           = Vector3.Distance( this._bones['b_p_l1'].position, this._bones['b_b_l1'].position );
            oldSize.anchorLeft          = this._bones['b_p_l1'].position.z;
            oldSize.innerLeftWidth      = Vector3.Distance( this._bones['b_f_l1'].position, this._bones['b_b_m4'].position );
            oldSize.anchorInnerLeft     = this._bones['b_f_l1'].position.z;
            oldSize.innerFrontWidth     = Vector3.Distance( this._bones['b_b_m4'].position, this._bones['b_p_l1'].position );
            oldSize.anchorInnerFront    = this._bones['b_b_m4'].position.x;
        } else if( this.isSidePorch() ) {
            oldSize.frontWidth          = Vector3.Distance( this._bones['b_f_r1'].position, this._bones['b_f_l1'].position );
            oldSize.anchorFront         = this._bones['b_f_r1'].position.x;
            oldSize.backWidth           = Vector3.Distance( this._bones['b_b_r1'].position, this._bones['b_b_l1'].position );
            oldSize.anchorBack          = this._bones['b_b_r1'].position.x;
        } else if( this.isAframeSingleGable() ) {
            oldSize.leftWidth           = Vector3.Distance( this._bones['b_f_l1'].position, this._bones['b_p_l1'].position );
            oldSize.anchorLeft          = this._bones['b_f_l1'].position.z;
            oldSize.innerLeftWidth      = Vector3.Distance( this._bones['b_p_l1'].position, this._bones['b_b_l1'].position );
            oldSize.anchorInnerLeft     = this._bones['b_p_l1'].position.z;
        }

        return oldSize;
    }

    getNewComponentPosition = ( args ) => {
        const { oldAnchor, newAnchor, oldWidth, newWidth, currentPosition, componentWidth, startPoint, endPoint, operator } = args;
        const oldDistance   = Math.abs( oldAnchor - currentPosition );
        const newDistance   = newWidth * oldDistance / oldWidth;
        let position        = eval( `newAnchor ${operator} newDistance` );

        if( position > startPoint - ( this._offsetDistance + componentWidth / 2 ) ) {
            position = startPoint - ( this._offsetDistance + componentWidth / 2 )
        } else if( position < endPoint + this._offsetDistance + componentWidth / 2 ) {
            position = endPoint + this._offsetDistance + componentWidth / 2;
        }

        return position;
    }
    
    /**
     * 
     * Get component on face
     * 
     * @param {string} componentType 
     * 
     * @returns {string} interior or exterior
     */
    getComponentFace = ( componentType ) => {
        return this.isInteriorComponent( componentType ) ? 'interior' : 'exterior';
    }

    /**
     * Check diagonal right side has component
     * 
     * @param side Front|Back|Left|Right|...
     * 
     * @returns {Boolean}
     */
    hasDiagonalRightComponent = ( side, type ) => {
        if( 'flower_box' === type ) return false;

        if( 'diagonal_right' !== side ) {
            return false;
        }

        const components  = this.getComponentBySideAndType( side, this.getComponentFace( type ) );

        return ! jQuery.isEmptyObject( components );
    }

    /**
     * Check diagonal right side has component
     * 
     * @param {String} side Side of model
     * @param {String} type Type of component
     * 
     * @returns {Boolean}
     */
    allowOneComponent = ( side : String, type : String ) : boolean => {

        if( 'structure_greenhouse_fiberglass' !== this._shedType || ( 'front' !== side && 'back' !== side ) ) {
            return false;
        }

        const components = this.getComponentBySideAndType( side, this.getComponentFace( type ) );

        return ! jQuery.isEmptyObject( components );
    }

    /**
     * Handle show|hide brace for deluxe porch
     */
    deluxePorchHandleBrace = () => {
        if( ! this.isDeluxeCornerLeftPorch() ) {
            return;
        }

        const braces = this.isEnableBrace() ? [ 'posts_obj', 'column_obj' ] : [ 'column_braces_obj', 'posts_braces_obj' ];
        braces.forEach( k => {
            const mesh = this.getMeshByName( k );
            if( mesh ) {
                mesh.dispose(); 
            }
        });
    }

    /**
    * Door's position (inches)
    * @returns {Number} Distance From Ground to Door
    */
    getDoorPositionFromGround = () => Number( jQuery( '#js-door-position-ground' ).data( 'door-position-ground' ) ) / 12;

    /**
     * Define door position from ground
     * @returns {Boolean} Apply this fixed height for all walls?
     */
    isApplyDoorPositionAllWall = () => 1 == jQuery( '#js-is-apply-all-door-on-side' ).data( 'is-apply-all-door-on-side' );

    /**
    * Door position on Porch sides (inches)
    * @returns {Number} Distance From Ground to Door on Porch Side
    */
    getDoorPositionFromGroundPorchSide = () => Number( jQuery( '#js-door-position-on-porch' ).data( 'door-position-on-porch' ) ) / 12;

    /**
     * is Porch Wall?
     * @param side front | back | left ...
     * @returns {Boolean} is Porch Wall?
     */
    isPorchWal = side => {
        if( this.isExternalPorch() && this._externalPorchLocation === side ) {
            return true;
        }

        if( this.isFrontPorch() && 'front' === side ) {
            return true;
        }
        
        if( this.isSidePorch() && 'structure_hiproof' !== this._shedType && 'left' === side ) {
            return true;
        }

        if( this.isCornerPorchLeft() ) {
            const sides = [ 'inner_left', 'inner_front' ];
            return -1 !== sides.indexOf( side );
        }

        if( this.isDeluxeCornerLeftPorch() ) {
            const sides = [ 'diagonal_right', 'inner_front', 'front', 'inner_right'  ];
            return -1 !== sides.indexOf( side );
        }

        if( this.isMiddlePorch() ) {
            const sides = [ 'left_inner_porch', 'right_inner_porch', 'front_inner_porch' ];
            return -1 !== sides.indexOf( side );
        }

        if( 'structure_ranch_cabin' === this._shedType || this.isAframeSingleGable() ) {
            const sides = [ 'inner_back', 'inner_left' ];
            return -1 !== sides.indexOf( side );
        }

        if( this.isDoubleGable() ) {
            const sides = [ 'left_inner_porch', 'right_inner_porch', 'front_inner_porch' ];
            return -1 !== sides.indexOf( side );
        }

        return false;
    }

    /**
     * Get Fixed height Door from Ground by Side
     * @param {String} side Side wall in model
     * @returns {Number}
     */
    getDoorFixedHeight = side => {
        const numberGround = this.getDoorPositionFromGround();
        if( this.isApplyDoorPositionAllWall() ) {
            return numberGround;
        }

        if( this.isPorchWal( side ) ) {
            return this.isExternalPorch() && ! this.isEnableExternalPorch() ? 0 : this.getDoorPositionFromGroundPorchSide();
        }

        return numberGround;
    }
    
    /**
     * Get limit upper vertical point
     * 
     * @returns {Number}
     */
    getLimitUpperVertical = () => {
        let limitUpperVertical = this._bones['b_f_l3'] ? this._bones['b_f_l3'].position.x : 0;
        if( 'structure_barn_deluxe_porch' === this._shedType ) {
            limitUpperVertical = this._bones['b_p_l5'] ? this._bones['b_p_l5'].position.x : limitUpperVertical;
        } else if( 'structure_old_west_modular' === this._shedType ) {
            limitUpperVertical = this._bones['b_f_l4'] ? this._bones['b_f_l4'].position.x : limitUpperVertical;
        } else if( 'structure_porch_barn' === this._shedType ) {
            limitUpperVertical = this._bones['b_f_l8'] ? this._bones['b_f_l8'].position.x : limitUpperVertical;
        }

        return limitUpperVertical;
    }

    setInitScaleForLogCabin = () => {
        if( 'structure_log_cabin' !== this._shedType ) {
            return;
        }

        const frontCloneObj = this.getMeshByName('log_cabin_f_clone_obj');
        const leftCloneObj  = this.getMeshByName('log_cabin_l_clone_obj');

        this._logCabinInitFBScale = frontCloneObj.scaling.x;
        this._logCabinInitLRScale = leftCloneObj.scaling.x;
    }

    logCabinCloneWall = () => {
        if( 'structure_log_cabin' !== this._shedType ) {
            return;
        }

        const width     = this.getWidth();
        const depth     = this.getDepth();
        const height    = this.getWallHeight();

        const frontCloneObj = this.getMeshByName('log_cabin_f_clone_obj');
        const backCloneObj  = this.getMeshByName('log_cabin_b_clone_obj');
        const leftCloneObj  = this.getMeshByName('log_cabin_l_clone_obj');
        const rightCloneObj = this.getMeshByName('log_cabin_r_clone_obj');

        this.removeClone( 'log_cabin_f_clone_obj' );
        this.removeClone( 'log_cabin_b_clone_obj' );
        this.removeClone( 'log_cabin_l_clone_obj' );
        this.removeClone( 'log_cabin_r_clone_obj' );

        frontCloneObj.isPickable    = false;
        backCloneObj.isPickable     = false;
        leftCloneObj.isPickable     = false;
        rightCloneObj.isPickable    = false;
        frontCloneObj.position.z    = depth / 2;
        backCloneObj.position.z     = - depth / 2;
        leftCloneObj.position.x     = - width / 2;
        rightCloneObj.position.x    = width / 2;

        const ratioFB1 = width / 8;
        const ratioFB2 = ( width + 2 ) / 8;
        const ratioLR1 = ( depth + 2 ) / 10;
        const ratioLR2 = depth / 10;

        frontCloneObj.scaling.x = ratioFB1 * this._logCabinInitFBScale;
        backCloneObj.scaling.x  = ratioFB1 * this._logCabinInitFBScale;

        leftCloneObj.scaling.x  = ratioLR1 * this._logCabinInitLRScale;
        rightCloneObj.scaling.x = ratioLR1 * this._logCabinInitLRScale;
        
        const logHeight     = 0.417;
        const numberClone   = Math.ceil( height / logHeight );
        for( let i = 1; i < numberClone; i++ ) {
            const frontLogClone      = frontCloneObj.createInstance( `log_cabin_f_clone_obj_${i}` );
            frontLogClone.isPickable = false;
            frontLogClone.position.y = frontLogClone.position.y + logHeight * i;
            if( i % 2 !== 0 ) {
                frontLogClone.scaling.x = ratioFB2 * this._logCabinInitFBScale;
            }

            const backLogClone      = backCloneObj.createInstance( `log_cabin_b_clone_obj_${i}` );
            backLogClone.isPickable = false;
            backLogClone.position.y = backLogClone.position.y + logHeight * i;
            if( i % 2 !== 0 ) {
                backLogClone.scaling.x = ratioFB2 * this._logCabinInitFBScale;
            }

            const leftLogClone      = leftCloneObj.createInstance( `log_cabin_l_clone_obj_${i}` );
            leftLogClone.isPickable = false;
            leftLogClone.position.y = leftLogClone.position.y + logHeight * i;
            if( i % 2 !== 0 ) {
                leftLogClone.scaling.x = ratioLR2 * this._logCabinInitLRScale;
            }
            
            const rightLogClone      = rightCloneObj.createInstance( `log_cabin_r_clone_obj_${i}` );
            rightLogClone.isPickable = false;
            rightLogClone.position.y = rightLogClone.position.y + logHeight * i;
            if( i % 2 !== 0 ) {
                rightLogClone.scaling.x = ratioLR2 * this._logCabinInitLRScale;
            }
        }
    }

    /**
     * 
     * @returns Enable Chiken Coop version
     */
    isChickenCoop = () => '1' === this._canvas.getAttribute('data-shed-chicken-coop') && ( 'open_gable' === this.getRoofShape() || 'box_gable' === this.getRoofShape() || 'saltbox_customize' === this.getRoofShape() );

    /**
     * Get limit when drag door component with component heigh highless wall height
     * @param positionY 
     * @param modelHeight 
     * @param modelWidth 
     * @returns Limit X of component
     */
    getEndXLimit = ( positionY, modelHeight, modelWidth ) => {
        if( positionY <= modelHeight ) {
            return {
                positiveLimit: modelWidth / 2,
                negativeLimit: - modelWidth / 2
            }
        }
        
        const buildHeight   = this.getBuildingHeight();
        let distance        = buildHeight - positionY;
        let roofDegree      = this._roofDegree;
        const roofStyle     = this.getRoofShape();
        let roofMidX        = 0;
        if( 'carport_standard_regularroof' === this._shedType ) {
            const x     = this._bones['b_b_t'].position.y - this._bones['b_b_l2'].position.y;
            roofDegree  = this.getRoofPitchFromRadian( x, this.getWidth() / 2 );
        }
        
        if( 'gambrel' === roofStyle || 'dutch_style' === roofStyle ) {
            if( positionY === this._roofMidPointY ) {
                return {
                    positiveLimit: Number( ( this._roofMidPointX ).toFixed( 3 ) ),
                    negativeLimit: - Number( ( this._roofMidPointX ).toFixed( 3 ) )
                }
            } else if( positionY > this._roofMidPointY ) {
                roofDegree  = this._roofUpperDegree ? this._roofUpperDegree : this._initRoofUpperDegree;
            } else {
                roofDegree  = this._roofLowerDegree ? this._roofLowerDegree : this._initRoofLowerDegree;
                distance    = this._roofMidPointY - positionY;
                roofMidX    = Number( ( this._roofMidPointX ).toFixed( 3 ) );
            }
        }

        let tanPositiveRoof = Number( helpers.getTanFromDegrees( roofDegree ).toFixed( 3 ) );
        let tanNegativeRoof = tanPositiveRoof;

        if( 'saltbox_customize' === roofStyle ) {
            const middleX = this._bones['b_f_t'].position.x;
            
            const lowerWallRoofPitch = Number( this.getSettingData( 'model_roof_angle' ) );
            const higherWallRoofPitch = this.getSettingData( 'model_second_roof_angle' ) ? Number( this.getSettingData( 'model_second_roof_angle' ) ) : lowerWallRoofPitch;
            const wallHeightDiff = this.getSettingData( 'slope_height_difference' ) ? Number( this.getSettingData( 'slope_height_difference' ) ) / 12 : 0;
            
            const lowerDistance = helpers.toFixed3( positionY - modelHeight );
            const higherDistance = helpers.toFixed3( positionY - (modelHeight + wallHeightDiff) );
            tanPositiveRoof = helpers.toFixed3( helpers.getTanFromDegrees( lowerWallRoofPitch ) );
            tanNegativeRoof = helpers.toFixed3( helpers.getTanFromDegrees( higherWallRoofPitch ) );

            const positiveLimit = helpers.toFixed3( modelWidth / 2 - Number( lowerDistance / tanPositiveRoof ) );
            const negativeLimit = - helpers.toFixed3( modelWidth / 2 - Number( higherDistance / tanNegativeRoof ) );
            return {
                positiveLimit: positiveLimit,
                negativeLimit: negativeLimit
            };
        }

        return {
            positiveLimit: Number( ( distance / tanPositiveRoof + roofMidX ).toFixed( 3 ) ),
            negativeLimit: - Number( ( distance / tanNegativeRoof + roofMidX ).toFixed( 3 ) )
        };
    }

    /**
     * Check Max Qty Electrical Package
     * 
     * @param name {String}
     */
    isMaxQty = ( name : string ) => {
        let max = 0;
        for( const component of this.getDefaultPackageComponents() ){
            if( component.component === name ){
                return component.max_qty;
            }
        }

        return max;
    }

    /**
     * Check a component key {parentKey} has child with {childType}
     * @param {string} side 
     * @param {string} parentKey 
     * @param {string} childType 
     * @returns {Boolean}
     */
    isChildComponent = ( side, parentKey, childType ) => {
        const components    = this._walls[side]["items"];
        const shutters      = this.getShuttersInObjectBySide( side );
        const allComponents = { ...components, ...shutters };
        for( const key in allComponents ) {
            const component = allComponents[key];
            if( component.type === childType && component.parent === parentKey ) {
                return true;
            }
        }

        return false;
    }

    /**
     * Get Child Component Height
     * @param side 
     * @param parentKey 
     * @param childType 
     * @returns {Number}
     */
    getChildSize = ( side, parentKey, childType ) => {
        let sideComponents = this._walls[side]["items"];

        let size = {
            width : 0,
            height : 0,
        }
        for( const cKey in sideComponents ) {
            let comp = sideComponents[cKey];
            if( comp.type === childType && comp.parent === parentKey ) {
                size.height =  this.getComponentHeight( comp.meshes, childType ) || 0;
                size.width =  this.getComponentWidth( comp.meshes, childType, comp.name ) || 0;
            }
        }

        return size;
    }

    isIntersectComponent = ( parent2D, child2D ) => {
        const { cFirstPointHorizontal, cLastPointHorizontal, cFirstPointHorizontalOld, cLastPointHorizontalOld }    = child2D.horizontal;
        const { cFirstPointVertical, cLastPointVertical, cFirstPointVerticalOld, cLastPointVerticalOld }            = child2D.vertical;
        const { firstPointHorizontal, lastPointHorizontal, firstPointHorizontalOld, lastPointHorizontalOld }        = parent2D.horizontal;
        const { firstPointVertical, lastPointVertical, firstPointVerticalOld, lastPointVerticalOld }                = parent2D.vertical;

        if( ! this.isValidHorizontal( firstPointHorizontal, lastPointHorizontal, firstPointHorizontalOld, lastPointHorizontalOld ) && ! this.isValidVertical( firstPointVertical, lastPointVertical, firstPointVerticalOld, lastPointVerticalOld ) )
            return true;
        if(  ! this.isValidHorizontal( cFirstPointHorizontal, cLastPointHorizontal, cFirstPointHorizontalOld, cLastPointHorizontalOld ) && ! this.isValidVertical( cFirstPointVertical, cLastPointVertical, cFirstPointVerticalOld, cLastPointVerticalOld ) )
            return true;
        if(  ! this.isValidHorizontal( cFirstPointHorizontal, cLastPointHorizontal, firstPointHorizontalOld, lastPointHorizontalOld ) && ! this.isValidVertical( cFirstPointVertical, cLastPointVertical, firstPointVerticalOld, lastPointVerticalOld ) )
            return true;
        if(  ! this.isValidHorizontal( firstPointHorizontal, lastPointHorizontal, cFirstPointHorizontalOld, cLastPointHorizontalOld ) && ! this.isValidVertical( firstPointVertical, lastPointVertical, cFirstPointVerticalOld, cLastPointVerticalOld ) )
            return true;

        return false;        
    }

    /**
     * Check is enable "Vertically Distribute Components On Porch Side"
     * 
     * @returns {Boolean}
     */
    isVerticalDistribute = () => {
        return 'true' === this._canvas.getAttribute( 'data-enable-vertical-distribute-porch-size' );
    }

    /**
     * Get porch wraparound siding size
     * 
     * @returns {Number}
     */
    getPorchWrapSiding = () => {
        return Number( this._canvas.getAttribute( 'data-porch-wraparound-siding' ) );
    }

    /**
     * Get porch bottom follow "Porch's wraparound siding" size in the settings
     * 
     * @returns {Number}
     */
    getPorchBottomFollowWrapSiding() {
        return this.getBuildingHeight() - this.getFullPorchHeight();
    }

    /**
     * Get porch height include "Porch's wraparound siding" size
     * 
     * @returns {Number}
     */
    getFullPorchHeight = () => {
        return this.getBuildingHeight() - this.getWallHeight() + this.getPorchWrapSiding();
    }

    /**
     * Calculate porch middle position for component follow vertical
     * 
     * @param {Number} componentHeight Height of component
     * @returns {Number}
     */
    calculateMiddlePosition = ( componentHeight ) => {
        const porchMiddleYPosition = this.getMiddleVerticalPosition();

        return helpers.toFixed3( porchMiddleYPosition + componentHeight / 2 );
    }

    /**
     * Get porch middle position follow vertical
     * 
     * @returns {Number}
     */
    getMiddleVerticalPosition = () => {
        const buildHeight       = this.getBuildingHeight();
        const bottomPosition    = this.getPorchBottomFollowWrapSiding();

        return ( bottomPosition + buildHeight ) / 2;
    }

    /**
     * Get limit Width for component on porch side
     * 
     * @param {Number} positionY        Component position Y
     * @param {Number} componentWidth   Width of component
     * @param {Number} componentHeight  Height of component
     * 
     * @returns {Number}
     */
    getLimitX = ( positionY, componentWidth, componentHeight ) => {
        const height            = this.getWallHeight();
        const width             = this.getWidth();
        const porchWrapSize     = this.getPorchWrapSiding();
        const haflCompHeight    = componentHeight / 2;
        const bottomPosition    = this.getPorchBottomFollowWrapSiding();
        const middlePosition    = this.getMiddleVerticalPosition();
        let roofDegree          = this._roofDegree;
        let tmpWidth            = width + 2 * porchWrapSize / helpers.getTanFromDegrees( roofDegree );
        let limitX              = 0;
        let distanceHeight      = positionY - bottomPosition;

        if( 'gambrel' === this.getRoofShape() ) {
            const topPosition   = middlePosition + haflCompHeight;
            if( topPosition === this._roofMidPointY ) {
                limitX          = this._roofMidPointX;
            } else if( topPosition > this._roofMidPointY ) {
                tmpWidth        = 2 * this._roofMidPointX;
                roofDegree      = this._roofUpperDegree ? this._roofUpperDegree : this._initRoofUpperDegree;
                distanceHeight  = topPosition - this._roofMidPointY;
            } else {
                roofDegree      = this._roofLowerDegree ? this._roofLowerDegree : this._initRoofLowerDegree;
                tmpWidth        = width + 2 * porchWrapSize / helpers.getTanFromDegrees( roofDegree );
            }
        }

        const tanRoof       = helpers.getTanFromDegrees( roofDegree );
        const distanceWidth = distanceHeight / tanRoof;
        limitX              = ( tmpWidth - ( 2 * distanceWidth ) ) / 2 - ( componentWidth / 2 );

        return limitX;
    }

    /**
     * Call API and get data structurer from network setting
     * 
     */
    getDataStructurerFromApi = () => {
        const shedWall = this.getSettingData( 'shed_wall' );

        if( ! shedWall.hasOwnProperty( 'wall' ) && ! shedWall.hasOwnProperty( 'roof_correction' ) && ! shedWall.hasOwnProperty( 'interior_wall_raw' ) ) {
            this._apiData['wall_raw'] = shedWall;
        } else {
            if( shedWall.hasOwnProperty( 'wall' ) ) {
                this._apiData['wall_raw'] = shedWall.wall;
            } 
            if( shedWall.hasOwnProperty( 'roof_correction' ) ) {
                this._apiData['roof_correction'] = shedWall.roof_correction;
            } 
            if( shedWall.hasOwnProperty( 'interior_wall' ) ) {
                this._apiData['interior_wall_raw'] = shedWall.interior_wall;
            } else {
                this._apiData['interior_wall_raw'] = [];
            }
        }
    }

    /**
     * Format data using for get wall info
     * 
     * @returns Wall Info
     */
    formatWallInfoFromAPI = ( data ) => {
        const _this = this;
        if( ! data ) return [];

        const wallInfo = helpers.filterUniqueItems( data, 'modelVersions', _this._shedTypeVersion );
        const walls    = [];
        wallInfo.forEach( wall => {
            const { side, position, name, bones, offset, oldName } = wall;
            const modelVersions = ( wall.hasOwnProperty( 'modelVersions' ) ) ? wall.modelVersions : [];
            const shape         = [];
            const trim          = this.getTrimByCondition( side );
            
            let parentMesh  = 'body_obj';
            const regex     = new RegExp( 'lean_to|porch' );
            const regResult = regex.exec( name );
            
            if( null !== regResult ) {
                parentMesh = `${regResult[0]}_obj`;
            }

            const isFloor   = 'floor' === wall.side;
            const isSoffit  = 'soffit_left' === wall.side || 'soffit_right' === wall.side;

            bones.forEach( bone => {
                const boneName = bone.name?.trim().toLowerCase();
                if( !boneName || !_this._bones[boneName] ) {
                    return;
                }

                const boneData = _this._bones[boneName];
                const offsetX  = bone.x !== null ? parseFloat( bone.x ) : 0;
                const offsetZ  = bone.y !== null ? parseFloat( bone.y ) : 0;
                let x = boneData.position.x;
                let z = isFloor ? boneData.position.z : boneData.position.y;

                if( !isFloor && - 1 !== zSide.indexOf( wall.side ) ) {
                    x = boneData.position.z;
                }
                
                if( isSoffit ) {
                    x = boneData.position.z;
                    z = boneData.position.x;
                }

                const defaultFloorOffset = 0.01;
                const defaultOffsetX     = isFloor && x > 0 ? - defaultFloorOffset : defaultFloorOffset;
                const defaultOffsetZ     = isFloor && z > 0 ? - defaultFloorOffset : defaultFloorOffset;

                shape.push( new Vector3(
                    x + ( isFloor ? defaultOffsetX : 0 ) + offsetX,
                    0,
                    z + ( isFloor ? defaultOffsetZ : 0 ) + offsetZ 
                ) );
            } );
            
            if ( shape && shape.length > 0 && ( ( 0 === modelVersions.length ) || helpers.hasKeyInArray( modelVersions, _this._shedTypeVersion ) ) ) {
                walls.push( { side, name, position, offset, oldName, bones: shape, materialName: `api_${name}_wall_mat`, parentMesh, trim } );
            }
        } );
        return walls;
    }

    /**
     * Get label for lock position
     * 
     * @param {String} componentName name of component
     * @param {String} lockPosition left|right
     * 
     * @returns {String||Undefined}
     */
    getLockPositionText = ( componentName, lockPosition ) => {
        return jQuery( `[data-upgrade-component-name='${componentName}']` ).siblings( '.upgrade-item-options' ).find( `.wrap-lock-setting .lock-setting[value="${lockPosition}"]` ).siblings( 'label' ).text();
    }

    /**
     * Get component Label from Dom
     * 
     * @param component
     */
    getComponentLabelIncludeAddOn = component => {
        const { name, type, side, key } = component;
        if( ! name ) return '';

        if( 'stair' === type ) return 'Stair';

        if( 'loft' === type || 'Opening' === name ) return name;

        let customName = jQuery( `[data-upgrade-component-name='${name}']` ).val();

        if( 'door' === type && this._walls[side]['items'][key]['customLock'] ) {
            customName += ' ' + this.getLockPositionText( name, this._walls[side]['items'][key]['customLock'] ).trim();
        }

        if( customName ) {
            return customName;
        }
        
        return name.split( '_' ).map( n => n.charAt( 0 ).toUpperCase() + n.slice( 1 ) ).join( ' ' );
    }
    
    initFrameSpacing = () => {
        let spacing = jQuery( 'input.ssb_frame_spacing:checked' ).val();
        if( undefined !== spacing ) {
            spacing = Number( spacing );
            if( spacing > 0 ) {
                return spacing;
            }
        }

        return this._legDistance;
    }

    /**
     * Load initial Legs
     */
    initLegs = () => {
        const legSelected = jQuery( 'input.ssb_legs_setting:checked' ).val() || 'standard_legs';
        this.updateLegs( legSelected );
    }

    /**
     * Hard code roof cap position
     * 
     * @returns {Number}
     */
    getRoofCapPosition = () => {
        const roofPitch     = jQuery( 'input.ssb_roof_pitch:checked' ).val();
        let roofCapPosition = 0.079;
        switch( roofPitch ) {
            // case "1":
            //     roofCapPosition = 0.147;
            //     break;
            // case "2":
            //     roofCapPosition = 0.115;
            //     break;
            case "3":
                roofCapPosition = 0.079;
                break;
            // case "4":
            //     roofCapPosition = 0.046;
            //     break;
            // case "5":
            //     roofCapPosition = 0.012;
            //     break;
            case "6":
                roofCapPosition = - 0.02;
                break;
            // case "7":
            //     roofCapPosition = - 0.051;
            //     break;
            // case "8":
            //     roofCapPosition = - 0.086;
            //     break;
            // case "9":
            //     roofCapPosition = - 0.121;
            //     break;
            // case "10":
            //     roofCapPosition = - 0.154;
            //     break;
            // case "11":
            //     roofCapPosition = - 0.187;
            //     break;
            // case "12":
            //     roofCapPosition = - 0.211;
            //     break;
            default:
                break;
        }

        return roofCapPosition;
    }

    /**
     * Hard code height for boxeave
     * 
     * @returns {Number}
     */
    getBoxEaveHeight = () => {
        const roofPitch     = jQuery( 'input.ssb_roof_pitch:checked' ).val();
        let boxEaveHeight   = 0.16;
        switch( roofPitch ) {
            case "3":
                boxEaveHeight = 0.125;
                break;
            case "6":
                boxEaveHeight = 0.16;
                break;
            default:
                break;
        }

        return boxEaveHeight;
    }

    /**
     * Check Sides & Ends is fully enclosed
     * 
     * @param side front|back|left|right
     * @returns {Boolean}
     */
    isFullEnclosedWall = ( side ) => {
        const wallHeight = 'left' === side && 'carport_lean_to_standalone' === this._shedType ? this.getBuildingHeight() : this.getWallHeight();
        if( 'front' === side || 'back' === side ) {
            const gableOption = jQuery(`.${side}-gable-end`).find('option:selected').data('gable-end');
            if( 'extended_top_gable' === gableOption ) {
                const wallOption        = Number( jQuery(`.${side}-gable-end`).find('option:selected').data('wall-height') );
                const measurementTypes  = jQuery(`.${side}-gable-end`).find('option:selected').data('measurement-types');
                if( 'percent' === measurementTypes && wallOption >= 100 ) {
                    return true;
                }
            }
        } else if( 'left' === side || 'right' === side ) {
            const wallOption        = Number( jQuery(`.${side}-sidewall`).find('option:selected').data('wall-height') );
            const measurementTypes  = jQuery(`.${side}-sidewall`).find('option:selected').data('measurement-types');
            if( 'percent' === measurementTypes && wallOption >= 100 ) {
                return true;
            }
        }

        return false;
    }

    /**
     * Handle change Legs
     * 
     * @param {String} legSelected standard|double_legs
     */
    updateLegs = ( legSelected, checkComponentPosition = false ) => {
        const frame                     = this.getMeshByName( 'frame_obj' );
        const frameClone                = this.getMeshByName( 'frame_clone_obj' );
        const framePostClone            = this.getMeshByName( 'frame_post_clone_obj' );
        const frameDoubleLegs           = this.getMeshByName( 'frame_double_legs_obj' );
        const frameDoubleLegsClone      = this.getMeshByName( 'frame_double_legs_clone_obj' );
        const frameDoubleLegsPotClone   = this.getMeshByName( 'frame_double_legs_post_clone_obj' );

        if( frame ) frame.setEnabled( false );
        if( frameClone ) frameClone.setEnabled( false );
        if( framePostClone ) framePostClone.setEnabled( false );
        if( frameDoubleLegs ) frameDoubleLegs.setEnabled( false );
        if( frameDoubleLegsClone ) frameDoubleLegsClone.setEnabled( false );
        if( frameDoubleLegsPotClone ) frameDoubleLegsPotClone.setEnabled( false );
        
        if( 'double_legs' === legSelected ) {
            if( frameDoubleLegs ) frameDoubleLegs.setEnabled( true );
            if( frameDoubleLegsClone ) frameDoubleLegsClone.setEnabled( true );
            if( frameDoubleLegsPotClone ) frameDoubleLegsPotClone.setEnabled( true );

            this.removeClone( 'frame_clone_obj' );
            this.cloneInnerFrame( 'frame_double_legs_clone_obj', 'horizontal' );
            this.carportCloneLeg( 'frame_double_legs_post_clone_obj' );
        } else {
            if( frame ) frame.setEnabled( true );
            if( frameClone ) frameClone.setEnabled( true );
            if( framePostClone ) framePostClone.setEnabled( true );

            this.removeClone( 'frame_double_legs_clone_obj' );
            this.cloneInnerFrame( 'frame_clone_obj', 'horizontal' );
            this.carportCloneLeg( 'frame_post_clone_obj' );
        }
    }

    getHorizontalOffset = ( type: string = '' ) => {
        if( this.isInteriorComponent( type ) || 'cupola' === type || 'dormer' === type ) {
            return 0;
        }

        return Number( this._canvas.getAttribute( 'data-horizontal-offset' ) );
    }

    isAllowAddComponent = ( side, componentName, componentType = '', sidingType = '' ) => {
        const preventData   = this.getPreventComponentData();
        const categories    = this.getComponentCategory( componentName );
        
        for( let i = 0; i < preventData.length; i++ ) {
            const conditions = preventData[i].condition;
            const components = preventData[i].data;
            for( let j = 0; j < components.length; j++ ) {
                const preventBy         = components[j].prevent_by;
                const preventItems      = '' === components[j].prevent_items ? [] : JSON.parse( atob( components[j].prevent_items ) );
                const preventCategory   = components[j].prevent_category;
                if( 'subcategory' === preventBy ) {
                    if( false !== categories && categories.category === preventCategory && -1 !== preventItems.indexOf( categories.sub_category ) && this.isPreventComponent( conditions, side ) ) {
                        return false;
                    }
                } else if( 'component' === preventBy && -1 !== preventItems.indexOf( componentName ) && this.isPreventComponent( conditions, side ) ) {
                    return false;
                }
            }
        }

        return true;
    }

    isPreventComponent = ( conditions, side ) => {
        for( let i = 0; i < conditions.length; i++ ) {
            if( ( 0 === conditions[i].side.length || helpers.hasKeyInArray( conditions[i].side, side ) )
                && helpers.isValidSize( conditions[i].width_condition.operator, helpers.getBaseWidth(), conditions[i].width_condition.min, conditions[i].width_condition.max )
                && helpers.isValidSize( conditions[i].length_condition.operator, helpers.getBaseDepth(), conditions[i].length_condition.min, conditions[i].length_condition.max )
                && helpers.isValidSize( conditions[i].height_condition.operator, helpers.getBaseHeight(), conditions[i].height_condition.min, conditions[i].height_condition.max )
            ) {
                return true;
            }
        }

        return false;
    }

    getComponentCategory = ( componentName ) => {
        try {
            const componentCategory = jQuery( `[data-upgrade-component-name='${componentName}']` ).data( 'component-category' );
            if( componentCategory ) {
                return JSON.parse( atob( componentCategory ) );
            }
        } catch( error ) {}

        return false;
    }

    getPreventComponentData = () => {
        return JSON.parse( atob( jQuery( '#js-data-restrict-component' ).data( 'restrict-component' ) ) );
    }

    /**
     * Get limit min spacing between components
     * 
     * @param {String} type Component type (door|windows|cupola|light,...)
     * @returns {Number}
     */
    getHorizontalSpacing = ( type: string = '' ) => {
        if( this.isInteriorComponent( type ) || 'cupola' === type || 'dormer' === type ) return 0;

        return Number( this._canvas.getAttribute( 'data-horizontal-spacing' ) );
    }

    /**
     * Set status to handle pickable for wrapper mesh of component
     *
     * @param {Mesh} firstMesh First mesh of component
     * @param {Boolean} status Status to pickable
     */
    setPickableForWrapperMesh = ( firstMesh: Mesh, status: boolean ) => {
        const childMeshes = firstMesh.getChildMeshes();
        childMeshes.forEach( m => {
            if( 'front' === m.name || 'back' === m.name ) {
                m.isPickable = status;
            }
        } );
    }

    /**
     * Get workbench height
     * 
     * @param {String} name workbench name
     * 
     * @returns {Object}
     */
    getWorkBenchInfo = ( name ) => {
        const box       = this.getMeshByName( `${name}_obj` );
        let distance    = 0;
        let height      = 0;
        if( box && box.geometry ) {
            distance    = helpers.toFixed3( box.geometry.extend.minimum.y );
            height      = helpers.toFixed3( box.geometry.extend.maximum.y - box.geometry.extend.minimum.y );
        }

        return { distance, height };
    }

    /**
     * Add wrapper for component to handle pickable
     *
     * @param {Array} meshes            List meshes of component
     * @param {String} type             Component type
     * @param {String} name             Component name
     * @param {Number} componentSize    Size of shelf and workbench
     */
    addWrapperObject = ( meshes, type, name, componentSize, disableMove = false ) => {
        if( disableMove ) return;
        
        const behavior  = this.getBehavior( name, type );
        const skips     = [ 'ramp', 'flower_box', 'shutters', 'dormer', 'cupola', 'light' ];
        if( -1 !== skips.indexOf( behavior ) ) {
            return;
        }

        let height          = 0;
        let distance        = 0;
        if( 'workbench' === behavior ) {
            const workbench = this.getWorkBenchInfo( name );
            distance        = workbench.distance;
            height          = workbench.height;
        } else {
            height          = this.getComponentHeight( meshes, behavior );
        }

        const width         = 'shelf' === type || 'workbench' === type ? componentSize : this.getComponentWidth( meshes, behavior, name );
        const box           = MeshBuilder.CreateBox( 'wrapper', { height, width, depth: 1 } );
        const front         = MeshBuilder.CreatePlane( 'front', { height, width } );
        const back          = MeshBuilder.CreatePlane( 'back', { height, width } );
        box.visibility      = 0;
        box.isPickable      = false;
        front.visibility    = 0;
        back.visibility     = 0;
        const firstMesh     = meshes[0];
        const position      = 0.001;

        box.setParent( firstMesh );
        front.setParent( firstMesh );
        back.setParent( firstMesh );

        switch( behavior ) {
            case 'shelf':
                box.position        = new Vector3( 0, height / 2 + distance, position );
                let minBox          = 0;
                let maxBox          = 0;
                const boundingBox   = this.filterByValue( meshes, 'name', '_bb_obj' ) as Mesh;
                if( boundingBox && boundingBox.geometry ) {
                    minBox = boundingBox.geometry.extend.minimum.y;
                    maxBox = boundingBox.geometry.extend.maximum.y;

                    const positionY = helpers.toFixed3( ( minBox + maxBox ) / 2 );
                    front.position  = new Vector3( 0, positionY, boundingBox.geometry.extend.maximum.z );
                    back.position   = new Vector3( 0, positionY, boundingBox.geometry.extend.maximum.z - position );
                } else {
                    front.position  = new Vector3( 0, height / 2 + distance, position );
                    back.position   = new Vector3( 0, height / 2 + distance, - position );
                }
                break;
            case 'door':
            case 'workbench':
                box.position    = new Vector3( 0, height / 2 + distance, position );
                front.position  = new Vector3( 0, height / 2 + distance, position );
                back.position   = new Vector3( 0, height / 2 + distance, - position );
                break;
            case 'skylight_window':
            case 'windows':
            case 'vent':
                box.position    = new Vector3( 0, - height / 2, position );
                front.position  = new Vector3( 0, - height / 2, position );
                back.position   = new Vector3( 0, - height / 2, - position );
                break;
            case 'outlet':
            case 'switch':
            case 'breakerbox':
                box.position    = new Vector3( 0, 0, position );
                front.position  = new Vector3( 0, 0, position );
                back.position   = new Vector3( 0, 0, - position );
                break;
        }
    }
    
    /**
     * Get position Y for first vent added to model
     * 
     * @param {Number} sideTop          The position to attach the vent
     * @param {Number} componentWidth   Width of component
     * @param {String} side             Side (front|back|left|right,...) of model to place component
     * 
     * @returns {Number}
     */
    getFirstVentPositionY = ( sideTop: Number, componentWidth: Number, side: String ) => {
        if( 0 === componentWidth ) {
            return sideTop;
        }

        if( this.is2StoryLeanTo() && ( 'front' === side || 'back' === side ) ) {
            return this.getWallHeight();
        }

        if( 'structure_ranch_cabin' === this._shedType && 'left' === side ) {
            const roofDegree    = this.getRoofDegree();
            const haflTopCorner = helpers.toFixed3( ( 180 - ( roofDegree * 2 ) ) / 2 );

            return this.calculateFirstVentDistance( sideTop, haflTopCorner, componentWidth );
        }

        if( 'structure_country_cottage' === this._shedType && 'left' === side ) {
            const roofDegree    = this._roofUpperDegree ? this._roofUpperDegree : this._initRoofUpperDegree;
            const haflTopCorner = helpers.toFixed3( ( 180 - ( roofDegree * 2 ) ) / 2 );

            return this.calculateFirstVentDistance( sideTop, haflTopCorner, componentWidth );
        }

        if( 'structure_classic_aframe_dormer' === this._shedType && 'left' === side ) {
            const roofDegree    = this._aframeDormerRoofDegree;
            const haflTopCorner = helpers.toFixed3( ( 180 - ( roofDegree * 2 ) ) / 2 );

            return this.calculateFirstVentDistance( sideTop, haflTopCorner, componentWidth );
        }

        if( 'front' !== side && 'back' !== side && 'porch' !== side ) {
            return sideTop;
        }

        if( 'structure_quaker_silvercreek' === this._shedType ) {
            if( this._bones['b_f_t'] && this._bones['b_f_r2'] ) {
                const opposite      = helpers.toFixed3( this._bones['b_f_t'].position.y - this._bones['b_f_r2'].position.y );
                const adjacent      = helpers.toFixed3( this._bones['b_f_r2'].position.x - this._bones['b_f_t'].position.x );
                const degree        = this.getRoofPitchFromRadian( opposite, adjacent );
                const haflTopCorner = helpers.toFixed3( 90 - degree );
                
                return this.calculateFirstVentDistance( sideTop, haflTopCorner, componentWidth );
            }

            return sideTop;
        }

        if( 'mainus_quaker' === this._shedType || 'structure_saltbox_mwshed' === this._shedType ) {
            if( this._bones['b_f_t'] && this._bones['b_f_l2'] ) {
                const opposite      = helpers.toFixed3( this._bones['b_f_t'].position.y - this._bones['b_f_l2'].position.y );
                const adjacent      = helpers.toFixed3( this._bones['b_f_t'].position.x - this._bones['b_f_l2'].position.x );
                const degree        = this.getRoofPitchFromRadian( opposite, adjacent );
                const haflTopCorner = helpers.toFixed3( 90 - degree );
                
                return this.calculateFirstVentDistance( sideTop, haflTopCorner, componentWidth );
            }

            return sideTop;
        }

        if( 'structure_quaker_wannabuysheds' === this._shedType || 'structure_horse_shelter' === this._shedType ) {
            if( this._bones['b_f_t'] && this._bones['b_f_l3'] ) {
                const opposite      = helpers.toFixed3( this._bones['b_f_t'].position.y - this._bones['b_f_l3'].position.y );
                const adjacent      = helpers.toFixed3( this._bones['b_f_t'].position.x - this._bones['b_f_l3'].position.x );
                const degree        = this.getRoofPitchFromRadian( opposite, adjacent );
                const haflTopCorner = helpers.toFixed3( 90 - degree );
                
                return this.calculateFirstVentDistance( sideTop, haflTopCorner, componentWidth );
            }

            return sideTop;
        }

        if( 'structure_cottage_shed' === this._shedType ) {
            if( this._bones['b_f_t'] && this._bones['b_f_l2'] && this._bones['b_f_l5'] ) {
                const opposite      = helpers.toFixed3( this._bones['b_f_t'].position.y - this._bones['b_f_l2'].position.y - this._bones['b_f_l5'].position.y );
                const adjacent      = helpers.toFixed3( this._bones['b_f_t'].position.x - this._bones['b_f_l2'].position.x - this._bones['b_f_l5'].position.x );
                const degree        = this.getRoofPitchFromRadian( opposite, adjacent );
                const haflTopCorner = helpers.toFixed3( 90 - degree );
                
                return this.calculateFirstVentDistance( sideTop, haflTopCorner, componentWidth );
            }

            return sideTop;
        }
        
        if( 'open_gable' === this.getRoofShape() || 'box_gable' === this.getRoofShape() ) {
            const roofDegree    = this.getRoofDegree();
            const haflTopCorner = helpers.toFixed3( ( 180 - ( roofDegree * 2 ) ) / 2 );

            return this.calculateFirstVentDistance( sideTop, haflTopCorner, componentWidth );
        }
        
        if( 'gambrel' === this.getRoofShape() ) {
            const roofDegree    = this._roofUpperDegree ? this._roofUpperDegree : this._initRoofUpperDegree;
            const haflTopCorner = helpers.toFixed3( ( 180 - ( roofDegree * 2 ) ) / 2 );

            return this.calculateFirstVentDistance( sideTop, haflTopCorner, componentWidth );
        }

        return sideTop;
    }

    /**
     * Calculate the position Y for vent
     * 
     * @param {Number} sideTop          The position to attach the vent
     * @param {Number} haflTopCorner    Half corner on the top of model (to calculate the distance from top of model to place the vent)
     * @param {Number} componentWidth   Width of component
     * 
     * @returns {Number}
     */
    calculateFirstVentDistance = ( sideTop: Number, haflTopCorner: Number, componentWidth: Number ) => {
        const haflWidth     = helpers.toFixed3( Number( componentWidth ) / 2 );
        const tan           = helpers.toFixed3( helpers.getTanFromDegrees( haflTopCorner ) );
        const distance      = helpers.toFixed3( haflWidth / tan );
        
        return helpers.toFixed3( Number( sideTop ) - distance );
    }

    /**
     * Handle show|hide hingle and lock for door
     * 
     * @param {String} lockData JSON string to get position for lock and hinge
     * @param {Mesh} meshes Door meshes
     */
    handleHingeAndLock = ( lockData, meshes: Mesh[] ) => {
        let hiddenMeshes    = [];
        let handleHingeType = true;
        let initHinges      = helpers.filterMeshByName( meshes, 'name', '_hinges_' );
        const initLock      = helpers.filterMeshByName( meshes, 'name', '_lock_' );
        initHinges          = initHinges.concat( initLock );
        for( let i = 0; i < initHinges.length; i++ ) {
            initHinges[i].setEnabled( true );
        }

        if( lockData && ( 'right' === lockData['customLock'] || 'left' === lockData['customLock'] ) ) {
            const strapHinges   = helpers.filterMeshByName( meshes, 'name', `_hinges_strap_${lockData['customLock']}_` );
            const normalHinges  = helpers.filterMeshByName( meshes, 'name', `_hinges_normal_${lockData['customLock']}_` );
            const lockKey       = 'left' === lockData['customLock'] ? 'right' : 'left';
            const lock          = helpers.filterMeshByName( meshes, 'name', `_lock_${lockKey}_` );
            hiddenMeshes        = hiddenMeshes.concat( strapHinges, normalHinges, lock );

            if( ! lockData['enableHinge'] ) {
                handleHingeType = false;
                hiddenMeshes    = hiddenMeshes.concat( helpers.filterMeshByName( meshes, 'name', '_hinges_' ) );
            }
        }

        if( handleHingeType ) {
            const hinges    = 'normal' === this.getHingeType() ? '_hinges_strap_' : '_hinges_normal_';
            hiddenMeshes    = hiddenMeshes.concat( helpers.filterMeshByName( meshes, 'name', hinges ) );
        }
        
        for( let i = 0; i < hiddenMeshes.length; i++ ) {
            hiddenMeshes[i].setEnabled( false );
        }
    }

    /**
     * Clone leg
     * 
     * @param {string} name Leg name
     */
    carportCloneLeg = ( name: string ) => {
        const frame = this._scene.getMeshByName( name ) as Mesh;
        if( ! frame ) {
            return;
        }

        this.removeFrame();

        const depth         = this.getDepth();
        const width         = this.getWidth();
        const height        = this.getWallHeight();
        const count         = Math.ceil( depth / this._legDistance );
        const frameHeight   = helpers.toFixed3( frame.geometry.extend.maximum.y - frame.geometry.extend.minimum.y );
        const scaleY        = helpers.toFixed3( height / frameHeight );

        for( let i = 1; i < count; i++ ) {
            const leftName  = `${name}_left_${i}`;
            const rightName = `${name}_right_${i}`;
            if( this._scene.getMeshByName( leftName ) || this._scene.getMeshByName( rightName ) ) {
                continue;
            }

            const leftClone                     = frame.clone( leftName );
            const rightClone                    = frame.clone( rightName );
            const positionZ                     = - ( depth / 2 ) + ( depth / count * i );
            leftClone.position                  = new Vector3( - ( width / 2 ), frame.position.y, positionZ );
            rightClone.position                 = new Vector3( width / 2, frame.position.y, positionZ );
            leftClone.rotation                  = new Vector3( 0, Math.PI, 0 );
            leftClone.scaling.y                 = scaleY;
            rightClone.scaling.y                = scaleY;
            leftClone.alwaysSelectAsActiveMesh  = true;
            rightClone.alwaysSelectAsActiveMesh = true;

            if( this._frames['left'] ) {
                this._frames['left'].push( leftClone );
            } else {
                this._frames['left'] = [ leftClone ];
            }

            if( this._frames['right'] ) {
                this._frames['right'].push( rightClone );
            } else {
                this._frames['right'] = [ rightClone ];
            }
        }

        frame.setEnabled( false );
    }

    /**
     * Remove cloned frame
     */
    removeFrame = () => {
        const _this = this;
        for( const side in _this._frames ) {
            for( let i = 0; i < _this._frames[side].length; i++ ) {
                _this._frames[side][i].dispose();
            }
            const frameNameTop  = constants.frameName + side + constants.frameTopName;
            let meshFrameTop    = this._scene.getMeshByName( frameNameTop );
            if( meshFrameTop ) meshFrameTop.dispose();

            const frameName  = constants.frameName + side;
            let meshFrame    = this._scene.getMeshByName( frameName );
            if( meshFrame ) meshFrame.dispose();
        }

        _this._frames = {};
    }

    /**
     * Remove frames associated with a specific side.
     *
     * @param {string} side - The side identifier for which frames should be removed.
     */
    removeFrameBySide = ( side: string ) => {
        if( this._frames[side] ) {
            for( let i = 0; i < this._frames[side].length; i++ ) {
                this._frames[side][i].dispose();
            }
            
            delete this._frames[side];
        }
    }

    /**
     * Update frame material to highlight mesh
     * 
     * @param {Mesh} frame Frame mesh
     * @param {StandardMaterial} material true|false
     * @param {number} groupId
     */
    frameUpdateMaterial = ( frame: Mesh, material: StandardMaterial, groupId: number ) => {
        frame.material          = material;
        frame.renderingGroupId  = groupId;
    }

    /**
     * Disable highlight frame
     * 
     * @param {string} side left|right
     */
    disableHighlightFrame = ( side: string ) => {
        if( this._isInterior ) {
            return;
        }

        const frames    = this.getFrameBySide( side );
        let frameMat    = this._frameMat;
        if( ! frameMat ) {
            frameMat = this._scene.getMaterialByName( 'carport_frame_mat' ) as StandardMaterial;
        }

        if( frameMat ) {
            for( let i = 0; i < frames.length; i++ ) {
                const frame = frames[i] as Mesh;
                this.frameUpdateMaterial( frame, frameMat, 0 );
            }
        }
        
        const wall = this.getWallBySide( side );
        if( wall ) {
            wall.visibility = 1;
        }
    }

    /**
     * Get frames for a specific side.
     *
     * @param {String} side - Side identifier for frames (e.g., 'left', 'right', etc.).
     * @returns {Mesh[]} An array of frame objects for the specified side, or an empty array if not found.
     */
    getFrameBySide = ( side: string ) => {
        return undefined !== this._frames[side] ? this._frames[side] : [];
    }

    /**
     * Merge and sort components and legs
     * 
     * @param {object} components Components list
     * @param {string} side front|back|left|right
     * 
     * @returns {Array}
     */
    sortComponentPosition = ( components: object, side: string ) => {
        let results = [];
        for( const key in components ) {
            if( -1 !== key.indexOf( '_ramp_' ) ) {
                continue;
            }
            if( undefined !== components[key].isIgnoreValidPosition && true === components[key].isIgnoreValidPosition ) {
                continue;
            }

            const componentWidth    = this.getComponentWidth( components[key].meshes, components[key].type, components[key].name, this.isMoveHorizontal( components[key].type ) );
            results.push( {
                position2D: components[key].position2D,
                position3D: components[key].position,
                componentDepth: components[key].componentDepth,
                componentWidth,
                componentHeight: this.getComponentHeight( components[key].meshes, components[key].type ),
                componentType: components[key].type,
                name: components[key].name,
            } );
        }

        results.sort( ( a, b ) => {
            if( 'front' === side || 'back' === side || this.isPorchSide( side ) || 'right_inner_porch' === side || 'left_inner_porch' === side || 'inner_back' === side ) {
                return a.position3D.x - b.position3D.x;
            }
            
            return a.position3D.z - b.position3D.z;
        } );
        
        if( 'front' === side || 'left' === side || 'roof_left' === side || 'dormer' === side || this.isPorchSide( side ) || 'left_front' === side || 'left_back' === side || 'front_inner_porch' === side || 'inner_left' === side || 'top_left' === side ) {
            results.reverse();
        }

        return results;
    }

    oldWestModularMoveBones = ( wallHeight, buildingHeight, halfWidth, halfDepth ) => {
        if( 'structure_old_west_modular' !== this._shedType ) {
            return ;
        }

        let bones               = this._bones;
        const leanToWidth       = halfWidth * 2 / 3;
        const midBoneX          = halfWidth / 3;
        const lowerHeight       = helpers.toFixed3( wallHeight + ( leanToWidth * helpers.getTanFromDegrees( 0 !== this._roofLowerDegree ? this._roofLowerDegree : this._initRoofLowerDegree ) ) );
        const collarTieWidth    = helpers.toFixed3( this._collarTieHeight / helpers.getTanFromDegrees( 0 !== this._roofUpperDegree ? this._roofUpperDegree : this._initRoofUpperDegree ) );
        const slidingBoneX      = 15 >= halfWidth ? 11 : 13;

        if( bones['b_b_l1'] ) bones['b_b_l1'].setPosition( new Vector3( - midBoneX, 0, - halfDepth ) );
        if( bones['b_b_l2'] ) bones['b_b_l2'].setPosition( new Vector3( - halfWidth, 0, - halfDepth ) );
        if( bones['b_b_l3'] ) bones['b_b_l3'].setPosition( new Vector3( - halfWidth, wallHeight, - halfDepth ) );
        if( bones['b_b_l4'] ) bones['b_b_l4'].setPosition( new Vector3( - midBoneX, lowerHeight, - halfDepth ) );
        if( bones['b_b_t'] ) bones['b_b_t'].setPosition( new Vector3( 0, buildingHeight, - halfDepth ) );
        if( bones['b_b_r1'] ) bones['b_b_r1'].setPosition( new Vector3( midBoneX, 0, - halfDepth ) );
        if( bones['b_b_r2'] ) bones['b_b_r2'].setPosition( new Vector3( halfWidth, 0, - halfDepth ) );
        if( bones['b_b_r3'] ) bones['b_b_r3'].setPosition( new Vector3( halfWidth, wallHeight, - halfDepth ) );
        if( bones['b_b_r4'] ) bones['b_b_r4'].setPosition( new Vector3( midBoneX, lowerHeight, - halfDepth ) );

        if( bones['b_f_l1'] ) bones['b_f_l1'].setPosition( new Vector3( - midBoneX, 0, halfDepth ) );
        if( bones['b_f_l2'] ) bones['b_f_l2'].setPosition( new Vector3( - halfWidth, 0, halfDepth ) );
        if( bones['b_f_l3'] ) bones['b_f_l3'].setPosition( new Vector3( - halfWidth, wallHeight, halfDepth ) );
        if( bones['b_f_l4'] ) bones['b_f_l4'].setPosition( new Vector3( - midBoneX, lowerHeight, halfDepth ) );
        if( bones['b_f_t'] ) bones['b_f_t'].setPosition( new Vector3( 0, buildingHeight, halfDepth ) );
        if( bones['b_f_r1'] ) bones['b_f_r1'].setPosition( new Vector3( midBoneX, 0, halfDepth ) );
        if( bones['b_f_r2'] ) bones['b_f_r2'].setPosition( new Vector3( halfWidth, 0, halfDepth ) );
        if( bones['b_f_r3'] ) bones['b_f_r3'].setPosition( new Vector3( halfWidth, wallHeight, halfDepth ) );   
        if( bones['b_f_r4'] ) bones['b_f_r4'].setPosition( new Vector3( midBoneX, lowerHeight, halfDepth ) );
        
        if( bones['r_b_l1'] ) bones['r_b_l1'].setPosition( new Vector3( - halfWidth, wallHeight, - halfDepth ) );
        if( bones['r_b_l2'] ) bones['r_b_l2'].setPosition( new Vector3( - midBoneX, lowerHeight, - halfDepth ) );
        if( bones['r_b_t'] ) bones['r_b_t'].setPosition( new Vector3( 0, buildingHeight, - halfDepth ) );
        if( bones['r_b_r1'] ) bones['r_b_r1'].setPosition( new Vector3( halfWidth, wallHeight, - halfDepth ) );
        if( bones['r_b_r2'] ) bones['r_b_r2'].setPosition( new Vector3( midBoneX, lowerHeight, - halfDepth ) );

        if( bones['r_f_l1'] ) bones['r_f_l1'].setPosition( new Vector3( - halfWidth, wallHeight, halfDepth ) );
        if( bones['r_f_l2'] ) bones['r_f_l2'].setPosition( new Vector3( - midBoneX, lowerHeight, halfDepth ) );
        if( bones['r_f_t'] ) bones['r_f_t'].setPosition( new Vector3( 0, buildingHeight, halfDepth ) );
        if( bones['r_f_r1'] ) bones['r_f_r1'].setPosition( new Vector3( halfWidth, wallHeight, halfDepth ) );
        if( bones['r_f_r2'] ) bones['r_f_r2'].setPosition( new Vector3( midBoneX, lowerHeight, halfDepth ) );
        
        if( bones['rm_l1'] ) bones['rm_l1'].setPosition( new Vector3( - halfWidth, wallHeight, - halfDepth ) );
        if( bones['rm_l2'] ) bones['rm_l2'].setPosition( new Vector3( - midBoneX, lowerHeight, - halfDepth ) );
        if( bones['rm_t'] ) bones['rm_t'].setPosition( new Vector3( 0, buildingHeight, - halfDepth ) );
        if( bones['rm_r1'] ) bones['rm_r1'].setPosition( new Vector3( halfWidth, wallHeight, - halfDepth ) );
        if( bones['rm_r2'] ) bones['rm_r2'].setPosition( new Vector3( midBoneX, lowerHeight, - halfDepth ) );
        
        if( bones['collar_tie_b_l1'] ) bones['collar_tie_b_l1'].setPosition( new Vector3( - collarTieWidth, buildingHeight - this._collarTieHeight, - halfDepth ) );
        if( bones['collar_tie_b_r1'] ) bones['collar_tie_b_r1'].setPosition( new Vector3( collarTieWidth , buildingHeight - this._collarTieHeight, - halfDepth ) );
        if( bones['collar_tie_b_t'] ) bones['collar_tie_b_t'].setPosition( new Vector3( 0, buildingHeight, - halfDepth ) );
        
        if( bones['collar_tie_f_l1'] ) bones['collar_tie_f_l1'].setPosition( new Vector3( - collarTieWidth, buildingHeight - this._collarTieHeight, halfDepth ) );
        if( bones['collar_tie_f_r1'] ) bones['collar_tie_f_r1'].setPosition( new Vector3( collarTieWidth , buildingHeight - this._collarTieHeight, halfDepth ) );
        if( bones['collar_tie_f_t'] ) bones['collar_tie_f_t'].setPosition( new Vector3( 0, buildingHeight, halfDepth ) );
        
        if( bones['sliding_b_l1'] ) bones['sliding_b_l1'].setPosition( new Vector3( - slidingBoneX, bones['sliding_b_l1'].position.y, - halfDepth ) );
        if( bones['sliding_b_r1'] ) bones['sliding_b_r1'].setPosition( new Vector3( slidingBoneX, bones['sliding_b_r1'].position.y, - halfDepth ) );
        if( bones['sliding_f_l1'] ) bones['sliding_f_l1'].setPosition( new Vector3( - slidingBoneX, bones['sliding_f_l1'].position.y, halfDepth ) );
        if( bones['sliding_f_r1'] ) bones['sliding_f_r1'].setPosition( new Vector3( slidingBoneX, bones['sliding_f_r1'].position.y, halfDepth ) );
    }

    ranchCabinMoveBones = ( wallHeight, buildingHeight, width, depth ) => {
        if( 'structure_ranch_cabin' !== this._shedType ) {
            return;
        }

        let bones       = this._bones;
        buildingHeight  = helpers.toFixed3( buildingHeight );
        const cabinTop  = helpers.toFixed3( this.calculateRoofPitchHeight( this.getRoofDegree(), this._cabinWidth / 2 ) );

        if( bones['b_b_l1'] ) bones['b_b_l1'].setPosition( new Vector3( -2, 0, bones['b_b_l1'].position.z ) );
        if( bones['b_b_l2'] ) bones['b_b_l2'].setPosition( new Vector3( bones['b_b_l1'].position.x, wallHeight, bones['b_b_l1'].position.z ) );
        if( bones['b_f_t'] ) bones['b_f_t'].setPosition( new Vector3( bones['b_f_t'].position.x, buildingHeight, bones['b_f_t'].position.z ) );
        if( bones['b_b_t'] ) bones['b_b_t'].setPosition( new Vector3( bones['b_b_t'].position.x, buildingHeight, bones['b_b_t'].position.z ) );
        if( bones['b_b_m1'] ) bones['b_b_m1'].setPosition( new Vector3( bones['b_f_r1'].position.x, 0, bones['b_f_r1'].position.z - this._cabinWidth ) );
        if( bones['b_b_m2'] ) bones['b_b_m2'].setPosition( new Vector3( bones['b_b_m1'].position.x, wallHeight, bones['b_b_m1'].position.z ) );
        if( bones['b_b_m3'] ) bones['b_b_m3'].setPosition( new Vector3( bones['b_b_m3'].position.x, wallHeight, bones['b_b_m1'].position.z ) );
        if( bones['b_b_m4'] ) bones['b_b_m4'].setPosition( new Vector3( bones['b_b_m3'].position.x, 0, bones['b_b_m3'].position.z ) );
        if( bones['b_m_t1'] ) bones['b_m_t1'].setPosition( new Vector3( 0, bones['b_f_t'].position.y, bones['b_f_t'].position.z - this._cabinWidth / 2 ) );
        if( bones['b_m_t2'] ) bones['b_m_t2'].setPosition( new Vector3( bones['b_f_l2'].position.x, wallHeight + cabinTop, bones['b_m_t1'].position.z ) );
        if( bones['b_p_l1'] ) bones['b_p_l1'].setPosition( new Vector3( bones['b_p_l1'].position.x, 0, bones['b_f_l1'].position.z - this._cabinWidth ) );
        if( bones['b_p_l2'] ) bones['b_p_l2'].setPosition( new Vector3( bones['b_p_l1'].position.x, wallHeight, bones['b_p_l1'].position.z ) );
        if( bones['b_p_l3'] ) bones['b_p_l3'].setPosition( new Vector3( - bones['b_b_r2'].position.x, wallHeight, - bones['b_f_l1'].position.z ) );
        if( bones['b_p_l4'] ) bones['b_p_l4'].setPosition( new Vector3( bones['b_p_l3'].position.x, bones['b_p_l4'].position.y, bones['b_p_l3'].position.z ) );
        if( bones['r_b_l1'] ) bones['r_b_l1'].setPosition( new Vector3( bones['b_p_l3'].position.x, bones['b_p_l3'].position.y, bones['b_p_l3'].position.z ) );
        if( bones['r_b_r2'] ) bones['r_b_r2'].setPosition( new Vector3( bones['b_b_r2'].position.x, bones['b_b_r2'].position.y, bones['b_b_r2'].position.z ) );
        if( bones['r_b_t'] ) bones['r_b_t'].setPosition( new Vector3( bones['b_b_t'].position.x, bones['b_b_t'].position.y, bones['b_b_t'].position.z ) );
        if( bones['r_f_l1'] ) bones['r_f_l1'].setPosition( new Vector3( bones['b_f_l2'].position.x, bones['b_f_l2'].position.y, bones['b_f_l2'].position.z ) );
        if( bones['r_f_r1'] ) bones['r_f_r1'].setPosition( new Vector3( bones['b_f_r2'].position.x, bones['b_f_r2'].position.y, bones['b_f_r2'].position.z ) );
        if( bones['r_f_t'] ) bones['r_f_t'].setPosition( new Vector3( bones['b_f_t'].position.x, bones['b_f_t'].position.y, bones['b_f_t'].position.z ) );
        if( bones['r_m_l1'] ) bones['r_m_l1'].setPosition( new Vector3( bones['b_p_l2'].position.x, bones['b_p_l2'].position.y, bones['b_p_l2'].position.z ) );
        if( bones['r_m_t1'] ) bones['r_m_t1'].setPosition( new Vector3( bones['b_m_t1'].position.x, bones['b_m_t1'].position.y, bones['b_m_t1'].position.z ) );
        if( bones['r_m_t2'] ) bones['r_m_t2'].setPosition( new Vector3( bones['b_m_t2'].position.x, bones['b_m_t2'].position.y, bones['b_m_t2'].position.z ) );
        if( bones['r_m_t3'] ) bones['r_m_t3'].setPosition( new Vector3( bones['r_f_t'].position.x, bones['r_f_t'].position.y, bones['r_f_t'].position.z - this._cabinWidth ) );
        
        if( bones['rail_v_l_b_l1'] ) bones['rail_v_l_b_l1'].setPosition( new Vector3( bones['b_b_l1'].position.x, bones['rail_v_l_b_l1'].position.y, bones['b_b_l1'].position.z ) );
        if( bones['rail_v_l_f_l1'] ) bones['rail_v_l_f_l1'].setPosition( new Vector3( bones['b_p_l4'].position.x, bones['rail_v_l_f_l1'].position.y, bones['b_p_l4'].position.z ) );
        
        if( bones['posts_braces_b_l1'] ) bones['posts_braces_b_l1'].setPosition( new Vector3( bones['b_b_l2'].position.x, bones['b_b_l2'].position.y, bones['b_b_l2'].position.z ) );
        if( bones['posts_braces_p_l1'] ) bones['posts_braces_p_l1'].setPosition( new Vector3( bones['b_p_l2'].position.x, bones['b_p_l2'].position.y, bones['b_p_l2'].position.z ) );
        if( bones['posts_braces_p_l2'] ) bones['posts_braces_p_l2'].setPosition( new Vector3( bones['b_p_l3'].position.x, bones['b_p_l3'].position.y, bones['b_p_l3'].position.z ) );

        if( bones['column_b'] ) bones['column_b'].setPosition( new Vector3( - width, 0, 0 ) );
        if( bones['column_t'] ) bones['column_t'].setPosition( new Vector3( - width, wallHeight, 0 ) );

        if( bones['baluster_f_l1'] ) bones['baluster_f_l1'].setPosition( new Vector3( 0.15, 0, 0 ) );
    }

    /**
     * Get the porch joint X value based on the porch width and model width.
     *
     * @returns {Number} The porch joint X value.
     */
    getPorchJointX = () => {
        const porchJointX   = ( <any>window ).shedpro.porch_joint_x;
        const porchWidth    = Number( jQuery( '[name="ssb-configurator-upgrade-porch"]:checked' ).attr( 'data-porch-size' ) );

        for( let i = 0; i < porchJointX.length; i++ ) {
            const modelWidthCondition   = porchJointX[i]['apply_to_width'];
            const PorchWidthCondition   = porchJointX[i]['apply_to_porch_depth'];

            if( helpers.isValidSize( modelWidthCondition.operator, this.getWidth(), modelWidthCondition['value'], modelWidthCondition['max_value'] ) && helpers.isValidSize( PorchWidthCondition.operator, porchWidth, PorchWidthCondition['value'], PorchWidthCondition['max_value'] ) ) {
                return Number( porchJointX[i]['value'] );
            }
        }

        return 1.34;
    }

    /**
     * Get the porch joint Y value based on the porch width.
     *
     * @returns {Number} The porch joint Y value.
     */
    getPorchJointY = () => {
        const porchJointY   = ( <any>window ).shedpro.porch_joint_y;
        const porchWidth    = Number( jQuery( '[name="ssb-configurator-upgrade-porch"]:checked' ).attr( 'data-porch-size' ) );
        for( let i = 0; i < porchJointY.length; i++ ) {
            const PorchWidthCondition   = porchJointY[i]['apply_to_porch_depth'];
            if( helpers.isValidSize( PorchWidthCondition.operator, porchWidth, PorchWidthCondition['value'], PorchWidthCondition['max_value'] ) ) {
                return Number( porchJointY[i]['value'] );
            }
        }

        return 2.167;
    }

    porchBarnMoveBones = ( wallHeight, halfWidth, halfDepth ) => {
        if( 'structure_porch_barn' !== this._shedType ) {
            return;
        }

        let bones               = this._bones;
        const porchWidth        = Number( jQuery( '[name="ssb-configurator-upgrade-porch"]:checked' ).attr( 'data-porch-size' ) );
        const fullWidth         = helpers.toFixed3( 2 * halfWidth );
        const roofMidX          = helpers.toFixed3( fullWidth / this._initGambrelMidXRatio );
        const tanLower          = helpers.toFixed3( helpers.getTanFromDegrees( 0 !== this._roofLowerDegree ? this._roofLowerDegree : this._initRoofLowerDegree ) );
        let roofMidY            = helpers.toFixed3( ( fullWidth / 2 - roofMidX ) * tanLower + wallHeight + 2 * this._porchCornerDistance );
        const roofTopHeight     = helpers.toFixed3( roofMidX * helpers.getTanFromDegrees( 0 !== this._roofUpperDegree ? this._roofUpperDegree : this._initRoofUpperDegree ) );
        let buildingHeight      = helpers.toFixed3( roofMidY + roofTopHeight );
        const porchJointX       = this.getPorchJointX();
        const porchJointY       = this.getPorchJointY();
        const tmpDistance       = helpers.toFixed3( this._porchCornerDistance / 2 );
        const porchPoint        = helpers.toFixed3( - ( halfWidth + porchWidth ) );
        const l5Y               = wallHeight + this._porchCornerDistance;
        const aframeRoofHeight  = helpers.toFixed3( tanLower * ( halfWidth - roofMidX - porchJointX ) );
        let l7Y                 = helpers.toFixed3( roofMidY - aframeRoofHeight );
        let offset              = helpers.toFixed3( porchJointY - ( l7Y - wallHeight ) );
        offset                  = offset > 0 ? offset : 0;
        const tmpX              = Math.abs( porchPoint - this._porchCornerDistance ) - halfWidth + porchJointX;
        const porchTan          = ( l7Y - ( wallHeight + this._porchCornerDistance ) ) / tmpX;
        const l6Y               = helpers.toFixed3( l7Y - porchTan * porchJointX + offset );
        const r6Y               = wallHeight + 2 * this._porchCornerDistance + offset;
        l7Y                     += offset;
        roofMidY                += offset;
        buildingHeight          += offset;
        
        const porchL3           = wallHeight + this._porchCornerDistance + porchTan * this._porchCornerDistance;

        this.setBuildingHeight( buildingHeight );

        if( bones['b_f_l1'] ) bones['b_f_l1'].setPosition( new Vector3( - halfWidth, 0, halfDepth ) );
        if( bones['b_f_l2'] ) bones['b_f_l2'].setPosition( new Vector3( - halfWidth, wallHeight, halfDepth ) );
        if( bones['b_f_l5'] ) bones['b_f_l5'].setPosition( new Vector3( porchPoint - this._porchCornerDistance, l5Y, halfDepth ) );
        if( bones['b_f_l6'] ) bones['b_f_l6'].setPosition( new Vector3( - halfWidth, l6Y, halfDepth ) );
        if( bones['b_f_l7'] ) bones['b_f_l7'].setPosition( new Vector3( - halfWidth + porchJointX, l7Y, halfDepth ) );
        if( bones['b_f_l8'] ) bones['b_f_l8'].setPosition( new Vector3( - roofMidX, roofMidY, halfDepth ) );
        if( bones['b_f_t'] ) bones['b_f_t'].setPosition( new Vector3( 0, buildingHeight, halfDepth ) );
        if( bones['b_f_r1'] ) bones['b_f_r1'].setPosition( new Vector3( halfWidth, 0, halfDepth ) );
        if( bones['b_f_r2'] ) bones['b_f_r2'].setPosition( new Vector3( halfWidth, wallHeight, halfDepth ) );
        if( bones['b_f_r6'] ) bones['b_f_r6'].setPosition( new Vector3( halfWidth, r6Y, halfDepth ) );
        if( bones['b_f_r7'] ) bones['b_f_r7'].setPosition( new Vector3( roofMidX, roofMidY, halfDepth ) );
        
        if( bones['b_b_l1'] ) bones['b_b_l1'].setPosition( new Vector3( - halfWidth, 0, - halfDepth ) );
        if( bones['b_b_l2'] ) bones['b_b_l2'].setPosition( new Vector3( - halfWidth, wallHeight, - halfDepth ) );
        if( bones['b_b_l5'] ) bones['b_b_l5'].setPosition( new Vector3( porchPoint - this._porchCornerDistance, l5Y, - halfDepth ) );
        if( bones['b_b_l6'] ) bones['b_b_l6'].setPosition( new Vector3( - halfWidth, l6Y, - halfDepth ) );
        if( bones['b_b_l7'] ) bones['b_b_l7'].setPosition( new Vector3( - halfWidth + porchJointX, l7Y, - halfDepth ) );
        if( bones['b_b_l8'] ) bones['b_b_l8'].setPosition( new Vector3( - roofMidX, roofMidY, - halfDepth ) );
        if( bones['b_b_t'] ) bones['b_b_t'].setPosition( new Vector3( 0, buildingHeight, - halfDepth ) );
        if( bones['b_b_r1'] ) bones['b_b_r1'].setPosition( new Vector3( halfWidth, 0, - halfDepth ) );
        if( bones['b_b_r2'] ) bones['b_b_r2'].setPosition( new Vector3( halfWidth, wallHeight, - halfDepth ) );
        if( bones['b_b_r6'] ) bones['b_b_r6'].setPosition( new Vector3( halfWidth, r6Y, - halfDepth ) );
        if( bones['b_b_r7'] ) bones['b_b_r7'].setPosition( new Vector3( roofMidX, roofMidY, - halfDepth ) );

        if( bones['b_p_l1'] ) bones['b_p_l1'].setPosition( new Vector3( porchPoint, 0, - halfDepth ) );
        if( bones['b_p_l2'] ) bones['b_p_l2'].setPosition( new Vector3( porchPoint, wallHeight, - halfDepth ) );
        if( bones['b_p_l3'] ) bones['b_p_l3'].setPosition( new Vector3( porchPoint, porchL3, - halfDepth ) );
        if( bones['b_p_r1'] ) bones['b_p_r1'].setPosition( new Vector3( porchPoint, 0, halfDepth ) );
        if( bones['b_p_r2'] ) bones['b_p_r2'].setPosition( new Vector3( porchPoint, wallHeight, halfDepth ) );
        if( bones['b_p_r3'] ) bones['b_p_r3'].setPosition( new Vector3( porchPoint, porchL3, halfDepth ) );

        if( bones['r_f_l1'] ) bones['r_f_l1'].setPosition( new Vector3( porchPoint - this._porchCornerDistance, l5Y, halfDepth ) );
        if( bones['r_f_l2'] ) bones['r_f_l2'].setPosition( new Vector3( - halfWidth + porchJointX, l7Y, halfDepth ) );
        if( bones['r_f_l3'] ) bones['r_f_l3'].setPosition( new Vector3( - roofMidX, roofMidY, halfDepth ) );
        if( bones['r_f_r2'] ) bones['r_f_r2'].setPosition( new Vector3( halfWidth, r6Y, halfDepth ) );
        if( bones['r_f_r3'] ) bones['r_f_r3'].setPosition( new Vector3( roofMidX, roofMidY, halfDepth ) );
        if( bones['r_f_t'] ) bones['r_f_t'].setPosition( new Vector3( 0, buildingHeight, halfDepth ) );
        
        if( bones['r_b_l1'] ) bones['r_b_l1'].setPosition( new Vector3( porchPoint - this._porchCornerDistance, l5Y, - halfDepth ) );
        if( bones['r_b_l2'] ) bones['r_b_l2'].setPosition( new Vector3( - halfWidth + porchJointX, l7Y, - halfDepth ) );
        if( bones['r_b_l3'] ) bones['r_b_l3'].setPosition( new Vector3( - roofMidX, roofMidY, - halfDepth ) );
        if( bones['r_b_r2'] ) bones['r_b_r2'].setPosition( new Vector3( halfWidth, r6Y, - halfDepth ) );
        if( bones['r_b_r3'] ) bones['r_b_r3'].setPosition( new Vector3( roofMidX, roofMidY, - halfDepth ) );
        if( bones['r_b_t'] ) bones['r_b_t'].setPosition( new Vector3( 0, buildingHeight, - halfDepth ) );

        if( bones['rm_l1'] ) bones['rm_l1'].setPosition( new Vector3( porchPoint - this._porchCornerDistance, l5Y, - halfDepth ) );
        if( bones['rm_l2'] ) bones['rm_l2'].setPosition( new Vector3( - halfWidth + porchJointX, l7Y, - halfDepth ) );
        if( bones['rm_l3'] ) bones['rm_l3'].setPosition( new Vector3( - roofMidX, roofMidY, - halfDepth ) );
        if( bones['rm_r2'] ) bones['rm_r2'].setPosition( new Vector3( halfWidth, r6Y, - halfDepth ) );
        if( bones['rm_r3'] ) bones['rm_r3'].setPosition( new Vector3( roofMidX, roofMidY, - halfDepth ) );
        if( bones['rm_t'] ) bones['rm_t'].setPosition( new Vector3( 0, buildingHeight, - halfDepth ) );

        if( bones['column_b'] ) bones['column_b'].setPosition( new Vector3( porchPoint, 0, 0 ) );
        if( bones['column_t'] ) bones['column_t'].setPosition( new Vector3( porchPoint, wallHeight + aframeCabin.porchOffsetHeight, 0 ) );
        
        if( bones['rail_v_r_b_l1'] ) bones['rail_v_r_b_l1'].setPosition( new Vector3( - halfWidth, bones['rail_v_r_b_l1'].position.y, halfDepth ) );
        if( bones['rail_v_r_f_l1'] ) bones['rail_v_r_f_l1'].setPosition( new Vector3( porchPoint, bones['rail_v_r_f_l1'].position.y, halfDepth ) );
        
        if( bones['rail_h_r_f_l1'] ) bones['rail_h_r_f_l1'].setPosition( new Vector3( porchPoint, bones['rail_h_r_f_l1'].position.y, bones['rail_h_r_f_l1'].position.z ) );
        if( bones['rail_h_r_f_l2'] ) bones['rail_h_r_f_l2'].setPosition( new Vector3( porchPoint, bones['rail_h_r_f_l2'].position.y, bones['rail_h_r_f_l2'].position.z ) );
        
        if( bones['rail_h_l_f_l1'] ) bones['rail_h_l_f_l1'].setPosition( new Vector3( porchPoint, bones['rail_h_l_f_l1'].position.y, bones['rail_h_l_f_l1'].position.z ) );
        if( bones['rail_h_l_f_l2'] ) bones['rail_h_l_f_l2'].setPosition( new Vector3( porchPoint, bones['rail_h_l_f_l2'].position.y, bones['rail_h_l_f_l2'].position.z ) );
        
        if( bones['rail_v_l_b_l1'] ) bones['rail_v_l_b_l1'].setPosition( new Vector3( - halfWidth, bones['rail_v_l_b_l1'].position.y, - halfDepth ) );
        if( bones['rail_v_l_f_l1'] ) bones['rail_v_l_f_l1'].setPosition( new Vector3( porchPoint, bones['rail_v_l_f_l1'].position.y, - halfDepth ) );

        if( bones['baluster_f_l1'] ) bones['baluster_f_l1'].setPosition( new Vector3( 0.2, 0, 0 ) );
    }

    leanToFrontPorchMoveBones = ( wallHeight, halfWidth, halfDepth ) => {
        if( 'structure_lean_to_with_front_porch' !== this._shedType ) {
            return;
        }

        let bones               = this._bones;
        const tanRoof           = helpers.getTanFromDegrees( this.getRoofDegree() );
        const midPointX         = helpers.toFixed3( this._porchWidth - halfWidth );
        const midPointY         = helpers.toFixed3( wallHeight + ( halfWidth - midPointX ) * tanRoof );
        const buildingHeight    = helpers.toFixed3( wallHeight + halfWidth * 2 * tanRoof );
        this._middleHeight      = midPointY;
        this.setBuildingHeight( buildingHeight );

        if( bones['b_f_p1'] ) bones['b_f_p1'].setPosition( new Vector3( - halfWidth, 0, halfDepth ) );
        if( bones['b_f_p2'] ) bones['b_f_p2'].setPosition( new Vector3( - halfWidth, midPointY, halfDepth ) );
        if( bones['b_f_p3'] ) bones['b_f_p3'].setPosition( new Vector3( - halfWidth, wallHeight, halfDepth ) );
        if( bones['b_f_t'] ) bones['b_f_t'].setPosition( new Vector3( - halfWidth, buildingHeight, halfDepth ) );
        if( bones['b_f_l1'] ) bones['b_f_l1'].setPosition( new Vector3( midPointX, 0, halfDepth ) );
        if( bones['b_f_l2'] ) bones['b_f_l2'].setPosition( new Vector3( midPointX, midPointY, halfDepth ) );
        if( bones['b_f_r1'] ) bones['b_f_r1'].setPosition( new Vector3( halfWidth, 0, halfDepth ) );
        if( bones['b_f_r2'] ) bones['b_f_r2'].setPosition( new Vector3( halfWidth, wallHeight, halfDepth ) );
        
        if( bones['b_b_p1'] ) bones['b_b_p1'].setPosition( new Vector3( - halfWidth, 0, - halfDepth ) );
        if( bones['b_b_p2'] ) bones['b_b_p2'].setPosition( new Vector3( - halfWidth, midPointY, - halfDepth ) );
        if( bones['b_b_p3'] ) bones['b_b_p3'].setPosition( new Vector3( - halfWidth, wallHeight, - halfDepth ) );
        if( bones['b_b_t'] ) bones['b_b_t'].setPosition( new Vector3( - halfWidth, buildingHeight, - halfDepth ) );
        if( bones['b_b_l1'] ) bones['b_b_l1'].setPosition( new Vector3( midPointX, 0, - halfDepth ) );
        if( bones['b_b_l2'] ) bones['b_b_l2'].setPosition( new Vector3( midPointX, midPointY, - halfDepth ) );
        if( bones['b_b_r1'] ) bones['b_b_r1'].setPosition( new Vector3( halfWidth, 0, - halfDepth ) );
        if( bones['b_b_r2'] ) bones['b_b_r2'].setPosition( new Vector3( halfWidth, wallHeight, - halfDepth ) );
        
        if( bones['r_f_l1'] ) bones['r_f_l1'].setPosition( new Vector3( - halfWidth, buildingHeight, halfDepth ) );
        if( bones['r_f_r1'] ) bones['r_f_r1'].setPosition( new Vector3( halfWidth, wallHeight, halfDepth ) );
        if( bones['r_b_l1'] ) bones['r_b_l1'].setPosition( new Vector3( - halfWidth, buildingHeight, - halfDepth ) );
        if( bones['r_b_r1'] ) bones['r_b_r1'].setPosition( new Vector3( halfWidth, wallHeight, - halfDepth ) );
        
        if( bones['rm_l1'] ) bones['rm_l1'].setPosition( new Vector3( - halfWidth, buildingHeight, - halfDepth ) );
        if( bones['rm_r1'] ) bones['rm_r1'].setPosition( new Vector3( halfWidth, wallHeight, - halfDepth ) );
    }

    /**
     * Adjusts the positions of bones for an A-Frame Double Gable structure based on given dimensions.
     * 
     * @param {number} wallHeight - The height of the shed walls.
     * @param {number} halfWidth - Half of the shed's total width.
     * @param {number} halfDepth - Half of the shed's total depth.
     */
    aframeDoubleGableMoveBones = ( wallHeight, halfWidth, halfDepth ) => {
        if( ! this.isDoubleGable() ) {
            return;
        }

        let bones = this._bones;
        const porchDepth = constants.doubleGablePorchDepth;
        const roofPitchHeight = helpers.toFixed3( this._initBoxGableWidth * helpers.getSinFromDegrees( this.getGlobalData( constants.initRoofGableDegrees ) ) );
        const halfGableWidth = this.getHalfGableWidth();
        const gableZ = helpers.toFixed3( halfDepth - ( 2 * halfGableWidth ) );
        const gableMiddleZ = helpers.toFixed3( halfDepth - halfGableWidth );
        const buildingHeight = helpers.toFixed3( wallHeight + roofPitchHeight );
        const BBM3_X = helpers.toFixed3( - halfWidth + porchDepth );
        this.setBuildingHeight( buildingHeight );
        
        // Move bones for body
        if( bones['b_f_l1'] ) bones['b_f_l1'].setPosition( new Vector3( - halfWidth, 0, halfDepth ) );
        if( bones['b_f_l2'] ) bones['b_f_l2'].setPosition( new Vector3( - halfWidth, wallHeight, halfDepth ) );
        if( bones['b_f_r1'] ) bones['b_f_r1'].setPosition( new Vector3( halfWidth, 0, halfDepth ) );
        if( bones['b_f_r2'] ) bones['b_f_r2'].setPosition( new Vector3( halfWidth, wallHeight, halfDepth ) );
        if( bones['b_f_t1'] ) bones['b_f_t1'].setPosition( new Vector3( halfWidth, buildingHeight, gableMiddleZ ) );
        if( bones['b_f_t2'] ) bones['b_f_t2'].setPosition( new Vector3( - halfWidth, buildingHeight, gableMiddleZ ) );

        if( bones['b_b_l1'] ) bones['b_b_l1'].setPosition( new Vector3( - halfWidth, 0, - halfDepth ) );
        if( bones['b_b_l2'] ) bones['b_b_l2'].setPosition( new Vector3( - halfWidth, wallHeight, - halfDepth ) );
        if( bones['b_b_r1'] ) bones['b_b_r1'].setPosition( new Vector3( halfWidth, 0, - halfDepth ) );
        if( bones['b_b_r2'] ) bones['b_b_r2'].setPosition( new Vector3( halfWidth, wallHeight, - halfDepth ) );
        if( bones['b_b_t1'] ) bones['b_b_t1'].setPosition( new Vector3( halfWidth, buildingHeight, - gableMiddleZ ) );
        if( bones['b_b_t2'] ) bones['b_b_t2'].setPosition( new Vector3( - halfWidth, buildingHeight, - gableMiddleZ ) );
        
        if( bones['b_p_l1'] ) bones['b_p_l1'].setPosition( new Vector3( - halfWidth, 0, gableZ ) );
        if( bones['b_p_l2'] ) bones['b_p_l2'].setPosition( new Vector3( - halfWidth, wallHeight, gableZ ) );
        if( bones['b_p_l3'] ) bones['b_p_l3'].setPosition( new Vector3( - halfWidth, 0, - gableZ ) );
        if( bones['b_p_l4'] ) bones['b_p_l4'].setPosition( new Vector3( - halfWidth, wallHeight, - gableZ ) );
        
        if( bones['b_b_m1'] ) bones['b_b_m1'].setPosition( new Vector3( halfWidth, 0, gableZ ) );
        if( bones['b_b_m2'] ) bones['b_b_m2'].setPosition( new Vector3( halfWidth, wallHeight, gableZ ) );
        if( bones['b_b_m3'] ) bones['b_b_m3'].setPosition( new Vector3( BBM3_X, wallHeight, gableZ ) );
        if( bones['b_b_m4'] ) bones['b_b_m4'].setPosition( new Vector3( BBM3_X, 0, gableZ ) );
        if( bones['b_b_m5'] ) bones['b_b_m5'].setPosition( new Vector3( halfWidth, 0, - gableZ ) );
        if( bones['b_b_m6'] ) bones['b_b_m6'].setPosition( new Vector3( halfWidth, wallHeight, - gableZ ) );
        if( bones['b_b_m7'] ) bones['b_b_m7'].setPosition( new Vector3( BBM3_X, wallHeight, - gableZ ) );
        if( bones['b_b_m8'] ) bones['b_b_m8'].setPosition( new Vector3( BBM3_X, 0, - gableZ ) );
        
        // Move bones for roof
        if( bones['r_b_t1'] ) bones['r_b_t1'].setPosition( new Vector3( bones['r_b_t1'].position.x, buildingHeight, - gableMiddleZ ) );
        if( bones['r_f_t1'] ) bones['r_f_t1'].setPosition( new Vector3( bones['r_f_t1'].position.x, buildingHeight, gableMiddleZ ) );
        if( bones['r_m_t1'] ) bones['r_m_t1'].setPosition( new Vector3( bones['r_m_t1'].position.x, buildingHeight, 0 ) );
        if( bones['r_m_t2'] ) bones['r_m_t2'].setPosition( new Vector3( bones['r_m_t2'].position.x, buildingHeight, gableZ ) );
        if( bones['r_m_t3'] ) bones['r_m_t3'].setPosition( new Vector3( bones['r_m_t3'].position.x, buildingHeight, - gableZ ) );
        
        // Move bones for porch's items
        if( bones['column_b'] ) bones['column_b'].setPosition( new Vector3( 0, 0, 0 ) );
        if( bones['column_t'] ) bones['column_t'].setPosition( new Vector3( 0, wallHeight, 0 ) );
        if( bones['baluster_f_l1'] ) bones['baluster_f_l1'].setPosition( new Vector3( this.getGlobalData( 'error_baluster_f_l1_x' ), bones['baluster_f_l1'].position.y, 0 ) );
    }

    /**
     * Adjusts the positions of bones for an A-Frame Single Gable structure based on given dimensions.
     *
     * @param {number} wallHeight - The height of the shed walls.
     * @param {number} buildingHeight - The total building height, including the roof pitch.
     * @param {number} halfWidth - Half of the shed's total width.
     * @param {number} halfDepth - Half of the shed's total depth.
     */
    moveBonesAframeSingleGable = ( wallHeight, buildingHeight, halfWidth, halfDepth ) => {
        if( ! this.isAframeSingleGable() ) return;

        const bones = this._bones;
        const openingWidth = 6;
        const dormerDepth = 14;
        const porchWidth = 4;
        const dormerZ = halfDepth - dormerDepth;
        const dormerMidZ = halfDepth - dormerDepth / 2;
        const porchJointX = this.getGlobalData( 'porchJointX' );

        const roofPitchHeight = this.getGlobalData( 'dormerRoofHeight' );
        buildingHeight = helpers.toFixed3( wallHeight + roofPitchHeight );
        this.setBuildingHeight( buildingHeight );
        this.setRoofPitchHeight( roofPitchHeight );

        if( bones['b_b_l1'] ) bones['b_b_l1'].setPosition( new Vector3( - halfWidth + porchWidth, bones['b_b_l1'].position.y, - halfDepth ) );
        if( bones['b_b_l2'] ) bones['b_b_l2'].setPosition( new Vector3( - halfWidth + porchWidth, wallHeight, - halfDepth ) );
        if( bones['b_b_l3'] ) bones['b_b_l3'].setPosition( new Vector3( - halfWidth + porchWidth + 2, wallHeight + porchJointX, - halfDepth ) );

        if( bones['b_b_m1'] ) bones['b_b_m1'].setPosition( new Vector3( halfWidth, bones['b_b_m1'].position.y, dormerZ ) );
        if( bones['b_b_m2'] ) bones['b_b_m2'].setPosition( new Vector3( halfWidth, wallHeight, dormerZ ) );
        if( bones['b_b_m3'] ) bones['b_b_m3'].setPosition( new Vector3( - halfWidth + porchWidth, wallHeight, dormerZ ) );
        if( bones['b_b_m4'] ) bones['b_b_m4'].setPosition( new Vector3( - halfWidth + porchWidth, bones['b_b_m4'].position.y, dormerZ ) );

        if( bones['b_b_r1'] ) bones['b_b_r1'].setPosition( new Vector3( halfWidth, bones['b_b_r1'].position.y, - halfDepth ) );
        if( bones['b_b_r2'] ) bones['b_b_r2'].setPosition( new Vector3( halfWidth, wallHeight, - halfDepth ) );
        if( bones['b_b_t'] ) bones['b_b_t'].setPosition( new Vector3( bones['b_b_t'].position.x, buildingHeight, - halfDepth ) );

        if( bones['b_f_l1'] ) bones['b_f_l1'].setPosition( new Vector3( - halfWidth, bones['b_f_l1'].position.y, halfDepth ) );
        if( bones['b_f_l2'] ) bones['b_f_l2'].setPosition( new Vector3( - halfWidth, wallHeight, halfDepth ) );
        if( bones['b_f_r1'] ) bones['b_f_r1'].setPosition( new Vector3( halfWidth, bones['b_f_r1'].position.y, halfDepth ) );
        if( bones['b_f_r2'] ) bones['b_f_r2'].setPosition( new Vector3( halfWidth, wallHeight, halfDepth ) );

        if( bones['b_f_t1'] ) bones['b_f_t1'].setPosition( new Vector3( halfWidth, buildingHeight, dormerMidZ ) );
        if( bones['b_f_t2'] ) bones['b_f_t2'].setPosition( new Vector3( - halfWidth, buildingHeight, dormerMidZ ) );

        if( bones['b_p_l1'] ) bones['b_p_l1'].setPosition( new Vector3( - halfWidth, bones['b_p_l1'].position.y, dormerZ ) );
        if( bones['b_p_l2'] ) bones['b_p_l2'].setPosition( new Vector3( - halfWidth, wallHeight, dormerZ ) );

        if( bones['b_p_l3'] ) bones['b_p_l3'].setPosition( new Vector3( - halfWidth, wallHeight, - halfDepth ) );
        if( bones['b_p_l4'] ) bones['b_p_l4'].setPosition( new Vector3( - halfWidth, bones['b_p_l4'].position.y, - halfDepth ) );

        if( bones['r_f_t1'] ) bones['r_f_t1'].setPosition( new Vector3( bones['r_f_t1'].position.x, buildingHeight, dormerMidZ ) );
        if( bones['r_m_t1'] ) bones['r_m_t1'].setPosition( new Vector3( bones['r_m_t1'].position.x, buildingHeight, - halfDepth ) );
        if( bones['r_m_t2'] ) bones['r_m_t2'].setPosition( new Vector3( bones['r_m_t2'].position.x, buildingHeight, dormerZ ) );

        if( bones['column_t'] ) bones['column_t'].setPosition( new Vector3( 0, wallHeight, 0 ) );
        if( bones['column_b'] ) bones['column_b'].setPosition( new Vector3( 0, 0, 0 ) );
        if( bones['rail_v_l_b_l1'] ) bones['rail_v_l_b_l1'].setPosition( new Vector3( - halfWidth + porchWidth, bones['rail_v_l_b_l1'].position.y, - halfDepth ) );
        if( bones['rail_v_l_f_l1'] ) bones['rail_v_l_f_l1'].setPosition( new Vector3( - halfWidth, bones['rail_v_l_f_l1'].position.y, - halfDepth ) );

        if( bones['baluster_f_l1'] ) bones['baluster_f_l1'].setPosition( new Vector3( this.getGlobalData( 'error_baluster_f_l1_x' ), bones['baluster_f_l1'].position.y, this.getGlobalData( 'error_baluster_f_l1_x' ) ) );
    }

    aframeCabinMoveBones = ( wallHeight, halfWidth, halfDepth ) => {
        if( 'structure_aframe_cabin' !== this._shedType ) {
            return;
        }
        
        let bones           = this._bones;
        const tanRoof       = helpers.getTanFromDegrees( this.getRoofDegree() );
        const porchWidth    = Number( jQuery( '[name="ssb-configurator-upgrade-porch"]:checked' ).attr( 'data-porch-size' ) );
        const porchJointX   = this.getPorchJointX();
        const porchJointY   = this.getPorchJointY();

        const halfCornerDistance    = helpers.toFixed3( this._porchCornerDistance / 2 );
        const roofHeight            = helpers.toFixed3( tanRoof * halfWidth );
        const porchPoint            = helpers.toFixed3( - ( halfWidth + porchWidth ) );
        let buildingHeight          = helpers.toFixed3( wallHeight + roofHeight + 2 * this._porchCornerDistance );
        const aframeRoofHeight      = helpers.toFixed3( tanRoof * ( halfWidth - porchJointX ) );
        let l7Y                     = helpers.toFixed3( buildingHeight - aframeRoofHeight );
        let offset                  = helpers.toFixed3( porchJointY - ( l7Y - wallHeight ) );
        offset                      = offset > 0 ? offset : 0;
        const tmpX                  = ( - halfWidth + porchJointX ) - ( porchPoint - this._porchCornerDistance );
        const porchTan              = ( l7Y - ( wallHeight + this._porchCornerDistance ) ) / tmpX;
        const l6Y                   = helpers.toFixed3( l7Y - porchTan * porchJointX + offset );
        l7Y                         += offset;
        buildingHeight              += offset;
        const l5Y                   = wallHeight + this._porchCornerDistance;
        const r5Y                   = l5Y + offset;
        const r6Y                   = wallHeight + 2 * this._porchCornerDistance + offset;
        const porchL3               = wallHeight + this._porchCornerDistance + porchTan * this._porchCornerDistance;

        this.setBuildingHeight( buildingHeight );

        if( bones['b_f_l1'] ) bones['b_f_l1'].setPosition( new Vector3( - halfWidth, 0, halfDepth ) );
        if( bones['b_f_l2'] ) bones['b_f_l2'].setPosition( new Vector3( - halfWidth, wallHeight, halfDepth ) );
        if( bones['b_f_l3'] ) bones['b_f_l3'].setPosition( new Vector3( porchPoint - halfCornerDistance, wallHeight, halfDepth ) );
        if( bones['b_f_l4'] ) bones['b_f_l4'].setPosition( new Vector3( porchPoint - this._porchCornerDistance, wallHeight + halfCornerDistance, halfDepth ) );
        if( bones['b_f_l5'] ) bones['b_f_l5'].setPosition( new Vector3( porchPoint - this._porchCornerDistance, l5Y, halfDepth ) );
        if( bones['b_f_l6'] ) bones['b_f_l6'].setPosition( new Vector3( - halfWidth, l6Y, halfDepth ) );
        if( bones['b_f_l7'] ) bones['b_f_l7'].setPosition( new Vector3( - halfWidth + porchJointX, l7Y, halfDepth ) );
        if( bones['b_f_t'] ) bones['b_f_t'].setPosition( new Vector3( 0, buildingHeight, halfDepth ) );
        if( bones['b_f_r1'] ) bones['b_f_r1'].setPosition( new Vector3( halfWidth, 0, halfDepth ) );
        if( bones['b_f_r2'] ) bones['b_f_r2'].setPosition( new Vector3( halfWidth, wallHeight, halfDepth ) );
        if( bones['b_f_r3'] ) bones['b_f_r3'].setPosition( new Vector3( halfWidth + halfCornerDistance, wallHeight, halfDepth ) );
        if( bones['b_f_r4'] ) bones['b_f_r4'].setPosition( new Vector3( halfWidth + this._porchCornerDistance, wallHeight + halfCornerDistance, halfDepth ) );
        if( bones['b_f_r5'] ) bones['b_f_r5'].setPosition( new Vector3( halfWidth + this._porchCornerDistance, r5Y, halfDepth ) );
        if( bones['b_f_r6'] ) bones['b_f_r6'].setPosition( new Vector3( halfWidth, r6Y, halfDepth ) );
        
        if( bones['b_b_l1'] ) bones['b_b_l1'].setPosition( new Vector3( - halfWidth, 0, - halfDepth ) );
        if( bones['b_b_l2'] ) bones['b_b_l2'].setPosition( new Vector3( - halfWidth, wallHeight, - halfDepth ) );
        if( bones['b_b_l3'] ) bones['b_b_l3'].setPosition( new Vector3( porchPoint - halfCornerDistance, wallHeight, - halfDepth ) );
        if( bones['b_b_l4'] ) bones['b_b_l4'].setPosition( new Vector3( porchPoint - this._porchCornerDistance, wallHeight + halfCornerDistance, - halfDepth ) );
        if( bones['b_b_l5'] ) bones['b_b_l5'].setPosition( new Vector3( porchPoint - this._porchCornerDistance, l5Y, - halfDepth ) );
        if( bones['b_b_l6'] ) bones['b_b_l6'].setPosition( new Vector3( - halfWidth, l6Y, - halfDepth ) );
        if( bones['b_b_l7'] ) bones['b_b_l7'].setPosition( new Vector3( - halfWidth + porchJointX, l7Y, - halfDepth ) );
        if( bones['b_b_t'] ) bones['b_b_t'].setPosition( new Vector3( 0, buildingHeight, - halfDepth ) );
        if( bones['b_b_r1'] ) bones['b_b_r1'].setPosition( new Vector3( halfWidth, 0, - halfDepth ) );
        if( bones['b_b_r2'] ) bones['b_b_r2'].setPosition( new Vector3( halfWidth, wallHeight, - halfDepth ) );
        if( bones['b_b_r3'] ) bones['b_b_r3'].setPosition( new Vector3( halfWidth + halfCornerDistance, wallHeight, - halfDepth ) );
        if( bones['b_b_r4'] ) bones['b_b_r4'].setPosition( new Vector3( halfWidth + this._porchCornerDistance, wallHeight + halfCornerDistance, - halfDepth ) );
        if( bones['b_b_r5'] ) bones['b_b_r5'].setPosition( new Vector3( halfWidth + this._porchCornerDistance, r5Y, - halfDepth ) );
        if( bones['b_b_r6'] ) bones['b_b_r6'].setPosition( new Vector3( halfWidth, r6Y, - halfDepth ) );

        if( bones['b_p_l1'] ) bones['b_p_l1'].setPosition( new Vector3( porchPoint, 0, - halfDepth ) );
        if( bones['b_p_l2'] ) bones['b_p_l2'].setPosition( new Vector3( porchPoint, wallHeight, - halfDepth ) );
        if( bones['b_p_l3'] ) bones['b_p_l3'].setPosition( new Vector3( porchPoint, porchL3, - halfDepth ) );
        if( bones['b_p_r1'] ) bones['b_p_r1'].setPosition( new Vector3( porchPoint, 0, halfDepth ) );
        if( bones['b_p_r2'] ) bones['b_p_r2'].setPosition( new Vector3( porchPoint, wallHeight, halfDepth ) );
        if( bones['b_p_r3'] ) bones['b_p_r3'].setPosition( new Vector3( porchPoint, porchL3, halfDepth ) );
        
        if( bones['r_f_l1'] ) bones['r_f_l1'].setPosition( new Vector3( porchPoint - this._porchCornerDistance, l5Y, halfDepth ) );
        if( bones['r_f_l2'] ) bones['r_f_l2'].setPosition( new Vector3( - halfWidth + porchJointX, l7Y, halfDepth ) );
        if( bones['r_f_r1'] ) bones['r_f_r1'].setPosition( new Vector3( halfWidth + this._porchCornerDistance, r5Y, halfDepth ) );
        if( bones['r_f_t'] ) bones['r_f_t'].setPosition( new Vector3( 0, buildingHeight, halfDepth ) );
        
        if( bones['r_b_l1'] ) bones['r_b_l1'].setPosition( new Vector3( porchPoint - this._porchCornerDistance, l5Y, - halfDepth ) );
        if( bones['r_b_l2'] ) bones['r_b_l2'].setPosition( new Vector3( - halfWidth + porchJointX, l7Y, - halfDepth ) );
        if( bones['r_b_r1'] ) bones['r_b_r1'].setPosition( new Vector3( halfWidth + this._porchCornerDistance, r5Y, - halfDepth ) );
        if( bones['r_b_t'] ) bones['r_b_t'].setPosition( new Vector3( 0, buildingHeight, - halfDepth ) );
        
        if( bones['rm_l1'] ) bones['rm_l1'].setPosition( new Vector3( porchPoint - this._porchCornerDistance, l5Y, - halfDepth ) );
        if( bones['rm_l2'] ) bones['rm_l2'].setPosition( new Vector3( - halfWidth + porchJointX, l7Y, - halfDepth ) );
        if( bones['rm_r1'] ) bones['rm_r1'].setPosition( new Vector3( halfWidth + this._porchCornerDistance, r5Y, - halfDepth ) );
        if( bones['rm_t'] ) bones['rm_t'].setPosition( new Vector3( 0, buildingHeight, - halfDepth ) );
        
        if( bones['column_b'] ) bones['column_b'].setPosition( new Vector3( porchPoint, 0, 0 ) );
        if( bones['column_t'] ) bones['column_t'].setPosition( new Vector3( porchPoint, wallHeight + aframeCabin.porchOffsetHeight, 0 ) );
        
        if( bones['rail_v_r_b_l1'] ) bones['rail_v_r_b_l1'].setPosition( new Vector3( - halfWidth, bones['rail_v_r_b_l1'].position.y, halfDepth ) );
        if( bones['rail_v_r_f_l1'] ) bones['rail_v_r_f_l1'].setPosition( new Vector3( porchPoint, bones['rail_v_r_f_l1'].position.y, halfDepth ) );
        
        if( bones['rail_h_r_f_l1'] ) bones['rail_h_r_f_l1'].setPosition( new Vector3( porchPoint, bones['rail_h_r_f_l1'].position.y, bones['rail_h_r_f_l1'].position.z ) );
        if( bones['rail_h_r_f_l2'] ) bones['rail_h_r_f_l2'].setPosition( new Vector3( porchPoint, bones['rail_h_r_f_l2'].position.y, bones['rail_h_r_f_l2'].position.z ) );
        
        if( bones['rail_h_l_f_l1'] ) bones['rail_h_l_f_l1'].setPosition( new Vector3( porchPoint, bones['rail_h_l_f_l1'].position.y, bones['rail_h_l_f_l1'].position.z ) );
        if( bones['rail_h_l_f_l2'] ) bones['rail_h_l_f_l2'].setPosition( new Vector3( porchPoint, bones['rail_h_l_f_l2'].position.y, bones['rail_h_l_f_l2'].position.z ) );
        
        if( bones['rail_v_l_b_l1'] ) bones['rail_v_l_b_l1'].setPosition( new Vector3( - halfWidth, bones['rail_v_l_b_l1'].position.y, - halfDepth ) );
        if( bones['rail_v_l_f_l1'] ) bones['rail_v_l_f_l1'].setPosition( new Vector3( porchPoint, bones['rail_v_l_f_l1'].position.y, - halfDepth ) );

        if( bones['baluster_f_l1'] ) bones['baluster_f_l1'].setPosition( new Vector3( 0.2, 0, 0 ) );
    }

    aframe2StoryAtticLeanToMoveBones = ( halfWidth, halfDepth ) => {
        if( 'structure_aframe_2story_attic_leanto' !== this._shedType ) {
            return;
        }

        let bones               = this._bones;
        const wallHeight        = this.getWallHeight();
        const leanToWidth       = atticModel.leanToWidth;
        const leanToRoofDegree  = atticModel.aFrameLeanToRoof
        const leanToRoofHeight  = leanToWidth * leanToRoofDegree;
        const leanToHeight      = atticModel.leanToHeight || wallHeight;
        const leanToFullHeight  = helpers.toFixed3( leanToHeight + leanToRoofHeight );

        const porchWidth        = atticModel.porchWidth;
        const porchRoofDegree   = atticModel.aFramePorchRoof;
        const porchOffsetHeight = atticModel.porchOffsetHeight;
        const porchRoofHeight   = porchWidth * porchRoofDegree;
        const porchHeight       = atticModel.porchHeight || wallHeight;
        const openingHeight     = helpers.toFixed3( porchHeight - porchOffsetHeight );
        const porchFullHeight   = helpers.toFixed3( porchHeight + porchRoofHeight );
        
        const secondFloorHeight = this.getSecondFloorHeight();
        const fullWallHeight    = helpers.toFixed3( wallHeight + secondFloorHeight );
        const roofHeight        = helpers.toFixed3( helpers.getTanFromDegrees( this.getRoofDegree() ) * halfWidth );
        const buildingHeight    = helpers.toFixed3( fullWallHeight + roofHeight );

        this.setBuildingHeight( buildingHeight );

        if( bones['b_f_l1'] ) bones['b_f_l1'].setPosition( new Vector3( - halfWidth, 0, halfDepth ) );
        if( bones['b_f_l2'] ) bones['b_f_l2'].setPosition( new Vector3( - halfWidth, fullWallHeight, halfDepth ) );
        if( bones['b_f_r1'] ) bones['b_f_r1'].setPosition( new Vector3( halfWidth, 0, halfDepth ) );
        if( bones['b_f_r2'] ) bones['b_f_r2'].setPosition( new Vector3( halfWidth, fullWallHeight, halfDepth ) );
        if( bones['b_f_t'] ) bones['b_f_t'].setPosition( new Vector3( 0, buildingHeight, halfDepth ) );
        
        if( bones['b_b_l1'] ) bones['b_b_l1'].setPosition( new Vector3( - halfWidth, 0, - halfDepth ) );
        if( bones['b_b_l2'] ) bones['b_b_l2'].setPosition( new Vector3( - halfWidth, fullWallHeight, - halfDepth ) );
        if( bones['b_b_r1'] ) bones['b_b_r1'].setPosition( new Vector3( halfWidth, 0, - halfDepth ) );
        if( bones['b_b_r2'] ) bones['b_b_r2'].setPosition( new Vector3( halfWidth, fullWallHeight, - halfDepth ) );
        if( bones['b_b_t'] ) bones['b_b_t'].setPosition( new Vector3( 0, buildingHeight, - halfDepth ) );
        
        if( bones['r_f_l1'] ) bones['r_f_l1'].setPosition( new Vector3( bones['b_f_l2'].position.x, bones['b_f_l2'].position.y, bones['b_f_l2'].position.z ) );
        if( bones['r_f_r1'] ) bones['r_f_r1'].setPosition( new Vector3( bones['b_f_r2'].position.x, bones['b_f_r2'].position.y, bones['b_f_r2'].position.z ) );
        if( bones['r_f_t'] ) bones['r_f_t'].setPosition( new Vector3( bones['b_f_t'].position.x, bones['b_f_t'].position.y, bones['b_f_t'].position.z ) );
        if( bones['r_b_l1'] ) bones['r_b_l1'].setPosition( new Vector3( bones['b_b_l2'].position.x, bones['b_b_l2'].position.y, bones['b_b_l2'].position.z ) );
        if( bones['r_b_r1'] ) bones['r_b_r1'].setPosition( new Vector3( bones['b_b_r2'].position.x, bones['b_b_r2'].position.y, bones['b_b_r2'].position.z ) );
        if( bones['r_b_t'] ) bones['r_b_t'].setPosition( new Vector3( bones['b_b_t'].position.x, bones['b_b_t'].position.y, bones['b_b_t'].position.z ) );
        
        if( bones['rm_l1'] ) bones['rm_l1'].setPosition( new Vector3( bones['b_b_l2'].position.x, bones['b_b_l2'].position.y, bones['b_b_l2'].position.z ) );
        if( bones['rm_r1'] ) bones['rm_r1'].setPosition( new Vector3( bones['b_b_r2'].position.x, bones['b_b_r2'].position.y, bones['b_b_r2'].position.z ) );
        if( bones['rm_t'] ) bones['rm_t'].setPosition( new Vector3( bones['b_b_t'].position.x, bones['b_b_t'].position.y, bones['b_b_t'].position.z ) );
        
        if( bones['lean_to_f_r1'] ) bones['lean_to_f_r1'].setPosition( new Vector3( bones['b_f_r1'].position.x, bones['b_f_r1'].position.y, bones['b_f_r1'].position.z ) );
        if( bones['lean_to_f_r2'] ) bones['lean_to_f_r2'].setPosition( new Vector3( bones['b_f_r1'].position.x + leanToWidth, bones['b_f_r1'].position.y, bones['b_f_r1'].position.z ) );
        if( bones['lean_to_f_r3'] ) bones['lean_to_f_r3'].setPosition( new Vector3( bones['lean_to_f_r2'].position.x, leanToHeight, bones['b_f_r1'].position.z ) );
        if( bones['lean_to_f_r4'] ) bones['lean_to_f_r4'].setPosition( new Vector3( bones['b_f_r1'].position.x, leanToFullHeight, bones['b_f_r1'].position.z ) );
        
        if( bones['lean_to_b_r1'] ) bones['lean_to_b_r1'].setPosition( new Vector3( bones['b_b_r1'].position.x, bones['b_b_r1'].position.y, bones['b_b_r1'].position.z ) );
        if( bones['lean_to_b_r2'] ) bones['lean_to_b_r2'].setPosition( new Vector3( bones['b_b_r1'].position.x + leanToWidth, bones['b_b_r1'].position.y, bones['b_b_r1'].position.z ) );
        if( bones['lean_to_b_r3'] ) bones['lean_to_b_r3'].setPosition( new Vector3( bones['lean_to_b_r2'].position.x, leanToHeight, bones['b_b_r1'].position.z ) );
        if( bones['lean_to_b_r4'] ) bones['lean_to_b_r4'].setPosition( new Vector3( bones['b_b_r1'].position.x, leanToFullHeight, bones['b_b_r1'].position.z ) );
        
        if( bones['roof_metal_lean_to_f_r1'] ) bones['roof_metal_lean_to_f_r1'].setPosition( new Vector3( bones['lean_to_f_r3'].position.x, bones['lean_to_f_r3'].position.y, bones['lean_to_f_r3'].position.z ) );
        if( bones['roof_metal_lean_to_f_t'] ) bones['roof_metal_lean_to_f_t'].setPosition( new Vector3( bones['lean_to_f_r4'].position.x, bones['lean_to_f_r4'].position.y, bones['lean_to_f_r4'].position.z ) );
        if( bones['roof_metal_lean_to_b_r1'] ) bones['roof_metal_lean_to_b_r1'].setPosition( new Vector3( bones['lean_to_b_r3'].position.x, bones['lean_to_b_r3'].position.y, bones['lean_to_b_r3'].position.z ) );
        if( bones['roof_metal_lean_to_b_t'] ) bones['roof_metal_lean_to_b_t'].setPosition( new Vector3( bones['lean_to_b_r4'].position.x, bones['lean_to_b_r4'].position.y, bones['lean_to_b_r4'].position.z ) );
        if( bones['roof_metal_lean_to_r1'] ) bones['roof_metal_lean_to_r1'].setPosition( new Vector3( bones['roof_metal_lean_to_b_r1'].position.x, bones['roof_metal_lean_to_b_r1'].position.y, bones['roof_metal_lean_to_b_r1'].position.z ) );
        if( bones['roof_metal_lean_to_t'] ) bones['roof_metal_lean_to_t'].setPosition( new Vector3( bones['roof_metal_lean_to_b_t'].position.x, bones['roof_metal_lean_to_b_t'].position.y, bones['roof_metal_lean_to_b_t'].position.z ) );
        
        if( bones['porch_f_l1'] ) bones['porch_f_l1'].setPosition( new Vector3( bones['b_f_l1'].position.x - porchWidth, bones['b_f_l1'].position.y, bones['b_f_l1'].position.z ) );
        if( bones['porch_f_l2'] ) bones['porch_f_l2'].setPosition( new Vector3( bones['porch_f_l1'].position.x, openingHeight, bones['porch_f_l1'].position.z ) );
        if( bones['porch_f_l3'] ) bones['porch_f_l3'].setPosition( new Vector3( bones['porch_f_l2'].position.x, porchHeight , bones['porch_f_l2'].position.z ) );
        if( bones['porch_f_l4'] ) bones['porch_f_l4'].setPosition( new Vector3( bones['b_f_l1'].position.x, porchFullHeight, bones['b_f_l1'].position.z ) );
        if( bones['porch_f_l5'] ) bones['porch_f_l5'].setPosition( new Vector3( bones['b_f_l1'].position.x, bones['porch_f_l2'].position.y, bones['b_f_l1'].position.z ) );
        
        if( bones['porch_b_l1'] ) bones['porch_b_l1'].setPosition( new Vector3( bones['b_b_l1'].position.x - porchWidth, bones['b_b_l1'].position.y, bones['b_b_l1'].position.z ) );
        if( bones['porch_b_l2'] ) bones['porch_b_l2'].setPosition( new Vector3( bones['porch_b_l1'].position.x, openingHeight, bones['porch_b_l1'].position.z ) );
        if( bones['porch_b_l3'] ) bones['porch_b_l3'].setPosition( new Vector3( bones['porch_b_l2'].position.x, porchHeight, bones['porch_b_l2'].position.z ) );
        if( bones['porch_b_l4'] ) bones['porch_b_l4'].setPosition( new Vector3( bones['b_b_l1'].position.x, porchFullHeight, bones['b_b_l1'].position.z ) );
        if( bones['porch_b_l5'] ) bones['porch_b_l5'].setPosition( new Vector3( bones['b_b_l1'].position.x, bones['porch_b_l2'].position.y, bones['b_b_l1'].position.z ) );
        
        if( bones['roof_porch_f_l1'] ) bones['roof_porch_f_l1'].setPosition( new Vector3( bones['porch_f_l3'].position.x, bones['porch_f_l3'].position.y, bones['porch_f_l3'].position.z ) );
        if( bones['roof_porch_f_t'] ) bones['roof_porch_f_t'].setPosition( new Vector3( bones['porch_f_l4'].position.x, bones['porch_f_l4'].position.y, bones['porch_f_l4'].position.z ) );
        if( bones['roof_porch_b_l1'] ) bones['roof_porch_b_l1'].setPosition( new Vector3( bones['porch_b_l3'].position.x, bones['porch_b_l3'].position.y, bones['porch_b_l3'].position.z ) );
        if( bones['roof_porch_b_t'] ) bones['roof_porch_b_t'].setPosition( new Vector3( bones['porch_b_l4'].position.x, bones['porch_b_l4'].position.y, bones['porch_b_l4'].position.z ) );
        if( bones['roof_metal_porch_l1'] ) bones['roof_metal_porch_l1'].setPosition( new Vector3( bones['porch_b_l3'].position.x, bones['porch_b_l3'].position.y, bones['porch_b_l3'].position.z ) );
        if( bones['roof_metal_porch_t'] ) bones['roof_metal_porch_t'].setPosition( new Vector3( bones['porch_b_l4'].position.x, bones['porch_b_l4'].position.y, bones['porch_b_l4'].position.z ) );
        
        if( bones['column_b'] ) bones['column_b'].setPosition( new Vector3( 0, 0, 0 ) );
        if( bones['column_t'] ) bones['column_t'].setPosition( new Vector3( 0, openingHeight + 0.25, 0 ) );
        
        if( bones['s_f_f_l1'] ) bones['s_f_f_l1'].setPosition( new Vector3( bones['b_f_l1'].position.x, wallHeight, bones['b_f_l1'].position.z ) );
        if( bones['s_f_f_r1'] ) bones['s_f_f_r1'].setPosition( new Vector3( bones['b_f_r1'].position.x, wallHeight, bones['b_f_r1'].position.z ) );
        if( bones['s_f_b_l1'] ) bones['s_f_b_l1'].setPosition( new Vector3( bones['b_b_l1'].position.x, wallHeight, bones['b_b_l1'].position.z ) );
        if( bones['s_f_b_r1'] ) bones['s_f_b_r1'].setPosition( new Vector3( bones['b_b_r1'].position.x, wallHeight, bones['b_b_r1'].position.z ) );
    }

    is2StoryLeanTo = () => {
        return 'structure_aframe_2story_attic_leanto' === this._shedType || 'structure_barn_2story_leanto' === this._shedType;
    }

    barn2StoryLeanToMoveBones = ( halfWidth, halfDepth ) => {
        if( 'structure_barn_2story_leanto' !== this._shedType ) {
            return;
        }

        let bones               = this._bones;
        const wallHeight        = this.getWallHeight();
        const barnLeanToSpace   = atticModel.barnLeanToSpace;
        const leanToHeight      = atticModel.leanToHeight || helpers.toFixed3( ( wallHeight * 12 - barnLeanToSpace ) / 12 );
        const leanToWidth       = atticModel.leanToWidth;
        const leanToRoofDegree  = atticModel.barnLeanToRoof;
        const fullLeanToHeight  = helpers.toFixed3( leanToHeight + leanToWidth * leanToRoofDegree );

        const porchHeight       = atticModel.porchHeight || helpers.toFixed3( wallHeight - barnLeanToSpace / 12 );
        const porchRoofDegree   = atticModel.barnPorchRoof;
        const porchWidth        = atticModel.porchWidth;
        const porchWallPoint    = helpers.toFixed3( porchHeight + atticModel.porchOffsetHeight );
        const fullPorchHeight   = helpers.toFixed3( porchWallPoint + ( porchWidth * porchRoofDegree ) + atticModel.barnOffsetHeight );

        const secondFloorHeight = helpers.toFixed3( wallHeight + this.getSecondFloorHeight() );
        const ratioX            = helpers.toFixed3( halfWidth / 3 );
        const pointX            = ratioX * 2;
        const roofLowerHeight   = helpers.toFixed3( secondFloorHeight + ( helpers.getTanFromDegrees( this._roofLowerDegree ? this._roofLowerDegree : this._initRoofLowerDegree ) * ratioX ) );
        const buildingHeight    = helpers.toFixed3( roofLowerHeight + ( helpers.getTanFromDegrees( this._roofUpperDegree ? this._roofUpperDegree : this._initRoofUpperDegree ) * pointX ) );

        this.setBuildingHeight( buildingHeight );

        if( bones['b_f_l1'] ) bones['b_f_l1'].setPosition( new Vector3( - halfWidth, 0, halfDepth ) );
        if( bones['b_f_l2'] ) bones['b_f_l2'].setPosition( new Vector3( - halfWidth, secondFloorHeight, halfDepth ) );
        if( bones['b_f_l3'] ) bones['b_f_l3'].setPosition( new Vector3( - pointX, roofLowerHeight, halfDepth ) );
        if( bones['b_f_r1'] ) bones['b_f_r1'].setPosition( new Vector3( halfWidth, 0, halfDepth ) );
        if( bones['b_f_r2'] ) bones['b_f_r2'].setPosition( new Vector3( halfWidth, secondFloorHeight, halfDepth ) );
        if( bones['b_f_r3'] ) bones['b_f_r3'].setPosition( new Vector3( pointX, roofLowerHeight, halfDepth ) );
        if( bones['b_f_t'] ) bones['b_f_t'].setPosition( new Vector3( 0, buildingHeight, halfDepth ) );

        if( bones['b_b_l1'] ) bones['b_b_l1'].setPosition( new Vector3( - halfWidth, 0, - halfDepth ) );
        if( bones['b_b_l2'] ) bones['b_b_l2'].setPosition( new Vector3( - halfWidth, secondFloorHeight, - halfDepth ) );
        if( bones['b_b_l3'] ) bones['b_b_l3'].setPosition( new Vector3( - pointX, roofLowerHeight, - halfDepth ) );
        if( bones['b_b_r1'] ) bones['b_b_r1'].setPosition( new Vector3( halfWidth, 0, - halfDepth ) );
        if( bones['b_b_r2'] ) bones['b_b_r2'].setPosition( new Vector3( halfWidth, secondFloorHeight, - halfDepth ) );
        if( bones['b_b_r3'] ) bones['b_b_r3'].setPosition( new Vector3( pointX, roofLowerHeight, - halfDepth ) );
        if( bones['b_b_t'] ) bones['b_b_t'].setPosition( new Vector3( 0, buildingHeight, - halfDepth ) );

        if( bones['r_f_l1'] ) bones['r_f_l1'].setPosition( new Vector3( - halfWidth, secondFloorHeight, halfDepth ) );
        if( bones['r_f_l2'] ) bones['r_f_l2'].setPosition( new Vector3( - pointX, roofLowerHeight, halfDepth ) );
        if( bones['r_f_r1'] ) bones['r_f_r1'].setPosition( new Vector3( halfWidth, secondFloorHeight, halfDepth ) );
        if( bones['r_f_r2'] ) bones['r_f_r2'].setPosition( new Vector3( pointX, roofLowerHeight, halfDepth ) );
        if( bones['r_f_t'] ) bones['r_f_t'].setPosition( new Vector3( 0, buildingHeight, halfDepth ) );

        if( bones['r_b_l1'] ) bones['r_b_l1'].setPosition( new Vector3( - halfWidth, secondFloorHeight, - halfDepth ) );
        if( bones['r_b_l2'] ) bones['r_b_l2'].setPosition( new Vector3( - pointX, roofLowerHeight, - halfDepth ) );
        if( bones['r_b_r1'] ) bones['r_b_r1'].setPosition( new Vector3( halfWidth, secondFloorHeight, - halfDepth ) );
        if( bones['r_b_r2'] ) bones['r_b_r2'].setPosition( new Vector3( pointX, roofLowerHeight, - halfDepth ) );
        if( bones['r_b_t'] ) bones['r_b_t'].setPosition( new Vector3( 0, buildingHeight, - halfDepth ) );

        if( bones['rm_l1'] ) bones['rm_l1'].setPosition( new Vector3( - halfWidth, secondFloorHeight, - halfDepth ) );
        if( bones['rm_l2'] ) bones['rm_l2'].setPosition( new Vector3( - pointX, roofLowerHeight, - halfDepth ) );
        if( bones['rm_r1'] ) bones['rm_r1'].setPosition( new Vector3( halfWidth, secondFloorHeight, - halfDepth ) );
        if( bones['rm_r2'] ) bones['rm_r2'].setPosition( new Vector3( pointX, roofLowerHeight, - halfDepth ) );
        if( bones['rm_t'] ) bones['rm_t'].setPosition( new Vector3( 0, buildingHeight, - halfDepth ) );
        
        if( bones['lean_to_f_r1'] ) bones['lean_to_f_r1'].setPosition( new Vector3( halfWidth, 0, halfDepth ) );
        if( bones['lean_to_f_r2'] ) bones['lean_to_f_r2'].setPosition( new Vector3( bones['lean_to_f_r1'].position.x + leanToWidth, bones['lean_to_f_r1'].position.y, halfDepth ) );
        if( bones['lean_to_f_r3'] ) bones['lean_to_f_r3'].setPosition( new Vector3( bones['lean_to_f_r2'].position.x, leanToHeight, halfDepth ) );
        if( bones['lean_to_f_r4'] ) bones['lean_to_f_r4'].setPosition( new Vector3( halfWidth, fullLeanToHeight, halfDepth ) );
        
        if( bones['lean_to_b_r1'] ) bones['lean_to_b_r1'].setPosition( new Vector3( halfWidth, 0, - halfDepth ) );
        if( bones['lean_to_b_r2'] ) bones['lean_to_b_r2'].setPosition( new Vector3( bones['lean_to_b_r1'].position.x + leanToWidth, bones['lean_to_b_r1'].position.y, - halfDepth ) );
        if( bones['lean_to_b_r3'] ) bones['lean_to_b_r3'].setPosition( new Vector3( bones['lean_to_b_r2'].position.x, leanToHeight, - halfDepth ) );
        if( bones['lean_to_b_r4'] ) bones['lean_to_b_r4'].setPosition( new Vector3( halfWidth, fullLeanToHeight, - halfDepth ) );

        if( bones['roof_metal_lean_to_f_r1'] ) bones['roof_metal_lean_to_f_r1'].setPosition( new Vector3( bones['lean_to_f_r3'].position.x, bones['lean_to_f_r3'].position.y, bones['lean_to_f_r3'].position.z ) );
        if( bones['roof_metal_lean_to_f_t'] ) bones['roof_metal_lean_to_f_t'].setPosition( new Vector3( bones['lean_to_f_r4'].position.x, bones['lean_to_f_r4'].position.y, bones['lean_to_f_r4'].position.z ) );
        if( bones['roof_metal_lean_to_b_r1'] ) bones['roof_metal_lean_to_b_r1'].setPosition( new Vector3( bones['lean_to_b_r3'].position.x, bones['lean_to_b_r3'].position.y, bones['lean_to_b_r3'].position.z ) );
        if( bones['roof_metal_lean_to_b_t'] ) bones['roof_metal_lean_to_b_t'].setPosition( new Vector3( bones['lean_to_b_r4'].position.x, bones['lean_to_b_r4'].position.y, bones['lean_to_b_r4'].position.z ) );
        if( bones['roof_metal_lean_to_r1'] ) bones['roof_metal_lean_to_r1'].setPosition( new Vector3( bones['roof_metal_lean_to_b_r1'].position.x, bones['roof_metal_lean_to_b_r1'].position.y, bones['roof_metal_lean_to_b_r1'].position.z ) );
        if( bones['roof_metal_lean_to_t'] ) bones['roof_metal_lean_to_t'].setPosition( new Vector3( bones['roof_metal_lean_to_b_t'].position.x, bones['roof_metal_lean_to_b_t'].position.y, bones['roof_metal_lean_to_b_t'].position.z ) );

        if( bones['porch_f_l1'] ) bones['porch_f_l1'].setPosition( new Vector3( bones['b_f_l1'].position.x - porchWidth, bones['b_f_l1'].position.y, bones['b_f_l1'].position.z ) );
        if( bones['porch_f_l2'] ) bones['porch_f_l2'].setPosition( new Vector3( bones['porch_f_l1'].position.x, porchHeight, bones['porch_f_l1'].position.z ) );
        if( bones['porch_f_l3'] ) bones['porch_f_l3'].setPosition( new Vector3( bones['porch_f_l2'].position.x, porchWallPoint , bones['porch_f_l2'].position.z ) );
        if( bones['porch_f_l4'] ) bones['porch_f_l4'].setPosition( new Vector3( bones['b_f_l1'].position.x, fullPorchHeight, bones['b_f_l1'].position.z ) );
        if( bones['porch_f_l5'] ) bones['porch_f_l5'].setPosition( new Vector3( bones['b_f_l1'].position.x, bones['porch_f_l2'].position.y, bones['b_f_l1'].position.z ) );
        
        if( bones['porch_b_l1'] ) bones['porch_b_l1'].setPosition( new Vector3( bones['b_b_l1'].position.x - porchWidth, bones['b_b_l1'].position.y, bones['b_b_l1'].position.z ) );
        if( bones['porch_b_l2'] ) bones['porch_b_l2'].setPosition( new Vector3( bones['porch_b_l1'].position.x, porchHeight, bones['porch_b_l1'].position.z ) );
        if( bones['porch_b_l3'] ) bones['porch_b_l3'].setPosition( new Vector3( bones['porch_b_l2'].position.x, porchWallPoint, bones['porch_b_l2'].position.z ) );
        if( bones['porch_b_l4'] ) bones['porch_b_l4'].setPosition( new Vector3( bones['b_b_l1'].position.x, fullPorchHeight, bones['b_b_l1'].position.z ) );
        if( bones['porch_b_l5'] ) bones['porch_b_l5'].setPosition( new Vector3( bones['b_b_l1'].position.x, bones['porch_b_l2'].position.y, bones['b_b_l1'].position.z ) );
        
        if( bones['roof_porch_f_l1'] ) bones['roof_porch_f_l1'].setPosition( new Vector3( bones['porch_f_l3'].position.x, bones['porch_f_l3'].position.y, bones['porch_f_l3'].position.z ) );
        if( bones['roof_porch_f_t'] ) bones['roof_porch_f_t'].setPosition( new Vector3( bones['porch_f_l4'].position.x, bones['porch_f_l4'].position.y, bones['porch_f_l4'].position.z ) );
        if( bones['roof_porch_b_l1'] ) bones['roof_porch_b_l1'].setPosition( new Vector3( bones['porch_b_l3'].position.x, bones['porch_b_l3'].position.y, bones['porch_b_l3'].position.z ) );
        if( bones['roof_porch_b_t'] ) bones['roof_porch_b_t'].setPosition( new Vector3( bones['porch_b_l4'].position.x, bones['porch_b_l4'].position.y, bones['porch_b_l4'].position.z ) );
        if( bones['roof_metal_porch_l1'] ) bones['roof_metal_porch_l1'].setPosition( new Vector3( bones['porch_b_l3'].position.x, bones['porch_b_l3'].position.y, bones['porch_b_l3'].position.z ) );
        if( bones['roof_metal_porch_t'] ) bones['roof_metal_porch_t'].setPosition( new Vector3( bones['porch_b_l4'].position.x, bones['porch_b_l4'].position.y, bones['porch_b_l4'].position.z ) );
        
        if( bones['column_b'] ) bones['column_b'].setPosition( new Vector3( 0, 0, 0 ) );
        if( bones['column_t'] ) bones['column_t'].setPosition( new Vector3( 0, porchHeight + 0.25, 0 ) );

        if( bones['s_f_f_l1'] ) bones['s_f_f_l1'].setPosition( new Vector3( bones['b_f_l1'].position.x, wallHeight, bones['b_f_l1'].position.z ) );
        if( bones['s_f_f_r1'] ) bones['s_f_f_r1'].setPosition( new Vector3( bones['b_f_r1'].position.x, wallHeight, bones['b_f_r1'].position.z ) );
        if( bones['s_f_b_l1'] ) bones['s_f_b_l1'].setPosition( new Vector3( bones['b_b_l1'].position.x, wallHeight, bones['b_b_l1'].position.z ) );
        if( bones['s_f_b_r1'] ) bones['s_f_b_r1'].setPosition( new Vector3( bones['b_b_r1'].position.x, wallHeight, bones['b_b_r1'].position.z ) );
    }

    /**
     * Handles cloning and positioning of balusters for an A-Frame Single Gable structure.
     * 
     * @param {string} meshName - The name of the mesh to use for baluster cloning.
     * @param {Mesh} balusterObj - The original baluster mesh to be cloned.
     * @param {Vector3} oPosition - The position vector where the baluster is placed.
    */
    aframeSingleGableCloneBaluster = () => {
        if( ! this.isAframeSingleGable() ) return;

        const meshName = 'baluster_obj';
        const balusterObj = this._scene.getMeshByName( meshName ) as Mesh;
        const space = this._balusterMiddleSpace * 12;
        const meshNameClone = `${meshName}__1`;

        let stubClone1 = this.getMeshByName( meshNameClone ) as Mesh;
        if( ! stubClone1 ) {
            stubClone1 = balusterObj.clone( meshNameClone );
        }
        stubClone1.skeleton = balusterObj.skeleton;

        this.removeClone( meshNameClone );

        if( this._enabledRailing ) {
            const results = this.instanceMesh( meshNameClone, space, 'b_p_l4', 'b_b_l1' );

            for( let i = 1; i <= results.length; i++ ) {
                this._instances['baluster'] = helpers.checkAndPushKeyToArray( this._instances['baluster'], `${meshNameClone}_${i}` );
            }
        } else {
            balusterObj.setEnabled( false );
            stubClone1.setEnabled( false );
        }
    }

    ranchCabinCloneBaluster = ( meshName, balusterObj, oPosition ) => {
        const space         = this._balusterMiddleSpace * 12;
        const meshName1     = `${meshName}__1`;
        const meshName2     = `${meshName}__2`;
        let stubClone1      = this.getMeshByName( meshName1 ) as Mesh;
        if( ! stubClone1 ) {
            stubClone1      = balusterObj.clone( meshName1 );
        }
        stubClone1.skeleton = balusterObj.skeleton;

        this.removeClone( meshName );
        this.removeClone( meshName1 );
        this.removeClone( meshName2 );

        if( this._enabledRailing ) {
            this.instanceMesh( meshName, space, 'rail_v_l_f_l1', 'rail_v_l_b_l1' );
            this.instanceMesh( meshName1, space, 'rail_h_r_f_l1', 'rail_h_r_f_l2' );

            let stubClone2      = this.getMeshByName( meshName2 ) as Mesh;
            if( ! stubClone2 ) {
                stubClone2      = balusterObj.clone( meshName2 );
            }
            stubClone2.skeleton = balusterObj.skeleton;

            if( 'middle' === oPosition ) {
                this.instanceMesh( meshName2, space, 'rail_h_l_f_l2', 'rail_h_l_f_l1' );
            } else {
                stubClone2.setEnabled( false );
            }
        } else {
            balusterObj.setEnabled( false );
            stubClone1.setEnabled( false );
        }
    }

    aframeCabinCloneBaluster = ( meshName, balusterObj, oPosition ) => {
        const space         = this._balusterMiddleSpace * 12;
        const meshName1     = `${meshName}__1`;
        const meshName2     = `${meshName}__2`;
        const meshName3     = `${meshName}__3`;

        let stubClone1      = this.getMeshByName( meshName1 ) as Mesh;
        if( ! stubClone1 ) {
            stubClone1      = balusterObj.clone( meshName1 );
        }
        stubClone1.skeleton = balusterObj.skeleton;
        stubClone1.rotation = new Vector3( 0, Math.PI / 2, 0 );

        let stubClone2      = this.getMeshByName( meshName2 ) as Mesh;
        if( ! stubClone2 ) {
            stubClone2      = balusterObj.clone( meshName2 );
        }
        stubClone2.skeleton = balusterObj.skeleton;

        let stubClone3      = this.getMeshByName( meshName3 ) as Mesh;
        if( ! stubClone3 ) {
            stubClone3      = balusterObj.clone( meshName3 );
        }
        stubClone3.skeleton = balusterObj.skeleton;

        this.removeClone( meshName );
        this.removeClone( meshName1 );
        this.removeClone( meshName2 );
        this.removeClone( meshName3 );

        if( this._enabledRailing ) {
            this.instanceMesh( meshName, space, 'rail_v_l_b_l1', 'rail_v_l_f_l1' );
            this.instanceMesh( meshName1, space, 'rail_v_r_b_l1', 'rail_v_r_f_l1' );
            if( 'middle' === oPosition ) {
                this.instanceMesh( meshName2, space, 'rail_h_l_f_l2', 'rail_h_l_f_l1' );
                this.instanceMesh( meshName3, space, 'rail_h_r_f_l1', 'rail_h_r_f_l2' );
            } else if( 'left' === oPosition ) {
                this.instanceMesh( meshName2, space, 'rail_h_l_f_l2', 'rail_h_l_f_l1' );
            } else if( 'right' === oPosition ) {
                this.instanceMesh( meshName3, space, 'rail_h_r_f_l1', 'rail_h_r_f_l2' );
            }
        }

        balusterObj.setEnabled( false );
        stubClone1.setEnabled( false );
        stubClone2.setEnabled( false );
        stubClone3.setEnabled( false );
    }

    ranchCabinHandleOpening = ( status ) => {
        const meshName  = this.isEnableBrace() ? 'column_braces_obj' : 'column_obj';
        const postObj   = this._scene.getMeshByName( meshName ) as Mesh;
        if( null === postObj ) {
            return;
        }

        this.removeClone( meshName );

        const railLObj = this._scene.getMeshByName( 'rail_h_l_obj' ) as Mesh;
        const railRObj = this._scene.getMeshByName( 'rail_h_r_obj' ) as Mesh;
        if( ! status ) {
            postObj.setEnabled( false );
            railLObj.setEnabled( false );
            railRObj.setEnabled( false );
            return;
        }

        postObj.setEnabled( true );
        postObj.alwaysSelectAsActiveMesh = true;

        if( railLObj ) railLObj.setEnabled( true );
        if( railRObj ) railRObj.setEnabled( true );

        let bones       = this._bones;
        const depth     = this.getDepth();
        const oPosition = this.getOpeningPosition();
        if( 'middle' === oPosition ) {
            const opening   = this.isEnableCustomOpeningSize() ? this.getOpeningSize() : 4;
            let postClone   = this._scene.getMeshByName( `${meshName}_1` );
            if( null === postClone ) {
                postClone   = postObj.createInstance( `${meshName}_1` );
            }

            postClone.setEnabled( true );
            
            if( bones['rail_h_r_f_l1'] ) bones['rail_h_r_f_l1'].setPosition( new Vector3( bones['rail_h_r_f_l1'].position.x, bones['rail_h_r_f_l1'].position.y, bones['b_p_l1'].position.z ) );
            if( bones['rail_h_r_f_l2'] ) bones['rail_h_r_f_l2'].setPosition( new Vector3( bones['rail_h_r_f_l2'].position.x, bones['rail_h_r_f_l2'].position.y, bones['b_p_l4'].position.z + ( ( depth - this._cabinWidth ) / 2 ) + opening / 2 ) );
            
            if( bones['rail_h_l_f_l1'] ) bones['rail_h_l_f_l1'].setPosition( new Vector3( bones['b_p_l4'].position.x, bones['rail_h_l_f_l1'].position.y, bones['b_p_l4'].position.z ) );
            if( bones['rail_h_l_f_l2'] ) bones['rail_h_l_f_l2'].setPosition( new Vector3( bones['b_p_l4'].position.x, bones['rail_h_l_f_l2'].position.y, bones['b_p_l4'].position.z + ( ( depth - this._cabinWidth ) / 2 ) - opening / 2 ) );
            
            postClone.alwaysSelectAsActiveMesh  = true;
            postObj.position.z                  = bones['rail_h_r_f_l2'].position.z;
            postClone.position.z                = bones['rail_h_l_f_l2'].position.z;
        } else {
            if( railLObj ) railLObj.setEnabled( false );

            const positionZ     = bones['b_p_l4'].position.z + ( depth - this._cabinWidth ) / 2;
            postObj.position.z  = positionZ;

            if( 'left' === oPosition ) {
                if( bones['rail_h_r_f_l1'] ) bones['rail_h_r_f_l1'].setPosition( new Vector3( bones['rail_h_r_f_l1'].position.x, bones['rail_h_r_f_l1'].position.y, positionZ ) );
                if( bones['rail_h_r_f_l2'] ) bones['rail_h_r_f_l2'].setPosition( new Vector3( bones['rail_h_r_f_l2'].position.x, bones['rail_h_r_f_l2'].position.y, bones['b_p_l4'].position.z ) );
            } else if( 'right' === oPosition ) {
                if( bones['rail_h_r_f_l1'] ) bones['rail_h_r_f_l1'].setPosition( new Vector3( bones['rail_h_r_f_l1'].position.x, bones['rail_h_r_f_l1'].position.y, bones['b_p_l1'].position.z ) );
                if( bones['rail_h_r_f_l2'] ) bones['rail_h_r_f_l2'].setPosition( new Vector3( bones['rail_h_r_f_l2'].position.x, bones['rail_h_r_f_l2'].position.y, positionZ ) );
            }
        }
    }

    /**
     * Save shutters in object to global data
     * 
     * @param {String} side Component side
     * @param {String} name Component name
     * @param {String} parent Parent key
     * @param {String} type Component type
     */
    saveShuttersInObject = ( side, name, parent, type ) => {
        const componentType     = 'shutters';
        const key               = `${side}_${name}_${new Date().getTime()}`;
        const face              = 'exterior';
        const meshes            = this._walls[side]['items'][parent].meshes.filter( mesh => -1 !== mesh.name.indexOf( '_shutter_' ) );
        const price             = jQuery( `[name='child_shutters'][data-upgrade-component-name='${name}']` ).data( 'price' );
        const niceName          = jQuery( `[name='child_shutters'][data-upgrade-component-name='${name}']` ).data( 'option-value' );
        const priceType         = jQuery( `[name='child_shutters'][data-upgrade-component-name='${name}']` ).data( 'price-type' );
        const originalNiceName  = jQuery( `[name='child_shutters'][data-upgrade-component-name='${name}']` ).data( 'option-original-value' );
        const colorComponent    = this.getColorComponent( meshes, componentType );
        const colors            = [
            {
                type: componentType,
                type_display: colorLabels[componentType] ? colorLabels[componentType] : '',
                display: colorComponent.color,
                color_price: colorComponent.colorPrice,
                color_price_type: colorComponent.colorPriceType,
                texture_type: colorComponent.textureType,
                texture: colorComponent.texture,
                description: colorComponent.description,
            }
        ]
        const shutterInfo       = {
            key,
            face,
            type,
            name,
            side,
            price,
            parent,
            meshes,
            niceName,
            priceType,
            originalNiceName,
            colors,
        }

        this._shuttersInObject[key] = shutterInfo;
        window.dispatchEvent( new CustomEvent( 'add-component', { detail: shutterInfo } ) );
    }

    /**
     * Remove shutters in object
     * 
     * @param {String} shuttersKey Shutters key
     */
    removeShuttersInObject = ( shuttersKey ) => {
        delete this._shuttersInObject[shuttersKey];
    }

    /**
     * Get all shutters in object by side
     * 
     * @param {String} side Component side
     * 
     * @returns {Object}
     */
    getShuttersInObjectBySide = ( side ) => {
        let results     = {};
        const shutters  = this.getShuttersInObject();
        for( const key in shutters ) {
            if( side === shutters[key].side ) {
                results[key] = shutters[key];
            }
        }

        return results;
    }

    /**
     * Update shutters in object - shutters attached direct in the window
     * 
     * @param {String}  shuttersName Name of shutters
     * @param {Array}   meshes All meshes of parent component
     * @param {String}  side Component side
     * @param {String}  parentKey Key of parent component (window)
     */
    updateShuttersInObject = ( shuttersName, meshes, side, parentKey ) => {
        if( 'in_object_vinyl' === shuttersName ) {
            this.enableVinylShutter( meshes );
        } else if( 'in_object_wood' === shuttersName ) {
            this.enableWoodShutter( meshes );
        }

        this.updateColor( 'group-shutter-color' );
        this.saveShuttersInObject( side, shuttersName, parentKey, 'shutters' );
    }

    /**
     * Check shutters must be attached direct in the window?
     * 
     * @param {String} shuttersName Name of shutters
     * 
     * @returns {Boolean}
     */
    isShuttersInObject = ( shuttersName ) => {
        return 'in_object_vinyl' === shuttersName || 'in_object_wood' === shuttersName;
    }

    /**
     * Check component before add to scene
     * 
     * @param {String}  componentName Name of component
     * @param {Array}   parentMeshes List meshes of parent component
     * @param {String}  side Component side
     * @param {String}  parentKey Key of parent component
     * @param {String}  componentType Component type (shutters, ramp,...)
     */
    addNewShutters = ( componentName, parentMeshes, side, parentKey, componentType = 'shutters' ) => {
        return new Promise( async resolve => {
            if( this.isShuttersInObject( componentName ) ) {
                this.updateShuttersInObject( componentName, parentMeshes, side, parentKey );
            } else {
                await this.addNewComponent( { side, componentType, componentName, parentKey } );
            }

            resolve( true );
        });
    }

    /**
     * Check addon is exist in the list addon of window
     * 
     * @param {String} name Name of shutters
     * @param {String} parentName Parent component name (window)
     * 
     * @returns {Boolean}
     */
    isExistAddon = ( name, parentName, key ) => {
        let windowAddon: any = jQuery( `[data-upgrade-component-name='${parentName}']` ).data( key );
        if( undefined === windowAddon ) {
            return false;
        }

        windowAddon = windowAddon ? JSON.parse( atob( windowAddon ) ) : [];

        return helpers.hasKeyInArray( windowAddon, name );
    }

    /**
     * Check is valid shutters
     * Condition: Associated Siding Material and in the list shutters of window
     * 
     * @param {String} sidingMaterial Siding material
     * @param {String} shuttersName Name of shutters
     * @param {String} parentName Parent component name (window)
     * 
     * @returns {Boolean}
     */
    isValidShutters = ( sidingMaterial, shuttersName, parentName ) => {
        let materials = jQuery( `[name='child_shutters'][data-upgrade-component-name="${shuttersName}"]` ).data( 'materials' );
        if( undefined === materials ) {
            return false;
        }

        let windowShutters  = jQuery( `[data-upgrade-component-name='${parentName}']` ).data( 'shutters' );
        if( undefined === windowShutters ) {
            return false;
        }

        materials       = materials ? JSON.parse( atob( materials ) ) : [];
        windowShutters  = windowShutters ? JSON.parse( atob( windowShutters ) ) : [];
        
        return -1 !== materials.indexOf( sidingMaterial ) && -1 !== windowShutters.indexOf( shuttersName );
    }

    /**
     * Get first shutters that satisfy all conditions (Associated Siding Material)
     * 
     * @param {String} sidingMaterial Siding material
     * @param {String} parentName Parent component name (window)
     * 
     * @returns {String|Boolean}
     */
    getFirstShutters = ( sidingMaterial, parentName ) => {
        let windowShutters  = jQuery( `[data-upgrade-component-name='${parentName}']` ).data( 'shutters' );
        windowShutters      = windowShutters ? JSON.parse( atob( windowShutters ) ) : [];
        const shuttersList  = jQuery( `[name='child_shutters']:not(#none_shutters)` );

        for( let i = 0; i < shuttersList.length; i++ ) {
            let availableMaterials  = jQuery( shuttersList[i] ).data( 'materials' );
            availableMaterials      = availableMaterials ? JSON.parse( atob( availableMaterials ) ) : [];
            const shuttersName      = jQuery( shuttersList[i] ).data( 'upgrade-component-name' );
            if( -1 !== availableMaterials.indexOf( sidingMaterial ) && -1 !== windowShutters.indexOf( shuttersName ) ) {
                return shuttersName;
            }
        }

        return false;
    }

    /**
     * Check is valid Flower Box
     * Condition: Associated Siding Material and in the list shutters of window
     * 
     * @param {String} name Name of shutters
     * @param {String} parentName Parent component name (window)
     * 
     * @returns {Boolean}
     */
    isValidFlowerBox = ( name, parentName ) => {
        let flowerBox = jQuery( `[data-upgrade-component-name='${parentName}']` ).data( 'flower_box' );
        if( undefined === flowerBox ) {
            return false;
        }

        flowerBox = flowerBox ? JSON.parse( atob( flowerBox ) ) : [];
        
        return helpers.hasKeyInArray( flowerBox, name );
    }

    /**
     * Get the first add-on that satisfies the specified conditions
     * 
     * @param {String} parentName Name of the parent component
     * @param {String} childType Type of the child component
     * 
     * @returns {String|Boolean} Name of the first valid add-on or false if none is found
     */
    getFirstAddOn = ( parentName, childType ) => {
        let addOnData = jQuery( `[data-upgrade-component-name='${parentName}']` ).data( `${childType}` );
        addOnData = addOnData ? JSON.parse( atob( addOnData ) ) : [];
        const listAddOns = jQuery( `[name='child_${childType}']:not(#none_${childType})` );

        for( let i = 0; i < listAddOns.length; i++ ) {
            const childName = jQuery( listAddOns[i] ).data( 'upgrade-component-name' );
            if( helpers.hasKeyInArray( addOnData, childName ) ) {
                return childName;
            }
        }

        return false;
    }

    /**
     * Get all meshes child
     * 
     * @param {Array} components Components list
     * 
     * @returns {Array}
     */
    getAllChildMeshes = ( components ) => {
        let results = [];
        for( let i = 0; i < components.length; i++ ) {
            results = results.concat( components[i].meshes );
        }

        return results;
    }

    /**
     * Filter mesh by key and value
     * 
     * @param {Array}   array List meshes
     * @param {String}  key Key need to filter
     * @param {String}  value Value need to filter
     * 
     * @returns {Array}
     */
    filterMeshes = ( array, key, value ) => {
        return array.filter( obj => obj[key].includes( value ) );
    }

    /**
     * Get position X for shutters follow meshes of parent component
     * 
     * @param {Array}   meshes All meshes of parent component
     * @param {String}  name Compoent name
     * 
     * @returns {Number}
     */
    getShuttersPositionX = ( meshes, name ) => {
        const shuttersBox = `${name}${constants.shuttersBox}`;
        for( let i = 0; i < meshes.length; i++ ) {
            if( shuttersBox === meshes[i].name ) {
                return helpers.toFixed3( ( meshes[i].geometry.extend.maximum.x - meshes[i].geometry.extend.minimum.x ) / 2 );
            }
        }

        const woodShutters  = `${name}_shutter_vinyl_obj`;
        const shuttersWidth = 0.75;
        for( let i = 0; i < meshes.length; i++ ) {
            if( woodShutters === meshes[i].name ) {
                return helpers.toFixed3( ( meshes[i].geometry.extend.maximum.x - meshes[i].geometry.extend.minimum.x ) / 2 - shuttersWidth );
            }
        }

        const objectName    = `${name}_obj`;
        const displayObject = `${name}_display_obj`;
        for( let i = 0; i < meshes.length; i++ ) {
            if( objectName === meshes[i].name || displayObject === meshes[i].name ) {
                return helpers.toFixed3( ( meshes[i].geometry.extend.maximum.x - meshes[i].geometry.extend.minimum.x ) / 2 );
            }
        }

        return 0;
    }

    /**
     * Get position Y for shutters follow meshes of parent component
     * 
     * @param {Array}   meshes All meshes of parent component
     * @param {String}  type Component type
     * @param {String}  name Compoent name
     * 
     * @returns {Number}
     */
    getShuttersPositionY = ( meshes, type, name ) => {
        const shuttersBox = `${name}${constants.shuttersBox}`;
        for( let i = 0; i < meshes.length; i++ ) {
            if( shuttersBox === meshes[i].name ) {
                return helpers.toFixed3( meshes[i].geometry.extend.maximum.y - meshes[i].geometry.extend.minimum.x );
            }
        }

        return this.getComponentHeight( meshes, type );
    }

    /**
     * Check window required shutters
     * 
     * @param {String} parentName Window name
     * * @param {String} attribute required-shutters | required-flower_box
     * 
     * @returns {Boolean}
     */
    isRequiredAddon = ( parentName : string, attribute : string ) => {
        return 1 === jQuery( `[data-upgrade-component-name='${parentName}']` ).data( attribute );
    }

    /**
     * Get the frame height based on the specified side and shed type.
     *
     * @param {string} side - The side for which the frame height is calculated.
     * @returns {number} - The calculated frame height.
     */
    getFrameHeight = ( side: string ) => {
        let frameHeight = this.getWallHeight();
        if( 'mainus_quaker' === this._shedType ) {
            frameHeight = frameHeight - 1;
        }

        if( 'structure_aframe_streamline' === this._shedType ) {
            frameHeight = frameHeight - 0.22;
        }

        if( 'structure_saltbox_run-in' === this._shedTypeVersion && 'left' !== side ) {
            frameHeight = frameHeight - 1.33;
        }

        if( 'left' === side && 'lean_to' === this._shedType ) {
            frameHeight = this.getBuildingHeight();
        } else if( 'left' === side && 'structure_saltbox_mwshed' === this._shedType ) {
            frameHeight = this._bones['b_b_l2'].position.y;
        } else if( 'left' === side && 'mainus_quaker' === this._shedType ) {
            frameHeight = frameHeight + 1;
        } else if( 'left' === side && 'structure_lean_to_with_front_porch' === this._shedType ) {
            frameHeight = this._middleHeight;
        }

        return frameHeight;
    }

    /**
     * Clone and position a frame mesh on a specific side of the shed.
     *
     * @param {string} side - The side for which the frame is cloned and positioned.
     */
    cloneFrameFollowSide = ( side: string ) => {
        const excludeSides = [ 'roof_left', 'roof_right', 'floor', 'porch', 'top_front', 'top_back', 'top_left', 'top_right', 'roof', 'dormer' ];

        if( undefined === this._frameMesh || ! this._frameRules || helpers.hasKeyInArray( excludeSides, side ) ) {
            return;
        }
        let distance    = 0;
        let offset:any  = 0;
        let isClone     = false;
        for( let key in this._frameRules ) {
            const item = this._frameRules[key];
            const sides = item.side;
            if( ! sides ) continue;
            if( sides.includes( 'all' ) || sides.includes( side ) ) {
                distance    = Number( item.distance );
                offset      = item.offset;
                isClone     = true;
            }
        }
        const cloneFull     = '' !== offset;
        offset              = 0 === Number( offset ) ? 0 : helpers.toFixed3( offset / 12 );
        const meshesInfo    = this.getWallBones( side );
        if( undefined === meshesInfo.left || undefined === meshesInfo.right || ! isClone ) {
            return;
        }

        const floorOffset   = this.getFloorOffset();
        const parentMesh    = this.getMeshByName( 'body_obj' );
        const rotation      = this.calculateComponentRotation( side, '' );
        const frameHeight   = this.getFrameHeight( side );
        const frameName     = constants.frameName + side;
        let mesh            = this._scene.getMeshByName( frameName );
        if( mesh ) mesh.dispose();
        mesh                = this._frameMesh.clone( frameName ) as Mesh;
        mesh.material       = this._frameMesh.material as StandardMaterial;
        mesh.rotation       = new Vector3( rotation.x, rotation.y, rotation.z );
        mesh.checkCollisions = true;
        const boneStart     = new Vector3( this._bones[meshesInfo.left].position.x, this._bones[meshesInfo.left].position.y + floorOffset + this._frameWidth, this._bones[meshesInfo.left].position.z );
        const boneEnd       = new Vector3( this._bones[meshesInfo.right].position.x, this._bones[meshesInfo.right].position.y + floorOffset + this._frameWidth, this._bones[meshesInfo.right].position.z );
        mesh.scaling.y      = helpers.toFixed3( ( frameHeight - floorOffset - 2 * this._frameWidth ) / this._frameHeight );
        
        const boneCloneStart = new Vector3( boneStart.x, boneStart.y, boneStart.z );
        const boneCloneEnd   = new Vector3( boneEnd.x, boneEnd.y, boneEnd.z );
       
        const axis = ( - 1 !== zSide.indexOf( side ) ) ? 'z' : 'x';

        if ( boneCloneStart[axis] > boneCloneEnd[axis] ) {
            boneCloneStart[axis] = boneCloneStart[axis] - offset;
            boneCloneEnd[axis]   = boneCloneEnd[axis] + offset;
        } else {
            boneCloneStart[axis] = boneCloneStart[axis] + offset;
            boneCloneEnd[axis]   = boneCloneEnd[axis] - offset;
        }

        this.cloneStud( { side, mesh, boneStart: boneCloneStart, boneEnd: boneCloneEnd, parentMesh, distance, cloneFull } );
        this.updateHorizontalFrame( { mesh, boneStart, boneEnd, side, frameHeight, parentMesh } );
    }

    /**
     * Clone frame
     * 
     * @param {String} name Babylon file name
     * @param {object} rules distribution roles between 2 frame
     * @param {Boolean} isChange Change or first load
     */
    cloneFrameComponent = async ( name: string, rules, isChange: Boolean ) => {
        this.handleShowHideStubModel( true );
        this.removeFrameInObject();
        let mainMesh = this._scene.getMeshByName( `${name}_obj` ) as Mesh;
        if( ! mainMesh ) {
            const frameMeshes   = await this.loadBabylonFile( name, 'stud' );
            if( frameMeshes ) {
                mainMesh        = frameMeshes[0] as Mesh;
            }
            
        }

        this.removeFrame();

        if( ! mainMesh || null === mainMesh.geometry ) {
            return;
        }
        const oldFrameMesh  = this._frameMesh;
        const oldFrameDepth = this._frameDepth;
        this._frameMesh     = mainMesh as Mesh;
        this._frameMat      = mainMesh.material as StandardMaterial;
        this._frameWidth    = this.getMeshWidth( mainMesh );
        this._frameDepth    = this.getMeshLength( mainMesh );
        this._frameHeight   = this.getMeshHeight( mainMesh );
        this._frameRules    = rules;
        const meshesInfo    = this.getWallBones();
        for( const side in meshesInfo ) {
            this.cloneFrameFollowSide( side );
            this.handleIntersectFrame( side );
        }

        mainMesh.setEnabled( false );

        if( isChange || this._isShareLink ) {
            this.updateInteriorComponentPosition( oldFrameDepth );
        }

        this.addAllFrameComponent();
        this.scaleKickBoard( true );
        this.scaleKickBoard( true, 'frame_kick_board' );
        this.handleUpdateTackRoom( false );
        if( this._isDrawInteriorSiding ) {
            this.drawInteriorlWallCeiling();
        }
    }

    /**
     * remove frame in model
     */
    removeFrameInObject = () => {
        const listObjectInModel = [ 
            'greenhouse_frame_cloned_obj', 
            'inner_frame_horizontal_clone_obj', 
            'inner_frame_vertical_clone_obj',
            'inner_frame_horizontal_clone_01_obj',
            'inner_frame_horizontal_clone_02_obj',
            'inner_frame_horizontal_clone_03_obj',
            'inner_frame_horizontal_clone_04_obj',
            'frame_clone_obj',
            'stub_back_clone_obj',
            'stub_back_clone_2_obj',
            'frame_window_f_clone_obj',
            'frame_window_b_clone_obj',
            'frame_window_lr_clone_obj'
        ];

        jQuery( '.ssb-configurator-options.frame input.ssb-option' ).each( ( i, v ) => {
            const distributionRules = JSON.parse( window.atob( jQuery( v ).attr( 'data-distribution-rules' ) ) );
            if( distributionRules ) {
                for( let key in distributionRules ) {
                    const item = distributionRules[key];
                    if( item.object_key ) {
                        listObjectInModel.push( item.object_key );
                    }
                }
            }
        } );

        listObjectInModel.forEach( item  => {
            let frameClonedMesh = this._scene.getMeshByName( item ) as Mesh;
            if( frameClonedMesh ) {
                this.removeClone( item );
                frameClonedMesh.setEnabled( false );
            }
        });
    }


    /**
     * Remove all Frame when no exits input checked
     */
    removeAllFrame = () => {
        this.removeFrame();
        this.removeFrameInObject();
        this.addAllFrameComponent( false )
        this._frameMesh = undefined;
    }

    /**
     * Clone Frame in model
     * @param rules 
     */
    cloneFrameInModelObject = ( rules ) => {
        const _this     = this;
        this._frameMesh = undefined;
        this.removeFrame();
        this.removeFrameInObject();

        const sidesWall = this.getWallBones();
        for( const side in sidesWall ) {
            this.cloneFrameInModelObjectFlowSide( side, rules );
        }
    }

    /**
     * Clone frame in the model based on the side and rules.
     * @param {string} side - The side to which the frame should be cloned.
     * @param {Array} rules - The rules that define the cloning logic for each side.
     * @returns 
     */
    cloneFrameInModelObjectFlowSide = ( side, rules ) => {
        const excludeSides = [ 'roof_left', 'roof_right', 'floor', 'porch', 'top_front', 'top_back', 'top_left', 'top_right', 'roof', 'dormer' ];
        if( helpers.hasKeyInArray( excludeSides, side ) ) {
            return;
        }

        let distance        = 0;
        let offset:any      = 0;
        let isClone         = false;
        let meshName        = '';
        const meshesInfo    = this.getWallBones( side );
        if( undefined === meshesInfo.left || undefined === meshesInfo.right ) {
            return;
        }
        for( let key in rules ) {
            const item  = rules[key];
            const sides = item.side;
            if( ! sides ) continue;
            if( sides.includes( 'all' ) || sides.includes( side ) ) {
                distance    = Number( item.distance );
                offset      = item.offset;
                meshName    = item.object_key;
                const meshClone     = this.getMeshByName( meshName );
                if( ! meshClone ) return;
                const cloneFull   = '' !== offset;
                offset            = 0 === Number( offset ) ? 0 : helpers.toFixed3( offset / 12 );
                let boneStart     = new Vector3( this._bones[meshesInfo.left].position.x, this._bones[meshesInfo.left].position.y, this._bones[meshesInfo.left].position.z );
                let boneEnd       = new Vector3( this._bones[meshesInfo.right].position.x, this._bones[meshesInfo.right].position.y, this._bones[meshesInfo.right].position.z );
                 
                if( 'stub_back_clone_obj' === meshName && this.isEnableTackDoor() ) {
                    const innerWallFrontMesh = this.getMeshByName( 'inner_wall_f_obj' );
                    boneStart.z              = innerWallFrontMesh.position.z;
                }

                const axis = ( - 1 !== zSide.indexOf( side ) ) ? 'z' : 'x';
                if ( boneStart[axis] > boneEnd[axis] ) {
                    boneStart[axis] = boneStart[axis] - offset;
                    boneEnd[axis]   = boneEnd[axis] + offset;
                } else {
                    boneStart[axis] = boneStart[axis] + offset;
                    boneEnd[axis]   = boneEnd[axis] - offset;
                }

                if( - 1 !== zSide.indexOf( side ) ) {
                    if( 'stub_back_clone_obj' !== meshName ) {
                        boneStart.x = meshClone.position.x;
                        boneEnd.x   = meshClone.position.x;
                    }
                } else {
                    boneStart.z = meshClone.position.z;
                    boneEnd.z   = meshClone.position.z;
                }
                this.instanceMesh( meshName, distance, boneStart, boneEnd, '', cloneFull );
            }
        }
        
    }
    
    /**
     * Clone Rafter
     */
    cloneRafterComponent = () => {
        const rafterInModelMesh = this.getMeshByName( 'Rafter_clone_obj' );
        if( rafterInModelMesh ) {
            rafterInModelMesh.setEnabled( false );
        }
        
        let nameFile    = this.getSettingData( 'rafter_style' );
        if( ! nameFile ) return;
        nameFile        = nameFile.value;
        const objName   = `${nameFile}_obj`;
        let mainMesh    = this.getMeshByName( objName ) as Mesh;
        if( ! mainMesh ) return;
        const bones             = this._bones;
        const newWidth          = this.getWidth() / 2;
        const newBuildingHeight = this.getBuildingHeight();
        const newWallHeight     = this.getWallHeight();
        let rafterAdjustment    = this.getSettingData( 'rafter_adjustment' ) ? Number( this.getSettingData( 'rafter_adjustment' ) ) : 0.044;
        const distance          = this.getSettingData( 'hat_channel_distance' );
        const rafterL           = this.filterByValue( mainMesh.skeleton.bones, 'name', 'Rafter_L' );
        const rafterR           = this.filterByValue( mainMesh.skeleton.bones, 'name', 'Rafter_R' );
        const rafterWidth       = this.getMeshLength( mainMesh );
        const rafterLength      = this.getMeshWidth( mainMesh );
        let pointClone          = this.getDepth() / 2;
        let positionRafterLx    = - newWidth
        let positionRafterRx    = newWidth;
        let positionRafterLy    = newBuildingHeight;
        let positionRafterRy    = newWallHeight;
        let roofShape           = this.getRoofShape();
        let poinCloneStart      = new Vector3( 0, 0, pointClone );
        let poinCloneEnd        = new Vector3( 0, 0, - pointClone );
        if( 'box_gable' === roofShape || 'open_gable' === roofShape ) {
            let roofDepth    = this.getDepth();
            positionRafterLx = rafterWidth/2;
            positionRafterRx = bones['r_b_r1'] ? bones['r_b_r1'].position.x : newWidth/2;
            positionRafterRy = bones['r_b_r1'] ? bones['r_b_r1'].position.y : positionRafterRy;
            positionRafterLy = bones['r_b_t'] ? bones['r_b_t'].position.y : positionRafterLy;

            if( bones['r_b_t'] && bones['r_f_t'] ) {
                roofDepth           = Vector3.Distance( bones['r_b_t'].position, bones['r_f_t'].position );
                poinCloneStart.z    = bones['r_b_t'].position.z;
                poinCloneEnd.z      = bones['r_f_t'].position.z;
            }

            if( rafterL ) rafterL.setPosition( new Vector3( positionRafterLx, positionRafterLy + rafterAdjustment, 0 ) );
            if( rafterR ) rafterR.setPosition( new Vector3( positionRafterRx, positionRafterRy + rafterAdjustment, 0 ) );
            const leftObjName   = `${nameFile}_left_obj`;
            let cloneRafterLeft = this.getMeshByName( leftObjName );
            if( ! cloneRafterLeft ) {
                cloneRafterLeft = mainMesh.clone( leftObjName );
            }
            
            let skeletonRafterLeft = this._scene.getSkeletonByName( 'skeleton_rafter_left' );
            if( ! skeletonRafterLeft ) {
                skeletonRafterLeft = mainMesh.skeleton.clone( 'skeleton_rafter_left' );
            }
            cloneRafterLeft.skeleton = skeletonRafterLeft;
            let cloneRafterMiddle    = this.getMeshByName( `${nameFile}_middle_obj` );
            if( ! cloneRafterMiddle ) {
                cloneRafterMiddle = mainMesh.clone( `${nameFile}_middle_obj` );
            }
            cloneRafterMiddle.isPickable = false;
            cloneRafterMiddle.rotation    = new Vector3( 0, Math.PI / 2, 0 );
            cloneRafterMiddle.scaling.x   = roofDepth / rafterLength;
            if( this.isTinyHomeCottage() || this.isTinyHomeFrontPorch() ) {
                cloneRafterMiddle.scaling.x = ( roofDepth + 0.66 ) / rafterLength; 
            }
            if( 'standard_cottage_front_porch' === this._shedType ) {
                cloneRafterMiddle.position.z = Vector3.Center( bones['r_b_t'].position, bones['r_f_t'].position ).z;
            }
            cloneRafterMiddle.position.y  = positionRafterLy + rafterAdjustment;
            cloneRafterMiddle.setEnabled( true );

            const rafterLeftL           = this.filterByValue( skeletonRafterLeft.bones, 'name', 'Rafter_L' );
            const rafterLeftR           = this.filterByValue( skeletonRafterLeft.bones, 'name', 'Rafter_R' );
            const positionRafterLeftRx  = bones['r_b_l1'] ? bones['r_b_l1'].position.x : -newWidth/2;
            const positionRafterLeftRy  = bones['r_b_l1'] ? bones['r_b_l1'].position.y : newWallHeight;
           
            if( rafterLeftR ) rafterLeftR.setPosition( new Vector3( -positionRafterLx, positionRafterLy + rafterAdjustment, 0 ) );
            if( rafterLeftL ) rafterLeftL.setPosition( new Vector3( positionRafterLeftRx, positionRafterLeftRy + rafterAdjustment, 0 ) );

            this.instanceMesh( objName, distance * 12, poinCloneStart, poinCloneEnd );
            this.instanceMesh( leftObjName, distance * 12, poinCloneStart, poinCloneEnd );

            this._blurMeshes = helpers.checkAndPushKeyToArray( this._blurMeshes, objName );
            this._blurMeshes = helpers.checkAndPushKeyToArray( this._blurMeshes, leftObjName );
        } else if( 'flat' === roofShape ) {
            if( bones['r_b_l1'] && bones['r_f_l1'] ) {
                poinCloneStart.z    = bones['r_b_l1'].position.z;
                poinCloneEnd.z      = bones['r_f_l1'].position.z;
            }
            positionRafterLx = bones['r_b_l1'] ? bones['r_b_l1'].position.x : positionRafterLx;
            positionRafterLy = bones['r_b_l1'] ? bones['r_b_l1'].position.y : positionRafterLy;
            positionRafterRx = bones['r_b_r1'] ? bones['r_b_r1'].position.x : positionRafterLx;
            positionRafterRy = bones['r_b_r1'] ? bones['r_b_r1'].position.y : positionRafterRy;

            if( rafterL ) rafterL.setPosition( new Vector3( positionRafterLx, positionRafterLy + rafterAdjustment, 0 ) );
            if( rafterR ) rafterR.setPosition( new Vector3( positionRafterRx, positionRafterRy + rafterAdjustment, 0 ) );
            this.instanceMesh( objName, distance * 12, poinCloneStart, poinCloneEnd );
            this._blurMeshes = helpers.checkAndPushKeyToArray( this._blurMeshes, objName );
        }
    }
    

    /**
     * Update the horizontal frame properties and create a top frame.
     *
     * @param {object} params - Parameters for updating the horizontal frame.
     * @param {Mesh} params.mesh - The main frame mesh to be updated.
     * @param {Vector3} params.boneStart - Starting point of the bone where the frame is attached.
     * @param {Vector3} params.boneEnd - Ending point of the bone where the frame is attached.
     * @param {string} params.side - Side identifier for positioning and adjustments.
     * @param {number} params.frameHeight - New height of the frame.
     * @param {Mesh|null} params.parentMesh - Optional parent mesh to attach the top frame to.
     */
    updateHorizontalFrame = ( params ) => {
        let { mesh, boneStart, boneEnd, side, frameHeight, parentMesh } = params;

        let positionX   = 0;
        let positionZ   = 0;
        if( - 1 !== zSide.indexOf( side ) ) {
            if( boneStart.z > boneEnd.z ) {
                positionZ = - this._frameOffset;
            } else {
                positionZ = this._frameOffset;
            }
        } else {
            if( boneStart.x > boneEnd.x ) {
                positionX = - this._frameOffset;
            } else {
                positionX = this._frameOffset;
            }
        }

        const bottom    = helpers.toFixed3( boneStart.y - this._frameWidth / 2 );
        mesh.rotation   = new Vector3( mesh.rotation.x, mesh.rotation.y, Math.PI / 2 );
        mesh.position   = new Vector3( boneStart.x + positionX, bottom, boneStart.z + positionZ );
        mesh.scaling.y  = helpers.toFixed3( ( Vector3.Distance( boneStart, boneEnd ) - 2 * this._frameOffset ) / this._frameHeight );
        
        if( ( 'inner_left' === side && this.isCornerPorchLeft() ) || ( 'inner_back' === side && ( 'structure_ranch_cabin' === this._shedType || this.isAframeSingleGable() ) ) || ( 'left_inner_porch' === side && ( this.isMiddlePorch() || this.isDoubleGable() ) ) || ( 'inner_front' === side && this.isDeluxeCornerLeftPorch() ) ) {
            mesh.scaling.y  = helpers.toFixed3( ( Vector3.Distance( boneStart, boneEnd ) + this._frameDepth ) / this._frameHeight );
        } else if( ( 'inner_front' === side && this.isCornerPorchLeft() ) || ( 'right_inner_porch' === side && ( this.isMiddlePorch() || this.isDoubleGable() ) ) ) {
            mesh.position   = new Vector3( boneStart.x + this._frameOffset, bottom, boneStart.z + positionZ );
        } else if( ( 'inner_left' === side && ( 'structure_ranch_cabin' === this._shedType || this.isAframeSingleGable() ) ) ) {
            mesh.position   = new Vector3( boneStart.x + positionX, bottom, boneStart.z + this._frameOffset );
        } else if( 'front_inner_porch' === side && ( this.isMiddlePorch() || this.isDoubleGable() ) ) {
            mesh.position   = new Vector3( boneStart.x + positionX, bottom, boneStart.z + this._frameOffset );
            mesh.scaling.y  = helpers.toFixed3( ( Vector3.Distance( boneStart, boneEnd ) + 2 * this._frameOffset + this._frameDepth ) / this._frameHeight );
        } else if( 'inner_right' === side && this.isDeluxeCornerLeftPorch() ) {
            mesh.position   = new Vector3( boneStart.x + positionX, bottom, boneStart.z - this._frameOffset );
            mesh.scaling.y  = helpers.toFixed3( Vector3.Distance( boneStart, boneEnd ) / this._frameHeight );
        }

        const frameTopName  =  mesh.name + constants.frameTopName;
        let frameTop        = this._scene.getMeshByName( frameTopName ) as Mesh;
        if( frameTop ) frameTop.dispose();
        frameTop            = mesh.clone( frameTopName ) as Mesh;
        frameTop.scaling    = mesh.scaling;
        const topY          = helpers.toFixed3( frameHeight - this._frameWidth / 2 );
        frameTop.position   = new Vector3( mesh.position.x, topY, mesh.position.z );
        frameTop.material   = mesh.material;

        if( null !== parentMesh ) {
            mesh.isPickable = false;
            mesh.setEnabled( true );
            mesh.setParent( parentMesh );
            
            frameTop.isPickable = false;
            frameTop.setEnabled( true );
            frameTop.setParent( parentMesh );
        }
    }

    /**
     * Clone and distribute a mesh along a specified bone, creating a series of frames.
     * 
     * @param {object} params - Parameters for cloning and distributing the mesh.
     * @param {string} params.side - Side identifier for the frames (e.g., 'left' or 'right').
     * @param {Mesh} params.mesh - The mesh to be cloned and distributed.
     * @param {Vector3} params.boneStart - Starting point of the bone where the clones will be distributed.
     * @param {Vector3} params.boneEnd - Ending point of the bone where the clones will be distributed.
     * @param {Mesh|null} params.parentMesh - Optional parent mesh to attach the clones to.
     */
    private cloneStud( params ) : void {
        const { side, mesh, boneStart, boneEnd, parentMesh, distance, cloneFull } = params;
        const divisions = Math.ceil( Vector3.Distance( boneStart, boneEnd ) / ( distance / 12  ) );
        if( ! this._frames[side] ) {
            this._frames[side] = [];
        }
        let start   = cloneFull ? 0 : 1;
        let end     = cloneFull ? divisions + 1 : divisions;

        for( start; start < end; start++ ) {
            const cloneName                 = `${mesh.name}_${start}`;
            const clone                     = mesh.clone( cloneName ) as Mesh;
            clone.position                  = Vector3.Lerp( boneStart, boneEnd, ( 1 / divisions ) * start );
            clone.isPickable                = false;
            clone.alwaysSelectAsActiveMesh  = true;
            clone.setEnabled( true );

            if( null !== parentMesh ) {
                clone.setParent( parentMesh );
            }

            this._frames[side].push( clone );

            this._blurMeshes = helpers.checkAndPushKeyToArray( this._blurMeshes, cloneName );
        }
    }

    /**
     * Get start and end bone names for all sides based on the current shed type.
     * 
     * @returns {Array} - An array containing bone names for each side.
     * 
     * For define left/right: based on a direct front view, if it is on the left side, it will be the left bone, and vice versa.
     */
    getWallBones = ( side = 'all' ) => {
        let measureBones = [];

        measureBones['floor'] = { left: 'b_f_r1', right: 'b_f_l1', top: 'b_b_l1', bottom: 'b_f_l1' };
        measureBones['porch'] = { left: 'b_p_r2', right: 'b_p_l2', top: 'b_p_t' };
        measureBones['front'] = { left: 'b_f_r1', right: 'b_f_l1', top: 'b_f_t' };
        measureBones['back'] = { left: 'b_b_l1', right: 'b_b_r1', top: 'b_b_t' };
        measureBones['left'] = { left: 'b_f_l1', right: 'b_b_l1', top: 'b_b_l2' };
        measureBones['right'] = { left: 'b_b_r1', right: 'b_f_r1', top: 'b_f_r2' };
        measureBones['roof'] = { left: 'b_f_t', right: 'b_b_t' };
        measureBones['roof_left'] = { left: 'b_f_t', right: 'b_b_t' };
        measureBones['roof_right'] = { left: 'b_b_t', right: 'b_f_t' };
        measureBones['top_front']= { left: 'b_f_l1', right: 'b_f_r1' };
        measureBones['top_back'] = { left: 'b_b_l1', right: 'b_b_r1' };
        measureBones['top_left'] = { left: 'b_b_l4', right: 'b_f_l4' };
        measureBones['top_right'] = { left: 'b_b_r4', right: 'b_f_r4' };

        if( 'flat' === this.getRoofShape() ) {
            this._bones['c_b_r_1'] = { position: new Vector3( 0, 0, this._bones['b_f_t']?.position?.z || 0 ) };
            this._bones['c_b_r_2'] = { position: new Vector3( 0, 0, this._bones['b_b_t']?.position?.z || 0 ) };

            measureBones['roof'] = { left: 'c_b_r_1', right: 'c_b_r_2' };
        }

        if( 'hip' === this.getRoofShape() ) {
            measureBones['roof'] = { left: 'r_b_t', right: 'r_f_t' };
        }

        if( 'saltbox_customize' === this.getRoofShape() && this._bones['b_f_l2'] ) {
            measureBones['left']['topWall'] = 'b_f_l2';
        }

        if( 'structure_ranch_cabin' === this._shedType || this.isAframeSingleGable() ) {
            measureBones['left'] = { left: 'b_f_l1', right: 'b_p_l1', top: 'b_f_t2' };
            measureBones['inner_left'] = { left: 'b_b_m4', right: 'b_b_l1' };
            measureBones['back'] = { left: 'b_b_l1', right: 'b_b_r1', top: 'b_b_t' };
            measureBones['inner_back'] = { left: 'b_p_l1', right: 'b_b_m4' };
            measureBones['right'] = { left: 'b_b_r1', right: 'b_f_r1', top: 'b_f_t1' };
            
            if( this.isAframeSingleGable() ) {
                measureBones['roof'] = { left: 'r_f_t1', right: 'r_m_t1' };
            }
        } else if( this.isDeluxeCornerLeftPorch() ) {
            measureBones['front'] = { left: 'b_f_l4', right: 'b_f_l1', top: 'b_f_l2' };
            measureBones['left'] = { left: 'b_f_l1', right: 'b_b_l1', top: 'b_f_l2' };
            measureBones['right'] = { left: 'b_b_r1', right: 'b_f_r1', top: 'b_b_r2' };
            measureBones['inner_front']         = { left: 'b_f_r1', right: 'b_f_r4' };
            measureBones['inner_right']         = { left: 'b_f_r4', right: 'b_f_r6' };
            measureBones['diagonal_right']      = { left: 'b_f_r6', right: 'b_f_l4' };
            measureBones['roof_left'] = { left: 'b_p_t', right: 'b_b_t' };
            measureBones['roof_right'] = { left: 'b_b_t', right: 'b_p_t' };
            measureBones['porch'] = { left: 'b_p_r2', right: 'b_p_l2', top: 'b_p_t' };
            measureBones['roof'] = { left: 'b_p_t', right: 'b_b_t' };
        } else if( this.isMiddlePorch() ) {
            measureBones['left_back'] = { left: 'b_p_l1', right: 'b_b_l1', top: 'b_b_l2' };
            measureBones['left_front'] = { left: 'b_f_l1', right: 'b_p_l3', top: 'b_f_l2' };
            measureBones['front_inner_porch'] = { left: 'b_f_m4', right: 'b_b_m4', top: 'b_p_l4' };
            measureBones['left_inner_porch'] = { left: 'b_p_l3', right: 'b_f_m4', top: 'b_f_m3' };
            measureBones['right_inner_porch'] = { left: 'b_b_m4', right: 'b_p_l1', top: 'b_b_m3' };

            delete measureBones['left'];
        } else if( this.isCornerPorchLeft() ) {
            measureBones['left'] = { left: 'b_p_l1', right: 'b_b_l1', top: 'b_b_l2' };
            measureBones['inner_front'] = { left: 'b_b_m4', right: 'b_p_l1', top: 'b_p_l2' };
            measureBones['inner_left'] = { left: 'b_f_l1', right: 'b_b_m4', top: 'b_f_l2' };
        } else if( 'structure_monitorbarns_helmuth' === this._shedType ) {
            this._bones['c_b_f_1'] = { position: new Vector3( helpers.getBaseWidth() / 2, 0, this._bones['b_f_r2'].position.z ) };
            this._bones['c_b_f_2'] = { position: new Vector3( - helpers.getBaseWidth() / 2, 0, this._bones['b_f_r2'].position.z ) };
            measureBones['front'] = { left: 'c_b_f_1', right: 'c_b_f_2', top: 'b_f_t' };
            
            this._bones['c_b_b_1'] = { position: new Vector3( - helpers.getBaseWidth() / 2, 0, this._bones['b_b_l2'].position.z ) };
            this._bones['c_b_b_2'] = { position: new Vector3( helpers.getBaseWidth() / 2, 0, this._bones['b_b_l2'].position.z ) };
            measureBones['back'] = { left: 'c_b_b_1', right: 'c_b_b_2', top: 'b_b_t' };

            let offset = 'structure_rancher_modular' === this._shedTypeVersion ? 0.1 : -0.2;
            this._bones['c_b_t_f_1'] = { position: this._bones['b_f_r3'].position.add( new Vector3( 0, offset, 0 ) ) };
            this._bones['c_b_t_f_2'] = { position: this._bones['b_f_l3'].position.add( new Vector3( 0, offset, 0 ) ) };
            measureBones['top_front'] = { left: 'c_b_t_f_1', right: 'c_b_t_f_2', top: 'b_f_t' };

            this._bones['c_b_t_b_1'] = { position: this._bones['b_b_l3'].position.add( new Vector3( 0, offset, 0 ) ) };
            this._bones['c_b_t_b_2'] = { position: this._bones['b_b_r3'].position.add( new Vector3( 0, offset, 0 ) ) };
            measureBones['top_back'] = { left: 'c_b_t_b_1', right: 'c_b_t_b_2', top: 'b_b_t' };

            measureBones['top_left'] = { left: 'b_f_l4', right: 'b_b_l4', top: 'b_f_l5', topWall: 'b_f_l5' };
            measureBones['top_right'] = { left: 'b_b_r4', right: 'b_f_r4', top: 'b_b_r5', topWall: 'b_f_l5' };

        } else if( 'mainus_quaker' === this._shedType || 'structure_saltbox_mwshed' === this._shedType ) {
            measureBones['front'] = { left: 'b_f_r4', right: 'b_f_l1', top: 'b_f_t' };
            measureBones['back'] = { left: 'b_b_l1', right: 'b_b_r4', top: 'b_b_t' };
            measureBones['right'] = { left: 'b_b_r4', right: 'b_f_r4', top: 'b_b_r3' };
        } else if( this.is2StoryLeanTo() ) {
            measureBones['top_left'] = { left: 's_f_f_l1', right: 's_f_b_l1', top: 'b_f_l2', topWall: 'b_f_l2' };
            measureBones['top_right'] = { left: 's_f_b_r1', right: 's_f_f_r1', top: 'b_b_l2', topWall: 'b_b_l2' };
            measureBones['lean_to_right'] = { left: 'lean_to_b_r2', right: 'lean_to_f_r2', top: 'lean_to_b_r3', topWall: 'lean_to_b_r3' };
            measureBones['lean_to_front'] = { left: 'lean_to_f_r2', right: 'lean_to_f_r1', top: 'lean_to_f_r3', topWall: 'lean_to_f_r3' };
            measureBones['lean_to_back'] = { left: 'lean_to_b_r1', right: 'lean_to_b_r2', top: 'lean_to_b_r3', topWall: 'lean_to_b_r3' };
            measureBones['top_front']= { left: 'b_f_l1', right: 'b_f_r1', top: 'b_f_t', topWall: 'b_f_l2' };
            measureBones['top_back'] = { left: 'b_b_l1', right: 'b_b_r1', top: 'b_b_t', topWall: 'b_b_l2' };
        } else if( 'structure_old_west_modular' === this._shedType ) {
            measureBones['front'] = { left: 'b_f_r2', right: 'b_f_l2', top: 'b_f_t' };
            measureBones['back'] = { left: 'b_b_l2', right: 'b_b_r2', top: 'b_b_t' };
            measureBones['left'] = { left: 'b_f_l2', right: 'b_b_l2', top: 'b_f_l3' };
            measureBones['right'] = { left: 'b_b_r2', right: 'b_f_r2', top: 'b_b_r3' };
        } else if( this.isDoubleGable() ) {
            measureBones['left_back'] = { left: 'b_p_l3', right: 'b_b_l1', top: 'b_b_t2' };
            measureBones['left_front'] = { left: 'b_f_l1', right: 'b_p_l1', top: 'b_f_t2' };
            measureBones['left_inner_porch'] = { left: 'b_p_l1', right: 'b_b_m4', top: 'b_p_l2' };
            measureBones['front_inner_porch'] = { left: 'b_b_m4', right: 'b_b_m8', top: 'b_b_m3' };
            measureBones['right_inner_porch'] = { left: 'b_b_m8', right: 'b_p_l3', top: 'b_b_m7' };
            measureBones['front'] = { left: 'b_f_r1', right: 'b_f_l1', top: 'b_f_r2' };
            measureBones['back'] = { left: 'b_b_l1', right: 'b_b_r1', top: 'b_b_l2' };
            measureBones['right'] = { left: 'b_b_r1', right: 'b_f_r1', top: 'b_f_t1' };
            measureBones['roof'] = { left: 'r_f_t1', right: 'r_b_t1' };
            
            delete measureBones['left'];
        } else if( 'structure_dog_kennel' === this._shedType ) { 
            measureBones['left'] = { left: 'b_m_l1', right: 'b_b_l1', top: 'b_b_l2' };
            measureBones['right'] = { left: 'b_b_r1', right: 'b_m_r1', top: 'b_b_r2' };
        } else if( 'structure_rancher' === this._shedType ) { 
            measureBones['top_front'] = { left: 'b_f_r3', right: 'b_f_l3', top: 'b_f_t' };
            measureBones['top_back'] = { left: 'b_b_l3', right: 'b_b_r3', top: 'b_b_t' };
        } else if( 'carport_lean_to_standalone' === this._shedType ) { 
            this._bones['c_b_r_1'] = { position: new Vector3( 0, 0, this._bones['b_f_l3'].position.z ) };
            this._bones['c_b_r_2'] = { position: new Vector3( 0, 0, this._bones['b_b_l3'].position.z ) };

            measureBones['roof'] = { left: 'c_b_r_1', right: 'c_b_r_2' };
        } else if( 'structure_loafing_shed' === this._shedType ) { 
            this._bones['c_b_r_1'] = { position: new Vector3( 0, 0, this._bones['b_f_l2'].position.z ) };
            this._bones['c_b_r_2'] = { position: new Vector3( 0, 0, this._bones['b_b_l2'].position.z ) };

            measureBones['roof'] = { left: 'c_b_r_1', right: 'c_b_r_2' };
        } else if( 'structure_alpinechalet' === this._shedType ) {
            measureBones['dormer'] = { left: 'b_f_t1', right: 'b_b_t1', top: 'b_f_t2' };
            measureBones['front'] = { left: 'b_f_r1', right: 'b_f_l1', top: 'b_f_t2' };
            measureBones['back'] = { left: 'b_b_l1', right: 'b_b_r1', top: 'b_b_t2' };
        } else if( 'structure_lakeside_bunkie_gambrel_roof' === this._shedType || 'structure_lakeside_bunkie_gable_roof' === this._shedType ) {
            this._bones['c_b_p_1'] = { position: this._bones['b_f_l2'].position.add( new Vector3( 0, 0, 32 / 12 ) ) };
            this._bones['c_b_p_2'] = { position: this._bones['b_f_r2'].position.add( new Vector3( 0, 0, 32 / 12 ) ) };
            this._bones['c_b_p_3'] = { position: this._bones['b_f_t'].position.add( new Vector3( 0, 0, 32 / 12 ) ) };

            measureBones['porch'] = { left: 'c_b_p_1', right: 'c_b_p_2', top: 'c_b_p_3' };
            measureBones['front'] = { left: 'b_f_r1', right: 'b_f_l1', top: 'b_f_r2' };
        }
        
        return 'all' === side ? measureBones : ( measureBones[side] ? measureBones[side] : {} );
    }

    /**
     * Load babylon file to scene
     * 
     * @param {string} name Babylon file name
     * @param {string} type Babylon file type
     */
    loadBabylonFile = ( name: string, type: string ) => {
        const _this = this;

        const version = helpers.getVersionComponent( name, _this._componentData );

        return new Promise( resolve => {
            const loadTask = _this._assetsManager.addMeshTask( `load_${name}`, '', `${_this._componentDir}${type}/${name}/`, `${name}.babylon${version}` );
            loadTask.onError    = ( task ) => {
                console.error( `Item ${name}.babylon not found.` );
                resolve( false );
            }

            loadTask.onSuccess  = task => {
                _this.importMaterials( task.loadedMeshes );
                if( task.loadedMeshes[0] ) {
                    task.loadedMeshes[0].setEnabled( false );
                }

                resolve( task.loadedMeshes );
            };

            _this.forceAssetLoading();
        } );
    }

    /**
     * Get mesh's wall
     * 
     * @param {String} side front|back|left,...
     * 
     * @returns {Mesh|undefined}
     */
    getWallBySide = ( side: String, isOrigin = false ) => {
        let name = `wall_${side}_clone`;
        let wall = this.getMeshByName( `${name}_clone` );
        if( wall && ! isOrigin ) return wall;

        wall = this.getMeshByName( name );
        if( wall ) return wall;

        if( 'floor' === side ) {
            return this.getMeshByName( side );
        }

        if( this._apiData['wall_raw'] ) {
            const sideData = this._apiData['wall_raw'].filter( e => e.side === side );
            if( sideData.length ) {
                name = sideData[0].name;
                wall = this.getMeshByName( `${name}_clone` );
                if( wall && ! isOrigin ) return wall;

                wall = this.getMeshByName( name );
                if( wall ) return wall;
            }
        }

        name = `${side}_wall`;
        wall = this.getMeshByName( `${name}_clone` );
        if( wall && ! isOrigin ) return wall;

        wall = this.getMeshByName( name );
        if( wall ) return wall;

        wall = this.getMeshByName( this.getOldWall( side ) );
        if( wall ) return wall;
        
        return undefined;
    }

    /**
     * Get old wall name
     * 
     * @param {String} side front|back|left,...
     * 
     * @returns {String}
     */
    getOldWall = ( side ) => {
        const firstCharacter = side.substring( 0, 1 );

        return `siding_${firstCharacter}_obj`
    }

    /**
     * Load frame from babylon file to scene
     */
    loadFrameFile = () => {
        return new Promise( async resolve => {
            if( ! this._isShareLink ) {
                const file = jQuery( `input[name="ssb-configurator-upgrade-frame"]:checked` ).data( 'file' );
                if( file && 'in-model' !== file ) {
                    const meshes        = await this.loadBabylonFile( file, 'stud' );
                    this._frameMesh     = meshes[0] as Mesh;
                    this._frameWidth    = this.getMeshWidth( this._frameMesh );
                    this._frameDepth    = this.getMeshLength( this._frameMesh );
                    this._frameHeight   = this.getMeshHeight( this._frameMesh );
                }
            }

            resolve( true );
        } );
    }

    /**
     * Update interior component position when adding frame
     * 
     * @param {Vector3} position component position
     * @param {String} side front|back|left,...
     * @param {Number} distance Distance from wall to interior component
     * @returns 
     */
    updateInteriorPosition = ( position, side, distance = 0 ) => {
        distance = 0 === distance ? this._frameDepth : distance;
        if( this._isDrawInteriorSiding ) {
            distance += constants.innerWallThickness;
        }
        if( 'diagonal_right' === side ) {
            const tmpPosition   = Math.sqrt( distance * distance + distance * distance ) / 2;
            position.x          = helpers.toFixed3( position.x - tmpPosition );
            position.z          = helpers.toFixed3( position.z - tmpPosition );
        } else {
            if( - 1 !== zSide.indexOf( side ) ) {
                if( - 1 !== positiveSide.indexOf( side ) ) {
                    position.x = helpers.toFixed3( position.x + distance );
                } else {
                    position.x = helpers.toFixed3( position.x - distance );
                }
            } else {
                if( - 1 !== positiveSide.indexOf( side ) ) {
                    position.z = helpers.toFixed3( position.z - distance );
                } else {
                    position.z = helpers.toFixed3( position.z + distance );
                }
            }
        }

        return position;
    }

    /**
     * Get floor offset
     */
    getFloorOffset = () => {
        const floorOffset = this.getSettingData( 'floor_offset' );
        return 0 === floorOffset ? floorOffset : helpers.toFixed3( floorOffset / 12 );
    }

    /**
     * Update all interior component position
     * 
     * @param oldFrameDepth Frame depth (to calculate distance from wall to interior component)
     */
    updateInteriorComponentPosition = ( oldFrameDepth ) => {
        const distance = helpers.toFixed3( this._frameDepth - oldFrameDepth );
        if( 0 === distance ) {
            return;
        }

        for( const key in this._allComponents ) {
            const component = this._allComponents[key];
            if( 'light' === component.type || ! this.isInteriorComponent( component.type ) || 'floor' === component.side ) {
                continue;
            }
            
            const position = this.updateInteriorPosition( this._walls[component.side]['items'][key].position, component.side, distance );
            this.placeComponent( component.meshes, position, component.rotation, component.type, component.side, component.name );
            this._walls[component.side]['items'][key].position = position;
        }

        for( let i = 0; i < this._stairs.length; i++ ) {
            if( this._stairs[i].metadata ) {
                const side          = this._stairs[i].metadata.side;
                const oldPosition   = new Vector3( this._stairs[i].position.x, this._stairs[i].position.y, this._stairs[i].position.z )
                const newPosition   = this.updateInteriorPosition( oldPosition, side, distance );

                this._stairs[i].position = new Vector3( newPosition.x, newPosition.y, newPosition.z );
            }
        }
    }

    /**
     * Update the frame on a specified side.
     *
     * @param {string} side - The side identifier for which the frame should be updated.
     */
    updateFrame = ( side: string ) => {
        if( this.isCarport() ) {
            return;
        }
        this.removeFrameBySide( side );
        this.cloneFrameFollowSide( side );
        this.handleIntersectFrame( side );
        this.blurMeshes();
    }

    /**
     * Cut a frame mesh by subtracting back meshes from it using Constructive Solid Geometry (CSG).
     *
     * @param {Mesh} frame - The main frame mesh to be cut.
     * @param {Mesh[]} backMeshes - Array of back meshes to subtract from the main frame.
     * @param {Mesh|null} parentMesh - Optional parent mesh to attach the resulting cut mesh to.
     * @param {Boolean} mainFrame - Main frame is stub horizontal
     * 
     * @returns {Mesh} The new mesh resulting from cutting the frame.
     */
    cutFrame = ( frame: Mesh, backMeshes: Mesh[], parentMesh: Mesh, mainFrame: boolean = true ): Mesh => {
        const frameCSG = CSG.FromMesh( frame );
        let isRemoveFrame = false;       

        for( let i = 0; i < backMeshes.length; i++ ) {
            let isValidPosition = false;
            const backMesh = backMeshes[i] as Mesh;
            const meshHeight = this.getMeshHeight( backMesh );
            const component = this.getComponentBySideAndKey( backMesh.metadata.side, backMesh.metadata.key );
            const wallHeight = this.getWallHeightBySide( backMesh.metadata.side );

            if( mainFrame && component.meshes ) {
                const position = - 1 !== zSide.indexOf( backMesh.metadata.side ) ? frame.position.z : frame.position.x;
                const firstHorizontalOld = position - this._frameWidth / 2;
                const lastHorizontalOld = position + this._frameWidth / 2;
                const firstVerticalOld = 0;
                const lastVerticalOld = this.getWallHeight(); 
                const position2D = this.getPosition2D( backMesh.metadata.side, component.meshes, component.meshes[0].position, component.type, component.name );
                const { lastPoint: lastPointVertical, firstPoint: firstPointVertical } = position2D.vertical;
                const { lastPoint: endXForLeg, firstPoint: startXForLeg } = position2D.horizontal;

                isValidPosition = this.isValidComponent( startXForLeg, endXForLeg, firstHorizontalOld, lastHorizontalOld, firstPointVertical, lastPointVertical, firstVerticalOld, lastVerticalOld );
            }
            
            if( isValidPosition && mainFrame ) continue;

            if( ! mainFrame || wallHeight > meshHeight ) {
                frameCSG.subtractInPlace( CSG.FromMesh( backMesh ) );
            } else {
                isRemoveFrame = true;
            }
        }

        const newMesh = frameCSG.toMesh( frame.name, frame.material, this._scene, false );
        newMesh.isPickable = false;
        newMesh.checkCollisions = true;
        if( parentMesh ) newMesh.setParent( parentMesh );

        if( isRemoveFrame ) newMesh.setEnabled( false );
        
        frame.dispose();

        return newMesh;
    }

    /**
     * Get a child mesh from an array of meshes based on its name.
     *
     * @param {string} name - The name of the child mesh to search for.
     * @param {Mesh[]} meshes - Array of meshes to search for the child mesh.
     * 
     * @returns {Mesh | false} The child mesh, or false if not found.
     */
    getChildMeshByName = ( name: string, meshes ): Mesh | false => {
        const mesh = meshes.find( mesh => mesh.name === name );

        return mesh || false;
    }

    /**
     * Get all back meshes associated with a specific side based on naming conventions and metadata.
     *
     * @param {string} side - The side identifier for which back meshes should be retrieved.
     * 
     * @returns {Mesh[]} Array of back meshes associated with the specified side.
     */
    getAllBackMeshes = ( side: string ): Mesh[] => {
        const backMeshes = [];
        for( const mesh of this._scene.meshes ) {
            if( -1 !== mesh.name.indexOf( constants.cutBlock ) ) {
                const parentMesh = mesh.parent as Mesh;
                if( parentMesh.metadata.side === side ) {
                    backMeshes.push( mesh );
                }
            }
        }
        
        return backMeshes;
    }

    /**
     * Handle the intersection of frames with back meshes on a specified side.
     *
     * @param {string} side - The side identifier for which frame intersections should be handled.
     */
    handleIntersectFrame = ( side: string ) => {
        const backMeshes = this.getAllBackMeshes( side );
        if( backMeshes.length ) {
            const frames        = this.getFrameBySide( side );
            const parentMesh    = this.getMeshByName( 'body_obj' );
            for( let i = 0; i < frames.length; i++ ) {
                this._frames[side][i] = this.cutFrame( frames[i] as Mesh, backMeshes, parentMesh );
            }

            const frameMesh     = this.getMeshByName( constants.frameName + side ) as Mesh;
            const frameMeshTop  = this.getMeshByName( constants.frameName + side + constants.frameTopName ) as Mesh;
            if( frameMesh ) this.cutFrame( frameMesh, backMeshes, parentMesh, false );
            if( frameMeshTop ) this.cutFrame( frameMeshTop, backMeshes, parentMesh, false );
        }
    }

    /**
     * Handle the frame components for a given component.
     *
     * @param {Object} component - The component data including meshes and other properties.
     */
    handleFrameComponent = ( component, visible = true ) => {
        if( undefined === this._frameMesh || 0 >= component.meshes.length ) {
            return;
        }

        const firstMesh     = component.meshes[0];
        const name          = component.name;
        const side          = component.side;
        const childMeshes   = firstMesh.getChildMeshes();
        const cutBlock      = this.getChildMeshByName( `${name}${constants.cutBlock}`, childMeshes );
        if( false === cutBlock || null === cutBlock.geometry ) {
            return;
        }

        const blockWidth    = this.getMeshWidth( cutBlock );
        const blockHeight   = this.getMeshHeight( cutBlock );
        const positionY     = cutBlock.position.y;
        let frameSides      = [
            {
                name:       `${name}${constants.frameComponent}${this._frameMesh.name}_1`,
                position:   new Vector3( helpers.toFixed3( blockWidth / 2 + this._frameWidth / 2 ), helpers.toFixed3( positionY - blockHeight / 2 ), 0 ),
                rotation:   new Vector3( 0, 0, 0 ),
                scalingY:   helpers.toFixed3( blockHeight / this._frameHeight ),
            },
            {
                name:       `${name}${constants.frameComponent}${this._frameMesh.name}_2`,
                position:   new Vector3( blockWidth / 2 + this._frameWidth, helpers.toFixed3( positionY + blockHeight / 2 + this._frameWidth / 2 ), 0 ),
                rotation:   new Vector3( 0, 0, Math.PI / 2 ),
                scalingY:   helpers.toFixed3( ( blockWidth + 2 * this._frameWidth ) / this._frameHeight ),
            },
            {
                name:       `${name}${constants.frameComponent}${this._frameMesh.name}_3`,
                position:   new Vector3( - helpers.toFixed3( blockWidth / 2 + this._frameWidth / 2 ), helpers.toFixed3( positionY - blockHeight / 2 ), 0 ),
                rotation:   new Vector3( 0, 0, 0 ),
                scalingY:   helpers.toFixed3( blockHeight / this._frameHeight ),
            }
        ];

        if( 'windows' === component.type || 'vent' === component.type ) {
            frameSides.push( {
                name:       `${name}${constants.frameComponent}${this._frameMesh.name}_4`,
                position:   new Vector3( blockWidth / 2 + this._frameWidth, helpers.toFixed3( positionY - blockHeight / 2 - this._frameWidth / 2 ), 0 ),
                rotation:   new Vector3( 0, 0, Math.PI / 2 ),
                scalingY:   helpers.toFixed3( ( blockWidth + 2 * this._frameWidth ) / this._frameHeight ),
            } );
        }

        this.addFrameComponent( name, firstMesh, frameSides, side, visible );
    }

    /**
     * Add frame components to a given mesh based on specified sides.
     *
     * @param {string} name - The name of the component.
     * @param {Mesh} firstMesh - The main mesh to which frame components will be added.
     * @param {Object[]} frameSides - Array of frame sides with properties like name, position, rotation, and scalingY.
     */
    addFrameComponent = ( name: string, firstMesh, frameSides: any[], side, isHide ) => {
        let visible = 0;
        if( isHide ) {
            for( let key in this._frameRules ) {
                const item  = this._frameRules[key];
                const sides = item.side;
                if( ! sides ) continue;
                if( sides.includes( 'all' ) || sides.includes( side ) ) {
                    visible = 1;
                }
            }
        }

        const childMeshes = firstMesh.getChildMeshes();
        for( let i = 0; i < childMeshes.length; i++ ) {
            if( - 1 !== childMeshes[i].name.indexOf( `${name}${constants.frameComponent}` ) ) {
                childMeshes[i].visibility = 0;
            }
        }

        for( let i = 0; i < frameSides.length; i++ ) {
            const oldMesh           = childMeshes.find( m => m.name === frameSides[i].name );
            if( oldMesh ) {
                oldMesh.visibility  = visible;
            } else {
                let newMesh         = this._frameMesh.clone( frameSides[i].name ) as Mesh;
                newMesh.material    = this._frameMesh.material as StandardMaterial;
                newMesh.setEnabled( this._isInterior );
                newMesh.setParent( firstMesh );
                newMesh.isPickable  = false;
                newMesh.position    = frameSides[i].position;
                newMesh.rotation    = frameSides[i].rotation;
                newMesh.scaling.y   = frameSides[i].scalingY;
            }
        }
    }

    /**
     * Scale the intersect mesh based on the type of mesh.
     * 
     * @param {Mesh[]} meshes - Array of meshes.
     * @param {string} componentName - Name of component.
     */
    scaleIntersectMesh = ( componentName, meshes ) => {
        const mesh = meshes.find( mesh => -1 !== mesh.name.indexOf( '_intersect_obj' ) );
        if( mesh ) {
            const componentCategory = this.getComponentCategory( componentName );
            if( false !== componentCategory && ( 'door_garage' === componentCategory['sub_category'] || 'door_rollup' === componentCategory['sub_category'] ) ) {
                const scalingNumber = 0.01;
                mesh.scaling.x      = 1 + scalingNumber;
                mesh.scaling.y      = 1 + scalingNumber;
                mesh.position = new Vector3( 0, - scalingNumber / 2, 0 );
            }
        }
    }

    /**
     * Handle the cut block for a given set of meshes based on the type and name.
     *
     * @param {Mesh[]} meshes - Array of meshes representing a component.
     * @param {string} type - Type of the component (e.g., 'windows', 'vent', 'door').
     * @param {string} name - Name of the component.
     */
    handleCutBlock = ( meshes: Mesh[], type: string, name: string ) => {
        if( 0 >= meshes.length ) {
            return;
        }

        const firstMesh     = meshes[0];
        const childMeshes   = firstMesh.getChildMeshes();
        const cutBlockName  = name + constants.cutBlock;
        const cutBlockMesh = this.getChildMeshByName( cutBlockName, childMeshes );
        if( cutBlockMesh && null !== cutBlockMesh.geometry ) {
            return this.calculateCutBlock( cutBlockName, cutBlockMesh, firstMesh, true );
        }

        const referenceMesh = this.getChildMeshByName( `${name}_backside_obj`, childMeshes );
        if( referenceMesh && null !== referenceMesh.geometry ) {
            const componentCategory = this.getComponentCategory( name );
            const errorNumber       = false !== componentCategory && ( 'door_garage' === componentCategory['sub_category'] || 'door_rollup' === componentCategory['sub_category'] ) ? 0.01 : 0;
            return this.calculateCutBlock( cutBlockName, referenceMesh, firstMesh, false, errorNumber );
        }

        if( 'windows' === type || 'vent' === type ) {
            const referenceMesh = this.getChildMeshByName( `${name}_obj`, childMeshes );
            if( referenceMesh && null !== referenceMesh.geometry ) {
                this.calculateCutBlock( cutBlockName, referenceMesh, firstMesh );
            }
        } else if( 'door' === type ) {
            const doorOffset    = 0.25;
            const component     = { name, meshes, type, holeWidth: 0 };
            const width         = this.getComponentWidthFromNetWork( component );
            const tmpHeight     = this.getComponentHeightFromNetWork( component );
            const height        = tmpHeight + doorOffset;
            const position      = new Vector3( 0, tmpHeight - ( height / 2 ) + 0.25, 0 );
            this.addCutBlock( { width, height, firstMesh, position, cutBlockName } );
        }
    }

    /**
     * Calculate and add a cut block to a given mesh based on a reference mesh.
     *
     * @param {string} cutBlockName - Name of the cut block mesh to be created.
     * @param {Mesh} referenceMesh - Reference mesh used for dimensions.
     * @param {Mesh} firstMesh - The main mesh to which the cut block will be added.
     */
    calculateCutBlock = ( cutBlockName: string, referenceMesh: Mesh, firstMesh: Mesh, disposeReference = false, errorNumber = 0 ) => {
        const width     = this.getMeshWidth( referenceMesh );
        const height    = this.getMeshHeight( referenceMesh ) + errorNumber;
        const bottom    = referenceMesh.geometry.extend.minimum.y - ( errorNumber / 2 );
        const position  = new Vector3( 0, helpers.toFixed3( bottom + height / 2 ), 0 );
        this.addCutBlock( { width, height, firstMesh, position, cutBlockName } );
        if( disposeReference ) {
            referenceMesh.dispose();
        }
    }

    /**
     * Add a cut block to a given mesh with specified dimensions and position.
     *
     * @param {Object} sizeData - Object containing data for creating the cut block.
     * @param {number} sizeData.width - Width of the cut block.
     * @param {number} sizeData.height - Height of the cut block.
     * @param {Mesh} sizeData.firstMesh - The main mesh to which the cut block will be added.
     * @param {Vector3} sizeData.position - Position of the cut block.
     * @param {string} sizeData.cutBlockName - Name of the cut block mesh to be created.
     */
    addCutBlock = ( data: { width: number; height: number; firstMesh: Mesh; position: Vector3; cutBlockName: string } ) => {
        const { width, height, firstMesh, position, cutBlockName } = data;
        const cutBlock      = MeshBuilder.CreateBox( cutBlockName, { height, width, depth: 2 }, this._scene );
        cutBlock.setParent( firstMesh );
        cutBlock.visibility = 0;
        cutBlock.isPickable = false;
        cutBlock.rotation   = new Vector3( 0, 0, 0 );
        cutBlock.position   = position;
    }

    /**
     * Add all frame components to the scene.
     *
     * Iterates through all frame components and calls the handleFrameComponent function
     * to add frame-related components to the scene.
     */
    addAllFrameComponent = ( visible = true ) => {
        for( const key in this._allComponents ) {
            this.handleFrameComponent( this._allComponents[key], visible );
        }
    }

    /**
     * Get component width
     * 
     * @param {Object} component component data
     * 
     * @return {Number} component width
     */
    getComponentWidthFromNetWork = ( component ): number => {
        const categories = (<any>window).api_cat;
        const { name, meshes, type } = component;

        if( 'Opening' === name ) {
            return component.holeWidth;
        }

        if( categories[name] && categories[name].width && categories[name].width > 0 && ! helpers.hasKeyInArray( [ 'shelf', 'workbench' ], type ) ) {
            return categories[name].width / 12;
        }

        return this.getComponentWidth( meshes, type, name );
    }

    /**
     * Get component height
     * 
     * @param {Object} component component data
     * 
     * @return {Number} component height
     */
    getComponentHeightFromNetWork = ( component ): number => {
        const categories = (<any>window).api_cat;
        const { name, meshes, type } = component;

        if( categories[name] && categories[name].height && categories[name].height > 0 ) {
            return categories[name].height / 12;
        }

        return this.getComponentHeight( meshes, type );
    }

    /**
     * Get component depth
     * 
     * @param {Object} component - Component data
     * @returns {Number} - Component length
     */
    getComponentLengthFromNetWork = ( component ): number => {
        const categories = (<any>window).api_cat;
        const { name, meshes, type } = component;

        if( categories[name] && categories[name].length && categories[name].length > 0 ) {
            return categories[name].length / 12;
        }

        return this.getComponentDepth( meshes );
    }
    
    /**
     * Calculates the position of a point on the line segment between two anchor points.
     *
     * @param {Vector3} bone1 - The first anchor point.
     * @param {Vector3} bone2 - The second anchor point.
     * @param {number} alpha - The weight factor for the segment starting from bone1.
     * @param {number} beta - The weight factor for the segment ending at bone2.
     * @returns {Vector3} - A Vector3 object containing the coordinates of the point on the line segment.
     */
    calculatePointOnSegment = ( bone1, bone2, alpha, beta ) => {
        const width = Vector3.Distance( bone1, bone2 );
        const leftBone = bone1.x < bone2.x ? bone1 : bone2;

        const x = helpers.toFixed3( ( width * beta ) / ( alpha + beta ) );
        const y = helpers.toFixed3( x * alpha );

        return { x: leftBone.x + x, y: leftBone.y + y, z: leftBone.z };
    };

    /**
     * Check condition to show|hide sliding
     */
    handleSliding = () => {
        if( 'structure_old_west_modular' !== this._shedType && 'structure_rancher_modular' !== this._shedTypeVersion ) {
            return;
        }

        this.updateSliding( 'front', 'sliding_f_obj', 'sliding_f_l1', 'sliding_f_r1' );
        this.updateSliding( 'back', 'sliding_b_obj', 'sliding_b_l1', 'sliding_b_r1' );
    }

    /**
     * Update the position and visibility of a sliding mesh based on the presence of doors on a specific side.
     *
     * @param {string} side - The side to update sliding on (e.g., 'front', 'back', 'left', 'right').
     * @param {string} slidingKey - The key/name of the sliding mesh to update.
     * @param {string} leftBone - The key/name of the left bone associated with the sliding mesh.
     * @param {string} rightBone - The key/name of the right bone associated with the sliding mesh.
     */
    updateSliding = ( side, slidingKey, leftBone, rightBone ) => {
        const doors     = this.filterDoorSliding( this._walls[side]['items'] );
        const sliding   = this.getMeshByName( slidingKey ) as Mesh;
        if( doors.length ) {
            if( sliding ) {
                sliding.setEnabled( true );
                const doorHeight    = this.getDoorHeightHighest( doors );
                const floorPorch    = this.getDoorFixedHeight( side );
                const slidingSize   = 0.3;
                const slidingHeight = helpers.toFixed3( doorHeight + floorPorch + slidingSize );

                if( this._bones[leftBone] ) this._bones[leftBone].setPosition( new Vector3( this._bones[leftBone].position.x, slidingHeight, this._bones[leftBone].position.z ) );
                if( this._bones[rightBone] ) this._bones[rightBone].setPosition( new Vector3( this._bones[rightBone].position.x, slidingHeight, this._bones[rightBone].position.z ) );
            }
        } else {
            if( sliding ) sliding.setEnabled( false );
        }
    }

    /**
     * Filter out sliding doors from a list of components.
     *
     * @param {Object} components - The list of components to filter.
     * @returns {Array} - An array containing only sliding doors from the input components.
     */
    filterDoorSliding = ( components ) => {
        let results = [];
        for( const key in components ) {
            const component = components[key];
            if( 'door' === component.type && -1 !== component.name.indexOf( 'sliding' ) ) {
                results.push( component );
            }
        }
    
        return results;
    }

    updateSideComponent = ( compnentInfo, positionBeforeDrag ) => {
        const allowSides = [ 'front', 'back', 'lean_to_front', 'lean_to_back', 'top_front', 'top_back', 'right', 'top_right', 'left', 'top_left' ];
        if( ! this.is2StoryLeanTo() || - 1 === allowSides.indexOf( compnentInfo.side ) ) {
            return compnentInfo;
        }

        const key               = compnentInfo.key;
        const side              = compnentInfo.side;
        const type              = compnentInfo.type;
        const firstMesh         = compnentInfo.meshes[0];
        const firstFloorHeight  = this.getWallHeight();
        let newSide             = 'right' === side || 'top_right' === side ? 'right' : ( 'left' === side || 'top_left' === side ? 'left' : ( compnentInfo.position.z > 0 ? 'front' : 'back' ) );

        if( 'right' === newSide || 'left' === newSide ) {
            if( firstMesh.position.y > firstFloorHeight ) {
                newSide = `top_${newSide}`;
            }
        } else {
            if( firstMesh.position.x > this._bones['lean_to_f_r1'].position.x ) {
                newSide = `lean_to_${newSide}`;
            } else if( firstMesh.position.y > firstFloorHeight ) {
                newSide = `top_${newSide}`;
            }
        }

        if( side === newSide ) {
            return compnentInfo;
        }

        if( ! this.isAllowAddComponent( newSide, compnentInfo.name ) ) {
            compnentInfo['meshes'][0].position = positionBeforeDrag;
            this.updateComponentsPosition2D( side, key, positionBeforeDrag, compnentInfo );
            if( this.isMobile() ) {
                let meshes          = compnentInfo.meshes;
                let meshHighlight   = this.filterByValue( meshes, 'name', '_bb_obj' ) as Mesh;
                this.enableHighlightMesh( meshHighlight, new Color4( 0, 0, 0, 1 ) );
            }

            this.createAlert( NOTICES.preventComponent );

            return compnentInfo;
        }

        delete this._walls[side]['items'][key];

        firstMesh.getChildMeshes()

        let meshes = compnentInfo.meshes;
        for( let i = 0; i < meshes.length; i++ ) {
            meshes[i].metadata.side = newSide;
        }

        compnentInfo.side   = newSide;
        compnentInfo.meshes = meshes;

        this._allComponents[key]            = compnentInfo;
        this._walls[newSide]['items'][key]  = compnentInfo;

        jQuery( `ul.added-components.${type}` ).find( `li[data-component-key='${key}']` ).remove();
        window.dispatchEvent( new CustomEvent( 'add-component', { detail: compnentInfo } ) );

        const addOnComponents = this.getComponentChildByParentKey( key, side );
        for( let i = 0; i < addOnComponents.length; i++ ) {
            let addOnInfo   = addOnComponents[i];
            const addOnKey  = addOnComponents[i]['key'];
            addOnInfo.side  = newSide;
            if( ! this.isShuttersInObject( addOnComponents[i].name ) ) {
                delete this._walls[side]['items'][addOnKey];
                
                this._allComponents[addOnKey]           = addOnInfo;
                this._walls[newSide]['items'][addOnKey] = addOnInfo;
            } else {
                this._shuttersInObject[addOnKey]        = addOnInfo;
            }

            window.dispatchEvent( new CustomEvent( 'add-component', { detail: addOnInfo } ) );
        }
        
        this._isUpdateLayout = true;
        this.handleAddCursorPointer();
        this.handleMoveComponent( newSide, key );

        return compnentInfo;
    }

    /**
     * Retrieves the height of the second floor in feet.
     * 
     * @returns {number} The height of the second floor in feet.
     */
    getSecondFloorHeight = () => {
        let secondFloorHeight = helpers.toFixed3( Number( this._canvas.getAttribute( 'data-second-floor-height' ) ) / 12 );
        if( 0 === secondFloorHeight ) {
            if( 'structure_barn_2story_leanto' === this._shedType ) {
                secondFloorHeight = 2.037;
            } else if( 'structure_aframe_2story_attic_leanto' === this._shedType ) {
                secondFloorHeight = 7;
                if( 'structure_aframe_2story_leanto' === this._shedTypeVersion ) {
                    secondFloorHeight = 2.5;
                }
            } else {
                secondFloorHeight = this.getGlobalData( constants.initSecondFloorHeight );
            }
        }

        return secondFloorHeight;
    }

    /**
     * Checks if placing components on the second floor is allowed.
     * 
     * @returns {boolean} True if placing components on the second floor is allowed, false otherwise.
     */
    isAllowPlaceComponentOn2ndFloor = () => {
        return 'true' === this._canvas.getAttribute( 'data-place-place-component-on-2nd-floor' );
    }
    
    /**
     * Retrieves the selected Lean-To size option data.
     * 
     * @returns {Object|boolean} Parsed JSON object if a Lean-To size option is selected, false otherwise.
     */
    getLeanToData = () => {
        const option = jQuery( 'input[name=ssb-configurator-upgrade-lean-to-size-option]:checked' ).attr( 'data-option' );
        if( ! option ) return false;

        return JSON.parse( window.atob( option ) );
    }

    /**
     * Retrieves the selected Porch custom size option data.
     * 
     * @returns {Object|boolean} Parsed JSON object if a Porch size option is selected, false otherwise.
     */
    getPorchCustomSizeData = () => {
        const option = jQuery( 'input[name=ssb-configurator-upgrade-porch-size-option]:checked' ).attr( 'data-option' );
        if( ! option ) return false;

        return JSON.parse( window.atob( option ) );
    }

    /**
     * Checks if a component with the given name belongs to the 'skylight_window' category.
     * 
     * @param {string} name - The name of the component to check.
     * 
     * @returns {boolean} - Returns true if the component belongs to the 'skylight_window' category, otherwise false.
     */
    isSkylightWindow = name => {
        const categories = this.getComponentCategory( name );

        return 'skylight_window' === categories.match_behavior || 'skylight_window' === categories.category;
    }

    /**
     * Checks if a component with the given name belongs to the 'gable_dormer' sub-category.
     * 
     * @param {string} name - The name of the component to check.
     * 
     * @returns {boolean} - Returns true if the component belongs to the 'gable_dormer' sub-category, otherwise false.
     */
    isGableDormer = name => {
        const categories = this.getComponentCategory( name );

        return 'gable_dormer' === categories.sub_category
    }

    /**
     * Determines if a dormer with the given name and type is valid based on the provided meshes.
     * 
     * @param {Array} meshes - The list of meshes to validate against.
     * @param {string} name - The name of the dormer.
     * @param {string} type - The type of the dormer.
     * 
     * @returns {boolean} - Returns true if the dormer is valid, otherwise false.
     */
    isValidDormer = ( meshes, name, type ) => {
        const roofHeight = helpers.getTanFromDegrees( this.getRoofDegree() ) * ( this.getWidth() / 2 );
        if( this.isGableDormer( name ) ) {
            return this.isValidGableDormer( meshes, name, type, roofHeight );
        }

        return roofHeight - this.getFlatDormerHeightDistance() >= this._flatDormerHeight;
    }

    /**
     * Checks if a gable dormer is valid based on its dimensions and the roof height.
     * 
     * @param {Array} meshes - The list of meshes representing the dormer components.
     * @param {string} name - The name of the dormer.
     * @param {number} roofHeight - The height of the roof.
     * 
     * @returns {boolean} - Returns true if the gable dormer is valid, otherwise false.
     */
    isValidGableDormer = ( meshes, name, type, roofHeight ) => {
        const dormerHeight = this.getGableDormerDistance( `${name}_frame_obj`, meshes, 'Dormer_B_F_T', 'Dormer_B_F_R1', 'y' );
        if( dormerHeight > roofHeight ) {
            return false;
        }

        const dormerWidth   = this.getGableDormerDistance( `${name}_frame_obj`, meshes, 'Dormer_B_F_L1', 'Dormer_B_F_R1', 'x' );
        const depth         = this.getDepth();
        if( dormerWidth > depth ) {
            return false;
        }

        return true;
    }

    /**
     * Calculates the distance between the first and last bones of a gable dormer along a specified axis.
     * 
     * @param {string} dormerFrame - The name of the dormer frame mesh.
     * @param {Array} meshes - The list of meshes containing the dormer frame.
     * @param {string} firstBone - The name of the first bone.
     * @param {string} lastBone - The name of the last bone.
     * @param {string} axis - The axis ('x', 'y', or 'z') along which to calculate the distance.
     * 
     * @returns {number} - The calculated distance between the first and last bones of the gable dormer along the specified axis.
     */
    getGableDormerDistance = ( dormerFrame, meshes, firstBone, lastBone, axis ) => {
        let dormerTop       = 0;
        let dormerBottom    = 0;
        meshes.forEach( m => {
            if( dormerFrame === m.name ) {
                m.skeleton.bones.forEach( b => {
                    if( firstBone === b.name ) {
                        dormerTop = b.position[axis];
                    } else if( lastBone === b.name ) {
                        dormerBottom = b.position[axis];
                    }
                } );
            }
        } );

        return helpers.toFixed3( dormerTop - dormerBottom );
    }

    /**
     * Calculates the position difference along the Z-axis between the top and bottom bones of a gable dormer.
     * 
     * @param {string} dormerFrame - The name of the dormer frame mesh.
     * @param {Array} meshes - The list of meshes containing the dormer frame.
     * @param {string} topBone - The name of the top bone.
     * @param {string} bottomBone - The name of the bottom bone.
     * 
     * @returns {number} - The calculated position difference along the Z-axis between the top and bottom bones of the gable dormer.
     */
    getGableDormerPositionZ = ( dormerFrame, meshes, topBone, BottomBone ) => {
        const roofDegree    = this.getRoofDegree();
        let dormerTop       = 0;
        let dormerBottom    = 0;
        meshes.forEach( m => {
            if( dormerFrame === m.name ) {
                m.skeleton.bones.forEach( b => {
                    if( topBone === b.name ) {
                        dormerTop = b.position.y;
                    } else if( BottomBone === b.name ) {
                        dormerBottom = b.position.y;
                    }
                } );
            }
        } );

        const dormerHeight = helpers.toFixed3( dormerTop - dormerBottom );

        return helpers.toFixed3( dormerHeight / helpers.getTanFromDegrees( roofDegree ) );
    }

    /**
     * Handles the operations related to a gable dormer, including moving bones, drawing walls, and handling the roof.
     * 
     * @param {string} dormerName - The name of the gable dormer.
     * @param {Mesh} firstMesh - The first mesh containing the dormer.
     * @param {string} componentKey - The key identifying the dormer component.
     */
    handleGableDomer = ( dormerName, firstMesh, componentKey ) => {
        const timeStamp     = componentKey.split( '_' ).pop();
        const childMeshes   = firstMesh.getChildMeshes();
        const dormerFrame   = `${dormerName}_frame_obj`;
        const dormerRoof    = `${dormerName}_roof_shingle_obj`;
        this.gableDormerMoveBones( dormerFrame, dormerRoof, firstMesh, childMeshes );
        // this.gableDormerAddIntersectObj( dormerName, dormerFrame, firstMesh, childMeshes, timeStamp );
        this.gableDormerDrawWall( dormerName, dormerFrame, firstMesh, childMeshes, timeStamp );
        this.gableDormerHandleRoof( dormerName, dormerFrame, dormerRoof, firstMesh, childMeshes, timeStamp );
    }

    /**
     * Updates the roof color for gable dormers.
     * 
     * This function retrieves the roof color data and updates the material properties based on the selected roof material option.
     */
    gableDormerHandleRoofColor = () => {
        const roofColorData         = this.getSidingColor( 'roof' );
        const { defaultDiffuse }    = this.getSidingMaterialData( jQuery( '#roof-material-option li.active' ) );

        if( ! roofColorData ) return;

        for( const key of this._roofMaterial ) {
            const mats  = this.getMaterialByName( key );
            if( ! mats ) continue;

            mats.forEach( mat => {
                switch( roofColorData.type ) {
                    case "image":
                        mat.diffuseColor = new Color3( 1, 1, 1 );
                        mat.diffuseTexture.updateURL( roofColorData.image );
                        break;

                    case "color":
                    case "color_picker":
                        mat.diffuseColor = Color3.FromHexString( roofColorData.code );
                        mat.diffuseTexture.updateURL( defaultDiffuse );
                        break;
                }
            } );
        };
    }

    /**
     * Handles the gable dormer roof, including disposing of old roof meshes, positioning and scaling new roof meshes,
     * and creating the final merged mesh for the roof.
     * 
     * @param {string} dormerName - The name of the gable dormer.
     * @param {string} dormerFrame - The name of the dormer frame mesh.
     * @param {string} dormerRoof - The name of the dormer roof mesh.
     * @param {Mesh} firstMesh - The first mesh containing the dormer.
     * @param {Mesh[]} childMeshes - Array of child meshes under the first mesh.
     * @param {string} timeStamp - The timestamp associated with the dormer component.
     */
    gableDormerHandleRoof = ( dormerName, dormerFrame, dormerRoof, firstMesh, childMeshes, timeStamp ) => {
        const listRoofs     = this.getListRoofs();
        const cutBlockCSG   = CSG.FromMesh( this._roofBlock );
        const skeletonKeys  = [ 'Dormer_R_B_R1', 'Dormer_R_F_R1', 'Dormer_R_F_T', 'Dormer_R_B_T', 'Dormer_R_B_L1', 'Dormer_R_F_L1' ];
        const roofBones     = [];
        const vertexRoof    = [];
        const vertexCelling = [];

        const roofMetalMesh             = this.filterByValue( firstMesh._children, 'name', '_roof_metal_obj' ) as Mesh;
        const roofShingleMesh           = this.filterByValue( firstMesh._children, 'name', '_roof_shingle_obj' ) as Mesh;
        const roofMetalCloneMesh        = this.filterByValue( firstMesh._children, 'name', '_roof_metal_clone_obj' ) as Mesh;
        const roofMetalHorizontalMesh   = this.filterByValue( firstMesh._children, 'name', '_roof_metal_horizontal_obj' ) as Mesh;

        if( roofMetalMesh ) {
            roofMetalMesh.setEnabled( 'metal' === this._roofType );
            this._roofMesh['metal'] = helpers.checkAndPushKeyToArray( this._roofMesh['metal'], roofMetalMesh.name );
        }

        if( roofShingleMesh ) {
            roofShingleMesh.setEnabled( 'shingle' === this._roofType );
            this._roofMesh['shingle'] = helpers.checkAndPushKeyToArray( this._roofMesh['shingle'], roofShingleMesh.name );
        }

        if( roofMetalHorizontalMesh ) {
            roofMetalHorizontalMesh.setEnabled( 'metal_horizontal' === this._roofType );
            this._roofMesh['metal_horizontal']  = helpers.checkAndPushKeyToArray( this._roofMesh['metal_horizontal'], roofMetalHorizontalMesh.name );
        }

        if( roofMetalCloneMesh ) {
            roofMetalCloneMesh.setEnabled( false );
        }

        skeletonKeys.forEach( key => {
            const bone          = this.filterByValue( roofMetalMesh.skeleton.bones, 'name', key );
            const boneOffset    = this.filterByValue( roofMetalMesh.skeleton.bones, 'name', `${key}_01` );
            if( bone ) {
                const position = bone.position;
                roofBones[key] = position;

                if( 'Dormer_R_B_L1' === key || 'Dormer_R_B_R1' === key ) {
                    position.z = 0;
                }

                const customPoint = boneOffset ? new Vector3( helpers.toFixed3( boneOffset.position.x ), helpers.toFixed3( boneOffset.position.y ), helpers.toFixed3( boneOffset.position.z ) ) : new Vector3( 0, 0.04, 0 );
                vertexRoof.push( position.add( new Vector3( 0, 0.04, 0 ) ) );
                vertexCelling.push( position.subtract( new Vector3( 0, 0.04, 0 ) ) );
            }
        } );

        const roofWidth     = helpers.toFixed3( Vector3.Distance( roofBones['Dormer_R_B_T'], roofBones['Dormer_R_B_L1'] ) );
        const roofLength    = helpers.toFixed3( Vector3.Distance( roofBones['Dormer_R_B_T'], roofBones['Dormer_R_F_T'] ) );

        for( let key of listRoofs ) {
            const dimensional = jQuery( `[data-roof-type="${key}"]` ).data( 'dimensional' );
            if( '2d' === dimensional ) {
                const meshRoofDraw = this.drawRoof2D( { key: `${key}_${timeStamp}`, vertexRoof, vertexCelling, meshWidth: roofWidth, meshLength: roofLength, roofKey: key } );
                meshRoofDraw.setParent( firstMesh );
                meshRoofDraw.position   = Vector3.Zero();
                meshRoofDraw.rotation   = Vector3.Zero();
        
                const roofCSG           = CSG.FromMesh( meshRoofDraw );
                const subtractMesh      = roofCSG.subtract( cutBlockCSG );
                const meshRoofCut       = subtractMesh.toMesh( `${meshRoofDraw.name}`, meshRoofDraw.material, this._scene, true );
                meshRoofCut.isPickable  = false;
                meshRoofCut.setEnabled( key === this._roofType );
                meshRoofCut.setParent( firstMesh );
                meshRoofCut.position    = Vector3.Zero();
                meshRoofCut.rotation    = Vector3.Zero();
                meshRoofDraw.dispose();
            } else if( '3d' === dimensional ) {
                const oldPosition   = new Vector3( firstMesh.position.x, firstMesh.position.y, firstMesh.position.z );
                const roofLeftName  = `${dormerName}_roof_${key}_left_${timeStamp}`;
                const roofRightName = `${dormerName}_roof_${key}_right_${timeStamp}`;
                const roofLeftMesh  = this.getMeshByName( roofLeftName );
                const roofRightMesh = this.getMeshByName( roofRightName );
                if( roofLeftMesh ) roofLeftMesh.dispose();
                if( roofRightMesh ) roofRightMesh.dispose();

                const metalName             = `${dormerName}_roof_metal_clone_obj`;
                const metalHorizontalName   = `${dormerName}_roof_metal_horizontal_obj`;
                let shingleMesh: Mesh;
                let metalMesh: Mesh;
                let metalHorizontalMesh: Mesh;
                let roofLeft    = new Vector3( 0, 0, 0 );
                let roofTop     = new Vector3( 0, 0, 0 );
                let startPoint  = new Vector3( 0, 0, 0 );
                let endPoint    = new Vector3( 0, 0, 0 );
                
                const roofThick     = 0.03;
                const wrongHeight   = 0.04;
                const wrongWidth    = 0.08;        

                childMeshes.forEach( m => {
                    if( dormerRoof === m.name ) {
                        shingleMesh = m as Mesh;
                        m.skeleton.bones.forEach( b => {
                            if( 'Dormer_R_F_L1' === b.name ) {
                                roofLeft    = new Vector3( b.position.x + wrongWidth, b.position.y - wrongHeight, b.position.z );
                                endPoint    = new Vector3( b.position.x + wrongWidth, b.position.y + roofThick, b.position.z );
                            } else if( 'Dormer_R_F_T' === b.name ) {
                                roofTop     = new Vector3( b.position.x, b.position.y, b.position.z );
                            } else if( 'Dormer_R_B_L1' === b.name ) {
                                startPoint  = new Vector3( b.position.x + wrongWidth, b.position.y + roofThick, 0 );
                            }
                        } );
                    } else if( metalHorizontalName === m.name ) {
                        metalHorizontalMesh = m as Mesh;
                    } else if( metalName === m.name ) {
                        metalMesh = m as Mesh;
                    }
                } );

                // if( shingleMesh ) shingleMesh.setEnabled( false );
                // if( metalHorizontalMesh ) metalHorizontalMesh.setEnabled( false );

                if( undefined === metalMesh ) {
                    return;
                }

                metalMesh.setEnabled( true );
                const meshWidth     = this.getMeshWidth( metalMesh );
                const meshLength    = this.getMeshLength( metalMesh );
                const roofWidth     = Vector3.Distance( roofLeft, roofTop );
                metalMesh.scaling.x = metalMesh.scaling.x * roofWidth / meshWidth;
                const vectorBetween = roofLeft.subtract( roofTop );
                const rotationZ     = Math.atan2( vectorBetween.y, vectorBetween.x );
                const distance      = Vector3.Distance( startPoint, endPoint );
                const divisions     = Math.floor( distance / meshLength );
                const partMissing   = distance - ( divisions * meshLength );
                const newMeshLength = ( partMissing / divisions ) + meshLength;
                const zScale        = newMeshLength / meshLength;
                const argsMeshMerge = [];

                for( let i = 0; i < divisions; i++ ) {
                    const meshClone         = metalMesh.clone( `${metalMesh.name}_${i}` );
                    meshClone.position      = Vector3.Lerp( startPoint, endPoint, ( 1 / divisions ) * i );
                    meshClone.rotation      = new Vector3( 0, 0, rotationZ );
                    meshClone.scaling._z    = zScale;
                    argsMeshMerge.push( meshClone );
                }

                firstMesh.position.z    = 0;
                const roof              = Mesh.MergeMeshes( argsMeshMerge );
                const cutBlockCSG       = CSG.FromMesh( this._roofBlock );
                const roofCSG           = CSG.FromMesh( roof );
                const subtractMesh      = roofCSG.subtract( cutBlockCSG );
                const mainMesh          = subtractMesh.toMesh( roofLeftName, roof.material, this._scene, true );
                const roofRight         = mainMesh.clone( roofRightName );
                roofRight.scaling.z     = -1;
                mainMesh.setParent( firstMesh );
                roofRight.setParent( firstMesh );
                roof.dispose();
                
                mainMesh.setEnabled( key === this._roofType );
                roofRight.setEnabled( key === this._roofType );
                this._roofMesh[key]  = helpers.checkAndPushKeyToArray( this._roofMesh[key], roofLeftName );
                this._roofMesh[key]  = helpers.checkAndPushKeyToArray( this._roofMesh[key], roofRightName );

                metalMesh.position  = new Vector3( 0, 0, 0 );
                metalMesh.rotation  = new Vector3( 0, 0, 0 );
                metalMesh.scaling   = new Vector3( 1, 1, 1 );
                firstMesh.position  = oldPosition;
                metalMesh.setEnabled( false );
            }
        }
    }

    /**
     * Draws the walls for the gable dormer based on its orientation.
     * 
     * @param {string} dormerName - The name of the gable dormer.
     * @param {string} dormerFrame - The name of the dormer frame mesh.
     * @param {Mesh} firstMesh - The first mesh containing the dormer.
     * @param {Mesh[]} childMeshes - Array of child meshes under the first mesh.
     * @param {string} timeStamp - The timestamp associated with the dormer component.
     */
    gableDormerDrawWall = ( dormerName, dormerFrame, firstMesh, childMeshes, timeStamp ) => {
        const frontData = { dormerName, side: 'front', dormerFrame, firstMesh, childMeshes, timeStamp, bone1: 'Dormer_B_F_L1', bone2: 'Dormer_B_F_L2', bone3: 'Dormer_B_F_T', bone4: 'Dormer_B_F_R2', bone5: 'Dormer_B_F_R1' };
        const leftData  = { dormerName, side: 'left', dormerFrame, firstMesh, childMeshes, timeStamp, bone1: 'Dormer_B_F_R1', bone2: 'Dormer_B_F_R2', bone3: 'Dormer_B_B_R1' };
        const rightData = { dormerName, side: 'right', dormerFrame, firstMesh, childMeshes, timeStamp, bone1: 'Dormer_B_F_L1', bone2: 'Dormer_B_F_L2', bone3: 'Dormer_B_B_L1' };
        this.gableDormerDrawSpecificWall( frontData );
        this.gableDormerDrawSpecificWall( leftData );
        this.gableDormerDrawSpecificWall( rightData );
    }

    /**
     * Draws a specific wall of the gable dormer based on its orientation.
     * 
     * @param {Object} input - The input parameters for drawing the specific wall.
     * @param {string} input.dormerName - The name of the gable dormer.
     * @param {string} input.side - The side of the dormer where the wall will be drawn (e.g., 'front', 'left', 'right').
     * @param {string} input.dormerFrame - The name of the dormer frame mesh.
     * @param {Mesh} input.firstMesh - The first mesh containing the dormer.
     * @param {Mesh[]} input.childMeshes - Array of child meshes under the first mesh.
     * @param {string} input.timeStamp - The timestamp associated with the dormer component.
     * @param {string} input.bone1 - The name of the first bone defining the shape of the wall.
     * @param {string} input.bone2 - The name of the second bone defining the shape of the wall.
     * @param {string} input.bone3 - The name of the third bone defining the shape of the wall.
     * @param {string} input.bone4 - The name of the fourth bone defining the shape of the wall.
     * @param {string} input.bone5 - The name of the fifth bone defining the shape of the wall.
     */
    gableDormerDrawSpecificWall = ( input ) => {
        const { dormerName, side, dormerFrame, firstMesh, childMeshes, timeStamp, bone1, bone2, bone3, bone4, bone5 } = input;

        let shape1: Vector3;
        let shape2: Vector3;
        let shape3: Vector3;
        let shape4: Vector3;
        let shape5: Vector3;
        let positon: Vector3;

        childMeshes.forEach( m => {
            if( dormerFrame === m.name ) {
                m.skeleton.bones.forEach( b => {
                    const x = helpers.hasKeyInArray( zSide, side ) ? b.position.z : b.position.x;
                    if( bone1 === b.name ) {
                        positon = new Vector3( b.position.x, b.position.y, b.position.z );
                        shape1  = new Vector3( x, 0, b.position.y );
                    } else if( bone2 === b.name ) {
                        shape2  = new Vector3( x, 0, b.position.y );
                    } else if( bone3 === b.name ) {
                        shape3  = new Vector3( x, 0, b.position.y );
                    } else if( bone4 === b.name ) {
                        shape4  = new Vector3( x, 0, b.position.y );
                    } else if( bone5 === b.name ) {
                        shape5  = new Vector3( x, 0, b.position.y );
                    }
                } );
            }
        } );

        if( undefined === positon ) {
            return;
        }

        const shapes      = [ shape1, shape2, shape3, shape4, shape5 ].filter( s => s !== undefined );
        const rotation    = this.getWallRotation( side );
        const endPosition = this.getWallPositionBySide( side, positon );

        this.drawWall( `${dormerName}_wall_${side}_${timeStamp}`, '', shapes, rotation, endPosition, `${dormerName}_mat_${side}_${timeStamp}`, undefined, firstMesh );
    }

    /**
     * Moves the bones of the gable dormer and roof meshes to specified positions based on their Z coordinates.
     * 
     * @param {string} dormerFrame - The name of the dormer frame mesh.
     * @param {string} dormerRoof - The name of the dormer roof mesh.
     * @param {Mesh} firstMesh - The first mesh containing the dormer.
     * @param {Mesh[]} childMeshes - Array of child meshes under the first mesh.
     */
    gableDormerMoveBones = ( dormerFrame, dormerRoof, firstMesh, childMeshes ) => {
        const backZ     = this.getGableDormerPositionZ( dormerFrame, childMeshes, 'Dormer_B_F_T', 'Dormer_B_F_R2' );
        const frontZ    = this.getGableDormerPositionZ( dormerFrame, childMeshes, 'Dormer_B_F_T', 'Dormer_B_F_R1' );

        childMeshes.forEach( m => {
            if( dormerFrame === m.name ) {
                m.skeleton.bones.forEach( b => {
                    if( 'Dormer_B_B_L1' === b.name || 'Dormer_B_B_R1' === b.name ) {
                        b.setPosition( new Vector3( b.position.x, b.position.y, backZ ) );
                    } else if( 'Dormer_B_F_L1' === b.name || 'Dormer_B_F_L2' === b.name || 'Dormer_B_F_R1' === b.name || 'Dormer_B_F_R2' === b.name || 'Dormer_B_F_T' === b.name ) {
                        b.setPosition( new Vector3( b.position.x, b.position.y, frontZ ) );
                    }
                } );
            } else if( dormerRoof === m.name ) {
                m.skeleton.bones.forEach( b => {
                    if( 'Dormer_R_B_L1' === b.name || 'Dormer_R_B_R1' === b.name ) {
                        b.setPosition( new Vector3( b.position.x, b.position.y, backZ ) );
                    } else if( 'Dormer_R_F_L1' === b.name || 'Dormer_R_F_R1' === b.name || 'Dormer_R_F_T' === b.name ) {
                        b.setPosition( new Vector3( b.position.x, b.position.y, frontZ ) );
                    }
                } );
            }
        } );
    }

    /**
     * Draws an intersect object for a gable dormer by creating a polyhedron based on the positions
     * 
     * @param {string} dormerName - The name of the dormer.
     * @param {string} dormerFrame - The name of the dormer frame mesh.
     * @param {Mesh} firstMesh - The main mesh of the dormer.
     * @param {Array<Mesh>} childMeshes - The child meshes of the main mesh.
     * @param {string} timeStamp - The timestamp used to create unique names.
     */
    gableDormerAddIntersectObj = ( dormerName, dormerFrame, firstMesh, childMeshes, timeStamp ) => {
        let point1: Vector3;
        let point2: Vector3;
        let point3: Vector3;
        let point4: Vector3;
        let point5: Vector3;
        let point6: Vector3;
       
        childMeshes.forEach( m => {
            if( dormerFrame === m.name ) {
                m.skeleton.bones.forEach( b => {
                    if( 'Dormer_B_F_L2' === b.name ) {
                        point1 = b.position.asArray();
                    } else if( 'Dormer_B_B_L1' === b.name ) {
                        point2 = b.position.asArray();
                    } else if( 'Dormer_B_B_T' === b.name ) {
                        point3 = b.position.asArray();
                    } else if( 'Dormer_B_F_T' === b.name ) {
                        point4 = b.position.asArray();
                    } else if( 'Dormer_B_F_R2' === b.name ) {
                        point5 = b.position.asArray();
                    } else if( 'Dormer_B_B_R1' === b.name ) {
                        point6 = b.position.asArray();
                    }
                } );
            }
        } );

        const dormerHeight  = this.getGableDormerDistance( `${dormerName}_frame_obj`, childMeshes, 'Dormer_B_F_R2', 'Dormer_B_F_R1', 'y' );
        const roofHeight    = this.getGableDormerDistance( `${dormerName}_frame_obj`, childMeshes, 'Dormer_B_F_T', 'Dormer_B_F_R2', 'y' );
        const topPoints     = [ point1, point2, point3, point4, point5, point6 ];
        const bottomPoints  = topPoints.map( ( p, index ) => {
            const height    = 2 === index || 3 === index ? dormerHeight + roofHeight : dormerHeight;
            return [ p[0], p[1] - height, p[2] ]
        } );
        const vertex        = [ ...topPoints, ...bottomPoints ];
        const face          = [
            [ 0, 1, 2, 3 ],
            [ 5, 4, 3, 2 ],
            [ 10, 11, 8, 7, 6 ],
            [ 11, 5, 2, 8 ],
            [ 8, 2, 1, 7 ],
            [ 6, 0, 3, 4, 10 ],
            [ 11, 10, 4, 5 ],
            [ 6, 7, 1, 0 ],
        ];

        const intersectName     = `${dormerName}_intersect_obj_${timeStamp}`;
        const oldIntersectObj   = this.getMeshByName( intersectName );
        if( oldIntersectObj ) oldIntersectObj.dispose();

        const intersectObj      = MeshBuilder.CreatePolyhedron( intersectName, { custom: { vertex, face }, updatable: true }, this._scene );
        intersectObj.rotation   = new Vector3( firstMesh.rotation.x, firstMesh.rotation.y, firstMesh.rotation.z );
        intersectObj.position   = new Vector3( firstMesh.position.x, firstMesh.position.y, firstMesh.position.z );
        intersectObj.visibility = 0;

        intersectObj.setParent( firstMesh );
    }

    /**
     * Updates all gable dormers by iterating over all components and handling each dormer.
     */
    updateGableDormer = () => {
        const components = this.getAllComponents();
        for( let key in components ) {
            const { meshes, name, type } = components[key];
            if( 'dormer' === type && meshes.length ) {
                if( this.isGableDormer( name ) ) {
                    this.handleGableDomer( name, meshes[0], key );
                }
            }
        }
    }

    updateFlatDormer = () => {
        const components = this.getAllComponents();
        for( let key in components ) {
            const { meshes, name, type } = components[key];
            if( 'dormer' === type && meshes.length ) {
                if( ! this.isGableDormer( name ) ) {
                    this.flatDormerHandleWall( meshes[0] );
                }
            }
        }
    }

    /**
     * Updates the roof block mesh by creating a polyhedron based on the provided bone positions.
     * Disposes of the previous roof block if it exists.
     * 
     * @param {Object} bones - An object containing bone positions.
     */
    updateRoofBlock = ( bones ) => {
        if ( 'box_gable' !== this.getRoofShape() && 'open_gable' !== this.getRoofShape() ) {
            return;
        }

        const keys = [ 'r_f_r1', 'r_b_r1', 'r_b_t', 'r_f_t', 'r_f_l1', 'r_b_l1' ];
        if( ! helpers.isExistBones( keys, bones ) ) {
            return;
        }

        if( undefined !== this._roofBlock ) {
            this._roofBlock.dispose();
        }

        const vertex    = [];
        const face      = [ [ 0, 1, 2, 3 ], [ 5, 4, 3, 2 ], [ 0, 3, 4 ], [ 2, 1, 5 ], [ 4, 5, 1, 0 ] ];
        keys.forEach( key => { if( bones[key] ) vertex.push( bones[key].position.asArray() ) } );

        if( vertex.length ) {
            this._roofBlock = MeshBuilder.CreatePolyhedron( '_roofBlock', { custom: { vertex, face }, updatable: true }, this._scene );
            this._roofBlock.setEnabled( false );
        }
    }

    /**
     * Sets the initial roof angle options for the product page.
     * Encodes the provided roof angle options as a base64 string and updates the corresponding input field.
     * 
     * @param {string} type - The type of roof angle.
     * @param {number} lower - The lower roof angle value.
     * @param {number} [upper=0] - The upper roof angle value (default is 0).
     */
    setInitRoofAngel = ( type, lower, upper = 0 ) => {
        jQuery( '.woocommerce.single input[name="ssb-option-init-roof"]' ).val( window.btoa( JSON.stringify( { type, lower, upper } ) ) );
    }

    /**
     * Checks if a component with the given name belongs to the 'window_bay' sub-category.
     * 
     * @param {string} name - The name of the component to check.
     * 
     * @returns {boolean} - Returns true if the component belongs to the 'window_bay' sub-category, otherwise false.
     */
    isBayWindow = name => {
        const categories = this.getComponentCategory( name );

        return 'window_bay' === categories.sub_category
    }
    
    /**
     * Handles the display and material settings for a bay window based on the roof type.
     * 
     * @param {Object} firstMesh - The main mesh object which may contain child meshes.
     * 
     * @returns {void} - This function does not return anything.
     */    
    handleBayWindow = firstMesh => {
        if( ! firstMesh || ! firstMesh._children ) return;

        let roofType        = 'shingle_3tab' === this.getRoofType() ? 'shingle' : this.getRoofType();
        const referenceRoof = jQuery( `[data-roof-type="${roofType}"]` ).attr( 'data-reference-key' );
        if( undefined !== referenceRoof && '' !== referenceRoof ) {
            roofType = referenceRoof;
        }

        for( const key in firstMesh._children ) {
            const mesh = firstMesh._children[key] as Mesh;

            if( ! mesh ) continue;

            const regex     = new RegExp( '.+?_roof_(.+?)_obj' );
            const regResult = regex.exec( mesh.name );

            if( regResult ) {
                mesh.setEnabled( regResult[1] === roofType );
                this._roofMesh[regResult[1]] = helpers.checkAndPushKeyToArray( this._roofMesh[regResult[1]], mesh.name );
            }
        }

        if( ! this._apiData['material'].includes( 'bay_window_siding_mat' ) ) {
            this._apiData['material'].push( 'bay_window_siding_mat' );
        }

    }

    /**
     * Notice replaces a component with a new one and provides a reason for the change.
     * 
     * @param {string} oldKey - The key of the old component.
     * @param {string} newKey - The key of the new component.
     * @param {Array<string>} reasons - The reasons for the change.
     */
    replaceComponentNotice = ( oldKey, newKey, reasons ) => {
        const oldName       = helpers.getLabel( oldKey );
        const newName       = helpers.getLabel( newKey );
        const last          = reasons.pop();
        const reasonText    = reasons.length ? reasons.join( ', ' ) + ' and ' + last : last;

        this.createAlert( `${oldName} has been changed to ${newName} due to ${reasonText}.` );
    }

    /**
     * Creates a notice for the removal of a component and provides the reason(s) for the removal.
     * 
     * @param {string} oldKey - The key of the component to be removed.
     * @param {Array<string>} reasons - The reasons for the removal.
     */
    removeComponentNotice = ( oldKey, reasons ) => {
        const oldName       = helpers.getLabel( oldKey );
        const last          = reasons.pop();
        const reasonText    = reasons.length ? reasons.join( ', ' ) + ' and ' + last : last;

        this.createAlert( `${oldName} has been removed due to ${reasonText}.` );
    }

    /**
     * Get all stairs in setting.
     * 
     * @returns {Object}
     */
    getAllStairs = () => {
        const stairs = this.getSettingData( 'stair' );
        if( false === stairs ) {
            return [];
        }

        return stairs;
    }

    /**
     * Get a stair object based on the current dimensions and conditions.
     * 
     * @returns {Object} - The stair object that matches the conditions.
     */
    getAvailableStairs = () => {
        return this.getAllStairs().filter( item => {
            return helpers.isValidSize( item['condition_width']['operator'], helpers.getBaseWidth(), item['condition_width']['value'], item['condition_width']['max_value'] )
            && helpers.isValidSize( item['condition_depth']['operator'], helpers.getBaseDepth(), item['condition_depth']['value'], item['condition_depth']['max_value'] )
            && helpers.isValidSize( item['condition_height']['operator'], helpers.getBaseHeight(), item['condition_height']['value'], item['condition_height']['max_value'] );
        } );
    }

    /**
     * Adjusts the position of a stair object based on its side and offset.
     *
     * @param {Object} position - The current position of the stair, with x and z coordinates.
     * @param {string} side - The side of the stair ('left', 'right', 'front', 'back') which determines the axis to adjust.
     * @param {number} offset - The distance by which to offset the stair's position.
     * @returns {Object} - The updated position object with adjusted x or z coordinate.
     */
    getFloorComponentPositionByOffset = ( position, side, offset ) => {
        const isZSide       = helpers.hasKeyInArray( zSide, side );
        const isPositive    = helpers.hasKeyInArray( positiveSide, side );
        if( isZSide ) {
            position.x += isPositive ? offset : - offset;
        } else {
            position.z += isPositive ? - offset : offset;
        }

        return position;
    }

    /**
     * Removes all stair objects from the scene.
     */
    removeStairs = () => {
        for( let i = 0; i < this._stairs.length; i++ ) {
            this._stairs[i].dispose();
        }

        this._stairs = [];
    }

    /**
     * Clones a stair mesh and prepares it for placement in the scene.
     *
     * @param {Object} stairInfo - Configuration data for the stair, including its name.
     * @param {number} index - The index used to differentiate the clone's name.
     * @returns {Promise<Mesh|undefined>} - A promise that resolves to the cloned stair mesh, or undefined if the mesh could not be loaded.
     */
    cloneStairs = async ( stairInfo, index ) => {
        return new Promise( async resolve => {
            const type          = 'stair';
            const stairName     = stairInfo['name'];
            const side          = stairInfo['place_along_side']['side'];
            const stairObjName  = `${stairName}_set_obj`;
            let stairMesh       = this._scene.getMeshByName( stairObjName ) as Mesh;
            
            if( ! stairMesh ) {
                const stairMeshes = await this.loadBabylonFile( stairName, type );
                if( stairMeshes ) {
                    stairMesh = stairMeshes[0];
                }
            }

            if( ! stairMesh ) {
                return resolve( false );
            }

            
            let width   = 0;
            let depth   = 0;
            let height  = 0;
            stairMesh.setEnabled( true );
            stairMesh.getChildMeshes().forEach( mesh => {
                mesh.isPickable = false;
                if( -1 !== mesh.name.indexOf( 'bb_obj' ) ) {
                    width           = this.getMeshWidth( mesh );
                    depth           = this.getMeshLength( mesh );
                    height          = this.getMeshHeight( mesh );
                    mesh.visibility = 0;
                }
            } );
            const stairClone    = stairMesh.clone( `${stairMesh.name}_${index}` ) as Mesh;
            stairClone.metadata = { side, width, depth, height, originalKey: stairName };
            stairMesh.setEnabled( false );
            
            resolve( stairClone );
        });
    }

    /**
     * Places a stair object within the scene based on provided configuration data.
     *
     * @param {Object} stairInfo - Configuration information for the stair, including its position, side, and offsets.
     * @param {Object} stairClone - The 3D mesh clone of the stair to be placed.
     */
    placeStair = ( stairInfo, stairClone ) => {
        const type          = 'stair';
        const childMeshes   = stairClone.getChildMeshes();
        const side          = stairInfo['place_along_side']['side'];
        const stairName     = stairInfo['name'];
        const meshes        = [ stairClone, ...childMeshes ];
        const anchor        = stairInfo['horizontal_position']['anchor'];
        const distance      = helpers.toFixed3( Number( stairInfo['horizontal_position']['distance'] ) / 12 );
        const positionType  = 'left' === anchor || 'right' === anchor ? 'manual' : 'default';
        const vertical      = { distance: this.getFloorOffset(), measured_from: 'ground' };
        const horizontal    = { distance, measured_from: anchor, roof_top_aligned: positionType };
        const rotation      = this.calculateComponentRotation( side, type );
        const tmpPosition = this.calculateLayoutPosition( meshes, side, vertical, horizontal, type, stairName );

        const offset        = ! isNaN( Number( stairInfo['place_along_side']['offset'] ) ) && 0 !== Number( stairInfo['place_along_side']['offset'] ) ? helpers.toFixed3( Number( stairInfo['place_along_side']['offset'] ) / 12 ) : 0;
        const position      = this.getFloorComponentPositionByOffset( tmpPosition, side, offset );

        stairClone.rotation = new Vector3( rotation.x, rotation.y, rotation.z );
        stairClone.position = new Vector3( position.x, position.y, position.z );
    }

    /**
     * Load and configures a stair object in the scene.
     */
    handleStairs = async () => {
        const type      = 'stair';
        const stairs    = this.getAvailableStairs();
        const allStairs = [];
        for( let index = 0; index < stairs.length; index++ ) {
            const stairClone = await this.cloneStairs( stairs[index], index ) as Mesh;
            if( stairClone ) {
                allStairs.push( stairClone );
                this.placeStair( stairs[index], stairClone );
            }
        }

        return allStairs;
    }

    /**
     * Get light offset
     */
    getLightOffset = () => {
        const lightOffset = this.getSettingData( 'light_offset' );
        
        return 0 === lightOffset ? lightOffset : helpers.toFixed3( lightOffset / 12 );
    }

    /**
     * Checks if the light component is placed horizontally on the side of the shed.
     * @param {string} type - The type of component to check.
     * 
     * @returns {boolean} true if the light is placed horizontally on the side, false otherwise.
     */
    isMoveHorizontal = ( type ) => {
        return 'light' === type && 'along_joist' === this.getSettingData( 'light_move_direction' );
    }

    /**
     * Get all the light positions from the settings data.
     * 
     * @returns {array} An array of light positions
     */
    getAllLightPositions = () => {
        const lightPosition = this.getSettingData( 'light_position' );

        return false === lightPosition ? [] : lightPosition;
    }

    /**
     * Gets the light position based on the current size of the shed.
     * 
     * @returns {object} The light position object
     */
    getLightPosition = () => {
        return this.getAllLightPositions().find( item => {
            return helpers.isValidSize( item['condition_width']['operator'], helpers.getBaseWidth(), item['condition_width']['value'], item['condition_width']['max_value'] )
            && helpers.isValidSize( item['condition_depth']['operator'], helpers.getBaseDepth(), item['condition_depth']['value'], item['condition_depth']['max_value'] )
            && helpers.isValidSize( item['condition_height']['operator'], helpers.getBaseHeight(), item['condition_height']['value'], item['condition_height']['max_value'] );
        } );
    }

    /**
     * Handles the swap side by swapping the sides of the components in the package and removing the frame if the shed is not a carport.
     */
    swapSideHandleModel = async () => {
        if( ! this.isCarport() ) {
            this.removeFrame();
        }

        this.setDefaultOverhangData();
        this.moveBones();
        this.doClone();
        this.drawRoof();
        this.adjustUVs( false );
        this.adjustCamera();
        this.resizeCollisionBox();
        this.showHorizontalTrims();
        this.removeLine();
        this.removeNoticeText();

        await this.buildMesh();

        window.dispatchEvent( new CustomEvent( 'change-size' ) );
    }

    /**
     * Swaps the sides of the components in the package.
     * 
     * @param {array} sides The sides to swap.
     */
    swapSideHandlePackageComponent = ( sides ) => {
        for( const component of this._defaultAllComponentInPackage ) {
            component['side'] = this.convertSides( sides, component['side'] );
        }
    }

    /**
     * Swaps the sides of the shutters in the objects.
     * 
     * @param {array} sides The sides to swap.
     */
    swapSideHandleShutterInObject = ( sides ) => {
        if( Object.keys( this._shuttersInObject ).length > 0 ) {
            for( let key in this._shuttersInObject ) {
                this._shuttersInObject[key]['side'] = this.convertSides( sides, this._shuttersInObject[key]['side'] );

                const parent = this._shuttersInObject[key]['parent'];
                const side = this._shuttersInObject[key]['side'];
                const type = this._shuttersInObject[key]['type'];
                const name = this._shuttersInObject[key]['name'];
                const face = 'exterior';
                const shutterInfo = {
                    key,
                    face,
                    type,
                    name,
                    side,
                    parent,
                }
        
                window.dispatchEvent( new CustomEvent( 'add-component', { detail: shutterInfo } ) );
            }
        }
    }

    /**
     * Handles the swap side.
     * 
     * @param {array} sides The sides to swap.
     * @param {boolean} fromLeftToRight Whether to swap from left to right.
     */
    swapSideHandleComponent = ( sides, fromLeftToRight ) => {
        this.swapSideHandlePackageComponent( sides );

        const components = this.getAllComponents();
        for( let k in components ) {
            const side          = components[k].side;
            const newSide       = this.convertSides( sides, side );
            let componentinfo   = this._walls[side]['items'][k];
            let { position, meshes, name, type, width } = componentinfo;

            if( side === newSide ) {
                if( 'light' === type ) {
                    let tmpPosition = this.getPositionChangeComponent( side, position, width, type );
                    tmpPosition.y   = this.getPositionYForLight();
                    this.placeComponent( meshes, tmpPosition, componentinfo.rotation, type, side, name );
                }
            } else {
                if( 'left' === newSide || 'right' === newSide ) {
                    position.y = position.y > this.getWallHeight() ? this.getWallHeight() : position.y;
                }

                componentinfo.rotation = this.calculateComponentRotation( newSide, type );
                componentinfo.position = fromLeftToRight ? new Vector3( - position.z, position.y, position.x ) : new Vector3( position.z, position.y, - position.x );

                if( null == components[k].parent ) {
                    this.placeComponent( meshes, componentinfo.position, componentinfo.rotation, type, side, name );
                }

                this.handleMoveComponentToSide( side, newSide, componentinfo );
            }
        }

        this.swapSideHandleShutterInObject( sides );
    }

    /**
     * Handles the swap side.
     * 
     * @param {array} sides The sides to swap.
     * @param {boolean} fromLeftToRight Whether to swap from left to right.
     */
    handleSwapSide = async ( sides, fromLeftToRight ) => {
        this._engine.displayLoadingUI();
        this._engine.stopRenderLoop();

        this.swapSideHandleModel();
        this.swapSideHandleComponent( sides, fromLeftToRight );

        if( ! fromLeftToRight ) {
            await this.handleLayoutComponent();
            this.distributeComponentEqually();
        }
        this.isValidAllComponents();

        this._engine.runRenderLoop( () => this._scene.render() );
        this._engine.hideLoadingUI();
    }
    
    /**
     * Gets the default package components.
     * 
     * @returns {array} The default package components.
     */
    getDefaultPackageComponents = () => {
        return JSON.parse( JSON.stringify( this._defaultAllComponentInPackage ) );
    }

    /**
     * Updates the given components with the converted sides if swapping sides is enabled.
     * 
     * @param {array} components - The components to update.
     * 
     * @returns {array} The updated components.
     */
    updatePackageComponents = ( components ) => {
        const tmpComponents = JSON.parse( JSON.stringify( components ) );

        if( ! helpers.isSwapSide() ) {
            return tmpComponents;
        }

        for( const item of tmpComponents ) {
            item['side'] = this.convertSides( convertSides, item['side'] );
        }

        return tmpComponents;
    }

    /**
     * Convert a side to its corresponding side in the given sides array.
     * 
     * @param {object} sides - An object with side names as keys and their corresponding side names as values.
     * @param {string} side - The side to convert.
     * 
     * @returns {string} The converted side.
     */
    convertSides = ( sides, side ) => {
        return undefined !== sides[side] ? sides[side] : side;
    }

    /**
     * Checks if the current shed size has an associated opening setting.
     * 
     * @returns {boolean} true if the current shed size has an opening setting, false otherwise
     */
    hasOpeningSetting = () => {
        const openingSettings = this.getSettingData( 'opening_and_post' );

        return false !== openingSettings;
    }

    /**
     * Get the opening setting based on the current size of the shed.
     * 
     * @returns {object | undefined} The opening setting object if found, otherwise undefined
     */
    getOpeningSetting = () => {
        const openingSettings = this.getSettingData( 'opening_and_post' );
        if( false === openingSettings ) {
            return undefined;
        }

        return openingSettings.find( item => {
            return helpers.isValidSize( item['condition_width']['operator'], helpers.getBaseWidth(), item['condition_width']['value'], item['condition_width']['max_value'] )
            && helpers.isValidSize( item['condition_depth']['operator'], helpers.getBaseDepth(), item['condition_depth']['value'], item['condition_depth']['max_value'] )
            && helpers.isValidSize( item['condition_height']['operator'], helpers.getBaseHeight(), item['condition_height']['value'], item['condition_height']['max_value'] );
        } );
    }

    /**
     * Checks if the current shed size has an associated setting to update the color of a specific component.
     * 
     * @param {string} componentType - The type of component (e.g. door, window, etc.).
     * 
     * @returns {boolean} true if the current shed size has an associated setting to update the color of the component, false otherwise.
     */
    isUpdateColorForSpecificComponent = ( componentType ) => {
        return this.getSettingData( `is_set_specific_${componentType}_color` );
    }

    /**
     * Updates the color of the specified component type based on the given texture information.
     * 
     * @param {string} componentType - The type of component (e.g. door, window, etc.).
     * @param {string} textureType - The type of the texture (e.g. diffuse, bump, etc.).
     * @param {string} textureImage - The URL of the new texture image.
     * @param {string} textureColor - The new color of the texture in hexadecimal format.
     * @param {Mesh[]} meshes - An array of meshes to update the color of.
     * 
     * @returns {boolean} true if the color of at least one component was updated, false otherwise.
     */
    updateComponentColor = ( componentType, textureType, textureImage, textureColor, meshes ) => {
        const materials = this.getSettingData( `${componentType}_materials` );
        let isUpdate = false;
        if( false === materials ) {
            return isUpdate;
        }

        meshes.forEach( mesh => {
            const componentMat = mesh.material as StandardMaterial;
            if( componentMat ) {
                if( -1 !== materials.indexOf( componentMat.name ) ) {
                    componentMat.metadata = { hasColor: false };
                    this.updateTexture( componentMat, textureType, textureImage, textureColor );
                    componentMat.metadata = { hasColor: true };
                    isUpdate = true;
                }
                if( componentMat['subMaterials'] ) {
                    const componentSubMats = componentMat['subMaterials'];
                    componentSubMats.forEach( subMat => {
                        if( -1 !== materials.indexOf( subMat.name ) ) {
                            subMat.metadata = { hasColor: false };
                            this.updateTexture( subMat, textureType, textureImage, textureColor );
                            subMat.metadata = { hasColor: true };
                            isUpdate = true;
                        }
                    } );
                }
            }
        } );

        return isUpdate;
    }

    /**
     * Checks if the shed type is a double gable.
     * 
     * @return {boolean} true if the shed type is a double gable, false otherwise.
     */
    isDoubleGable = (): boolean => {
        return 'structure_aframe_double_gable' === this._shedType;
    }

    /**
     * Calculates the half gable width based on the initial box gable width and roof gable degrees.
     * 
     * @returns {number} The calculated half gable width.
     */
    getHalfGableWidth = (): number => {
        // Calculate the half gable width using trigonometry
        return helpers.toFixed3( this._initBoxGableWidth * helpers.getCosFromDegrees( this.getGlobalData( constants.initRoofGableDegrees ) ) );
    }

    /**
     * Calculates the porch width for a double gable structure.
     * 
     * @return {number} The porch width, calculated as the shed depth minus four times the half gable width.
     */
    doubleGableGetPorchWidth = (): number => {
        // Calculate porch width by subtracting four times the half gable width from the total depth
        return helpers.toFixed3( this.getDepth() - 4 * this.getHalfGableWidth() );
    }

    /**
     * Handles post and baluster cloning for double gable structures.
     * 
     * If the structure is a double gable, it clones the post and baluster from the starting bone (b_p_l1) to the ending bone (b_p_l3).
     */
    handlePostAndBaluster = () => {
        let startBone = '';
        let endBone = '';
        if( this.isDoubleGable() ) {
            // For double gable structures, start at the left front corner of the porch and end at the left back corner of the porch
            startBone = 'b_p_l1';
            endBone = 'b_p_l3';
        } else if( this.isAframeSingleGable() ) {
            startBone = 'b_p_l1';
            endBone = 'b_p_l4';
        }

        this.clonePostAndBaluster( startBone, endBone );
        this.aframeSingleGableCloneBaluster();
    }

    /**
     * Calculates the total number of posts needed, with an adjustment 
     * based on whether the structure is an "A-frame Single Gable."
     * 
     * @param {number} numberPost - The initial number of posts to calculate from.
     * @returns {number} - The adjusted total number of posts.
    */
    getTotalPost = ( numberPost ) => {

        if( this.isAframeSingleGable() ) {
            numberPost -= 1;
        }

        return Number( numberPost );
    }

    /**
     * Determines if the specified side is the floor.
     * 
     * @param {string} side - The side to check.
     * @returns {boolean} true if the side is the floor, false otherwise.
     */
    ignoreFloor = ( side ) => {
        return 'floor' === side;
    }

    /**
     * Removes all components from the specified side if they don't fit
     * 
     * @param {string} side - The side to remove components from.
     */
    removeComponentBySide = ( side ) => {
        if( ! this._isUpdateLayout || ! this._walls[side] ) {
            return;
        }

        // Get all the keys of the components in the specified side
        const keys = Object.keys( this._walls[side].items );
        for( const key of keys ) {
            const componentInfo = this._walls[side].items[key];
            const targetPosition = new Vector3( componentInfo.position.x, componentInfo.position.y, componentInfo.position.z );
            const args = {
                side,
                targetPosition,
                component: componentInfo,
                componentWidth: componentInfo.componentWidth,
                componentHeight: componentInfo.componentDepth
            };

            // Check if the component still fits in the specified side
            if( ! this.isComponentInSideMeshes( args ) ) {
                this._isUpdateLayout = true;
                // Delete the component if it doesn't fit
                this.deleteComponentV2( side, key, componentInfo.componentType );
                this.createAlert( this.NOTICES.itemNotFit );
                // Update the shed info
                window.dispatchEvent( new CustomEvent( 'update-shed-info' ) );
            }
        }
    }

    /**
     * Checks if a feature is enabled in the beta features list.
     *
     * @param {String} featureKey The key of the feature to check.
     *
     * @returns {Boolean} True if the feature is enabled, false otherwise.
     */
    isBetaFeature = ( featureKey ) => {
        const betaFeatures = this.getSettingData( 'beta_features' );

        return betaFeatures && betaFeatures.includes( featureKey );
    }

    /**
     * Get the upcharge cost for the specified color and component.
     * 
     * @param {string} colorName - The name of the color.
     * @param {string} componentName - The name of the component.
     * @returns {Object} - An object with the label, price, and priceType of the upcharge. Empty object if no upcharge is needed.
     */
    getColorUpcharge = ( colorName, componentName ) => {
        if( ! helpers.isOhioShedSolutions() ) {
            // Ohio Shed Solutions only
            return {};
        }

        const categories = this.getComponentCategory( componentName );
        if( ! categories || 'door_wood' !== categories.sub_category ) {
            // Only apply upcharge for door_wood components
            return {};
        }

        const sidingColorInfo = this.getSidingColor( 'siding' );
        const trimColorInfo = this.getSidingColor( 'trim' );
        const sidingColorText = sidingColorInfo.name;
        const trimColorText = trimColorInfo.name;
        if( undefined === colorName || undefined === sidingColorText || undefined === trimColorText ) {
            // If any of the colors are undefined, return empty object
            return {};
        }

        if( colorName != sidingColorText && colorName != trimColorText && sidingColorText != trimColorText ) {
            // If the door color is different from the siding and trim colors, apply the upcharge
            return {
                type_display: 'Upcharge',
                display: 'custom color for door',
                price: 25,
                priceType: 'fixed'
            };
        }

        // No upcharge is needed
        return {};
    }
}

DefaultLoadingScreen.prototype.displayLoadingUI = function () {
    if (document.getElementById("ssb-loading-screen")) {
        // Do not add a loading screen if there is already one
        document.getElementById("ssb-loading-screen").style.display = "flex";
        return;
    }
    this._loadingDiv = document.createElement("div");
    this._loadingDiv.id = "ssb-loading-screen";
    this._loadingDiv.innerHTML = '<img src="/wp-content/plugins/seadev-shed-builder-beta/public/img/loading.gif" alt="Loading" class="ssb-loading-img">';
    var customLoadingScreenCss = document.createElement('style');
    customLoadingScreenCss.type = 'text/css';
    customLoadingScreenCss.innerHTML = `
    #ssb-loading-screen{
        width: 100% !mportant;
        height: 100% !important;
        top: 0px !important;
        left: 0px !important;
        background-color: rgba(255,255,255,0.5);
        color: white;
        text-align: center;
        display: flex;
        display: -ms-flex;
        display: -moz-flex;
        display: -webkit-flex;
        justify-content: center;
        -ms-justify-content: center;
        -moz-justify-content: center;
        -webkit-justify-content: center;
        align-items: center;
        -ms-align-items: center;
        -moz-align-items: center;
        -webkit-align-items: center;
    }
    .ssb-loading-img {
        width: 100px;
        height: 100px;
    }`;
    document.getElementsByTagName('head')[0].appendChild(customLoadingScreenCss);
    this._resizeLoadingUI();
    window.addEventListener("resize", this._resizeLoadingUI);
    document.getElementById('ssb-builder').appendChild(this._loadingDiv);
};

DefaultLoadingScreen.prototype.hideLoadingUI = function(){
    jQuery('form.cart [name="add-to-cart"]:not(.is_disabled)').prop('disabled', false);
    document.getElementById("ssb-loading-screen").style.display = "none";
}